// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
import { lbModuleTips } from './lb-extras.js';
export const lb = {
    ai_governance_pages: {
        nav: {
            inventory: 'AI inventory reviews',
            payment: 'Payment authorities',
            interventions: 'Interventions',
            complaints: 'AI complaints',
            framework: 'Framework record and three lines',
            board: 'Board report: AI figures',
        },
        common: {
            loading: 'Loading…',
            loadFailed: 'This could not be read.',
            retry: 'Try again',
            retrySr: 'read it again',
            notRecorded: 'Not recorded',
            dueNotInForce: 'Not counted: the cadence is not yet in force',
            none: 'None',
            choose: 'Choose…',
            cancel: 'Cancel',
            close: 'Close',
            saving: 'Saving…',
            saveFailed: 'The record was not saved.',
            requiredMark: '(required)',
            missingHeading: 'The record cannot be saved yet. These are missing:',
            directoryFailed: 'The list of people could not be read.',
            directoryPartial: 'Showing {shown} people. The list is not complete.',
            yes: 'Yes',
            no: 'No',
            readOnly: 'Read only',
            noAnswer: 'The server did not answer.',
            listTruncated: 'Showing {shown} of {total} rows. The list is not complete.',
            awaitingOtherApprover: 'Awaiting approval by another person',
            obligationsRetrySr: 'read the dated obligations again',
            systemDatesRetrySr: "read this system's dated obligations again",
            actions: 'Actions',
        },
        states: {
            incomplete: 'Incomplete',
            complete: 'Complete',
            not_recorded: 'Not recorded',
            not_evidenced: 'Not evidenced',
            pending_approval: 'Awaiting a second approval',
            expired: 'Expired',
            matches: 'Matches',
            mismatch: 'Mismatch',
            requested: 'Requested',
            confirmed: 'Confirmed',
            recorded: 'Recorded',
            performed: 'Performed',
            not_mandatory: 'Not mandatory',
            not_applicable: 'Not applicable',
            open: 'Open',
            closed: 'Closed',
            routed: 'Routed',
            stale: 'Stale',
            signed: 'Signed',
            current: 'Current',
            overdue: 'Overdue',
            due: 'Due',
            cadence_not_declared: 'No cadence declared',
            not_yet_in_force: 'Not yet in force',
            not_judged: 'Not judged',
            not_required: 'Not required',
            not_trained: 'Not trained',
            completed: 'Completed',
            unsettled: 'Unsettled',
            present: 'Present',
            absent: 'Absent',
            pending: 'Pending',
            ready: 'Ready',
            active: 'Active',
            suspended: 'Suspended',
            revoked: 'Revoked',
            approved: 'Approved',
            reopened: 'Reopened',
            basis_not_retained: 'Basis not retained',
            reason_notified_basis_recorded: 'Reason notified; basis held',
            needs_evidence: 'Needs evidence',
            evidenced: 'Evidenced',
            not_started: 'Not started',
            in_progress: 'In progress',
            evidence_captured: 'Evidence captured',
            answered: 'Answered',
        },
        missing: {
            risk_tier: 'An approved risk level from a reviewed classification is needed.',
            impact_assessment: 'An approved impact assessment is needed for a high-risk system.',
            validation: 'A validation signed for this version is needed.',
            validation_findings: 'Every finding on this version’s validation must be closed on a re-test.',
            security_tests: 'Every attack category needs a record, and none of poisoning, evasion or extraction may be open for a high-risk version.',
            rollback_plan: 'A rollback plan naming an earlier version of this system is needed.',
            senior_management_approval: 'A high-risk go-live needs the senior management body that approved it, and the date.',
            documents_retained_for_audit: 'The documents kept for audit',
            data_disposition: 'How the data was disposed of or isolated',
            model_disposition: 'How the models were disposed of or isolated',
            configuration_disposition: 'How the configuration was disposed of or isolated',
        },
        revalidation: {
            revalidation_required: 'Re-validation required',
            revalidation_not_required: 'Re-validation not required',
        },
        separation: {
            required: 'Required: a different person re-tests',
            not_required: 'Not required: basis stated',
        },
        securityStates: {
            not_mandatory: 'Not mandatory',
            performed: 'Performed',
            not_applicable: 'Not applicable',
            open: 'Open',
        },
        attackCategories: {
            privacy_breach: 'Privacy breach',
            data_poisoning: 'Data poisoning',
            model_poisoning: 'Model poisoning',
            evasion: 'Evasion',
            model_extraction: 'Model extraction',
            prompt_injection: 'Prompt injection',
            hallucination: 'Hallucination',
            bias_in_generated_output: 'Bias in generated output',
        },
        validationAreas: {
            data_fit: 'Fit of training and test data',
            algorithm_soundness: 'Soundness of algorithm and architecture',
            performance_accuracy: 'Performance and accuracy',
            explainability: 'Explainability',
            fairness_bias_control: 'Fairness and bias control',
            stability: 'Stability',
            third_party_purpose_scope_risk: 'Third party: purpose, scope and risk',
            third_party_black_box_on_own_data: 'Third party: black-box tests on the bank data',
            third_party_output_under_operating_conditions: 'Third party: output under operating conditions',
            third_party_provider_documentation: 'Third party: provider documentation',
            third_party_human_controls_and_monitoring: 'Third party: human controls and monitoring',
        },
        versions: {
            heading: 'Versions, change control and rollback',
            description: 'Each change writes a version row; the prior value is kept. A change after release needs a re-validation decision by a named person.',
            add: 'Add a version',
            addTitle: 'Add a version',
            addSubmit: 'Save version',
            empty: 'No version is recorded yet.',
            colSequence: 'No.',
            colKind: 'Kind',
            colModel: 'Model version',
            colApplied: 'Applied',
            colChanged: 'Changed or stopped',
            colConfig: 'Configuration',
            colTests: 'Test result',
            colRollbackPlan: 'Rollback plan',
            colRestored: 'Version restored',
            colRestoredAt: 'Restored at',
            colReason: 'Reason',
            kind: { baseline: 'Baseline', change: 'Change', rollback: 'Rollback' },
            planRecorded: 'Recorded',
            planNotRecorded: 'Not recorded',
            stillInUse: 'In use',
            recordChange: 'Record a change',
            setPlan: 'Set rollback plan',
            fModel: 'Model version',
            fScope: 'Scope affected',
            fPrompt: 'Prompt or instruction set reference',
            fReferenceData: 'Reference-data reference',
            fIntegration: 'Integration reference',
            fConfig: 'Configuration reference',
            fTests: 'Test result reference',
            fAutonomy: 'Autonomy setting',
            fListedDevelopers: 'Listed developers of this version',
            fRevalidation: 'Re-validation decision',
            fRevalidationReason: 'Reason for the decision',
            revalidationRequiredHint: 'Required when the system is in production.',
            revalidationOptionalHint: 'Needed only for a change in production.',
            changeDecisionHint: 'A change needs a re-validation decision and a reason.',
            changeTitle: 'Record a change',
            changeSubmit: 'Save change',
            fChangeSummary: 'What changed',
            fChangedFields: 'Fields changed',
            changedField: {
                model_version: 'Model version',
                prompt_reference: 'Prompt reference',
                reference_data_reference: 'Reference-data reference',
                integration_reference: 'Integration reference',
                autonomy_level: 'Autonomy setting',
                scope_affected: 'Scope affected',
                configuration_reference: 'Configuration reference',
                test_result_reference: 'Test result reference',
                listed_developer_user_ids: 'Listed developers',
            },
            planTitle: 'Rollback plan',
            planSubmit: 'Save plan',
            fLastGoodVersion: 'Last known-good version',
            fSteps: 'Steps to restore',
            fPlanOwner: 'Owner of the plan',
            fLastRehearsal: 'Date of the last rehearsal',
            rollbackHeading: 'Rollback events',
            rollbackEmpty: 'No rollback is recorded.',
            recordRollback: 'Record a rollback',
            rollbackTitle: 'Record a rollback',
            rollbackSubmit: 'Save rollback',
            fRestoredVersion: 'Version restored',
            fRollbackReason: 'Reason',
        },
        security: {
            fNotMandatory: 'Reason not mandatory',
            notMandatoryHint: 'Required when the state is not mandatory. Only poisoning, evasion and extraction may be recorded so, and never for a high-risk system.',
            findingsOpen: 'Findings',
            findingsTitle: 'Security findings',
            findingsDescription: 'A finding closes only when it has a severity, a finding owner, a due date and a change record, and a re-test by someone other than the person who fixed it.',
            findingsEmpty: 'No finding is recorded for this category.',
            addFinding: 'Open a finding',
            fDescription: 'What the finding is',
            fSeverity: 'Severity',
            fFindingOwner: 'Finding owner',
            fDueDate: 'Due date',
            fChangeRecord: 'Change record',
            noChangeRecord: 'No change record yet',
            recordFix: 'Record the fix',
            closeOnRetest: 'Close on re-test',
            setDetails: 'Set severity, owner, due date and change record',
            fFixedBy: 'Fixed by',
            fRetestDate: 'Date of the re-test',
            fRetester: 'Re-tested by',
            fRetestResult: 'Re-test result',
            findingSubmit: 'Save finding',
            findingNotClosed: 'Not closed',
            findingStatus: {
                open: 'Open',
                fixed: 'Fixed',
                closed: 'Closed',
            },
            severities: {
                low: 'Low',
                medium: 'Medium',
                high: 'High',
                critical: 'Critical',
            },
            periodic: {
                heading: 'Periodic security testing',
                description: "The period and the last test date are the bank's own and are entered here. The platform derives the next test date and reads it. It runs no test.",
                nextDate: 'Next test date: {date}',
                noNextDate: 'No schedule is set for this version.',
                set: 'Set the schedule',
                formTitle: 'Periodic security testing schedule',
                fPeriod: 'Period in months',
                periodHint: 'A whole number of months, from 1 to 120.',
                fLastTest: 'Date of the last test',
                submit: 'Save schedule',
                states: {
                    not_scheduled: 'Not scheduled',
                    scheduled: 'Scheduled',
                    due: 'Due',
                    overdue: 'Overdue',
                },
            },
            heading: 'Security testing by attack category',
            description: 'One record per attack category for the current version. A blank category is shown as not recorded. The platform runs no test.',
            empty: 'No security test is recorded.',
            blank: 'Not recorded',
            colCategory: 'Attack category',
            colState: 'State',
            colOwner: 'Owner',
            colTest: 'Test',
            colFindings: 'Findings',
            findingCount: '{open} open of {total}',
            record: 'Record',
            update: 'Update',
            formTitle: 'Security test',
            submit: 'Save test record',
            fState: 'State',
            fNotApplicable: 'Reason not applicable',
            naHint: 'Required when the state is not applicable.',
            fMethod: 'Method',
            fTester: 'Tester',
            fTestDate: 'Date of the test',
            fResult: 'Result',
            fArtefact: 'Artefact reference',
            fPersonalData: 'Does the artefact hold personal data?',
            fOwner: 'Owner of this category',
            fSeparation: 'Separation of duties on a re-test',
            fSeparationBasis: 'Basis for not separating',
            separationBasisHint: 'Required when separation is not required.',
        },
        validation: {
            heading: 'Independent validation',
            description: 'A validator independent of the development, deployment, operation and supplier signs the validation. The platform records the attestation and does not judge it.',
            add: 'Open a validation',
            empty: 'No validation is signed.',
            colScope: 'Scope',
            colValidator: 'Validator',
            colUnit: 'Validating unit',
            colSigned: 'Signed',
            colTests: 'Tests',
            colFindings: 'Findings',
            closeFinding: 'Close finding',
            colStatus: 'Status',
            signNow: 'Sign off',
            signNowTitle: 'Sign off this validation',
            signNowMessage: 'Signing records the sign-off against the version under test and cannot be undone. The validator’s independence statement is recorded as stated.',
            signNowSubmit: 'Sign off',
            respond: 'Respond',
            respondTitle: 'Owner response',
            fResponse: 'Response',
            respondSubmit: 'Save response',
            responseLabel: 'Owner response',
            addFinding: 'Open a finding',
            signTitle: 'Open a validation',
            signSubmit: 'Open validation',
            fScope: 'Scope of the validation',
            fValidator: 'Validator',
            fUnit: 'Validating unit',
            fIndependence: 'Independence statement',
            independenceHint: 'The validator states how the unit is independent of development, deployment, operation and the supplier.',
            testsLegend: 'Test',
            fTestArea: 'Test area',
            fTestName: 'Test name',
            fTestMethod: 'Method',
            fTestData: 'Data',
            fTestResult: 'Result',
            confirmTitle: 'Confirm the validation',
            confirmMessage: 'Opening records this validation against the version under test. It is not signed until the validator signs it, and signing needs at least one test on the record.',
            findingTitle: 'Finding',
            openSubmit: 'Open finding',
            closeSubmit: 'Close finding',
            fFindingDescription: 'Description',
            fFindingOwner: 'Owner of the finding',
            fDueDate: 'Due date',
            fRetestDate: 'Date of the re-test',
            fRetestResult: 'Result of the re-test',
            retestHint: 'A finding closes only on a re-test dated after the fix.',
        },
        release: {
            heading: 'Release approval',
            description: 'A release is refused until each precondition is recorded. The approver cannot be the owner, and a listed developer cannot approve the release of their own version.',
            add: 'Approve a release',
            empty: 'No release is approved.',
            colStatus: 'Status',
            colGoLive: 'Go-live date',
            colLabel: 'Approval timing',
            colApprover: 'Approved by',
            colApproved: 'Approved on',
            colStale: 'Stale because',
            beforeGoLive: 'Before go-live',
            afterTheFact: 'After the fact',
            staleBecause: 'Changed:',
            formTitle: 'Approve a release',
            formDescription: 'The seven preconditions are checked when you save. A refused release names each one that is missing.',
            checklistLegend: 'Preconditions',
            checklistUnmet: 'Missing',
            checklistNotChecked: 'Checked on save',
            preconditions: {
                risk_tier: 'A risk level from an approved classification run',
                impact_assessment: 'An approved impact assessment, for a high-risk system',
                validation: 'A validation signed for this version',
                validation_findings: 'Every finding on the signed validation is closed on a re-test',
                security_tests: 'A security-test record for every category, with no open poisoning, evasion or extraction category',
                rollback_plan: 'A rollback plan naming an earlier version',
                senior_management_approval: 'For a high-risk go-live, the senior management body and the decision date',
            },
            rehearsalLabel: 'Rollback rehearsal',
            rehearsalNotDone: 'Not rehearsed',
            rehearsalDone: 'Rehearsed on',
            openFindingsLabel: 'Open validation findings',
            gateUnreadable: 'The gate for this version could not be read. Choose the version again to retry.',
            fVersion: 'Version to release',
            fGoLive: 'Go-live date',
            fSeniorBody: 'Senior management body',
            fSeniorDate: 'Date of the senior management decision',
            seniorHint: 'Required for a high-risk go-live.',
            submit: 'Approve release',
            confirmTitle: 'Approve this release',
            confirmMessage: 'This records the release approval for the chosen version. Approving does not deploy the system; the release is made in the bank’s own systems.',
        },
        impact: {
            heading: 'Impact assessment',
            description: 'The five contents of the assessment, the fairness section and the approval. A change of function, data or operating conditions reopens an approved assessment.',
            add: 'Record an impact assessment',
            empty: 'No impact assessment is recorded.',
            colStatus: 'Status',
            colPurpose: 'Purpose',
            colDpia: 'DPIA or reason',
            colFairness: 'Fairness section',
            colApproval: 'Approval',
            approve: 'Approve',
            fairnessAction: 'Fairness section',
            fairnessRecorded: 'Recorded',
            fairnessNotRecorded: 'Not recorded',
            addTitle: 'Record an impact assessment',
            addSubmit: 'Save assessment',
            fPurpose: 'Purpose',
            fScope: 'Scope',
            fMechanism: 'How the system works',
            fOversight: 'How a person oversees and intervenes',
            oversightHint: 'Describe the human oversight and the means of intervention.',
            fFairness: 'Fairness risk',
            fTransparency: 'Transparency risk',
            fExplainability: 'Explainability risk',
            fDiscrimination: 'Risk of discrimination against customers',
            fDpia: 'DPIA record identifier',
            dpiaHint: 'The identifier of the DPIA record. The DPIA text is not copied.',
            fPersonalDataNotApplicable: 'Reason the personal-data assessment does not apply',
            personalDataHint: 'Give a DPIA identifier, or a reason it does not apply.',
            fControls: 'Controls',
            fMonitoring: 'Monitoring plan',
            fIncident: 'Incident response',
            fairnessTitle: 'Fairness section',
            fairnessSubmit: 'Save fairness section',
            fFairnessAssessment: 'Fairness assessment',
            fBiasControls: 'Bias controls',
            fVulnerable: 'Vulnerable groups',
        },
        inventory: {
            heading: 'Annual inventory review',
            description: 'The annual review of this system for the year shown. A profile not confirmed by its due date reads overdue.',
            confirm: 'Confirm review',
            empty: 'This system has no review for this year.',
            colPeriod: 'Period',
            colAnchor: 'Anchor',
            colDue: 'Due',
            colReading: 'Reading',
            anchorKind: {
                first_recorded: 'First recorded',
                last_confirmation: 'Last confirmation',
                review_duty_effective: 'Review duty took effect',
            },
            formTitle: 'Confirm the annual review',
            submit: 'Confirm review',
            fOutcome: 'Outcome',
            outcome: { confirmed: 'Confirmed without change', changed: 'Changed' },
            fChangeSummary: 'What changed',
        },
        components: {
            heading: 'AI component register',
            description: 'Libraries, models, datasets and dependencies, each row with its integrity check. A row missing a field reads incomplete.',
            empty: 'No component register is recorded.',
            noRows: 'The register lists no component yet.',
            record: 'Record the register',
            integrity: 'Record an integrity check',
            advisory: 'Record an advisory',
            supplyReview: 'Record a supply-chain review',
            incompleteCount: '{count} rows are incomplete.',
            supplyReviewReading: 'Supply-chain review',
            colKind: 'Kind',
            colName: 'Name',
            colSupplier: 'Supplier or developer',
            colVersion: 'Version',
            colIntegrity: 'Integrity check',
            colReading: 'Reading',
            missingFields: 'Missing',
            integrityHeading: 'Integrity checks',
            integrityEmpty: 'No integrity check is recorded.',
            advisoryHeading: 'Advisories',
            advisoryEmpty: 'No advisory is recorded.',
            kind: { library: 'Library', model: 'Model', dataset: 'Dataset', dependency: 'Dependency' },
            formTitle: 'Record the component register',
            formDescription: 'Saving appends a new snapshot. The earlier register stays readable.',
            submit: 'Save',
            rowLegend: 'Component {n}',
            fKind: 'Kind',
            fName: 'Name',
            fSupplier: 'Supplier or developer',
            fVersion: 'Version',
            fLicence: 'Licence',
            fCheck: 'Integrity check value',
            fOrigin: 'Origin',
            fAffected: 'Attack categories this component affects',
            addRow: 'Add a component',
            removeRow: 'Remove',
            integrityFormTitle: 'Record an integrity check',
            fComponent: 'Component',
            fMethod: 'Method of check',
            fCheckValue: 'Check value',
            fCheckedOn: 'Date checked',
            advisoryFormTitle: 'Record an advisory',
            fAdvisoryRef: 'Advisory reference',
            fReceivedOn: 'Date received',
            fRiskDecision: 'Risk decision',
            reviewFormTitle: 'Record a supply-chain review',
            fReviewSummary: 'Summary of the review',
        },
        provenance: {
            heading: 'Data provenance and operational logs',
            description: 'The sources of training, test and operating data, the quality and leakage references, and the log retention with its citation.',
            record: 'Record provenance',
            empty: 'No provenance is recorded.',
            sources: 'Sources',
            thirdParty: 'Third party',
            synthetic: 'Synthetic data',
            qualityPolicy: 'Data-quality policy',
            leakageControl: 'Leakage control',
            retention: 'Log retention',
            citation: 'Citation',
            citationMissing: 'No citation recorded',
            revision: 'Revision',
            dataUse: { training: 'Training', test: 'Test', operating: 'Operating' },
            formTitle: 'Record data provenance',
            submit: 'Save provenance',
            fDataUse: 'Data use',
            fSource: 'Source of the data',
            fLawfulBasis: 'Lawful-basis statement',
            fThirdParty: 'Supplied by a third party',
            fSupplier: 'Supplier name',
            fWarranty: 'Warranty of source',
            fSharing: 'Right to share',
            fSynthetic: 'Uses synthetic data',
            fSyntheticAssessment: 'Synthetic-data assessment',
            fInputLog: 'Input-processing log reference',
            fQualityPolicy: 'Data-quality policy reference',
            fLeakage: 'Leakage control reference',
            fRetention: 'Log retention period',
            retentionHint: 'The period the bank sets for operational logs.',
            fCitation: 'Citation of the rule that sets the period',
            citationHint: 'Required with a retention period.',
        },
        decisions: {
            heading: 'Decision records',
            description: 'For each automated decision that affects an obligation, a financial position or access to a product: the basis and key factors, and the explanation given.',
            add: 'Record a decision',
            empty: 'No decision is recorded.',
            colDate: 'Decision date',
            colRef: 'Reference',
            colEffect: 'Effect',
            colBasis: 'Basis',
            colExplanation: 'Explanation given',
            colVersion: 'Version on the decision date',
            versionLegacy: 'Version current when recorded',
            versionNone: 'No version was in force on the decision date',
            basisRuleHeading: "The platform's reading",
            basisRule: "ComplianceOne's reading of Điều 20 khoản 5: for every automated decision that directly affects a customer's legal liability, financial obligations, or access to or use of a product or service, the basis and the main factors are kept, whatever the decision's purpose. For a decision made to prevent fraud, scams, money laundering or terrorism financing, or to ensure cyber security, your organisation need only tell the customer a reason suited to its risk review and to keeping the customer's assets safe; it may tell the customer more. No explanation may disclose source code, detailed algorithms, parameter sets, or business or technology secrets. This is the platform's reading of the text, not a legal review.",
            effect: {
                legal_or_financial_obligation: 'Legal liability or financial obligation',
                customer_access: 'Access to a product or service',
            },
            category: {
                standard: 'Standard',
                fraud: 'Fraud',
                scam: 'Scams',
                money_laundering: 'Money laundering',
                terrorism_financing: 'Terrorism financing',
                cyber_security: 'Cyber-security',
            },
            formTitle: 'Record a decision',
            submit: 'Save decision',
            fDate: 'Decision date',
            fRef: 'Decision reference',
            fEffect: 'Effect',
            fCategory: 'Decision category',
            categoryHint: 'For fraud, scams, money laundering, terrorism financing or cyber security, the institution need only tell the customer a reason; the basis and key factors are still stored.',
            fBasis: 'Basis for the decision',
            basisHint: 'Leave empty only if the basis was not kept; the record then shows basis not retained.',
            fKeyFactors: 'Key factors',
            factorsHint: 'One factor per line.',
            fExplanation: 'Explanation given to the customer',
            fDelivered: 'Date delivered',
            fChannel: 'Channel',
            fStatedReason: 'Stated reason',
            restrictedHint: 'What the customer is told. For this category the institution need only tell the customer a reason; if it told the customer more, record that explanation too.',
            restrictedBasisHint: 'Stored, not told to the customer. Leave empty only if the basis was not kept; the record then shows basis not retained.',
        },
        disclosures: {
            heading: 'AI disclosures in use',
            description: 'The text in use on the date shown, per kind, product and channel. A new text is recorded beside the old one; it never replaces it.',
            add: 'Record a disclosure',
            notRecorded: 'No disclosure is recorded for this date.',
            colKind: 'Kind',
            colProduct: 'Product',
            colChannel: 'Channel',
            colVersion: 'Version',
            colGoLive: 'Go-live date',
            colText: 'Text',
            kind: {
                pre_service_information: 'Information before the service',
                ai_interaction_statement: 'Statement that the customer is dealing with AI',
                internal_user_information: 'Information for internal users',
            },
            formTitle: 'Record a disclosure',
            submit: 'Save disclosure',
            fOn: 'Date in use',
            fKind: 'Kind',
            fProduct: 'Product',
            fChannel: 'Channel',
            fGoLive: 'Go-live date',
            fText: 'Text in use',
            textHint: 'Enter or paste the text. The platform does not write it.',
            fPurpose: 'Purpose',
            fScope: 'Scope',
            fLimits: 'Limits',
            fGuidance: 'Interpretation guidance',
            fRoute: 'Route to ask for human intervention',
            fFeedback: 'Feedback channel',
            fSameDayReason: 'Reason for a second version on the same day',
            sameDayHint: 'Required only when another version of this text goes live on the same day.',
            noChannels: 'Record a channel before recording a disclosure.',
            pendingHeading: 'Versions awaiting a second person',
            pendingDescription: 'A version takes effect only when a second person approves it. The person who recorded it cannot approve it.',
            pendingEmpty: 'No version is awaiting approval.',
            inUseHeading: 'In use on the date shown',
            approve: 'Approve version',
        },
        payment: {
            heading: 'Automated payment authority',
            description: 'An authority per customer agreement: the eleven elements, the limits against the ceiling the installed framework declares, and suspension or revocation. The platform does not execute or stop a payment.',
            add: 'Record an authority',
            empty: 'No payment authority is recorded for this system.',
            colState: 'State',
            colAgreement: 'Agreement',
            colAutonomy: 'Autonomy on the system',
            colPerDay: 'Per-day limit',
            colCeiling: 'Ceiling source',
            colMissing: 'Missing elements',
            colRelief: 'Confirmation relief',
            relief: {
                heading: 'Transaction confirmation relief',
                open: 'Confirmation relief',
                openSr: 'for agreement {agreement}',
                close: 'Close the relief',
                intro: 'What the installed framework declares about relief from transaction confirmation for payments the AI system executes within this agreement, and which of its conditions are recorded on this authority.',
                notApplied: 'The platform records what a person states. It does not apply the relief, confirm, count or classify a transaction, or compute any total the referenced instrument defines.',
                statedBy: 'Stated by {framework}, {provision}.',
                refersTo: 'Refers to',
                amendedBy: 'as amended by {list}',
                lifts: 'Confirmation it lifts',
                quantities: 'Totals the value is left out of',
                annexVersion: 'Annex versions',
                conditionsHeading: 'Conditions',
                colCondition: 'Condition',
                colBasis: 'Basis',
                colRecorded: 'On this authority',
                colMissing: 'Still to record',
                recorded: 'Recorded',
                notRecorded: 'Not recorded',
                effectHeading: 'Effect',
                notLiftedHeading: 'What it does not lift',
                reachHeading: 'Reach',
                namedSection: '{section}: {framework}, {provision}',
                reading: {
                    conditions_not_recorded: 'Conditions not recorded',
                    conditions_recorded: 'Conditions recorded',
                    relied_on: 'Relied on',
                    relied_on_conditions_no_longer_recorded: 'Relied on; conditions no longer recorded',
                },
                reliance: 'Relied on by {who} on {when}. Reason: {reason}',
                rely: 'Record that the unit relies on this relief',
                relyDisabled: 'Reliance can be recorded once every condition is recorded on this authority.',
                relyTitle: 'Record reliance on the relief',
                relyDescription: 'Records who relies on the relief for this agreement, when and why. It cannot be recorded while no approved terms are in force, by the person who wrote the terms in force, or by anyone who wrote an approved version that set or changed either of the two statements. The platform does not apply the relief.',
                relySubmit: 'Record reliance',
                fReason: 'Why the unit relies on the relief',
                relied: 'Reliance on the relief is recorded.',
                statements: 'Record the statements',
                statementsTitle: 'Record the statements the relief rests on',
                statementsDescription: 'Saved as a change to the authority. It takes effect when a second person approves it.',
                statementsSubmit: 'Save as a change',
                statementsSaved: 'The change is saved. A second person must approve it.',
                statementsPending: 'The statements can be recorded once a second person approves the change pending on this authority.',
                fAiExecution: 'How the unit tells a transaction the system executed',
                aiExecutionHint: 'The reference of the marker, field or log that shows a transaction was executed by the system and not by a person.',
                fAutomationConditions: 'Statement that the conditions for automation hold',
                automationConditionsHint: 'A person states that each condition the instrument sets for automating this system holds.',
                evidence: {
                    ai_execution_reference: 'Statement of how AI-executed transactions are identified',
                    automation_conditions_statement: 'Statement that the conditions for automation hold',
                    terms_approved: 'Terms approved by a second person',
                    ceiling_checked: 'Per-day limit checked against the declared ceiling',
                    authority_active: 'Authority active (not suspended, revoked or expired)',
                },
                noneDeclared: 'No installed framework declares a relief from transaction confirmation for payment authorities.',
                unreadable: 'A confirmation relief an installed framework declares could not be read.',
                notDeclared: 'Not declared',
                listReferenceOnly: 'Declared by a framework installed for reference only',
                listNotOperational: 'Declared by a framework that is not operational',
                listUnreadable: 'Declared, but could not be read',
                activationUpcoming: 'The framework that declares this relief is not yet in force. The relief applies from the date it comes into force; reliance can be recorded before then.',
            },
            scopeHeading: 'Scope flagged for legal review',
            scopeIntro: 'An organisation the installed framework names in one provision and leaves out of its scope provision. The platform treats it as neither covered nor excluded.',
            scopeNamed: 'Named in {named_in}; not named in {absent_from}.',
            scopeStatus: { under_legal_review: 'Flagged for legal review' },
            scopeSource: 'Stated by {framework}',
            scopeUnreadable: 'A scope qualification an installed framework declares could not be read.',
            scopeLoadFailed: 'The scope notice could not be read.',
            scopeRetrySr: 'read the scope notice again',
            recordEvent: 'Record an event',
            approve: 'Approve this change',
            exportHistory: 'Export authority history',
            customerClass: { individual: 'Individual', organisation: 'Organisation' },
            element: {
                customer_class: 'Customer class',
                agreement_reference: 'Agreement reference',
                automated_transaction_scope: 'Scope of automated transactions',
                per_transaction_limit_vnd: 'Per-transaction limit',
                per_day_limit_vnd: 'Per-day limit',
                valid_from: 'Valid from',
                valid_until: 'Valid until',
                suspension_conditions: 'Suspension conditions',
                customer_termination_clause_reference: 'Customer termination clause',
                identity_verification_statement: 'Identity-verification statement',
                pause_or_cancel_tool_reference: 'Customer pause or cancel tool',
            },
            formTitle: 'Record a payment authority',
            formDescription: 'A per-day limit above the ceiling the installed framework declares is refused, and the refusal names that framework and its clause.',
            submit: 'Save authority',
            fCustomerClass: 'Customer class',
            fAgreement: 'Agreement reference (a file or a reference)',
            agreementHint: 'The agreement is recorded by reference.',
            fScope: 'Scope of automated transactions',
            fScopeItem: 'Transaction type',
            scopeHint: 'Add each type, one at a time.',
            addScope: 'Add to the scope',
            removeScope: 'Remove',
            fPerTransaction: 'Per-transaction limit (VND)',
            fPerDay: 'Per-day limit (VND)',
            perDayHint: 'Checked against the ceiling the installed framework declares.',
            fValidFrom: 'Valid from',
            fValidUntil: 'Valid until',
            fSuspension: 'Suspension conditions',
            fTermination: 'Customer termination clause reference',
            fIdentity: 'Identity-verification statement',
            identityHint: 'Entered by the person who checked the verification. The platform does not check biometrics.',
            fPauseTool: 'Customer pause or cancel tool reference',
            fLead: 'Payments lead',
            eventTitle: 'Record an event',
            eventSubmit: 'Save event',
            eventKind: {
                activate: 'Activate',
                suspend: 'Suspend',
                revoke: 'Revoke',
                execution_summary: 'Execution summary',
            },
            fEventKind: 'Event',
            fReportedAt: 'Time reported',
            reportedHint: 'The time the stop was reported.',
            fImplementedAt: 'Time implemented',
            implementedHint: 'Leave empty until the stop is implemented; the state then reads not evidenced.',
            fReason: 'Reason',
            fCustomerRef: 'Customer reference',
            fExecutedOn: 'Date executed',
            fTxCount: 'Number of transactions',
            fLargest: 'Largest transaction (VND)',
            fLargestRef: 'Reference of the largest transaction',
            fDailyTotal: 'Daily total (VND)',
        },
        interventions: {
            heading: 'Human oversight and interventions',
            description: 'Each intervention is recorded with its reason. A pause or stop reads requested until a person enters the confirmed time.',
            add: 'Record an intervention',
            empty: 'No intervention is recorded.',
            colKind: 'Kind',
            colContent: 'Content changed',
            colReason: 'Reason',
            colReading: 'State',
            colRequested: 'Requested at',
            colConfirmed: 'Confirmed at',
            kind: {
                content_change: 'Content change',
                override: 'Override',
                pause: 'Pause',
                stop: 'Stop',
                emergency_stop: 'Emergency stop',
                manual_operation: 'Manual operation',
                resume: 'Resume',
            },
            confirm: 'Enter confirmed time',
            formTitle: 'Record an intervention',
            submit: 'Save intervention',
            fKind: 'Kind',
            fContent: 'Content changed',
            fReason: 'Reason',
            fRequestedAt: 'Time requested',
            requestedHint: 'Required for a pause, stop, emergency stop or manual operation.',
            confirmTitle: 'Enter the confirmed time',
            confirmSubmit: 'Save confirmed time',
            fConfirmedAt: 'Time confirmed in the bank’s system',
            confirmedHint: 'Taken from the bank’s own system; the platform does not read it.',
            alreadyRetired: 'This system is retired.',
            retireHeading: 'Retirement',
            retireSectionDescription: 'Retire this system when it is taken out of service; retiring it needs a decommissioning record.',
            retire: 'Retire this system',
            retireTitle: 'Retire this system',
            retireDescription: 'A retired system needs a decommissioning record: the documents kept for audit, and how the data, models and configuration were disposed of or isolated.',
            retireSubmit: 'Retire system',
            decom: {
                documents_retained_for_audit: 'Documents retained for audit',
                data_disposition: 'Disposition of the data',
                model_disposition: 'Disposition of the models',
                configuration_disposition: 'Disposition of the configuration',
            },
            colChannel: 'Channel',
            colDecision: 'Decision',
            fChannel: 'Channel',
            fDecision: 'Decision identifier',
            decisionHint: 'Required for a content change, an override or a manual operation. Leave blank for a pause, stop, emergency stop or resume.',
            fResumes: 'Intervention resumed',
            resumeHint: 'The pause, stop or emergency stop this resume follows.',
            requestedNote: 'The time of the request is recorded when you save.',
            approve: 'Approve',
            awaitingApproval: 'Awaiting a second person',
            colApproval: 'Second person',
            approved: 'Approved',
            notRequired: 'Not required',
        },
        channels: {
            heading: 'Channels and supervisor lines',
            description: 'Each AI-assisted channel of this system, with the supervisor’s line over it. Interventions and disclosures name a channel.',
            add: 'Record a channel',
            empty: 'No channel is recorded for this system.',
            colName: 'Channel',
            colLine: 'Supervisor’s line',
            colRecorded: 'Recorded on',
            export: 'Export log and disclosures',
            formTitle: 'Record a channel',
            submit: 'Save channel',
            fName: 'Channel name',
            fLine: 'Supervisor’s line',
            lineHint: 'The unit or role that supervises this channel.',
            exportTitle: 'Export the channel log',
            exportSubmit: 'Export',
            exportFrom: 'From',
            exportTo: 'To',
            colDualControl: 'Dual control',
            fDualControl: 'A second person approves each pause, stop and resume',
            dualControlHint: 'When set, a pause, a stop and a resume take effect only after a second person approves it. An emergency stop is never held.',
            registerExport: 'Export the pause and stop register',
            registerTitle: 'Export the pause and stop register',
        },
        channelStatus: {
            heading: 'Channel status',
            description: 'The state of each channel today, read from its record: the disclosures in use, the versions awaiting a second person, and the interventions still open. Read only.',
            empty: 'No channel is recorded for this system.',
            colChannel: 'Channel',
            colState: 'State',
            colDisclosures: 'Disclosures in use',
            colAwaiting: 'Awaiting a second person',
            colOpen: 'Open interventions',
            noneOpen: 'None',
            openAwaiting: 'awaiting a second person',
            openUnanswered: 'escalation unanswered',
            openRequested: 'awaiting the bank’s confirmation',
            state: {
                running: 'Running',
                paused: 'Paused',
                stopped: 'Stopped',
                emergency_stopped: 'Emergency stopped',
            },
        },
        escalations: {
            heading: 'Escalations and responses',
            description: 'Every intervention is escalated to the system owner for cause analysis and to the second line for the board report. Each escalation stays open until its addressee records a response.',
            empty: 'No escalation is raised.',
            colAddressee: 'Addressed to',
            colIntervention: 'Intervention',
            colRaised: 'Raised on',
            colResponse: 'Response',
            none: 'No one is named; the task went to the person who recorded it.',
            respond: 'Record a response',
            respondTitle: 'Record a response',
            respondSubmit: 'Save response',
            fResponse: 'Response',
            responseHint: 'Recorded once, with the addressee’s name and the time.',
            addressee: {
                owner: 'System owner',
                second_line: 'Second line',
            },
            colAnsweredAs: 'Answered as',
            answeredAs: {
                owner: 'System owner',
                attest_fallback: 'Attest holder, in place of the owner',
            },
        },
        briefings: {
            heading: 'Staff briefings on the system’s limits',
            description: 'Who was briefed, when, and on which version of the procedure. Completion of training is held in the training register.',
            add: 'Record a briefing',
            empty: 'No briefing is recorded.',
            colDate: 'Briefed on',
            colProcedure: 'Procedure version',
            colAttendees: 'Briefed',
            formTitle: 'Record a briefing',
            submit: 'Save briefing',
            fDate: 'Day of the briefing',
            fLimits: 'Limits covered',
            fProcedure: 'Procedure version',
            fAttendees: 'Staff briefed (user IDs, separated by commas)',
            fTraining: 'Training record reference (optional)',
            addAttendee: 'Add this person',
            removeAttendee: 'Remove',
        },
        monitoring: {
            heading: 'Performance and behaviour monitoring',
            description: 'Comparisons of predicted and actual outcomes entered or imported, the owner’s thresholds, and events that stay open until a person closes them.',
            addComparison: 'Record a comparison',
            addEvent: 'Record an event',
            setThresholds: 'Set thresholds',
            eventsHeading: 'Events',
            eventsEmpty: 'No event is recorded.',
            comparisonsHeading: 'Comparisons',
            comparisonsEmpty: 'No comparison is recorded.',
            colReading: 'State',
            colKind: 'Kind',
            colDetected: 'Detected on',
            colDescription: 'Description',
            colResponse: 'Response',
            colMetric: 'Metric',
            colMeasured: 'Measured on',
            colPredicted: 'Predicted',
            colActual: 'Actual',
            colDeviation: 'Deviation',
            colCrossed: 'Threshold crossed',
            crossedYes: 'Yes',
            crossedNo: 'No',
            close: 'Close with a response',
            kind: {
                output_deviation: 'Output deviation',
                quality_decline: 'Quality decline',
                bias: 'Bias',
                model_drift: 'Model drift',
                data_drift: 'Data drift',
                threshold_crossing: 'Threshold crossed',
            },
            source: { entered: 'Entered by a person', imported: 'Imported' },
            eventTitle: 'Record an event',
            submitEvent: 'Save event',
            fKind: 'Kind',
            fDescription: 'Description',
            fMetric: 'Metric',
            comparisonTitle: 'Record a comparison',
            submitComparison: 'Save comparison',
            fMeasuredOn: 'Measured on',
            fPredicted: 'Predicted value',
            fActual: 'Actual value',
            fSource: 'Source',
            closeTitle: 'Close the event',
            submitClose: 'Close event',
            fResponse: 'Response',
            thresholdsTitle: 'Thresholds',
            submitThresholds: 'Save thresholds',
            thresholdLegend: 'Threshold {n}',
            fMaxDeviation: 'Largest permitted deviation',
            addThreshold: 'Add a threshold',
        },
        duties: {
            heading: 'Duty holders and competence',
            description: 'The people named against a duty to monitor, detect deviation or intervene. Training is read against a completed assignment; the review falls due yearly.',
            add: 'Name a duty holder',
            capacity: 'Record capacity',
            empty: 'No duty holder is named.',
            colPerson: 'Person',
            colDuty: 'Duty',
            colTraining: 'Training',
            colCompetence: 'Competence review',
            colDue: 'Review due',
            duty: { monitor: 'Monitor', detect_deviation: 'Detect deviation', intervene: 'Intervene' },
            review: 'Record a competence review',
            addTitle: 'Name a duty holder',
            submit: 'Save duty holder',
            fPerson: 'Person',
            fDuty: 'Duty',
            fTraining: 'Training assignment reference (optional)',
            trainingHint: 'A training completion counts only against a completed assignment.',
            reviewTitle: 'Competence review',
            submitReview: 'Save review',
            fReviewedOn: 'Date reviewed',
            fSummary: 'Summary of the review',
            capacityTitle: 'Capacity record',
            submitCapacity: 'Save capacity',
            fBasis: 'Basis',
            basis: {
                unit_builds_or_runs: 'The unit builds or runs the system',
                outsourced: 'Outsourced',
            },
            fCapacity: 'Capacity statement',
            fSupplierName: 'Supplier name',
            supplierHint: 'Required for an outsourced system.',
            fSupplierCapacity: 'Supplier capacity statement',
            fAssessedOn: 'Date the supplier was assessed',
        },
        bankingTriggers: {
            heading: 'Banking triggers',
            intro: 'The trigger facts recorded for this system, compared with the thresholds the installed framework declares. Where the triggers reach the system and a fact exceeds a threshold, the platform raises a classification proposal, shown here and in the classification review. A person classifies; the platform sets no tier.',
            listLabel: 'Within a high-risk entry of the official list (conditions other than value)',
            listStatus: { listed: 'Within an entry', not_listed: 'Not within an entry' },
            listNotRecorded: 'Not recorded',
            listRecorded: 'Recorded by {user} on {date}',
            listHint: 'Recorded on the system form: whether the system meets the conditions of a high-risk entry of the official list other than the transaction or loan value, which the thresholds supply. You record the answer; the platform does not decide it.',
            preconditionLabel: 'Precondition',
            precondition: "The triggers reach only a system within an entry of {list}, on that entry's conditions other than value ({provision}).",
            preconditionNone: 'No installed framework declares a precondition: the triggers reach every system.',
            scopeLabel: 'Reach',
            scope: {
                applies: 'The triggers reach this system',
                not_applicable: 'Not within an entry of the list: no banking trigger applies',
                not_recorded: 'Not recorded: whether the triggers reach this system is not known',
                precondition_unreadable: 'The precondition an installed framework declares could not be read, so the platform cannot show whether the triggers reach this system, and raises no proposal',
            },
            factsNotRecorded: 'No trigger facts are recorded for this system.',
            noThresholds: 'No installed framework declares a banking threshold.',
            unreadable: '{count} declaration(s) an installed framework made could not be read.',
            exceedancesHeading: 'Thresholds exceeded',
            noExceedance: 'No recorded fact exceeds a declared threshold.',
            colThreshold: 'Trigger',
            colValue: 'Recorded value (VND)',
            colAmount: 'Threshold amount (VND)',
            colProvision: 'Provision',
            measure: {
                per_transaction_payment: 'One payment',
                daily_total_per_customer: "One customer's daily total",
                credit_amount: 'Credit amount',
            },
            customerClass: {
                individual: 'individual customer',
                organisation: 'organisation customer',
                any: 'any customer',
            },
            comparison: { greater_than: 'over', greater_or_equal: 'at least' },
            thresholdLabel: '{measure}, {class}: {comparison} the amount',
            proposalHeading: 'Classification proposal raised',
            proposalText: 'Run a classification screening to review them: the classification review shows them beside the tier control. The tier is the one a reviewer records there.',
            noProposal: 'No classification proposal is raised.',
            reviewProposalHeading: 'Banking-trigger proposal for this review',
            reviewProposalIntro: 'This system is recorded as within a high-risk entry of the official list the installed framework names, and a recorded fact exceeds the thresholds below. Weigh them in this review; the tier is the one you record.',
            reviewProposalIntroNoPrecondition: 'A recorded fact on this system exceeds the thresholds below, which the installed framework applies to every system. Weigh them in this review; the tier is the one you record.',
            reviewProposalItem: '{threshold}: {value} VND against {amount} VND ({provision})',
        },
        systemDates: {
            heading: 'Dated obligations for this system',
            intro: 'The commencement dates the installed framework declares, as they fall on this system: whether it was in operation before the framework took effect, and whether your organisation has a system of the tier a carve-out names.',
            inOperationSince: 'In official operation since',
            inOperationNotRecorded: 'Not recorded. Set it on the system form; until then a date that depends on it is not shown.',
            empty: 'No installed framework declares a dated obligation for AI systems.',
            colReference: 'Commencement provision',
            colDate: 'Date for this system',
            colReason: 'Why',
            colSource: 'From',
            noDate: 'No date',
            notStated: 'a date not stated',
            partNotNamed: 'the provisions the transitional stage does not move',
            decidedBy: 'For a system put into official operation before {cutoff}, {reference} sets another date for all or part of this.',
            decidedByRow: 'For a system put into official operation before {cutoff}, {reference} sets another date for all or part of this — see that row.',
            reason: {
                every_system: 'The same date for every system',
                unit_condition_not_held: 'No system of your organisation is recorded as {tier}, the tier the carve-out names, so the stated date applies to every system',
                unit_condition_undetermined: 'Depends on classification: {count} system of your organisation not yet classified. If it is classified {tier}, {cutoffDate} applies; otherwise {date}.',
                unit_condition_undetermined_plural: 'Depends on classification: {count} systems of your organisation not yet classified. If any is classified {tier}, {cutoffDate} applies; otherwise {date}.',
                new_systems_only: 'Applies only to a system put into operation on or after {cutoff}',
                new_system_in_stated_period: 'Put into operation on or after {cutoff}: this date applies if that was within the period the provision states',
                existing_system: 'In operation before {cutoff}',
                existing_system_moved: 'In operation before {cutoff}: the transitional date applies',
                existing_system_partly_moved: 'In operation before {cutoff}: this date applies to the provisions the transitional stage does not move; see that stage for the rest',
                new_system: 'Put into operation on or after {cutoff}',
                unit_holds_tier: 'Your organisation has a system classified {tier}, the tier the carve-out names',
                existing_systems_only: 'Applies only to a system in operation before {cutoff}',
                operation_start_not_recorded: 'Depends on when the system went into operation, which is not recorded',
                every_system_in_part: 'Applies to {part} for every system; for the rest it depends on when the system went into operation, which is not recorded',
                declaration_unreadable: 'The commencement the installed framework declares for this could not be read',
                applies_through_condition_met: 'In operation before {cutoff}, and its recorded autonomy level automates the execution of decisions: the provision applies to it through another from this date',
                applies_through_condition_not_met: 'In operation before {cutoff}, and its recorded autonomy level does not automate the execution of decisions: the date for systems already in operation applies',
                applies_through_condition_not_recorded: 'Depends on the autonomy level, which is not recorded: {dateMet} if the system automates the execution of decisions, {dateNotMet} if not. Nothing reads overdue.',
            },
            // fix-sprint-20261031-1200 F39(e) — a provision brought in through another, with its reading.
            throughNote: '{provision} through {via}: {reading}',
        },
        inventoryRegister: {
            heading: 'Inventory completeness',
            statement: 'This register holds the AI systems recorded in ComplianceOne and the legacy entries imported from your organisation’s existing inventory. It can show only what has been recorded: a system nobody recorded is not in it.',
            recordedSystems: 'Systems recorded',
            legacyEntries: 'Imported legacy entries',
            noImport: 'No legacy import has been made.',
            lastImport: 'Last import',
            received: 'Received',
            imported: 'Imported',
            notImported: 'Not imported',
            refusalHeading: 'Rows not imported',
            colRow: 'Row',
            colName: 'Name',
            colReason: 'Reason',
            refusal: {
                name_missing: 'No name',
                already_registered: 'Already registered',
                duplicate_in_batch: 'Repeated in this file',
            },
            importHeading: 'Legacy import',
            importHint: 'One system per line: name | legacy reference | owner as recorded | status as recorded. A name already in the register, or repeated in the file, is not imported.',
            importField: 'Rows from the existing inventory',
            importSource: 'Source label',
            importSubmit: 'Import rows',
            importDone: 'Import recorded.',
            importFailed: 'The import was not recorded.',
            importEmpty: 'Enter at least one row.',
            legacyHeading: 'Imported legacy entries',
            legacyEmpty: 'No legacy entry is imported.',
            colLegacyRef: 'Legacy reference',
            colOwnerRecorded: 'Owner as recorded',
            colStatusRecorded: 'Status as recorded',
            colImportedOn: 'Imported on',
            colTier: 'Risk tier',
            notClassified: 'Not classified',
            importedMarker: 'Imported',
            obligationsHeading: 'Dated obligations',
            obligationsHint: 'The dates the installed AI instruments state, each with the status and the source it is read from. What each date covers, including any transitional rule or carve-out, is in its own column, worded as the installed framework words it.',
            obligationsEmpty: 'No installed AI instrument states a dated obligation.',
            colReference: 'Article',
            colFramework: 'Instrument',
            colStatus: 'Status',
            colDate: 'Date or period',
            colReasonNotEnforced: 'Why not enforced',
            colCarveOut: 'What the date covers',
            colSource: 'Pack source',
            noPeriod: 'The framework states no date or period.',
            reasonNotApplicable: 'Not applicable',
            annualRecurrence: 'Each year, by {day}/{month}',
            unitUnknown: '{amount} ({unit})',
            unit: {
                hours: 'hours',
                days: 'days',
                working_days: 'working days',
                calendar_days: 'calendar days',
                weeks: 'weeks',
                months: 'months',
                years: 'years',
                annual: 'years',
                working_days_before_event: 'working days before the event',
                calendar_days_before_event: 'calendar days before the event',
                calendar_days_after_event: 'calendar days after the event',
                before_use: 'Before the system is put into use',
            },
            unitOne: {
                hours: 'hour',
                days: 'day',
                working_days: 'working day',
                calendar_days: 'calendar day',
                weeks: 'week',
                months: 'month',
                years: 'year',
                annual: 'year',
                working_days_before_event: 'working day before the event',
                calendar_days_before_event: 'calendar day before the event',
                calendar_days_after_event: 'calendar day after the event',
            },
            periodRecurring: 'Every {period}',
            noCarveOut: 'The installed framework states no separate scope for this date.',
            unitHoldsTier: 'Your organisation has a system classified at the tier the carve-out names, so the carve-out date applies. Without one, the date is {stated}.',
            status: {
                in_force: 'In force',
                upcoming: 'Upcoming',
                not_recorded: 'Not recorded',
                not_enforced: 'Not enforced',
            },
            reason: {
                reference_install: 'Installed for reference only',
                not_operational: 'Not operational for this organisation',
            },
            exportHeading: 'Export the inventory',
            exportIntro: 'The organisation-wide register as a file: every system and imported entry, with the completeness limit in its header. The platform assigns no tier.',
            exportButton: 'Export inventory',
            exportFailed: 'The inventory could not be exported.',
        },
        evidenceIndex: {
            heading: 'Regulator evidence index',
            intro: 'The regulator-facing evidence this system holds, by area, with where each area sits.',
            colArea: 'Area',
            colCount: 'Records',
            colLatest: 'Latest',
            colWhere: 'Where it sits',
            none: 'None recorded',
            area: {
                classification: 'Classification runs',
                validation: 'Validation',
                security_tests: 'Security tests',
                interventions: 'Interventions',
                complaints: 'Complaints',
                supplier_arrangements: 'Supplier arrangements',
            },
            where: {
                classification: 'This page, classification section',
                validation: 'This page, validation section',
                security_tests: 'This page, security section',
                interventions: 'This page, interventions section, and the interventions register',
                complaints: 'Complaints register',
                supplier_arrangements: 'This page, supplier section',
            },
        },
        suppliers: {
            heading: 'Supplier arrangements',
            description: 'The arrangement with a vendor, read on the vendor record. A term with no state reads not recorded. The register counts the terms recorded present, out of the eight Điều 18 khoản 3 names; it does not judge the contract.',
            add: 'Record an arrangement',
            empty: 'No arrangement is recorded with this vendor.',
            vendorHint: 'Choose the vendor to read its arrangements.',
            colPeriod: 'Period',
            colClauses: 'Clauses',
            colCompliant: 'Terms recorded',
            colAssurance: 'Assurance report',
            colTraining: 'Training data',
            compliantYes: 'All eight terms recorded present',
            compliantNo: 'Not all eight recorded',
            expires: 'expires',
            clause: {
                assurance_evidence: 'Assurance evidence',
                version_reporting: 'Version reporting',
                monitoring_access: 'Monitoring access',
                continuity_and_transition: 'Continuity and transition',
                regulatory_cooperation: 'Regulatory cooperation',
                training_data_commitment: 'Training-data commitment',
                it_service_contract_rules: 'IT-service contract rules',
                technical_information_and_limits: 'Technical information, model limits and security measures',
            },
            clauseState: { present: 'Present', absent: 'Absent', pending: 'Pending' },
            clauseHint: 'Choose a state; leave empty for not recorded.',
            training: {
                commitment: 'Commitment',
                consent: 'Written consent',
                not_recorded: 'Not recorded',
            },
            // Fix-sprint-20261031-1200 F32 — the supplier page's boundary statement.
            boundary: "ComplianceOne records what the contract says and what the supplier reported. It does not connect to the supplier's systems, does not verify a model's contents and does not judge the contract: monitoring access is evidenced by your organisation's record.",
            // F4 — the assurance warning period, and where its figure comes from.
            fWarnDays: 'Warning period (days before expiry)',
            warnDaysDefault: '30 days unless your organisation sets another figure. This is a ComplianceOne default: the installed framework sets no warning period.',
            warnDaysReading: 'Warning period: {days} days ({source})',
            warnDaysSource: {
                organisation: 'set by your organisation',
                platform_default: 'ComplianceOne default',
            },
            // F35 — the training-data term read with its recorded position.
            trainingBasis: "Điều 18 khoản 3 điểm h: the third party commits not to use your organisation's data to train, fine-tune or improve its model without your organisation's written consent.",
            trainingTerm: {
                recorded: 'Recorded with its position',
                position_not_recorded: 'Position not recorded: the eight terms do not read as recorded',
                clause_not_present: 'The clause is not recorded present',
            },
            fConsentReference: 'Written consent reference',
            fConsentBasis: "Your organisation's basis for consenting",
            consentBasisHint: 'Where personal data is involved, the DPO reviews this basis.',
            // F7 — the supplier record export per period.
            exportRecord: 'Export the supplier record',
            exportTitle: 'Export the supplier record',
            exportDescription: "The arrangements overlapping the period, their terms, the events dated in it, and each system's component register at the end of the period.",
            fExportPeriod: 'Period',
            exportPeriodHint: 'A year (YYYY) or a month (YYYY-MM).',
            fExportFormat: 'File format',
            exportFormat: { csv: 'CSV', xlsx: 'Excel (XLSX)', pdf: 'PDF' },
            exportSubmit: 'Export',
            exportFailed: 'The supplier record could not be exported.',
            formTitle: 'Record a supplier arrangement',
            submit: 'Save arrangement',
            fVendor: 'Vendor',
            fPeriodStart: 'Period starts',
            fPeriodEnd: 'Period ends',
            fTrainingPosition: 'Training-data position',
            fTrainingScope: 'Scope of the consent',
            colVersions: 'Version reports',
            validationSigned: 'validated',
            validationUnknown: 'validation unknown',
            recordedOn: 'Recorded',
            fReviewCycle: 'Review cycle (months)',
            fReviewCycleHint: 'The bank’s review cycle for this arrangement. Leave empty for no cycle.',
            fAssuranceRef: 'Assurance report reference',
            fAssuranceStart: 'Report period starts',
            fAssuranceEnd: 'Report period ends',
            fAssuranceExpires: 'Report expires on',
            colReadings: 'Readings',
            readingReview: 'Review',
            reviewNotSet: 'no cycle set',
            reviewCurrent: 'on cycle',
            reviewOverdue: 'overdue',
            reviewDueOn: 'due',
            assurance: {
                current: 'current',
                expiring: 'expiring',
                expired: 'expired',
            },
            terminatedOn: 'Terminated on',
            chainFlagged: 'Sub-processor change not yet reviewed',
            continuityTested: 'Continuity last tested',
            monitoringReviewed: 'Monitoring access last reviewed',
            regulatorContact: 'Regulator contact',
            regulatorRequests: 'Regulator requests logged',
            addEvent: 'Record a supplier event',
            eventFormTitle: 'Record a supplier event',
            eventSubmit: 'Save event',
            fEventArrangement: 'Arrangement',
            fEventKind: 'Event',
            eventKind: {
                model_version: 'Model version reported',
                sub_processor_change: 'Sub-processor change',
                termination: 'Termination',
                regulator_request: 'Regulator request',
                regulator_contact: 'Regulator contact named',
                continuity_test: 'Continuity test',
                monitoring_access_review: 'Monitoring access review',
                review: 'Arrangement review',
            },
            fModelVersion: 'Model version',
            fSupplierReportedOn: 'Supplier reported on',
            fSubProcessor: 'Sub-processor',
            fComponentRef: 'Component changed',
            fChangedOn: 'Changed on',
            fSummary: 'Summary',
            fEffectiveOn: 'Effective on',
            fReason: 'Reason',
            fRequestReference: 'Request reference',
            fReceivedOn: 'Received on',
            fContactUserId: 'Named contact (a person of this organisation)',
            fRoute: 'Route a regulator request takes',
            fTestedOn: 'Tested on',
            fReviewedOn: 'Reviewed on',
            fOutcome: 'Outcome',
        },
        panels: {
            heading: 'Duties by obligation kind',
            description: 'Each kind lists its duties with the status and the evidence state. A duty marked complete without evidence needs evidence.',
            kind: {
                human_oversight: 'Oversight',
                transparency_labeling: 'Transparency and labelling',
                monitoring: 'Monitoring',
                incident: 'Incident',
            },
            count: '{count} duties',
            empty: 'No duty of this kind is attached.',
            colDuty: 'Duty',
            colStatus: 'Status',
            colEvidence: 'Evidence',
            evidence: {
                needs_evidence: 'Needs evidence',
                evidenced: 'Evidence recorded',
                not_recorded: 'Not recorded',
            },
            needsEvidenceWhy: 'The status cannot stand as complete without an evidence record.',
            edit: 'Edit',
        },
        registers: {
            backToRegister: 'Back to the register',
            inventoryGuide: 'Use this page once a year: choose the period, then confirm each system or record what changed. The full guide explains each outcome and when a review reads overdue.',
            paymentGuide: "Read the payment authorities an AI system holds, with their limits and what each one still lacks. Authorities are recorded and approved on the system's own page.",
            interventionsGuide: "Read the pauses, stops and other human interventions recorded for an AI system. Interventions are recorded on the system's own page; the full guide explains when one reads confirmed.",
            complaintsGuide: 'Record a complaint about an AI-influenced decision, route it to an independent reviewer, note any item withheld from the customer, then record the re-review and the close. The counts below show when a product reaches the threshold your organisation sets.',
            frameworkGuide: 'Record the approved risk-management framework for AI and the map of the three lines of defence. The full guide explains what makes a line complete.',
            boardGuide: 'These are the AI figures the board report carries, read from the registers now. The table shows the time they were read. To change a figure, change the register it is read from.',
            systemsLoading: 'Loading the AI systems…',
            systemsFailed: 'The list of AI systems could not be read.',
            systemsRetrySr: 'read the list of AI systems again',
            systemsEmpty: 'No AI system is recorded in the register yet.',
            thresholdCurrent: 'Current threshold',
            thresholdComplaints: '{count} complaints',
            thresholdLoading: 'Loading the threshold…',
            thresholdFailed: 'The threshold setting could not be read.',
            thresholdRetrySr: 'read the threshold setting again',
            fSystem: 'AI system',
            inventoryTitle: 'AI inventory reviews',
            inventorySubtitle: 'Annual review of every AI system in the register',
            inventorySummary: 'Each system is confirmed or changed by a named person. A system not confirmed by its due date reads overdue.',
            inventoryHeading: 'Reviews for the period',
            inventoryEmpty: 'No system has a review for this period.',
            fPeriod: 'Period',
            colSystem: 'System',
            colPeriod: 'Period',
            colDue: 'Due',
            colReading: 'Reading',
            confirm: 'Confirm',
            confirmTitle: 'Confirm the annual review',
            submit: 'Save',
            fOutcome: 'Outcome',
            paymentTitle: 'Payment authorities',
            paymentSubtitle: 'Automated payment authorities, by system',
            paymentSummary: 'Choose a system to read its payment authorities. The platform records each authority; it does not execute a payment.',
            paymentHeading: 'Authorities for the system',
            paymentEmpty: 'No payment authority is recorded for this system.',
            openSystem: 'Open the system record',
            interventionsTitle: 'Interventions',
            interventionsSubtitle: 'Human oversight records, by system',
            interventionsSummary: 'Choose a system to read its interventions. A pause or stop reads requested until a person enters the confirmed time.',
            interventionsHeading: 'Interventions for the system',
            interventionsEmpty: 'No intervention is recorded for this system.',
            complaintsTitle: 'AI complaints',
            complaintsSubtitle: 'Complaints about AI-influenced decisions, and their human re-review',
            complaintsSummary: 'A complaint is routed to a reviewer who is neither the system owner nor a listed developer. It closes only with a recorded re-review.',
            complaintsHeading: 'Complaints',
            complaintAdd: 'Record a complaint',
            complaintsEmpty: 'No complaint is recorded.',
            colReceived: 'Received',
            colProduct: 'Product',
            colCategory: 'Decision category',
            colStatus: 'Status',
            colReviewer: 'Reviewer',
            colWithheld: 'Withheld items',
            colReReview: 'Re-review',
            reReviewRecorded: 'Recorded',
            route: 'Route',
            withhold: 'Withhold an item',
            reReview: 'Re-review',
            close: 'Close',
            countsHeading: 'Complaints by system and product',
            countsEmpty: 'No complaint is counted yet.',
            colCount: 'Count',
            colThreshold: 'Threshold',
            thresholdNotSet: 'Not set',
            colCrossed: 'Threshold crossed',
            crossedYes: 'Yes',
            crossedNo: 'No',
            setThreshold: 'Set the threshold',
            createTitle: 'Record a complaint',
            fCategory: 'Decision category',
            fReceivedOn: 'Date received',
            fSummary: 'Summary of the complaint',
            fProduct: 'Product',
            fStatedReason: 'Stated reason',
            fExplanation: 'Explanation given',
            colBasis: 'Decision basis',
            basisReading: {
                recorded: 'Recorded',
                basis_not_retained: 'Basis not retained',
                reason_notified_basis_recorded: 'Reason notified; basis held',
            },
            noDecision: 'No decision linked',
            fDecision: 'Decision complained of',
            fDecisionHint: 'Optional. The decision as recorded in the AI decision register.',
            fExplanationDay: 'Date the explanation was delivered',
            fExplanationChannel: 'Channel the explanation was delivered by',
            fNotifiedMethod: 'How the customer was notified',
            fNotifiedOn: 'Date the customer was notified',
            exportFile: 'Export the complaint file',
            routeTitle: 'Route the complaint',
            fReviewer: 'Reviewer',
            reviewerHint: 'Not the system owner or a listed developer.',
            submitRoute: 'Route complaint',
            withholdTitle: 'Withhold an item',
            fWithheldItem: 'Item withheld',
            fWithheldReason: 'Reason for withholding',
            fWithheldCategory: 'Category',
            withheldCategory: {
                source_code_algorithm_or_parameters: 'Source code, detailed algorithm or parameter set',
                business_or_technology_secret: 'Business or technology secret',
                third_party_information: 'Third-party information',
                security_sensitive: 'Security-sensitive',
                restricted_decision: 'Restricted decision',
                other: 'Other',
            },
            submitWithhold: 'Save item',
            reviewTitle: 'Re-review the complaint',
            fOutcomeSummary: 'Outcome of the re-review',
            reviewHint: 'The re-review is the final basis for the reply. Its outcome is recorded for every category.',
            reviewReasonHint: 'What the customer is told. For this category the institution need only tell the customer a reason; the outcome above is held internally.',
            submitReview: 'Save re-review',
            thresholdTitle: 'Complaint threshold',
            fThresholdCount: 'Complaints that raise a task',
            thresholdHint: 'The organisation sets this figure; the regulation states none.',
            fSecondLine: 'Second-line holder',
            submitThreshold: 'Save threshold',
            closeTitle: 'Close the complaint',
            closeHint: 'The server refuses a close with no re-review recorded.',
            submitClose: 'Close complaint',
            complaintsBoundary: 'ComplianceOne holds the complaint, the decision basis and the re-review. It does not decide a complaint and does not write the explanation to the customer.',
            closeNeedsReReview: 'No re-review is recorded for this complaint, so it cannot be closed yet. Record the re-review first.',
            fComplaintReason: 'Reason for the complaint',
            complaintReasonHint: "ComplianceOne's own categories; the Circular names none.",
            colComplaintReason: 'Reason',
            complaintReason: {
                decision_outcome: 'Disagrees with the outcome',
                explanation: 'Asks for an explanation, or finds it inadequate',
                unfair_treatment: 'Unfair treatment or discrimination',
                data_error: 'Wrong or outdated data',
                ai_interaction: 'Dealing with an AI system',
                service_failure: 'System error or interruption',
                other: 'Other',
                not_recorded: 'Not recorded',
            },
            colAiSystem: 'AI system',
            colCountInPeriod: 'Complaints in the period',
            colByReason: 'By reason',
            thresholdWithWindow: '{count} in {months} month(s)',
            thresholdNoWindow: 'counted over every complaint recorded; no window set',
            periodFrom: 'From month',
            periodTo: 'To month',
            periodHint: 'The default is the six months ending this month.',
            exportTrend: 'Export the trend by month',
            fThresholdWindow: 'Window, in months',
            thresholdWindowHint: 'Optional, from 1 to 12 months ending with the month counted. Left empty, every complaint recorded is counted.',
            patternHeading: 'Responses to a pattern of complaints',
            patternLabel: "Your organisation's response to a pattern of complaints. The Circular sets no threshold and no response for a pattern; ComplianceOne decides neither.",
            patternEmpty: 'No response to a pattern of complaints is recorded.',
            patternRespond: 'Record the response',
            patternTitle: 'Record the response to a pattern of complaints',
            patternSubmit: 'Save response',
            fPatternKind: 'What your organisation did',
            patternKind: {
                no_change_with_reason: 'No change, with the reason',
                revalidation_requested: 'Re-validation requested',
                intervention_recorded: 'Intervention recorded',
                incident_recorded: 'Incident recorded',
                change_recorded: 'Change recorded',
                other: 'Other',
            },
            fPatternLinked: 'The record it refers to',
            patternLinkedEmpty: 'This system has no record of this kind to link.',
            patternRespondSr: 'for {system}, {product}',
            patternRespondSrCrossed: 'for {system}, {product}, crossed on {date}',
            patternCrossedOn: 'Crossed on {date}.',
            patternCrossingNote: 'The response answers the latest complaint that brought this count to the threshold. ComplianceOne finds that complaint and records its day with the response.',
            patternWho: "Only the system's owner, the second-line holder the threshold names, or a person on the second line of the current three-lines map whose role in the register is P-VN-42 can record the response.",
            patternLinkedLoading: 'Reading the records of this system…',
            patternLinkedFailed: 'The records of this system could not be read.',
            patternLinkedRetrySr: 'read the records to link again',
            patternAlreadyAnswered: 'This crossing of the threshold already has its response.',
            periodInvalid: 'A period covers at most 60 months.',
            lineAddedInVersion: 'Attested by someone added to this line in the version they attested.',
            fPatternSummary: 'What was done, and why',
            fRespondedOn: 'Day of the response',
            colCrossedOn: 'Threshold crossed on',
            colResponseKind: 'Response',
            colRespondedBy: 'Responded by',
            colRespondedOn: 'Responded on',
            colExplanations: 'Explanations',
            explanationAdd: 'Record an explanation',
            explanationTitle: 'Record an explanation after the re-review',
            explanationHint: 'Record each explanation delivered to the customer after the re-review, as it was delivered. None is required to close the complaint.',
            fExplanationText: 'Explanation as delivered',
            explanationSubmit: 'Save explanation',
            explanationStage: {
                at_intake: 'At intake',
                after_re_review: 'After the re-review',
            },
            frameworkTitle: 'Framework record and three lines',
            frameworkSubtitle: 'The risk-management framework and the three lines of defence',
            frameworkSummary: 'The framework is approved by a named body and person. The three-lines map shows each line’s unit, people and attestation.',
            frameworkHeading: 'Risk-management framework',
            frameworkAdd: 'Record a framework',
            frameworkEmpty: 'No framework is recorded.',
            frameworkFormTitle: 'Record the framework',
            fFrameworkTitle: 'Framework title',
            fApproverBody: 'Approving body',
            fApprover: 'Approver',
            fApprovedOn: 'Date approved',
            fRiskCode: 'Risk type code',
            fDefinition: 'Definition',
            fThresholds: 'Thresholds',
            fControls: 'Controls',
            colTitle: 'Title',
            colApprover: 'Approver',
            colApproved: 'Approved on',
            colRiskTypes: 'Risk types',
            linesHeading: 'Three lines of defence',
            linesAdd: 'Record the map',
            linesEmpty: 'No three-lines map is recorded.',
            linesFormTitle: 'Record the three-lines map',
            linesFormDescription: 'Each line names its unit and its holders. A line is attested by one of its own holders; a line not attested, or attested by someone not on it, reads incomplete.',
            colVersion: 'Version',
            colIncomplete: 'Incomplete lines',
            colLines: 'Lines',
            attestedYes: 'attested',
            attestedNo: 'not attested',
            line: { first: 'First line', second: 'Second line', third: 'Third line' },
            fUnit: 'Unit',
            fHolder: 'Person',
            fRole: 'Role',
            fPersona: 'Role in the register',
            fAttestation: 'Attestation statement',
            attestationHint: 'Entered by the signatory.',
            fAttested: 'Attested',
            // Fix-sprint-20261031-1200 F32 — the framework and three-lines page's boundary statement.
            frameworkBoundary: "ComplianceOne holds the framework record, the three-lines map and each line's attestation, as the people named entered them. It approves no framework, judges no line's independence and attests nothing on anyone's behalf.",
            // F6 — one line per attestation, by one of that line's own holders.
            attestLine: 'Attest this line',
            attestTitle: 'Attest the {line}',
            attestDescription: 'You are named on this line. Your attestation is recorded in your name and dated today.',
            submitAttest: 'Save attestation',
            lineAttestedOn: 'Attested on {date}',
            lineCarried: 'Attested on {date} (carried from version {n})',
            lineNotAttested: 'Not attested',
            lineNonHolder: 'Attested by someone not named on this line: re-attest.',
            sharedHolder: '{name} is named on the {lineA} and the {lineB}. Điều 14 khoản 2 asks for independent lines.',
            lineLower: { first: 'first line', second: 'second line', third: 'third line' },
            lineJoin: ', the ',
            boardTitle: 'Board report: AI figures',
            boardSubtitle: 'The AI figures in the board report, read from the AI registers',
            boardSummary: 'Overdue inventory reviews count the reviews of the current calendar year. A pause, stop, emergency stop, manual operation or resume counts as an open intervention from when it is recorded until a person enters its confirmed time.',
            boardHeading: 'Current figures',
            boardEmpty: 'The register returned no AI figures.',
            colFigure: 'Figure',
            colValue: 'Value',
            figSystems: 'AI systems',
            figLow: 'Low risk',
            figMedium: 'Medium risk',
            figHigh: 'High risk',
            figUnclassified: 'Unclassified',
            figOverdue: 'Overdue inventory reviews',
            figFindings: 'Open validation findings',
            figInterventions: 'Open interventions',
            figDisclosures: 'Disclosures in use',
            figAsOf: 'As of',
        },
        // fix-sprint-20261031-1200 F1 and F34 — the periodic report contents, retirement and a change's significance.
        boardReport: {
            heading: 'Periodic report contents',
            intro: 'The contents the installed framework names for the report to the senior management body, read from the registers for the window below. ComplianceOne counts what the registers hold; it does not write or present the report.',
            windowFrom: 'Window from',
            windowTo: 'Window to',
            applyWindow: 'Read for this window',
            windowInvalid: 'Enter a window that opens on or before the day it closes.',
            statusComplete: 'Reads the five contents Điều 12 khoản 6 names — operation, risk, serious incidents, complaints and drift trends — from the registers, for {from} to {to}. ComplianceOne does not present the report to the senior management body or the parent bank.',
            statusPartial: 'Partial against Điều 12 khoản 6: {gaps}. The figures below are what the registers hold for {from} to {to}.',
            gapUnclassified: "{n} systems have no recorded tier, so they cannot be placed in or out of the report's scope",
            gapSignificance: '{n} changes to high-risk systems have no significance answer',
            gapOperationDate: '{n} systems have no operation date, so the day the report is first owed is not known for them',
            gapUnreadable: '{content} could not be read',
            windowLonger: 'This window is longer than the six months the text sets as the minimum frequency.',
            nextDue: 'Next report due by {date} (from {anchor})',
            anchorLastExport: 'the last board report export on {date}',
            anchorFirstApplies: 'the earliest day the report applies to a system in scope, {date}',
            nextDueNotDeclared: 'No next due date: no installed framework in force sets the report interval.',
            nextDueNoScope: "No next due date: no system of your organisation is in the report's scope.",
            nextDueUnknown: 'The next due date is not known: no system in scope has an operation date.',
            autonomyNotRecorded: '{n} systems in scope have no recorded autonomy level; the earlier of the two possible days is counted.',
            colContent: 'Content',
            opProduction: 'Systems in scope in production',
            opInterventionsKind: 'Interventions recorded in the window: {kind}',
            opChanges: 'Version changes recorded in the window',
            riskTier: 'Systems in scope: {tier}',
            incSerious: 'Incidents determined serious in the window',
            incUndetermined: 'Incidents opened in the window whose seriousness is not determined',
            incCompletion: 'Serious incidents with a completion report',
            cReceived: 'Complaints received in the window',
            cClosed: 'Complaints closed in the window',
            cOpen: 'Complaints open now',
            cCrossings: 'Complaint threshold crossings in the window',
            driftRow: '{kind}: {now} in the window; {before} from {from} to {to}',
            driftUnreadable: 'Monitoring events by kind, in the window and the window before it',
            nextDueUndated: 'No next due date: the installed framework states no date from which this applies.',
            retrySr: 'read the periodic report contents again',
            gapRecordNotInstalled: 'No installed framework declares the report to the senior management body, so its scope, contents and interval are not known',
            changesHeading: 'Change records',
            changesEmpty: 'No change is recorded.',
            colChangeRecorded: 'Recorded on',
            significanceRecord: 'Record significance',
            significanceRecordSr: 'for the change recorded on {date}',
            significanceBasisShort: 'Basis',
            significanceChoose: 'Choose yes or no.',
            significanceAnsweredBy: 'Answered by {user} on {date}',
            significanceAlreadyRecorded: 'This change already carries a significance answer.',
            significanceBasisRequired: 'Record the basis with the answer.',
            sigHeading: 'Significant changes to high-risk systems',
            sigSince: 'Recorded after the last report export on {date}.',
            sigSinceNone: 'No report export has carried these contents yet, so every change is listed.',
            sigEmpty: 'No change is recorded as significant.',
            sigRow: '{system}: {change} ({date})',
            sigNotRecorded: 'Changes with no significance answer',
            retiredFigure: 'Retired systems',
            retiredOn: 'Retired on {date}: no annual review is due after retirement.',
            retiredNoDay: 'Retired; retirement day not recorded.',
            retiredReading: 'Retired',
            significanceLabel: 'Significant change',
            significanceHint: 'Whether this is a significant change within Điều 2 khoản 9, which refers to the re-assessment cases of the decree that implements the AI law. A person records the answer and its basis; ComplianceOne does not decide it.',
            significanceYes: 'Yes, significant',
            significanceNo: 'No, not significant',
            significanceBasis: 'Basis for the answer',
            significanceBasisHint: 'Required with a yes or no answer.',
        },
        incidentAi: {
            heading: 'AI system in this incident',
            description: 'Link the incident to the AI system it concerns, and record the serious-incident duties of the AI record. The platform submits no report to a State authority.',
            fLink: 'AI system',
            linkHint: 'Choose the AI system whose operation this incident concerns.',
            noProfiles: 'No AI system is recorded for your organisation.',
            saveLink: 'Save link',
            linkSaved: 'Linked to this incident.',
            recordHeading: 'Serious-incident record',
            fSerious: 'Serious incident',
            seriousYes: 'Serious',
            seriousNo: 'Not serious',
            notDetermined: 'Not determined',
            fSeriousBasis: 'Basis for the determination',
            basisHint: 'A serious determination raises one reassessment task for the system owner.',
            reassessment: 'Reassessment task raised',
            reassessmentYes: 'Yes, to the system owner',
            reassessmentNo: 'No',
            deadline: 'Report deadline',
            deadlineStatedOne: 'Not computed. {framework} states no period for this report.',
            deadlineStatedMany: 'Not computed. These frameworks state no period for this report: {frameworks}.',
            deadlineNoneDeclared: 'Not computed. The platform sets no report date for this incident.',
            deadlineSeparateOne: '{framework} states reporting periods for serious AI incidents. The period that applies depends on how this incident is determined, which a person records, so none is applied here.',
            deadlineSeparateMany: 'Reporting periods for serious AI incidents are stated in {frameworks}. The period that applies depends on how this incident is determined, which a person records, so none is applied here.',
            deadlineRecordBelow: 'Record the report you made below.',
            notInForce: 'not yet in force',
            stateReports: 'Reports to the authorities',
            authority: {
                competent_state_authority: 'Competent State authority',
                state_bank_it_department: 'State Bank IT Department',
            },
            fRemediation: 'Remediation',
            fAuthority: 'Authority the report was made to',
            authorityHint: 'Record the reference of a report made by a person outside the platform.',
            fReportedOn: 'Date reported',
            fReference: 'Reference of the report',
            referenceHint: 'The reference the authority gave, or the reference in your own file.',
            completion: 'Completion report',
            fCause: 'Cause',
            fDamage: 'Damage',
            fCompletedOn: 'Date completed',
            emergency: 'Emergency stop or manual operation',
            emergencyNote: 'Recorded as an intervention of the AI system, with its time and person.',
            notification: 'Customer notification',
            notificationNote: 'Read from the notification statuses of this incident, not copied here.',
            status: 'Serious-incident state',
            recordAction: 'Record the serious-incident duties',
            recordTitle: 'Serious-incident record',
            saveRecord: 'Save record',
        },
    },
    // Sprint 63.24.0 Slice A — controlled-testing case pages. English copy until the R-57 deferral (sprint 57.7.0).
    controlled_testing: {
        // Sprint 63.36.0: authored English fallback for this module; locale translation remains separate.
        dossierChecklist: {
            heading: 'Dossier documentary reference',
            reference: 'Current amended source reference',
            regime: 'Whether this source regime governed this case is not determined.',
            unassigned: 'No documentary route is assigned. Source routes below are unselected references only.',
            selected: 'Selected documentary route',
            unselected: 'Unselected source reference — no case coverage is assigned',
            observations: 'Current documentary observations may include facts recorded after the filing or role reference date. These counts do not describe the dossier as it stood at filing or assess legal completeness.',
            recorded: 'Recorded fact items',
            partial: 'Partial items',
            pending: 'Pending items',
            historical: 'Historical items',
            source: 'Source',
            pages: 'PDF pages',
            referenceOn: 'Reference date',
            filing: 'Filing date used for route and role context',
            observedOn: 'Current observations as of',
            as_of: 'Current date used for route and role context',
            context: 'Documentary route context',
            form: 'Application form',
            kind: 'Applicant kind',
            unknown: 'Not recorded or not determinable',
            noProjection: 'No matching documentary projection is available; review required.',
            person: 'Personnel record',
            factDate: 'Actual fact reference date',
            status: {
                available: 'Source reference available',
                undeclared: 'No dossier checklist is declared.',
                unavailable: 'Dossier checklist is unavailable; review required.',
                not_applicable: 'This is not an admission-form documentary route.',
            },
            codes: {
                case_anchor: 'Immutable case form anchor',
                filed_snapshot: 'Immutable filed snapshot',
                draft_record: 'Current draft record',
                reported_registry_information: 'Reported registry information',
                none: 'No supported fact source',
                required: 'Source-listed component',
                conditional_if_any: 'Conditional source component',
                one_of: 'Documentary alternatives',
                historical_repealed: 'Historical separate attachment — repealed',
                documentary_route: 'Documentary route',
                unknown: 'Applicability not determined',
                historical: 'Historical reference',
                recorded_facts: 'Recorded bounded facts',
                recorded_fact: 'Recorded bounded fact',
                partial: 'Partial documentary observation',
                not_recorded: 'Not recorded',
                not_determinable: 'Not determinable',
                recorded: 'Recorded',
                pending: 'Pending',
                conflict: 'Conflicting facts',
                withheld: 'Withheld',
                not_held: 'Not held',
                field_only: 'Descriptive field only',
                filing_metadata_only: 'Filing metadata only; not a completed signed application',
                document_presence_only: 'Held document presence only; contents not assessed',
                classification_reference_only: 'Classification reference only; no approval or compliance assessment',
                statement_date_only: 'Statement date only; no held document inferred',
                reported_information_only: 'Reported information only',
                not_declared: 'No source declaration',
                invalid_declaration: 'Invalid source declaration',
                invalid_declaration_context: 'Invalid source context',
                ambiguous_declaration: 'Ambiguous source declarations',
                applicant_kind_required: 'Explicit applicant context is missing',
                track_not_declared: 'No matching documentary route is declared',
                ambiguous_track: 'Multiple matching documentary routes',
                not_admission_form: 'Not an admission form; no parent route is substituted',
                coverage_limit_exceeded: 'Documentary projection exceeds its technical bound',
                unsupported_binding: 'No supported documentary binding',
                missing_record: 'Required fact is not recorded',
                missing_person: 'Relevant person is not recorded',
                unknown_applicability: 'Conditional applicability is not determined',
                missing_actual_date: 'Actual date is not recorded',
                information_pending: 'Information remains pending; a request is not the information',
                legacy_unconverted: 'Legacy facts have not been converted',
            },
        },
        annexGuidance: {
            heading: 'Source guidance',
            undeclared: 'Guidance is not declared by the installed framework.',
            unavailable: 'Declared guidance is unavailable; review its source declaration.',
            internalSummary: 'Internal source summary. This guidance does not assess a submitted document, signature or filing.',
            sourceDocument: 'Source document',
            sourcePages: 'Source PDF pages',
            legacy: 'Historical record: a provisional report-format absence marker was stored. Current guidance is shown separately.',
            reasons: {
                not_declared: 'No guidance declaration is installed.',
                invalid_declaration: 'The guidance declaration is invalid.',
                invalid_declaration_context: 'The guidance source context is invalid.',
                ambiguous_declaration: 'Multiple guidance declarations require review.',
            },
        },
        parentCopy: {
            operationCapturedAt: 'Creation request captured at',
            acceptedAt: 'Creation accepted at',
            initiatedBy: 'Creation initiated by',
            operationPage: 'Recorded creations page',
            previous: 'Previous page',
            next: 'Next page',
            listFailed: 'Recorded child creations could not be read. Retry the status check; do not submit another creation request.',
            reviewOperations: 'Review recorded child creations',
            knownOperation: 'Recorded operation reference',
            noResume: 'The selected recorded creation has no resume action available.',
            heading: 'Frozen parent solution evidence',
            capturedVersion: 'Captured source solution version',
            capturedAt: 'Source captured at',
            captureHint: 'This snapshot preserves the source version and capture time shown here.',
            copy_absent: 'No parent solution copy recorded.',
            not_recorded: 'The copied solution has no recorded technical-statement file.',
            available: 'The original copied file is available.',
            withheld: 'Parent solution content is withheld. Independent parent read access is required.',
            legacy_lineage_unverified: 'The earlier copy has no verified source lineage. Its file cannot be downloaded through this snapshot.',
            source_unavailable: 'The source solution is unavailable. The copied reference is preserved.',
            lineage_invalid: 'The source lineage needs review. No file is available through this snapshot.',
            not_held: 'The copied file is not currently held.',
            deleted: 'The copied file is deleted. Its original reference remains recorded.',
            released: 'The copied file was released under retention rules. Its original reference remains recorded.',
            release_in_progress: 'Release or destruction is in progress. The copied file is unavailable.',
            loading: 'Checking copied evidence availability…',
            loadFailed: 'Copied evidence availability could not be checked.',
            refresh: 'Reload copied evidence',
            download: 'Download original copied statement',
            downloadFailed: 'The copied file could not be downloaded. Reload its availability and retry.',
            downloadUnavailable: 'Download is unavailable with the current access.',
            opening: 'Creating the child case…',
            creationPending: 'Child creation is awaiting completion. This form keeps the original request; retrying does not create another child.',
            creationUnknown: 'The result of this request is uncertain. Check its status before creating another child. Your original form is preserved.',
            checkCreation: 'Check child creation status',
            resume: 'Complete the recorded child creation',
            operationChoice: 'Recorded child creation',
            chooseOperation: 'Choose the recorded operation to review',
            preparing: 'Creation is being prepared',
            committed: 'Creation accepted; completion pending',
            published: 'Child case is available',
            cancelled: 'Creation was cancelled',
            integrity_conflict: 'Recorded creation needs integrity review',
            resumeFailed: 'The recorded child creation could not be completed.',
            originalRequest: 'Original child request',
            newRequest: 'Return to a new request after review',
            recoveryHint: 'Review the recorded operations explicitly. No operation is selected from its date or name.',
            noOperations: 'No recoverable operation is listed. Review the child list before starting a new request.',
        },
        stopReasonMessages: {
            framework_not_operational: 'The framework is not operational for this case. No transitional determination is made.',
            transitional_rule_ambiguous: 'Multiple transitional rule declarations apply. Review their source declarations.',
            transitional_rule_invalid: 'The declared receipt or confirmation references need source review.',
            transitional_documentary_sources_conflict: 'The actual receipt or confirmation sources disagree or are ambiguous. Compare the separate documentary records.',
            confirmation_mapping_not_declared: 'No source-declared confirmation reference is available. A receipt cannot replace it.',
            legacy_confirmation_claim_not_verified: 'A prior confirmation-date claim exists, but its documentary evidence is not verified. Review that claim separately from receipt.',
            confirmation_scope_conflict: 'The confirmation evidence and its applicable recording scope conflict. Review the preserved sources.',
            historical_recording_scope_review_required: 'The historical processing regime still needs source review. Verified receipt does not resolve that applicability question.',
            authored_documentary_counterpart_not_verified: 'A declared receipt or confirmation counterpart still lacks verified evidence. Review the separate source records.',
            confirmation_regime_not_verified: 'The actual confirmation is recorded, but its processing regime or commencement boundary is not verified.',
            main_and_nested_proceedings: 'Main and nested STOP facts both exist. Review the proceeding identity.',
            stop_reference_not_declared: 'The installed framework does not identify this STOP proceeding.',
            stop_reference_invalid: 'The declared STOP procedure or receipt reference needs review.',
            stored_stop_procedure_changed: 'The installed target differs from the preserved proceeding. Recorded facts have not been moved.',
            stop_lifecycle_not_declared: 'The source-declared proceeding steps are unavailable. Review the declaration.',
            receipt_occurrences_conflict: 'Multiple or conflicting receipt facts need documentary review.',
            receipt_not_verified: 'The actual receipt evidence still needs verification.',
            receipt_not_recorded: 'No actual receipt fact is recorded. Record it from its evidence when known.',
            multiple_decision_occurrences: 'Multiple lifecycle decision facts need documentary review.',
            decision_dates_disagree: 'The actual decision dates differ across documentary sources.',
            decision_references_disagree: 'The decision references differ across documentary sources.',
            decision_evidence_conflict: 'Decision evidence contains a conflict. Preserve the actual sources for review.',
            completion_and_termination_recorded: 'A completion certificate and termination facts are both recorded. Review their documentary conflict.',
            stop_context_unresolved: 'The proceeding context is unresolved. New requests need review; actual facts remain preserved.',
            grant_not_declared: 'No grant declaration is available for a new voluntary request.',
            grant_not_recorded: 'The declared grant has not been recorded. No grant date is inferred.',
            completion_certified: 'An actual completion certificate is recorded. A new voluntary stop request is unavailable.',
            termination_recorded_or_unresolved: 'Termination facts are recorded or need review. New initiation is unavailable.',
            stop_already_filed: 'A voluntary request is already recorded.',
            voluntary_ground_not_declared_or_ambiguous: 'The voluntary-request ground is missing or ambiguous in the installed source.',
            completion_already_filed: 'A completion request is already recorded.',
        },
        stopProceeding: {
            documentaryEnding: 'Documentary ending for this clock',
            verified: 'Evidence verified',
            pending_anchor: 'Required scope anchor not recorded',
            requestFiled: 'Applicant request recorded',
            heading: 'STOP proceeding and documentary decisions',
            intro: 'Record actual documentary acts in their declared proceeding. Applicant requests, receipt, authority decisions and post-stop duties remain separate records.',
            context: 'Proceeding context',
            main: 'Main case proceeding',
            nested: 'Separate proceeding within this case',
            not_declared: 'Not declared by the installed framework',
            unresolved: 'Proceeding declaration needs review',
            ambiguous: 'More than one proceeding context needs review',
            request: 'Voluntary applicant request',
            notFiled: 'No applicant request recorded',
            eligibility: 'Eligibility for a new voluntary request',
            allowed: 'New request available',
            refused: 'New request unavailable',
            review: 'Review the recorded facts and source declarations before this action.',
            receipt: 'Actual stop-dossier receipt',
            receiptHint: 'Enter the authority receipt date and its evidence. Filing, complete-valid confirmation and recording dates remain distinct.',
            pending: 'Pending evidence or applicability',
            recorded: 'Recorded',
            pending_evidence: 'Evidence needs verification',
            conflict: 'Documentary facts disagree or are ambiguous',
            none: 'No documentary decision recorded',
            recordReceipt: 'Record actual receipt',
            recordEvent: 'Record proceeding fact',
            recordTransition: 'Record actual proceeding step',
            recordAuthority: 'Record actual authority decision',
            authority: 'Authority termination record',
            legacy: 'Preserved earlier revocation record',
            raw: 'Decision in the proceeding history',
            decisions: 'Documentary decision comparison',
            decisionHint: 'Each source remains independent. A recorded decision does not fill a closure anchor or erase continuing duties.',
            protocol: 'Saving held evidence',
            ready: 'Held evidence can be linked',
            not_ready: 'Held evidence cannot be linked yet',
            protocolHint: 'Keep this draft and retry after evidence binding becomes available. Uploading holds a file; it does not file a request or record a decision.',
            noInference: 'Receipt does not confirm a complete-valid dossier. A pending phase does not replace an actual receipt date.',
            phase: 'Recorded proceeding state',
            history: 'Immutable proceeding history',
            events: 'Actual proceeding facts',
            clocks: 'Source-declared proceeding clocks',
            noFacts: 'No fact recorded',
            noStates: 'No declared next step available',
            event: 'Declared event',
            state: 'Declared next step',
            date: 'Actual documentary date',
            receivedOn: 'Actual receipt date',
            evidence: 'Evidence references',
            evidenceHint: 'One actual reference per line. A local file reference is subject to its held-evidence checks.',
            ground: 'Declared ground',
            groundNotRecorded: 'Ground not recorded',
            source: 'Source citation',
            actor: 'Documentary actor',
            recordedBy: 'Recorded by',
            recordedAt: 'Recorded at',
            fileHeld: 'Held',
            fileUnavailable: 'File unavailable',
            submit: 'Record documentary fact',
            saved: 'Documentary fact recorded',
            failed: 'The documentary fact could not be recorded.',
            refresh: 'Reload current proceeding',
            stale: 'The case changed. Your draft is retained. Reload before reviewing and submitting again.',
            draftChanged: 'The proceeding or selected declaration changed. Your retained draft has not been reinterpreted.',
            reset: 'Discard unavailable selection',
            close: 'Cancel',
            recipients: 'Actual notice recipients',
            recipient: 'Recipient description',
            addRecipient: 'Add recipient',
            removeRecipient: 'Remove recipient',
            channel: 'Actual notification channel',
            recipientsHint: 'Record actual recipients and channel. This does not establish complete customer coverage.',
            notRecorded: 'Not recorded',
            advisory: 'Source notice advisory',
            invalid_declaration: 'Advisory declaration needs review',
            not_determinable: 'Applicable rule is not determined',
            received_not_confirmed: 'Receipt recorded; complete-valid confirmation not recorded',
            review_required: 'Historical source applicability requires review',
            confirmation: 'Actual complete-valid confirmation',
            transitionalReceipt: 'Actual dossier receipt evidence',
            authorityHint: 'Record an actual authority act independently of applicant filing. Conflicting existing documentary sources remain visible.',
            decisionRef: 'Authority decision reference',
            decidedOn: 'Actual decision date',
            decisionText: 'Authority decision text',
            file: 'Held decision evidence',
            filingHint: 'A voluntary filing records the applicant request only; receipt and authority decision are recorded separately.',
            groundRequired: 'Choose the declared voluntary ground',
            noClock: 'No numeric clock output available',
            reason: 'Review reason',
            confirm: 'Confirm voluntary filing',
            confirmationHint: 'Confirm this applicant request with the entered date and held files. This does not record authority receipt or a decision.',
        },
        registryFacts: {
            correction: 'Outcome data correction',
            correctionHint: 'An evidence-backed correction identifies an erroneous previous fact. It is not a waiver or a successful retrieval.',
            correctionIntent: 'This change corrects an erroneous previous fact',
            correctionReason: 'Correction reason',
            outcomeHistory: 'Previous registry outcome facts',
            person: 'Exact linked personnel record',
            choosePerson: 'Choose a personnel record explicitly',
            personHint: 'Names do not identify a record. Choose the exact record ID; no person is inferred. An unassigned legacy row can be linked once.',
            personUnavailable: 'Personnel choices could not be read or no declared eligible role is available.',
            linkedOutcome: 'Outcome held only on the linked personnel record',
            outcomeHint: 'Record or correct this outcome in the personnel section. This registry row does not write a second outcome.',
            legacy: 'Preserved nonauthoritative registry outcome',
            retrievedOn: 'Actual source-retrieval date',
            suppliedOn: 'Actual component supplied date',
            requestHeading: 'Documentary follow-up requests',
            requestHint: 'Record an existing written request. A request is not the information supplied and does not waive missing information. Requests are append-only.',
            requestOn: 'Actual written-request issue date',
            reference: 'Written-document reference',
            request: 'Append documentary request',
            requestMissing: 'No declared source-specific request is available.',
            information: 'Information availability',
            available_as_reported: 'Available as reported at the recorded date; no eligibility conclusion',
            pending: 'Information remains pending',
            undetermined: 'Information availability is undetermined',
            requestRecorded: 'Documentary request recorded; this does not establish information receipt',
            evidenceNoLongerHeld: 'Request attachment is no longer held',
            noRequest: 'No documentary request is recorded',
            copy: 'Applicant-copy file',
            copyHint: 'Upload holds a file only. Bind it explicitly to this row. A held copy does not override the linked personnel outcome.',
            upload: 'Upload a registry documentary file',
            uploaded: 'Uploaded but not bound: {name}',
            bind: 'Bind uploaded applicant copy',
            unbind: 'Unbind applicant copy',
            held: 'Bound file is held for this exact row',
            notHeld: 'Bound file is not held for this row',
            notBound: 'No applicant-copy file is bound',
            useRequestFile: 'Use this upload as the request attachment',
            noRequestFile: 'No request attachment selected',
            decisionHint: 'A separately recorded decision is preserved as a fact; it does not waive request or information requirements.',
            coverage: 'Requirement coverage',
            count: 'Recorded rows: {count}; unassigned rows: {unassigned}',
            people: 'Eligible people: {eligible}; linked people: {linked}',
            scopeUnavailable: 'Requirement scope is not declared; person linkage remains undetermined',
            refresh: 'Read current registry row',
            refreshed: 'Current row read. Your entries are retained; review current scope, person and source declarations before retrying.',
            stale: 'The row changed. Read it again before retrying; your entries are retained.',
            unavailableDraft: 'Your unsaved person choice is no longer available or differs from a now-fixed link. Clear the draft choice to retain the current link.',
            keepLink: 'Use the current recorded link',
            failed: 'The documentary change could not be recorded.',
            history: 'Immutable request history',
            date: 'Actual date',
            actor: 'Recorded by',
            recordedAt: 'Recorded at',
            classificationOn: 'Outcome classification uses the recorded outcome date',
            outcomeUnresolved: 'The linked outcome or personnel scope is unresolved; the stored code is not treated as available information.',
            requestDeclarationChanged: 'The source-specific request declaration changed. Your draft is preserved and cannot be silently recorded as another kind of request.',
            resetRequestDraft: 'Discard this request draft and use the current declaration',
        },
        applicantSolution: {
            kind: 'Recorded applicant kind',
            kindHint: 'Choose the applicant kind explicitly. The shared application form does not determine it. Leaving it unrecorded keeps the track undetermined and does not create a filing requirement.',
            choose: 'Not recorded',
            kindLocked: 'Applicant kind is locked after filing or authority facts. The recorded value is preserved.',
            keepRecorded: 'Use the current recorded kind and discard the unsaved kind change',
            filedKind: 'Applicant kind in the immutable filed snapshot',
            absentAtFiling: 'Not captured in this legacy filed snapshot',
            track: 'Declared dossier track',
            recorded: 'Recorded using the current declaration',
            not_recorded: 'Applicant kind is not recorded',
            recorded_not_declared: 'Stored value is not resolved by the current declaration',
            not_declared: 'No usable declaration is available',
            invalid_declaration: 'The installed declaration requires review',
            resolved: 'Track resolved from the recorded form and applicant kind',
            applicant_kind_required: 'Record applicant kind to determine the track',
            applicant_kind_not_declared: 'The stored applicant kind cannot resolve a current track',
            track_not_declared: 'No matching declared track is available',
            ambiguous: 'Several conflicting declarations match; no choice is made',
            conditional: 'The cited dossier component includes an investment registration certificate if any. This does not establish that this applicant has or must obtain one.',
            notListed: 'An investment registration certificate is not listed in this cited dossier provision. This is not a general exemption from investment-registration obligations.',
            scheme: 'Referenced information-system security scheme',
            declared: 'The exact referenced scheme is installed',
            named_not_installed: 'The named scheme is declared but not installed. Install the referenced framework; no alternative scheme is substituted.',
            schemeNotDeclared: 'No usable scheme reference is declared. No other installed scheme is substituted.',
            reference: 'Declared target',
            source: 'Source',
            legacyLevel: 'This original security-level link is preserved. It has not been converted or assigned missing source provenance.',
            levelHint: 'New selections record the declaring framework, scheme and tier explicitly. Changing the live solution does not refresh existing frozen child copies or certify system compliance.',
            loadFailed: 'Applicant-kind declarations could not be read. The kind remains unrecorded; no choice is inferred.',
        },
        filing: {
            heading: 'Organisation filing record',
            record: 'Record a filing already made',
            recordHint: 'Record the filing the organisation already made. This action does not send documents or record authority receipt.',
            notDeclared: 'The installed procedure declares no filing requirements. A reviewed filing declaration is needed before a filing can be recorded.',
            immutable: 'The filed snapshot is preserved. This page cannot change a filing already recorded.',
            frozenGroup: 'Draft customer group in the frozen parent-solution copy',
            filedGroup: 'Customer group actually filed',
            filedReport: 'Results-report file reference at filing',
            notRecorded: 'Not recorded',
            notRecordedAtFiling: 'Not recorded in the filed snapshot',
            reference: 'Organisation’s filing reference',
            submittedOn: 'Actual filing date',
            childGroupHint: 'Prefilled from the child’s frozen parent-solution copy. Enter what was actually filed, including a missing draft group. This does not change that copy or the parent’s draft solution.',
            parentGroupHint: 'Enter the customer group actually filed. This records the filed snapshot and does not change the draft solution.',
            approvalReady: 'Internal sign-off is recorded as complete for this child.',
            approvalRequired: 'The child’s internal sign-off must be recorded as complete before its filing can be recorded.',
            draftReport: 'Draft results report',
            reportHint: 'Uploading holds a file. Attach it to this draft as a separate step. Neither action records a filing or verifies the results-report format.',
            reportNotBound: 'No results report is attached to this draft.',
            reportBound: 'Draft report attached and held (file reference: {reference}).',
            reportNotHeld: 'The draft report is no longer held or does not belong to this child. Detach or replace it before recording a filing.',
            reportUnknown: 'The draft report’s held status could not be determined. Read the case again before recording a filing.',
            reportChangeFailed: 'The draft report attachment could not be changed.',
            uploadReport: 'Upload a draft results report',
            uploadHeld: 'Upload completed: {name}. It is not yet attached to this draft.',
            bindReport: 'Attach uploaded report to draft',
            unbindReport: 'Detach draft report',
            leaveUnbound: 'Leave this upload unattached',
            saved: 'The filing record was saved.',
            failed: 'The filing record could not be saved.',
            refreshFailed: 'The current case could not be read. Reload the case before retrying.',
            stale: 'The case changed. Its current version has been read and your entries are retained. Review the current report and sign-off before retrying.',
        },
        lifecycle: {
            heading: 'Documentary facts and their actual dates',
            intro: 'Record what the evidence shows happened, including facts received after an authority outcome. The occurrence date is distinct from when this entry is recorded. The platform makes no authority decision.',
            record: 'Record a documentary fact',
            event: 'Declared fact',
            occurredOn: 'Actual occurrence date',
            evidence: 'Evidence references',
            verification: 'Verification against declared prerequisites',
            recordedAt: 'Entry recorded at',
            actor: 'Documentary actor: {actor}',
            actorAuthority: 'Authority',
            actorOrganisation: 'Organisation',
            actorRelevantMinistries: 'Relevant ministries',
            source: 'Source: {source}',
            recordedBy: 'Entry recorded by: {actor}',
            scopeVerified: 'This application’s recorded anchor is within a reviewed recording period.',
            scopePending: 'Recording period pending: record the declared anchor fact with its actual date and evidence.',
            scopeReview: 'This application’s recording period requires review. No historical regime is inferred.',
            scopeConflict: 'Recorded application anchors conflict. Review their dates and evidence.',
            scopeNotDeclared: 'No reviewed recording period is declared by the installed procedure.',
            scopeAnchor: 'Recorded application anchor: {date}',
            notDeclared: 'No documentary fact declared by the installed procedure',
            empty: 'No documentary facts recorded.',
            verified: 'Prerequisites verified from the recorded facts',
            pending: 'Pending prerequisite facts or a countable declared window',
            conflict: 'Dates or prerequisite facts conflict',
            verificationHint: 'Review the actual dates, supporting evidence and prerequisites in the source. A pending or conflicting fact cannot satisfy a transition prerequisite.',
            choose: 'Choose a declared fact',
            saved: 'The documentary fact was recorded.',
            failed: 'The documentary fact could not be recorded.',
            transitionRemedy: 'Only declared next steps are offered. If a prerequisite is missing, record the documentary fact and its actual date, then retry this step.',
            noNextStep: 'No next step is declared from the recorded state. Independent documentary facts remain recordable.',
            clockEnded: 'Clock ended',
            clockEndedOn: 'Ended on the source-specific actual date: {date}',
            clockRecordedStart: 'Recorded start date: {date}',
            clockHistoricalDue: 'Historical due date: {date}',
            clockConflict: 'Clock dates conflict',
            clockConflictHint: 'The recorded end precedes the start. Review the source evidence; no active due date is shown.',
            clockReview: 'Clock requires review',
            clockReviewHint: 'The installed declaration requires an anchor reading before this clock can be dated.',
            notRecorded: 'Not recorded',
        },
        title: 'Controlled-testing cases',
        subtitle: 'The organisation’s own record of each application to the controlled-testing mechanism. The authority records each step; the platform keeps it with its evidence and derives the clocks the installed record declares. The platform decides nothing.',
        moduleGuideSummary: 'Hold each controlled-testing application: the procedure it follows, the steps the authority has recorded and the clocks the installed record declares. The platform does not assess eligibility or decide the outcome.',
        restricted: 'You need permission to read controlled-testing cases to see them.',
        loading: 'Loading',
        reload: 'Reload',
        notYetRecorded: 'Not yet recorded by the authority',
        notDeclared: 'Not declared by the installed record',
        noChannel: 'No filing channel recorded',
        yes: 'Yes',
        no: 'No',
        list: {
            filtersLabel: 'Filter cases',
            chooseFrameworkFirst: 'Choose a framework first',
            chooseProcedureFirst: 'Choose a procedure first',
            noMatch: 'No match',
            heading: 'Cases',
            openCase: 'Open a case',
            filterFramework: 'Framework',
            filterProcedure: 'Procedure',
            filterStep: 'Recorded step',
            anyFramework: 'Any framework',
            anyProcedure: 'Any procedure',
            anyStep: 'Any step',
            clearFilters: 'Clear filters',
            colApplicant: 'Applicant',
            colProcedure: 'Procedure',
            colStep: 'Recorded step',
            colUpdated: 'Last updated',
            total: '{count} case(s) in total',
            empty: 'No case is held yet. Open one on an installed procedure to start.',
            emptyFiltered: 'No case matches these filters.',
            loadError: 'The cases could not be loaded.',
            retrySubject: 'the case list',
            pagination: 'Pages of cases',
            procedureLabelError: 'The procedure could not be read from the installed record.',
            stepLabelError: 'The step name could not be read from the installed record.',
        },
        pick: {
            frameworkHint: 'Only frameworks that declare a controlled-testing procedure are listed.',
            noCaseFrameworks: 'No installed framework declares a procedure that can carry a case.',
            noFormsOption: '{label} (no application form declared)',
            noFormsInFramework: 'No procedure in this framework declares an application form, so no case can be opened on it. A framework that amends it may declare the forms.',
            framework: 'Installed framework',
            procedure: 'Procedure',
            chooseFramework: 'Choose a framework',
            chooseProcedure: 'Choose a procedure',
            noProcedures: 'This framework declares no procedure that can carry a case.',
            noOpenableFrameworks: 'No installed framework can take a case: a framework installed for reference only does not.',
            hiddenUndeclared: '{count} procedure(s) in this framework declare no lifecycle and are not offered.',
            loadFrameworksError: 'The installed frameworks could not be read.',
            loadProceduresError: 'The procedures of this framework could not be read.',
            needFrameworkRead: 'Reading a framework’s procedures needs the framework read permission.',
        },
        open: {
            noForms: 'This procedure declares no application form, so a case cannot be opened on it. Choose another procedure.',
            title: 'Open a case',
            hint: 'Choose the installed procedure this application follows. The case opens with no authority step recorded.',
            form: 'Application form',
            formHint: 'The forms this procedure declares.',
            applicant: 'Applicant name',
            notes: 'Notes',
            submit: 'Open case',
            cancel: 'Cancel',
            saving: 'Saving…',
            blocked: 'Choose a framework, a procedure and a form, and enter the applicant name.',
            chooseForm: 'Choose a form',
            required: 'Required',
            opened: 'The case was opened.',
            failed: 'The case could not be opened.',
        },
        detail: {
            back: 'Back to cases',
            heading: 'Case',
            applicant: 'Applicant',
            procedure: 'Procedure',
            form: 'Application form',
            framework: 'Framework',
            channel: 'Filing channel',
            usb: 'USB copy',
            usbRecorded: 'USB copy recorded',
            usbNotRecorded: 'USB copy not yet recorded',
            receivedOn: 'Received on',
            notRecorded: 'Not recorded',
            state: 'Recorded step',
            stateUndeclared: 'The recorded step is no longer declared by the installed procedure.',
            history: 'Recorded steps',
            historyEmpty: 'No step has been recorded.',
            colStep: 'Step',
            colEnteredOn: 'Date on the authority’s document',
            colEvidence: 'Evidence',
            colRecordedAt: 'Recorded on',
            clocks: 'Clocks',
            clocksNone: 'The installed record declares no clock for this case.',
            clockRunning: 'Running',
            clockNotStarted: 'Not started',
            clockNoNumeric: 'No numeric period',
            clockNotStartedText: 'The step the clock starts from has no recorded entry, so nothing is counted yet.',
            clockNoNumericText: 'The installed record states no numeric period for this clock.',
            clockPeriod: '{period} {unit}',
            clockStartsOn: 'Counted from {date}',
            clockDue: 'Due {date}',
            clockPaused: 'Paused for {days} day(s)',
            clockSource: 'Source: computed from the recorded anchor',
            clockOwner: 'Obligation owner: {owner}',
            clockName: 'Clock',
            clockStatusHeading: 'Status',
            clockDueHeading: 'Due',
            clockSourceHeading: 'Source',
            clockUnitCalendarDays: 'calendar days',
            clockUnitWorkingDays: 'working days',
            clockUnitMonths: 'months',
            suppressedFrameworkNotOperational: 'Clocks are not computed: the framework is installed for reference only.',
            suppressedProcedureNotDeclared: 'Clocks are not computed: the installed procedure declares no lifecycle.',
            suppressedNotInForce: 'Clocks are not computed: the framework is not in force on {date}.',
            suppressedActivationUnknown: 'Clocks are not computed: the date they bind from is not stated by the installed record.',
            transitionalTitle: 'Transitional status',
            transitionalNotApplicable: 'This procedure carries no transitional rule.',
            transitionalNotDeterminable: 'The installed record does not let the platform determine the transitional status.',
            transitionalAnchorNotRecorded: 'The transitional status waits for the anchor date to be recorded.',
            transitionalYes: 'Transitional: the anchor date is before {cutoff}. No numeric clock is shown for it.',
            transitionalNo: 'Not transitional: the anchor date is on or after {cutoff}.',
            completeness: 'Open items',
            completenessOther: 'An open item applies to this case. The installed record states it.',
            completenessUsb: 'The filing channel requires a USB copy of the dossier set. Record it once it is received. An open item does not block a step.',
            channelUndeclared: 'The recorded channel is not declared by the installed framework.',
            asOf: 'Clocks computed on {date}',
            edit: 'Edit notes and channel',
            editNotes: 'Edit notes',
            recordStep: 'Record a step',
            loadError: 'The case could not be loaded.',
            frameworkUnreadable: 'The framework’s procedure and channel names could not be read, so steps and channels are shown by their identifiers.',
        },
        transition: {
            title: 'Record an authority step',
            hint: 'Enter the step as the authority’s own document states it, with the date on that document and the evidence that shows it. The platform records the step; it does not decide one.',
            step: 'Step',
            chooseStep: 'Choose a step',
            enteredOn: 'Date on the authority’s document',
            evidence: 'Evidence, one reference per line',
            evidenceHint: 'Name the document or attachment that shows this step. At least one is required.',
            submit: 'Record step',
            cancel: 'Cancel',
            saving: 'Saving…',
            recorded: 'The step was recorded.',
            failed: 'The step could not be recorded.',
            blocked: 'Choose a step, enter its date and at least one evidence reference.',
            required: 'Required',
        },
        edit: {
            title: 'Notes and channel',
            titleNotes: 'Notes',
            applicant: 'Applicant name',
            notes: 'Notes',
            channel: 'Filing channel',
            noChannel: 'No channel chosen',
            usbRecorded: 'A USB copy came with the filing',
            usbNotRecorded: 'No USB copy recorded',
            receivedOn: 'Date received',
            submit: 'Save changes',
            saving: 'Saving…',
            saved: 'The case was saved.',
            failed: 'The case could not be saved.',
        },
        stale: 'The case changed since it was opened. It has been reloaded and what you entered is still here. Check it, then submit again.',
        refusedUndeclared: 'The installed procedure does not declare that step. Nothing was recorded.',
        solution: {
            heading: 'Solution evidence',
            intro: 'The one solution this application proposes to test: its type, the customer group it reaches, the technical statement it rests on and the security level it is held to. The platform records what the solution states. It does not decide whether the solution is suitable.',
            loading: 'Loading the solution record…',
            loadError: 'The solution record could not be loaded.',
            retrySubject: 'the solution record',
            readOnly: 'You can read this record. Recording it, changing it and attaching the technical statement need the manage permission.',
            add: 'Record the solution',
            edit: 'Change the solution',
            notRecorded: 'Not yet recorded',
            typeLabel: 'Solution type',
            typeChosen: 'Chosen: {label}',
            typeNotUsed: 'Not used: {label}',
            typeNoneDeclared: 'The installed record declares no solution type, so none can be recorded yet.',
            typeSingleDeclared: 'The installed record declares one solution type only: {label}. The solution description states a choice between two.',
            typeUndeclared: 'Not declared by the installed record ({code})',
            customerGroupLabel: 'Prospective customer group',
            statementLabel: 'Technical statement',
            statementNotAttached: 'Not yet attached',
            statementAttached: 'Attached: {name}',
            statementAttachedNoName: 'Attached. Its name needs the permission to download attachments.',
            statementUpload: 'Attach the technical statement',
            statementRejected: 'The technical statement could not be attached.',
            statementNotBound: 'The file is held for this solution but is not yet attached to it. {message}',
            levelLabel: 'Security level',
            levelRecorded: '{label} ({scheme}; basis: {basis})',
            levelUndeclaredTier: 'Not declared by the installed record ({code})',
            levelNotDeclared: 'Not declared by an installed framework',
            criteriaLabel: 'Technical criteria',
            criteriaTextNotInCorpus: 'Text not in corpus. Source: {source}. No criterion is shown here as a verified requirement.',
            criteriaNotDeclared: 'Not declared by an installed framework.',
            dossierLabel: 'Solution record',
            dossierComplete: 'All four items are recorded. The authority decides what follows.',
            dossierIncomplete: 'Not yet complete. Missing: {items}.',
            item_solution_type: 'the solution type',
            item_customer_group: 'the customer group',
            item_technical_statement: 'the technical statement',
            item_security_level: 'the security level',
            editor_title: 'Record the solution',
            editor_titleEdit: 'Change the solution',
            editor_hint: 'Choose the solution type the installed record declares, enter the customer group as the solution description states it, and choose the security level an installed framework declares. Attach the technical statement after the solution is saved.',
            editor_typeChoose: 'Choose a solution type',
            editor_typeNone: 'The installed record declares no solution type, so none can be chosen.',
            editor_customerGroup: 'Prospective customer group',
            editor_levelChoose: 'Choose a security level',
            editor_levelNone: 'No installed framework declares a security level, so none can be chosen.',
            editor_levelOption: '{label} ({scheme})',
            editor_submit: 'Save the solution',
            editor_saving: 'Saving…',
            editor_cancel: 'Cancel',
            editor_saved: 'The solution was saved.',
            editor_failed: 'The solution could not be saved.',
            editor_stale: 'The solution changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
        },
        registry: {
            heading: 'Authoritative-source records',
            intro: 'Documentary source records for this application. Person-scoped rows link an exact personnel record; its criminal outcome remains in the personnel section. Requests, source retrieval and actual component supply are separate dated facts. A request or decision does not replace missing information.',
            loading: 'Loading the registry records…',
            loadError: 'The registry records could not be loaded.',
            retrySubject: 'the registry records',
            readOnly: 'You can read these records. Recording a document, changing it and attaching a supplement file need the manage permission.',
            add: 'Record a document',
            edit: 'Change',
            notRecorded: 'Not yet recorded',
            stateBankRetrieves: 'The State Bank retrieves this record. The platform does not call the registry, and the outcome shown is the one recorded from the State Bank’s answer.',
            requirementLabel: 'Requirement',
            requirementNotDeclared: 'Not declared by an installed framework',
            sourceLabel: 'Source',
            sourceRegistry: 'Retrieved from the registry by the State Bank',
            sourceApplicant: 'A copy the organisation supplies',
            outcomeLabel: 'State Bank outcome',
            outcomeClass_retrieved: 'Counts as retrieved',
            outcomeClass_not_retrieved: 'Does not count as retrieved',
            outcomeClass_incomplete_or_inaccurate: 'Does not count as retrieved: incomplete or inaccurate',
            outcomeSeen: 'Date seen: {date}',
            outcomeNotDeclared: 'Not declared by an installed framework, so no outcome can be recorded.',
            outcomeUndeclared: 'Not declared by the installed record ({code})',
            outcomeNotApplicable: 'Not applicable: the document is a copy the organisation supplies.',
            recorderLabel: 'Recorded by',
            supplementLabel: 'Supplement or decision',
            supplementNotNeeded: 'Not needed: the outcome counts as retrieved.',
            supplementRecorded: 'Supplement dated {date}: {reference}',
            supplementFileOnly: 'Supplement dated {date}, with its file attached.',
            supplementFileAttached: 'File: {name}',
            supplementFileNoName: 'File attached. Its name needs the permission to download attachments.',
            decisionRecorded: 'Decision dated {date}: {text}',
            supplementMissing: 'Needed. Record a dated supplement with its reference or file, or a dated decision that none is required.',
            limbLabel: 'Investment registration limb',
            limbApplies: 'Applies on this track. Source: {source}.',
            item_scope_declaration: 'declared requirement scope',
            item_personnel_link: 'exact personnel link',
            item_retrieved_on: 'actual source-retrieval date',
            item_failure_follow_up_declaration: 'source-specific follow-up declaration',
            item_failure_follow_up_request: 'documentary follow-up request',
            item_supplement: 'actual supplied component',
            item_applicant_copy_file: 'held applicant-copy file',
            item_information_pending: 'information remains pending',
            limbConditional: 'This cited dossier component includes an investment registration certificate if any. It does not establish that this applicant has or must obtain one. Source: {source}.',
            limbNotListed: 'This cited dossier component does not list an investment registration certificate. This is not a general investment-registration exemption. Source: {source}.',
            limbNotOnTrack: 'Does not apply on this track.',
            limbTrackNotDeclared: 'The track or source-component declaration is undetermined. No document obligation or exemption is inferred.',
            dossierLabel: 'Record',
            dossierComplete: 'Complete: the row holds what its source and outcome require. The authority decides what follows.',
            dossierIncomplete: 'Not yet complete. Missing: {items}.',
            item_source: 'the source',
            item_outcome: 'the State Bank outcome and the date it was seen',
            item_supplement_or_decision: 'a dated supplement or a recorded decision',
            editor_title: 'Record a document',
            editor_titleEdit: 'Change the document',
            editor_hint: 'Choose the requirement, the document’s source, and the outcome the State Bank answered with the date it was seen. Where the State Bank did not retrieve a document in full, it needs a dated supplement, or a dated decision that none is required.',
            editor_requirement: 'Requirement',
            editor_requirementChoose: 'Choose a requirement',
            editor_requirementNone: 'Every requirement the installed framework declares is already recorded, or none is declared.',
            editor_source: 'Source',
            editor_sourceChoose: 'Choose a source',
            editor_outcome: 'State Bank outcome',
            editor_outcomeChoose: 'Choose an outcome',
            editor_outcomeNone: 'The installed record declares no outcome for this requirement, so none can be chosen.',
            editor_outcomeOn: 'Date the outcome was seen',
            editor_supplementOn: 'Actual component supplied date',
            editor_supplementReference: 'Supplement reference',
            editor_supplementReferenceHint: 'Up to 500 characters: the letter or file reference.',
            editor_decisionOn: 'Decision date',
            editor_decisionText: 'Decision text',
            editor_decisionTextHint: 'Up to 1000 characters: preserve the actual documentary decision and explanation. It does not waive source-specific requests or missing information.',
            editor_submit: 'Save the document',
            editor_saving: 'Saving…',
            editor_cancel: 'Cancel',
            editor_saved: 'The document was saved.',
            editor_failed: 'The document could not be saved.',
            editor_stale: 'The document changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
            supplementUpload: 'Attach the supplement file',
            supplementRejected: 'The supplement file could not be attached.',
            supplementNotBound: 'The file is held for this document but is not yet attached to it. {message}',
        },
        personnel: {
            generic: {
                heading: 'Declared documentary conditions',
                hint: 'Record each declared answer with its own evidence reference. These are documentary records, not eligibility decisions. Changing a choice requires its evidence to be entered again.',
                evidence: 'Evidence reference for this condition',
                required: 'required when an answer is chosen',
                source: 'Source',
                notDeclared: 'No usable condition declaration is available. No eligibility conclusion is made.',
                not_recorded: 'Not recorded',
                recorded_with_evidence: 'Answer and its evidence reference recorded',
                evidence_missing: 'Recorded answer has no evidence reference',
                option_not_declared: 'The recorded option is no longer declared',
                does_not_apply: 'Does not apply to this record’s declared form and roles',
                not_declared: 'Condition declaration requires review',
                clear: 'Clear this condition answer explicitly',
                legacyHeading: 'Preserved earlier condition content',
                legacyHint: 'Earlier content is preserved separately and has not been converted into current condition answers.',
                legacyBasis: 'Preserved earlier role qualification',
                clearBasis: 'Clear earlier role qualification',
                history: 'Immutable earlier role-qualification history',
                historyNone: 'No earlier role snapshot is recorded.',
                role: 'Role',
                qualification: 'Earlier qualification code',
                dates: 'Recorded role dates',
                evidenceIds: 'Recorded evidence references; current held status is not asserted',
                actor: 'Recorded by',
                time: 'Recorded at',
                versions: 'Record versions',
                reload: 'Read current personnel record',
                stale: 'The record changed. Read the current record, then review your retained entries before retrying.',
                reloaded: 'Current record read. Your entries remain unchanged; review the declared choices and current version before retrying.',
                retiredAnswer: 'Preserved answer not declared by the installed record',
                retainedDraft: 'Retained draft change needs review against the current declaration. It has not been saved.',
                discardDraft: 'Discard this unsaved draft change',
            },
            heading: 'Personnel eligibility evidence',
            intro: 'The people this application names, the dated roles each one holds and the evidence each role rests on. The platform records what the evidence shows. It does not decide whether anyone is eligible.',
            loading: 'Loading the personnel records…',
            loadError: 'The personnel records could not be loaded.',
            retrySubject: 'the personnel records',
            vocabError: 'The roles and outcomes declared by the installed record could not be read.',
            notDeclaredRoles: 'The installed record declares no role for this framework, so no role can be recorded yet.',
            notDeclaredBases: 'The installed record declares no qualification alternative for this framework.',
            notDeclaredOutcomes: 'The installed record declares no criminal-record outcome for this framework, so none can be recorded yet.',
            empty: 'No person is recorded for this case yet.',
            total: '{count} personnel record(s) in total',
            pagination: 'Pages of personnel records',
            readOnly: 'You can read these records. Recording, changing them and attaching evidence need the manage permission.',
            add: 'Add a person',
            personName: 'Name of the person',
            personHint: 'Type the name as it appears on the personal record statement.',
            statementSignedOn: 'Date the personal record statement was signed',
            statementNotRecorded: 'Statement date not recorded',
            edit: 'Edit this record',
            colRole: 'Role',
            colBasis: 'Qualification alternative the evidence shows',
            colDates: 'Dates held',
            colApplicability: 'Applies to the application form',
            colEvidence: 'Evidence',
            noRoles: 'No role is recorded for this person yet.',
            basisNotRecorded: 'Not yet recorded',
            undeclaredRole: 'Role not declared by the installed record ({code})',
            undeclaredBasis: 'Alternative not declared by the installed record ({code})',
            undeclaredOutcome: 'Outcome not declared by the installed record ({code})',
            heldFrom: 'From {from}',
            heldUntil: 'to {to}',
            heldOpen: 'still held',
            applies: 'Applies to this application form',
            doesNotApply: 'Does not apply to this application form',
            applicabilityNotDeclared: 'Not declared by the installed record',
            evidenceNone: 'No evidence is attached to this role.',
            evidenceCount: '{count} file(s) attached. Their names need the permission to download attachments.',
            evidenceUpload: 'Attach evidence to {role}',
            evidenceRejected: 'The evidence could not be attached.',
            evidenceNotBound: 'The file is held for this record but is not yet attached to the role. {message}',
            currency: {
                heading: 'Currency of the statement',
                current: 'Within the declared limit until {date}.',
                older: 'Evidence is older than the declared limit.',
                notDeterminable: 'Not determinable: {reason}.',
                reasonNoStatementDate: 'no statement date is recorded',
                reasonNoLimit: 'the installed record declares no currency limit',
                reasonNoLimitInForce: 'the declared limit is not in force on this date',
                reasonActivationUnknown: 'the date the declared limit takes effect is not stated',
                reasonNotOperational: 'the framework is installed for reference only',
                basisReceipt: 'Judged on the receipt date, {date}.',
                basisAsOf: 'Judged on the date of this view, {date}.',
            },
            outcome: {
                heading: 'Criminal-record check outcome',
                recordOutcome: 'Record the authority’s outcome',
                changeOutcome: 'Change the recorded outcome',
                recordedOn: '{outcome}, reported on {date}',
                title: 'Record the criminal-record outcome the authority gave',
                hint: 'Choose the outcome the State Bank reported and enter the date the authority gave it. The platform keeps that outcome only. The content of the check stays with the authority that holds it, and none of it can be entered here.',
                code: 'Outcome',
                chooseCode: 'Choose an outcome',
                on: 'Date the authority gave the outcome',
                submit: 'Record outcome',
                saving: 'Saving…',
                cancel: 'Cancel',
                saved: 'The outcome was recorded.',
                failed: 'The outcome could not be recorded.',
                blocked: 'Choose an outcome and enter the date the authority gave it.',
            },
            record: {
                addTitle: 'Add a person',
                editTitle: 'Edit this personnel record',
                hint: 'Enter the person’s name and each role with the dates it was held, as the evidence states them. For each role, record the qualification alternative the evidence shows. The platform records that choice; it does not decide whether the person qualifies.',
                roleRow: 'Role {n}',
                role: 'Role',
                chooseRole: 'Choose a role',
                basis: 'Qualification alternative the evidence shows',
                basisNotRecorded: 'Not yet recorded',
                from: 'Held from',
                to: 'Held until',
                toOpen: 'Still held (leave empty)',
                removeRole: 'Remove this role',
                addRole: 'Add a role',
                submitAdd: 'Record person',
                submitEdit: 'Save record',
                saving: 'Saving…',
                cancel: 'Cancel',
                required: 'Required',
                blocked: 'Enter the person’s name and, for each role, the role and the date it was held from.',
                added: 'The person was recorded.',
                saved: 'The record was saved.',
                failed: 'The record could not be saved.',
                stale: 'The record changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
            },
            conditions: {
                heading: 'Conditions of the legal representative and general director',
                recordConditions: 'Record the conditions',
                changeConditions: 'Change the recorded conditions',
                notRecorded: 'Not recorded',
                nationality: 'Nationality',
                conviction: 'Criminal record',
                sanction: 'Administrative sanction in finance, banking or cybersecurity',
                notDeclared: 'The installed record declares no condition for this application form.',
                title: 'Record what the evidence shows about each condition',
                hint: 'For each condition, choose what the evidence shows. Leave a condition empty when it is not recorded. The platform keeps the choice the evidence shows. It does not decide on the condition.',
                chooseOption: 'Not recorded',
                undeclared: 'Not an option the installed record declares ({code})',
                submit: 'Record conditions',
                saving: 'Saving…',
                cancel: 'Cancel',
                saved: 'The conditions were recorded.',
                failed: 'The conditions could not be recorded.',
                clearRetired: 'Clear the retired criminal-record value',
                clearRetiredHint: 'The criminal-record code was retired, and the installed record no longer declares it. Clearing removes it from this record. The two conditions above are not changed.',
                clearedRetired: 'The retired value was cleared.',
            },
        },
        copy: {
            recordedBy: 'Recorded by',
            notice: 'Platform notice',
        },
        outcome: {
            heading: 'Stop, completion and closure',
            intro: 'A stop is the only way to end a case early. The platform records what the organisation files and what the State Bank decides. The State Bank decides every stop, revocation and completion, and issues every decision and certificate. The platform holds the copy the authority gives and says where that copy came from.',
            readOnly: 'You can read these records. Filing or recording needs the manage permission.',
            // Sprint 62.51.0 (CL-3) — a procedure that declares no organisation outcome filing.
            stopNotOffered: 'The installed procedure of this case offers no stop filing by the organisation, so none is recorded here.',
            completionNotOffered: 'The installed procedure of this case offers no completion filing by the organisation, so none is recorded here.',
            recordedOnIntro: 'Its outcome is recorded from the authority’s and the body’s own documents in:',
            recordedOnNone: 'The installed procedure names no register where its outcome is recorded.',
            loadError: 'The stop, completion, closure and requests could not be read.',
            retry: 'Try again',
            notDeclared: 'Not declared by an installed framework.',
            stop: {
                heading: 'Stop',
                none: 'No stop has been filed for this case.',
                filed: 'Stop filed. The State Bank’s decision is awaited. The platform records no decision of its own.',
                decided: 'The State Bank’s decision is recorded, as the authority gave it.',
                stopDate: 'Stop date, as a person entered it: {date}.',
                groundsHeading: 'Grounds',
                viewGrounds: 'View the grounds ({count})',
                groundsNone: 'Not declared by an installed framework. The platform records no ground.',
                filesHeading: 'Files the stop is filed with',
                role_application: 'Application',
                role_results_report: 'Results report',
                role_stop_plan: 'Stop plan',
                held: 'Held: {name}.',
                notHeld: 'Not held under this case.',
                notFiled: 'Not filed.',
                missing: 'Still missing: {roles}.',
                complete: 'Every file the stop needs is held.',
                endedByCertificate: 'The authority’s certificate of completion is recorded, so no stop is filed on this case.',
                decisionEndedByCertificate: 'The authority’s certificate of completion is recorded, so no decision to stop and revoke the test is recorded on this case.',
                filedEndedByCertificate: 'Stop filed. The authority’s certificate of completion is recorded, so no decision on this stop is awaited.',
                decisionHeading: 'The State Bank’s decision',
                decisionNone: 'No decision is recorded. The platform records one only from the authority’s document.',
                decisionRef: 'Decision reference: {ref}',
                decidedOn: 'Date of the decision: {date}',
                decisionText: 'Text of the decision, as the authority gave it:',
                evidence: 'Evidence: {names}',
            },
            completion: {
                heading: 'Completion',
                none: 'No completion request has been filed.',
                filed: 'Completion request filed. The State Bank evaluates it. The platform decides nothing.',
                role_evaluation_report: 'Evaluation report',
                role_results_report: 'Results report',
                endedByRevocation: 'The State Bank’s decision to stop and revoke the test is recorded, so no completion request or copy of a certificate is recorded on this case.',
                certificateNone: 'No copy of the certificate is recorded. The platform issues no certificate.',
                certificateRecorded: 'The authority’s copy is recorded.',
                copySource: 'Source: the authority’s copy.',
                copyDate: 'Date on the authority’s copy: {date}.',
            },
            closure: {
                heading: 'Closure',
                unavailable: 'Closure is recorded after the authority’s decision is recorded.',
                noticesHeading: 'Stop notices',
                noticesNone: 'No stop notice is recorded.',
                notice: 'Notice sent {date}: {name}.',
                liquidationHeading: 'Liquidation record',
                liquidationNone: 'No liquidation record is recorded.',
                liquidation: 'Liquidation record of {date}: {name}.',
                dutiesHeading: 'Post-stop duties',
                dutiesIntro: 'These duties come from the base instrument. The platform records them and does not create them. Each runs from its own anchor date, which a person enters on the closure record. A duty whose anchor date is not entered shows no date.',
                duty_entitlements_settled: 'Customer rights and obligations are settled',
                duty_settlement_report: 'The settlement is reported in writing',
                citation: 'Source: {citation}',
                dutyNotDeclared: 'Not declared by an installed framework. No period is shown and no date is computed.',
                dutyAmbiguous: 'More than one installed record declares this duty. The platform does not choose between them.',
                dutyNotCountable: 'The installed period is not one the platform counts. No date is computed.',
                dutyDueFromAnchor: 'Period: {period} {unit} from {anchor}. Due {due}.',
                dutyAnchorNotRecorded: 'Anchor not recorded ({anchor}). No date is shown.',
                dutyNotInForce: 'The installed clock for this duty is not in force yet, or the record states no start date from which it can be read. No date is shown.',
                dutyContinuingNoPeriod: 'Continuing duty. The text states no period for a solution outside the scope of this duty, so no period and no date are shown.',
                dutySolutionPointNotRecorded: 'Solution point not recorded. The case’s application form does not show which solution point it is, so no date is computed.',
                anchor_stop_decision: 'the State Bank stop decision date',
                anchor_settlement_completion: 'the settlement completion date',
                anchorLabel_stop_decision: 'State Bank stop decision date',
                anchorLabel_settlement_completion: 'Settlement completion date',
                anchorsHeading: 'Anchor dates',
                settlementReferencePending: 'Settlement reference: pending until the State Bank stop decision date is recorded.',
                settlementReferenceRecorded: 'Settlement reference: {date}, the recorded State Bank stop decision date.',
                anchorRecorded: '{label}: {date}. Evidence: {evidence}.',
                anchorNotRecorded: '{label}: not recorded.',
                unit_months: 'months',
                unit_calendar_days: 'calendar days',
                unit_working_days: 'working days',
            },
            directions: {
                heading: 'The authority’s requests and on-site notices',
                intro: 'Each is recorded in the direction register. Its response period is read from the installed record that declares its kind.',
                none: 'None recorded.',
                kindUndeclared: 'Recorded as “{kind}”, a kind no installed framework of this case declares.',
                registerWithheld: 'The reference, authority, receipt and response period are held in the direction register, which needs its own read permission.',
                manageNeeded: 'Recording a request or notice also needs the direction register’s manage permission.',
                ref: 'Reference: {ref}',
                authority: 'Authority: {name}',
                received: 'Received {date}',
                clockRunning: 'Response due {due}, from the installed record.',
                clockNotDeclared: 'Response period: not declared by an installed framework.',
                clockOther: 'Response period not computed: {reason}.',
                clockNotInForceFrom: 'Response period not computed: the framework that declares it applies only from {date}.',
            },
            actions: {
                fileStop: 'File a stop',
                recordDecision: 'Record the authority’s decision',
                fileCompletion: 'File a completion request',
                recordCertificate: 'Record the authority’s copy',
                recordClosure: 'Record closure',
                recordDirection: 'Record a request or notice',
                chooseFile: 'Choose a file',
                uploading: 'Holding the file…',
                uploaded: 'Held under this case.',
                uploadFailed: 'The file could not be held under this case.',
                cancel: 'Cancel',
                saving: 'Saving…',
                continue: 'Continue to confirmation',
            },
            stopForm: {
                title: 'File a stop',
                hint: 'The stop needs the application, the results report and the stop plan, each held under this case, and the stop date as a person knows it.',
                incomplete: 'Hold the application, the results report and the stop plan, and enter the stop date, before continuing.',
                stopDate: 'Stop date',
            },
            stopConfirm: {
                title: 'Confirm the stop',
                text: 'Filing records the organisation’s stop. The State Bank decides whether the test is stopped and whether the certificate is revoked. Nothing on this page decides for it.',
                confirm: 'File the stop',
                done: 'The stop is filed.',
                failed: 'The stop could not be filed.',
            },
            revoke: {
                title: 'Record the State Bank’s decision',
                hint: 'Record only the authority’s own decision, from its document. The reference and the text are kept as the authority gave them.',
                incomplete: 'Enter the decision reference, the date, the text and the decision file. Each is kept as the authority gave it.',
                ref: 'Decision reference',
                decidedOn: 'Date of the decision',
                text: 'Text of the decision',
                evidence: 'Decision file',
                submit: 'Record the decision',
                done: 'The decision is recorded.',
                failed: 'The decision could not be recorded.',
            },
            completionForm: {
                title: 'File a completion request',
                hint: 'The request needs the evaluation report and the results report, each held under this case.',
                incomplete: 'Hold both reports before filing the request.',
                submit: 'File the request',
                done: 'The completion request is filed.',
                failed: 'The completion request could not be filed.',
            },
            certificate: {
                title: 'Record the authority’s copy',
                hint: 'Record only a copy the authority gave. The platform issues no certificate.',
                incomplete: 'Hold the authority’s copy and enter its date.',
                copyDate: 'Date on the authority’s copy',
                file: 'The authority’s copy',
                submit: 'Record the copy',
                done: 'The authority’s copy is recorded.',
                failed: 'The authority’s copy could not be recorded.',
            },
            anchorCorrection: {
                heading: 'Anchor and documentary-event comparison',
                limitation: 'This compares declared events on this case only. Correcting an anchor does not change a lifecycle event or revocation record. Wrong lifecycle facts require a separate correction path.',
                not_declared: 'No same-case event comparison is declared.',
                pending_anchor: 'Documentary event recorded; an explicit anchor is still required.',
                anchor_only: 'Explicit anchor recorded; no counterpart event is recorded.',
                pending_fact: 'Counterpart evidence is pending verification. No affected due date is shown.',
                agreed: 'The explicit anchor and verified documentary event agree.',
                conflict: 'The records conflict. Both dates are preserved; no affected due date is shown.',
                mappingInvalid: 'The installed comparison declaration needs review.',
                multipleEvents: 'Several counterpart occurrences are recorded. None is selected as authoritative.',
                datesDisagree: 'The recorded actual dates differ.',
                evidenceReview: 'Review the counterpart event’s evidence and declared prerequisites.',
                anchorDate: 'Explicit anchor date',
                eventDate: 'Documentary event occurrence date',
                event: 'Declared event',
                source: 'Source',
                notRecorded: 'Not recorded',
                anchorNotDeclared: 'The installed duty declares no anchor. No due date is computed.',
                dutyConflict: 'The anchor conflicts with its documentary event. No due date is computed.',
                dutyPending: 'The anchor’s counterpart evidence is pending verification. No due date is computed.',
                declaredPeriod: 'Declared period: {period} {unit}.',
                settlementConflict: 'Settlement reference: conflicting documentary dates. No reference date is selected.',
                correct: 'Correct anchor',
                title: 'Correct an explicitly recorded anchor',
                hint: 'Enter the actual corrected date, evidence and justification. The previous record is preserved in immutable history. This action does not record or correct an authority decision, lifecycle fact or revocation.',
                date: 'Corrected actual date',
                evidence: 'Evidence reference for the correction',
                reason: 'Reason for correction',
                required: 'required',
                submit: 'Record anchor correction',
                saved: 'The anchor correction was recorded.',
                failed: 'The anchor correction could not be recorded.',
                stale: 'The case changed. Reload its closure record before trying again. Your entries are retained.',
                reload: 'Reload closure record',
                reloaded: 'Current closure read. Review the current anchor and retained entries before retrying.',
                history: 'Immutable anchor correction history',
                historyNone: 'No anchor correction is recorded.',
                previous: 'Previous anchor record',
                corrected: 'Corrected anchor record',
                recordedBy: 'Recorded by',
                recordedAt: 'Recorded at',
                versions: 'Case versions',
            },
            closureForm: {
                title: 'Record closure',
                hint: 'Record a stop notice, the liquidation record, or both, as each was made.',
                incomplete: 'Hold a stop notice with its date, the liquidation record with its date, or both.',
                noticeFile: 'Stop notice file',
                noticeDate: 'Date the notice was sent',
                liquidationFile: 'Liquidation record file',
                liquidationDate: 'Date of the liquidation record',
                anchorsHint: 'Enter each date as the record states it, with the evidence it rests on. No date is computed here.',
                stopDecisionDate: 'State Bank stop decision date',
                stopDecisionEvidence: 'Evidence of the stop decision',
                settlementDate: 'Settlement completion date',
                settlementEvidence: 'Evidence of the settlement',
                noticeIncomplete: 'A stop notice needs its file and the date it was sent. Enter both, or leave the notice empty.',
                liquidationIncomplete: 'The liquidation record needs its file and its date. Enter both, or leave it empty.',
                anchorIncomplete: 'Each anchor date needs its date and its evidence. Enter both, or leave that anchor empty.',
                anchorRecordedNote: '{label}: already recorded. It is not entered again.',
                refusal: {
                    STOP_DECISION_IN_FUTURE: 'The stop decision date cannot be after today.',
                    SETTLEMENT_BEFORE_STOP: 'The settlement completion date cannot be before the stop it follows.',
                    POST_STOP_ANCHOR_ALREADY_RECORDED: 'This date is already recorded. It is not entered again.',
                },
                submit: 'Record',
                done: 'The closure is recorded.',
                failed: 'The closure could not be recorded.',
            },
            directionForm: {
                kindsLoading: 'Reading the kinds the installed record declares…',
                kindsNone: 'No installed framework of this case declares a kind of request or notice, so none can be recorded here.',
                kindsLoadError: 'The kinds of request or notice could not be read.',
                kindChoose: 'Choose a kind',
                title: 'Record a request or notice',
                hint: 'Recorded in the direction register. Its clock is read from the installed record, not set here.',
                incomplete: 'Complete each field, and give at least one receipt reference.',
                kind: 'Kind',
                ref: 'Reference',
                authority: 'Authority',
                scope: 'Scope',
                received: 'Date and time received',
                receivedDay: 'Date received',
                receivedTime: 'Time received',
                receipt: 'Receipt references, one per line',
                submit: 'Record',
                done: 'The request or notice is recorded.',
                failed: 'The request or notice could not be recorded.',
            },
            fieldLabels: {
                application_file_id: 'Application',
                results_report_file_id: 'Results report',
                stop_plan_file_id: 'Stop plan',
                stop_date: 'Stop date',
                decision_ref: 'Decision reference',
                decided_on: 'Date of the decision',
                decision_text: 'Text of the decision',
                evidence_file_ids: 'Decision file',
                evaluation_report_file_id: 'Evaluation report',
                file_id: 'The authority’s copy',
                copy_dated_on: 'Date on the authority’s copy',
                notice: 'Stop notice',
                liquidation: 'Liquidation record',
                kind: 'Kind',
                direction_ref: 'Reference',
                authority_name: 'Authority',
                scope_description: 'Scope',
                received_at: 'Date and time received',
                receipt_evidence_refs: 'Receipt references',
                'anchors.stop_decision.date': 'State Bank stop decision date',
                'anchors.stop_decision.evidence_ref': 'Evidence of the stop decision',
                'anchors.settlement_completion.date': 'Settlement completion date',
                'anchors.settlement_completion.evidence_ref': 'Evidence of the settlement',
                'anchors.stop_decision': 'State Bank stop decision date',
                'anchors.settlement_completion': 'Settlement completion date',
            },
        },
        children: {
            heading: 'Adjustments and extensions',
            intro: 'A child case changes or continues an application this case already holds. The platform records the steps the State Bank reports and computes the periods the installed record declares. The State Bank decides each request.',
            kindAdjustment: 'Adjustment',
            kindExtension: 'Extension',
            openAdjustment: 'Open an adjustment',
            openExtension: 'Open an extension',
            openTitleAdjustment: 'Open an adjustment',
            openTitleExtension: 'Open an extension',
            openHint: 'The platform copies this case’s solution record as it stands now. The copy does not change when the solution is edited later.',
            formLabel: 'Application form',
            formNone: 'The installed record declares no application form for this kind.',
            notesLabel: 'Notes (optional)',
            submit: 'Open the case',
            saving: 'Opening…',
            cancel: 'Cancel',
            opened: 'The child case is open.',
            openFailed: 'The child case could not be opened.',
            empty: 'No adjustment or extension is recorded on this case.',
            loadError: 'The adjustments and extensions could not be read.',
            unavailable: 'No child case can be opened on this case. {reason}',
            refusedReason: {
                grant_not_declared: 'The parent case has no grant recorded by an installed declaration.',
                grant_not_recorded: 'The parent case has no grant recorded yet.',
                parent_procedure_not_declared: 'The parent case’s procedure declares no lifecycle, so no child case can open on it.',
                framework_not_operational: 'The framework is installed for reference, so no child case can open on it.',
                parent_framework_not_installed: 'The framework is not installed for this organisation.',
                kind_not_declared: 'No installed record declares this kind of child case.',
                kind_ambiguous: 'More than one installed record declares this kind of child case.',
                parent_stopped_and_revoked: 'The State Bank’s decision to stop and revoke the parent test is recorded, so no child case can open on it.',
            },
            readOnly: 'You can read these cases. Opening a child case needs the manage permission.',
            clocksHeading: 'Clocks',
            noClocks: 'No clock applies to this request under the installed record.',
            clockRunning: '{name}: due {due}. Counted {period} {unit} from {start}.',
            clockPaused: '{name}: due {due}. Counted {period} {unit} from {start}, with {paused} {unit} paused.',
            clockNotStarted: '{name}: not started. No entry is recorded for its start step.',
            clockNoNumeric: '{name}: the installed record declares no numeric period.',
            clockSource: 'Computed from the recorded step date. It is not a date set by the authority.',
            clockOwnerAuthority: 'Owed by the State Bank.',
            clockOwnerOrganisation: 'Owed by the organisation.',
            unitWorkingDays: 'working days',
            unitCalendarDays: 'calendar days',
            unitMonths: 'months',
            resultsHeading: 'Results report',
            resultsProvisional: 'Format provisional. The results-report format is in Phụ lục V of Decree 94, which is not in the corpus. No format is shown as verified.',
            filingHeading: 'Filing',
            filingNotRecorded: 'Not recorded. A filing is recorded only against an internal sign-off that is recorded as complete.',
            filingRecorded: 'Recorded by the organisation: reference {reference}, filed {date}.',
            signoffNone: 'No internal sign-off has been requested for this case.',
            signoffPending: 'Internal sign-off: pending. A filing cannot be recorded until the sign-off is recorded as complete.',
            signoffComplete: 'Internal sign-off: recorded as complete.',
            signoffNotComplete: 'Internal sign-off: recorded as not complete. A filing cannot be recorded on it.',
            solutionHeading: 'Solution copied from the parent',
            solutionCopied: 'Copied from the parent solution record at version {version}.',
            solutionNone: 'The parent holds no solution record, so nothing was copied.',
            recordStep: 'Record a step',
            retry: 'Try again',
            currentStep: 'Current step: {step}',
            noStep: 'No step is recorded yet.',
            chooseForm: 'Choose a form',
        },
    },
    // Sprint 63.23.0 — the OAuth consent screen at /oauth/consent.
    oauth_consent: {
        error_request_not_found: 'This request has expired or was already answered. Return to the app and start connecting it again.',
        error_request_already_decided: 'This request was already answered. Return to the app; if it is not connected, start connecting it again.',
        error_client_revoked: 'This app has been revoked, so it cannot connect. Ask an administrator of your organisation if you need it.',
        page_title: 'Approve an app',
        title: 'Allow {client} to use ComplianceOne?',
        title_pending: 'Approve an app',
        loading: 'Loading the request…',
        intro: '{client} is asking to use ComplianceOne on your behalf.',
        client_label: 'App',
        origin_label: 'Registered by',
        origin_admin: 'An administrator of your organisation',
        origin_dynamic: 'The app itself — no administrator has reviewed it',
        redirect_label: 'Sends you back to',
        organisation_label: 'Organisation',
        scopes_heading: 'It will be able to',
        scope_read: 'Read your organisation’s records (mcp:read)',
        scope_write: 'Propose changes for approval (mcp:write)',
        scope_write_note: 'A proposed change is not made until an administrator approves it in the approval queue.',
        scope_other: 'Another permission: {scope}',
        tools_heading: 'Tools it can use',
        tools_none: 'No tools.',
        acts_as_you: 'It acts as you, with no more access than your own role gives you. You can revoke it at any time under Connected apps on your profile.',
        approve: 'Allow',
        deny: 'Deny',
        submitting: 'Sending your decision…',
        missing_request: 'This page was opened without a request. Return to the app and start connecting it again.',
        error_load: 'The request could not be read. It may have expired or already been answered; return to the app and start connecting it again, or reload the request.',
        reload: 'Reload the request',
        error_decide: 'Your decision could not be sent.',
        redirecting: 'Returning you to the app…',
        // Gate E12.5 pass 1 (P2-8) — a redirect whose "host" says nothing about
        // who receives the approval.
        redirect_warning_title: 'Check where this approval goes',
        redirect_warning_loopback: 'This app receives the approval at an address on your own computer ({host}). Any program running on this computer could be listening there. Allow it only if you started this connection yourself, on this computer.',
        redirect_warning_private_use_scheme: 'This app receives the approval through a link that an app installed on your device opens ({host}). Any installed app can claim such a link. Allow it only if you started this connection yourself, from the app you expect.',
    },
    login: {
        title: 'AesirX ComplianceOne',
        subtitle: 'Loggt Iech an Äre Kont an',
        email: 'E-Mail',
        password: 'Passwuert',
        sign_in: 'Aloggen',
        signing_in: 'Gëtt ageloggt...',
        mfa_code: 'MFA-Code',
        mfa_placeholder: '6-stellege Code aginn',
        mfa_instruction: 'Gitt den 6-stellege Code vun Ärer Authentifizéierungs-App an.',
        // ── Sprint 63.7.0: organisation-level MFA enforcement + IP allowlist enforcement ──
        password_change_required_title: 'Change your password',
        password_change_required_intro: 'Your administrator requires you to choose a new password before you continue. Nothing else is available until you do.',
        password_change_current: 'Current password',
        password_change_current_hint: 'The password you signed in with.',
        password_change_new: 'New password',
        password_change_new_hint: 'At least 12 characters, with an upper-case letter, a lower-case letter, a digit and a special character. It must differ from your last 12 passwords.',
        password_change_confirm: 'Confirm new password',
        password_change_mismatch: 'The two new passwords do not match.',
        password_change_field_required: 'This field is required.',
        password_change_submit: 'Change password',
        password_change_failed: 'Your password could not be changed.',
        password_change_done: 'Your password has been changed and you have been signed out everywhere. Sign in with your new password.',
        password_change_sign_out: 'Sign out',
        mfa_enrolment_required_title: 'Set up two-factor authentication',
        mfa_enrolment_required_intro: 'Your organisation requires a second factor for your account. Set one up to continue — it takes a minute and you can finish it here.',
        mfa_enrolment_required_sign_out: 'Sign out instead',
        mfa_enrolment_required_continue: 'Continue to the platform',
        mfa_enrolment_required_blocked: 'Until this is set up, the rest of the platform is unavailable to you.',
        // ── Sprint 63.6.3, gate E12.5 pass 2: the TOTP step-up page for a LIVE session held at MFA_REQUIRED ──
        mfa_step_up_title: 'Enter your verification code',
        mfa_step_up_intro: 'Your organisation asks for a code from your authenticator app before this session can continue. Enter it below to carry on where you were.',
        mfa_step_up_instruction: 'Enter the 6-digit code from your authenticator app, or one of your backup codes.',
        mfa_step_up_locked: 'Too many incorrect codes were entered. Wait a few minutes, then try again.',
        mfa_step_up_not_enrolled: 'This account has no authenticator set up, so there is no code to enter. Sign out and sign in again.',
        mfa_step_up_accepted: 'Code accepted. Continuing.',
        mfa_step_up_code_label: 'Verification code',
        mfa_step_up_placeholder: 'Authenticator code or backup code',
        mfa_step_up_refresh_failed: 'Your code was accepted, but the page could not confirm it with the server. Try again to continue.',
        verify: 'Bestätegen',
        verifying: 'Gëtt bestätegt...',
        forgot_password: 'Passwuert vergiess?',
        account_locked: 'De Kont ass gespaart. Probéiert et w.e.g. méi spéit nach eng Kéier.',
        account_deactivated: 'De Kont gouf deaktivéiert. Kontaktéiert Ären Administrateur.',
        invalid_mfa: 'Ongëltege MFA-Code. Probéiert et w.e.g. nach eng Kéier.',
        invalid_credentials: 'Ongëlteg E-Mail oder Passwuert.',
        login_failed: 'Umeldung fehlgeschloen',
        session_limit_reached: 'Äre Kont huet d’Limitt vun aktive Sessiounen erreecht. Mellt Iech op engem aneren Apparat of oder frot Ären Administrateur, eng bestoend Sessioun ze widderruffen, a probéiert nach eng Kéier.',
        session_expired: "Är Sëtzung ass ofgelaf. Luet d'Säit w.e.g. nei an probéiert et nach eng Kéier.",
        sso_divider: 'or',
        sso_sign_in_with: 'Sign in with',
        sso_checking: 'Checking sign-in options…',
        sso_completing: 'Completing sign-in…',
        sso_failed: 'Single sign-on did not complete. Contact your administrator.',
        sso_required: 'This organisation requires single sign-on. Use the link your administrator gave you.',
        sso_options_label: 'Single sign-on options',
        sso_provider_session_not_ended: 'You are signed out of ComplianceOne, but your identity provider session was not ended. If you share this device, sign out at your identity provider as well.',
    },
    auth: {
        forgot_password: {
            title: 'Passwuert vergiess',
            subtitle: 'Gitt Är E-Mail-Adress an a mir schécken Iech e Link fir Äert Passwuert zréckzesetzen.',
            send: 'Zrécksetze-Link schécken',
            sending: 'Gëtt geschéckt...',
            success: 'Wann déi E-Mail-Adress existéiert, gouf e Passwuert-Zrécksetze-Link geschéckt.',
            back_to_login: 'Zréck zum Login',
        },
        reset_password: {
            title: 'Passwuert zrécksetzen',
            subtitle: 'Gitt Äert neit Passwuert hei drënner an.',
            new_password: 'Neit Passwuert',
            confirm_password: 'Passwuert bestätegen',
            reset: 'Passwuert zrécksetzen',
            resetting: 'Gëtt zréckgesat...',
            success: 'Äert Passwuert gouf erfollegräich zréckgesat.',
            go_to_login: 'Op de Login goen',
            password_mismatch: "D'Passwierder stëmmen net iwwereneen.",
            invalid_token: 'Ongëltegen oder fehlende Zrécksetze-Token. Frot w.e.g. en neie Passwuert-Zrécksetze-Link un.',
            requirements_title: 'Passwuert-Ufuerderungen:',
            password_requirements: "D'Passwuert muss déi folgend Ufuerderungen erfëllen:",
            rules: {
                min_length: 'Mindestens 12 Zeechen',
                uppercase: 'Mindestens ee grousse Buschtaf',
                lowercase: 'Mindestens ee klenge Buschtaf',
                digit: 'Mindestens eng Ziffer',
                special: 'Mindestens ee Spezialzeechen',
            },
        },
        verify_email: {
            title: 'E-Mail-Verifizéierung',
            verifying: 'Är E-Mail-Adress gëtt verifizéiert...',
            success: 'Är E-Mail-Adress gouf erfollegräich verifizéiert.',
            invalid_token: 'Ongëltegen oder fehlende Verifizéierungs-Token.',
            failed: 'E-Mail-Verifizéierung fehlgeschloen. Den Token ass eventuell ongëlteg oder ofgelaf.',
            go_to_login: 'Op de Login goen',
        },
        offline: {
            title: 'Dir sidd offline',
            message: 'Et gesäit aus wéi wann Dir Är Internetverbindung verluer hutt. Iwwerpréift w.e.g. Äert Netzwierk a probéiert et nach eng Kéier.',
        },
    },
    intake: {
        title: 'Ufro fir Datenrechter',
        subtitle: 'Schéckt eng Ufro fir Datenrechter un eis Organisatioun',
        fields: {
            full_name: 'Vollstännegen Numm',
            email: 'E-Mail-Adress',
            phone: 'Telefonsnummer (optional)',
            request_type: 'Aart vun der Ufro',
            description: 'Beschreift Är Ufro',
            preferred_language: 'Bevorzugte Sprooch',
            consent: "Ech ginn mäi Averständnis fir d'Veraarbechtung vun dëser Ufro",
        },
        submit: 'Ufro aschécken',
        validation: {
            name_required: 'De vollstännegen Numm ass obligatoresch',
            email_required: 'Eng gülteg E-Mail-Adress ass obligatoresch',
            type_required: 'Wielt w.e.g. eng Aart vun der Ufro',
            description_required: 'Beschreift w.e.g. Är Ufro',
            consent_required: 'Dir musst Äert Averständnis ginn fir weiderzefueren',
        },
        error_title: 'Feeler',
        config_load_error: "Konfiguratioun konnt net gelueden ginn. Iwwerpréift w.e.g. d'URL.",
        organization_label: 'Organisatioun',
        customer_reference: 'Client-Referenznummer',
        country: 'Land / Jurisdiktioun',
        preferred_channel: 'Bevorzugten Äntwertkanal',
        channels: {
            email: 'E-Mail',
            postal: 'Post',
            phone: 'Telefon',
            portal: 'Portal',
        },
        confirmation: {
            // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
            next_steps_legal_matter: 'Your request will be handled as a legal matter: the organisation will review it and contact you about it. Please keep your reference number.',
            title: 'Ufro kritt',
            message: 'Är Ufro gouf erfollegräich opgeholl. Är Referenznummer ass:',
            next_steps: 'Mir wäerten Är Ufro sou séier wéi méiglech am Aklang mat dem gëltende Gesetz beaarbechten. Déi erwaarte Veraarbechtungszäit ass 30 Aarbechtsdeeg.',
        },
        // English until Phase 25 (R-57).
        // fix-sprint #431 (owner ruling 2026-10-03) — the acknowledgement emailed
        // to the requester when Settings › Notifications "Send confirmation email to
        // requester" is on. {name}, {organization} and {reference} are filled in.
        acknowledgement_email: {
            subject: 'We received your request (reference {reference})',
            greeting: 'Hello,',
            body: '{organization} has received your request concerning your personal data.',
            reference: 'Your reference is {reference}. Please quote it in any message about this request.',
            closing: 'We will contact you about the next steps. This message was sent automatically.',
        },
        request_type_not_offered: "This kind of request is not one this organisation's installed frameworks provide for. Please choose another request type.",
        // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
        legal_matter_right: 'What do you wish to raise?',
        legal_matter_right_hint: 'This request is received as a legal matter. Choose which of these you wish to raise.',
        legal_matter_right_not_sure: 'Not sure',
        error_summary: 'Your request was not sent. Please correct the following:',
        request_type_not_enabled: 'This form does not accept that request type. Please choose another request type.',
        submit_failed: 'Your request could not be submitted: {reason}',
    },
    request_types: {
        access: 'Zougang (DSAR)',
        deletion: 'Läschung',
        correction: 'Korrektur / Aktualiséierung',
        objection: 'Widdersproch',
        portability: 'Datenportabilitéit',
        restriction: 'Aschränkung vun der Veraarbechtung',
        consent_withdrawal: 'Réckzuch vun der Aweisung',
        protective_measures: 'Moossname fir de Schutz vu perséinlechen Donnéeën ufroen',
        processing_information: 'Be informed about the processing of personal data',
        consent_decision: 'Consent or refuse consent to processing',
        complaint_or_compensation: 'Complaint, denunciation, lawsuit or claim for damages',
    },
    statuses: {
        new: 'Nei',
        in_review: 'A Revisioun',
        waiting_on_requester: 'Waarden op Ufroer',
        waiting_on_internal_data: 'Waarden op intern Donnéeën',
        approved: 'Genehmegt',
        closed: 'Zougemaach',
        rejected: 'Refuséiert',
    },
    admin: {
        nav: {
            rights_requests: 'Ufroe fir Rechter',
            templates: 'Schablounen',
            response_templates: 'Äntwert Schablounen',
            data_mapping: 'Dateninventaire',
            governance: 'Gouvernance',
            consent: 'Zoustëmmung',
            assessments: 'Evaluatiounen',
            classification: 'Klassifikatioun',
            monitoring: 'Iwwerwaachung',
            incidents: 'Tëschefäll',
            onboarding: 'Onboarding',
            connectors: 'Konnektoren',
            vendor_risk: 'Fournisseur-Risiko',
            deletion_orchestration: 'Läsch-Orchestréierung',
            discovery: 'Datenentdeckung',
            incident_ops: 'Tëschefall-Operatiounen',
            program_governance: 'Programm-Gouvernance',
            access_accountability: 'Zougangsverantwortlechkeet',
            consent_properties: 'Zoustëmmungs-Eegeschafte',
            localization: 'Lokaliséierung',
            regulatory: 'Regulatioun',
            partner_workspace: 'Partner-Aarbechtsberäich',
            audit_trail: 'Audit-Protokoll',
            forseti: 'Forseti',
            settings: 'Astellungen',
            help_center: 'Hëllef-Zentrum',
            dashboard: 'Iwwersiicht',
            platform: 'Plattform',
            integrations: 'Integratiounen',
            cmp_dsar_sync: 'CMP-DSAR-Synchronisatioun',
            cmp_integration: 'CMP-Integratioun',
            purpose_mapping: 'Zweck-Mapping',
            webhook_config: 'Webhook-Konfiguratioun',
            bulk_sync: 'Masse-Synchronisatioun',
            privacy_scanner: 'Dateschutz-Scanner',
            privacy_monitoring: 'Dateschutz-Iwwerwaachung',
            real_time_scanner: 'Echtzäit-Scanner',
            compliance_forms: 'Konformitéitsformulairën',
            forms_dashboard: 'Formulairë-Iwwersiicht',
            form_templates: 'Schablounen',
            form_catalog: 'Formulaire-Katalog',
            pack_catalog: 'Pack-Katalog',
            org_groups: 'Organisatiounsgruppen',
            consolidation: 'Konsolidéierung',
            organizations: 'Organisatiounen',
            org_list: 'All Organisatiounen',
            intake_configs: 'Opnam Configs',
            task_management: 'Aufgabeverwaltung',
            unified_audit: 'Eenheetlech Audit',
            evidence_packs: 'Beweispäck',
            framework_evidence_packs: 'Framework evidence packs',
            audit_periods: 'Auditperioden',
            regulatory_updates: 'Regulatoresch Aktualiséierungen',
            departments: 'Abteilungen',
        },
        sidebar_category_data_privacy_rights: 'Dateschutz & Rechter',
        sidebar_category_vendor_third_party: 'Liwweranten- & Drëttpartei-Management',
        sidebar_category_risk_assessment: 'Risiko & Bewäertung',
        sidebar_category_operations_automation: 'Operatiounen & Automatiséierung',
        sidebar_category_sector_overlays: 'Sector Overlays',
        sidebar_category_task_workflow: 'Aufgaben- & Workflow-Management',
        sidebar_groups: {
            consent_cmp_webhooks: 'CMP & Webhooks',
            // Sprint 62.28.0 — marketing-communications registers.
            marketing_communications: 'Marketing Communications',
            data_mapping_ropa: 'RoPA Register',
            data_mapping_classification: 'Klassifikatioun',
            data_mapping_governance: 'Gouvernance',
            access_live_monitoring: 'Live Iwwerwaachung',
            access_reviews_investigations: 'Rezensiounen & Ermëttlungen',
            access_evidence_policies: 'Beweis & Politik',
            classification_taxonomies: 'Taxonomien & Etiketten',
            classification_workflow: 'Workflow',
            classification_insights: 'Abléck',
            connectors_runtime: 'Runtime',
            connectors_due_diligence: 'Due Diligence',
            connectors_coverage: 'Ofdeckung',
            cp_definitions: 'Definitiounen',
            cp_lifecycle: 'Liewenszyklus',
            cp_quality: 'Qualitéit & Beweis',
            deletion_execution: 'Ausféierung',
            deletion_vendors: 'Verkeefer',
            deletion_evidence: 'Beweis & Integratioun',
            discovery_rules: 'Regelen & Schablounen',
            discovery_scanning: 'Scannen',
            discovery_review: 'Iwwerpréift Workflow',
            discovery_insights: 'Abléck',
            governance_oversight: 'Iwwerwaachung',
            governance_reporting: 'Berichterstattung',
            governance_vendors: 'Verkeefer & Kräiz-Grenz',
            governance_lifecycle: 'Liewenszyklus',
            incident_ops_dashboards: 'Dashboards',
            incident_ops_sla: 'SLA Config',
            incident_ops_playbooks: 'Spillbicher',
            incident_ops_vendor_regulator: 'Verkeefer & Regulateur',
            incident_ops_evidence: 'Beweis & Iwwerpréiwung',
            incidents_workload: 'Aarbechtsbelaaschtung',
            incidents_regulatory: 'Regulatioun',
            incidents_reporting: 'Berichterstattung',
            localization_content: 'Inhalt',
            regulatory_frameworks: 'Frameworks',
            localization_regulatory_registers: 'Regulatory Registers',
            localization_penalties_enforcement: 'Penalties & Enforcement',
            localization_sector_overlays: 'Sector Overlays',
            // Sprint 63.43.0 — Sector Overlays (Module 30): management and sector groups.
            sector_overlays_management: 'Overlay Management',
            sector_overlays_banking_payments: 'Banking & Payments',
            sector_overlays_crypto_assets: 'Crypto Assets',
            sector_overlays_manufacturing: 'Manufacturing',
            sector_overlays_food_traceability: 'Food Traceability',
            sector_overlays_transport_logistics: 'Transport & Logistics',
            sector_overlays_public_administration: 'Public Administration',
            sector_overlays_digital_technology: 'Digital Technology',
            sector_overlays_publishing: 'Publishing',
            sector_overlays_education: 'Education',
            sector_overlays_cross_sector: 'Cross-Sector',
            localization_data_exploitation: 'Data Exploitation',
            localization_cybersecurity_assurance_index: 'Cybersecurity Assurance Index',
            localization_food_traceability: 'Food Traceability',
            localization_observability: 'Observabilitéit',
            monitoring_metrics: 'Metriken',
            monitoring_programs: 'Programmer',
            monitoring_tasks: 'Aufgaben',
            monitoring_controls: 'Kontrollen',
            org_entities: 'Entitéiten',
            org_config: 'Configuratioun',
            org_frameworks: 'Kaderen & Rollup',
            partner_overview: 'Iwwersiicht',
            partner_partners: 'Partner',
            partner_delivery: 'Liwwerung',
            partner_support: 'Ënnerstëtzung',
            platform_identity: 'Identitéit & Rollen',
            platform_access_control: 'Zougang Kontroll',
            platform_auth_session: 'Authentifikatioun & Sëtzung',
            platform_access_reviews: 'Zougang Rezensiounen',
            platform_files: 'Fichieren',
            platform_notifications: 'Notifikatiounen',
            platform_module_config: 'Modul Config',
            platform_ops: 'Ops & Logs',
            pg_health: 'Programm Gesondheet',
            pg_maturity: 'Maturitéit',
            pg_audit: 'Audit & Beweiser',
            pg_training: 'Formatioun',
            pg_privacy_office: 'Privacy Office',
            pg_obligations: 'Obligatiounen',
            pg_documents: 'Dokumenter',
            vr_overview: 'Iwwersiicht',
            vr_onboarding: 'Onboarding',
            vr_assessment: 'Bewäertung',
            vr_oversight: 'Iwwerwaachung',
        },
        tickets: {
            title: 'Rights Ufroen',
            create: 'Erstellen Ufro',
            filters: 'Filteren',
            search_placeholder: 'Search tickets...',
            export_csv: 'Export CSV',
            exporting: 'Exportéiert...',
            bulk_export_failed: 'Bulk Export gescheitert',
            bulk_export: {
                label: 'Export',
                format: {
                    pdf: 'PDF Resumé',
                    zip: 'ZIP (pro-Fall Beweis Packs)',
                    csv: 'CSV',
                },
                warning_over_50: 'Dëst generéiert ongeféier {count} Case Packs - kann e puer Minutten huelen. Fuert weider?',
                blocked_over_500: 'Schmuel Filter op 500 oder manner Fäll ier Dir ZIP exportéiert (aktuell: {count}).',
                success: 'Bulk Export gespäichert an Dateie Browser ({count} Fäll)',
                failed: 'Bulk Export gescheitert',
                continue: 'Fuert weider',
                cancel: 'Ofbriechen',
            },
            no_results: 'Keng requests fonnt',
            ticket_id: 'Ticket-ID',
            request_type: 'Ufro Typ',
            requester: 'Usteller',
            source: 'Quell',
            all_sources: 'All Quellen',
            assigned_to: 'Zougewisen Op',
            created: 'Erstellt',
            load_error: 'Konnt net load cases. Probéiert et nach eng Kéier.',
            case_creation_failed: 'Fall erstellen ass fehlgeschloen',
            business_unit: 'Geschäftseenheet',
            reference: 'Referenz',
            sla: {
                title: 'SLA-Status',
                on_track: '{days} Deeg reschtlech',
                approaching: 'Frist nähert sech',
                overdue: '{days} Deeg iwwerfälleg',
                ack_overdue: '{hours}St iwwert Bestätegungsfrist',
                ack_warning: 'Bestätegung geschwënn fälleg',
                closed: 'Zou',
                completed_in: 'Ofgeschloss an {days} Deeg',
                source_statutory: 'Statutory period',
                source_tenant_target: 'Your SLA target',
                source_platform_default: 'Platform default, not a legal period',
                awaiting_activation: 'Awaiting activation from {date}',
                no_installed_clock: 'No installed clock applies',
                // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
                handled_in_legal_matters: 'Handled in Legal Matters',
            },
        },
        templates: {
            title: 'Äntwert Schablounen',
            new: 'Nei Schabloun',
            edit: 'Änneren Schabloun',
            all_types: 'All Typen',
            all_locales: 'All Sproochen',
            type_access: 'Zougang/DSAR',
            type_deletion: 'Läschung',
            type_correction: 'Korrektur',
            type_objection: 'Asproch',
            type_portability: 'Iwwerdrobarkeit',
            type_other: 'Aner',
            type_restriction: 'Aschränkung',
            type_all: 'All',
            locale_en: 'Englesch',
            locale_vi: 'Vietnamesesch',
            locale_all: 'Béid',
            name: 'Numm',
            request_type: 'Ufro Typ',
            locale: 'Sprooch',
            subject: 'Betreff',
            body: 'Inhalt',
            name_placeholder: 'Schabloun Numm...',
            subject_placeholder: 'E-Mail/Bréif-Betreff...',
            body_placeholder: 'Schabloun Kierper. Benotzt {{requester_name}}, {{ticket_id}}, {{request_type}}, {{org_name}}, {{response_date}} als Plazhalter.',
            available_placeholders: 'Verfügbar placeholders:',
            create_template: 'Erstellen Schabloun',
            preview: 'Virschau',
            close_preview: 'Zoumaachen Virschau',
            default_badge: 'Standard',
            no_templates: 'Keng Schablounen fonnt',
            confirm_delete: 'Läschen dëst Schabloun?',
            load_error: 'Konnt net load Schablounen. Probéiert et nach eng Kéier.',
            create_error: 'Konnt net create Schabloun',
            update_error: 'Konnt net update Schabloun',
            delete_error: 'Konnt net delete Schabloun',
            preview_error: 'Konnt net preview Schabloun',
            response_templates_badge: 'Äntwert Schablounen',
            browse_form_templates: 'Compliance Formular Schablounen duerchsichen',
            templates_loaded: 'Virlagen gelueden',
            sample_organization: 'Beispilorganisatioun',
        },
        detail: {
            requester_info: 'Usteller Information',
            status: 'Status',
            assignment: 'Zouweisung',
            notes: 'Notizen',
            audit_trail: 'Auditprotokoll',
            add_note: 'Derbäisetzen Notiz',
            case_not_found: 'Case not fonnt',
            load_error: 'Konnt net load case. Probéiert et nach eng Kéier.',
            partial_load_failed: 'Some information for this case could not be loaded, so parts of this page may be incomplete.',
            partial_load_parts: 'Not loaded: {parts}.',
            note_error: 'Konnt net add note. Probéiert et nach eng Kéier.',
            submitted_from: 'Agereecht vun',
            via_cmp: 'iwwer AesirX CMP',
            cmp_ticket: 'CMP-Ticket:',
            select_template: 'Eng Virlag auswielen',
            direction_inbound: 'CMP → GRC',
            direction_outbound: 'GRC → CMP',
            tabs: {
                details: 'Detailer',
                evidence: 'Bewäis',
                communications: 'Kommunikatiounen',
                escalations: 'Eskalatiounen',
                audit_trail: 'Auditprotokoll',
                fulfillment: 'Erfëllung',
                response: 'Äntwert',
                extension: 'Verlängerung',
            },
            requester: {
                name: 'Numm',
                email: 'E-Mail',
                phone: 'Telefon',
                language: 'Sprooch',
                source: 'Quell',
                customer_ref: 'Kunnereferenz',
                country: 'Land',
                // fix-sprint #431 — the country as received, where it was stored as a code.
                country_as_received: '(received as “{value}”)',
                preferred_channel: 'Bevorzugten Kanal',
            },
            // fix-sprint #447 W9 (owner ruling OR3) — processor or third party must act
            third_party: {
                label: 'Processor or third party must act',
                hint: 'The installed deadline records may state a different period when a processor or third party must act. Changing this dates the case again from the day it was received, by the record that matches; an approved extension and closed pauses are carried onto the new date.',
                closed: 'The case is closed, so whether a processor or third party must act can no longer be changed.',
                confirm_title: 'Date the case again?',
                confirm_body: 'The case will be dated again from the day it was received, by the installed deadline record that matches. An approved extension and closed pauses are carried onto the new date. The old and the new due date are recorded.',
                confirm_label: 'Change and date again',
                saved: 'Saved. The case shows its old and new due date.',
                redated: 'Changed on {date}: dated again from receipt. Due date was {old}, now {new}.',
                not_redated_no_installed_record: 'Changed on {date}. No installed framework dates this case, so its due date ({due}) is unchanged.',
                not_redated_no_due_date: 'Changed on {date}. The case holds no due date, so nothing was dated again.',
                carried_extension: '{days} approved extension day(s) carried onto the new date.',
                carried_paused: '{amount} paused {unit} carried onto the new date.',
                units: {
                    calendar_days: 'calendar day(s)',
                    working_days: 'working day(s)',
                    hours: 'hour(s)',
                },
                mapping_invalid: 'An installed deadline record for this kind of request cannot be read as a period, so the case could not be dated again and the change was not saved.',
            },
            sla: {
                title: 'SLA',
                on_track: 'Am Plang',
                due_soon: 'Geschwënn fälleg',
                overdue: 'Iwwerfälleg',
                days_remaining: 'd Rescht',
                due_date: 'Due Datum',
                breached: 'Verletzt',
                paused: 'Pauséiert',
                total_paused_days: 'Gesamt Pauséiert Deeg',
                pause_intervals: 'Pause Intervals',
                pause_intervals_help: 'Each period the clock was stopped, so waiting-on-requester time can be told apart from processing time.',
                pause_intervals_none: 'No pauses recorded on this case.',
                no_due_date: 'No due date recorded',
                stopped_case_closed: 'SLA stopped — case closed',
                pause_interval_open: 'Paused since {from} — still waiting',
                pause_interval_closed: '{from} to {to} — {days} day(s)',
                pause_interval_by: 'Paused by {who}',
                pause_interval_actor_unknown: 'Paused by: not recorded',
                pause_interval_from_status: 'Paused from {status}',
                pause_interval_from_status_unknown: 'Paused from: not recorded',
                pause_interval_reason_unknown: 'Reason: not recorded',
                pause_interval_resumed_by: 'Resumed by {who}',
                processing_days: 'Processing time',
                waiting_days: 'Waiting on requester',
                days_elapsed: 'Deeg Elapsed',
                progress: 'Fortschrëtt',
                prov_heading_ack: 'Acknowledgement deadline',
                prov_heading_fulfil: 'Fulfilment deadline',
                prov_source_statutory: 'Statutory period: {reference}',
                prov_source_statutory_from_activation: 'Statutory period: {reference}, running from {date}, the day it came into force',
                prov_source_tenant_target: 'Your SLA target: {days} days from receipt',
                prov_source_platform_default: 'Platform default, not a legal period: {days} days from receipt',
                prov_source_none: 'Not dated: no installed clock, organisation target or platform default applies',
                prov_due: 'Due {date}',
                prov_awaiting_activation: 'Awaiting activation from {date}: the installed clock {reference} applies from that date, and the case will be dated by it then.',
                prov_no_installed_clock: 'No installed clock applies to this kind of request.',
                // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
                prov_handled_in_legal_matters: 'Handled in Legal Matters: this request is received as a legal matter, which is the record that is worked. No deadline applies to this intake record.',
                prov_open_legal_matter: 'Open the legal matter',
                prov_framework_unnamed: 'an installed framework',
                prov_legal_matter_no_access: 'The legal matter is visible to people who hold the permission to read legal matters.',
                prov_excluded: 'Installed clocks that could not be dated and took no part: {reasons}',
                prov_excluded_activation_date_not_stated: '{reference}: no effective date stated',
                prov_excluded_framework_record_unreadable: '{reference}: framework record could not be read',
                prov_excluded_phase_reference_unresolved: '{reference}: phase start not dated',
            },
            identity: {
                title: 'Identity Verifikatioun',
                verified: 'Verifizéiert',
                not_verified: 'Net verified',
                method: 'Method',
                notes: 'Notizen',
                verified_by: 'Verifizéiert vun',
                verified_at: 'Verifizéiert den',
                warning: 'Identitéit nach net verifizéiert.',
                verify_button: 'Verifizéieren Identity',
                select_method: '-- Auswielen --',
                methods: {
                    email_confirmation: 'E-Mail Confirmation',
                    id_document: 'ID Dokument',
                    phone_call: 'Telefon Call',
                    in_person: 'Perséinlech',
                    other: 'Aner',
                },
            },
            closure: {
                reason: 'Ofschloss Grond',
                summary: 'Ofschloss Zesummefaassung',
                summary_placeholder: 'Zesummefaassung vun how den request was resolved...',
                reason_required: 'Ofschloss summary an reason are required',
                response_not_sent: 'Eng formell Äntwert muss un den Datesubjekt geschéckt ginn ier dëse Fall zougemaach ka ginn',
                reasons: {
                    fulfilled: 'Erfëllt',
                    partially_fulfilled: 'Deelweis erfëllt',
                    unfounded: 'Onbegrënnt',
                    excessive: 'Iwwerméisseg',
                    withdrawn: 'Zréckgezunn',
                    duplicate: 'Duplizéieren',
                    identity_unverified: 'Identitéit net verifizéiert',
                },
            },
            // English until Phase 25 (R-57).
            // fix-sprint #431 — moving a case into a waiting status stops its clock,
            // and the server requires the reason (`pause_reason`).
            pause: {
                reason_label: 'Why is the case waiting?',
                reason_placeholder: 'Select a reason',
                reason_required: 'Choose why the case is waiting before moving it to a waiting status.',
                reasons: {
                    awaiting_subject: 'Waiting for the data subject',
                    awaiting_verification: 'Waiting for identity verification',
                    third_party_dependency: 'Waiting for a third party',
                    other: 'Other',
                },
            },
            // fix-sprint #431 — rejecting a case requires a reason code
            // (`rejection_reason_code`).
            rejection: {
                code_label: 'Rejection reason',
                code_placeholder: 'Select a reason',
                code_required: 'Choose a rejection reason before rejecting the case.',
                description_label: 'Rejection details (optional)',
                codes: {
                    identity_not_verified: 'Identity not verified',
                    manifestly_unfounded: 'Manifestly unfounded',
                    repetitive: 'Repeated request',
                    exempt: 'Exempt',
                    out_of_scope: 'Out of scope',
                    duplicate: 'Duplicate request',
                },
            },
            // English until Phase 25 (R-57).
            // fix-sprint #431 — what the cross-border detector found when the case
            // was created (`cross_border_finding`). A record; it decides nothing.
            cross_border_finding: {
                title: 'Cross-border check at creation',
                result: 'Finding',
                evaluated_at: 'Checked at',
                triggered: 'The rule matched: the data subject’s country ({subject}) differs from the organisation’s ({org}).',
                reasons: {
                    request_type_not_disclosing: 'Not checked further: this request type discloses no data.',
                    subject_country_unknown: 'Not matched: the data subject’s country is not recorded.',
                    same_country: 'Not matched: the data subject is in the organisation’s country ({org}).',
                    // fix-sprint #431 — the organisation records no country: never assumed.
                    org_country_not_recorded: 'Not evaluated: the organisation’s country is not recorded.',
                    // fix-sprint #431 — a recorded country that reads as no code is not compared.
                    subject_country_not_recognised: 'Not evaluated: the data subject’s country, as recorded, is not a country code or name the platform recognises.',
                },
                other_reason: 'Not matched ({reason}).',
                org_country_pointer: 'The cross-border check compares the data subject’s country with the organisation’s. The organisation’s country is recorded on its record: Organizations › All Organizations, then the organisation’s Country field.',
                org_country_link: 'Record the organisation’s country',
                not_evaluated: 'Could not be evaluated when the case was created.',
                not_recorded: 'Not recorded: this case was created before the check ran on creation.',
                decides_nothing: 'This records what the rule found. It changes no status, deadline or assignment, and opens no cross-border dossier.',
                // fix-sprint #431 (owner ruling 2026-10-03) — viewing a case opens
                // nothing; a person opens the cross-border dossier with the button.
                current: 'Assessment now',
                dossier: 'Cross-border dossier',
                dossier_reading: 'Reading…',
                dossier_unreadable: 'Whether a dossier is open for this case could not be read.',
                no_dossier: 'No cross-border dossier has been opened for this case.',
                dossier_opened: 'Dossier {dossier}, opened {at}.',
                filing: 'Statutory filing',
                filing_no_form: 'None opened: no installed framework declares a filing form for this transfer.',
                filing_not_opened: 'None opened: the filing could not be created when the dossier was opened.',
                filing_not_recorded: 'None recorded.',
                open_button: 'Open cross-border dossier',
                open_hint: 'Opening the dossier is your action, recorded in the case audit log under your name. Where an installed framework declares a filing form for this transfer, the statutory filing is opened with it.',
                opening: 'Opening…',
                confirm_title: 'Open cross-border dossier?',
                confirm_message: 'This opens a cross-border dossier for case {case} and, where an installed framework declares a filing form for this transfer, a statutory filing. It is recorded under your name.',
                confirm_label: 'Open dossier',
                opened_toast: 'Cross-border dossier {dossier} opened.',
                already_open_toast: 'A cross-border dossier was already open for this case: {dossier}.',
                open_error: 'The cross-border dossier could not be opened.',
                // fix-sprint #431 (gate E12.5 remediation 1) — why opening the dossier was refused.
                not_detected: 'No cross-border transfer is detected for this case now, so no dossier was opened.',
                case_not_found: 'This case could not be found.',
                // gate E12.5 remediation 1 — retrying a filing that could not be opened; the subject country compared and its source.
                filing_opening: 'Being opened now.',
                retry_filing_button: 'Retry the statutory filing',
                retry_filing_hint: 'The statutory filing could not be opened with this dossier. Retrying it is your action, recorded in the case audit log under your name.',
                retry_filing_failed: 'The statutory filing still could not be opened.',
                filing_retried_toast: 'Statutory filing {filing} opened.',
                // Gate E12.5 remediation 2 — a stopped opening, and what a retry answered.
                filing_opening_stalled: 'None opened: the request that was opening it stopped before it finished.',
                retry_filing_in_progress: 'Another request is opening the statutory filing now, so this retry started nothing.',
                retry_filing_no_form: 'No statutory filing was opened: no installed framework declares a filing form for this transfer now.',
                // Gate E12.5 pass 3 — a filing retry that could not be attempted, and why.
                retry_filing_unavailable_service: 'This retry started nothing: statutory filings cannot be opened on this installation.',
                retry_filing_unavailable_org_country: 'This retry started nothing: the dossier records no organisation country, which the statutory filing needs.',
                confirm_retry_title: 'Retry the statutory filing?',
                confirm_retry_message: 'This tries again to open the statutory filing for dossier {dossier} of case {case}. It is recorded under your name.',
                confirm_retry_label: 'Retry filing',
                subject_compared: 'Data subject’s country compared',
                subject_compared_value: '{country}, from {source}',
                subject_sources: {
                    residence_country: 'the country of residence recorded with the request',
                    case_country: 'the case’s Country field',
                    address_country: 'the country in the data subject’s address',
                },
            },
            export_title: 'Exportéieren',
            export_pdf: 'Exportéieren PDF',
            export_zip: 'Exportéieren ZIP',
            case_file_pdf: 'Export PDF (Fall Datei)',
            evidence_pack_zip: 'Export ZIP (Evidence Pack)',
            cross_border_warning: 'This request may involve a cross-border transfer. Review whether your transfer impact assessment covers it.',
            breach: {
                flag_btn: 'Fändel als Breach-Causing',
                modal: {
                    title: 'Fändel Fall als Verstouss verursaacht',
                    type_label: 'Typ vun Verletzung',
                    type: {
                        misdirected_response: 'Falsch Äntwert',
                        unauthorised_disclosure: 'Onerlaabt Offenbarung',
                        identity_mismatch: 'Identitéit Mëssverständnis',
                        other: 'Aner',
                    },
                    description: 'Beschreiwung (optional)',
                    description_placeholder: 'Wat ass geschitt? (maximal 1000 Zeechen)',
                    confirm: 'Markéieren an d’Melduhr ufänken',
                    cancel: 'Ofbriechen',
                    flagging: 'Fändel ...',
                },
                banner: {
                    title: 'Verstouss markéiert: {type}',
                    detected_at: 'Op {timestamp} entdeckt',
                    filing_deadline: 'Meldefrist: an {hours} Stonnen',
                    overdue: 'Meldefrist: IWWERFÄLLEG',
                },
                toasts: {
                    flagged: 'Fall markéiert als Verletzung verursaacht - Melduhr ugefaang',
                    flag_failed: 'Ausgefall de Fall als Verstouss verursaacht',
                },
            },
            export: {
                generating: 'Generéiere ... weg wait',
                success: 'Export gespäichert an Dateie Browser',
                failed: 'Export gescheitert - kontrolléiert Audit Log',
            },
            no_notes: 'Nach keng Notizen',
            assign_placeholder: 'Benotzer ID op assign...',
            reassign: 'Nei zouweisen',
            evidence: {
                checklist_title: 'Bewäis Checklëscht',
                complete_pct: '% fäerdeg',
                no_checklist: 'Keng Checklëschtelementer',
                attachments: 'Unhäng',
                upload_attachment: 'Undlag eroplueden',
                upload_button: 'Lueden',
                uploading: 'Gëtt gelueden...',
                attachment_uploaded: 'Undlag erfolgräich eropgelueden',
                upload_error: 'Eroplueden fehlgeschloen',
                category: 'Kategorie',
                categories: {
                    identity_proof: 'Identitéitsnachweiss',
                    correspondence: 'Korrespondenz',
                    internal_data: 'Intern Daten',
                    legal: 'Juridescht',
                    other: 'Soss',
                },
                no_attachments: 'Keng Unhäng',
                references_title: 'Bewäis References',
                no_references: 'Keng Referenzen',
                add_reference: 'Derbäisetzen Referenz',
                ref_type: 'Typ',
                ref_value: 'Wäert',
                ref_desc: 'Beschreiwung',
                ref_value_placeholder: 'URL, E-Mail ID, oder reference...',
                ref_desc_placeholder: 'Beschreiwung...',
                ref_types: {
                    url: 'URL',
                    email_id: 'E-Mail ID',
                    crm_record: 'CRM Datesat',
                    ticket_ref: 'Ticket Referenz',
                    other: 'Aner',
                },
            },
            communications: {
                title: 'Kommunikatiounen',
                add: 'Derbäisetzen Message',
                no_records: 'Keng Kommunikatiounen opgeholl',
                direction: 'Richtung',
                channel: 'Kanal',
                visibility: 'Siichtbarkeet',
                subject: 'Betreff',
                body: 'Inhalt',
                send: 'Schécken',
                inbound: 'Erakommend',
                outbound: 'Ausgangend',
                internal: 'Intern',
                external: 'Extern',
                tab_all: 'All',
                tab_internal: 'Intern',
                tab_external: 'Extern',
                subject_placeholder: 'Betreff (optional)',
                body_placeholder: 'Noriichtentext...',
                channels: {
                    email: 'E-Mail',
                    portal: 'Portal',
                    phone: 'Telefon',
                    postal: 'Postalësch',
                    in_person: 'Perséinlech',
                },
                apply_template: 'Schabloun Uwenden',
                select_template: 'Eng Schabloun auswielen...',
                no_templates: 'Keng Schablounen verfügbar',
                templates_load_error: 'Templates could not be loaded',
                template_applied: "Schabloun op d'Äntwert ugewannt.",
                template_apply_error: 'Schabloun konnt net ugewannt ginn.',
            },
            escalations: {
                title: 'Eskalatioun Verlaf',
                no_events: 'No escalation Evenementer',
                tier: 'Stuf',
                threshold: 'Schwellwäert',
                action: 'Aktioun',
                target: 'Zil',
                triggered_at: 'Ausgeléist At',
                // fix-sprint #431 (owner ruling 2026-10-03) — what a tier did. English until
                // Phase 25 (R-57).
                outcome: 'Outcome',
                outcome_acted: 'Acted',
                outcome_baselined: 'Passed before automatic escalation was on',
                // fix-sprint #431 — a tier recorded because a ladder edit added, switched on or lowered it.
                outcome_baselined_ladder_edit: 'Passed before this tier was added, switched on or lowered',
                outcome_baselined_detail: 'No one was notified and the case was not reassigned.',
            },
            cmp_sync: {
                title: 'AesirX CMP Synchroniséierung',
                sync_status: 'Synchroniséierung Status',
                last_synced: 'Lescht Synced',
                not_synced: 'Net Synced',
                resync: 'Elo nei synchroniséieren',
                syncing: 'Synchroniséiere...',
                sync_history: 'Synchroniséierung Verlaf (Last 10)',
                direction: 'Richtung',
                time: 'Zäit',
                retries: 'Widderholungen',
                error_col: 'Feeler',
            },
            audit: {
                filter_event_placeholder: 'Filteren no Evenement type...',
                filter_actor_placeholder: 'Filteren no actor...',
            },
            fulfillment: {
                title: 'Abteilungserfëllung',
                assign_departments: 'Abteilungen zouweisen',
                department: 'Abteilung',
                assignee: 'Zougewisen',
                deadline: 'Frist',
                evidence_count: 'Bewäiser',
                signoff: 'Ofzeechen',
                signed_off: 'Ofgezeechent',
                pending_signoff: 'Aussteend',
                start_button: 'Ufänken',
                complete_button: 'Als ofgeschloss markéieren',
                signoff_button: 'Zeechen Off',
                no_tasks: 'Nach keng Erfëllungsaufgaben zougewisen.',
                tasks_table: 'Erfëllungsaufgaben',
                progress_label: 'Erfëllungsfortschrëtt',
                overdue: 'iwwerfälleg',
                assigned_success: 'Abteilungserfëllungsaufgab erfollegräich zougewisen.',
                assign_error: 'Erfëllungsaufgab konnt net zougewisen ginn.',
                signoff_success: 'Abteilungsofzeechen registréiert.',
                signoff_error: 'Ofzeechen konnt net registréiert ginn.',
                identity_required: 'Identity verification required before fulfillment actions.',
                remove_task: 'Remove Task',
                remove_success: 'Fulfillment task removed successfully.',
                add_evidence_btn: 'Beweis bäifügen',
                evidence_uploaded: 'Beweis eropgelueden a verlinkt',
                evidence_upload_error: 'Beweis eroplueden fehlgeschloen',
                suggest_data_stores: 'Dateläger virschloen',
                suggest_error: 'Datelägervirschléi konnten net gelueden ginn.',
                suggest_stores_title: 'Recommandéiert Data Stores',
                suggest_stores_none: 'Keng Dategeschäfter passen dëse Fall. Systemer an Data Mapping konfiguréieren fir Suggestiounen ze verbesseren.',
                suggest_stores_add: 'Füügt op de Fall',
                suggest_stores_added: 'Donnéeën Buttek zu Fall dobäi',
                no_departments_title: 'Keng Departementer konfiguréiert',
                no_departments_body: 'Departementer loossen Iech DSR Erfëllung Aufgaben op eng Funktioun routen (Privatsphär, Legal, HR, IT Ops). Installéiert Proufdepartementer oder frot Ären Admin fir Departementer an den Organisatiounsastellungen ze konfiguréieren.',
                no_departments_cta: 'Installéiere Proufdaten',
                department_placeholder: 'Abteilungsnumm...',
                assignee_placeholder: 'Benotzer-ID fir zouzeweisen...',
                assign_button: 'Zouweisen',
                status_pending: 'Hängeg',
                status_in_progress: 'Am gaang',
                status_completed: 'Ofgeschloss',
                status_verified: 'Verifizéiert',
                confirm_approve: 'Sidd Dir sécher, dass Dir dës Verlängerung guttheesche wëllt?',
                confirm_deny: 'Sidd Dir sécher, dass Dir dës Verlängerung ofleene wëllt?',
            },
            response: {
                title: 'Äntwertgeneréierung',
                select_template: 'Äntwertschabloun',
                choose_template: 'Schabloun auswielen...',
                generate: 'Äntwert generéieren',
                preview: 'Äntwertvirschau',
                send: 'Äntwert schécken',
                delivery_method: {
                    label: 'Liwwermethod',
                    email: 'E-Mail',
                    portal: 'Portal',
                    postal: 'Post Post',
                    in_person: 'Perséinlech',
                },
                recipient: {
                    email: 'E-Mail vum Empfänger',
                    portal: 'Portal Benotzer ID',
                    address: 'Post Adress',
                },
                auto_generate_btn: 'Generéiere a schéckt Äntwert',
                auto_generate_success: 'Äntwert generéiert a geliwwert',
                auto_generate_missing_recipient: 'Gitt w.e.g. en Empfänger virum Versand.',
                auto_fields: 'Automatesch ausgefëllte Felder',
                case_id: 'Fall-ID',
                requester_name: 'Numm vum Ufroder',
                rights_type: 'Rechtstyp',
                history: 'Geschéckt Äntwerten',
                generated_at: 'Generéiert',
                confirmation: 'Bestätegung',
                sent_label: 'Geschéckt',
                draft_label: 'Entworf',
                generated_success: 'Äntwert erfollegräich generéiert.',
                generate_error: 'Äntwert konnt net generéiert ginn.',
                sent_success: 'Äntwert erfollegräich geschéckt.',
                send_error: 'Äntwert konnt net geschéckt ginn.',
                load_error: 'Äntwertschablounen konnten net gelueden ginn.',
                pdf_title: 'Äntwertbréif un betraffe Persoun',
                download_pdf: 'PDF eroflueden',
                download_all: 'Alles eroflueden',
                download_error: 'Äntwert konnt net erofgeluede ginn',
            },
            extension: {
                title: 'Fristverlängerungen',
                acknowledgment_deadline: 'Bestätegungsfrist',
                fulfillment_deadline: 'Erfëllungsfrist',
                request_extension: 'Verlängerung ufroen',
                reason: 'Grond',
                reason_placeholder: 'Grond fir zousätzlech Zäit unzefroen...',
                additional_days: 'Zousätzlech Deeg',
                submit_request: 'Verlängerungsufro areechen',
                history: 'Verlängerungshistorique',
                requested_at: 'Ugefrot',
                approve: 'Guttgeheescht',
                deny: 'Refuséieren',
                request_success: 'Verlängerungsufro agereecht.',
                request_error: 'Verlängerungsufro konnt net agereecht ginn.',
                approved_success: 'Verlängerung guttgeheescht.',
                denied_success: 'Verlängerung refuséiert.',
                decision_error: 'Verlängerungsentscheedung konnt net veraarbecht ginn.',
                status_pending: 'Hängeg',
                status_approved: 'Genehmegt',
                status_denied: 'Ofgelehnt',
                confirm_approve: 'Sidd Dir sécher, dass Dir dës Verlängerungsufro guttheesche wëllt?',
                confirm_deny: 'Sidd Dir sécher, dass Dir dës Verlängerungsufro ofleene wëllt?',
                decided_by: 'Entscheet vun',
            },
            // fix-sprint #431 (gate E12.5 remediation 1) — the banner’s link to the check; why a status change was refused.
            cross_border_warning_link: 'See the cross-border check',
            status_errors: {
                invalid_transition: 'The case cannot move to this status from its current status.',
                invalid_sub_status: 'The chosen sub-status does not apply to this status.',
                assignment_required: 'Assign the case before moving it to In Review.',
                identity_verification_required: 'Verify the requester’s identity before fulfilling the request.',
                closure_delivery_required: 'Record how and when the response was delivered before closing the case.',
                invalid_value: 'The change was not saved: a value is missing or not accepted.',
            },
            evidence_unlink_read_failed: 'The attachment was deleted, but the fulfilment tasks could not be read, so it may still be listed as evidence on a task.',
            partial_load_part_names: {
                advisor_summary: 'advisor summary',
                sla_status: 'SLA status',
                attachments: 'attachments',
                fulfilment_tasks: 'fulfilment tasks',
                checklist: 'evidence checklist',
                references: 'references',
                escalations: 'escalations',
                cmp_sync_history: 'CMP sync history',
                flagged_breaches: 'flagged breaches',
                audit_trail: 'audit trail',
                communications: 'communications',
            },
            partial_load_reload: 'Reload the missing parts',
            request_type_not_granted: 'This kind of request is not a right under the frameworks this organisation has installed. The request is kept and can still be worked.',
        },
        audit: {
            export_csv: 'Exportéieren CSV',
            exporting: 'Exportéiere...',
            search_placeholder: 'Sich Audit details...',
            filter_by_actor: 'Filteren no actor...',
            all_event_types: 'All Evenement Typen',
            seq: '#',
            timestamp: 'Zäitstempel',
            event: 'Evenement',
            case_col: 'Fall',
            actor: 'Akteur',
            summary: 'Zesummefaassung',
            no_events: 'Keng Audit Evenementer fonnt',
            export_success: 'Audit Trail erfollegräich exportéiert.',
            export_error: 'Konnt net Export Audit CSV',
            load_error: 'Konnt net load Audit trail. Probéiert et nach eng Kéier.',
            announce_loaded: 'Audit-Spur gelueden',
            start_date: 'Startdatum',
            end_date: 'Enddatum',
            per_page: '{count} / Säit',
            unknown: 'Onbekannt',
            source_filter: {
                label: 'Quell',
                all: 'All',
                module: 'Modul',
                forseti: 'Forseti',
                aria_label: 'Filter Auditevenementer no Quell',
            },
            event_types: {
                case_created: 'Case Erstellt',
                status_changed: 'Status Geännert',
                case_assigned: 'Case Zougewisen',
                note_added: 'Notiz Added',
                attachment_added: 'Unhang Added',
                attachment_deleted: 'Unhang Geläscht',
                reference_added: 'Referenz Added',
                checklist_updated: 'Checklëscht Aktualiséiert',
                identity_verified: 'Identitéit verifizéiert',
                closure_summary_added: 'Ofschloss Zesummefaassung',
                approval_granted: 'Genehmegung Granted',
                template_applied: 'Schabloun Applied',
                export_generated: 'Exportéieren Generéiert',
                sla_warning: 'SLA Warnung',
                sla_overdue: 'SLA Iwwerfälleg',
                sla_flag_revisited: 'SLA flag revisited',
                third_party_involvement_changed: 'Processor or third-party involvement changed',
                aml_diligence_linked: 'AML due diligence record linked',
                aml_diligence_confirmed: 'AML due diligence record confirmed',
                aml_scoping_recorded: 'AML reporting-entity scoping recorded',
                aml_scoping_deactivation_refused: 'AML programme-inactive role refused',
                aml_two_person_policy_changed: 'AML two-person rule changed',
                aml_retention_scope_recorded: 'AML records retention scope recorded',
                aml_retention_scope_updated: 'AML records retention scope updated',
                aml_indicator_row_updated: 'AML indicator coverage row updated',
                aml_indicator_entity_row_recorded: 'AML organisation-defined indicator recorded',
                aml_indicator_config_approved: 'AML indicator configuration approved',
                aml_case_opened: 'Suspicious-activity case opened',
                aml_case_updated: 'Suspicious-activity case edited',
                aml_case_viewed: 'Suspicious-activity case read',
                aml_case_listed: 'Suspicious-activity case list read',
                aml_case_read_denied: 'Suspicious-activity case read refused',
                aml_case_determined: 'Suspicious-activity case determination recorded',
                aml_case_approved: 'Suspicious-activity case determination approved',
                aml_case_self_approval_refused: 'Suspicious-activity case approval by the determiner refused',
                aml_case_approval_reverted: 'Suspicious-activity case approval taken back',
                aml_case_filing_recorded: 'Suspicious-activity case report reference recorded',
                aml_case_closed: 'Suspicious-activity case closed',
                aml_case_cdd_refresh_requested: 'Due diligence refresh asked for from a suspicious-activity case',
                aml_diligence_refresh_requested: 'AML due diligence refresh asked for',
                aml_beneficial_owner_recorded: 'Beneficial owners recorded',
                aml_beneficial_owner_updated: 'Beneficial owners updated',
                aml_launch_recorded: 'AML launch record created',
                aml_launch_scope_determined: 'AML launch scope determination recorded',
                aml_launch_assessment_recorded: 'AML pre-launch risk assessment recorded',
                aml_launch_assessment_decided: 'AML pre-launch risk assessment decision recorded',
                aml_launch_approved: 'AML launch approved',
                aml_launch_approval_refused: 'AML launch approval refused',
                aml_inspection_pack_exported: 'AML inspection pack exported',
                aml_indicator_config_self_approval_refused: 'AML indicator configuration approval by an editor of the draft refused',
                aml_indicator_review_task_raised: 'AML indicator review task raised from a launch assessment',
                aml_indicator_review_task_completed: 'AML indicator review recorded',
                aml_retention_scope_approved: 'AML records retention scope signed',
                aml_retention_scope_self_approval_refused: 'Second signature of an AML records retention scope entry by the same person refused',
                aml_case_related_action_recorded: 'Suspicious-activity case related action recorded',
                aml_case_retention_start_recorded: 'Suspicious-activity case retention start recorded',
                aml_launch_self_approval_refused: 'AML launch approval by the assessment decider refused',
                aml_launch_post_launch_review_set: 'AML launch post-launch review date recorded',
                aml_case_reads_retention_swept: 'Suspicious-activity case read log retention sweep',
                org_profile_statement_recorded: 'Organization profile statement recorded',
                dossier_authority_communication_recorded: 'Authority communication recorded on a filing',
                dossier_authority_communication_exported: 'Authority communications of a filing exported',
                framework_summary_exported: 'Framework summary exported',
                framework_briefing_recorded: 'Briefing or consultation comment recorded on a framework',
                framework_briefing_approved: 'Briefing on a framework approved',
                dossier_receipt_evidence_recorded: 'Receipt of a dossier recorded on a filing',
                dossier_handling_statement_recorded: 'Handling authority recorded on a filing',
                dossier_result_recorded: 'Authority’s result recorded on a filing',
                dossier_result_supplement_request_recorded: 'Supplement request after a result recorded on a filing',
                dossier_failure_follow_up_recorded: 'Follow-up to a result recorded on a filing',
                dossier_authority_stated_result_date_recorded: 'Result date stated by the authority recorded on a filing',
                dossier_completion_request_recorded: 'Completion request recorded on a filing',
                dossier_completion_submitted: 'Completion submitted on a filing',
                dossier_update_filing_created: 'Update filing created for a filing',
                dossier_update_addressee_recorded: 'Recipient and competent authority recorded on an update filing',
                regulatory_update_comparison_reviewed: 'Comparison review recorded on a regulatory update',
                access_statement_recorded: 'Access statement recorded on a processing activity',
            },
            unified_audit: {
                title: 'Eenheetlecht Auditprotokoll',
                module_filter: 'Modul',
                framework_filter: 'Referenzrahmen',
                select_framework: 'E Referenzrahme wielen...',
                entity_type_filter: 'Entitéitstyp',
                action_filter: 'Aktioun',
                actor_filter: 'Akteur',
                select_actor: 'En Akteur wielen...',
                search_filter: 'Sichen',
                search_placeholder: 'Audit-Evenementer sichen...',
                filters_heading: 'Filteren',
                date_range: 'Zäitraum',
                no_events_found: 'Keng Auditereignisser fonnt.',
                event_count: 'Unzuel vun Ereignisser',
                action: 'Aktioun',
                actor: 'Akteur',
                date_range_start: 'Ufank vum Datumberäich',
                date_range_end: 'Enn vum Datumberäich',
                filtering_events: 'Audit-Evenementer gi gefiltert',
                framework_codes: 'Rahmewierkscoden',
                entity_type_col: 'Entitéitstyp',
                aria_table: 'Vereenegt Audit-Evenementer',
                page_of: 'Säit {page} vun {total}',
                entity_types: {
                    user: 'Benotzer',
                    case: 'Fall',
                    incident: 'Tëschefall',
                    assessment: 'Bewäertung',
                    vendor: 'Verkeefer',
                    consent: 'Zoustëmmung',
                    policy: 'Politik',
                    data_map: 'Daten Kaart',
                    approval: 'Approval',
                    consent_record: 'Consent record',
                    data_flow: 'Data flow',
                    org_profile_statement: 'Organization profile statement',
                    dpia: 'DPIA',
                    dpia_mitigation: 'DPIA mitigation',
                    evidence_item: 'Evidence item',
                    processing_activity: 'Processing activity',
                    transfer_mechanism: 'Transfer mechanism',
                    consent_policy: 'Consent policy',
                    data_category: 'Data category',
                    drift_event: 'Drift event',
                    maturity_model: 'Maturity model',
                    program: 'Monitoring program',
                    purpose: 'Processing purpose',
                    review_record: 'Review record',
                    risk_score: 'Risk score',
                    system: 'System',
                    taxonomy: 'Taxonomy',
                },
                actions: {
                    form_dossier_statement_changed: 'Dossier statement changed',
                    create: 'Schafen',
                    update: 'Update',
                    delete: 'Läschen',
                    view: 'Vue',
                    export: 'Export',
                    status_change: 'Status änneren',
                    // Sprint 63.25.0, gate E12.5 remediation 1 (P1-1) — the verbs producers write.
                    created: 'Created',
                    updated: 'Updated',
                    deleted: 'Deleted',
                    viewed: 'Viewed',
                    exported: 'Exported',
                    approved: 'Approved',
                    rejected: 'Rejected',
                    assigned: 'Assigned',
                    completed: 'Completed',
                    closed: 'Closed',
                    status_changed: 'Status changed',
                    submitted_for_review: 'Submitted for review',
                    risk_added: 'Risk added',
                    mitigation_added: 'Mitigation added',
                    evidence_linked: 'Evidence linked',
                    scored: 'Scored',
                    reviewed: 'Reviewed',
                    activated: 'Activated',
                    period_started: 'Period started',
                    version_created: 'Version created',
                    evaluated: 'Evaluated',
                    triage_completed: 'Triage completed',
                    impact_assessed: 'Impact assessed',
                    breach_assessed: 'Breach assessed',
                    case_created: 'Case created',
                    assignment_changed: 'Assignment changed',
                    case_closed: 'Case closed',
                    identity_verified: 'Identity verified',
                    maturity_model_created: 'Maturity model created',
                    maturity_model_activated: 'Maturity model activated',
                    maturity_model_evaluated: 'Maturity model evaluated',
                    maturity_model_approved: 'Maturity model approved',
                    response_generated: 'Response generated',
                    response_delivered: 'Response delivered',
                    assembled: 'Assembled',
                    reassembled: 'Reassembled',
                    verified: 'Verified',
                    returned: 'Returned',
                    recorded_as_submitted: 'Recorded as submitted',
                    audit_evidence_pack_assembled: 'Evidence pack assembled',
                    audit_evidence_pack_reassembled: 'Evidence pack reassembled',
                    audit_evidence_pack_partial: 'Evidence pack partial',
                    audit_evidence_pack_drift_checked: 'Evidence pack sources checked for changes',
                    audit_evidence_pack_verified: 'Evidence pack verified',
                    audit_evidence_pack_returned: 'Evidence pack returned',
                    audit_evidence_pack_recorded_as_submitted: 'Evidence pack recorded as submitted',
                    audit_evidence_pack_transition_refused: 'Evidence pack status change refused',
                    audit_evidence_pack_exported: 'Evidence pack exported',
                    // Sprint 63.37.0 — the form-instance events the sample packages write.
                    instance_created: 'Form created',
                    instance_updated: 'Form updated',
                    consent_captured: 'Consent captured',
                    revoked: 'Revoked',
                    mitigation_status_changed: 'Mitigation status changed',
                    ai_advisor_record_summary_fetched: 'Forseti read the record summary',
                    regulatory_guidance_installed: 'Regulatory guidance installed',
                    regulatory_guidance_version_bumped: 'Regulatory guidance moved to a new version',
                    unknown: 'Action not recorded',
                },
                additional_filters: 'Additional audit filters',
                sample_marker: 'Sample',
                sample_marker_label: 'Sample data',
                sample_package_label: 'Sample data from the industry package {package}',
                actor_system: 'The platform',
                entity_none: 'No record type',
                summary_composed: '{action} · {entity}',
                // Sprint 63.29.0 (#585, AC3/AC6) — the server export and the integrity check.
                export_title: 'Unified Audit Trail export',
                export_generated: 'Generated: {date}',
                export_filters: 'Filters: {filters}',
                export_no_filters: 'Filters: none',
                export_count: 'Events: {count}',
                export_source_unreadable: 'Source {source} ({collection}): could not be read; its events are not in this file.',
                list_sources_unreadable: 'These audit sources could not be read, so their events are neither listed nor counted: {sources}.',
                list_page_beyond_window: 'Pages beyond the first {max} events cannot be listed. Narrow the filters, or export the filtered set.',
                list_page_beyond_window_no_export: 'Pages beyond the first {max} events cannot be listed. Narrow the filters.',
                list_window_note: 'Only the first {max} of {total} events can be paged through here. To see the rest, narrow the filters or export the filtered set.',
                list_window_note_no_export: 'Only the first {max} of {total} events can be paged through here. To see the rest, narrow the filters.',
                export_col_entity_id: 'Record ID',
                export_col_actor_id: 'Actor ID',
                export_menu_label: 'Export',
                integrity: {
                    column: 'Integrity',
                    check: 'Check integrity',
                    check_aria: 'Check the integrity of the audit events of record {id}',
                    checking: 'Checking…',
                    heading: 'Integrity check: {type}',
                    heading_untyped: 'Integrity check',
                    record_ref: 'Record ID: {id}',
                    case_ref: 'Case ID: {case}',
                    chain_verified: 'Hash chain verified, {verified} of {events} events.',
                    chain_broken: '{verified} of {events} events verified before the break.',
                    chain_legacy_unverifiable: '{count} events were written before the current link rule; their content matches the stored checksum, but their order in the chain cannot be verified.',
                    no_chain: 'No hash chain names this record.',
                    break_checksum: 'Break found in case {case} at sequence {sequence}: the stored checksum does not match the one recomputed from the event before it.',
                    break_gap: 'Break found in case {case}: sequence {sequence} is missing from the chain.',
                    break_withheld: 'A break was found in the chain of case {case} at an event you are not permitted to read, so its position is not shown. The chain cannot be relied on; a holder of the restricted grant can see where it breaks.',
                    no_break: 'No break found.',
                    not_chained: '{source}: {events} events. Not chained; consistency checked only.',
                    not_chained_inconsistent: '{source}: {events} events. Not chained; consistency checked only. {inconsistent} carry an unreadable or future date (first: {at}).',
                    proofs_link: 'Proofs and anchors are verified in the Integrity Center.',
                    failed: 'The integrity check could not be run.',
                    close: 'Close the integrity result',
                    // Gate E12.5 pass 1 (P2-3) — a source the check could not read.
                    unreadable: 'The check could not read {sources}; the result does not cover the events held there.',
                },
            },
            evidence_packs: {
                title: 'Audit Beweispäck',
                assemble_pack: 'Beweispak zesummestellen',
                framework: 'Referenzrahmen',
                select_framework: 'E Referenzrahme wielen...',
                framework_code_label: 'Framework-Code',
                entity_types: 'Entitéitstypen',
                select_entity_types: 'Entitéitstype wielen...',
                period: 'Period',
                period_start: 'Ufank vum Zäitraum',
                period_end: 'Enn vum Zäitraum',
                completeness: 'Vollstännegkeet',
                no_packs_found: 'Keng Beweispäck fonnt.',
                verify: 'Iwwerpréiwen',
                export_pack: 'Exportéieren',
                exporting: 'Gëtt exportéiert...',
                pack_assembled: 'Beweispaket zesummegestallt',
                pack_exported: 'Beweispaket exportéiert',
                aria_table: 'Audit-Beweispaketen',
                aria_export_pack: 'Paket exportéieren',
                export_format: 'Exportformat',
                export_in_progress: 'Export am Gaang...',
                status_draft: 'Entworf',
                status_assembled: 'Zesummegestallt',
                status_verified: 'Iwwerpréift',
                status_submitted: 'Agereecht',
                entity_type_labels: {
                    user: 'Benotzer',
                    case: 'Fall',
                    incident: 'Tëschefall',
                    assessment: 'Bewäertung',
                    vendor: 'Verkeefer',
                    consent: 'Zoustëmmung',
                    policy: 'Politik',
                    data_map: 'Daten Kaart',
                },
                template_id: 'Schabloun-ID',
                csv_headers: {
                    // Sprint 63.29.0 (#542) — the actor's name from the user directory, beside the id.
                    actor_name: 'Actor Name',
                    module: 'Modul',
                    action: 'Aktioun',
                    actor_id: 'Schauspiller',
                    actor_role: 'Actor Role',
                    entity_type: 'Entitéit Typ',
                    entity_id: 'Entitéit ID',
                    summary: 'Resumé',
                    timestamp: 'Zäitstempel',
                    record_type: 'Record Type',
                    framework_code: 'Kader',
                },
                // Sprint 63.25.0 — versioned packs: statuses, record types and the export header (spec G1, G2).
                status_partial: 'Partial',
                status_returned: 'Returned',
                status_recorded_as_submitted: 'Recorded as submitted',
                status_legacy_unversioned: 'Assembled before versioning',
                record_type_labels: {
                    consent: 'Consent',
                    data_map: 'Data Map',
                    assessment: 'Assessment',
                    vendor: 'Vendor',
                    incident: 'Incident',
                    case: 'Case',
                    policy: 'Policy',
                    deletion: 'Deletion',
                    monitoring: 'Monitoring',
                    forms: 'Forms',
                    task: 'Task',
                    access: 'Access',
                    user: 'User',
                    platform: 'Platform',
                    evidence_pack_lifecycle: 'Evidence pack lifecycle',
                },
                framework_none: 'no framework filter',
                export_header_pack: 'Pack {pack_id} — version {version}',
                export_header_name: 'Name: {name}',
                export_header_manifest: 'Manifest SHA-256: {sha}',
                export_header_period_tz: 'Period: {start} to {end} ({timezone})',
                export_header_untagged_included: 'Events not tagged to a framework: included',
                export_header_untagged_excluded: 'Events not tagged to a framework: not included',
                export_header_record_types: 'Record types: {types}',
                export_header_modules: 'Modules: {names}',
                export_header_all_modules: 'Modules: all',
                export_header_counts: 'Events: {total} ({tagged} tagged to a framework, {untagged} untagged)',
                export_header_coverage: 'Record-type coverage: {covered} of {denominator} selected record types have at least one event',
                export_header_coverage_missing: 'Selected record types with no event: {types}',
                export_header_unresolved: 'References not resolved: {count}',
                export_header_unresolved_unchecked: 'References not resolved: not checked',
                export_header_review: 'Review: {decision} by {reviewer} on {date}',
                export_header_review_note: 'Review note: {note}',
                export_header_not_reviewed: 'Review: not reviewed',
                export_header_submission: 'Recorded as submitted: to {recipient} on {sent_on} via {channel}, reference {reference}',
                export_header_submission_disclaimer: 'This records that the pack was sent. It is not proof that the recipient received or accepted it.',
                export_header_no_submission: 'Submission: none recorded',
                export_header_source_collected: 'Source {source} ({collection}): {count} events collected',
                export_header_source_failed: 'Source {source} ({collection}): could not be read ({error})',
                export_header_sources_not_read: 'Sources not read: {count} (not selected)',
                export_header_excluded_by_rule: 'Excluded by rule: record-summary read events ({count})',
                export_header_excluded_by_rule_uncounted: 'Excluded by rule: record-summary read events (not counted)',
                export_header_excluded_by_rule_not_applied: 'Excluded by rule: record-summary read events (rule not applied: this version was assembled before the rule existed)',
                value_none: 'none',
                // Gate E12.5 remediation 1 (P1-2): an event whose producer wrote no sentence.
                summary_composed: '{action} · {record_type}',
                export_doc_title: 'Evidence Pack {pack_id}',
                export_header_framework: 'Beweispak — Rahmewierk: {framework}',
                export_header_period: 'Period: {start} bis {end}',
                export_header_completeness: 'Vollständegkeet: {score}%',
                export_header_status: 'Status: {status}',
                col_entity_type: 'Entity Type',
                events_collected: 'events collected',
                missing_label: 'Missing',
                no_events_in_pack: 'No audit events found for this evidence pack.',
                // Sprint 63.25.0 (Slices C3, C4, F, H) — the versioned audit evidence pack page.
                page_description: 'Collect the audit events recorded in a period into a versioned dossier. Each version is a snapshot: its count, its event list, its coverage and every export read the same events.',
                cross_ref_audit: "Audit evidence packs collect the audit events recorded in a period; framework evidence packs gather one framework's evidence items for a regulator.",
                cross_ref_open_framework: 'Go to framework evidence packs',
                cross_ref_framework: "Framework evidence packs gather one framework's evidence items for a regulator; audit evidence packs collect the audit events recorded in a period.",
                cross_ref_open_audit: 'Go to audit evidence packs',
                filter_status: 'Status',
                filter_all_statuses: 'All statuses',
                col_name: 'Name',
                col_events: 'Events',
                col_version: 'Version',
                col_coverage: 'Record-type coverage',
                col_assembled: 'Assembled',
                coverage_short: '{covered} of {denominator}',
                not_a_member_count: 'Not a list of events',
                show_details: 'Show details for {name}',
                list_load_failed: 'The evidence pack list could not be read.',
                list_retry_subject: 'the evidence pack list',
                list_pagination: 'Pages of evidence packs',
                field_name: 'Name',
                name_hint: 'A name people will recognise, for example "Q3 inspection".',
                framework_hint: 'Optional. Only frameworks installed for your organisation are listed.',
                no_framework_option: 'No framework filter',
                include_untagged: 'Include events not tagged to any framework',
                include_untagged_hint: 'Most audit events carry no framework tag. Leave this ticked unless you want only the events tagged to the chosen framework. The pack shows how many of each it holds either way.',
                include_untagged_no_effect: 'With no framework chosen, every event in the period is included and this box has no effect.',
                period_hint: "Dates are read in your organisation's timezone, and the end date is included in full.",
                record_types_legend: 'Record types',
                record_types_hint: 'Choose at least one.',
                modules_legend: 'Modules',
                modules_hint: 'Optional. Leave every box unticked to include all modules.',
                error_name_required: 'Enter a name.',
                error_period_start_required: 'Choose a start date.',
                error_period_end_required: 'Choose an end date.',
                error_period_order: 'The end date must be on or after the start date.',
                error_record_types_required: 'Choose at least one record type.',
                assembling: 'Assembling…',
                detail_loading: 'Loading the pack…',
                detail_load_failed: 'This pack could not be read.',
                detail_retry_subject: 'this evidence pack',
                scope_heading: 'Scope and counts',
                meta_period: 'Period',
                meta_untagged: 'Events not tagged to a framework',
                meta_record_types: 'Record types',
                meta_modules: 'Modules',
                meta_counts: 'Events',
                meta_manifest: 'Manifest SHA-256',
                period_value: '{start} to {end} ({timezone})',
                untagged_included: 'Included',
                untagged_excluded: 'Not included',
                all_modules: 'All modules',
                counts_value: '{total} ({tagged} tagged to a framework, {untagged} untagged)',
                not_checked: 'Not checked',
                legacy_notice: 'This pack was assembled before versioning. Its stored count is not a list of events, so it cannot be reviewed or exported. Re-assemble it to create a reviewable version.',
                version_select: 'Version shown',
                version_option: 'Version {version}',
                reassemble: 'Re-assemble',
                reassembling: 'Re-assembling…',
                reassembled: 'Version {version} assembled.',
                drift_check: 'Check for changes since assembly',
                drift_checking: 'Checking…',
                drift_none: 'Nothing has changed since version {version} was assembled.',
                drift_new: '{count} events recorded in this period since assembly. Re-assemble to include them.',
                drift_gone: '{count} events in this version no longer exist at their source.',
                drift_changed: '{count} events in this version have changed at their source.',
                drift_failed_sources: 'Sources that could not be read during the check: {sources}.',
                drift_failed: 'The check for changes could not be run.',
                coverage_heading: 'Record-type coverage',
                coverage_sentence: '{covered} of {denominator} selected record types have at least one event.',
                coverage_missing: 'No event for: {types}.',
                coverage_not: 'This shows which selected record types have events. It does not measure whether the evidence is sufficient, or whether the organisation complies with any law.',
                history_heading: 'Version history',
                col_assembled_by: 'Assembled by',
                col_assembled_at: 'Assembled on',
                col_reviewer: 'Reviewer',
                col_decision: 'Decision',
                col_note: 'Note',
                col_reviewed_at: 'Reviewed on',
                col_submission: 'Recorded as submitted',
                decision_verified: 'Verified',
                decision_returned: 'Returned',
                not_reviewed: 'Not reviewed',
                no_submission: 'None recorded',
                submission_line: 'To {recipient} on {sent_on} via {channel}',
                items_heading: 'Events in version {version}',
                items_loading: 'Loading the events of this version…',
                items_empty_scope: 'It was assembled for {period}; framework: {framework}; record types: {types}; modules: {module_list}.',
                items_denied: 'You do not have permission to read the events of this pack. Reading them needs the "Read audit events" permission (audit:events:read).',
                items_failed: 'The events of this version could not be read.',
                items_retry_subject: 'the events of this version',
                items_populated: '{count} events in this version.',
                partial_banner: 'Some sources could not be read when this version was assembled, so it is partial and cannot be verified. Sources not read: {sources}.',
                partial_reassemble_hint: 'A person with permission to export audit events can re-assemble it.',
                partial_no_members: 'No events were collected from the sources that answered.',
                source_item: '{source} ({collection})',
                col_record_type: 'Record type',
                col_actor: 'Actor',
                col_timestamp: 'Time',
                col_module: 'Module',
                col_action: 'Action',
                col_summary: 'Summary',
                items_pagination: 'Pages of events',
                review: 'Review',
                review_title: 'Review version {version}',
                review_decision: 'Decision',
                review_option_verified: 'Verify this version',
                review_option_returned: 'Return it to the person who assembled it',
                review_note: 'Note',
                review_note_hint: 'Required when you return a version: say what needs to change.',
                error_decision_required: 'Choose a decision.',
                error_note_required: 'Enter a note saying why the version is returned.',
                review_submit: 'Record decision',
                review_recorded: 'Your decision on version {version} was recorded.',
                review_own_version: 'You assembled this version, so another person must review it.',
                record_submission: 'Record as submitted',
                submission_title: 'Record version {version} as submitted',
                submission_disclaimer: 'This records that the pack was sent. It is not proof that the recipient received or accepted it.',
                submission_recipient: 'Recipient',
                submission_sent_on: 'Sent on',
                submission_channel: 'Channel',
                submission_channel_hint: 'How it was sent, for example by email or by hand.',
                submission_reference: 'Reference',
                submission_reference_hint: 'Optional. For example a letter or ticket number.',
                submission_proof: 'Proof of sending',
                submission_proof_hint: 'Optional. A file showing the pack was sent, such as a dispatch receipt.',
                error_recipient_required: 'Enter the recipient.',
                error_sent_on_required: 'Choose the date it was sent.',
                error_channel_required: 'Enter the channel.',
                submission_recorded: 'Version {version} was recorded as submitted.',
                export_version: 'Export version {version}',
                // Sprint 63.25.0 (spec E3) — references that did not resolve at assembly.
                reference_not_found: 'Reference not found',
                reference_not_found_record: 'The source event names no record.',
                reference_not_found_actor: 'The person who acted is not in the user directory.',
                unresolved_line: 'References not resolved: {count}',
                unresolved_not_checked: 'References not resolved: not checked',
                unresolved_breakdown: '{records} with no record named; {actors} whose actor is not in the user directory.',
                unresolved_breakdown_actors_unchecked: '{records} with no record named; actors not checked.',
                server_error_reviewer_is_assembler: 'You assembled this version, so you cannot review it. Another person with the review permission must record the decision.',
                server_error_evidence_pack_version_not_found: 'This version of the pack was not found. The pack may have been re-assembled or removed.',
                server_error_submission_proof_not_owned: 'The proof file is not held under this version. Upload it to this version first.',
                server_error_evidence_pack_legacy: 'This pack was assembled before versioning. Re-assemble it to create a version that can be checked.',
                server_error_evidence_pack_too_large: 'More events match this scope than one pack can hold. Choose a shorter period or a narrower filter.',
                server_error_evidence_pack_definition_invalid: "This pack's stored definition cannot be assembled again. Create a new pack.",
            },
            audit_periods: {
                title: 'Auditperioden',
                create_period: 'Auditperiod erstellen',
                framework_code_label: 'Framework-Code',
                select_framework: 'E Referenzrahme wielen...',
                period_label: 'Periodelabel',
                period_start: 'Ufank vum Zäitraum',
                period_end: 'Enn vum Zäitraum',
                start: 'Start',
                end: 'Enn',
                framework: 'Kader',
                retention_days: 'Opbewahrungsdeeg',
                section_period_dates: 'Zäitraum-Datumen',
                no_periods_found: 'Keng Auditperioden fonnt.',
                period_created: 'Audit-Period erstallt',
                aria_table: 'Audit-Perioden',
                suggested_boundaries: 'Virgeschloe Grenzen',
                suggested_boundaries_info: 'Virgeschloe Periodegrenzen gi vum regulatoreschen Berichtszyklus vun all Rahmewierk ofgeleet. Passt se no Bedarf fir Är Organisatioun un.',
                reporting_cycle: 'Berichtszyklus',
                suggested_start: 'Virgeschloen Ufank',
                suggested_end: 'Virgeschloent Enn',
                suggested_next: 'Nächst Period proposéiert',
                cycle_monthly: 'Mount',
                cycle_quarterly: 'Trimester',
                cycle_semi_annual: 'Semi-jährlechen',
                cycle_annual: 'Jährlech',
                cycle_custom: 'Benotzerdefinéiert',
                period_deleted: 'Audit Period geläscht',
                list_truncated: 'Only {shown} of {total} audit periods could be read, so this list is incomplete.',
            },
        },
        create_case: {
            customer_ref: 'Customer Referenz',
            country: 'Land / Jurisdiktioun',
            preferred_channel: 'Bevorzugten Kanal',
            channels: {
                email: 'E-Mail',
                postal: 'Postalësch',
                phone: 'Telefon',
                portal: 'Portal',
            },
            source: 'Quell',
            sources: {
                admin_manual: 'Admin Handbuch',
                public_form: 'Ëffentlech Form',
                cmp_gateway: 'CMP Gateway',
                aesirx_cmp: 'AesirX CMP',
                phone_received: 'Telefon kritt',
                postal_received: 'Post kritt',
                email_received: 'E-Mail kritt',
                in_person: 'Perséinlech',
                legacy_import: 'Legacy Import',
            },
            required_fields: 'Ufro type, Numm, an E-Mail are required',
            create_error: 'Konnt net create case',
            // fix-sprint #447 W9 (owner ruling OR3)
            third_party_involved: 'A processor or third party must act on this request',
            third_party_involved_hint: 'The installed deadline records may state a different period when a processor or third party must act. The case is dated by the record that matches. It can be changed on the case later, which dates the case again from receipt.',
            request_type_not_offered: 'This kind of request is not a right under the frameworks your organisation has installed, so it cannot be recorded as a new request.',
            request_type_no_installed_clock: 'No installed framework states a period for this kind of request. The case will be dated from your SLA target for it or, failing that, the platform default, which is not a legal period.',
            // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
            request_type_handled_in_legal_matters: 'This kind of request is received as a legal matter in Legal Matters. No deadline is set for it.',
            legal_matter_right: 'Which right it raises',
            legal_matter_right_optional_hint: 'Optional. If it is not yet known, leave it empty: a person records it on the legal matter.',
            legal_matter_right_not_known: 'Not yet known',
        },
        settings: {
            title: 'Astellungen',
            request_types: 'Ufro-Aarten',
            request_types_desc: "Konfiguréiert wéi eng Ufro-Aarten fir d'ëffentlech Agangsformulaire aktivéiert sinn.",
            notifications: 'Notifikatiounen',
            ticket_numbering: 'Ticket-Nummeréierung',
            ticket_format: 'Aktuellt Format',
            ticket_prefix: 'Präfix',
            ticket_prefix_note: 'net konfiguréierbar',
            sla_deadlines: 'SLA-Frist',
            sla_desc: 'Setzt déi maximal Äntwerttzäit (a Kalennerdeeg) fir all Ufro-Aart. Fäll, déi sech dem Schwellwäert näheren, weisen Warnungen un.',
            days: 'Deeg',
            warning_threshold: 'Warnschwellwäert',
            escalation_label: 'Automatesch Eskalatioun bei SLA-Verletztung aktivéieren',
            // fix-sprint #431 F1 — English until Phase 25 (R-57).
            escalation_hint: 'Runs the escalation ladder below. When off, no tier acts; warnings and breaches are still flagged.',
            warning_threshold_hint: 'A case no installed framework dates shows a warning at this share of its period and is flagged critical at 90%, or at this share if it is higher. A case an installed framework dates is warned at 75% and flagged critical at 90%.',
            // Gate E12.5 remediation 1 (fix-sprint #431) — escalation settings need sla:config:write.
            escalation_settings_need_write: 'Changing automatic escalation or the warning threshold needs the SLA configuration permission.',
            // Gate E12.5 remediation 2 — neither sla:config:write nor case:update: nothing here can be saved.
            sla_settings_read_only: 'You can view these settings but not change them: saving them needs the SLA configuration permission, or the case update permission for the deadlines alone.',
            sla_save_forbidden: 'The SLA settings were not saved: changing automatic escalation or the warning threshold needs the SLA configuration permission.',
            sla_save_invalid: 'The SLA settings were not saved: a deadline is set for something that is not a request type, or a value is out of range.',
            save_sla: 'SLA-Konfiguratioun späicheren',
            saving: 'Späicheren...',
            saved: 'Erfollegräich gespäichert',
            announce_loaded: 'Astellungen gelueden',
            email_on_creation: 'E-Mail-Notifikatioun bei Ticket-Erstellung',
            send_confirmation: 'Bestätegungsemail un den Ufroer schécken',
            users: 'Benotzer',
            roles: 'Rollen',
            api_keys: 'API Schlësselen',
            mfa: 'MFA',
            sessions: 'Sessiounen',
            webhooks: 'Webhooks',
            modules: 'Moduler',
            // Sprint 62.45.0 (AC17) — the organisation calendar section.
            calendar: {
                heading: 'Working days and holidays',
                effect_notice: 'Every working-day deadline in the platform counts only the working days below, skips these holidays and counts these make-up working days — rights requests and their pauses, incidents, obligations, enforcement limitation periods, dossier filings and supervisory reports among them. A deadline worked out after you save uses the new calendar; a due date already recorded on a record keeps its date.',
                no_list_notice: "The platform supplies no public-holiday list: enter your organisation's own days off, public holidays included.",
                working_days_legend: 'Working days of the week',
                working_days_hint: 'Working-day deadlines count only these days.',
                working_days_required: 'Select at least one working day.',
                day_0: 'Sunday',
                day_1: 'Monday',
                day_2: 'Tuesday',
                day_3: 'Wednesday',
                day_4: 'Thursday',
                day_5: 'Friday',
                day_6: 'Saturday',
                holidays_heading: 'Holidays',
                holidays_caption: 'Holidays entered: {count}',
                empty: 'No holidays entered: working-day deadlines skip only the days outside the working week.',
                add_legend: 'Add a holiday',
                date_label: 'Date',
                label_label: 'Label (optional)',
                label_hint: 'A short name for the day, for example why the office is closed.',
                add: 'Add date',
                remove: 'Remove',
                remove_aria: 'Remove {date}',
                col_date: 'Date',
                col_label: 'Label',
                col_actions: 'Actions',
                no_label: 'No label',
                date_required: 'Enter a date.',
                date_out_of_range: 'Enter a date between {min} and {max}.',
                duplicate: '{date} is already in the list.',
                limit_reached: 'At most {max} holidays can be entered. Remove one before adding another.',
                save: 'Save calendar',
                saved: 'Calendar saved. Working-day deadlines worked out from now on count these working days, skip these holidays and count these make-up working days.',
                save_error: 'The calendar was not saved. Your entries are still here.',
                conflict: 'Someone else saved the calendar after you opened it, so your changes were not saved. Reload it to see their version, then enter your changes again.',
                reload: 'Reload calendar',
                load_error: 'The calendar could not be read, so it is not shown and cannot be changed here.',
                retry: 'Try again',
                last_saved: 'Last saved {date}.',
                never_saved: 'This calendar has not been saved here yet.',
                read_only: 'You can view this calendar but not change it.',
                unsaved: 'Unsaved changes.',
                holiday_is_extra: '{date} is entered as a make-up working day. A date is either a holiday or a make-up working day.',
                extra_heading: 'Make-up working days',
                extra_intro: 'A day your organisation works although it falls outside the working week, for example a Saturday worked in exchange for a day off beside a public holiday. Every working-day deadline counts it as a working day.',
                extra_caption: 'Make-up working days entered: {count}',
                extra_empty: 'No make-up working days entered.',
                extra_add_legend: 'Add a make-up working day',
                extra_date_label: 'Date worked',
                extra_label_label: 'Label for the day worked (optional)',
                extra_label_hint: 'A short name for the day, for example the day off it makes up for.',
                extra_add: 'Add day worked',
                extra_remove_aria: 'Remove make-up working day {date}',
                extra_is_holiday: '{date} is entered as a holiday. A date is either a holiday or a make-up working day.',
                extra_limit_reached: 'At most {max} make-up working days can be entered. Remove one before adding another.',
                extra_is_working_day: '{date} is a {weekday}, which is already a working day. A make-up working day is a date outside the working days of the week.',
                extra_on_working_weekday: 'These make-up working days fall on a working day of the week, so they would change nothing: {dates}. Remove them, or remove that day from the working days.',
            },
            // fix-sprint #431 F1 — Settings › Escalation ladder. English until Phase 25 (R-57).
            escalation_ladder: {
                heading: 'Escalation ladder',
                intro: "When a rights request reaches a tier's share of its period, the background SLA check acts on it once: it notifies the case's assignee and the people and role the tier names, in the platform or by email, as far as each person's own notification preferences allow. A reassign tier also moves the case. The share is the time from receipt of the request to the due date the case shows. Paused cases and cases with no due date are not escalated.",
                off_notice: 'Automatic escalation is off, so no tier acts. Warnings and breaches are still flagged. Turn it on under SLA Deadlines.',
                on_notice: 'Automatic escalation is on. The background SLA check runs these tiers.',
                suggested_notice: 'Your organisation has not saved a ladder, so these suggested tiers run. Save them, or change them and save, to make them your own.',
                caption: 'Escalation tiers: {count}',
                col_threshold: 'Share of period',
                col_name: 'Name',
                col_action: 'Action',
                col_targets: 'Notifies',
                col_channels: 'Sent by',
                col_actions: 'Actions',
                action_notify: 'Notify',
                action_escalate_to_role: 'Escalate to a role',
                action_reassign: 'Reassign',
                channel_in_app: 'In the platform',
                channel_email: 'Email',
                target_assignee: 'The assignee',
                target_role: 'Holders of {role}',
                target_users: 'Named: {names}',
                reassign_to: 'Moves the case to: {target}',
                threshold_breach: '100% (due date passed)',
                inactive: 'Inactive',
                edit: 'Edit',
                edit_aria: 'Edit the tier at {pct}%',
                remove: 'Remove',
                remove_aria: 'Remove the tier at {pct}%',
                add: 'Add tier',
                add_legend: 'Add a tier',
                edit_legend: 'Edit the tier at {pct}%',
                threshold_label: 'Share of the period elapsed (%)',
                threshold_hint: 'A whole number from 1 to 100. At 100 the tier acts once the due date has passed.',
                name_label: 'Name (optional)',
                name_hint: "Shown in notifications and in the case's escalation history. Left blank, the tier is named by its share.",
                action_label: 'Action',
                action_hint: 'Every tier notifies the assignee and the people it names. Escalate to a role also notifies everyone holding the role. Reassign moves the case to the first active person named, or else to the first person holding the role, unless the case is already with one of them.',
                role_label: 'Role',
                role_none: 'No role',
                users_label: 'Named people',
                users_hint: 'For a reassign tier, the case goes to the first active person in this list.',
                user_select_label: 'Person to add',
                user_placeholder: 'Choose a person',
                user_add: 'Add person',
                user_remove_aria: 'Remove {name}',
                users_empty: 'No one named.',
                users_retry_subject: 'the list of people',
                channels_legend: 'Send by',
                channels_hint: 'Each person still receives only what their own notification preferences allow.',
                active_label: 'Active',
                apply: 'Apply to the ladder',
                cancel: 'Cancel',
                save: 'Save escalation ladder',
                saved: 'Escalation ladder saved. The background SLA check runs it from its next sweep.',
                unsaved: 'Unsaved changes.',
                save_error: 'The escalation ladder was not saved. Your changes are kept.',
                conflict: 'Someone else saved the escalation ladder after you opened this page, so your changes were not saved. Reload the ladder to see that version, then make your changes again.',
                reload: 'Reload ladder',
                load_error: 'The escalation ladder could not be read, so it is not shown and cannot be changed here.',
                retry: 'Try again',
                read_only: 'You can view the escalation ladder but not change it.',
                empty: 'No tiers. Add at least one tier, or turn automatic escalation off under SLA Deadlines.',
                last_saved: 'Last saved {date}.',
                err_ladder_empty: 'Add at least one tier. To stop automatic escalation, turn it off under SLA Deadlines.',
                err_too_many_tiers: 'A ladder holds at most {max} tiers.',
                err_invalid_value: 'Tier {n}: a value is not valid.',
                err_threshold_out_of_range: 'Tier {n}: the share must be a whole number from 1 to 100.',
                err_threshold_duplicate: 'Tier {n}: another tier already acts at this share.',
                err_tier_id_duplicate: 'Tier {n}: this tier appears twice.',
                err_channels_required: 'Tier {n}: choose at least one way to send.',
                err_target_role_required: 'Tier {n}: choose the role to escalate to.',
                err_target_role_unknown: 'Tier {n}: the role is not an active role of your organisation.',
                err_reassign_target_required: 'Tier {n}: name a person or a role to reassign the case to.',
                err_target_user_unknown: 'Tier {n}: {name} is not an active user of your organisation.',
            },
            deadline_labels: {
                access: 'Zougang (DSAR)',
                deletion: 'Läschung',
                correction: 'Korrektur',
                portability: 'Portabilitéit',
                objection: 'Widdersproch',
                restriction: 'Aschränkung',
                consent_withdrawal: 'Réckzuch vun der Aweisung',
                protective_measures: 'Schutzmoossnamen',
                processing_information: 'Information about Processing',
                consent_decision: 'Consent Decision',
                complaint_or_compensation: 'Complaint, Denunciation, Lawsuit or Damages Claim',
            },
            sla_source_platform_default: 'Not saved: cases of this type that no installed clock dates are dated from the platform default of {days} days, which is not a legal period. Save a value to set your own target.',
            sla_source_tenant_target: 'Your SLA target: it dates cases of this type that no installed clock in force dates.',
            request_type_enabled_not_offered: 'Enabled, but not a right under the frameworks this organisation has installed: the public form does not offer it. Untick it to clear the setting.',
            request_types_from_frameworks: 'The request types listed are the rights the installed frameworks grant.',
        },
        section_assignments: {
            title: 'Sektiounszouweisungen',
            assign_section: 'Sektioun zouweisen',
            section_ref: 'Sektiounsreferenz',
            section_label: 'Sektiounsbezeechnung',
            department_id: 'Departement',
            assignee_id: 'Zougewisene Persoun',
            mandatory: 'Obligatoresch',
            assign: 'Zouweisen',
            no_assignments: 'Keng Sektiounszouweisungen',
            status: 'Status',
            completion: 'Ofschloss',
            mark_complete: 'Ofschléissen',
        },
        consolidation: {
            title: 'Konsolidéierungsfortschrëtt',
            add_child: 'Unteraufgab dobäisetzen',
            child_task_id: 'Unteraufgab-ID',
            department_id: 'Departement',
            link: 'Verknëppung',
            overall_completion: 'Gesamtofschloss',
            departments_complete: 'Departementer ofgeschloss',
            overdue: 'Iwwerfälleg',
            ready: 'Prett',
            not_ready: 'Net prett',
            master_status: 'Gesamtstatus',
            department_breakdown: 'Departementsuebléck',
            department: 'Departement',
            status: 'Status',
            completion: 'Ofschloss',
            due_date: 'Fällegkeetsdatum',
            overdue_flag: 'Iwwerfälleg',
            child_tasks: 'Unteraufgaben',
            task: 'Aufgab',
        },
        conflict_detection: {
            title: 'Konflikterkennung',
            unresolved: 'ongeléist',
            no_conflicts: 'Keng Konflikter festgestallt',
            section: 'Sektioun',
            field: 'Feld',
            dept_a: 'Departement A',
            value_a: 'Wäert A',
            dept_b: 'Departement B',
            value_b: 'Wäert B',
            severity: 'Schwéieregrad',
            status: 'Status',
            resolved: 'Geléist',
            open: 'Oppen',
            resolve: 'Léisen',
        },
        org_maturity: {
            title: 'Organisatoresch Reifheet',
            departments: 'Abteilungen',
            department_hierarchy: 'Abteilungshierarchie',
            create_department: 'Abteilung erstellen',
            department_name: 'Abteilungsname',
            department_code: 'Code',
            level: 'Niveau',
            division: 'Beräich',
            department: 'Abteilung',
            team: 'Equipe',
            parent_department: 'Iwwergeuerdent Abteilung',
            head_user: 'Abteilungsleeder',
            no_departments: 'Keng Abteilunge konfiguréiert.',
            user_mappings: 'Benotzerzuuerdnungen',
            map_user: 'Benotzer Zuuerdnen',
            user_id: 'Benotzer-ID',
            role: 'Roll',
            no_users: 'Keng Benotzer dëser Abteilung zougewiesen.',
            remove: 'Ewechhuelen',
            framework_assignments: 'Kaderwierk-Zouweisungen',
            assign_framework: 'Kaderwierk Zouweisen',
            entity_id: 'Entitéits-ID',
            entity_type: 'Entitéitstyp',
            framework_id: 'Kaderwierk-ID',
            framework_code: 'Kaderwierks-Code',
            no_assignments: 'Keng Kaderwierk-Zouweisungen.',
            compliance_rollup: 'Compliance-Zesummefaassung',
            entity_name: 'Entitéit',
            total_assessments: 'Bewertungen insgesamt',
            completed: 'Ofgeschloss',
            completion_pct: 'Ofschloss %',
            open_gaps: 'Oppe Lücken',
            critical_gaps: 'Kritesch',
            compliance_score: 'Punktzuel',
            no_rollup: 'Keng Compliance-Zesummefaassungsdaten verfügbar.',
            entity_summary: 'Compliance-Zesummefaassung vun der Entitéit',
            frameworks_assigned: 'Kaderwierker',
            overall_score: 'Gesamtpunktzuel',
            load_departments_error: 'Feeler beim Lueden vun den Abteilungen',
            load_users_error: 'Feeler beim Lueden vun de Benotzer',
            department_created: 'Departement erstallt',
            department_created_announce: 'Departement erstallt',
            create_department_error: 'Feeler beim Erstellen vun der Abteilung',
            user_mapped: 'Benotzer zougewisen',
            user_mapped_announce: 'Benotzer dem Departement zougewisen',
            map_user_error: 'Feeler beim Zouweisen vum Benotzer',
            user_removed: 'Benotzer ewechgeholl',
            load_rollup_error: 'Feeler beim Lueden vun der Zesummefaassung',
            load_assignments_error: 'Feeler beim Lueden vun den Zouweisungen',
            framework_assigned: 'Framework zougewisen',
            framework_assigned_announce: 'Framework der Entitéit zougewisen',
            assign_framework_error: 'Feeler beim Zouweisen vum Kader',
            assignment_removed: 'Zouweissung ewechgeholl',
            enter_entity_id_placeholder: 'Entitéits-ID aginn fir Zouweisungen ze gesinn',
            entity_type_organization: 'Organisatioun',
            entity_type_department: 'Departement',
        },
        onboarding_maturity: {
            title: 'Onboarding Maturitéit',
            quick_starts: 'Quick Starts',
            progress: 'Onboarding Fortschrëtter',
            personas: 'Persona Weeër',
            persona_onboarding: 'Persona Onboarding',
            quick_start_scenarios: 'Quick-Start Szenarien',
            start: 'Starten',
            restart: 'Restart',
            estimated_minutes: 'Geschätzte Minutten',
            steps: 'Schrëtt',
            scenario: 'Szenario',
            no_scenarios: 'Keng Quick-Start Szenarie verfügbar.',
            persona_id: 'Perséinlech ID',
            persona_label: 'Persona',
            role_description: 'Roll Beschreiwung',
            primary_modules: 'Primär Moduler',
            recommended_tours: 'Recommandéiert Touren',
            sample_data_modules: 'Prouf Daten Moduler',
            quick_start_scenario: 'Quick-Start Szenario',
            no_personas: 'Keng Persona Weeër verfügbar.',
            framework_setup: 'Regulatoresch Kaderen',
            framework_code: 'Kader Code',
            framework_name: 'Kader Numm',
            related_modules: 'Zesummenhang Moduler',
            persona_relevance: 'Relevant Personnagen',
            no_frameworks: 'Keng Kader Setup Optiounen verfügbar.',
            tier_config: 'Tier Modul Configuratioun',
            tier: 'Tier',
            available_modules: 'Verfügbar Moduler',
            module_count: 'Modul Zuel',
            overall_completion: 'Allgemeng Ofschloss',
            wizard_completed: 'Wizard fäerdeg',
            tours_completed: 'Tours ofgeschloss',
            quick_starts_completed: 'Quick Starts Ofgeschloss',
            selected_frameworks: 'Ausgewielten Kaderen',
            no_progress: 'Keng Onboarding Fortschrëtter Daten verfügbar.',
            detected_persona: 'Detektéiert Persona',
            current_tier: 'Aktuell Tier',
            completion_pct: 'Ofschloss',
            progress_page: {
                loaded: 'Fortschrëtt gelueden',
                no_progress: 'Keng Onboarding-Fortschrëttsdaten disponibel.',
                overall: 'Gesamtfortschrëtt',
                detected_persona: 'Erkannte Persona',
                tier: 'Stufe',
                modules: 'Moduler',
                wizard: 'Assistent',
                completed: 'Ofgeschloss',
                pending: 'Ausstehend',
                tours: 'Rundgäng',
                quick_starts: 'Schnellstarten',
                frameworks: 'Gewielt Rahmewerker',
                no_frameworks: 'Keng Rahmewerker gewielt.',
                go_to_wizard: 'Astellungsassistent ofschléissen',
                framework_selection: 'Rahmewerkauswiel',
                framework_toggle: 'Rahmewerker fir Äert Compliance-Programm aktivéieren oder deaktivéieren.',
                framework_updated: 'Rahmewerkauswiel aktualiséiert.',
                view_tours: 'Rundgäng kucken',
                view_quick_starts: 'Schnellstarten kucken',
                // English until Phase 25 (R-57).
                // fix-sprint #447 W5 — no progress record yet; reading the page creates none.
                not_started: 'Not started',
                not_started_body: 'No onboarding progress record exists for you yet. Opening this page does not create one: it is created when you complete the Setup Wizard. The figures below count the tours and quick starts you have already completed.',
            },
            persona_page: {
                description: 'Wielt eng Persona fir empfohlene Moduler, Rundgäng a Schnellstartszenarie ze gesinn.',
                no_personas: 'Keng Persona-Weeër disponibel.',
                modules: 'Moduler',
                tours: 'Rundgäng',
                quick_start: 'Schnellstart',
                details: 'Detailer',
                primary_modules: 'Primär Moduler',
                recommended_tours: 'Empfohlene Rundgäng',
                framework_options: 'Regulatoresch Kaderen',
                no_frameworks: 'Keng Rahmewerk-Astellungsoptiounen disponibel.',
                no_frameworks_explain: 'Fir Är Organisatioun sinn nach keng regulatoresch Kaderen installéiert, dofir gëtt et hei näischt anzestellen.',
                no_frameworks_action: "Installéiert e Maartpak fir d'Kaderen dobäizesetzen déi fir Iech gëllen",
                start_tour: 'Start',
                no_tours: 'Keng Rundgäng disponibel.',
                help_center: 'Hëllefszentrum',
                no_articles: 'Keng Artikelen fonnt.',
                primary: 'Primär',
                framework_code: 'Rahmewerkkode',
                framework_label: 'Bezeechnung',
                region: 'Regioun',
                category: 'Kategorie',
                enabled: 'Aktivéiert',
                disabled: 'Deaktivéiert',
                tier_modules: 'Stufenmodulkonfiguratioun',
                tiers_loaded: 'Stufenkonfiguratiounen gelueden',
                no_tier_config: 'Keng Stufenkonfiguratioun disponibel.',
                view_tours: 'Modulrundgäng kucken',
                view_quick_start: 'Schnellstart ufänken',
                toggle_framework: 'Rahmewerk ëmschalten',
                framework_updated: 'Rahmewerkauswiel aktualiséiert.',
                tier_desc_light: 'Basis Dateschutz-Essentielen — PDPL-Compliance-Grondlagen',
                tier_desc_standard: 'Komplett Compliance-Suite — all Moduler mat Aarbechtsofleef',
                tier_desc_enterprise: 'Erweidert Governance an Automatisatioun — Multi-Entitéit, Gruppeberichter',
                persona_highlight: 'Är Moduler',
                aria_go_to_module: '{name} - gitt op de Modul',
                aria_start_tour: 'Start {title}',
                aria_quick_start: 'Schnellstart: {title}',
                aria_help_article: 'Hëllef Artikel: {title}',
                aria_view_framework: '{label}{primary} - kuckt Kader Detailer',
                aria_primary_for_persona: '{name} (primär fir dës Persona)',
                primary_for_persona_suffix: ' (primär fir dës Persona)',
                aria_view_in_persona_paths: '{name} - Vue an Persona Weeër',
                progress_of_total: '{completed} vun {total} ofgeschloss',
            },
            // Sprint 63.28.0 — Persona Paths revised. English copy until the R-57 deferral (sprint 57.7.0).
            persona_paths: {
                page_description: 'Find your role among the personas your organisation can use. The roles your installed frameworks name come first. Choose one to see the frameworks that name it, the registers it works in and its quick start.',
                recommended_heading: 'Recommended for you',
                your_persona: 'Your persona',
                source_chosen: 'Chosen by you',
                source_detected: 'Detected automatically',
                source_detected_role: 'Detected from your user role',
                source_detected_default: 'Suggested as a starting point, because nothing else matched',
                chosen_unavailable: 'The persona you chose is no longer available to your organisation, because the market pack it needs was removed. The detected persona is shown instead.',
                recommended_none_ranked: 'No installed framework names a role yet, so only your persona is shown.',
                recommended_failed: 'Your persona could not be read.',
                recommended_loading: 'Reading your persona…',
                recommended_ranked: 'Named by your installed frameworks',
                toolbar_label: 'Filter roles',
                search_label: 'Search roles',
                search_placeholder: 'Role, description, sector, framework name or ID',
                family_label: 'Role family',
                family_all: 'All',
                sector_label: 'Sector',
                sector_placeholder: 'All sectors',
                market_label: 'Market',
                market_all: 'All markets',
                named_toggle: 'Named by my installed frameworks',
                sort_label: 'Sort by',
                sort_relevance: 'Relevance',
                sort_name: 'Name',
                sort_id: 'Persona ID',
                showing: 'Showing {shown} of {total} roles',
                clear_filters: 'Clear filters',
                list_label: 'Roles by family',
                list_heading: 'All roles',
                list_heading_named: 'Roles named by your installed frameworks',
                group_count_one: '1 role',
                group_count: '{count} roles',
                relevance_named_one: 'Named by 1 installed framework',
                relevance_named: 'Named by {n} installed frameworks',
                relevance_legacy_only: 'Only legacy or draft frameworks installed',
                selected: 'selected',
                load_failed: 'The list of roles could not be loaded.',
                reload: 'Reload',
                none_available_title: 'No roles are available to your organisation',
                none_available_description: 'Install a market pack to make its roles available.',
                no_match_title: 'No role matches these filters',
                no_match_description: 'Change or clear the filters to see more roles.',
                no_match_outside_one: '1 role matches outside your installed frameworks.',
                no_match_outside: '{count} roles match outside your installed frameworks.',
                hidden_matches_one: '1 more matches outside your installed frameworks.',
                hidden_matches: '{count} more match outside your installed frameworks.',
                show_all: 'Show all',
                notice_unavailable: '{name} ({id}) needs a market pack for {market}, which your organisation has not installed.',
                notice_unknown: 'No persona has the ID "{id}", so nothing was opened.',
                notice_unknown_use_case: 'No use case has the ID "{id}", so nothing was opened.',
                detail_label: 'Role details',
                detail_back: 'Back to the list of roles',
                detail_loading: 'Loading this role…',
                detail_failed: 'The details of this role could not be loaded.',
                detail_market: 'Market',
                detail_sectors: 'Sectors',
                detail_family: 'Role family',
                detail_persona_id: 'Persona ID',
                frameworks_heading: 'Frameworks for this role',
                frameworks_description: 'Read from the regulatory packs: every framework that names this role, as primary or supporting.',
                frameworks_installed: 'Installed',
                frameworks_not_installed: 'Not installed',
                frameworks_not_installed_count: 'Not installed ({count})',
                frameworks_none_installed: 'None of your installed frameworks names this role.',
                frameworks_only_retired_installed: 'Only retired or draft instruments are installed.',
                frameworks_none_not_installed: 'Every framework that names this role is installed.',
                frameworks_retired: 'Retired and draft instruments ({count})',
                role_primary: 'Primary',
                role_supporting: 'Supporting',
                status_active: 'Active',
                status_upcoming: 'Upcoming, in force from {date}',
                status_upcoming_undated: 'Upcoming',
                status_legacy: 'Legacy',
                status_draft: 'Draft',
                status_unknown: 'Status not stated',
                status_temporary_active: 'Temporarily in force',
                find_in_catalog: 'Find in the Market Packs Catalog',
                find_in_catalog_aria: 'Find in the Market Packs Catalog: {name}',
                basis_label: 'Basis: {article}',
                basis_link_aria: 'Open {article} of {name} in the Source tab',
                attached_through: 'Attached through {parent}',
                second_read_pending: 'Second reading pending',
                basis_missing: 'No article recorded',
                destinations_heading: 'Where the work happens',
                destinations_description: 'The registers this role works in, each marked by whether your tier includes it.',
                in_tier: 'In your tier',
                not_in_tier: 'Not in your tier',
                tier_checking: 'Checking your tier…',
                tier_unchecked: 'Your tier could not be checked, so no register is marked.',
                tier_not_permitted: 'Your user role cannot see which modules your tier includes, so no register is marked.',
                destination_no_access: 'Your user role cannot open this register',
                quick_start_heading: 'Your quick start',
                quick_start_none: 'No quick start has been written for this role yet.',
                quick_start_not_listed: 'The quick start "{id}" is not in your list, so nothing is highlighted.',
                tours_heading: 'Guided tours',
                tour_title: '{name} tour',
                start_tour: 'Start',
                start_tour_aria: 'Start: {title}',
                starting: 'Starting…',
                no_tours: 'No tours for this role.',
                help_heading: 'Help articles',
                help_none: "No help articles for this role's modules.",
                use_cases_heading: 'Use cases',
                use_cases_none: 'No validated use case names this role as its primary persona.',
                about_link: 'About this persona',
                save_action: 'Use as my onboarding persona',
                save_action_aria: 'Use as my onboarding persona: {name}',
                saving: 'Saving…',
                save_success: '{name} is now your onboarding persona.',
                already_yours: 'This is your onboarding persona.',
                profile_label: 'Onboarding persona',
                change_in_persona_paths: 'Change it in Persona Paths',
            },
            // Sprint 63.28.0 (B8) — the shared persona select. English copy until the R-57 deferral (sprint 57.7.0).
            persona_select: {
                placeholder: 'Choose a persona',
                no_options: 'No persona matches',
                checking: 'Checking which personas your organisation can use…',
                availability_failed: 'Which personas your organisation can use could not be checked, so every persona is listed.',
                retry: 'Try again',
            },
            quick_starts_page: {
                description: 'Übt üblech Aarbechtsofleef mat guidéierte Schnellstartszenarie.',
                no_scenarios: 'Keng Schnellstartszenarie disponibel.',
                started: 'Schnellstart gestart',
                status_completed: 'Ofgeschloss',
                status_in_progress: 'Am Gaang',
                status_not_started: 'Net gestart',
                minutes: 'Min.',
                steps: 'Schrëtt',
                step: 'Schrëtt',
                restart: 'Nei starten',
                start: 'Starten',
                go_to_module: 'Zum Modul goen',
                next_step: 'Nächste Schrëtt',
                complete: 'Ofschléissen',
                reset: 'Zrécksetzen',
                step_of: 'vun',
                action_navigate: 'Navigéieren',
                action_form_fill: 'Formulaire ausfëllen',
                action_review: 'Iwwerpréifen',
                action_confirm: 'Bestätegen',
                step_changed: 'Schrëtt {current} vun {total}: {title}',
                completed_message: 'Schnellstart erfollegräich ofgeschloss!',
                reset_message: 'De Schnellstart gouf zréckgesat.',
            },
            persona_p_vn_01_label: 'Entreprise-Datebschutzbeoptraagten',
            persona_p_vn_01_description: 'Data Protection Officer running the enterprise-wide personal data protection programme: impact assessments and their filings, rights requests, breach decisions and liaison with the authority',
            persona_p_vn_02_label: 'Departementsverantwortlechen',
            persona_p_vn_02_description: "Departementsleeder, deen fir d'Konformitéitsberichterstattung vu sengem Beräich verantwortlech ass",
            persona_p_vn_03_label: 'E-Commerce Konformitéit',
            persona_p_vn_03_description: 'Compliance manager at an e-commerce platform owning its registration and change notifications, the registration dossier, and the seller and traceability information it supplies when an authority requests it',
            persona_p_vn_04_label: 'Telecom Regulatory Affairs Manager',
            persona_p_vn_04_description: 'Regulatory affairs lead at a telecom operator handling authority requests for subscriber information and disclosure governance',
            persona_p_vn_05_label: 'Verantwortlechen fir Date-Governance',
            persona_p_vn_05_description: 'Lead owning data mapping, classification and the annual governance attestation, and the data-service provider notification and reports where they apply',
            persona_p_vn_06_label: 'Cybersécherheets- & Juristesch Ops',
            persona_p_vn_06_description: 'Cybersecurity lead owning incident reporting decisions, authority requests and directions, disclosure logging, and data localisation and local presence readiness',
            persona_p_vn_07_label: 'Verantwortlechen fir KI-Governance',
            persona_p_vn_07_description: "Verantwortlechen, deen d'Governance vun de KI-Systemer, d'Risikobewäertungen an d'Klassifikatioun iwwerwaacht",
            persona_p_vn_08_label: 'Grenziwwerschreidend & Liwwerantegouvernanz',
            persona_p_vn_08_description: 'Lead owning cross-border personal data transfer impact assessments and their filings, recipient and vendor safeguards, and data localisation checks',
            persona_p_vn_09_label: 'Verantwortlechen fir Verletzungsreaktioun',
            persona_p_vn_09_description: "Verantwortlechen, dee d'Verletzungsmeldung an d'Virfallsreaktioun koordinéiert",
            persona_p_vn_10_label: 'Verantwortlechen fir Interne Revisioun',
            persona_p_vn_10_description: 'Lead running internal audits and regulator inspections across frameworks: evidence completeness, gaps, audit packs and sign-off',
            persona_p_vn_11_label: 'HR- & Mataarbechter-Daten-Verantwortlechen',
            persona_p_vn_11_description: 'HR manager handling employee consent records, electronic labour contracts and internal DSARs',
            persona_p_vn_12_label: 'Compliance-Leader multinational Duechtergesellschaft',
            persona_p_vn_12_description: 'DSB an enger vietnamesescher Duechtergesellschaft vun engem auslännesche Betrib mat lokaler Compliance-Schicht',
            persona_p_vn_13_label: 'Compliance-Leader Bildungswiesel',
            persona_p_vn_13_description: 'Compliance-Leader an enger auslännescher Schoul fir den Dateschutz vu Schüler a Elteren',
            qs_hr_consent_title: 'Mataarbechter-Zoustëmmungen registréieren',
            qs_hr_consent_description: 'Duerchgang vun der Registréierung vu Mataarbechter-Zoustëmmungen an Erstelle vun engem Ofdeckungsbericht.',
            qs_subsidiary_dpia_title: 'DSFA-Bewäertung fir Duechtergesellschaft erstellen',
            qs_subsidiary_dpia_description: 'Duerchgang vun der Erstelle vun enger DSFA fir eng Veraarbechtungsaktivitéit vun der Duechtergesellschaft.',
            qs_student_consent_title: 'Schüler-Zoustëmmungserfaassung ariichten',
            qs_student_consent_description: 'Duerchgang vun der Erstelle vun Elteren-Zoustëmmungsrichtlinne fir Schülerdaten.',
            persona_p_vn_14_label: 'Externe Juristesche Beroder',
            persona_p_vn_14_description: "Senior-Partner vun engem Affekotebüro, dee méi Cliente-Organisatiounen iwwer d'PDPL-Konformitéit beréit",
            persona_p_vn_15_label: 'Bedreiwsleeder vum Affekotebüro',
            persona_p_vn_15_description: "Koordinator fir Cliente-Engagement, dee d'Liwwerverfolgung an d'Fristekoordinéierung geréiert",
            persona_p_vn_16_label: 'Geschäftsinhaber / CEO',
            persona_p_vn_16_description: "CEO oder Generaldirekter, dee gesetzlech den Dateverantwortlechen ënner der PDPL ass a fir d'Konformitéitsinvestitioun verantwortlech ass",
            qs_dpia_filing_title: 'Är éischt DSFA-Andreechung ofschléissen',
            qs_dept_compliance_title: 'Är éischt Departementskonformitéitssektioun aginn',
            qs_ecommerce_consent_title: 'Prepare your e-commerce platform registration dossier',
            qs_telecom_data_title: 'Verschaffung vun Telekom-Abonnentendaten ariichten',
            qs_data_flow_title: 'Äre éischte Dateflaass kartéieren',
            qs_incident_response_title: 'Aarbechtsoflaaf fir Virfallsreaktioun konfiguréieren',
            qs_ai_risk_title: 'Eng Risikobewäertung vun engem KI-System ausféieren',
            qs_cross_border_title: 'Eng grenziwwerschreidend Dateniwwerdroung bewäerten',
            qs_breach_notification_title: 'En Aarbechtsoflaaf fir Verletzungsmeldung simuléieren',
            qs_audit_trail_title: 'Eng Konformitéitspréifungsspur iwwerpréiwen',
            qs_legal_advisor_portfolio_title: 'Multi-Cliente-Konformitéitsportfolio iwwerpréiwen',
            qs_legal_advisor_portfolio_description: 'Konformitéitsstatus iwwer méi Cliente-Organisatiounen iwwerpréiwen.',
            qs_law_firm_ops_title: 'Liwwerstatus vum Client suivréieren',
            qs_law_firm_ops_description: "Fortschrëtt vun de Beroderliwwerungen an d'Cliente-Engagement iwwerwaachen.",
            qs_ceo_dashboard_title: 'Direktiouns-Konformitéits-Dashboard kucken',
            qs_ceo_dashboard_description: 'Iwwergeuerdneten Konformitéitsstatus an Investitiounsindikateuren iwwerpréiwen.',
        },
        rights_requests: {
            response_templates: {
                title: 'Äntwert Schablounen',
                subtitle: "Verwalte d'DSR Äntwert Bréif Templates déi ResponseGenerator benotzt fir Äntwerten op Datesubjekter auszeschaffen.",
                create_btn: 'Nei Schabloun',
                edit_btn: 'Schabloun änneren',
                filter_right_type: 'Filter no riets Typ',
                filter_locale: 'Filter no Locale',
                filter_status: 'Filter no Status',
                all_right_types: 'All Recht Zorte',
                any_right_type: 'All (gëllt fir all Typ)',
                all_locales: 'All Uertschaften',
                all_statuses: 'All',
                status_active: 'Aktiv',
                status_inactive: 'Inaktiv',
                empty_state: 'Keng Template passen mat den aktuellen Filteren.',
                columns: {
                    template_id: 'Schabloun ID',
                    outcome: 'Resultat',
                    locale: 'Lokal',
                    right_type: 'Richteg Typ',
                    is_active: 'Status',
                    last_updated: 'Last Aktualiséiert',
                    actions: 'Aktiounen',
                },
                actions: {
                    edit: 'Edit',
                    clone: 'Klonen',
                    deactivate: 'Deaktivéieren',
                    reactivate: 'Reaktivéieren',
                    preview: 'Virschau',
                },
                form: {
                    template_id: 'Schabloun ID',
                    template_id_help: 'Klengbuchstaben Identifizéierer, z.B. dsr_access_fulfilled_vi',
                    outcome: 'Resultat',
                    locale: 'Lokal',
                    right_type: 'Richteg Typ',
                    subject: 'Sujet',
                    body: 'Kierper Schabloun',
                    body_help: 'Benotzt Plazhalter wéi {case_reference}, {data_subject_name}, {fulfilled_at}. Minimum 15 Linnen fir Multi-Sektioun Bréiwer.',
                    is_active: 'Aktiv',
                    official_form_id: 'Offiziell Form ID (optional)',
                },
                outcomes: {
                    fulfilled: 'Erfëllt',
                    partially_fulfilled: 'Deelweis erfëllt',
                    denied: 'Refuséiert',
                },
                clone_modal_title: 'Klon Schabloun',
                clone_target_locale: 'Ziel Locale',
                clone_help: 'Eng nei Schabloun gëtt an der Zillokal erstallt mat dëser Schabloun als Ausgangspunkt.',
                preview_modal_title: 'Schabloun Virschau',
                preview_note: 'Preview benotzt Demo Case DSR-DEMO-0020 Plazhalter. Real Liwwerung Ersatzspiller Live Fall Daten.',
                preview_unavailable: "Preview erfuerdert en existente Fall. Befestegt dës Schabloun un e Fall a benotzt d'Entworf Äntwert Aktioun op der Fall Detail Säit.",
                deactivate_confirm: 'Dës Schabloun deaktivéieren? Et wäert weider op existente Fäll gëllen déi et referenzéieren.',
                toasts: {
                    created: 'Schabloun erstallt',
                    updated: 'Schabloun aktualiséiert',
                    deactivated: 'Schabloun deaktivéiert',
                    reactivated: 'Schabloun reaktivéiert',
                    cloned: 'Schabloun gekloont',
                    load_error: 'Ausgefall Templates ze lueden',
                    save_error: "Konnt d'Schabloun net späicheren",
                    clone_error: "Ausgefall d'Schabloun ze klonen",
                },
            },
        },
        organizations: {
            departments: {
                title: 'Departementer',
                subtitle: "Verwalte d'Departementer déi DSR Fäll, DPIA Aufgaben a Konformitéit Workflows routen.",
                create_btn: 'Nei Departement',
                edit_btn: 'Edit Departement',
                columns: {
                    name: 'Numm',
                    description: 'Beschreiwung',
                    active_cases: 'Aktiv Fäll',
                    members: 'Memberen',
                    status: 'Status',
                    actions: 'Aktiounen',
                },
                status_active: 'Aktiv',
                status_inactive: 'Inaktiv',
                form: {
                    name: 'Numm',
                    description: 'Beschreiwung',
                },
                actions: {
                    edit: 'Edit',
                    deactivate: 'Deaktivéieren',
                    reactivate: 'Reaktivéieren',
                    delete: 'Läschen',
                    manage_members: 'Verwalte Memberen',
                },
                delete_modal_title: 'Läschen Departement',
                delete_warning: 'Dës Departement huet nach aktiv Fäll. Wielt eng Departement fir se nei ze zouzeweisen ier se geläscht gëtt.',
                delete_no_cases: 'Dës Departement huet keng aktiv Fäll a kann sécher geläscht ginn.',
                reassign_prompt: 'Aktive Fäll ëmsetzen op:',
                reassign_none_available: 'Keng aner aktiv Departementer verfügbar. Schafen eng aner Departement éischt.',
                member_section_title: 'Memberen',
                add_member: 'Add Member',
                remove_member: 'Ewechzehuelen',
                member_role: 'Roll',
                no_members: 'Keng Memberen op dëser Departement kartéiert.',
                toasts: {
                    created: 'Departement geschaf',
                    updated: 'Departement aktualiséiert',
                    deactivated: 'Departement deaktivéiert',
                    reactivated: 'Departement reaktivéiert',
                    deleted: 'Departement geläscht',
                    reassigned_count: '{count} Fall(en) nei zougewisen',
                    member_added: 'Member dobäi',
                    member_removed: 'Member geläscht',
                    load_error: 'Ausgefall Departementer ze lued',
                    save_error: 'Ausgefall Departement ze retten',
                    delete_error: 'Ausgefall Departement ze läschen',
                },
            },
            retention_policies: {
                title: 'Retention Politiken',
                subtitle: 'Per-Zil-Typ Retentiounsfenster konfiguréieren. Identitéitsverifizéierungsdokumenter benotzen dëst fir retention_expires_at op zukünfteg Eroplueden ze stempelen.',
                create_btn: 'Nei Politik',
                edit_btn: 'Politik änneren',
                columns: {
                    target_type: 'Zil Typ',
                    retention_days: 'Retention (Deeg)',
                    owner: 'Politik Besëtzer',
                    effective_from: 'Effektiv Vun',
                    last_reviewed: 'Last iwwerpréift',
                    status: 'Status',
                    actions: 'Aktiounen',
                },
                status_active: 'Aktiv',
                status_inactive: 'Inaktiv',
                form: {
                    target_type: 'Zil Typ',
                    target_type_help: "Wéi eng Datekategorie gëllt d'Retentiounsfenster. De Moment ginn nëmmen DSR Identitéitsverifizéierungsdokumenter ënnerstëtzt.",
                    retention_days: 'Retentiounsfenster (Deeg)',
                    retention_days_help: 'Tëscht 1 an 3650 Deeg (maximal 10 Joer).',
                    owner: 'Politik Besëtzer',
                    owner_help: "Normalerweis den DPO. Verantwortlech fir d'Retentiounsentscheedung ënner DG-2.",
                    effective_from: 'Effektiv Vun',
                },
                target_types: {
                    dsr_identity_verification_document: 'DSR Identitéitsverifizéierungsdokumenter',
                },
                deactivate_notice: 'Desaktivéiere beaflosst net scho gestempelt Uschlëss - nëmmen zukünfteg Eropluede kréien net méi eng Retentiounsfenster.',
                deactivate_confirm: 'Dës Retentiounspolitik desaktivéieren?',
                toasts: {
                    created: 'Retentiounspolitik erstallt',
                    updated: 'Retentiounspolitik aktualiséiert',
                    deactivated: 'Retentiounspolitik desaktivéiert',
                    load_error: 'Ausgefall Retention Politiken ze lued',
                    save_error: 'Ausgefall Retention Politik ze späicheren',
                },
            },
        },
    },
    data_mapping: {
        // Sprint 62.58.0 (Slice N) — English for key parity until Phase 25 (R-57).
        deployment_privacy: {
            audience_kinds: {
                public: 'The public',
                data_subject_group: 'Named data subjects',
            },
            channels: {
                website: 'Website',
                point_of_collection: 'At the point of collection',
                employment_documentation: 'Employment documentation',
                contract_or_terms: 'Contract or terms',
                in_app: 'In the app',
                email: 'Email',
                other: 'Other',
            },
            access_kinds: {
                view: 'View',
                extract: 'Extract',
                export: 'Export',
                delete: 'Delete',
                configure: 'Configure',
            },
            export: {
                coverage_heading: 'Privacy notices by data subject',
                coverage_withheld: 'Notice details are shown only to a reader who may read the privacy notice register.',
                col_activity: 'Processing activity',
                col_data_subject: 'Data subject',
                col_notice: 'Notice',
                col_revision: 'Revision',
                col_channels: 'Channels',
                col_effective_from: 'In effect from',
                col_status: 'Status',
                not_recorded: 'Not recorded',
                no_current_notice: 'No current notice: every notice that names this data subject is withdrawn, superseded or replaced by a newer revision',
                status_replaced: '{status}, replaced by a newer revision',
                not_stated: 'Not stated',
                access_heading: 'Who may access the data',
                col_version: 'Version',
                col_reviewed_on: 'Reviewed on',
                col_role_or_position: 'Role or position',
                col_access_kinds: 'Permitted actions',
                col_means: 'By what means',
                col_conditions: 'On what conditions',
                no_access_statement: 'No access statement recorded',
                no_data_subjects: 'No data subjects recorded',
                public_linked: 'Also linked: {count} public notices, which name no data subject',
                public_linked_one: 'Also linked: 1 public notice, which names no data subject',
                coverage_truncated: 'More notices are linked to some processing activities than this export lists, so for those activities the notices are listed published first, then approved, draft, withdrawn and superseded, the most recently drafted first within each. The processing activities concerned: {activities}. A data subject reads "Not recorded" only when no linked notice names it.',
                access_withheld: 'Access statements are shown only to a reader who may read processing activities.',
                col_approved_by: 'Approved by',
                col_systems: 'Systems',
            },
            notice_form: {
                heading: 'Audience, channels and deployment',
                intro: 'Say whom this revision is given to, how they are given it, from which day, and the processing activities and systems it is given for.',
                audience_label: 'Whom the notice is given to',
                audience_public_hint: 'Anyone can read it, for example on your website. A public notice names no data subject, so the notice coverage of a processing activity does not list it against any data subject.',
                audience_group_hint: 'Named data subjects of the linked processing activities, such as drivers, passengers or staff.',
                audience_group_need_grant: 'Naming data subjects needs the permission to read processing activities, which your role does not hold: the data subjects are chosen from the linked processing activities. The notice can be given to the public.',
                data_subjects_label: 'Data subjects the notice is given to',
                data_subjects_hint: 'Chosen from the data subjects recorded on the linked processing activities.',
                data_subjects_need_activity: 'Link at least one processing activity first: the data subjects are chosen from those it records.',
                data_subjects_none: 'The linked processing activities record no data subjects.',
                data_subjects_required: 'Choose at least one data subject.',
                data_subjects_unknown: '{value} (not recorded on the linked processing activities)',
                channels_label: 'How the audience is given the notice',
                channels_hint: 'Choose at least one. A sticker or a screen where the data is collected, such as inside a vehicle, is "At the point of collection".',
                channels_required: 'Choose at least one channel.',
                channel_note_label: 'Describe the other channel',
                channel_note_required: 'Describe the other channel.',
                effective_from_label: 'In effect from',
                effective_from_hint: 'The day the notice takes effect, as your organization states it. Leave it empty if the day is not yet known.',
                activities_label: 'Processing activities the notice is given for',
                activities_hint: 'The data subjects of these activities are the ones the notice can name.',
                systems_label: 'Data-map systems the notice covers',
                links_need_grant: 'Linking a notice to processing activities or systems needs the permission to read processing activities, which your role does not hold. The notice can be drafted without links.',
                systems_need_grant: 'Choosing data-map systems needs the permission to read systems, which your role does not hold.',
                picker_loading: 'Loading the list…',
                activities_empty: 'No processing activity is recorded.',
                systems_empty: 'No data-map system is recorded.',
                activities_failed: 'The processing activities could not be read.',
                systems_failed: 'The data-map systems could not be read.',
                activities_manual_label: 'Processing activity IDs',
                systems_manual_label: 'Data-map system IDs',
                manual_hint: 'Enter the IDs separated by commas; they are checked when you save.',
                off_list: '{value} (not in the list read)',
                retry: 'Reload',
                reloading: 'Reloading…',
                activities_retry_subject: 'the processing activities',
                systems_retry_subject: 'the data-map systems',
                error_link_grant: 'Your role cannot link a notice to processing activities or systems. Remove the links, or ask for the permission to read processing activities.',
                error_link_not_found: 'A linked processing activity or system was not found in this organization. What you entered is kept.',
                error_audience_invalid: 'A chosen data subject is not recorded on the linked processing activities. What you entered is kept.',
                col_audience: 'Audience',
                col_channels: 'Channels',
                col_effective_from: 'In effect from',
                col_links: 'Linked to',
                links_activities: 'Processing activities: {names}',
                links_activities_count: '{count} processing activities',
                links_activities_count_one: '1 processing activity',
                links_systems: 'Systems: {names}',
                links_systems_count: '{count} systems',
                links_systems_count_one: '1 system',
                links_none: 'None',
                other_channel: 'Other: {note}',
                data_subjects_unconfirmed: '{value} (not confirmed: a linked processing activity could not be read)',
                error_systems_grant: 'Your role cannot link a notice to data-map systems. Remove the systems, or ask for the permission to read systems.',
                audience_with_subjects: '{label}: {subjects}',
            },
            coverage: {
                heading: 'Notices',
                intro: 'For each data subject recorded on this activity, the linked privacy notices that name it, with revision, channels, date in effect and status. Nothing here is counted or scored.',
                not_visible: 'Your role cannot read the privacy notice register, so the notices are not shown here. This does not say whether a notice exists.',
                no_subjects: 'This activity records no data subjects.',
                list_label: 'Notices by data subject',
                notice_line: '{title}, revision {revision}',
                notice_meta: 'Channels: {channels} · In effect from {date} · Status: {status}',
                load_failed: 'The notices could not be read.',
                retry_subject: 'the notices of this activity',
                pagination: 'Pages of data subjects',
                public_heading: 'Also linked: public notices',
                public_intro: 'These notices are addressed to the public. A public notice names no data subject, so it is not listed against any data subject above.',
                public_failed: 'The public notices linked to this activity could not be read.',
                public_retry_subject: 'the public notices',
                truncated: 'More notices are linked to this activity than are listed here, so they are listed published first, then approved, draft, withdrawn and superseded, the most recently drafted first within each. A data subject reads "Not recorded" only when no linked notice names it.',
                subject_state: '{subject}: {state}',
            },
            access: {
                intro: 'Who may view, extract, export, delete or configure the data of this activity, by what means and on what conditions. Every revision is kept; the latest is first.',
                record: 'Record a revision',
                record_first: 'Record the access statement',
                current: 'Current',
                version: 'Version {version}',
                reviewed: 'Reviewed on {date}',
                approved: 'Approved by {name}',
                not_approved: 'No approver recorded',
                recorded: 'Recorded by {name} on {date}',
                systems: 'Systems: {names}',
                entries_label: 'Entries of version {version}',
                evidence_count: 'Documents: {count}',
                evidence_none: 'No document attached',
                evidence_open: 'Documents',
                evidence_open_aria: 'Documents of version {version}',
                load_failed: 'The access statement could not be read.',
                retry_subject: 'the access statement',
                pagination: 'Pages of access statement revisions',
                history_label: 'Revisions of the access statement',
                unknown_person: 'Unknown person',
            },
            access_form: {
                title: 'Record a revision of the access statement',
                replaces: 'This revision replaces version {version}. The earlier versions are kept.',
                first: 'This is the first revision of the access statement for this activity.',
                entries_heading: 'Who may access the data',
                entry_legend: 'Entry {n}',
                role_label: 'Role or position',
                role_hint: 'A role or a position, such as "Fleet safety officer". A person\'s name is not needed.',
                access_kinds_label: 'Permitted actions',
                means_label: 'By what means',
                means_hint: 'For example, the extraction software named, a console, or a request to IT.',
                conditions_label: 'On what conditions',
                conditions_hint: 'For example, a ticket, two people present, or a stated purpose.',
                add_entry: 'Add an entry',
                remove_entry: 'Remove entry {n}',
                reviewed_on_label: 'Reviewed on',
                reviewed_on_hint: 'The day the statement was reviewed. It cannot be later than today.',
                approver_label: 'Approved by (optional)',
                approver_none: 'No approver',
                approver_retry_subject: 'the list of people',
                systems_label: 'Data-map systems the statement covers',
                systems_kept: 'The systems the current version covers are kept, because your role cannot read the data-map systems.',
                missing: 'Give every entry a role or position and at least one permitted action, and give the review date.',
                submit: 'Record the revision',
                saving: 'Recording…',
                recorded: 'Revision recorded.',
                record_failed: 'The revision could not be recorded. What you entered is kept.',
                conflict: 'Someone recorded version {version} of this statement while you were editing, so yours was not recorded. What you entered now builds on version {version}: the entries and systems you did not change read as in version {version}, and your additions, changes and removals are kept. Check the entries, then record again.',
                conflict_reread_failed: 'Someone recorded a newer revision of this statement while you were editing, so yours was not recorded, and the newer revision could not be read. What you entered is kept; record again to read it once more.',
            },
            access_files: {
                heading: 'Documents',
                intro: "The documents behind this revision, such as the access policy, the extraction software's role list or the signed approval.",
                slot: 'Documents of this revision',
                none: 'No document is attached to this revision.',
                file_hash: 'SHA-256: {hash}',
                not_held: 'Document no longer held',
                download: 'Download',
                remove: 'Remove',
                upload: 'Add a document',
                bound: 'Document added.',
                removed: 'Document removed.',
                bind_failed: 'The document could not be added.',
                remove_failed: 'The document could not be removed.',
                not_owned: 'The revision this document belongs to was not found.',
                remove_confirm: 'Remove this document from the revision and delete it?',
                load_failed: 'The documents could not be loaded.',
            },
        },
        lawful_basis: {
            title: 'Lawful basis',
            load_failed: 'The lawful bases your installed frameworks declare could not be read.',
            none_declared: 'No installed framework declares a lawful-basis vocabulary. Record the basis and the article it rests on below.',
            declared_by: 'Declared by {instrument}',
            citation: 'Citation',
            statement_label: 'Lawful basis (recorded by you)',
            statement_citation_label: 'Article this basis rests on',
            other_ground: 'Another lawful basis, recorded by you',
            needs_redetermination: 'This record carries "{basis}", which no installed framework declares. It needs re-determining.',
            needs_redetermination_declared: 'This record carries "{basis}" and is marked as needing re-determining. Confirm this basis or choose another, then save.',
            stored_unverified: 'This record carries "{basis}". Some installed frameworks have not been verified yet, so whether one of them declares it is not known.',
            instruments_heading: 'Instruments checked',
            instrument_declares_none: '{instrument} — verified: declares no lawful basis',
            instrument_not_verified: '{instrument} — not yet verified',
            retry: 'Try again',
            list_needs_redetermination: 'Needs re-determining: {basis}',
            declared_version_changed: 'Recorded against {framework} {version}; installed now: {installed}.',
            declared_version_withdrawn: 'Recorded against {framework} {version}; no installed framework declares this basis now.',
            loading: 'Loading the lawful bases your installed frameworks declare.',
            save_blocked_unread: 'A lawful basis is required, and the bases your installed frameworks declare have not been read yet.',
            citation_blank: 'Enter the article this basis rests on. Spaces alone are not an article.',
            citation_without_statement: 'An article is entered but no basis is recorded. Record the basis it supports, or clear the article.',
        },
        // OD42 (fix-sprint-20261003-0625 F28) — the transfer-mechanism control and
        // its list sentences. The mechanism is what the installed frameworks declare.
        transfer_mechanism_vocabulary: {
            title: 'Transfer mechanism',
            load_failed: 'The transfer mechanisms your installed frameworks declare could not be read.',
            loading: 'Loading the transfer mechanisms your installed frameworks declare.',
            retry: 'Try again',
            none_declared: 'No installed framework declares a transfer mechanism. Describe the transfer in the detail field.',
            none_declared_unverified: 'No installed framework has declared a transfer mechanism, and some have not been verified yet. Describe the transfer in the detail field.',
            declared_by: 'Declared by {instrument}',
            citation: 'Citation',
            needs_redetermination: 'This record carries "{mechanism}", which no installed framework declares. It needs re-determining.',
            needs_redetermination_declared: 'This record carries "{mechanism}" and is marked as needing re-determining. Confirm this mechanism or choose another, then save.',
            unverified: 'This record carries "{mechanism}". No verified installed framework declares it; not yet verified: {instruments}.',
            instruments_heading: 'Instruments checked',
            instrument_declares_none: '{instrument} — verified: declares no transfer mechanism',
            instrument_not_verified: '{instrument} — not yet verified',
            list_needs_redetermination: 'Needs re-determining: {mechanism}',
            list_unverified: '{mechanism} (not yet verified: {instruments})',
            stored_unmeasured: 'This record carries "{mechanism}". No installed framework has stated which transfer mechanisms it provides yet, so whether it is declared has not been checked.',
        },
        nav: {
            systems: 'Systemer',
            categories: 'Donnéeën Kategorien',
            registry: 'Dateregister',
            purposes: 'Veraarbechtung Zwecker',
            retention: 'Opbewarung',
            flows: 'Dateflëss',
            activities: 'Veraarbechtungsaktivitéiten',
            evidence: 'Beweismaterial',
            exports: 'Exporter',
            attestation: 'Attestation Cycles',
            governance: 'Gouvernance',
            review: 'Iwwerpréifungsaufgaben',
            inventory: 'Inventaire',
            exceptions: 'Ausnahmen',
            history: 'Verlaf',
            ropa_completeness: 'ROPA Vollständegkeet',
        },
        ropa_completeness_autofix: {
            ask_button: 'Frot de Forseti',
            drafting: 'Entworf…',
            accept: 'Akzeptéieren',
            reject: 'Refuséieren',
            accepted: 'Akzeptéiert - gëllen iwwer den Aktivitéitseditor.',
            rejected: 'Refuséiert.',
            needs_human_input: 'Forseti brauch méi Aktivitéitskontext (zum Beispill en dokumentéierten Zweck) ier dëst Feld ausgeschafft gëtt.',
            ask_again: 'Ask again',
            draft_failed: 'Forseti could not draft a value.',
            decision_failed: 'The decision could not be saved.',
        },
        ropa_completeness: {
            rules: {
                nd165_scope_rationale: 'Confirm and evidence the transfer scope; review mixed data before selecting the applicable assessment route.',
                nd165_scope_condition: 'Important/core cross-border data with mixed or unresolved personal-data scope',
                nd165_personal_dpia_rationale: 'Record the processing impact assessment reference when required, including confirmed personal core/important cross-border data.',
                nd165_personal_dpia_condition: 'Confirmed personal important/core data + cross-border transfer',
                nd165_personal_transfer_rationale: 'Record the separate personal-data cross-border transfer impact assessment dossier reference.',
                nd165_personal_transfer_condition: 'Confirmed personal important/core data + cross-border transfer',
                nd165_nonpersonal_assessment_rationale: 'Core non-personal data requires a regulator-issued assessment for cross-border transfers.',
                nd165_nonpersonal_assessment_condition: 'Confirmed non-personal core data + cross-border transfer',
            },
            title: 'ROPA Vollständegkeet Bericht',
            subtitle: 'Per-Feld Hygiène Audit vun Äre Records of Processing Aktivitéiten, scoped vum reglementaresche Kader.',
            framework_label: 'Kader',
            kpi_total: 'Aktivitéiten geschoss',
            kpi_critical: 'Kritesch',
            kpi_high: 'Héich',
            kpi_conditional: 'Bedingt',
            field_chart_heading: 'Vermësst Felder no Frequenz',
            drilldown_heading: 'Aktivitéiten mat Lücken',
            col_activity: 'Aktivitéit',
            col_field: 'Feld',
            col_severity: 'Gravitéit',
            col_condition: 'Zoustand',
            col_action: 'Fix',
            action_fix: 'Fix',
            severity_critical: 'Kritesch',
            severity_high: 'Héich',
            severity_conditional: 'Bedingt',
            severity_informational: 'Informatioun',
            include_informational: 'Informatioun enthalen',
            export_csv: 'Download CSV',
            exporting: 'Exportéiert...',
            loading: 'Lueden…',
            empty_state: 'All aktiv Aktivitéiten hunn komplett ROPA records fir dëse Kader.',
            error_fetch: 'Ausgefall fir de Vollständegkeetsrapport ze lueden.',
            error_unsupported_framework: 'Dëse Kader gëtt net ënnerstëtzt.',
            error_framework_not_installed: 'Dëse Kader ass net fir Är Organisatioun installéiert.',
            no_misses_for_field: 'Keng Aktivitéiten vermësst dëst Feld.',
            framework_PDPL: 'PDPL (Gesetz 91/2025 + Dekret 356)',
            framework_GDPR: 'GDPR Artikel 30',
            framework_DECREE_165: 'Dekret 165 (Vietnam Dategesetz)',
            framework_ISO_27701: 'ISO 27701 (Informatioun)',
        },
        registry: {
            // Sprint 64.72.0 — Kolonnentitele vum Export vum Datenregister.
            export_col: {
                registry_item_id: 'Registeragab-ID',
                name: 'Numm',
                description: 'Beschreiwung',
                field_identifiers: 'Feldbezeechnungen',
                category_id: 'Kategorie-ID',
                category_name: 'Numm vun der Kategorie',
                data_type: 'Datentyp',
                effective_sensitivity: 'Gëlteg Sensibilitéit',
                sensitivity_override: 'Ofwäichend Sensibilitéit',
                data_subjects: 'Betraffe Persounen',
                volume_band: 'Volumeberäich',
                source_system_ids: 'IDe vun de Quellsystemer',
                processing_activity_ids: 'IDe vun de Veraarbechtungsaktivitéiten',
                retention_policy_id: 'ID vun der Opbewahrungsreegel',
                effective_retention_policy_id: 'ID vun der gëltiger Opbewahrungsreegel',
                purposes: 'Zwecker',
                collection_method: 'Aart vun der Erhiewung',
                legal_basis: 'Rechtsgrondlag',
                data_tier: 'Datenniveau',
                data_tier_source: 'Hierkonft vum Datenniveau',
                storage_locations: 'Späicherplazen',
                cross_border: 'Grenziwwerschreidend',
                status: 'Status',
                source_form_submission_id: 'Araches-ID vum Quellformulaire',
                source_form_template_id: 'Virlag-ID vum Quellformulaire',
                last_reviewed_at: 'Fir d’lescht iwwerpréift',
                next_review_at: 'Nächst Iwwerpréifung',
                created_at: 'Erstallt den',
                updated_at: 'Aktualiséiert den',
                data_element: 'Datenelement',
                data_category: 'Datekategorie',
                systems: 'Systemer',
                retention_policy: 'Opbewahrungsreegel',
                name_of_processing: 'Bezeechnung vun der Veraarbechtung',
                categories_of_personal_data: 'Kategorie vu perséinlechen Donnéeën',
                categories_of_data_subjects: 'Kategorie vu betraffene Persounen',
                recipients: 'Empfänger',
                third_country_transfers: 'Iwwerdroungen an Drëttlänner',
                retention_period: 'Opbewahrungsdauer',
                doc_title: 'Datenregister',
                doc_generated: 'Export erstallt {timestamp}',
                doc_item_count: '{count} Registeragab(en)',
                doc_template_full: 'Vollstänneg',
            },
            export_vocab: {
                data_type: {
                    text: 'Text',
                    number: 'Zuel',
                    date: 'Datum',
                    datetime: 'Datum an Auerzäit',
                    boolean: 'Jo/Nee',
                    binary: 'Binär',
                    structured: 'Strukturéiert',
                    file: 'Fichier',
                    identifier: 'Kennung',
                    geolocation: 'Geolokaliséierung',
                },
                collection_method: {
                    direct_from_subject: 'Direkt bei der betraffener Persoun',
                    third_party: 'Vun engem Drëtten',
                    derived: 'Aus anere Donnéeën ofgeleet',
                    observed: 'Beobachtet',
                    inferred: 'Erschloss',
                    public_source: 'Ëffentlech Quell',
                },
                data_subject_type: {
                    customer: 'Client',
                    employee: 'Mataarbechter',
                    contractor: 'Optragnehmer',
                    prospect: 'Méiglechen Client',
                    minor: 'Mannerjäregen',
                    patient: 'Patient',
                    student: 'Student',
                    beneficiary: 'Beneficiaire',
                    vendor_contact: 'Kontakt beim Fournisseur',
                    website_visitor: 'Besucher vun der Websäit',
                    other: 'Anerer',
                },
                volume_band: {
                    lt_1k: 'Manner wéi 1.000 Datesätz',
                    '1k_to_10k': '1.000 bis 10.000 Datesätz',
                    '10k_to_100k': '10.000 bis 100.000 Datesätz',
                    '100k_to_1m': '100.000 bis 1 Millioun Datesätz',
                    gt_1m: 'Méi wéi 1 Millioun Datesätz',
                    unknown: 'Net bekannt',
                },
                tier_source: {
                    assessed_and_approved: 'Bewäert a guttgeheescht',
                    manual_legacy: 'Aus engem alen Wäert iwwerholl',
                    legacy_platform_field: 'Vun der Plattform ierfte Wäert',
                    scheme_scoped_only: 'No schemaspezifesche Stufe bewäert — kee eenzelne Code deckt méi gesetzlech Stufeskalen of',
                },
            },
            page: {
                title: 'Dateregister',
                subtitle: 'Perséinlech Dateelementer déi Är Organisatioun hält, organiséiert ënner Datekategorien.',
                empty_state: 'Keng Registréierungsartikelen nach. Füügt en Artikel manuell oder importéiert aus engem reglementaresche Register.',
                empty_filter_state: 'Keng Elementer passen mat Äre Filteren.',
            },
            filter: {
                category: 'Kategorie',
                sensitivity: 'Sensibilitéit',
                data_subject: 'Date Sujet',
                status: 'Status',
                data_tier: 'Daten Tier',
                cross_border: 'Grenziwwerschreidend',
                has_owner: 'Huet Besëtzer',
                has_form_source: 'Importéiert aus Form',
                search_placeholder: 'Sichen Numm, Beschreiwung oder Feldidentifizéierer ...',
                clear: 'Filteren kloer',
                option_all: 'All',
            },
            col: {
                name: 'Numm',
                category: 'Kategorie',
                sensitivity: 'Sensibilitéit',
                data_subjects: 'Donnéeën Sujeten',
                systems: 'Systemer',
                activities: 'Aktivitéiten',
                owner: 'Besëtzer',
                retention: 'Retention',
                data_tier: 'Daten Tier',
                status: 'Status',
                last_reviewed: 'Lescht iwwerpréift',
                actions: 'Aktiounen',
            },
            action: {
                add_manual: 'Artikel dobäisetzen',
                add_from_form: 'Füügt aus Regulatiounsregister',
                import_history: 'Import fréier Soumissioun',
                export: 'Export',
                archive: 'Archiv',
                restore: 'Restauréieren',
                edit: 'Edit',
                assign_owner: 'Besëtzer zouzeschreiwen',
                attach_evidence: 'Befestegt Beweiser',
                open_lineage: 'Open Lineage',
            },
            modal_editor: {
                section_identity: 'Identitéit',
                section_classification: 'Klassifikatioun',
                section_subjects: 'Sujeten & Volumen',
                section_location: 'Location & Transfer',
                section_systems: 'Systemer & Aktivitéiten',
                section_retention: 'Retention & legal',
                section_governance: 'Gouvernance',
                section_notes: 'Notizen',
                hint_inherited: '(ierflecher vun der Kategorie)',
                hint_overridden: '(iwwerschratt)',
                placeholder_field_identifiers: 'z.B. Clienten.primary_email, hris.employee_id',
                validation_name_required: 'Numm ass néideg',
                validation_category_required: 'Kategorie ass néideg',
                validation_data_subjects_required: "Op d'mannst een Typ vun Datesubjekt ass erfuerderlech",
                label_data_type: 'Daten Typ',
                label_field_identifiers: 'Feldidentifizéierer (komma-getrennt)',
                label_data_subject_types: 'Daten Sujet Zorte',
                label_volume_band: 'Band Band',
                label_storage_locations: 'Späicherplazen (komma-getrennt)',
                placeholder_storage_locations: 'ISO-3166 Coden oder gratis Text (z.B. VN, Hetzner EU)',
                label_cross_border: 'Grenziwwerschreidend Transfert',
                label_source_systems: 'Quelle Systemer',
                label_processing_activities: 'Veraarbechtung Aktivitéiten',
                label_collection_method: 'Sammelmethod',
                label_purposes: 'Zwecker',
                hint_multi_select: 'Halt Cmd / Ctrl fir Multiple ze wielen.',
                option_inherit_category: '- (Ierwen vun der Kategorie)',
                option_dash: '-',
                // fix-sprint-20260925-2123 (Finding 11) — a stale save refused with 409.
                error_conflict: 'This registry item was changed by someone else while you were editing, so your change was not saved. Close this form and open the item again to see the current version.',
                // The form was re-read and now shows the saved version.
                error_conflict_refreshed: 'This registry item was changed by someone else while you were editing, so your change was not saved. The form now shows the current version, and your unsaved entries were not kept. Make your change again and save.',
            },
            modal_import: {
                scan_none: 'No mapped regulatory-register templates are registered yet.',
                scan_count: 'Scans {count} template(s) for historical submissions and imports items.',
                dry_run_heading: 'Import Virschau',
                label_to_create: 'Ze schafen',
                label_to_update: 'Fir ze aktualiséieren',
                label_skipped_archived: 'Iwwersprangen (archivéiert)',
                label_conflicts: 'Konflikter',
                commit: 'Import',
                cancel: 'Ofbriechen',
                conflict_new_item: 'New item',
                conflict_value_own_words: 'a ground recorded in a person’s own words: “{statement}”',
                conflict_reason: 'Reason: {reason}',
                conflict_row_kept_edit: '{field} — the value you edited was kept: {current}. The form’s value was not applied: {mapper}.',
                conflict_row_kept_current: '{field} — the current value was kept: {current}. The form’s value was refused: {mapper}.',
                conflict_row_not_created: '{field} — the item was not created. The form’s value was refused: {mapper}.',
                conflict_reasons: {
                    field_user_edited: 'A person edited this field after an earlier import, and an import never overwrites a person’s edit.',
                    basis_not_declared: 'No installed framework declares a lawful basis, so a coded basis cannot be recorded.',
                    basis_required: 'This record needs a lawful basis, and the form gave none.',
                    basis_needs_person: 'Only an authenticated person may record or change this lawful basis, and the import did not run as one.',
                    basis_statement_rules: 'A lawful basis is an option your installed frameworks declare, or a ground in your own words with the article it rests on; the form’s value is neither.',
                    unknown: 'The import did not apply this value.',
                },
                conflict_fields: {
                    description: 'Description',
                    field_identifiers: 'Field identifiers',
                    data_type: 'Data type',
                    sensitivity_override: 'Sensitivity (override)',
                    data_subject_types: 'Data subject types',
                    volume_band: 'Volume band',
                    source_system_ids: 'Source systems',
                    processing_activity_ids: 'Processing activities',
                    retention_policy_id: 'Retention policy',
                    purposes: 'Purposes',
                    collection_method: 'Collection method',
                    legal_basis: 'Lawful basis',
                    data_tier: 'Data tier',
                    storage_locations: 'Storage locations',
                    cross_border: 'Cross-border transfer',
                },
            },
            drawer: {
                section_category: 'Kategorie',
                section_systems: 'Quelle Systemer',
                section_activities: 'Veraarbechtung Aktivitéiten',
                section_flows: 'Daten fléisst',
                section_retention: 'Effektiv Retention',
                section_governance: 'Gouvernance',
                section_evidence: 'Beweiser',
                section_audit: 'Rezent Audit Trail',
                section_source_form: 'Quell Form Soumissioun',
                badge_inherited: 'Ierflecher',
                badge_overridden: 'Iwwerdribblen',
            },
            toast: {
                created: 'Registry Element erstallt.',
                updated: 'Registry Element aktualiséiert.',
                archived: 'Registry Element archivéiert.',
                imported: '{count} Elementer importéiert.',
                conflict_warning: '{count} Feld(en) hunn Är Ännerungen behalen - kuckt Konfliktlëscht.',
            },
            // Sprint 62.35.0 (AC6) — English placeholder; locale work beyond EN/VI defers to Phase 25 (R-57).
            evidence: {
                section_title: 'Provenance, notice and quality',
                section_intro: 'Evidence the organisation records about this data. These are records, not verdicts: an empty field means not recorded.',
                not_recorded: 'Not recorded',
                group_provenance: 'Provenance and authorisation',
                group_notice: 'Notice',
                group_quality: 'Authenticity and quality',
                label_source_description: 'Source of the data',
                hint_source_description: 'Collected directly, received from whom, or created by whom.',
                label_data_owner: 'Data owner',
                label_authorisation_basis: 'Authorisation relied on',
                label_authorisation_evidence_refs: 'Where the authorisation evidence is held (comma-separated)',
                label_notified_purpose: 'Purpose notified',
                label_notified_scope: 'Scope notified',
                label_notice_reference: 'Notice reference',
                label_authenticity_checked: 'Authenticity checked',
                label_authenticity_method: 'How authenticity was checked, and the result',
                label_quality_checked: 'Quality checked',
                label_quality_method: 'How quality was checked, and the result',
                label_matches_source: 'Collected data matches the source data',
                label_checked_on: 'Checked on',
                hint_checked_on: 'The day the check was made. It cannot be after today.',
                label_checked_by: 'Checked by',
                checked_by_retry_subject: 'the list of people who can be named as the checker',
                error_checked_on_in_future: "The check cannot be dated after today in the organisation's calendar ({date}).",
                error_checked_by_not_found: 'The person named as the checker is not a user of this organisation.',
                label_recorded_by: 'Check recorded by',
                label_recorded_at: 'Check recorded at',
                hint_recorded_stamp: 'Recorded automatically when any check field changes.',
                option_yes: 'Yes',
                option_no: 'No',
                option_not_determined: 'Not determined',
                vocabulary_none: 'No installed framework declares an authorisation vocabulary, so this can only be left not recorded.',
                vocabulary_not_in_force: 'Declared by a framework that is not yet in force (effective {date}).',
                vocabulary_load_failed: 'The authorisation options could not be loaded.',
                value_undeclared: '{value} (not declared by any installed framework)',
            },
        },
        systems: {
            title: 'Systeminventaire',
            new: 'Neit System',
            edit: 'System änneren',
            name: 'Numm',
            owner: 'Besëtzer',
            criticality: 'Kritizitéit',
            dpa_status: 'DPA-Status',
            location: 'Standuert',
            system_type: 'Systemtyp',
            metadata: 'Metadaten (JSON)',
            department: 'Departement',
            hosting_location: 'Hosting-Standuert',
            hosting_provider: 'Hosting-Ubidder',
            data_residency_country: 'Land vun der Dateresidenz',
            hosting: 'Hosting',
            no_systems: 'Keng Systemer fonnt',
            confirm_archive: 'System "{name}" archivéieren?',
            count: 'Systemer',
            count_one: 'System',
            type_internal: 'Intern',
            type_external: 'Extern',
            type_cloud_service: 'Cloud-Service',
            type_on_premise: 'Lokal',
            type_hybrid: 'Hybrid',
            status_active: 'Aktiv',
            status_decommissioning: 'Gëtt ausser Betrib geholl',
            status_decommissioned: 'Ausser Betrib',
            status_planned: 'Geplangt',
            dpa_not_required: 'Net Néideg',
            dpa_required_missing: 'Néideg Feelt',
            dpa_in_progress: 'Am Lafen',
            dpa_signed: 'Ënnerschriwwen',
            all_criticalities: 'All Kritizitéiten',
            status_inactive: 'Inactive',
        },
        categories: {
            title: 'Datekategorien',
            new: 'Nei Kategorie',
            edit: 'Kategorie änneren',
            name: 'Numm',
            sensitivity: 'Sensibilitéit',
            tags: 'Tags',
            used_in: 'Benotzt an',
            examples: 'Beispiller',
            examples_hint: 'Beispiller (kommagetrennt)',
            default: 'Standard',
            all_sensitivities: 'All Sensibilitéiten',
            all_statuses: 'All Statussen',
            no_categories: 'Keng Kategorien fonnt',
            confirm_delete: 'Kategorie "{name}" läschen?',
            count: 'Kategorien',
            count_one: 'Kategorie',
            status_draft: 'Entworf',
            status_proposed: 'Virgeschloen',
            status_under_review: 'An Iwwerpréifung',
            status_approved: 'Geneemegt',
            status_deprecated: 'Veraleet',
            status_updated: 'Kategorie-Status aktualiséiert',
            propose: 'Virschloen',
            approve: 'Geneemegen',
            deprecate: 'Veraleeten',
            items: 'Registry Items',
        },
        purposes: {
            title: 'Veraarbechtungszwecker',
            new: 'Neien Zweck',
            edit: 'Zweck änneren',
            name: 'Numm',
            legal_basis: 'Rechtsgrondlag',
            retention_period: 'Opbewarungszäit (Deeg)',
            justification: 'Begrënnung fir legitim Interessi',
            legal_basis_detail: 'Detail vun der Rechtsgrondlag',
            all_legal_bases: 'All Rechtsgrondlagen',
            no_purposes: 'Keng Zwecker fonnt',
            confirm_delete: '"{name}" läschen?',
            default: 'Standard',
            count: '{count} Zwecker',
            count_one: '{count} Zweck',
        },
        retention: {
            title: 'Opbewarungsrichtlinnen',
            new: 'Nei Richtlinn',
            edit: 'Richtlinn änneren',
            name: 'Numm',
            retention_days: 'Opbewarungsdeeg',
            disposal_method: 'Entsorgungsmethod',
            data_categories: 'Datekategorien',
            activities: 'Aktivitéiten',
            review_frequency: 'Iwwerpréiwungsfrequenz',
            review_frequency_days: 'Iwwerpréiwungsfrequenz (Deeg)',
            no_policies: 'Keng Opbewarungsrichtlinne fonnt',
            confirm_delete: '"{name}" läschen?',
            count: '{count} Richtlinnen',
            count_one: '{count} Richtlinn',
            days: 'Deeg',
            disposal_deletion: 'Läschung',
            disposal_anonymization: 'Anonymiséierung',
            disposal_archival: 'Archivéierung',
            review_overdue: 'Iwwerpréiwung iwwerfälleg',
            review_status: 'Iwwerpréiwungsstatus',
            review_ok: 'Am Plang',
            department: 'Abteilung',
            all_departments: 'All Abteilungen',
            status_draft: 'Draft',
            status_active: 'Active',
            status_superseded: 'Superseded',
            status_retired: 'Retired',
        },
        flows: {
            view_tabs_aria: 'Flëss Vue',
            view_list: 'Lëscht',
            view_map: 'Kaart',
            title: 'Dateflëss',
            new: 'Neie Floss',
            edit: 'Floss änneren',
            name: 'Numm',
            from_system: 'Vum System',
            to_system: 'Op System',
            flow_type: 'Flossaart',
            cross_border: 'Grenziwwerschreidend',
            risk_level: 'Risikoniveau',
            mechanism: 'Mechanismus',
            transfer_mechanism: 'Transfermechanismus',
            frequency: 'Frequenz',
            source_country: 'Quellland',
            destination_country: 'Zilland',
            transfer_safeguard: 'Transferschutz',
            transfer_safeguard_detail: 'Transfer Safeguard Detail',
            all_risk_levels: 'All Risikoniveauen',
            no_flows: 'Keng Dateflëss fonnt',
            confirm_archive: '"{name}" archivéieren?',
            count: '{count} Flëss',
            count_one: '{count} Floss',
            status_active: 'Aktiv',
            status_inactive: 'Inaktiv',
            status_under_review: 'An Iwwerpréiwung',
            mechanism_api: 'API',
            mechanism_file_transfer: 'Fichier-Transfer',
            mechanism_database_replication: 'Datebank-Replikatioun',
            mechanism_manual: 'Manuell',
            mechanism_email: 'E-Mail',
            mechanism_other: 'Anert',
            frequency_real_time: 'Echtzäit',
            frequency_hourly: 'Stënnlech',
            frequency_daily: 'Deeglech',
            frequency_weekly: 'Wëchentlech',
            frequency_monthly: 'Méintlech',
            frequency_on_demand: 'Op Ufro',
            frequency_one_time: 'Eemolleg',
            data_categories_label: 'Data Categories',
            purposes_label: 'Purposes',
            safeguard: 'Safeguard',
            section_scope_title: 'Processing scope',
            section_scope_description: 'Data categories and purposes carried by this flow.',
            jurisdiction: 'Jurisdiktioun',
        },
        activities: {
            title: 'Veraarbechtungsaktivitéiten',
            new: 'Nei Aktivitéit',
            edit: 'Aktivitéit änneren',
            activity_title: 'Titel',
            category: 'Kategorie',
            legal_basis: 'Rechtsgrondlag',
            dpa_required: 'DPIA Required',
            ropa_generated: 'RoPA generéiert',
            version: 'Versioun',
            generate_ropa: 'RoPA generéieren',
            controller: 'Responsabelen',
            controller_name: 'Numm vum Responsabelen',
            controller_contact: 'Kontakt vum Responsabelen',
            legal_basis_detail: 'Detail vun der Rechtsgrondlag',
            organization_info: 'Organization Information',
            org_address: 'Adress vun der Organisatioun',
            tax_code: 'Steiercode',
            contact_person: 'Kontaktpersoun',
            contact_email: 'Kontakt-E-Mail',
            contact_phone: 'Kontakttelefon',
            select_entity: 'Wielt…',
            no_entities: 'Keng Elementer fonnt',
            dpo_name: 'DPO-Numm',
            dpo_contact: 'DPO-Kontakt',
            processor_name: 'Veraarbechternumm',
            processor_contact: 'Veraarbechterkontakt',
            data_subjects: 'Betraffe Persounen',
            data_subjects_hint: 'Betraffe Persounen (kommagetrennt)',
            recipients: 'Empfänger',
            recipients_hint: 'Empfänger (kommagetrennt)',
            security_measures: 'Sécherheetsmossnahmen',
            dpia_reference: 'DPIA-Referenz',
            no_activities: 'Keng Veraarbechtungsaktivitéite fonnt',
            confirm_archive: '"{name}" archivéieren?',
            count: '{count} Aktivitéiten',
            count_one: '{count} Aktivitéit',
            ropa_success: 'RoPA-Andrag generéiert. Gitt op Exporter fir et erofzelueden.',
            ropa_error: 'Feeler beim Generéiere vum RoPA',
            required_label: 'Erfuerderlech',
            status_draft: 'Entworf',
            status_active: 'Aktiv',
            status_under_review: 'An Iwwerpréiwung',
            status_archived: 'Archivéiert',
            decree_165_heading: 'Dekret 165 Daten Tier (Vietnam Data Law)',
            decree_165_helper: 'Record the data tier and whether the assessed transfer data is personal. Decree 356 Article 42(3) routes impact assessments for core or important personal data through personal-data protection law. Other applicable Data Law duties remain subject to review.',
            personal_data_scope: 'Personal-data scope of assessed transfer data',
            personal_data_scope_personal: 'Confirmed personal data',
            personal_data_scope_non_personal: 'Confirmed non-personal data',
            personal_data_scope_mixed: 'Mixed personal and non-personal data',
            personal_data_scope_unknown: 'Not yet determined',
            personal_data_scope_hint: 'Classify the assessed transfer data and record evidence before determining which impact-assessment route applies. No scope is inferred from the data tier.',
            personal_data_scope_evidence: 'Scope evidence and rationale',
            personal_data_scope_evidence_required: 'Record evidence before confirming personal or non-personal data scope.',
            cross_border_impact_assessment_reference: 'Cross-border impact-assessment reference',
            cross_border_impact_assessment_reference_label: 'Cross-border impact-assessment reference',
            cross_border_impact_assessment_reference_placeholder: 'Reference to the assessment for the applicable legal route',
            scope_review: 'Mixed or undetermined scope requires legal review before an impact-assessment route is selected. Record how each data component is assessed.',
            security_assessment_reference_help: 'Use only where the Decree 165 security-assessment route applies. Core or important personal-data impact assessments follow personal-data protection law under Decree 356 Article 42(3).',
            data_tier_label: 'Daten Tier',
            data_tier_help: 'Record regular, important or core data separately from personal-data scope. A tier alone does not determine the impact-assessment route.',
            data_tier_column_header: 'Daten Tier',
            data_tier_regular: 'Regelméisseg',
            data_tier_important: 'Wichteg',
            data_tier_core: 'Kär',
            transfer_mechanism_label: 'Kräiz-Grenz Transfermaart Mechanismus',
            transfer_mechanism_placeholder: 'e.g. the transfer mechanism your installed frameworks provide, with its article',
            security_assessment_reference_label: 'Sécherheet Bewäertung Referenz',
            security_assessment_reference_placeholder: 'z.B. MPS-SA-2026-001',
            data_categories_label: 'Data Categories',
            purposes_label: 'Purposes',
            registry_items_label: 'Specific registry items (optional)',
            registry_items_hint: 'Most activities leave this empty — data categories already cover RoPA. Set it when an inspection asks which exact fields an activity touches.',
            registry_items_pending: 'Loading the registry items currently linked to this activity. Saving before they load will leave those links unchanged.',
            registry_items_unavailable: 'The registry items currently linked to this activity could not be loaded. Saving will leave those links unchanged.',
            registry_items_unlisted_option: 'Linked item {id} (not in the list)',
            retention_period_description: 'Retention Period Description',
        },
        evidence: {
            title: 'Beweismaterial-Tresor',
            new: 'Neit Beweismaterial',
            edit: 'Beweismaterial änneren',
            name: 'Numm',
            evidence_type: 'Beweismaterial-Typ',
            linked_entities: 'Verlinkt Entitéiten',
            uploaded: 'Eropgelueden',
            expiry: 'Verfallsdatum',
            file_url: 'Fichier-URL',
            source_mode_label: 'Beweisquell',
            source_mode_file: 'Datei eroplueden',
            source_mode_url: 'Extern URL linken',
            attachment_label: 'Unhang',
            link_to: 'Beweismaterial verbannen mat',
            entity_type: 'Entitéitstyp',
            entity_id: 'Entitéits-ID',
            no_evidence: 'Kee Beweismaterial fonnt',
            manage_links: 'Verwalte Linken',
            unlink: 'Unlink',
            confirm_delete: 'Beweismaterial "{name}" läschen?',
            count: '{count} Elementer',
            count_one: '{count} Element',
            type_document: 'Dokument',
            type_screenshot: 'Bildschirmfoto',
            type_policy: 'Richtlinn',
            type_contract: 'Kontrakt',
            type_assessment: 'Bewäertung',
            type_correspondence: 'Korrespondenz',
            type_other: 'Anert',
            linked_type_system: 'System',
            linked_type_data_flow: 'Daten Flow',
            linked_type_processing_activity: 'Veraarbechtung Aktivitéit',
            linked_type_data_category: 'Daten Kategorie',
            linked_type_purpose: 'Zweck',
            linked_type_retention_policy: 'Retentiounspolitik',
            link_entity: 'Link zu Entitéit',
            link_success: 'Entitéit erfollegräich verlinkt',
            confirm_unlink: 'Sidd Dir sécher datt Dir dës Entitéit ofschléissen wëllt?',
            unlink_success: 'Entitéit erfollegräich ofgeschloss',
            no_linked_entities: 'Keng verlinkt Entitéiten',
            select_entity: 'Wielt…',
            no_entities: 'Keng Elementer',
        },
        exports: {
            title: 'Datenexporter',
            recent: 'Rezent Exporter',
            export_type: 'Exporttyp',
            format: 'Format',
            requested_by: 'Ugefrot vun',
            requested_at: 'Ugefrot um',
            download: 'Eroflueden',
            csv_export: 'CSV-Export',
            csv_description: 'Donnéeën am CSV-Format erofluede fir Tabellekalkul-Analyse.',
            pdf_reports: 'PDF-Rapporten',
            pdf_description: 'Formatéiert Rapporten fir Konformitéitsdokumentatioun generéieren.',
            csv_systems: 'Systeminventaire',
            csv_categories: 'Datekategorien',
            csv_flows: 'Dateflëss',
            csv_activities: 'Veraarbechtungsaktivitéiten',
            csv_cross_border: 'Grenziwwerschreidend Flëss',
            csv_retention_policies: 'Retention Politiken',
            csv_evidence: 'Beweis Elementer',
            csv_governance_assignments: 'Gouvernance Aufgaben',
            pdf_ropa: 'RoPA-Register',
            pdf_systems: 'Systembericht',
            pdf_cross_border: 'Grenziwwerschreidende Bericht',
            pdf_flow_map: 'Flossdatekaart',
            export_failed: 'Export fehlgeschloen',
            pdf_generated: 'PDF erfollegräich generéiert.',
            ropa: 'RoPA Register',
            retention_policies: 'Retention Politiken',
            governance_assignments: 'Gouvernance Aufgaben',
            col_entity: 'Export Entitéit',
            col_description: 'Beschreiwung',
            desc_ropa: 'Voll ROPA Register vun all Veraarbechtung Aktivitéiten mat legal Basis, DPIA Status, a Kontrollen.',
            desc_systems: 'Systeminventar mat Besëtzer, Datenkategorie Tags, a Konformitéitsstatus.',
            desc_activities: 'Individuell Veraarbechtungsaktivitéite mat Controller, gesetzlecher Basis an DPIA Klassifikatioun.',
            desc_categories: 'Datekategorien a Sensibilitéitsschichte benotzt iwwer Klassifikatioun an Auditen.',
            desc_purposes: 'Veraarbechtungszwecker mat der Rechtsgrondlag, déi fir all Zweck festgehale gouf.',
            desc_flows: 'Date fléisst tëscht Systemer, dorënner intern a grenziwwerschreidend Strecken.',
            desc_cross_border: 'Nëmme grenziwwerschreidend Floss, mat Transfermechanismus a Risikobewäertung.',
            desc_retention_policies: 'Retentiounspolitik, Entsuergungsmethoden a Kadens iwwerpréiwen.',
            desc_evidence: 'Beweiser verbonne mat Veraarbechtungsaktivitéiten: Certificaten, DPA Kontrakter, DPIA Berichter.',
            desc_governance_assignments: 'Gouvernance Aufgaben: Besëtzer, Genehmegungen, an ISO 27001 Referenzen.',
            unified_heading: 'Export Entitéiten',
            unified_description: 'Export all Datemapping Entitéit am CSV, XLSX, PDF, oder DOCX Format. All Exporter ginn an de Dateibrowser gespäichert.',
            export_button_label: 'Export',
            csv_download_success: 'CSV erfollegräich erofgelueden.',
            col_action: 'Export',
        },
        attestation: {
            title: 'Attestatiounscyklen',
            create: 'Neie Cyclus',
            name: 'Cyclusnumm',
            year: 'Joer',
            deadline: 'Frist',
            scope: 'Ëmfang',
            status: 'Status',
            tasks: 'Abteilungsaufgaben',
            assign_tasks: 'Aufgaben zouweisen',
            department: 'Abteilung',
            assignee: 'Zougewisenen',
            progress: 'Fortschrëtt',
            stale_entries: 'Veralte Aträg',
            detect_stale: 'Veralte erkennen',
            export_bundle: 'Paket exportéieren',
            no_cycles: 'Nach keng Attestatiounscyklen.',
            create_success: 'Attestatiounscyclus erstallt.',
            assign_success: 'Aufgaben zougewisen.',
            complete_success: 'Aufgab ofgeschloss.',
            stale_detected: 'Veralte Aträg erkannt.',
            stale_systems: 'Veralte Systemer',
            stale_flows: 'Veralte Fléiss',
            no_stale: 'Keng veralte Aträg erkannt.',
            stale_name: 'Numm',
            stale_announcement: '{0} veralte Systemer an {1} veralte Fléiss fonnt',
            last_updated: 'Lescht aktualiséiert',
            status_draft: 'Entworf',
            status_active: 'Aktiv',
            status_completed: 'Ofgeschloss',
            task_status_pending: 'Aussteend',
            task_status_in_progress: 'Am gaang',
            task_status_completed: 'Ofgeschloss',
            scope_systems: 'Systemer',
            scope_flows: 'Dateflëss',
            scope_activities: 'Aktivitéiten',
            scope_categories: 'Kategorien',
            scope_purposes: 'Zwecker',
            scope_retention: 'Opbewahrung',
            complete_cycle: 'Complete Cycle',
            confirm_activate: 'Are you sure you want to activate this cycle?',
            confirm_complete: 'Are you sure you want to complete this cycle? This action cannot be undone.',
            cycle_complete_success: 'Cycle completed successfully',
            task_status_verified: 'Verified',
            activate_success: 'Cycle activated successfully',
            activate: 'Activate Cycle',
        },
        governance: {
            title: 'Governance-Zouweisungen',
            assign: 'Besëtzer zouweisen',
            entity_type: 'Entitéitstyp',
            entity: 'Entitéit',
            role: 'Roll',
            assigned_by: 'Zougewisen vun',
            assigned_at: 'Zougewisen um',
            remove: 'Ewechhuelen',
            no_assignments: 'Keng Governance-Zouweisungen.',
            assign_success: 'Besëtzer zougewisen.',
            remove_success: 'Zouweisunge geläscht.',
            assignee: 'Zougewisene Persoun',
            department: 'Abteilung',
            all_departments: 'All Abteilungen',
            roles: {
                data_owner: 'Datenbesëtzer',
                data_steward: 'Dateverwalter',
                data_custodian: 'Datenan Hüter',
                informed: 'Informéiert',
            },
            entity_types: {
                system: 'System',
                flow: 'Datefloss',
                activity: 'Aktivitéit',
                category: 'Kategorie',
                purpose: 'Zweck',
                retention_policy: 'Opbewahrungsrichtlinn',
            },
        },
        risk_assessment: {
            title: 'Risikobewäertung',
            create: 'Nei Bewäertung',
            safeguard_type: 'Schutzmoossnahmentyp',
            adequacy_status: 'Adequatstatus',
            risk_factors: 'Risikofaktoren',
            mitigations: 'Géigemoossnamen',
            reviewer: 'Préifer',
            decision: 'Decisioun',
            approve: 'Guttgeheescht',
            reject: 'Refuséiert',
            no_assessments: 'Keng Risikobewäertungen.',
            create_success: 'Risikobewäertung erstallt.',
            approve_success: 'Bewäertung guttgeheescht.',
            reject_success: 'Bewäertung refuséiert.',
            one_per_line: 'Eent pro Zeil',
            adequacy_adequate: 'Ugemessen',
            adequacy_inadequate: 'Net ugemessen',
            adequacy_under_review: 'An Iwwerpréiwung',
            adequacy_not_assessed: 'Net bewäert',
            decision_approved: 'Guttgeheescht',
            decision_rejected: 'Ofgelehnt',
        },
        review: {
            title: 'Iwwerpréifungsaufgaben',
            description: 'Iwwerpréifungsaufgaben ginn automatesch erstallt wann eng Datekategorie virgeschloe gëtt. Fir eng nei Aufgab ze erstellen, gitt op Datekategorien a klickt op Virschléien bei enger Entworfskategorie.',
            count: 'Iwwerpréifungsaufgaben',
            summary: 'Iwwerpréifungsiwwersiicht',
            pending: 'Aussstänneg',
            resolved_today: 'Haut geléist',
            escalated: 'Eskaléiert',
            overdue: 'Iwwerfälleg (>5 Deeg)',
            all_statuses: 'All Statussen',
            status_pending: 'Aussstänneg',
            status_resolved: 'Geléist',
            status_escalated: 'Eskaléiert',
            category_name: 'Kategorie',
            system_name: 'System',
            owner: 'Besëtzer',
            resolve: 'Léisen',
            resolve_task: 'Iwwerpréifungsaufgab léisen',
            resolution: 'Léisung',
            resolution_approve: 'Geneemegen',
            resolution_modify: 'Änneren an geneemegen',
            resolution_reject: 'Oflehnen',
            resolution_escalate: 'Eskaléieren',
            notes: 'Léisungsnotizen',
            notes_required: 'Notizen néideg (mindestens 10 Zeechen) fir Änneren, Oflehnen oder Eskaléieren',
            resolved_success: 'Iwwerpréifungsaufgab geléist',
            bulk_approve: 'Massegeneemegung',
            bulk_reject: 'Masseoflehnung',
            bulk_success: '{count} Aufgabe veraarbecht',
            no_tasks: 'Keng Iwwerpréifungsaufgabe fonnt',
        },
        inventory: {
            title: 'Klassifizéierungsinventaire',
            count: 'Systemer',
            summary: 'Inventaireiwwersiicht',
            total_systems: 'Systemer am Ganzen',
            total_labels: 'Etiketten am Ganzen',
            system_name: 'System',
            label_count: 'Etiketten',
            approved_count: 'Geneemegt',
            pending_count: 'Aussstänneg',
            no_items: 'Keng Inventaire-Elementer fonnt',
        },
        exceptions: {
            title: 'Klassifizéierungsausnahmen',
            count: 'Ausnahmen',
            all_severities: 'All Schwéiergrade',
            all_statuses: 'All Statussen',
            system_name: 'System',
            severity_label: 'Schwéiergrad',
            status_open: 'Op',
            status_resolved: 'Geléist',
            status_accepted_risk: 'Akzeptéiert Risiko',
            resolve: 'Léisen',
            resolve_exception: 'Ausnahm léisen',
            resolution_type: 'Léisungstyp',
            notes: 'Notizen',
            justification_required: 'Begrënnung néideg (mindestens 10 Zeechen) fir akzeptéiert Risiko',
            resolved_success: 'Ausnahm geléist',
            no_exceptions: 'Keng Ausnahme fonnt',
        },
        history: {
            title: 'Klassifizéierungsverlaf',
            count: 'Evenementer',
            actor: 'Akteur',
            action: 'Aktioun',
            entity_type: 'Entitéitstyp',
            changes: 'Ännerungen',
            view_lineage: 'Hierkonnft uweisen',
            lineage_title: 'Klassifizéierungshierkonnft',
            no_events: 'Keng Verlafsevenementer fonnt',
            no_lineage: 'Keng Hierkonnftsdaten verfügbar',
            modified: 'Geännert',
            created: 'Erstellt',
            removed: 'Ewechgeholl',
            action_created: 'Geschaf',
            action_updated: 'Aktualiséiert',
            action_archived: 'Archivéiert',
            action_approved: 'Genehmegt',
            action_deprecated: 'Veraleet',
            entity_label: 'Etikett',
            entity_taxonomy: 'Taxonomie',
            entity_review_task: 'Iwwerpréifungsaufgab',
            entity_exception: 'Ausnahm',
            before: 'Virdrun',
            after: 'Nodrun',
        },
    },
    governance: {
        audit_trail_title: 'Governance-Audit-Spuer',
        audit_trail_subtitle: 'Iwwergräifende Auditstroum fir Governance-Aktiounen — Freigaben, Playbook-Ausféierungen, Beweislinks a méi.',
        audit_trail_item_count: 'Auditeventer',
        audit_trail_fetch_error: 'Audit-Eventer konnten net gelueden ginn.',
        audit_trail_no_events: 'Keng Audit-Eventer fir déi aktuell Filter fonnt.',
        audit_trail_entity_type: 'Entitéitstyp',
        audit_trail_entity_id: 'Entitéits-ID',
        audit_trail_action: 'Aktioun',
        audit_trail_actor: 'Akteur',
        audit_trail_timestamp: 'Zäitstempel',
        audit_trail_date_from: 'Vun',
        audit_trail_date_to: 'Bis',
        audit_trail_no_rows_to_export: 'Keng Zeilen zum Exportéieren.',
        // Gate E12.5 remediation A (Sprint 63.8.2) — the audit trail filter options.
        audit_trail_entity_types: {
            vendor: 'Vendor',
            vendor_subprocessor: 'Sub-processor',
            transfer_mechanism: 'Transfer mechanism',
            drift_event: 'Drift event',
            playbook: 'Playbook',
            execution: 'Execution',
            approval: 'Approval',
        },
        audit_trail_actions: {
            created: 'Created',
            updated: 'Updated',
            status_changed: 'Status changed',
            assigned: 'Assigned',
            approved: 'Approved',
            rejected: 'Rejected',
            evidence_linked: 'Evidence linked',
            step_completed: 'Step completed',
            executed: 'Executed',
            terminated: 'Terminated',
            exported: 'Exported',
        },
        nav: {
            vendors: 'Fournisseuren',
            subprocessors: 'Ënnertraitanten',
            cross_border: 'Grenziwwerschreidend',
            drift: 'Drift-Monitor',
            scores: 'Konformitéitsscore',
            playbooks: 'Spillbicher',
            approvals: 'Genehmegungen',
            audit_trail: 'Auditpad',
            board_report: 'Verwaltungsrotbericht',
            evidence: 'Beweismaterial',
            onboarding: 'Onboarding-Pipeline',
            dpa_reviews: 'DPA-Iwwerpréiwungen',
            periodic_reviews: 'Perioodesch Iwwerpréiwungen',
            offboarding: 'Offboarding',
        },
        vendors: {
            data_categories_processed: 'Data categories processed',
            title: 'Fournisseur-Register',
            new: 'Neie Fournisseur',
            edit: 'Fournisseur änneren',
            name: 'Numm',
            jurisdiction: 'Jurisdiktioun',
            dpa_status: 'DPA-Status',
            risk_level: 'Risikoniveau',
            onboarding_status: 'Onboarding-Status',
            contact_email: 'Kontakt-E-Mail',
            contact_name: 'Kontaktnumm',
            contact_phone: 'Kontakttelefon',
            website: 'Websäit',
            services_csv: 'Servicer (kommagetrennt)',
            country: 'Land',
            country_placeholder: 'z.B. LU',
            last_reviewed: 'Lescht iwwerpréift',
            offboard: 'Ofschloss',
            offboard_vendor: 'Fournisseur ofschléissen',
            offboard_reason: 'Grond fir Ofschloss',
            no_vendors: 'Keng Fournisseuren fonnt',
            vendor_count: '{count} Fournisseur(en)',
            all_dpa_status: 'All DPA-Statusen',
            all_risk_levels: 'All Risikoniveauen',
            all_onboarding: 'All Onboarding-Prozesser',
            fetch_error: 'Fehlgeschloen. Probéiert et w.e.g. nach eng Kéier.',
            save_error: 'Feeler beim Späicheren vum Fournisseur',
            offboard_error: 'Feeler beim Ofschléissen',
            reach: {
                action: 'Foreign reach',
                title: 'Foreign reach: {name}',
                intro: "An installed framework asks whether this vendor is within its foreign reach. A person records the answer and the reason; the vendor's country and processing locations do not decide it.",
                none_declared: 'No installed framework asks a foreign-reach question.',
                not_determined: 'Not determined',
                within: 'Within reach',
                not_within: 'Not within reach',
                decided: 'Recorded {date}',
                related: 'Provisions that act on a party within it: {list}',
                question: 'Question',
                answer: 'Is this vendor within the reach?',
                rationale: 'Reason',
                citation: 'Citation (optional)',
                save: 'Record determination',
                saved: 'Foreign-reach determination recorded.',
                load_error: 'The foreign-reach questions could not be read.',
                retry: 'Reload',
                retry_sr: 'the foreign-reach questions',
                loading: 'Reading the foreign-reach questions…',
                retry_busy: 'Reloading…',
                stale: 'This vendor was changed by someone else. The latest version has been loaded; review it and record the determination again.',
                stale_unreadable: 'This vendor was changed by someone else, and the latest version could not be read. Close this dialog and open it again before recording.',
                save_error: 'The determination could not be recorded. Try again.',
                answer_required: 'Choose an answer and give a reason.',
                country_context: 'Country on record: {country}',
                read_only: 'Recording a determination needs permission to edit vendors.',
            },
        },
        vendor_types: {
            processor: 'Optragsveraarbechter',
            sub_processor: 'Ënneroptragsveraarbechter',
            controller: 'Responsabelen',
            joint_controller: 'Gemeinsam Responsabelen',
        },
        onboarding_statuses: {
            pending_review: 'Iwwerpréiwung Ausståend',
            approved: 'Geneemegt',
            active: 'Aktiv',
            suspended: 'Ausgesat',
            offboarded: 'Ofgeschloss',
        },
        dpa_statuses: {
            not_started: 'Not Started',
            in_progress: 'In Progress',
            not_required: 'Net Néideg',
            required_missing: 'Néideg Feelt',
            draft: 'Entworf',
            under_review: 'An Iwwerpréiwung',
            signed: 'Ënnerschriwwen',
            expired: 'Ofgelaf',
        },
        risk_levels: {
            critical: 'Kritesch',
            high: 'Héich',
            medium: 'Mëttel',
            low: 'Niddreg',
        },
        approval_statuses: {
            pending: 'Ausståend',
            approved: 'Geneemegt',
            rejected: 'Ofgeleent',
            expired: 'Ofgelaf',
        },
        approval_action_types: {
            vendor_onboarding: 'Liwwerant Unbuerden',
            vendor_offboarding: 'Liwwerant Ofschloss',
            cross_border_acceptance: 'Grenziwwerschreidend Geneemegung',
            playbook_activation: 'Playbook-Aktivéierung',
            drift_accept_risk: 'Ofweichung Risiko Akzeptéiert',
            activity_activation: 'Aktivitéitsaktivéierung',
        },
        drift_check_types: {
            dpa_expiry: 'DPA-Oflaf',
            dpa_missing: 'DPA Feelt',
            review_overdue: 'Iwwerpréiwung Iwwerfälleg',
            cross_border_gap: 'Grenziwwerschreidend Lück',
            sub_processor_chain: 'Ënneroptragsveraarbechterketten',
            risk_assessment_overdue: 'Risikobewäertung Iwwerfälleg',
            retention_policy_missing: 'Opbewarungsrichtlinn Feelt',
            evidence_expiring: 'Beweiser Leeft Of',
        },
        drift_statuses: {
            open: 'Op',
            acknowledged: 'Bestätegt',
            in_progress: 'Am Lafen',
            resolved: 'Geléist',
            accepted_risk: 'Akzeptéiert Risiko',
        },
        drift_entity_types: {
            vendor: 'Liwwerant',
            system: 'System',
            data_flow: 'Datefloss',
            processing_activity: 'Veraarbechtungsaktivitéit',
            evidence_item: 'Beweismëttel',
        },
        playbook_statuses: {
            draft: 'Entworf',
            active: 'Aktiv',
            archived: 'Archivéiert',
        },
        playbook_action_types: {
            delete_data: 'Daten Läschen',
            verify_deletion: 'Läschung Verifizéieren',
            notify_vendor: 'Liwwerant Informéieren',
            confirm_receipt: 'Empfang Bestätegen',
            document_evidence: 'Dokumentéieren',
            other: 'Anert',
        },
        playbook_target_types: {
            system: 'System',
            vendor: 'Liwwerant',
        },
        dpa_chain_statuses: {
            complete: 'Komplett',
            incomplete: 'Onkomplett',
            unknown: 'Onbekannt',
        },
        sub_processor_statuses: {
            active: 'Aktiv',
            terminated: 'Ofgeschloss',
        },
        evidence_types: {
            dpa_agreement: 'DPA-Accord',
            risk_assessment: 'Risikobewäertung',
            audit_report: 'Prüfbericht',
            certification: 'Zertifizéierung',
            deletion_confirmation: 'Läschbestätegung',
            training_record: 'Formatiounsnochweis',
            incident_report: 'Tëschefallbericht',
            document: 'Dokument',
            screenshot: 'Bildschirmfoto',
            policy: 'Richtlinn',
            contract: 'Kontrakt',
            assessment: 'Bewäertung',
            correspondence: 'Korrespondenz',
            other: 'Anert',
        },
        linkable_entity_types: {
            vendor: 'Liwwerant',
            transfer_mechanism: 'Transfermechanismus',
            playbook: 'Spillbuch',
            execution: 'Ausféierung',
            system: 'System',
            activity: 'Aktivitéit',
        },
        subprocessors: {
            title: 'Ënnertraitant-Bezéiungen',
            new: 'Nei Verbindung',
            vendor: 'Fournisseur',
            subprocessor: 'Ënnertraitant',
            relationship_type: 'Bezéiungsaart',
            countries: 'Länner',
            countries_csv: 'Länner (kommagetrennt)',
            dpa_chain: 'DPA-Ketten',
            dpa_chain_status: 'DPA-Kettestatus',
            no_processors: 'Keng Veraarbechter fonnt.',
            no_subprocessors: 'Keng Ënnertraitanten fonnt.',
            processor_vendor: 'Veraarbechter-Fournisseur',
            select_processor: 'Veraarbechter auswielen',
            select_vendor: 'Fournisseur auswielen',
            services: 'Servicer',
            sub_processor_vendor_id: 'Ënnertraitant-Fournisseur-ID',
            terminate: 'Ofschléissen',
            confirm_terminate: 'Sidd Dir sécher, datt Dir dës Bezéiung ofschléisse wëllt?',
            create_error: 'Feeler beim Erstellen vun der Verbindung.',
            fetch_error: 'Feeler beim Luede vun den Ënnertraitanten.',
            processor: 'Veraarbechter',
            services_csv: 'Servicer (kommagetrennt)',
            terminate_error: 'Feeler beim Ofschléissen.',
            version_history_heading: 'Ënner-Prozessor änneren Versioun',
            change_tracking_note: 'All Ënner-Prozessor Relatioun Ännerungen sinn versioned. All Ännerung records der virun / no Staat fir Audit Trail.',
            history_retention_note: "D'Ännerungsgeschicht gëtt fir déi voll Dateschutzzäit behalen. Iwwerpréift Changement Logbicher iwwer de Verkeefer Detail Vue.",
            vendor_risk_cross_view_heading: 'Ënnerbehandler-Register (Vendor Risk)',
            vendor_risk_cross_view_source: 'Quell: Vendor Risk Register',
            vendor_risk_cross_view_empty: 'Keng Entréeën am Vendor Risk Register.',
            vendor_risk_cross_view_load_error: 'The Vendor Risk sub-processor register could not be read.',
            partial_load: 'The sub-processors of {count} processor(s) could not be read, so the register below is incomplete.',
        },
        cross_border: {
            title: 'Grenziwwerschreidende Gouvernance-Rapport',
            flows: 'Grenziwwerschreidend Flëss',
            transfers: 'Grenziwwerschreidend Transferten',
            detected_gaps: 'Festgestallte Lücken',
            safeguards: 'Schutzmoossnamen',
            cross_border_vendors: 'Grenziwwerschreidend Fournisseuren',
            destination: 'Destinatioun',
            no_cross_border_vendors: 'Keng grenziwwerschreidend Fournisseuren fonnt.',
            no_safeguard: 'Kee Schutz',
            gap_needs_redetermination: 'Recorded mechanism needs re-determining',
            gap_unverified: 'Recorded mechanism not yet verified',
            gap_recorded_mechanism: 'Recorded: {mechanism}',
            redetermine_mechanism: 'Re-determine',
            redetermine_mechanism_title: "Re-determine the vendor's transfer mechanism",
            redetermine_mechanism_intro: '{vendor} records the transfer mechanism "{mechanism}" for {source} to {destination}, and no installed framework declares it as it stands. Choose a mechanism an installed framework declares. Only the mechanism is saved.',
            redetermine_choose_first: 'Choose a transfer mechanism first.',
            redetermine_success: 'Transfer mechanism re-determined.',
            redetermine_error: 'Could not re-determine the transfer mechanism. Please try again.',
            processing_locations: 'Veraarbechtungsstanduerter',
            export_error: 'Feeler beim Exportéiere vum Rapport.',
            export_pdf: 'PDF exportéieren',
            export_success: 'Rapport erfollegräich exportéiert.',
            exporting: 'Gëtt exportéiert...',
            fetch_error: 'Feeler beim Luede vun de grenziwwerschreidende Donnéeën.',
            source: 'Quell',
            pdpl_p2_heading: 'PDPL-P2 Cross-Border Transfer Compliance',
            dossier_prep_note: 'Dossier Virbereedung: Sammelt Impakt Bewäertung, legal Basis a Schutzdokumentatioun virum Areeche.',
            filing_tracking_note: 'Areeche Tracking: Iwwerwaacht Soumissioun Status mat der Autoritéit (PDPC) vun der initialer Uwendung duerch Genehmegung.',
            supplement_loop_note: 'Ergänzungsschleife: reagéiert op Autoritéitsinformatiounsufroen a schéckt geännert Dossieren erëm of, wéi néideg.',
            official_forms_note: 'Official forms: choose the official form this filing uses in the Form type selector when you create it.',
            vendor_search_label: 'Fournisseursich',
            vendor_search_placeholder: 'No Fournisseursnumm sichen…',
            jurisdiction_filter_label: 'Juridiktioun',
            filings_section_heading: 'Kräiz-Grenz Filings',
            filings_empty: 'Nach keng Umeldungen. Schafen engem dossier ufänken.',
            filings_create_button: 'Nei Areeche',
            filings_table_id: 'Aschreiwung ID',
            filings_table_jurisdiction: 'Juridictioun',
            filings_table_type: 'Form Typ',
            filings_table_status: 'Status',
            filings_table_updated: 'Aktualiséiert',
            filings_table_open: 'Oppen',
            filings_detail_title: 'Kräiz-Grenz Areeche',
            filings_back_to_list: 'Zréck op Cross-Border',
            filings_status_heading: 'Liewenszyklus',
            filings_evidence_heading: 'Beweiser',
            filings_history_heading: 'Audit Trail',
            filings_field_filing_id: 'Aschreiwung ID',
            filings_field_jurisdiction: 'Juridictioun',
            filings_field_type: 'Form Typ',
            filings_field_status: 'Status',
            filings_field_reference: 'Autoritéit Referenz',
            filings_field_mps_receipt: 'MPS Empfang',
            filings_field_submitted_at: 'Erlaabt am',
            filings_field_acknowledged_at: 'Unerkannt um',
            filings_field_archived_at: 'Archivéiert op',
            filings_field_version: 'Versioun',
            filings_field_notes: 'Notizen',
            filings_action_advance: 'Viraus Status',
            filings_action_reject: 'Refuséieren',
            filings_action_archive: 'Archiv',
            filings_action_submit: 'Mark presentéiert',
            filings_action_acknowledge: 'Mark unerkannt',
            filings_action_resume_draft: 'Weiderféieren als Entworf',
            filings_status_draft: 'Entworf',
            filings_status_assessed: 'Bewäert',
            filings_status_pending_review: 'Erwaardung iwwerpréiwen',
            filings_status_ready_to_file: 'Prett ze Fichier',
            filings_status_submitted: 'Erlaabt',
            filings_status_acknowledged: 'Unerkannt',
            filings_status_rejected: 'Refuséiert',
            filings_status_archived: 'Archivéiert',
            filings_type_none_installed: 'No fileable form is installed for this organisation',
            filings_new_dialog_title: 'Nei grenziwwerschreidend Areechung',
            filings_new_form_jurisdiction: 'Juridictioun',
            filings_new_form_type: 'Form Typ',
            filings_new_form_notes: 'Notizen (optional)',
            filings_new_submit: 'Schafen',
            filings_new_cancel: 'Ofbriechen',
            filings_reason_label: 'Grond',
            filings_reference_label: 'Autoritéit Referenz Zuel',
            filings_mps_receipt_label: 'MPS Empfangsreferenz',
            filings_create_error: 'Ausgefall fir Areeche ze kreéieren.',
            filings_create_success: 'Dossier erstallt.',
            filings_update_error: "Ausgefall op d'Aktualiséierung vun der Datei.",
            filings_update_success: 'Dossier aktualiséiert.',
            filings_fetch_error: "Ausgefall op d'Aschreiwungen.",
            filings_archive_confirm: 'Dësen Dossier archivéieren? Dëst kann net réckgängeg gemaach ginn.',
            filings_status_rejected_detour: 'Areeche gouf no Iwwerpréiwung refuséiert. Resume als Entworf fir de Flow nei ze starten.',
            recent_exports_heading: 'Rezent Exporter',
            recent_exports_empty: 'Keen Export nach. Klickt op "Export PDF" fir een ze generéieren.',
            filings_forms_empty: 'No regulatory forms generated yet. Use the Forms module to generate the required outputs for this filing.',
            filings_forms_heading: 'Regulatory Forms',
            filings_type_placeholder: 'Select a form type…',
        },
        drift: {
            title: 'Konfiguratiounsdrift-Monitor',
            drift_type: 'Driftaart',
            severity: 'Schwéiergrad',
            detected_at: 'Festgestallt um',
            resolve: 'Léisen',
            all_check_types: 'All Prüfaarten',
            all_entity_types: 'All Entitéitstypen',
            event_count: 'Unzuel vun Evenementer',
            accept_risk: 'Risiko akzeptéieren',
            acknowledge: 'Bestätegen',
            assign_error: 'Feeler beim Zouweisen.',
            assign_event: 'Zouweisen',
            assigned: 'Zougewisen',
            enter_resolution_notes: 'Léisungsnotizen aginn...',
            enter_user_id: 'Benotzer-ID aginn...',
            entity: 'Entitéit',
            fetch_error: 'Feeler beim Luede vun Drift-Evenementer.',
            no_events: 'Keng Drift-Evenementer fonnt.',
            resolution_notes: 'Léisungsnotizen',
            update_status_error: 'Feeler beim Aktualiséiere vum Status.',
        },
        scores: {
            title: 'Konformitéitsscore-Iwwersiicht',
            overall: 'Gesamtkonformitéitsscore',
            breakdown: 'Score-Opdeelung no Modul',
            trend: 'Historesche Verlaf',
            module: 'Modul',
            score: 'Bewäertung',
            grade: 'Grad',
            calculated_at: 'Berechent um',
            generate_board_report: 'Verwaltungsrotrapport generéieren',
            lang_english: 'Englesch',
            lang_vietnamese: 'Vietnamesesch',
            fetch_error: 'Feeler beim Luede vun de Konformitéitsscoren.',
            generate_report: 'Rapport generéieren',
            generating: 'Gëtt generéiert...',
            recommendations: 'Empfeelungen',
            report_error: 'Feeler beim Generéiere vum Rapport.',
            factor_owner_assigned: 'Besëtzer zougewisen',
            factor_data_categories_linked: 'Datekategorien verlinkt',
            factor_purposes_linked: 'Zwecker verlinkt',
            factor_data_flows_documented: 'Dateflëss dokumentéiert',
            factor_processing_activity_linked: 'Veraarbechtungsaktivitéit verlinkt',
            factor_dpa_status: 'DPA-Status',
            factor_retention_policy_linked: 'Opbewahrungspolitik verlinkt',
            factor_last_reviewed: 'Lescht iwwerpréift bannent dem Schwellwäert',
            factor_risk_assessment_current: 'Risikobewäertung aktuell',
            factor_sub_processors_documented: 'Ënnerveraarbechter dokumentéiert',
            factor_cross_border_safeguards: 'Grenziwwerschreidend Schutzmoossnamen',
            factor_contact_info_complete: 'Kontaktinformatiounen komplett',
            factor_onboarding_approved: 'Onboarding guttgeheescht',
            factor_avg_system_score: 'Duerchschnëttleche Systempunktwäert',
            factor_avg_vendor_score: 'Duerchschnëttlechen Ubidderpunktwäert',
            factor_processing_activity_coverage: 'Ofdeckung vun de Veraarbechtungsaktivitéiten',
            factor_ropa_completeness: 'RoPA-Vollständegkeet',
            factor_open_drift_events: 'Oppe Ofwäichungsevenementer',
        },
        playbooks: {
            title: 'Läsch-Playbooks',
            new: 'Neit Playbook',
            edit: 'Playbook änneren',
            steps: 'Schrëtt',
            add_step: 'Schrëtt derbäisetzen',
            remove_step: 'Schrëtt ewechhuelen',
            data_categories_csv: 'Datekategorien (kommagetrennt)',
            estimated_duration: 'Geschate Dauer',
            estimated_duration_label: 'Geschate Dauer',
            evidence_description: 'Beweisbeschreiwung',
            no_playbooks: 'Keng Playbooks fonnt.',
            playbook_count: 'Unzuel vun Playbooks',
            requires_evidence: 'Beweismaterial erfuerderlech',
            step_label: 'Schrëtt-Etikett',
            target_name: 'Zil Numm',
            target_type: 'Ziltyp',
            action_type: 'Aktiounstyp',
            archive_error: 'Feeler beim Archivéieren.',
            confirm_archive: 'Sidd Dir sécher, datt Dir dëst Playbook archivéiere wëllt?',
            data_categories: 'Datekategorien',
            days: 'Deeg',
            fetch_error: 'Feeler beim Luede vun de Playbooks.',
            sla_days: 'SLA (Deeg)',
            target_id: 'Zil-ID',
            template_class_heading: 'Schabloun Klassifikatioun',
            official: 'Offiziell',
            internal: 'Intern',
            classification: 'Klassifikatioun',
            official_note: 'Offiziell: reglementaresche Mandat Templates ausgeriicht mat PDPL, GDPR, oder sektorspezifesch Ufuerderungen.',
            internal_note: 'Intern: Organisatioun-spezifesch Prozeduren net aus reglementaresche Schabloune ofgeleet.',
            active_executions_heading: 'Aktiv Ausféierungen',
            no_active_executions: 'Keng aktiv Ausféierungen.',
            playbook_label: 'Spillbuch',
            ticket_id_label: 'Ticket',
            progress_label: 'Fortschrëtt',
            actions_label: 'Aktiounen',
            view_steps_label: 'Schrëtt weisen',
            execution_detail_title: 'Ausféierungsdetail',
            playbook_missing: 'Playbook net fonnt.',
            evidence_required_label: 'Beweis erfuerderlech',
            upload_evidence_label: 'Beweis eroplueden',
            mark_complete_label: 'Als fäerdeg markéieren',
            evidence_required_toast: 'Beweis erfuerderlech ier dëse Schrëtt ofgeschloss gëtt.',
            evidence_blocking_message: 'Hänk e Beweisfichier drun fir weiderzemaachen.',
            step_completed_toast: 'Schrëtt ofgeschloss.',
            executions_fetch_error: 'Ausféierungen konnten net gelueden ginn.',
            step_evidence_default_name: 'Beweis fir Ausféierungsschrëtt',
            attached_evidence_label: 'Ugehéngt Beweiser',
        },
        approvals: {
            // Sprint 63.30.0 (#485) — approval expiry.
            expiry_setting_heading: 'Approval expiry',
            expiry_setting_label: 'Approval requests expire after (days)',
            expiry_setting_hint: 'Whole days from {min} to {max}. Applies to requests made after the change. A request still undecided at its expiry is marked Expired and its requester is told.',
            expiry_setting_value: 'Approval requests expire after {days} days.',
            expiry_setting_save: 'Save expiry',
            expiry_setting_saved: 'Approval expiry set to {days} days',
            expiry_setting_load_failed: 'The approval expiry setting could not be read.',
            expiry_setting_save_failed: 'The approval expiry setting could not be saved.',
            expires_on: 'Expires {date}',
            expired_undecided_note: 'Past its expiry; it can no longer be decided.',
            already_decided: 'This request has already been decided. The list has been refreshed to show its decision.',
            expired_refused: 'This request is past its expiry and can no longer be decided. The list has been refreshed.',
            title: 'Genehmegungsworkflows',
            new: 'Nei Genehmegung',
            edit: 'Genehmegung änneren',
            request_type: 'Ufro-Aart',
            requester: 'Ufroer',
            approve: 'Genehmegen',
            reject: 'Refuséieren',
            justification: 'Begrënnung',
            all_action_types: 'All Aktiounstypen',
            decided_by: 'Entscheet vun',
            no_approvals: 'Keng Genehmegungen fonnt.',
            approval_count: 'Unzuel vun Genehmegungen',
            approval_notes_optional: 'Notizen (optional)',
            approve_error: 'Feeler beim Genehmegen.',
            approve_request: 'Ufro genehmegen',
            decision: 'Entscheedung',
            entity: 'Entitéit',
            fetch_error: 'Feeler beim Luede vun de Genehmegungen.',
            reject_error: 'Feeler beim Refuséieren.',
            reject_request: 'Ufro refuséieren',
            rejection_notes_required: 'Refus-Notizen obligatoresch',
            rejection_notes_required_msg: 'Gitt w.e.g. e Grond fir de Refus un.',
            requested_at: 'Ugefrot um',
            decision_rules_heading: 'How decisions are recorded',
            decision_rule_second_person: 'A request is decided by someone other than the person who made it. You cannot approve your own request.',
            decision_rule_notes: 'Rejecting needs a written reason. Approving can carry notes, and either decision can attach supporting evidence.',
            decision_rule_scope: 'A decision is recorded here and in the governance audit trail. It does not carry out the action the request is about.',
            own_request_note: 'Your request. Another person decides it.',
            supporting_evidence_label: 'Ënnerstëtzend Beweis (optional)',
            decision_evidence_name: 'Decisiounsbeweis',
        },
        evidence: {
            title: 'Gouvernance-Beweismaterial',
            new: 'Neit Beweismaterial',
            link_to: 'Verbannen mat',
            entity_id_placeholder: 'Entitéits-ID',
            file_path_placeholder: '/Wee/zur/Datei',
            evidence_label: 'Beweisetikett',
            file_path: 'Fichierpad',
            source_mode_label: 'Beweisquell',
            source_mode_file: 'Datei eroplueden',
            source_mode_url: 'Extern URL linken',
            attachment_label: 'Unhang',
            item_count: 'Unzuel vun Elementer',
            link_button: 'Beweismaterial verbannen',
            confirm_delete: 'Sidd Dir sécher, datt Dir dëst Beweismaterial läsche wëllt?',
            enter_entity_id: 'Entitéits-ID aginn...',
            entity_id: 'Entitéits-ID',
            entity_type: 'Entitéitstyp',
            expires: 'Leeft of',
            expires_at: 'Leeft of um',
            fetch_error: 'Feeler beim Luede vum Beweismaterial.',
            link_error: 'Feeler beim Verbannen vum Beweismaterial.',
            no_evidence: 'Kee Beweismaterial fonnt.',
            tags_csv: 'Tags (kommagetrennt)',
            uploaded: 'Eropgelueden',
            url: 'URL',
            framework_tag_heading: 'Kader-Tagged Beweis',
            framework_tag_note: 'All Beweisrekord erfaasst Kadercode, Schauspiller an Zäitstempel. Filter no Kader fir Audit-relevant records ze isoléieren.',
            framework_code: 'Kader Code',
            filter_by_framework: 'Filter no Kadercode ...',
            actor: 'Schauspiller',
        },
        certification: {
            not_applicable: 'Net zoutreffend',
            not_certified: 'Net zertifiéiert',
            pending: 'Ausstänneg',
            certified: 'Zertifiéiert',
            revoked: 'Zréckgezunn',
        },
        onboarding_pipeline: {
            title: 'Onboarding-Pipeline',
            new_workflow: 'Neien Onboarding-Workflow',
            vendor_id: 'Liwweranten-ID',
            stages: 'Phasen',
            current_stage: 'Aktuell Phase',
            advance: 'Phase virubréngen',
            advance_success: 'Phase erfollegräich virubruecht',
            workflow_status: 'Workflow-Status',
            initiated: 'Ugefaangen',
            in_progress: 'Am Gaang',
            completed: 'Ofgeschloss',
            cancelled: 'Ofgebrach',
            no_workflows: 'Keng Onboarding-Workflows fonnt',
            stages_placeholder: 'Phase 1, Phase 2, Phase 3',
            assigned_to: 'Zougewisen un',
            notes: 'Notizen',
            pdpl_lifecycle_heading: 'PDPL Verkeefer Liewenszyklus - Onboarding',
            due_diligence_note: 'Due Diligence Etapp ass obligatoresch ier Dir all Prozessor ënner PDPL onboard. Verifizéiert Datenveraarbechtung Ëmfang, legal Basis a Sécherheetsmoossnamen.',
            contract_review_note: 'Kontrakt Iwwerpréiwung muss DPA Klauselen, Dateschutzlimiten, an Ënnerprozessor Restriktiounen bestätegen.',
            create_dpa_review_cta: 'DPA-Iwwerpréiwung erstellen',
            dpa_review_linked: 'DPA-Iwwerpréiwung verlinkt',
        },
        dpa_review: {
            title: 'DPA-Iwwerpréiwungen',
            new_review: 'Nei DPA-Iwwerpréiwung',
            review_type: 'Iwwerpréiwungstyp',
            findings: 'Resultater',
            outcome: 'Resultat',
            complete_review: 'Iwwerpréiwung ofschléissen',
            complete_success: 'Iwwerpréiwung erfollegräich ofgeschloss',
            link_assessment: 'Sécherheetsbewäertung verbannen',
            link_success: 'Bewäertung erfollegräich verbannen',
            assessment_id: 'Bewäertungs-ID',
            combined_title: 'Kombinéiert Bewäertung',
            no_reviews: 'Keng DPA-Iwwerpréiwunge fonnt',
            telecom_disclosure_heading: 'Telecom Offenbarung Gouvernance',
            disclosure_trigger_note: 'Offenbarung ausgeléist Rezensiounen erfaassen Telekom Abonnent Daten Deele Eventer pro VN Telecom Law.',
            telecom_t2_note: "TELECOM-T2 Niveau Genehmegung ass erfuerderlech ier Dir Abonnentdaten un d'Autoritéite verroden.",
            quarterly_log_note: "Benotzt de Véierellog fir all Verëffentlechungsevenementer ze dokumentéieren an e kontinuéierleche Audit Trail z'erhalen.",
        },
        periodic_review: {
            title: 'Perioodesch Iwwerpréiwungen',
            new_review: 'Nei perioodesch Iwwerpréiwung',
            review_type: 'Iwwerpréiwungstyp',
            due_date: 'Fällegkeetsdatum',
            next_review: 'Nächsten Iwwerpréiwungsdatum',
            trigger_reassessment: 'Neibewäertung ausléisen',
            reassessment_reason: 'Grond fir Neibewäertung',
            reassessment_success: 'Neibewäertung erfollegräich ausgeléist',
            complete_success: 'Iwwerpréiwung erfollegräich ofgeschloss',
            scheduled: 'Geplangt',
            in_progress: 'Am Gaang',
            completed: 'Ofgeschloss',
            overdue: 'Iwwerfälleg',
            material_change: 'Wesentlech Ännerung',
            no_reviews: 'Keng perioodesch Iwwerpréiwunge fonnt',
            mark_complete: 'Als ofgeschloss markéieren',
            pdpl_periodic_heading: 'PDPL Verkeefer Liewenszyklus - Periodesch Bewäertungen',
            scheduled_review_note: "Periodesch Rezensiounen mussen op d'mannst all Joer fir all aktive Prozessoren geplangt ginn. Iwwerdeems Rezensiounen ausléisen Konformitéitsalarmer.",
            material_change_note: 'Materiell Ännerungen an der Dateveraarbechtung erfuerderen direkt nei Bewäertung onofhängeg vum Zäitplang.',
            overdue_badge: 'Iwwerfällig',
            overdue_aria: 'Iwwerfälleg Iwwerpréiwung',
            only_overdue_filter: 'Nëmmen iwwerfälleg',
        },
        offboarding: {
            title: 'Liwweranten-Offboarding',
            initiate: 'Offboarding ufänken',
            initiate_success: 'Offboarding erfollegräich ugefaangen',
            reason: 'Offboarding-Grond',
            data_return_required: 'Dateréckgab erfuerderlech',
            verify_data: 'Dateréckgab iwwerpréiwen',
            verify_success: 'Dateréckgab erfollegräich iwwerpréift',
            sign_off: 'Ënnerschreiwen',
            sign_off_success: 'Offboarding erfollegräich ënnerschriwwen',
            status_initiated: 'Ugefaangen',
            status_data_pending: 'Dateréckgab ausstänneg',
            status_data_verified: 'Dateréckgab iwwerpréift',
            status_completed: 'Ofgeschloss',
            status_cancelled: 'Ofgebrach',
            task_id: 'Aufgaben-ID',
            no_tasks: 'Keng Offboarding-Aufgabe fonnt',
            evidence_id: 'Bewäis-ID',
            pdpl_offboarding_heading: 'PDPL Verkeefer Liewenszyklus - Offboarding & Läschen',
            deletion_capture_note: "Offboarding muss d'Dateläsche Bestätegung vum Verkeefer erfaassen. Verifizéieren d'Donnéeën zréck virum Ofschloss.",
            evidence_attachment_note: 'Befestegt Läschbeweiser (Zertifikater, Screenshots, Verkeeferbestätegungen) fir den Offboarding-Rekord ofzeschléissen.',
            verify_mode_label: 'Verifizéierungsmethod',
            verify_mode_upload: 'Nei Datei eroplueden',
            verify_mode_link: 'Existent Beweis linken',
            verify_mode_none: 'Keng Donnéeën zréckzeginn',
            upload_data_return_evidence: 'Beweis fir Donnéeënréckgab eroplueden',
            no_data_justification_label: 'Begrënnung',
            no_data_justification_placeholder: 'Erkläer firwat keng Donnéeën ze retournéieren sinn',
            no_data_justification_hint: 'Mindestens 20 Zeechen',
            upload_required_toast: 'Hänk eng Datei drun fir weiderzemaachen',
            evidence_link_required_toast: 'Wiel e Beweis fir weiderzemaachen',
            no_data_justification_required_toast: 'Gitt eng Begrënnung vu mannst 20 Zeechen',
            data_return_evidence_name: 'Beweis fir Donnéeënréckgab',
            verify_before_signoff: 'Verify data return before sign-off',
        },
        dpa_review_types: {
            initial: 'Ufank',
            annual: 'Jäerlech',
            renewal: 'Erneierung',
            incident_triggered: 'Duerch Tëschefall ausgeléist',
        },
        periodic_review_types: {
            initial: 'Ufank',
            annual: 'Jäerlech',
            renewal: 'Erneierung',
            incident_triggered: 'Virfall-ausgeléist',
            material_change: 'Wesentlech Ännerung',
            disclosure_triggered: 'Offenlegung-ausgeléist',
            quarterly_log: 'Quartals-Rapport',
        },
        dpa_review_statuses: {
            pending: 'Waardend',
            in_progress: 'Am Gaang',
            completed: 'Ofgeschloss',
            cancelled: 'Annuléiert',
        },
        onboarding_stage_names: {
            due_diligence: 'Due Diligence',
            contract_review: 'Kontraktkontroll',
            security_assessment: 'Sécherheetsbewäertung',
            data_mapping: 'Datenmapping',
            approval: 'Geneemegung',
            integration: 'Integratioun',
        },
        access_denied_title: 'Zougang verweigert',
        access_denied: 'Dir hutt keng Berechtigung fir dës Säit ze gesinn. Kontaktéiert Ären Administrateur.',
    },
    consent: {
        nav: {
            policies: 'Politiken',
            purposes: 'Zweck-Katalog',
            workflows: 'Aarbechtsofleef',
            snapshots: 'CMP-Snapshots',
            change_awareness: 'Ännerungs-Opmierksamkeet',
            audit: 'Zoustëmmungs-Audit',
            evidence: 'Consent Evidence',
            marketing_consent: 'Marketing Consent',
            marketing_suppression: 'Suppression Register',
            marketing_suppression_enforcement: 'Suppression Enforcement',
            marketing_sender_name_certificates: 'Sender-name Certificates',
        },
        gap_analysis: {
            nav_label: 'Gap Analyse',
            page_title: 'Zoustëmmung Gap Analyse',
            page_subtitle: 'Forseti checks your active consent purposes against the consent families of your installed frameworks, and suggests a remediation for each critical or high gap. Only installed families are analysed.',
            framework_picker_label: 'Kaderen',
            org_flags_label: 'Organisatioun Kontext',
            flag_withdrawal: 'Huet global Réckzuch Mechanismus dokumentéiert',
            flag_parental: 'Huet Elteren Zoustëmmung Flux fir ënner 16 Sujeten',
            flag_cross_border: 'Huet grenziwwerschreidend Areechung op Rekord',
            run_label: 'Run Spalt Analyse',
            running_label: 'Lafen…',
            requires_advisor_label: 'Verlaangt ai_advisor:chat Erlaabnis.',
            results_heading: 'Gaps surfaced ({count})',
            tokens_label: '≈ {count} Tokens',
            empty_label: 'Keng Lücken festgestallt fir déi gewielte Kaderen.',
            remediation_label: 'Sanéierung proposéiert:',
            dismiss_label: 'Entlooss als net applicabel',
            resolve_label: 'Opléisen',
            dismissed_label: 'Entlooss als net applicabel',
            resolved_label: 'Geléist',
            // Sprint 62.27.0 gate E12.5 pass 2 — R-57: English until Phase 25.
            families_loading: 'Reading the installed frameworks…',
            families_load_error: 'The installed frameworks could not be read, so no family can be offered for analysis.',
            families_retry_subject: 'the installed frameworks',
            families_none: 'None of your installed frameworks belongs to a family this analysis reads, so there is nothing to analyse.',
            family_instruments: 'Installed: {codes}',
            analysed_label: 'Analysed: {families}',
            not_analysed_label: 'Not installed, so not analysed: {families}',
            nothing_analysed: 'Nothing was analysed: none of the selected families is installed, so no gap can be reported.',
            acknowledged_count_label: '{count} acknowledged finding(s), not counted as open gaps',
            run_failed: 'The gap analysis could not be run.',
            triage_failed: 'The decision could not be recorded.',
            purpose_scope: 'Purpose: {name}',
            org_wide_scope: 'Organisation-wide',
            attributes: {
                legal_basis_set: 'Lawful basis recorded',
                withdrawal_mechanism: 'Withdrawal mechanism',
                granular_per_purpose: 'Consent separable per purpose',
                parental_consent_for_minors: 'Consent for children',
                cross_border_filing: 'Cross-border transfer record',
                retention_period: 'Retention period',
                sensitive_data_ground_review: 'Sensitive-data ground review',
            },
            acknowledge_label: 'Acknowledge review',
            acknowledge_title: 'Acknowledge the sensitive-data ground review',
            acknowledge_description: 'Purpose: {name}. Record that you reviewed whether your installed frameworks set a condition for processing sensitive data for this purpose beyond its lawful basis and, if so, which. Later analyses show the finding as acknowledged until the purpose’s sensitivity or its recorded ground changes, or a framework family you did not review is installed.',
            acknowledge_note_label: 'Note (required)',
            acknowledge_note_hint: 'What you reviewed, and whether a condition is recorded on the purpose and, if so, which, with its article.',
            acknowledge_submit: 'Acknowledge',
            acknowledge_done: 'Review acknowledged',
            acknowledge_failed: 'The acknowledgement could not be recorded.',
            acknowledged_by: 'Acknowledged by {name} on {date}: {note}',
            acknowledgement_lapsed: 'An acknowledgement by {name} on {date} lapsed because {reasons}.',
            lapse_sensitivity_changed: 'the purpose’s sensitivity changed',
            lapse_basis_changed: 'its recorded ground changed',
            lapse_family_added: 'a family it did not review was installed: {families}',
            lapse_reason_joiner: ' and ',
        },
        import: {
            title: 'Zoustëmmungs-Workflows importéieren',
            nav_title: 'Massenimport',
            description: "Luet eng CSV-Datei erop fir Zoustëmmungs-Workflows am Grupp z'importéieren.",
            select_file: 'CSV-Datei auswielen',
            file_help: 'CSV mat Iwwerschrëften déi mat de Workflow-Felder iwweranenee stëmmen.',
            column_count_help: 'A row whose number of values differs from the number of columns in the header is not imported; it is listed under Errors with its row number.',
            required_columns_help: 'Néideg Spalten: Workflow-Typ, Dateninhaber, Kanal.',
            file_too_large: 'Fichier ze grouss. Maximal Gréisst ass 2MB.',
            preview: 'Virschau',
            columns_detected: 'Erkannte Spalten: {count}',
            column_mapping: 'Spalten-Zuweisung',
            import_button: 'Importéieren',
            import_complete: 'Import ofgeschloss',
            import_error: 'Import feelgeschloen',
            result: 'Importresultat',
            total: 'Gesamt',
            imported: 'Importéiert',
            skipped: 'Iwwersprongen',
            failed: 'Feelgeschloen',
            errors: 'Feeler',
            error_row: 'Zeil',
            error_field: 'Feld',
            error_message: 'Noriicht',
            history: 'Importverlaf',
            no_history: 'Keen Importverlaf.',
            history_file: 'Dateinumm',
            history_date: 'Datum',
            create_profiles_label: 'Dateninhaber-Profiler fir nei Sujete erstellen',
            create_profiles_help: "Imports ouni passend Profil erstellen automatesch een. Ofhaken fir nëmmen Workflows z'importéieren.",
            profiles_created: 'Profiler erstallt',
            profiles_matched: 'Profiler zougeordent',
            subject_kind_help: 'Optional Spalten: subject_kind, subject_email, subject_display_name, subject_phone, external_ref_key.',
            error_subject_invalid_email: 'Ongëlteg E-Mail an der subject_email-Spalt.',
            error_subject_unknown_kind: 'Onbekannten subject_kind-Wäert. Erwaart: email, visitor_uuid, employee_id, student_id, external_ref.',
            error_missing_external_ref_key: 'external_ref_key ass néideg wann subject_kind=external_ref',
        },
        policies: {
            title: 'Zoustëmmungspolitiken',
            new: 'Nei Politik',
            edit: 'Politik änneren',
            name: 'Numm',
            version: 'Versioun',
            policy_text: 'Politiktext',
            publish: 'Verëffentlechen',
            versions: 'Versiounen',
            hide_versions: 'Verstoppen Versiounen',
            submit_for_review: 'Ofschécken fir Iwwerpréiwen',
            suspend: 'Spären',
            retire: 'Stillleeën',
            jurisdictions: 'Jurisdiktiounen',
            jurisdictions_csv: 'Jurisdiktiounen (comma-separated)',
            approval_mode: 'Genehmegung Modus',
            reviewer_ids: 'Préifer IDen (comma-separated)',
            tags_csv: 'Tags (kommagetrennt)',
            effective_date: 'Effektiv Datum',
            review_date: 'Iwwerpréiwen Datum',
            consent_model_statement: 'Zoustëmmung Modell Statement',
            consent_model_placeholder: 'Fakultativ Zoustëmmung Modell statement...',
            default_settings: 'Standard Astellungen (JSON)',
            changes: 'Ännerungen',
            fetch_error: 'Konnt net fetch Zoustëmmung policies. Probéiert et nach eng Kéier.',
            save_error: 'Konnt net save Zoustëmmung Richtlinn. Probéiert et nach eng Kéier.',
            submit_review_error: 'Konnt net submit Richtlinn fir Iwwerpréifung. Probéiert et nach eng Kéier.',
            activate_error: 'Konnt net activate Richtlinn. Probéiert et nach eng Kéier.',
            suspend_error: 'Konnt net suspend Richtlinn. Probéiert et nach eng Kéier.',
            retire_error: 'Konnt net retire Richtlinn. Probéiert et nach eng Kéier.',
            submit_review_success: 'Politik presentéiert fir Iwwerpréiwung.',
            activate_success: 'Politik aktivéiert.',
            suspend_success: 'Politik suspendéiert.',
            retire_success: 'Politik pensionnéierte.',
            versions_error: 'Konnt net fetch Richtlinn versions. Probéiert et nach eng Kéier.',
            invalid_json: 'Ongëlteg JSON an Standard Astellungen field. Please try again.',
            page_title: 'Zoustëmmung Politiken',
            version_updated: 'Aktualiséiert',
            version_initial: 'Éischt Versioun',
            json_format: 'JSON Format',
            sensitive: 'Sensibel',
            flag_sensitive: 'Fändel sensibel',
            flag_sensitive_title: 'Fändelpolitik als sensibel',
            require_enhanced_review: 'Verlaangt verstäerkte Iwwerpréiwung',
            enhanced_review_success: 'Erweidert Iwwerpréiwung Ufuerderung aktivéiert.',
            return_to_draft: 'Return to draft',
            return_to_draft_hint: 'End this review round and make the policy editable again',
            return_to_draft_title: 'Return policy to draft',
            return_to_draft_body: "The decisions recorded in this review round are kept in the policy's approval history. The policy becomes a draft, to be edited and submitted for review again.",
            return_to_draft_reason: 'Reason (required)',
            return_to_draft_reason_required: 'Enter the reason for returning this policy to draft.',
            return_to_draft_success: 'Policy returned to draft.',
            return_to_draft_error: 'The policy could not be returned to draft.',
            in_review_locked_note: 'In review: the policy cannot be edited, flagged sensitive or routed to enhanced review. Return it to draft to change it.',
            refusal_policy_in_review: 'This policy is in review and cannot be changed. Return it to draft to change it.',
            refusal_policy_not_in_review: 'This policy is no longer in review. The list has been refreshed.',
            refusal_policy_changed_concurrently: 'The policy changed while this was being saved. The list has been refreshed; check the policy and try again.',
            refusal_not_a_named_reviewer: 'Only the reviewers this policy names can decide it.',
            select_categories: 'Wielt déi applicabel PDPL Artikel 2 sensibel Datekategorien aus:',
            no_policies: 'Keng Zoustëmmungsrichtlinnen fonnt. Erstellt Är éischt Richtlinn fir unzefänken.',
            approval_success: 'Richtlinn genehmegt.',
            reviewers_label: 'Iwwerpréifer',
            enhanced_review_title: 'Erweidert Iwwerpréiwung erfuerderlech',
            enhanced_review_badge: 'Erweidert Iwwerpréiwung erfuerderlech',
            enhanced_review_status_pending: 'Iwwerpréiwung ausstehenд',
            enhanced_review_status_approved: 'Iwwerpréiwung genehmegt',
            enhanced_review_status_rejected: 'Iwwerpréiwung ofgeleent',
            enhanced_review_reviewed_by: 'Iwwerpréift vun',
            enhanced_review_notes: 'Iwwerpréiwungsnotizen',
            enhanced_review_approve: 'Iwwerpréiwung genehmégen',
            enhanced_review_reject: 'Iwwerpréiwung ofléinen',
            enhanced_review_notes_required: 'Iwwerpréiwungsnotizen sinn erfuerderlech',
            enhanced_review_approved: 'Erweidert Iwwerpréiwung genehmegt',
            enhanced_review_rejected: 'Erweidert Iwwerpréiwung ofgeleent — Richtlinn op Entworf zréckgesat',
            enhanced_review_reset_to_draft: 'Erweidert Iwwerpréiwung ofgeleent — Richtlinn op Entworf zréckgesat',
            enhanced_review_reject_warning: "D'Ofleening setzt dës Richtlinn op de Status Entworf zréck. Den DSB muss se no der Behiewung vun de Problemer erneut anzereichen.",
            submitted_modal_title: 'Fir erweidert Iwwerpréiwung agereecht',
            submitted_modal_body: 'Dës Richtlinn gouf agereecht. E Juristesch/Dateschutz-Iwwerpréifer muss se genehmégen ier se aktivéiert ka ginn.',
            activate_hint: 'Make this policy effective',
            approval_comments: 'Comments (optional)',
            approve_title: 'Approve Policy',
            flag_sensitive_hint: 'Mark as involving sensitive data',
            reactivate_hint: 'Resume this suspended policy',
            reject_hint: 'Decline this policy and return it to draft',
            reject_title: 'Reject Policy',
            rejection_reason: 'Rejection reason (required)',
            rejection_success: 'Policy rejected',
            require_enhanced_review_hint: 'Route to a second-level reviewer',
            retire_hint: 'Permanently retire this policy',
            review_context_description: 'Confirm the policy scope and review controls before saving.',
            review_context_hint: 'Reviewers, jurisdictions, dates, and consent defaults determine how this policy is governed.',
            review_context_title: 'Review context',
            sensitive_filter: 'Sensitive',
            suspend_hint: 'Temporarily pause this policy',
            suspend_reason_label: 'Suspension reason (required)',
            suspend_title: 'Suspend Policy',
            type_content_edit: 'Content Edit',
            type_status_change: 'Status Change',
            reactivate: 'Re-activate',
            no_versions: 'No version history yet.',
            approval_mode_hint: 'All: every named reviewer must approve, and only a named reviewer may decide. Any: one approval decides. In both, one rejection rejects the policy.',
            refusal_use_enhanced_review: 'This policy is routed to enhanced review, so it is decided there, not through the standard approval. The list has been refreshed.',
            refusal_reviewer_cannot_decide_named: 'These named reviewers could never decide this policy: {names}. Every named reviewer must be an active member of the organisation who holds the permission to approve consent policies, or, for a policy routed to enhanced review, the permission to carry out enhanced review.',
            refusal_reviewer_cannot_decide_none: 'This policy names no reviewer, and every named reviewer must approve it. Name at least one reviewer before submitting.',
            refusal_invalid_status_transition: 'This policy is no longer in a status that allows this. The list has been refreshed.',
            approval_waiting: 'Your approval is recorded. The policy is approved once {count} more named reviewer(s) approve.',
            enhanced_review_waiting: 'Your enhanced-review approval is recorded. The policy is approved once {count} more named reviewer(s) approve.',
            decision_recorded_in_review: 'Your decision is recorded. The policy is still in review.',
            decision_recorded: 'Your decision is recorded.',
            review_history: 'Review history',
            review_history_current: 'Decisions in the current review round',
            review_history_earlier: 'Earlier review rounds',
            review_history_empty: 'No review decision has been recorded for this policy yet.',
            history_col_reviewer: 'Reviewer',
            history_col_decision: 'Decision',
            history_col_review_kind: 'Review',
            history_col_decided_at: 'Decided',
            history_col_notes: 'Notes',
            history_kind_standard: 'Standard approval',
            history_kind_enhanced: 'Enhanced review',
            history_decision_approved: 'Approved',
            history_decision_rejected: 'Rejected',
            history_col_ended_at: 'Round ended',
            history_col_outcome: 'How it ended',
            history_col_ended_by: 'Ended by',
            history_col_reason: 'Reason',
            history_col_round_decisions: 'Decisions in the round',
            history_ended_resubmitted: 'Submitted for review again',
            history_ended_returned: 'Returned to draft',
            history_no_decisions: 'No decision was recorded',
            history_decision_by: '{decision} by {name}',
        },
        sensitive_categories: {
            health: 'Gesondheetsdaten',
            biometric: 'Biometresch Daten',
            political: 'Politesch Meenungen',
            ethnic: 'Ethnesch Hierkonft',
            genetic: 'Genetesch Daten',
            criminal: 'Stroofregester',
        },
        purposes: {
            title: 'Zweck-Katalog',
            new: 'Neien Zweck',
            edit: 'Zweck änneren',
            name: 'Numm',
            legal_basis: 'Rechtsgrondlag',
            retention_days: 'Opbewarungszäit (Deeg)',
            is_active: 'Aktiv',
            filter_by_category: 'Filteren no category',
            policy_ids: 'Richtlinn IDen (comma-separated)',
            ropa_activity_ids: 'ROPA Aktivitéit IDen (comma-separated)',
            policies: 'Richtlinnen',
            deprecate: 'Ofschreiwwen',
            fetch_error: 'Konnt net fetch Zoustëmmung purposes. Probéiert et nach eng Kéier.',
            save_error: 'Konnt net save Zoustëmmung purpose. Probéiert et nach eng Kéier.',
            deprecate_error: 'Konnt net deprecate Zoustëmmung purpose. Probéiert et nach eng Kéier.',
            page_title: 'Zweck Katalog',
            no_purposes: 'Keng Zoustëmmungszwecker fonnt. Erstellt Ären éischten Zweck fir unzefänken.',
            category_personal_data: 'Perséinlech Donnéeën',
            category_sensitive_data: 'Sensibel Donnéeën',
            category_health_data: 'Gesondheet Daten',
            category_financial_data: 'Finanzdaten',
            category_biometric_data: 'Biometresch Daten',
            category_children_data: "Kanner d'Daten",
            category_location_data: 'Standuert Daten',
            category_communications_data: 'Kommunikatiounsdaten',
            category_other: 'Aner',
        },
        workflows: {
            title: 'Zoustëmmungs-Workflows',
            new: 'Neie Workflow',
            edit: 'Workflow änneren',
            workflow_type: 'Workflow-Aart',
            user_id: 'Benotzer-ID',
            purpose_id: 'Zweck-ID',
            purpose_ids_csv: 'Zweck ID (comma-separated)',
            reason: 'Grond',
            process: 'Veraarbechten',
            data_subject_ref: 'Donnéeën Betreff Referenz',
            data_subject: 'Donnéeën Betreff',
            channel: 'Kanal',
            policy_id: 'Richtlinn ID',
            scope: 'Ëmfang',
            legal_grounds: 'Rechtlech Grounds',
            assessment: 'Bewäertung',
            select_outcome: 'Auswielen Resultat',
            partially_approved: 'Partially Genehmegt',
            denied: 'Ofgeleent',
            outcome_upheld: 'Bestätegt',
            outcome_pending: 'Ausstehend',
            outcome_denied: 'Ofgeleent',
            status_count: 'Statusunzuel',
            channel_filter: 'No Kanal filteren',
            add_action: '+ Aktioun',
            by_type: 'By Typ',
            by_status: 'No Status',
            downstream_actions: 'Downstream Aktiounen',
            downstream_actions_for: 'Downstream Aktiounen fir {subject} ({type})',
            add_downstream_action: 'Derbäisetzen Downstream Aktioun',
            assigned_to: 'Zougewisen Op',
            fetch_error: 'Konnt net fetch Zoustëmmung workflows. Probéiert et nach eng Kéier.',
            dashboard_error: 'Konnt net fetch Workflow dashboard. Probéiert et nach eng Kéier.',
            save_error: 'Konnt net save Zoustëmmung Workflow. Probéiert et nach eng Kéier.',
            status_update_error: 'Konnt net update Workflow Status. Probéiert et nach eng Kéier.',
            assessment_error: 'Konnt net update Workflow assessment. Probéiert et nach eng Kéier.',
            add_action_error: 'Konnt net add Workflow Aktioun. Probéiert et nach eng Kéier.',
            update_action_error: 'Konnt net update Workflow Aktioun. Probéiert et nach eng Kéier.',
            type_refusal: 'Ofsoe',
            type_withdrawal: 'Zréckzéiung',
            type_restriction: 'Aschränkung',
            type_objection: 'Asproch',
            status_received: 'Kritt',
            status_processing: 'Veraarbechtung',
            status_under_assessment: 'Ënner Bewäertung',
            status_implemented: 'Implementéiert',
            evidence_button: 'Beweiser',
            evidence_modal_title: 'Workflow Beweiser',
            no_evidence: 'Kee Beweis un dësem Workflow verbonnen.',
            upload_evidence_label: 'Eroplueden Workflow Beweiser',
            completed_at: 'Ofgeschloss am',
            effective_date: 'Effektiv Datum',
            page_title: 'Zoustëmmung Workflows',
            no_workflows: 'Keng Zoustëmmungs-Workflows fonnt. Workflows ginn erstallt wann Betraffener hir Rechter ausüben.',
            type_grant: 'Geneemegung',
            propagation_none: 'Keen',
            propagation_pending: 'Austehend',
            propagation_pending_warning: "Kann net ofgeschloss ginn — d'Réckzuchsweidergab ass nach austehend",
            propagation_completed: 'Ofgeschloss',
            propagation_partial: 'Deelweis',
            propagation_incomplete_error: "Workflow kann net ofgeschloss ginn wärend d'Réckzuchsweidergab nach austehend ass",
            propagation_status: 'Weidergabstatus',
        },
        snapshots: {
            title: 'CMP-Snapshots',
            new: 'Neie Snapshot',
            label: 'Etikett',
            snapshot_date: 'Snapshot-Datum',
            purposes_count: 'Unzuel vun Zwecker',
            compare: 'Vergläichen',
            snapshot_data: 'Snapshot-Donnéeën (JSON)',
            select_left: 'Auswielen Left Snapshot',
            select_right: 'Auswielen Right Snapshot',
            comparison_results: 'Vergläich Resultater',
            field: 'Feld',
            left: 'Lénks',
            right: 'Riets',
            close_comparison: 'Zoumaachen Vergläich',
            trigger: 'Ausléisen',
            trigger_manual: 'Manuell',
            trigger_policy_approval: 'Richtlinn Genehmegung',
            cmp_version: 'CMP Versioun',
            policy_version_id: 'Richtlinn Versioun ID',
            banner_config: 'Banner Konfiguratioun (JSON)',
            active_purposes: 'Aktiv Zwecker (JSON)',
            active_legal_bases: 'Aktiv Rechtlech Bases (JSON)',
            domain_scope: 'Domän Ëmfang',
            domain_scope_csv: 'Domän Ëmfang (comma-separated)',
            raw_config: 'Raw Konfiguratioun (JSON)',
            checksum: 'Préifsumm',
            created: 'Erstellt',
            fetch_error: 'Konnt net fetch CMP snapshots. Probéiert et nach eng Kéier.',
            save_error: 'Konnt net save CMP Snapshot. Probéiert et nach eng Kéier.',
            compare_error: 'Konnt net compare snapshots. Probéiert et nach eng Kéier.',
            page_title: 'Zoustëmmung Snapshots',
            json_format: 'JSON Format',
            json_array_format: 'JSON Array Format',
            manual_snapshot_note: 'Nëmmen fir Administrateuren/Entwéckler. Am normale Betrib ginn Snapshots automatesch iwwert den CMP-Webhook oder bei der Richtliniengenehmigung erstalt. Füügt de JSON direkt vun Ärem CMP-Ubidder (z.B. OneTrust, Cookiebot) an.',
            banner_config_hint: "Füügt d'Banner-Konfigurationsobjekt aus Ärem CMP-Export an.",
            active_purposes_hint: 'JSON-Array vun aktiven Zwecker-Objekte kopéiert aus Ärem CMP-Export.',
            active_legal_bases_hint: 'JSON-Array vun aktiven Rechtsgrondlag-Objekte kopéiert aus Ärem CMP-Export.',
            raw_config_hint: 'Komplett Roh-Konfigurations-JSON vun Ärem CMP. Aner JSON-Felder ginn dovunner extrahéiert. E SHA-256-Kontrollsumme gëtt automatesch berechent.',
            changed_field: 'Geännert Feld: ',
            no_snapshots: 'Keng CMP-Snapshots fonnt. Snapshots ginn erstallt wann Richtlinnen guttgeheescht oder manuell ausgeléist ginn.',
            detail_title: 'Snapshot-Detail',
            view_snapshot_detail: 'Snapshot-Detail ukucken',
            policy_version: 'Richtlinneversion',
            removed: 'Virdrun',
            added: 'Duerno',
        },
        change_awareness: {
            title: 'Zoustëmmungs-Ännerungsopmierksamkeet',
            change_type: 'Ännerungsaart',
            entity_type: 'Entitéitstyp',
            entity_id: 'Entitéits-ID',
            detected_at: 'Festgestallt um',
            acknowledge: 'Bestätegen',
            new_alert: 'Nei Warnung',
            new_alert_form: 'Nei Änneren Awareness Warnung',
            total_open: 'Gesamt Op',
            by_severity: 'By Schwéiergrad',
            snapshot_id: 'Snapshot-ID',
            update_status: 'Aktualiséieren Status',
            resolution_notes: 'Léisung Notizen',
            resolution_required: 'Léisung Notizen (required)',
            resolution_required_msg: 'Léisung notes are required when resolving eng item.',
            status_open: 'Op',
            status_under_review: 'Ënner Iwwerpréiwen',
            status_resolved: 'Geléist',
            status_accepted: 'Akzeptéiert',
            severity_informational: 'Informativ',
            severity_requires_review: 'Requires Iwwerpréiwen',
            severity_policy_violation: 'Richtlinn Violation',
            create_reconciliation: 'Erstelle Reconciliatioun Aufgab',
            reconciliation_created: 'Reconciliatioun Aufgab erfollegräich erstallt.',
            reconciliation_error: 'Ausgefall Reconciliatioun Aufgab ze schafen. Probéiert w.e.g. nach eng Kéier.',
            fetch_error: 'Konnt net fetch change awareness items. Probéiert et nach eng Kéier.',
            dashboard_error: 'Konnt net fetch change awareness dashboard. Probéiert et nach eng Kéier.',
            create_error: 'Konnt net create change awareness item. Probéiert et nach eng Kéier.',
            status_update_error: 'Konnt net update change awareness Status. Probéiert et nach eng Kéier.',
            page_title: 'Bewosstsinn änneren',
            no_items: 'Keng Ännerungsbewosstsinn-Elementer fonnt. Elementer ginn erstallt wann Ännerungen an der Zoustëmmungskonfiguratioun erkannt ginn.',
            affected_policies: 'Affected Policies',
            affected_purposes: 'Affected Purposes',
            estimated_affected_subjects: 'Estimated Affected Subjects',
            impact_scope: 'Impact Scope',
            affected_policies_short: 'Policies',
            affected_purposes_short: 'Purposes',
            affected_subjects_short: 'Subjects',
        },
        audit: {
            title: 'Zoustëmmungs-Audit-Protokoll',
            event_type: 'Evenement-Typ',
            user_id: 'Benotzer-ID',
            timestamp: 'Zäitstempel',
            details: 'Detailer',
            actor: 'Akteur',
            filter_by_entity_type: 'Filteren no entity type',
            filter_by_action: 'Filteren no Aktioun',
            fetch_error: 'Konnt net fetch Zoustëmmung Audit trail. Probéiert et nach eng Kéier.',
            page_title: 'Zoustëmmung Audit',
            export_csv: 'Export CSV',
            exporting: 'Exportéiert…',
            export_success: 'Audit Trail erfollegräich exportéiert.',
            export_error: 'Ausgefall Zoustëmmung Audit Trail ze exportéieren. Probéiert w.e.g. nach eng Kéier.',
            no_entries: 'Keng Audit-Entréeën fonnt. Audit-Evenementer ginn automatesch opgeholl wann Zoustëmmungsdaten sech änneren.',
            entity_policy: 'Richtlinn',
            entity_purpose: 'Zweck',
            entity_workflow: 'Workflow',
            entity_consent_record: 'Zoustëmmungsdatesaz',
            entity_change_awareness: 'Ännerungsbewosstsinn',
            entity_cmp_snapshot: 'CMP-Momentopnam',
            action_created: 'Erstallt',
            action_updated: 'Aktualiséiert',
            action_status_changed: 'Status geännert',
            action_drift_detected: 'Ofwäichung erkannt',
            action_snapshot_created: 'Momentopnam erstallt',
            action_approval_submitted: 'Genehmegung agereecht',
            action_archived: 'Archivéiert',
            action_purpose_auto_mapped: 'Zweck automatesch zougeuerdn',
            action_purpose_mapping_updated: 'Zweckzuuerdnung aktualiséiert',
            action_webhook_configured: 'Webhook konfiguréiert',
            entity_propagation: 'Verbreedung',
            entity_reconciliation: 'Reconciliatioun',
            entity_marketing_consent: 'Marketing consent',
            entity_marketing_suppression: 'Marketing suppression',
            entity_marketing_suppression_enforcement: 'Suppression enforcement check',
            action_propagation_confirmed: 'Verbreedung bestätegt',
            action_propagation_failed: 'Ausbreedung gescheitert',
            action_consent_captured: 'Zoustëmmung Erfaasst',
            action_reconciliation_created: 'Reconciliatioun erstallt',
            action_reconciliation_resolved: 'Reconciliatioun geléist',
            action_submitted_for_review: 'Fir Iwwerpréiwung agereecht',
            action_activated: 'Aktivéiert',
            action_suspended: 'Suspendéiert',
            action_retired: 'Ausser Betrib',
            action_enhanced_review_required: 'Erweidert Iwwerpréiwung néideg',
            action_enhanced_review_approved: 'Erweidert Iwwerpréiwung genehmegt',
            action_enhanced_review_rejected: 'Erweidert Iwwerpréiwung ofgeleent — Richtlinn op Entworf zréckgesat',
            action_consented: 'Zoustëmmung',
            action_consented_customize: 'Zoustëmmung Benotzerdefinéiert',
            action_revoked: 'Entlooss',
            action_rejected: 'Refuséiert',
        },
        web_records: {
            title: 'Web Zoustëmmung Records',
            page_title: 'Web Zoustëmmung Records',
            domain: 'Domain',
            visitor_id: 'Visiteur ID',
            consent_action: 'Zoustëmmung Aktioun',
            received_at: 'Empfang am',
            admin_status: 'Admin Status',
            revoke: 'Revokéieren',
            reject: 'Refuséieren',
            reason: 'Grond',
            reason_placeholder: 'Grond fir dës Aktioun (optional)',
            revoke_confirm: 'Dësen Zoustëmmungsrekord zréckzéien?',
            reject_confirm: 'Dësen Zoustëmmungsrekord refuséieren?',
            override_success: 'Rekordstatus erfollegräich aktualiséiert.',
            override_error: 'Ausgefall Rekord Status ze aktualiséieren.',
            no_records: 'Keng Web Zoustëmmung records fonnt.',
            fetch_error: 'Konnt net lueden Web Zoustëmmung records.',
            filter_by_domain: 'Filter no Domain',
            filter_by_action: 'Filter no Aktioun',
            active: 'Aktiv',
        },
        legal_bases: {
            all: 'All Rechtlech Bases',
        },
        common_statuses: {
            suspended: 'Gespaart',
            retired: 'Stillgeluecht',
            deprecated: 'Ofgeschriwwen',
        },
        approval_modes: {
            all: 'All',
            any: 'Any',
        },
        cmp_integration: {
            title: 'AesirX CMP Integratioun',
            back_to_consent: 'Back op Zoustëmmung',
            back_to_integration: 'Back op Integratioun',
            webhook_status: 'Webhook-Status',
            events_received: 'Evenementer Kritt',
            web_consent_records: 'Web Zoustëmmung Datesätz',
            unmapped_purposes: 'Net zougewisen Zwecker',
            integration_management: 'Integratioun Gestioun',
            webhook_configuration: 'Webhook Konfiguratioun',
            webhook_config_desc: 'Konfiguréieren den CMP Webhook endpoint an manage den HMAC secret.',
            purpose_mapping: 'Zweck Zouweisung',
            purpose_mapping_desc: 'Zouweisen CMP purpose identifiers op GRC Zoustëmmung catalog purposes.',
            bulk_sync: 'Bulk Synchroniséierung',
            bulk_sync_desc: 'Importéieren historical Zoustëmmung Donnéeën an view Synchroniséierung history.',
            consent_analytics: 'Web Zoustëmmung Analytics (Last 30 Days)',
            consent_grant_rate: 'Zoustëmmung Grant Quote',
            top_domain: 'Top Domän',
            records: 'Datesätz',
            banner_interactions: 'Banner-Interaktiounen',
            consent_actions: 'Zoustëmmungsaktioune',
            opt_in_trend: 'Zoustëmmung/Ofmeldung-Trend',
            records_by_domain: 'Records vum Domain',
            date: 'Datum',
            grants: 'Gewärungen',
            denials: 'Ofsoen',
            not_configured: 'Net Configured',
            load_error: 'Konnt net load Integratioun Donnéeën',
            webhook_not_configured: 'Webhook net konfiguréiert.',
            webhook_not_configured_msg: 'Wann ech gelift {link} als éischt.',
            configure_webhook_link: 'konfiguréieren den Webhook',
            setup_wizard: 'Ariichten Wizard',
            step_configure_webhook: 'Konfiguréieren Webhook',
            step_map_purposes: 'Zouweisen Zwecker',
            step_run_sync: 'Ausféieren Initial Bulk Synchroniséierung',
            step_verify_data: 'Verifizéieren Donnéeën an Dashboard',
            configure: 'Konfiguréieren',
            view_dashboard: 'Ukucken Dashboard',
            syncing: 'Synchroniséiere...',
            sync_now: 'Synchroniséierung Now',
            sync_completed: 'Synchroniséierung completed: {imported} imported, {skipped} skipped, {failed} gescheitert.',
            sync_failed: 'Synchroniséierung gescheitert',
            last_sync_result: 'Lescht Synchroniséierung Result',
            imported: 'Importéiert',
            skipped: 'Iwwersprong',
            errors: 'Feeler',
            index: 'Index',
            error: 'Feeler',
            sync_history: 'Synchroniséierung Verlaf',
            no_sync_jobs: 'No Synchroniséierung jobs recorded yet.',
            load_sync_error: 'Konnt net load Synchroniséierung Donnéeën',
            purpose_mapping_title: 'CMP Zweck Zouweisung',
            auto_mapping: 'Automatesch Zouweisung...',
            auto_map_purposes: 'Auto-Map Zwecker',
            unmapped_warning: '{count} onmappéierten Zweck(en) -- dës CMP Zwecker mussen op GRC Zwecker kartéiert ginn.',
            mapping_saved: 'Zouweisung saved.',
            mapping_deleted: 'Zouweisung deleted.',
            auto_map_complete: 'Auto-Zouweisung fäerdeg: {mapped} zougewisen, {unmatched} net zougewisen.',
            auto_map_failed: 'Auto-map gescheitert',
            save_mapping_error: 'Konnt net save Zouweisung',
            delete_mapping_error: 'Konnt net delete Zouweisung',
            load_data_error: 'Konnt net load Donnéeën',
            cmp_purpose: 'CMP Zweck',
            grc_purpose: 'GRC Zweck',
            select_grc_purpose: '-- Auswielen GRC Zweck --',
            auto_mapped: 'Automatesch zougewisen',
            mapped: 'Zougewisen',
            unmapped: 'Net zougewisen',
            no_mappings: 'Keng Zweckzuuerdnunge fonnt. Zuuerdnunge ginn automatesch erstallt wann Zoustëmmungsevenementer empfaange ginn.',
            total_mappings: 'Gesamt: {count} mapping(s)',
            webhook_config_title: 'Webhook Konfiguratioun',
            current_configuration: 'Aktuell Konfiguratioun',
            setup_webhook: 'Ariichten Webhook',
            setup_webhook_desc: 'Konfiguréieren den AesirX CMP Webhook op receive Zoustëmmung Evenementer.',
            allowed_domains: 'Allowed Domänen (one per line)',
            webhook_secret_warning: 'Webhook Geheimnes (save dëst now -- it will not be shown again):',
            last_event: 'Lescht Evenement',
            none_received: 'Keng',
            update_domains: 'Aktualiséieren Domänen',
            pause_webhook: 'Pauséieren Webhook',
            resume_webhook: 'Weiderféieren Webhook',
            regenerate_secret: 'Regenerate Geheimnes',
            configuring: 'Konfiguréiere...',
            create_webhook_config: 'Erstellen Webhook Konfiguratioun',
            webhook_endpoint: 'Webhook Endpunkt',
            webhook_endpoint_desc: 'Konfiguréieren your AesirX CMP op send Zoustëmmung Evenementer op:',
            webhook_signature_note: 'Abebannen den HMAC-SHA256 signature an den {header} header.',
            configured_success: 'Webhook erfollegräich konfiguréiert.',
            config_updated: 'Konfiguratioun updated.',
            config_error: 'Konnt net konfiguréieren Webhook',
            update_error: 'Konnt net update Konfiguratioun',
            toggle_error: 'Konnt net toggle Webhook',
            deactivated: 'Webhook deaktivéiert.',
            deactivate_error: 'Konnt net deactivate Webhook',
            page_title: 'CMP Integratioun',
            webhook_config_page_title: 'CMP Webhook Konfiguratioun',
            bulk_sync_page_title: 'Re-Kaart Records',
            purpose_mapping_page_title: 'Zweck Mapping',
            data_loaded: 'CMP Integratiounsdaten gelueden',
            sync_all: 'All synchroniséieren',
            sync_selected: 'Ausgewielt synchroniséieren',
            sync_status: 'Synchroniséierungsstatus',
            last_sync: 'Lescht Synchroniséierung',
            no_records: 'Keng Datesätz fonnt.',
            sync_in_progress: 'Synchroniséierung leeft...',
            sync_complete: 'Synchroniséierung ofgeschloss.',
            sync_error: 'Synchroniséierung gescheitert. Probéiert w.e.g. nach eng Kéier.',
            map_purpose: 'Zweck zouweisen',
            unmap: 'Zouweisung ophiewen',
            mapping_status: 'Zouweisungsstatus',
            save_mapping: 'Zouweisung späicheren',
            remap_unmapped_records: 'Re-map Unmapped Records',
            remap_records: 'Re-Kaart Records',
            add_mappings_first: 'Füügt als éischt Zweck Mappings (Schrëtt 2)',
            remap_result: 'Re-Mapping Resultat',
            remap_result_desc: 'Veraarbechtt all Zoustëmmung records mat unmapped oder deelweis kartéiert Zwecker.',
            total_processed: 'Ganz veraarbecht',
            newly_mapped: 'Nei Kartéiert',
            still_unmapped_label: 'Nach ëmmer Onmapéiert',
            still_unmapped_msg: '{count} Rekord(en) hunn nach ëmmer net mapéiert Zwecker. {link} a klickt dann nach eng Kéier op Re-map.',
            add_more_mappings: 'Füügt méi Kaarten',
            remap_complete: 'Re-Mapping fäerdeg: {remapped} nei kartéiert, {still_unmapped} nach ëmmer net kartéiert.',
            add_mapping: 'Mapping addéieren',
            cmp_purpose_id_label: 'CMP Zweck ID *',
            cmp_purpose_id_required: 'CMP Zweck ID ass erfuerderlech',
            cmp_purpose_name_label: 'CMP Zweck Numm',
            grc_purpose_label: 'GRC Zweck',
        },
        propagation: {
            title: 'Réckzuch-Verbreedung',
            systems: 'Verknäpfte Systemer',
            status: 'Status',
            confirmed: 'Bestätegt',
            pending: 'Aussteend',
            failed: 'Feelgeschloen',
            confirm: 'Réckzuch bestätegen',
            fail: 'Als feelgeschloen markéieren',
            failure_reason: 'Feelergrond',
            can_close: 'All Systemer bestätegt — Workflow kann zougemaach ginn',
            cannot_close: 'Waart op Systembestätegungen',
            no_tasks: 'Keng Verbreedungsaufgaben.',
            confirm_success: 'Systemréckzuch bestätegt.',
            fail_success: 'System als feelgeschloen markéiert.',
            propagate_success: 'Réckzuch un verknäpfte Systemer weidergeleeet.',
            propagate: 'Propagéiert op Systemer',
            add_system: 'System dobäisetzen',
            remove_system: 'Ewechzehuelen',
            system_id: 'System ID',
            system_name: 'System Numm',
            owner_id: 'Besëtzer ID',
            propagate_error: 'Ausgefall Austrëtt ze propagéieren. Probéiert w.e.g. nach eng Kéier.',
            no_systems: "Füügt op d'mannst ee System dobäi.",
            page_title: 'Austrëtt Ausbreedung',
            workflow_id: 'Wielt Réckzuch Workflow',
            system_id_hint: 'z.B. crm-001, erp-prod',
            owner_id_hint: 'Systembesëtzer',
            elapsed: 'Verlaf',
        },
        sensitive: {
            title: 'Richtlinne fir sensibel Donnéeën',
            flag: 'Als sensibel markéieren',
            categories: 'Sensibel Kategorien',
            enhanced_review: 'Erweidert Iwwerpréiwung erfuerderlech',
            enhanced_review_success: 'Erweidert Iwwerpréiwung ugefrot.',
            flag_success: 'Richtlinn als sensibel markéiert.',
            no_sensitive: 'Keng Richtlinne fir sensibel Donnéeën.',
        },
        reconciliation: {
            title: 'Ofwäichungsofstëmmung',
            create: 'Nei Ofstëmmungsaufgab',
            system: 'System',
            owner: 'Besëtzer',
            description: 'Beschreiwung',
            resolution: 'Léisung',
            resolve: 'Léisen',
            update_grc: 'GRC aktualiséieren',
            update_cmp: 'CMP aktualiséieren',
            accept_difference: 'Ënnerscheed akzeptéieren',
            escalate: 'Eskaléieren',
            resolution_notes: 'Léisungsnotizen',
            no_tasks: 'Keng Ofstëmmungsaufgaben.',
            resolve_success: 'Ofstëmmungsaufgab geléist.',
            create_success: 'Ofstëmmungsaufgab ugeluecht.',
            open: 'Op',
            resolved: 'Geléist',
            escalated: 'Eskaléiert',
            dashboard_open: 'Oppen Aufgaben',
            dashboard_resolved: 'Geléist Aufgaben',
            dashboard_escalated: 'Eskaléiert Aufgaben',
            page_title: 'Mismatch Reconciliatioun',
            my_tasks: 'Meng Aufgaben',
            all_tasks: 'All Aufgaben',
            severity: 'Gravitéit',
        },
        sync: {
            title: 'CMP-Synchroniséierung',
            direction: 'Richtung',
            cmp_to_grc: 'CMP → GRC',
            grc_to_cmp: 'GRC → CMP',
            conflict: 'Konflikt',
            resolve_conflict: 'Konflikt léisen',
            no_events: 'Keng Synchroniséierungsevenementer.',
            resolve_success: 'Synchroniséierungskonflikt geléist.',
            create_success: 'Synchroniséierungsevenement opgeholl.',
            page_title: 'CMP Synchroniséiert',
            detect_drift: 'Entdeckt Konflikter',
            detecting: 'Entdeckt ...',
            drift_result: '{detected} Konflikt(en) entdeckt aus {total} Zweck(en) verglach.',
            no_drift: 'Keng nei Konflikter fonnt.',
            drift_error: 'Konflikterkennung konnt net ausgefouert ginn.',
            resolve_modal_title: 'Konflikt léisen',
            resolve_field: 'Feld',
            resolve_grc_value: 'Den héchste Wäert vun GRC',
            resolve_cmp_value: 'CMP Wäert',
            accept_cmp: 'Akzeptéieren CMP Wäert',
            accept_cmp_desc: 'Update GRC fir dem CMP ze passen - den CMP Wäert gëtt den neie GRC Wäert.',
            keep_grc: 'Halt GRC Wäert',
            keep_grc_desc: "Zoumaachen dëse Konflikt ouni GRC z'änneren - informéiert de CMP Besëtzer fir sech auszegläichen.",
            resolve_applied: 'Konflikt geléist. GRC Zweck aktualiséiert fir ze passen CMP.',
        },
    },
    assessments: {
        /*
         * Sprint 65.4.0 (AC5) — the DPIA review-reminder task body.
         *
         * It used to be an English sentence concatenated inside
         * `dpia-review-scheduler-service.ts`, asserting a "6-month review" over a
         * record free to declare any cadence at all. The period is gone from the
         * wording because the record owns it, and the string lives here so a
         * Vietnamese tenant does not read one English line in its task list.
         */
        review_reminder: {
            task_title: 'DPIA review due: {title}',
            task_description: 'The review scheduled on this assessment is due on {date}. Confirm the safeguards remain adequate, or file an updated assessment.',
        },
        // ── Sprint 65.4.0 (AC2) — the operator-recorded commencement moment ──
        //
        // Shared by the DPIA and cross-border transfer surfaces: one fact, one
        // vocabulary. The undated wording is deliberately explicit that a
        // lifecycle timestamp is NOT the answer, because a blank date field
        // reads as "nobody filled this in" while the truth is "the clock cannot
        // lawfully start yet".
        commencement: {
            section_title_dpia: 'When the processing began',
            section_title_transfer: 'When the transfer began',
            loading: 'Loading the recorded moment',
            load_failed: 'The recorded commencement moment could not be loaded. Refresh the page to try again.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the recorded commencement moment',
            undated_label: 'Statutory clock undated',
            undated_icon: 'Undated',
            undated_reason: 'No one has recorded when this began, so the statutory period has nothing lawful to count from. It is never counted from the date this record was created, approved or last edited — those are the moments somebody opened a form here, not the moment the activity started.',
            recorded_label: 'Statutory clock anchored',
            recorded_icon: 'Anchored to a recorded moment',
            occurred_at_label: 'Began',
            recorded_by_label: 'Stated by',
            recorded_at_label: 'Stated on',
            source_field_label: 'Recorded in',
            correction_note_label: 'Reason for the restatement',
            instant_label: 'Date and time it began',
            instant_hint: 'Entered in your own time zone. The statutory period is counted from this moment, not from when you enter it.',
            note_label: 'Why is this being restated?',
            note_hint: 'Required. The record has to be able to explain why its due date moved.',
            save: 'Record the moment',
            save_correction: 'Correct the moment',
            saving: 'Saving',
            saved: 'Commencement moment recorded',
            saved_correction: 'Commencement moment corrected',
            saved_unchanged: 'That is already what this record states. Nothing was changed.',
            error_not_iso: 'Enter a full date and time. The moment is stored as an exact instant, so a time without a time zone cannot be accepted.',
            error_future: 'That moment is in the future. A statutory period cannot be counted from something that has not happened yet.',
            error_note_required: 'This record already states when it began. Say why you are restating it before saving.',
            error_note_not_correction: 'Nothing has been recorded here yet, so there is nothing to correct. Remove the note and record the moment.',
            error_generic: 'The commencement moment could not be recorded.',
        },
        // ── Sprint 65.7.0 — the two moments a FILED dossier carries ──
        //
        // Shared by the DPIA and cross-border transfer surfaces, like the
        // commencement vocabulary above: one fact, one wording. The first filing is
        // write-once, so this block carries NO edit copy for it — a "Change the
        // filing date" control would offer an act the server refuses, and the
        // sentence that explains why is the affordance instead.
        dossier_update: {
            section_title_dpia: 'The dossier filed for this assessment',
            section_title_transfer: 'The dossier filed for this transfer',
            loading: 'Loading the recorded dossier moments',
            load_failed: 'The recorded dossier moments could not be loaded. Refresh the page to try again.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the recorded dossier moments',
            filing_heading: 'First filing',
            filing_absent_label: 'No first filing recorded',
            filing_absent_icon: 'No filing recorded',
            filing_absent_reason: 'No one has recorded when this dossier was first filed, so anything counted from that filing has nothing to count from. It is never counted from the date this record was created, approved or last edited — a dossier is filed by a person, on a day, outside this platform, and told to the platform afterwards.',
            filing_recorded_label: 'First filing recorded',
            filing_recorded_icon: 'Anchored to a recorded filing',
            filing_write_once_notice: 'Recorded once, and never moved. The update cycle is counted from the first filing, so changing it here would silently move every date derived from it. If the dossier was filed again, record what changed in it below.',
            filed_at_label: 'Filed on',
            filing_instant_label: 'Date and time it was first filed',
            filing_instant_hint: 'Entered in your own time zone. This is the moment the dossier was filed, not the moment you enter it here.',
            filing_save: 'Record the first filing',
            filing_saving: 'Saving',
            filing_saved: 'First filing recorded',
            filing_saved_unchanged: 'That is already what this record states. Nothing was changed.',
            change_heading: 'Change to the registered particulars',
            change_blocked_label: 'Nothing filed yet',
            change_blocked_reason: 'There are no registered particulars to change until a dossier has been filed. Record the first filing above, then record what changed in it.',
            change_absent_label: 'No change recorded',
            change_absent_reason: 'The dossier has been filed, and no one has recorded a change to the particulars registered in it.',
            change_recorded_label: 'Change recorded',
            change_latest_notice: 'The most recent change stands here. Recording another replaces it, and each statement is kept as its own fact in the record history.',
            changed_at_label: 'Changed on',
            change_description_value_label: 'What changed',
            change_instant_label: 'Date and time the particulars changed',
            change_instant_hint: 'Entered in your own time zone.',
            change_description_label: 'What changed?',
            change_description_hint: 'Required. Say what changed in your own words. Which changes oblige an update is stated by the frameworks you have installed, and the person acting on it needs your account of what happened.',
            change_note_label: 'Why is this being restated?',
            change_note_hint: 'Optional. Use it when this statement replaces one that already stood.',
            change_save: 'Record the change',
            change_save_restate: 'Replace the recorded change',
            change_saving: 'Saving',
            change_saved: 'Change to the registered particulars recorded',
            change_saved_unchanged: 'That is already what this record states. Nothing was changed.',
            recorded_by_label: 'Stated by',
            recorded_at_label: 'Stated on',
            source_field_label: 'Recorded in',
            correction_note_label: 'Reason for the restatement',
            error_not_iso: 'Enter a full date and time. The moment is stored as an exact instant, so a time without a time zone cannot be accepted.',
            error_future: 'That moment is in the future. Nothing can be counted from something that has not happened yet.',
            error_already_recorded: 'This record already states when its dossier was first filed, and that statement cannot be moved — every date counted from it would move too. If the dossier was filed again, record what changed in it instead.',
            error_before_first_filing: 'No dossier has been filed for this record yet, so there are no registered particulars to change. Record the first filing first.',
            error_description_required: 'Say what changed. A change with no account of what changed cannot be acted on by the person who has to review it.',
            error_generic: 'The dossier moment could not be recorded.',
        },
        dossier_preflight: {
            advisory_notice: 'Dëse Bereetschaftsrapport ass berodend. En approuvéiert, montéiert, materialiséiert, reecht oder schéckt den Dossier net.',
            run_label: 'Run Pre-Fluch',
            running: 'Lafen virum Fluch…',
            assemble_label: 'Auto-montéiert Package',
            assembling: 'Assemblée…',
            verdict_label: 'Pre-Fluch Uerteel',
            tokens_label: '≈ {count} Tokens',
            remediation_label: 'Sanéierung',
            no_gaps: 'Keng Pre-Fluch Lücken festgestallt. De Fichier ass prett fir ze montéieren.',
            override_label: 'Iwwerschreiden Block Uerteel - Assemblée gëtt Audit-Stempel.',
            accept_label: 'Akzeptéieren Bericht',
            reject_label: 'Rapport refuséieren',
            assembled_label: 'Soumissioun Pak versammelt',
            package_id_label: 'Package ID',
            package_filename_label: 'Dateinumm',
            package_hash_label: 'Package Hash',
            evidence_count_label: '{count} Beweisartikele gebündelt',
            error_generic: 'Konnt net virum Fluch lafen oder de Package zesummesetzen. Probéiert nach eng Kéier oder kontrolléiert den Areechestatus.',
        },
        errors: {
            fetch: 'Konnt net load assessments..',
            save: 'Konnt net save assessment..',
            fetch_gap_report_failed: 'Konnt net fetch Lück report..',
            fetch_transfers_failed: 'Konnt net fetch transfers..',
            save_transfer_failed: 'Konnt net save Iwwerdroung..',
            submit_review_failed: 'Konnt net submit fir Iwwerpréifung..',
            process_decision_failed: 'Konnt net process Entscheedung..',
            deprecate_failed: 'Konnt net deprecate..',
            evaluate_risk_failed: 'Konnt net evaluate Risiko..',
            fetch_safeguards_failed: 'Konnt net fetch Schutzmoossnamen..',
            save_safeguard_failed: 'Konnt net save Schutzmoossnam..',
            update_safeguard_status_failed: 'Konnt net update Schutzmoossnam Status..',
        },
        nav: {
            dpias: 'DPIAen',
            transfers: 'Transfer-Evaluatiounen',
            dashboard: 'Iwwersiicht',
            gap_report: 'Lücke-Rapport',
        },
        risk_level_critical: 'Kritesche Risiko',
        risk_level_high: 'Héicht Risiko',
        risk_level_medium: 'Mëttlert Risiko',
        risk_level_low: 'Niddregt Risiko',
        severity_critical: 'Kritesch Schwéiregkeetsgrad',
        severity_high: 'Héije Schwéiregkeetsgrad',
        severity_medium: 'Mëttleren Schwéiregkeetsgrad',
        severity_low: 'Niddregen Schwéiregkeetsgrad',
        overdue: 'Iwwerfälleg',
        framework_filter_label: 'Kader',
        aria_filter_by_framework: 'Filter no Kader',
        dashboard: {
            title: 'Evaluatiounsiwwersiicht',
            page_title: 'Bewäertungs-Dashboard',
            announce_loaded: 'Bewäertungs-Dashboard gelueden',
            announce_chart_view: 'Diagramm-Usiicht weisen',
            announce_table_view: 'Datentabell-Usiicht weisen',
            aria_metrics_table: 'Bewäertungs-Dashboard Kennzuelen',
            metric_col: 'Kennzuel',
            value_col: 'Wäert',
            dpia_prefix: 'DSFA',
            transfer_prefix: 'Iwwerdroung',
            risk_prefix: 'Risiko',
            aria_dpias_by_status: 'DSFAe no Status',
            aria_transfers_by_status: 'Iwwerdroungen no Status',
            aria_top_risks: 'Top-Risiken',
            aria_top_risks_table: 'Top-Risiken an DSFAen',
            overdue_dpias_attention: '{count} iwwerfälleg DSFA-Bewäertungen brauchen Opmierksamkeet',
            no_overdue_dpias: 'Keng iwwerfälleg DSFA-Bewäertungen',
            overdue_transfers_attention: '{count} iwwerfälleg Iwwerdroungs-Bewäertungen brauchen Opmierksamkeet',
            no_overdue_transfers: 'Keng iwwerfälleg Iwwerdroungs-Bewäertungen',
            dpias_overview: 'DPIA-Iwwerbléck',
            transfers_overview: 'Transfer-Evaluatiounen-Iwwerbléck',
            overdue_items: 'Iwwerfälleg Elementer',
            overdue_dpias: 'Iwwerfälleg DPIAen',
            overdue_transfers: 'Iwwerfälleg Transfer-Evaluatiounen',
            count: 'Unzuel',
            no_data: 'No Donnéeën available.',
            no_risks: 'Keng risks fonnt.',
            no_dashboard_data: 'No dashboard Donnéeën available.',
            fetch_error: 'Konnt net fetch dashboard Donnéeën. Probéiert et nach eng Kéier.',
        },
        dpias: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            continue: 'Continue',
            review: 'Review',
            section_basic_info: 'Basic Information',
            page_title: 'DSFAen',
            announce_submitted_for_review: 'DSFA fir Iwwerpréiwung ageschéckt',
            announce_deprecated: 'DSFA veraltet',
            announce_risks_loaded: '{count} Risiko(en) gelueden',
            announce_mitigations_loaded: '{count} Moossnam(en) gelueden',
            announce_risk_deleted: 'Risiko geläscht',
            announce_mitigation_status_updated: 'Moossnamestatus aktualiséiert',
            aria_filter: 'DSFAe filteren',
            aria_filter_by_status: 'No Status filteren',
            aria_filter_by_lead: 'No Bewäertungsleeder filteren',
            aria_table: 'Dateschutz-Folgenofschätzungen',
            announce_found: '{count} DSFA(en) fonnt',
            announce_decision: 'DSFA {decision}',
            mitigation_start: 'Starten',
            mitigation_mark_implemented: 'Als ëmgesat markéieren',
            mitigation_verify: 'Verifizéieren',
            title: 'Dateschutz-Impakt-Evaluatiounen',
            new: 'Nei DPIA',
            edit: 'DPIA änneren',
            dpia_title: 'Titel',
            edit_dpia_aria: 'Edit DPIA: {title}',
            approve_or_reject_aria: 'Approve or reject: {title}',
            more_actions_for_aria: 'More actions for {title}',
            risk_details_aria: 'Risk details for {title}',
            edit_risk_aria: 'Edit risk: {title}',
            delete_risk_aria: 'Delete risk: {title}',
            mitigation_details_aria: 'Mitigation details for {title}',
            edit_mitigation_aria: 'Edit mitigation: {title}',
            dossier_filing_details_aria: 'Dossier filing details for {title}',
            activity: 'Aktivitéit',
            risk_score: 'Risikoscore',
            risks: 'Risiken',
            mitigations: 'Mitigatiounen',
            add_risk: 'Risiko derbäisetzen',
            add_mitigation: 'Mitigatioun derbäisetzen',
            necessity: 'Noutwendegkeet / Proportionalitéit',
            likelihood: 'Warscheinlechkeet',
            impact: 'Auswierkung',
            severity_label: 'Schwéiergrad',
            purpose: 'Zweck',
            consultation_notes: 'Berodung Notizen',
            dpo_opinion: 'DPO-Stellungnam',
            processing_activity_ids: 'Veraarbechtung Aktivitéit IDen (comma-separated)',
            system_ids: 'System IDen (comma-separated)',
            vendor_ids: 'Ubidder IDen (comma-separated)',
            data_types: 'Donnéeën Typen (comma-separated)',
            data_types_short: 'Donnéeën Typen',
            data_subjects: 'Donnéeën Subjects (comma-separated)',
            assessment_lead_id: 'Bewäertung Leedung ID',
            assessment_lead: 'Bewäertung Leedung',
            dpo_id: 'DPO ID',
            reviewer_ids: 'Préifer IDen (comma-separated)',
            approval_mode: 'Genehmegung Modus',
            approval_all: 'All',
            approval_any: 'Any',
            require_dpo_signoff: 'DPO-Zoustëmmung erfuerderlech',
            review_frequency_days: 'Review cadence (days)',
            review_frequency_days_hint: 'Optional. Days between reviews of this DPIA once it is approved. Leave empty and the DPIA gets no review date.',
            error_dpo_required: 'This DPIA requires a DPO sign-off. Choose the DPO before submitting it for review.',
            error_dpo_invalid: 'The DPO named on this DPIA is not an active user of this organisation. Choose an active DPO before submitting it for review.',
            error_dpo_cannot_approve: 'The DPO named on this DPIA does not hold the permission to approve DPIAs, so the sign-off could never be given. Choose a DPO who holds it before submitting it for review.',
            decision_awaiting_dpo: 'Your decision is recorded. The DPIA stays in review until its DPO signs it off.',
            withdraw_review: 'Withdraw from review',
            withdraw_review_hint: 'Return this DPIA to draft so it can be edited',
            withdraw_review_error: 'Failed to withdraw DPIA from review. Please try again.',
            announce_withdrawn: 'DPIA withdrawn from review',
            review_date: 'Iwwerpréiwen Datum',
            submit_for_review: 'Ofschécken fir Iwwerpréiwen',
            approve_reject: 'Genehmegen/Oflehnen',
            reopen: 'Reopen as draft',
            reopen_hint: 'Start a new review round. The rejection is recorded on the record and no longer counts.',
            reopen_error: 'Could not reopen the DPIA. Please try again.',
            announce_reopened: 'DPIA reopened as a draft',
            decision_named_reviewers_note: 'This DPIA names its reviewers. A decision from anyone else is recorded in the approval log, but counts toward neither approval nor rejection.',
            decision_not_counted: 'Your decision was recorded, but it does not count: only the named reviewers decide this DPIA.',
            approval_incomplete_badge: 'Approval incomplete',
            approval_incomplete_note: 'This DPIA is marked approved, but its approval log does not show an approval from the named reviewers. It keeps its status. Whether to review it again is for your organisation to decide.',
            approval_rounds_count: 'Earlier review rounds recorded on this record: {count}',
            deprecate: 'Ofschreiwwen',
            deprecated: 'Ofgeschriwwen',
            hide_risks: 'Verstoppen Risiken',
            hide_mitigations: 'Verstoppen Moossnamen',
            comments: 'Kommentaren',
            submit_decision: 'Ofschécken Entscheedung',
            category_label: 'Kategorie',
            category_data_breach: 'Donnéeën Verstouss',
            category_unauthorized_access: 'Unauthorized Zougang',
            category_function_creep: 'Funktioun Creep',
            category_re_identification: 'Re-Identifizéierung',
            category_discrimination: 'Diskriminéierung',
            category_surveillance: 'Iwwerwaachung',
            category_other: 'Aner',
            affected_rights: 'Betraff Rechter (kommagetrennt)',
            no_risks: 'Keng risks fonnt.',
            no_mitigations: 'Keng mitigations fonnt.',
            no_dpias: 'Keng DPIAs fonnt.',
            mitigation_type: 'Typ',
            mitigation_technical: 'Technesch',
            mitigation_organizational: 'Organisatoresch',
            mitigation_contractual: 'Kontraktuell',
            risk_ids: 'Risiko IDen (comma-separated)',
            responsible_party: 'Verantwortlech Party',
            responsible_party_placeholder: 'Oder Numm manuell aginn',
            affected_rights_placeholder: 'Gewielte Rechter ginn hei ugewisen',
            affected_rights_freetext_placeholder: 'Betraffe Rechter aginn (mat Komma getrennt)',
            responsible: 'Verantwortlech',
            target_date: 'Zil Datum',
            created: 'Erstellt',
            fetch_error: 'Konnt net fetch DPIAs. Probéiert et nach eng Kéier.',
            save_error: 'Konnt net save DPIA. Probéiert et nach eng Kéier.',
            submit_review_error: 'Konnt net submit DPIA fir Iwwerpréifung. Probéiert et nach eng Kéier.',
            decision_error: 'Konnt net process Entscheedung. Probéiert et nach eng Kéier.',
            deprecate_error: 'Konnt net deprecate DPIA. Probéiert et nach eng Kéier.',
            fetch_risks_error: 'Konnt net fetch risks. Probéiert et nach eng Kéier.',
            save_risk_error: 'Konnt net save Risiko. Probéiert et nach eng Kéier.',
            delete_risk_error: 'Konnt net delete Risiko. Probéiert et nach eng Kéier.',
            fetch_mitigations_error: 'Konnt net fetch mitigations. Probéiert et nach eng Kéier.',
            save_mitigation_error: 'Konnt net save mitigation. Probéiert et nach eng Kéier.',
            update_mitigation_status_error: 'Konnt den Ofmoossstatus net aktualiséieren. Probéiert et nach eng Kéier.',
            link_form_template: 'Link Reguléierungsformular Schabloun',
            link_form_template_placeholder: 'Wielt eng DPIA Form Schabloun (optional)',
            link_form_template_none: 'Keen',
            linked_form: 'Verknëppelt Form',
            linked_form_creating: 'Verlinkt Formulaire erstellen ...',
            linked_form_error: 'Konnt verlinkte Forminstanz net erstellen. Den DPIA gouf ouni verlinkte Form erstallt.',
            view_linked_form: 'View Form',
            announce_created_with_form: 'DPIA erstallt mat verlinkter Form Instanz',
            creation_mode: 'DPIA Kreatiounsmodus',
            mode_standard: 'Standard DPIA',
            mode_template: 'Benotzt Reguléierungsform Schabloun',
            select_form_template: 'Wielt Form Schabloun',
            create_and_fill_form: 'Erstellt & fëllt Form',
            template_sections_title: 'Schabloun Sektiounen',
            field_singular: 'Feld',
            field_plural: 'Felder',
            no_sections: 'Keng Rubriken an dëser Schabloun fonnt.',
            template_mode_hint: 'No der Schafung, gitt Dir ëmgeleet fir de komplette reglementaresche Formulaire auszefëllen.',
            linked_dossier_filings: 'Verknäpft Dossier-Areechen',
            no_linked_dossier_filings: 'Keng Dossier-Areechen mat dëser DSFA verknäpft.',
            view_dossier_filing: 'Areechen weisen',
            hide_dossier_filings: 'Dossier-Areechen verstoppen',
            supporting_evidence: 'Ënnerstëtzend Beweiser',
            hide_supporting_evidence: 'Verstoppen Beweiser',
            no_supporting_evidence: 'Keng ënnerstëtzende Beweiser verbonnen.',
            upload_supporting_evidence: 'Eroplueden ënnerstëtzen Beweiser',
            view_form_responses: 'Formularäntwerten gesinn',
            regulatory_framework: 'Regulatoresche Kader',
            all_frameworks: 'All Kader',
            no_templates_for_market: 'Keng Schablounen fir de gewielte Marché verfügbar. Wielt w.e.g. en anere Marché oder erstellt eng Schabloun.',
            affected_rights_freetext_hint: 'No framework rights available — enter manually (comma-separated)',
            ai_draft_eyebrow: 'AI assist',
            deprecate_hint: 'Retire this DPIA without deleting it',
            inherent_risk_level: 'Inherent Risk',
            mitigation_contractual_hint: 'DPA clauses, SCCs, contractual obligations',
            mitigation_description_placeholder: 'Describe the mitigation measure in detail',
            mitigation_organizational_hint: 'Policies, training, procedures, staff awareness',
            mitigation_technical_hint: 'Encryption, pseudonymisation, access controls, system hardening',
            mitigation_title_placeholder: 'e.g. Encrypt data at rest',
            no_risks_to_link: 'No risks found for this DPIA. Add risks first.',
            score_high: 'High',
            score_low: 'Low',
            score_medium: 'Medium',
            score_very_high: 'Very High',
            score_very_low: 'Very Low',
            section_assessment_details: 'Assessment Details',
            section_implementation: 'Implementation',
            section_processing: 'Processing Details',
            section_scoring: 'Risk Scoring',
            section_team: 'Team & Approval',
            selected_rights_count: 'Selected',
            submit_for_review_hint: 'Advance this DPIA to review',
        },
        transfers: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            description_placeholder: 'Brief summary of this transfer',
            title_placeholder: 'e.g. EU–US CRM Data Transfer',
            vendor_id_placeholder: 'Vendor ID (optional)',
            page_title: 'Dateniwwerdroungen',
            announce_found: '{count} Iwwerdroungs-Bewäertung(en) fonnt',
            announce_submitted_for_review: 'Iwwerdroung fir Iwwerpréiwung ageschéckt',
            announce_deprecated: 'Iwwerdroung veraltet',
            announce_decision: 'Iwwerdroung {decision}',
            announce_risk_eval_completed: 'Risikobewäertung ofgeschloss',
            announce_safeguard_status_updated: 'Schutzmoossnamestatus aktualiséiert',
            announce_safeguards_loaded: '{count} Schutzmoossnam(en) gelueden',
            aria_filter: 'Iwwerdroungs-Bewäertungen filteren',
            aria_filter_by_status: 'No Status filteren',
            aria_filter_by_mechanism: 'No Iwwerdroungs-Mechanismus filteren',
            aria_table: 'Dateniwwerdroungs-Bewäertungen',
            title: 'Grenziwwerschreidend Transfer-Evaluatiounen',
            new: 'Neien Transfer',
            edit: 'Transfer änneren',
            name: 'Numm',
            from_country: 'Vum Land',
            to_country: 'Op Land',
            safeguards: 'Schutzmoossnamen',
            risk_level: 'Risikoniveau',
            assessment_notes: 'Evaluatiounsnotizen',
            mechanism: 'Mechanismus',
            transfer_mechanism: 'Iwwerdroung Mechanismus',
            transfer_mechanism_detail: 'Iwwerdroung Mechanismus Detail',
            all_mechanisms: 'All Mechanismen',
            mechanism_other: 'Aner',
            data_types: 'Donnéeën Typen (comma-separated)',
            data_subjects: 'Donnéeën Subjects (comma-separated)',
            purpose: 'Zweck',
            recipient_name: 'Empfänger Numm',
            create_success: 'Transferbewäertung erstallt.',
            update_success: 'Transferbewäertung aktualiséiert.',
            recipient_details: 'Empfängerdetailer',
            recipient_details_placeholder: 'Roll, Standuert a Systemer vum Empfänger, an Detailer zur Weiderleedung',
            vendor_id: 'Ubidder ID',
            processing_activity_ids: 'Veraarbechtung Aktivitéit IDen (comma-separated)',
            reviewer_ids: 'Préifer IDen (comma-separated)',
            approval_mode: 'Genehmegung Modus',
            approval_all: 'All',
            approval_any: 'Any',
            require_dpo_signoff: 'DPO-Zoustëmmung erfuerderlech',
            review_date: 'Iwwerpréiwen Datum',
            created: 'Erstellt',
            deprecated: 'Ofgeschriwwen',
            submit_for_review: 'Ofschécken fir Iwwerpréiwen',
            approve_reject: 'Genehmegen/Oflehnen',
            reopen: 'Reopen as draft',
            reopen_hint: 'Start a new review round. The rejection is recorded on the record and no longer counts.',
            reopen_error: 'Could not reopen the transfer assessment. Please try again.',
            announce_reopened: 'Transfer assessment reopened as a draft',
            redetermine_mechanism: 'Re-determine mechanism',
            redetermine_mechanism_title: 'Re-determine the transfer mechanism',
            redetermine_mechanism_intro: 'This record is {status}, so it cannot be edited, but its transfer mechanism is waiting for a person to re-determine it. Choose a mechanism an installed framework declares. Only the mechanism is saved; the status and the approval record do not change.',
            redetermine_choose_first: 'Choose a transfer mechanism first.',
            redetermine_success: 'Transfer mechanism re-determined.',
            redetermine_error: 'Could not re-determine the transfer mechanism. Please try again.',
            withdraw_review: 'Withdraw from review',
            withdraw_review_hint: 'Return this transfer assessment to draft. The review round is closed and its decisions no longer count. Use this when a named reviewer can no longer decide.',
            withdraw_review_error: 'Could not withdraw the transfer assessment from review. Please try again.',
            announce_withdrawn: 'Transfer assessment withdrawn from review',
            decision_named_reviewers_note: 'This transfer assessment names its reviewers. A decision from anyone else is recorded in the approval log, but counts toward neither approval nor rejection.',
            decision_not_counted: 'Your decision was recorded, but it does not count: only the named reviewers decide this transfer assessment.',
            approval_incomplete_badge: 'Approval incomplete',
            approval_incomplete_note: 'This transfer assessment is marked approved, but its approval log does not show an approval from the named reviewers. It keeps its status, but a cross-border identity exchange flow cannot rest on it. Whether to review it again is for your organisation to decide.',
            approval_rounds_count: 'Earlier review rounds recorded on this record: {count}',
            deprecate: 'Ofschreiwwen',
            risk_eval: 'Risiko Eval',
            hide_safeguards: 'Verstoppen Schutzmoossnamen',
            mechanism_proof: 'Mechanismus Beweis',
            hide_mechanism_proof: 'Verstoppen Mechanismus Beweis',
            mechanism_proof_hint: 'Upload the signed document the transfer rests on (for example the contract, or the impact-assessment dossier your installed frameworks require) to prove this transfer assessment.',
            no_mechanism_proof: 'Kee Mechanismus Beweis verbonnen.',
            upload_mechanism_proof: 'Eroplueden Mechanismus Beweis',
            comments: 'Kommentaren',
            submit_decision: 'Ofschécken Entscheedung',
            data_sensitivity: 'Donnéeën Sensitivity (1-5)',
            country_risk: 'Land Risiko (1-5)',
            safeguard_adequacy: 'Schutzmoossnam Adequatheet (1-5)',
            volume_factor: 'Volumefaktor (1-5)',
            evaluate: 'Bewäerten',
            add_safeguard: 'Derbäisetzen Schutzmoossnam',
            responsible_party: 'Verantwortlech Party',
            responsible_party_placeholder: 'Oder Numm manuell aginn',
            responsible: 'Verantwortlech',
            implementation_date: 'Implementéierung Datum',
            implementation: 'Implementéierung',
            expiry_date: 'Oflaf Datum',
            expiry: 'Oflaf',
            expire: 'Oflafen',
            activate_unavailable_expired: 'An expired safeguard cannot be reactivated. Record a new safeguard instead.',
            no_safeguards: 'Keng Schutzmoossnamen fonnt.',
            no_transfers: 'Keng Iwwerdroung assessments fonnt.',
            fetch_error: 'Konnt net fetch Iwwerdroung assessments. Probéiert et nach eng Kéier.',
            save_error: 'Konnt net save Iwwerdroung assessment. Probéiert et nach eng Kéier.',
            submit_review_error: 'Konnt net submit fir Iwwerpréifung. Probéiert et nach eng Kéier.',
            decision_error: 'Konnt net process Entscheedung. Probéiert et nach eng Kéier.',
            deprecate_error: 'Konnt net deprecate Iwwerdroung. Probéiert et nach eng Kéier.',
            risk_eval_error: 'Konnt net evaluate Risiko. Probéiert et nach eng Kéier.',
            fetch_safeguards_error: 'Konnt net fetch Schutzmoossnamen. Probéiert et nach eng Kéier.',
            save_safeguard_error: 'Konnt net save Schutzmoossnam. Probéiert et nach eng Kéier.',
            update_safeguard_status_error: 'Konnt de Schutzmoossnamstatus net aktualiséieren. Probéiert et nach eng Kéier.',
            safeguard_type_encryption_in_transit: 'Verschlësselung an Transit',
            safeguard_type_encryption_at_rest: 'Verschlësselung bei Rest',
            safeguard_type_access_controls: 'Zougang Kontrollen',
            safeguard_type_contractual_clauses: 'Kontraktuell Klauselen',
            safeguard_type_data_minimization: 'Donnéeën Minimization',
            safeguard_type_pseudonymization: 'Pseudonymiséierung',
            safeguard_type_audit_rights: 'Auditrechter',
            safeguard_type_breach_notification: 'Verstouss Notifikatioun',
            safeguard_type_data_localization: 'Donnéeën Lokaliséierung',
            safeguard_type_other: 'Aner',
            activate: 'Aktivéieren',
            all_statuses: 'All Status',
            any: 'Any',
            mechanism_detail: 'Mechanismus Detail',
            safeguard_types: {
                encryption_in_transit: 'Verschlësselung am Transit',
                encryption_at_rest: 'Verschlësselung am Rou',
                access_controls: 'Zougangskontrolle',
                contractual_clauses: 'Vertragklauselen',
                data_minimization: 'Dateminiméierung',
                pseudonymization: 'Pseudonymisatioun',
                audit_rights: 'Auditrechter',
                breach_notification: 'Verletzungsmeldung',
                data_localization: 'Datelokaliséierung',
                other: 'Aner',
            },
            activity_ids_placeholder: 'Activity IDs (optional)',
            deprecate_hint: 'Retire this assessment without deleting it',
            file_dossier: 'File Dossier',
            file_dossier_hint: 'Start a regulatory filing from this assessment',
            mark_implemented: 'Implement',
            mark_verified: 'Verify',
            mechanism_detail_placeholder: 'e.g. the contract or document the transfer rests on',
            purpose_placeholder: 'e.g. CRM, analytics, support',
            csv_hint: '(comma-separated)',
            data_subjects_placeholder: 'e.g. employees, customers',
            data_types_placeholder: 'e.g. name, email, phone',
            from_country_placeholder: 'e.g. EU',
            to_country_placeholder: 'e.g. US',
            recipient_placeholder: 'e.g. Salesforce Inc.',
            reviewers_placeholder: 'Select reviewers…',
            risk_eval_hint: 'Score data sensitivity, country & safeguards',
            section_mechanism: 'Mechanism & Data',
            section_review: 'Review & Approval',
            section_route: 'Transfer Route',
        },
        gap_report: {
            page_title: 'Gap-Bericht fir Bewäertungen',
            announce_loaded: 'Gap-Bericht gelueden. {count} Lück(en) identifizéiert',
            aria_activities_without_dpia: 'Aktivitéiten ouni DSFA-Bewäertung',
            aria_activities_unclassified: 'Aktivitéite mat net klasséiertem DSFA-Besoin',
            activities_unclassified_section_title: 'Aktivitéite mat net klasséiertem DSFA-Besoin',
            activities_unclassified_description: "Dës Aktivitéite goufen net iwwerpréift fir ze bestëmmen, ob eng DSFA gesetzlech néideg ass. Klasséiert all eenzel fir d'Signal ze läschen.",
            activities_unclassified_empty: 'All aktiv Aktivitéiten si klasséiert.',
            aria_unmitigated_risks: 'Net behandelt héij a kritesch Risiken',
            aria_transfers_without_safeguards: 'Iwwerdroungen ouni adequat Schutzmoossnamen',
            aria_expired_assessments: 'Ofgelafe Bewäertungen déi Erneierung erfuerderen',
            title: 'Evaluatiounslücke-Rapport',
            no_dpia: 'Aktivitéiten ouni DPIA',
            no_assessment: 'Transferte ouni Evaluatioun',
            unmitigated: 'DPIAe mat onmitigéierte Risiken',
            activity_id: 'Aktivitéit ID',
            dpia_id: 'DPIA ID',
            assessment_id: 'Bewäertung ID',
            expired_assessments: 'Ofgelaf Bewäertungen',
            no_gaps_activities: 'Keng Lücken fonnt All activities have DPIAs..',
            no_unmitigated_risks: 'Keng unmitigated high/critical risks fonnt.',
            no_gaps_safeguards: 'Keng Lücken fonnt All transfers have adequate Schutzmoossnamen..',
            no_expired: 'Keng expired assessments fonnt.',
            no_report_data: 'No Lück report Donnéeën available.',
            fetch_error: 'Konnt net fetch Lück report. Probéiert et nach eng Kéier.',
            no_expired_assessments: 'Keng ofgelafene Bewäertungen.',
            no_gaps_dpia: 'Keng DPIA Lücken fonnt.',
            no_data: 'No Donnéeën available.',
            all_clear: 'No compliance gaps detected.',
            list_truncated_notice: 'Only the first 50 activities are listed; more match. Resolve these, then reload the report to see the rest.',
            subtitle: 'Identify compliance gaps across DPIAs, risks, transfers, and assessments.',
        },
        dossier_filings: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            aria_filters: 'Filter dossier filings',
            description_placeholder: 'Optional summary of this filing',
            title_placeholder: 'e.g. DPIA Filing – Annex I',
            page_title_detail: 'Dossier-Areechtungsdetail',
            page_title_list: 'Dossier-Areechtungsfäll',
            announce_filings_found: '{count} Areechtung(en) fonnt',
            announce_results: '{count} Resultater',
            aria_filter_by_status: 'No Status filteren',
            title: 'Dossier Areeche Fäll',
            nav_label: 'Dossier Dossieren',
            create: 'Erstellt Dossier Areechung',
            form_type: 'Form Typ',
            phu_luc_i_label: 'Standard assessment workflow (internal)',
            phu_luc_ii_label: 'Cross-border / high-risk assessment workflow (internal)',
            dpia_reference: 'DPIA Referenz',
            transfer_reference: 'Transfer Assessment Reference',
            transfer_select_placeholder: 'Link to a transfer assessment',
            arrived_parent_heading: 'Filing for',
            prior_filing_heading: 'This organisation has already filed this dossier',
            prior_filing_body: 'The installed framework provides for one dossier of this kind per organisation, updated afterwards ({citation}). This organisation already filed one: {filing}. For a new purpose or another change to its content, start the update on this assessment instead. You can still open a first filing, for example to file again after the authority assessed the earlier dossier as not meeting the requirements.',
            prior_filing_start_update: 'Start the update: {form}',
            prior_filing_no_update_form: 'No update form is installed for this kind of dossier.',
            arrived_parent_loading: 'Looking up the assessment record…',
            arrived_parent_unresolved: 'The assessment this filing was started from could not be found. It may have been deleted, or it may belong to another organisation. Choose a parent below before filing.',
            parent_none: 'No assessment linked',
            parent_unresolved: 'linked record not found ({id})',
            error_parent_required: 'Choose the assessment this filing discharges — either an impact assessment or a transfer assessment.',
            error_transfer_parent_not_found: 'The transfer assessment this filing names no longer exists. Pick another one, or reopen the filing from the transfer register.',
            error_dpia_parent_not_found: 'The impact assessment this filing names no longer exists. Pick another one.',
            submitted_date: 'Datum presentéiert',
            outcome: 'Resultat',
            parent_filing: 'Elterendeel',
            no_filings: 'Nee dossier Fichieren fonnt.',
            fetch_error: 'Ausgefall fir den Dossier ze sichen. Probéiert w.e.g. nach eng Kéier.',
            save_error: 'Ausgefall dossier Areeche schafen. Probéiert w.e.g. nach eng Kéier.',
            created: 'Dossier Areechung erstallt.',
            loaded: 'Dossier Areechung gelueden.',
            not_found: 'Dossier Areechung net fonnt.',
            back_to_list: "Zréck op d'Lëscht",
            section_assignments: 'Sektioun Uerderen',
            assign_sections: 'Assignéiert Sektiounen',
            section_id: 'Sektioun',
            department: 'Departement',
            assignee: 'Assignee',
            deadline: 'Frist',
            no_assignments: 'Nach keng Sektiounsaufgaben.',
            sections_assigned_msg: 'Sektiounen zougewisen.',
            assign_error: 'Ausgefall Rubriken zouzeschreiwen. Probéiert w.e.g. nach eng Kéier.',
            lifecycle: 'Filing Liewenszyklus',
            submission: 'Soumissioun Detailer',
            submission_channel: 'Soumissioun Channel',
            confirmation_number: 'Confirmatiouns Zuel',
            // Fix sprint #447 (round 2) — the Submit dialog offers the declared channels.
            select_channel: 'Select a channel',
            submission_note: 'This records the submission your organisation made. The platform files nothing with the authority.',
            channels_load_failed: 'The submission channels could not be read.',
            channel_refusal_no_framework: 'No installed framework declares an authority filing, so no submission can be recorded.',
            channel_refusal_no_channels: 'The filing framework declares no submission channels, so no submission can be recorded.',
            channel_refusal_no_authority: 'The filing framework names no authority that accepts filings, so no submission can be recorded.',
            status_submitted: 'Filed — no result recorded',
            submit_for_review: 'Fir Iwwerpréiwung androen',
            submitted_for_review: 'Fir intern Iwwerpréiwung presentéiert.',
            submit_review_error: 'Ausgefall fir Iwwerpréiwung ofzeginn. Probéiert w.e.g. nach eng Kéier.',
            legal_signoff: 'Legal Signoff',
            legal_signoff_done: 'Legal Signoff fäerdeg.',
            legal_signoff_error: 'Ausgefall fir legal Ënnerschrëft ze kompletéieren. Probéiert w.e.g. nach eng Kéier.',
            approve: 'Approve internally',
            approved_msg: 'Filing approved internally. It has not been filed with the authority.',
            approve_error: 'Ausgefall Areeche guttgeheescht. Probéiert w.e.g. nach eng Kéier.',
            materialisation_status_column: 'Soumissioun artefakt',
            materialisation_na: 'Net applicabel',
            materialisation_pending: 'Erwaardung',
            materialisation_success: 'Materialiséiert',
            materialisation_failed: 'Ausgefall',
            materialisation_banner_title: 'Soumissioun Artefakt konnt net erstallt ginn',
            materialisation_banner_body: 'The filing was approved internally, but the underlying dossier record could not be created. Cover-letter and submission exports are unavailable until you retry.',
            materialisation_retry_button: 'Nach eng Kéier probéieren',
            materialisation_retry_count: 'Virdrun nei Versuch:',
            materialisation_retry_success: 'Soumissioun Artefakt materialiséiert erfollegräich.',
            materialisation_retry_announce: 'De Versuch ass gelongen',
            filter_materialisation_failed: 'Weist nëmmen Materialiséierungsfehler',
            column_title: 'Titel',
            column_form_type: 'Form',
            submit_to_mps: 'Record filing with the authority',
            submitted_to_mps_msg: 'Your organisation’s submission to the authority is recorded.',
            submit_mps_error: 'Failed to record the filing with the authority. Please try again.',
            supplement_request: 'Zousaz Ufro',
            supplement_requests: 'Zousaz Ufroen',
            supplement_response: 'Ergänzungsäntwert',
            record_supplement: 'Rekord Zousaz Ufro',
            supplement_recorded: 'Zousaz Ufro opgeholl.',
            supplement_absence_declaring_framework_unreadable: 'No deadline: the framework that states the period could not be read.',
            supplement_absence_declaring_framework_not_in_force: 'No deadline: the framework that states the period is not yet in force.',
            supplement_absence_uncomputable_period: 'No deadline: the installed period cannot be counted as a date.',
            supplement_absence_multiple_candidate_clocks: 'No deadline: more than one installed period could apply and the platform does not choose between them. Enter the deadline stated in the authority’s letter.',
            supplement_absence_no_installed_clock: 'No deadline: no installed framework states a period for responding.',
            supplement_absence_receipt_not_evidenced: 'No deadline: the date the applicant received the authority’s result has not been recorded.',
            supplement_absence_declaring_framework_reference_mode: 'No deadline: the framework that states the period is installed as Reference only, so its clocks are not run.',
            supplement_absence_declaring_framework_not_operational: 'No deadline: the framework that states the period is superseded or retired, so it no longer sets a deadline.',
            supplement_deadline_warning_differs_from_installed: 'The date you entered falls on a different day from the one the installed period gives. The date from the authority’s letter stands.',
            supplement_deadline_warning_in_the_past: 'The date you entered was already past when it was recorded.',
            supplement_deadline_platform_estimate: 'Platform estimate recorded earlier: not a period stated by an installed framework or by the authority’s letter.',
            supplement_deadline_installed_due: 'The installed period gives {date}.',
            supplement_deadline_none: 'No deadline is recorded for this request.',
            supplement_deadline_source_unknown: 'The source of this deadline was not recorded',
            supplement_deadline_source_operator_anon: 'Stated in the authority’s letter; entered by a user',
            supplement_deadline_source_operator: 'Stated in the authority’s letter; entered by {user}',
            supplement_deadline_source_installed: 'Counted from the date of receipt, using the period in the installed framework',
            supplement_response_received: 'Responded',
            supplement_response_pending: 'Awaiting response',
            supplement_response_deadline: 'Response deadline',
            supplement_items_requested: 'Items requested',
            supplement_received_future: 'The date of receipt cannot be in the future.',
            supplement_deadline_help: 'A completion period is counted from a communication your organisation recorded, once you state that it asks for the dossier to be completed, or that it states the dossier is incomplete, invalid or not in accordance with the regulations. With a deadline stated in the authority’s letter, the platform records that deadline and who entered it. With neither, the request has no deadline and the page says why.',
            supplement_response_deadline_input: 'Deadline stated in the authority’s letter (optional)',
            supplement_received_at: 'Date the applicant received the authority’s result',
            supplement_items: 'Items the authority requested (one per line)',
            supplement_error: 'Ausgefall Zousaz Ufro opzehuelen. Probéiert w.e.g. nach eng Kéier.',
            submit_supplement: 'Gitt Zousaz Äntwert of',
            supplement_submitted_msg: 'Zousaz Äntwert presentéiert.',
            supplement_submit_error: 'Ausgefall Zousaz Äntwert ofginn. Probéiert w.e.g. nach eng Kéier.',
            response_status: 'Äntwert Status',
            response_notes: 'Äntwert Notizen',
            resubmit: 'Nees ofginn',
            resubmitted_msg: 'Dossier nees agereecht.',
            resubmit_error: 'Konnt den Dossier net ofginn. Probéiert w.e.g. nach eng Kéier.',
            outcome_error: 'Ausgefall Resultat opzehuelen. Probéiert w.e.g. nach eng Kéier.',
            accepted: 'Akzeptéiert',
            rejected: 'Ofgeleent',
            close: 'Zoumaachen',
            closed_msg: 'Dossier zougemaach.',
            close_error: 'Ausgefall op de Fichier zoumaachen. Probéiert w.e.g. nach eng Kéier.',
            change_filing: 'Erstellt Ännerungsdossier',
            change_filing_created: 'Ännere Fichier erstallt.',
            change_filing_error: 'Ausgefall Ännerung Areeche schafen. Probéiert w.e.g. nach eng Kéier.',
            change_reason: 'Grond fir Ännerung',
            filing_lineage: 'Aschreiwung Lineage',
            timers: 'Aktiv Timer',
            timer_mps_review: 'Authority review',
            timer_supplement_response: 'Zousaz Termin',
            timer_resubmission_review: 'Resubmission Deadline',
            timer_department_input: 'Departement Frist',
            days_remaining: 'Deeg iwwreg',
            days_overdue: 'Deeg iwwerfälleg',
            cover_letter: 'Cover Letter',
            checklist: 'Checkliste',
            evidence_index: 'Beweis Index',
            status_draft: 'Entworf',
            status_internal_review: 'Intern Iwwerpréifung',
            status_legal_signoff: 'Legal Signoff',
            status_approved: 'Approved internally — not filed',
            status_supplement_requested: 'Ergänzung gefrot',
            status_supplement_submitted: 'Supplement presentéiert',
            status_resubmitted: 'Nei agereecht',
            status_accepted: 'Result recorded: Accepted',
            status_rejected: 'Result recorded: Rejected',
            status_closed: 'Zougemaach',
            // Sprint 62.57.0 (gate E12.5 pass 1, D10) — step_approved, step_submitted,
            // step_accepted and step_rejected are English until Phase 25 (R-57); the
            // other step_* keys keep this locale's translation.
            step_draft: 'Entworf',
            step_internal_review: 'Intern Iwwerpréifung',
            step_legal_signoff: 'Legal Signoff',
            step_approved: 'Approved internally',
            step_submitted: 'Filed',
            step_supplement_requested: 'Ergänzung gefrot',
            step_supplement_submitted: 'Supplement presentéiert',
            step_resubmitted: 'Nei agereecht',
            step_accepted: 'Result recorded: Accepted',
            step_rejected: 'Result recorded: Rejected',
            step_closed: 'Zougemaach',
            linked_dpia: 'Verknäpft DSFA',
            linked_dpia_none: 'Keng DSFA mat dëser Areeche verknäpft.',
            linked_parents: 'Linked assessment',
            linked_parents_none: 'This filing names no assessment.',
            parent_kind_dpia: 'Impact assessment',
            parent_kind_transfer: 'Transfer assessment',
            view_linked_dpia: 'DSFA weisen',
            linked_dpia_label: 'Dësen Dossier-Areechen ass mat der folgender DSFA verknäpft:',
            all_sections_must_complete: 'All sections must be completed or reviewed before submitting',
            change_section_status: 'Change status for',
            dpia_select_placeholder: 'Link to a DPIA (optional)',
            no_departments_hint: 'No departments available. You can still assign by selecting a user below.',
            no_users_hint: 'Unable to load users. Please check your connection and try again.',
            phu_luc_i_hint: 'Internal workflow category. Statutory processing notices are Mẫu số 02a for organisations and 02b for individuals, with report 10; cross-border notices are 01a/01b, with report 09. Select the formal form separately.',
            phu_luc_ii_hint: 'Internal workflow category. Statutory processing notices are Mẫu số 02a for organisations and 02b for individuals, with report 10; cross-border notices are 01a/01b, with report 09. Select the formal form separately.',
            section_add_new: 'Add new section...',
            section_assignments_explanation: 'Assign sections of this dossier filing to departments for completion. Each assigned section creates a task for the assignee with a deadline. All sections must be completed before the filing can proceed to internal review.',
            section_consultation: 'Consultation & Stakeholders',
            section_custom_placeholder: 'Enter custom section name',
            section_data_categories: 'Data Categories & Processing',
            section_data_subject_rights: 'Data Subject Rights',
            section_impact_assessment: 'Impact Assessment',
            section_legal_basis: 'Legal Basis & Necessity',
            section_risk_mitigation: 'Risk Mitigation Measures',
            section_status_label: 'Section status',
            section_status_updated: 'Section status updated to',
            transition_to: 'Transition to...',
            view_filing: 'View',
            // Sprint 63.37.0 (Stream J) — the forms a filing files, and the dossier assembled from it.
            filing_forms: 'Forms in this filing',
            filing_forms_none: 'No form is linked to this filing yet.',
            filing_forms_failed: 'The forms of this filing could not be read.',
            filing_form_label: 'Form this filing records',
            assembled_dossier: 'Assembled dossier {reference}',
            dossier_role_primary_application: 'Primary form of the dossier',
            dossier_role_supporting_document: 'Supporting document',
            dossier_role_authority_response: 'Authority response',
            dossier_role_supplement: 'Supplement',
            // Sprint 63.37.0 (Stream K, UC-VN-01) — the person who recorded the submission; the platform records, the organisation files.
            submission_recorded_by: 'Recorded by',
            submission_notes: 'Notes',
            // Sprint 63.37.0 (Stream K, UC-VN-01) — the four section statuses (DossierSectionStatus); the table printed the stored code.
            section_status_assigned: 'Assigned',
            section_status_in_progress: 'In Progress',
            section_status_completed: 'Completed',
            section_status_reviewed: 'Reviewed',
            timer_estimate_passed: '{days} days past the estimate',
            timer_platform_estimate: 'Platform estimate — not a statutory period',
            reload: 'Reload',
            reloading: 'Reloading…',
            timers_load_failed: 'The filing timers could not be read, so none are shown. That does not mean the filing has none.',
            retry_subject_timers: 'the filing timers',
            lineage_load_failed: 'The filing lineage could not be read, so earlier and later filings are not shown.',
            retry_subject_lineage: 'the filing lineage',
            form_status_load_failed: 'The status of the forms could not be read, so the Forms column may be incomplete.',
            form_status_not_read: 'Not read',
            retry_subject_form_status: 'the form status',
            supplement_absence_declaring_framework_phase_reference_unresolved: 'No deadline: the period applies from a phase of its framework, and the framework does not date that phase.',
            // Sprint 62.57.0 (Slice R) — authority communications on a filing, recorded as
            // documents; the limitation notice; the latest-communication column and filter.
            result_recorded_qualifier: 'as your organisation recorded it from the authority’s notice',
            limitation_heading: 'What ComplianceOne records about the authority',
            limitation_body: 'ComplianceOne records the authority’s written communications as documents, and records the handling body, the result and any completion request as your organisation states them from the authority’s communications, and an update’s recipient and competent authority as your organisation records them. Completion dates are provisional where the governing text’s start event is pending legal confirmation, and ComplianceOne never decides which body handles a dossier or an update.',
            comms_heading: 'Authority communications',
            comms_intro: 'The written communications your organisation received from an authority about this filing, as a person in your organisation recorded them. Recording one changes nothing else on the filing: no status, result or deadline.',
            comms_record: 'Record a communication',
            comms_export: 'Export communications',
            comms_export_done: 'The communications were exported.',
            comms_export_failed: 'The communications could not be exported.',
            comms_none: 'No communication has been recorded on this filing.',
            comms_loading: 'Loading the communications…',
            comms_load_failed: 'The communications could not be read, so none are shown. That does not mean none were recorded.',
            retry_subject_comms: 'the authority communications',
            comms_pagination: 'Pages of authority communications',
            comms_col_kind: 'Kind',
            comms_col_sender: 'Sender',
            comms_col_communication_date: 'Date on the communication',
            comms_col_received_at: 'Received',
            comms_col_wording: 'The authority’s wording',
            comms_col_evidence: 'Evidence',
            comms_col_correction: 'Correction',
            comms_col_recorded: 'Recorded',
            comms_row_statement: 'Recorded by {person} from the authority’s communication — ComplianceOne has not verified it.',
            comms_reference: 'Reference: {reference}',
            comms_stated_items: 'Items stated',
            comms_stated_period: 'Period stated: {period}',
            comms_notes: 'Notes: {notes}',
            comms_no_wording: 'No wording recorded',
            comms_not_evidenced: 'Not evidenced',
            comms_evidence_count: '{count} file(s)',
            comms_evidence_open: 'Files',
            comms_evidence_open_label: 'Evidence files of the communication received on {date}',
            comms_evidence_load_failed: 'The evidence files could not be read.',
            comms_corrects: 'Corrects the entry of {date}',
            comms_corrects_unknown: 'Corrects an earlier entry',
            comms_corrected_by: 'Corrected by a later entry',
            comms_export_corrects: 'Corrects the entry received {date} ({kind})',
            comms_export_corrected_by: 'Corrected by a later entry (received {date})',
            comms_correction_note: 'Why: {note}',
            comm_kind_receipt_acknowledgement: 'Receipt acknowledgement',
            comm_kind_transfer_notice: 'Transfer notice',
            comm_kind_result_notice: 'Result notice',
            comm_kind_supplement_request: 'Supplement request',
            comm_kind_refusal: 'Refusal',
            comm_kind_other_written_communication: 'Other written communication',
            comms_form_intro: 'Record what the authority sent, as it is written. This record changes no status, result or deadline on the filing.',
            comms_form_evidence_hint: 'Evidence files are attached after the communication is recorded, from its row in the history.',
            comms_field_kind: 'Kind of communication',
            comms_field_kind_placeholder: 'Choose the kind',
            comms_field_sender: 'Sender, as the communication names it',
            comms_field_reference: 'Reference on the communication',
            comms_field_communication_date: 'Date on the communication',
            comms_field_received_at: 'Date your organisation received it',
            comms_field_wording: 'The authority’s wording, verbatim',
            comms_field_wording_hint: 'Up to {max} characters. Required for a result notice or a refusal.',
            comms_field_wording_count: '{count} of {max} characters used',
            comms_field_items: 'Items the communication states',
            comms_field_item: 'Item {n}',
            comms_add_item: 'Add an item',
            comms_remove_item: 'Remove item {n}',
            comms_items_none: 'No item added.',
            comms_field_period: 'Period the communication states, verbatim',
            comms_field_period_hint: 'Recorded as written. No deadline is computed from it.',
            comms_field_notes: 'Internal notes',
            comms_field_corrects: 'Corrects an earlier entry',
            comms_field_corrects_none: 'Not a correction',
            comms_field_corrects_option: '{kind}, received {date}',
            comms_field_corrects_truncated: 'Only the {count} most recent entries are listed.',
            comms_field_corrects_failed: 'The earlier entries could not be read, so none can be chosen.',
            comms_field_correction_note: 'Why the earlier entry is corrected',
            comms_error_required: 'Enter this before recording.',
            comms_error_kind_required: 'Choose the kind of communication.',
            comms_error_wording_required: 'Enter the authority’s wording for a result notice or a refusal.',
            comms_error_correction_note_required: 'Say why the earlier entry is corrected.',
            comms_error_receipt_invalid: "The date of receipt must not be later than today in your organisation's time zone.",
            comms_error_not_submitted: 'This filing has not been filed, so no communication can be recorded. The page has updated the filing’s details; what you entered is kept.',
            comms_record_failed: 'The communication could not be recorded. What you entered is kept.',
            comms_recorded: 'Communication recorded.',
            comms_recording: 'Recording…',
            comms_files_heading: 'Evidence files',
            comms_files_intro: 'The files this communication rests on, as your organisation attached them. ComplianceOne has not verified them.',
            comms_files_slot: 'Files of this communication',
            comms_files_none: 'No file is attached. This communication is not evidenced.',
            comms_file_hash: 'SHA-256: {hash}',
            comms_file_not_held: 'File no longer held',
            comms_file_download: 'Download',
            comms_file_remove: 'Remove',
            comms_file_upload: 'Attach a file',
            comms_file_bound: 'File attached.',
            comms_file_removed: 'File removed.',
            comms_file_bind_failed: 'The file could not be attached.',
            comms_file_remove_failed: 'The file could not be removed.',
            comms_file_not_owned: 'The communication this file belongs to was not found.',
            comms_file_remove_confirm: 'Remove this file from the communication and delete it?',
            latest_communication: 'Latest communication',
            latest_communication_none: 'None recorded',
            latest_communication_value: '{kind}, received {date}',
            aria_filter_by_communication: 'Filter by latest communication',
            filter_communication_none: 'No communication recorded',
            comms_export_heading: 'Communications recorded by the organisation',
            comms_export_filing: 'Filing: {filing}',
            comms_export_entries: 'Entries: {count}',
            comms_export_col_reference: 'Reference',
            comms_export_col_statement: 'Recorded',
            comms_export_truncated: 'Only the newest {shown} of {total} entries are included.',
            // Sprint 62.57.0 (Slices A and B) — the governing text and receipt, the handling
            // authority, the result from pack options, the post-result supplement and follow-up,
            // the provisional authority period and its legal reservation.
            // English until this locale is translated (R-57).
            status_result_recorded: 'Result recorded',
            status_result_supplement_required: 'Result recorded — supplement required',
            status_result_supplement_submitted: 'Result recorded — supplement submitted',
            step_result_recorded: 'Result recorded',
            step_result_supplement_required: 'Supplement required after the result',
            step_result_supplement_submitted: 'Supplement submitted after the result',
            timer_authority_review: 'Authority review',
            timer_provisional: 'Provisional — pending legal confirmation',
            timer_obligation_authority: 'The authority’s period, not your organisation’s',
            timer_calendar: 'Counted on the installed public-service calendar of working days and public holidays',
            timer_authority_stated: 'Result date stated by the authority',
            timer_provisional_passed: 'Provisional period passed — no result recorded',
            timer_absence_complete_receipt: 'No provisional date — complete-dossier receipt not recorded',
            timer_absence_calendar: 'No provisional date — the public-service calendar for {year} is not installed',
            timer_absence_no_period: 'No authority period declared',
            timer_absence_governing_text: 'No provisional date — governing text not determined',
            timer_absence_multiple_periods: 'No provisional date — the installed content declares more than one period, and none is chosen',
            timer_absence_uncomputable: 'No provisional date — the declared period cannot be counted as a date',
            timer_absence_calendar_not_declared: 'No provisional date — the declared period names no calendar to count on',
            timer_absence_calendar_unavailable: 'No provisional date — the public-service calendar is not installed',
            timer_absence_reference_mode: 'No provisional date — the content that states the period is installed for reference only',
            timer_absence_unreadable: 'No provisional date — the content that states the period could not be read',
            timer_absence_reappraisal_not_declared: 'No provisional date after the completion — not declared',
            timer_absence_completed_receipt: 'No provisional date — receipt of the completed dossier not recorded',
            timer_period_closed: 'A result is recorded; the period no longer runs',
            timer_absence_heading: 'Authority review',
            reservation_pending: 'Pending legal confirmation',
            comm_pick_empty: 'No communication is recorded on this filing yet. Record it under Authority communications first.',
            comm_pick_failed: 'The communications on this filing could not be read.',
            comm_pick_manual_label: 'Communication record id',
            comm_pick_manual_hint: 'Enter the id of a communication recorded on this filing; it is checked when you save.',
            comm_pick_off_list: 'Communication {value} (not in the list)',
            comm_pick_none: 'No communication',
            comm_pick_option_reference: '{kind}, received {date}, reference {reference}',
            error_communication_invalid: 'Choose a communication recorded on this filing.',
            error_corrected_entry_not_found: 'The entry this corrects was not found on this filing. Choose it again.',
            error_entry_already_corrected: 'That entry has already been corrected. Correct the later entry instead.',
            correction_target_moved: 'The entry you chose was corrected by another entry; this now corrects that entry. Check it before you save.',
            correction_target_required: 'Choose the entry this corrects.',
            receipt_error_already_recorded: 'A receipt of this kind is already recorded. To change it, correct that entry.',
            error_assignee_not_in_organisation: 'A person chosen is not a member of your organisation. Choose someone else.',
            error_filing_changed: 'The filing changed while you were entering this. The page has read it again; what you entered is kept. Save again.',
            error_not_submitted: 'This filing has not been filed, so this cannot be recorded. The page has updated the filing’s details; what you entered is kept.',
            error_field_refused: 'This value was not accepted. Check it and try again.',
            gov_heading: 'Governing text',
            gov_intro: 'The text that governed this dossier when the authority received it, matched from the receipt date your organisation recorded. It is never worked out from the date the filing was created or submitted.',
            gov_not_determined: 'Governing text not determined — record the receipt date',
            gov_reason_receipt_not_evidenced: 'No receipt date has been recorded.',
            gov_reason_no_declaration: 'The installed content declares no governing text for this kind of dossier.',
            gov_reason_receipt_outside_declared_windows: 'The receipt date falls outside every period the installed content declares.',
            gov_reason_declaration_unreadable: 'The installed declaration could not be read.',
            gov_requirement: 'Practice requirement: {title}',
            gov_requirement_framework: 'Practice requirement: {title} ({framework})',
            gov_receipt_date: 'Receipt date used',
            gov_evidenced_by: 'Receipt recorded by',
            gov_end_label: 'End of this period',
            gov_declared_by: 'Declared by',
            gov_declared_by_not_installed: 'A framework that is not installed',
            gov_review_required: 'The stored governing text no longer matches the installed declaration — review.',
            receipt_heading: 'Receipt by the authority',
            receipt_record: 'Record the receipt date',
            receipt_correct: 'Correct the receipt date',
            receipt_form_intro: 'Record the date the authority received the dossier, as its receipt or your evidence shows it. The date decides which text governs the dossier. A correction is a new entry; the earlier one stays in the history.',
            receipt_field_event: 'What the authority received',
            receipt_event_dossier_received: 'Dossier received',
            receipt_event_complete_dossier_received: 'Complete and valid dossier received',
            receipt_field_received_on: 'Date the authority received it',
            receipt_field_postmarked_on: 'Postmark date (dossier sent by post)',
            receipt_field_postmarked_hint: 'Only for a dossier sent by post. Kept beside the receipt date, and read only where the installed text counts the postmark.',
            receipt_field_communication: 'Communication that evidences it (optional)',
            receipt_field_corrects: 'Entry this corrects',
            receipt_field_corrects_none: 'Not a correction',
            receipt_corrects_option: '{event}, {date}',
            receipt_error_date_invalid: 'The date cannot be later than today, and the dates must run in order: postmark, dossier received, then complete and valid dossier received.',
            receipt_error_event_required: 'Choose what the authority received.',
            receipt_error_event_mismatch: 'A correction must state the same thing the authority received as the entry it corrects.',
            receipt_record_failed: 'The receipt could not be recorded. What you entered is kept.',
            receipt_recorded: 'Receipt date recorded.',
            receipt_history: 'Receipt entries',
            receipt_col_event: 'Event',
            receipt_col_received_on: 'Received on',
            receipt_col_postmarked_on: 'Postmark',
            receipt_col_communication: 'Communication',
            receipt_col_correction: 'Correction',
            receipt_col_recorded: 'Recorded',
            receipt_source_submission_acknowledgment: 'Carried over from the date of the authority’s acknowledgement of receipt, recorded with the submission',
            receipt_row_statement: 'Recorded by {person} — ComplianceOne has not verified it.',
            handling_heading: 'Handling authority',
            handling_intro: 'The body the dossier was filed with is the one the installed form names. The body that handles the dossier is recorded only by a person in your organisation, from the authority’s own communication; ComplianceOne never works it out or proposes one.',
            handling_filed_with: 'Filed with',
            handling_filed_with_unknown: 'Not declared by the installed form',
            handling_handled_by: 'Handled by',
            handling_not_known: 'Not known — no transfer recorded',
            handling_other_body: 'Other body named',
            handling_option_not_named: 'A body the installed content no longer names',
            handling_class_label: 'Body',
            handling_named_as: 'Named in the communication',
            handling_undeclared: 'The installed form for this filing declares no handling authority, so none can be recorded here.',
            handling_transfer_basis: 'Transfer basis',
            handling_transfer_none_stated: 'No transfer is provided for in this text',
            handling_transfer_awaiting_receipt: 'The transfer basis is shown once the receipt is recorded',
            handling_criteria: 'Criteria for a transfer',
            handling_may_transfer_to: 'Bodies the installed form names',
            handling_applies_from: 'The installed form states this applies to dossiers received from {date}.',
            handling_record: 'Record the handling authority',
            handling_history: 'Statements recorded',
            handling_none: 'No statement has been recorded.',
            handling_loading: 'Loading the statements…',
            handling_load_failed: 'The statements could not be read, so none are shown. That does not mean none were recorded.',
            retry_subject_handling: 'the handling statements',
            handling_pagination: 'Pages of handling statements',
            handling_col_body: 'Body',
            handling_col_basis: 'Basis',
            handling_col_communication: 'Communication',
            handling_col_evidence: 'Evidence',
            handling_col_correction: 'Correction',
            handling_col_recorded: 'Recorded',
            handling_basis_transfer_notice: 'Transfer notice',
            handling_basis_authority_result: 'The authority’s result',
            handling_basis_other_written_communication: 'Other written communication',
            handling_source_corrected: 'Source communication corrected — review',
            handling_communication_value: '{kind}, received {date}',
            handling_communication_unknown: 'A communication not on the first page of the history',
            handling_form_intro: 'State the body that handles the dossier as the authority’s communication names it. Choose it from the bodies the installed form names, or name another body. Nothing is filled in for you.',
            handling_field_basis: 'Basis for the statement',
            handling_field_basis_placeholder: 'Choose the basis',
            handling_field_communication: 'Communication it rests on',
            handling_field_option: 'Body',
            handling_field_option_placeholder: 'Choose the body',
            handling_option_other: 'Another body (name it below)',
            handling_field_body_name: 'The body’s name, as the communication gives it',
            handling_field_body_name_hint: 'Up to {max} characters, copied from the communication.',
            handling_field_corrects: 'Statement this corrects',
            handling_field_corrects_none: 'Not a correction',
            handling_corrects_option: '{body}, recorded {date}',
            handling_error_basis_required: 'Choose the basis for the statement.',
            handling_error_option_required: 'Choose the body, or choose another body and name it.',
            handling_error_option_not_declared: 'That body is not among those the installed form names. Choose another, or name it as another body.',
            handling_record_failed: 'The statement could not be recorded. What you entered is kept.',
            handling_recorded: 'Handling authority recorded.',
            result_heading: 'The authority’s result',
            result_frame: 'Result recorded: {label} — as your organisation recorded it from the authority’s notice',
            result_legacy_frame: 'Result recorded: {label} — recorded before results followed the governing text',
            result_none: 'No result has been recorded.',
            result_label_not_installed: 'Result recorded — its label is not in the installed content',
            result_different_text: 'Recorded under a different governing text — review',
            result_date: 'Date on the notice',
            result_notice_received: 'Date your organisation received the notice',
            result_communication: 'Communication',
            result_notes: 'Notes',
            result_record: 'Record the authority’s result',
            result_needs_receipt: 'Recording a result needs the receipt date first: the result options are those of the text that governed the dossier when the authority received it.',
            result_absent: 'No installed declaration offers result options for this filing, so no result can be recorded here. Record the authority’s notice under Authority communications.',
            result_absent_update: 'No result is recorded on an update dossier. Record the authority’s notice under Authority communications.',
            result_form_intro: 'Record the result as the authority’s notice states it. The options are those of the text that governed the dossier when the authority received it.',
            result_field_option: 'Result, as the notice states it',
            result_field_option_placeholder: 'Choose the result',
            result_field_communication: 'Communication the result comes from',
            result_error_option_required: 'Choose the result the notice states.',
            result_error_option_not_offered: 'That result is not offered for this filing’s governing text.',
            result_error_date_invalid: 'The date must not be later than today or earlier than the day the filing was submitted.',
            result_error_notice_before_result: 'The date your organisation received the notice cannot be before the date on the notice.',
            result_error_not_determined: 'The governing text is not determined. Record the receipt date first.',
            result_error_declaration_absent: 'No installed declaration offers result options for this filing.',
            result_error_status: 'A result cannot be recorded in the filing’s current state. The page has read the filing again; what you entered is kept.',
            result_record_failed: 'The result could not be recorded. What you entered is kept.',
            result_saved: 'The result is recorded.',
            result_correct: 'Correct this result',
            result_correct_intro: 'Record the result again as the authority’s notice states it, and say why. The earlier entry stays in the history. A result can be corrected only while it is the latest result, the filing is not closed, and no supplement request, supplement submission or follow-up has been recorded for it.',
            result_correcting_target: 'You are correcting: {result}, dated {date}',
            result_field_correction_note: 'Why the result is corrected',
            result_corrects_earlier: 'Corrects an earlier entry. Reason: {note}',
            result_earlier_heading: 'Earlier entries',
            result_corrected: 'The result is corrected.',
            result_error_correction_not_open: 'This result can no longer be corrected: it is not the latest result, the filing is closed, or a supplement request, supplement submission or follow-up has been recorded for it. The page has read the filing again.',
            result_error_not_found: 'The result being corrected was not found on this filing. The page has read the filing again; what you entered is kept.',
            result_error_already_corrected: 'This result has already been corrected by another entry. The page is reading the filing again.',
            result_error_correction_blocked: 'Nothing can be saved from this dialog. Close it; what you entered will not be recorded. If the page still offers a correction, start it again from the current result.',
            result_row_statement: 'Recorded by {person} from the authority’s notice — ComplianceOne has not verified it.',
            post_result_citation: 'What the governing text provides after this result: {citation}',
            stated_date_heading: 'Result dates stated by the authority',
            stated_date_record: 'Record a result date the authority stated',
            stated_date_form_intro: 'Record the result date the authority itself states, on a receipt slip or a later notice. It replaces the provisional date. A correction is a new entry.',
            stated_date_field_date: 'Result date the communication states',
            stated_date_field_communication: 'Communication that states it',
            stated_date_field_corrects: 'Entry this corrects',
            stated_date_corrects_option: '{date}, recorded {recorded}',
            stated_date_value: 'Stated by the authority: {date}',
            stated_date_recorded: 'Stated result date recorded.',
            stated_date_record_failed: 'The stated date could not be recorded. What you entered is kept.',
            rsupp_heading: 'Supplement after the result',
            rsupp_intro: 'The result and any concrete request are separate records. Record the items and any period the notice states; ComplianceOne computes no date from the result.',
            rsupp_no_request: 'Supplement required — no request recorded',
            rsupp_requested: 'Supplement requested — not yet submitted',
            rsupp_submitted: 'Supplement submitted',
            rsupp_record: 'Record the supplement request',
            rsupp_no_period: 'No period stated in the notice',
            rsupp_stated_deadline: 'Date stated in the notice: {date}',
            rsupp_item_owner: 'Owner: {person}',
            rsupp_item_task: 'Task assigned',
            rsupp_col_items: 'Items requested',
            rsupp_col_communication: 'Communication',
            rsupp_col_period: 'Period',
            rsupp_col_recorded: 'Recorded',
            rsupp_form_intro: 'Record what the notice asks for, item by item. An item with an owner becomes a task for that person. Only a date the notice itself states is recorded; none is computed.',
            rsupp_field_communication: 'Communication the request comes from',
            rsupp_field_items: 'Items the notice asks for',
            rsupp_field_item: 'Item {n}',
            rsupp_field_item_owner: 'Owner of item {n} (optional)',
            rsupp_add_item: 'Add an item',
            rsupp_remove_item: 'Remove item {n}',
            rsupp_field_period: 'Period the notice states, verbatim',
            rsupp_field_deadline: 'Date the notice states (optional)',
            rsupp_field_deadline_hint: 'Only a date the notice itself states. Each item with an owner becomes a task due on this date, or with no due date when the notice states none.',
            rsupp_error_items_required: 'Add at least one item the notice asks for.',
            rsupp_error_not_open: 'No supplement is open after this result. The page has read the filing again; what you entered is kept.',
            rsupp_record_failed: 'The supplement request could not be recorded. What you entered is kept.',
            rsupp_recorded: 'Supplement request recorded.',
            followup_heading: 'Follow-up after the result',
            followup_intro: 'This result leaves the filing open. Record the reasons the authority stated, the remediation your organisation chose and any enforcement risk. Nothing on any other record changes.',
            followup_not_recorded: 'No follow-up recorded yet',
            followup_refusal: 'The authority’s communication',
            followup_reasons: 'Reasons stated',
            followup_remediation: 'Remediation',
            followup_actions: 'Remediation actions',
            followup_action_due: 'Your organisation’s own date: {date}',
            followup_action_owner: 'Owner: {person}',
            followup_enforcement_risk: 'Potential enforcement risk',
            followup_enforcement_risk_none: 'None stated',
            followup_revision: 'Recorded by {person} on {date}',
            followup_record: 'Record the follow-up',
            followup_revise: 'Revise the follow-up',
            followup_form_intro: 'Record the reasons the notice states and the remediation your organisation chose. Each action’s owner and date are your organisation’s own choice; ComplianceOne computes no date.',
            followup_field_reasons: 'Reasons the notice states',
            followup_field_summary: 'Remediation',
            followup_field_actions: 'Remediation actions',
            followup_field_action: 'Action {n}',
            followup_field_action_title: 'What is to be done (action {n})',
            followup_field_action_owner: 'Owner (action {n})',
            followup_field_action_due: 'Your organisation’s own date (action {n})',
            followup_add_action: 'Add an action',
            followup_remove_action: 'Remove action {n}',
            followup_actions_none: 'No action added.',
            followup_field_enforcement: 'Potential enforcement risk (optional)',
            followup_field_enforcement_hint: 'Leave it empty when none is stated; the record then says so.',
            followup_field_correction_note: 'Why the follow-up is revised',
            followup_error_not_open: 'No follow-up is open for this filing. The page has read the filing again; what you entered is kept.',
            followup_record_failed: 'The follow-up could not be recorded. What you entered is kept.',
            followup_recorded: 'Follow-up recorded.',
            close_title: 'Close the filing',
            close_intro: 'Closing records that your organisation has finished with this filing. It is not a decision of the authority.',
            close_field_reason: 'Closing reason',
            close_field_reason_hint: 'Required where the page says so; otherwise optional.',
            close_refusal_DOSSIER_RESULT_SUPPLEMENT_OPEN: 'The supplement after the result is still open. Record it as submitted before closing.',
            close_refusal_DOSSIER_FAILURE_FOLLOW_UP_OPEN: 'No follow-up is recorded for this result. Record the follow-up, or give the reason for closing without one.',
            close_refusal_DOSSIER_CLOSE_REASON_REQUIRED: 'Give the reason for closing this filing.',
            close_closing: 'Closing…',
            close_failed: 'The filing could not be closed. What you entered is kept.',
            closing_reason_value: 'Closing reason: {reason}',
            column_handled_by: 'Handled by',
            aria_filter_by_handling: 'Filter by handling authority',
            filter_handling_not_known: 'Not known — no transfer recorded',
            filter_handling_other: 'Other body named',
            prior_filing_open_follow_up: 'Open — follow-up recorded',
            prior_filing_open_no_follow_up: 'Open — the result did not pass, and no follow-up is recorded yet',
            prior_filing_body_open: 'The installed framework provides for one dossier of this kind per organisation, updated afterwards ({citation}). This organisation filed one: {filing}. A result is recorded on it. For a new purpose or another change to its content, start the update on this assessment instead.',
            // Sprint 62.57.0 (Slices A, B, F; A-1) — the filing record export’s labels.
            record_export_receipt_heading: 'Receipt of the dossier and the governing text',
            record_export_receipt_none: 'No receipt of the dossier has been recorded.',
            record_export_col_event: 'Receipt',
            record_export_col_received_on: 'Received by the authority',
            record_export_col_postmarked_on: 'Postmark',
            record_export_col_status: 'Entry',
            record_export_entry_standing: 'Standing',
            record_export_entry_corrected: 'Corrected by a later entry',
            record_export_governing_text: 'Governing text: {label}',
            record_export_governing_end: 'End of the window: {label}',
            governing_text_not_determined: 'Governing text not determined — record the receipt date',
            record_export_governing_review: 'The recorded governing text no longer matches the installed declaration — review',
            record_export_reservation: 'Pending legal confirmation: {note}',
            record_export_handling_heading: 'Handling authority',
            record_export_filed_with: 'Filed with: {body}',
            record_export_handled_by_unknown: 'Handled by: not known — no transfer recorded',
            record_export_transfer_basis: 'Transfer basis: {text}',
            record_export_transfer_none_stated: 'No transfer is provided for in this text',
            record_export_transfer_no_stamp: 'The transfer basis is shown once the receipt is recorded',
            record_export_col_body: 'Body named in the communication',
            record_export_col_option: 'Declared body',
            record_export_col_basis: 'Basis',
            record_export_col_source: 'Source communication',
            record_export_other_body: 'Other body named',
            record_export_source_corrected: 'Source communication corrected — review',
            record_export_result_heading: 'Result recorded',
            record_export_result_none: 'No result has been recorded.',
            record_export_result_corrected_by: 'Corrected by a later entry',
            record_export_result_corrects: 'Corrects an earlier entry. Reason: {note}',
            record_export_result_label: 'Result recorded: {label} — as your organisation recorded it from the authority’s notice',
            record_export_result_legacy: 'Result recorded: {legacy} — recorded before results followed the governing text',
            record_export_result_dates: 'Date on the notice: {result_date}. Received by the organisation: {received}.',
            result_recorded_under_different_governing_text: 'Recorded under a different governing text — review',
            record_export_result_follow_up: 'What the result opens: {citation}',
            record_export_supplement_heading: 'Supplement requests after the result',
            result_supplement_no_request: 'Supplement required — no request recorded',
            record_export_supplement_period: 'Period stated in the notice: {period}',
            no_period_stated_in_notice: 'No period stated in the notice',
            record_export_follow_up_heading: 'Follow-up to the result',
            record_export_follow_up_none: 'No follow-up has been recorded.',
            record_export_follow_up_reasons: 'Reasons stated: {text}',
            record_export_follow_up_remediation: 'Remediation: {text}',
            record_export_follow_up_risk: 'Potential enforcement risk: {text}',
            record_export_col_action: 'Remediation action',
            record_export_col_owner: 'Person responsible',
            record_export_col_own_date: 'Your organisation’s own date',
            record_export_period_heading: 'The authority’s period',
            record_export_period_stated: 'Result date stated by the authority: {date}',
            record_export_period_counted: 'Counted from {start} on the {calendar} calendar: {date}',
            record_export_period_passed: 'Provisional period passed — no result recorded',
            record_export_period_not_dated: 'No provisional date ({reason})',
            // Sprint 62.57.0 (Slices C and D, list half of AC-F5) — the completion period, the
            // completion request and submission, the update's recipient and competent authority,
            // the list's governing-text column and filters, the requirement link.
            status_result_completion_submitted: 'Completion submitted — no further result recorded',
            step_result_completion_submitted: 'Completion submitted',
            receipt_event_completed_dossier_received: 'Completed dossier received',
            receipt_field_completion_submission: 'Completion submission received',
            receipt_completion_option: 'Completion submitted on {date}',
            receipt_completion_option_recorded: 'Completion recorded on {date}',
            receipt_error_completion_invalid: 'Choose the completion submission the authority received.',
            completion_heading: 'Completion period',
            completion_intro: 'A completion period opens only when your organisation records that a communication asks it to complete the dossier, or records a result for which the governing text provides one. The dates are worked out from the installed regulatory content each time the filing is read.',
            completion_none: 'No completion period is open on this filing.',
            completion_current: 'Current period',
            completion_history: 'Earlier periods',
            completion_date_provisional: 'Provisional date — start event pending legal confirmation',
            completion_date_confirmed: 'Completion date',
            completion_date_label: 'Completion date',
            completion_passed_provisional: 'Provisional completion date passed',
            completion_passed_confirmed: 'Completion period passed',
            completion_running: 'Within the period',
            completion_completed: 'Completion submitted on {date}',
            completion_completed_no_date: 'Completion submitted',
            completion_not_dated: 'No completion date',
            completion_state: 'State',
            completion_notice_date: 'Notice date',
            completion_received_on: 'Received by your organisation',
            completion_authority_stated: 'Date stated by the authority',
            completion_trigger: 'Opened by',
            completion_trigger_completion_request: 'A completion request your organisation recorded',
            completion_trigger_result: 'The result your organisation recorded',
            completion_source: 'Source communication',
            completion_deficiency: 'What the governing text provides',
            completion_citation: 'Citation',
            completion_start_event: 'Start event',
            completion_period_length: 'Period: {period} {unit}',
            completion_unit_calendar_days: 'calendar days',
            completion_unit_working_days: 'working days',
            completion_end_roll_none: 'ComplianceOne shows the end date as counted and does not move it to the next working day when it falls on a day off.',
            completion_end_roll_next_statutory_working_day: 'An end that falls on a day off moves to the next working day.',
            completion_obligation: 'Your organisation’s period, not the authority’s',
            completion_task_raised: 'A task was raised when the date passed.',
            completion_col_period: 'Period',
            completion_absence_trigger_not_documented: 'No completion period — this request cites no recorded communication',
            completion_absence_trigger_not_declared: 'No completion period is declared for this result under the governing text',
            completion_absence_governing_text_not_determined: 'No completion date — the governing text is not determined; record the receipt date',
            completion_absence_no_period_declared: 'No completion period is declared in the installed content for this dossier',
            completion_absence_multiple_candidate_periods: 'No completion date — the installed content declares more than one period and none is chosen',
            completion_absence_statutory_calendar_unavailable: 'No completion date — the statutory calendar is not installed',
            completion_absence_receipt_not_evidenced: 'No completion date — your organisation’s receipt of the communication is not recorded',
            completion_absence_declaring_framework_reference_mode: 'No completion date — the content that declares the period is installed for reference only',
            completion_absence_declaring_framework_not_in_force: 'No completion date — the content that declares the period was not in force at the dossier’s receipt',
            completion_absence_declaring_framework_unreadable: 'No completion date — the content that declares the period could not be read',
            completion_absence_no_installed_clock: 'No completion period is declared in the installed content for this dossier',
            completion_absence_multiple_candidate_clocks: 'No completion date — the installed content declares more than one period and none is chosen',
            completion_absence_uncomputable_period: 'No completion date — the declared period cannot be counted as a date',
            completion_absence_declaring_framework_phase_reference_unresolved: 'No completion date — the content that declares the period does not date the phase it names',
            completion_absence_declaring_framework_not_operational: 'No completion date — the content that declares the period is not in operation',
            completion_consequence_heading: 'Potential enforcement risk',
            completion_body_handled_by: '{name} — from your recorded handling statement',
            completion_body_filed_with: '{name} — the body the dossier was filed with; no handling statement recorded',
            completion_fine_area_absent: 'The penalties framework that declares this is not installed in your organisation',
            completion_fine_area_articles: 'Articles: {articles}',
            completion_fine_area_reference: 'Installed for reference only',
            completion_fine_area_link: 'Open {area} in Penalty Readiness',
            completion_requests_heading: 'Completion requests recorded',
            completion_requests_none: 'No completion request has been recorded.',
            completion_col_communication: 'Communication',
            completion_col_recorded: 'Recorded',
            completion_col_correction: 'Correction',
            completion_request_record: 'Record a completion request',
            completion_request_correct: 'Correct this request',
            completion_request_intro: 'Choose the communication and state what it asks. ComplianceOne does not read the communication’s wording to decide this.',
            completion_field_communication: 'Communication',
            completion_statement: 'The communication asks your organisation to complete the dossier, or states that it is incomplete, invalid or not in accordance with the regulations',
            completion_statement_required: 'Tick the statement to record the request.',
            completion_context_heading: 'What the governing text provides (for reference)',
            completion_field_corrects: 'Request this corrects',
            completion_field_corrects_none: 'Not a correction — a new request',
            completion_corrects_option: '{communication}, recorded {date}',
            completion_request_recorded: 'Completion request recorded.',
            completion_request_failed: 'The completion request could not be recorded. What you entered is kept.',
            completion_error_not_open: 'A completion request can be recorded here only while a supplement request or a result is recorded on the filing. Before a result, record it with the supplement request.',
            completion_request_row: 'Recorded by {person} on {date}',
            completion_request_withdraw: 'Withdraw this entry',
            completion_request_withdraw_title: 'Withdraw this completion request entry',
            completion_request_withdraw_intro: 'Withdraw an entry that recorded a request in error: no completion period is counted from it any more. The entry stays in the history with your note. If another communication asks your organisation to complete the dossier, record a new request instead.',
            completion_request_withdraw_target: 'Entry withdrawn',
            completion_request_withdraw_note: 'Why this entry is withdrawn',
            completion_request_withdraw_note_required: 'Say why the entry is withdrawn.',
            completion_request_withdrawing: 'Withdrawing…',
            completion_request_withdrawn_msg: 'Completion request entry withdrawn.',
            completion_request_withdraw_failed: 'The withdrawal could not be recorded. What you entered is kept.',
            completion_request_withdraw_gone: 'This entry has been corrected or withdrawn meanwhile, or is no longer on this filing. The page has read the filing again.',
            completion_request_withdrawn: 'Withdrawn',
            completion_request_withdraws: 'Withdraws the entry recorded on {date}',
            completion_request_withdrawn_by: 'Withdrawn by a later entry',
            supplement_field_communication: 'Communication that asks for the supplement',
            supplement_communication_hint: 'A completion period is counted only from a recorded communication. Record the communication first under Authority communications.',
            supplement_error_completion_needs_communication: 'Choose the communication before you tick the statement.',
            supplement_request_failed: 'The supplement request could not be recorded. What you entered is kept.',
            result_completion_hint: 'Tick only if this communication asks your organisation to complete the dossier, or states that it is incomplete, invalid or not in accordance with the regulations. A result alone opens a completion period only where the governing text provides for one.',
            completion_submit: 'Record completion submitted',
            completion_submitted_on: 'Date submitted',
            completion_submit_intro: 'Record the day your organisation submitted the completed dossier to the authority. A further result may then be recorded on this filing.',
            completion_submit_intro_supplement: 'Record the day your organisation submitted the completed dossier in answer to the supplement request, and what it submitted.',
            supplement_submit_intro: 'Record what your organisation submitted in answer to the supplement request.',
            completion_submitted_msg: 'Completion submitted recorded.',
            completion_submit_failed: 'The submission could not be recorded. What you entered is kept.',
            completion_error_date_invalid: 'The date cannot be in the future or before your organisation received the request.',
            completion_error_status: 'This filing’s state no longer allows this. The page has been read again; what you entered is kept.',
            column_governing_text: 'Governing text',
            aria_filter_by_governing_text: 'Filter by governing text',
            filter_governing_not_determined: 'Not determined',
            filter_completion_passed: 'Completion period: date passed',
            list_completion_due_provisional: 'Provisional completion date: {date}',
            list_completion_due: 'Completion date: {date}',
            chip_governed_by_requirement: 'Filings governed by the requirement you opened',
            chip_governed_by_requirement_title: 'Filings governed by: {title}',
            chip_updates_of: 'Updates of the original filing',
            chip_remove: 'Remove filter: {label}',
            requirement_filings_link: 'Filings this governs: {n}',
            requirement_filings_link_failed: 'The filings this governs could not be counted.',
            change_revise: 'Revise this filing',
            change_update: 'Prepare an update filing',
            change_mode_legend: 'What do you want to do?',
            change_update_form: 'Update form',
            change_update_loading: 'Reading the original filing and the installed update forms…',
            change_update_load_failed: 'The original filing or the installed update forms could not be read.',
            change_update_intro: 'The update is a new filing in ComplianceOne that names this one; preparing it sends nothing to the authority. Choose each body yourself; nothing is chosen for you.',
            update_sent_to: 'Sent to',
            update_sent_to_help: 'The body your organisation files this update with; the cover letter is addressed to it when it is not the body the original was filed with.',
            update_competent: 'Competent to handle',
            update_competent_help: 'The body your organisation records, from the authority’s communications, as competent to handle the dossier. ComplianceOne does not determine it.',
            update_context: 'Original dossier — filed with: {filed_with}. Handled by: {handled_by}.',
            update_context_not_known: 'not known — no transfer recorded',
            update_context_filed_with_not_known: 'not declared by the installed form',
            update_choice_filed_with: 'The body the original dossier was filed with: {name}',
            update_choice_handled_by: 'The body recorded as handling the original dossier: {name}',
            update_choice_named: 'Another body (name it as the communication does)',
            update_named_label: 'Name of the body, as the communication gives it',
            update_reason: 'Why this differs from the body handling the original dossier',
            update_reason_now_required: 'A reason is now required: Why this differs from the body handling the original dossier',
            update_error_choice_required: 'Choose a body.',
            update_error_name_required: 'Enter the body’s name.',
            update_error_reason_required: 'Say why this differs from the body handling the original dossier.',
            update_error_body_invalid: 'This body can no longer be chosen: the original dossier has no recorded handling body. Choose another.',
            update_error_not_declared: 'No installed update form of this kind provides for recording the recipient and competent authority.',
            update_error_form_invalid: 'The update form could not be used for this filing. Start again.',
            update_error_target_invalid: 'An update cannot be prepared from an update. Open the original filing.',
            update_error_locked: 'These can no longer be changed: the update dossier has been prepared for filing or filed.',
            update_error_original_not_found: 'The original filing was not found. Choose it again.',
            update_error_original_invalid: 'This filing cannot be chosen as the original. Choose another.',
            update_error_forms_denied: 'Your role does not give you access to forms, so an update filing cannot be prepared here. Ask an administrator for access to forms.',
            update_original_choose_first: 'Choose the original filing first.',
            update_error_not_submitted: 'The original filing has not been filed, so no update can be prepared from it.',
            update_create_failed: 'The update could not be created. What you entered is kept.',
            update_form_kept: 'An update form was started but not linked to this filing. Prepare the update again from this filing to use it; do not complete it from the assessment’s forms, which would create an update that names no original filing.',
            update_form_reused: 'This update reuses the update form already started from this filing: {form}.',
            completion_mode_changed: 'The completion period on this filing changed while you were entering this. The dialog now records the supplement response only; what you entered is kept.',
            completion_mode_changed_completion: 'The completion period on this filing changed while you were entering this. The dialog now records the completion submitted and asks for the date submitted; what you entered is kept.',
            supplement_request_no_longer_open: 'A supplement request can no longer be recorded on this filing: its status changed meanwhile (for example, another person recorded one). The page has read it again. Close this dialog.',
            update_form_no_longer_offered: 'The update form started earlier is no longer offered by your installed content; choose a form.',
            update_created: 'Update filing created. Fill in the update form.',
            update_creating: 'Creating…',
            update_section_heading: 'Recipient and competent authority',
            update_section_intro: 'Your organisation records where it files the update, and, from the authority’s communications, which body is competent to handle it. ComplianceOne never decides which body handles a dossier or an update.',
            update_not_recorded: 'Not recorded yet.',
            update_record: 'Record the recipient and competent authority',
            update_edit: 'Change the recipient and competent authority',
            update_saving: 'Saving…',
            update_recorded_msg: 'Recipient and competent authority recorded.',
            update_record_failed: 'The recipient and competent authority could not be recorded. What you entered is kept.',
            update_history: 'Earlier records',
            update_recorded_by: 'Recorded by {person} on {date}',
            update_source_filed_with: 'The body the original dossier was filed with',
            update_source_handled_by: 'The body recorded as handling the original dossier',
            update_source_named: 'Named by your organisation',
            update_addressee_review_prompt: 'The body recorded as handling the original dossier has changed since this was recorded — review',
            update_window_rule_flag: 'The text that governs this update provides for filing it with {body}; your organisation recorded {sent_to} — review',
            update_window_rule_flag_unnamed: 'The text that governs this update provides for filing it with another body than the one your organisation recorded ({sent_to}) — review',
            update_bases_disclosure: 'Which body an update is filed with depends on when the authority receives it',
            update_basis_own_window: 'What the text that governs this update provides',
            update_original: 'Updates the filing',
            update_original_open: 'Open the original filing',
            updates_of_this: 'Updates of this dossier ({n})',
            gov_update_own_receipt: 'An update is dated by its own receipt, not by the original dossier’s.',
            completion_task_title_provisional: 'Provisional completion date passed: {filing}',
            completion_task_title_confirmed: 'Completion period passed: {filing}',
            update_original_pick_loading: 'Reading the filings…',
            update_original_pick_empty: 'No filed filing can be chosen as the original.',
            update_original_pick_failed: 'The filings could not be read.',
            update_original_pick_manual_label: 'Original filing record ID',
            update_original_pick_manual_hint: 'Enter the ID of a filed filing; it is checked when you save.',
            update_original_pick_off_list: 'Filing {value} (not in the list)',
            followup_completion_link: 'See the completion period',
            record_export_completion_heading: 'Completion period',
            record_export_completion_none: 'No completion request has been recorded.',
            record_export_completion_trigger_request: 'Completion request recorded from the communication {reference}',
            record_export_completion_trigger_result: 'Result recorded: {label}',
            record_export_completion_dates: 'Notice date: {notice}. Received by your organisation: {received}.',
            record_export_completion_provisional: 'Provisional date — start event pending legal confirmation: {date}',
            record_export_completion_confirmed: 'Completion date: {date}',
            record_export_completion_stated: 'Date stated by the authority: {date}',
            record_export_completion_passed_provisional: 'Provisional completion date passed',
            record_export_completion_passed: 'Completion period passed',
            record_export_completion_completed: 'Completion submitted: {date}',
            record_export_completion_body_not_recorded: 'the body competent to handle the dossier (not recorded on this filing)',
            record_export_completion_area: 'Penalty area: {name} ({articles})',
            record_export_completion_area_absent: 'The penalties framework that declares this is not installed in your organisation',
            record_export_update_heading: 'Recipient and competent authority',
            record_export_update_sent_to: 'Sent to: {body}',
            record_export_update_competent: 'Competent to handle: {body}',
            record_export_update_reason: 'Why this differs from the body handling the original dossier: {reason}',
            record_export_update_basis: '{window}: {basis}',
            record_export_update_window_flag: 'The text that governs this update provides for filing it with another body than the one recorded — review',
        },
        materialisation_health: {
            nav_label: 'Soumissioun artefakt Gesondheet',
            page_title: 'Soumissioun Artefakt Gesondheet',
            page_description: 'Entdeckt a remedéiert dossiermaterialiséierungsproblemer. Lëschte sinn op Är Organisatioun scoped.',
            failed_title: 'Ausgefall Materialisatiounen',
            failed_empty: 'Keng gescheitert Materialisatiounen - gutt.',
            pending_title: 'Genehmegt awer net materialiséiert',
            pending_empty: 'All guttgeheescht Areeche hunn eng Ënnerstëtzung dossier.',
            orphan_title: 'Orphan Dossieren',
            orphan_description: 'Dossieren erstallt duerch Materialiséierung déi net méi vun all Areeche referenzéiert sinn - typesch de Verléierer vun enger concurrent Course. Opgezielt fir Enquête; Botzen ass manuell.',
            orphan_empty: 'Keng Orphan Dossieren fonnt.',
            materialise_button: 'Material elo',
            column_failure_reason: 'Grond fir Feeler',
            column_failed_at: 'Ausgefall op',
            column_retries: 'Wieder probéiert',
            column_approved_at: 'Genehmegt um',
            column_dossier_id: 'Dossier ID',
            column_source_filing_id: 'Source Areeche',
            column_dossier_type: 'Typ',
            column_created_at: 'Erstellt am',
        },
        // Sprint 62.35.0 (Slice C) — structural parity; English until Phase 25 (R-57).
        pre_collection: {
            nav_label: 'Pre-Collection Assessments',
            page_title: 'Pre-Collection Assessments',
            page_description: 'An assessment of the protection measures for a data registry item, made before that data is first collected. The completion date may not be after the first collection date.',
            module_guide: 'Record, for each data registry item, the assessment of protection measures made before collection begins: the collection procedure and its version, the assessment, the measures applied, the date the assessment was completed and the date the data was first collected. Whether an assessment is required is set by your installed frameworks.',
            new_button: 'New assessment',
            create_title: 'New pre-collection assessment',
            edit_title: 'Edit pre-collection assessment',
            edit: 'Edit',
            field_registry_item: 'Data registry item',
            registry_item_placeholder: 'Select a registry item',
            registry_item_unresolved: 'Registry item not found',
            registry_load_error: 'The data registry could not be read, so no registry item can be offered.',
            registry_empty: 'The data registry has no items yet.',
            field_procedure_reference: 'Collection procedure reference',
            field_procedure_version: 'Procedure version',
            field_summary: 'Assessment',
            field_measures: 'Measures applied',
            field_measures_hint: 'One measure per line.',
            field_completed_on: 'Assessment completed on',
            field_first_collection_on: 'First collected on',
            date_hint: 'The assessment must be completed on or before the first collection date. Leave the first collection date empty if collection has not begun.',
            error_after_first_collection: 'The assessment must be made before the data is first collected: the completion date is after the first collection date.',
            field_status: 'Status',
            status_draft: 'Draft',
            status_approved: 'Approved',
            status_deprecated: 'Deprecated',
            column_registry_item: 'Registry item',
            column_procedure: 'Collection procedure',
            column_actions: 'Actions',
            not_recorded: 'Not recorded',
            empty: 'No pre-collection assessments have been recorded yet.',
            list_load_error: 'The pre-collection assessments could not be read.',
            reload: 'Reload',
            reload_sr: 'reload the pre-collection assessments',
            registry_reload_sr: 'reload the data registry',
            save: 'Save',
            saving: 'Saving…',
            cancel: 'Cancel',
            created_toast: 'Pre-collection assessment recorded.',
            updated_toast: 'Pre-collection assessment updated.',
            save_error: 'The assessment could not be saved.',
            total: 'Total',
            section_scope: 'What is assessed',
            section_assessment: 'Assessment and dates',
            approved_locked: 'This assessment is approved, so its content cannot be changed. It can only be deprecated.',
            error_conflict: 'This assessment was changed by someone else while you were editing, so your change was not saved. Close this form and open the assessment again to see the current version.',
            error_conflict_refreshed: 'This assessment was changed by someone else while you were editing, so your change was not saved. The form now shows the current version, and your unsaved entries were not kept. Make your change again and save.',
            registry_not_readable: 'Your role cannot read the data registry, so no registry item can be offered here.',
            create_needs_registry_read: 'Your role cannot read the data registry, so it cannot choose the registry item a new assessment needs. Existing assessments can still be edited.',
            registry_item_name_hidden: 'Name not visible to your role',
        },
    },
    classification: {
        access_denied_title: 'Zougang refuséiert',
        access_denied: 'Dir hutt net déi néideg Permissiounen fir Zougang zum Klassifikatiounsmodul ze kréien. Kontaktéiert Ären Administrator.',
        nav: {
            taxonomies: 'Taxonomien',
            labeling: 'Etikettéierung',
            lifecycle: 'Liewenszyklus',
            gaps: 'Lücke-Rapport',
            exports: 'Exporter',
        },
        exports: {
            title: 'Klassifikatiounsexporter',
            csv_section: 'CSV-Exporter',
            pdf_section: 'PDF-Rapporten',
            inventory: 'Inventar',
            classification_register: 'Klassifikatiounsregëster',
            raci: 'RACI-Matriiss',
            exceptions: 'Ausnahmen',
            evidence_pack: 'Noweispaket',
            delta_report: 'Delta-Rapport',
            executive_summary: 'Zesummefaassung',
            exporting: 'Gëtt exportéiert…',
            export_success: 'Export erfollegeräich.',
            export_error: 'Export gescheitert. Probéiert et nach eng Kéier.',
            csv_download: 'CSV-Download prett.',
            pdf_download: 'PDF-Generéierung gestart.',
            no_data: 'Keng Daten fir ze exportéieren.',
        },
        delta: {
            title: 'Ännerungsdetailer',
            added: 'Derbäigesat',
            removed: 'Ewechgeholl',
            changed: 'Geännert',
            added_count: 'Derbäigesat ({count})',
            removed_count: 'Ewechgeholl ({count})',
            changed_count: 'Geännert ({count})',
            object_id: 'Objet-ID',
            object_type: 'Objettyp',
            taxonomy: 'Taxonomie',
            level: 'Stufe',
            previous: 'Vireg',
            current: 'Aktuell',
            no_records: 'Keng Aschreiwunge an dësem Abschnëtt.',
            empty_section: 'Dësen Abschnëtt ass eidel.',
        },
        schedule: {
            title: 'Entdeckungszyklus plangen',
            enabled: 'Aktivéiert',
            interval_days: 'Intervall (Deeg)',
            interval_label: 'All N Deeg ausféieren',
            manual_only: 'Nëmme manuell',
            every_n_days: 'All {n} Deeg',
            save: 'Zäitplang späicheren',
            cancel: 'Ofbriechen',
            schedule_updated: 'Zäitplang aktualiséiert.',
        },
        taxonomies: {
            title: 'Klassifikatiounstaxonomien',
            new: 'Nei Taxonomie',
            edit: 'Taxonomie änneren',
            name: 'Numm',
            levels: 'Klassifikatiounsniveauen',
            seed_defaults: 'Initialiséieren Standardwäerter',
            scope: 'Ëmfang',
            scope_organization: 'Organisatioun',
            scope_module_specific: 'Modul Specific',
            deprecate: 'Ofschreiwwen',
            hide_levels: 'Verstoppen Niveauen',
            versions: 'Versiounen',
            hide_versions: 'Verstoppen Versiounen',
            add_level: 'Derbäisetzen Niveau',
            new_level: 'Nei Niveau',
            edit_level: 'Änneren Niveau',
            code: 'Code',
            label: 'Etikett',
            color: 'Faarf',
            ordinal: 'Ordinalzuel',
            handling_requirements: 'Handhabung Ufuerderungen',
            version_history: 'Versioun Verlaf',
            changes: 'Ännerungen',
            failed_fetch: 'Konnt net fetch taxonomies. Probéiert et nach eng Kéier.',
            failed_save: 'Konnt net save Taxonomie. Probéiert et nach eng Kéier.',
            failed_activate: 'Konnt net activate Taxonomie. Probéiert et nach eng Kéier.',
            failed_deprecate: 'Konnt net deprecate Taxonomie. Probéiert et nach eng Kéier.',
            failed_seed: 'Konnt net seed Standard taxonomies. Probéiert et nach eng Kéier.',
            failed_fetch_levels: 'Konnt net fetch Taxonomie levels. Probéiert et nach eng Kéier.',
            failed_save_level: 'Konnt net save Taxonomie Niveau. Probéiert et nach eng Kéier.',
            failed_delete_level: 'Konnt net delete Taxonomie Niveau. Probéiert et nach eng Kéier.',
            failed_fetch_versions: 'Konnt net fetch Taxonomie versions. Probéiert et nach eng Kéier.',
            loaded_count: '{count} Taxonomien gelueden',
            defaults_seeded: 'Standard-Taxonomien initialiséiert.',
            no_levels: 'Keng Klassifikatiounsniveauen definéiert — setzt Niveauen derbäi fir Är Klassifikatiounshierarchie opzebauen.',
            no_versions: 'Nach keng Versiounshistorik — Versioune ginn erstallt wann eng aktiv Taxonomie aktualiséiert gëtt.',
        },
        labeling: {
            title: 'Datenetikettéierung',
            new: 'Neit Etikett',
            edit: 'Etikett änneren',
            entity_type: 'Entitéitstyp',
            entity_id: 'Entitéits-ID',
            taxonomy: 'Taxonomie',
            classification_level: 'Klassifikatiounsniveau',
            applied_by: 'Applizéiert vun',
            applied_at: 'Applizéiert um',
            bulk_apply: 'Masse-Applikatioun',
            bulk_remove: 'Masse-Ewechhuelen',
            all_taxonomies: 'All Taxonomien',
            system: 'System',
            dataset: 'Datesat',
            level_code_placeholder: 'Niveau Code...',
            select_taxonomy: 'Auswielen Taxonomie...',
            select_level: 'Auswielen Niveau...',
            rationale: 'Begrënnung',
            review_date: 'Iwwerpréiwen Datum',
            review: 'Iwwerpréiwen',
            selected: 'ausgewielt',
            select_all_labels: 'All Etiketten auswielen',
            loaded_count: '{count} Etiketten gelueden',
            hide_stale_labels: 'Verstoppen Stale Etiketten',
            show_conflicts: 'Weisen Konflikter',
            hide_conflicts: 'Verstoppen Konflikter',
            label_conflicts: 'Etikett Konflikter',
            conflict_reason: 'Konflikt Grond',
            labels: 'Etiketten',
            failed_fetch: 'Konnt net fetch labels. Probéiert et nach eng Kéier.',
            failed_fetch_taxonomies: 'Konnt net fetch taxonomies. Probéiert et nach eng Kéier.',
            failed_fetch_levels: 'Konnt net fetch Taxonomie levels. Probéiert et nach eng Kéier.',
            failed_save: 'Konnt net save Etikett. Probéiert et nach eng Kéier.',
            failed_review: 'Konnt net Iwwerpréifung Etikett. Probéiert et nach eng Kéier.',
            failed_bulk: 'Konnt net bulk Etikett. Probéiert et nach eng Kéier.',
            failed_stale: 'Konnt net fetch stale labels. Probéiert et nach eng Kéier.',
            failed_conflicts: 'Konnt net fetch Etikett conflicts. Probéiert et nach eng Kéier.',
            review_permission_denied: 'Dir hutt keng Autorisatioun fir dëst Label ze iwwerpréiwen.',
            review_not_found: 'Label net fonnt — et gouf méiglecherweis geläscht.',
            approve_selected: 'Ausgewielten geneemegen',
            reject_selected: 'Ausgewielten oflehnen',
            approved: 'Geneemegt',
            rejected: 'Ofgeleent',
            labels_count: 'Labelen',
            failed_count: 'gescheitert',
            confirm_approve: 'Geneemegung bestätegen',
            confirm_reject: 'Oflehnung bestätegen',
            approve_n_labels: '{count} Labelen geneemegen?',
            reject_n_labels: '{count} Labelen oflehnen?',
            submit_for_review: 'Zur Iwwerpréiwung astellen',
            approve: 'Genehmigen',
            reject: 'Ofleenen',
            parent_system: 'Elteresystem',
            parent_system_placeholder: 'Elteresystem auswielen...',
            parent_system_help: 'Gett fir Konfliktdetektioun benotzt.',
            dataset_level: 'Datensatz-Niveau',
            parent_level: 'Eltereniveau',
            bulk_created: '{created} Beschriftung(en) erstallt',
            bulk_skipped: '{skipped} iwwersprong',
            bulk_all_skipped: 'All {skipped} iwwersprong',
            ids_hint: 'IDen duerch Kommaen getrennt aginn',
            delete_confirm: 'Beschriftung fir "{id}" laeschen?',
        },
        lifecycle: {
            title: 'Dateliewenszyklus-Iwwerwaachung',
            new: 'Neien Andrag',
            current_stage: 'Aktuell Phase',
            last_transition: 'Lescht Iwwergank',
            transition: 'Iwwergank',
            view_history: 'Geschicht weisen',
            stages: {
                creation: 'Erstellung',
                collection: 'Sammlung',
                processing: 'Veraarbechtung',
                storage: 'Späicherung',
                retention: 'Opbewarung',
                disposal: 'Entsorgung',
                archived: 'Archivéiert',
                active: 'Aktiv',
                pending_deletion: 'Ausstehend Deletion',
                deleted: 'Geläscht',
                on_hold: 'On Zréckstellen',
            },
            all_stages: 'All Stufen',
            object_id: 'Objet ID',
            object_type: 'Objet Typ',
            policy_reference: 'Richtlinn Referenz',
            retention_period_days: 'Opbewahrung Period (days)',
            retention_basis: 'Opbewahrung Grondlag',
            deletion_method: 'Läschmethod',
            deletion_secure_erase: 'Sécher Erase',
            deletion_anonymization: 'Anonymiséierung',
            deletion_physical_destruction: 'Physesch Zerstéierung',
            deletion_other: 'Aner',
            responsible_party: 'Verantwortlech Party',
            responsible_user_id: 'Verantwortlech Benotzer ID',
            retention_expiry: 'Opbewahrung Oflaf',
            hold_reason: 'Zréckstellen Grond',
            hold: 'Zréckstellen',
            release: 'Fräiginn',
            hide_events: 'Verstoppen Evenementer',
            events_timeline: 'Evenementer Zäitverlaf',
            add_event: 'Derbäisetzen Evenement',
            event_type: 'Evenement Typ',
            create_event: 'Erstellen Evenement',
            from_stage: 'Vun Stuf',
            to_stage: 'Op Stuf',
            actor: 'Akteur',
            reason_placeholder: 'Grond...',
            go: 'Go',
            show_expired_retentions: 'Weisen Ofgelaf Retentions',
            hide_expired_retentions: 'Verstoppen Ofgelaf Retentions',
            expired_retentions_alert: 'Ofgelaf Retentions Warnung',
            failed_fetch: 'Konnt net fetch lifecycle Datesätz. Probéiert et nach eng Kéier.',
            failed_save: 'Konnt net save lifecycle Datesat. Probéiert et nach eng Kéier.',
            failed_transition: 'Konnt net transition lifecycle stage. Probéiert et nach eng Kéier.',
            failed_hold: 'Konnt net place Zréckstellung. Probéiert et nach eng Kéier.',
            failed_release: 'Konnt net release Zréckstellung. Probéiert et nach eng Kéier.',
            failed_fetch_events: 'Konnt net fetch lifecycle Evenementer. Probéiert et nach eng Kéier.',
            failed_create_event: 'Konnt net create lifecycle Evenement. Probéiert et nach eng Kéier.',
            failed_expired: 'Konnt net fetch expired retentions. Probéiert et nach eng Kéier.',
            hold_reason_prompt: 'Aginn Zréckstellung reason:',
            loaded_count: '{count} Liewenszyklus-Datebank gelueden',
            stage_transition_for: 'Phasen-Iwwergang fir {id}',
            event_created: 'Erstallt',
            event_updated: 'Aktualiséiert',
            event_approved: 'Geneemegt',
            event_deprecated: 'Ofgeschaaft',
            event_proposed: 'Proposéiert',
            event_reviewed: 'Iwwerpréift',
            event_archived: 'Archivert',
            event_restored: 'Erëmhergestallt',
        },
        gap_report: {
            title: 'Klassifikatiounslücke-Rapport',
            unlabeled: 'Onetikettéiert Donnéeën',
            missing_lifecycle: 'Fehlende Liewenszyklus',
            stale_labels: 'Vereelst Etiketten',
            run_detection: 'Ausféieren Lück Erkennung',
            detecting: 'Erkennen...',
            detection_complete: 'Lück detection complete. {count} gap(s) detected.',
            detection_failed: 'Lück detection gescheitert.',
            by_severity: 'By Schwéiergrad',
            by_gap_type: 'By Lück Typ',
            all_gap_types: 'All Lück Typen',
            conflicting_labels: 'Conflicting Etiketten',
            expired_retention: 'Ofgelaf Opbewahrung',
            missing_handling: 'Feelend Handhabung',
            info: 'Info',
            gap_type: 'Lück Typ',
            object_id: 'Objet ID',
            object_type: 'Objet Typ',
            detected_at: 'Erkannt At',
            open: 'Op',
            acknowledged: 'Bestätegt',
            remediated: 'Behuewen',
            accepted: 'Akzeptéiert',
            acknowledge: 'Bestätegen',
            remediate: 'Behiewen',
            accept_risk: 'Akzeptéieren Risiko',
            accept_risk_prompt: 'Aginn Risiko acceptance Begrënnung:',
            failed_fetch: 'Konnt net fetch Lücken. Probéiert et nach eng Kéier.',
            failed_acknowledge: 'Konnt net acknowledge Lück. Probéiert et nach eng Kéier.',
            failed_remediate: 'Konnt net remediate Lück. Probéiert et nach eng Kéier.',
            failed_accept_risk: 'Konnt net accept Lück Risiko. Probéiert et nach eng Kéier.',
            failed_detect: 'Konnt net run Lück detection. Probéiert et nach eng Kéier.',
            loaded_count: '{count} Klassifizéierungslécken gelueden',
            related_object_id: 'Verbonnen Objet-ID',
            related_object_type: 'Verbonnen Objettyp',
            gap_types: {
                unlabeled_asset: 'Net beschrifte Objet',
                missing_lifecycle: 'Fehlend Liewenszyklus',
                expired_retention: 'Ofgelaf Aufbewahrung',
                extended_pending_deletion: 'Verlengert austehend Laeschung',
            },
        },
        label_workflow: {
            title: 'Etikett-Workflow',
            proposed: 'Virgeschloen',
            under_review: 'An der Iwwerpréiwung',
            approved: 'Geneemegt',
            deprecated: 'Ofgeschaaft',
            submit_review: 'Fir Iwwerpréiwung areechen',
            approve: 'Etikett geneemegen',
            deprecate: 'Ofschafen',
            bulk_approve: 'Mass-Geneemegung',
            bulk_approve_success: 'Etiketten erfollegräich geneemegt.',
            submit_success: 'Etikett fir Iwwerpréiwung agereecht.',
            approve_success: 'Etikett geneemegt.',
            deprecate_success: 'Etikett ofgeschaaft.',
        },
        review_tasks: {
            title: 'Iwwerpréiwungsaufgaben',
            create: 'Nei Iwwerpréiwungsaufgab',
            system: 'System',
            owner: 'Besëtzer',
            description: 'Beschreiwung',
            resolution: 'Léisung',
            resolve: 'Léisen',
            approve: 'Geneemegen',
            modify: 'Änneren',
            reject: 'Refuséieren',
            escalate: 'Eskaléieren',
            escalated: 'Eskaléiert Aufgaben',
            no_tasks: 'Keng Iwwerpréiwungsaufgaben.',
            resolve_success: 'Iwwerpréiwungsaufgab geléist.',
            status_pending: 'Aussteierend',
            status_resolved: 'Geléist',
            status_escalated: 'Eskaléiert',
            notes: 'Notizen',
            create_success: 'Iwwerpréiwungsaufgab erstallt.',
            no_tasks_detail: 'Nach keng Iwwerpréiwungsaufgaben — erstellt eng Aufgab fir Label-Iwwerpréiwungen u Systembesëtzer zouzeweisen.',
            label_id: 'Label-ID',
            system_id: 'System-ID',
            system_name: 'Systemnumm',
            owner_id: 'Besëtzer-ID',
            resolution_col: 'Leisung',
            notes_col: 'Notizen',
        },
        exceptions: {
            title: 'Ausnam-Waardeschlang',
            create: 'Nei Ausnam',
            severity: 'Schwéiergrad',
            system: 'System',
            owner: 'Besëtzer',
            resolve: 'Léisen',
            no_exceptions: 'Keng Ausnamen fonnt.',
            resolve_success: 'Ausnam geléist.',
            dashboard_open: 'Oppen Ausnamen',
            dashboard_resolved: 'Geléist Ausnamen',
            dashboard_total: 'Gesamt',
            dashboard_by_severity: 'No Schwéiergrad',
            status_open: 'Op',
            status_resolved: 'Geléist',
            status_accepted_risk: 'Akzeptéiert Risiko',
            create_success: 'Ausnam erstallt.',
            no_exceptions_detail: 'Nach keng Ausnamen — Ausnamen ginn aus schwéiere Lücken erstallt déi speziell Behandlung brauchen.',
            gap_id: 'Lück-ID',
            system_id: 'System-ID',
            system_name: 'Systemnumm',
            owner_id: 'Besëtzer-ID',
            resolution_notes: 'Léisungsnotizen',
            resolution_notes_placeholder: 'Beschreiwt wéi dës Ausnam geléist gouf...',
            dashboard_accepted_risk: 'Accepted Risk',
            resolution_outcome: 'Outcome',
            resolve_success_accepted_risk: 'Exception closed as accepted risk.',
        },
        enterprise_map: {
            title: 'Betribsdatekaart',
            inventory: 'Dateinventar',
            total_systems: 'Systemer insgesamt',
            total_labels: 'Etiketten insgesamt',
            approved_count: 'Geneemegt Etiketten',
            proposed_count: 'Virgeschloen Etiketten',
            no_data: 'Keng Klassifikatiounsdaten disponibel.',
            no_approved_labels: 'Nach keng etikettéiert Objeten — en Objet erschéngt um Datekaart soubal et eng Klassifikatiounsetikett huet, egal ob proposéiert, an der Iwwerpréiwung oder guttgeheescht.',
            loaded_count: 'Lued Enterprise Daten Kaart',
        },
        discovery: {
            title: 'Entdeckungszykle',
            create: 'Neie Zyklus',
            cycle_name: 'Zyklusnumm',
            run_delta: 'Delta-Erkennung ausféieren',
            delta_success: 'Delta-Erkennung ofgeschloss.',
            create_success: 'Entdeckungszyklus erstallt.',
            no_cycles: 'Keng Entdeckungszykle.',
            status_created: 'Geschaf',
            status_scanning: 'Scannen',
            status_review: 'Iwwerpréiwung',
            status_approved: 'Genehmegt',
            status_published: 'Verëffentlecht',
            status_draft: 'Entworf',
            running: 'Leeft',
            completed: 'Ofgeschloss',
            cancelled: 'Ofgebrach',
            delta_result_summary: '{added} derbäigesat, {removed} ewechgeholl, {changed} geännert Labelen fonnt.',
            delta_results: 'Delta-Resultater',
            view_results: 'Resultater kucken',
            scope_type: 'Ëmfang Typ',
            scope_type_all: 'All',
            scope_type_systems: 'Systemer',
            scope_type_datasets: 'Datesets',
            scope_ids: 'Ëmfang IDen',
            scope_ids_placeholder: 'Gitt IDen getrennt mat Kommaen',
            cycle_explanation: "En Entdeckungszyklus scannt Är Dateinfrastruktur fir Datenverméigen z'identifizéieren, ze klassifizéieren an ze verfolgen. Konfiguréiert den Ëmfang fir spezifesch Systemer oder Datesätz ze zielen.",
            empty_state_title: 'Keng Entdeckungszyklen nach',
            empty_state_description: 'Erstellt Ären éischten Entdeckungszyklus fir ze scannen an Är Dateverméigen ze klassifizéieren.',
            empty_state_cta: 'Erstellt Éischt Zyklus',
            validation_name_required: 'Zyklus Numm ass néideg',
            validation_scope_type_required: 'Ëmfang Typ ass erfuerderlech',
            validation_scope_ids_required: "Op d'mannst eng Ëmfang ID ass erfuerderlech",
        },
        regulatory: {
            sensitivity_importance_info: 'Donnéeën Gesetz (Gesetz 60/2024/QH15, req. 18e4bfd1) verlaangt Klassifikatioun souwuel Empfindlechkeet a Wichtegkeet als separat Dimensiounen. Erstellt eng Taxonomie fir all Dimensioun fir dës Ufuerderung ze erfëllen.',
            handling_rules_info: 'All Klassifikatioun Niveau soll Ëmgank Regelen pro Req definéieren. b89981ac ("Handhabungsregele definéieren no Klassifikatiounsniveau").',
            template_internal_ids_info: 'All Klassifikatioun Template benotzen intern IDen (zB vn_data_law_data_classification_record). Keng Schabloun gëtt als offiziell Ministèreform presentéiert.',
            ai_risk_tiers_info: "D'AI-Gesetz (Gesetz 134/2025/QH15, req. 3a87ad63) definéiert véier AI-System-Risiko-Niveauen: minimal, limitéiert, héich an inakzeptabel. Erstellt eng AI-Risiko-Taxonomie mat dësen Niveauen.",
            ai_risk_tier_minimal: 'Minimal',
            ai_risk_tier_limited: 'Begrenzt',
            ai_risk_tier_high: 'Heich',
            ai_risk_tier_unacceptable: 'Onakzeptabel',
            ai_register_info: 'AI Klassifikatioun records soll zu engem Link AI System Register Entrée (req. 0f4475d3) erfaassen: System Numm, Besëtzer, Zweck, Détachement Kontext, Risiko Niveau, Verkeefer Engagement.',
            ai_template_internal_info: 'AI-Klassifikatiounstemplaten benotzen intern IDen (zB AI-RISK-01). Keng offiziell AI-Plazhalter-IDen (AI-HC-01 bis AI-HC-08) gi benotzt.',
            ai_audit_trail_info: 'AI Klassifikatioun Ännerungen produzéiere engem versioned Audit Trail pro UC-VN-06: Schauspiller, Zäitstempel, virdrun Wäert, neie Wäert, an rationale.',
            lifecycle_data_d1_info: 'Liewenszyklus Transitioune Kaart ze daten Gesetz Prozedur DATA-D1 Schrëtt: Katalog, klasséieren, definéieren Ëmgank Regelen, Beweiser erfaassen, Zäitplang Kritik.',
            lifecycle_deletion_link_info: "E klasséierten Dateverméigen, deen d'Enn vum Liewen erreecht, sollt e Läschplangrekord iwwer de Modul vun der Läschorchester ausléisen.",
            lifecycle_evidence_info: 'All Liewenszyklus Staat Ännerung (Entworf, aktiv, Ënner-Iwwerpréiwung, archivéiert, geläscht) erfaasst Schauspiller, Zäitstempel, an Justifikatioun als Audit Trail Entrée.',
            escalation_timers_info: 'UC-VN-10 Eskalatiounsfristen: 5 Aarbechtsdeeg fir Besëtzer Bewäertung, 48 Stonnen fir Ausnam Eskalatioun, 72 Stonnen fir héich-Risiko / sensibel Elementer.',
            escalation_owner_review: '5 Aarbechtsdeeg',
            escalation_exception: '48 Stonnen',
            escalation_high_risk: '72 Stonnen',
            gap_risk_areas_info: 'Dategesetz Req. f6c3060a ("Daterisikobewäertung") erfuerdert Spaltanalyse iwwer 4 Risikoberäicher: Privatsphär, Cybersécherheet, Zougangskontroll an operationell Risiko.',
            gap_risk_area_privacy: 'Dateschutz',
            gap_risk_area_cybersecurity: 'Cybersecherheet',
            gap_risk_area_access: 'Zougangssteierung',
            gap_risk_area_operational: 'Operationell',
            gap_framework_filter_info: 'Filter Lücken no reglementaresche Kader (VN_DATA_LAW oder VN_AI_LAW_2026) fir relevant Lücken fir all reglementaresche Ëmfang ze gesinn.',
            framework_vn_data_law: 'VN Dategesetz',
            framework_vn_ai_law: 'VN KI-Gesetz 2026',
            enterprise_map_approved_info: 'Dës Datekaart weist nëmmen guttgeheescht Klassifikatiounsrecords pro UC-VN-10 Workflow Schrëtt 10 ("Genehmegt Etiketten ginn déi aktuell Enterprise Wourecht"). Entworf a proposéiert records sinn ausgeschloss.',
            exports_bundle_info: 'Exportbündel enthalen: Klassifikatiounsregister, Ëmgank Regelen, Besëtz / RACI, a Beweispak - passend UC-VN-10 Exportfuerderungen an DATA-EVD-01 Beweisblattformat.',
        },
    },
    monitoring: {
        no_permission_title: 'Onzureichend Berechtigungen',
        no_permission_message: 'Dir hutt keng Berechtigung fir desen Iwwerwaachungsmodul ze benotzen.',
        errors: {
            fetch: 'Konnt net load..',
            save: 'Konnt net save..',
            fetch_programs_failed: 'Konnt net fetch programs..',
            fetch_kpi_failed: 'Konnt net fetch KPI Donnéeën..',
            create_snapshot_failed: 'Konnt net create Snapshot..',
            fetch_periods_failed: 'Konnt net fetch periods..',
            create_period_failed: 'Konnt net create Period..',
            auto_generate_failed: 'Konnt net auto-generate periods..',
            start_period_failed: 'Konnt net start Period..',
            close_period_failed: 'Konnt net close Period..',
            save_program_failed: 'Konnt net save Programm..',
            activate_program_failed: 'Konnt net activate Programm..',
            pause_program_failed: 'Konnt net pause Programm..',
            complete_program_failed: 'Konnt net complete Programm..',
            archive_program_failed: 'Konnt net archive Programm..',
            fetch_ric_templates_failed: 'Konnt net fetch RIC Schablounen..',
            fetch_ric_cases_failed: 'Konnt net fetch RIC cases..',
            create_ric_case_failed: 'Konnt net create RIC case..',
            transition_ric_failed: 'Konnt net transition RIC case..',
            add_follow_up_failed: 'Konnt net add follow-up..',
            respond_follow_up_failed: 'Konnt net respond op follow-up..',
            link_evidence_failed: 'Konnt net link Bewäis..',
            fetch_task_templates_failed: 'Konnt net fetch Aufgab Schablounen..',
            save_task_template_failed: 'Konnt net save Aufgab Schabloun..',
            delete_task_template_failed: 'Konnt net delete Aufgab Schabloun..',
            delete_framework_link_failed: 'Konnt de Framework-Link net läschen.',
            seed_defaults_failed: 'Konnt net seed defaults..',
            fetch_tasks_failed: 'Konnt net fetch Aufgaben..',
            create_task_failed: 'Konnt net create Aufgab..',
            instantiate_tasks_failed: 'Konnt net instantiate Aufgaben..',
            start_task_failed: 'Konnt net start Aufgab..',
            complete_task_failed: 'Konnt net complete Aufgab..',
            defer_task_failed: 'Konnt net defer Aufgab..',
            block_task_failed: 'Konnt net block Aufgab..',
            override_complete_failed: 'Konnt net override complete..',
            assign_task_failed: 'Konnt net assign Aufgab..',
            update_checklist_failed: 'Konnt net update checklist..',
        },
        nav: {
            programs: 'Programmer',
            periods: 'Perioden',
            task_templates: 'Aufgab-Schablounen',
            tasks: 'Aufgaben',
            kpi: 'KPI-Iwwersiicht',
            ric: 'Regulateure-Ufroen',
            framework_link: 'Kaderiwwerwaachung',
            authority_readiness: 'Autoritéitsberedschaft',
            change_review: 'Ännerungsprüfung',
            raci: 'RACI Matrix',
        },
        programs: {
            page_title: 'Iwwerwaachungsprogrammer',
            title: 'Iwwerwaachungsprogrammer',
            new: 'Neit Programm',
            edit: 'Programm änneren',
            name: 'Numm',
            scope: 'Ëmfang',
            frequency: 'Frequenz',
            owner: 'Besëtzer',
            activate: 'Aktivéieren',
            pause: 'Pauséieren',
            resume: 'Weiderfueren',
            complete: 'Ofschléissen',
            archive: 'Archivéieren',
            custom_frequency_days: 'Personaliséiert Heefegkeet (days)',
            select_user: 'Benotzer wielen…',
            assign_training: 'Formatioun zouweisen',
            linked_forms: 'Verlinkte Formulairen',
            linked_forms_none: 'Nach keng verlinkte Formulairen',
            loaded_count: '{0} Programmer gelueden',
            scope_labels: {
                full: 'Voll',
                partial: 'Deelweis',
                targeted: 'Gezillt',
                consent: 'Zoustëmmung',
                dpia: 'DPIA',
                vendors: 'Liwweranten',
                classification: 'Klassifikatioun',
                rights_requests: 'Rechtufroen',
                data_mapping: 'Datekartéierung',
                transfers: 'Transferten',
                security_monitoring: 'Sécherheet Iwwerwachung',
                authority_response: 'Autoritéit Äntwert',
                evidence_preservation: 'Beweis Erhaalung',
                localization_review: 'Lokalisatioun Kritik',
                ai_system_registration: 'AI System Umeldung',
                ai_pre_deployment: 'AI Pre-Deployment Assessment',
                ai_transparency: 'AI Transparenzcheck',
                ai_incident_review: 'AI Tëschefall Iwwerpréiwung',
            },
            framework_cadence_info: 'Cybersecurity Law programs typically use continuous/monthly/quarterly cadences. AI Law programs typically use annual review cadences.',
        },
        periods: {
            page_title: 'Iwwerwachung Perioden',
            title: 'Revisiounsperioden',
            new: 'Nei Period',
            label: 'Perioden-Etikett',
            start_date: 'Startdatum',
            end_date: 'Schluss-Datum',
            reviewers: 'Revisoren',
            auto_generate: 'Perioden automatesch generéieren',
            start: 'Ufänken',
            close: 'Zoumaachen',
            change_digest: 'Ännerungs-Resumé',
            no_digest: 'Keen Digest verfügbar.',
            reviewers_count: 'Préifer Unzuel',
            select_program: 'Auswielen Programm',
            select_program_prompt: 'Auswielen eng Programm op view periods.',
            hide_digest: 'Verstoppen Digest',
            loaded_count: 'Lueden {0} Perioden',
            periods_auto_generated: 'Perioden automatesch generéiert',
            label_placeholder: 'z.B. 2026-Q1',
        },
        task_templates: {
            page_title: 'Aufgabeschablounen',
            title: 'Aufgab-Schablounen',
            new: 'Nei Schabloun',
            edit: 'Schabloun änneren',
            category: 'Kategorie',
            priority: 'Prioritéit',
            checklist_items: 'Checklëscht-Elementer',
            effort: 'Geschaten Opwand (Stonnen)',
            pre_seeded: 'Virausgefillt',
            seed_defaults: 'Standard-Donnéeë laden',
            add_checklist: 'Checklëscht-Element derbäisetzen',
            guidance: 'Leedungsnotizen',
            all_categories: 'All Kategorien',
            loaded_count: 'Luede {0} Aufgab Schablounen',
            defaults_seeded: 'Standard Aufgab Template gesaat',
            checklist_item_label: 'Checkliste Artikel {0}',
            remove_checklist_item: 'Ewechzehuelen Checklëscht Element {0}',
            category_info: 'Categories cover PDPL compliance, Cybersecurity Law tasks, and AI Law tasks.',
        },
        tasks: {
            page_title: 'Iwwerpréifungsopgaven',
            title: 'Revisiounsaufgaben',
            new: 'Nei Aufgab',
            assigned_to: 'Zougewisen un',
            due_date: 'Fällegkeetsdatum',
            checklist: 'Checklëscht',
            start: 'Ufänken',
            complete: 'Ofschléissen',
            defer: 'Verschieben',
            block: 'Blockéieren',
            override_complete: 'Iwwerschreiden a Fäerdegstellen',
            link_evidence: 'Beweismaterial verbannen',
            direct_upload_label: 'Beweisdatei eroplueden',
            instantiate: 'Aus Schablounen erstellen',
            defer_reason: "Grond fir d'Verschiebung",
            override_justification: "Begrënnung fir d'Iwwerschreidung",
            filter_assignee: 'Filteren no assignee',
            select_period: 'Auswielen Period',
            select_program: 'Auswielen Programm',
            select_program_period_prompt: 'Auswielen eng Programm an Period op view Aufgaben.',
            assign_placeholder: 'Zouweisen op...',
            loaded_count: '{0} Aufgabe gelueden',
            tasks_instantiated: 'Aufgabe vun Schabloune erstellt',
            overdue: 'Iwwerfälleg',
            checklist_for: 'Kontrollëlëscht fir',
            manage_checklist: 'Kontrollëlëscht verwalten',
            add_checklist_item: 'Element derbäisetzen',
            checklist_item_label: 'Element',
            checklist_item_description: 'Beschreiwung',
            remove_checklist_item: 'Element ewechhuelen',
            save_checklist: 'Kontrollëlëscht späicheren',
            no_checklist_items: 'Nach keng Kontrollëlëschtelementer.',
            confirm_delete_attachment: 'Sidd Dir sécher, dass Dir dës Beweismaterialdatei wëllt läschen? Dëst kann net réckgängeg gemaach ginn.',
        },
        kpi: {
            page_title: 'KPI-Iwwersiicht',
            title: 'KPI-Iwwersiicht',
            kpi_name: 'KPI-Numm',
            current_value: 'Aktuellen Wäert',
            target: 'Zil',
            trends: 'Tendensen',
            capture_snapshot: 'Erfaassen Snapshot',
            select_program: 'Auswielen Programm',
            select_program_prompt: 'Auswielen eng Programm op view KPIs.',
            snapshot: 'Snapshot',
            kpi_labels: {
                on_track: 'Am Plang',
                at_risk: 'At Risiko',
                behind: 'Hannen drun',
                rights_request_sla_rate: 'Rechter Ufro SLA Taux',
                vendor_review_currency: 'Verkeefer Bewäertung Währung',
                dpia_completion_rate: 'DPIA Fäerdegstellung Taux',
                classification_coverage: 'Klassifikatioun Ofdeckung',
                consent_policy_currency: 'Zoustëmmung Politik Währung',
                security_event_detection_rate: 'Sécherheet Event Detektioun Taux',
                evidence_preservation_compliance_rate: 'Beweis Erhaalung Compliance Taux',
                authority_response_timeliness: 'Autoritéit Äntwert Timeliness',
                localization_readiness_score: 'Lokalisatioun Bereetschaft Score',
                ai_register_completeness: 'AI Systemregister Vollständegkeet',
                pre_deployment_gate_pass_rate: 'Pre-Deployment Gate Pass Rate',
                transparency_disclosure_coverage: 'Transparenz Disclosure Coverage',
                ai_incident_readiness_score: 'AI Tëschefall Bereetschaft Score',
            },
            pdpl_kpi_info: 'The predefined KPI codes are PDPL-specific metrics. For Cybersecurity Law or AI Law monitoring, use framework-specific KPI codes.',
            cybersec_kpi_info: 'Cybersecurity Law KPIs: Sécherheet Event Detektiounsquote, Beweiskonservatiounskonformitéitsquote, Autoritéitsreaktiounszäitlechkeet, Lokaliséierungsbereetschaft Score.',
            ai_law_kpi_info: 'AI Gesetz KPIs: AI System Register Vollständegkeet, Pre-Deployment Gate Pass Rate, Transparenz Disclosure Coverage, AI Tëschefall Bereetschaft Score.',
            no_data: 'No Donnéeën available.',
            snapshot_date: 'Snapshot Datum',
            loaded_count: '{0} KPI-Momentopnamen gelueden',
            snapshot_captured: 'KPI-Momentopnam erfaasst',
            showing_chart: 'Diagrammvue',
            showing_table: 'Datentabellvue',
            aria_current_values: 'KPI aktuell Wäerter',
            kpi_label: 'KPI',
            actual: 'Aktuell',
            trend: 'Trend',
            notes: 'Notizen',
            trend_up: 'Steigerend',
            trend_down: 'Fallend',
            trend_stable: 'Stabil',
        },
        ric: {
            page_title: 'RIC-Fäll',
            title: 'Regulateure-Ufrofäll',
            new: 'Neie RIC-Fall',
            reference: 'Referenz',
            regulator: 'Regulateur',
            inquiry_type: 'Ufro-Aart',
            deadline: 'Äntwertfrist',
            lead: 'Verantwortlechen',
            follow_ups: 'Nofaassungen',
            add_follow_up: 'Nofaassung derbäisetzen',
            respond: 'Äntwerten',
            question: 'Fro',
            response: 'Äntwert',
            asked_at: 'Gefrot den',
            no_follow_ups: 'Nach keng Nofollegen.',
            received_date: 'Kritt Datum',
            responded_at: 'Geäntwert At',
            hide_follow_ups: 'Verstoppen Follow-ups',
            inquiry_types: {
                general: 'Allgemeng',
                investigation: 'Ënnersichung',
                audit: 'Iwwerpréiwung',
                complaint: 'Reklamatioun',
                general_audit: 'Allgemeng Prüfung',
                rights_requests_review: 'Prüfung vun Rechtufroen',
                cross_border_transfer_review: 'Prüfung vun grenziwwerschreidende Transferten',
                vendor_governance_review: 'Prüfung vun Liwweranteverwalting',
                consent_management_review: 'Prüfung vun Zoustëmmungsmanagement',
                data_mapping_ropa_review: 'Prüfung vun Datekartéierung / ROPA',
                incident_readiness_review: 'Prüfung vun Tëschefallbereetschaft',
                security_access_control_review: 'Prüfung vun Sécherheet an Zougangscontroll',
                targeted_processing_review: 'Prüfung vun gezilter Veraarbechtung',
                bank_readiness_pack: 'Bankbereetschaftspaket',
            },
            link_evidence: 'Verbannen Bewäis',
            loaded_count: 'Lueden {0} RIC Fäll',
            to_follow_up: 'zu Suivi Fro',
            overdue: 'Iwwerhaapt',
            overdue_indicator: 'Äntwertfrist iwwerschratt',
            template_info: 'RIC templates are internal platform templates. They are not official government-mandated forms.',
            status_labels: {
                received: 'Kritt',
                in_triage: 'An Triage',
                evidence_gathering: 'Beweissammlung',
                response_drafting: 'Äntwert-Entworf',
                internal_review: 'Intern Iwwerpréiwung',
                submitted: 'Agereecht',
                follow_up: 'Nofollegung',
                closed: 'Zougemaach',
            },
        },
        framework_link: {
            title: 'Kaderverlinkten Iwwerwaachung',
            link_program: 'Programm mat Kader verbannen',
            framework_code: 'Kadercode',
            select_framework: 'Kader auswielen...',
            select_program: 'Programm auswielen...',
            readiness_score: 'Bereetschaftsscore',
            no_links: 'Keng kaderverlinkten Programmer fonnt',
            seed_defaults: 'Standardvirlagen erstellen',
            program_id: 'Programm ID',
            program: 'Programm',
        },
        authority_readiness: {
            title: 'Autoritéitsufro-Bereetschaft',
            create_entry: 'Neie Bereetschaftsandrag',
            select_framework: 'Kader auswielen...',
            framework_code: 'Kader',
            readiness_status: 'Bereetschaftsstatus',
            readiness_package: 'Bereetschaftspaket',
            run_drill: 'Bereetschaftsübung duerchféieren',
            drill_success: 'Bereetschaftsübung agedroen',
            last_drill: 'Lescht Übung',
            never_drilled: 'Ni',
            no_readiness: 'Keng Autoritéitsbereetschaftsdaten fonnt',
            entry_created: 'Bereetschaftsandrag erstallt',
            ready: 'Bereet',
            partial: 'Deelweis',
            not_ready: 'Net bereet',
            items: 'items',
        },
        change_review: {
            title: 'Regulatoresch Ännerungsprüfung',
            new_review: 'Nei Ännerungsprüfung',
            change_id: 'Ännerungs-ID',
            select_change: 'Eng Ännerung auswielen...',
            select_framework: 'Kader auswielen...',
            impact_assessment: 'Impaktbewäertung',
            decision: 'Entscheedung',
            no_action: 'Keng Aktioun',
            internal_update: 'Intern Aktualiséierung',
            authority_filing: 'Autoritéitsmeldung',
            no_reviews: 'Keng Ännerungsprüfunge fonnt',
            auto_cycle: 'Automatesche Prüfungszyklus',
            cadence: 'Prüfungsrhythmus',
            framework: 'Kader',
        },
        export_csv: 'Export CSV',
        exporting: 'Exportéiert…',
        export_success: 'CSV erfollegräich exportéiert.',
        export_failed: 'CSV Export ass gescheitert.',
        export_attestation_pack: 'Export Attestation Pack',
        staleness: {
            badge_label: '{0} al Saachen',
            days_since: '{0} Deeg zënter dem leschten Update',
            detail_title: 'Stale Artikelen',
            accept_justification: 'Akzeptéieren mat Justifikatioun',
            justification_label: 'Justifikatioun',
            no_stale: 'Keng al Saachen fonnt.',
            threshold_label: 'Staleness Threshold (Deeg)',
            threshold_min: 'Minimum 30 Deeg',
            threshold_max: 'Maximum 730 Deeg',
            threshold_saved: 'Staleness Schwell aktualiséiert.',
        },
        csv_columns: {
            name: 'Numm',
            status: 'Status',
            owner: 'Besëtzer',
            created_at: 'Erstellt am',
            updated_at: 'Aktualiséiert Am',
            scope: 'Ëmfang',
            frameworks: 'Kaderen',
            period_label: 'Period Label',
            start_date: 'Start Datum',
            end_date: 'Enn Datum',
            task_count: 'Aufgab Zuel',
            completion_rate: 'Fäerdegstellung Taux',
            title: 'Titel',
            assigned_to: 'Zugewielt',
            due_date: 'Termin Datum',
            priority: 'Prioritéit',
            completed_at: 'Ofgeschloss am',
            kpi_code: 'KPI Code',
            kpi_label: 'KPI Label',
            target: 'Ziel',
            actual: 'Tatsächlech',
            trend: 'Trend',
            reference: 'Referenz',
            regulator: 'Regulateur',
            inquiry_type: 'Ufro Typ',
            lead: 'Féierung',
            received_date: 'Datum kritt',
            deadline: 'Frist',
            framework_code: 'Kader Code',
            readiness_status: 'Bereetschaft Status',
            package_count: 'Package Zuel',
            change_id: 'Änneren ID',
            impact_assessment: 'Impakt Bewäertung',
            decision: 'Entscheedung',
            program: 'Programm',
            readiness_score: 'Bereetschaft Score',
            category: 'Kategorie',
            checklist_count: 'Checkliste Zuel',
            effort_hours: 'Effort (Stonnen)',
            version: 'Versioun',
            department: 'Departement',
            function_area: 'Funktioun Beräich',
            role: 'Roll',
            assignee: 'Assignee',
            frequency: 'Frequenz',
        },
        raci: {
            page_title: 'Gouvernance RACI Matrix',
            title: 'Gouvernance RACI Matrix',
            department: 'Departement',
            function_area: 'Funktioun Beräich',
            responsible: 'Responsabel',
            accountable: 'Verantwortlech',
            consulted: 'Consultéiert',
            informed: 'Informéiert',
            assign_role: 'Gitt RACI Roll',
            select_program: 'Wielt Programm',
            select_program_prompt: 'Wielt e Programm fir eng Vue RACI Matrixentgasung.',
            no_assignments: 'Keng RACI Uerderen fonnt.',
            role_updated: 'RACI Aufgab aktualiséiert.',
            role_labels: {
                R: 'Responsabel',
                A: 'Verantwortlech',
                C: 'Consultéiert',
                I: 'Informéiert',
            },
            loaded_count: 'Lueden {0} RACI Uerderen',
            single_accountability_warning: 'Nëmmen eng verantwortlech (A) Roll ass pro Funktiounsberäich erlaabt. Eng nei Verantwortungsroll ze ginn ersetzt déi existent.',
            cybersec_function_areas: 'Cybersecurity Law Funktioun Beräicher: Tëschefall Äntwert, Beweis Conservatioun, Autoritéit Zesummenaarbecht, Lokalisatioun review.',
            ai_law_function_areas: 'AI Gesetz Funktioun Beräicher: System Aschreiwung, Pre-Deployment Bewäertung, Transparenz, Tëschefall Ëmgank.',
            suggested_function_areas: 'Recommandéiert Funktioun Beräicher',
        },
        prior_cycle: {
            link_label: 'Link op de viregte Zyklus',
            select_prior: 'Wielt virdrun Zyklus ...',
            linked: 'Joer-iwwer-Joer',
            no_prior: 'Keng virdrun Zyklen verfügbar.',
            linked_success: 'Virdrun Zyklus verbonnen.',
        },
        digest_table: {
            module: 'Modul',
            entity_type: 'Entitéit Typ',
            change_type: 'Änneren Typ',
            description: 'Beschreiwung',
            changed_at: 'Geännert Am',
        },
        view_related_ric: 'View Zesummenhang RIC Fäll',
        linked_program: 'Verknëppelt Programm',
        nav_raci: 'RACI Matrix',
    },
    incidents: {
        authority_response_draft: {
            draft_label: 'Entworf Äntwert',
            drafting: 'Entworf…',
            results_heading: 'Entworf Äntwerten ({count})',
            tokens_label: '≈ {count} Tokens',
            counsel_label: 'Conseiller review néideg:',
            counsel_default: "Op d'mannst eng Äntwert erfuerdert Berodungsiwwerpréiwung ier Dir schéckt.",
            requires_counsel_label: 'Verlaangt Affekot Iwwerpréiwung.',
            accept_label: 'Entworf akzeptéieren',
            reject_label: 'Entworf refuséieren',
            error_generic: "Konnt d'Äntwert net ausschaffen. Probéiert nach eng Kéier oder verfeinert den Ufrotext.",
            question_action_accepted: 'Accept answer',
            question_action_rejected: 'Reject answer',
            question_action_needs_counsel: 'Needs counsel',
            question_action_aria: '{action} ({question})',
            question_decision_label: 'Decision on this answer: {decision}',
            question_decision_accepted: 'accepted',
            question_decision_rejected: 'rejected',
            question_decision_needs_counsel: 'sent for counsel review',
            download_docx_label: 'Download response template (DOCX)',
            draft_resolved_accepted: 'Every answer is decided. The draft is accepted.',
            draft_resolved_rejected: 'Every answer is decided. The draft is rejected.',
        },
        breach_timeline: {
            compute_label: 'Berechnen Verstouss Notifikatioun Timeline',
            computing: 'Rechent…',
            results_heading: 'Verstouss Notifikatiounsfristen ({count})',
            tokens_label: '≈ {count} Tokens',
            handoff_label: 'Forseti proposéiert:',
            empty_label: 'Kee installéierte Kader deklaréiert eng Meldeflicht fir dësen Tëschefall.',
            mandatory_label: 'Obligatoresch Felder:',
            draft_label: 'Notifikatioun Entworf',
            remaining_label: '{count}h bleiwen',
            overdue_label: 'OVERDUE vun {count}h',
            acknowledge_label: 'Zäitplang bestätegen',
            acknowledging: 'Unerkennung…',
            error_generic: "Konnt d'Verstouss Timeline net berechnen. Probéiert nach eng Kéier.",
            tasks_note: 'Urgent tasks were raised for the duties that match this incident’s jurisdictions. Computing again reuses them instead of creating more.',
        },
        breach_determination: {
            yes: 'Bestätegt Verstouss',
            no: 'Net eng Verstouss',
            uncertain: 'Ausstehend',
        },
        errors: {
            fetch: 'Konnt net load incidents..',
            save: 'Konnt net save Tëschefall..',
            fetch_failed: 'Konnt net fetch incidents..',
            save_failed: 'Konnt net save Tëschefall..',
            transition_failed: 'Konnt net transition Tëschefall..',
            close_failed: 'Konnt net close Tëschefall..',
            fetch_timeline_failed: 'Konnt net fetch Zäitverlaf..',
            fetch_decisions_failed: 'Konnt net fetch decisions..',
            triage_failed: 'Konnt net update triage..',
            impact_update_failed: 'Konnt net update impact assessment..',
            breach_update_failed: 'Konnt net update breach assessment..',
            create_timeline_failed: 'Konnt net create Zäitverlaf entry..',
            create_decision_failed: 'Konnt net create Entscheedung..',
            notification_update_failed: 'Konnt net update Notifikatioun..',
            // Sprint 62.35.0 gate E12.5 pass 2 (P2-1) — English until Phase 25 (R-57)
            breach_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the breach assessment was not saved. The latest version of the incident is being loaded and your entries are kept: check them and save again.",
            notification_status_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the notification status was not changed. The latest version of the incident is being loaded: check the status and change it again if it still needs changing.",
            fetch_tasks_failed: 'Konnt net fetch Aufgaben..',
            create_task_failed: 'Konnt net create Aufgab..',
            update_task_status_failed: 'Konnt den Aufgabestatus net aktualiséieren.',
            update_checklist_failed: 'Konnt net update checklist..',
            assign_task_failed: 'Konnt net assign Aufgab..',
            escalate_task_failed: 'Konnt net escalate Aufgab..',
            fetch_templates_failed: 'Konnt net fetch Schablounen..',
            permission_denied: 'Dir hutt keng Berechtigungen fir desen Beraich ze gesinn. Kontaktei Ieren Administrateur.',
        },
        harm_likelihood: {
            unlikely: 'Onwahrscheinlech',
            possible: 'Méiglech',
            likely: 'Wahrscheinlech',
            very_likely: 'Ganz Wahrscheinlech',
        },
        // Sprint 63.37.0 (legal second read S12) — the impact assessment's two vocabularies as words.
        data_category_options: {
            personal_data: 'Personal data',
            sensitive_personal_data: 'Sensitive personal data',
            financial_data: 'Financial data',
            health_data: 'Health data',
            childrens_data: "Children's personal data",
            other: 'Other personal data',
        },
        consequence_options: {
            identity_theft: 'Identity theft',
            financial_loss: 'Financial loss',
            reputational_damage: 'Reputational damage',
            discrimination: 'Discrimination',
            emotional_distress: 'Emotional distress',
            physical_harm: 'Physical harm',
            loss_of_confidentiality: 'Loss of confidentiality',
            loss_of_access: 'Loss of access to the data',
        },
        reported_via: {
            email: 'E-Mail',
            phone: 'Telefon',
            internal_system: 'Intern System',
            third_party: 'Drëtt Party',
            automated_detection: 'Automatiséiert Erkennung',
        },
        task_transition_labels: {
            start: 'Starten',
            complete: 'Ofschléissen',
            block: 'Blockéieren',
            defer: 'Verschieben',
            cancel: 'Ofbriechen',
            unblock: 'Entblockéieren',
        },
        transition_labels: {
            start: 'Starten',
            complete: 'Ofschléissen',
            block: 'Blockéieren',
            defer: 'Verschieben',
        },
        nav: {
            all: 'All Tëschefäll',
            tasks: 'Äntwert-Aufgaben',
            reports: 'Rapporten',
            breach_notifications: 'Verletzungsmeldungen',
            supplements: 'Ergänzungen',
            remediation: 'Behiewung',
            authority_requests: 'Behördeuffroen',
            disclosure_register: 'Offenlegungsregëster',
        },
        list: {
            page_title: 'Tëschefäll',
            title: 'Tëschefäll',
            new: 'Neien Tëschefall',
            edit: 'Tëschefall änneren',
            incident_title: 'Titel',
            severity: 'Schwéiergrad',
            reported_at: 'Gemellt um',
            reported_by: 'Gemellt vun',
            assigned_to: 'Zougewisen un',
            view_detail: 'Detail weisen',
            reference: 'Referenz',
            lead: 'Leedung',
            reported_via: 'Gemellt Via',
            discovery_date: 'Entdeckung Datum',
            // fix-sprint #447 W10 — discovery is a date and time
            discovery_hint: 'The date and time the incident was discovered. Hour-based notification clocks run from this moment.',
            discovery_no_time: '(no time of day recorded)',
            discovery_day_only_hint: 'Discovery was recorded as the day {day}, with no time of day. Enter the date and time to record it, or leave it empty to keep the day.',
            hide_details: 'Verstoppen Detailer',
            close_incident: 'Zoumaachen Tëschefall',
            close_justification_prompt: 'Please provide eng Begrënnung fir closing dëst Tëschefall.',
            justification: 'Begrënnung',
            lead_id: 'Leedung ID',
            lead_not_assigned: 'Not assigned',
            date_not_recorded: 'Not recorded',
            contact: 'Kontakt',
            category: 'Kategorie',
            not_assessed: 'Net assessed',
        },
        detail: {
            page_title: 'Tëschefall Detail',
            title: 'Tëschefall-Detail',
            page_description: 'One incident in full: its overview, triage, impact and breach assessments, timeline, decisions, evidence and audit trail.',
            forseti_heading: 'Forseti for this incident',
            tabs: {
                overview: 'Iwwerbléck',
                triage: 'Triage',
                impact: 'Auswierkungs-Evaluatioun',
                breach: 'Dateschutzvirfall-Evaluatioun',
                timeline: 'Zäitstrahl',
                decisions: 'Entscheedungen',
                evidence: 'Beweiser',
                audit_trail: 'Audit Trail',
            },
            evidence_upload_label: 'Beweisdatei bäifügen',
            evidence_uploaded: 'Beweis eropgelueden',
            evidence_deleted: 'Beweis geläscht',
            evidence_delete_error: 'Beweis konnt net geläscht ginn',
            evidence_uploaded_at: 'Eropgelueden',
            evidence_empty: 'Keng Beweisdateien un dësem Tëschefall ugehaang.',
            evidence_size: 'Gréisst',
            triage_notes: 'Triage-Notizen',
            initial_classification: 'Éischt Klassifikatioun',
            individuals_affected: 'Zuel vun betraffene Persounen',
            data_categories: 'Betraffe Datekategorien',
            systems_compromised: 'Kompromittéiert Systemer',
            geographic_scope: 'Geographesch Ëmfang',
            breach_determination: 'Dateschutzvirfall-Feststellung',
            // fix-sprint #447 W10 — the notification clock runs from discovery
            notification_clock: {
                label: 'Notification clock',
                started_from_discovery: 'Running from the recorded discovery: {date}',
                started_at_triage: 'Running from {date}, when the breach was determined; no discovery was recorded then. Recording the discovery dates the clock from it.',
                started_from_earlier_discovery: 'Running from {date}, the discovery recorded when the breach was determined. The discovery recorded now is different; the clock is not moved.',
                not_started_no_discovery: 'Not started: a breach was determined, but no discovery date and time is recorded. The clock runs from discovery and is never started from triage. Record when the incident was discovered to start it.',
                not_started_no_determination: 'Not started: no breach has been determined. Once one is, the clock runs from the recorded discovery.',
            },
            notification_required: 'Meldung erfuerderlech',
            notification_deadline: 'Meldungsfrist',
            pdpc_reportable: 'CNPD-meldepflichteg',
            add_timeline: 'Zäitstrahl-Andrag derbäisetzen',
            add_decision: 'Entscheedung derbäisetzen',
            not_found: 'Tëschefall not fonnt.',
            team_members: 'Team Memberen',
            checklist: 'Checklëscht',
            completed: 'Ofgeschloss',
            impact_severity_score: 'Calculated Auswirkung Schwéiergrad Bewäertung',
            records_affected: 'Datesätz Affected',
            potential_consequences: 'Potential Konsequenzen',
            harm_likelihood: 'Schued Wahrscheinlechkeet',
            legal_basis: 'Rechtlech Grondlag',
            legal_basis_not_recorded: 'Not recorded',
            jurisdictions: 'Jurisdiktiounen',
            jurisdictions_comma: 'Jurisdiktiounen (comma-separated)',
            jurisdiction: 'Jurisdiktioun',
            authority_status: 'Autoritéit Status',
            subject_status: 'Betreff Status',
            authority_deadline: 'Autoritéit Frist',
            subject_deadline: 'Betreff Frist',
            mark_authority_done: 'Markéieren Autoritéit Done',
            mark_subject_done: 'Markéieren Betreff Done',
            // Sprint 62.35.0 gate E12.5 pass 2 (P2-2) — English until Phase 25 (R-57)
            requirement_statuses: {
                not_required: 'Not required',
                required: 'Required',
                in_progress: 'In progress',
                completed: 'Completed',
                overdue: 'Overdue',
            },
            status_contradiction: {
                declared_required: 'The assessment now declares the {notification} required; its recorded status is {status}.',
                declared_not_required: 'The assessment now declares the {notification} not required; its recorded status is {status}.',
                notifications: {
                    authority_status: 'notification to the authority',
                    subject_status: 'notification to the data subjects',
                    other_status: 'notification to other parties',
                },
            },
            // Sprint 62.35.0 (AC9) — English until Phase 25 (R-57)
            affected_parties: {
                title: 'Affected parties notified',
                intro: 'Organisations and individuals concerned, other than the data subjects, who were told of this incident under this requirement, who are still to be told, or who need not be told.',
                requirement_heading: 'Requirement: {jurisdiction}',
                no_requirements: 'No notification requirement is recorded on this incident, so there is no requirement to record affected parties against.',
                empty: 'No affected party is recorded for this requirement.',
                not_recorded: 'Not recorded',
                add: 'Add affected party',
                add_aria: 'Add affected party for requirement {jurisdiction}',
                edit: 'Edit',
                edit_aria: 'Edit affected party: {name}',
                remove: 'Remove',
                remove_aria: 'Remove affected party: {name}',
                remove_confirm_title: 'Remove this affected party?',
                remove_confirm_message: 'The row is removed from this requirement. The audit trail keeps what it recorded.',
                modal_add_title: 'Add affected party',
                modal_edit_title: 'Edit affected party',
                table_label: 'Affected parties for requirement {jurisdiction}',
                col_party: 'Party',
                col_type: 'Type',
                col_status: 'Status',
                col_date: 'Date notified',
                field_party_name: 'Party name',
                field_party_type: 'Party type',
                field_contact: 'Contact',
                field_status: 'Notification status',
                field_notified_on: 'Date notified',
                field_reason: 'Why notification is not required',
                field_note: 'Note',
                types: {
                    data_owner: 'Data owner',
                    partner: 'Partner',
                    handover_recipient: 'Recipient of a handover',
                    other: 'Other',
                },
                statuses: {
                    pending: 'Pending',
                    notified: 'Notified',
                    not_required: 'Not required',
                },
                errors: {
                    party_name_required: 'Enter the name of the party.',
                    notified_on_required: 'Enter the date the party was notified.',
                    notified_on_in_future: 'The date notified cannot be in the future.',
                    reason_required: 'Enter why notification is not required.',
                    save_failed: 'The affected party could not be saved.',
                    remove_failed: 'The affected party could not be removed.',
                    conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so nothing was saved. The latest version of the incident is being loaded and your entries are kept: check them and save again.",
                    remove_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the party was not removed. The latest version of the incident is being loaded: check the list and remove the party again if it is still there.",
                    conflict_party_changed: 'Another save changed this affected party after you opened it, so nothing was saved. The form now shows what that save recorded, and the entries you made were not kept: review the values and enter your changes again before saving.',
                    conflict_party_gone: 'Another save removed this affected party after you opened it, so nothing was saved and the form was closed.',
                    conflict_requirement_gone: 'Another save removed this notification requirement from the incident after you opened the form, so nothing was saved and the form was closed.',
                },
                saved: 'Affected party saved',
                removed: 'Affected party removed',
            },
            // Sprint 62.45.0 — the service-interruption window on the incident record
            interruption_window: {
                title: 'Interruption window',
                intro: 'When a system or service stopped, when it came back, and when remediation was completed. Recorded once, here, so anything that follows from these times reads them from this record rather than retyping them. Remediation completed is not the same as closing the incident.',
                // Gate E12.5 pass 3 (QA P2) — shown only to a viewer who can read the register.
                intro_register: 'When a supervised system or service stopped, when it came back, and when remediation was completed. Recorded once, here: the Supervisory Reporting register reads these times for the interruption notice and the incident report and never retypes them. Remediation completed is not the same as closing the incident.',
                started_at: 'Interruption started',
                ended_at: 'Interruption ended',
                remediation_completed_at: 'Remediation completed',
                not_recorded: 'Not recorded',
                ongoing: 'No end is recorded, so the interruption is treated as continuing.',
                edit: 'Edit interruption window',
                save: 'Save interruption window',
                saving: 'Saving…',
                cancel: 'Cancel',
                local_time_hint: 'Your local date and time. Leave a field empty to clear it.',
                unchanged: 'Change a time to save.',
                saved: 'Interruption window saved',
                save_failed: 'The interruption window could not be saved.',
                errors: {
                    start_first: 'Record when the interruption started first.',
                    before_start: 'This cannot be earlier than the interruption start.',
                    invalid: 'The interruption window is not in order, so nothing was saved.',
                },
            },
            not_assessed: 'Net assessed',
            event_type: 'Evenement Typ',
            timestamp: 'Zäitstempel',
            actor: 'Akteur',
            no_timeline_events: 'No Zäitverlaf Evenementer yet.',
            decision_type: 'Entscheedung Typ',
            decision: 'Entscheedung',
            rationale: 'Begrënnung',
            decision_made: 'Entscheedung Made',
            no_decisions: 'Nach keng Entscheedungen erfaasst.',
            no_audit_events: 'Keen Audit Eventer opgeholl nach.',
            export_pdf: 'PDF exportéieren',
            pdf_title: 'Virfall-Zesummefaassung',
            description: 'Beschreiwung',
            linked_records: 'Verlinkte Recorden',
            export_success: 'Incident report exported',
        },
        tasks: {
            page_title: 'Tëschefall Äntwert Aufgaben',
            title: 'Tëschefall-Äntwert-Aufgaben',
            new: 'Nei Aufgab',
            edit: 'Aufgab änneren',
            task_type: 'Aufgab-Aart',
            assignee: 'Zougewisenen',
            due_date: 'Fällegkeetsdatum',
            dependencies: 'Ofhängegkeeten',
            mark_complete: 'Als fäerdeg markéieren',
            templates: 'Aufgab-Schablounen',
            select_incident: 'Tëschefall auswielen',
            all_statuses: 'All Statussen',
            all_categories: 'All Kategorien',
            new_response_task: 'Nei Äntwert-Aufgab',
            category: 'Kategorie',
            priority: 'Prioritéit',
            depends_on: 'Hänkt of vun (komma-getrennt Aufgab-IDen)',
            checklist_items: 'Checklëscht-Elementer',
            remove: 'Ewechhuelen',
            add: 'Derbäisetzen',
            new_checklist_item: 'Neit Checklëscht-Element',
            no_templates: 'Keng Schablounen disponibel.',
            assign_task: 'Aufgab zouweisen',
            assign_to: 'Zouweisen un',
            assign: 'Zouweisen',
            select_incident_prompt: 'Wielt w.e.g. en Tëschefall aus fir seng Äntwert-Aufgaben ze gesinn.',
            checklist: 'Checklëscht',
            hide_checklist: 'Checklëscht verstoppen',
            escalate: 'Eskaléieren',
            escalations: 'Eskalatiounen',
            no_checklist_items: 'Keng Checklëscht-Elementer.',
        },
        reports: {
            page_title: 'Tëschefall Rapporten',
            title: 'Tëschefall-Rapporten',
            summary: 'Zesummefaassungs-Statistiken',
            by_severity: 'No Schwéiergrad',
            by_status: 'No Status',
            recent: 'Rezent Tëschefäll',
            total_incidents: 'Total Tëschefäll',
            open: 'Op',
            closed: 'Zou',
            export_csv: 'CSV exportéieren',
            export_pdf: 'PDF exportéieren',
            export_success: 'Bericht erfollegräich exportéiert.',
            export_error: 'Bericht konnt net exportéiert ginn.',
            reference: 'Referenz',
            view_report: 'Rapport weisen',
        },
        statuses: {
            reported: 'Gemellt',
            triage: 'Triage',
            contained: 'Enthale',
            investigation: 'Ënnersuchung',
            remediation: 'Behiewung',
            closed: 'Zougemaach',
            false_positive: 'Falschen Alarm',
        },
        task_statuses: {
            pending: 'Ausstehend',
            in_progress: 'Am Gaang',
            completed: 'Ofgeschloss',
            blocked: 'Blockéiert',
            cancelled: 'Annulléiert',
        },
        severity: {
            low: 'Niddreg',
            medium: 'Mëttel',
            high: 'Héich',
            critical: 'Kritesch',
        },
        task_types: {
            containment: 'Andämmung',
            investigation: 'Ënnersuchung',
            remediation: 'Behiewung',
            communication: 'Kommunikatioun',
            documentation: 'Dokumentatioun',
            notification: 'Meldung',
        },
        categories: {
            ai_system: 'AI System',
            data_breach: 'Donnéeën Verstouss',
            unauthorized_access: 'Unauthorized Zougang',
            data_loss: 'Donnéeën Loss',
            system_compromise: 'Systemkompromittéierung',
            malware: 'Malware',
            phishing: 'Phishing',
            insider_threat: 'Insider-Bedrohung',
            other: 'Aner',
        },
        reported_via_options: {
            email: 'E-Mail',
            phone: 'Telefon',
            internal_system: 'Intern System',
            third_party: 'Drëtt Party',
            automated_detection: 'Automatiséiert Erkennung',
        },
        harm_likelihood_options: {
            unlikely: 'Onwahrscheinlech',
            possible: 'Méiglech',
            likely: 'Wahrscheinlech',
            very_likely: 'Ganz Wahrscheinlech',
        },
        breach_determination_options: {
            confirmed_breach: 'Bestätegt Verstouss',
            suspected_breach: 'Verdächteg Verstouss',
            not_a_breach: 'Net eng Verstouss',
            pending: 'Ausstehend',
        },
        timeline_event_types: {
            detection: 'Erkennung',
            escalation: 'Eskalatioun',
            containment: 'Andämmung',
            investigation_start: 'Ënnersichung Starten',
            evidence_collected: 'Bewäis Collected',
            notification_sent: 'Notifikatioun Sent',
            remediation_action: 'Behiewung Aktioun',
            status_change: 'Status Änneren',
            communication: 'Kommunikatioun',
            other: 'Aner',
        },
        decision_types: {
            containment: 'Andämmung',
            notification: 'Notifikatioun',
            remediation: 'Behiewung',
            escalation: 'Eskalatioun',
            closure: 'Ofschloss',
            other: 'Aner',
        },
        task_transitions: {
            start: 'Starten',
            complete: 'Ofschléissen',
            block: 'Blockéieren',
            cancel: 'Ofbriechen',
            unblock: 'Entspären',
        },
        announce: {
            loaded: '{count} Tëschefäll gelueden',
            reports_loaded: '{count} Tëschefall Berichter gelueden',
            tasks_loaded: '{count} Aufgaben gelueden',
            created: 'Tëschefall erfollegräich geschaf',
            updated: 'Tëschefall erfollegräich aktualiséiert',
            closed: 'Tëschefall erfollegräich zougemaach',
            status_changed: 'Tëschefall Status geännert',
            triage_completed: 'Triage erfollegräich ofgeschloss',
            impact_updated: 'Impakt Bewäertung aktualiséiert',
            breach_updated: 'Verstouss Bewäertung aktualiséiert',
            timeline_added: 'Timeline Event dobäigesat',
            decision_recorded: 'Entscheedung opgeholl',
            notification_updated: 'Notifikatioun Status aktualiséiert',
            task_created: 'Aufgab erfollegräich erstallt',
            task_status_changed: 'Aufgab Status geännert',
            checklist_completed: 'Checklëscht Element fäerdeg',
            checklist_unchecked: 'Checklëscht Element net kontrolléiert',
            task_assigned: 'Aufgab erfollegräich zougewisen',
            task_escalated: 'Aufgab eskaléiert',
            export_initiated: 'Export ugefaangen',
        },
        breach_notifications: {
            page_title: 'Verstouss Notifikatiounen',
            title: 'Verstouss Notifikatiounen',
            new_notification: 'Nei Notifikatioun',
            no_notifications: 'Keng Verletzung Notifikatiounen fonnt',
            status: 'Status',
            submit_to_mps: 'Gitt op MPS',
            decision: 'Entscheedung',
            decision_maker: 'Décideuren',
            notify: 'Notéieren',
            do_not_notify: 'Notéiert net',
            rationale: 'Begrënnung',
            filing_deadline: 'Areeche Frist',
            overdue: 'Iwwerfälleg',
            due_soon: 'Geschwënn fälleg',
            on_track: 'Am Zäitplang',
            legal_review: 'Juristesch Iwwerpréiwung',
            incident_id: 'Tëschefall ID',
            // Fix sprint #447 W1 — the decision moves the status; submission through /submit.
            decision_hint: 'Notify moves the notification to Decision to Notify, ready to draft. Do Not Notify closes it; the decision and its rationale are kept.',
            rationale_required: 'Enter the rationale for this decision.',
            mark_drafted: 'Mark notification drafted',
            submission_channel: 'Submission channel',
            submission_confirmation: 'Confirmation or reference',
            submission_note: 'This records the submission your organisation made. The platform files nothing with the authority.',
            select_channel: 'Select a channel',
            channels_load_failed: 'The submission channels could not be read.',
            channel_refusal_no_framework: 'No installed framework declares an authority filing, so no submission can be recorded.',
            channel_refusal_no_channels: 'The filing framework declares no submission channels, so no submission can be recorded.',
            channel_refusal_no_authority: 'The filing framework names no authority that accepts filings, so no submission can be recorded.',
            notification_statuses: {
                assessment: 'Bewäertung',
                decision_to_notify: 'Entscheedung fir Notifikatioun',
                notification_drafted: 'Notifikatioun ausgeschafft',
                legal_review: 'Juristesch Iwwerpréiwung',
                submitted_to_authority: "Un d'Autoritéit geschéckt",
                supplement_requested: 'Zousaz ugefrot',
                supplement_submitted: 'Supplement presentéiert',
                acknowledged: 'Unerkannt',
                closed: 'Zougemaach',
            },
            select_incident: 'Linked Incident',
            // Breach notifications page layout — summary band, details dialog, forms dialog.
            page_description: 'Decide whether each personal data breach has to be notified, record that decision with its rationale, and follow every filing deadline through to the submission your organisation makes to the authority.',
            kpi_total: 'Notifications',
            kpi_awaiting_decision: 'Awaiting decision',
            kpi_deadline_at_risk: 'Due soon or overdue',
            kpi_submitted: 'Submitted',
            filter_toolbar: 'Filter breach notifications',
            results_count: 'Showing {shown} of {total} notifications',
            incident: 'Incident',
            forms: 'Forms',
            forms_count_one: '1 form',
            forms_count: '{count} forms',
            no_forms: 'No forms',
            forms_dialog_title: 'Forms filed on {incident}',
            view_details: 'View details',
            details_title: 'Breach notification details',
            details_decision_section: 'Decision and submission',
            created_at: 'Created',
            submitted_at: 'Submitted on',
        },
        supplements: {
            page_title: 'Zousaz Ufroen',
            title: 'Zousaz Ufroen',
            new_supplement: 'New Supplement',
            no_supplements: 'Keng Ergänzungsfuerderunge fonnt',
            request_details: 'Ufro Detailer',
            response_package: 'Äntwert Package',
            submit_response: 'Gitt Äntwert of',
            deadline: 'Frist',
            notification_id: 'Notifikatioun ID',
            computed_deadline: 'Computed deadline',
            computed_deadline_none: 'None computed',
            computed_deadline_pending: 'Not started yet',
            enter_notification_id: 'Gitt eng Notifikatioun ID fir Ergänzungen ze gesinn.',
            supplement_statuses: {
                requested: 'Gefrot',
                drafting: 'Ausschaffen',
                submitted: 'Erlaabt',
                accepted: 'Akzeptéiert',
                rejected: 'Refuséiert',
            },
        },
        remediation: {
            page_title: 'Sanéierung Aktiounen',
            title: 'Sanéierung Aktiounen',
            new_action: 'Nei Aktioun',
            no_actions: 'Keng Sanéierungsaktioune fonnt',
            description: 'Beschreiwung',
            owner: 'Besëtzer',
            department: 'Departement',
            deadline: 'Frist',
            statuses: {
                planned: 'Geplangt',
                in_progress: 'Am Fortschrëtt',
                completed: 'Ofgeschloss',
                verified: 'Verifizéiert',
            },
            verify: 'Verifizéieren',
            incident_id: 'Tëschefall ID',
            enter_incident_id: 'Gitt eng Tëschefall ID fir Sanéierungsaktiounen ze gesinn.',
            start: 'Start',
            complete_action: 'Ofschléissen',
        },
        authority_requests: {
            page_title: 'Autoritéit Demanden',
            title: 'Autoritéit Demanden',
            new_request: 'Nei Ufro',
            no_requests: 'Keng Autoritéitsufroe fonnt',
            authority_name: 'Autoritéit Numm',
            request_details: 'Ufro Detailer',
            response_package: 'Äntwert Package',
            submission_channel: 'Soumissioun Channel',
            submit_response: 'Gitt Äntwert of',
            deadline: 'Frist',
            confirmation: 'Bestätegung',
            incident_id_optional: 'Tëschefall ID (optional)',
            response_context_label: "Äntwertkontext (fir d'Materialiséierung vum Aräichdokument)",
            response_context_none: '(Materialiséierung iwwersprangen)',
            response_context_supplement: 'Zousazäntwert',
            response_context_information: 'Äntwert op Informatiounsufro',
            response_context_inspection: 'Äntwert op Inspektioun',
            materialisation_status_column: 'Aräichdokument',
            materialisation_pending: 'Ausstänneg',
            materialisation_success: 'Materialiséiert',
            materialisation_failed: 'Feelgeschloen',
            materialisation_retry_button: 'Nach eng Kéier probéieren',
            ar_statuses: {
                received: 'Erhalen',
                verified: 'Verifizéiert',
                assembling: 'Assemblée',
                submitted: 'Agereecht',
                closed: 'Zougemaach',
                response_packaged: 'Response Packaged',
                scope_assessed: 'Scope Assessed',
                legal_review: 'Juristesch Iwwerpréiwung',
            },
            channel_in_person: 'In Person',
            channel_mps_portal: 'MPS Portal',
            channel_postal: 'Postal',
            channel_email: 'Email',
            verification_checklist_title: 'Verification Checklist',
            checklist: 'Checkliste',
            no_checklist_items: 'No checklist items yet. Add items below.',
            add_checklist_item: 'Add checklist item...',
            advance_to_verified: 'Advance to Verified',
            advance_to_scope_assessed: 'Advance to Scope Assessed',
            advance_to_assembling: 'Advance to Assembling',
            advance_to_response_packaged: 'Advance to Response Packaged',
            advance_to_legal_review: 'Advance to Legal Review',
            advance_to_submitted: 'Advance to Submitted',
            advance_to_closed: 'Advance to Closed',
        },
        disclosure_register: {
            page_title: 'Verëffentlechungsregister',
            title: 'Verëffentlechungsregister',
            new_entry: 'Nei Entrée',
            no_entries: 'Keng Verëffentlechungsregistrierungen fonnt',
            disclosure_type: 'Offenbarung Typ',
            disclosure_date: 'Verëffentlechungsdatum',
            recipient: 'Empfänger',
            authority_name: 'Autoritéit Numm',
            channel: 'Kanal',
            content_summary: 'Inhalt Resumé',
            incident_id_optional: 'Tëschefall ID (optional)',
            channel_in_person: 'In Person',
            channel_mps_portal: 'MPS Portal',
            channel_postal: 'Postal',
            type_court_order: 'Court-Ordered Disclosure',
            type_data_breach: 'Data Breach Notification',
            type_regulatory_response: 'Regulatory Response',
            type_voluntary: 'Voluntary Disclosure',
            channel_email: 'Email',
        },
        log_disclosure: 'Log Disclosure',
    },
    onboarding: {
        nav: {
            wizard: 'Setup-Assistent',
            tours: 'Modul-Tourën',
            sample_data: 'Beispilldonnéeën',
        },
        wizard: {
            title: 'Setup-Assistent',
            welcome: 'Wëllkomm beim AesirX ComplianceOne',
            welcome_message: 'Loosst eis Är Organisatioun ariichten. Dësen Assistent féiert Iech duerch déi éischt Konfiguratioun.',
            setup_complete: 'Setup fäerdeg',
            skipped_message: 'Den Setup-Assistent gouf iwwersprongen. Dir kënnt en zu all Moment lafen loossen fir Är Organisatioun ze konfiguréieren.',
            start_setup: 'Setup ufänken',
            skip_wizard: 'Assistent iwwersprangen',
            go_to_admin: 'Gitt op Admin',
            skip_for_now: 'Fir de Moment iwwersprangen',
            next_step: 'Nächste Schrëtt',
            previous_step: 'Virege Schrëtt',
            finish_setup: 'Setup ofschléissen',
            rerun: 'Setup-Assistent nei lafen loossen',
            step_label: 'Schrëtt {number}',
            progress_label: 'Setup progress: Step {number} of {total}',
            validation_org_name_required: 'Organisatiounsnumm ass obligatoresch.',
            validation_admin_email_required: 'Admin-E-Mail ass obligatoresch.',
            validation_admin_email_invalid: 'Gitt w.e.g. eng gëlteg E-Mail-Adress an.',
            validation_cmp_endpoint_required: 'CMP-Endpunkt ass obligatoresch wann CMP aktivéiert ass.',
            steps: {
                license_activation: 'Lizenz Aktivéierung',
                org_profile: 'Organisatiounsprofil',
                module_selection: 'Modulauswiel',
                cmp_integration: 'CMP-Integratioun',
                notification_preferences: 'Notifikatiounen',
                review: 'Iwwerpréifen & Bestätegen',
            },
            step_descriptions: {
                license_activation: 'Gitt Äre Lizenzschlëssel fir Äre Package ze aktivéieren an ze bestëmmen wéi eng Moduler verfügbar sinn.',
                org_profile: "Stellt den Organisatiounsnum, d'Rechtsgebitt, d'Branche, d'Gréisst an d'bevorzugte Sprooch an.",
                module_selection: 'Wielt aus wéi eng Compliance-Module fir Är Organisatioun aktivéiert ginn.',
                cmp_integration: "Konfiguréiert d'Consent Management Platform Integratioun (optional).",
                notification_preferences: 'Konfiguréiert wéi Dir Notifikatiounen fir Compliance-Evenementer kritt.',
                review: 'Iwwerpréift Är Konfiguratioun a schléisst de Setup of.',
            },
            license_step: {
                validate_button: 'Validéieren Lizenz',
                skip_warning: 'Modul Zougang gëtt limitéiert op de Standard Package Tier. Dir kënnt eng Lizenz méi spéit aktivéieren op Plattform> Lizenz Astellungen.',
            },
            module_descriptions: {
                rights_requests: 'Veraarbecht Ufroe fir Zougang an Läsche vu perséinlechen Donnéeën.',
                data_mapping: 'Kartéiert Systemer, Dateflëss a Veraarbechtungsaktivitéiten.',
                governance: "Iwwerwaacht Fournisseuren, Genehmegungen, Ofwäichungen an d'Beweisregëstere.",
                consent: 'Geréiert Zoustëmmungspolitiken an Zweck-Katalogen.',
                consent_properties: 'Geréiert Zoustëmmung iwwer verschidden digital Eegeschaften.',
                assessments: 'Féiert Impakt-Bewäertungen a grenziwwerschreidend Donnéeëntransferen duerch.',
                classification: 'Klassiféiert a markéiert Donnéeë-Aktiven mat Liewenszyklus-Tracking.',
                monitoring: 'Geplangte Compliance-Iwwerpréifungen a KPI-Tracking.',
                incidents: 'Mellt a geréiert Sécherheets- a Privatsphär-Tëschefäll.',
                connectors: 'Verbinnt Iech mat externen Systemer fir automatiséierten Datenaustausch.',
                vendor_risk: 'Bewäert Liwweranten, verfollegt Kontrakter a geréiert Due Diligence.',
                deletion_orchestration: 'Koordinéiert Dateläschung iwwer Systemer a Liwweranten.',
                discovery: 'Entdeckt a klassifizéiert Daten an Ärer ganzer Infrastruktur.',
                incident_ops: 'Avancéiert Incident-Playbooks, SLA-Tracking an Eskalatiounen.',
                program_governance: 'Reifegradbewiertungen, Managementberiichter an Auditbereetschaft.',
                access_accountability: 'Iwwerwaacht Benotzerzougang, Sessiounen a Begrënnung-Workflows.',
                localization: 'Maartspezifisch Compliance-Paketen a regulatoresch Kaders.',
                sector_overlays: 'Install sector overlays and open the registers an activated overlay brings into use, by sector.',
                partner_workspace: 'Partner- an Händleraarbechtsberäich.',
                privacy_scanner: 'Echtzäit-Dateschutziwwerwaachung vun digitalen Eegeschaften.',
                onboarding: 'Ariichtungsassistent, guidéiert Touren an Beispilldatenverwaltung.',
                compliance_forms: 'Interaktiv Compliance-Schablounen an Dokumentegeneratioun.',
                audit_trail: 'Ëmfaassend onverännerbar Auditprotokoll a Bewaarung.',
                organizations: 'Multi-Organisatiouns- a Gruppeverwaltung.',
                task_management: 'Eenheetlech Aufgabeplanung, Workflow-Motor a Kalenneransichten.',
            },
            module_categories: {
                data_privacy_rights: 'Dateschutz a Rechter',
                vendor_third_party: 'Verkeefer & Drëtte-Partei Management',
                risk_assessment: 'Risiko & Bewäertung',
                operations_automation: 'Operatiounen & Automatisatioun',
                platform_admin: 'Plattform & Administratioun',
            },
            package_tier_label: 'Äert Paket',
            package_module_unavailable: 'Net an Äerem Paket abegraff',
            select_all: 'Alles Auswielen',
            deselect_all: 'Alles Ofwielen',
            load_sample_data: 'Beispilldaten no der Ariichtung lueden',
            load_sample_data_description: "Installéiert realistesch Demodaten fir d'Plattform z'entdecken. Kann spéider ënnert Onboarding > Beispilldaten geläscht ginn.",
            language_label: 'Bevorzugte Sprooch',
            language_description: "Wielt d'Standardsprooch fir Är Organisatioun.",
            admin_account_title: 'Administrateur-Kont',
            admin_account_created: 'Administrateur-Kont erstallt',
            admin_full_name: 'Vollstännegen Numm',
            admin_email: 'E-Mail-Adress',
            admin_password: 'Passwuert',
            admin_confirm_password: 'Passwuert bestätegen',
            admin_show_password: 'Passwuert weisen',
            admin_hide_password: 'Passwuert verstoppen',
            admin_name_placeholder: 'z.B. Jane Schmitz',
            admin_email_placeholder: 'admin@example.com',
            admin_password_placeholder: 'Minimum 8 Zeechen',
            admin_password_confirm_placeholder: 'Passwuert nach eng Kéier aginn',
            validation_admin_name_required: 'Den Administrateur-Numm ass erfuerderlech',
            validation_admin_password_required: 'Passwuert ass erfuerderlech',
            validation_admin_password_min_length: "D'Passwuert muss mindestens 8 Zeechen hunn",
            validation_passwords_mismatch: "D'Passwierder stëmmen net iwwereneen",
            notification_email_enabled: 'E-Mail-Notifikatiounen aktivéieren',
            notification_email_description: 'Kritt E-Mail-Notifikatiounen iwwer Aufgabezouweisungen, SLA-Verstooss a Compliance-Evenementer.',
            notification_digest_frequency: 'Zesummefaassungsfrequenz',
            notification_digest_daily: 'Deeglech',
            notification_digest_weekly: 'Wëchentlech',
            notification_digest_none: 'Keng Zesummefaassung',
            notification_in_app_enabled: 'Plattform-Notifikatiounen aktivéieren',
            notification_in_app_description: 'Weist Notifikatiounsbadge a -Schublade fir Echtzäitwarnungen un.',
            jurisdictions: {
                vietnam: 'Vietnam',
                asean: 'ASEAN',
                eu_eea: 'EU/EWR',
                global: 'Global',
            },
            languages: {
                en: 'Englesch',
                vi: 'Tiếng Việt',
                da: 'Dansk',
                sv: 'Svenska',
                nb: 'Norsk Bokmål',
                nn: 'Norsk Nynorsk',
                de: 'Deutsch',
                fi: 'Suomi',
                nl: 'Nederlands',
                fr: 'Français',
                lb: 'Lëtzebuergesch',
                ga: 'Gaeilge',
                es: 'Español',
                ca: 'Català',
                eu: 'Euskara',
                pt: 'Português',
            },
            industries: {
                financial_services: 'Finanzdéngschtleeschtungen',
                healthcare: 'Gesondheetswiewen',
                technology: 'Technologie',
                telecommunications: 'Telekommunikatioun',
                government: 'Ëffentleche Secteur',
                education: 'Bildung',
                manufacturing: 'Fabrikatioun',
                retail: 'Eenzelhandel',
                other: 'Aner',
            },
            placeholders: {
                org_name: 'zB ABC',
                admin_email: 'admin@company.vn',
                team_emails: 'user1@company.vn, user2@company.vn',
                cmp_endpoint: 'https://cmp.example.com/api',
            },
            fields: {
                org_name: 'Organisatiounsnumm',
                jurisdiction: 'Jurisdiktioun',
                industry: 'Industrie',
                org_size: 'Organisatiounsgréisst',
                language: 'Bevorzugte Sprooch',
                admin_email: 'Admin-E-Mail',
                team_emails: 'Team-Member-E-Mailen (komma-getrennt)',
                enable_cmp: 'CMP-Integratioun aktivéieren',
                cmp_endpoint: 'CMP-API-Endpunkt',
                email_notifications: 'E-Mail-Notifikatiounen',
                in_app_notifications: 'Plattform-Notifikatiounen',
                digest_frequency: 'Zesummefaassungsfrequenz',
            },
            validation_jurisdiction_required: 'Jurisdiction is required.',
        },
        tours: {
            title: 'Modul-Onboarding-Tourën',
            start: 'Tour ufänken',
            continue_tour: 'Tour weiderfueren',
            replay: 'Tour erëmspillen',
            exit: 'Tour verloossen',
            skip: 'Tour iwwersprangen',
            next: 'Weider',
            previous: 'Virdrun',
            finish: 'Tour ofschléissen',
            step_of: 'Schrëtt {current} vun {total}',
            info: 'Maacht guidéiert Tourë vun all Modul fir Haaptfunktiounen a Workflows ze léieren. Tourë kënne jidderzeist erëmgespillt ginn.',
            autoplay: 'Auto-Spill',
            interactive: 'Interaktiv',
            play_all: 'Spill All',
            search_tours: 'Sichen Touren ...',
            duration: 'Dauer',
            steps_label: '{count} Schrëtt',
            mode: 'Modus',
            pause: 'Paus',
            resume: 'Resumé',
            speed: 'Speed',
            restart: 'Restart',
            reset: 'Zerécksetzen',
            reset_success: 'Tour Fortschrëtter gouf zréckgesat.',
            category_core_privacy: 'Kär Privatsphär',
            category_governance: 'Gouvernance',
            category_operations: 'Operatiounen',
            category_platform: 'Plattform',
            category_advanced: 'Fortgeschratt',
            category_data_privacy_rights: 'Dateschutz a Rechter',
            category_vendor_third_party: 'Verkeefer & Drëtte-Partei Management',
            category_risk_assessment: 'Risiko & Bewäertung',
            category_operations_automation: 'Operatiounen & Automatisatioun',
            category_platform_admin: 'Plattform & Administratioun',
            steps_count: '{count} Schrëtt',
            autoplay_mode: 'Auto-play mode',
            interactive_mode: 'Interactive mode',
            mode_switch_name: '{current}: switch to {other}',
            revision_conflict: 'This tour has changed since you started it. Restart the tour to see the current version.',
        },
        sample_data: {
            evaluation_title: 'Forseti Evaluation — 100 Vietnam Cases',
            evaluation_info: 'Install synthetic linked assessment records for repeatable evaluation. Loading makes no AI calls. Ordinary sample data is unchanged. Run evaluations separately; legal review is still required.',
            evaluation_loaded: '{count} of {total} cases installed',
            evaluation_load: 'Install evaluation cases',
            evaluation_clear: 'Reset evaluation cases',
            evaluation_confirm_load: 'Install all 100 synthetic Vietnam cases without replacing existing evaluation work? Installation does not run AI.',
            evaluation_confirm_clear: 'Remove only this evaluation dataset’s activity and assessment records? AI runs and audit history remain.',
            title: 'Beispilldonnéeën-Verwaltung',
            select_modules: 'Moduler auswielen',
            current_status: 'Aktuellen Status',
            operation_history: 'Operatiounsgeschicht',
            load: 'Beispilldonnéeë lueden',
            clear: 'Beispilldonnéeë läschen',
            processing: 'Veraarbechten...',
            loaded: 'Gelueden',
            not_loaded: 'Net gelueden',
            records: 'Beispilldatensätz',
            confirm_clear_all: 'Clear the sample data of every module? Only cross-industry sample records are removed; real records are not affected. Industry packages are kept: remove a package from its own card.',
            confirm_clear: 'Sidd Dir sécher, datt Dir all Beispilldonnéeë fir déi ausgewielten Moduler läsche wëllt?',
            confirm_clear_title: 'Beispilldaten läschen',
            confirm_load_title: 'Beispilldaten lueden',
            confirm_load: 'Sidd Dir sécher, datt Dir Beispilldaten fir déi ausgewielte Moduler luede wëllt?',
            action_load: 'Lueden',
            action_clear: 'Läschen',
            info: 'Luet realistesch Demo-Donnéeë fir Training an Demonstratiounen. Beispilldonnéeë si markéiert a kënnen ouni Auswierkung op richteg Donnéeën sécher geläscht ginn.',
            clear_success: 'Sample data cleared successfully',
            load_success: 'Sample data loaded successfully',
            action_install: 'Install package',
            action_remove: 'Remove package',
            // Sprint 63.37.0 (I2) — the forms module's standing counts in the cross-industry status table.
            status_forms_counts: '{official} official, {internal} internal',
            cross_industry_title: 'Cross-industry sample data',
            cross_industry_info: 'Fictional records for every module, shared by all industries. Choose the modules to load or clear below.',
            packages: {
                title: 'Industry sample data',
                info: 'Each package is one connected, fictional scenario named for its industry, never for a customer. Installing writes sample records in this organisation only, and sends no notification, e-mail, webhook, connector run or AI call.',
                list_aria: 'Industry sample-data packages',
                list_failed: 'The industry packages could not be read.',
                list_retry_subject: 'the industry package list',
                list_forbidden: 'Reading industry packages needs the {permission} permission.',
                empty: 'No industry package is available on this platform.',
                synthetic_badge: 'Synthetic data',
                industry: 'Industry',
                line_of_business: 'Line of business',
                version: 'Version',
                reference_prefix: 'Reference prefix',
                contents_heading: 'Contents',
                contents_item: '{area}: {count} records',
                branches_heading: 'Optional parts',
                branch_default: 'chosen by default',
                branches_none: 'None',
                prerequisites_heading: 'Prerequisites',
                prerequisite_frameworks: 'Frameworks the package reads',
                prerequisite_modules: 'Modules the package writes',
                prerequisite_present: 'Installed',
                prerequisite_missing: 'Not installed',
                prerequisite_module_available: 'Included in your plan',
                prerequisite_module_unavailable: 'Not included in your plan',
                prerequisite_note: 'Records that need a missing framework or module are left out at install and listed with the reason.',
                status_heading: 'Status',
                status_not_installed: 'Not installed',
                status_installed: 'Installed',
                installed_language: 'Content language',
                installed_branches: 'Optional parts',
                installed_at: 'Installed on',
                installed_by: 'Installed by',
                installed_records: 'Records written',
                evidence_pack: 'Audit evidence pack',
                evidence_pack_link: 'Open audit evidence packs (pack {id})',
                evidence_pack_none: 'None assembled',
                attachments: 'Stored synthetic files',
                upgrade_available: 'Version {version} is available. Reinstall to replace the installed version {installed}.',
                install: 'Install',
                reinstall: 'Reinstall',
                remove: 'Remove',
                installing: 'Installing…',
                removing: 'Removing…',
                load_needs: 'Installing needs the {permission} permission.',
                clear_needs: 'Removing needs the {permission} permission.',
                dialog_install_title: 'Install {name}',
                dialog_reinstall_title: 'Reinstall {name}',
                language_legend: 'Content language',
                language_en: 'English',
                language_vi: 'Vietnamese',
                language_required: 'Choose the content language.',
                branches_legend: 'Optional parts',
                branches_none_declared: 'This package has no optional parts.',
                will_write: 'Writes up to {count} sample records in these modules:',
                will_drop: 'A record whose framework is not installed, or whose module your plan does not include, is left out and listed with the reason.',
                nothing_sent: 'No notification, e-mail, webhook, connector run or AI call is sent. When the Immutable Audit Trail is on, the audit entries for the stored files and the evidence pack are anchored like any other audit entry.',
                no_accounts: 'No sign-in accounts are created.',
                reinstall_note: "With the same language and parts, nothing changes. With different ones, the package's records are removed and written again.",
                remove_confirm_title: 'Remove {name}',
                remove_confirm: "Remove {name}? This deletes the {count} records the package wrote in its modules and its {files} stored synthetic files. Its audit evidence pack is deleted too, unless a person reviewed it or a framework evidence pack collected it: then it is kept as your organisation's own record. Audit entries recording the install and the removal stay. Cross-industry sample data and your real records stay.",
                install_failed: 'The package could not be installed.',
                remove_failed: 'The package could not be removed.',
                result_heading: 'Result of the last action',
                result_installed: '{name} installed.',
                result_reinstalled: '{name} reinstalled.',
                result_unchanged: 'Nothing changed: {name} is already installed at this version, in this language, with these parts.',
                result_removed: '{name} removed.',
                result_written: 'Records written: {count}',
                result_removed_first: 'Records removed before writing: {count}',
                result_attachments: 'Synthetic files stored: {count}',
                result_dropped_heading: 'Records left out ({count})',
                result_dropped_none: 'No record was left out.',
                drop_framework_not_installed: 'a framework it needs is not installed ({detail})',
                drop_requirement_not_installed: 'the requirement it maps to is not in the installed framework ({detail})',
                drop_module_not_available: 'your plan does not include this module ({detail})',
                drop_reference_dropped: 'it refers to a record that was left out ({detail})',
                dropped_item: '{record} ({area}): {reason}',
                // Sprint 63.37.0 (I2) — the Forms sub-section of a package card and the install result.
                forms_heading: 'Forms',
                forms_counts: '{official} official, {internal} internal, {not_installed} not installed on this organisation',
                forms_list_aria: 'Forms of {name}',
                form_official: 'Official',
                form_internal: 'Internal — no official form declares this',
                form_not_installed: 'Not installed on this organisation; it needs {framework}',
                form_not_installed_unnamed: 'Not installed on this organisation',
                form_restricted: 'Not available on this organisation: the form it needs is restricted',
                form_not_fileable: 'Not available on this organisation: the installed form cannot be filed',
                forms_failed: 'The forms of this package could not be read.',
                forms_retry_subject: 'the package forms',
                result_forms_written: 'Forms written ({count})',
                result_forms_dropped: 'Forms not written ({count})',
                result_form_written_item: '{record}: {form} ({standing})',
                drop_form_not_installed: 'no installed form answers it ({detail})',
                drop_form_restricted: 'the form it needs is restricted on this organisation ({detail})',
                drop_form_not_fileable: 'the installed form cannot be filed ({detail})',
                evidence_pack_assembled: 'Audit evidence pack assembled, not verified: {id}.',
                evidence_pack_failed: 'The audit evidence pack could not be assembled: {error}. The records stand.',
                evidence_pack_not_assembled: 'No audit evidence pack was assembled.',
                guide_heading: 'Scenario guide',
                guide_intro: 'The demonstration path, in order. Each step opens the record or its list.',
                guide_language: 'Guide language: {language}',
                guide_no_accounts: 'Sample data includes no sign-in accounts. Present with your own account; a record that names a person by account names the user who installed the package.',
                guide_reviewers: "Pending approvals and the impact assessment are decided by your organisation's own reviewers, not during the demonstration.",
                guide_point: 'Point',
                guide_do_not_claim: 'Do not claim',
                guide_step_unavailable: "This step's record was not installed.",
                guide_failed: 'The scenario guide could not be read.',
                guide_retry_subject: 'the scenario guide',
                result_removed_records: 'Records deleted from the modules: {count}',
                result_removed_pack: 'Audit evidence pack removed ({count} stored rows)',
                kept_heading: 'Audit evidence pack versions kept ({count})',
                kept_reviewed: "Pack {pack_id}, version {version}: kept because it was reviewed. It is now your organisation's own record. The events it lists were sample rows and went with the package, so Check for changes since assembly reports them as gone, or as changed where a reinstall wrote them again.",
                kept_collected: "Pack {pack_id}, version {version}: kept because a framework evidence pack collected it. It is now your organisation's own record. The events it lists were sample rows and went with the package, so Check for changes since assembly reports them as gone, or as changed where a reinstall wrote them again.",
                reinstall_failed_removed: 'The reinstall failed after the previous install was removed: {count} records were deleted and nothing was written. The package is no longer installed.',
                kept_outputs_heading: 'Exports of sample forms kept ({count})',
                kept_output_item: "Export {output_id} (file {file_id}): kept because {cited_by} names its file. It is now your organisation's own file. The sample form it was printed from went with the package.",
                kept_output_by_compliance_execution_proof: 'compliance proof {id}',
                kept_output_by_cross_border_filing_evidence: 'cross-border filing {id}',
                kept_output_by_handbook_assessment_evidence: 'handbook assessment {id}',
                kept_output_by_ai_obligation_evidence: 'AI obligation record {id}',
                kept_output_by_evidence_pack_dispatch_proof: 'evidence pack dispatch {id}',
                kept_output_by_legal_hold: 'legal hold {id}',
                kept_output_by_other: 'record {id}',
                kept_output_by_legal_hold_policy: 'the legal hold set on {id} files',
                kept_held_files_heading: 'Sample files kept, not deleted ({count})',
                kept_held_file_item: "File {file_id}: kept, not deleted, because {cited_by} names it. It stays as your organisation's file, and can be deleted in Files once nothing names it.",
            },
            // Sprint 63.37.0 (rehearsal J2) — the forms row when the load left forms out because their framework is not installed.
            status_forms_counts_not_installed: '{official} official, {internal} internal, {not_installed} not installed on this organisation',
        },
        help: {
            title: 'Hëllef-Zentrum',
            search: 'Sichen',
            search_placeholder: 'Hëllef-Artikele sichen...',
            clear: 'Läschen',
            all_categories: 'All Kategorien',
            all_modules: 'All Moduler',
            back: 'Zréck zum Hëllef-Zentrum',
            callout_important: 'Wichteg',
            callout_tip: 'Tipp',
            no_results: 'Keng Artikele fonnt déi mat Ärer Sich iwwerenee stëmmen.',
            no_articles: 'Nach keng Hëllef-Artikele disponibel. Artikele wäerte hei erschéngen wann Inhalt derbäigesat gëtt.',
            results_for: '{count} Resultat(er) fir "{query}"',
            quick_links: {
                getting_started: 'Ufänken',
                getting_started_desc: 'Installatioun, Astellung an Schnellstart-Guide.',
                user_guide: 'Benotzerhandbuch',
                user_guide_desc: 'Modul-fir-Modul Benotzungsdokumentatioun.',
                admin_guide: 'Admin-Guide',
                admin_guide_desc: 'Konfiguratioun, Ënnerhalt an Troubleshooting.',
                faq: 'FAQ',
                faq_desc: 'Heefeg gestallte Froen an Äntwerten.',
            },
            search_articles_aria: 'Hëllefsartikelen sichen',
            article_metadata_aria: 'Artikel-Metadaten',
            faq_editor_title: 'FAQ-Editeur vum Hëllefzenter',
            faq_editor_description: 'Fügt Fro-an-Äntwert-Puer an all Artikel vum Hëllefzenter derbäi, ännert se oder läscht se. Den Editeur ass nach net verfügbar; dës Adress ass dofir reservéiert.',
            faq_editor_empty: 'Den FAQ-Editeur ass nach net verfügbar. Frot bis dohin Ären Administrateur, FAQ-Sektiounen fir Iech an en Artikel vum Hëllefzenter derbäizesetzen.',
            articles_list_aria: 'Hëllefsartikelen',
            category_nav_aria: 'Kategorienavigatioun',
            quick_links_aria: 'Schnelllinks zu Hëllefskategorien',
            returned_to_list: "Zréck op d'Artikellëscht",
            opening_article: 'Artikel gëtt opgemaach: {title}',
            read_article_aria: 'Artikel liesen: {title}',
            pagination_nav_aria: 'Artikel-Säitennummeréierung',
            error_unavailable: "D'Hëllefzenter ass am Moment net verfügbar. Probéiert et w.e.g. méi spéit nach eng Kéier.",
            retry: 'Nach eng Kéier probéieren',
            list_truncated: 'Only {shown} of {total} help articles are listed. Use search to find the rest.',
        },
        // Sprint 63.11.0 (#34) — the persona and use-case detail pages. Every
        // value these pages show ABOUT a persona or a use case is read from the
        // shared registries; these strings are only the frame around it.
        // English seed for structural parity (R-57): translation is
        // docs/product/phases/phase-25/sprint-58.7.0.md.
        persona_detail: {
            page_title: 'Persona',
            text_loading: 'Loading the role text…',
            text_failed: 'The role text could not be loaded.',
            not_found_title: 'Persona not found',
            not_found_body: 'No persona in the reference set has the identifier "{slug}". Choose one from the persona paths instead.',
            back_to_list: 'Back to persona paths',
            open_path: 'Open onboarding path',
            profile_heading: 'Profile',
            field_persona_id: 'Persona ID',
            field_market: 'Market',
            field_quick_start: 'Suggested first task',
            modules_heading: 'Primary modules',
            modules_description: 'The modules this persona works in most, limited to those enabled for your organisation.',
            modules_checking: 'Checking which of these modules are enabled for your organisation…',
            modules_unchecked: 'We could not check which of these modules are enabled for your organisation, so all of them are listed.',
            modules_not_permitted: 'Your role cannot see which modules are enabled for your organisation, so all of them are listed.',
            modules_not_enabled: 'Not enabled for your organisation: {names}',
            modules_none_enabled: "None of this persona's primary modules is enabled for your organisation.",
            use_cases_heading: 'Use cases',
            use_cases_description: 'Validated use cases that name this persona as their primary persona.',
            use_cases_none: 'No validated use case names this persona as its primary persona.',
            availability_heading: 'Availability',
            availability_checking: 'Checking whether this persona is available to your organisation…',
            availability_available: 'Available to your organisation.',
            availability_ungated: 'Available to every organisation — this persona is not tied to one market.',
            availability_unavailable: 'This persona belongs to a market your organisation has not installed ({market}). Its onboarding path, quick start and recommendations are not offered to your organisation until a market pack for that market is installed.',
            availability_failed: 'We could not check whether this persona is available to your organisation, so this page does not say either way.',
            retry: 'Try again',
            retrying: 'Checking…',
            market_vn: 'Vietnam',
            market_eu: 'European Union / EEA',
            market_uk: 'United Kingdom',
            market_gl: 'Global — not tied to one market',
        },
        use_case_detail: {
            page_title: 'Use case {id}',
            description: 'A validated use case from the reference set and the persona it is written for. The full narrative is in the reference document named below; this page does not restate it.',
            not_found_title: 'Use case not found',
            not_found_body: 'No use case in the reference set has the identifier "{id}". Open a persona to see the use cases written for it.',
            back_to_list: 'Back to persona paths',
            open_path: 'Open the onboarding path for its primary persona',
            details_heading: 'Details',
            field_use_case_id: 'Use case ID',
            field_primary_persona: 'Primary persona',
            field_persona_id: 'Persona ID',
            field_market: 'Market',
            availability_heading: 'Availability',
            availability_checking: 'Checking whether this use case is available to your organisation…',
            availability_available: 'Available to your organisation.',
            availability_ungated: 'Available to every organisation — its primary persona is not tied to one market.',
            availability_unavailable: 'This use case belongs to a market your organisation has not installed ({market}). Its primary persona and onboarding path are not offered to your organisation until a market pack for that market is installed.',
            availability_failed: 'We could not check whether this use case is available to your organisation, so this page does not say either way.',
            retry: 'Try again',
            retrying: 'Checking…',
        },
    },
    common: {
        vendor: 'Vendor',
        // R-57 — structural parity with English values; real translations are EN and VI.
        list_truncation: {
            loading: 'Loading the list…',
            failed: 'The list could not be loaded, so nothing can be chosen from it right now.',
            partial_with_total: 'Showing {shown} of {total}. Narrow the search to reach the rest.',
            partial_without_total: 'Showing the first {shown}. Narrow the search to reach the rest — how many more there are is not known.',
        },
        // fix-sprint-20261013-0200 R1 — the remedy a failed directory read offers, said once for every
        // DirectoryNotice and UserSelect (components/register/DirectoryNotice.tsx `directoryRetry`).
        directory_retry: 'Reload',
        directory_retrying: 'Reloading…',
        directory_retry_subject: 'the user directory',
        // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
        // no locale declared, so each fell through to its hardcoded English third
        // argument and rendered English in all 16 locales. The two DPIA row-action
        // tooltips were worse: their fallbacks are themselves translated keys, so the
        // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
        // action read 'Approve/Reject' where it intends 'Review'.
        change: 'Change',
        current: 'current',
        next_page: 'Next page',
        selected: 'selected',
        // Fix sprint F30 — the shared "for example" wrapper. Three pages built
        // `e.g. ${code}` as a template literal, so the words rendered in English in
        // all 16 locales; the abbreviation differs per language (see
        // `escalation_contact_placeholder`, which already had it right).
        example_value: 'z. B. {value}',
        /* R-57 — structural parity; real translation deferred to the locale programme. */
        export_incomplete_heading: 'This export is incomplete',
        export_incomplete_body: '{count} value(s) in {fieldCount} field(s) ({fields}) were withheld because you are not authorized to view them. The file marks each one.',
        export_not_scanned: 'This export was produced from pre-formatted content, so its completeness was not checked.',
        save: 'Späicheren',
        cancel: 'Ofbriechen',
        system_actor: 'System',
        delete: 'Läschen',
        edit: 'Änneren',
        create: 'Erstellen',
        update: 'Aktualiséieren',
        loading: 'Lueden...',
        error: 'En Feeler ass opgetrueden',
        success: 'Erfolleg',
        logout: 'Ofmellen',
        actions: 'Aktiounen',
        retrying: 'Probéiert nach eng Kéier…',
        permission_denied: 'Dir hutt keen Zougang zu dëser Säit.',
        status: 'Status',
        description: 'Beschreiwung',
        name: 'Numm',
        created_at: 'Erstallt um',
        updated_at: 'Aktualiséiert um',
        yes: 'Jo',
        no: 'Nee',
        confirm: 'Bestätegen',
        confirm_dialog_title: 'Confirm action',
        back: 'Zréck',
        next: 'Weider',
        previous: 'Virdrun',
        search: 'Sichen',
        filter: 'Filteren',
        page_x_of_y: 'Säit {page} vun {total}',
        pagination_label: 'Paginatioun',
        view_mode_label: 'Usiichtsmodus',
        export: 'Exportéieren',
        exporting: 'Exportéiert…',
        sidebar_navigation: 'Admin Sidebar Navigatioun',
        items_count: '{count} Artikelen',
        items_count_one: '{count} Element',
        export_success: 'Dokument erfollegräich generéiert.',
        generate_pdf: 'PDF generéieren',
        import: 'Importéieren',
        download: 'Eroflueden',
        upload: 'Eroplueden',
        view: 'Weisen',
        close: 'Zoumaachen',
        close_notification: 'Close notification',
        discard_prompt: {
            title: 'Discard unsaved changes?',
            message: 'This dialog has unsaved changes. Closing it now discards them.',
            confirm: 'Discard',
            cancel: 'Keep editing',
        },
        submit: 'Aschécken',
        saved: 'Gespäichert',
        saving: 'Späicheren...',
        submitting: 'Schécken...',
        created: 'Ugeluecht',
        all: 'All',
        none: 'Keen',
        select: 'Auswielen',
        select_user: 'Wielt Benotzer ...',
        custom_input: 'Manuell aginn',
        required: 'Obligatoresch',
        optional: 'Fakultativ',
        notes: 'Notizen',
        tags: 'Tags',
        archive: 'Archivéieren',
        clone: 'Klonen',
        refresh: 'Aktualiséieren',
        retry: 'Nach eng Kéier probéieren',
        // fix-sprint-20260810-2128 residue — English placeholder for structural
        // parity; locale work beyond EN/VI defers to Phase 25 (R-57, sprint-57.7.0).
        options_unreadable: 'The choices for this field could not be read. That does not mean there are none.',
        options_unreadable_pointer: 'Could not be read — see the message below.',
        reset: 'Zrécksetzen',
        add: 'Derbäisetzen',
        remove: 'Ewechhuelen',
        removing: 'Gëtt ewechgeholl…',
        attachment: 'Unhang',
        upload_file: 'Datei eroplueden',
        upload_file_required: 'Datei eroplueden (obligatoresch)',
        // fix-sprint-20260808-0511-followup-2 F1 — FileUpload.tsx drop-zone,
        // progress and validation-toast strings (were hardcoded EN in a shared
        // component reached from every file_upload form field).
        upload_drag_or_browse: 'Zéien a falen loossen oder klicken fir ze duerchsichen',
        upload_max_size: 'Max. Gréisst: {size} MB',
        upload_accepted_types: 'Akzeptéiert: {types}',
        upload_in_progress: '{name} gëtt eropgelueden...',
        upload_size_exceeded: "D'Fichersgréisst iwwerschreit déi maximal erlaabte Gréisst vu {size} MB.",
        upload_type_not_accepted: 'Fichertyp net akzeptéiert. Akzeptéiert Typen: {types}',
        upload_succeeded: 'De Fichier "{name}" gouf eropgelueden.',
        upload_failed: 'De Fichier konnt net eropgeluede ginn.',
        select_entity_type_first: 'Wielt als éischt en Entitéitstyp',
        failed_to_remove_file: "D'Datei konnt net ewechgeholl ginn.",
        entity_type_label: 'Entitéitstyp',
        entity_label: 'Entitéit',
        entity_reference: 'Entitéitsreferenz',
        failed_to_load_entities: 'Entitéite konnten net geluede ginn',
        enable: 'Aktivéieren',
        disable: 'Desaktivéieren',
        no_data: 'Keng Donnéeë fonnt',
        confirm_delete: 'Sidd Dir sécher, datt Dir dësen Element läsche wëllt?',
        confirm_action: 'Sidd Dir sécher, datt Dir weiderfuere wëllt?',
        save_success: 'Erfollegräich gespäichert',
        save_error: 'Späichere feeler',
        delete_success: 'Erfollegräich geläscht',
        delete_error: 'Läsche feeler',
        hour: 'Stonn',
        hours: 'Stonnen',
        load_error: 'Donnéeë konnten net geluede ginn',
        operation_error: 'Operatioun gescheitert',
        something_went_wrong: 'Eppes ass schiefgaangen',
        go_home: "Op d'Iwwersiicht goen",
        access_denied: 'Zougang refuséiert',
        no_permission: 'Dir hutt keng Autorisatioun fir op dës Säit zouzegräifen.',
        no_permission_title: 'Onzureichend Berechtigungen',
        no_permission_message: 'Dir hutt keng Berechtigung fir op dës Säit ze kommen.',
        unknown: 'Onbekannt',
        back_to_dashboard: "Zréck op d'Iwwersiicht",
        id: 'ID',
        type: 'Typ',
        date: 'Datum',
        created_by: 'Erstallt vun',
        updated_by: 'Aktualiséiert vun',
        page_of: 'Säit {page} vun {total}',
        showing_items: '{count} Elementer ugewisen',
        items_per_page: 'Elementer pro Säit',
        no_results: 'Keng Datensätz entspriechen Äre Filteren',
        no_items: 'Keng Elementer fonnt',
        sort_by: 'Zortéieren no',
        ascending: 'Opsteigend',
        descending: 'Ofsteigend',
        all_statuses: 'All Statussen',
        all_types: 'All Typen',
        all_categories: 'All Kategorien',
        all_severities: 'All Schwéiergrader',
        date_range: 'Datumberäich',
        from_date: 'Vun',
        to_date: 'Bis',
        clear_filters: 'Filteren zrécksetzen',
        apply_filters: 'Filteren uwenden',
        save_changes: 'Ännerunge späicheren',
        discard_changes: 'Ännerunge verwerfen',
        confirm_discard: 'Dir hutt ongespaarte Ännerungen. Wëllt Dir se verwerfen?',
        copy: 'Kopéieren',
        duplicate: 'Duplizéieren',
        move: 'Réckelen',
        assign: 'Zouweisen',
        unassign: 'Zouweisung ophiewen',
        approve: 'Genehmegen',
        reject: 'Refuséieren',
        deleting: 'Läschen...',
        processing: 'Veraarbechten...',
        loading_page: 'Säit gëtt gelueden...',
        operation_success: 'Operatioun erfollegräich ofgeschloss',
        operation_failed: 'Operatioun fehlgeschloen. Probéiert et w.e.g. nach eng Kéier.',
        /*
         * Sprint 64.x-batch-17 — moved out of two inline `{ en, vi }` maps.
         *
         * `CHECKED_DOWNLOAD_FAILED_COPY` lived in `useCheckedDownload.ts` and is
         * the fallback toast for EVERY one of the 26 migrated download surfaces;
         * `RETRIEVAL_DENIED_COPY` lived in `EvidencePackDetailPage.tsx`. I18N.md
         * names `{ en, vi }` lookup objects as the documented root cause of "14
         * languages showing only English" and bans them outright, and neither
         * string had a key in any of the 16 bundles — so both were invisible to
         * the Phase 25 translation batches and could never reach parity.
         *
         * The hook's docblock justified the inline map with "a key earns its place
         * when more than one surface needs the same string", a rule that is not in
         * I18N.md; the string was in fact read by every migrated call site.
         */
        download_failed: 'Download failed.',
        download_forbidden: 'The file could not be downloaded: downloading files needs a permission your role does not hold.',
        export_needs_download: 'Exporting needs the permission to download files, which your role does not hold.',
        export_download_forbidden: 'The export was made, but the file could not be downloaded: downloading files needs a permission your role does not hold.',
        retrieval_denied: 'Retrieving this version requires a permission your role does not hold.',
        retrieval_denied_generic: 'Downloading this file requires a permission your role does not hold.',
        /* Sprint 62.58.0 PR 3 (product-qa walk N-2) — a stored file refused because
           it printed sections read under grants the role does not all hold; the
           grants named in plain words (`download_permission_names`). */
        download_refusal_sections: 'The file could not be downloaded: it includes sections that need these permissions, which your role does not hold: {permissions}.',
        /* Step-2 confirmation P3-4 — the same refusals in *File details*, which
           the reader only opened: nothing was downloaded. */
        details_refusal_sections: 'The file details cannot be shown: the file includes sections that need these permissions, which your role does not hold: {permissions}.',
        details_refused_generic: 'The file details cannot be shown: viewing this file needs a permission your role does not hold.',
        download_permission_names: {
            privacy_notice_read: 'read privacy notices',
            dm_activity_read: 'read processing activities',
            dm_system_read: 'read data-map systems',
            biometric_capture_read: 'read biometric capture components',
        },
        date_cleared: 'Date cleared',
        clear_date: 'Clear date',
        date_picker: 'Date picker',
        date_selected: 'Selected',
        not_measured: 'Not measured',
        empty_dash: '-',
        network_error: 'Netzwierkfeeler. Iwwerpréift Är Verbindung.',
        session_expired: 'Sessioun ofgelaf. Loggt Iech w.e.g. nach eng Kéier an.',
        test: 'Test',
        pause: 'Pauséieren',
        resume: 'Weiderfueren',
        activate: 'Aktivéieren',
        last_sync: 'Lescht Synchronisatioun',
        health: 'Gesondheet',
        email: 'E-Mail',
        role: 'Roll',
        title: 'Titel',
        owner: 'Besëtzer',
        priority: 'Prioritéit',
        category: 'Kategorie',
        details: 'Detailer',
        overview: 'Iwwerbléck',
        configuration: 'Konfiguratioun',
        history: 'Geschicht',
        new_item: 'Nei',
        edit_item: 'Änneren',
        select_option: 'Auswielen...',
        total: 'Gesamt',
        active: 'Aktiv',
        inactive: 'Inaktiv',
        enabled: 'Aktivéiert',
        disabled: 'Desaktivéiert',
        last_login: 'Lescht Umeldung',
        never: 'Ni',
        password_changed: 'Passwuert geännert. Dir gitt ausgeloggt.',
        password_change_failed: 'Passwuert-Ännerung fehlgeschloen',
        passwords_mismatch: "D'Passwierder stëmmen net iwwereneen",
        install: 'Installéieren',
        install_named: 'Installéieren {name}',
        uninstall: 'Desinstalléieren',
        installed: 'Installéiert',
        available: 'Verfügbar',
        catalog: 'Katalog',
        published: 'Verëffentlecht',
        version: 'Versioun',
        market: 'Maart',
        language: 'Sprooch',
        framework: 'Kaderreegelung',
        template: 'Schabloun',
        connector_type: 'Konnektor-Typ',
        polling_interval: 'Offrointervall (Minutten)',
        new_instance: 'Nei Instanz',
        change_password: 'Passwuert änneren',
        current_password: 'Aktuellt Passwuert',
        new_password: 'Neit Passwuert',
        confirm_password: 'Passwuert bestätegen',
        comments: 'Kommentaren',
        completed: 'Ofgeschloss',
        contact: 'Kontakt',
        hide: 'Verstoppen',
        hide_details: 'Verstoppen Detailer',
        label: 'Etikett',
        max: 'Max',
        min: 'Min',
        na: 'N/A',
        of: 'of',
        showing: 'Weist',
        page: 'Säit',
        preview: 'Virschau',
        print: 'Drécken',
        show: 'Weisen',
        status_active: 'Aktiv',
        status_completed: 'Ofgeschloss',
        status_draft: 'Entworf',
        status_in_progress: 'In Fortschrëtt',
        status_pending: 'Ausstehend',
        view_details: 'Ukucken Detailer',
        all_priorities: 'All Prioritéiten',
        pipeline: 'Pipeline',
        all_requests: 'All Ufroen',
        schedule: 'Plangen',
        deploy: 'Bereitstellen',
        rollback: 'Zrécksetzen',
        advance_stage: 'Advance Stuf',
        submit_for_approval: 'Ofschécken fir Genehmegung',
        approval_log: 'Genehmegung Protokoll',
        approver: 'Genehmeger',
        decision: 'Entscheedung',
        decided_at: 'Entscheed den',
        rollout_stages: 'Aféierung Stufen',
        approval_comment: 'Genehmegung Kommentar',
        optional_comment: 'Fakultativ Kommentar...',
        impact_summary: 'Auswirkung Zesummefaassung',
        target_property_ids: 'Zil Eegeschaft IDen (comma-separated)',
        policy_id: 'Richtlinn ID',
        target_version: 'Zil Versioun',
        target_version_placeholder: 'z. B. 2.0.0',
        scheduled_label: 'Geplangd',
        scheduled_start: 'Geplangd Starten',
        scheduled_end: 'Geplangd Enn',
        properties_count: '{count} Eegeschaften',
        results: 'Resultater',
        comparison_results: 'Vergläich Resultater',
        past_comparison_reports: 'Past Vergläich Berichten',
        scanning: 'Scanne...',
        run_inconsistency_scan: 'Ausféieren Inconsistency Scannen',
        property_ids_label: 'Eegeschaft IDen (comma-separated, min 2)',
        comparison_type: 'Vergläich Typ',
        golden_template_id: 'Golden Schabloun ID (optional)',
        leave_blank_compare: 'Leave blank op compare Eegeschaften against each other',
        add_notes_placeholder: 'Derbäisetzen your analysis notes here...',
        covered_of_active: '{covered} vun {active} aktiv Eegeschaften covered',
        fetch_error: 'Konnt net fetch Donnéeën. Probéiert et nach eng Kéier.',
        run_comparison_error: 'Konnt net run comparison. Probéiert et nach eng Kéier.',
        save_notes_error: 'Konnt net save notes. Probéiert et nach eng Kéier.',
        scan_complete: 'Inconsistency Scan completed successfully.',
        scan_error: 'Konnt net run inconsistency Scan. Probéiert et nach eng Kéier.',
        min_two_properties: 'Please provide bei least 2 property IDen fir comparison.',
        comma_separated: 'kommagetrennt',
        language_english: 'Englesch',
        language_vietnamese: 'Vietnamesesch',
        language_vietnamese_native: 'Tiếng Việt',
        author: 'Auteur',
        dependencies: 'Ofhängegkeeten',
        checksum: 'Préifsumm',
        changelog: 'Ännerungsprotokoll',
        tier: 'Stuf',
        phone: 'Telefon',
        launch: 'Starten',
        complete: 'Ofschléissen',
        back_to_list: 'Back op Lëscht',
        clear_selection: 'Läschen Auswiel',
        deactivate: 'Deaktivéieren',
        scope: 'Ëmfang',
        deadline: 'Frist',
        progress: 'Fortschrëtt',
        frequency: 'Heefegkeet',
        reviewer: 'Préifer',
        user: 'Benotzer',
        campaign: 'Kampagne',
        items: 'Elementer',
        next_run: 'Nächst Ausféieren',
        last_run: 'Lescht Ausféieren',
        overdue: 'IWWERFÄLLEG',
        creating: 'Erstelle...',
        launching: 'Starte...',
        loaded: 'loaded',
        page_size: 'Säit Gréisst',
        per_page: '/ Säit',
        urgent: 'Dringend',
        pagination: 'Paginatioun',
        metric: 'Metresch',
        value: 'Wäert',
        country: 'Land',
        showing_chart_view: 'Diagramm-Ansicht gëtt ugewisen',
        showing_table_view: 'Daten-Tabellen-Ansicht gëtt ugewisen',
        policies: 'Politiken',
        compliant: 'Konform',
        non_compliant_status: 'Net-kompatibel',
        all_data: 'All Daten',
        sr_urgent: 'Dringend',
        n_per_page: '{n} / Säit',
        select_user_placeholder: '-- Wielt Benotzer --',
        field_required: 'Dëst Feld ass erfuerderlech',
        results_found: 'Resultater fonnt',
        select_placeholder: '-- Auswielen --',
        unknown_user: 'Onbekannte Benotzer',
        // fix-sprint #431 F7 — the names of the platform's own actors
        // (`SystemActorId`), as the audit trail and an assignee column show them.
        // English until Phase 25 (R-57).
        system_actors: {
            intake: 'System Intake',
            sla_engine: 'SLA Engine',
            export_job: 'Export Job',
            cmp_gateway: 'CMP Gateway',
            notification: 'Notification System',
            migration: 'System Migration',
            other: 'System: {name}',
        },
        export_csv: 'CSV exportéieren',
        not_applicable: 'N/A',
        aria_overdue_since: 'Iwwerfälleg zënter {date}',
        placeholders: {
            enter_justification: 'Aginn Begrënnung',
            justification: 'Begrënnung',
            campaign_name_example: 'Q1 2026 Zougang Iwwerpréiwen',
            optional_description: 'Fakultativ Beschreiwung',
            enter_user_id: 'Aginn den user ID vun den reviewer',
            enter_user_id_short: 'Aginn user ID',
            schedule_name_example: 'Trimesteriell Zougang Attestéierung',
            enter_property_id: 'Aginn property ID',
            enter_policy_id: 'Aginn Richtlinn ID',
            property_or_group_id: 'Eegeschaft oder Grupp ID',
            describe_template_purpose: 'Beschreiwen den Schabloun purpose...',
            step_title: 'Schrëtt title...',
            detailed_instructions: 'Detailléiert Instruktiounen...',
            filter_by_taxonomy_id: 'Filteren no Taxonomie ID...',
            describe_classification_rule: 'Beschreiwen den classification Reegel...',
            describe_dedup_rule: 'Beschreiwen den dedup Reegel...',
            describe_import_template: 'Beschreiwen den Import Schabloun...',
            reason_for_rejection: 'Grond fir rejection...',
            jurisdiction_codes_example: 'VN, SG, EU',
            filter_by_locale: 'Filteren no locale...',
            filter_by_event_type: 'Filteren no Evenement type',
            filter_by_actor_id: 'Filteren no actor ID',
            filter_by_market_code: 'Filteren no market Code...',
            filter_by_framework_id: 'Filteren no framework ID...',
            add_comment: 'Derbäisetzen eng Kommentar...',
            provide_rejection_reason: 'Uginn eng reason fir rejecting dëst Areechung...',
            filter_by_jurisdiction: 'Filteren no jurisdiction Code...',
            enter_report_title: 'Aginn report title',
            enter_name: 'Aginn Numm',
            enter_description: 'Aginn Beschreiwung',
            enter_org_id: 'Aginn Org ID op add',
            describe_evidence_gap: 'Beschreiwen den Bewäis Lück...',
            expected_evidence: 'What Bewäis is expected',
            entity_identifier: 'Entitéit identifier',
            remediation_steps: 'Schrëtter op remediate dëst Lück',
            user_id_or_name: 'Benotzer ID oder Numm',
            reporting_period_id: 'Berichterstattung Period ID',
            maturity_model_id: 'Reifegkeet Modell ID',
            describe_maturity_model: 'Beschreiwen dëst maturity Modell...',
            describe_role_purpose: "Describe this role's purpose...",
            provide_deny_reason: 'Uginn eng reason fir denying dëst request',
            describe_emergency: 'Beschreiwen den emergency situation requiring elevated Zougang (minimum 20 characters)',
            enter_delegate_id: 'Aginn user ID vun den delegate',
            provide_delegation_reason: 'Uginn eng reason fir dëst delegation',
            client_identifier: 'Client-Kennung',
            client_secret: 'Client-Geheimnes',
            time_format: 'HH:MM',
            describe_sod_rule: 'Beschreiwen den purpose vun dëst SoD Reegel...',
            enter_role_id: 'Aginn role ID',
            explain_exception: 'Explain why dëst exception is needed...',
            describe_compensating_controls: 'Beschreiwen compensating controls an place...',
            full_name: 'Voll Numm',
            min_chars: 'Min. 12 Zeechen',
            enter_override_justification: 'Aginn Begrënnung fir overriding dëst gate evaluation',
            enter_evidence_id: 'Gitt Beweis ID fir ze verlinken:',
            date_format: 'DD/MM/JJJJ',
            describe_pack_contents: 'Beschreift den Inhalt an den Zweck vum Paquet',
            select_reviewers: 'Iwwerpréiwer auswielen...',
        },
        aria_previous_page: 'Virdrun Säit',
        aria_next_page: 'Nächst Säit',
        aria_results_per_page: 'Resultater pro Säit',
        per_page_25: '25 / Säit',
        per_page_50: '50 / Säit',
        per_page_100: '100 / Säit',
        select_language: 'Sprooch wielen',
        today: 'Haut',
        tomorrow: 'Muer',
        dashboard_load_error: 'Dashboard konnt net gelueden ginn. Probéiert et nach eng Kéier.',
        enum_grade: {
            red: 'Rout',
            amber: 'Amber',
            yellow: 'Giel',
            green: 'Gréng',
        },
        enum_severity: {
            critical: 'Kritesch',
            high: 'Héich',
            medium: 'Mëttel',
            low: 'Niddreg',
        },
        enum_priority: {
            critical: 'Kritesch',
            high: 'Héich',
            medium: 'Mëttel',
            low: 'Niddreg',
        },
        enum_status: {
            pending: 'Ausschwebend',
            in_progress: 'Am Gaang',
            completed: 'Ofgeschloss',
            blocked: 'Blockéiert',
            escalated: 'Eskaléiert',
            cancelled: 'Annuléiert',
            overdue: 'Iwwerfälleg',
            not_started: 'Net ugefaangen',
            new: 'Nei',
            open: 'Oppen',
            closed: 'Zougemaach',
            acknowledged: 'Unerkannt',
            resolved: 'Geléist',
        },
        enum_department: {
            unassigned: 'Net zougewisen',
        },
        enum_form_category: {
            assessment: 'Bewäertung',
            assessment_report: 'Bewäertung Rapport',
            dpia: 'DPIA',
            authority_submission: 'Autoritéit Soumissioun',
            cross_border_transfer: 'Kräiz-Grenz Transfert',
            vendor: 'Verkeefer',
            breach: 'Verstouss',
            consent: 'Zoustëmmung',
            governance: 'Gouvernance',
            register: 'Aschreiwen',
            monitoring: 'Iwwerwachung',
            dpa: 'DPA',
            dsr: 'DSR',
            privacy: 'Privatsphär',
            incident: 'Tëschefall',
            org: 'Organisatioun',
            sector: 'Secteur',
            classification: 'Klassifikatioun',
            discovery: 'Entdeckung',
            // Sprint 63.37.0 (Stream J) — the pack categories the forms dashboard showed as raw codes.
            evidence_record: 'Evidence Record',
            application: 'Application',
            report: 'Report',
            data_mapping: 'Data Mapping',
            record: 'Record',
            evidence: 'Evidence',
            certificate: 'Certificate',
            log: 'Log',
            sandbox: 'Controlled Testing',
            decision: 'Decision',
            licence: 'Licence',
            declaration: 'Declaration',
            administrative_notice: 'Administrative Notice',
            plan: 'Plan',
            dossier: 'Dossier',
            training: 'Training',
            checklist: 'Checklist',
            training_report: 'Training Report',
            reference_list: 'Reference List',
            evidence_pack: 'Evidence Pack',
            forms: 'Forms',
            authority_response_record: 'Authority Response Record',
            request: 'Request',
            attestation: 'Attestation',
            notification: 'Notification',
            evidence_pack_manifest: 'Evidence Pack Manifest',
            email_submission_record: 'Email Submission Record',
            evidence_file: 'Evidence File',
            worksheet: 'Worksheet',
            uncategorized: 'Uncategorized',
        },
        enum_module: {
            rights_requests: 'Rechter Ufroen',
            assessments: 'Bewäertungen',
            incidents: 'Tëschefäll',
            incident_ops: 'Incident Ops',
            monitoring: 'Iwwerwachung',
            vendor_risk: 'Verkeefer Risiko',
            governance: 'Gouvernance',
            deletion_orchestration: 'Läschen',
            access_review: 'Zougang Kritik',
            consent: 'Zoustëmmung',
            consent_properties: 'Zoustëmmung Properties',
            forms: 'Formen',
            localization: 'Lokalisatioun',
            program_governance: 'Programm Gouvernance',
            privacy_scanner: 'Privatsphär Scanner',
            classification: 'Klassifikatioun',
            discovery: 'Entdeckung',
            manual: 'Manuell',
            data_mapping: 'Data Mapping',
        },
        enum_risk_level: {
            critical: 'Kritesch',
            high: 'Héich',
            medium: 'Mëttel',
            low: 'Niddreg',
        },
        enum_request_type: {
            all: 'All',
            access: 'Zougang (DSAR)',
            deletion: 'Läschen',
            correction: 'Korrektur',
            objection: 'Contestatioun',
            portability: 'Portabilitéit',
            restriction: 'Restriktioun',
            consent_withdrawal: 'Zoustëmmung Réckzuch',
            protective_measures: 'Schutzmoossnamen',
            processing_information: 'Information about Processing',
            consent_decision: 'Consent Decision',
            complaint_or_compensation: 'Complaint, Denunciation, Lawsuit or Damages Claim',
        },
        enum_locale: {
            en: 'Englesch',
            vi: 'Vietnamesesch',
            all: 'Souwuel',
        },
        enum_event_type: {
            case_created: 'Fall erstallt',
            status_changed: 'Status geännert',
            case_assigned: 'Fall zougewisen',
            note_added: 'Notiz dobäigesat',
            attachment_added: 'Uschlëss dobäigesat',
            attachment_deleted: 'Unhang geläscht',
            reference_added: 'Referenz dobäigesat',
            checklist_updated: 'Checkliste aktualiséiert',
            identity_verified: 'Identitéit verifizéiert',
            closure_summary_added: 'Zoumaache Resumé',
            approval_granted: 'Genehmegung gëtt',
            template_applied: 'Schabloun ugewannt',
            export_generated: 'Export generéiert',
            sla_warning: 'SLA Opgepasst',
            sla_overdue: 'SLA Verspéid',
            sla_flag_revisited: 'SLA flag revisited',
            third_party_involvement_changed: 'Processor or third-party involvement changed',
        },
        more: 'more',
        use: 'Use',
        ok: 'OK',
        back_to: 'Return to',
        click_to_copy: 'click to copy',
        copied: 'Copied',
        more_actions: 'More actions',
        actions_for: 'Actions for {title}',
        previous_page: 'Previous page',
        lookup_read_failed: 'A list this page offers for choosing could not be loaded, so a picker may be empty or incomplete.',
        lookup_read_subject: 'the lists offered for choosing',
        export_error: 'Export fehlgeschloen',
        you: 'You',
        export_failed: 'Export gescheitert. Probéiert et nach eng Kéier.',
        filters: 'Filteren',
        failed: 'Feelgeschloen',
        attachment_release_action: 'Release and destroy the file',
        attachment_release_title: 'Release this evidence file',
        attachment_release_body: 'Releasing destroys the file and records the destruction on the deletion proof chain. Every entry that cites the file will then say it was destroyed, and when. This cannot be undone.',
        attachment_release_basis_legend: 'Why is the file released?',
        attachment_release_basis_retention: 'Its retention period has ended',
        attachment_release_basis_erasure: 'An erasure request reached it',
        attachment_release_reason: 'Reason (required)',
        attachment_release_retention_date: 'Date the retention period ended (required)',
        attachment_release_retention_date_hint: 'No retention period is installed for this evidence, so state the date its retention ended. The file cannot be released before that date.',
        attachment_release_erasure_request: 'Erasure request (required)',
        attachment_release_erasure_placeholder: 'Choose the erasure request',
        attachment_release_erasure_none: 'No erasure request is recorded.',
        attachment_release_erasure_none_not_decided: 'An erasure request about this person is recorded but has not been decided for fulfilment yet, so none is listed. Release the file once the erasure is decided.',
        attachment_release_erasure_none_not_qualifying: 'No erasure request recorded about this person qualifies.',
        attachment_release_confirm: 'Release and destroy',
        attachment_release_releasing: 'Releasing…',
        attachment_release_done: 'The file was released and destroyed.',
        attachment_release_failed: 'The file was not released.',
        attachment_release_not_ended: 'The retention period ends on {date}, so the file cannot be released before then.',
        attachment_release_under_hold: 'The file is under a hold, so it is not released.',
        attachment_release_not_needed: 'No evidence entry rests on this file, so it is not released: delete it the ordinary way.',
        attachment_release_decided_by_register: 'This file is evidence on a register whose own retention rule decides when its evidence is released, so it is not released by a stated date.',
        attachment_release_date_installed: 'The installed retention period ends on {date}; a different date cannot be stated.',
        attachment_released_retention: 'Evidence destroyed at the end of its retention period on {date}',
        attachment_released_erasure: 'Evidence destroyed for erasure request {request}',
        attachment_released_erasure_unnamed: 'Evidence destroyed for an erasure request',
        attachment_release_erasure_label_none: 'Erasure request',
        attachment_released_on: 'Destroyed on {date}',
        attachment_released_heading: 'Files destroyed at the end of their retention or for an erasure request',
        attachment_released_retention_stated: 'Evidence destroyed at the end of its retention period on {date}, a date stated by the person who released it',
        attachment_release_installed_date: 'Date the installed retention period ends',
        attachment_release_installed_date_hint: 'This date comes from the retention period installed for this evidence, so it is not stated here.',
        attachment_release_installed_floor_hint: 'A retention period installed for other evidence citing this file ends on {date}; the date stated may not be before it.',
        attachment_release_date_before_installed: 'A retention period installed for evidence citing this file ends on {date}; a date before it cannot be stated.',
        attachment_release_in_progress: 'This file is already being released by another request, so it was not released twice.',
        // fix-sprint-20261026-0900 F10 — the 502 the release returns when the stored object cannot be deleted.
        attachment_release_storage_delete_failed: 'The stored file could not be destroyed, so the file was not released. Submit the release again to retry it.',
        attachment_release_not_computable: 'A retention period is installed for this evidence, but when it ends cannot be computed, so the file is not released.',
        attachment_release_erasure_hint: "Only requests for erasure about the person this evidence concerns are listed: decided for fulfilment, not rejected, withdrawn or closed without being fulfilled, and with the requester's identity verified.",
        attachment_release_erasure_subject_not_named: 'The register this evidence is on does not record which person it concerns, so the file cannot be released for an erasure request.',
        attachment_release_erasure_subject_not_linked: 'The entry this evidence is on is not linked to a data subject profile, so the file cannot be released for an erasure request.',
        attachment_release_erasure_different_subjects: 'This file is evidence for entries about different people, so it cannot be released for one erasure request.',
        attachment_release_erasure_other_subject: 'That erasure request is about a different person from the one this evidence concerns.',
        attachment_release_erasure_not_actionable: "That erasure request was rejected, withdrawn or closed without being fulfilled, or the requester's identity is not verified.",
        attachment_release_erasure_not_decided: 'That erasure request has not been decided for fulfilment yet, so it does not reach this evidence. Release the file once the erasure is decided.',
        attachment_release_erasure_read_required: 'Naming a rights request as the reason for a release needs the permission to read rights requests, which you do not hold.',
        attachment_release_erasure_withheld: 'You do not hold the permission to read rights requests, so no erasure request can be listed or named here. A person who holds it can release the file for an erasure request.',
        attachment_release_preview_load_error: "What this file's release rests on could not be read.",
    },
    common_statuses: {
        removed: 'Geläscht',
        draft: 'Entworf',
        active: 'Aktiv',
        inactive: 'Inaktiv',
        pending: 'Ausstehend',
        in_progress: 'Am Gaang',
        completed: 'Ofgeschloss',
        archived: 'Archivéiert',
        published: 'Verëffentlecht',
        approved: 'Genehmegt',
        rejected: 'Refuséiert',
        cancelled: 'Annulléiert',
        blocked: 'Blockéiert',
        deferred: 'Verschoben',
        not_started: 'Net ugefaangen',
        dismissed: 'Ofgewisen',
        failed: 'Gescheitert',
        in_review: 'In Iwwerpréiwen',
        deprecated: 'Ofgeschriwwen',
        scheduled: 'Geplangd',
        deploying: 'Bereetstellung',
        deployed: 'Bereetgestallt',
        paused: 'Pauséiert',
        rolled_back: 'Zréckgesat',
        received: 'Kritt',
        processing: 'Veraarbechtung',
        under_assessment: 'Ënner Bewäertung',
        implemented: 'Implementéiert',
        pending_approval: 'Ausstehend Genehmegung',
        pending_deletion: 'Ausstehend Deletion',
        on_hold: 'On Zréckstellen',
        deleted: 'Geläscht',
        expired: 'Ofgelaf',
        outdated: 'Vereelst',
        drifted: 'Ofgewach',
        decommissioned: 'Ausser Betrib',
        match: 'Iwwereneestëmmen',
        different: 'Ënnerschiddlech',
        missing: 'Feelend',
        confirmed: 'Bestätegt',
        revoked: 'Zréckgezunn',
        escalated: 'Eskaléiert',
        system: 'System',
        custom: 'Personaliséiert',
        warning: 'Warnung',
        blocking: 'Blockéierend',
        analyst: 'Analystesch',
        viewer: 'Betruechter',
        daily: 'Deeglech',
        weekly: 'Wëchentlech',
        policies: 'Richtlinnen',
        denied: 'Refuséiert',
        verified: 'Verifizéiert',
        detected: 'entdeckt',
        ignored: 'Ignoréiert',
        open: 'Oppen',
        resolved: 'Geléist',
        suspended: 'Suspendéiert',
        retired: 'Pensionéiert',
        review: 'Iwwerpréiwung',
        generated: 'Generéiert',
        exported: 'Exportéiert',
        // Sprint 63.37.0 (Stream J) — the embedded form statuses a page prints.
        finalized: 'Finalized',
        superseded: 'Superseded',
    },
    severity: {
        low: 'Niddreg',
        medium: 'Mëttel',
        high: 'Héich',
        critical: 'Kritesch',
    },
    sensitivity: {
        standard: 'Standard',
        sensitive: 'Sensibel',
        special_category: 'Speziell Kategorie',
        badge_sensitive: 'Sensibel',
    },
    change_types: {
        policy_update: 'Richtlinn Aktualiséieren',
        new_binding: 'Nei Bindung',
        remove_binding: 'Ewechhuelen Bindung',
        configuration_change: 'Konfiguratioun Änneren',
        banner_update: 'Banner Aktualiséieren',
    },
    comparison_types: {
        full: 'Voll',
        banner_only: 'Nëmmen Banner',
        purposes_only: 'Zwecker Only',
        legal_bases_only: 'Rechtlech Bases Only',
        consent_settings: 'Zoustëmmung Astellungen',
    },
    lifecycle_stages: {
        active: 'Aktiv',
        archived: 'Archivéiert',
        pending_deletion: 'Ausstehend Deletion',
        deleted: 'Geläscht',
        on_hold: 'On Zréckstellen',
        creation: 'Creation',
        exception_hold: 'On Hold',
    },
    object_types: {
        personal_data: 'Perséinlech Donnéeën',
        consent_record: 'Zoustëmmung Datesat',
        user_account: 'Benotzer Kont',
        audit_log: 'Audit Protokoll',
        document: 'Dokument',
        backup: 'Sécherheetskopie',
    },
    risk_categories: {
        data_breach: 'Donnéeën Verstouss',
        unauthorized_access: 'Unauthorized Zougang',
        data_loss: 'Donnéeën Loss',
        regulatory_non_compliance: 'Regulatoresch Non-Compliance',
        third_party_risk: 'Third-Party Risiko',
        operational: 'Operationell',
        technical: 'Technesch',
        legal: 'Rechtlech',
    },
    mitigation_types: {
        technical: 'Technesch',
        organizational: 'Organisatoresch',
        contractual: 'Kontraktuell',
        procedural: 'Prozedural',
    },
    safeguard_types: {
        encryption_in_transit: 'Verschlësselung am Transit',
        encryption_at_rest: 'Verschlësselung am Rescht',
        access_controls: 'Zougang Kontrollen',
        contractual_clauses: 'Vertraglech Klauselen',
        data_minimization: 'Dateminiméierung',
        pseudonymization: 'Pseudonymiséierung',
        audit_rights: 'Audit Rechter',
        breach_notification: 'Verstouss Notifikatioun',
        data_localization: 'Daten Lokalisatioun',
        other: 'Aner',
    },
    scoring_methods: {
        weighted_average: 'Weighted Duerchschnëtt',
        simple_average: 'Simple Duerchschnëtt',
        maximum: 'Maximum',
        manual: 'Manuell',
    },
    dpa_status: {
        not_required: 'Net erfuerderlech',
        required: 'Erfuerderlech',
        in_progress: 'Am Gaang',
        completed: 'Ofgeschloss',
        signed: 'Ënnerschriwwen',
    },
    disposal_methods: {
        anonymize: 'Anonymiséieren',
        pseudonymize: 'Pseudonymiséieren',
        aggregate: 'Aggregéieren',
        delete: 'Läschen',
    },
    dashboard: {
        workspace: {
            tabs_label: 'Dashboard sections',
            overview_heading: 'Your compliance posture',
            overview_description: 'Score, recommendations, coverage and operational signals.',
            tasks_heading: 'Turn priorities into progress',
            tasks_description: 'Ownership, overdue work, upcoming tasks and department delivery.',
            insights_heading: 'The full compliance picture',
            insights_description: 'Filings, third-party risk, assessments, incidents and regulatory activity.',
            visible_widgets: '{count} widgets visible',
            widgets_view: 'Widgets',
            config_description: 'Choose which widgets to show and their order. Changes are saved when you apply them.',
            search_widgets: 'Find a widget',
            search_widgets_placeholder: 'Search widgets',
            density: 'Widget spacing',
            density_comfortable: 'Comfortable',
            density_compact: 'Compact',
            default_tab: 'Open dashboard on',
            apply_configuration: 'Apply configuration',
            cancel_configuration: 'Cancel',
            move_up: 'Move {widget} up',
            move_down: 'Move {widget} down',
            enabled_count: '{enabled} of {total} items enabled',
            empty_section: 'No widgets are visible in this section. Use Configure Dashboard to choose available widgets.',
        },
        title: 'Iwwersiicht',
        no_data: 'Keng Iwwersiichtsdonnéeën disponibel.',
        no_data_heading: 'Nach keng Live-Donnéeën',
        no_data_detail: 'Live figures could not be loaded. The layout is shown; values will appear once the connection is restored.',
        consent_coverage: 'Zoustëmmungsowierkung',
        ctia_filings: 'CTIA-Meldungen',
        next_dpia_review: 'Nächst DPIA-Iwwerpréiwung',
        next_review_none: 'Keng geplangt',
        /*
         * Sprint 65.4.0 (AC5) — why the next-review tile has four states.
         *
         * A blank date reads as "nothing due soon". Measured, it almost always
         * meant something else: the only production writer of
         * `grc_dpia_records.next_review_at` was the demo seed, so every real
         * tenant saw the same dash. The tile now says which absence it is.
         */
        next_review_no_dpias: 'No DPIAs recorded',
        next_review_cadence_absent: 'No review cadence declared',
        next_review_not_scheduled: 'Not yet scheduled',
        incident_mps_filings: 'MPS-Meldungen fir Virfäll',
        compliance_score: 'Konformitéitsscore',
        grade: 'Grad',
        no_score: 'Nach kee Konformitéitsscore berechent.',
        rights_requests: 'Ufroe fir Rechter',
        open: 'Op',
        sla_breached: 'SLA iwwerschratt',
        sla_approaching: 'SLA geschwënn fälleg',
        closed_month: 'Zougemaach (Mount)',
        incidents: 'Tëschefäll & Dateschutzvirfäll',
        active: 'Aktiv',
        breaches: 'Dateschutzvirfäll',
        overdue_tasks: 'Iwwerfälleg Aufgaben',
        breach_72h_notice: '72-Stonn reglementaresche Notifikatioun Frist gëllt fir aktiv Verstéiss',
        recommendations: 'Recommandatiounen',
        take_action: 'Maacht Aktioun',
        drift_events: 'Drift & Genehmegungen',
        open_events: 'Oppen Evenementer',
        pending_approvals: 'Ausstehend Genehmegungen',
        monitoring: 'Iwwerwaachungsaufgaben',
        programs: 'Programmer',
        overdue: 'Iwwerfälleg',
        pending: 'Ausstehend',
        ric_cases: 'RIC-Fäll',
        coverage: 'Datenofdeckung',
        systems: 'Systemer',
        activities: 'Aktivitéiten',
        vendors: 'Fournisseuren',
        dpias: 'DPIAen',
        open_gaps: 'oppe Lücken',
        view_as_table: 'Als Tabell uweisen',
        view_as_chart: 'Als Diagramm uweisen',
        table_metric_header: 'Kennzuel',
        table_value_header: 'Wert',
        announce_chart_view: 'Diagrammusiicht gett ugewisen',
        announce_table_view: 'Tabellusiicht gett ugewisen',
        section_core_metrics: 'Karnkennzuelen',
        section_task_management: 'Aufgabeverwaltung',
        section_compliance_insights: 'Compliance-Ablechter',
        configure_dashboard: 'Dashboard konfigureeiren',
        config_col_active: 'Aktiv',
        config_col_widget: 'Widget',
        collapse_section: 'Zouklapppen',
        expand_section: 'Opklapppen',
        reset_to_defaults: 'Op Standard zrécksetzen',
        preferences_saved: 'Dashboard-Astellungen gespäichert',
        preferences_reset: 'Dashboard op Rollenstandard zréckgesat',
        preferences_load_error: 'Dashboard-Astellunge konnten net gelueden ginn',
        preferences_save_error: 'Dashboard preferences could not be saved.',
        factor_avg_system_score: 'Duerchschnëtt System Score',
        factor_avg_vendor_score: 'Duerchschnëtt Verkeefer Score',
        factor_processing_coverage: 'Veraarbechtungsaktivitéit Ofdeckung',
        factor_ropa_completeness: 'RoPA Vollständegkeet',
        factor_drift_penalty: 'Drift Evenementer Strof',
        rec_improve_system_gov: 'Verbesseren System Gouvernance Dokumentatioun',
        rec_improve_vendor_gov: 'Verbesseren Verkeefer Gouvernance Dokumentatioun',
        rec_document_processing: 'Dokumentveraarbechtungsaktivitéite fir opgedeckte Systemer',
        rec_generate_ropa: 'Generéiere RoPA records fir aktiv Veraarbechtungsaktivitéiten',
        rec_resolve_drift: 'Léisen oppe kritesch an héich Schwieregkeet Drift Eventer',
        score_good: 'Gutt',
        score_fair: 'Akzeptabel',
        score_poor: 'Schlecht',
        score_critical: 'Kritesch',
        drag_to_reorder: 'Zéien fir ëmzesortéieren',
        widget_moved: 'Widget verschoben',
        press_space_to_grab: "Dréckt d'Leertast fir ze gräifen, Pfailtasten fir ze réckelen, Leertast fir ofzeleeën",
        reorder_instructions: 'Benotzt Drag-and-Drop oder Tastekierzel fir Widgeten ëmzesortéieren',
        page_title: 'Compliance Dashboard',
        welcome_back: 'Welcome back, {name}',
        subtitle: 'Your real-time view of compliance posture, risk, and progress across your organization.',
        section_overview: 'Overview',
        kpi_strip_label: 'KPI overview strip',
        today_label: 'Today',
        avatar_aria: 'Signed in as {name}',
        no_prior_period: 'No prior period yet',
        period_current: 'Current period',
        gauge_aria: '{score} out of 100, {label}',
        donut_aria: '{total} vendors by risk level',
        bar_aria: '{label}: {value} percent',
        config_saving: 'Saving…',
        task_widgets: {
            tasks_overview: 'Aufgaben Iwwersiicht',
            pending: 'Erwaardung',
            in_progress: 'Am Fortschrëtt',
            overdue: 'Iwwerhaapt',
            done_7d: 'fäerdeg (7d)',
            by_priority: 'Duerch Prioritéit',
            view_all_tasks: 'View All Aufgaben',
            upcoming_tasks: 'Upëff Aufgaben (nächst 7 Deeg)',
            no_upcoming: 'Keng Aufgaben an den nächsten 7 Deeg.',
            view_calendar: 'View Kalenner',
            overdue_alerts: 'Iwwerhaapt',
            no_overdue: 'Keng iwwerlaascht Aufgaben. Alles ass op der Streck.',
            assigned_to: 'Zougewisen an',
            unassigned_actor: 'Unassigned',
            untitled_task: 'Untitled task',
            view_all_overdue: 'View All Iwwerfall',
            notifications: 'Rezent Notifikatiounen',
            unread: 'unread',
            no_notifications: 'Keng Notifikatiounen. Dir sidd all agefaang!',
            view_all_notifications: 'View All Notifikatiounen',
            load_error: 'Daten konnten net gelueden ginn.',
            day_late: 'Dag spéit',
            days_late_plural: 'Deeg spéit',
            due_today: 'Géint haut',
            just_now: 'just elo',
            minutes_short: 'm',
            hours_short: 'h',
            days_short: 'd',
            cross_org_stats: 'Kräiz-Organisatioun Statistiken',
            by_organization: 'Vun Organisatioun',
            overdue_across_orgs: 'iwwerdriwwen Aufgaben iwwer Organisatiounen',
            overdue_count_label: '{count} overdue tasks',
            total: 'Ganzen',
            tasks: 'tasks',
            organization: 'Organisatioun',
            current_org: 'Aktuell Organisatioun',
            all_orgs: 'All orgs',
            all_organizations: 'All Organisatiounen',
            filter_by_org: 'Filter no Organisatioun',
            cross_org: 'Kräiz-org',
            empty_calendar: 'Keng Aufgaben fir dës Period. Erstellt Är éischt Aufgab oder kontrolléiert Är Filteren.',
            empty_my_tasks: 'Dir hutt keng Aufgaben zougewisen. Super Aarbecht!',
            empty_overdue: 'Keng iwwerlaascht Aufgaben. Alles ass op der Streck.',
            empty_notifications: 'Keng Notifikatiounen. Dir sidd all agefaang!',
            empty_workflows: 'Keng Workflows konfiguréiert. Browst Templates fir unzefänken.',
            remediation_pipeline: 'Tasks & Remediation Pipeline',
            no_open_tasks: 'No open tasks — the pipeline is clear.',
            open_tasks: 'Open tasks',
        },
        my_tasks_widget: {
            title: 'Meng Aufgaben',
            no_tasks: 'Keng Aufgaben un Iech zougewisen.',
            load_error: 'Aufgabe konnten net gelueden ginn.',
            overdue: 'iwwerfälleg',
            remaining: 'iwwreg',
            view_all: 'All Aufgaben uweisen',
        },
        my_section_status_widget: {
            title: 'Mäin Sektiounsstatus',
            overall_completion: 'Gesamt',
            sections_count: 'Sektiounen',
            no_sections: 'Keng Sektiounen un Är Abteilung zougewisen.',
            load_error: 'Sektiounsstatus konnt net gelueden ginn.',
            department_tasks_title: 'Aufgabe vun der Abteilung',
            no_department_tasks: 'Är Abteilung huet keng Aufgaben.',
            department_tasks_error: "D'Aufgabe vun der Abteilung konnten net gelueden ginn.",
            no_department: 'Dir sidd keng Abteilung zougeuerdnet.',
        },
        revision_requests_widget: {
            title: 'Revisiounsufroën',
            no_revisions: 'Keng Revisiounsufroën. All Sektiounen sinn um richtege Wee.',
            load_error: 'Revisiounsufroën konnten net gelueden ginn.',
        },
        consent_summary: {
            title: 'Zoustëmmung',
            active: 'Aktiv',
            withdrawn_30d: 'zréckgezunn (30 d)',
            expiring_7d: 'Ausfall (7d)',
        },
        transfer_assessments: {
            title: 'Transfermaart Bewäertungen',
            open: 'Oppen',
            approved: 'Genehmegt',
            gaps_flagged: 'Lücken markéiert',
        },
        breach_notifications: {
            title: 'Verstouss Notifikatiounen',
            deadline_72h: '72h Termin',
            submitted: 'Erlaabt',
            pending: 'Erwaardung',
        },
        authority_requests: {
            title: 'Autoritéit Demanden',
            pending: 'Erwaardung',
            overdue: 'Iwwerhaapt',
            responded_30d: 'geäntwert (30 d)',
        },
        data_subjects_widget: {
            title: 'Donnéeën Sujeten',
            total: 'Ganzen',
            high_risk: 'Héich Risiko',
            active_30d: 'Aktiv (30 d)',
        },
        audit_activity_summary: {
            title: 'Audit Aktivitéit',
            events_today: 'Evenementer haut',
            events_7d: 'Evenementer (7d)',
            unique_actors_30d: 'Eenzegaarteg Schauspiller (30d)',
        },
        onboarding_progress: {
            title: 'Onboarding Fortschrëtter',
            completion_pct: 'Komplett',
            status_label: 'Status',
            current_step: 'Aktuelle Schrëtt',
            status: {
                in_progress: 'Am Fortschrëtt',
                completed: 'Ofgeschloss',
                skipped: 'Iwwersprongen',
                not_started: 'Net ugefaangen',
            },
        },
        sector_overlay_reports_due: {
            title: 'Fälleg Berichter vu Sektor-Iwwerlagerungen',
        },
        kpi: {
            framework_coverage: 'Framework Coverage',
            open_actions: 'Open Actions',
            high_priority: '{count} high priority',
            vendor_health: 'Vendor Health',
            at_risk: '{count} at risk',
            open_count: '{count} open',
            active_count: '{count} active',
            grade_label: 'Grade: {grade}',
            consent_coverage_hint: 'of active consent workflows',
            consent_coverage_source_web_consent_records: 'of web consent records, last 30 days',
            consent_coverage_source_web_consent_events: 'of web consent events, last 30 days',
            consent_coverage_source_consent_records: 'of consent records, last 30 days',
            of_installed_frameworks: 'of installed frameworks',
            no_frameworks_installed: 'No frameworks installed',
            unavailable: 'Not available',
            consent_no_records: 'No consent records in the last 30 days',
            tile_hint: {
                no_score: 'Not calculated yet',
                load_error: 'Could not be loaded',
                no_frameworks_installed: 'Mandatory: none installed',
                vendor_at_risk: '{at_risk} of {total} high or critical',
                vendor_load_error: 'Could not be read',
                no_vendor_data: 'No vendor data',
                consent_no_records: 'No records, last 30 days',
                consent_source_web_consent_records: 'of web consent records',
                consent_source_web_consent_events: 'of web consent events',
                consent_source_consent_records: 'of consent records',
                not_reported: 'Not reported',
                delta_since: 'points since {period}',
            },
        },
        summary_reloaded: 'Dashboard reloaded.',
        scope_incidents: 'Incidents still open. Breaches are the subset flagged as a personal-data breach.',
        scope_breach_response: 'The same open breaches, by notification and remediation state.',
        scope_breach_notifications: 'Notification filings. Due in 72h counts those with a filing deadline inside the next 72 hours; submitted and pending count every filing in that state, whatever its deadline.',
        vendor_health_hint: '{at_risk} of {total} vendors at high or critical risk',
        vendor_health_load_error: 'Vendor data could not be read.',
        figure_not_reported: 'This figure was not reported in the last dashboard read.',
        framework_coverage: {
            title: 'Framework Coverage',
            overall: 'Overall coverage',
            none: 'The installed frameworks declare no requirements yet.',
            none_installed: 'No frameworks installed. Install a regulatory pack to see its coverage here.',
            requirements_mapped: '{mapped} of {total} requirements mapped',
            no_requirements: 'No requirements declared',
            overall_denominator: 'across {count} frameworks that declare requirements',
            no_requirements_count: '{count} installed frameworks declare no requirements yet',
            no_requirements_count_one: '1 installed framework declares no requirements yet',
            showing_lowest: 'Showing the {shown} lowest-covered of {total}',
            gaps: '{count} open gaps',
            view_all: 'View frameworks',
            load_error: 'Framework coverage could not be loaded.',
        },
        automation_impact: {
            title: 'Automation Impact',
            scope: 'The controls your installed frameworks declare as applicable to this organization, and how many of them have automated work recorded.',
            load_error: 'Automation impact could not be loaded.',
            no_frameworks_installed: 'No installed framework is one your organization must comply with',
            not_declared: 'No installed framework states which of its controls apply to this organization',
            profile_incomplete: 'Record your organization profile to see which controls apply',
            none_applicable: 'No declared control applies to this organization',
            no_emitter: 'No automated work is recorded against controls yet',
            no_point: 'No completed month has a figure to show yet',
            figure_hint: '{automated} of {applicable} applicable controls automated in {period}',
            figure_hint_one: '{automated} of 1 applicable control automated in {period}',
            delta_since: 'percentage points since {period}',
            bucket_applicable: 'Applicable',
            bucket_out_of_scope: 'Declared not to apply to you',
            bucket_not_declared: 'Applicability not declared',
            bucket_profile_incomplete: 'Awaiting your organization profile',
            applicable_from: 'The applicable controls come from {count} installed frameworks',
            applicable_from_one: 'The applicable controls come from 1 installed framework',
            review_profile: 'Review your organization profile',
            tile_hint: {
                load_error: 'Could not be loaded',
                no_frameworks_installed: 'No mandatory framework installed',
                not_declared: 'Applicability not declared',
                profile_incomplete: 'Organization profile needed',
                none_applicable: 'No declared control applies',
                no_emitter: 'No automated work recorded',
                no_point: 'No month to show yet',
                figure: '{automated} of {applicable} controls, {period}',
                figure_one: '{automated} of 1 control, {period}',
            },
        },
        recommendations_widget: {
            title: 'Key Recommendations',
            view_all: 'View all ({count})',
            priority_high: 'High',
            priority_medium: 'Medium',
            priority_low: 'Low',
            none: 'No recommendations. Every score factor is within its healthy range.',
        },
        rights_status: {
            title: 'Rights Requests',
            open_requests: 'Open requests',
        },
        module_status: {
            title: 'Module Status',
            enabled: 'Enabled',
            modules_count: '{count} modules',
            modules_count_one: '{count} module',
            none: 'No modules are enabled for this organization.',
        },
        recent_activity: {
            title: 'Recent Activity',
            view_all: 'View all',
            empty: 'No recent activity.',
            load_error: 'Recent activity could not be loaded.',
        },
        persona_widgets: {
            framework_filter: 'No Kader filteren',
            all_frameworks: 'All Kaderen',
            filing_lifecycle: 'Andreechtungsläbenszyklus',
            filing_pipeline: 'Andreechtungspipeline',
            draft: 'Entworf',
            internal_review: 'Intern Iwwerpréiwung',
            approved: 'Genehmegt',
            submitted: 'Erlaabt',
            supplement_requested: 'Zousaz ugefrot',
            resubmitted: 'Representéiert',
            accepted: 'Akzeptéiert',
            rejected: 'Refuséiert',
            closed: 'Zougemaach',
            deadline: 'Frist',
            days_remaining: 'Deeg bleiwen',
            days_overdue: 'Deeg zefridden',
            no_filings: 'Keng aktiv Andreechtungen.',
            department_consolidation: 'Abteilungskonsolidéierung',
            departments_completed: 'Abteilungen ofgeschloss',
            departments_pending: 'Abteilunge waarden',
            departments_overdue: 'Abteilungen iwwerfälleg',
            no_consolidation_cases: 'Keng abteilungsiwwergräifend Fäll a Gang.',
            vendor_risk: 'Liwweranten- an Drëttpartei-Risiko',
            pending_assessment: 'Bewäertung waart',
            overdue_questionnaires: 'Iwwerfälleg Froebéigen',
            dpa_review_queue: 'DPA-Iwwerpréiwungswuerteschlang',
            risk_distribution: 'Risiko Verdeelung',
            total_vendors: 'Gesamt Liwweranten',
            by_risk_level: 'No Risikoniveau',
            no_vendor_data: 'Keng Liwweranterisikodaten disponibel.',
            discovery_summary: 'Daten Entdeckung',
            discovery_classification: 'Dateentdeckung an -klassifizéierung',
            active_scans: 'Aktiv Scans',
            labels_pending: 'Etiketten Erwaardung Kritik',
            exceptions_unresolved: 'Ausnahmen ongeléist',
            pending_review: 'Iwwerpréiwung waart',
            exceptions: 'Ausnahmen',
            approved_labels: 'Geneemegt Labelen',
            total_classified: 'Gesamt klassifizéiert',
            no_discovery_data: 'Keng Entdeckungsdaten disponibel.',
            ai_governance: 'KI-Systemsteierung',
            systems_inventory: 'Systemer am Inventar',
            systems_in_inventory: 'Systemer am Inventar',
            classification_breakdown: 'Klassifikatioun Decompte',
            dossier_completion: 'Dossier-Fäerdegstellung',
            by_classification: 'No Klassifizéierung',
            labeling_status: 'Kennzeechnungsstatus',
            not_started: 'Net ugefaangen',
            no_ai_data: 'Keng KI-Steierungsdaten disponibel.',
            obligations_complete: 'Obligations Complete',
            obligations_complete_of: '{complete} of {applicable} applicable obligations',
            no_obligations_apply: 'No applicable obligation recorded',
            ai_register_restricted: 'Your role cannot read the AI system register.',
            breach_response: 'Verletztungs- an Tëschefallreaktioun',
            active_breaches: 'Aktiv Verletzungen',
            notification_status: 'Notifikatiounsstatus',
            remediation_progress: 'Behiewungsfortschrëtt',
            supplement_loops: 'Ergänzungsschläifen',
            no_breach_data: 'Keng aktiv Verletzungsfäll.',
            audit_readiness: 'Préiwungs- a Bewäertungsberedschaft',
            assessments: 'Bewäertungen',
            gap_findings: 'Lückefondën',
            evidence_completion: 'Noweisfäerdegstellung',
            export_ready: 'Exportbereet',
            export_ready_yes: 'Ready',
            export_ready_no: 'Not ready',
            unspecified_framework_group: 'No framework recorded',
            not_ready: 'Net bereet',
            no_audit_data: 'Keng Préiwungsberedschaftsdaten disponibel.',
            framework_label_vn_pdpl: 'VN PDPL 2025',
            framework_label_vn_cybersecurity: 'VN Cybersecherheet 2018',
            framework_label_vn_ecommerce: 'VN E-Commerce 2023',
            framework_label_vn_telecom: 'VN Telekommunikatioun 2023',
            framework_label_vn_ai: 'VN KI-Gesetz 2025',
            framework_label_gdpr: 'DSGVO',
            framework_label_unspecified: 'Onspezifizéierten',
            my_department: 'Meng Abteilung',
            my_tasks: 'Meng Aufgaben',
            section_status: 'Sektiounsstatus',
            revision_requests: 'Revisiounsufroën',
            completion: 'Fäerdegstellung',
            reason: 'Grond',
            requested_at: 'Ugefrot den',
            no_tasks_assigned: 'Keng Aufgaben zougewisen.',
            no_sections: 'Keng Sektiounen Ärer Abteilung zougewisen.',
            no_revisions: 'Keng Revisiounsufroën.',
            load_error: 'Ausgefall Daten ze lueden.',
            vendor_health_needs_attention: '{count} vendors require immediate attention',
            review_vendors: 'Review Vendors',
            total_vendors_label: 'Total Vendors',
        },
    },
    connectors: {
        nav: {
            instances: 'Instanzen',
            credentials: 'Umeldungsinformatiounen',
            event_bus: 'Evenement-Bus',
            definitions: 'Definitiounen',
            brds: 'BRD-Erfaassung',
            security_reviews: 'Sécherheetsiwwerpréiwungen',
            poc_scorecards: 'POC-Bewäertungskaarten',
            automation_planning: 'Automatiséierungsplanung',
            system_inventory: 'Systeminventar',
            traceability_matrix: 'Réckverfollegbarkeetmatrix',
            gap_register: 'Léckeregister',
        },
        instances: {
            title: 'Konnektor-Instanzen',
            page_title: 'Konnektoren',
            add: 'Instanz derbäisetzen',
            test: 'Verbindung testen',
            sync_now: 'Elo synchroniséieren',
            new_instance: 'Nei Konnektor Instanz',
            no_instances: 'Keng Konnektor Instanzen fonnt',
            instance_count: '{count} Instanz(en)',
            confirm_delete: 'Läschen Konnektor Instanz "{name}"?',
            all_connector_types: 'All Konnektor Typen',
            select_connector: 'Auswielen eng Konnektor...',
            fetch_error: 'Konnt net fetch Konnektor Instanzen. Probéiert et nach eng Kéier.',
            fetch_definitions_error: 'Konnt net fetch Konnektor definitions. Probéiert et nach eng Kéier.',
            create_error: 'Konnt net create Konnektor Instanz',
            test_error: 'Konnt net test Konnektor. Probéiert et nach eng Kéier.',
            status_error: 'Konnt de Konnektorstatus net aktualiséieren. Probéiert et nach eng Kéier.',
            delete_error: 'Konnt net delete Konnektor Instanz. Probéiert et nach eng Kéier.',
            confirm_disable: 'Disable "{name}"? Sync will stop until the instance is reactivated.',
            credentials_label: 'Credentials',
            instance_created: 'Connector instance created',
            base_url_label: 'Base URL',
            base_url_hint: "The https address of this connection's endpoint. Test sends the connector type's declared health check to a path on it.",
            base_url_invalid: 'Enter an https address, such as https://api.example.com.',
            edit_instance: 'Edit Connector Instance',
            edit_instance_action: 'Edit',
            instance_updated: 'Connector instance updated',
            update_error: 'The connector instance could not be updated.',
            no_credentials_hint: 'No credentials available. Create one in the Credential Vault first.',
            test_passed: 'Test passed',
            test_failed: 'Test failed',
            status_changed: 'Status aktualiséiert',
            reactivate: 'Reaktivéieren',
            mark_ready_for_testing: 'Mark ready for testing',
            marked_ready: 'Marked ready for testing. No connection check was made: this connector type declares no health check.',
            test_healthy: 'Health check passed ({detail})',
            test_unhealthy: 'Health check failed ({detail})',
            probe_detail_expected_status: 'the endpoint answered with the expected status',
            probe_detail_unexpected_status: 'the endpoint answered with a status other than the expected one',
            probe_detail_timeout: 'no answer before the timeout',
            probe_detail_network_error: 'the endpoint could not be reached',
            probe_detail_target_refused: "the endpoint's address is not allowed",
            record_sync: 'Record sync result',
            record_sync_title: 'Record sync result: {name}',
            record_sync_note: 'Record the outcome of a sync run that ran outside the platform. The platform does not run the sync itself.',
            sync_outcome: 'Outcome',
            sync_outcome_success: 'Success',
            sync_outcome_partial: 'Partial',
            sync_outcome_failure: 'Failure',
            records_processed: 'Records processed',
            records_created: 'Records created',
            records_failed: 'Records failed',
            sync_recorded: 'Sync result recorded.',
            sync_record_error: 'Failed to record the sync result.',
            recording: 'Recording…',
            refusal_base_url_invalid: 'This base URL cannot be used: enter the https address of an endpoint the platform may call, such as https://api.example.com. Private and local addresses are not allowed.',
            refusal_base_url_missing: 'This connection has no base URL, so its health check has nowhere to go. Use Edit to add the base URL, then test again.',
            refusal_base_url_missing_ask_admin: 'This connection has no base URL, so its health check has nowhere to go. Ask an administrator who can edit connections to add the base URL, then test again.',
            refusal_base_url_invalid_ask_admin: "This connection's base URL cannot be used: it must be the https address of an endpoint the platform may call, and private and local addresses are not allowed. Ask an administrator who can edit connections to correct it, then test again.",
        },
        credentials: {
            title: 'Umeldungsinformatiounen-Tresor',
            page_title: 'Konnektor-Umeldedaten',
            add: 'Umeldungsinformatioun derbäisetzen',
            rotate: 'Rotéieren',
            expired: 'Ofgelaf',
            expiring: 'Leeft geschwënn of',
            orphaned: 'Verwaist',
            new_credential: 'Nei Umeldedaten',
            no_credentials: 'Keng credentials fonnt',
            no_expiry: 'No Oflaf',
            linked_connectors: 'Verbonnen Konnektoren',
            expires_at: 'Leeft of den',
            value_label: 'Wäert * (write-only)',
            confirm_delete_title: 'Läschen bestätegen',
            confirm_delete_message: 'Dësen Umeldungsdaten läschen? Verlinkte Konnektoren verléieren den Zougang.',
            rotate_title: 'Umeldungsdaten rotéieren',
            new_credential_value: 'Neie Wäert vun den Umeldungsdaten',
            fetch_error: 'Konnt net load credentials. Probéiert et nach eng Kéier.',
            health_error: 'Konnt net load Zoustand. Probéiert et nach eng Kéier.',
            create_error: 'Konnt net create credential',
            rotate_error: 'Konnt net rotate credential',
            delete_error: 'Konnt net delete credential',
        },
        event_bus: {
            title: 'Evenement-Bus',
            page_title: 'Event-Bus',
            events: 'Evenementer',
            subscriptions: 'Abonnementer',
            dlq: 'Dead-Letter-Schlaang',
            publish: 'Evenement publizéieren',
            retry: 'Nach eng Kéier probéieren',
            dismiss: 'Ofweisen',
            search_events: 'Sich Evenementer...',
            filter_event_type: 'Filteren no Evenement type...',
            event_type: 'Evenement Typ',
            source_module: 'Quell Modul',
            timestamp: 'Zäitstempel',
            delivery_status: 'Liwwerung Status',
            correlation_id: 'Korrelatioun ID',
            tracking_id: 'Tracking-ID',
            delivery_status_hint: 'De Liwwerstatus gëtt automatesch aktualiséiert wann Evenementer veraarbecht ginn.',
            no_events: 'Keng Evenementer fonnt',
            total_with_count: '{count} gesamt',
            prev: 'Zréck',
            new_subscription: '+ Nei Abonnement',
            subscriber_type: 'Abonnent Typ',
            subscriber_id: 'Abonnent ID',
            event_type_pattern: 'Evenement Typ Muster',
            event_type_pattern_placeholder: 'z.B. Tëschefall.*',
            filter_expression: 'Filteren Expression',
            filter_expression_placeholder: 'Fakultativ filter',
            subscriber_type_hint: 'Wielt wéi Evenementer geliwwert ginn: Webhook schéckt HTTP-Ufroen, E-Mail schéckt Notifikatiounen, Warteschlaang leet an eng Messagewarteschlaang.',
            subscriber_id_hint: "Gitt d'Destinatioun fir Evenementer an. Fir Webhooks, benotzt eng URL. Fir E-Mail, benotzt eng Adress. Fir Warteschlaangen, benotzt den Numm vun der Warteschlaang.",
            event_type_pattern_hint: 'Evenementer no Typ mat Platzhalter matchen. Benotzt * fir all Segment ze matchen (z.B. incident.* matcht incident.created, incident.resolved).',
            filter_expression_hint: 'Fakultativ. Evenementer no Inhalt weider filteren (z.B. severity=high).',
            subscriber_id_placeholder_webhook: 'z.B. https://är-app.com/webhook',
            subscriber_id_placeholder_email: 'z.B. alarmer@är-firma.com',
            subscriber_id_placeholder_queue: 'z.B. evenement-warteschlaang',
            event_pattern: 'Evenement Muster',
            filter: 'Filteren',
            enabled: 'Aktivéiert',
            delivery_stats: 'Liwwerung Stats',
            no_subscriptions: 'Keng subscriptions fonnt',
            on: 'ON',
            off: 'OFF',
            delivered_failed: '{delivered} delivered / {failed} gescheitert',
            confirm_delete_subscription: 'Läschen dëst Abonnement?',
            unresolved: 'Ongeléist',
            resolved: 'Geléist',
            event_id: 'Evenement ID',
            subscription_id: 'Abonnement ID',
            error_code: 'Feeler Code',
            error_message: 'Feeler Message',
            attempts: 'Versich',
            last_failed: 'Lescht Failed',
            no_dlq_entries: 'Keng dead letter entries fonnt',
            dlq_count: '{count} entry/entries gesamt',
            fetch_events_error: 'Konnt net fetch Evenementer. Probéiert et nach eng Kéier.',
            fetch_subscriptions_error: 'Konnt net fetch subscriptions. Probéiert et nach eng Kéier.',
            fetch_dlq_error: 'Konnt net fetch DLQ entries. Probéiert et nach eng Kéier.',
            create_subscription_error: 'Konnt net create Abonnement',
            toggle_subscription_error: 'Konnt net toggle Abonnement',
            delete_subscription_error: 'Konnt net delete Abonnement',
            retry_error: 'Retry gescheitert',
            dismiss_error: 'Oflehnen gescheitert',
            event_published: 'Evenement publizéiert',
            publish_event_error: 'Evenement konnt net publizéiert ginn. Probéiert et w.e.g. nach eng Kéier.',
            priority_label: 'Prioritéit',
            priority_low: 'Niddreg',
            priority_normal: 'Normal',
            priority_high: 'Héich',
            payload_helper_text: 'Gitt Evenementdaten als JSON an. Beispill: {"incident_id": "INC-001", "severity": "high"}',
            subscription_created: 'Abonnement erstallt',
            subscription_toggled: 'Abonnement ëmgeschalt',
            subscription_deleted: 'Abonnement geläscht',
            dlq_retried: 'DLQ-Message nei probéiert',
            dlq_dismissed: 'DLQ-Message verworf',
        },
        definitions: {
            title: 'Connector Definitiounen',
            page_title: 'Konnektordefinitionen',
            add: 'Add Definitioun',
            new_definition: 'Neie Connector Definitioun',
            edit_definition: 'Connector Definitioun änneren',
            no_definitions: 'Keng Verbindungsdefinitioune fonnt',
            definition_count: '{count} Definitioun(en)',
            all_categories: 'All Kategorien',
            select_category: 'Wielt eng Kategorie ...',
            capabilities: 'Kënnen',
            capabilities_hint: 'Fäegkeeten (komma-getrennt)',
            capabilities_placeholder: 'z.B. liesen, schreiwen, synchroniséieren',
            created: 'Connector Definitioun erstallt',
            updated: 'Connector Definitioun aktualiséiert',
            fetch_error: "Konnt d'Verbindungsdefinitioune net erofhuelen. Probéiert w.e.g. nach eng Kéier.",
            create_error: 'Konnt Konnektordefinitioun erstellen',
            update_error: 'Feeler beim Aktualiséiere vun der Konnektordefinition',
            health_check: 'Health check',
            health_check_none: 'None declared',
            health_check_declare: 'This connector type declares a health check',
            health_check_method: 'Method',
            health_check_path: 'Path',
            health_check_expected_status: 'Expected HTTP status',
            health_check_timeout: 'Timeout (seconds)',
            health_check_hint: "Test sends this one request to the connection's base URL (its base_url setting) and records it as healthy only when the answer has the expected status. Without a health check, Test only marks a connection ready for testing.",
        },
        brds: {
            title: 'BRD-Erfaassung',
            page_title: 'BRD-Erfaassung',
            create_brd: 'BRD erstellen',
            brd_title: 'BRD-Titel',
            description: 'Beschreiwung',
            requirements: 'Ufuerderungen',
            status: 'Status',
            stakeholders: 'Bedeelegt',
            connector_label: 'Konnektor',
            select_connector: 'Konnektor auswielen...',
            approval: 'Geneemegung',
            no_brds_found: 'Keng BRDen fonnt',
            decision_records: 'Entscheedungsprotokoller',
            create_decision: 'Entscheedung erstellen',
            alternatives_considered: 'Iwwerleegte Alternativen',
            alternative_name: 'Alternativnumm',
            reason_rejected: 'Grond fir Ofleenung',
            add_alternative: 'Alternativ derbäisetzen',
            remove_alternative: 'Alternativ {name} ewechhuelen',
            alternatives: 'Alternativen',
            rationale: 'Begrënnung',
            selected_connector: 'Gewielte Konnektor',
            comma_separated_names: 'Komma-getrennte Nimm',
            brd_created: 'BRD erstallt',
            decision_record_created: 'Entscheedungsdatesaz erstallt',
            no_decision_records_found: 'Keng Entscheedungsdatesäz fonnt',
            aria_brds_table: 'BRDen',
            aria_decision_records_table: 'Entscheedungsdatesäz',
            aria_approve: '{name} guttstreidegen',
            aria_archive: '{name} archivéieren',
            brd_title_help: 'E kuerzen, mënschlech liesbare Numm fir dës BRD - z.B. "Jira Ticketing Integratioun", "Okta SSO Rollout".',
            brd_title_placeholder: 'z.B. Jira Ticketing Integratioun',
            description_help: 'De Geschäftsproblem léist dëse Connector. Wéi eng Resultat erwaart Dir eemol et live ass?',
            description_placeholder: 'z.B. Automatiséiert Tëschefall Ticket Kreatioun aus Iwwerwachung Alarmer sou datt On-Call bannent 60er Säite gëtt.',
            connector_help: 'Fakultativ. Wielt e Connector fir dësen BRD ass - loosst eidel wann de Connector nach net gewielt gouf.',
            requirements_help: 'Komma-getrennt Lëscht vu Must-Have Capabilities - z.B. "OAuth2 Login, Webhook Liwwerung, Audit Export".',
            requirements_placeholder: 'OAuth2 Login, Webhook Liwwerung, Audit Export',
            stakeholders_help: 'Komma-getrennten Nimm oder E-Maile vu Leit, déi sech mussen ofmellen.',
            selected_connector_help: 'Wielt de Connector op dës Entscheedung gëllt.',
            rationale_help: 'Firwat dëse Connector gewielt gouf. Deckt déi entscheedend Faktoren sou datt en zukünftege Lieser den Uruff versteet.',
            rationale_placeholder: "z.B. Wielt Jira well et schonn eis Ticket Geschicht hält an d'Equipe ass op et trainéiert.",
            alternatives_considered_help: 'Aner Stecker evaluéiert a firwat se net gewielt goufen. Füügt Verteidegung fir Audit.',
            alternative_name_placeholder: 'z.B. ServiceNow',
            reason_rejected_placeholder: 'z.B. Käschten 3 × méi héich, kee Virdeel fir eise Gebrauchsfall',
        },
        security_reviews: {
            title: 'Sécherheetsiwwerpréiwungen',
            page_title: 'Sécherheetsiwwerpréiwungen',
            create_review: 'Iwwerpréiwung erstellen',
            connector_name: 'Konnektornumm',
            connector_label: 'Konnektor',
            select_connector: 'Konnektor auswielen...',
            reviewer: 'Préiwer',
            select_reviewer: 'Préiwer auswielen...',
            findings: 'Feststellungen (eng pro Zeil)',
            risk_level: 'Risikoniveau',
            risk_level_placeholder: 'z.B. héich, mëttel, niddreg',
            notes: 'Notizen',
            review_status: 'Iwwerpréiwungsstatus',
            checklist: 'Checklëscht',
            privacy_impact: 'Dateschutzauswirkung',
            approval_gate: 'Geneemegungspaart',
            no_reviews_found: 'Keng Iwwerpréiwungen fonnt',
            review_created: 'Sécherheetsiwwerpréiwung erstallt',
            create_control_mapping: 'Kontrollzouweissung erstellen',
            control_mapping_created: 'Kontrollzouweissung erstallt',
            connector_id_label: 'Konnektor-ID',
            control_id_label: 'Kontroll-ID',
            control_name_label: 'Kontrollnumm',
            aria_approve: '{name} guttstreidegen',
            aria_reject: '{name} ofleenen',
            scan_imported: 'Scan erfollegräich importéiert',
            import_scan: 'Scan importéieren',
            scan_format: 'Scanformat',
            scan_data: 'Scandaten',
            import_results: 'Importresultater',
            recommendations: 'Empfeelungen',
            auto_populated_from: 'Auto-populéiert vun {tool}',
            manual_entry: 'Manuell Entrée',
            automation_type: 'Automatioun Typ',
            select_automation_type: 'Wielt Automatiséierungstyp ...',
            automation_full: 'Voll - Connector automatiséiert dës Kontroll komplett',
            automation_partial: 'Deelweis - Connector dréit Beweiser bäi; Mënschen handelen nach ëmmer',
            automation_monitoring: 'Iwwerwachung nëmmen - Connector observéiert, duerchsetze net',
            control_id_help: 'Benotzt de Code vun Ärem Konformitéitsschema (zB ISO-27001-A.12.1, SOC2-CC6.1 oder Är intern Kontroll ID).',
            control_name_help: 'Kuerz mënschlech liesbar Numm (zB "Netzzougang Restriktioun").',
            description_label: 'Beschreiwung',
            load_sarif_example: 'Luet SARIF Beispill',
            load_json_example: 'Lueden Benotzerdefinéiert JSON Beispill',
            scan_format_help: 'SARIF ass den OASIS Standard deen vun de meeschte Scanner emittéiert gëtt (Snyk, CodeQL, Semgrep). Benotzt Benotzerdefinéiert JSON fir Tools déi SARIF net emittéieren.',
            scan_data_help: "Paste déi voll Scanoutput, lued eng .sarif/.json Datei erop, oder klickt op e Luet Beispill Knäppchen fir d'Form ze gesinn.",
            upload_file_label: 'Eroplueden .sarif oder .json Fichier',
            invalid_scan_json: "Konnt d'Scandaten net parséieren als JSON. Kuckt no Syntaxfehler no beim markéierte Charakter.",
            save_as_review: 'Späicheren als Sécherheetsbewäertung',
            discard_preview: 'Virschau ofzeginn',
            preview_hint: 'Dëst ass eng Virschau - näischt ass nach gespäichert. Klickt op "Späicheren als Sécherheetsbewäertung" fir eng pendend Iwwerpréiwung ze kreéieren déi mat dëse Wäerter virausgefëllt ass.',
            reviewer_required_for_save: 'Wielt e Rezensor fir dëse Scan als Sécherheetsrevisioun ze späicheren.',
        },
        poc_scorecards: {
            title: 'POC-Bewäertungskaarten',
            page_title: 'POC-Scorecards',
            create_scorecard: 'Bewäertungskaart erstellen',
            connector_label: 'Konnektor',
            select_connector: 'Konnektor auswielen...',
            vendor_name: 'Ubiddernumm',
            recommendation: 'Empfeelung',
            recommendation_placeholder: 'z.B. Virufueren, Net virufueren, Brauch weider Evaluatioun',
            criteria: 'Bewäertungskrittären',
            criterion_name: 'Krittärnumm',
            score: 'Punktzuel',
            max_score: 'Maximalpunktzuel',
            notes: 'Notizen',
            add_criterion: 'Krittär derbäisetzen',
            remove_criterion: 'Krittär {name} ewechhuelen',
            total_score: 'Gesamtpunktzuel',
            api_quality: 'API-Qualitéit',
            reliability: 'Zouverlässegkeet',
            support_quality: 'Supportqualitéit',
            cost_score: 'Käschtebewäertung',
            overall_score: 'Gesamtbewäertung',
            evidence: 'Nowiiser',
            no_scorecards_found: 'Keng Bewäertungskaarten fonnt',
            scorecard_created: 'POC-Scorecard erstallt',
            connector_help: 'Wielt de Connector fir dës POC Evaluatioun ass.',
            vendor_name_help: 'De Verkeefer oder Produkt dat bewäert gëtt - z.B. "Okta", "Datadog", "Snowflake".',
            vendor_name_placeholder: 'z.B. Okta, Datadog, Snowflake',
            recommendation_help: 'Är lescht Uruff no der Evaluatioun. Halt et kuerz an entscheedend.',
            criteria_help: 'Score de Verkeefer op all Evaluatiounskriterium. Füügt eng Zeil pro Critère derbäi (API Qualitéit, Zouverlässegkeet, Ënnerstëtzung, Käschten, etc.).',
            criterion_name_help: 'Wat Dir maacht - z.B. "API Qualitéit", "Zouverlässegkeet", "Support", "Käschte".',
            criterion_name_placeholder: 'z.B. API Qualitéit',
            score_help: 'Score ausgezeechent fir dëse Critère. Däerf net de Max Score iwwerschreiden.',
            max_score_help: "Top Score verfügbar fir dëse Critère. Benotzt déiselwecht Skala iwwer Reihen sou datt d'Gesamtzuel vergläichbar ass.",
            notes_help: 'Fakultativ. Füügt Beweiser, Viraussetzungen oder Linken - alles wat de Score erkläert.',
        },
        common_labels: {
            aria_filter_instances: 'Konnektorinstanzen filteren',
            aria_filter_definitions: 'Konnektordefinitionen filteren',
            aria_filter_credentials: 'Umeldedaten filteren',
            aria_filter_events: 'Evenementer filteren',
            aria_filter_dlq: 'Dead-Letter-Schlaang filteren',
            aria_instance_summary: 'Konnektorinstanz-Zesummefaassung',
            aria_active_connectors: 'Aktiv Konnektoren',
            aria_error_connectors: 'Fehlerhaft Konnektoren',
            aria_credential_health: 'Zesummefaassung vum Umeldedaten-Status',
            aria_items_per_page: 'Elementer pro Säit',
            show_password: 'Passwuert weisen',
            hide_password: 'Passwuert verstoppen',
            payload_json_label: 'Notzlaascht (JSON)',
            payload_invalid_json: 'Notzlaascht muss gëlteg JSON sinn',
            placeholder_event_type: 'z.B. incident.created',
            placeholder_source_module: 'z.B. monitoring',
            placeholder_version: 'z.B. 1.0',
        },
        statuses: {
            configuring: 'Konfiguréieren',
            testing: 'Testen',
            active: 'Aktiv',
            paused: 'Pauséiert',
            error: 'Feeler',
            disabled: 'Behënnert',
            draft: 'Entworf',
            approved: 'Genehmegt',
            archived: 'Archivéiert',
            pending: 'Erwaardung',
            rejected: 'Refuséiert',
        },
        system_types: {
            application: 'Applikatioun',
            database: 'Datebank',
            service: 'Service',
            infrastructure: 'Infrastruktur',
            third_party: 'Third party',
        },
        hosting_types: {
            on_premise: 'On-Premise',
            cloud: 'Wollek',
            hybrid: 'Hybrid',
            managed: 'Geréiert',
        },
        system_statuses: {
            active: 'Aktiv',
            decommissioned: 'Ausser Betrib',
            planned: 'Geplangt',
            under_review: 'An der Iwwerpréifung',
        },
        automation_statuses: {
            planning: 'Planung',
            in_progress: 'Am Gaang',
            completed: 'Ofgeschloss',
            on_hold: 'Op Halt',
        },
        gap_types: {
            functional: 'Funktionell',
            technical: 'Technesch',
            security: 'Sécherheet',
            compliance: 'Konformitéit',
            process: 'Prozess',
        },
        severity_labels: {
            critical: 'Kritesch',
            high: 'Héich',
            medium: 'Mëttel',
            low: 'Niddreg',
        },
        gap_statuses: {
            open: 'Oppen',
            in_progress: 'Am Gaang',
            mitigated: 'Ofgeschwächt',
            closed: 'Zougemaach',
            accepted: 'Akzeptéiert',
        },
        verification_statuses: {
            not_started: 'Net ugefaangen',
            in_progress: 'Am Gaang',
            verified: 'Verifizéiert',
            failed: 'Fehlgeschloen',
        },
        subscriber_types: {
            webhook: 'Webhook',
            internal: 'Intern',
            email: 'E-Mail',
            queue: 'Schlaang',
        },
        credential_types: {
            password: 'Passwuert',
            api_key: 'API-Schlëssel',
            oauth2_client: 'OAuth2-Client',
            certificate: 'Zertifikat',
            token: 'Token',
            ssh_key: 'SSH-Schlëssel',
            imap_credentials: 'IMAP-Umeldungsdaten',
        },
        categories: {
            hr: 'HR',
            crm: 'CRM',
            erp: 'ERP',
            cloud: 'Wollek',
            security: 'Sécherheet',
            itsm: 'ITSM',
            custom: 'Benotzerdefinéiert',
        },
        delivery_statuses: {
            pending: 'Ausstehend',
            delivered: 'Geliwwert',
            partial: 'Deelweis',
            failed: 'Fehlgeschloen',
        },
        automation_planning: {
            title: 'Automatiséierungsplanung',
            page_title: 'Automatiséierungsplanung',
            program_name: 'Programmnumm',
            program_status: 'Status',
            description: 'Beschreiwung',
            linked_artifacts: 'Verknëppten Artefakten',
            create_program: 'Programm erstellen',
            edit_program: 'Programm änneren',
            no_programs_found: 'Keng Programmer fonnt',
            program_created: 'Programm erstallt',
            program_updated: 'Programm aktualiséiert',
            target_date: 'Zildatum',
            owner: 'Besëtzer',
            select_status: 'Status wielen...',
            aria_programs_table: 'Automatiséierungsprogrammer',
            aria_edit: '{name} änneren',
            export_programs: 'Programmer exportéieren',
            title_label: 'Programm Titel',
            title_help: 'E kuerzen, mënschlech liesbare Numm fir den Automatisatiounsprogramm - z.B. "Q3 Zwëschefall-zu-Jira Rollout", "PDPL Zougang-Ufro Automatisatioun".',
            title_placeholder: 'z.B. Q3 Tëschefall-zu-Jira Ausrollung',
            description_help: "Wat dëse Programm deckt an d'Resultat déi Dir erwaart wann et live ass. Fakultativ awer recommandéiert.",
            description_placeholder: 'z.B. Wire Iwwerwachung Alarmer an Jira sou datt On-Call innerhalb vun de 60er Säite gëtt.',
            owner_label: 'Programm Besëtzer',
            owner_help: 'Déi Persoun verantwortlech fir dëse Programm. Si kréien Notifikatiounen iwwer Fortschrëtter a Paarte.',
            select_owner: 'Wielt e Besëtzer ...',
            frameworks_in_scope: 'Kaderen am Ëmfang',
            frameworks_in_scope_help: "Konformitéitskader ënnerstëtzt dëse Programm. Hëlleft den Audit Trail ze weisen wéi eng Reglementer d'Automatisatioun regéiert gëtt.",
            select_frameworks: 'Wielt Kaderen ...',
            status_draft: 'Entworf',
            status_active: 'Aktiv',
            status_completed: 'Ofgeschloss',
            status_archived: 'Archivéiert',
            change_status: 'Status änneren',
            status_updated: 'Status aktualiséiert',
            aria_change_status: 'Status änneren fir {name}',
        },
        system_inventory: {
            title: 'Systeminventar',
            page_title: 'Systeminventar',
            system_name: 'Systemnumm',
            system_type: 'Systemtyp',
            hosting: 'Hosting',
            status: 'Status',
            description: 'Beschreiwung',
            create_system: 'System erstellen',
            edit_system: 'System änneren',
            no_systems_found: 'Keng Systemer fonnt',
            system_created: 'System erstallt',
            system_updated: 'System aktualiséiert',
            select_type: 'Typ wielen...',
            select_hosting: 'Hosting wielen...',
            select_status: 'Status wielen...',
            owner: 'Besëtzer',
            department: 'Departement',
            data_categories: 'Datenkategorien',
            data_categories_placeholder: 'Komma-getrennt, z. B. PII, Finanzen, Gesondheet',
            jurisdiction: 'Juridictioun',
            jurisdiction_placeholder: 'z. B. EU, US, Global',
            interfaces: 'Schnittstellen',
            interfaces_placeholder: 'Komma-getrennt, z. B. REST API, SFTP, JDBC',
            owner_placeholder: 'Wielt e verantwortleche Besëtzer...',
            department_placeholder: 'Wielt déi verantwortlech Organisatiounseenheet...',
            system_name_hint: 'Benotzt de geschäftlech unerkannte System- oder Applikatiounsnumm.',
            system_type_hint: "Klassifizéiert de System, fir datt d'Reporting ähnlech Assets gruppéiere kann.",
            hosting_hint: 'Identifizéiert, wou de System fir Residenz, Sécherheet a Fournisseursbewäertung leeft.',
            status_hint: 'Setzt de Liewenszyklusstatus fest, dee vun Iwwerpréiwungen, Exporter an Auditbeweiser benotzt gëtt.',
            owner_hint: "Wielt den aktive Benotzer aus, dee fir d'Systemgovernance a Follow-up-Aufgaben verantwortlech ass.",
            department_hint: "Benotzt d'Organisatiounseenheet, déi fir de Betrib oder d'Verwaltung vun dësem System verantwortlech ass.",
            data_categories_hint: "Lëscht d'Datenklassen op, déi vun dësem System veraarbecht ginn, getrennt duerch Kommaen.",
            jurisdiction_hint: 'Erfaasst déi wichtegst juristesch oder Hosting-Juridictioun fir de Compliance-Mapping.',
            interfaces_hint: 'Lëscht Connector- oder Integratiounsweeër op, déi zum Datenaustausch benotzt ginn.',
            description_hint: "Faasst zesummen, wat de System mécht a firwat et fir d'Datemap wichteg ass.",
            owner_department_required: 'Besëtzer an Departement sinn erfuerderlech.',
            aria_systems_table: 'Systeminventar',
            aria_edit: '{name} änneren',
            export_systems: 'Systemer exportéieren',
            aria_filter_systems: 'Filter systems',
            governance_context: 'Governance context',
            governance_context_hint: 'Confirm ownership, jurisdiction, data categories, and interfaces before saving.',
            governance_context_summary: 'This context drives data mapping, residency review, and connector accountability.',
        },
        traceability_matrix: {
            title: 'Réckverfollegbarkeetmatrix',
            page_title: 'Réckverfollegbarkeetmatrix',
            brd_selector: 'BRD wielen',
            requirement: 'Ufuerderung',
            linked_artifact: 'Verknëppten Artefakt',
            verification_status: 'Verifizéierungsstatus',
            verified_by: 'Verifizéiert vun',
            create_entry: 'Andrag erstellen',
            edit_entry: 'Andrag änneren',
            no_entries_found: 'Keng Andreg fonnt',
            entry_created: 'Andrag erstallt',
            entry_updated: 'Andrag aktualiséiert',
            select_brd: 'Eng BRD wielen...',
            select_status: 'Status wielen...',
            aria_matrix_table: 'Réckverfollegbarkeetmatrix',
            aria_edit: 'Andrag fir {name} änneren',
            export_matrix: 'Matrix exportéieren',
            brd_selector_hint: 'Select the Business Requirement Document (BRD) this entry belongs to.',
            fetch_brds_error: 'Failed to load BRD list',
            requirement_id_hint: 'Unique reference code from the BRD or regulatory source (e.g. REQ-001).',
            requirement_text: 'Requirement Text',
            requirement_text_hint: 'Detailed description of the requirement as defined in the source document.',
            verification_method: 'Verification Method',
            verification_method_hint: 'Process used to confirm the requirement is met (e.g. Unit Test, Audit).',
            verification_method_placeholder: 'e.g. test, inspection, review',
            verification_status_hint: 'Current verification state of the requirement implementation.',
            requirement_id: 'Ufuerderungs-ID',
        },
        gap_register: {
            title: 'Léckeregister',
            page_title: 'Léckeregister',
            gap_title: 'Lécketitel',
            gap_type: 'Lécketyp',
            severity: 'Schwéiergrad',
            status: 'Status',
            description: 'Beschreiwung',
            remediation_plan: 'Behiewungsplang',
            assigned_to: 'Zougedeelt un',
            due_date: 'Fällegkeetsdatum',
            create_gap: 'Lécke erstellen',
            edit_gap: 'Lécke änneren',
            no_gaps_found: 'Keng Lécke fonnt',
            gap_created: 'Lécke erstallt',
            gap_updated: 'Lécke aktualiséiert',
            select_type: 'Typ wielen...',
            select_severity: 'Schwéiergrad wielen...',
            select_status: 'Status wielen...',
            aria_gaps_table: 'Léckeregister',
            aria_edit: '{name} änneren',
            export_gaps: 'Lécken exportéieren',
            gap_title_hint: 'Provide a concise name for the identified gap.',
            gap_type_hint: 'Select the nature of the gap (e.g., missing coverage or integration issue).',
            severity_hint: 'The impact of this gap on compliance and operations.',
            status_hint: 'Current stage of the gap resolution lifecycle.',
            owner_hint: 'The person responsible for tracking and remediating this gap.',
            description_hint: 'Detailed context regarding the gap, its root cause, and impact.',
            mitigation_plan_hint: 'Steps planned or taken to address and close the gap.',
            linked_requirement_ids_hint: 'Comma-separated requirement codes this gap affects.',
            linked_system_ids_hint: 'Comma-separated system IDs associated with this gap.',
            gap_types: {
                coverage: 'Coverage',
                capability: 'Capability',
                integration: 'Integration',
                compliance: 'Compliance',
            },
            linked_ids_placeholder: 'Comma-separated IDs',
            linked_requirement_ids: 'Linked Requirement IDs',
            linked_system_ids: 'Linked System IDs',
            mitigation_plan: 'Mitigation Plan',
            select_owner: 'Select owner',
        },
        regulatory: {
            system_inventory_governance_info: 'System inventory aligns with Data Law enterprise governance. System types map to regulatory categories, hosting classification determines jurisdiction-specific controls. Use framework linkage to connect systems to applicable regulatory frameworks.',
            automation_planning_workflow_info: 'Automation programs align with compliance workflow obligations defined by applicable regulatory frameworks. Framework scope determines which requirements the program addresses.',
            traceability_regulatory_source_info: 'Each traceability entry connects a BRD requirement to its regulatory source. Verification status tracks whether implementation satisfies the regulatory obligation.',
            security_reviews_control_mapping_info: 'Security reviews evaluate framework-specific controls applicable to each connector. Risk level captures assessed exposure, findings document deviations from control requirements.',
            gap_register_framework_info: 'Gap Register captures compliance gaps per regulatory framework. Use framework filter to view gaps for a specific framework. Severity levels align with framework risk classification.',
            poc_scorecards_criteria_info: 'POC scorecards evaluate connectors against regulatory criteria. Criteria structure supports weighted scoring with evidence notes. Vendor linkage connects to vendor record.',
            evidence_trail_info: 'All connector module actions generate audit events for cross-module evidence trail integrity. CSV exports preserve referential integrity.',
        },
        system_inventory_extra: {
            framework_linkage: 'Kaderverknëppung',
            framework_linkage_placeholder: 'z.B. GDPR, ISO 27001, NIS2',
        },
        automation_planning_extra: {
            framework_scope: 'Kaderomfang',
            framework_scope_placeholder: 'z.B. GDPR, PCI-DSS',
            procedure_linkage: 'Prozedurverknëppung',
            procedure_linkage_placeholder: 'z.B. SOP-001, PROC-DATA-003',
        },
        traceability_matrix_extra: {
            regulatory_source: 'Reglementaresch Quell',
            regulatory_source_placeholder: 'z.B. GDPR Art. 30, ISO 27001 A.8',
        },
        security_reviews_extra: {
            control_mapping_col: 'Kontrollmapping',
            control_mappings_section: 'Kontroll Mappings',
            no_control_mappings: 'Keng Kontroll Mappings nach. Klickt op "Kontrollmapping erstellen" fir eng derbäi ze ginn.',
            automation_type_col: 'Automatisatioun',
        },
        gap_register_extra: {
            framework_filter: 'Kader',
            framework_id: 'Kader-ID',
            framework_id_placeholder: 'z.B. GDPR, ISO 27001',
            all_frameworks: 'All Kaderen',
        },
        poc_scorecards_extra: {
            criteria_count: 'Kritären',
        },
        export_labels: {
            export_brds: 'BRDen exportéieren',
            export_decision_records: 'Entscheedungsregister exportéieren',
            export_security_reviews: 'Sécherheetsbewäertungen exportéieren',
            export_poc_scorecards: 'POC-Scorekaarten exportéieren',
            export_instances: 'Export Instanzen',
            export_definitions: 'Export Definitiounen',
            export_credentials: 'Export Umeldungsinformatiounen',
            export_events: 'Export Evenementer',
            exporting: 'Gëtt exportéiert...',
            export_success: 'Export ofgeschloss',
            export_error: 'Export gescheitert',
        },
    },
    // fix-sprint-20260506-2121-followup F16 — the contracts module's own
    // section (it was filed under vendor_risk).
    contracts: {
        obligation_extraction: {
            extract_label: 'Extrait Obligatiounen',
            extracting: 'Extrait…',
            results_heading: 'Extraitéiert Obligatiounen ({count})',
            tokens_label: '≈ {count} Tokens',
            no_obligations: 'Keng Verpflichtungsmuster an dësem Kontrakt Text festgestallt.',
            col_class: 'Klass',
            col_summary: 'Resumé (editable)',
            col_excerpt: 'Klausel Auszuch',
            col_due_date: 'Termin (editable)',
            col_priority: 'Prioritéit',
            due_date_placeholder: 'YYYY-MM-DD',
            class_notice: 'Notifikatioun Period',
            class_audit: 'Audit richteg',
            class_insurance: 'Versécherung',
            class_sla: 'Äntwert SLA',
            class_breach: 'Verstouss Notifikatioun',
            class_renewal: 'Erneierung Fënster',
            citations_label: 'Zitater',
            accept_hint: 'Akzeptéieren wäert eng Aufgab pro Obligatioun erstellen (Cap 8) mat der Prioritéit + Verfallsdatum gewisen.',
            accept_label: 'Akzeptéieren Obligatiounen',
            reject_label: 'Rapport refuséieren',
            resolve_summary: 'Opléisung: {status} · {tasks} Aufgab(en) erstallt',
            error_generic: 'Konnt Obligatiounen net extrahéieren. Probéiert nach eng Kéier oder kontrolléiert de Kontrakt Text.',
            summary_input_label: 'Summary for obligation {n}: {classification}',
            due_date_input_label: 'Due date for obligation {n}: {classification}',
            tasks_dropped: '{count} task(s) could not be created. Create them from the obligations above.',
        },
    },
    vendor_risk: {
        risk_acceptance_reasoning: {
            draft_label: 'Entworf Begrënnung',
            drafting: 'Begrënnung ausschaffen ...',
            results_heading: 'AI-entworf Begrënnung ({risk_level})',
            tokens_label: '≈ {count} Tokens',
            narrative_label: 'Justifikatioun narrativ',
            controls_heading: 'Kompenséieren Kontrollen',
            no_controls: 'Keng kompenséierend Kontrollen proposéiert.',
            citations_label: 'Zitater',
            accept_label: 'Akzeptéieren Begrënnung',
            reject_label: 'Entworf refuséieren',
            accept_hint: "Akzeptéieren notéiert d'Begrënnung iwwer de persistéierten Entworf fir IAT Lineage. D'narrativ + Kontrollen aktualiséieren den Akzeptanzrekord net automatesch - Bedreiwer Paste-Back ass erfuerderlech.",
            resolve_summary: 'Resolutioun: {status}',
            error_generic: "Konnt d'Begrënnung net ausschaffen. Probéiert nach eng Kéier oder kontrolléiert de Risiko Akzeptanzstatus.",
        },
        redline_assistant: {
            run_label: 'Redline mat Bibliothéik',
            running: 'Redline lafen ...',
            results_heading: 'Redline Suggestiounen ({count})',
            tokens_label: '≈ {count} Tokens',
            no_suggestions: 'Keng Standardklausel Lücken festgestallt.',
            jurisdiction_label: 'Juridictioun',
            col_kind: 'Aart',
            col_severity: 'Gravitéit',
            col_title: 'Virschlag',
            col_excerpt: 'Gepasst Auszuch',
            col_apply: 'Gëlle',
            kind_missing: 'Vermësst Standard',
            kind_weaker: 'Méi schwaach wéi Standard',
            kind_jurisdiction: 'Juridictioun Iwwerlagerung',
            proposed_label: 'Proposéiert Text',
            citations_label: 'Zitater',
            accept_label: 'Akzeptéieren ausgewielt',
            reject_label: 'Rapport refuséieren',
            partial_label: 'Wielt Auswiel',
            accept_hint: "Wielt Auswiel notéiert déi gewielte Virschléi iwwer den Entworf fir IAT Lineage. Keng automatesch Ännerung gëtt op de Kontrakttext ugewannt - de Bedreiwer fiert d'Redline.",
            resolve_summary: 'Resolutioun: {status} · {count} Virschlag(en) applizéiert',
            error_generic: 'Konnt keng Redline produzéieren. Probéiert nach eng Kéier oder kontrolléiert de Kontraktstatus.',
            clause_library_link: 'Klauselbibliothéik verwalten',
            text_source_extracted: 'the extracted contract text',
            text_source_inline: 'the text supplied for this scan',
            text_source_summary: 'the contract description',
            scanned_text_note: 'Scanned {source} ({count} characters). The uploaded contract file is not read.',
            rescan_button: 'Scan again',
            rescan_label: 'Scan for another jurisdiction (country code)',
            jurisdiction_provided: '{code} (chosen for this scan)',
            jurisdiction_no_overlay: '{code} (no jurisdiction overlay exists for it; only your clause library was checked)',
            jurisdiction_assumed: '{code} (assumed: the contract records no jurisdiction)',
        },
        clause_library: {
            page_title: 'Klausel Bibliothéik',
            add_label: 'Klausel addéieren',
            edit_label: 'Edit',
            delete_label: 'Läschen',
            slug_label: 'Schleck',
            title_label: 'Titel',
            body_label: 'Kierper',
            jurisdiction_label: 'Juridictioun',
            tags_label: 'Tags (komma getrennt)',
            must_contain_label: 'Must-enthale Markéierer (komma getrennt)',
            severity_label: 'Gravitéit wann vermësst',
            no_clauses: 'Nach keng Klauselen. Füügt een fir de Redline Assistent ze säen.',
            save_label: 'Späicheren',
            cancel_label: 'Ofbriechen',
            load_error: "Konnt d'Klauselbibliothéik net lueden.",
            save_error: "Konnt d'Klausel net späicheren.",
            delete_error: "Konnt d'Klausel net läschen.",
            delete_confirm: 'Klausel "{slug}" läschen? Dëst kann net réckgängeg gemaach ginn.',
        },
        subprocessor_impact: {
            assess_label: 'Bewäerten Impakt',
            assessing: 'Impakt bewäerten ...',
            results_heading: 'Ënnerprozessor Impakt ({severity})',
            tokens_label: '≈ {count} Tokens',
            risk_delta_label: 'Risiko Delta',
            notification_heading: 'Client Notifikatioun',
            subject_label: 'Sujet',
            deadline_label: 'Notifikatioun Termin',
            body_label: 'Notifikatioun Kierper',
            required_fields_label: 'Felder vun der Notifikatioun (Art.28 (2))',
            mitigation_heading: 'Mitigatioun Schrëtt',
            citations_label: 'Zitater',
            accept_label: 'Akzeptéieren Bewäertung',
            reject_label: 'Refus Bewäertung',
            accept_hint: 'Akzeptéieren wäert eng Client Notifikatioun Aufgab erstellen mat der SLA Frist als Verfallsdatum.',
            resolve_summary: 'Resolutioun: {status}{task}',
            error_generic: "Konnt dës Ënnerprozessor änneren net bewäerten. Probéiert nach eng Kéier oder kontrolléiert d'Ännerungsrecord.",
        },
        onboarding_evidence: {
            collect_label: 'Run AI Beweissammlung',
            collecting: 'Beweiser sammelen ...',
            results_heading: 'Beweissammlung ({tier})',
            tokens_label: '≈ {count} Tokens',
            findings_heading: 'Bestehend Beweiser',
            missing_heading: 'Vermësst Artefakte',
            no_findings: 'Keng existent Beweiser festgestallt.',
            no_missing: 'Keng erfuerderlech Artefakte fehlen fir dëse Risikoniveau.',
            next_step_label: 'Nächste Schrëtt',
            class_dpa: 'Dateveraarbechtungsvertrag',
            class_soc2: 'SOC 2 Bericht',
            class_iso27001: 'ISO 27001 Zertifikat',
            class_insurance: 'Cyber ​​Versécherungspolice',
            class_incident_history: 'Virdrun Tëschefall',
            accept_hint: 'Accepting creates a task for each missing artefact (up to 5) and records each document found as draft evidence on the vendor. Draft evidence stays unapproved until a person with evidence approval rights reviews it. Prior incidents are listed for your review only.',
            accept_label: 'Akzeptéieren Bericht',
            reject_label: 'Rapport refuséieren',
            resolve_summary: 'Resolution: {status} · {tasks} task(s) created · {evidence} draft evidence record(s) attached',
            error_generic: 'Konnt AI Beweissammlung net lafen. Probéiert nach eng Kéier oder kontrolléiert den Onboarding Fall Status.',
        },
        no_permission_title: 'Net genuch Permissiounen',
        no_permission_message: 'Dir hutt keng Erlaabnis fir Zougang zu Verkeefer Risikomanagement.',
        unknown_vendor: 'Onbekannt Verkeefer',
        nav: {
            dashboard: 'Risikoiwwersiicht',
            scoring: 'Risikobewäertung',
            questionnaires: 'Froebugen',
            contracts: 'Kontrakter',
            gates: 'Geneemegungsporten',
            reviews: 'Liwwerantbewäertungen',
            signals: 'Risikosignaler',
            reassessments: 'Neibewäertungen',
            portfolio: 'Risikoportfolio',
            acceptances: 'Risikoacceptanz',
            review_routing: 'Iwwerpréiwungsroutage',
            onboarding_cases: 'Onboarding-Fäll',
            sub_processor_register: 'Ënnerdatenveraarbechter-Register',
        },
        dashboard: {
            title: 'Liwwerantrisikoiwwersiicht',
            page_title: 'Verkeefer Risiko Dashboard',
            loading: 'Lueden vun dashboard Donnéeën...',
            risk_tier_header: 'Risiko Tier',
            count_header: 'Grof',
            percentage_header: 'Prozentsaz',
            sections_nav: 'Verkeefer Risiko Rubriken',
            urgent_expiry: 'Dréngend: leeft bannent 14 Deeg',
            no_previous_tier: 'Keng virdrun Tierm',
            view_as_chart: 'Kuckt als Diagramm',
            view_as_table: 'Kuckt als Dësch',
            showing_chart: 'Diagramm-Usiicht',
            showing_table: 'Datetagell-Usiicht',
            load_error: 'Konnt net load Ubidder Risiko dashboard',
            risk_distribution: 'Risiko Distribution Overview',
            recent_tier_changes: 'Rezent Stuf Ännerungen',
            no_tier_changes: 'Keng rezent Stufenännerungen.',
            review_kpis: 'Iwwerpréiwen KPIs',
            total_vendors_reviewed: 'Gesamt Ubidder Iwwerpréift',
            reviews_by_outcome: 'Iwwerpréifungen no Resultat',
            contract_coverage: 'Kontrakt Ofdeckung',
            total_vendors: 'Gesamt Ubidder',
            with_active_dpa: 'Mat Aktiv DPA',
            without_dpa: 'Ouni DPA',
            dpa_coverage: 'DPA Ofdeckung',
            active_contracts_by_type: 'Aktiv Kontrakten no Typ',
            expiring_contracts: 'Oflafend Kontrakten (Next 90 Days)',
            no_expiring_contracts: 'No contracts expiring within den next 90 Deeg.',
            vendor: 'Ubidder',
            previous_tier: 'Vireg Stuf',
            current_tier: 'Aktuell Stuf',
            changed_at: 'Geännert At',
            expires_at: 'Leeft of den',
            days_remaining: 'Deeg Rescht',
            days: 'Deeg',
            vendors_label: 'Ubidder',
            no_data: 'No Donnéeën',
            critical: 'Kritesch',
            high: 'Héich',
            medium: 'Mëttel',
            low: 'Niddreg',
            expiring_acceptances_heading: 'Oflafend Risiko-Akzeptanzen',
            expiring_30d_label: 'Leeft no 30 Deeg of',
            expiring_60d_label: 'Leeft no 60 Deeg of',
            expiring_90d_label: 'Leeft no 90 Deeg of',
        },
        scoring: {
            title: 'Risikobewäertung',
            page_title: 'Verkeefer Risiko Scoring',
            filter_scores: 'Filter Risiko Scores',
            filter_by_tier: 'Filter no Risikoniveau',
            scores_pagination: 'Risiko Scores Paginatioun',
            config_dimensions: 'Punktzuel Dimensiounen Configuratioun',
            scores_loaded: '{count} Risiko Scores gelueden',
            weight_must_equal_100: 'Gesamtgewiicht muss gläich sinn 100 (aktuell: {current})',
            out_of_10: 'aus 10, bewäert {tier}',
            dimension_name: 'Dimensioun {index} Numm',
            dimension_weight: '{name} Gewiicht (Prozentsaz)',
            must_equal_100: 'muss gläich 100',
            config_title: 'Scoring Konfiguratioun',
            scores_title: 'Ubidder Risiko Bewäertungen',
            new_score_title: 'Bewäertung eng Ubidder',
            new: 'Nei Bewäertung',
            vendor_id: 'Ubidder ID',
            risk_tier: 'Risiko Stuf',
            score: 'Bewäertung',
            scored_at: 'Bewäert den',
            no_scores: 'Keng Risiko scores fonnt.',
            all_tiers: 'All Stufen',
            load_config_error: 'Konnt net load Risiko scoring Konfiguratioun',
            update_config: 'Bewäertungskonfiguratioun aktualiséieren',
            update_config_error: 'Konnt net update Risiko scoring Konfiguratioun',
            load_scores_error: 'Konnt net load Ubidder Risiko scores',
            calculate_error: 'Konnt net calculate Ubidder Risiko score',
            weight: 'Gewiicht',
            score_range: 'Bewäertung (1-5)',
            method: 'Method',
        },
        questionnaires: {
            title: 'Froebugen',
            page_title: 'Liwwerant-Froebugen',
            filter_questionnaires: 'Froebou-Filteren',
            questionnaire_views: 'Froebou-Usiichten',
            templates_list: 'Lëscht vun de Froebou-Schablounen',
            instances_list: 'Lëscht vun de Froebou-Instanzen',
            filter_by_status: 'No Froebou-Status filteren',
            templates_per_page: 'Schablounen pro Säit',
            instances_per_page: 'Instanzen pro Säit',
            select_template: 'Froebou-Schabloun auswielen',
            template_category: 'Schablounekategorie',
            templates_tab: 'Schablounen',
            instances_tab: 'Instanzen',
            new: 'Neie Froebou',
            new_template: 'Nei Schabloun',
            new_instance: 'Nei Instanz',
            vendor_id: 'Liwwerant-ID',
            template_id: 'Schabloun-ID',
            assigned_to: 'Zougewisen un',
            due_date: 'Fällegkeetsdatum',
            responses: 'Äntwerten',
            risk_score: 'Risikobewäertung',
            no_questionnaires: 'Keng Froebuge fonnt.',
            load_templates_error: 'Froebou-Schablounen konnten net gelueden ginn',
            load_instances_error: 'Froebuge konnten net gelueden ginn',
            create_template_error: 'Froebou-Schabloun konnt net erstallt ginn',
            initiate_error: 'Froebou konnt net gestart ginn',
            submit_error: 'Froebou konnt net agereecht ginn',
            review_error: 'Froebou konnt net iwwerpréift ginn',
            resubmit: 'Nei areechen',
            approve_error: 'Froebou konnt net guttgeheescht ginn',
            reject_error: 'Froebou konnt net ofgeleent ginn',
            resubmit_error: 'Froebou konnt net nei agereecht ginn',
            templates_loaded: '{count} Froebou-Schablounen gelueden',
            instances_loaded: '{count} Froebou-Instanze gelueden',
            fill_responses_cta: 'Äntwerten ausfëllen',
            fill_responses_title: 'Froebou-Äntwerten ausfëllen',
            template_missing: 'Schabloun net fonnt.',
            responses_saved: 'Äntwerte gespäichert.',
            load_template_error: 'Schabloun konnt net gelueden ginn.',
            version_history: 'Versiounsgeschicht',
            download_previous_version: 'Viregt Versioun eroflueden',
            trigger: 'Ausléiser',
            template_label: 'Schabloun',
            reviewer: 'Iwwerpréifer',
            reviewer_required: 'Fir {trigger}-Froebuge muss en Iwwerpréifer zougewise ginn (Trennung vun de Pflichten).',
            select_option: 'Eng Optioun auswielen',
            multi_choice_hint: 'All zoutreffend Optiounen auswielen',
            category_initial_onboarding: 'Éischt Onboarding',
            category_annual_review: 'Jäerlech Iwwerpréiwung',
            category_incident_triggered: 'Duerch Tëschefall ausgeléist',
            category_contract_renewal: 'Vertragserneierung',
            template_editor: {
                page_title: 'Froebou-Schabloun änneren',
                preview_page_title: 'Froebou-Schabloun virusgesinn',
                edit_structure: 'Struktur änneren',
                preview: 'Virschau',
                actions: 'Aktiounen',
                back_to_list: 'Zréck zu de Schablounen',
                load_error: 'Schabloun konnt net gelueden ginn',
                save_error: 'Schablounestruktur konnt net gespäichert ginn',
                saved: 'Schablounestruktur gespäichert.',
                sections_heading: 'Sektiounen',
                questions_heading: 'Froen',
                add_section: 'Sektioun bäifügen',
                edit_section: 'Sektioun änneren',
                delete_section: 'Sektioun läschen',
                move_section_up: 'Sektioun no uewen réckelen',
                move_section_down: 'Sektioun no ënnen réckelen',
                confirm_delete_section: 'Sektioun „{title}" läschen? All hir Froe ginn ewechgeholl.',
                section_title: 'Sektiounstitel',
                section_description: 'Sektiounsbeschreiwung',
                section_id: 'Sektiouns-ID',
                section_id_hint: 'Automatesch generéiert, ausser Dir setzt eng manuell.',
                no_sections: 'Nach keng Sektiounen. Fügt eng Sektioun bäi fir unzefänken.',
                no_questions: 'Nach keng Froen an dëser Sektioun.',
                add_question: 'Fro bäifügen',
                edit_question: 'Fro änneren',
                delete_question: 'Fro läschen',
                move_question_up: 'Fro no uewen réckelen',
                move_question_down: 'Fro no ënnen réckelen',
                confirm_delete_question: 'Fro „{text}" läschen?',
                question_text: 'Frotext',
                question_type: 'Frotyp',
                question_id: 'Fro-ID',
                evidence_required: 'Beweis erfuerderlech',
                risk_weight: 'Risikogewiicht',
                scoring_map: 'Bewäertungsmapping',
                scoring_map_json_hint: 'JSON-Objet, deen Äntwertwäerter op numeresch Bewäertungen zouuerdent.',
                scoring_map_invalid_json: 'Bewäertungsmapping muss gëlteg JSON sinn.',
                options_heading: 'Äntwertoptiounen',
                add_option: 'Optioun bäifügen',
                remove_option: 'Optioun ewechhuelen',
                option_label: 'Bezeechnung',
                option_value: 'Wäert',
                option_risk_weight: 'Risikogewiicht',
                min_options_error: "Auswielfroe brauchen op d'mannst 2 Optiounen.",
                option_label_required: 'All Optioun brauch eng Bezeechnung.',
                option_value_required: 'All Optioun brauch e Wäert.',
                option_value_unique: 'Optiounswäerter mussen eenzegaarteg sinn.',
                branching_rules_heading: 'Verzweigungsregelen',
                branching_rules_hint: 'Dës Fro op Basis vun der Äntwert vun enger fréierer Fro weisen oder verstoppen.',
                add_branching_rule: 'Verzweigungsregel bäifügen',
                remove_branching_rule: 'Verzweigungsregel ewechhuelen',
                branching_condition_question: "Wann d'Fro",
                branching_condition_value: 'de Wäert huet',
                branching_action: 'da',
                branching_action_show: 'dës Fro weisen',
                branching_action_hide: 'dës Fro verstoppen',
                branching_select_condition_question: 'Eng fréier Fro auswielen',
                branching_select_condition_value: 'E Wäert auswielen',
                save: 'Struktur späicheren',
                saving: 'Späicheren...',
                cancel: 'Ofbriechen',
                close: 'Zoumaachen',
                question_text_required: 'Frotext ass erfuerderlech.',
                section_title_required: 'Sektiounstitel ass erfuerderlech.',
                type_single_choice: 'Eenzelauswiel',
                type_multi_choice: 'Méifachauswiel',
                type_free_text: 'Fräitext',
                type_numeric: 'Numeresch',
                type_date: 'Datum',
                type_file_upload: 'Datei eropluedung',
                type_yes_no: 'Jo / Nee',
                preview_heading: 'Schablounevirschau',
                preview_subtitle: 'Nëmme-liesen-Virschau, wéi de Froebou engem Liwwerant erschéngt.',
                preview_no_content: 'Dës Schabloun huet nach keng Sektiounen oder Froen.',
                preview_branching_notice: "Verzweigungsregele ginn beim Ausfëllen ausgewäert; d'Virschau weist all Fro.",
            },
            review_modal: {
                title: 'Froebou-Äntwerten iwwerpréifen',
                open_cta: 'Iwwerpréifen',
                loading: 'Froebou gëtt gelueden...',
                template_missing: 'Schabloun net fonnt.',
                load_error: "Froebou konnt net fir d'Iwwerpréiwung gelueden ginn.",
                annotation_heading: 'Annotatiounen',
                annotation_placeholder: 'Eng Iwwerpréifer-Annotatioun fir dës Fro bäifügen...',
                annotation_status_label: 'Annotatiounsstatus',
                annotation_status_acceptable: 'Akzeptabel',
                annotation_status_concern: 'Bedenken',
                annotation_status_requires_action: 'Erfuerdert Handlung',
                review_comments_label: 'Allgemeng Iwwerpréifungskommentaren',
                review_comments_placeholder: 'Är Iwwerpréifungserkenntnisser zesummefaassen...',
                justification_label: 'Begrënnung vun der Entscheedung',
                justification_placeholder: "D'Begrënnung fir Är Entscheedung erklären...",
                reject_requires_comments: "D'Oflehnen vun engem Froebou erfuerdert Iwwerpréifungskommentaren.",
                decision_heading: 'Entscheedung',
                decision: {
                    approved: 'Guttheeschen',
                    rejected: 'Oflehnen',
                    additional_info_requested: 'Zousätzlech Informatioune ufroen',
                },
                submit: 'Iwwerpréiwung areechen',
                submitting: 'Iwwerpréiwung gëtt agereecht...',
                cancel: 'Ofbriechen',
                submit_success: 'Iwwerpréiwung agereecht.',
                submit_error: 'Iwwerpréiwung konnt net agereecht ginn.',
                separation_of_duties_error: "Dir kënnt e Froebou net iwwerpréiwen, deen Iech zougewise war ze fëllen. D'Trennung vun de Pflichten erfuerdert en aneren Iwwerpréifer.",
                decision_required: 'Wielt w. e. g. eng Iwwerpréifungsentscheedung aus.',
                announce_opened: 'Iwwerpréifungsfënster opgemaach.',
                announce_submitted: 'Iwwerpréiwung agereecht.',
            },
            instance_detail: {
                submitted_at: 'Agereecht',
                reviewed_at: 'Iwwerpréift',
                reviewer: 'Iwwerpréifer',
                review_decision: 'Entscheedung',
                review_comments: 'Iwwerpréifungskommentaren',
                decision_approved: 'Guttgeheescht',
                decision_rejected: 'Ofgeleent',
                decision_additional_info_requested: 'Méi Informatioune gefrot',
                decision_pending: 'Iwwerpréiwung ausstänneg',
                expand_comments: 'Voll Kommentaren weisen',
                collapse_comments: 'Voll Kommentaren verstoppen',
                no_reviewer: 'Keen Iwwerpréifer zougewisen',
                earlier_reviews: 'Earlier reviews',
                earlier_review_entry: '{decision} by {name} on {date}.',
                earlier_review_comments: 'Comments: {comments}',
                earlier_review_question_note: 'Note on {question}: {comments}',
                earlier_review_note: 'Question note: {comments}',
                earlier_review_unknown_person: 'an unrecorded reviewer',
                earlier_review_unknown_date: 'an unrecorded date',
                // Sprint 62.58.0 gate E12.5 pass 1 (P3-4) — the instance export's line
                // for one earlier review: its number, then the page's own sentence
                // (`earlier_review_entry`), so the two never word a review two ways.
                earlier_review_export_line: 'Earlier review {index}: {entry}',
                earlier_review_no_decision: 'No decision recorded',
            },
            instance_statuses: {
                draft: 'Entworf',
                in_progress: 'Am Gaang',
                submitted: 'Submitted',
                under_review: 'Under review',
                approved: 'Genehmegt',
                rejected: 'Refuséiert',
                expired: 'Ofgelaf',
            },
            export: {
                portfolio_label: 'Portfolio exportéieren',
                instance_label: 'Exportéieren',
                portfolio_success: 'Portfolio-Export prett.',
                instance_success: 'Froebou-Export prett.',
                portfolio_error: 'Froebou-Portfolio konnt net exportéiert ginn.',
                instance_error: 'Froebou konnt net exportéiert ginn.',
                header_line: '{label}: {value}',
                default_title: 'Vendor questionnaire',
                unassigned: 'Unassigned',
                risk_score_none: 'Not scored',
                column_question: 'Question',
                column_answer: 'Answer',
                column_evidence: 'Evidence',
                column_annotation: 'Reviewer annotation',
                section_no_questions: 'No questions in this section.',
                portfolio_title: 'Vendor questionnaires portfolio',
                portfolio_generated: 'Generated',
                portfolio_rows: 'Rows',
                portfolio_status_filter: 'Status filter',
                portfolio_vendor_filter: 'Vendor filter',
                portfolio_assignee_filter: 'Assignee filter',
            },
            branching: {
                hidden_notice: 'Verschidde Froe sinn duerch Verzweigungsregele verstoppt.',
                cycle_warning: 'En Zyklus an de Verzweigungsregele gouf festgestallt. Betraffe Froe sinn als siichtbar gesat.',
            },
        },
        contracts: {
            edit_error: 'Failed to update contract',
            edit_success: 'Contract updated',
            edit_contract: 'Edit contract details',
            record_signature: 'Record signature',
            signature_section: 'Signatures on this contract',
            signature_none: 'No signature has been recorded for this contract.',
            signatory_name: 'Signatory name',
            signatory_title: 'Signatory title',
            signatory_party: 'Signing party',
            signatory_party_organization: 'Our organisation',
            signatory_party_counterparty: 'Counterparty',
            signed_on: 'Date signed',
            signature_evidence: 'Evidence reference',
            signature_evidence_none: 'No evidence reference is recorded on this contract yet. Attach the signed document below instead.',
            signature_evidence_attachment: 'Signed document',
            signature_evidence_attachment_hint: 'Upload the signed document when there is no evidence reference to select.',
            signature_evidence_hint: 'A contract enters into force on a recorded signature, so a signature needs evidence: reference evidence already on this contract, or attach the signed document.',
            signature_saved: 'Signature recorded',
            signature_error: 'Failed to record the signature',
            title: 'Kontrakter',
            page_title: 'Verkeefer Kontrakter',
            new: 'Nei Kontrakt',
            vendor_id: 'Ubidder ID',
            filter_contracts: 'Filter Kontrakter',
            filter_by_document_type: 'Filter no Dokumenttyp',
            filter_by_status: 'Filter no Kontraktstatus',
            per_page: 'Kontrakter pro Säit',
            expired_notice: 'Dëse Kontrakt ass ofgelaf',
            transition_reason: 'Reason for this change',
            transition_reason_required: 'A reason is required to take this contract out of force.',
            transition_reason_placeholder: 'Why is this contract changing status?',
            transition_none_available: 'This status is final. A successor contract is a new record.',
            document_type: 'Dokument Typ',
            expiry_date: 'Oflaf Datum',
            no_contracts: 'Keng contracts fonnt.',
            all_document_types: 'All Dokument Typen',
            load_error: 'Konnt net load contracts',
            create_error: 'Konnt net create Kontrakt',
            delete_error: 'Konnt net delete Kontrakt',
            update_error: 'Konnt net update Status',
            confirm_delete: 'Läschen Kontrakt "{title}"?',
            renewal_date: 'Erneierung Datum',
            effective_date: 'Effektiv Datum',
            parties: 'Parteien',
            summary: 'Zesummefaassung',
            n_contracts: '{count} Kontrakter',
            n_contract: '{count} Kontrakt',
            contracts_loaded: '{count} Kontrakter gelueden',
            doc_type_dpa: 'DPA',
            doc_type_security_addendum: 'Sécherheetsnachträg',
            doc_type_scc_annex: 'SCC-Unhang',
            doc_type_sub_processor_list: 'Ënnerveraaschter-Lëscht',
            doc_type_bcr: 'BCR',
            doc_type_nda: 'NDA',
            doc_type_msa: 'MSA',
            doc_type_sla: 'SLA',
            doc_type_other: 'Anert',
            status_draft: 'Entworf',
            status_under_negotiation: 'An Verhandlung',
            status_pending_signature: 'Ënnerschrëft ausstänneg',
            status_active: 'Aktiv',
            status_expired: 'Ofgelaf',
            status_terminated: 'Beendet',
            status_superseded: 'Ersat',
            contract_file: 'File',
            upload_contract_file: 'Contract File',
            auto_renew: 'Renews automatically',
            auto_renew_hint: 'On the expiry date the contract renews for one more term of the same length, recorded in its history. It does not renew if its effective date or renewal notice period is missing, or if it is not active.',
            renewal_period_days: 'Renewal notice (days)',
            renewal_period_days_hint: 'A renewal reminder task is raised this many days before the expiry date.',
            clauses_heading: 'Clauses',
            clause_title: 'Clause title',
            clause_title_required: 'A clause with text needs a title.',
            clause_content: 'Clause text',
            clause_mandatory: 'Mandatory clause',
            add_clause: 'Add clause',
            remove_clause: 'Remove clause',
            contract_file_save_first: 'Save the contract first. Its file can be attached once the contract exists.',
            expiry_date_keep_hint: 'An expiry date, once set, can be changed but not removed.',
            renewal_history: 'Renewal history',
            renewal_history_note: 'Each row is an automatic renewal the platform made because the contract renews automatically: the expiry date moved by one term.',
            renewal_col_previous_expiry: 'Previous expiry',
            renewal_col_new_expiry: 'New expiry',
            renewal_col_term: 'Term',
            renewal_col_renewed_on: 'Renewed on',
            renewal_term_months: '{count} month(s)',
            renewal_term_days: '{count} day(s)',
            clause_field_label: 'Clause {n}, {field}',
            field_other: 'Another field',
        },
        gates: {
            title: 'Geneemegungsporten',
            page_title: 'Gate Evaluatioune',
            new: 'Nei Gate Evaluation',
            vendor_id: 'Ubidder ID',
            gate_name: 'Gate Numm',
            outcome: 'Resultat',
            evaluator: 'Bewäerter',
            no_gates: 'Keng gate evaluations fonnt.',
            load_error: 'Konnt net load gate evaluations',
            evaluate_error: 'Konnt net evaluate Ubidder gate',
            detail_error: 'Konnt net fetch evaluation Detail',
            override_error: 'Konnt net override gate evaluation',
            criteria_results: 'Kritären Resultater',
            evaluate_form: 'Evaluéieren Verkeefer Gate',
            per_page: 'Bewäertungen pro Säit',
            passed: 'Bestanen',
            failed: 'Gescheitert',
            override: 'Iwwerschreiwen',
            override_justification: 'Iwwerschreiwen Begrënnung',
            override_placeholder: 'Aginn Begrënnung fir overriding dëst gate evaluation',
            approval_id: 'Genehmegung ID',
            overall_passed: 'Bestanen',
            overall_failed: 'Net bestanen',
            overall_overridden: 'Iwwerschriwwen',
            evaluations_loaded: '{count} Portevaluatiounen gelueden',
            create_approval_cta: 'Zoustëmmung erstellen',
        },
        reviews: {
            title: 'Liwwerantbewäertungen',
            page_title: 'Verkeefer Rezensiounen',
            new: 'Nei Iwwerpréiwen',
            vendor_id: 'Ubidder ID',
            filter_by_outcome: 'Filter no Bewäertungsresultat',
            per_page: 'Rezensiounen pro Säit',
            kpi_summary: 'Iwwerpréift KPI Resumé',
            review_outcome_label: 'Iwwerpréiwen Resultat',
            program_unresolved: 'Program {id} (name not available)',
            programs_unavailable: 'Programs could not be listed. Enter the program ID.',
            edit_outcome: 'Iwwerpréiwung Resultat änneren',
            reviews_loaded: '{count} Verkeefer Bewäertungen gelueden',
            reviewer: 'Préifer',
            review_type: 'Iwwerpréiwen Typ',
            outcome: 'Resultat',
            findings: 'Resultater',
            no_reviews: 'Keng Ubidder reviews fonnt.',
            load_kpis_error: 'Konnt net fetch Ubidder Iwwerpréifung KPIs',
            load_error: 'Konnt net fetch Ubidder reviews',
            update_error: 'Verkeefer-Bewäertung konnt net aktualiséiert ginn',
            create_error: 'Konnt net create Ubidder Iwwerpréifung',
            total_reviewed: 'Gesamt Ubidder Iwwerpréift',
            total_reviews: 'Gesamt Iwwerpréifungen',
            satisfactory: 'Zefriddestellend',
            concerns_identified: 'Bedenken Identified',
            remediation_required: 'Behiewung Erfuerderlech',
            escalated: 'Eskaléiert',
            program_id: 'Programm ID',
            period_id: 'Period-ID',
            task_id: 'Aufgab ID',
            recommendations: 'Empfeelungen',
            reviewed_at: 'Reviewed At',
        },
        risk_signals: {
            title: 'Risikosignaler',
            page_title: 'Risiko Signaler',
            new_signal: 'Neie Risikosignal',
            signal_type: 'Signaltyp',
            severity: 'Schwéiergrad',
            description: 'Beschreiwung',
            source: 'Quell',
            vendor_id: 'Verkeefer ID',
            certification_expiry: 'Zertifizéierungsoflaaf',
            breach_notification: 'Verstoussnotifikatioun',
            financial_instability: 'Finanziell Onstabilitéit',
            compliance_change: 'Compliance-Ännerung',
            custom: 'Personaliséiert',
            active: 'Aktiv',
            acknowledged: 'Unerkennt',
            resolved: 'Geléist',
            no_signals: 'Keng Risikosignaler fonnt',
            source_external_feed: 'Externen Feed',
            source_manual: 'Manuell',
            source_integration: 'Integratioun',
            source_regulatory: 'Regulatoresch',
            source_media: 'Medien',
            trigger_reassessment_prompt: 'Dëse Signal ass héich prioritär. Elo eng Neibewäertung vum Fournisseur starten?',
            reassessment_triggered: 'Neibewäertung ugestouss.',
            reassessment_trigger_error: 'Neibewäertung konnt net ugestouss ginn.',
        },
        reassessments: {
            title: 'Neibewäertungsopgaben',
            page_title: 'Reevaluatioun Aufgaben',
            trigger: 'Neibewäertung ausléisen',
            trigger_success: 'Neibewäertung erfollegräich ausgeléist',
            reason: 'Grond',
            deadline: 'Frist',
            assigned_to: 'Zougewisen un',
            vendor_id: 'Verkeefer ID',
            outcome: 'Resultat',
            new_risk_level: 'Neit Risikoniveau',
            deadline_hint: 'The deadline is the end of the chosen day (UTC).',
            reason_required: 'Enter a reason.',
            vendor_required: 'Choose a vendor.',
            already_completed: 'This reassessment has already been completed.',
            complete: 'Neibewäertung ofschléissen',
            complete_success: 'Neibewäertung erfollegräich ofgeschloss',
            pending: 'Aussteigend',
            in_progress: 'Am Gaang',
            completed: 'Ofgeschloss',
            cancelled: 'Annulléiert',
            no_tasks: 'Keng Neibewäertungsopgaben fonnt',
            risk_trend: 'Risikotrend',
            notes: 'Notizen',
        },
        portfolio: {
            title: 'Risikoportfolio',
            page_title: 'Risiko Portfolio',
            dashboard: 'Portfolioiwwersiicht',
            vendor_id: 'Verkeefer ID',
            total_vendors: 'Total Liwweranten',
            average_score: 'Duerchschnëttlech Risikobewäertung',
            high_risk_count: 'Héichrisiko-Liwweranten',
            trend_direction: 'Trendrichtung',
            improved: 'Verbessert',
            stable: 'Stabil',
            degraded: 'Verschlechtert',
            vendor_trend: 'Liwwerantentrend',
            outstanding_questionnaires: 'Aussteigend Froebugen',
            overdue_assessments: 'Iwwerfälleg Bewäertungen',
            no_outstanding: 'Keng aussteigend Froebugen',
            no_overdue: 'Keng iwwerfälleg Bewäertungen',
            due_date: 'Fällegkeetsdatum',
        },
        risk_acceptance: {
            expiring_within: 'Expiring within {days} days',
            title: 'Risikoacceptanzregëster',
            page_title: 'Risiko Akzeptanz Register',
            new_acceptance: 'Nei Risikoacceptanz',
            vendor_id: 'Verkeefer ID',
            risk_level: 'Risikoniveau',
            conditions: 'Bedéngungen',
            justification: 'Begrënnung',
            expiry_date: 'Oflaafdatum',
            create_success: 'Risikoacceptanz erfollegräich erstallt',
            review_condition: 'Bedéngung iwwerpréiwen',
            condition_met: 'Bedéngung erfëllt',
            condition_not_met: 'Bedéngung net erfëllt',
            review_success: 'Bedéngung erfollegräich iwwerpréift',
            export_register: 'Regëster exportéieren',
            active: 'Aktiv',
            expired: 'Ofgelaf',
            revoked: 'Zréckgeholl',
            no_acceptances: 'Keng Risikoacceptanzen fonnt',
            met_count: '{met}/{total} begéint',
        },
        review_routing: {
            title: 'Iwwerpréiwungsroutage',
            page_title: 'Iwwerpréiwen Routing',
            route_to_reviewers: 'Un Iwwerpréifer schécken',
            route_success: 'Erfollegräich un Iwwerpréifer geschéckt',
            vendor_id: 'Verkeefer ID',
            reviewer_id: 'Iwwerpréifer-ID',
            reviewer_role: 'Iwwerpréiferroll',
            questionnaire_id: 'Froebuch-ID',
            review_status: 'Iwwerpréiwungsstatus',
            reviewers_label: 'Bewäertungen (user_id, Roll pro Zeil)',
            pending: 'Aussteigend',
            in_review: 'An der Iwwerpréiwung',
            completed: 'Ofgeschloss',
            no_routing: 'Keng Iwwerpréiwungsroutage fonnt',
            primary_reviewer: 'Haaptiwwerpréifer',
            secondary_reviewer: 'Zweet Iwwerpréifer',
            approver: 'Genehmeger',
        },
        onboarding: {
            title: 'Liwweranten-Onboarding-Fäll',
            page_title: 'Liwweranten-Onboarding-Fäll',
            new_case: 'Neien Onboarding-Fall',
            case_detail: 'Onboarding-Fall',
            open_mps_dossier_filing: 'MPS-Aweisung opmaachen',
            dossier_filing_description: "Erstellt eng nei MPS-Aweisung, virausgefüllt aus dësem Onboardingcase. D'Aweisung gëtt mam Case verbonnen.",
            dossier_filing_dpia_id: 'DPIA-ID',
            dossier_filing_dpia_id_placeholder: 'z.B. dpia_01TEST000000000000000001',
            dossier_filing_title: 'Titel vun der Aweisung',
            dossier_filing_form_type: 'Formulartyp',
            dossier_filing_submit: 'Aweisung erstellen',
            dossier_filing_validation_error: 'DPIA-ID an Titel sinn erfuerderlech.',
            dossier_filing_created: 'MPS-Aweisung erstallt',
            dossier_filing_error: 'MPS-Aweisung konnt net erstallt ginn.',
            linked_questionnaire_due_date: 'Fälegkeetsdatum',
            linked_questionnaire_assignee: 'Zougewisen un',
            linked_questionnaire_submitted_at: 'Agereecht',
            score_last_scored: 'Lescht Bewäertung',
            score_dimensions: 'Dimensiounen',
            linked_contract_document_type: 'Dokumenttyp',
            linked_contract_effective_date: 'Startdatum',
            linked_contract_expiry_date: 'Ofloofdatum',
            linked_sub_processor_countries: 'Veraarbechtungslänner',
            linked_sub_processor_last_assessment: 'Lescht Bewäertung',
            linked_sub_processor_next_assessment: 'Nächst Bewäertung',
            case_id: 'Fall-ID',
            vendor: 'Liwwerant',
            case_type: 'Falltyp',
            service_description: 'Servicebeschreiwung',
            data_categories: 'Datekategorien',
            systems_accessed: 'Zougräifend Systemer',
            jurisdiction: 'Juridiktioun',
            hosting_model: 'Hosting-Modell',
            controller_role: 'Verantwortlechen/Veraarbechter-Roll',
            status_pipeline: 'Status-Pipeline',
            tabs_label: 'Onboarding-Fall Sektiounen',
            per_page: 'Fäll pro Säit',
            filter_cases: 'Onboarding-Fäll filteren',
            cases_count: 'Fäll',
            cases_loaded: 'Onboarding-Fäll gelueden',
            no_cases: 'Keng Onboarding-Fäll fonnt.',
            vendor_lookup_placeholder: 'Liwweranten-ID aginn',
            data_categories_placeholder: 'z.B. perséinlech Donnéeën, Finanzen, Gesondheet',
            systems_placeholder: 'z.B. CRM, ERP, HR-System',
            status_open: 'Op',
            status_in_progress: 'Am gaangen',
            status_awaiting_approval: 'Waart op Genehmegung',
            status_approved: 'Genehmegt',
            status_remediation_required: 'Behiewung néideg',
            status_closed: 'Zou',
            type_new_vendor: 'Neie Liwwerant',
            type_service_change: 'Serviceännerung',
            type_renewal: 'Erneierung',
            type_reassessment: 'Neibewäertung',
            tab_overview: 'Iwwerbléck',
            tab_due_diligence: 'Suerffaltspflicht',
            tab_security_assessment: 'Sécherheetsbewäertung',
            tab_contractual_controls: 'Vertraglech Kontrollen',
            tab_sub_processors: 'Ënnerdatenveraarbechter',
            tab_approval_gates: 'Genehmegungspaarten',
            tab_evidence: 'Beweiser',
            tab_placeholder: 'Den Inhalt vun dëser Sektioun gëtt während dem Onboarding-Prozess ausgefëllt.',
            completion_not_started: 'Net ugefaangen',
            completion_in_progress: 'Am gaangen',
            completion_completed: 'Ofgeschloss',
            completion_requiring_attention: 'Opmierksamkeet erfuerderlech',
            gate_name: 'Paart-Numm',
            approver: 'Genehmeger',
            decision: 'Decisioun',
            comments: 'Kommentaren',
            decide: 'Decisioun treffen',
            decision_approved: 'Genehmegt',
            decision_rejected: 'Ofgeleent',
            decision_deferred: 'Verschoben',
            decision_pending: 'Pending',
            no_approvals: 'Keng Genehmegungspaarte fir dëse Fall konfiguréiert.',
            approval_success: 'Genehmegungsdecisioun erfollegräich erfaasst',
            approval_error: 'Genehmegungsdecisioun konnt net erfaasst ginn',
            transition_status: 'Status wiesselen',
            confirm_transition: 'Fall vun "{from}" op "{to}" wiesselen?',
            transition_success: 'Fallstatus erfollegräich geännert',
            transition_error: 'Fallstatus konnt net geännert ginn',
            create_success: 'Onboarding-Fall erfollegräich erstallt',
            create_error: 'Onboarding-Fall konnt net erstallt ginn',
            load_error: 'Onboarding-Fäll konnten net geluede ginn',
            detail_error: 'Falldetailer konnten net geluede ginn',
            export: 'Exportéieren',
            export_error: 'Onboarding-Fall konnt net exportéiert ginn',
            evidence_tab_description: 'Lued Beweiser fir den Onboarding-Fall erop an exportéier se.',
            upload_evidence_label: 'Beweis eroplueden',
            attached_evidence_heading: 'Ugehängt Beweiser',
            no_evidence_attached: 'Nach keng Beweiser ugehängt.',
            export_bundle_button: 'Beweispak exportéieren',
            load_attachments_error: 'Beweiser konnten net gelueden ginn.',
            linked_questionnaires_heading: 'Verlinkt Questionnairen',
            no_linked_questionnaires: 'Keng verlinkt Questionnairen.',
            linked_scores_heading: 'Verlinkt Risikobewäertungen',
            no_linked_scores: 'Keng verlinkt Bewäertungen.',
            latest_score_label: 'Rezentst Bewäertung',
            linked_contracts_heading: 'Verlinkt Kontrakter',
            no_linked_contracts: 'Keng verlinkt Kontrakter.',
            linked_sub_processors_heading: 'Verlinkt Ënnerbehandler',
            no_linked_sub_processors: 'Keng verlinkt Ënnerbehandler.',
            approval_created: 'Approval created',
            create_approval: 'Create Approval',
            export_bundle_success: 'Evidence bundle generated',
            gate_type: 'Gate Type',
            no_recent_bundles: 'No evidence bundles generated yet.',
            recent_bundles: 'Recent Bundles',
            tab_completed: 'Section marked complete',
            tab_gate_not_met: 'This section is already marked complete.',
            tab_linked_records_missing: 'This section cannot be marked complete yet: it needs at least one {kind}.',
            linked_kind_questionnaire: 'due-diligence questionnaire for this vendor that has not expired',
            linked_kind_risk_score: 'risk score for this vendor',
            linked_kind_contract: 'active contract with this vendor that is in force today',
            linked_kind_sub_processor: 'sub-processor entry recorded for this case',
            decide_again: 'Decide again',
            resolve_rejection: 'Resolve rejection',
            resolution_notes: 'Documented resolution',
            resolution_notes_hint: 'Required. Say how the reason for the rejection was addressed. The rejection stays in the gate history and the gate returns to undecided, to be decided again.',
            resolution_notes_required: 'Enter the resolution before reopening the gate.',
            resolve_success: 'Rejection resolved. The gate can be decided again.',
            resolve_error: 'The rejection could not be resolved.',
            prior_gate_blocked: 'An earlier gate is rejected or deferred. Decide it again, or resolve its rejection, first.',
            case_gate_blocked: 'The case cannot be approved while a gate is rejected or deferred.',
            case_gate_undecided: 'The case cannot be approved while a gate is still undecided. Decide every gate first.',
            case_final_gate_missing: 'The case cannot be approved until its final gate has been created and approved.',
            case_status_changed: 'The case or its gates changed while this was being saved. The case has been reloaded; check it and try again.',
            gate_already_decided: 'This gate has already been decided. Reload to see the decision.',
            deferred_gate_hint: 'Deferred: this gate and every gate after it wait until it is decided again.',
            mark_complete: 'Als ofgeschloss markéieren',
            earlier_decisions: 'Earlier decisions on this gate',
            history_entry: '{decision} by {name} on {date}.',
            history_notes: 'Notes: {notes}.',
            history_conditions: 'Conditions: {conditions}.',
            history_redecided: 'Decided again on {date}.',
            history_resolved: 'Rejection resolved by {name} on {date}: {notes}',
            history_unknown_person: 'a person not recorded',
            history_unknown_date: 'a date not recorded',
        },
        sub_processors: {
            appointment_decision: 'Decision to appoint anyway',
            appointment_decision_required: 'The DPA chain is not resolved. Record who is accepting that, and why.',
            appointment_decision_placeholder: 'Why is this sub-processor being appointed before the chain is in place?',
            title: 'Ënnerdatenveraarbechter-Register',
            page_title: 'Ënnerdatenveraarbechter-Register',
            new_record: 'Neien Ënnerdatenveraarbechter',
            edit_record: 'Ënnerdatenveraarbechter änneren',
            onboarding_case: 'Onboarding-Fall',
            services: 'Geliewert Servicer',
            data_categories: 'Datekategorien',
            processing_countries: 'Veraarbechtungslänner',
            dpa_chain_status: 'DPA-Kettstatus',
            certifications: 'Zertifikatiounen',
            last_assessment: 'Lescht Bewäertung',
            next_assessment: 'Nächst Bewäertung',
            change_history: 'Ännerungshistorique',
            history: 'Historique',
            changed_by: 'Geännert vun',
            comma_separated: 'Komma-getrennt Wäerter',
            filter_records: 'Entrée filteren',
            filter_by_dpa: 'No DPA-Kettstatus filteren',
            all_dpa_statuses: 'All DPA-Statussen',
            per_page: 'Entréeën pro Säit',
            records_count: 'Ënnerdatenveraarbechter',
            records_loaded: 'Entréeën gelueden',
            no_records: 'Keng Ënnerdatenveraarbechter fonnt.',
            no_history: 'Keen Ännerungshistorique disponibel.',
            dpa_active: 'Aktiv',
            dpa_pending: 'Ausstehend',
            dpa_expired: 'Ofgelaf',
            dpa_not_required: 'Net erfuerderlech',
            create_success: 'Ënnerdatenveraarbechter erfollegräich erstallt',
            create_error: 'Konnt net erstallt ginn',
            update_success: 'Ënnerdatenveraarbechter erfollegräich aktualiséiert',
            update_error: 'Konnt net aktualiséiert ginn',
            load_error: 'Register konnt net geluede ginn',
            history_error: 'Ännerungshistorique konnt net geluede ginn',
            export_csv: 'CSV exportéieren',
            export_json: 'JSON exportéieren',
            export_error: 'Donnéeë konnten net exportéiert ginn',
        },
    },
    deletion_orchestration: {
        nav: {
            dashboard: 'Orchestréierungsiwwersiicht',
            runs: 'Orchestréierungsleef',
            tasks: 'Ausfüerungsopgaben',
            templates: 'Checklistevirlagen',
            vendor_requests: 'Liwwerantufroen',
            proof_chain: 'Noweisketten',
            dsr_integration: 'DSR-Integratioun',
            vendor_verification: 'Liwwerante-Verifizéierung',
            completeness: 'Vollstännegkeetsiwersiicht',
        },
        dashboard: {
            title: 'Orchestréierungsiwwersiicht',
            loading: 'Lueden vun dashboard Donnéeën...',
            active_runs: 'Aktiv Runs',
            completion_rate: 'Ofschloss Quote',
            avg_completion_time: 'Avg Ofschloss Zäit',
            overdue_tasks: 'Iwwerfälleg Aufgaben',
            vendor_compliance: 'Ubidder Konformitéit',
            vendor_compliance_none: 'No vendor request sent in the last 90 days',
            recent_runs: 'Rezent Runs',
            no_recent_runs: 'Keng rezent Orchestréierungsduerchleef.',
            run_id: 'Ausféieren ID',
            ticket_id: 'Ticket-ID',
            progress: 'Fortschrëtt',
            systems: 'Systemer',
            vendors: 'Ubidder',
            load_error: "Ausgefall fir d'Orchestratiounsdashboard ze lueden. Probéiert w.e.g. nach eng Kéier.",
            aria_table: 'Rezent Orchestratioun leeft',
        },
        runs: {
            title: 'Orchestréierungsleef',
            new: 'Nei Ausféieren',
            run_id: 'Ausféieren ID',
            ticket_id: 'Ticket-ID',
            progress: 'Fortschrëtt',
            systems: 'Systemer',
            vendors: 'Ubidder',
            data_subject_ref: 'Donnéeën Betreff Ref',
            data_categories: 'Donnéeën Kategorien',
            enable_proof_chain: 'Aktivéieren Bewäis Ketten',
            start: 'Starten',
            started: 'Run ugefaangen',
            mark_ready: 'Mark Ready',
            marked_ready: 'Run markéiert als prett',
            complete: 'Komplett',
            complete_exceptions: 'Komplett w / Ausnahmen',
            pause: 'Paus',
            resume: 'Resumé',
            cancel: 'Ofbriechen',
            no_runs: 'Keng orchestration runs fonnt.',
            load_error: 'Ausgefall Orchestratioun Lafen ze lued. Probéiert w.e.g. nach eng Kéier.',
            create_error: 'Ausgefall Orchestratioun Run ze schafen',
            aria_filter_status: 'Filter no lafen Status',
            aria_table: 'Orchestratioun leeft',
            placeholder_ticket_id: 'z.B. TKT-000123',
            placeholder_data_subject_ref: 'z.B. DS-REF-001',
            placeholder_data_categories: 'e.g., personal_data, finanziell, Gesondheet',
            cancel_confirm_title: 'Cancel this run?',
            cancel_confirm_body: 'Run {run} will be cancelled. A cancelled run cannot be started or resumed again.',
            cancel_reason_label: 'Reason (optional)',
            cancel_reason_hint: 'Recorded with the cancellation.',
            cancel_keep: 'Keep run',
            cancel_confirm: 'Cancel run',
            cancelling: 'Cancelling…',
            cancelled_success: 'Run cancelled',
            cancel_error: 'The run could not be cancelled.',
            title_label: 'Title',
            run_type: 'Run Type',
            run_type_labels: {
                data_subject_request: 'Data Subject Request',
                retention_expiry: 'Retention Expiry',
                manual: 'Manual',
                scheduled: 'Scheduled',
            },
            field_guidelines: {
                title: 'A descriptive name for this orchestration run.',
                run_type: 'The origin or trigger for the deletion process.',
                ticket_id: 'The associated ticket ID from your DSR or support system.',
                data_subject_ref: 'A unique identifier for the data subject (e.g., internal ID, email hash).',
                data_categories: 'Specific categories of personal data to be deleted (comma-separated).',
                proof_enabled: 'Maintains an immutable cryptographic chain of evidence for this run.',
            },
            actions: 'Actions',
            progress_guideline: 'Calculated based on completed system tasks and vendor requests.',
            placeholder_title: 'e.g., Q2 2026 Customer Deletion Batch',
            refusal_run_executing: 'This run is executing and cannot be cancelled. Pause it first, then cancel it.',
            refusal_invalid_status_transition: 'The run is no longer in a status that allows this change. The list has been refreshed.',
            refusal_run_status_changed: 'The run changed status while this was being saved. The list has been refreshed; check the run and try again.',
            refusal_cancel_permission_required: 'Cancelling a deletion run needs the permission to cancel deletion runs, which you do not hold.',
        },
        tasks: {
            title: 'Ausfüerungsopgaben',
            task_id: 'Aufgab ID',
            run_id: 'Ausféieren ID',
            system_name: 'System Numm',
            assigned_to: 'Zougewisen Op',
            sla_deadline: 'SLA Frist',
            progress: 'Fortschrëtt',
            enter_run_id: 'Aginn Ausféieren ID op filter...',
            select_run: 'Wielt e Run fir Aufgaben ze gesinn',
            unassigned: 'Net zougewisen',
            overdue: 'Iwwerfälleg',
            assign_task: 'Zouweisen Aufgab',
            assignee_id: 'Zougewisenen ID',
            update_status: 'Aktualiséieren Status',
            new_status: 'Nei Status',
            no_transitions: 'Dës Aufgab ass an engem Endzoustand a kann de Status net änneren.',
            select_status_placeholder: 'Wielt en neie Status',
            skip_justification: 'Begrënnung fir d’Iwwersprangen',
            failure_reason: 'Grond vum Echec',
            no_tasks: 'Keng execution Aufgaben fonnt.',
            new_task: 'Nei Aufgab',
            system_id: 'System ID',
            data_classification: 'Daten Klassifikatioun',
            load_error: 'execution tasks konnten net gelueden ginn. Probéiert w.e.g. nach eng Kéier.',
            assign_error: 'Feeler beim assign task',
            status_error: 'Feeler beim Aktualiséiere vum Aufgabestatus',
            no_tasks_for_run: 'Dëse Läschungslaf huet nach keng Ausféierungsaufgaben.',
            aria_filter_run_id: 'Filter no Run ID',
            aria_filter_status: 'Filter no Taskstatus',
            aria_table: 'Ausféierung Aufgaben',
            view_steps: 'Schrëtt weisen',
            steps_modal_title: 'Ausféierungsschrëtt',
            step_title: 'Schrëtt',
            step_status: 'Schrëttstatus',
            step_notes: 'Notizen',
            step_evidence: 'Beweisdateien',
            step_evidence_required: 'Beweis ass fir dësen Schrëtt erfuerderlech',
            step_evidence_missing: 'Nach keng Beweiser eropgelueden',
            step_save: 'Schrëtt späicheren',
            step_saved: 'Schrëtt aktualiséiert',
            step_save_error: 'Schrëtt konnt net aktualiséiert ginn',
            create_success: 'Ausféierungsaufgab erstallt',
            create_error: 'Ausféierungsaufgab konnt net erstallt ginn',
            step_upload_label: 'Beweis bäifügen',
            // fix-sprint #431 (gate E12.5 remediation 1) — separation of duties on verification.
            verify_own_task: 'You are assigned to this task, so another person verifies it.',
            verify_separation_of_duties: 'You cannot verify a task you are assigned to: another person must verify it.',
        },
        templates: {
            title: 'Checklistevirlagen',
            new: 'Nei Schabloun',
            template_id: 'Schabloun ID',
            system_type: 'System Typ',
            est_duration: 'Est. Dauer',
            steps: 'Schrëtter',
            action_type: 'Aktioun Typ',
            instructions: 'Instruktioune',
            mandatory: 'Obligatoresch',
            add_step: 'Derbäisetzen Schrëtt',
            step_n: 'Schrëtt',
            no_templates: 'Keng checklist Schablounen fonnt.',
            all_system_types: 'All System Typen',
            load_error: 'Ausgefall Checklëscht Schablounen ze lued. Probéiert w.e.g. nach eng Kéier.',
            create_error: 'Ausgefall Checklëscht Schabloun ze schafen',
            aria_filter_system_type: 'Filter no Systemtyp',
            aria_filter_status: 'Filter no Schablounstatus',
            aria_table: 'Checkliste Schablounen',
            placeholder_name: 'e.g., Datebank Läschen Checkliste',
            guideline_name: 'Use a clear operating name, for example “Database Deletion Checklist”.',
            guideline_system_type: 'Choose the system category this checklist will usually run against.',
            guideline_status: 'Draft templates can be reviewed before activation; active templates can be reused in runs.',
            guideline_description: 'Summarise scope, retention context, and when teams should select this template.',
            update_success: 'Schabloun erfolgräich aktualiséiert',
            create_success_msg: 'Schabloun erfolgräich erstallt',
            update_error: 'Feeler beim Aktualiséieren vun der Schabloun',
            activate_success: 'Schabloun erfolgräich aktivéiert',
            activate_error: 'Feeler beim Aktivéieren vun der Schabloun',
            archive_success: 'Schabloun erfolgräich archivéiert',
            archive_error: 'Feeler beim Archivéiere vun der Schabloun',
            clone_success: 'Schabloun erfolgräich gekloont',
            clone_error: 'Feeler beim Klonen vun der Schabloun',
        },
        vendor_requests: {
            title: 'Ubidder Deletion Ufroen',
            new: 'Nei Ufro',
            run: 'Orchestration Run',
            no_run: 'Not part of a run',
            run_hint: 'Optional. A request recorded against a run counts toward that run’s progress.',
            request_id: 'Ufro ID',
            vendor_name: 'Ubidder Numm',
            vendor_id: 'Ubidder ID',
            data_subject_ref: 'Donnéeën Betreff Ref',
            data_categories: 'Donnéeën Kategorien',
            legal_basis: 'Rechtlech Grondlag',
            sla_days: 'SLA Deeg',
            requested_completion: 'Ugefrot Ofschloss',
            communications: 'Kommunikatiounen',
            total_requests: 'Gesamt Ufroen',
            no_requests: 'Keng Ubidder deletion requests fonnt.',
            filter_vendor_id: 'Filteren no Ubidder ID...',
            change_status: 'Status Wiesselen',
            new_status: 'Neie Status',
            actions: 'Aktiounen',
            load_error: 'Ausgefall fir Verkeefer Läschen Ufroen ze lueden. Probéiert w.e.g. nach eng Kéier.',
            create_error: 'Ausgefall fir Verkeefer Läsch Ufro ze schafen',
            dashboard_error: "Ausgefall fir den Dashboard fir d'Läsche vum Verkeefer ze lueden. Probéiert w.e.g. nach eng Kéier.",
            aria_filter_status: 'Filter no Ufro Status',
            aria_filter_vendor_id: 'Filter no Verkeefer ID',
            aria_table: 'Verkeefer Läschen Demanden',
            placeholder_vendor_id: 'z.B. VND-001',
            placeholder_vendor_name: 'zB Acme Analytics',
            placeholder_data_subject_ref: 'z.B. DS-REF-001',
            placeholder_data_categories: 'z.B. personal_data, Analyse',
            placeholder_legal_basis: 'z.B. PDPL Artikel 19',
            // fix-sprint #431 (gate E12.5 remediation 1) — record the vendor’s confirmation; a failed runs read.
            runs_read_error: 'The list of orchestration runs could not be read, so no run can be chosen. Retry, or record the request without a run.',
            confirm_action: 'Record confirmation',
            confirm_title: 'Record the vendor’s confirmation',
            confirm_intro: 'Record that {vendor} confirmed the deletion for request {request}. Recording it completes the request.',
            confirm_date: 'Date confirmed',
            confirm_method: 'How the vendor confirmed',
            confirm_method_choose: 'Choose how…',
            confirm_methods: {
                email: 'Email',
                portal: 'Vendor portal',
                letter: 'Letter',
                phone: 'Phone',
                other: 'Other',
            },
            confirm_contact: 'Vendor contact person',
            confirm_systems: 'Systems affected',
            confirm_systems_hint: 'Optional. Separate systems with commas.',
            confirm_notes: 'Notes',
            confirm_submit: 'Record confirmation',
            confirm_success: 'Vendor confirmation recorded; the request is completed.',
            confirm_refused_status: 'This request cannot receive a confirmation: it is a draft, cancelled or already completed.',
            confirm_missing_fields: 'Enter the date confirmed, how the vendor confirmed and the vendor’s contact person.',
            confirm_not_found: 'This request no longer exists.',
            confirm_error: 'The confirmation could not be recorded.',
            confirm_changed: 'Nothing was recorded: the request changed on the server while this confirmation was being saved. The list behind this dialog is read again; check the request there, and record the confirmation again if it still needs one.',
        },
        proof_chain: {
            title: 'Bewäis Ketten Betruechter',
            enter_run_id: 'Aginn Ausféieren ID...',
            load_chain: 'Lueden Ketten',
            verify_chain: 'Verifizéieren Ketten',
            verifying: 'Verifizéiere...',
            chain_valid: 'Ketten is valid',
            chain_invalid: 'Ketten is broken',
            chain_length: 'Ketten Length',
            broken_at_index: 'Gebrach bei index',
            no_proof_records: 'Keng Bewäis Datesätz fonnt fir dëst run..',
            proof_id: 'Bewäis ID',
            entity_type: 'Entitéit Typ',
            entity_id: 'Entitéit ID',
            event_hash: 'Evenement Hash',
            previous_hash: 'Vireg Hash',
            attestation: 'Attestéierung',
            attestation_actor: 'Duerchgeféiert vun',
            attestation_signed_at: 'Ënnerschriwwen den',
            attestation_signature: 'Ënnerschrëft',
            attestation_evidence_refs: 'Beweisreferenzen',
            run_field_label: 'Läschungslaf',
            select_run: 'Wielt e Läschungslaf…',
            aria_select_run: "Läschungslaf fir d'Sich an der Beweiskette",
            chain_broken_here: 'Ketten broken here',
            load_error: 'Ausgefall Beweis Kette ze lued. Probéiert w.e.g. nach eng Kéier.',
            verify_error: "Ausgefall Beweis Kette z'iwwerpréiwen. Probéiert w.e.g. nach eng Kéier.",
            aria_run_id: 'Run ID fir Beweis Kette Lookup',
            // Sprint 62.35.0 — a displayed value the event hash does not cover.
            not_covered_by_hash: 'not covered by the hash',
            // Sprint 62.35.0 (AC8) — English until Phase 25 (R-57).
            destruction: {
                action: 'Record destruction',
                modal_title: 'Record destruction',
                intro: 'Identify the data this run destroyed. The record is added to the proof chain and covered by its hash, so it cannot be changed once saved.',
                section_title: 'Destruction record',
                name: 'Name',
                type: 'Type',
                size: 'Size (bytes)',
                format: 'Format',
                locations: 'Locations',
                locations_hint: 'One location per line. List every copy, including backups, replicas and exports.',
                identifier: 'Identifier',
                hash: 'Hash',
                hash_algorithm: 'Hash algorithm',
                hash_algorithm_hint: 'Required when a hash is given, for example SHA-256.',
                lawful_data_unaffected: 'Lawful data left unaffected',
                lawful_yes: 'Yes, lawful data was left unaffected',
                lawful_no: 'No, lawful data was affected',
                lawful_not_recorded: 'Not recorded',
                lawful_data_note: 'Note on lawful data',
                lawful_data_note_hint: 'Required when lawful data was affected.',
                yes: 'Yes',
                no: 'No',
                not_recorded: 'Not recorded',
                submit: 'Record destruction',
                saving: 'Recording…',
                saved: 'Destruction record added to the proof chain.',
                save_error: 'The destruction record could not be saved.',
                err_required: 'This field is required.',
                err_locations: 'Enter at least one location.',
                err_size: 'Enter a number of bytes, zero or more.',
                err_hash_algorithm: 'Enter the algorithm used to compute this hash.',
                err_algorithm_without_hash: 'Remove the algorithm, or enter the hash it was used for.',
                err_lawful_note: 'Explain how lawful data was affected.',
            },
        },
        dsr_integration: {
            change_status: 'Status änneren',
            request_type: 'Ufrostyp',
            data_subject_hint: 'Identifikatioun vun der betraffener Persoun, där hir Donnéeë geläscht ginn (z. B. E-Mail oder Client-ID). Entsprécht normalerweis dem Ufrocker um ausgewielten DSR-Fall.',
            title: 'DSR-Läschungsintegratioun',
            create_run: 'DSR-Läschungslaf erstellen',
            dsr_case_id: 'DSR-Fall-ID',
            data_subject_id: 'Betraffenen-ID',
            systems: 'Zilsystemer',
            route_to_department: 'Un Abteelung weiderginn',
            confirm_deletion: 'Läschung bestätegen',
            no_runs: 'Keng DSR-Läschungslafen fonnt',
            status: 'Läschungsstatus',
            deadline: 'Frist',
            no_further_status_changes: 'No further status changes',
            refusal_run_executing: 'This deletion run is in progress and cannot be cancelled. Complete it instead, with or without exceptions.',
        },
        vendor_verification: {
            title: 'Liwwerante-Läschungsverifizéierung',
            create_followup: 'Noveruerfolgung erstellen',
            deadline: 'Bestätegungsfrist',
            escalation_contact: 'Eskalatiounskontakt',
            escalate: 'Eskaléieren',
            confirm_escalate_followup: 'Escalate this follow-up? Its status becomes Escalated. Nothing is sent to the vendor or the escalation contact.',
            no_followups: 'Keng Liwwerante-Noveruerfolgunge fonnt',
            vendor_request: 'Verkeefer Ufro',
            vendor: 'Liwwerant',
            unknown_vendor: 'Onbekannte Liwwerant',
            overdue_badge: 'Iwwerfälleg',
            mark_confirmed: 'Als bestätegt markéieren',
            mark_confirmed_for: 'Läschung als bestätegt markéiere fir {vendor}',
            confirm_mark_followup_confirmed: 'Mark this follow-up confirmed? The vendor deletion request is not changed; record the vendor’s confirmation on Vendor Requests as well.',
            confirmation_success: 'Liwwerant-Läschung bestätegt',
            confirmation_failed: 'Liwwerant-Läschung konnt net bestätegt ginn',
            select_request: 'Eng Verkeefer Ufro auswielen…',
            escalation_contact_placeholder: 'z. B. dpo@vendor.example',
            create_success: 'Liwwerant-Noveruerfolgung erfollegräich erstallt',
            create_failed: 'Liwwerant-Noveruerfolgung konnt net erstallt ginn',
            deadline_help: 'The date by which you expect the vendor to confirm. Every follow-up is listed; a pending one past this date is marked Overdue.',
            escalation_success: 'Liwwerant-Läschung erfollegräich eskaléiert',
            escalation_failed: 'Eskaléierung vun der Liwwerant-Läschung gescheitert',
            overdue_filter: 'Overdue (pending, deadline passed)',
            status_labels: {
                pending: 'Pending',
                confirmed: 'Confirmed',
                escalated: 'Escalated',
            },
        },
        completeness: {
            title: 'Läschungsvollstännegkeet',
            check_conflicts: 'Opbewahrungskonflikter préiwen',
            no_conflicts: 'Keng Opbewahrungskonflikter fonnt',
            conflict_resolution: 'Konfliktléisung',
            extend_retention: 'Opbewahrung verlängeren',
            proceed_deletion: 'Mat Läschung weiderfueren',
            escalate_conflict: 'Eskaléieren',
            resolve: 'Konflikt léisen',
            data_subject_id: 'Daten Sujet ID',
            placeholder_data_subject_id: "Gitt d'Datenberäich ID",
            data_subject_hint: 'Identifikatioun vun der Persoun, där hir Läschung Dir iwwerpréift (z. B. E-Mail, Client-ID oder DSR-Referenz). Wielt e bekannte Sujet oder gitt een an.',
            guidance: "Gitt eng Datesubjekt-ID an oder wielt eng, wielt optional Systemer, a féiert d'Kontroll aus fir d'Vollstännegkeet vun der Läschung an d'Retentiounskonflikter ze gesinn.",
            completion: 'Läschungsofschloss',
            pending_items: 'Ausstoend Elementer',
            system: 'System',
            policy: 'Richtlinn',
            retention_end: 'Retention Enn',
            completed_items: 'Ofgeschloss Elementer',
            last_review_label: 'Last review',
            days_ago: 'viru Deeg',
            review_overdue: 'Iwwerpréiwung Zyklus iwwerschratt. Re-Iwwerpréiwung néideg.',
            conflicts_not_checked: 'Retention conflicts were not checked because no system is selected. Choose at least one system and run the check again to see them.',
        },
        run_status_labels: {
            pending: 'Ausstoend',
            planning: 'Planung',
            ready: 'Bereet',
            in_progress: 'Am Gaang',
            paused: 'Pauséiert',
            completed: 'Ofgeschloss',
            completed_with_exceptions: 'Ofgeschloss mat Ausnahmen',
            cancelled: 'Annulléiert',
        },
        task_status_labels: {
            pending: 'Waardend',
            assigned: 'Zougewisen',
            in_progress: 'Am Gaang',
            awaiting_verification: 'Waart op Verifizéierung',
            verified: 'Verifizéiert',
            failed: 'Gescheitert',
            skipped: 'Iwwersprong',
        },
        system_type_labels: {
            database: 'Datebank',
            saas_application: 'SaaS-Applikatioun',
            file_storage: 'Fichiersspäicher',
            email_system: 'E-Mail-System',
            analytics_platform: 'Analyse-Plattform',
            backup_system: 'Backup-System',
            vendor_managed: 'Ubidder-geréiert',
            custom: 'Personnaliséiert',
        },
        template_status_labels: {
            draft: 'Entworf',
            active: 'Aktiv',
        },
        action_type_labels: {
            manual_check: 'Manuell Kontroll',
            api_call: 'API-Opruff',
            sql_query: 'SQL-Ufro',
            script_execution: 'Skriptausfierung',
            file_deletion: 'Fichiersläschung',
            verification: 'Verifizéierung',
            notification: 'Notifikatioun',
            approval: 'Genehmegung',
        },
        request_status_labels: {
            draft: 'Entworf',
            sent: 'Geschéckt',
            acknowledged: 'Bestätegt',
            in_progress: 'Am Gaang',
            completed: 'Ofgeschloss',
            overdue: 'Iwwerfälleg',
            escalated: 'Eskaléiert',
            disputed: 'Bestridden',
            cancelled: 'Cancelled',
        },
        event_type_labels: {
            initiation: 'Initiéierung',
            data_deletion: 'Dateläschung',
            completion: 'Ofschloss',
            verification: 'Verifizéierung',
            rollback: 'Réckgängeg',
        },
        count_labels: {
            one_template: '1 Schabloun',
            n_templates: '{n} Schablounen',
            one_run: '1 Duerchlaaf',
            n_runs: '{n} Duerchleef',
            one_task: '1 Aufgab',
            n_tasks: '{n} Aufgaben',
            one_request: '1 Ufro',
            n_requests: '{n} Ufroen',
        },
        aria: {
            completion_rate: 'Ofschlossquot: {pct}%',
            vendor_compliance: 'Ubidder-Compliance: {pct}%',
            run_progress: 'Fortschrëtt: {pct}%',
            run_progress_systems: 'Fortschrëtt: {completed} vun {total} Systemer veraarbecht',
            chain_broken: 'Kette gebrach bei dësem Eintrag',
            chain_valid: 'Kette gëlteg bei dësem Eintrag',
            chain_unknown: 'Kettestatus onbekannt',
            escalate_vendor: 'Eskaléieren {id}',
            resolve_conflict: 'Konflikt fir {system} léisen',
        },
        regulatory: {
            annual_review_cycle_info: "Vollständegkeet vun der Läschung muss mindestens all 365 Deeg iwwerpréift ginn. Opzeechnungen, déi dës Schwell iwwerschreiden, gi markéiert fir obligatoresch Iwwerpréiwung fir d'Reguléierungskonformitéit z'erhalen.",
            runs_data_d1_lifecycle_info: 'Orchestration Laf Staaten Kaart op de DATA-D1 Liewenszyklus: Planung = Katalog, prett = klassifizéieren, in_progress = Regelen gëllen, fäerdeg = Beweis generéiert, completed_with_exceptions = Iwwerpréiwung néideg. All Iwwergank gëtt an der Beweiskette opgeholl.',
            vendor_record_linkage_info: "All Verkeefer Läschen Ufro ass verbonne mat sengem Verkeefer Rekord iwwer vendor_id. Ofschlossbestätegung erfuerdert kryptographesch Beweiser vun der Beweiskette ier d'Ufro als fäerdeg markéiert ka ginn.",
            templates_internal_classification_info: 'All Läschchecklëscht Template ginn als intern organisatoresch Dokumenter klasséiert. Si droen keng offiziell reglementaresch Form IDen a sollen net als reglementaresche Areeche presentéiert ginn.',
            proof_chain_evidence_info: 'All Beweisrekord erfaasst den Datesubjekt/Kategorie, Zäitstempel, Schauspiller, legal Basis an en SHA-256 Hash. Zesummen produzéieren dës Felder kryptografesch Läschbeweiser déi reglementaresch Auditfuerderunge entspriechen.',
            retention_data_law_info: "Retentiounspläng op dësem Dashboard ginn ofgeleet vun der Dategesetz Klassifikatioun, déi un all Datekategorie zougewisen ass. Wann eng Klassifikatioun ännert, aktualiséieren verlinkte Retentiounsregelen automatesch fir d'Reguléierungsausrichtung ze garantéieren.",
            dsr_erasure_route_info: 'DSR Erausfuerderunge si verbonne mat dem ursprénglechen Rechter Ufro Fall. De reglementaresche Frist (typesch 30 Deeg ënner GDPR Art. 17) gëtt automatesch verfollegt. Iwwerfälleg Fäll gi fir Eskalatioun markéiert.',
        },
    },
    discovery: {
        nav: {
            dashboard: 'Erkennungsiwwersiicht',
            scans: 'Erkennungsscans',
            staging_area: 'Bereetstellungsberäich',
            dedup_rules: 'Deduplizéierungsregelen',
            classification_rules: 'Klassifizéierungsregelen',
            suggestions: 'Virschlagswuerteschlang',
            analytics: 'Virschlagsanalyse',
            import_templates: 'Importvirlagen',
            connectors: 'Konnektorverwaltung',
            discovery_cycles: 'Erkennungszyklen',
            owner_review: 'Besëtzeriwwerpréiwung',
            classification_lineage: 'Klassifizéierungshistorik',
        },
        dashboard: {
            title: 'Erkennungsiwwersiicht',
            loading: 'Lueden vun dashboard Donnéeën...',
            total_scans: 'Gesamt Scans',
            active_scans: 'Aktiv Scans',
            completed_30d: 'Ofgeschloss (30d)',
            failed_30d: 'Gescheitert (30D)',
            systems_discovered: 'Systemer Entdeckt',
            pending_staged_records: 'Ausstehend Staged Datesätz',
            recent_scans: 'Rezent Scans',
            no_recent_scans: 'Keng rezent Scans.',
            scan_id: 'Scannen ID',
            discovered: 'Entdeckt',
            view_all_scans: 'Ukucken All Scans',
            staging_area: 'Staging-Beräich',
            suggestion_queue: 'Virschlag Waardeschlang',
            records_reviewed: 'Iwwerpréifte Dateien',
            records_approved: 'Guttgeheeschte Dateien',
            exceptions_pending: 'Oppen Ausnahmen',
            view_as_chart: 'Als Diagramm weisen',
            view_as_table: 'Als Tabell weisen',
        },
        scans: {
            title: 'Erkennungsscans',
            new: 'Nei Scannen',
            scan_id: 'Scannen ID',
            scan_type: 'Scannen Typ',
            progress: 'Fortschrëtt',
            discovered: 'Entdeckt',
            no_scans: 'Keng discovery scans fonnt.',
            all_scan_types: 'All Scannen Typen',
            source_config: 'Quell Konfiguratioun (JSON)',
            invalid_json: 'Ongëlteg JSON an source config',
            load_error: 'Konnt net load discovery scans. Probéiert et nach eng Kéier.',
            create_error: 'Konnt net create discovery Scan',
            change_status: 'Status änneren',
            new_status: 'Neie Status',
            actions: 'Aktiounen',
            type_endpoint: 'Endpunkt',
            type_spreadsheet_import: 'Tabelle-Import',
            type_cmdb_sync: 'CMDB-Synchroniséierung',
            type_cloud_sync: 'Cloud-Synchroniséierung',
            status_pending: 'Waardend',
            status_running: 'Leeft',
            status_completed: 'Ofgeschloss',
            status_failed: 'Fehlgeschloen',
            status_cancelled: 'Ofgebrach',
            source_config_placeholder: '{"host": "...", "port": 443}',
            adapter_not_implemented_toast: 'Dësen Scan-Typ ass nach net verfügbar. Et kënne just Endpunkt-Scanne erstallt ginn.',
            adapter_not_implemented_warning: 'Nach net verfügbar. Scanne vun dësem Typ kënnen net erstallt ginn, bis d’Ënnerstëtzung dofir verëffentlecht ass.',
            source_config_endpoint_help: 'Provide a list of FQDNs in the "domains" array. The worker resolves DNS (CNAME / MX / TXT / A) and probes HTTPS / HTTP for each domain.',
            source_config_example_label: 'Example:',
            source_config_spreadsheet_help: 'Provide the file_id of a previously uploaded CSV / XLSX and the template_id of the import template whose column mappings apply.',
            placeholder_source_config: '{"domains": ["example.com"]}',
        },
        staging_area: {
            title: 'Bereetstellungsberäich',
            record_id: 'Datesat ID',
            source_scan: 'Quell Scannen',
            entity_type: 'Entitéit Typ',
            suggested_name: 'Suggested Numm',
            confidence: 'Vertrauen',
            no_records: 'Keng staged Datesätz fonnt.',
            promote: 'Förderen',
            discard: 'Verwierfen',
            review: 'Iwwerpréiwen',
            merge: 'Zesummeféieren',
            merge_title: 'Merge Record',
            merge_target_id: 'Zesummeféieren Zil ID',
            reject_reason_placeholder: 'Grond fir rejection...',
            promotion_status: 'Förderung Status',
            dedup_status: 'Deduplikatiounsstatus',
            all_source_types: 'All Quell Typen',
            all_promotion_statuses: 'All Förderung Statuses',
            all_dedup_statuses: 'All Deduplikatiounsstatussen',
            load_error: 'Konnt net load staged Datesätz. Probéiert et nach eng Kéier.',
            promote_error: 'Konnt net promote Datesat',
            reject_error: 'Konnt net reject Datesat',
            merge_error: 'Konnt net merge Datesat',
            review_error: 'Konnt net mark Datesat fir Iwwerpréifung',
            bulk_promote_error: 'Konnt net bulk promote Datesätz',
            bulk_reject_error: 'Konnt net bulk reject Datesätz',
            service_type_web_server: 'Webserver',
            service_type_database: 'Datebank',
            service_type_api_gateway: 'API-Gateway',
            service_type_storage: 'Späicher',
            service_type_authentication: 'Authentifikatioun',
            service_type_analytics: 'Analytik',
            service_type_unknown: 'Onbekannt',
            source_type_endpoint_scan: 'Endpunkt-Scan',
            source_type_spreadsheet: 'Tabelle',
            source_type_cmdb: 'CMDB',
            source_type_cloud_inventory: 'Cloud-Inventar',
            promotion_status_new: 'Nei',
            promotion_status_under_review: 'An der Iwwerpréiwung',
            promotion_status_promoted: 'Promovéiert',
            promotion_status_rejected: 'Ofgeleent',
            promotion_status_merged: 'Fusionéiert',
            dedup_status_unmatched: 'Keng Iwwerasteммung',
            dedup_status_potential_match: 'Méiglech Iwwerasteммung',
            dedup_status_exact_match: 'Exakt Iwwerasteммung',
            dedup_status_resolved: 'Geléist',
            confirm_bulk_reject: 'Sidd Dir sécher, datt Dir déi ausgewielten Datensätz oflehne wëllt?',
            scan_batch_id: 'Batch-ID',
            promote_to_registry_title: 'Promotioun an Dateregister',
            promote_modal_instructions: "Erstellt en Dateregister Element mat collection_method: beobachtet a Provenance Verknëppung zu dësem Entdeckungsrekord. D'Element fänkt am Entworf Status fir Iwwerpréiwung.",
            promote_label_category: 'Kategorie',
            promote_label_name_override: 'Numm (optional Iwwerschreiden)',
            placeholder_name_override: 'Default fir den Numm / Hostnumm vum Entdeckungsrekord',
            promote_label_sensitivity: 'Empfindlechkeet proposéiert',
            inherit_from_category: 'Ierwen aus Kategorie',
            dedup_status_help: 'Whether this discovered item already exists in inventory. Unmatched = safe to promote; Potential / Exact Match = review or merge first.',
            promote_to_registry: 'Promote to Registry',
            promotion_status_help: 'Where this record is in the review pipeline: New → Under Review → Promoted (or Rejected / Merged).',
            route_to_owner: 'Route to Owner',
            route_to_owner_error: 'Failed to route to owner.',
            route_to_owner_success: 'Owner review task created. Open Owner Review to resolve it.',
            service_type: 'Service Type',
        },
        dedup_rules: {
            title: 'Deduplizéierungsregelen',
            new: 'Nei Reegel',
            rule_name: 'Reegel Numm',
            match_fields: 'Iwwereneestëmmen Felder',
            threshold: 'Schwellwäert',
            no_rules: 'Keng dedup rules fonnt.',
            seed_defaults: 'Initialiséieren Standardwäerter',
            match_type: 'Iwwereneestëmmen Typ',
            similarity_threshold: 'Similarity Schwellwäert',
            field_name: 'Feld Numm',
            load_error: 'Konnt net load dedup rules. Probéiert et nach eng Kéier.',
            create_error: 'Konnt net create dedup Reegel',
            toggle_error: 'Konnt net toggle dedup Reegel',
            seed_error: 'Konnt net seed defaults',
            priority: 'Prioritéit',
            enabled_status: 'Aktivéiert',
            disabled_status: 'Behënnert',
            default_badge: 'Default',
            enable: 'Aktivéieren',
            disable: 'Desaktivéieren',
            match_type_exact: 'Exakt',
            match_type_fuzzy: 'Onscharf',
            match_type_regex: 'Regex',
            match_type_ip_range: 'IP-Beräich',
            threshold_validation: 'Must be a number between 0 and 1',
        },
        classification_rules: {
            title: 'Klassifizéierungsregelen',
            new: 'Nei Reegel',
            rule_name: 'Reegel Numm',
            pattern: 'Muster',
            classification: 'Klassifikatioun',
            no_rules: 'Keng classification rules fonnt.',
            seed_defaults: 'Initialiséieren Standardwäerter',
            run_rules_now: 'Run Regelen Elo',
            run_rules_success: 'Generéiert {count} nei Klassifikatiounsvirschlag(en)',
            run_rules_no_results: 'Keng nei Suggestiounen generéiert - all passende Reegelen hu scho Suggestiounen',
            run_rules_error: 'Ausgefall Klassifikatioun Regelen ze lafen. Probéiert w.e.g. nach eng Kéier.',
            taxonomy_id: 'Taxonomie ID',
            level_id: 'Niveau ID',
            condition_logic: 'Bedingung Logic',
            weight: 'Gewiicht',
            conditions: 'Bedingungen',
            condition_field: 'Feld',
            condition_operator: 'Bedreiwer',
            condition_value: 'Wäert',
            effectiveness: 'Wierksamkeet',
            match_count: 'Iwwereneestëmmungen',
            approval_count: 'Genehmegungen',
            rejection_count: 'Ableenungen',
            filter_taxonomy: 'Filteren no Taxonomie ID...',
            load_error: 'Konnt net load classification rules. Probéiert et nach eng Kéier.',
            create_error: 'Konnt net create classification Reegel',
            seed_error: 'Konnt net seed defaults',
            toggle_error: 'Konnt net toggle Reegel. Probéiert et nach eng Kéier.',
            priority: 'Prioritéit',
            enabled_status: 'Aktivéiert',
            disabled_status: 'Behënnert',
            enable: 'Aktivéieren',
            disable: 'Desaktivéieren',
            logic_and: 'AN',
            logic_or: 'ODER',
            operator_equals: 'Gläich',
            operator_contains: 'Enthält',
            operator_matches_regex: 'Entsprécht Regex',
            operator_in_list: 'An der Lëscht',
            operator_not_equals: 'Net gläich',
            operator_not_contains: 'Enthält net',
            enabled_label: 'Aktivéiert',
            disabled_label: 'Desaktivéiert',
            taxonomy_label: 'Taxonomie',
            operator_label: 'Bedreiwer',
            value_label: 'Wäert',
            conditions_description: 'Define the signals that must match before this classification is applied.',
        },
        suggestions: {
            title: 'Virschlagswuerteschlang',
            entity_type: 'Entitéit Typ',
            suggested_name: 'Suggested Numm',
            source: 'Quell',
            confidence: 'Vertrauen',
            accept: 'Akzeptéieren',
            reject: 'Oflehnen',
            no_suggestions: 'No Virschléi an queue.',
            modify_approve: 'Änneren & Genehmegen',
            delegate: 'Delegéieren',
            delegate_user_id: 'Delegéieren Benotzer ID',
            new_level_id: 'Nei Niveau ID',
            reject_reason_placeholder: 'Grond fir rejection...',
            current_level: 'Aktuell Niveau',
            suggested_level: 'Suggested Niveau',
            filter_taxonomy: 'Filteren no Taxonomie ID...',
            all_confidence: 'All Vertrauen Bands',
            load_error: 'Konnt net load Virschléi. Probéiert et nach eng Kéier.',
            approve_error: 'Konnt net approve Virschlag',
            reject_error: 'Konnt net reject Virschlag',
            modify_error: 'Konnt net modify & approve Virschlag',
            delegate_error: 'Konnt net delegate Virschlag',
            bulk_approve_error: 'Konnt net bulk approve Virschléi',
            bulk_reject_error: 'Konnt net bulk reject Virschléi',
            suggestion_type_new_label: 'Neit Label',
            suggestion_type_validation: 'Validéierung',
            suggestion_type_reclassification: 'Neiklassifizéierung',
            status_pending: 'Waardend',
            status_approved: 'Guttgeheescht',
            status_rejected: 'Ofgeleent',
            status_superseded: 'Ersat',
            confidence_high: 'Héich',
            confidence_medium: 'Mëttel',
            confidence_low: 'Niddreg',
            confirm_bulk_reject: 'Sidd Dir sécher, datt Dir déi ausgewielten Virschléi oflehne wëllt?',
            unknown_level_tooltip: 'This rule references a level that is no longer defined in the taxonomy.',
            all_taxonomies: 'All Taxonomien',
        },
        analytics: {
            title: 'Virschlagsanalyse',
            total_suggestions: 'Gesamt Virschléi',
            accepted: 'Akzeptéiert',
            rejected: 'Ofgeleent',
            pending: 'Ausstehend',
            acceptance_rate: 'Acceptance Quote',
            by_entity_type: 'By Entitéit Typ',
            by_source: 'By Quell',
            avg_decision_time: 'Avg Entscheedung Zäit',
            rule_effectiveness: 'Reegel Wierksamkeet',
            by_status: 'No Status',
            by_confidence: 'By Vertrauen Band',
            load_error: 'Konnt net load Virschlag analytics. Probéiert et nach eng Kéier.',
            confidence_high: 'Héich',
            confidence_medium: 'Mëttel',
            confidence_low: 'Niddreg',
            by_confidence_band: 'No Vertrauensstufe',
            approval_rate: 'Genehmigungsquote',
            by_label_state: 'No Label-Status',
            no_data_hint: 'No suggestions yet. Promote a staged record or click "Run Rules Now" on the Classification Rules page to generate the first batch.',
            rejection_rate: 'Rejection Rate',
        },
        import_templates: {
            title: 'Importvirlagen',
            new: 'Nei Schabloun',
            template_name: 'Schabloun Numm',
            file_format: 'Fichier Format',
            column_mappings: 'Spalt Mappings',
            no_templates: 'Keng Import Schablounen fonnt.',
            source_column: 'Quell Spalt',
            target_field: 'Zil Feld',
            transform: 'Transforméieren',
            source_column_label: 'Quell Kolonn',
            target_field_label: 'Zilfeld',
            transform_label: 'Transforméieren',
            confirm_delete: 'Are you sure you want op delete dëst Import Schabloun?',
            load_error: 'Konnt net load Import Schablounen. Probéiert et nach eng Kéier.',
            save_error: 'Konnt net save Import Schabloun',
            delete_error: 'Konnt net delete Import Schabloun',
            file_type_csv: 'CSV',
            file_type_xlsx: 'XLSX',
        },
        connectors: {
            title: 'Konnektorverwaltung',
            new: 'Nei Konnektor',
            connector_name: 'Konnektor Numm',
            connector_type: 'Typ',
            last_sync: 'Lescht Synchroniséierung',
            no_connectors: 'Keng connectors fonnt.',
            cmdb_syncs: 'CMDB-Synchroniséierungen',
            cloud_syncs: 'Cloud-Synchroniséierungen',
            new_sync: 'Nei Synchroniséierung',
            no_syncs: 'Keng syncs fonnt.',
            cmdb_error: 'Konnt net load CMDB scans. Probéiert et nach eng Kéier.',
            cloud_error: 'Konnt net load cloud scans. Probéiert et nach eng Kéier.',
            trigger_error: 'Konnt net trigger Synchroniséierung',
            trigger_sync_error: 'Feeler beim trigger {type}',
            cmdb_section_title: 'CMDB-Synchronisatiounen',
            cloud_section_title: 'Cloud-Synchronisatiounen',
            column_connector: 'Konnektor',
            action_start: 'Start',
            confirm_cancel_sync: 'Dës Synchronisatioun ofbriechen?',
            start_error: 'Synchronisatioun konnt net gestart ginn',
            cancel_error: 'Synchronisatioun konnt net ofgebrach ginn',
            type_endpoint: 'Endpunkt-Scan',
            type_spreadsheet_import: 'Tabellen-Import',
            type_cmdb_sync: 'CMDB-Synchronisatioun',
            type_cloud_sync: 'Cloud-Synchronisatioun',
        },
        discovery_cycles: {
            title: 'Erkennungszyklen',
            new_cycle: 'Neien Erkennungszyklus',
            cycle_name: 'Zyklusnumm',
            scope_type: 'Ëmfangstyp',
            scope_ids: 'Ëmfangs-IDen',
            cadence_days: 'Frequenz (Deeg)',
            status: 'Status',
            no_cycles: 'Keng Erkennungszyklen fonnt',
            compare: 'Zyklen vergläichen',
            delta_report: 'Ännerungsbericht',
            new_records: 'Nei Datesätz',
            changed_records: 'Geännert Datesätz',
            removed_records: 'Ewechgeholl Datesätz',
            schedule: 'Scan plangen',
            scope_organization: 'Organisatioun',
            scope_department: 'Departement',
            scope_system: 'System',
            scope_systems: 'Systemer',
            bulk_actions: 'Massenaktiounen',
            selected: '{count} ausgewielt',
            bulk_create: 'Masseerstellen',
            bulk_assign_scope: 'Massenzuweisen Ëmfang',
            bulk_advance: 'Masseavancéieren',
            confirm_bulk_action: 'Massenaktioun bestätegen',
            bulk_confirm_message: 'Sidd Dir sécher datt Dir dës Aktioun ausféiere wëllt?',
            cycles_affected: '{count} Zyklen wäerten betraff sinn',
            select_all: 'All auswielen',
            select_cycle: 'Zyklus {name} auswielen',
            from_template: 'vun Virlag',
            bulk_results: 'Masseresultater',
            failed_label: 'gescheitert',
            template_id: 'Virlag-ID',
            count: 'Unzuel',
            advance_to: 'Zilstatus',
            bulk_success: 'Massoperatioun ofgeschloss',
        },
        owner_review: {
            title: 'Besëtzeriwwerpréiwungsopgaben',
            assign_owner: 'Dem Besëtzer zouweisen',
            owner_id: 'Besëtzer',
            decision: 'Entscheedung',
            approve: 'Genehmegen',
            modify: 'Änneren',
            reject: 'Ofleenen',
            escalate: 'Eskaléieren',
            no_tasks: 'Keng Iwwerpréiwungsopgaben fonnt',
            bulk_resolve: 'Massenopléisung',
            exceptions: 'Ausnamposten',
            no_exceptions: 'Keng Ausnamposten fonnt',
            record_id_label: 'Rekord ID',
            selected: 'ausgewielt',
            notes: 'Notizen',
            select_all: 'All Auswielen',
            select: 'Auswielen',
            confirm_resolve: 'Bestätegen',
        },
        classification_lineage: {
            title: 'Klassifizéierungshistorik',
            link_suggestion: 'Mat proposéiertem Label verbannen',
            suggestion_id: 'Virschlag',
            proposed_label_id: 'Proposéiert Label',
            no_lineage: 'Keng Historikdaten fonnt',
            full_lineage: 'Voll Historik',
            record_id_label: 'Rekord ID',
            staged_record_label: 'Inszenéiert Rekord',
            enter_staged_record_id: 'Gitt inszenéiert Rekord ID',
            scan_id_label: 'Scan ID',
            select_record: 'En Datensaz auswielen',
            select_suggestion: 'E Virschlag auswielen',
            select_label: 'En Etikett auswielen',
        },
        common_labels: {
            n_rules: '{n} Regelen',
            one_rule: '1 regel',
            n_scans: '{n} scannt',
            one_scan: '1 scannt',
            n_syncs: '{n} synchroniséiert',
            one_sync: '1 synchroniséiert',
            n_records: '{n} records',
            one_record: '1 record',
            n_templates: '{n} Schablounen',
            one_template: '1 Schabloun',
            n_suggestions: '{n} Virschléi',
            one_suggestion: '1 Virschlag',
            n_selected: '{n} ausgewielt',
            dashboard_loaded: 'Entdeckung Dashboard gelueden',
            n_scans_loaded: '{n} scannt gelueden',
            n_staged_records_loaded: '{n} inszenéiert records gelueden',
            confirm_delete_import_template: 'Sidd Dir sécher datt Dir dës Importschabloun läsche wëllt?',
            invalid_json_source_config: 'Ongülteg JSON an source_config',
            scope_organization: 'Organisatioun',
            scope_department: 'Departement',
            scope_system: 'System',
            aria_filter_scans: 'Filter scannt',
            aria_filter_staged_records: 'Filter inszenéiert records',
            aria_filter_suggestions: 'Filteren',
            aria_filter_by_taxonomy_id: 'Filter no Taxonomie ID',
            aria_filter_by_enabled_status: 'Filter no ageschalt Status',
            aria_filter_by_source_type: 'Filter no Quelltyp',
            aria_filter_by_promotion_status: 'Filter no Promotiounsstatus',
            aria_filter_by_dedup_status: 'Filter no dedup Status',
            aria_filter_by_scan_type: 'Filter no Scannentyp',
            aria_filter_by_status: 'Filter no Status',
            aria_filter_by_confidence: 'Filter no Vertrauensband',
            aria_select_all_records: 'Wielt all records',
            aria_select_record: 'Wielt Rekord {id}',
            aria_select_all_suggestions: 'Wielt all Virschléi',
            aria_select_suggestion: 'Wielt Virschlag {id}',
            aria_promote_record: 'Promotioun Rekord {id}',
            aria_discard_record: 'Verwerfen Rekord {id}',
            aria_merge_record: 'Fusiounsrekord {id}',
            aria_review_record: 'Iwwerpréift Rekord {id}',
            aria_accept_suggestion: 'Akzeptéiert Virschlag {id}',
            aria_reject_record: 'Oflehnen Rekord',
            aria_merge_record_dialog: 'Fusioun Rekord',
            aria_create_new_scan: 'Erstellt neie Scan',
            aria_sync_progress: 'Synchroniséiert Fortschrëtter: {processed} vun {total} Elementer veraarbecht',
            aria_scan_progress: 'Scan Fortschrëtter: {processed} vun {total} Elementer veraarbecht',
            aria_confidence: 'Vertrauen: {pct}%',
            aria_effectiveness: 'Effektivitéit: {pct}',
            aria_promote_n_records: 'Promotioun {n} ausgewielt records',
            aria_discard_n_records: 'Wegwerfen {n} ausgewielt records',
            placeholder_pii_detection_rule: 'e.g., PII Detektioun Regel',
            placeholder_tax_id: 'z.B. TAX-001',
            placeholder_lvl_id: 'z.B. LVL-001',
            placeholder_service_type: 'z.B. service_type',
            placeholder_database: 'z.B. Datebank',
            placeholder_hostname_match: 'zB Hostnumm Exact Match',
            placeholder_hostname: 'z.B. Hostnumm',
            placeholder_hr_systems_csv: 'z.B. HR Systems CSV Import',
            placeholder_server_name: 'e.g., Server Numm',
            placeholder_hostname_field: 'z.B. Hostnumm',
            placeholder_lowercase: 'z.B. Kleinbuchstaben',
            placeholder_source_config: '{"host": "...", "port": 443}',
            placeholder_stg_id: 'z.B. STG-000123',
            placeholder_lvl_002: 'z.B. LVL-002',
            placeholder_usr_id: 'z.B. USR-000123',
            decision_for: 'Entscheedung fir {id}',
        },
        exports: {
            title: 'Exportéieren',
            inventory: 'Dateninventar',
            classification_register: 'Klassifikatiouns-Register',
            raci: 'RACI-Matriiss',
            exceptions: 'Ausnamebericht',
            evidence_pack: 'Beweispaket',
            delta_report: 'Ännerungsbericht',
            executive_summary: 'Direktiouns-Resumé',
            export_button: 'Exportéieren',
            downloading: 'Gëtt erofgelueden...',
            download_complete: 'Download fäerdeg.',
            download_error: 'Export-Download huet net geklappt. Probéiert et nach eng Kéier.',
        },
        flags: {
            title: 'Nofolger-Markéierungen',
            pending: 'Ausstoend Markéierungen',
            cross_border_review_needed: 'Grenziwwerschreidend Iwwerpréiwung néideg',
            sharing_disclosure_needed: 'Deelungsoffenlegung néideg',
            dpia_review_needed: 'DSFA-Iwwerpréiwung néideg',
            acknowledge: 'Bestätegen',
            resolve: 'Léisen',
            no_flags: 'Keng Nofolger-Markéierungen.',
        },
        delta: {
            title: 'Ännerungsiwwersiicht',
            new_count: '{n} nei',
            removed_count: '{n} ewechgeholl',
            changed_count: '{n} geännert',
            re_review_triggered: 'Nei Iwwerpréiwung ausgeléist',
            create_re_review: 'Nei Iwwerpréiwung erstellen',
            recent_alerts: 'Rezent Ännerungswarnungen',
            no_alerts: 'Keng rezent Ännerungswarnungen.',
            threshold_exceeded: 'Ännerungsschwellenwäert iwwerschratt (>10%)',
        },
        regulatory: {
            dashboard_governance_info: 'Dëst Dashboard weist Zyklus-Niveau Gouvernance Status: Systemer gescannt, records inszenéiert, records iwwerpréift, records guttgeheescht, an Ausnahmen waarden. Dëst entsprécht de Gouvernance Lead Erfolleg Critèren: "Live Dashboard vun wien hiren Inventar aktualiséiert huet, Klassifikatioun an Deele Logbicher" (P-VN-05).',
            export_artifacts_info: 'Entdeckungsexport enthält all erfuerderlech Artefakttypen pro UC-VN-10 an DATA-EVD-01: guttgeheescht Datinventar, Klassifikatiounsregister, Eegentum / RACI Kartéierung, Ausnahmsschlaang, Exekutiv Zesummefaassung, Beweis Pack, an Delta Rapport.',
            scan_inventory_pipeline_info: "All fäerdeg Scan produzéiert records kompatibel mat der vn_data_law_data_inventory_register Schabloun (Ufuerderung 88dd9877). Entdeckt Dateverméigen populéieren automatesch d'Dateinventarregister fir d'Dategesetz Verpflichtung ze erfëllen fir en digitalen Dateinventar z'erhalen.",
            connector_linkage_info: 'All Connector Konfiguratioun muss enthalen: Systemtyp (zB CMDB, Cloud Plattform), Dategeschäft Referenz (Datebank, Dateigeschäft, SaaS), a Geschäftsprozessverbindung (UC-VN-10 erfuerderlech Ënnerstëtzungsrecords). Dës Felder garantéieren datt entdeckte Verméigen op hire Quellsystem a Geschäftskontext tracéiert sinn.',
            owner_routing_info: 'Iwwerpréiwung Aufgaben sinn op déi richteg Departement oder Applikatioun Besëtzer rout baséiert op System / Donnéeën Besëtz (UC-VN-10 Schrëtt 5). Routing benotzt de System Besëtzer Register, net ad-hoc Aufgab.',
            owner_actions_audit_info: 'Besëtzer kënnen all Klassifikatioun approuvéieren, änneren, refuséieren oder eskaléieren (UC-VN-10 Schrëtt 6). All Aktioun erfaasst Schauspiller, Zäitstempel a Grond, produzéiert Gouvernance Audit Beweiser Eventer pro Fuerderung f12fc090 (Jores Gouvernance Attestation). Den Audit Trail weist wien iwwerpréift huet, wat geännert gouf a wéini.',
            escalation_timers_info: "Eskalatioun Timer: Besëtzer Bewäertung Frist ass 5 Aarbechtsdeeg; Ausnam Eskalatioun ass 48 Stonnen (UC-VN-10 Timer). Ongeléist Elementer eskaléieren automatesch wann d'Deadline ofleeft. Timer sinn konfiguréierbar pro Organisatiounspolitik.",
            original_suggestion_preserved_info: "D'Original Maschinn / System Klassifikatioun Virschlag ass nieft all Besëtzer iwwerdribblen preservéiert (UC-VN-10 Beweis Noutwendegkeete). Den originelle Virschlag gëtt ni iwwerschriwwen a bleift fir Auditzwecker siichtbar.",
            dedup_lineage_info: 'Deduplikatiounsregele verhënneren duplizéiert Inventaire Entréen wann deeselwechten Dateschutz iwwer verschidde Scans oder Connectoren entdeckt gëtt. Déi rezent Scan Metadaten ginn als primäre Rekord bewahrt, wärend Lineage Referenzen op all fréier Entdeckungen fir Audit Tracabilitéit behalen.',
            label_states_info: "Analytics ënnerscheeden 4 Label Staaten fir Gouvernance Iwwerpréiwung: Maschinn-proposéiert (Erwaardung), Besëtzer-guttgeheescht, Besëtzer-geännert, a refuséiert. Dës Staaten erlaben Auditeuren d'Automatiséierungsgenauegkeet an d'Besëtzer Engagement ze bewäerten.",
            import_pipeline_info: "Import Templates mussen records produzéieren kompatibel mat der Entdeckungspipeline. Importéiert Opzeechnunge ginn d'Inszenéierung mat de selwechte Metadaten Ufuerderunge wéi gescannte Rekorder: Quellsystem Identifikatioun, Batch ID, an Zäitstempel. Kolonnemappings solle Felder zielen, déi an vn_data_law_data_inventory_register definéiert sinn.",
            lineage_full_path_info: 'Klassifikatioun Lineage verfollegt de ganze Wee: Scan / Import -> Inszenéierung -> Besëtzer Bewäertung -> guttgeheescht Label -> Enregistréiere Entrée (UC-VN-10 Schrëtt 1-10). All Etapp weist Rekord ID, Schauspiller, Zäitstempel, a Staat Transitioun fir komplett Audit Traceability.',
            staging_workflow_info: 'Inszenéiert Opzeechnunge ginn visuell vun approuvéierten Inventar Entréen duerch hir Promotiounsstatus Badge ënnerscheet (UC-VN-10 Schrëtt 3). All Rekord behält Provenance Metadaten: Quellsystem, Scan Batch ID, an Entdeckungszäitstempel fir voll Audit Tracabilitéit.',
            suggestion_data_d1_info: 'Suggestioune alignéieren mat Data Law Klassifikatioun Prozedur DATA-D1: (1) Katalog Daten Verméigen, (2) Klassifikatioun vun Empfindlechkeet Niveau, (3) Ëmgank Regelen definéieren. All Virschlag enthält Empfindlechkeet a Wichtegkeet Dimensiounen pro Noutwendegkeete 18e4bfd1.',
            downstream_flags_info: 'Artikele markéiert fir Deele, grenziwwerschreidend Transfert oder héich-Risiko Behandlung ausléise verbonnen Downstream Workflows: grenziwwerschreidend Transferbewäertung, Deele Risikobewäertung, an DPIA Iwwerpréiwung (UC-VN-10 Schrëtt 8).',
            delta_detection_info: 'Entdeckungszyklen ënnerstëtzen Deltadetektioun pro UC-VN-10 Schrëtt 11: all Zyklus weist wéi eng Opzeechnungen nei sinn, geännert oder geläscht zënter der leschter guttgeheescht Baseline. Material Ännerungen (> 10% Delta) ausléisen automatesch Re-Iwwerpréiwung Workflows.',
        },
    },
    incident_ops: {
        errors: {
            fetch: 'Lueden feelgeschloen.',
            save: 'Späicheren feelgeschloen.',
            fetch_escalation_log_failed: 'Eskalatiounsprotokoll konnt net gelueden ginn.',
            fetch_activations_failed: 'Aktivéierunge konnten net gelueden ginn.',
            deactivate_failed: 'Deaktivéierung feelgeschloen.',
            complete_activation_failed: 'Aktivéierung konnt net ofgeschloss ginn.',
            fetch_playbook_templates_failed: 'Playbook-Schablounen konnten net gelueden ginn.',
            create_playbook_template_failed: 'Playbook-Schabloun konnt net erstallt ginn.',
            clone_template_failed: 'Schabloun konnt net geklont ginn.',
            update_status_failed: 'Status konnt net aktualiséiert ginn.',
            deprecate_template_failed: 'Schabloun konnt net als veroudert markéiert ginn.',
            seed_defaults_failed: 'Standardwäerter konnten net initialiséiert ginn.',
            fetch_regulator_exports_failed: 'Behéierdenexporter konnten net gelueden ginn.',
            create_regulator_export_failed: 'Behéierdenexport konnt net erstallt ginn.',
            update_submission_status_failed: 'Areechungsstatus konnt net aktualiséiert ginn.',
            fetch_response_metrics_failed: 'Äntwertmetriken konnten net gelueden ginn.',
            fetch_sla_dashboard_failed: 'SLA-Iwwersiicht konnt net gelueden ginn.',
            fetch_sla_definitions_failed: 'SLA-Definitiounen konnten net gelueden ginn.',
            create_sla_definition_failed: 'SLA-Definitioun konnt net erstallt ginn.',
            seed_sla_defaults_failed: 'SLA-Standardwäerter konnten net initialiséiert ginn.',
            fetch_vendor_coordination_failed: 'Liwwerantkoordinatioun konnt net gelueden ginn.',
            fetch_vendor_detail_failed: 'Liwwerant-Detail konnt net gelueden ginn.',
            create_vendor_coordination_failed: 'Liwwerantkoordinatioun konnt net erstallt ginn.',
            add_communication_failed: 'Kommunikatioun konnt net bäigefüügt ginn.',
            add_info_request_failed: 'Informatiounsufro konnt net bäigefüügt ginn.',
            add_action_request_failed: 'Aktiounsufro konnt net bäigefüügt ginn.',
        },
        nav: {
            playbooks: 'Playbook-Schablounen',
            sla_definitions: 'SLA-Definitiounen',
            sla_dashboard: 'SLA-Iwwersiicht',
            escalation_log: 'Eskalatiounsprotokoll',
            regulator_exports: 'Behéierdenexporter',
            deadlines: 'Benoriichtegungsfristen',
            vendor_coordination: 'Liwwerantkoordinatioun',
            metrics: 'Äntwertmetriken',
            playbook_links: 'Playbook-Verknëppungen',
            regulatory_sla: 'Regulatoresch SLA',
            coordination_dashboard: 'Koordinatioun',
            evidence_chain: 'Beweisketten',
            post_review: 'Noinzidenz-Iwwerpréiwung',
        },
        playbooks: {
            page_title: 'Playbook-Schablounen',
            activations_page_title: 'Playbook-Aktivéierungen',
            title: 'Playbook-Schablounen',
            new: 'Nei Schabloun',
            seed_defaults: 'Standardwäerter initialiséieren',
            scenario_type: 'Szenariotyp',
            severity_applicability: 'Uwendbarkeet vum Schwéiergrad',
            all_scenario_types: 'All Szenariotypen',
            new_playbook: 'Nei Playbook-Schabloun',
            clone: 'Klonen',
            clone_template: 'Schabloun klonen',
            template_name: 'Schablounennumm',
            deprecate: 'Als veroudert markéieren',
            confirm_deprecate: 'Schabloun „{name}" als veroudert markéieren?',
            create_template: 'Schabloun erstellen',
            update_status: 'Status aktualiséieren',
            no_templates: 'Keng Playbook-Schablounen fonnt',
            deactivation_reason_placeholder: "Grond fir d'Deaktivéierung...",
            description_placeholder: 'Playbook-Beschreiwung...',
            name_placeholder: 'Playbook-Numm...',
            procedure_steps: 'Procedure Steps',
        },
        sla_definitions: {
            page_title: 'SLA-Definitiounen',
            title: 'SLA-Definitiounen',
            new: 'Nei SLA-Definitioun',
            sla_name: 'SLA-Numm',
            target_hours: 'Zilstonnen',
            escalation_after: 'Eskalatioun no',
            applies_to: 'Gëlt fir',
            no_definitions: 'Keng SLA-Definitioune fonnt.',
            target_value_placeholder: 'Zilwäert',
            name_placeholder: 'SLA-Numm...',
            threshold_hours_label: 'Schwellenwäert-Stonnen',
            severity_label: 'Schwéiergrad',
            all_types: 'All Typen',
            all_severities: 'All Schwéiergrader',
            default_label: 'Standard',
            bound_heading: 'Regulatory clocks behind the default notification target',
            bound_capped: 'Default notification-time targets are capped at {hours} h — the shortest notification period among the installed clocks already in force.',
            bound_not_capped: 'No installed notification clock in force states a period, so default notification-time targets are not capped.',
            bound_bounding_label: 'Bounded by — in force',
            bound_upcoming_label: 'Left out — not yet in force',
            bound_unreadable_label: 'Left out — timing unknown',
            bound_date_not_stated_label: 'Left out — no effective date stated',
            bound_hours: '{hours} h',
            bound_unreadable: 'The regulatory clocks behind the default notification target could not be read.',
            bound_retry_subject: 'the notification clocks',
            load_retry_subject: 'the SLA definitions',
            bound_phase_unresolved_label: 'Left out — phase start not dated',
        },
        sla_dashboard: {
            compliant: 'Konform',
            page_title: 'SLA-Iwwersiicht',
            title: 'SLA-Iwwersiicht',
            loading: 'SLA-Donnéeë gi gelueden...',
            compliance_rate: 'Konformitéitsquote',
            breached: 'Verletzt',
            at_risk: 'A Gefor',
            on_track: 'Am Plang',
            by_severity: 'No Schwéiergrad',
            total_records: 'Total Andréeg',
            met: 'Erfëllt',
            sla_records: 'SLA-Andréeg',
            sla_reference: 'SLA-Referenz',
            record_id: 'Andrag-ID',
            metric_type: 'Metriktyp',
            severity: 'Schwéiergrad',
            elapsed: 'Verlafe Zäit',
            clock_start: 'Auerstart',
            statutory_baseline: 'Statutory baseline: the notification deadline required by the frameworks this organization has installed.',
            statutory_baseline_heading: 'Statutory Baseline',
        },
        escalation_log: {
            page_title: 'Eskalatiounsprotokoll',
            title: 'Eskalatiounsprotokoll',
            incident_id: 'Inzidenz-ID',
            escalated_by: 'Eskaléiert vun',
            escalated_to: 'Eskaléiert un',
            reason: 'Grond',
            escalated_at: 'Eskaléiert um',
            no_escalations: 'Keng Eskalatioune fonnt.',
            filter_incident_id: 'No Inzidenz-ID filteren',
            escalation_id: 'Eskalatiouns-ID',
            level: 'Niveau',
            level_range: 'Niveau (L1–L4)',
            create_escalation: 'Eskalatioun erstellen',
            incident_id_placeholder: 'z. B. INC-001',
            target_role_placeholder: 'z. B. dpo, incident_manager',
            select_placeholder: '— wielen —',
        },
        regulator_exports: {
            page_title: 'Behéierdenexporter',
            title: 'Behéierdenexporter',
            new: 'Neien Export',
            incident_id: 'Inzidenz-ID',
            regulator: 'Reguléierungsbehéierd',
            export_format: 'Format',
            exported_at: 'Exportéiert um',
            no_exports: 'Keng Behéierdenexporter fonnt.',
            enter_incident_id: 'Inzidenz-ID aginn',
            export_id: 'Export-ID',
            watermark: 'Waasserzeechen',
            submission_status: 'Areechungsstatus',
            all_jurisdictions: 'All Juridiktiounen',
            no_transitions: 'Keng weider Iwwergäng verfügbar vun',
            select_new_status: '— neie Status wielen —',
            sections_included: 'Abegraff Sektiounen',
            form_classification: 'Klassifikatioun',
            submission_channel: 'Kanal',
        },
        deadlines: {
            title: 'Benoriichtegungsfristen',
            incident_id: 'Inzidenz-ID',
            deadline: 'Frist',
            regulator: 'Reguléierungsbehéierd',
            remaining: 'Verbleiwend',
            no_deadlines: 'Keng Benoriichtegungsfriste fonnt.',
        },
        vendor_coordination: {
            page_title: 'Liwwerantkoordinatioun',
            title: 'Liwwerantkoordinatioun',
            new: 'Nei Koordinatioun',
            incident_id: 'Inzidenz-ID',
            vendor_name: 'Liwwerantennumm',
            contact: 'Kontakt',
            coordination_status: 'Koordinatiounsstatus',
            status_labels: {
                initiated: 'Initiated',
                in_progress: 'In progress',
                awaiting_vendor: 'Awaiting vendor',
                vendor_responded: 'Vendor responded',
                resolved: 'Resolved',
                closed: 'Closed',
                escalated: 'Escalated',
            },
            no_items: 'Keng Liwwerantkoordinatiounselementer fonnt.',
            assessment_placeholder: 'Éischt Bewäertung vum Liwwerant...',
            coordination_id: 'Koordinatiouns-ID',
            communications: 'Kommunikatiounen',
            info_requests: 'Informatiounsufroen',
            action_requests: 'Aktiounsufroen',
            direction: 'Richtung',
            channel_label: 'Kanal',
            no_transitions: 'Keng weider Iwwergäng verfügbar.',
            vendor_ref_number: 'Referenznummer',
            report_date: 'Berichtsdatum',
            contact_email: 'E-Mail',
            contact_phone: 'Telefon',
            channel_email: 'E-Mail',
            channel_meeting: 'Sëtzung',
            channel_phone: 'Telefon',
            channel_portal: 'Portal',
            incident_id_placeholder: 'No Inzidenz-ID filteren...',
            priority_critical: 'Kritesch',
            priority_high: 'Héich',
            priority_low: 'Niddreg',
            priority_medium: 'Mëttel',
            vendor_id_placeholder: 'No Liwwerant-ID filteren...',
            description_placeholder: 'Beschreiwung...',
            filter_vendor_id: 'No Liwwerant-ID filteren',
            inbound: 'Erakommend',
            outbound: 'Erausgaend',
            summary_placeholder: 'Zesummefaassung...',
            priority_label: 'Prioritéit',
            due_date_label: 'Fällegkeetsdatum',
            info_request_desc: 'Beschreiwung vun der Informatiounsufro',
            action_request_desc: 'Beschreiwung vun der Aktiounsufro',
            content_label: 'Inhalt',
            select_incident: 'Tëschefall auswielen',
            select_vendor: 'Fournisseur auswielen',
            discovery_date: 'Entdeckung Datum',
            contact_role: 'Contact Role',
            processor_role: 'Processor Role',
            sla_elapsed: 'SLA Elapsed',
            overdue: 'Overdue',
            actor: 'Akteur',
        },
        metrics: {
            page_title: 'Äntwertmetriken',
            title: 'Äntwertmetriken',
            loading: 'Metriken-Donnéeë gi gelueden...',
            avg_response_time: 'Duerchschn. Äntwertzäit',
            avg_resolution_time: 'Duerchschn. Léisungszäit',
            total_incidents: 'Total Inzidenzen',
            by_severity: 'No Schwéiergrad',
            by_status: 'No Status',
            trend: 'Trend',
            start_sla_clock: 'SLA-Auer starten',
            sla_records: 'SLA-Andréeg',
            no_sla_records: 'Nach keng SLA-Andréeg. Klickt op „SLA-Auer starten" fir een ze erstellen.',
            record_id: 'Andrag-ID',
            severity: 'Schwéiergrad',
            progress: 'Fortschrëtt',
            incident_id_required: 'Inzidenz-ID ass erfuerderlech',
            sla_definition_required: 'SLA-Definitioun ass erfuerderlech',
            metric_type_required: 'Metriktyp ass erfuerderlech',
            threshold_required: 'Schwellenwäert-Stonne sinn erfuerderlech',
            sla_clock_started: 'SLA-Auer gestart',
            sla_definition: 'SLA-Definitioun',
            no_definitions: 'Keng SLA-Definitioune fonnt. Erstellt zéischt eng ënner „SLA-Definitiounen".',
            select_definition: 'SLA-Definitioun wielen…',
            select_metric_type: 'Metriktyp wielen…',
            select_severity: 'Schwéiergrad wielen…',
            threshold_hours: 'Schwellenwäert (Stonnen)',
            warning_threshold_pct: 'Warnungsschwellenwäert (%)',
            start_clock: 'Auer starten',
            starting: 'Start…',
            metric_label: 'Metrik',
            value_label: 'Wäert',
            incident_id: 'Inzidenz-ID',
            threshold_placeholder: 'z. B. 24',
            metric_types: {
                triage_time: 'Triage-Zäit',
                containment_time: 'Andämmungszäit',
                notification_time: 'Benoriichtegungszäit',
                resolution_time: 'Léisungszäit',
            },
            severities: {
                critical: 'Kritesch',
                high: 'Héich',
                medium: 'Mëttel',
                low: 'Niddreg',
            },
            sla_reference: 'SLA-Referenz',
        },
        playbook_links: {
            page_title: 'Playbook-Verknëppungen',
            title: 'Playbook-Benoriichtegungsverknëppungen',
            new_link: 'Playbook mat Benoriichtegung verknëppen',
            activation_id: 'Aktivéierungs-ID',
            notification_id: 'Benoriichtegungs-ID',
            select_activation: 'Eng Aktivéierung wielen...',
            select_notification: 'Eng Benoriichtegung wielen...',
            no_links: 'Keng Playbook-Verknëppunge fonnt',
            seed_pdpl: 'Playbook-Virlag-Verknëppunge bäifügen',
            created: 'Playbook-Verknëppung erstallt',
            seeded: 'Playbook-Virlag-Verknëppunge bäigefüügt',
            create_failed: 'Playbook-Verknëppung konnt net erstallt ginn',
            seed_failed: 'Playbook-Virlag-Verknëppunge konnten net bäigefüügt ginn',
            seed_already: 'The template playbook links are already present; nothing was added',
            template_activation: 'Template (not an activation of your own)',
            template_notification: 'Template (not a notification of your own)',
        },
        regulatory_sla: {
            // fix-sprint-20260811-2123-followup — keyed on the route's
            // `skipped_reason_code`. The English prose the route also returns is a
            // fallback for an unrecognised code, never the operator's explanation.
            // fix-sprint-20260808-2111 (finding #35) — the declared incident-notification
            // deadlines a seed did not turn into a mapping, and why.
            not_seeded_heading: 'Declared deadlines that were not added',
            not_seeded_intro: 'These incident-notification deadlines are declared by your installed frameworks, but no mapping was added for them:',
            not_seeded_reason: {
                unclassified: 'its deadline type is not one this table records',
                no_numeric_period: 'it states its period in words, not as a number of hours or days, so a mapping cannot hold it',
                not_yet_in_force: 'it is not yet in force: its clock starts to bind on {date}',
                activation_not_determinable: 'neither the framework nor the phase it names states when its clock starts to bind',
            },
            seed_skipped: {
                no_governing_framework: "Kee installéierte Kader reguléiert d'Reaktioun op Virfäll, dofir gëtt et keng Fristen derbäizesetzen.",
                declared_but_none_active: "E Kader fir d'Reaktioun op Virfäll ass installéiert, awer net a Kraaft.",
                no_declared_deadlines: "Déi installéiert Kaderen fir d'Reaktioun op Virfäll leeë keng Meldefristen fest.",
                declared_deadlines_withheld: 'The installed frameworks declare notification deadlines, but none could be added yet. They are listed below under the deadlines that were not added.',
                already_seeded: 'Every notification deadline the installed frameworks declare already has its mapping. Nothing new was added.',
            },
            // Fix sprint F25 — the period is rendered WITH the unit the framework
            // declared. The table hardcoded an `h` suffix, so a deadline the pack
            // states as 15 working days was shown to the operator as "15h": a
            // misstated statutory period on a regulator-facing screen. `deadline_unit`
            // had one writer and zero readers; this is the reader.
            //
            // The pack unit vocabulary is open (26 distinct values in the corpus), so
            // `unit_unknown` carries anything not listed rather than guessing hours.
            deadline_period_label: 'Frist',
            deadline_units: {
                hours: '{count} Stonnen',
                working_days: '{count} Aarbechtsdeeg',
                calendar_days: '{count} Kalennerdeeg',
                days: '{count} Deeg',
                weeks: '{count} Wochen',
                months: '{count} Méint',
                years: '{count} Joer',
            },
            deadline_unit_unknown: '{count} {unit}',
            // English until Phase 25 (R-57).
            // fix-sprint #447 W6 (owner ruling OR2) — a reference table: it sets no clock,
            // and a row is refused without the unit its period is stated in.
            reference_notice: 'This is a reference table. A mapping sets no clock and escalates nothing: no incident, breach notification or SLA reads these rows. Every regulatory deadline in the product comes from the deadline records your installed frameworks declare.',
            seed_only_conditional: 'The installed frameworks declare notification deadlines only for specific activities or conditions, so none was added automatically.',
            seed_conditional_heading: 'Deadlines not added automatically',
            seed_conditional_intro: 'These installed deadlines apply only to a stated activity or condition. The platform cannot tell whether your organisation carries it, so a person decides: if it does, add the mapping from here.',
            seed_conditional_scope_activity: 'Applies to the activity: {value}',
            seed_conditional_scope_condition: 'Applies only if: {value}',
            seed_conditional_add: 'Add this mapping',
            deadline_amount: 'Period',
            deadline_unit: 'Unit',
            select_deadline_unit: 'Select a unit...',
            deadline_unit_options: {
                hours: 'Hours',
                working_days: 'Working days',
                calendar_days: 'Calendar days',
                days: 'Days',
                weeks: 'Weeks',
                months: 'Months',
                years: 'Years',
            },
            page_title: 'Regulatoresch SLA-Zouuerdnungen',
            title: 'Regulatoresch SLA-Zouuerdnungen',
            new_mapping: 'Nei SLA-Zouuerdnung',
            sla_definition: 'SLA-Definitioun',
            select_sla_definition: 'Eng SLA-Definitioun wielen...',
            framework_code: 'Kader-Code',
            deadline_type: 'Fristtyp',
            deadline_hours: 'Frist a Stonnen',
            seed_pdpl: 'Zouuerdnunge vun installéierte Fristen bäifügen',
            no_mappings: 'Keng regulatoresch SLA-Zouuerdnunge fonnt',
            dashboard: 'Regulatoresch SLA-Iwwersiicht',
            deadline_types: {
                initial_notification: 'Éischt Benoriichtegung',
                supplement_response: 'Ergänzungsäntwert',
                authority_response: 'Behéierdenäntwert',
                remediation_report: 'Behiewungsrapport',
                subject_notification: 'Data Subject Notification',
            },
            statutory_reference: 'Gesetzlech Referenz',
        },
        coordination_dashboard: {
            page_title: 'Koordinatiounsiwwersiicht',
            title: 'Koordinatiounsiwwersiicht',
            incident_id: 'Inzidenz-ID',
            select_incident: 'En Inzidenz wielen...',
            team_role: 'Teamroll',
            members: 'Memberen',
            tasks: 'Aufgaben',
            sla_status: 'SLA-Status',
            no_teams: 'Keng Koordinatiounsteame fonnt',
            new_team: 'Neit Koordinatiounsteam',
            enter_incident_id: "Gitt eng Inzidenz-ID an, fir d'Koordinatiounsteamen ze gesinn.",
            member_ids_placeholder: 'Benotzer1, Benotzer2',
            edit_team: 'Equipe änneren',
            task_count: 'Aufgab Zuel',
            tasks_completed: 'Ofgeschloss',
            team_roles: {
                security: 'Sécherheet',
                legal: 'Juristesch',
                it: 'IT',
                management: 'Direktioun',
                department_owner: 'Departementsverantwortlechen',
                external: 'Extern',
            },
        },
        evidence_chain: {
            page_title: 'Beweisketten',
            title: 'Beweisketten (Chain of Custody)',
            new_entry: 'Neien Beweisandrag',
            select_incident: 'En Inzidenz wielen...',
            actor: 'Akteur',
            select_actor: 'En Akteur wielen...',
            evidence_id: 'Beweis-ID',
            action: 'Aktioun',
            recipient: 'Empfänger',
            no_entries: 'Keng Beweiskettenandréeg fonnt',
            enter_incident_id: "Gitt eng Inzidenz-ID an, fir d'Beweisketten ze gesinn.",
            evidence_id_placeholder: 'z. B. EVD-2026-0001',
            evidence_id_help: 'Eng eenzegaarteg Referenz fir de Beweisstéck — eng Logdatei, e Disk-Bild oder e Screenshot. E Virschlag ass virausgefëllt; passt en un ären Beweiskatalog un.',
            select_incident_first: 'Wielt uewen en Tëschefall aus, ier Dir en Beweisintrag dobäisetzt.',
            for_incident: 'Fir den Tëschefall',
            actions: {
                collected: 'Gesammelt',
                shared: 'Gedeelt',
                exported: 'Exportéiert',
                archived: 'Archivéiert',
            },
        },
        post_review: {
            page_title: 'Noinzidenz-Iwwerpréiwung',
            title: 'Noinzidenz-Iwwerpréiwung',
            new_review: 'Nei Iwwerpréiwung',
            select_incident: 'En Inzidenz wielen...',
            lessons_learned: 'Geléiert Lektiounen',
            recommendations: 'Empfeelungen',
            improvement_actions: 'Verbesserungsmoossnamen',
            submit_for_review: 'Fir Iwwerpréiwung areechen',
            aria_submit_review: 'Iwwerpréiwung {name} fir Iwwerpréiwung areechen',
            aria_sign_off: 'Iwwerpréiwung {name} fräiginn',
            sign_off: 'Fräigab',
            no_reviews: 'Keng Noinzidenz-Iwwerpréiwunge fonnt',
            enter_incident_id: "Gitt eng Inzidenz-ID an, fir d'Noinzidenz-Iwwerpréiwung ze gesinn.",
            one_per_line: 'Een pro Zeil',
            statuses: {
                draft: 'Entworf',
                in_review: 'An der Iwwerpréiwung',
                signed_off: 'Fräiginn',
                closed: 'Zougemaach',
            },
        },
        activations: {
            title: 'Playbook-Aktivéierungen',
            activation_id: 'Aktivéierungs-ID',
            phases: 'Phasen',
            activated_at: 'Aktivéiert um',
            activated_by: 'Aktivéiert vun',
            deactivate: 'Deaktivéieren',
            complete: 'Ofschléissen',
            deactivation_reason: "Grond fir d'Deaktivéierung",
            evidence_button: 'Beweis',
            evidence_modal_title: 'Aktivéierungsbeweis',
            no_evidence: 'Keen Beweis un dës Aktivéierung ugehaang.',
            upload_evidence_label: 'Aktivéierungsbeweis eroplueden',
            procedure_followed: 'Procedure Followed',
            phases_completed_sr: '{completed} of {total} phases completed',
        },
    },
    program_governance: {
        // ── Sprint 65.8.0 (AC9) — the audit engagement register ─────
        //
        // The record an audit is planned, scoped, staffed, worked and closed as.
        // Every gate this copy names is the SERVER's — the lifecycle, the two
        // mandatory reasons, the calendar-date period, and the refusal to close an
        // engagement over live findings. Each is stated BEFORE the operator meets
        // it, because a rule an operator first learns as a 409 is a rule the
        // product kept to itself.
        audit_engagements: {
            title: 'Audit Engagements',
            description: 'Plan an audit, record what it covers and who is performing it, raise findings inside it, and close it once every finding has been resolved.',
            module_guide_summary: 'An audit engagement carries its scope, the period audited, who is auditing, and a lifecycle that runs from planning through fieldwork and reporting to closed. Findings are raised inside an engagement and never on their own, and an engagement cannot be closed while any of its findings is still unresolved.',
            load_error: 'The audit engagements could not be loaded. Try again.',
            counts_error: 'The engagement counts could not be read, so no totals are shown.',
            counts_label: 'Engagements by state',
            total: 'All engagements',
            filter_state: 'State',
            filter_all_states: 'All states',
            filter_auditor_kind: 'Audit type',
            filter_all_auditor_kinds: 'All audit types',
            filters_label: 'Filter audit engagements',
            col_title: 'Engagement',
            col_period: 'Period audited',
            col_auditor: 'Auditor',
            col_state: 'State',
            no_engagements: 'No audit engagements have been planned yet.',
            no_engagements_filtered: 'No audit engagement matches these filters.',
            select_hint: 'Select an engagement to see its findings and the actions available on it.',
            open_engagement: 'Open this engagement',
            selected_label: 'Selected engagement',
            new_engagement: 'Plan an engagement',
            create_title: 'Plan an audit engagement',
            section_scope: 'What is being audited',
            section_auditor: 'Who is auditing',
            field_title: 'Title',
            field_scope: 'Scope',
            field_scope_hint: 'What this engagement covers, in the words of whoever scoped it.',
            field_period_start: 'Period start',
            field_period_end: 'Period end',
            period_hint: 'Calendar dates. The period audited is a span of days somebody wrote down, so it carries no time of day.',
            period_invalid: 'The period end must not fall before the period start.',
            period_required: 'Both period dates are required, each as a calendar date.',
            field_auditor_kind: 'Audit type',
            auditor_internal: 'Internal',
            auditor_external: 'External',
            field_auditor_name: 'Auditor',
            auditor_name_hint_internal: 'The person or team performing the audit.',
            auditor_name_hint_external: 'The firm performing the audit. An external auditor has no account on this platform, so no user is named.',
            field_auditor_user: 'Platform user performing the audit',
            auditor_user_placeholder: 'Select a user…',
            auditor_user_required: 'An internal audit must name the platform user performing it.',
            created: 'Audit engagement planned',
            create_error: 'The audit engagement could not be created.',
            lifecycle: 'Lifecycle',
            lifecycle_actions: 'Actions on this engagement',
            advance_to: 'Move to {state}',
            transitioned: 'Engagement moved to {state}',
            transition_error: 'The engagement could not be moved.',
            terminal_notice: 'This engagement is {state}. Its scope, period and auditor can no longer be changed.',
            cancel_engagement: 'Cancel engagement',
            cancel_title: 'Cancel this audit engagement',
            cancel_reason: 'Why is it being cancelled?',
            cancel_reason_required: 'Cancelling an audit engagement requires a reason.',
            cancel_confirm: 'Cancel the engagement',
            cancellation_reason: 'Cancellation reason',
            close_engagement: 'Close engagement',
            close_gate_label: 'Whether this engagement can be closed',
            close_blocked: 'This engagement cannot be closed: {count} finding(s) raised inside it are still unresolved. Close or reject each one first.',
            close_ready: 'Every finding raised inside this engagement is closed or rejected, so the engagement can be closed.',
            close_gate_unknown: 'The unresolved-finding count could not be read, so the platform cannot say whether this engagement may be closed.',
            closed_on: 'Closed on',
            cancelled_on: 'Cancelled on',
            created_on: 'Planned on',
            states: {
                planned: 'Planned',
                in_fieldwork: 'In fieldwork',
                reporting: 'Reporting',
                closed: 'Closed',
                cancelled: 'Cancelled',
            },
            directory_truncated: 'Only the first {count} users are listed here.',
            directory_unavailable: 'The user directory could not be loaded, so no user can be chosen right now.',
            directory_reload: 'Reload',
            directory_reloading: 'Reloading…',
            directory_retry_subject_auditor: 'the user directory for the auditor',
            directory_retry_subject_owner: 'the user directory for the finding owner',
            reload: 'Reload',
            list_retry_subject: 'the audit engagements',
            findings_retry_subject: 'the findings',
            findings: {
                title: 'Findings',
                section_label: 'Findings raised inside this engagement',
                unresolved: 'Unresolved',
                load_error: 'The findings could not be loaded. Try again.',
                no_findings: 'No findings have been raised inside this engagement.',
                new_finding: 'Raise a finding',
                create_title: 'Raise a finding inside this engagement',
                engagement: 'Engagement',
                anchor_hint: 'A finding is always raised inside an engagement. It cannot be recorded without one.',
                field_title: 'Title',
                field_observation: 'What was observed',
                field_severity: 'Severity',
                field_owner: 'Owner',
                owner_placeholder: 'Select a user…',
                col_title: 'Finding',
                col_severity: 'Severity',
                col_owner: 'Owner',
                col_state: 'State',
                created: 'Finding raised',
                create_error: 'The finding could not be raised.',
                transitioned: 'Finding moved to {state}',
                transition_error: 'The finding could not be moved.',
                accept: 'Accept',
                start_remediation: 'Start remediation',
                close: 'Close finding',
                reject: 'Reject',
                reject_title: 'Reject this finding',
                reject_reason: 'Why is this not a finding?',
                reject_reason_required: 'Rejecting an audit finding requires a reason.',
                reject_confirm: 'Reject the finding',
                rejection_reason: 'Rejection reason',
                reject_only_from_open: 'A finding can only be rejected while it is open. Once it has been accepted, the organisation has already said it is a finding.',
                states: {
                    open: 'Open',
                    accepted: 'Accepted',
                    remediating: 'Remediating',
                    closed: 'Closed',
                    rejected: 'Rejected',
                },
            },
        },
        maturity_walkthrough: {
            run_label: 'Gitt mech duerch Lücken',
            running: 'Spadséiergäng…',
            results_heading: 'Verbesserung Fahrplang ({count} Spalt{plural})',
            tokens_label: '≈ {count} Tokens',
            no_gaps: 'Keng Lücken festgestallt - all Domainen um oder iwwer Zil.',
            col_priority: 'Prioritéit',
            col_domain: 'Domain',
            col_levels: 'Aktuell → Zil',
            col_severity: 'Gravitéit',
            col_actions: 'Sanéierungsaktiounen',
            citations_label: 'Zitater',
            accept_label: 'Akzeptéieren Fahrplang',
            reject_label: 'Entworf refuséieren',
            export_label: 'Export DOCX',
            exported_label: 'Roadmap exportéiert',
            accept_hint: 'Accepting records your decision on the saved draft; it does not change the assessment. Use Export DOCX to download the roadmap, then carry the actions you choose into tasks or your own plan.',
            resolve_summary: 'Resolutioun: {status}',
            resolve_status_accepted: 'Akzeptéiert',
            resolve_status_rejected: 'Ofgelehnt',
            error_generic: 'Konnt net Spalt Walkthrough lafen. Probéiert nach eng Kéier oder kontrolléiert de Bewäertungsstatus.',
        },
        policy_gap: {
            run_label: 'Entdeckt politesch Lücken',
            running: 'Lücken erkennen...',
            results_heading: 'Politik Lücken ({count})',
            tokens_label: '≈ {count} Tokens',
            no_gaps: 'Keng Ofdeckungslücken festgestallt.',
            col_severity: 'Gravitéit',
            col_kind: 'Aart',
            col_clause: 'Klausel / Ufuerderung',
            kind_missing: 'Vermësst Klausel',
            kind_weak: 'Schwaach Ofdeckung',
            kind_stale: 'Stale (Iwwerpréiwung eriwwer)',
            remediation_label: 'Sanéierung proposéiert',
            citations_label: 'Zitater',
            accept_label: 'Akzeptéieren Spalt Rapport',
            reject_label: 'Rapport refuséieren',
            export_label: 'Export DOCX',
            accept_hint: 'Akzeptéieren notéiert de Spaltbericht iwwer de bestännegen Entworf. Benotzt Export DOCX fir e Policy Audit Report ze generéieren; de Bedreiwer fiert déi aktuell Politik änneren.',
            resolve_summary: 'Resolutioun: {status}',
            error_generic: 'Konnt net politesch Lücken entdecken. Probéiert nach eng Kéier oder kontrolléiert den Dokumentstatus.',
        },
        remediation_plan_generator: {
            run_label: 'Generéiere Plang aus Lücken',
            running: 'Generéiere Plang…',
            results_heading: 'Sanéierungsplang ({phase_count} Phase{plural})',
            tokens_label: '≈ {count} Tokens',
            phase_label: 'Phase {index}',
            window_label: 'Fënster',
            owner_label: 'Besëtzer Roll',
            evidence_label: 'Beweistypen erfuerderlech',
            tasks_label: 'Aufgaben',
            no_phases: 'Keng Phasen produzéiert - Input Spalt-Set kann eidel sinn.',
            citations_label: 'Zitater',
            accept_label: 'Akzeptéieren Plang',
            reject_label: 'Oflehnen Plang',
            export_label: 'Export DOCX',
            accept_hint: "Accepting creates one task for each task listed (up to 5 per phase and 12 in all). Each task's description names this plan; the tasks are not linked to a record in the Remediation Plans register and have no assignee, so give each one an owner from the task list. Use Export DOCX for the full deliverable.",
            resolve_summary: 'Opléisung: {status} · {tasks} Aufgab(en) erstallt',
            error_generic: "Konnt net Sanéierungsplang generéieren. Probéiert nach eng Kéier oder kontrolléiert d'Spalt-Set-Input.",
            source_picker_label: 'Quell',
            source_all: 'All',
            source_consent: 'Zoustëmmung Spalt Analyse',
            source_policy: 'Politik Spalt Detektioun',
            source_vendor: 'Verkeefer onboarding',
            load_upstream_label: 'Lued vun Upstream',
            loading_upstream: 'Lueden…',
            upstream_count: '{count} Spalt(en) gelueden',
            export_done: 'Saved {filename} to your downloads.',
            no_gaps_in_source: 'The selected source holds no gaps, so nothing was generated and no tasks can be created. Run that analysis first, or choose another source.',
            no_gaps: 'No gaps are loaded, so there is nothing to plan. Choose a source and click Load from upstream.',
        },
        nav: {
            audit_engagements: 'Audit Engagements',
            handbook_assessments: 'Handbook Assessments',
            maturity_models: 'Reifegradmodeller',
            maturity_assessments: 'Reifgradbewäertungen',
            trend_dashboard: 'Trendiwwersiicht',
            reporting_periods: 'Berichtszäiträim',
            executive_summaries: 'Zesummefaassungen',
            audit_readiness: 'Kontrollbereetschaft',
            evidence_gaps: 'Noweislücken',
            program_health: 'Programmzoustand',
            obligation_mappings: 'Flichtenzuuerdnungen',
            governance_documents: 'Governance-Dokumenter',
            remediation_plans: 'Korrekturpläng',
            governance_model: 'Governance-Modell',
            document_drafting: 'Dokumententworf',
            document_issuance: 'Dokumentenausgab',
            acknowledgment_tracking: 'Bestätegungsverfolgung',
            change_log: 'Ännerungsprotokoll',
            review_cadence: 'Iwwerpréiwungskadenz',
        },
        maturity_models: {
            title: 'Reifegradmodeller',
            new: 'Nei Modell',
            seed_defaults: 'Initialiséieren Standardwäerter',
            domains: 'Domänen',
            activate: 'Aktivéieren',
            no_models: 'Keng maturity models fonnt',
            new_maturity_model: 'Nei Reifegkeet Modell',
            create_model: 'Erstellen Modell',
            load_error: 'Reifegradmodeller konnten net gelueden ginn. Probéiert nach eng Kéier.',
            create_error: 'Reifegradmodell konnt net erstallt ginn',
            status_error: 'Modellstatus konnt net aktualiséiert ginn',
            seed_error: 'Standardwäerter konnten net generéiert ginn',
            aria_seed_defaults: 'Standard-Reifegradmodeller generéieren',
            aria_create_new: 'Neit Reifegradmodell erstellen',
            loading_sr: 'Reifegradmodeller ginn gelueden',
            model_name_placeholder: 'z.B., Dateschutz-Reifegradmodell',
            status_changed: 'Modellstatus geännert op {status}',
        },
        maturity_assessments: {
            models_load_error: 'The maturity model list could not be loaded, so model names may show as unknown.',
            no_models_action: 'Open Maturity Models',
            no_models_body: 'An assessment scores your programme against a maturity model, and this organization has none. Create one first.',
            no_models_title: 'No maturity models yet',
            title: 'Reifgradbewäertungen',
            new: 'Nei Bewäertung',
            model: 'Modell',
            assessor: 'Bewäerter',
            overall_score: 'Gesamt Bewäertung',
            overall_score_help: 'Eng gewiichte Reifebewäertung vun 0–5, déi automatesch berechent gëtt, wann eng Bewäertung ofgeschloss gëtt. Erhéicht se, andeems Dir all Domän wärend der Evaluatioun méi héich bewäert an d’Bewäertung duerno duerch Fir Iwwerpréiwung androen → Ofschléissen féiert.',
            assessed_at: 'Bewäert den',
            no_assessments: 'Keng maturity assessments fonnt.',
            load_error: 'Reifegradbeurteilunge konnten net gelueden ginn. Probéiert nach eng Kéier.',
            create_error: 'Bewäertung konnt net erstallt ginn',
            start_error: 'Bewäertung konnt net gestart ginn',
            archive_error: 'Bewäertung konnt net archivéiert ginn',
            eval_error: 'Bewäertung konnt net gelueden ginn fir Evaluatioun',
            eval_min_score: "Bewäert op d'mannst eng Domän",
            eval_save_error: 'Domänbewäertunge konnten net gespäichert ginn',
            review_error: 'Konnt net fir Iwwerpréiwung agereecht ginn',
            finalize_error: 'Bewäertung konnt net ofgeschloss ginn',
            start: 'Starten',
            start_confirm_title: 'Bewäertung starten',
            start_confirm_message: 'Sidd Dir sécher, datt Dir dës Bewäertung starte wëllt?',
            archive_confirm_title: 'Bewäertung archivéieren',
            archive_confirm_message: 'Sidd Dir sécher, datt Dir dës Bewäertung archivéiere wëllt?',
            evaluate_domains: 'Evaluéieren',
            evaluate_domains_title: 'Domänen evaluéieren',
            submit_for_review: 'Fir Iwwerpréiwung androen',
            finalize: 'Ofschléissen',
            archive: 'Archivéieren',
            all_reviewers: 'All Préifer musse genehmegen',
            any_reviewer: 'All Préifer kann genehmegen',
            select_level: 'Reifegradniveau auswielen...',
            findings_placeholder: 'Resultater, Notizen, Evidenzreferenzen...',
            no_domains: 'Keng Domäne fir dëse Modell fonnt.',
        },
        trend_dashboard: {
            title: 'Trendiwwersiicht',
            loading: 'Lueden vun trend Donnéeën...',
            score_trend: 'Bewäertung Trend',
            domain_comparison: 'Domän Vergläich',
            period: 'Period',
            load_error: 'Trendresumé konnt net gelueden ginn. Probéiert nach eng Kéier.',
            metrics_load_error: 'Trendmetriken konnten net gelueden ginn. Probéiert nach eng Kéier.',
            module: 'Modul',
            metric: 'Kennzuel',
            value: 'Wäert',
            trend: 'Trend',
            aria_filters: 'Filteren fir Trendmetriken',
            showing_chart: 'Diagramm-Usiicht',
            showing_table: 'Datetagell-Usiicht',
            create_metric: 'Kennzuel erstellen',
            data_points: 'Datepunkten',
            trend_direction: 'Trendrichtung',
            unit: 'Eenheet',
        },
        reporting_periods: {
            title: 'Berichtszäiträim',
            new: 'Nei Period',
            period_name: 'Period Numm',
            start_date: 'Starten Datum',
            end_date: 'Enn Datum',
            no_periods: 'Keng reporting periods fonnt.',
            load_error: 'Berichtszäiträim konnten net gelueden ginn. Probéiert nach eng Kéier.',
            create_error: 'Berichtszäitraum konnt net erstallt ginn',
            auto_generate_error: 'Berichtszäiträim konnten net automatesch generéiert ginn',
            status_error: 'Periodestatus konnt net aktualiséiert ginn',
            start_collecting: 'Erhiewung ufänken',
            mark_as_draft: 'Als Entworf markéieren',
            submit_for_review: 'Fir Iwwerpréiwung androen',
            publish: 'Verëffentlechen',
            auto_generate: 'Berichtszäiträim automatesch generéieren',
            period_label_placeholder: 'z.B., Q1 2026',
            status_updated: 'Periodestatus aktualiséiert',
        },
        executive_summaries: {
            title: 'Zesummefaassungen',
            new: 'Nei Zesummefaassung',
            period: 'Period',
            generated_at: 'Generéiert At',
            generated_by: 'Generéiert By',
            no_summaries: 'Keng executive summaries fonnt.',
            generate: 'Generéieren',
            load_error: 'Führungsresumée konnten net gelueden ginn. Probéiert nach eng Kéier.',
            generate_error: 'Führungsresumé konnt net generéiert ginn',
            save_error: 'Führungsresumé konnt net gespäichert ginn',
        },
        audit_readiness: {
            title: 'Kontrollbereetschaft',
            loading: 'Lueden vun Audit readiness Donnéeën...',
            overall_readiness: 'Gesamt Readiness',
            by_domain: 'By Domän',
            evidence_coverage: 'Bewäis Ofdeckung',
            gaps: 'Lücken',
            load_error: 'Aktuell Bereetschaft konnt net gelueden ginn. Probéiert nach eng Kéier.',
            history_error: 'Bereetschaftsverlaf konnt net gelueden ginn. Probéiert nach eng Kéier.',
            calculate_error: 'Auditbereetschaft konnt net berechent ginn',
            aria_recalculate: 'Auditbereetschaftsbewäertung nei berechnen',
            aria_current_readiness: 'Aktuell Auditbereetschaft',
            aria_history: 'Verlaf vun der Bereetschaftsbewäertung',
            recalculated: 'Auditbereetschaft nei berechent',
            loading_sr: 'Auditbereetschaftsdaten ginn gelueden',
        },
        evidence_gaps: {
            title: 'Noweislücken',
            domain: 'Domän',
            control: 'Kontroll',
            gap_type: 'Lück Typ',
            severity: 'Schwéiergrad',
            no_gaps: 'Keng Bewäis Lücken fonnt.',
            load_error: 'Evidenzlücken konnten net gelueden ginn. Probéiert nach eng Kéier.',
            stats_error: 'Lückestatistiken konnten net gelueden ginn. Probéiert nach eng Kéier.',
            create_error: 'Evidenzlück konnt net erstallt ginn',
            update_error: 'Evidenzlück konnt net aktualiséiert ginn',
            status_error: 'Status konnt net aktualiséiert ginn',
            aria_create_new: 'Nei Evidenzlück erstellen',
            aria_statistics: 'Statistiken zu Evidenzlücken',
            loading_sr: 'Evidenzlücken ginn gelueden',
            status_updated: 'Lückestatus aktualiséiert',
            entity_type_placeholder: 'z.B., Liwwerant, System',
            all_modules: 'All Domänen',
            all_severities: 'All Schwéiergrader',
        },
        program_health: {
            title: 'Programmzoustand',
            loading: 'Lueden vun Programm Zoustand Donnéeën...',
            overall_health: 'Gesamt Zoustand',
            by_module: 'By Modul',
            risk_areas: 'Risiko Areas',
            recommendations: 'Empfeelungen',
            load_error: 'Programm-Gesondheetsdate konnten net gelueden ginn. Probéiert nach eng Kéier.',
            aria_key_metrics: 'Programm-Gesondheets Schlësselkennzuelen',
            aria_module_scorecard: 'Modul-Bewäertungskaarten-Opschlësselung',
            loading_sr: 'Programm-Gesondheetsdate ginn gelueden',
            trending_up: 'Opwäerts-Trend',
            trending_down: 'Owäerts-Trend',
            trending_stable: 'Stabil',
            data_loaded: 'Programm-Gesondheetsdate gelueden',
        },
        obligation_mappings: {
            title: 'Flichtenzuuerdnungen',
            create_mapping: 'Flichtenzuuerdnung erstellen',
            framework: 'Kaderwierk',
            select_framework: 'Kader auswielen...',
            obligation: 'Flicht',
            obligation_ref: 'Flichtreferenz',
            obligation_description: 'Flichtbeschreiwung',
            role: 'Roll',
            assigned_role: 'Zougewisen Roll',
            select_role: 'Eng Roll auswielen...',
            assigned_department: 'Zougewisen Abteilung',
            select_department: 'Eng Abteilung auswielen...',
            control_ids: 'Kontroll-IDen',
            control_ids_placeholder: 'z. B. A.5.1, CC6.1',
            control_ids_hint: 'Duerch Komma getrennte Kontroll-Referenzcoden.',
            raci_assignments: 'RACI-Zuuerdnungen',
            create_raci: 'RACI-Zuuerdnung erstellen',
            activity: 'Aktivitéit',
            activity_name: 'Aktivitéitsnum',
            activity_description: 'Aktivitéitsbeschreiwung',
            responsible: 'Verantwortlech',
            select_users: 'Benotzer auswielen...',
            accountable: 'Rechenschaftsflichteg',
            select_user: 'Benotzer auswielen...',
            consulted: 'Consultéiert',
            informed: 'Informéiert',
            review_frequency: 'Iwwerpréiwungsfrequenz',
            frequency_monthly: 'Monatslech',
            frequency_quarterly: 'Trimesteriell',
            frequency_semi_annual: 'Hallefjoerlech',
            frequency_annual: 'Jährlich',
            no_mappings_found: 'Keng Flichtenzuuerdnunge fonnt.',
            no_raci_found: 'Keng RACI-Zuuerdnunge fonnt.',
            mapping_created: 'Verpflichtungszuuerdnung erstallt',
            mapping_updated: 'Flichtenzuuerdnung aktualiséiert',
            mapping_deleted: 'Flichtenzuuerdnung geläscht',
            raci_created: 'RACI-Zuweisung erstallt',
            raci_updated: 'RACI-Zuuerdnung aktualiséiert',
            raci_deleted: 'RACI-Zuuerdnung geläscht',
            aria_edit_mapping: 'Zuuerdnung {name} änneren',
            aria_delete_mapping: 'Zuuerdnung {name} läschen',
            aria_edit_raci: 'RACI {name} änneren',
            aria_delete_raci: 'RACI {name} läschen',
            confirm_delete_mapping: 'Dës Flichtenzuuerdnung läschen?',
            confirm_delete_raci: 'Dës RACI-Zuuerdnung läschen?',
            table_view: 'Tabelluesiicht',
            matrix_view: 'Matrixusiicht',
            print_export: 'Drécken / Exportéieren',
            filter_framework: 'Filteren no Rahmewierk',
            no_matrix_data: 'Keng RACI-Zuweisungen fir an der Matrixusiicht unzeweisen.',
            aria_print_export: 'RACI-Matrix drécken oder exportéieren',
        },
        governance_documents: {
            title: 'Governance-Dokumenter',
            create_document: 'Governance-Dokument erstellen',
            document_title: 'Dokumenttitel',
            document_type: 'Dokumenttyp',
            content_url: 'Inhalts-URL',
            content_url_placeholder: 'https://...',
            framework_codes: 'Kadercoden',
            select_frameworks: 'Kader auswielen...',
            effective_date: 'Akraafttriedungsdatum',
            review_date: 'Iwwerpréiwungsdatum',
            status: 'Status',
            version: 'Versioun',
            acknowledgments: 'Bestätegungen',
            acknowledge: 'Bestätegen',
            acknowledged_by: 'Bestätegt vun',
            department: 'Abteilung',
            no_documents_found: 'Keng Governance-Dokumenter fonnt.',
            draft: 'Entworf',
            review: 'Iwwerpréiwung',
            approved: 'Geneemegt',
            issued: 'Ausgestallt',
            superseded: 'Ofgeléist',
            document_created: 'Dokument erstallt',
            document_acknowledged: 'Dokument uerkannt',
            policy: 'Richtlinn',
            procedure: 'Prozedur',
            standard: 'Standard',
            guideline: 'Richtlinn',
        },
        governance_model: {
            title: 'Governance-Modell',
            create_model: 'Governance-Modell',
            edit_model: 'Governance-Modell',
            model_name: 'Model',
            description: 'Beschreiwung',
            governance_type: 'Type',
            type_centralized: 'Zentraliséiert',
            type_federated: 'Federéiert',
            type_hybrid: 'Hybrid',
            framework_codes: 'Framework',
            select_frameworks: '...',
            review_frequency: 'Frequency',
            freq_monthly: 'Mount',
            freq_quarterly: 'Trimester',
            freq_semi_annual: 'Semi-jährlechen',
            freq_annual: 'Jährlech',
            committees: 'Comitée',
            committee_name: 'Name',
            committee_members: 'Members',
            committee_mandate: 'Mandat',
            add_committee: '+',
            no_committees: '—',
            delegation_rules: 'Delegation',
            from_role: 'From',
            to_role: 'To',
            scope: 'Ëmfang',
            add_delegation_rule: '+',
            control_owner_summary: 'RACI',
            no_raci_data: '—',
            activity: 'Aktivitéit',
            responsible: 'R',
            accountable: 'A',
            consulted: 'C',
            informed: 'I',
            no_models_found: '—',
            model_created: 'OK',
            model_updated: 'OK',
            model_deleted: 'OK',
            confirm_delete: '?',
            expand: '+',
            collapse: '-',
            aria_expand: '{name}',
            aria_edit: '{name}',
            aria_delete: '{name}',
            aria_export_csv: '{name} CSV',
            aria_export_json: '{name} JSON',
            framework_read_permission_warning: "Dir braucht d'regulatory_framework: liesen Erlaabnis fir Kaderen ze wielen. Frot en Administrateur et z'erkennen.",
            select_members: 'Wielt Comité Memberen',
            select_from_role: 'Wielt delegéiert Roll',
            select_to_role: 'Wielt Empfangsroll',
            mandate_markdown_hint: 'Markdown ënnerstëtzt: ** fett **, Lëschten, Linken.',
            scope_tab_obligation: 'Obligatioun',
            scope_tab_framework: 'Kader',
            scope_tab_free_text: 'Gratis Text',
            select_obligation: 'Wielt eng Obligatioun',
            select_framework: 'Wielt e Kader',
            aria_remove_committee: 'Comité {n} ewechhuelen',
            aria_remove_delegation_rule: 'Delegatiounsregel {n} ewechhuelen',
            no_frameworks_available: "Keng Kadere verfügbar. Setzt fir d'éischt reglementaresch Kadere derbäi.",
            confirm_discard_changes: 'Discard your unsaved governance model changes?',
        },
        document_drafting: {
            title: 'Dokumententworf',
            document_title: 'Document',
            document_type: 'Type',
            status: 'Status',
            drafter: 'Zeechner',
            deadline: 'Frist',
            assign_drafter: 'Assign',
            select_drafter: '...',
            assign: 'Assignéieren',
            submit_for_review: 'Submit',
            legal_review: 'Review',
            reviewer: 'Rezensor',
            select_reviewer: '...',
            comments: 'Kommentaren',
            review_action: 'Action',
            approve: 'Zoustëmmen',
            request_changes: 'Ufro Ännerungen',
            submit_review: 'Submit',
            executive_approval: 'Approval',
            no_drafts_found: '—',
            drafter_assigned: 'OK',
            status_updated: 'OK',
            review_approved: 'OK',
            changes_requested: 'OK',
            document_approved: 'OK',
            aria_assign: '{name}',
            aria_submit_review: '{name}',
            aria_review: '{name}',
            aria_approve: '{name}',
        },
        document_issuance: {
            title: 'Dokumentenausgab',
            document_title: 'Document',
            document_type: 'Type',
            status: 'Status',
            version: 'Versioun',
            effective_date: 'Date',
            distribution_scope: 'Distribution',
            distribution_placeholder: '...',
            issuance_number: 'Number',
            issuance_number_placeholder: 'ISS-2026-001',
            supersedes: 'Supersedes',
            none: '—',
            issue_document: 'Issue',
            ready_for_issuance: 'Ready',
            issued_documents: 'Issued',
            no_issued_documents: '—',
            view_lineage: 'Lineage',
            supersession_lineage: 'Supersession',
            document_issued: 'OK',
            document_superseded: 'OK',
            aria_issue: '{name}',
            aria_view_lineage: '{name}',
            no_approved_documents: 'No approved documents available to issue.',
        },
        acknowledgment_tracking: {
            title: 'Bestätegungsverfolgung',
            document_title: 'Dokument',
            acknowledged_count: 'Unerkannt',
            pending_count: 'Erwaardung',
            percentage: '%',
            details: 'Detailer',
            send_reminder: 'Remind',
            acknowledgment_details: 'Details',
            user: 'Benotzer',
            department: 'Departement',
            acknowledged_at: 'Date',
            status: 'Status',
            acknowledged: 'OK',
            no_issued_documents: '—',
            no_acknowledgments: '—',
            reminder_sent: 'OK',
            aria_expand: '{name}',
            aria_send_reminder: '{name}',
            // Fix sprint #447 W3 — recipients, acknowledging, and the reminder count.
            // English until Phase 25 (R-57).
            recipients: 'Recipients',
            record_recipients: 'Record recipients',
            aria_record_recipients: 'Record recipients for {name}',
            recipients_dialog_title: 'Recipients of {name}',
            recipients_dialog_help: 'Choose the people asked to acknowledge this document. Only people whose role can open Acknowledgment Tracking are listed, because only they can acknowledge.',
            recipients_none_eligible: 'No one in your organization holds a role that can acknowledge documents.',
            recipients_saved: 'Recipients recorded',
            recipients_not_recorded: 'Not recorded',
            acknowledge: 'Acknowledge',
            aria_acknowledge: 'Acknowledge {name}',
            acknowledge_dialog_title: 'Acknowledge {name}',
            acknowledge_dialog_body: 'You confirm that you have read this document. Your name and the time are recorded.',
            acknowledge_department: 'Department (optional)',
            reminder_sent_count: 'Reminder sent to {count} of {pending} people who have not acknowledged',
            export_documents_count: 'Documents',
            export_acknowledgments_count: 'Acknowledgments',
        },
        change_log: {
            title: 'Ännerungsprotokoll',
            date: 'Datum',
            document: 'Dokument',
            change_type: 'Type',
            actor: 'Schauspiller',
            summary: 'Resumé',
            export_csv: 'Export CSV',
            filters: 'Filters',
            filter_document: 'Document',
            all_documents: 'All',
            filter_change_type: 'Type',
            all_types: 'All',
            date_from: 'From',
            date_to: 'To',
            version_history: 'History',
            version_archive: 'Archive',
            no_entries: '—',
            no_history: '—',
            exported: 'OK',
            aria_export: 'CSV',
            aria_view_history: '{name}',
            // English until Phase 25 (R-57).
            // fix-sprint #447 W6 — `DocumentChangeType` labels; the page showed raw codes.
            type_created: 'Created',
            type_status_transition: 'Status changed',
            type_fields_updated: 'Fields updated',
            type_superseded: 'Superseded',
            type_acknowledged: 'Acknowledged',
            // fix-sprint #447 round 2 — metadata line of the server-rendered export.
            export_generated_at: 'Generated at',
        },
        review_cadence: {
            title: 'Iwwerpréiwungskadenz',
            configure_cadence: 'Iwwerpréiwungskadenz',
            edit_cadence: 'Iwwerpréiwungskadenz',
            document: 'Dokument',
            select_document: '...',
            review_frequency: 'Frequency',
            freq_30: '30',
            freq_60: '60',
            freq_90: '90',
            freq_180: '180',
            freq_365: '365',
            last_reviewed: 'Last',
            next_review: 'Next',
            status: 'Status',
            overdue: 'Iwwerhaapt',
            on_track: 'OK',
            days: 'days',
            horizon: 'Horizon',
            overdue_warning: 'Overdue',
            overdue_count: '{count}',
            no_cadences: '—',
            cadence_created: 'OK',
            cadence_updated: 'OK',
            aria_edit: '{name}',
        },
        remediation_plans: {
            title: 'Korrekturpläng',
            create_plan: 'Korrekturplang erstellen',
            gap_id: 'Lückereferenz',
            select_gap: 'Eng Lück auswielen...',
            plan_title: 'Titel',
            description: 'Beschreiwung',
            priority: 'Prioritéit',
            priority_critical: 'Kritesch',
            priority_high: 'Héich',
            priority_medium: 'Mëttel',
            priority_low: 'Niddreg',
            assigned_to: 'Zougewisen un',
            select_user: 'Benotzer auswielen...',
            target_date: 'Zildatum',
            framework_code: 'Kader',
            select_framework: 'Kader auswielen...',
            gap_finding: 'Lückefeststellung',
            action_items: 'Moossnahmepunkten',
            owner: 'Besëtzer',
            deadline: 'Frist',
            planned: 'Geplangt',
            in_progress: 'Am Gaang',
            completed: 'Ofgeschloss',
            verified: 'Verifizéiert',
            no_plans_found: 'Keng Korrekturpläng fonnt.',
            evidence: 'Noweis',
            plan_created: 'Ofhëllefsplang erstallt',
            action_items_hint: 'Moossnamen (eng pro Linn)',
        },
        statuses: {
            identified: 'Identifizéiert',
            assigned: 'zougewisen',
            in_remediation: 'An der Sanéierung',
            resolved: 'Geléist',
            accepted_risk: 'Akzeptéiert Risiko',
            planned: 'Geplangt',
            in_progress: 'Am Fortschrëtt',
            under_review: 'Ënner review',
            finalized: 'Finaliséiert',
            archived: 'Archivéiert',
            draft: 'Entworf',
            active: 'Aktiv',
            upcoming: 'Upëff',
            collecting: 'Sammelen',
            published: 'Verëffentlecht',
            review: 'Iwwerpréiwung',
            approved: 'Genehmegt',
            issued: 'Erausginn',
            superseded: 'Ersat',
            completed: 'Ofgeschloss',
            verified: 'Verifizéiert',
        },
        severities: {
            critical: 'Kritesch',
            high: 'Héich',
            medium: 'Mëttelméisseg',
            low: 'Low',
        },
        priorities: {
            critical: 'Kritesch',
            high: 'Héich',
            medium: 'Mëttelméisseg',
            low: 'Low',
        },
        gap_types: {
            missing: 'Vermësst',
            expired: 'Ofgelaaf',
            insufficient: 'Net genuch',
        },
        document_types: {
            policy: 'Politik',
            procedure: 'Prozedur',
            standard: 'Standard',
            guideline: 'Richtlinn',
            template: 'Schabloun (intern)',
        },
        maturity_levels: {
            level_1: 'Niveau 1 - Ufank',
            level_2: 'Niveau 2 - Entwécklung',
            level_3: 'Niveau 3 - Definéiert',
            level_4: 'Niveau 4 - Gestioun',
            level_5: 'Niveau 5 - Optimiséieren',
        },
        grades: {
            excellent: 'exzellent',
            good: 'Gutt',
            fair: 'Fair',
            poor: 'Aarmséileg',
            critical: 'Kritesch',
            needs_improvement: 'Braucht Verbesserung',
            not_assessed: 'Net bewäert',
        },
        modules: {
            data_mapping: 'Data Mapping',
            consent: 'Zoustëmmung',
            governance: 'Gouvernance',
            incidents: 'Tëschefäll',
            monitoring: 'Iwwerwachung',
            classification: 'Klassifikatioun',
            vendor_risk: 'Liwwerantrisiko',
            discovery: 'Entdeckung',
            deletion: 'Läschen',
        },
        cadences: {
            monthly: 'Mount',
            quarterly: 'Trimester',
            semi_annual: 'Semi-jährlechen',
            annual: 'Jährlech',
        },
        section_labels: {
            opening: 'Ouverture',
            key_metrics: 'Schlëssel Metriken',
            highlights: 'Highlights',
            concerns: 'Bedenken',
            recommendations: 'Recommandatiounen',
            outlook: 'Ausbléck',
        },
        out_of_100: '/ 100',
        count_gaps: '{count} Spalt',
        count_gaps_plural: '{count} Lücken',
        count_summaries: '{count} Resumé',
        count_summaries_plural: '{count} Zesummefaassungen',
        count_assessments: '{count} Bewäertung',
        count_assessments_plural: '{count} Bewäertungen',
        count_models: '{count} Modell',
        count_models_plural: '{count} Modeller',
        count_periods: '{count} Period',
        count_periods_plural: '{count} Perioden',
        count_metrics: '{count} Metrik',
        count_metrics_plural: '{count} Metriken',
        unknown_user: 'Onbekannt Benotzer',
        unknown_gap: 'Onbekannt Gap',
        no_permission_title: 'Net genuch Permissiounen',
        no_permission_message: 'Dir hutt keng Erlaabnis op dës Säit ze kommen.',
        ac_vn_frameworks_title: 'VN Framework Coverage',
        ac_vn_frameworks_body: 'Maturitéit Bewäertungen decken all 6 vietnamesesch reglementaresche Kaderen: PDPL, Dategesetz, Telekomgesetz, Cybersecurity Law, AI Gesetz, an E-Commerce Gesetz - jidderee mat Kaderspezifesche Bewäertungskriterien.',
        ac_controller_processor_title: 'Controller vs Prozessor',
        ac_controller_processor_body: "Obligatiounsmappingen ënnerscheeden tëscht Datekontroller an Dateprozessor Verantwortung. Assignéiert Rollen mat der RACI Matrix fir sécherzestellen datt all Verpflichtung e klore Besëtzer huet, egal ob d'Organisatioun als Controller oder Prozessor handelt.",
        ac_versioned_evidence_title: 'Versioune Beweis Trail',
        ac_versioned_evidence_body: 'All Statustransitioun (Entworf, Iwwerpréiwung, Genehmegt, Erausginn) erstellt en Zäitstempel, Versiounsrecord, deen als auditable Beweis déngt. Den Change Log erfaasst déi ganz Dokument Liewenszyklusgeschicht.',
        ac_remediation_gap_link_title: 'Zesummenhang mat Assessment Gaps',
        ac_remediation_gap_link_body: "All Sanéierungsplang verbënnt direkt mat engem identifizéierte Bewäertungslück. Gitt e Besëtzer un a set en Zilfrist fir d'Resolutioun Fortschrëtter vu Spalt bis Zoumaache ze verfolgen.",
        ac_annual_attestation_title: 'Annual Attestation Cycle',
        ac_annual_attestation_body: 'Berichterstattungsperioden entspriechen dem vietnameseschen Dategesetz alljährlechen Attestatiounsfuerderung. Benotzt d\'"Jores" Kadens (365-Deeg Zyklus) fir automatesch Attestation-ausgeriicht Perioden ze generéieren.',
        ac_evidence_per_obligation_title: 'Beweis pro Kader Obligatioun',
        ac_evidence_per_obligation_body: 'Beweislücken identifizéieren fehlend, ofgelaaf oder net genuch Beweiser fir all Kaderverpflichtung. Filter no Domain fir ze kucken wéi eng reglementaresch Ufuerderunge ënnerstëtzend Beweis feelen.',
        ac_cross_framework_title: 'Cross-Framework Vollständegkeet',
        ac_cross_framework_body: 'Audit Bereetschaft gëtt eng Kräiz-Framework Vollständegkeet Vue. Komponent Scores reflektéieren Ofdeckung iwwer all mapéierte Kaderen, beliicht Beräicher déi Opmierksamkeet brauchen ier en Audit.',
        ac_cadence_default_title: 'Standard Bewäertung Cadence',
        ac_cadence_default_body: "D'Standard Bewäertungskadens ass 180 Deeg. Verzögerte Rezensiounen ginn automatesch markéiert. Upasst d'Frequenz pro Dokument wéi néideg - Optiounen reeche vun 30 bis 365 Deeg.",
        ac_templates_internal_title: 'Schabloun Klassifikatioun',
        ac_templates_internal_body: 'Schabloune ginn als intern Dokumenter klasséiert. Benotzt den Dokumenttyp "Schabloun" fir wiederverwendbar Gouvernance Artefakte mat internen eenzegen Verdeelungsomfang ze markéieren.',
        // fix-sprint-20260822-2115 F1/F3 — this panel used to carry a module-local
        // `const COPY = { en: {…}, vi: {…} }` and pick a branch with
        // `locale.startsWith('vi') ? 'vi' : 'en'`. Both shapes are named in
        // I18N.md §2 and §3 as the root cause of 14 locales rendering English, and
        // the strings were registered in no bundle at all, so deepMerge had nothing
        // to fall back to. The four enum label maps below close F3: `status`,
        // `legal_review_status`, `evidence_state` and `answer_state` were rendering
        // raw slugs next to an already-labelled badge on the same row.
        handbook_assessment: {
            ask_forseti: 'Ask Forseti about this checklist',
            candidate_link: 'Link this record',
            candidate_linked: 'Already linked',
            candidate_linking: 'Linking…',
            candidate_no_date: 'No date recorded',
            candidate_open: 'Open record',
            evidence_kind_manual: 'Manual entry',
            evidence_kind_platform: 'Platform record',
            link_manual_fallback: 'If none of the above applies, record the evidence manually.',
            reason_module_not_readable: 'The regulatory pack names a module this platform build cannot read for evidence. Link the record manually instead.',
            reason_module_not_permitted: 'Your role cannot read the module that holds this evidence. Ask an administrator for access, or link the record manually.',
            reason_no_matching_record: 'No record of this kind exists in this organization yet.',
            reason_record_not_accepted: 'A record exists but is not in an accepted state. Complete or approve it, then resolve again.',
            source_form: 'Form',
            source_module: 'Module',
            source_role: 'Evidence role',
            sources_error: 'The platform sources for this question could not be loaded.',
            sources_heading: 'Evidence the platform can supply',
            sources_intro: 'The regulatory pack declares which of your own records can satisfy this question. These are the matching records this organization holds.',
            sources_loading: 'Loading platform sources…',
            unreconciled_question: 'This question is not on this assessment\u2019s own record and cannot be answered here: {question}',
            sources_toggle: 'Show platform sources',
            collapse_all: 'Collapse all questions',
            crosslink_action: 'Open handbook assessments',
            crosslink_body: 'A maturity assessment scores your programme against a maturity model. The official handbook checklist is a separate record: it collects evidence against a regulatory handbook and deliberately produces no score.',
            crosslink_title: 'Official handbook evidence checklist',
            expand_all: 'Expand all questions',
            group_progress: '{answered} of {total} answered',
            module_guide_summary: 'Complete an official regulatory handbook evidence checklist. Each question records an answer, an evidence state and the supporting records held in the platform. The checklist produces no compliance score — a human legal review closes it.',
            page_description: 'Official regulatory handbook evidence checklists. Each question records an answer, an evidence state, and the platform records that support it.',
            page_title: 'Handbook Assessments',
            question_fields: 'Question response',
            loading: 'Loading official handbook assessment',
            load_error: 'The official handbook assessment could not be loaded.',
            start: 'Start official handbook assessment',
            empty: 'No official handbook assessment has been started for this organization.',
            progress: 'questions answered',
            no_score: 'This evidence checklist does not calculate or claim a legal-compliance score. Human legal review is required.',
            save: 'Save answer',
            saving: 'Saving…',
            resolve: 'Resolve existing evidence',
            resolving: 'Resolving…',
            evidence: 'Evidence state',
            answer: 'Answer',
            notes: 'Notes and applicability rationale',
            linked: 'Linked evidence',
            no_evidence: 'No evidence is linked yet.',
            manual: 'Link manual evidence',
            description: 'Evidence description',
            source: 'Evidence source',
            owner: 'Evidence owner',
            date: 'Evidence date',
            url: 'Evidence URL',
            invalid_url: 'Enter a complete http:// or https:// URL.',
            link: 'Link evidence',
            linking: 'Linking…',
            remediate: 'Create remediation task',
            remediating: 'Creating task…',
            review_question: 'Mark question reviewed',
            reviewing: 'Reviewing…',
            submit: 'Submit all 30 questions for review',
            finalize: 'Complete legal review',
            return_revision: 'Return for revision',
            export: 'Export reviewed evidence report',
            exporting: 'Exporting…',
            status: 'Assessment status',
            legal_review: 'Legal review',
            saved: 'Answer saved.',
            resolved: 'Existing evidence resolution completed.',
            linked_done: 'Evidence linked for review.',
            remediation_done: 'Remediation task created.',
            review_done: 'Review state updated.',
            exported: 'Reviewed evidence report exported.',
            export_format: 'Format',
            export_language: 'Language',
            export_format_pdf: 'PDF',
            export_format_docx: 'DOCX',
            export_language_en: 'English',
            export_language_vi: 'Tiếng Việt',
            export_language_bilingual: 'English / Tiếng Việt',
            answer_state: {
                unanswered: 'Unanswered',
                yes: 'Yes',
                no: 'No',
                unknown: 'Unknown',
                not_applicable: 'Not applicable',
            },
            assessment_status: {
                in_progress: 'In progress',
                under_review: 'Under review',
                reviewed: 'Reviewed',
                archived: 'Archived',
            },
            legal_review_status: {
                pending: 'Pending',
                reviewed: 'Reviewed',
                revision_requested: 'Revision requested',
            },
            evidence_state: {
                resolved: 'Resolved',
                missing: 'Missing',
                stale: 'Stale',
                unsupported: 'Unsupported',
                manual_pending_review: 'Manual — pending review',
            },
        },
    },
    mcp_pending: {
        title: 'MCP — Schreifoperatiounen am Waardestand',
        subtitle: 'Extern Claude-Clienten proposéieren hei Schreifoperatiounen. Genehmegt oder refuséiert all Entrée — Forseti féiert eréischt no der Validatioun vum Administrator aus.',
        no_pending: 'Keng Schreifoperatiounen am Waardestand.',
        filter_status_label: 'Filter by status',
        filter_all: 'All',
        no_results_for_status: 'No writes with this status.',
        col_tool: 'Tool',
        col_preview: 'Virschau',
        col_key: 'API Schlëssel',
        col_requested: 'Ugefrot',
        col_expires: 'Leeft of',
        col_status: 'Status',
        status_pending: 'Am Waardestand',
        status_approved: 'Genehmegt',
        status_declined: 'Refuséiert',
        status_expired: 'Ofgelaf',
        status_executed: 'Ausgefouert',
        status_failed: 'Failed — nothing written',
        approve_failed: 'Approved, but the write did NOT happen — {tool} was not carried out ({code}). Nothing was created or changed.',
        approve_executed: '{tool} was carried out.',
        approve_label: 'Genehmegen',
        decline_label: 'Refuséieren',
        refresh_label: 'Aktualiséieren',
        error_generic: 'Konnt d’Schreifoperatiounen am Waardestand net lueden.',
        confirm_approve: "D'Genehmegung wäert d'Tool fir är Organisatioun mat de Berechtegunge vum API key-Inhaber ausféieren. Weiderfueren?",
        confirm_decline: 'Dës Schreifoperatioun am Waardestand refuséieren? Dëst kann net réckgängeg gemaach ginn.',
        // Sprint 64.123.0 — approving a queued MCP write now mints the human
        // confirmation the tool manifest requires, so the surface has to say what
        // that approval is worth, and when it is self-attestation.
        confirmation_note: 'Approving records the human confirmation these tools require, and the write is then carried out with the API key holder’s permissions — not yours. Where you proposed the write yourself, your approval is recorded as self-approved and is not an independent review.',
        self_approval_badge: 'You proposed this write — approving it is self-attestation, not a second review.',
        confirm_approve_self: 'You proposed this write. Approving it records YOUR OWN confirmation — no second person will review it. The audit trail will show it as self-approved. Continue?',
        advisory_banner: 'Schreifoperatiounen am Waardestand lafen 24 Stonnen no der Erstellung of. De stënneschen GC-Sweep refuséiert ofgelaaf Entréen automatesch.',
        consequential_label: 'Regulatorily consequential filing',
        consequential_target: 'Target record',
        consequential_authority: 'Authority',
        consequential_authority_unknown: 'Not declared by the installed template',
        consequential_prior: 'Prior',
        consequential_proposed: 'Proposed',
        consequential_empty: '(none)',
        consequential_unresolved: 'The target record could not be read when this was queued —',
        consequential_captured_at: 'Values read at',
        // fix-sprint-20261003-1200 F5 — the approve refusals, by server code.
        refusal_proposer_lacks_forseti: 'This write was not carried out: the role of the person who proposed it no longer allows Forseti, so it cannot run as them. Nothing was changed. Decline it to remove it from the queue.',
        refusal_proposer_inactive: 'This write was not carried out: the person who proposed it is no longer an active member of your organisation. Nothing was changed. Decline it to remove it from the queue.',
        refusal_key_inactive: 'This write was not carried out: the API key or connected app that proposed it may no longer make it. Nothing was changed. Decline it to remove it from the queue.',
        refusal_already_resolved: 'Someone has already resolved this write. Refresh to see its current status.',
        refusal_expired: 'This write has expired and can no longer be approved.',
        refusal_state_changed: 'This write changed status while you were approving it. Refresh to see its current status.',
        decline_dialog_title: 'Decline pending write',
        decline_reason_label: 'Reason for declining',
        decline_reason_hint: 'Required, up to 500 characters. It is recorded with your decision.',
        decline_submit: 'Decline write',
        decline_cancel: 'Keep pending',
        col_actions: 'Aktiounen',
    },
    audit_walkthrough: {
        title: 'Forseti — Generator fir Auditrundgang',
        subtitle: 'Generéiert e strukturéierten Rundgang fir den Auditstart: Eropfäung Fro pro Kontroll, erwaartene Beweis, Folleg Froen, méiglech Lücken.',
        audit_picker_label: 'Audit',
        audit_picker_hint: 'Wielt en geplangten Audit fir e Rundgang ze generéieren.',
        run_label: 'Rundgang generéieren',
        running: 'Generéiert…',
        results_heading: 'Rundgang — {count} Kontroll(en)',
        tokens_label: '≈ {count} Tokens',
        no_controls: 'Keng Kontrollen am Ëmfank. Füügt Kontrollen zum Auditrekord derbäi.',
        col_control: 'Kontroll',
        col_question: 'Eropfäung Fro',
        col_evidence: 'Beweis',
        col_gaps: 'Méiglech Lücken',
        expected_evidence_label: 'Erwaartene Beweis',
        followup_probes_label: 'Folleg Froen',
        accept_label: 'Rundgang akzeptéieren',
        reject_label: 'Refuséieren',
        export_label: 'DOCX exportéieren',
        resolve_summary: 'Resultat: {status}',
        resolve_status_accepted: 'Akzeptéiert',
        resolve_status_rejected: 'Ofgelehnt',
        controls_range: 'Controls {from}–{to} of {total}',
        next_page_label: 'Generate next controls',
        error_generic: 'Konnt de Rundgang net generéieren. Versicht et erëm oder iwwerpréift den Auditstatus.',
    },
    privilege_creep: {
        title: 'Forseti — Erkennung vu schleichender Rechterausweidung',
        subtitle: 'Periodescht Scannen vun de Rolleberäichungen. Befunde si beroden — Forseti zitt ni automatesch Rechter zréck.',
        run_label: 'Elo scannen',
        running: 'Scant…',
        findings_heading: '{count} Befund',
        no_findings: 'Keng schleichend Rechterausweidung markéiert.',
        col_user: 'Benotzer',
        col_job_function: 'Jobfunktioun',
        col_severity: 'Schwéiergrad',
        col_unexpected: 'Onerwaart Berechtegungen',
        col_remediation: 'Virgeschloe Behiewung',
        col_status: 'Status',
        severity_critical: 'Kritesch',
        severity_high: 'Héich',
        severity_medium: 'Mëttel',
        severity_low: 'Niddreg',
        status_open: 'Op',
        status_dismissed: 'Verworf',
        status_remediated: 'Behuewen',
        dismiss_label: 'Verwerfen',
        remediate_label: 'Als behuewen markéieren',
        note_label: 'Notiz (fakultativ)',
        error_generic: 'Konnt schleichend Rechterausweidung net scannen. Versicht et méi spéit erëm.',
        advisory_banner: 'Dës Befunde si beroden. Forseti zitt ni Berechtegungen automatesch zréck — iwwerpréift mam Rolleninhaber, ier Dir handelt.',
    },
    compliance_rules: {
        rule_mapping: {
            title: 'Konformitéit Regel Auto-Mapping',
            subtitle: 'Forseti Spadséiergank engem installéiert reglementaresche Pak a proposéiert nei oder erfrëscht Konformitéit Regelen Grondsteen an der Quell Artikel.',
            pack_picker_label: 'Regulatioun Pak',
            pack_picker_hint: 'Wielt en installéiert Pak fir ze mapen.',
            run_label: 'Kaart aus Pak',
            running: 'Mapping…',
            results_heading: '{count} Virschlag(en)',
            tokens_label: '≈ {count} Tokens aus Ärem monatlecht Eemer',
            no_suggestions: 'Keng Suggestioune - Pak Inhalt kann scho komplett vun bestehend Reegelen ofgedeckt ginn.',
            kind_new: 'Nei Regel',
            kind_refresh: 'Already covered (review only)',
            kind_no_change: 'Keng Ännerung néideg',
            col_kind: 'Aart',
            col_title: 'Proposéiert Titel',
            col_existing: 'Bestehend Regel',
            col_source: 'Quell',
            proposed_body_label: 'Kierper proposéiert',
            source_quote_label: 'Quell Zitat',
            accept_selected: 'Akzeptéieren ausgewielt',
            reject_all: 'All refuséieren',
            accept_all: 'Akzeptéieren all',
            resolve_summary: 'Resolution: {status} · {created} draft rule(s) created · {skipped} left unchanged because a rule already covers the requirement',
            error_generic: "Konnt d'Regelmapping net lafen. Probéiert nach eng Kéier oder kontrolléiert de Packcode.",
            write_permission_hint: 'Accepting suggestions needs permission to edit regulatory frameworks. You can still reject the run.',
            examined_label: 'Read passages {from}–{to} of the {total} that this pack search returns, ranked by one fixed search. A new run starts again at passage 1.',
            examined_remaining: '{remaining} passage(s) have not been read yet. Use Read next passages to continue from where this run stopped.',
            read_next_label: 'Read next passages',
            read_next_blocked: 'Accept or reject the suggestions above first; the next passages are read after this draft is resolved.',
            run_blocked: 'Accept or reject the suggestions above first; mapping again from this pack replaces the draft on screen.',
            drafts_hint: 'Accepted suggestions are saved as draft rules in the rule library. A draft rule is not active until someone activates it, and the product has no page for compliance rules yet. A passage that an existing rule already covers is shown for review only and creates nothing.',
        },
    },
    access_accountability: {
        nav: {
            session_dashboard: 'Sëtzungsiwwersiicht',
            access_log: 'Zougangsprotokoll',
            justification_review: 'Begrënndungskontroll',
            investigations: 'Ënnersichungen',
            pattern_analysis: 'Musteranalyse',
            anchoring_status: 'Verankerungsstatus',
            configuration: 'Konfiguratioun',
            access_policies: 'Zougangsrichtlinnen',
            review_campaigns: 'Iwwerpréiwungskampagnen',
            evidence_packages: 'Noweispaketen',
        },
        session_dashboard: {
            title: 'Sëtzungsiwwersiicht',
            loading: 'Lueden vun den Iwwersiichtsdonnéeën...',
            total_sessions: 'Sessiounen insgesamt',
            active_sessions: 'Aktiv Sessiounen',
            avg_duration: 'Duerchschnëttlech Dauer',
            peak_day_sessions: 'Aktiivsten Dag',
            auth_method_breakdown: 'Opschlësselung no Authentifizéierungsmethod',
            geo_country_breakdown: 'Opschlësselung no Land',
            recent_session_events: 'Rezent Sessiounsevenementer',
            user_id: 'Benotzer-ID',
            session_id: 'Sessiouns-ID',
            ip_address: 'IP-Adress',
            auth_method: 'Authentifizéierungsmethod',
            started_at: 'Gestart den',
            event_type: 'Evenementstyp',
            all_event_types: 'All Evenementstypen',
            filter_by_user: 'No Benotzer filteren',
            no_active_sessions: 'Keng aktiv Sessiounen',
            no_session_events: 'Keng Sessiounsevenementer fonnt',
            revoke: 'Zréckzéien',
            revoke_session: 'Sessioun zréckzéien',
            aria_revoke_session: 'Sessioun {user} zréckzéien',
            revoke_reason: 'Grond',
            revoke_placeholder: 'Gitt e Grond un, firwat dës Sessioun zréckgezu gëtt...',
            session_label: 'Sessioun:',
            load_failed: "D'Sessiounsiwwersiicht konnt net gelueden ginn. Probéiert et nach eng Kéier.",
            events_load_failed: 'Sessiounsevenementer konnten net gelueden ginn. Probéiert et nach eng Kéier.',
            revoke_failed: 'Sessioun konnt net zréckgezu ginn',
            session_events_loaded: '{n} Sessiounsevenementer gelueden',
            event_types: {
                session_started: 'Sessioun gestart',
                session_refreshed: 'Sessioun erfrëscht',
                session_expired: 'Sessioun ofgelaaf',
                session_revoked: 'Sessioun zréckgezunn',
                session_terminated: 'Sessioun ofgeschloss',
            },
        },
        access_log: {
            title: 'Zougangsprotokoll',
            user_id: 'Benotzer ID',
            resource: 'Ressource',
            action: 'Aktioun',
            ip_address: 'IP Adress',
            timestamp: 'Zäitstempel',
            no_logs: 'Keng Zougang log entries fonnt.',
            filter_by_user: 'Filteren no Benotzer ID...',
            load_failed: 'Konnt net load Zougang log. Probéiert et nach eng Kéier.',
            export_success: 'Exportéieren initiated successfully',
            export_failed: 'Konnt net Export Zougang log',
            record_count: 'Datesat',
            record_count_plural: 'Datesätz',
            unclassified: 'Net klassifizéiert',
            classification_missing: 'Klassifizéierung feelt',
            classification_level: 'Klassifikatiounsniveau',
            response_code: 'Äntwert Code',
            access_surface: 'Zougangskanal',
            api_key: 'API-Schlëssel',
            classification_levels: {
                public: 'Ëffentlech',
                internal: 'Intern',
                confidential: 'Vertraulech',
                restricted: 'Beschränkt',
                unclassified: 'Onklasséiert',
            },
        },
        justification_review: {
            title: 'Begrënndungskontroll',
            user_id: 'Benotzer ID',
            resource: 'Ressource',
            justification: 'Begrënnung',
            submitted_at: 'Ofgeschéckt At',
            reviewer: 'Préifer',
            no_reviews: 'Keng Begrënnung reviews fonnt.',
            load_failed: 'Konnt net load justifications. Probéiert et nach eng Kéier.',
            review_failed: 'Konnt net Iwwerpréifung Begrënnung',
            decline_failed: 'Konnt net decline Zougang',
            justification_count: 'Begrënnung',
            justification_count_plural: 'Begrënnungen',
            reviewed: 'Iwwerpréift',
            concerning: 'Besuergnesserregend',
            escalated: 'Eskaléiert',
            link_investigation_placeholder: 'Verbannen op eng Ënnersichung (optional)',
            review_queue: 'Iwwerpréiwen Waardeschlang',
            access_id_label: 'Zougangs-ID',
            access_id_placeholder: 'Zougangsprotokoll-Andrag-ID',
            resource_type_label: 'Ressourcentyp',
            resource_type_placeholder: 'z.B. document, database',
            resource_id_label: 'Ressourcen-ID',
            resource_id_placeholder: 'Ressourcenidentifiant',
            reason_placeholder: 'Grond fir op dës Ressource zouzegräifen...',
            create_failed: 'Feeler beim create justification',
            unclassified: 'Net klassifizéiert',
            review_statuses: {
                pending: 'Erwaardung',
                reviewed: 'Iwwerpréift',
                concerning: 'Betreffend',
                escalated: 'Eskaléiert',
            },
            reason_codes: {
                business_need: 'Business Bedarf',
                legal_obligation: 'Juristesch Obligatioun',
                data_subject_request: 'Date Sujet Ufro',
                audit_requirement: 'Audit Ufuerderung',
                incident_response: 'Tëschefall Äntwert',
                research: 'Fuerschung',
                administrative: 'Administrativ',
                other: 'Aner',
            },
            classification_level: 'Klassifikatiounsniveau',
            review_decision: 'Iwwerpréiwen Decisioun',
        },
        investigations: {
            title: 'Ënnersichung Workspace',
            new: 'Nei Ënnersichung',
            subject: 'Betreff',
            investigator: 'Ermëttler',
            opened_at: 'Opgemaach At',
            findings: 'Resultater',
            no_investigations: 'Keng investigations fonnt.',
            load_failed: 'Konnt net load investigations. Probéiert et nach eng Kéier.',
            create_failed: 'Konnt net create Ënnersichung',
            timeline_add_failed: 'Konnt net add Zäitverlaf entry',
            link_access_failed: 'Konnt net link Zougang IDen',
            link_justification_failed: 'Konnt net link Begrënnung IDen',
            close_failed: 'Konnt net close Ënnersichung',
            export_success: 'Exportéieren initiated successfully',
            export_failed: 'Konnt net Export Ënnersichung',
            refresh_failed: 'Konnt net refresh Ënnersichung Detail. Probéiert et nach eng Kéier.',
            update_failed: 'Feeler beim Aktualiséiere vun der Ënnersuchung',
            investigation_count: 'Ënnersichung',
            investigation_count_plural: 'Ënnersichungen',
            title_placeholder: 'Ënnersichung title',
            description_placeholder: 'Detailed Beschreiwung vun den Ënnersichung...',
            assignee_placeholder: 'Benotzer ID vun assignee (optional)',
            timeline_action_placeholder: 'e.g., Iwwerpréift Zougang logs',
            timeline_note_placeholder: 'Fakultativ note',
            access_ids_placeholder: 'Zougang-ID-1, Zougang-ID-2',
            justification_ids_placeholder: 'justification-id-1, justification-id-2',
            findings_placeholder: 'Summarize den Ënnersichung findings...',
            by_actor: 'by',
            close_investigation: 'Zoumaachen Ënnersichung',
            add_timeline_failed: 'Konnt net add Zäitverlaf entry..',
            fetch_failed: 'Konnt net fetch investigations..',
            change_status: 'Enquêtestatus änneren',
            details: 'Enquête-Detailer',
            link_evidence: 'Beweis verlinken',
            link_justifications: 'Begrénnungen verlinken',
            access_ids_label: 'Zougangs-IDen',
            justification_ids_label: 'Begrénnungs-IDen',
            access_ids_link_placeholder: 'Zougang-ID-1, Zougang-ID-2',
            justification_ids_link_placeholder: 'justification-id-1, justification-id-2',
            investigations_loaded: '{n} investigations gelueden',
            status_changed_to: 'Status op {status} geännert',
            add_timeline_entry: 'Zäitlëscht-Andrag derbäisetzen',
            add_access_ids: 'Zougangs-IDen derbäisetzen',
            add_justification_ids: 'Begrénnungs-IDen derbäisetzen',
            investigation_types: {
                anomaly_review: 'Anomalie Bewäertung',
                complaint_driven: 'Plainte gedriwwen',
                scheduled_review: 'Fuerplang Iwwerpréiwung',
                whistleblower_report: 'Whistleblower Bericht',
            },
            severity_labels: {
                low: 'Niddereg',
                medium: 'Mëttelméisseg',
                high: 'Héich',
                critical: 'Kritesch',
            },
            status_labels: {
                open: 'Oppen',
                in_progress: 'Am Fortschrëtt',
                pending_review: 'Erwaardung Iwwerpréiwung',
                closed_substantiated: 'Zougemaach (ënnerstëtzt)',
                closed_unsubstantiated: 'Zougemaach (Onbeständeg)',
                closed_inconclusive: 'Zougemaach (onklusiv)',
            },
            disposition_labels: {
                substantiated: 'Ënnerstëtzt',
                unsubstantiated: 'Onbeständeg',
                inconclusive: 'Onkonklusiv',
            },
        },
        pattern_analysis: {
            accesses_count: '{n} accesses',
            title: 'Musteranalyse',
            resource_type_header: 'Resource type',
            resource_id_header: 'Resource ID',
            loading: 'Lueden vun Muster Donnéeën...',
            anomalies_detected: 'Anomalien Erkannt',
            patterns: 'Mustere',
            by_user: 'By Benotzer',
            by_resource: 'By Ressource',
            time_range: 'Zäit Range',
            load_failed: 'Konnt net analyze patterns. Probéiert et nach eng Kéier.',
            top_resources: 'Top 10',
            access_by_hour: 'Zougang no Hour vun Dag',
            access_by_day: 'Zougang no Dag vun Week',
            fetch_failed: 'Konnt net fetch Muster analysis..',
            analysis_loaded: 'Musteranalyse fir {user} gelueden, {n} Sëtzungen fonnt',
            date_format_placeholder: 'DD/MM/JJJJ',
            access_by_hour_heading: 'Zougang no Stonn vum Dag',
            access_by_day_heading: 'Zougang no Wochendag',
            by_classification: 'No Klassifizéierungsstufe',
            top_accessed_resources: 'Am meeschte opgerufene Ressourcen',
            user_access_summary: 'Benotzerzougangsiwwersiicht',
            days: {
                sunday: 'Sonndeg',
                monday: 'Méindeg',
                tuesday: 'Dënschdeg',
                wednesday: 'Mëttwoch',
                thursday: 'Donneschdeg',
                friday: 'Freideg',
                saturday: 'Samschdeg',
            },
            accesses_in_period: 'Accesses in period',
            sessions_label: 'Sessions',
            recent_accesses: 'Recent accesses in the period',
            recent_accesses_desc: "This user's most recent access log entries in the analysed period, newest first (up to {n}).",
        },
        anchoring_status: {
            kpi_summary_label: 'Anchoring status summary',
            title: 'Verankerungsstatus',
            loading: 'Lueden vun anchoring Donnéeën...',
            anchored: 'Verankert',
            pending: 'Ausstehend',
            failed: 'Gescheitert',
            last_anchor: 'Lescht Anker',
            no_records: 'Keng anchoring Datesätz fonnt.',
            status_load_failed: 'Konnt net load anchoring Status. Probéiert et nach eng Kéier.',
            proofs_load_failed: 'Konnt net load Zougang Bewäiser. Probéiert et nach eng Kéier.',
            verify_failed: 'Konnt net verify Bewäis chain. Probéiert et nach eng Kéier.',
            anchor_failed: 'Konnt net anchor Zougang Bewäis',
            batch_anchor_failed: 'Konnt net batch anchor Zougang Bewäiser',
            proof_count: 'Bewäis',
            proof_count_plural: 'Bewäiser',
            anchor_single: 'Anker Single',
            anchor_batch: 'Anker Batch',
            access_id_placeholder: 'Aginn Zougang ID op anchor',
            batch_ids_placeholder: 'access-id-1, access-id-2, access-id-3',
            verify_chain: 'Verifizéieren Ketten',
            chain_breaks: 'Ketten Breaks',
            total_checked: 'Gesamt Checked',
            anchor_placeholder: 'Aginn Zougang ID op anchor',
            fetch_failed: 'Konnt net fetch anchoring Status..',
            select_to_anchor: 'Select access log entry to anchor',
            select_to_anchor_batch: 'Select entries to anchor',
            no_unanchored_entries: 'No unanchored access-log entries found.',
            loading_unanchored: 'Loading access log entries...',
            fetch_unanchored_failed: 'Failed to load unanchored access log entries.',
            fetch_proofs_failed: 'Konnt net fetch Bewäiser..',
            access_id: 'Zougangs-ID',
            chain_sequence: 'Kettsequenz',
            proof_hash_label: 'Beweis-Hash',
            batch_id: 'Batch-ID',
            proofs_loaded: '{n} access proofs gelueden',
            proof_version_label: 'Version',
            proof_version_value: 'Version {n}',
            verify_valid_summary: 'The chain is intact. Every link and hash recomputes.',
            verify_invalid_summary: 'The chain does not verify. The proofs listed below no longer match what was anchored.',
            verify_proofs_v1: 'Version 1 proofs',
            verify_proofs_v2: 'Version 2 proofs',
            verify_content_verified: 'Log rows matching their proof',
            verify_content_destroyed: 'Content destroyed after retention',
            verify_content_destroyed_note: 'The chain still verifies for these proofs. The access-log row each one anchors has passed its retention date and been destroyed, so its content can no longer be proven.',
            verify_content_removed_sample: 'Demo log rows removed when sample data was cleared',
            verify_content_removed_sample_note: 'The chain still verifies for these proofs. Each one anchors a demo access-log row that was removed when the sample data was cleared; the removal is recorded by the platform. These are not retention destructions.',
            verify_v1_note: 'Version 1 proofs protect the order of the chain and the fact that an access event existed. They do not cover the content of the log row.',
            verify_v2_note: 'A version 2 proof also covers the content of its log row, for as long as the row exists.',
            verify_breaks_heading: 'Proofs that failed',
            verify_break_types: {
                field_mismatch: 'Log row changed after it was anchored',
                hash_mismatch: 'Proof hash does not recompute',
                chain_link_mismatch: 'Link to the previous proof is broken',
                unsupported_proof_version: 'Proof version is not supported',
                content_missing: 'Log row missing — the platform has no record of removing it',
            },
            anchor_statuses: {
                chained: 'Chained',
                pending: 'Erwaardung',
                confirmed: 'Confirméiert',
                failed: 'Ausgefall',
            },
            anchor_types: {
                sha256_chain: 'SHA-256 Kette',
                blockchain_anchor: 'Blockchain Anker',
                zkp_stub: 'ZKP Stéck',
            },
        },
        configuration: {
            title: 'Konfiguratioun',
            session_settings: 'Sessioun Astellungen',
            log_retention: 'Protokoll Opbewahrung (days)',
            anomaly_threshold: 'Anomalie Schwellwäert',
            anchoring_enabled: 'Verankerung Aktivéiert',
            save_config: 'Späicheren Konfiguratioun',
            load_failed: 'Konnt net load Konfiguratioun. Probéiert et nach eng Kéier.',
            save_success: 'Konfiguratioun saved successfully',
            save_failed: 'Konnt net save Konfiguratioun. Probéiert et nach eng Kéier.',
            justification_rules: 'Begrënnung Reegelen',
            reason_codes: 'Grond Codes',
            anchoring_settings: 'Verankerung Astellungen',
            anchoring_method: 'Verankerung Method',
            anchoring_frequency: 'Verankerung Heefegkeet',
            batch_interval: 'Batch-Intervall (Minutten)',
            eligibility_criteria: 'Berechtegung Kritären',
            add_criterion_placeholder: 'Derbäisetzen eligibility criterion...',
            retention_settings: 'Opbewahrung Astellungen',
            standard_retention: 'Standard Opbewahrung (days)',
            sensitive_retention: 'Sensibel Opbewahrung (days)',
            working_hours: 'Aarbechts Hours',
            work_start: 'Starten Zäit',
            work_end: 'Enn Zäit',
            timezone: 'Zäitzon',
            timezone_placeholder: 'z.B. Asien/Ho_Chi_Minh',
            working_days: 'Aarbechts Deeg',
            concurrent_sessions: 'Gläichzäiteg Sessioun Limits',
            concurrent_threshold: 'Gläichzäiteg Sessioun Schwellwäert',
            classification_level: 'Klassifikatioun Niveau',
            prompt_mode: 'Prompt Modus',
            grace_period: 'Toleranz Period (s)',
            code_placeholder: 'Code',
            is_custom: 'Personaliséiert',
            label_placeholder: 'Etikett',
            description_placeholder: 'Beschreiwung',
            custom: 'Personaliséiert',
            fetch_failed: 'Konnt net fetch Konfiguratioun..',
            classification_levels: {
                public: 'Ëffentlech',
                internal: 'Intern',
                confidential: 'Vertraulech',
                restricted: 'Beschränkt',
                unclassified: 'Onklasséiert',
            },
            prompt_modes: {
                reason_only: 'Nëmme Grond',
                reason_text: 'Grond + Text',
                reason_text_supervisor: 'Grond + Text + Supervisor',
            },
            anchoring_methods: {
                sha256_chain: 'SHA-256 Kette',
                blockchain_anchor: 'Blockchain Anker',
                zkp_stub: 'ZKP Stéck',
            },
            anchoring_frequencies: {
                realtime: 'Echtzäit',
                batched: 'Batched',
                on_demand: 'Op Ufro',
            },
            retention_position: {
                title: 'Log Retention Position',
                description: 'What the retention sweep would destroy, and what it would keep. Reading this changes nothing.',
                as_of: 'As of',
                window_in_force: 'Window in force',
                days: '{n} days',
                swept: 'Past its window',
                retained_held: 'Kept — legal hold or retention rule',
                retained_anchored: 'Kept — anchored by an older proof',
                retained_investigation: 'Kept — linked to an open access investigation',
                unstamped: 'No retention date recorded',
                skipped_swept: 'Already accounted for',
                eligible_sample: 'Oldest entries past their window',
                resource: 'Resource',
                expires_at: 'Window ended',
                run_sweep: 'Run retention sweep',
                running: 'Running...',
                sweep_success: 'Retention sweep complete: {swept} destroyed, {retained} kept.',
                sweep_failed: 'The retention sweep could not be run. Please try again.',
                load_failed: 'The retention position could not be read. Please try again.',
                reload: 'Reload',
                empty: 'No access-log entry is past its retention window.',
                unstamped_note: 'An entry with no retention date is never destroyed. It is counted above so the number is visible rather than assumed.',
                anchored_note: 'An entry anchored by a version 1 proof (made before proofs covered content) is kept past its window, because destroying it would leave that proof pointing at nothing. An entry anchored by a newer proof is destroyed on its date and the chain still verifies.',
            },
        },
        access_policies: {
            title: 'Zougangsrichtlinnen',
            create_policy: 'Zougangsrichtlinn erstellen',
            policy_name: 'Politik Numm',
            framework_code: 'Kaderwierk',
            select_framework: 'Kaderwierk auswielen...',
            review_frequency: 'Iwwerpréiwungsfrequenz',
            scope: 'Geltungsberäich',
            no_policies_found: 'Keng Zougangsrichtlinne fonnt.',
            policy_created: 'Zougangsrichtlinn erstallt',
            frequency_labels: {
                monthly: 'Monatslech',
                quarterly: 'Véiereljäerlech',
                semi_annual: 'Halbjäerlech',
                annual: 'Jäerlech',
            },
        },
        review_campaigns: {
            title: 'Zougangsiwwerpréiwungskampagnen',
            create_campaign: 'Iwwerpréiwungskampagne erstellen',
            reviewer: 'Iwwerpréiwer',
            due_date: 'Fällegkeetsdatum',
            certify: 'Zertifizéieren',
            no_campaigns_found: 'Keng Zougangsiwwerpréiwungskampagne fonnt.',
            status_open: 'Op',
            status_in_review: 'An der Iwwerpréiwung',
            status_completed: 'Ofgeschloss',
            campaign_created: 'Iwwerpréiwungskampagne erstallt',
            review_certified: 'Zougangsprüfung zertifizéiert',
            certify_campaign: 'Kampagne {id} zertifizéieren',
            policy_id: 'Richtlinnen-ID',
            select_policy: 'Richtlinn auswielen...',
            reviewer_ids_label: 'Iwwerpréiwer',
            select_reviewers: 'Iwwerpréiwer auswielen...',
            status_labels: {
                open: 'Oppen',
                in_review: 'An der Iwwerpréiwung',
                in_progress: 'Lafennt',
                cancelled: 'Ofgebrach',
                completed: 'Ofgeschloss',
            },
        },
        evidence_packages: {
            title: 'Noweispaketen',
            generate_package: 'Noweispacket generéieren',
            export_type: 'Exporttyp',
            package_type: 'Packettyp',
            framework_scope: 'Kaderwierkberäich',
            period: 'Zäitraum',
            generated_by: 'Generéiert vun',
            generated_at: 'Generéiert um',
            no_packages_found: 'Keng Noweispakete fonnt.',
            package_generated: 'Beweispaket generéiert',
            export_type_full: 'Vollstännegen Export',
            export_type_summary: 'Zesummefaassung',
            export_type_certification_only: 'Nëmmen Zertifizéierung',
            package_type_access_review: 'Zougangsprüfung',
            package_type_investigation: 'Enquête',
            package_type_certification: 'Zertifizéierung',
            package_type_authority_request: 'Autoritéitsufro',
            period_start: 'Ufank vum Zäitraum',
            period_end: 'Enn vum Zäitraum',
            select_frameworks: 'Kaderen auswielen...',
            completeness_col: 'Vollständegkeet',
            status_col: 'Status',
            generated_by_col: 'Generéiert Vun',
        },
        evidence_package_detail: {
            title: 'Beweispaket Detailer',
            not_found: 'Beweispaket net fonnt.',
            package_metadata: 'Paketmetadaten',
            status: 'Status',
            item_count: 'Unzuel vun Elementer',
            disposition: 'Dispositioun',
            closed_at: 'Zougemaach um',
            completeness_dashboard: 'Vollständegkeets-Iwwersiicht',
            no_completeness_data: 'Keng Vollständegkeetsdaten disponibel.',
            controls_covered: 'Kontrollen ofgedeckt',
            missing: 'Feelt',
            sign_off_tracker: 'Fräigab-Verfolgung',
            stage_framework_owner: 'Kader-Besëtzer',
            stage_legal_compliance: 'Recht a Compliance',
            stage_internal_audit: 'Intern Revisioun',
            approved: 'Genehmegt',
            returned: 'Zréckginn',
            pending: 'Aussteesend',
            approve: 'Genehmegen',
            return_btn: 'Zréckginn',
            sign_off_submitted: 'Fräigab agereecht',
            artifacts: 'Artefakter',
            no_artifacts: 'Keng Artefakter fonnt.',
            template_id: 'Schabloun-ID',
            description: 'Beschreiwung',
            classification: 'Klassifikatioun',
            evidence_type: 'Beweistyp',
            framework_section: 'Kader-Sektioun',
            history: 'Genehmegungshistoire',
            expand: 'Erweideren',
            collapse: 'Zouklappen',
            action: 'Aktioun',
            actor: 'Akteur',
            timestamp: 'Zäitstempel',
            comment: 'Kommentar',
            close_case: 'Fall zoumaachen',
            confirm_close: 'Zoumaache bestätegen',
            closure_notes: 'Ofschlossnotizen',
            disposition_compliant: 'Konform',
            disposition_non_compliant: 'Net konform',
            disposition_partial: 'Deelweis',
            case_closed: 'Beweispaket zougemaach',
            export_package: 'Paket exportéieren',
            export_complete: 'Beweispaket exportéiert',
            completeness_explainer: "A control is one of the organisation's active access policies for the framework. It is covered when at least one access-review certification was recorded in the package period under a campaign of that policy.",
            completeness_not_measured: 'No active access policy is declared for this framework, so there is no control to measure coverage against.',
            artifacts_explainer: "Each artifact is an access-review certification recorded in the package period under a campaign of one of the framework's access policies.",
            policy: 'Access policy',
            decision: 'Decision',
            certified_at: 'Certified at',
            certified_by: 'Certified by',
            decision_approved: 'Access approved',
            decision_revoked: 'Access revoked',
            evidence_type_access_certification: 'Access certification',
        },
    },
    consent_properties: {
        nav: {
            dashboard: 'Iwwersiicht',
            properties: 'Eegeschafte',
            groups: 'Gruppen',
            bindings: 'Bindungen',
            change_requests: 'Ännerungsufroen',
            comparison: 'Vergläich',
            inconsistencies: 'Inkonsistenzen',
            snapshot_schedules: 'Momentopnampläng',
            golden_templates: 'Gëllene Virlagen',
            evidence_packs: 'Nowiespaketen',
            audit: 'Kontroll',
        },
        select_property: 'Eegeschaft auswählen…',
        select_policy: 'Richtlinn auswählen…',
        select_properties: 'Eegeschaften auswählen…',
        select_target_properties: 'Zileegeschaften auswählen…',
        select_owner: 'Besëtzer auswählen…',
        select_technical_contact: 'Technesche Kontakt auswählen…',
        property_statuses: {
            active: 'Aktiv',
            inactive: 'Inaktiv',
            pending: 'Aussteend',
            decommissioned: 'Ausser Betrib',
        },
        dashboard: {
            title: 'Iwwergräifend Eegeschaftsiwwersiicht',
            loading: 'Lueden vun dashboard Donnéeën...',
            total_properties: 'Gesamt Eegeschaften',
            active_properties: 'Aktiv Eegeschaften',
            snapshot_coverage: 'Snapshot Ofdeckung',
            properties_covered: 'Eegeschaften ofgedeckt',
            total_bindings: 'Bindungen Total',
            deployed_bindings: 'Deployéiert',
            pending_bindings: 'Deployment am Waart',
            deployment_progress: 'Deployment Fortschrëtt',
            on_target: 'Um Ziel',
            below_target: 'Ënnert dem Ziel',
            target_label: 'Ziel',
            total_groups: 'Gesamt Gruppen',
            pending_changes: 'Ausstehend Ännerungen',
            inconsistencies: 'Inkonsistenzen',
            recent_activity: 'Rezent Aktivitéit',
            no_recent_activity: 'Keng rezent Aktivitéit.',
            fetch_error: 'Konnt net load dashboard. Probéiert et nach eng Kéier.',
        },
        properties: {
            title: 'Eegeschaftsregëster',
            new: 'Nei Eegeschaft',
            edit: 'Änneren Eegeschaft',
            property_name: 'Numm',
            property_type: 'Typ',
            identifier: 'Kennung',
            environment: 'Ëmgebung',
            owner_id: 'Besëtzer ID',
            technical_contact_id: 'Technesch Kontakt ID',
            decommission: 'Ausser Betrib setzen',
            confirm_decommission: 'Are you sure you want op decommission dëst property?',
            no_properties: 'Keng Eegeschaften fonnt.',
            all_environments: 'All Ëmgebungen',
            all_types: 'All Typen',
            cmp_instance_id: 'CMP Instanz ID',
            deployment_date: 'Deployment Datum',
            identifier_placeholder: 'zB www.example.com oder com.example.app',
            owner_name: 'Besëtzer',
            technical_contact_name: 'Technesche Kontakt',
            sensitive_data_categories: 'Sensibel Donnéeën Kategorien',
            sensitive_data_categories_placeholder: 'Wielt sensibel Datekategorien',
            requires_enhanced_review: 'Verlaangt verstäerkte Iwwerpréiwung',
            sensitive_review_status: 'Sensibel daten Kritik',
            sensitive_review_pending: 'Erwaardung Iwwerpréiwung',
            sensitive_review_approved: 'Genehmegt',
            sensitive_review_rejected: 'Refuséiert',
            sensitive_review_submit: 'Iwwerpréiwung ofginn',
            sensitive_review_approve: 'Zoustëmmen',
            sensitive_review_reject: 'Refuséieren',
            sensitive_review_notes: 'Iwwerpréiwen Notes',
            sensitive_review_notes_placeholder: 'Gitt Bewäertungsnotizen ...',
            record_withdrawal: 'Rekord Réckzuch',
            withdrawal_subject_id: 'Sujet ID',
            withdrawal_consent_id: 'Zoustëmmung ID',
            withdrawal_reason: 'Grond',
            withdrawal_downstream_systems: 'Downstream Systemer',
            withdrawal_downstream_placeholder: 'System-1, System-2',
            withdrawal_success: 'Austrëtt propagéiert erfollegräich.',
            withdrawal_error: 'Ausgefall Austrëtt ze propagéieren.',
            withdrawal_data_subject_id: 'Daten Sujet ID',
            withdrawal_data_subject_id_placeholder: 'z. B. usr_abc123',
            withdrawal_consent_record_id: 'Zoustëmmung Record ID',
            withdrawal_consent_record_id_placeholder: 'z. B. consent_xyz789',
            withdrawal_purposes: 'Entzéien Zwecker',
            withdrawal_purposes_placeholder: "Wielt Zwecker fir d'Zoustëmmung zréckzéien fir ...",
            withdrawal_withdrawn_at: 'Austrëtt Datum',
            withdrawal_tasks_created: 'Aufgaben erstallt',
            withdrawal_affected_bindings: 'Betraff Bindungen',
            withdrawal_affected_activities: 'Betraff Veraarbechtungsaktivitéiten',
            withdrawal_history: 'Austrëtt Geschicht',
        },
        groups: {
            title: 'Eegeschaftsgruppen',
            new: 'Nei Grupp',
            group_name: 'Grupp Numm',
            member_count: 'Memberen',
            no_groups: 'Keng property groups fonnt.',
            policies: 'Politiken',
            confirm_delete: 'Sidd Dir sécher datt Dir dëse Grupp läschen wëllt?',
        },
        bindings: {
            title: 'Bindungsmatrix',
            property: 'Eegeschaft',
            purpose: 'Zweck',
            legal_basis: 'Rechtlech Grondlag',
            bound: 'Gebonnen',
            no_bindings: 'Keng bindings fonnt.',
            bind_policy: 'Bindungspolitik',
            bulk_bind: 'Bulk Bind',
            bind_policy_to_property: 'Bindepolitik zu Property',
            bulk_bind_policy: 'Bulk Bind Politik',
            unbound: 'Ongebonnen',
            policy_id: 'Richtlinn-ID',
            version_placeholder: 'z.B. 1.0.0',
            property_ids_placeholder: 'prop-id-1, prop-id-2, prop-id-3',
            binding_name: 'Bindung',
            property_name: 'Eegeschaftsnumm',
            policy_name: 'Richtlinnennumm',
            bindings_list: 'Bindungslëscht',
            binding_actions: {
                start_deploy: 'Bereetstellung starten',
                mark_deployed: 'Als bereetgestallt markéieren',
                rollback: 'Zréckrollen',
            },
        },
        change_requests: {
            title: 'Ännerungsufropprozess',
            new: 'Nei Änneren Ufro',
            requester: 'Usteller',
            change_type: 'Änneren Typ',
            property: 'Eegeschaft',
            submitted_at: 'Ofgeschéckt At',
            no_requests: 'Keng change requests fonnt.',
            fetch_pipeline_error: 'Konnt net fetch Pipeline. Probéiert et nach eng Kéier.',
            fetch_list_error: 'Konnt net fetch change requests. Probéiert et nach eng Kéier.',
            create_error: 'Konnt net create change request. Probéiert et nach eng Kéier.',
            action_error: 'Konnt net perform Aktioun. Probéiert et nach eng Kéier.',
            approver_name: 'Genehmeger',
            placeholder_target_props: 'prop-id-1, prop-id-2',
            view_all: 'All',
            view_my_properties: 'Meng Eegeschaften',
            stage_requests: '{stage}-Ufroe',
            policy_required: "Choose the policy this change applies to. A policy update, a new binding or a binding removal changes that policy's bindings.",
        },
        comparison: {
            title: 'Vergläichsaarbechtsberäich',
            select_properties: 'Auswielen Eegeschaften op Vergläichen',
            compare: 'Vergläichen',
            no_comparison: 'Auswielen bei least two Eegeschaften op compare.',
            fetch_error: 'Konnt net fetch comparison reports. Probéiert et nach eng Kéier.',
            run_error: 'Konnt net run comparison. Probéiert et nach eng Kéier.',
            save_notes_error: 'Konnt net save notes. Probéiert et nach eng Kéier.',
            scan_complete: 'Inconsistency Scan completed successfully.',
            scan_error: 'Konnt net run inconsistency Scan. Probéiert et nach eng Kéier.',
            min_two_properties: 'Please provide bei least 2 property IDen fir comparison.',
            view_inconsistencies: 'Inkonsistenzen ukucken',
            click_to_select: 'Klickt fir zwou oder méi Eegeschaften ze wielen',
            no_properties_available: 'Keng Eegeschaften verfügbar',
            no_matching_properties: 'Keng passend Eegeschaften',
            select_at_least_two_props: 'Wielt mindestens zwou Eegeschaften aus dem Dropdown hei uewen.',
            no_differences_found: 'Keng Ënnerscheeder tëscht den ausgewielten Eegeschaften fonnt.',
            found_inconsistencies: '{count} Inkonsistenzen iwwer {properties} Eegeschaften a {bindings} Bindunge fonnt.',
        },
        inconsistencies: {
            title: 'Inkonsistenzlëscht',
            property: 'Eegeschaft',
            field: 'Feld',
            expected: 'Erwaart',
            actual: 'Aktuell',
            severity: 'Schwéiergrad',
            no_inconsistencies: 'Keng inconsistencies fonnt.',
            trend: 'Inkonsistenz Trend',
            trend_chart_label: 'Inkonsistenz Trenddiagramm weist Zuelen iwwer Zäit',
            acknowledge: 'Bestätegen',
            resolve: 'Léisen',
            suppress: 'Ënnerbinden',
            cr_id_placeholder: 'CR ID',
            view_all: 'All',
            view_my_properties: 'Meng Eegeschaften',
            view_dpo_review: 'DPO Iwwerpréiwungsschlaang',
            change_request_id_required: 'Änneren Ufro ID ass néideg fir dës Inkonsistenz ze léisen.',
        },
        snapshot_schedules: {
            title: 'Momentopnampläng',
            new: 'Nei Plangen',
            schedule_name: 'Plangen Numm',
            frequency: 'Heefegkeet',
            next_run: 'Nächst Ausféieren',
            last_run: 'Lescht Ausféieren',
            no_schedules: 'Keng Snapshot schedules fonnt.',
            target_type: 'Zil Typ',
            target_id: 'Zil ID',
            custom_frequency_days: 'Benotzerdefinéiert Frequenz (Deeg)',
            time_of_day: 'Zäit vum Dag',
            time: 'Zäit',
            retention_days: 'Retention (Deeg)',
            execute_now: 'Elo ausféieren',
            placeholder_days: 'zb 10',
            target_name: 'Zilnumm',
        },
        golden_templates: {
            title: 'Gëllene Virlagen',
            new: 'Nei Schabloun',
            template_name: 'Schabloun Numm',
            property_type: 'Eegeschaft Typ',
            no_templates: 'Keng golden Schablounen fonnt.',
            applicable_group_ids: 'Applicabel Grupp IDen (komma-getrennt)',
            applicable_groups: 'Applicabel Gruppen',
            purposes: 'Zwecker',
            legal_bases: 'Juristesch Basen',
            banner_config: 'Banner Config',
            consent_settings: 'Zoustëmmungsastellungen',
            group_ids_placeholder: 'group-id-1, group-id-2',
            apply_to_property: 'Op Eegeschaft uwendden',
            apply_to_group: 'Op Grupp uwendden',
            apply_success: 'Schabloun erfollegräich ugewannt.',
            applications: 'Uwendungshistorik',
            no_applications: 'Nach keng Schablounen-Uwendungen.',
            no_configuration_details: "Schabloun huet keng Konfiguratiounsdetailer. Änner d'Schabloun fir Zwecker, rechtlech Grondlagen, Bannereinstellunge oder Zoustëmmungsastellungen derbäizesetzen.",
        },
        rollouts: {
            title: 'Schablounen-Rollouts',
            rollout_status: 'Rollout-Status',
            confirmed: 'Bestätegt',
            pending: 'Aussteend',
            failed: 'Fehlgeschloen',
            confirm: 'Eegeschaft bestätegen',
            confirm_success: 'Eegeschaft am Rollout bestätegt.',
            can_close: 'All Eegeschaften bestätegt — Rollout ofgeschloss',
            cannot_close: 'Waarden op Eegeschaftsbestätegungen',
            no_rollouts: 'Keng Rollouts fonnt.',
            in_progress: 'Am Gaang',
            completed: 'Ofgeschloss',
            cancelled: 'Ofgesot',
            reject: 'Eegeschaft oflehnen',
            reject_success: 'Eegeschaft am Rollout ofgelehnt.',
            confirm_all: 'All bestätegen',
            reject_all: 'All oflehnen',
            confirm_label: 'Rollout bestätegen fir',
            reject_label: 'Rollout oflehnen fir',
            confirm_all_label: 'All aussteend Rollouts bestätegen',
            reject_all_label: 'All aussteend Rollouts oflehnen',
            no_properties_in_rollout: 'Dës Ausrollung huet keng Eegeschaften.',
        },
        analytics: {
            title: 'Eegeschaftsanalyse',
            opt_in_rate: 'Opt-in-Rate',
            opt_out_rate: 'Opt-out-Rate',
            withdrawal_rate: 'Réckzuchsrate',
            total_consents: 'Total Zoustëmmungen',
            total_withdrawals: 'Total Réckzich',
            compliance_score: 'Konformitéitsbewäertung',
            non_compliant: 'Net-konform Eegeschaften',
            no_non_compliant: 'All Eegeschaften sinn konform.',
            threshold: 'Konformitéitsschwellwäert',
            property_id: 'Immobilie ID',
            compliant: 'Konform',
            non_compliant_status: 'Net-kompatibel',
            compliance_help_summary: 'Wéi gëtt de Compliance Score berechent, a wéi fixen ech et?',
            compliance_help_intro: 'Eng Immobilie gëtt als Net-kompatibel markéiert wann säi Score ënner 80% ass. De Score gëtt berechent wéi:',
            compliance_help_with_template_label: 'Wann eng Golden Schabloun applizéiert gëtt:',
            compliance_help_with_template_text: '% vun de Konfiguratiounsschlësselen (Zwecker, legal_bases, banner_config, consent_settings) déi genee mat der Schabloun passen.',
            compliance_help_no_template_label: 'Wann keng Golden Schabloun applizéiert gëtt:',
            compliance_help_no_template_text: "% vun Bindungen (Zorten Zoustëmmung un d'Propriétéit zougewisen) mat deployment_status = agesat. Eng Immobilie ouni Bindungen ass Standard op 100%.",
            compliance_help_fix_intro: 'Fir eng Immobilie aus dëser Lëscht ze läschen, maacht ee vun de folgenden:',
            compliance_help_fix_deploy_label: 'Deploy seng Bindungen',
            compliance_help_fix_deploy_text: "öffnen d'Binding Matrix Säit a setzt den Deploymentstatus vun all Bindung op ofgesat.",
            compliance_help_fix_template_label: 'Gëlle eng Golden Schabloun',
            compliance_help_fix_template_text: "benotzt Apply to Group op der Schabloun, bestätegt dann d'Rollout fir dës Immobilie. Vergewëssert Iech datt d'Konfiguratioun vun der Immobilie mat der Schabloun entsprécht.",
            compliance_help_fix_decommission_label: "Decommission d'Propriétéit",
            compliance_help_fix_decommission_text: 'benotzt den Decommission Button am Property Detail Modal wann et net méi am Gebrauch ass.',
            compliance_help_fix_no_bindings_label: 'Ewechzehuelen all Bindungen',
            compliance_help_fix_no_bindings_text: 'wann eng Immobilie keng Schabloun a keng Bindungen huet, kritt se 100% par défaut.',
        },
        evidence_packs: {
            title: 'Nowiespaketen',
            new: 'Nei Paket',
            pack_name: 'Paket Numm',
            properties: 'Eegeschaften',
            generated_at: 'Generéiert At',
            no_packs: 'Keng Bewäis packs fonnt.',
            generate: 'Generéieren',
            content_type_consent_records: 'Zoustëmmung Datesätz',
            content_type_policy_versions: 'Richtlinn Versiounen',
            content_type_binding_history: 'Bindung Verlaf',
            content_type_change_requests: 'Änneren Ufroen',
            content_type_inconsistency_reports: 'Inconsistency Berichten',
            content_type_snapshot_data: 'Snapshot Donnéeën',
            content_type_audit_trail: 'Auditprotokoll',
        },
        audit: {
            title: 'Astëmmungseegeschaftskontroll',
            event_type: 'Evenement Typ',
            entity_type: 'Entitéit Typ',
            entity_id: 'Entitéit ID',
            actor: 'Akteur',
            timestamp: 'Zäitstempel',
            no_events: 'Keng Audit Evenementer fonnt.',
            entity_type_property: 'Eegeschaft',
            entity_type_group: 'Grupp',
            entity_type_binding: 'Bindung',
            entity_type_change_request: 'Ännerungsufroen',
            entity_type_golden_template: 'Gëllen Schabloun',
            entity_type_snapshot_schedule: 'Snapshot-Zäitplang',
            entity_type_inconsistency: 'Inkonsistenz',
            entity_type_evidence_pack: 'Beweispaket',
            action_created: 'Erstallt',
            action_updated: 'Aktualiséiert',
            action_deleted: 'Geläscht',
            action_decommissioned: 'Ausser Betrib',
            action_status_changed: 'Status geännert',
            action_submitted: 'Agereecht',
            action_approved: 'Genehmegt',
            action_rejected: 'Ofgeleent',
            action_deployed: 'Agesat',
            action_rolled_back: 'Zréckgesat',
            action_paused: 'Pauséiert',
            action_resumed: 'Weidergefouert',
            action_cancelled: 'Ofgebrach',
            action_acknowledged: 'Bestätegt',
            action_resolved: 'Geléist',
            action_suppressed: 'Ënnerdréckelt',
            action_executed: 'Ausgefouert',
        },
        property_types: {
            website: 'Websäit',
            mobile_app: 'Mobil App',
            ott: 'OTT',
            iot_device: 'IoT-Apparat',
            kiosk: 'Kiosk',
            api_service: 'API-Service',
            sdk: 'SDK',
        },
        environments: {
            production: 'Produktioun',
            staging: 'Inszenéierung',
            development: 'Entwécklung',
            testing: 'Test',
        },
        group_types: {
            brand: 'Mark',
            region: 'Regioun',
            product_line: 'Produktlinn',
            business_unit: 'Geschäftseenheet',
            custom: 'Personaliséiert',
        },
        target_types: {
            property: 'Eegeschaft',
            group: 'Grupp',
            all: 'All',
        },
        frequencies: {
            hourly: 'Stënnlech',
            daily: 'Deeglech',
            weekly: 'Wëchentlech',
            monthly: 'Monatslech',
            custom: 'Personaliséiert',
        },
        binding_statuses: {
            current: 'Aktuell',
            outdated: 'Veraltet',
            pending: 'Ofwaardend',
            deploying: 'Gëtt agesat',
            rolled_back: 'Zréckgesat',
        },
        sensitive_categories: {
            health: 'Gesondheet Daten',
            biometric: 'Biometresch Daten',
            genetic: 'Genetesch Daten',
            racial_ethnic: 'Rassesch / Ethnesch Hierkonft',
            political_opinions: 'Politesch Meenungen',
            religious_beliefs: 'Reliéis Iwwerzeegungen',
            trade_union: 'Gewerkschaft Memberschaft',
            sexual_orientation: 'Sexuell Orientéierung',
            criminal_records: 'Criminal Records',
            financial: 'Finanzdaten',
            children: "Kanner d'Daten",
            geolocation: 'Präzis Geolocation',
        },
        export_csv: 'CSV exporteieren',
        exporting: 'Exporteieren...',
        page_x_of_y: 'Sait {page} vun {total}',
        regulatory: {
            capture_method_guidance: 'PDPL erfuerdert Zoustëmmungsbeweis fir de Sammelkanal ze passen. Property Zorte Kaart fir Zoustëmmung Capture Methoden: Websäit / OTT = Banner-baséiert Zoustëmmung, Handy App / SDK = In-App Zoustëmmung Flux, Kiosk = kierperlech / Écran Zoustëmmung Capture, API Service = programmatesch Zoustëmmung, IoT Apparat = Apparat-Niveau Zoustëmmung Mechanismus.',
            dashboard_metrics_info: "Dëst Dashboard gëtt den DPO mat enger eenzeger Vue vun Zoustëmmung Haltung iwwer all digital Eegeschafte. Schlëssel Metriken enthalen: verbindlech Ofdeckungs Prozentsaz (ofgesat vs Gesamtbindungen), Schablounenhänken iwwer den Inkonsistenz-Widget, pendend Ännerungsufroen, déi Iwwerpréiwung erfuerderen, an Immobiliestatus Decompte weist Eegeschafte mat aktive, inaktiven oder ofgebaute Staaten. Properties mat sensiblen Datekategorien ginn iwwer d'Immobilieregister verfollegt.",
            change_request_impact_info: "Ännerung Ufroen Zoustëmmung Eegeschafte Afloss muss reglementaresche Impakt dokumentéieren: déi PDPL Ufuerderunge sinn betraff, ob d'Ännerung sensibel Date Veraarbechtung beaflosst (Art. 2.4), an ob der Ännerung erfuerdert nei Zoustëmmung vun Date Sujeten. Den Impakt Resumé an all Ännerung Ufro Detailer der PDPL reglementaresche Referenze fir Audit Beweiser.",
            outdated_binding_warning: 'Bindungen mat "alem" Status weisen datt e Besëtz eng Politikversioun leeft déi net aktuell ass. Dës Eegeschafte reflektéieren vläicht net déi lescht PDPL Ufuerderungen oder Updates vun der Organisatiounspolitik. Iwwerpréift an aktualiséieren al Bindungen prompt fir PDPL Konformitéit iwwer all Zoustëmmungseigenschaften z\'erhalen.',
        },
    },
    localization: {
        nav: {
            dashboard: 'Iwwersiicht',
            market_packs: 'Maartpaketen',
            language_packs: 'Sproochpaketen',
            frameworks: 'Regulatoresch Kaderen',
            mappings: 'Modulzuuerdnungen',
            submissions: 'Paketerareechen',
            sector_overlay_catalog: 'Overlay Catalog',
            translations: 'Iwwersetzungsvirschau',
            audit: 'Kontrollprotokoll',
        },
        dashboard: {
            title: 'Lokaliséierungsiwwersiicht',
            loading: 'Lueden vun dashboard Donnéeën...',
            active_market_packs: 'Aktiv Maart Paketen',
            published_packs_available: 'Verëffentlecht Maartpaketen verfügbar',
            language_packs: 'Sprooch Paketen',
            total_language_packs: 'Gesamt language packs registered',
            active_regulatory_frameworks: 'Aktiv Regulatoresch Rahmewierker',
            frameworks_active: 'Rahmewierker mat aktiv Status',
            pending_pack_submissions: 'Ausstehend Paket Areechungen',
            submissions_awaiting_review: 'Areechungen awaiting Iwwerpréifung',
            recent_audit_events: 'Rezent Audit Evenementer',
            no_recent_audit: 'No recent Audit Evenementer.',
            event_type: 'Evenement Typ',
            entity_type: 'Entitéit Typ',
            entity_id: 'Entitéit ID',
            actor: 'Akteur',
            timestamp: 'Zäitstempel',
        },
        market_packs: {
            title: 'Maartpaketkatalog',
            new: 'Nei Maart Paket',
            pack_name: 'Paket Numm',
            market: 'Maart',
            locale: 'Sprooch',
            no_packs: 'Keng market packs fonnt.',
        },
        language_packs: {
            title: 'Sproochpaketen',
            new: 'Nei Sprooch Paket',
            language: 'Sprooch',
            locale_code: 'Sprooch Code',
            completeness: 'Vollstännegkeet',
            completeness_detail: 'Vollständegkeet Detail',
            overall_completeness: 'Allgemeng Vollständegkeet',
            total_keys: 'Ganzen Schlësselen',
            translated_keys: 'Iwwersat Schlësselen',
            missing_keys: 'Vermësst Schlësselen',
            namespace_breakdown: 'Nummraum Ënnerdeelung',
            namespace: 'Nummraum',
            translated: 'Iwwersat',
            no_packs: 'Keng language packs fonnt.',
            install_language_pack: 'Sproochpaket installéieren',
            available_packs: 'Verfügbar Sproochpaketen',
            install: 'Installéieren',
            all_installed: 'All Sproochpaketen sinn schonn installéiert.',
            install_success: 'Sproochpaket erfollegräich installéiert.',
            market: 'Maart',
            featured: 'Virgestallt',
        },
        frameworks: {
            // Sprint 63.32.0 (#543) — every framework_type code, in the reader's language.
            type_national_law: 'National law',
            type_parent_framework: 'Parent framework',
            type_child_decree: 'Child decree',
            type_child_decision: 'Child decision',
            type_child_procedure_decree: 'Procedural decree',
            type_conditional_child_decree: 'Conditional child decree',
            type_implementing_decree: 'Implementing decree',
            type_enforcement_decree: 'Sanctions decree',
            type_sanctions_reference: 'Sanctions reference',
            type_official_guidance: 'Official guidance',
            type_guidance_overlay: 'Guidance overlay',
            type_sector_overlay: 'Sector overlay',
            type_security_overlay: 'Security overlay',
            type_security_control_overlay: 'Security control overlay',
            type_temporary_procedural_overlay: 'Temporary procedural overlay',
            type_cross_sector: 'Cross-sector',
            type_directive: 'Directive',
            type_regulation: 'Regulation',
            type_national_transposition: 'National transposition',
            type_international_standard: 'International standard',
            title: 'Regulatoresch Kaderen',
            new: 'Nei Rahmewierk',
            framework_name: 'Rahmewierk Numm',
            jurisdiction: 'Jurisdiktioun',
            effective_date: 'Effektiv Datum',
            no_frameworks: 'Keng regulatory frameworks fonnt.',
            view_requirements: 'Ufuerderunge kucken',
            hide_requirements: 'Ufuerderunge verstoppen',
            show_legacy: 'Al Rahmewerker uweisen',
            effective_until: 'Gëlteg bis',
            effective_on: 'Gëlteg vum',
            replaced_by: 'Ersat duerch',
            replaces_label: 'Ersetst',
            status_active: 'Aktiv',
            status_draft: 'Entworf',
            status_unclassified: 'Unrecognised status',
            status_legacy: 'Al',
            status_upcoming: 'Kommend',
            status_active_reference: 'In force, reference only',
            status_temporary_active: 'Temporarily in force',
            watchlist: 'Iwwerwaachungslëscht',
            watchlist_empty: 'Keen Entworf oder zukünfteg Kaderen',
            watchlist_empty_desc: 'Frameworks mam Status "Entworf" oder "Upcoming" erschéngen hei fir d\'Iwwerwaachung.',
            expected_effective: 'Erwaart effektiv',
            effective_date_tbd: 'Effektiv Datum: TBD',
            activate: 'Aktivéieren',
            ai_law_title: 'Vietnam AI Gesetz',
            ai_risk_classification: 'AI Risiko Klassifikatioun',
            ai_system_dossier: 'AI System Dossier',
            ai_labeling_checklist: 'AI Etikettéieren & Verëffentlechung',
            ai_incident_report: 'AI Tëschefall Bericht',
            ai_impact_assessment: 'AI Impakt Bewäertung',
            ai_sandbox_application: 'Sandbox Applikatioun',
            ai_sandbox_monitoring: 'Sandbox Iwwerwaachung',
            ai_sandbox_completion: 'Sandbox Fäerdegstellung',
            ai_submission_tracker: 'Soumissioun Tracker',
            ai_evidence_capture: 'Beweis Capture',
            risk_level_minimal: 'Minimum Risiko',
            risk_level_limited: 'Limitéiert Risiko',
            risk_level_high: 'Héich Risiko',
            risk_level_unacceptable: 'Inakzeptabel Risiko',
            submission_status_draft: 'Entworf',
            submission_status_submitted: 'Erlaabt',
            submission_status_returned: 'Zréckkomm fir Ännerung',
            submission_status_accepted: 'Akzeptéiert',
            submission_status_rejected: 'Refuséiert',
            submission_status_closed: 'Zougemaach',
            ecommerce_law_title: 'Vietnam E-Commerce Gesetz',
            ecommerce_platform_classification: 'Plattform Klassifikatioun',
            ecommerce_seller_register: 'Verkeefer Register',
            ecommerce_complaint_register: 'Plainte Register',
            ecommerce_takedown_register: 'Ofhuelen Register',
            ecommerce_submission_tracking: 'Soumissioun Tracking',
            ecommerce_dossier_checklist: 'Dossier Checkliste',
            ecommerce_cross_border: 'Cross-Border Compliance',
            ecommerce_trustmark: 'Trustmark Programm',
            ecommerce_notification: 'Notifikatioun',
            ecommerce_registration: 'Aschreiwung',
            ecommerce_termination: 'Terminatioun',
            ecommerce_reporting_readiness: 'Berichterstattung',
            ecommerce_evidence_capture: 'Beweis Capture',
            submission_status_approved: 'Genehmegt',
            cybersec_law_2025_title: 'Vietnam Cybersecurity Law (2025)',
            cybersec_system_classification: 'System Klassifikatioun',
            cybersec_risk_assessment: 'Risiko Bewäertung',
            cybersec_incident_report: 'Tëschefall Rapport',
            cybersec_authority_cooperation: 'Autoritéit Zesummenaarbecht',
            cybersec_data_localization: 'Daten Lokalisatioun',
            cybersec_vendor_security: 'Verkeefer Sécherheet',
            cybersec_submission_tracker: 'Soumissioun Tracker',
            cybersec_evidence_capture: 'Beweis Capture',
            cybersec_dossier_checklist: 'Dossier Checkliste',
            cybersec_classification_general: 'Général',
            cybersec_classification_important: 'Wichteg',
            cybersec_classification_critical: 'Kritesch',
            data_law_title: 'Vietnam Dategesetz',
            data_inventory: 'Daten Inventar',
            data_classification: 'Daten Klassifikatioun',
            data_sharing: 'Daten Deele',
            data_governance: 'Date Gouvernance',
            data_submission_tracker: 'Soumissioun Tracker',
            data_evidence_capture: 'Beweis Capture',
            data_dossier_checklist: 'Dossier Checkliste',
            telecom_law_title: 'Vietnam Telecom Gesetz',
            telecom_authority_request: 'Autoritéit Ufro',
            telecom_disclosure: 'Offenbarung',
            telecom_confidentiality: 'Confidentialitéit',
            telecom_submission_tracker: 'Soumissioun Tracker',
            telecom_evidence_capture: 'Beweis Capture',
            telecom_dossier_checklist: 'Dossier Checkliste',
            pdpl_law_title: 'Vietnam Perséinlech Dateschutzgesetz',
            pdpl_breach_notification: 'Verstouss Notifikatioun',
            pdpl_dpo_notification: 'DPO Rendez-vous Notifikatioun',
            pdpl_data_subject_notification: 'Date Sujet Notifikatioun',
            pdpl_submission_tracker: 'Soumissioun Tracker',
            pdpl_evidence_capture: 'Beweis Capture',
            pdpl_dossier_checklist: 'Dossier Checkliste',
            pdpl_authority_filing: 'Autoritéit Areeche',
            pdpl_mops_filing: 'MoPS Datei',
        },
        mappings: {
            title: 'Modulzuuerdnungen',
            new: 'Nei Zouweisung',
            module: 'Modul',
            // fix-sprint #447 W8 — module labels, from the bundle (no `{en, vi}` map on the page).
            module_consent: 'Zoustëmmung',
            module_dpia: 'DPIA',
            module_incident: 'Tëschefall',
            module_rights_request: 'Rechter Ufro',
            module_data_mapping: 'Data Mapping',
            module_classification: 'Klassifikatioun',
            module_monitoring: 'Iwwerwachung',
            module_governance: 'Gouvernance',
            module_vendor: 'Verkeefer',
            module_risk: 'Risiko',
            module_sector_licensing: 'Sector Licensing',
            module_investor_verification: 'Investor Verification',
            module_account_data_governance: 'Account Data Governance',
            module_regulatory_disclosure: 'Regulatory Disclosure',
            module_authority_direction: 'Authority Direction',
            module_issued_record: 'Issued Records',
            module_publication_assessment: 'Publication Assessment',
            module_legal_deposit: 'Legal Deposit',
            module_legal_representative: 'Legal Representative',
            market_pack: 'Maart Paket',
            mapping_status: 'Zouweisung Status',
            // fix-sprint #431 — the list count, chosen by `count === 1` (the bundle's `count_one` convention).
            count: '{count} mappings',
            count_one: '{count} mapping',
            no_mappings: 'Keng Modul mappings fonnt.',
            incomplete_entries_warning: '{count} Zouweisung(en) verstoppt wéinst feelender Framework-ID, Ufuerderungs-ID oder Modul.',
            // fix-sprint #431 — seeding is a person's action, never a side effect of opening the page.
            seed_action: 'Create mappings from installed frameworks',
            seed_hint: 'Creates a direct mapping for each requirement that names a module, in every installed framework that has no mappings yet. Opening this page creates nothing.',
            seed_confirm_title: 'Create mappings from installed frameworks?',
            seed_confirm: 'This creates a mapping for every requirement that names a module, in each installed framework that has no mappings yet. It can add many rows, and each can be edited or deleted afterwards.',
            seed_done: '{count} mapping(s) created.',
            seed_none: 'No mappings were created: every installed framework already has mappings, or none of its requirements names a module.',
            seed_error: 'The mappings could not be created.',
            // fix-sprint #431 (gate E12.5 remediation 1) — a failed list read says so, and nothing else.
            load_error: 'The module mappings could not be read, so none are shown and none can be created from installed frameworks here.',
        },
        submissions: {
            title: 'Paketerareechen',
            new: 'Nei Areechung',
            submitter: 'Areechner',
            pack: 'Paket',
            submitted_at: 'Ofgeschéckt At',
            no_submissions: 'Keng Paket submissions fonnt.',
            installed_sector_overlays_title: 'Sektor-Iwwerlagerungen ({count})',
            installed_sector_overlays_aria: 'Installéiert Sektor-Iwwerlagerungen',
            installed_col_name: 'Numm',
            installed_col_code: 'Code',
            installed_col_sector: 'Sektor',
            installed_col_mode: 'Modus',
            installed_col_status: 'Status',
            installed_refresh: 'Aktualiséieren',
            installed_count_one: '1 installed pack',
            installed_count: '{count} installed packs',
            installed_market_packs_title: 'Market Packs ({count})',
            installed_market_packs_aria: 'Installed Market Packs',
            installed_language_packs_title: 'Language Packs ({count})',
            installed_language_packs_aria: 'Installed Language Packs',
            installed_frameworks_title: 'Regulatory Frameworks ({count})',
            installed_frameworks_aria: 'Installed Regulatory Frameworks',
            installed_col_market_code: 'Market Code',
            installed_col_display_name: 'Display Name',
            installed_col_locale: 'Locale',
            installed_col_version: 'Version',
            tab_submissions: 'Submissions',
            tab_install_zip: 'Install from Zip',
            tab_installed: 'Installed Packs',
        },
        translations: {
            title: 'Iwwersetzungsvirschau',
            select_locale: 'Auswielen Sprooch',
            select_module: 'Auswielen Modul',
            key: 'Key',
            value: 'Wäert',
            no_translations: 'Keng translations fonnt.',
        },
        audit: {
            title: 'Lokaliséierung Audit Trail',
            event_type: 'Evenement Typ',
            // Gate E12.5 remediation 1 (fix-sprint #431) — Module Mappings audit events: the key is
            // `event_type_` + the recorded action with `.` read as `_`.
            event_type_regulatory_mapping_created: 'Module mapping created',
            event_type_regulatory_mapping_updated: 'Module mapping updated',
            event_type_regulatory_mapping_deleted: 'Module mapping deleted',
            event_type_regulatory_mapping_seeded_from_frameworks: 'Mappings created from installed frameworks',
            // Gate E12.5 remediation 2 — every other event the localization services record (event_type, else action).
            event_type_market_pack_created: 'Market pack created',
            event_type_market_pack_updated: 'Market pack updated',
            event_type_market_pack_status_changed: 'Market pack status changed',
            event_type_market_pack_installed: 'Market pack installed',
            event_type_market_pack_uninstalled: 'Market pack uninstalled',
            event_type_pack_created: 'Market pack created',
            event_type_pack_updated: 'Market pack updated',
            event_type_pack_status_changed: 'Market pack status changed',
            event_type_pack_installed: 'Market pack installed',
            event_type_pack_uninstalled: 'Market pack uninstalled',
            event_type_cluster_installed: 'Market pack cluster installed',
            event_type_pack_installed_from_zip: 'Pack installed from an uploaded file',
            event_type_pack_zip_install_refused: 'Uploaded pack refused: it carries no signature',
            event_type_pack_submission_created: 'Pack submission created',
            event_type_pack_submission_review_started: 'Pack submission review started',
            event_type_pack_submission_comment_added: 'Comment added to a pack submission',
            event_type_pack_submission_approved: 'Pack submission approved',
            event_type_pack_submission_rejected: 'Pack submission rejected',
            event_type_pack_artifact_created: 'Pack artifact created',
            event_type_pack_artifact_updated: 'Pack artifact updated',
            event_type_pack_artifact_deleted: 'Pack artifact deleted',
            event_type_regulatory_framework_updated: 'Framework updated',
            event_type_regulatory_framework_status_changed: 'Framework status changed',
            event_type_regulatory_framework_requirement_added: 'Requirement added to a framework',
            event_type_regulatory_framework_requirement_removed: 'Requirement removed from a framework',
            event_type_regulatory_framework_stack_layer_status_changed: 'Framework Stack layer status changed',
            event_type_regulatory_framework_transition_review_updated: 'Transition review updated',
            event_type_language_pack_created: 'Language pack created',
            event_type_language_pack_updated: 'Language pack updated',
            event_type_language_pack_activated: 'Language pack activated',
            event_type_language_pack_deactivated: 'Language pack deactivated',
            event_type_language_pack_installed: 'Language pack installed',
            event_type_language_pack_uninstalled: 'Language pack uninstalled',
            event_type_sector_overlay_installed: 'Sector overlay installed',
            event_type_sector_overlay_uninstalled: 'Sector overlay uninstalled',
            event_type_regulatory_overlay_installed: 'Regulatory overlay installed',
            event_type_regulatory_overlay_uninstalled: 'Regulatory overlay uninstalled',
            event_type_installed_framework_mode_changed: 'Installed framework mode changed',
            event_type_phased_obligation_early_implemented: 'Phased obligation marked early-implemented',
            entity_type: 'Entitéit Typ',
            entity_id: 'Entitéit ID',
            entity_name: 'Entitéitsnumm',
            actor: 'Akteur',
            timestamp: 'Zäitstempel',
            payload: 'Notzlaascht',
            collapse_payload: 'Notzlaascht-Detailer zesummeklappen',
            payload_none: 'No details recorded',
            payload_one_field_recorded: '1 field recorded',
            payload_fields_recorded: '{count} fields recorded',
            payload_one_field_changed: '1 field changed',
            payload_fields_changed: '{count} fields changed',
            payload_one_field_removed: '1 field removed',
            payload_fields_removed: '{count} fields removed',
            payload_col_field: 'Field',
            payload_col_value: 'Value',
            payload_col_before: 'Before',
            payload_col_after: 'After',
            payload_empty_value: '(empty)',
            payload_not_set: 'Not set',
            proof_not_anchored: 'Not anchored',
            proof_not_anchored_tooltip: 'No proof record exists for this event. Events written before Immutable Audit Trails was enabled are not anchored, and events in batch mode appear here once their batch is sealed.',
            expand_payload: 'Notzlaascht-Detailer ausklappen',
            collapse: 'Zesummeklappen',
            expand: 'Ausklappen',
            all_entity_types: 'All Entitéitstypen',
            no_events: 'Keng Audit Evenementer fonnt.',
        },
        market_packs_detail: {
            installed_at: 'Installéiert um',
            pack_details_label: 'Paketdetailer',
            market_code: 'Maartcode',
            supported_locales: 'Ënnerstëtzt Sprooche',
            min_platform_version: 'Mindest-Plattformversioun',
            pack_information: 'Paketinformatioun',
            pack_contents: 'Pack Inhalt',
            included_frameworks: 'Abegraff Kaders',
            pack_artifacts: 'Paket-Artefakten',
            no_artifacts: 'Keng Artefakten fir dëse Paket fonnt',
            pack_not_found: 'Paket net fonnt',
        },
        mappings_form: {
            framework_id: 'Framework-ID',
            requirement_id: 'Ufuerderungs-ID',
            module_label: 'Modul',
            mapping_type: 'Zouweisungstyp',
            configuration_json: 'Konfiguratioun (JSON)',
            conflict_resolution: 'Konfliktléisung',
            is_override: 'Ass Iwwerschreiwung',
            all_modules: 'All Modulen',
            all_mapping_types: 'All Zouweisungstypen',
        },
        submissions_detail: {
            submission_id: 'Andrags-ID',
            validation: 'Validatioun',
            reviewers: 'Prüfer',
            start_review: 'Iwwerpréiwung starten',
            hide_comments: 'Kommentarer verstoppen',
            start_review_named: 'Start Review for {name}',
            approve_submission_named: 'Approve submission {name}',
            reject_submission_named: 'Reject submission {name}',
            comments_named: 'Comments for {name}',
            hide_comments_named: 'Hide comments for {name}',
            review_comments: 'Iwwerpréiwungskommentarer',
            loading_comments: 'Kommentarer gi gelueden...',
            no_comments: 'Nach keng Kommentarer.',
            add_comment: 'Kommentar derbäisetzen',
            reject_submission: 'Andrag ofleenen',
            reason_for_rejection: "Grond fir d'Ofleenung",
            reviewer_ids: 'Prüfer-IDen (Komma-getrennt)',
            submit_new_pack: 'Neie Paket androen',
            pack_type: 'Pakettyp',
            market_code: 'Maartcode',
            target_locales: 'Zilsproochen',
            pack_file: 'Paketdatei',
            pack_format_info: 'Packformat: Maartpäck solle lokalspezifesch JSON Dateie mat Nummraum/Schlëssel Iwwersetzungen enthalen. Sproochepacken sollen Iwwersetzunge fir déi spezifizéiert Zillokaler ubidden. Gesinn Dokumentatioun fir detailléiert Schema.',
            accepted_formats: 'Akzeptéiert Formater: .json oder .zip. JSON Dateie sollen e flaach oder Namespaced Objet vun Iwwersetzungsschlësselwäertpaaren enthalen. ZIP-Dateien sollen een oder méi JSON locale Dateien enthalen.',
            comma_separated_locales: 'Komma-getrennte Sproochcoden fir dëse Sproochpaket',
            confirm_approval: 'Guttstreidegung bestätegen',
            approve_message: 'Dës Paketandrag guttstreidegen?',
            submission_label: 'Andrag',
            all_pack_types: 'All Pakettypen',
            passed: 'bestan',
            failed_label: 'fehlgeschloen',
            market_code_required: 'Maartcode ass erfuerderlech',
            version_required: 'Versioun ass erfuerderlech',
            pack_type_regulatory: 'Regulatoresch',
            framework_code: 'Kadercode',
            jurisdiction_code: 'Juridiktiounscode',
            pack_source: 'Quell',
            pack_source_manual: 'Manuell',
            pack_version: 'Pakversioun',
            install_regulatory_pack: 'Regulatorescht Pak installéieren',
            regulatory_pack_desc: 'Lued en regulatorescht Kaderpak erop (.zip)',
            framework_required: 'De Rahmewierk-Code ass erfuerderlech',
            jurisdiction_required: 'De Jurisdiktiounscode ass erfuerderlech',
        },
        catalog_detail: {
            packs_installed_of: 'vun {total} Paketen installéiert',
            install_all: 'All installéieren',
            installing: 'Gëtt installéiert...',
            removing: 'Gëtt ewechgeholl...',
            install_all_named: 'Install all packs in {name}',
            installing_named: 'Installing... {name}',
            uninstall_named: 'Uninstall {name}',
            removing_named: 'Removing... {name}',
            manage_no_permission: 'Installing or uninstalling a market pack needs permission to manage market packs.',
            available: 'Available',
            cluster_installed_count: '({installed}/{total} installed)',
            frameworks_count_one: '1 framework',
            frameworks_count: '{count} frameworks',
            languages_count_one: '1 language',
            languages_count: '{count} languages',
            forms_count_one: '1 form',
            forms_count: '{count} forms',
            artifacts_count_one: '1 artifact',
            artifacts_count: '{count} artifacts',
            market_packs_count_one: '1 market pack',
            market_packs_count: '{count} market packs',
            install_success: 'Installed {market}: {frameworks}, {items}.',
            cluster_install_success: 'Cluster installed: {frameworks}, {forms}.',
            uninstall_success: 'Uninstalled {market}: removed {frameworks} and {artifacts}.',
            uninstall_confirm: 'Uninstall {market}? This removes all frameworks, artifacts and language packs for this market.',
            dependents_exist: 'Cannot uninstall {market}: other installed packs depend on it.',
            dependencies_required: '{market} needs {dependencies} installed first.',
            dependencies_required_unnamed: '{market} needs another market pack installed first.',
            requires: 'Erfuerdert',
            tab_catalog: 'Katalog',
            tab_installed: 'Installéiert',
            details: 'Detailer',
        },
        // Sprint 63.33.0 (#594) — standards packs installed on their own.
        standalone_packs: {
            title: 'Standards',
            description: 'International standards are installed on their own: no market pack includes them. Installing one adds its frameworks, requirements and forms for your organisation.',
            install: 'Install',
            install_named: 'Install {name}',
            installing: 'Installing…',
            installing_named: 'Installing… {name}',
            installed: 'Installed',
            installed_other_route: 'Installed by another route',
            installed_other_route_hint: 'Its frameworks are present for your organisation, but this standard did not install them: another pack or a record created by hand did. There is nothing for it to remove here.',
            confirm_title: 'Install {name}?',
            confirm_message: 'This installs {name} for your organisation, with the frameworks, requirements and forms it carries.',
            confirm_label: 'Install',
            install_success: '{name} installed: {frameworks}, {forms}.',
            install_failed: 'The standard could not be installed.',
            install_no_permission: 'Installing a standard needs permission to change regulatory frameworks.',
            remove: 'Remove',
            remove_named: 'Remove {name}',
            removing: 'Removing…',
            removing_named: 'Removing… {name}',
            remove_confirm_title: 'Remove {name}?',
            remove_confirm_message: 'This removes {name} from your organisation, with the frameworks, requirements, deadlines and forms it installed, and every Module Mapping recorded against its frameworks, including mappings your organisation created or edited. Installing it again does not restore those mappings. Forms your organisation has filled in are kept. A removal cannot be undone.',
            remove_confirm_label: 'Remove',
            remove_success: '{name} removed: {frameworks}, {forms}.',
            remove_failed: 'The standard could not be removed.',
            remove_no_permission: 'Removing a standard needs permission to change regulatory frameworks.',
            refusal_already_installed: 'This standard is already installed for your organisation.',
            refusal_not_installable_alone: 'This pack is not installed on its own here: it is installed with its market, or from the sector overlay catalogue.',
            refusal_not_found: 'This standard is no longer in the catalogue. Reload the page.',
            refusal_not_installed: 'This standard is not installed for your organisation, so there is nothing to remove.',
            refusal_installed_by_another_route: 'This standard did not install its frameworks for your organisation, so it has nothing to remove. Remove them where they were installed.',
            refusal_forbidden: 'You do not have permission to change regulatory frameworks.',
            refusal_custody: 'This standard installs restricted registers; installing it requires the regulatory-artifact custody permission.',
        },
        translations_detail: {
            source_locale: 'Quellsproch',
            target_locale: 'Zilsproch',
            locales_must_differ: 'Quell- a Zilsproch musse verschidde sinn.',
            all_keys_translated: 'All Schlësselen iwwersat',
            keys_missing: '{count} Schlëssel(en) feelen',
            show_missing_only: 'Nëmme fehlend weisen',
            namespace: 'Nummraum',
            source: 'Quell',
            target: 'Zil',
        },
        frameworks_detail: {
            // fix-sprint-20260923-2136 — the detail page's own states, before the
            // framework record has been read (title, missing code, not found, failed).
            page_title_fallback: 'Framework',
            page_unavailable_heading: 'Framework unavailable',
            page_missing_code: 'No framework code was given in the address.',
            page_not_found: 'Framework {code} not found.',
            page_not_found_generic: 'Framework not found.',
            page_load_failed: 'The framework could not be loaded.',
            tab_frameworks: 'Kaderen',
            tab_watchlist: 'Iwwerwaachungslëscht',
            authority: 'Autoritéit',
            requirements_count: 'Ufuerderungen',
            all_types: 'All Typen',
            collapse_all: 'All zesummeklappen',
            expand_all: 'All ausklappen',
            all_sectors: 'All sectors',
            filter_by_sector: 'Filter by sector',
            search_frameworks: 'Search by name or code...',
            sef_url_label: 'Framework URL',
            sef_url_copy: 'Copy',
            sef_url_copy_label: 'Copy framework URL',
            sef_url_copied: 'Framework URL copied',
            sef_url_copy_failed: 'Could not copy the framework URL',
            filtered_to: 'Gefiltert op',
            clear_filter: 'Filter läschen',
            no_requirements: 'Keng Ufuerderungen fir dëse Framework fonnt.',
            requirements_label: 'Ufuerderungen',
            status_updated: 'Status erfollegräich aktualiséiert',
            overview_tab: 'Iwwersiicht',
            forms_tab: 'Formulairen',
            procedures_tab: 'Prozeduren',
            fines_risks_tab: 'Strofen a Risiken',
            deadlines_tab: 'Frist',
            guidance_tab: 'Orientatioun',
            guidance_empty: 'Kee Leedungsinhalt fir dëse Kader.',
            guidance_loading: 'Lueden Leedung…',
            guidance_section: 'Orientatioun Rubrik',
            guidance_view: 'View Orientatioun',
            guidance_map_to_workflow: 'Kaart op Workflow',
            guidance_link_evidence: 'Link Beweis',
            guidance_filter_type: 'Typ',
            guidance_filter_category: 'Kategorie',
            guidance_filter_module: 'Modul',
            guidance_filter_status: 'Status',
            guidance_filter_search: 'Sich Orientatioun',
            guidance_filter_all: 'All',
            guidance_table_reference: 'Referenz',
            guidance_table_name: 'Leedung Numm',
            guidance_table_type: 'Typ',
            guidance_table_category: 'Kategorie',
            guidance_table_effective_date: 'Effektiv Datum',
            guidance_table_modules: 'Zesummenhang Moduler',
            guidance_table_actions: 'Aktiounen',
            guidance_reference_only_banner: 'Nëmme Referenz - gëllt fir {{sector}} Secteur awer net fir Är Organisatioun.',
            guidance_open_official_source: 'Offiziell Quell opmaachen',
            guidance_no_detail: 'Keng zousätzlech Detailer fir dës Guidance verfügbar.',
            enum_guidance_type_cross_framework_note: 'Cross-Framework Note',
            enum_guidance_type_draft_status_note: 'Draft Status Note',
            enum_guidance_type_framework_hierarchy_note: 'Framework Hierarchy Note',
            enum_guidance_type_framework_overlay_note: 'Framework Overlay Note',
            enum_guidance_type_implementing_decree_summary: 'Implementing Decree Summary',
            enum_guidance_type_practical_unofficial_practice: 'Practical (Unofficial) Practice',
            enum_guidance_category_auction: 'Auction',
            enum_guidance_category_complaint_dispute: 'Complaint & Dispute',
            enum_guidance_category_draft_governance: 'Draft Governance',
            enum_guidance_category_email_zip_submission: 'Email/ZIP Submission',
            enum_guidance_category_exchange_classification: 'Exchange Classification',
            enum_guidance_category_framework_hierarchy: 'Framework Hierarchy',
            enum_guidance_category_high_risk_ai: 'High-Risk AI',
            enum_guidance_category_lifecycle_controls: 'Lifecycle Controls',
            enum_guidance_category_listing_review: 'Listing Review',
            enum_guidance_category_national_database_access: 'National Database Access',
            enum_guidance_category_outsourced_processing: 'Outsourced Processing',
            enum_guidance_category_participant_registration: 'Participant Registration',
            enum_guidance_category_personal_data_linkage: 'Personal Data Linkage',
            enum_guidance_category_personnel_conditions: 'Personnel Conditions',
            enum_guidance_category_pricing_and_public_asset: 'Pricing & Public Asset',
            enum_guidance_category_procedural_overlay: 'Procedural Overlay',
            enum_guidance_category_public_sector_ai: 'Public-Sector AI',
            enum_guidance_category_requirement_detail: 'Requirement Detail',
            enum_guidance_category_responsibilities: 'Responsibilities',
            enum_guidance_category_risk_and_remediation: 'Risk & Remediation',
            enum_guidance_category_sandbox: 'Sandbox',
            enum_guidance_category_scope: 'Scope',
            enum_guidance_category_security_drills_incidents: 'Security Drills & Incidents',
            enum_guidance_category_submission_channel: 'Submission Channel',
            enum_guidance_category_transaction_evidence: 'Transaction Evidence',
            enum_guidance_category_transparency_labeling: 'Transparency Labeling',
            enum_module_evidence_management: 'Evidence Management',
            enum_module_security: 'Security',
            form_category_access: 'Access',
            form_category_application: 'Application',
            form_category_authority_response_record: 'Authority Response Record',
            form_category_business_plan: 'Business Plan',
            form_category_capital_adequacy: 'Capital Adequacy',
            form_category_capital_adequacy_report: 'Capital Adequacy Report',
            form_category_certificate: 'Certificate',
            form_category_classification: 'Classification',
            form_category_compliance: 'Compliance',
            form_category_cross_border_transfer: 'Cross-Border Transfer',
            form_category_decision: 'Decision',
            form_category_email_submission_record: 'Email Submission Record',
            form_category_evidence: 'Evidence',
            form_category_evidence_pack: 'Evidence Pack',
            form_category_evidence_pack_manifest: 'Evidence Pack Manifest',
            form_category_forms: 'Forms',
            form_category_information_system: 'Information System',
            form_category_internal_audit: 'Internal Audit',
            form_category_internal_audit_report: 'Internal Audit Report',
            form_category_internal_control: 'Internal Control',
            form_category_internal_control_report: 'Internal Control Report',
            form_category_model_risk: 'Model Risk',
            form_category_notification: 'Notification',
            form_category_official_certificate: 'Official Certificate',
            form_category_official_reference: 'Official Reference',
            form_category_policy: 'Policy',
            form_category_record: 'Record',
            form_category_remediation: 'Remediation',
            form_category_report: 'Report',
            form_category_reporting: 'Reporting',
            form_category_risk: 'Risk',
            form_category_risk_data: 'Risk Data',
            form_category_risk_management_report: 'Risk Management Report',
            form_category_sandbox: 'Sandbox',
            form_category_security_plan: 'Security Plan',
            form_category_technical_plan: 'Technical Plan',
            form_category_training: 'Training',
            unit_absolute_date: 'Absolute date',
            unit_annual: 'Annual',
            unit_before_transfer: 'Before transfer',
            unit_before_use: 'Before use',
            unit_calendar_days_after_event: 'Calendar days after event',
            unit_calendar_days_before_event: 'Calendar days before event',
            unit_date: 'Date',
            unit_days: 'Days',
            unit_event: 'Event',
            unit_hours_from_authentication: 'Hours from authentication',
            unit_month: 'Month',
            unit_months: 'Months',
            unit_months_from_future_effective_date: 'Months from future effective date',
            unit_on_request: 'On request',
            unit_quarterly: 'Quarterly',
            unit_upon_request: 'Upon request',
            unit_weeks: 'Weeks',
            unit_without_undue_delay: 'Without undue delay',
            unit_working_days_before_event: 'Working days before event',
            unit_year: 'Year',
            unit_year_or_material_change: 'Year or material change',
            unit_years: 'Years',
            unit_years_minimum: 'Years (minimum)',
            unit_day: 'Day',
            unit_week: 'Week',
            sla_type_phased_installation_obligation: 'Phased installation obligation',
            sla_type_transitional_window_end: 'Transitional period ends',
            sla_type_data_retention_minimum: 'Data retention, minimum',
            sla_type_authority_appraisal_result_d356_as_issued: 'Authority’s appraisal result (text as issued)',
            sla_type_authority_appraisal_result_amended: 'Authority’s appraisal result (amended text)',
            sla_type_authority_appraisal_result_nq22: 'Authority’s appraisal result (transitional text)',
            sla_type_transitional_end_date: 'Automatic-debit transition ends',
            sla_type_pre_launch_report_submission: 'Pre-launch report to the State Bank',
            sla_type_testing_term_ceiling: 'Maximum testing term',
            sla_type_end_of_purpose_disposition: 'Return, deletion or destruction when the purpose ends',
            sla_type_publicity_on_regulator_requirement: "Notice and publicity on the regulator's requirement",
            sla_type_information_on_regulator_request: "Information provided on the regulator's request",
            sla_type_depositor_mismatch_handling_period: "Handling period for a depositor's mismatch report",
            sla_type_depositor_lookup_start: 'Depositor lookup start',
            sla_type_periodic_security_assessment: 'Annual security assessment',
            sla_type_periodic_software_list_review: 'Approved software list review',
            sla_type_periodic_risk_review: 'Risk situation review',
            sla_type_periodic_continuity_drill: 'Continuity drill',
            sla_type_periodic_staff_training: 'Staff security training',
            sla_type_record_retention_minimum: 'Online log retention, minimum',
            sla_type_record_backup_minimum: 'Log backup retention, minimum',
            sla_type_periodic_vulnerability_scan: 'Internet-facing vulnerability scan',
            sla_type_remediation_patch_window_critical_internet_facing: 'Critical vulnerability patch, component directly connected to the Internet',
            sla_type_remediation_patch_window_critical_other: 'Critical vulnerability patch, other components',
            sla_type_remediation_patch_window_high_internet_facing: 'High-rated vulnerability patch, component directly connected to the Internet',
            sla_type_remediation_patch_window_high_other: 'High-rated vulnerability patch, other components',
            sla_type_periodic_security_review: 'Mobile app security review',
            sla_type_credential_max_validity: 'PIN and password, maximum validity',
            sla_type_credential_default_validity: 'Credential, default validity',
            sla_type_otp_max_validity_sms: 'SMS OTP, maximum validity',
            sla_type_otp_max_validity_voice: 'Voice OTP, maximum validity',
            sla_type_otp_max_validity_email: 'Email OTP, maximum validity',
            sla_type_otp_max_validity_matrix_card: 'OTP from an OTP matrix card, maximum validity',
            sla_type_otp_max_validity_soft: 'Soft OTP code, maximum validity',
            sla_type_otp_max_validity_token: 'Token OTP, maximum validity',
            sla_type_card_max_usable_life: 'OTP matrix card, maximum usable life',
            sla_type_confirmation_request_validity: 'Two-channel confirmation request, validity',
            sla_type_biometric_match_time_limit_device: 'Device biometric match, time limit',
            sla_type_biometric_match_time_limit_stored_by_organisation: 'Biometric match against data held by the organisation, time limit',
            sla_type_ai_risk_classification_before_use: 'AI risk classification (before use)',
            sla_type_annual_risk_assessment_report: 'Annual risk assessment report',
            sla_type_annual_training_readiness: 'Annual training readiness',
            sla_type_art_30_records_availability_to_dpa: 'Art. 30 records availability to DPA',
            sla_type_audit_window_type_i: 'Audit window (Type I)',
            sla_type_audit_window_type_ii_min: 'Audit window (Type II, minimum)',
            sla_type_audit_window_type_ii_typical: 'Audit window (Type II, typical)',
            sla_type_authentication_result_retention_readiness: 'Authentication result retention readiness',
            sla_type_breach_communication_to_data_subjects: 'Breach communication to data subjects',
            sla_type_breach_notification_to_supervisory_authority: 'Breach notification to supervisory authority',
            sla_type_classification_lifecycle_review: 'Classification lifecycle review',
            sla_type_consultation_end: 'Consultation end',
            sla_type_annual_activity_report_proposed: 'Annual activity report (proposed)',
            sla_type_approved_list_publication_proposed: 'Approved list publication (proposed)',
            sla_type_course_result_report_proposed: 'Course result report (proposed)',
            sla_type_foreign_certificate_application_proposed: 'Foreign certificate recognition application (proposed)',
            sla_type_network_registration_review_proposed: 'Training network registration review (proposed)',
            sla_type_recognised_body_change_notice_proposed: 'Recognised body change notice (proposed)',
            sla_type_agency_label_publication_proposed: 'Agency publication of the label and DPID (proposed)',
            sla_type_label_dpid_update_proposed: 'Label update with the DPID (proposed)',
            sla_type_inspection_annual_report_proposed: 'Annual inspection report to the Ministry (proposed)',
            sla_type_content_takedown_response: 'Content takedown response',
            sla_type_cookie_consent_reconfirmation: 'Cookie consent reconfirmation',
            sla_type_corrective_action_default: 'Corrective action (default)',
            sla_type_cross_border_core_authority_review_complex: 'Cross-border core data authority review (complex)',
            sla_type_cross_border_core_authority_review_standard: 'Cross-border core data authority review (standard)',
            sla_type_cross_border_exception_postfiling: 'Cross-border exception (post-filing)',
            sla_type_cross_border_important_prefiling: 'Cross-border important data (pre-filing)',
            sla_type_ctia_dossier_filing: 'CTIA dossier filing',
            sla_type_cross_border_dossier_applicant_completion: 'Applicant completes the cross-border transfer impact dossier',
            sla_type_database_update: 'Database update',
            sla_type_decision_effective_date: 'Decision effective date',
            sla_type_decree_effective_date: 'Decree effective date',
            sla_type_circular_effective_date: 'Circular effective date',
            sla_type_system_upgrade_completion: 'System upgrade completion',
            sla_type_incident_recovery_resumption: 'Resumption after an incident is fixed',
            sla_type_report_correction_resubmission: 'Report correction and resubmission',
            sla_type_regulator_review_period: 'Authority review period',
            sla_type_submission_window_lock: 'Submission window lock',
            sla_type_transitional_reporting_regime: 'Transitional reporting arrangement',
            sla_type_staged_commencement_date: 'Staged commencement date',
            sla_type_decree_issued: 'Decree issued',
            sla_type_direct_marketing_opt_out_honor: 'Direct marketing opt-out honoring',
            sla_type_document_review_annual: 'Document review (annual)',
            sla_type_dpia_prior_consultation_dpa_response: 'DPIA prior consultation — DPA response',
            sla_type_dsar_refusal_notification: 'DSAR refusal notification',
            sla_type_dsar_response: 'DSAR response',
            sla_type_dsar_response_extension: 'DSAR response extension',
            sla_type_emergency_drill_cadence: 'Emergency drill cadence',
            sla_type_entrusted_processing_record_retention: 'Entrusted processing record retention',
            sla_type_event_review: 'Event review',
            // Sprint 62.49.1 (DB-10) — the consumer-rights pack's acknowledgement-of-receipt row.
            sla_type_consumer_complaint_receipt_acknowledgement: 'Notice to the consumer that a report, request or complaint was received',
            sla_type_food_traceability_records_request: 'Food traceability records request (24 hours)',
            sla_type_food_traceability_system_update: 'Food traceability system update',
            sla_type_evidence_currency_limit: 'Personal record statement currency (no more than 6 months before filing)',
            sla_type_framework_effective_date: 'Framework effective date',
            sla_type_high_risk_conformity_assessment_before_use: 'High-risk conformity assessment (before use)',
            sla_type_ict_systems_yearly_testing: 'ICT systems yearly testing',
            sla_type_important_core_transfer_approval_readiness: 'Important/core data transfer approval readiness',
            sla_type_incident_early_warning: 'Incident early warning',
            sla_type_incident_final_report: 'Incident final report',
            sla_type_incident_initial_notification: 'Incident initial notification',
            sla_type_cybersecurity_law_art45_1_assigned_level_protection_transition: 'Information systems with an assigned security level — protection conditions transition',
            sla_type_incident_initial_notification_outer_limit: 'Incident initial notification — outer limit from awareness',
            sla_type_incident_intermediate_report: 'Incident intermediate report',
            sla_type_incident_notification: 'Incident notification',
            sla_type_information_sharing_arrangement_notification: 'Information-sharing arrangement notification',
            sla_type_internal_audit_annual: 'Internal audit (annual)',
            sla_type_internal_post_consultation_review: 'Internal post-consultation review',
            sla_type_internal_post_session_review: 'Internal post-session review',
            sla_type_internal_review: 'Internal review',
            sla_type_law_effective_date: 'Law effective date',
            sla_type_lawful_deletion_request: 'Lawful deletion request',
            sla_type_management_body_training_cycle: 'Management body training cycle',
            sla_type_management_review_annual: 'Management review (annual)',
            sla_type_national_assembly_session: 'National Assembly session',
            sla_type_national_assembly_plenary_submission: 'Government submission heard by the National Assembly',
            sla_type_national_synthetic_database_response: 'National synthetic database response',
            sla_type_personnel_reassessment_readiness: 'Personnel reassessment readiness',
            sla_type_platform_registration_amendment: 'Platform registration amendment',
            sla_type_processing_log_retention: 'Processing log retention',
            sla_type_recertification_annual: 'Recertification (annual)',
            sla_type_recertification_cycle: 'Recertification cycle',
            sla_type_reclassification_authority_notification: 'Reclassification authority notification',
            sla_type_recurring_review: 'Recurring review',
            sla_type_reference_issued: 'Reference issued',
            sla_type_remediation_default: 'Remediation (default)',
            sla_type_risk_management_review_cycle: 'Risk management review cycle',
            sla_type_risk_management_self_assessment_cycle: 'Risk management self-assessment cycle',
            sla_type_roadmap_issued: 'Roadmap issued',
            sla_type_ropa_review_cycle: 'RoPA review cycle',
            sla_type_sandbox_certificate_review_ministry: 'Sandbox certificate review (ministry)',
            sla_type_sandbox_certificate_review_multi_authority: 'Sandbox certificate review (multi-authority)',
            sla_type_sandbox_certificate_review_provincial: 'Sandbox certificate review (provincial)',
            sla_type_sandbox_completion_report: 'Sandbox completion report',
            sla_type_sandbox_completion_certificate_review: 'Sandbox completion certificate review',
            sla_type_sandbox_dossier_validity_check: 'Sandbox dossier validity check',
            sla_type_sandbox_extension_application: 'Sandbox extension application',
            sla_type_sandbox_extension_decision: 'Sandbox extension decision',
            sla_type_sandbox_transitional_extension_request: 'Transitional operation extension request',
            sla_type_sandbox_transitional_extension_decision: 'Transitional operation extension decision',
            sla_type_sandbox_periodic_report_level_1_2: 'Sandbox periodic report (level 1–2)',
            sla_type_sandbox_periodic_report_level_3: 'Sandbox periodic report (level 3)',
            sla_type_sandbox_serious_incident_report: 'Sandbox serious incident report',
            sla_type_self_sovereign_log_sync_readiness: 'Self-sovereign log sync readiness',
            sla_type_serious_incident_final_report: 'Serious incident final report',
            sla_type_serious_incident_preliminary_other: 'Serious incident preliminary report (other)',
            sla_type_serious_incident_preliminary_severe: 'Serious incident preliminary report (severe)',
            sla_type_state_agency_ai_impact_assessment_before_use: 'State-agency AI impact assessment (before use)',
            sla_type_supply_chain_reassessment_cycle: 'Supply-chain reassessment cycle',
            sla_type_surveillance_audit_annual: 'Surveillance audit (annual)',
            sla_type_telecom_breach_authority_notification: 'Telecom breach — authority notification',
            sla_type_telecom_breach_authority_supplement: 'Telecom breach — authority supplement',
            sla_type_telecom_breach_subject_notification: 'Telecom breach — subject notification',
            sla_type_third_party_register_annual_reporting: 'Third-party register annual reporting',
            sla_type_tlpt_cycle: 'TLPT cycle',
            sla_type_traceability_system_connection_start: 'Traceability system connection start',
            sla_type_traffic_data_erasure_review: 'Traffic data erasure review',
            sla_type_transition_existing_systems: 'Transition — existing systems',
            sla_type_transition_new_systems: 'Transition — new systems',
            sla_type_transition_readiness: 'Transition readiness',
            guidance_related_link: 'Zesummenhang Orientatioun',
            deadlines_empty: 'Keng regulatoresch Fristen fir dëse Kader fonnt.',
            deadlines_sla_type: 'SLA-Typ',
            deadlines_value: 'Frist',
            deadlines_unit: 'Eenheet',
            deadlines_extension: 'Verlängerung',
            deadlines_last_day: 'Last day: {date}',
            deadlines_date_basis_computed: 'computed from the {period} period under the counting rule the legal reference names; not printed in the text',
            deadlines_date_basis_printed: 'printed in the signed text',
            deadlines_date_basis_next_working_day: 'an end on a non-working day moves to the next working day',
            deadlines_reference: 'Juristesch Referenz',
            framework_stack: 'Rechtsrahmen-Struktur',
            layer_parent_law: 'Stammgesetz',
            layer_implementing_decree: 'Ëmsetzungsveruerdnung',
            layer_enforcement_decree: 'Vollstreckungsveruerdnung',
            layer_admin_procedures: 'Verwaltungsprozeduren',
            // fix-sprint-20260912-1003 — Framework Stack layer groups and row states.
            layer_prime_minister_decision: 'Prime Minister Decision',
            layer_official_roadmap: 'Reference',
            layer_child_instrument: 'Ënnergeuerdnete Rechtsakten',
            stack_status_in_force: 'In force',
            stack_status_in_force_reference: 'In force (reference)',
            stack_status_in_force_until: 'In force to {date}',
            stack_status_from_date: 'From {date}',
            stack_status_draft: 'Draft',
            stack_status_promulgated: 'Promulgated {date}',
            stack_status_superseded: 'Superseded {date}',
            stack_status_reference: 'Reference',
            stack_status_unknown: 'Status not recognised',
            stack_show_lineage: 'Show lineage',
            stack_became: 'enacted as this instrument on {date}',
            stack_replaced_by: 'replaced by this instrument on {date}',
            stack_became_law: 'enacted as this law on {date}',
            stack_replaced_by_law: 'replaced by this law on {date}',
            stack_has_lineage_promulgated: 'enacted from a draft',
            stack_has_lineage_superseded: 'replaced an earlier instrument',
            stack_no_framework_page: 'no framework page',
            status_active: 'Aktiv',
            status_draft: 'Entworf',
            draft_enforcement_badge: 'Entworf — nach net erloss',
            effective_date_draft: 'Entworf — nach net a Kraaft',
            effective_date: 'Akraafttriede-Datum',
            forms_table_id: 'Formulaire-ID',
            forms_table_name: 'Formulairnumm',
            forms_table_source: 'Quell',
            forms_table_category: 'Kategorie',
            forms_table_type: 'Typ',
            fill_form: 'Formulaire ausfëllen',
            view_reference: 'View Referenz',
            forms_empty: 'Keng Formulairen fir dëse Rechtsrahmen fonnt.',
            forms_loading: 'Formulaire ginn gelueden...',
            filter_by_source_layer: 'Filter no Quellschicht',
            all_layers: 'All Schichten',
            procedures_id: 'Prozedur-ID',
            procedures_name: 'Prozedur',
            procedures_trigger: 'Ausléiser',
            procedures_steps: 'Schrëtt',
            procedures_outputs: 'Resultater',
            procedures_related_forms: 'Zougehéiereg Formulairen',
            procedures_submission_channels: 'Soumissioun Channels',
            procedures_empty: 'Keng Prozedure fir dëse Rechtsrahmen fonnt.',
            fines_area: 'Verstoussberäich',
            fines_articles: 'Artikelen',
            fines_individual: 'Strof (Eenzelperséin)',
            fines_organization: 'Strof (Organisatioun)',
            fines_revenue: 'Ëmsatzeskalatioun',
            fines_deadlines: 'Fristen',
            fines_remediation: 'Ofhëllefsmoossnamen',
            fines_related: 'Zougehéiereg Prozeduren',
            fines_empty: 'Keng Vollstreckungsdaten fir dëse Rechtsrahmen disponibel.',
            // fix-sprint D1 F22 — `fetchWatchlistFrameworks`'s catch set an empty
            // watchlist with no flag, so a failed read printed "No draft or upcoming
            // frameworks" — a POSITIVE finding about the regulatory horizon, and the
            // most consequential thing that tab can say wrongly.
            watchlist_load_failed: 'The draft and upcoming frameworks could not be read. Reload to try again.',
            watchlist_retry_subject: 'the regulatory watchlist',
            /*
              fix-sprint D1 F9 — the six regulatory-content tabs on the framework
              detail view swallowed every read failure into an empty array and then
              STATED absence: "No requirements found for this framework.", "No forms
              found…", "No procedures found…", "No guidance content…". On a
              regulatory surface "this framework declares no requirements" is a claim
              an operator acts on, and a failed read is not that claim. Each tab now
              states the failure instead, beside the reload that clears it.
            */
            content_reload: 'Reload',
            content_reloading: 'Reloading…',
            requirements_load_failed: 'The requirements for this framework could not be read. Reload to try again.',
            forms_load_failed: 'The forms for this framework could not be read. Reload to try again.',
            procedures_load_failed: 'The procedures for this framework could not be read. Reload to try again.',
            deadlines_load_failed: 'The deadlines for this framework could not be read. Reload to try again.',
            fines_load_failed: 'The enforcement data for this framework could not be read. Reload to try again.',
            guidance_load_failed: 'The guidance for this framework could not be read. Reload to try again.',
            content_partial_failed: 'Part of this content could not be read, so what is shown may be incomplete. Reload to try again.',
            requirements_retry_subject: 'the requirements for this framework',
            forms_retry_subject: 'the forms for this framework',
            procedures_retry_subject: 'the procedures for this framework',
            deadlines_retry_subject: 'the deadlines for this framework',
            fines_retry_subject: 'the enforcement data for this framework',
            guidance_retry_subject: 'the guidance for this framework',
            fines_risk_type: 'Risikotyp',
            fines_risk_triggers: 'Wat et ausléist',
            fines_risk_only_note: 'Dës Andréinnengen beschreiwen Konformitéits- a Betribsrisiko, keng Geldstrofen. Wa Geldstrofe gëllen, sinn se am iwwergeuerdnete Kader festgeluecht.',
            fines_enforcement_pending: 'Vollstreckungsrahmen nach net publizéiert. Sanktiounsdaten gi bäigesat, wann den Ëmsetzungsinstrument erloss gëtt.',
            view_details: 'Detailer weisen',
            back_to_list: "Zréck op d'Lëscht",
            description: 'Beschreiwung',
            jurisdiction: 'Zoustännegkeet',
            lineage: 'Ofstamung',
            replaces: 'Ersetzt',
            replaced_by: 'Ersaat duerch',
            requirements: 'Ufuerderungen',
            tab_navigation: 'Rechtsrahmen-Detail-Tabs',
            forms_table_actions: 'Aktiounen',
            create_form: 'Formulaire erstellen',
            mark_promulgated: 'Als promulgéiert markéieren',
            status_promulgated: 'Promulgéiert',
            promulgated_success: 'Schichtstatus op promulgéiert aktualiséiert',
            transition_review: 'Iwwergangsuberprüfung',
            transition_review_desc: "Dëse Kader huet eng ausstehend Iwwergangsuberprüfung fir d'Iwwernam vum Nofolgergesetz.",
            transition_review_by: 'Iwwerpréiwungsfrist',
            transition_review_status: 'Iwwerpréiwungsstatus',
            transition_review_notes: 'Notizen',
            transition_review_pending: 'Ausstehend',
            transition_review_in_progress: 'Am Gaang',
            transition_review_completed: 'Ofgeschloss',
            transition_review_successor: 'Nofolger-Kader',
            transition_review_lead_time: 'Virbereedungszäit',
            transition_review_lead_time_days: '{days} Deeg virum Akraafttriede',
            transition_review_overdue: 'overdue',
            transition_review_forms_heading: 'Formulaire-Iwwergangsuberprüfungen',
            transition_review_forms_desc: 'Iwwerpréift all operativ Schabloun op Relevanz ënner dem Nofolger-Kader.',
            transition_review_outcome: 'Resultat',
            transition_review_outcome_carry_forward: 'Bäibehalen',
            transition_review_outcome_carry_forward_revised: 'Bäibehalen (iwwerschafft)',
            transition_review_outcome_retire: 'Ausser Kraaft setzen',
            transition_review_outcome_split: 'Opdeelen',
            transition_review_outcome_awaiting_decree: 'Waart op Reglement',
            transition_review_no_forms: 'Keng Formulaire-Iwwergangsuberprüfunge konfiguréiert.',
            transition_review_update_success: 'Iwwergangsuberprüfung aktualiséiert',
            transition_review_operational_note: "Dëst sinn prozessënnerstëtzend operativ Schablounen, keng gesetzlech Formularzuelen. Déi juristesch Zouuerdnung bleift beim ale Kader, bis d'Nofolgergesetz a Kraaft trëtt.",
            rights_tab: 'Rechter',
            rights_empty: 'Keng Datesubjekt Rechter fir dëse Kader definéiert.',
            rights_code: 'Code',
            rights_name: 'Richteg',
            rights_legal_basis: 'Juristesch Basis',
            rights_elaborating_articles: 'Ausbau Artikelen',
            rights_description: 'Beschreiwung',
            requirements_reference: 'Referenz',
            requirements_mandatory: 'Obligatoresch',
            // Sprint 62.58.0 (AC-B1, AC-B3) — whom each requirement addresses, and the filter over the declared classes.
            requirements_addressed_to: 'Addressed to',
            requirements_addressee_filter_hint: 'Shows the requirements addressed to any class you choose. Nothing is chosen for you.',
            requirements_addressee_count: 'Showing {shown} of {total} requirements',
            requirements_addressee_clear: 'Show all requirements',
            requirements_basis_note_show: 'Read the whole note',
            requirements_basis_note_hide: 'Show less',
            requirements_yes: 'Yes',
            requirements_no: 'No',
            requirements_source: 'Quell',
            category_legal_basis: 'Juristesch Basis',
            category_data_subject_rights: 'Donnéeën Sujet Rechter',
            category_notification: 'Notifikatioun',
            category_assessment: 'Bewäertung',
            category_governance: 'Gouvernance',
            category_record_keeping: 'Rekordhaltung',
            category_cross_border: 'Grenziwwerschreidend',
            category_technical_measures: 'Technesch Moossnamen',
            category_incident_reporting: 'Tëschefall Berichterstattung',
            category_risk_management: 'Risiko Management',
            category_supply_chain: 'Versuergung Kette',
            category_certification: 'Zertifikatioun',
            child_frameworks: '{count} Kand Kader',
            child_frameworks_plural: '{count} Kand Kaderen',
            replaces_prefix: 'Ersetzt:',
            replaced_by_prefix: 'Ersat duerch:',
            responsible_prefix: 'Responsabel:',
            watchlist_empty_hint: 'Frameworks mam Status "Entworf" oder "Upcoming" erschéngen hei fir d\'Iwwerwaachung.',
            framework_count: '{count} Kader',
            framework_count_plural: '{count} Kaderen',
            hiding_legacy: '(verstoppt {count} Legacy)',
            label_VN_PDPD_DECREE_13: 'PDPD Dekret 13/2023',
            label_VN_PDPL_LAW_2025: 'PDPL Gesetz 91/2025',
            label_VN_PDPL_DECREE_356_2025: 'Dekret 356/2025/ND-CP',
            label_VN_PDPL_ADMIN_PROCEDURES_778_2026: 'QĐ 778/QĐ-BCA-A05',
            label_VN_PDPL_ENFORCEMENT_DRAFT_2026: 'PDPL Enforcement Draft 2026',
            label_VN_MOPS_ADMIN_SIMPLIFICATION_NQ22_2026: 'NQ22 Temporary Overlay 2026',
            label_VN_DATA_LAW: 'Dategesetz',
            label_VN_DATA_LAW_DECREE_165_2025: 'Dategesetz Dekret 165/2025',
            label_VN_DATA_EXCHANGES_DRAFT_2026: 'Datenbörsen Entworf 2026',
            label_VN_TELECOM_LAW_2023: 'Telekommunikatiounsgesetz 2023',
            label_VN_TELECOM_DECREE_163_2024: 'Telecom Dekret 163/2024',
            label_VN_CYBERSEC_LAW: 'Cybersecurity Law (24/2018)',
            label_VN_CYBERSEC_DECREE_53: 'Cybersec Dekret 53/2022',
            label_VN_CYBERSEC_LAW_2025: 'Cybersecurity Law 116/2025',
            label_VN_ECOMMERCE_LAW_2025: 'E-Commerce Gesetz 122/2025',
            label_VN_AI_LAW_2026: 'AI Gesetz 2026',
            label_VN_AI_DECREE_142_2026: 'AI Decree 142/2026/NĐ-CP',
            issuer_company: 'Firma',
            issuer_authority_a05_mps: 'MPS/A05',
            issuer_authority_mic: 'MIC',
            issuer_authority_mps_ndc: 'MPS/NDC',
            issuer_other_authority: 'Autoritéit',
            sla_type_rights_request_ack: 'Rechter Ufro Unerkennung',
            'sla_type_Access control review': 'Zougang Kontroll iwwerschaffen',
            'sla_type_Annual governance attestation': 'Jährlech Gouvernance Attest',
            'sla_type_Audit review cycle': 'Audit review Zyklus',
            'sla_type_Authority request response': 'Autoritéit Ufro Äntwert',
            'sla_type_Change notification processing': 'Ännerung Notifikatioun Veraarbechtung',
            'sla_type_Complaint acknowledgment': 'Plainte Unerkennung',
            'sla_type_Complaint resolution': 'Plainte Resolutioun',
            'sla_type_Confidentiality policy review': 'Iwwerpréiwung vun der Confidentialitéit Politik',
            'sla_type_Cybersecurity incident full report': 'Cybersecurity Tëschefall voll Bericht',
            'sla_type_Cybersecurity incident initial report': 'Cybersecurity Incident initial Report',
            'sla_type_Data inventory review': 'Date Stamminventar Iwwerpréiwung',
            'sla_type_Disclosure log review cycle': 'Offenbarung Log Iwwerpréiwung Zyklus',
            'sla_type_Incident classification decision': 'Tëschefall Klassifikatioun Decisioun',
            'sla_type_Information system classification review': 'Informatiounssystem Klassifikatioun Bewäertung',
            'sla_type_Notification processing (sales website/app)': 'Notifikatiounsveraarbechtung (Verkaf Websäit / App)',
            'sla_type_Pen test cycle': 'Pen Test Zyklus',
            'sla_type_Registration processing (service platform)': 'Aschreiwung Veraarbechtung (Service Plattform)',
            'sla_type_Risk assessment cycle': 'Risiko Bewäertung Zyklus',
            'sla_type_Takedown decision (after review)': 'Entfernungsentscheedung (no Iwwerpréiwung)',
            'sla_type_Termination notice (public)': 'Kënnegung Notiz (ëffentlech)',
            sla_type_ai_impact_assessment_completion: 'AI Impakt Bewäertung Ofschloss',
            sla_type_ai_sandbox_application_review: 'AI Sandbox Applikatioun Bewäertung',
            sla_type_ai_serious_incident_report: 'AI sérieux Tëschefall Bericht',
            sla_type_ai_system_registration: 'AI Systemregistrierung',
            sla_type_authority_request_response: 'Autoritéit Ufro Äntwert',
            sla_type_certificate_issuance_review: 'Zertifikat Emissioun Iwwerpréiwung',
            sla_type_complaint_handling_approach_notice: 'Plainte Ëmgank Approche Notiz',
            sla_type_complaint_incomplete_info_request: 'Plainte onkomplett Info Ufro',
            sla_type_data_governance_attestation: 'Date Gouvernance Attest',
            sla_type_dsr_acknowledgment: 'DSR Unerkennung',
            sla_type_dsr_cl2_withdraw_restrict_object: 'DSR (Cl.2) - Zréckzéien / limitéieren / Objet',
            sla_type_dsr_cl2_withdraw_restrict_object_third_party: 'DSR (Cl.2) - Drëtte Partei',
            sla_type_dsr_cl3_access_correct_provide: 'DSR (Cl.3) - Zougang / korrekt / bidden',
            sla_type_dsr_cl3_access_correct_provide_third_party: 'DSR (Cl.3) - Drëtte Partei',
            sla_type_dsr_cl4_deletion: 'DSR (Cl.4) - Läschen',
            sla_type_dsr_cl4_deletion_third_party: 'DSR (Cl.4) - Läschen, drëtt Partei',
            sla_type_dsr_cl3_access_third_party: 'DSR (Cl.3) — Access, third party',
            sla_type_chemical_incident_system_declaration: 'Chemical incident — declaration to the System',
            sla_type_code_termination_explanation_window: 'Code termination — window to explain',
            sla_type_code_restoration_by_managing_body: 'Code restoration by the managing body',
            sla_type_traceability_data_update_frequency: 'Traceability data — update frequency',
            sla_type_authority_decision_deadline: 'Authority decision — due date',
            sla_type_authority_list_update_cycle: 'Authority list — update cycle',
            sla_type_dsr_cl4_provide_restrict_third_party: 'DSR (Cl.4) — Restriction, third party',
            sla_type_dsr_cl5_protective_measures: 'DSR (Cl.5) - Schutzmoossnamen',
            sla_type_telecom_authority_request_response: 'Telecom Autoritéit Ufro Äntwert',
            sla_type_rights_request_completion: 'Rechter Ufro Ofschloss',
            sla_type_incident_authority_notification: 'Tëschefall Autoritéit Notifikatioun',
            sla_type_incident_subject_notification: 'Tëschefall Sujet Notifikatioun',
            sla_type_dpia_completion: 'DPIA Ofschloss',
            sla_type_data_retention_review: 'Donnéeën Retention Iwwerpréiwung',
            sla_type_authority_filing_review: 'Autoritéit Areeche Iwwerpréiwung',
            sla_type_authority_supplement_response: 'Autoritéit Zousaz Äntwert',
            sla_type_authority_resubmission: 'Autoritéit resubmission',
            sla_type_authority_filing_supplement_response: 'Autoritéit Areeche Zousaz Äntwert',
            sla_type_authority_filing_resubmission: 'Autoritéit Areeche resubmission',
            sla_type_authority_delivery_window: 'Authority delivery window',
            sla_type_authority_examination_window: 'Authority examination window',
            sla_type_authority_notice_trigger: 'Authority notice trigger',
            sla_type_authority_processing_window: 'Authority processing window',
            sla_type_before_event: 'Before the event',
            sla_type_instalment_maximum_period: 'Instalment maximum period',
            sla_type_lead_time_before_event: 'Lead time before the event',
            sla_type_reporting_cycle: 'Reporting cycle',
            sla_type_reporting_period: 'Reporting period',
            sla_type_statutory_filing_window: 'Statutory filing window',
            sla_type_statutory_payment_deadline: 'Statutory payment deadline',
            sla_type_taxpayer_filing_window: 'Taxpayer filing window',
            sla_type_taxpayer_notification_window: 'Taxpayer notification window',
            sla_type_taxpayer_response_window: 'Taxpayer response window',
            sla_type_taxpayer_supplementation_window: 'Taxpayer supplementation window',
            sla_type_transition_deadline: 'Transition deadline',
            sla_type_provision_end_date: 'Provision ceases to apply',
            sla_type_authority_notification_window: 'Authority notification window',
            sla_type_enforcement_trigger: 'Enforcement applies from',
            sla_type_authority_advance_notice: 'Authority advance notice',
            sla_type_refund_limitation_period: 'Refund limitation period',
            sla_type_reinspection_limitation_period: 'Re-inspection limitation period',
            sla_type_violation_classification_threshold: 'Violation classification threshold',
            sla_type_write_off_eligibility_threshold: 'Write-off eligibility threshold',
            unit_working_days: 'schaffen Deeg',
            unit_calendar_days: 'Kalenner Deeg',
            unit_hours: 'Stonnen',
            form_category_dpa: 'DPA',
            form_category_dsr: 'DSR',
            form_category_consent: 'Zoustëmmung',
            form_category_privacy: 'Privatsphär',
            form_category_breach: 'Verstouss',
            form_category_dpia: 'DPIA',
            form_category_register: 'Aschreiwen',
            form_category_monitoring: 'Iwwerwachung',
            form_category_vendor: 'Verkeefer',
            form_category_org: 'organisatoresch',
            form_category_sector: 'Secteur',
            form_category_data_mapping: 'Data Mapping',
            form_category_governance: 'Gouvernance',
            form_category_incident: 'Tëschefall',
            form_category_authority_submission: 'Autoritéit Soumissioun',
            form_category_notice: 'Notiz',
            form_category_assessment_report: 'Bewäertung Rapport',
            form_category_request: 'Ufro',
            form_category_dossier: 'Dossier',
            form_category_filing: 'Areeche',
            module_consent: 'Zoustëmmung',
            module_rights_request: 'Rechter Ufro',
            module_classification: 'Klassifikatioun',
            module_dpia: 'DPIA',
            module_governance: 'Gouvernance',
            module_monitoring: 'Iwwerwachung',
            module_register: 'Aschreiwen',
            module_risk: 'Risiko',
            module_data_mapping: 'Data Mapping',
            module_incident: 'Tëschefall',
            module_assessment: 'Bewäertung',
            module_vendor: 'Verkeefer',
            module_policy: 'Politik',
            module_audit: 'Audit',
            module_training: 'Training',
            module_discovery: 'Entdeckung',
            // Sprint 62.58.0 (walk O-2) — requirement applicable_module values with no label
            module_sector_licensing: 'Sector Licensing',
            module_electronic_transactions: 'Electronic Transactions',
            module_consumer_protection: 'Consumer Protection',
            module_ecommerce_governance: 'E-Commerce Governance',
            module_identity_verification: 'Identity Verification',
            module_program_governance: 'Program Governance',
            module_compliance_operations: 'Compliance Operations',
            module_vendor_governance: 'Vendor Governance',
            module_cybersecurity: 'Cybersecurity',
            module_authority_direction: 'Authority Direction',
            module_incident_response: 'Incident Response',
            module_data_governance: 'Data Governance',
            module_incident_operations: 'Incident Operations',
            module_regulatory_disclosure: 'Regulatory Disclosure',
            module_customer_protection: 'Customer Protection',
            module_regulatory_change: 'Regulatory Change',
            module_third_party_risk: 'Third-Party Risk',
            module_legal_register: 'Legal Register',
            module_change_control: 'Change Control',
            module_records: 'Records',
            enum_guidance_type_calculation_guidance: 'Berechnung Orientatioun',
            enum_guidance_type_implementation_guidance: 'Ëmsetzung Guidance',
            enum_guidance_type_interpretive_note: 'Interpretativ Notiz',
            enum_guidance_type_legal_guidance: 'Juristesch Orientatioun',
            enum_guidance_type_procedural_guidance: 'Prozedurale Leedung',
            enum_guidance_type_sector_guidance: 'Secteur Leedung',
            enum_guidance_type_technical_methodology: 'Technesch Methodik',
            enum_guidance_category_annual_reporting: 'Joresbericht',
            enum_guidance_category_authority_cooperation: 'Autoritéit Zesummenaarbecht',
            enum_guidance_category_authority_coordination: 'Autoritéit Koordinatioun',
            enum_guidance_category_business_continuity: 'Business Kontinuitéit',
            enum_guidance_category_capital_adequacy: 'Kapitaladequatitéit',
            enum_guidance_category_complaint_handling: 'Plainte Ëmgank',
            enum_guidance_category_consent: 'Zoustëmmung',
            enum_guidance_category_consent_management: 'Zoustëmmung Management',
            enum_guidance_category_consumer_protection: 'Konsumenteschutz',
            enum_guidance_category_control_environment: 'Kontroll Ëmfeld',
            enum_guidance_category_cross_border: 'Grenziwwerschreidend',
            enum_guidance_category_cross_border_transfers: 'Kräiz-Grenz Transfere',
            enum_guidance_category_data_classification: 'Daten Klassifikatioun',
            enum_guidance_category_data_localization: 'Daten Lokalisatioun',
            enum_guidance_category_data_protection_impact_assessment: 'Dateschutz Impakt Bewäertung',
            enum_guidance_category_direct_marketing: 'Direkte Marketing',
            enum_guidance_category_dpia: 'DPIA',
            enum_guidance_category_governance: 'Gouvernance',
            enum_guidance_category_ict_risk_management: 'ICT Risiko Management',
            enum_guidance_category_incident_management: 'Tëschefall Management',
            enum_guidance_category_internal_audit: 'Intern Audit',
            enum_guidance_category_isms_documentation: 'ISMS Dokumentatioun',
            enum_guidance_category_lawful_basis: 'Gesetzlech Basis',
            enum_guidance_category_licensing: 'Lizenz',
            enum_guidance_category_local_presence: 'Lokal Präsenz',
            enum_guidance_category_logging_and_retention: 'Logging & Retentioun',
            enum_guidance_category_logical_and_physical_access: 'Logesch & Kierperlech Zougang',
            enum_guidance_category_operational_resilience_testing: 'Operationell Widderstandsfäegkeet Testen',
            enum_guidance_category_outsourcing: 'Outsourcing',
            enum_guidance_category_platform_registration: 'Plattform Aschreiwung',
            enum_guidance_category_quality_of_service: 'Qualitéit vum Service',
            enum_guidance_category_risk_classification: 'Risiko Klassifikatioun',
            enum_guidance_category_risk_management: 'Risiko Management',
            enum_guidance_category_scope_definition: 'Ëmfang Definitioun',
            enum_guidance_category_sectoral_localisation: 'Sektoriell Lokalisatioun',
            enum_guidance_category_subscriber_protection: 'Abonnent Schutz',
            enum_guidance_category_supply_chain: 'Versuergung Kette',
            enum_guidance_category_system_classification: 'System Klassifikatioun',
            enum_guidance_category_system_operations: 'System Operatiounen',
            enum_guidance_category_telecommunications_data: 'Telekommunikatiounsdaten',
            enum_guidance_category_third_party_risk: 'Drëtt Partei Risiko',
            enum_guidance_category_transitional_provisions: 'Iwwergangsbestëmmungen',
            enum_guidance_category_vulnerability_management: 'Vulnerabilitéit Management',
            enum_guidance_status_active: 'Aktiv',
            enum_guidance_status_draft: 'Entworf',
            // Sprint 62.58.0 (walk D-1) — every guidance status a pack declares
            enum_guidance_status_deprecated: 'Deprecated',
            enum_guidance_status_superseded: 'Superseded',
            enum_guidance_status_upcoming: 'Upcoming',
            enum_guidance_status_legacy: 'Legacy',
            enum_guidance_status_temporary_active: 'Temporarily in effect',
            enum_guidance_status_active_reference: 'In force, reference only',
            enum_module_access_management: 'Zougang Management',
            enum_module_ai_governance: 'AI Gouvernance',
            enum_module_asset_management: 'Asset Management',
            enum_module_audit_trail: 'Audit Trail',
            enum_module_breach_notification: 'Verstouss Notifikatioun',
            enum_module_capital_adequacy: 'Kapitaladequatitéit',
            enum_module_classification: 'Klassifikatioun',
            enum_module_consent: 'Zoustëmmung',
            enum_module_consent_management: 'Zoustëmmung Management',
            enum_module_cross_border_transfers: 'Kräiz-Grenz Transfere',
            enum_module_data_localization: 'Daten Lokalisatioun',
            enum_module_data_mapping: 'Data Mapping',
            enum_module_data_protection: 'Dateschutz',
            enum_module_data_subject_rights: 'Donnéeën Sujet Rechter',
            enum_module_dpia: 'DPIA',
            enum_module_encryption: 'Verschlësselung',
            enum_module_governance: 'Gouvernance',
            enum_module_incident: 'Tëschefall',
            enum_module_incident_management: 'Tëschefall Management',
            enum_module_internal_audit: 'Intern Audit',
            enum_module_isms_documentation: 'ISMS Dokumentatioun',
            enum_module_isms_governance: 'ISMS Gouvernance',
            enum_module_logging_and_monitoring: 'Logging & Iwwerwachung',
            enum_module_monitoring: 'Iwwerwachung',
            enum_module_policy_management: 'Politik Gestioun',
            enum_module_risk: 'Risiko',
            enum_module_risk_management: 'Risiko Management',
            enum_module_training: 'Training',
            enum_module_vendor: 'Verkeefer',
            enum_module_vendor_management: 'Vendor Management',
            enum_module_vendor_risk: 'Verkeefer Risiko',
            enum_module_vulnerability_management: 'Vulnerabilitéit Management',
            // Sprint 62.58.0 (walk O-6) — English copy; translated in Phase 25 (R-57).
            enum_guidance_type_annex_reference: 'Annex Reference',
            enum_guidance_type_boundary_guidance: 'Boundary Guidance',
            enum_guidance_type_calculation_methodology: 'Calculation Methodology',
            enum_guidance_type_classification_guidance: 'Classification Guidance',
            enum_guidance_type_cross_framework_mapping: 'Cross-Framework Mapping',
            enum_guidance_type_enforcement_mechanics: 'Enforcement Mechanics',
            enum_guidance_type_follow_on_instrument_watchlist: 'Follow-On Instrument Watchlist',
            enum_guidance_type_framework_overview: 'Framework Overview',
            enum_guidance_type_framework_relationship: 'Framework Relationship',
            enum_guidance_type_framework_relationship_note: 'Framework Relationship Note',
            enum_guidance_type_handling_guidance: 'Handling Guidance',
            enum_guidance_type_implementation_note: 'Implementation Note',
            enum_guidance_type_implementation_timeline: 'Implementation Timeline',
            enum_guidance_type_obligation_map: 'Obligation Map',
            enum_guidance_type_official_reference_artifact: 'Official Reference Artifact',
            enum_guidance_type_operational_boundary: 'Operational Boundary',
            enum_guidance_type_operational_guidance: 'Operational Guidance',
            enum_guidance_type_platform_boundary: 'Platform Boundary',
            enum_guidance_type_readiness_mapping: 'Readiness Mapping',
            enum_guidance_type_reference_note: 'Reference Note',
            enum_guidance_type_regulatory_overview: 'Regulatory Overview',
            enum_guidance_type_requirement_detail: 'Requirement Detail',
            enum_guidance_type_scope_and_applicability: 'Scope and Applicability',
            enum_guidance_type_scope_guidance: 'Scope Guidance',
            enum_module_compliance: 'Compliance',
            enum_guidance_type_source_limitation: 'Source Limitation',
            enum_guidance_type_source_limits: 'Source Limits',
            enum_guidance_type_source_note: 'Source Note',
            enum_guidance_type_technical_reference: 'Technical Reference',
            enum_guidance_type_timeline: 'Timeline',
            enum_guidance_type_transition_guidance: 'Transition Guidance',
            enum_guidance_type_transition_note: 'Transition Note',
            enum_guidance_category_access: 'Access',
            enum_guidance_category_account_eligibility: 'Account Eligibility',
            enum_guidance_category_account_linkage: 'Account Linkage',
            enum_guidance_category_ai_functions: 'AI Functions',
            enum_guidance_category_amendments: 'Amendments',
            enum_guidance_category_annex: 'Annex',
            enum_guidance_category_assessment: 'Assessment',
            enum_guidance_category_audit: 'Audit',
            enum_guidance_category_authority: 'Authority',
            enum_guidance_category_authority_submission: 'Authority Submission',
            enum_guidance_category_boundary: 'Boundary',
            enum_guidance_category_certification: 'Certification',
            enum_guidance_category_claims: 'Platform Claims',
            enum_guidance_category_classification: 'Classification',
            enum_guidance_category_confidentiality: 'Confidentiality',
            enum_guidance_category_connection_procedure: 'Connection Procedure',
            enum_guidance_category_coordination: 'Coordination',
            enum_guidance_category_data_disclosure: 'Data Disclosure',
            enum_guidance_category_data_lifecycle: 'Data Lifecycle',
            enum_guidance_category_data_protection_and_security: 'Data Protection and Security',
            enum_guidance_category_database_regime: 'Database Regime',
            enum_guidance_category_deadlines: 'Deadlines',
            enum_guidance_category_dispute_support: 'Dispute Support',
            enum_guidance_category_document_reuse: 'Document Reuse',
            enum_guidance_category_duty_allocation: 'Duty Allocation',
            enum_guidance_category_effective_date: 'Effective Date',
            enum_guidance_category_electronic_enforcement: 'Electronic Enforcement',
            enum_guidance_category_electronic_submission: 'Electronic Submission',
            enum_guidance_category_enforcement: 'Enforcement',
            enum_guidance_category_enforcement_authority: 'Enforcement Authority',
            enum_guidance_category_enforcement_measures: 'Enforcement Measures',
            enum_guidance_category_evidence: 'Evidence',
            enum_guidance_category_exploitation_duties: 'Exploitation Duties',
            enum_guidance_category_forms_and_transition: 'Forms and Transition',
            enum_guidance_category_framework_map: 'Framework Map',
            enum_guidance_category_framework_mapping: 'Framework Mapping',
            enum_guidance_category_framework_relationships: 'Framework Relationships',
            enum_guidance_category_handling_measures: 'Handling Measures',
            enum_guidance_category_identification: 'Identification',
            enum_guidance_category_identification_code: 'Identification Code',
            enum_guidance_category_implementation: 'Implementation',
            enum_guidance_category_incident: 'Incident',
            enum_guidance_category_legal_basis: 'Legal Basis',
            enum_guidance_category_lifecycle: 'Lifecycle',
            enum_guidance_category_listing: 'Listing',
            enum_guidance_category_mapping: 'Mapping',
            enum_guidance_category_marketing_communications_boundary: 'Marketing Communications Boundary',
            enum_guidance_category_methodology: 'Methodology',
            enum_guidance_category_monitoring: 'Monitoring',
            enum_guidance_category_obligations: 'Obligations',
            enum_guidance_category_penalties: 'Penalties',
            enum_guidance_category_personal_data: 'Personal Data',
            enum_guidance_category_personal_data_protection: 'Personal Data Protection',
            enum_guidance_category_phase_in_timeline: 'Phase-In Timeline',
            enum_guidance_category_platform_boundary: 'Platform Boundary',
            enum_guidance_category_platform_duties: 'Digital Platform Duties',
            enum_guidance_category_platform_operation: 'Digital Platform Operation',
            enum_guidance_category_pricing: 'Pricing',
            enum_guidance_category_privacy: 'Privacy',
            enum_guidance_category_procedures: 'Procedures',
            enum_guidance_category_product_scope: 'Product Scope',
            enum_guidance_category_provenance: 'Provenance',
            enum_guidance_category_public_sector: 'Public Sector',
            enum_guidance_category_readiness: 'Readiness',
            enum_guidance_category_record_keeping: 'Record Keeping',
            enum_guidance_category_reference_data: 'Reference Data',
            enum_guidance_category_regulatory_watch: 'Regulatory Watch',
            enum_guidance_category_related_instruments: 'Related Instruments',
            enum_guidance_category_remediation: 'Remediation',
            enum_guidance_category_reporting: 'Reporting',
            enum_guidance_category_sanctions: 'Sanctions',
            enum_guidance_category_source: 'Source',
            enum_guidance_category_status: 'Status',
            enum_guidance_category_system_connection: 'System Connection',
            enum_guidance_category_technical_measures: 'Technical Measures',
            enum_guidance_category_testing: 'Testing',
            enum_guidance_category_timeline: 'Timeline',
            enum_guidance_category_transition: 'Transition',
            enum_guidance_category_transitional: 'Transitional Rules',
            enum_module_access: 'Access',
            enum_module_access_review: 'Access Reviews',
            enum_module_ai_advisor: 'AI Advisor',
            enum_module_assessment: 'Assessment',
            enum_module_audit: 'Audit',
            enum_module_authority_operations: 'Authority Operations',
            enum_module_authority_direction: 'Authority Direction',
            enum_module_continuity: 'Business Continuity',
            enum_module_deidentification: 'De-identification',
            enum_module_deletion_orchestration: 'Deletion Orchestration',
            enum_module_identity_verification: 'Identity Verification',
            enum_module_legal_matters: 'Legal Matters',
            enum_module_program_governance: 'Program Governance',
            enum_module_rights_request: 'Rights Request',
            enum_module_sector_licensing: 'Sector Licensing',
            enum_module_technical_measures: 'Technical Measures',
            category_access: 'Zougang',
            category_availability: 'Disponibilitéit',
            category_change_management: 'Changement Management',
            category_classification: 'Klassifikatioun',
            category_communication_and_information: 'Kommunikatioun & Informatioun',
            category_compliance: 'Konformitéit',
            category_confidentiality: 'Confidentialitéit',
            category_control_activities: 'Kontroll Aktivitéiten',
            category_control_environment: 'Kontroll Ëmfeld',
            category_incident: 'Tëschefall',
            category_internal_audit: 'Intern Audit',
            category_internal_control: 'Intern Kontroll',
            category_logical_and_physical_access: 'Logesch & Kierperlech Zougang',
            category_mis_data: 'Management Informatiounen System Daten',
            category_monitoring: 'Iwwerwachung',
            category_monitoring_activities: 'Iwwerwachung Aktivitéiten',
            category_outsourcing: 'Outsourcing',
            category_privacy: 'Privatsphär',
            category_processing_integrity: 'Veraarbechtung Integritéit',
            category_reporting: 'Berichterstattung',
            category_risk_assessment: 'Risiko Bewäertung',
            category_risk_mitigation: 'Risiko Reduktioun',
            category_sector_applicability: 'Secteur Applikatioun',
            category_system_operations: 'System Operatiounen',
            category_training: 'Training',
            category_vendor: 'Verkeefer',
            guidance_related_forms_advisory: 'Puer Formen hunn Zesummenhang Orientatioun - Vue an der',
            guidance_related_procedures_advisory: 'Puer Prozeduren hunn Zesummenhang Orientatioun - Vue an der',
            guidance_related_requirements_advisory: 'Puer Ufuerderunge hunn Zesummenhang Orientatioun - Vue an der',
        },
        channels: {
            title: 'Andragskanäl an Fristen',
            channels: 'Andragskanäl',
            add_channel: 'Kanal derbäisetzen',
            channel_type: 'Typ',
            name: 'Numm',
            instructions: 'Instruktiounen',
            active: 'Aktiv',
            no_channels: 'Keng Andragskanäl konfiguréiert',
            portal: 'Portal',
            in_person: 'Perséinlech',
            postal: 'Post',
            email: 'E-Mail',
            add: 'Derbäisetzen',
            deadlines: 'Regulatoresch Fristen',
            add_deadline: 'Frist derbäisetzen',
            label: 'Bezeechnung',
            days: 'Deeg',
            day_type: 'Dagstyp',
            calendar_days: 'Kalennerdéeg',
            working_days: 'Aarbechtsdeeg',
            no_deadlines: 'Keng Fristen konfiguréiert',
            description: 'Beschreiwung',
            failed_load: 'Feeler beim load channel data',
            channel_added: 'Kanal derbäigesat',
            channel_added_announce: 'Andragskanal derbäigesat',
            failed_add_channel: 'Feeler beim add channel',
            deadline_added: 'Frist derbäigesat',
            deadline_added_announce: 'Regulatoresch Frist derbäigesat',
            failed_add_deadline: 'Feeler beim add deadline',
            channel_code: 'Declared channel',
            channel_code_none: 'Not bound to a declared channel',
            channel_code_hint: 'Which channel the instrument provides. Only a channel bound here can be used on a filing.',
            framework_declares_no_channels: 'This framework declares no submission channels',
            framework_not_installed: 'This framework is not installed for your organisation',
        },
        module_links: {
            title: 'Framework-Verknëppung',
            modules: 'Verknëppte Modulen',
            add_module: 'Modul derbäisetzen',
            module_code: 'Modulcode',
            required: 'Erfuerderlech',
            link: 'Verlinken',
            no_modules: 'Keng Modulen verlinkt',
            notes: 'Notizen',
            personas: 'Verknëppte Personaen',
            add_persona: 'Persona derbäisetzen',
            persona_id: 'Persona-ID',
            role_description: 'Rollbeschreiwung',
            no_personas: 'Keng Personaen verlinkt',
            failed_load: 'Feeler beim load framework links',
            module_linked: 'Modul verlinkt',
            module_linked_announce: 'Modul erfollegräich verlinkt',
            failed_link_module: 'Feeler beim link module',
            persona_linked: 'Persona verlinkt',
            persona_linked_announce: 'Persona erfollegräich verlinkt',
            failed_link_persona: 'Feeler beim link persona',
        },
        regulatory_updates: {
            statuses: {
                pending: 'Ausstoend',
                assessed: 'Bewäert',
                applied: 'Ugewannt',
                dismissed: 'Verworf',
            },
            title: 'Regulatoresch Aktualiséierungen an Framework-Dashboard',
            page_description: "Op ee Bléck d'Ofdeckung fir all installéierte regulatoresche Kader, plus de Changelog vun de regulatoreschen Aktualiséierungen am Produkt: Ännerungen, Inkrafttriede-Datumer a Wierkungsanalyse.",
            coverage_dashboard: 'Framework-Ofdeckungs-Dashboard',
            no_frameworks: 'Keng Frameworks fonnt',
            framework: 'Kader',
            modules: 'Modulen',
            templates: 'Schablounen',
            forms: 'Formulairen',
            channels: 'Kanäl',
            deadlines: 'Fristen',
            pending: 'Ausstehend Aktualiséierungen',
            updates: 'Regulatoresch Aktualiséierungen',
            create_update: 'Aktualiséierung erstellen',
            framework_id: 'Framework-ID',
            update_title: 'Titel',
            description: 'Beschreiwung',
            effective_date: 'Inkrafttriede-Datum',
            amendment_ref: 'Ännerung Referenz',
            create: 'Erstellen',
            no_updates: 'Keng regulatoresch Aktualiséierungen',
            status: 'Status',
            impact: 'Impakt',
            change_summary: 'Ännerungszesummefaassung',
            assess: 'Bewäerten',
            failed_load: 'Feeler beim load data',
            update_created: 'Aktualiséierung erstallt',
            update_created_announce: 'Regulatoresch Aktualiséierung erstallt',
            failed_create: 'Feeler beim create update',
            update_assessed: 'Aktualiséierung bewäert',
            failed_assess: 'Feeler beim assess update',
            col_pack_version: 'Pack Version',
            amendment_ref_inline: 'Amendment reference: {reference}',
            default_assess_note: 'Impact assessed — review complete',
        },
        framework_relationships: {
            panel_title: 'Verbonne Kaderwierker',
            panel_intro: 'Wéi dëse Kader mat anere verbonnen ass. En Overlay kann zur selwechter Zäit ënner méintere Mamm-Kaderwierker gëllen; jidderee gëtt hei genau eemol ugewisen.',
            outgoing_heading: 'Dëse Kader gëllt fir',
            permalink_aria: 'Link to {name} in this list',
            incoming_heading: 'Gëllt ënner dësem Kader',
            outgoing_caption: 'Kaderwierker op déi dëse Kader weist',
            incoming_caption: 'Kaderwierker déi ënner dësem Kader stinn',
            col_framework: 'Kader',
            col_relationship: 'Bezéiung',
            col_scope: 'Quell',
            scope_global: 'Aus dem Pak-Inhalt',
            scope_org: 'Organisatiounsspezifesch',
            load_failed: 'Verbonne Kaderwierker konnten net gelueden ginn',
            relationship_kinds: {
                parent: 'Mamm-Kader',
                applies_to: 'Gëllt fir',
                cross_reference: 'Kräizverweis',
                supersedes: 'Ersetzt',
            },
        },
        sector_overlay: {
            sector_labels: {
                banking: 'Banken',
                insurance: 'Versécherung',
                healthcare: 'Gesondheetswiesen',
                education: 'Education',
                ict_critical: 'Kritesch IKT',
                telecom: 'Telekommunikatioun',
                energy: 'Energie',
                transport: 'Transport',
                digital_services: 'Digital Servicer',
                digital_technology_industry: 'Digital Technologie-Industrie',
                manufacturing: 'Manufacturing',
                public_administration: 'Ëffentlech Verwaltung',
                payments: 'Bezuelungen',
                publishing: 'Verlagswiesen',
                crypto_assets: 'Kryptowäerter',
                other: 'Aner',
            },
            scope_labels: {
                cross_sector: 'Cross-sector',
                security_overlay: 'Security overlay',
                security_control_overlay: 'Security control overlay',
                activity_scope: 'Activity scope',
                process_scope: 'Process scope',
                consumer_facing_process_scope: 'Consumer-facing processes',
                platform_and_einvoice_process_scope: 'Platform and e-invoice processes',
                regulatory_overlay: 'Regulatory overlay',
            },
            sector_label: 'Sektor',
            direct_scope_label: 'Direkten gesetzlechen Uwendungsberäich',
            chip_direct: 'Direkt uwendbar',
            chip_reference: 'Nëmme Referenz',
            banner_direct_copy: 'Direkt uwendbar op Är Organisatioun. Sektorberichter a Fristen sinn aktiv.',
            banner_reference_copy: 'Nëmme Referenz — Är Organisatioun ass net an dësem Sektor klasséiert.',
            edit_mode_button: 'Modus änneren',
            edit_form_title: 'Installatiounsmodus änneren',
            mode_direct_label: 'Direkt — gesetzlech uwendbar',
            mode_direct_help: 'Plangt Sektorfristen an Dashboard-Widgeten.',
            mode_reference_label: 'Nëmme Referenz',
            mode_reference_help: 'Konsultéierbar mä ouni Fristen oder Widgeten.',
            reason_note_label: 'Grond (fakultativ)',
            reason_note_placeholder: 'z.B. Bank-Duechtergesellschaft erworben',
            cancel: 'Ofbriechen',
            confirm: 'Bestätegen',
            confirming: 'Späicheren…',
            save: 'Späicheren',
            saving: 'Späicheren…',
            toast_mode_changed: 'Modus vun der Sektor-Iwwerlagerung aktualiséiert.',
            toast_installed: 'Sektor-Iwwerlagerung installéiert.',
            toast_sectors_saved: 'Sektor-Selbstidentifikatioun gespäichert.',
            install_modal_title: 'Sektor-Iwwerlagerung installéieren',
            install_modal_intro: 'Sektor-Iwwerlagerunge sinn fakultativ regulatoresch Pakete fir eng eenzeg Branche.',
            install_modal_footer: 'Dir kënnt den Installatiounsmodus méi spéit op der Framework-Detailsäit änneren.',
            install_button: 'Installéieren',
            installed_label: 'Installéiert',
            uninstall_button: 'Deinstalléieren',
            uninstall_confirm: 'Confirméieren Uninstall',
            uninstall_cancel: 'Ofbriechen',
            suggested_label: 'Recommandéiert',
            catalog_page_title: 'Sektor-Iwwerlagerungen',
            catalog_page_intro: 'Fakultativ regulatoresch Pakete fir e bestëmmte Branchesektor.',
            catalog_empty_title: 'Keng Sektor-Iwwerlagerunge verfügbar',
            catalog_empty_description: 'Sektor-Iwwerlagerungspakete, déi op Ärer Plattform publizéiert sinn, erschéngen hei.',
            authority_label: 'Autoritéit',
            org_sectors_title: 'Branchesektoren',
            org_sectors_intro: "Wielt d'Branchesektoren, an deenen Är Organisatioun täteg ass.",
            widget_title: 'Fälleg Berichter vu Sektor-Iwwerlagerungen',
            widget_aria_label: 'Fälleg Berichter vu Sektor-Iwwerlagerungen',
            widget_empty: 'Keng kommend Fristen vu Sektor-Iwwerlagerungen.',
            audit_event_mode_changed: 'Installatiounsmodus vun der Sektor-Iwwerlagerung geännert',
            audit_event_installed: 'Sektor-Iwwerlagerung installéiert',
            view_details_button: 'Detailer kucken →',
            banner_aria_label: 'Status vun der Sektor-Iwwerlagerung',
            toast_load_failed: 'Lueden vun de Sektor-Iwwerlagerunge gescheitert',
            toast_install_failed: 'Installatioun vun der Sektor-Iwwerlagerung gescheitert',
            toast_uninstall_confirm_required: 'Tippt "{packCode}" fir d\'Deinstallatioun ze bestätegen.',
            toast_uninstalled: '{packCode} deinstalléiert.',
            toast_uninstall_failed: 'Deinstallatioun vun der Sektor-Iwwerlagerung gescheitert',
            uninstall_warning_destructive_label: 'Destruktiv Aktioun.',
            uninstall_warning_body: "D'Deinstalléiere läscht den Framework-Eintrag an all persistéiert regulatoresch Fristen, déi mat {frameworkCode} fir dës Organisatioun verbonne sinn.",
            uninstall_warning_type_to_confirm: 'Tippt {packCode} fir ze bestätegen.',
            uninstall_in_progress: 'Deinstalléieren…',
            widget_view_all: 'All Sektor-Iwwerlagerunge kucken →',
            widget_due_in_days: 'Fälleg an {days}d',
            widget_overdue_days: 'Iwwerfälleg ëm {days}d',
            widget_due_today: 'Haut fälleg',
            widget_due_unknown: '—',
            // Catalog overview, filters and feature-views modal
            catalog_summary_total: 'Overlays',
            catalog_summary_installed: 'Installed',
            catalog_summary_direct: 'Direct',
            catalog_summary_reference: 'Reference only',
            catalog_summary_available: 'Not installed',
            filter_toolbar_label: 'Filter the overlay catalog',
            filter_search_label: 'Search overlays',
            filter_search_placeholder: 'Search by name, code or authority',
            filter_sector_all: 'All sectors and scopes',
            filter_status_all: 'All statuses',
            filter_status_installed: 'Installed',
            filter_status_not_installed: 'Not installed',
            filter_mode_label: 'Install mode',
            filter_mode_all: 'All modes',
            filter_clear: 'Clear filters',
            filter_results: 'Showing {shown} of {total} overlays',
            filter_no_match_title: 'No overlay matches these filters',
            filter_no_match_description: 'Change or clear the filters to see more of the catalog.',
            col_overlay: 'Overlay',
            col_scope: 'Sector or scope',
            col_mode: 'Mode',
            col_authority: 'Authority',
            col_dates: 'Dates',
            col_version: 'Version',
            col_status: 'Status',
            col_views: 'Feature views',
            col_actions: 'Actions',
            date_effective: 'Effective',
            date_issued: 'Issued',
            date_released: 'Released',
            desc_expand: 'Show description',
            desc_collapse: 'Hide description',
            status_not_installed: 'Not installed',
            views_none: 'None',
            views_none_title: 'This overlay declares nothing that a dedicated screen reads.',
            views_button: '{enabled} of {total} enabled',
            views_button_aria: 'Show the feature views that {name} brings into use ({enabled} of {total} enabled)',
            views_modal_title: 'Feature views of {name}',
            views_modal_intro: 'The registers and pages that read what the pack of this overlay declares. A view is enabled once the overlay is installed as Direct and the module it belongs to is switched on.',
            views_col_view: 'View',
            views_col_menu: 'Where in the menu',
            views_col_state: 'State',
            views_open: 'Open',
            views_state_enabled: 'Enabled',
            views_state_reference: 'Reference only — shows nothing until installed as Direct',
            views_state_not_installed: 'Not enabled — overlay not installed',
            views_state_module_off: 'Not enabled — module switched off',
            views_no_access: 'Your role cannot open this view',
            views_module_state_unconfirmed: 'Module settings could not be read, so a switched-off module is not shown here.',
            views_unclaimed_heading: 'Declared without a dedicated screen',
            views_unclaimed_description: 'The pack also declares these record kinds, but no dedicated screen reads them for this overlay: {kinds}.',
            declared_kind_labels: {
                biometric_technical_regulation_commencement: 'Start of the voice and iris biometric technical regulations',
                chemical_traceability_code_termination: 'Chemical traceability code termination',
                chemical_traceability_commencement: 'Start of chemical traceability',
                chemical_traceability_incident_declaration: 'Chemical traceability incident declaration',
                consumer_complaint_receipt_acknowledgement: 'Notice to the consumer that a report, request or complaint was received',
                cybersecurity_fee_schedule_commencement: 'Start of the cybersecurity fee schedule',
                food_database_traceability_commencement: 'Start of food database traceability',
                food_traceability_connection_start: 'Start of the food traceability connection',
                food_traceability_records_request: 'Food traceability records request',
                food_traceability_system_update: 'Food traceability system update',
            },
            views_declared_only: 'No dedicated screen',
            views_declared_only_aria: 'Show what {name} declares without a dedicated screen',
            views_basis_records: 'Reads records the pack declares',
            views_basis_regime: 'Reads a regime the pack declares',
            views_basis_fine_areas: 'Uses fine areas the pack declares',
            views_open_framework: 'Open the framework page',
            views_independent_note: 'Screens that work the same with or without an overlay, such as Legal Holds, are not listed.',
            menu_payments_evidence: 'Payments & eKYC Evidence',
            menu_trust_evidence: 'Trust & Identity Evidence',
            menu_platform_evidence: 'Platform Evidence',
            menu_public_data: 'Public Data Exchange',
            menu_product_compliance: 'Product Compliance & Traceability',
            menu_producer_substance: 'Producer & Substance Obligations',
            menu_trade_customs: 'Trade & Customs Evidence',
            menu_parcel_delivery: 'Parcel & Delivery Evidence',
            view_labels: {
                controlled_testing_cases: 'Controlled-Testing Cases',
                credit_information_supply: 'Credit-Information Supply Register',
                authority_directions: 'Authority Directions',
                regulated_accounts: 'Regulated Accounts',
                sector_licences: 'Sector Licences',
                licence_applications: 'Licence Applications and Fees',
                fee_lines_by_period: 'Fee Lines by Period',
                data_service_providers: 'Data-Service Provider Records',
                pcf_shared_information: 'Shared Supervisory Information',
                pcf_branch_publicity: 'Branch Publicity Evidence',
                pcf_branch_information_requests: 'Branch Information Requests',
                pcf_depositor_enquiries: 'Depositor Enquiries',
                pcf_supervision_system_records: 'Supervision System Records',
                data_exploitation_routes: 'Exploitation Routes',
                data_exploitation_requests: 'Requests and Grants',
                data_exploitation_testing: 'Controlled Testing Records',
                data_exploitation_notices: 'Incident Notices',
                data_exploitation_dispositions: 'End-of-Purpose Disposition',
                data_exploitation_incubator: 'Incubator Records',
                data_exploitation_foreign_cooperation: 'Foreign Cooperation Assessments',
                cybersecurity_assurance_index_periods: 'Assessment Periods',
                cybersecurity_assurance_index_statistics: 'Direction and Management Statistics',
                cybersecurity_assurance_index_supplier_requests: 'Supplier Evidence Requests',
                food_traceability_establishment: 'Food Establishment',
                food_traceability_lot_results: 'Lot Test Results',
                food_traceability_inspections: 'Food Inspections',
                food_traceability_warnings: 'Warnings',
                food_traceability_notices: 'Notices and Quantities',
                food_traceability_decisions: 'Violation Decisions',
                food_traceability_corrections: 'Corrections and Supplier Corrections',
                food_traceability_complaints: 'Complaints',
                food_traceability_routes: 'Connection Routes',
                biometric_capture_components: 'Biometric Capture Components',
                biometric_chain_of_custody: 'Biometric Chain-of-Custody Records',
                biometric_readiness: 'Biometric Readiness Reviews',
                biometric_supervision_records: 'Biometric Supervision and Inspection Records',
                product_classifications: 'Product Classifications',
                conformity_certificates: 'Conformity Certificates',
                student_records: 'Student Records',
                publications: 'Publications',
                publication_assessments: 'Publication Assessments',
                regulatory_placeholders: 'Regulatory Placeholders',
                supervisory_reporting: 'Supervisory Reporting',
                aml_programme: 'AML Programme',
                aml_cases: 'Suspicious-Activity Cases',
                identity_exchanges: 'Cross-Border Identity Exchanges',
                marketing_approvals: 'Marketing Approvals',
                investor_verifications: 'Investor Verifications',
                account_data_disclosures: 'Account Data Disclosures',
                regulatory_disclosures: 'Regulatory Disclosures',
                legal_representatives: 'Legal Representatives',
                penalty_readiness: 'Penalty Readiness',
                exposure_inputs: 'Exposure Inputs',
                enforcement_register: 'Enforcement Register',
                enforcement_cases: 'Enforcement Cases',
                identity_register: 'Identity Register',
                trust_providers: 'Trust Providers',
                electronic_submissions: 'Electronic Submissions',
                electronic_agreements: 'Electronic Agreements',
                payment_services: 'Payment Services',
                ekyc_evidence: 'eKYC Evidence',
                exception_review: 'Exception Review',
                retention_reconciliation: 'Retention Reconciliation',
                seller_register: 'Seller Register',
                transaction_reconciliation: 'Transaction Reconciliation',
                authority_correspondence: 'Authority Correspondence',
                dataset_catalog: 'Dataset Catalog',
                sharing_register: 'Sharing Register',
                open_data_releases: 'Open Data Releases',
                product_register: 'Product Register',
                batch_register: 'Batch & Serial Register',
                trace_timeline: 'Trace Timeline',
                conformity_evidence: 'Conformity Evidence',
                epr_reconciliation: 'EPR Reconciliation',
                chemical_register: 'Chemical Register',
                chemical_traceability_declarations: 'Chemical Traceability Declarations',
                customs_evidence: 'Customs Evidence',
                customs_tax_dossiers: 'Tax Dossiers and Received Documents',
                customs_tax_payments: 'Tax Payments and Due Dates',
                customs_tax_duty_free_lists: 'Duty-Free Lists and Usage Notifications',
                customs_tax_examinations: 'Tax Examinations',
                customs_tax_transition: 'Transition and Applicability',
                parcel_claims: 'Claims',
                postal_authorisations: 'Postal Authorisations',
            },
        },
        phased_obligations: {
            panel_title: 'Etappewäis Verflichtungen',
            panel_aria_label: 'Etappewäis Verflichtungen',
            panel_intro: 'E puer Klauselen an dësem Framework gi méi spéit aktiv wéi de Framework selwer.',
            phased_effective_date_label: 'Etappewäis a Kraaft:',
            status_pending: 'Ausstänneg',
            status_active: 'Aktiv',
            status_early: 'Aktiv (fréizäiteg ëmgesat)',
            notice_window_label: 'Notifikatiounsfënster vun der Autoritéit:',
            days_label: 'Deeg',
            recipient_label: 'Empfänger:',
            early_implement_button: 'Als fréizäiteg ëmgesat markéieren',
            early_implement_closed: 'Early implementation can be recorded only before the phased effective date, {date}.',
            effective_at_before_hint: 'Choose a date before the phased effective date, {date}.',
            effective_at_too_late: 'The date must be before the phased effective date, {date}.',
            early_implement_modal_title: 'Etappewäis Verflichtung als fréizäiteg ëmgesat markéieren',
            early_implement_modal_intro: 'Recording an early implementation stores the date your organisation began applying this obligation. This panel then shows the obligation as active from that date, and shows the date the notice is due, counted from the recorded date. The platform does not send the notice and keeps no record of it being sent.',
            effective_at_label: 'A Kraaft vum',
            reason_label: 'Grond (fakultativ)',
            reason_placeholder: "z.B. Verwaltungsrot huet d'fréizäiteg Adoptioun de 2026-09-01 guttgeheescht",
            confirm: 'Bestätegen',
            toast_early_implemented: 'Fréizäiteg Ëmsetzung opgeholl.',
            early_read_failed: 'The early implementations recorded for this framework could not be read. Until they are, the status of each obligation is not shown and none can be marked early-implemented.',
            early_read_subject: 'the early implementations',
            audit_event_early_implemented: 'Etappewäis Verflichtung als fréizäiteg ëmgesat markéiert',
            widget_rolling_window_label: 'Etappewäis Aktivéierungen bannent 90 Deeg',
            notice_due_label: 'Notice due by the end of',
            notice_due_time_suffix: '(organisation time)',
            notice_rolled_note: 'The last day fell on a weekend or holiday, so it moved to the next working day.',
            notice_platform_sends_nothing: 'The platform does not send the notice and keeps no record of it being sent.',
            notice_basis: {
                day_after_is_day_one_last_day_rolls_past_non_working_day: 'Computed: the day after implementation is day 1, the last day moves past a weekend or holiday.',
                day_after_is_day_one: 'Computed: the day after implementation is day 1.',
            },
            notice_gap: {
                no_notice_clock_declared: 'No notice due date: no installed record states the notice period.',
                notice_clock_ambiguous: 'No notice due date: more than one installed record states a notice period, and none says which one applies.',
                notice_clock_not_computable: 'No notice due date: the platform does not compute the period the installed record states.',
                implemented_on_unreadable: 'No notice due date: the recorded date could not be read.',
                notice_clock_not_in_force: 'No notice due date: the framework is not yet in force on the day the notice would fall due.',
                framework_installed_for_reference: 'No notice due date: this framework is installed for reference, so its clocks are not run.',
                notice_clock_activation_unknown: 'No notice due date: whether the clock binds is not known, because the framework record states no date or could not be read.',
                implemented_on_not_before_phased_date: 'No notice due date: the recorded date is on or after the phased effective date, so the obligation applies from then anyway and was not implemented early.',
            },
        },
        deadline_activation: {
            token_live: 'In force',
            token_upcoming: 'Not yet in force',
            in_force_from: 'In force from {date}',
            start_date_not_stated: 'Start date not stated',
            token_unknown: 'Timing unknown',
            unknown_sentence: 'The framework record this deadline belongs to could not be read, so whether it applies yet is not known.',
            retry: 'Retry',
            retry_busy: 'Retrying…',
            // Gate E12.5 pass 1 (P2-3) — the framework record was read and states no date.
            token_phase_unresolved: 'Phase start not dated',
            phase_unresolved_sentence: 'This deadline applies from a phase of its framework, and the framework does not date that phase, so it is not treated as in force.',
            token_not_stated: 'No effective date stated',
            not_stated_sentence: 'The framework states no date from which this deadline applies, so it is not treated as in force.',
            retry_subject: 'the regulatory deadlines',
            column_heading: 'When it applies',
            widget_overlays_failed: 'The installed sector overlays could not be read.',
            widget_reads_failed: 'Deadlines for {count} sector overlay(s) could not be read.',
            widget_retry_subject: 'the sector overlay deadlines',
            settings_sources_label: 'Deadlines behind this suggestion',
            settings_upcoming_not_counted: 'Not used for the suggestion until it is in force.',
            settings_unknown_not_counted: 'Not used for the suggestion.',
            settings_not_calendar_days_not_counted: 'Not used for the suggestion: its period is not counted in calendar days.',
            settings_retry_subject: 'the framework deadlines',
            channels_retry_subject: 'the submission channels and deadlines',
            token_not_owed: 'Not owed',
            not_owed_framework_status: 'The framework is not in force.',
            not_owed_row_suspended: 'This duty is suspended.',
            not_owed_superseded_by: 'Superseded by {framework}.',
            not_owed_superseded_unnamed: 'Superseded by another framework.',
            not_owed_row_legacy: 'Legacy — no longer in force.',
            not_owed_unrecognised: 'The record states that this duty is not owed.',
            settings_not_owed_not_counted: 'Not used for the suggestion: this duty is not owed.',
        },
        // Sprint 62.58.0 (Slice C) — English for key parity until Phase 25 (R-57).
        framework_briefings: {
            summary_export: {
                title: 'Framework summary: {name}',
                section_status: 'Status and dates',
                col_field: 'Field',
                col_value: 'Value',
                field_code: 'Framework code',
                field_status: 'Status',
                field_issued: 'Issued',
                field_effective: 'In force from',
                draft_not_in_force: 'Draft — not yet in force',
                not_stated: 'Not stated',
                section_parent: 'Parent framework',
                parent_line: "This framework is read with its parent framework, {name} ({code}), status: {status}. The dated rows below are the parent framework's, not this framework's.",
                parent_not_installed: 'The parent framework {code} is not installed in this organization, so its dated rows are not shown.',
                no_parent: 'This framework names no parent framework.',
                parent_no_rows: 'No dated rows of the parent framework are in use in this organization.',
                col_belongs_to: 'Belongs to',
                section_guidance: 'Guidance',
                no_guidance: 'No guidance notes are installed for this framework.',
                col_title: 'Title',
                col_summary: 'Summary',
                section_requirements: 'Requirements',
                no_requirements: 'No requirements are installed for this framework.',
                col_requirement_id: 'ID',
                col_reference: 'Reference',
                col_addressed_to: 'Addressed to',
                col_mandatory: 'Mandatory',
                mandatory_yes: 'Yes',
                mandatory_no: 'No',
                mandatory_not_stated: 'Not stated',
                mandatory_no_draft: 'No — proposed in a draft',
                mandatory_no_legacy: 'No — the framework is legacy',
                mandatory_no_suspended: "No — the framework's application is suspended",
                section_deadlines: 'Deadlines',
                no_deadlines: 'No deadline rows are installed for this framework.',
                col_deadline: 'Deadline',
                col_date_or_period: 'Date or period',
                col_note: 'Note',
                stated_date: '{date} (as stated)',
                authority_clock: "The authority's period, not a duty of the organization.",
                section_pack: 'Pack',
                pack_line: 'Installed from pack {code}, version {version}.',
                pack_not_recorded: 'The pack this framework was installed from is not recorded.',
                generated_line: "Generated on {date} from this organization's installed framework record. This summary is for reading only; ComplianceOne submits nothing to any authority.",
            },
            record: {
                tab: 'Briefings',
                heading: 'Briefings and consultation comments',
                intro: 'Record the briefings your organization held about this framework and the consultation comments it sent. An entry is kept as recorded; a correction is a new entry that names the one it corrects.',
                boundary: 'A consultation comment records what your organization sent, to whom and when. ComplianceOne submits nothing to any authority.',
                kind_briefing: 'Briefing',
                kind_consultation_comment: 'Consultation comment',
                channel_meeting: 'Meeting',
                channel_email: 'Email',
                channel_document: 'Document',
                channel_webinar: 'Webinar',
                channel_other: 'Other',
                col_kind: 'Kind',
                col_audience: 'Audience',
                col_held_on: 'Date',
                col_channel: 'Channel',
                col_approver: 'Approver',
                col_approval: 'Approval',
                col_pack_version: 'Pack version',
                approval_none: 'No approver named',
                approval_pending: 'Awaiting approval',
                approval_approved: 'Approved on {date}',
                record: 'Record an entry',
                recording: 'Recording…',
                approve: 'Approve',
                approving: 'Approving…',
                approve_aria: 'Approve the entry of {date} for {audience}',
                field_kind: 'Kind',
                field_audience: 'Audience',
                field_audience_hint_comment: 'For a consultation comment: to whom your organization sent it.',
                field_held_on: 'Date held or sent',
                field_held_on_hint: 'A calendar date, not after today.',
                field_channel: 'Channel',
                field_channel_note: 'Channel details',
                field_channel_note_required: 'Describe the channel when it is Other.',
                field_reference: 'Reference',
                field_reference_hint: 'The reference of what was sent, such as a letter number or a portal receipt.',
                field_summary: 'Summary',
                field_approver: 'Approver',
                field_correction_of: 'Corrects entry',
                field_correction_note: 'Correction note',
                empty: 'No briefings or consultation comments are recorded for this framework.',
                load_failed: 'The briefings and consultation comments could not be loaded.',
                record_failed: 'The entry could not be recorded.',
                approve_failed: 'The entry could not be approved.',
                recorded_toast: 'Entry recorded.',
                approved_toast: 'Entry approved.',
                pack_changed: 'Recorded against pack version {version}; this framework has changed since — see Regulatory Updates',
                corrected_by: 'Corrected by a later entry',
                correction_label: 'Correction',
                evidence_count: 'Documents: {count}',
                attach_summary_offer: 'Attach this summary to an entry',
                kind_placeholder: 'Choose a kind',
                channel_placeholder: 'Choose a channel',
                approver_placeholder: 'No approver',
                correction_none: 'Not a correction',
                correction_option: '{kind} of {date} for {audience}',
                correction_picker_failed: 'The earlier entries could not be read, so a correction cannot be chosen now.',
                error_required: 'This field is required.',
                error_held_on_future: 'The date cannot be after today.',
                error_correction_note_required: 'Explain what this entry corrects.',
                error_approver_not_found: 'The approver is not a user of this organization.',
                error_not_approver: 'Only the named approver can approve this entry.',
                error_already_approved: 'This entry is already approved.',
                error_self_approval: 'The approver must be someone other than the person recording the entry.',
                form_intro: 'Record what was held or sent. Documents are added after the entry is recorded, from Documents on its row.',
                summary_count: '{count} of {max} characters',
                loading: 'Loading the entries…',
                retry_subject: 'the entries',
                reload: 'Reload',
                reloading: 'Reloading…',
                pagination: 'Pages of entries',
                col_summary: 'Summary',
                col_documents: 'Documents',
                documents_open: 'Documents',
                documents_open_aria: 'Documents of the entry of {date} for {audience}',
                reference_line: 'Reference: {reference}',
                channel_note_line: 'Channel details: {note}',
                correction_note_line: 'Correction note: {note}',
                corrects_line: 'Corrects the entry of {date}',
                corrects_unknown: 'Corrects an earlier entry',
                pack_changed_open_comparison: 'See what changed',
                pack_changed_open_updates: 'Open Regulatory Updates',
                replaced_since: 'Recorded against this text; it has since been replaced by another framework —',
                pack_version_not_recorded: 'Not recorded',
                recorded_while: 'Framework status when recorded: {status}',
                framework_status_suspended: 'Suspended',
                recorded_line: 'Recorded by {person} on {date}',
                files_heading: 'Documents',
                files_intro: 'The documents behind this entry, such as the slides, the letter sent or the downloaded framework summary.',
                files_slot: 'Documents of this entry',
                files_none: 'No document is attached to this entry.',
                file_hash: 'SHA-256: {hash}',
                file_not_held: 'Document no longer held',
                file_download: 'Download',
                file_remove: 'Remove',
                file_upload: 'Add a document',
                file_bound: 'Document added.',
                file_removed: 'Document removed.',
                file_bind_failed: 'The document could not be added.',
                file_remove_failed: 'The document could not be removed.',
                file_not_owned: 'The entry this document belongs to was not found.',
                file_remove_confirm: 'Remove this document from the entry and delete it?',
                files_approved_kept: 'This entry has been approved, so its documents are kept as approved and cannot be removed. To correct it, record a correction entry.',
                documents_load_failed: 'The documents could not be loaded.',
                export_label: 'Export summary',
                export_done: 'Framework summary exported.',
                export_failed: 'The framework summary could not be exported.',
                attach_summary_hint: 'To keep this summary with an entry, open the Briefings tab, choose Documents on the entry and add the downloaded file.',
                directory_unavailable: 'The list of people could not be read, so an approver cannot be chosen now.',
                approver_retry_subject: 'the list of people',
            },
        },
        // Sprint 62.58.0 (Slice F) — the regulatory-update comparison and its review.
        update_comparison: {
            page: {
                module_guide: "What changed between the text installed before and the text installed now, requirement by requirement, with your organization's reading of it and the records filled under the earlier text.",
                title: 'What changed: {name}',
                title_fallback: 'What changed',
                description: 'The requirements of the text installed before, beside the text installed now. This comparison is information: it does not change what your organization owes.',
                back: 'Back to Regulatory Updates',
                loading: 'Loading the comparison…',
                load_failed: 'The comparison could not be loaded.',
                not_found: 'This regulatory update was not found.',
                retry_subject: 'the comparison',
                reload: 'Reload',
                reloading: 'Reloading…',
                no_comparison: 'This regulatory update holds no requirement comparison. An update recorded by hand, or one installed before comparisons were kept, has none.',
                status_heading: 'Status change',
                status_change: '{from} → {to}',
                replaced_by: 'Replaced by {name}',
                status_not_recorded: 'Not recorded',
                basis_computed: 'Compared requirement by requirement between the two installed versions of this framework.',
                basis_declared: 'Compared using the correspondence the replacing framework declares.',
                derived_on_read: 'These rows were worked out when this page was opened, from the frameworks installed now.',
                from_heading: 'Earlier text',
                to_heading: 'Text installed now',
                side_status: 'Status: {status}',
                side_pack_version: 'Pack version: {version}',
                counts_heading: 'Summary of changes',
                count_line: '{label}: {count}',
                change_changed: 'Changed',
                change_removed: 'Removed',
                change_new: 'New',
                change_unchanged: 'Unchanged',
                rows_heading: 'Requirements',
                filter_label: 'Show',
                filter_all: 'All requirements',
                showing: 'Showing {shown} of {total}',
                col_requirement: 'Requirement',
                col_change: 'Change',
                col_from_title: 'Title in the earlier text',
                col_to_title: 'Title in the text installed now',
                col_mandatory: 'Mandatory',
                col_note: 'Note',
                mandatory_yes: 'Yes',
                mandatory_no: 'No',
                mandatory_not_stated: 'Not stated',
                mandatory_change: '{from} → {to}',
                not_in_text: 'Not in this text',
                also_corresponds: 'Also corresponds to: {ids}',
                rows_empty: 'No requirement matches this filter.',
                rows_pagination: 'Pages of requirements',
                superseded_texts_not_installed: 'No comparison: one of the two texts is no longer installed in your organization, or the replacing framework no longer declares that it replaces the earlier text. This regulatory update is kept as it was recorded.',
                withheld_other_pack: 'No comparison: the previous record was installed from another pack ({code}).',
                withheld_other_pack_unknown: 'No comparison: the previous record was not installed from this pack.',
            },
            review: {
                heading: "Your organization's reading",
                intro: "Record how each requirement that changed, was removed or is new affects your organization, and what you concluded. A reading is your organization's own record: it does not change what the framework requires, its status or any deadline.",
                record: 'Record comparison review',
                recording: 'Recording…',
                none: 'No comparison review is recorded yet.',
                current_heading: 'Current review',
                recorded_line: 'Recorded by {person} on {date}',
                conclusion: 'Conclusion',
                next_steps: 'Next steps',
                no_next_steps: 'No next steps recorded.',
                readings_heading: 'Readings',
                col_reading: 'Reading',
                reading_affects_us: 'Affects us',
                reading_does_not_affect_us: 'Does not affect us',
                reading_to_assess: 'To assess',
                no_rows_read: 'This review gives no reading for a single requirement.',
                history_heading: 'Earlier reviews',
                history_pagination: 'Pages of earlier reviews',
                col_recorded: 'Recorded',
                col_recorded_by: 'Recorded by',
                col_conclusion: 'Conclusion',
                col_readings: 'Readings',
                readings_summary: '{affects} affect us, {not_affect} do not, {to_assess} to assess',
                modal_intro: 'Give a reading for every requirement that changed, was removed or is new, then state your conclusion.',
                row_legend: '{id}: {title}',
                field_reading: 'Reading',
                field_reading_placeholder: 'Choose a reading',
                field_note: 'Note',
                field_conclusion: 'Conclusion',
                field_next_steps: 'Next steps',
                text_count: '{count} of {max} characters',
                error_reading_required: 'Choose a reading for this requirement.',
                error_conclusion_required: 'State your conclusion.',
                record_failed: 'The comparison review could not be recorded.',
                recorded_toast: 'Comparison review recorded.',
                no_comparison_error: 'This update holds no comparison to review.',
                rows_loading: 'Loading the requirements to read…',
                rows_load_failed: 'The requirements to read could not be loaded.',
                no_rows_to_read: 'Every requirement is unchanged, so only a conclusion is needed.',
            },
            reread: {
                heading: 'Records filled under the replaced text',
                intro: 'These records were filled while the earlier text applied. They are kept as they are: nothing in them is changed, scored or reopened. Re-read each one against the final text.',
                forms_heading: 'Form records',
                col_record: 'Record',
                col_form: 'Form',
                col_status: 'Status',
                col_updated: 'Last updated',
                forms_none: 'No form record was filled under the replaced text.',
                forms_withheld: 'Form records are listed only to people who may read form records.',
                untitled: 'Untitled record',
                forms_pagination: 'Pages of form records',
                questionnaires_heading: 'Vendor questionnaires',
                notice: 'Filled under {from}, which has been replaced by {to} — re-read it against the final text',
                notice_link: 'See what changed',
                notice_link_aria: 'See what changed between {from} and {to}',
                notice_same_text: 'Filled under pack version {version} of {name}; this text has changed since — re-read it against the current text',
                notice_same_text_unversioned: 'Filled under an earlier pack version of {name}; this text has changed since — re-read it against the current text',
                notice_same_text_link_aria: 'See what changed in {name}',
            },
            recheck: {
                will_draft: 'The pack publisher will recheck this draft on {date}.',
                will_legacy: 'The pack publisher will recheck this legacy framework on {date}.',
                will_suspended: 'The pack publisher will recheck this framework, whose application is suspended, on {date}.',
                was_due_draft: 'The pack publisher was due to recheck this draft on {date}; this record has not yet been updated.',
                was_due_legacy: 'The pack publisher was due to recheck this legacy framework on {date}; this record has not yet been updated.',
                was_due_suspended: 'The pack publisher was due to recheck this framework, whose application is suspended, on {date}; this record has not yet been updated.',
                records_line: "This framework's record changes only when a new pack version is installed; your own records are never changed.",
            },
            list: {
                col_changed: 'Changed requirements',
                no_comparison_short: 'No comparison',
                pagination: 'Pages of regulatory updates',
                view_comparison: 'View comparison',
                view_comparison_aria: 'View comparison for {title}',
                reviews_count: 'Reviews: {count}',
            },
        },
        // Sprint 62.49.1 (DB-2) — Framework Detail's Suspension section.
        framework_suspension: {
            heading: 'Suspension',
            intro: 'As the record states it: when the suspension of this framework’s application began, each instrument that suspended it, and what ends it.',
            start_label: 'Began',
            start_value: 'Suspended from {date}',
            basis_label: 'Basis for that day',
            steps_caption: 'Instruments that suspended this framework, in the order they took effect',
            col_instrument: 'Instrument',
            col_issued: 'Issued',
            col_effective: 'Took effect',
            col_until: 'Suspends until',
            col_replaces: 'Replaces',
            replaces_none: 'None',
            until_date: 'Until {date}',
            source_line: 'Read from: {source}',
            end_label: 'Ends',
            end_date: 'On {date}',
            end_event: 'When this happens: {event}',
            event_not_occurred: 'Not yet occurred',
            event_occurred_on: 'Occurred on {date}',
        },
        market_pack_installed: 'Market pack installed',
        missing_market_code: 'Cannot uninstall: missing market code',
        pack_installed_announce: 'Pack installed successfully',
        install_zip_refused_title: 'Uploaded pack files are not installed',
        install_zip_refused_body: 'An uploaded pack .zip must be signed, and no pack signing key is configured for this installation, so every uploaded zip is refused. Packs that ship with the release are installed from Localization (Market Packs, Language Packs and the Overlay Catalog), and each one is checked against the release before it installs.',
        pack_uninstalled: 'Language pack uninstalled',
    },
    partner_marketplace: {
        /**
         * fix-sprint-20260828-2118 F20 — promoted out of
         * `pages/admin/partner-marketplace/strings.ts`.
         *
         * That file is an inline `{ en, vi }` pair resolved by `extractLocale`, so
         * the 14 non-EN/VI locales received the English sentence at all 25 of this
         * module's call sites — on pages whose every other string comes from `t.*`.
         * The wording is 62.5.0's, unchanged.
         */
        selector_truncated: 'Showing {shown} of {total}. Narrow the search to reach the rest.',
        nav: {
            dashboard: 'Iwwersiicht',
            partner_orgs: 'Partnerorganisatiounen',
            service_tiers: 'Serviceniveauen',
            deliverable_templates: 'Liwwervirlagen',
            deliverables: 'Liwwergéigestänn',
            brand_profiles: 'Markeprofiler',
            support_tickets: 'Supportufroen',
            workflow_templates: 'Aarbechtsoflaafvirlagen',
            workflows: 'Aarbechtsofleef',
            escalation: 'Eskalatioun',
            reports: 'Berichter',
            audit: 'Kontrollprotokoll',
        },
        dashboard: {
            title: 'Partneriwwersiicht',
            loading: 'Lueden vun dashboard Donnéeën...',
            total_managed_tenants: 'Gesamt Verwalt Tenants',
            avg_health_score: 'Avg Zoustand Bewäertung',
            programme_health_score: 'Partner Programme Health',
            health_band_green: 'Healthy',
            health_band_amber: 'Needs attention',
            health_band_red: 'Critical',
            health_coverage_note: 'Scored from your own partner operations: open tickets, resolution times against target, deliverable timeliness and escalations. It does not cover the compliance posture or data freshness of the tenants you manage.',
            open_tickets: 'Op Ticketen',
            upcoming_deliverables: 'Ustehend Deliverables',
            escalated_tickets: 'Eskaléiert Ticketen',
            managed_tenants: 'Verwalt Tenants',
            partner_tier: 'Partner Stuf',
            managed_tenants_count: 'Verwalt Tenants Unzuel',
            onboarding_date: 'Onboarding Datum',
            view_details: 'Ukucken Detailer',
            no_partner_orgs: 'Keng partner organizations fonnt.',
        },
        partner_orgs: {
            title: 'Partnerorganisatiounen',
            new: 'Nei Partner Org',
            contact_name: 'Kontakt Numm',
            contact_email: 'Kontakt E-Mail',
            partner_tier: 'Partner Stuf',
            managed_orgs: 'Verwalt Orgs',
            onboarding_date: 'Onboarding Datum',
            no_orgs: 'Keng partner organizations fonnt.',
            partner_type: 'Partner Type',
            type_consulting: 'Consulting Firm',
            type_technology: 'Technology Provider',
            type_legal: 'Legal Firm',
            type_audit: 'Audit & Assurance',
            type_managed_services: 'Managed Services Provider',
            type_reseller: 'Reseller',
            contact_phone: 'Kontakt Telefon',
            metadata_json: 'Metadaten (JSON)',
            change_status: 'Änneren Status',
            new_status: 'Nei Status',
            invalid_json_metadata: 'Ongëlteg JSON an metadata field',
            suspended: 'Gespaart',
            offboarded: 'Ausgeschidden',
            all_tiers: 'All Stufen',
            silver: 'Sëlwer',
            gold: 'Gold',
            platinum: 'Platin',
            status_offboarded: 'Ausgeschidden',
            status_suspended: 'Gespaart',
            tier_gold: 'Gold',
            tier_platinum: 'Platin',
            tier_silver: 'Sëlwer',
            org_status_updated: 'Organization status updated',
        },
        service_tiers: {
            title: 'Serviceniveauen',
            new: 'Nei Stuf',
            tier_name: 'Stuf Numm',
            monthly_fee: 'Monatleg Fee',
            features: 'Funktiounen',
            no_tiers: 'Keng service tiers fonnt.',
            tier_code: 'Stuf Code',
            response_time_hrs: 'Äntwert Zäit (hrs)',
            resolution_time_hrs: 'Léisung Zäit (hrs)',
            review_frequency: 'Iwwerpréiwen Heefegkeet',
            sla_response_time: 'SLA Äntwert Zäit (hours)',
            sla_resolution_time: 'SLA Léisung Zäit (hours)',
            module_profile: 'Included Modules',
            module_profile_hint: 'The modules this tier covers. Descriptive only — it records what the tier is sold as, and does not grant or restrict access.',
            module_profile_selected: '{count} of {total} selected',
            module_profile_select_all: 'Select all',
            module_profile_clear_all: 'Clear all',
            module_profile_unknown: 'Unrecognised — kept from an earlier version of this tier',
            comparing: 'Vergläichen...',
            compare: 'Vergläichen',
            comparison: 'Service Stuf Vergläich',
            attribute: 'Attribut',
            deprecate: 'Ofschreiwwen',
            deprecated: 'Ofgeschriwwen',
            select_compare: 'Auswielen bei least 2 tiers op compare',
            comparison_title: 'Service Stuf Vergläich',
            status_deprecated: 'Ofgeschriwwen',
            activate: 'Aktivéieren',
            resolution_time: 'Léisung Zäit',
            response_time: 'Äntwert Zäit',
            select_at_least_two: 'Auswielen bei least 2 tiers',
        },
        deliverable_templates: {
            title: 'Liwwervirlagen',
            new: 'Nei Schabloun',
            template_name: 'Schabloun Numm',
            deliverable_type: 'Liwwerobjekt Typ',
            no_templates: 'Keng deliverable Schablounen fonnt.',
            frequency: 'Heefegkeet',
            est_effort_hrs: 'Est. Opwand (hrs)',
            estimated_effort: 'Geschat Opwand (hours)',
            associated_module: 'Associated Modul',
            linked_tiers_count: 'Verbonnen Stufen Unzuel',
            evidence_requirements: 'Bewäis Ufuerderungen (comma-separated)',
            tier_ids: 'Stuf IDen (comma-separated)',
            module_none: 'No module',
            module_unrecognised: '{value} — not a platform module; kept as stored',
            frequency_one_time: 'One Zäit',
            frequency_monthly: 'Monatleg',
            frequency_quarterly: 'Trimesteriell',
            frequency_annually: 'Jährlech',
            type_report: 'Bericht',
            type_review: 'Iwwerpréiwen',
            type_assessment: 'Bewäertung',
            type_meeting: 'Sëtzung',
            type_configuration: 'Konfiguratioun',
            freq_one_time: 'One Zäit',
            freq_monthly: 'Monatleg',
            freq_quarterly: 'Trimesteriell',
            freq_annually: 'Jährlech',
            status_deprecated: 'Ofgeschriwwen',
        },
        deliverables: {
            title: 'Liwwergéigestänn',
            new: 'Nei Liwwerobjekt',
            partner: 'Partner',
            deliverable_type: 'Liwwerobjekt Typ',
            due_date: 'Due Datum',
            no_deliverables: 'Keng deliverables fonnt.',
            assigned_to: 'Zougewisen Op',
            completion_date: 'Ofschloss Datum',
            update_status: 'Aktualiséieren Status',
            hide_details: 'Verstoppen Detailer',
            view_details: 'Ukucken Detailer',
            evidence_ids: 'Bewäis IDen',
            evidence_expected: 'Evidence expected',
            evidence_expected_none: 'No evidence requirements are recorded on this deliverable.',
            client_approval: 'Client Genehmegung',
            bulk_update: 'Bulk Aktualiséieren',
            bulk_update_status: 'Bulk Aktualiséieren Status',
            generate_schedule: 'Generéieren Plangen',
            generate_deliverable_schedule: 'Generéieren Liwwerobjekt Plangen',
            unassigned: 'Unassigned',
            assigned_to_user: 'Assigned to {name}.',
            assignee_cleared: 'Deliverable is now unassigned.',
            edit_notes: 'Edit notes',
            edit_evidence: 'Edit evidence',
            evidence_select_label: 'Select evidence items',
            evidence_none_available: 'No evidence items are available. Add them in the evidence repository first.',
            approval_note_label: 'Note (optional)',
            approval_status_approved: 'Approved',
            approval_status_rejected: 'Rejected',
            approval_by_at: 'by {name} on {date}',
            approval_recorded: 'Client approval recorded.',
            approval_rejection_recorded: 'Client rejection recorded.',
            notes_saved: 'Notes saved.',
            evidence_saved: 'Evidence updated.',
            generated_count: 'Generated {count} deliverables.',
            generated_none: 'No deliverables were generated. No active deliverable template is linked to this tier — create one under Deliverable Templates and link it to this tier code first.',
            organization_id: 'Organisatioun ID',
            service_tier_id: 'Service Stuf ID',
            filter_assigned_to: 'Filteren no Zougewisen Op',
            select_status: 'Auswielen Status...',
            new_status: 'Nei Status',
            upcoming: 'Ustehend',
            overdue: 'Iwwerfälleg',
            status_deferred: 'Verschoben',
            status_overdue: 'Iwwerfälleg',
            status_upcoming: 'Ustehend',
        },
        brand_profiles: {
            title: 'Markeprofiler',
            new: 'Nei Mark Profil',
            partner: 'Partner',
            brand_name: 'Mark Numm',
            primary_color: 'Primär Faarf',
            logo_url: 'Logo URL',
            no_profiles: 'Keng brand profiles fonnt.',
            secondary_color: 'Secondary Faarf',
            header_text: 'Kapptext Text',
            footer_text: 'Fousstext Text',
            contact_email: 'Kontakt E-Mail',
            contact_phone: 'Kontakt Telefon',
            contact_website: 'Kontakt Websäit',
            contact_address: 'Kontakt Adress',
            is_default: 'Is Standard',
            set_default: 'Set Standard',
            preview: 'Virschau',
            report_header_preview: 'Bericht Kapptext Virschau',
            report_footer_preview: 'Bericht Fousstext Virschau',
            preview_description: 'This is eng preview vun den report header mat den ausgewielt brand colors.',
            contact_website_short: 'Websäit',
            logo_preview: 'Logo-Virschau',
        },
        support_tickets: {
            title: 'Supportufroen',
            new: 'Nei Ticket',
            partner: 'Partner',
            subject: 'Betreff',
            assigned_to: 'Zougewisen Op',
            no_tickets: 'Keng support Ticketen fonnt.',
            category: 'Kategorie',
            priority: 'Prioritéit',
            escalation_tier: 'Eskalatioun Stuf',
            sla_status: 'SLA-Status',
            update_status: 'Aktualiséieren Status',
            escalate: 'Eskaléieren',
            add_note: 'Derbäisetzen Notiz',
            escalate_ticket: 'Eskaléieren Ticket',
            escalate_to_tier: 'Eskaléieren Op Stuf',
            notes_timeline: 'Notizen Zäitverlaf',
            no_notes: 'Nach keng Notizen.',
            attachments: 'Unhäng',
            no_attachments: 'Keng Unhäng.',
            client_org_id: 'Client-Organisatiouns-ID',
            content: 'Inhalt',
            note_type: 'Typ',
            view_detail: 'Ukucken Detail',
            hide: 'Verstoppen',
            new_status: 'Nei Status',
            all_categories: 'All Kategorien',
            all_priorities: 'All Prioritéiten',
            all_tiers: 'All Stufen',
            ticket_not_found: 'Ticket not fonnt',
            ticket_id: 'Ticket-ID',
            client_org: 'Client-Organisatioun',
            deescalate: 'Deeskaléieren',
            deescalate_ticket: 'Ticket deeskaléieren',
            deescalate_to_tier: 'De-escalate Op Stuf',
            add_attachment: 'Derbäisetzen Unhang',
            file: 'Fichier',
            linked_entity: 'Verbonnen Entitéit',
            sla_target: 'Zil',
            breached: 'Verletzt',
            remaining_hours: 'Rescht',
            na: 'N/A',
            tickets_found: 'Ticketen fonnt',
            reason: 'Grond',
            target: 'Zil',
            upload: 'Eroplueden',
        },
        workflow_templates: {
            title: 'Aarbechtsoflaafvirlagen',
            new: 'Nei Schabloun',
            template_name: 'Schabloun Numm',
            steps: 'Schrëtter',
            no_templates: 'Keng Workflow Schablounen fonnt.',
            category: 'Kategorie',
            view_steps: 'Ukucken Schrëtter',
            hide_steps: 'Verstoppen Schrëtter',
            step: 'Schrëtt',
            step_title: 'Titel',
            step_description: 'Beschreiwung',
            assignee_role: 'Zougewisenen Roll',
            sla_hours: 'SLA-Stonnen',
            evidence_required: 'Bewäis Erfuerderlech',
            guidance_notes: 'Leitfaden Notizen',
            add_step: 'Derbäisetzen Schrëtt',
            no_steps: 'Keng Schrëtter definéiert.',
            no_steps_defined: 'Keng Schrëtter definéiert.',
            templates_found: 'Schablounen fonnt',
            all_categories: 'All Kategorien',
        },
        workflows: {
            client_org: 'Client-Organisatioun',
            title: 'Workflow Instanzen',
            new: 'Nei Workflow',
            template: 'Schabloun',
            partner: 'Partner',
            current_step: 'Aktuell Schrëtt',
            progress: 'Fortschrëtt',
            no_workflows: 'Keng Workflow Instanzen fonnt.',
            instances_found: 'Instanzen fonnt',
        },
        escalation: {
            title: 'Eskalatioun Dashboard',
            ticket: 'Ticket',
            partner: 'Partner',
            escalated_at: 'Eskaléiert At',
            reason: 'Grond',
            no_escalations: 'Keng escalations fonnt.',
            time_in_tier: 'Zäit an Stuf',
            auto_evaluate: 'Auto Bewäerten',
            priority_critical: 'Kritesch',
            priority_high: 'Héich',
            priority_medium: 'Mëttel',
            priority_low: 'Low',
            deescalated_from_dashboard: 'De-escalated vun dashboard',
            sla_breached: 'SLA Verletzt',
            sla_remaining: 'SLA Rescht: {hours}h',
            sr_total_escalated: 'Total escalated',
            sr_by_tier: 'By tier',
            sr_by_priority: 'By priority',
        },
        reports: {
            client_org: 'Client-Organisatioun',
            title: 'Berichter',
            partner: 'Partner',
            report_type: 'Bericht Typ',
            generated_at: 'Generéiert At',
            no_reports: 'Keng reports fonnt.',
            generate: 'Generéieren Bericht',
        },
        audit: {
            title: 'Partner-Auditprotokoll',
            event_type: 'Evenement Typ',
            entity_type: 'Entitéit Typ',
            entity: 'Entitéit',
            entity_id: 'Entitéit ID',
            actor: 'Akteur',
            timestamp: 'Zäitstempel',
            no_events: 'Keng Audit Evenementer fonnt.',
            events_found: 'Evenementer fonnt',
            client_org: 'Client-Organisatioun',
            all_actors: 'All Akteuren',
            event_type_search: 'Evenementstyp sichen',
            event_type_created: 'Geschaf',
            event_type_updated: 'Aktualiséiert',
            event_type_deleted: 'Geläscht',
            event_type_set_default: 'Set as Default',
            event_type_status_changed: 'Status Changed',
            event_type_status_updated: 'Status Updated',
            event_type_generated: 'Generated',
            event_type_export_generated: 'Export Generated',
            event_type_escalated: 'Escalated',
            event_type_auto_escalated: 'Auto-Escalated',
            event_type_deescalated: 'De-escalated',
            event_type_managed_org_added: 'Managed Client Added',
            event_type_managed_org_removed: 'Managed Client Removed',
            event_type_versioned: 'New Version',
            event_type_attachment_added: 'Attachment Added',
            event_type_note_added: 'Note Added',
            event_type_step_advanced: 'Step Advanced',
            event_type_cancelled: 'Cancelled',
            entity_type_partner_org: 'Partner Org',
            entity_type_service_tier: 'Service Tier',
            entity_type_deliverable_template: 'Liwwerung Schabloun',
            entity_type_deliverable_instance: 'Liwwerbar Instanz',
            entity_type_brand_profile: 'Mark Profil',
            entity_type_support_ticket: 'Ënnerstëtzung Ticket',
            entity_type_workflow_template: 'Workflow Schabloun',
            entity_type_workflow_instance: 'Workflow Instanz',
            entity_type_generated_report: 'Generéiert Bericht',
            entity_type_audit_export: 'Audit-Export',
        },
        no_permission_title: 'Net genuch Permissiounen',
        no_permission_message: 'Dir hutt keng Erlaabnis fir Zougang zum Partner Aarbechtsberäich ze kréien.',
        status_active: 'Aktiv',
        status_suspended: 'Suspendéiert',
        status_offboarded: 'Offboard',
        status_completed: 'Ofgeschloss',
        status_cancelled: 'Annuléiert',
        status_deprecated: 'Deprecéiert',
        status_new: 'Nei',
        status_triaged: 'Triaged',
        status_in_progress: 'Am Fortschrëtt',
        status_waiting_on_client: 'Waarden op Client',
        status_waiting_on_vendor: 'Waart op Verkeefer',
        status_escalated: 'Eskaléiert',
        status_resolved: 'Geléist',
        status_closed: 'Zougemaach',
        status_pending: 'Erwaardung',
        status_skipped: 'Iwwersprongen',
        client_org_column: 'Client org',
        client_org_unresolved: 'Client org not recorded',
        category_onboarding: 'Onboarding',
        category_incident_escalation: 'Incident Eskalatioun',
        category_config_change: 'Configuratioun änneren',
        category_periodic_review: 'Periodesch Iwwerpréiwung',
        category_offboarding: 'Offboarding',
        category_custom: 'Benotzerdefinéiert',
        category_configuration: 'Configuratioun',
        category_incident: 'Tëschefall',
        category_compliance_question: 'Konformitéit Fro',
        category_feature_request: 'Feature Ufro',
        category_bug_report: 'Bug Bericht',
        priority_critical: 'Kritesch',
        priority_high: 'Héich',
        priority_medium: 'Mëttelméisseg',
        priority_low: 'Niddereg',
        tier_silver: 'Sëlwer',
        tier_gold: 'Gold',
        tier_platinum: 'Platin',
        report_type_compliance_summary: 'Compliance Resumé',
        report_type_risk_overview: 'Risiko Iwwersiicht',
        report_type_incident_report: 'Tëschefall Rapport',
        report_type_consent_status: 'Zoustëmmung Status',
        report_type_program_maturity: 'Programm Maturitéit',
        report_type_executive_pack: 'Executive Pack',
        note_type_internal: 'Intern',
        note_type_client_visible: 'Client sichtbar',
        status_updated: 'Status erfollegräich aktualiséiert',
        deliverable_status_updated: 'Liwwerbar Status erfollegräich aktualiséiert',
        ticket_status_updated: 'Ticket Status erfollegräich aktualiséiert',
        ticket_escalated: 'Ticket erfollegräich eskaléiert',
        note_added: 'Notiz erfollegräich dobäigesat',
        attachment_uploaded: 'Uschloss erfollegräich eropgelueden',
        ticket_deescalated: 'Ticket erfollegräich de-eskaléiert',
        auto_evaluation_completed: 'Auto-Evaluatioun fäerdeg',
        template_created: 'Workflow Schabloun erfollegräich erstallt',
        template_updated: 'Workflow Schabloun erfollegräich aktualiséiert',
        brand_set_default: 'Markeprofil erfollegräich als Standard gesat',
        brand_created: 'Markeprofil erfollegräich erstallt',
        brand_updated: 'Mark Profil erfollegräich aktualiséiert',
        tier_created: 'Service Tier erfollegräich erstallt',
        tier_updated: 'Service Tier erfollegräich aktualiséiert',
        tier_deprecated: 'Service Tier erfollegräich ofgeschaaft',
        tier_activated: 'Service Tier erfollegräich aktivéiert',
        period: 'Period',
        format: 'Format',
        period_start: 'Period Start',
        period_end: 'Period Enn',
        reports_count: '{count} bericht',
        reports_count_one: '{count} Bericht',
        metric: 'Metresch',
        value: 'Wäert',
        showing_chart_view: 'Weist Chart Vue',
        showing_table_view: 'Weist Daten Dësch Vue',
        evidence_ids_placeholder: 'z.B. ev-001, ev-002',
        confirm_cancel_workflow: 'Sidd Dir sécher datt Dir dëse Workflow annuléiere wëllt? Dës Aktioun kann net réckgängeg gemaach ginn.',
        export_csv: 'Export CSV',
        compliance_qualifications_title: 'Compliance Service Qualifikatiounen',
        compliance_qualifications_desc: "The partner tier (Silver, Gold or Platinum) is a label on the partner record. The platform reads it in one place: a support ticket opened for an organisation the partner manages takes its resolution-time target from the partner's active service tier with the same code, or 24 hours when there is none. The tier grants no access and does not qualify a partner for any kind of engagement.",
        regulatory_output_title: 'Reguléierungsausgang Ausriichtung',
        regulatory_output_desc: "All Schablountyp kartéiert op eng reglementaresch Outputkategorie: Berichter produzéiere Konformitéitsdokumentatioun, Bewäertungen validéieren d'Effizienz vun der Kontroll, Bewäertungen decken Risikobewäertungen (DPIA, Spaltanalyse), Reuniounen dokumentéieren Gouvernance Sessiounen. Beweis Ufuerderunge definéieren d'Artefakte vun all Liwwerung muss fir Audit Bereetschaft Referenz.",
        sla_compliance_title: 'SLA Tracking fir Compliance Liwwerung',
        sla_compliance_desc: "Äntwert an Opléisung Zäit SLAs sinn kontraktuell bindend fir d'Konformitéitsservice Liwwerung. Iwwerpréiwung Frequenz bestëmmt wéi dacks Partner Konformitéit Liwwerunge bewäert ginn. SLA-Verstéiss op Konformitéitskritesch Ticketen ausléisen automatesch Eskalatioun a ginn am Partner Audit Trail fir reglementaresch Beweiser ageloggt.",
        regulatory_engagement_title: 'Reguléierungs Engagement Musteren',
        regulatory_engagement_desc: 'Workflow Kategorien Kaart op reglementaresche Engagement Typen: Onboarding deckt initial Konformitéitsopstellung a Spaltanalyse, Periodesch Iwwerpréiwung handhabt widderhuelend Konformitéitsbewäertungen, Incident Escalation geréiert reglementaresche Verstouss Äntwert, Offboarding suergt fir Datebehalen Verpflichtungen erfëllt ginn. All Schrëtt kann Beweiser Sammlung verlaangen fir Audit Trail Vollständegkeet.',
        compliance_escalation_title: 'Konformitéit-kritesch Eskalatioun Prozeduren',
        compliance_escalation_desc: 'L1 geréiert Standard operationell Themen. L2 deckt Konformitéit-impaktéierend Feeler (verpasst reglementaresch Termin, Kontrolllücken). L3 ass reservéiert fir reglementaresche Verstouss Szenarie déi direkt exekutiv Opmierksamkeet erfuerderen. SLA Verletzung op L2 / L3 Ticketen generéiert Audit Trail Entréen automatesch. All Eskalatiouns- an Deeskalatiounsaktioune si permanent protokolléiert.',
        audit_regulatory_title: 'Regulatiounsbeweis Trail',
        audit_regulatory_desc: 'Dësen Audit Trail liwwert tamper-evident records fir reglementaresch Konformitéit. All Partneraktioun (erstellen, aktualiséieren, läschen, Eskalatioun, Status änneren) gëtt mat Akteuridentitéit, Zäitstempel a voll Notzlaaschtdiff protokolléiert. Benotzt Entity Type Filtere fir Beweiser no Konformitéitsdomän ze isoléieren. Export gefilterte Resultater fir Audit-prett Dokumentatioun fir reglementaresch Inspektiounen ze produzéieren.',
        evidence_trail_title: 'Kräiz-Modul Beweis Trail',
        evidence_trail_desc: 'All Workflow-Schrëtt kann Beweis-IDs sammelen, déi mat Liwwerungen, Auditevenementer a Konformitéitsartefakter iwwer Moduler verbannen. E Schrëtt mat Beweis IDs virzegoen erstellt eng verifizéierbar Kette déi Partner Workflows mat reglementaresche Dokumentatioun verbënnt. Ofgeschloss Workflows mat voller Beweisketten déngen als Beweis vun der Konformitéit Engagement Liwwerung.',
        evidence_ids_label: 'Beweis IDen',
    },
    org_groups: {
        title: 'Organisatiounsgruppen',
        new: 'Erstellen Grupp',
        group_name: 'Grupp Numm',
        member_count: 'Mëmber Unzuel',
        root_org: 'Stamm Org',
        no_groups: 'Keng org groups fonnt.',
        add_organization: 'Organisatioun derbäisetzen',
        select_organization: 'Eng Organisatioun auswielen...',
        no_available_orgs: 'Keng verfügbar Organisatiounen zum Derbäisetzen.',
        no_members: 'Keng Member-Organisatiounen an dësem Grupp.',
        member_added: 'Organisatioun zum Grupp derbäigesat.',
        member_removed: 'Organisatioun aus dem Grupp ewechgeholl.',
        already_in_group: 'Dës Organisatioun ass schonn an engem Grupp.',
        org_not_found: 'Organisatioun net fonnt.',
        add_member_error: "Konnt d'Organisatioun net zum Grupp derbäisetzen.",
        remove_member_error: "Konnt d'Organisatioun net aus dem Grupp ewechhuelen.",
        confirm_remove_member: 'Sidd Dir sécher, datt Dir "{name}" aus dësem Grupp ewechhuele wëllt?',
        load_error: "Konnt d'Org-Gruppendetailer net lueden.",
        page_title_list: 'Organisatioun Gruppen',
        page_title_detail: 'Organisatioun Group Detailer',
        page_title_create: 'Schafen Organisatioun Group',
        page_title_settings: 'Organisatioun Group Astellunge',
        page_title_users: 'Grupp Benotzer',
        page_title_reports: 'Grupp Rapporten',
        page_title_dashboard: 'Group Dashboard',
        page_title_audit: 'Group Audit',
        page_title_org_audit: 'Organisatioun Group Audit',
        page_title_shared_vendors: 'Shared Verkeefer',
        page_title_shared_frameworks: 'Shared Frameworks',
        page_title_shared_forms: 'Shared Form Schablounen',
        page_title_merged_vendors: 'Fusioune Verkeefer',
        page_title_settings_inheritance: 'Astellunge Ierfschaft',
        announce_loaded_groups: 'Luede {count} Organisatiounsgruppen',
        announce_loaded_group: 'Luede Grupp {name}',
        announce_loaded_users: 'Luede {count} Grupp Benotzer',
        announce_loaded_reports: 'Lueden {count} Berichter',
        announce_loaded_vendors: 'Lueden {count} gedeelt Ubidder',
        announce_loaded_frameworks: 'Lueden {count} gedeelt Kaderen',
        announce_loaded_templates: 'Lueden {count} gedeelt Form Templates',
        announce_loaded_audit: 'Lueden {count} Audit Evenementer',
        announce_loaded_merged: 'Luede {shared} gedeelt a {local} lokal Ubidder',
        announce_dashboard_loaded: 'Dashboard gelueden.',
        announce_dashboard_refreshed: 'Dashboard erfrëscht.',
        announce_dashboard_refresh_failed: 'Konnt Dashboard net erfrëschen.',
        announce_refreshing: 'Dashboard erfrëschend ...',
        announce_settings_loaded: 'Astellunge Ierfschaft gelueden.',
        announce_settings_saved: 'Astellungen erfollegräich gespäichert.',
        announce_saving_settings: 'Astellunge späicheren...',
        announce_error_saving: 'Feeler beim späicheren Astellungen.',
        announce_group_dissolved: 'Grupp opgeléist.',
        announce_creating_group: 'Erstellt eng Organisatiounsgrupp ...',
        announce_group_created: 'Organisatioun Grupp erfollegräich geschaf.',
        announce_error_creating: 'Feeler beim Erstellen vun der Organisatiounsgrupp.',
        announce_vendor_published: 'Verkeefer publizéiert erfollegräich.',
        announce_vendor_retired: 'Verkeefer pensionnéierte.',
        announce_framework_published: 'Kader erfollegräich publizéiert.',
        announce_framework_retired: 'Kader pensionnéierte.',
        announce_template_published: 'Form Schabloun erfollegräich publizéiert.',
        announce_template_retired: 'Form Schabloun pensionnéierte.',
        announce_role_assigned: 'Roll erfollegräich zougewisen.',
        announce_error_assign_role: 'Feeler beim Zouzeechnung vun der Roll.',
        announce_role_revoked: 'Roll erfollegräich zréckgezunn.',
        announce_report_generating: 'Generéiere Bericht ...',
        announce_report_requested: 'Rapport Generatioun gefrot.',
        announce_error_report: 'Feeler beim Ufro vum Bericht.',
        announce_report_deleted: 'Rapport geläscht.',
        announce_search_applied: 'Sich applizéiert.',
        announce_search_cleared: 'Sich geläscht.',
        announce_tab_selected: '{tab} Tab ausgewielt',
        announce_error_add_member: 'Feeler beim Zousatz vun engem Member an de Grupp.',
        announce_category_reverted: '{category} zréck op ierflecher vum Elterendeel.',
        announce_category_overridden: '{category} op lokal Iwwerschrëft gesat.',
        announce_override_saved: '{category} Iwwerschreiden gespäichert.',
        announce_export_started: 'Audit exportéieren als {format}...',
        announce_export_completed: 'Export als {format} fäerdeg.',
        announce_export_failed: 'Export gescheitert.',
        announce_filter_org: 'Filtert no Organisatioun {name}',
        announce_filter_org_cleared: 'Organisatioun Filter geläscht',
        announce_filter_event: 'Filtert no Eventtyp {type}',
        announce_filter_cleared: 'Filter geläscht',
        announce_download: 'Eroflueden {name} als {format}',
        error_load_groups: 'Konnt net lueden Organisatiounsgruppen. Probéiert w.e.g. nach eng Kéier.',
        error_load_users: 'Konnt net lueden Grupp Benotzer.',
        error_load_reports: 'Konnt net lueden Grupp Berichter.',
        error_load_dashboard: 'Konnt net gelueden Grupp Dashboard.',
        error_load_settings: 'Konnt net lueden Grupp Astellungen.',
        error_load_vendors: 'Konnt fusionéierte Verkeefer net lueden.',
        error_save_settings: 'Konnt Astellunge späicheren.',
        error_dissolve_group: "Konnt d'Grupp net opléisen.",
        error_assign_role: 'Ausgefall Roll ze ginn.',
        error_create_group: "Konnt d'Organisatiounsgrupp net erstellen. Probéiert w.e.g. nach eng Kéier.",
        error_request_report: 'Ausgefall Rapport ze froen.',
        error_group_name_required: 'Grupp Numm ass néideg.',
        success_settings_saved: 'Astellungen erfollegräich gespäichert.',
        confirm_retire_vendor: 'Sidd Dir sécher datt Dir de Verkeefer "{name}" zréckzéien wëllt?',
        confirm_retire_framework: 'Sidd Dir sécher datt Dir de Kader "{name}" zréckzéien wëllt?',
        confirm_retire_template: 'Sidd Dir sécher datt Dir d\'Formulaire Schabloun "{name}" zréckzéie wëllt?',
        confirm_dissolve_group: 'Sidd Dir sécher datt Dir "{name}" opléise wëllt? Dës Aktioun kann net réckgängeg gemaach ginn.',
        confirm_revoke_role: 'Sidd Dir sécher datt Dir d\'Roll "{role}" vun "{name}" zréckzéien wëllt?',
        confirm_delete_report: 'Sidd Dir sécher datt Dir de Bericht "{name}" läschen wëllt?',
        dissolve_group: 'Grupp opléisen',
        dissolve_warning: 'Dëst wäert den Organisatiounsgrupp permanent opléisen. All Member Organisatiounen ginn ewechgeholl.',
        count_groups: '{count} Grupp',
        count_groups_plural: '{count} Gruppen',
        count_users: '{count} Benotzer',
        count_users_plural: '{count} Benotzer mat Grupprollen',
        count_events: '{count} Event',
        count_events_plural: '{count} Evenementer',
        count_reports: '{count} Bericht',
        count_reports_plural: '{count} bericht',
        count_vendors: '{count} Verkeefer iwwer de Grupp gedeelt',
        count_vendors_plural: '{count} Ubidder iwwer de Grupp gedeelt',
        count_frameworks: '{count} Kader iwwer de Grupp gedeelt',
        count_frameworks_plural: '{count} Kaderen iwwer de Grupp gedeelt',
        count_templates: '{count} Schabloun iwwer de Grupp gedeelt',
        count_templates_plural: '{count} Schabloune gedeelt iwwer de Grupp',
        count_shared_vendors: '{count} gedeelt Verkeefer',
        count_local_vendors: '{count} lokal Ubidder',
        merged_combined_view: 'Kombinéiert Vue vun {shared} gedeelt an {local} lokal Ubidder',
        dashboard: {
            title: 'Gruppeniwwersiicht',
            loading: 'Lueden vun dashboard Donnéeën...',
            total_groups: 'Gesamt Gruppen',
            total_members: 'Gesamt Memberen',
            active_groups: 'Aktiv Gruppen',
            overall_compliance_score: 'Allgemeng Compliance Score',
            group_risk_level: 'Grupp Risiko Niveau',
            sla_compliance: 'SLA Konformitéit',
            compliance_posture: 'Konformitéit Haltung',
            risk_heatmap: 'Risiko Heatmap',
            sla_performance: 'SLA Leeschtung',
            recent_incidents: 'Rezent Tëschefäll',
            recent_activity: 'Rezent Aktivitéit',
            refresh_label: "D'Daten vum Dashboard erfrëschen",
            score_good: 'Gutt',
            score_needs_attention: 'Braucht Opmierksamkeet',
            score_critical: 'Kritesch',
            trend_up: 'Trend erop',
            trend_down: 'Trend erof',
            trend_stable: 'Stabil',
            risk_low: 'Low',
            risk_medium: 'Mëttelméisseg',
            risk_high: 'Héich',
            risk_critical: 'Kritesch',
            risk_unknown: 'Onbekannt',
            col_organization: 'Organisatioun',
            col_score: 'Score',
            col_trend: 'Trend',
            col_open_issues: 'Oppen Emissiounen',
            col_critical_issues: 'Kritesch Themen',
            col_health: 'Gesondheet',
            col_vendor_risk: 'Verkeefer Risiko',
            col_data_risk: 'Donnéeën Risiko',
            col_compliance_risk: 'Konformitéit Risiko',
            col_total_cases: 'Ganzen Fäll',
            col_on_time: 'Op Zäit',
            col_breached: 'Gebrach',
            col_sla_percent: 'SLA %',
            col_avg_days: 'Duerchschnëtt Deeg',
            sr_vendor_risk: 'Verkeefer Risiko Score: {score}',
            sr_data_risk: 'Daterisiko Score: {score}',
            sr_compliance_risk: 'Konformitéitsrisiko Score: {score}',
            sr_sla_breaches: 'Opgepasst: {count} SLA Verstéiss',
            sr_severity: 'Gravitéit: {severity}',
        },
        detail: {
            title: 'Gruppendetailer',
            members: 'Memberen',
            settings: 'Astellungen',
            audit: 'Iwwerpréiwung',
            sr_loading: 'Luede Organisatioun Grupp Detailer',
            sr_loading_hierarchy: 'Lueden Hierarchie',
            tab_tasks: 'Shared tasks',
            tasks_intro: "The tasks of every organization in this group, read across the group because task sharing is switched on in this group's settings.",
            tasks_total: '{count} tasks across the group',
            tasks_overdue: '{count} overdue',
            tasks_by_status: 'By status',
            tasks_by_org: 'By organization',
            tasks_latest: 'Latest tasks',
            tasks_showing: 'Showing the {shown} most recent of {total}.',
            tasks_empty: 'No organization in this group has any tasks.',
            col_title: 'Title',
            col_org: 'Organization',
            col_status: 'Status',
            col_priority: 'Priority',
            col_due: 'Due',
            tasks_load_failed: "The group's shared tasks could not be read, so nothing is shown for them.",
            tasks_subject: 'shared tasks',
            sr_loading_tasks: 'Loading shared tasks',
            member_org: 'Member Organisatioun',
            root_org_label: 'root Organisatioun',
            hierarchy: 'Hierarchy',
        },
        users: {
            title: 'Gruppebenotzer',
            add_user: 'Derbäisetzen Benotzer',
            no_users: 'No users an dëst Grupp.',
            role_group_admin: 'Group Admin',
            role_group_reader: 'Grupp Lieser',
            sr_loading: 'Luede Grupp Benotzer',
        },
        reports: {
            title: 'Gruppeberichter',
            no_reports: 'Keng reports fonnt.',
            generate_report: 'Generéiere neie Rapport',
            report_type_compliance: 'Konformitéit Resumé',
            report_type_risk: 'Risiko Iwwersiicht',
            report_type_sla: 'SLA Leeschtung',
            report_type_incident: 'Tëschefall Rapport',
            report_type_executive: 'Executive Brief',
            report_type_board: 'Verwaltungsrot Rapport',
            status_queued: 'Queue',
            status_generating: 'Generéiere',
            status_completed: 'Ofgeschloss',
            status_failed: 'Ausgefall',
            period_start: 'Period Start',
            period_end: 'Period Enn',
            sr_loading: 'Luede Grupp Rapporten',
            /*
             * The two row actions' accessible names, interpolated rather than built
             * as English template literals.
             *
             * They shipped as `aria-label={`Delete report ${…}`}` and
             * `aria-label={`Download ${…} as ${…}`}` — an `aria-label` is
             * user-visible text, so 15 non-English locales heard these in English.
             * The same defect was fixed by hand one page over in the same commit
             * that touched these two lines and left them.
             */
            download_report_aria: 'Download {title} as {format}',
            delete_report_aria: 'Delete report {title}',
        },
        shared_vendors: {
            title: 'Gedeelt Liwweranten',
            no_vendors: 'Keng shared Ubidder fonnt.',
            merged_vendors: 'Zesummegeféiert Ubidder',
            publish_form_label: 'Verëffentlechen gedeelt Verkeefer',
            sr_loading: 'Luede gedeelt Ubidder',
            sr_loading_merged: 'Luede fusionéierte Verkeefer',
            local_vendors: 'Lokal Verkeefer',
            source_shared: 'Vun Grupp gedeelt',
            source_local: 'Lokal Verkeefer',
            category_technology: 'Technologie',
            category_consulting: 'Berodung',
            category_outsourcing: 'Outsourcing',
            category_cloud: 'Wollek',
            category_data_processing: 'Daten Veraarbechtung',
            category_other: 'Aner',
            risk_tier_low: 'Low',
            risk_tier_medium: 'Mëttelméisseg',
            risk_tier_high: 'Héich',
            risk_tier_critical: 'Kritesch',
            compliance_compliant: 'Konform',
            compliance_non_compliant: 'Net-kompatibel',
            compliance_pending: 'Erwaardung',
            compliance_under_review: 'Ënner review',
            status_active: 'Aktiv',
            status_retired: 'Pensionéiert',
        },
        shared_frameworks: {
            // Fix sprint F28 (WCAG 3.3.2) — the framework-code field was labelled
            // `common.type` ("Type"), so its accessible name said nothing about what
            // it takes. With no installed framework the placeholder resolves to '',
            // leaving a required field with neither a meaningful name nor a hint.
            framework_code_label: 'Framework-Code',
            title: 'Gedeelt Kaderen',
            no_frameworks: 'Keng shared frameworks fonnt.',
            publish_form_label: 'Verëffentlechen gemeinsame Kader',
            sr_loading: 'Luede gedeelt Kaderen',
            status_active: 'Aktiv',
            status_retired: 'Pensionéiert',
        },
        shared_forms: {
            title: 'Gedeelt Formulairvirlagen',
            no_forms: 'Keng shared form Schablounen fonnt.',
            publish_form_label: 'Verëffentlechen gedeelt Form Schabloun',
            sr_loading: 'Lueden gedeelt Form Templates',
            status_active: 'Aktiv',
            status_retired: 'Pensionéiert',
        },
        audit: {
            title: 'Gruppekontroll',
            consolidated_title: 'Konsolidéiert Audit Trail',
            consolidated_subtitle: 'Cross-organization Audit Evenementer fir dëst Grupp',
            audit_log_title: 'Grupp Audit Protokoll',
            event_type: 'Evenement Typ',
            actor: 'Akteur',
            timestamp: 'Zäitstempel',
            source_org: 'Quell Org',
            org: 'Org',
            summary: 'Zesummefaassung',
            details: 'Detailer',
            no_events: 'Keng Audit Evenementer fonnt.',
            all_events: 'All Evenementer',
            all_orgs: 'All Organisatiounen',
            organization: 'Organisatioun',
            from: 'Vun',
            to: 'To',
            search_events: 'Sich Evenementer...',
            clear: 'Läschen',
            events_24h: 'Evenementer (24h)',
            events_7d: 'Evenementer (7d)',
            active_orgs: 'Aktiv Orgs',
            exporting: 'Exportéiere...',
            export_csv: 'Exportéieren CSV',
            export_json: 'Exportéieren JSON',
            back_to_group: 'Back op Grupp',
            back_to_detail: 'Back op Detail',
            sr_loading: 'Lueden Audit Evenementer',
            sr_loading_summary: 'Lueden Audit Resumé',
            disabled_by_settings: 'The consolidated audit is switched off in the settings of this group. The root organization of the group can switch it back on under Settings.',
        },
        settings: {
            title: 'Grupp Astellungen',
            shared_tasks: 'Share task lists, statistics and calendars across the member organizations of this group',
            inheritance: 'Astellungen Inheritance',
            inheritance_subtitle: 'Konfiguréieren wéi eng Astellungskategorien aus der Grupp ierwen vs',
            sla_settings: 'SLA Astellungen',
            calendar_settings: 'Kalenner Astellungen',
            notification_settings: 'Notifikatioun Astellungen',
            intake_settings: 'Opnam Astellungen',
            cmp_integration: 'CMP Integratioun',
            overridden: 'Iwwerschriwwen',
            inheriting: 'Verierwen',
            hide_defaults: 'Verstoppen Standardwäerter',
            show_group_defaults: 'Weisen Grupp Standardwäerter',
            group_defaults: 'Grupp Standardwäerter',
            inherit_from_group: 'Ierwen vun Grupp',
            use_local_override: 'Use Local Iwwerschreiwen',
            local_override_json: 'Local Iwwerschreiwen (JSON)',
            save_override: 'Späicheren Iwwerschreiwen',
            saving: 'Späichere...',
            sr_loading: 'Lueden Astellungen Ierfschaft',
            sr_loading_settings: 'Luede Grupp Astellunge',
            sr_enabled: 'Aktivéiert',
            sr_disabled: 'Behënnert',
            sr_override_active: 'Lokal Iwwerschreiden aktiv',
            sr_inherited: 'Ierf vun Elterendeel',
            danger_zone: 'Gefor Zone',
            sharing_options: 'Deele Optiounen',
            advanced_options: 'Fortgeschratt Optiounen',
            max_member_orgs: 'Maximum member organizations',
        },
        status_active: 'Aktiv',
        status_dissolved: 'Opgeléist',
        status_pending: 'Erwaardung',
        status_suspended: 'Suspendéiert',
    },
    integrations: {
        nav: {
            webhooks: 'Webhooks',
            domains: 'Domainen',
            export_schedules: 'Export-Pläng',
            email_log: 'E-Mail-Protokoll',
        },
    },
    platform: {
        abandoned_uploads: {
            title: 'Abandoned uploads',
            description: 'Files uploaded on a form nobody saved, past the grace window. Reading this changes nothing.',
            grace_window: 'Grace window',
            hours: '{n} hours',
            as_of: 'As of',
            candidates: 'Uploads no record claimed',
            retained_held: 'Kept — legal hold',
            file: 'File',
            uploaded_for: 'Uploaded for',
            uploaded_by: 'Uploaded by',
            uploaded_at: 'Uploaded',
            past_window_by: 'Past the window by',
            hours_short: '{n} h',
            run_sweep: 'Delete abandoned uploads',
            running: 'Deleting...',
            sweep_success: '{swept} upload(s) deleted, {held} kept under a legal hold.',
            sweep_failed: 'The sweep could not be run. Please try again.',
            load_failed: 'The abandoned-upload register could not be read. Please try again.',
            reload: 'Reload',
            empty: 'No upload is past the grace window unclaimed.',
            restore_note: 'A deleted upload is recoverable: it is soft-deleted and can be restored from this page.',
        },
        nav: {
            users: 'Benotzer',
            roles: 'Rollen',
            profile: 'Mäi Profil',
        },
        security: {
            mfa: 'MFA-Astellungen',
            sso: 'SSO-Konfiguratioun',
            api_keys: 'API-Schlësselen',
            service_accounts: 'Service-Konten',
            ip_allowlist: 'IP-Erlaabnislëscht',
            delegations: 'Delegatiounen',
            break_glass: 'Noutfall-Zougang',
            session_settings: 'Sessiouns-Astellungen',
            active_sessions: 'Aktiv Sessiounen',
            access_reviews: 'Zougangsiwwerpréifungen',
            my_reviews: 'Meng Iwwerpréifungen',
            role_analytics: 'Rollen-Analytik',
            attestation_schedules: 'Attestatiounspläng',
            sod_rules: 'SoD-Reegelen',
            sod_violations: 'SoD-Verletzungen',
        },
        module_config: {
            title: 'Modul-Konfiguratioun',
            subtitle: 'Aktivéiert oder desaktivéiert Feature-Moduler fir Är Organisatioun. Desaktivéiert Moduler ginn an der Navigatioun verstopp.',
            enabled: 'Aktivéiert',
            disabled: 'Desaktivéiert',
            save: 'Konfiguratioun späicheren',
            saving: 'Späicheren...',
            saved: 'Konfiguratioun erfollegräich gespäichert',
            nav_label: 'Modul-Konfiguratioun',
            settings: 'Astellungen',
            webhooks: 'Webhooks',
            domains: 'Domainen',
            export_schedules: 'Export-Pläng',
            email_log: 'E-Mail-Protokoll',
            announce_loaded: 'Module configuration loaded',
        },
        files_nav: {
            section: 'Fichieren',
            configuration: 'Konfiguratioun',
            browser: 'Fichier-Browser',
            dashboard: 'Fichier-Dashboard',
        },
        immutable_audit_trails: {
            nav_group_label: 'Audit & Beweis',
            nav_settings: 'Astellungen',
            nav_status: 'Beweisstatus',
            nav_integrity_center: 'Integritéitszentrum',
            nav_usage: 'Notzung & Kreditter',
            settings_title: 'Onverännerlech Audit-Spueren — Astellungen',
            settings_subtitle: 'Per-Organisatioun-Astellungen fir den Add-on "Onverännerlech Audit-Spueren".',
            settings_banner: 'Den Add-on "Onverännerlech Audit-Spueren" ass net fir Är Organisatioun aktivéiert. Aktivéiert en hei fir Beweiser fir Tier-A- a Tier-B-Audit-Evenementer ze veranker.',
            status_title: 'Onverännerlech Audit-Spueren — Beweisstatus',
            status_subtitle: "Beweisrelatiounen pro Evenement iwwer all d'Moduler.",
            status_banner: 'Den Add-on "Onverännerlech Audit-Spueren" ass net fir Är Organisatioun aktivéiert. Gitt op d\'Astellungen fir en ze aktivéieren.',
            integrity_center_title: 'Onverännerlech Audit-Spueren — Integritéitszentrum',
            integrity_center_subtitle: 'Iwwerpréift Beweiser an exportéiert Integritéitspaketer.',
            integrity_center_banner: 'Den Add-on "Onverännerlech Audit-Spueren" ass net fir Är Organisatioun aktivéiert. Gitt op d\'Astellungen fir en ze aktivéieren, ier Dir eng Iwwerpréifung start oder Integritéitspaketer exportéiert.',
            usage_title: 'Onverännerlech Audit-Spueren — Notzung & Kreditter',
            usage_subtitle: 'Kredittsaldo, Paketkeef an Notzungshistorik.',
            usage_banner: "Aktivéiert d'Onverännerlech Audit-Spueren an den Astellungen, fir Beweiskreditter ze verbrauchen.",
            module_tips_placeholder: 'Tipps an Uleedung fir dës Säit sinn am Hëllef-Zentrum verfügbar.',
            column_label: 'Beweis',
            no_proof_aria: 'Kee Beweis registréiert',
            proof_status_receipt_label: 'Quittung {id}',
            in_progress_hint: 'Amgaang - automatesch aktualiséiert',
            status_pills: {
                pending: 'Ausstoend',
                submitted: 'Ofgeschéckt',
                anchored: 'Verankert',
                confirmed: 'Bestätegt',
                retrying: 'Erneit Versuch',
                paused_cap: 'Pauséiert (Limit)',
                failed: 'Feelgeschloen',
                failed_auth: 'Authentifikatioun feelgeschloen',
            },
            status_tooltips: {
                pending: 'Erwaardung - an der Schlaang fir de Beweisservice. Wäert geschwënn op Submitted plënneren; keng Aktioun néideg.',
                submitted: 'Ugeschéckt - Ären Event gouf un de Beweisservice geschéckt. Fir de Batchmodus waart et op versiegelt an an der Kette verankert ze ginn. Aktualiséierungen automatesch; keng Aktioun néideg.',
                anchored: "Verankert - d'Batch ass op der Kette geschriwwe ginn a waart op definitiv Bestätegung. Aktualiséierungen automatesch; keng Aktioun néideg.",
                confirmed: 'Bestätegt - de Beweis ass an der Kette verankert a verifizéierbar.',
                retrying: 'Neiprobéieren - e transiente Feeler ass geschitt. De Reconciler probéiert automatesch erëm; keng Aktioun néideg.',
                paused_cap: 'Pauséiert - Äre monatlecht Beweiskredittkapp ass erreecht ginn. Erléist méi Kreditter oder waart bis den nächste Rechnungsmonat.',
                failed: 'Ausgefall - Verankerung huet net gelongen. De Reconciler wäert automatesch erëm probéieren; kontaktéiert Ënnerstëtzung wann et bestoe bleift.',
                failed_auth: "Auth gescheitert - de Beweisservice huet eis Umeldungsinformatiounen refuséiert. Kuckt d'Immutable Audit Trails Settings Säit.",
            },
            mode_pills: {
                direct: 'Direkt',
                batch: 'Batch',
            },
            drawer: {
                title: 'Beweis-Detailer',
                close: 'Zoumaachen',
                section_proof: 'Beweis',
                section_batch: 'Batch-Memberschaft',
                status: 'Status',
                proof_reference: 'Beweisreferenz',
                receipt_id: 'Quittungs-ID',
                taxonomy_class: 'Taxonomieklass',
                units_consumed: 'Verbraucht Eenheeten',
                created_at: 'Erstallt',
                verification_ref: "Referenz fir d'Ketten-Verifikatioun",
                chain: 'Kette',
                tx_hash: 'Tx-Hash',
                block_ref: 'Blockreferenz',
                batch_reference: 'Batch-Referenz',
                leaf_index: 'Blat-Index',
                local_event_hash: 'Lokalen Evenement-Hash',
                show_inclusion: 'Inklusiounswee weisen',
                hide_inclusion: 'Inklusiounswee verstoppen',
                privacy_note: "D'Notzlaascht ass gehasht; keng perséinlech Donnéeën verloossen ComplianceOne fir dëst Evenement.",
                verify_now: 'Elo iwwerpréifen',
                verify_pending: 'Iwwerpréifung leeft…',
                verify_verified: 'Iwwerpréift — lokal a Kette-Säit-Kontrollen erfollegräich.',
                verify_issue_prefix: 'Iwwerpréifungsproblem: ',
                verify_failed: 'Iwwerpréifungsruff feelgeschloen.',
                verify_result_prefix: 'Iwwerpréifungsverdikt: ',
                verify_pending_note: "Verifizéierung erwaard: dëse Beweis waart nach ëmmer op Verankerung op der Kette. Probéiert nach eng Kéier nodeems d'Batch versiegelt ass.",
                awaiting_anchor_note: "Dëse Beweis ass Deel vun enger oppener Partie. Verifizéierung gëtt verfügbar wann d'Batch versiegelt an an der Kette verankert ass - typesch an der konfiguréierter Batchfenster vun der Organisatioun. De Status Badge gëtt automatesch aktualiséiert.",
                terminal_error_note: 'Dëse Beweis konnt net verankert ginn. De Beweis-Status Reconciler wäert automatesch erëm probéieren; manuell Verifizéierung huet nach näischt ze kontrolléieren.',
            },
            integrity_center: {
                section_run: 'Iwwerpréifung lafe loossen',
                section_progress: 'Lafprogress',
                section_issues: 'Problemer',
                period_start: 'Ufank vun der Period',
                period_end: 'Enn vun der Period',
                module_filter: 'Moduler (komma-getrennt, fakultativ)',
                modules_placeholder: 'rights-requests, incidents',
                run_button: 'Iwwerpréifung lafe loossen',
                export_button: 'Integritéitspaket exportéieren',
                export_success: 'Integritéitspaket exportéiert.',
                export_failed: 'Export feelgeschloen.',
                empty_disabled: "Aktivéiert d'Onverännerlech Audit-Spueren an den Astellungen, fir Iwwerpréifungspäss lafen ze loossen.",
                error_period_required: 'Wielt en Ufanks- an Enndatum.',
                error_start_run: 'De Lafstart ass feelgeschloen.',
                processed: 'Veraarbecht: {processed} vun {total}',
                col_verdict: 'Verdikt',
                col_count: 'Unzuel',
                col_proof_link: 'Beweis-Link',
                col_module: 'Modul',
                col_event_type: 'Evenementstyp',
                col_expected: 'Erwaarten Hash',
                col_recomputed: 'Nei berechenten Hash',
                col_raised_at: 'Gemellt um',
                any_verdict: 'Egal wéi e Verdikt',
                no_issues: 'Keng Problemer fonnt.',
            },
            settings_page: {
                load_error: 'Astellunge konnten net geluede ginn.',
                save: 'Späicheren',
                save_success: 'Astellunge gespäichert.',
                save_error: 'Späicheren feelgeschloen.',
                discard: 'Ännerunge verwerfen',
                read_only_banner: 'Dir hutt nëmme Liesrechter. Kontaktéiert e Plattform-Administrateur fir Ännerungen ze maachen.',
                section_status: 'Status & Aktivéierung',
                enabled_label: 'Add-on aktivéiert',
                enabled_help: 'Wann aktivéiert, gëtt all Tier-A- an Tier-B-Audit-Evenement gehasht a verankert.',
                section_mode: 'Standard-Beweismodus',
                mode_direct: 'Direkt (een Anker pro Evenement)',
                mode_batch: 'Batch (Merkle-Root-Anker pro Grupp)',
                mode_hybrid: 'Hybrid (Standard pro Klass-Register)',
                batch_window_label: 'Batch-Fënster (Minutten, 1–60)',
                section_taxonomy: 'Taxonomie vun de geschützten Evenementer',
                taxonomy_help: 'Iwwerschreiwungen pro Evenementsklass. Tier A → Direkt-Modus, Tier B → Batch-Modus, Aus → net bewisen.',
                taxonomy_search: 'Iwwerschreiwunge sichen',
                taxonomy_reset: 'Op Standard zerécksetzen',
                taxonomy_event_class: 'Evenementsklass',
                taxonomy_override: 'Iwwerschreiwung',
                taxonomy_actions: 'Aktiounen',
                taxonomy_clear: 'Läschen',
                taxonomy_no_overrides: 'Keng Iwwerschreiwungen — Standardwäerter aus dem Register ginn benotzt.',
                taxonomy_add_class: 'event_class.code',
                taxonomy_add: 'Iwwerschreiwung dobäisetzen',
                section_caps: 'Limitten & Begrenzungen',
                cap_label: 'Monatlech Limit (Eenheeten; eidel = onlimitéiert)',
                warn_label: "Warnschwell (Eenheeten; muss manner sinn ewéi d'Limit)",
                auto_pause_label: "Nei Beweiser automatesch pauséieren wann d'Limit erreecht ass",
                section_credentials: 'Zougangsdonnéeën',
                credential_id: 'ID vun den Zougangsdonnéeën',
                credential_rotated_at: 'Lescht Rotatioun',
                rotate_button: 'Zougangsdonnéeë rotéieren',
                rotate_success: 'Zougangsdonnéeë rotéiert. Al Zougangsdonnéeë sinn nach {n} Minutte gëlteg.',
                rotate_failed: 'Rotatioun vun den Zougangsdonnéeë feelgeschloen.',
                rotate_modal_title: 'AesirX-Zougangsdonnéeë rotéieren?',
                rotate_modal_body: "D'Rotatioun stellt nei Zougangsdonnéeën aus. Déi al Zougangsdonnéeë bleiwen 60 Minutte laang vun AesirX akzeptéiert, sou datt lafend Aarbechter hir aktuell Aufgabe fäerdeg maache kënnen. No 60 Minutte ginn déi al Zougangsdonnéeë widderrufen.",
                rotate_modal_confirm: 'Elo rotéieren',
                tier_a_off_modal_title: 'Beweis fir eng Evenementsklass desaktivéieren?',
                tier_a_off_modal_body: "Wann eng Evenementsklass op „Aus\" gesat gëtt, stoppt de Beweisfaass dës Evenementer ze verankeren. D'Architektur §6 erkläert d'reglementaresch Implikatioun: Tier-A-Evenementer sinn relevant fir den Iwwerwaacher; se auszeschalten kann d'Verteidegbarkeet vun Ärer Audit-Spuer schwächen.",
                tier_a_off_modal_confirm: 'Ech verstinn — Iwwerschreiwung uwenden',
                error_warn_lt_cap: "D'Warnschwell muss manner sinn ewéi d'monatlech Limit.",
                error_window_range: "D'Batch-Fënster muss tëschent 1 a 60 Minutte leien.",
                error_run_in_progress: 'Eng Iwwerpréifung leeft grad; den Add-on kann elo net desaktivéiert ginn.',
            },
            status_page: {
                filter_status: 'Status',
                filter_mode: 'Beweismodus',
                filter_module: 'Modul',
                filter_source_collection: 'Quellsammlung',
                filter_from_date: 'Vun',
                filter_to_date: 'Bis',
                filter_search: 'Sichen',
                any_status: 'Egal wéi e Status',
                any_mode: 'Egal wéi e Modus',
                col_module: 'Modul',
                col_event_type: 'Evenementstyp',
                col_proof_status: 'Status',
                col_proof_mode: 'Modus',
                col_created_at: 'Erstallt',
                col_units: 'Eenheeten',
                empty_disabled: 'Den Add-on "Onverännerlech Audit-Spueren" ass net fir Är Organisatioun aktivéiert. Gitt op d\'Astellungen fir en ze aktivéieren.',
                empty_enabled_no_rows: 'Keng Beweis-Linke passen op déi aktuell Filteren.',
                export_csv: 'CSV exportéieren',
                export_xlsx: 'XLSX exportéieren',
                export_success: 'Export ofgeschloss',
                export_failed: 'Export feelgeschloen',
            },
            usage_page: {
                section_alerts: 'Warnungen',
                alert_dismiss: 'Entlooss',
                alert_dismiss_aria: 'Entlooss Alarm {id}',
                alert_dismissed: 'Alarm entlooss',
                alert_dismiss_failed: 'Ausgefall Alarm ze entloossen',
                section_balance: 'Kredittsaldo',
                balance_label: 'Verfügbar Kreditter',
                balance_hint: 'Verbleiwend Eenheeten (1 Eenheet = 1 Beweis vun engem geschützten Evenement).',
                section_current_month: 'Aktuelle Mount',
                consumed_label: 'Verbraucht',
                granted_label: 'Zougestan',
                refunded_label: 'Erstaat',
                net_label: 'Netto',
                section_history: 'Notzungshistorik',
                history_kind: 'Aart',
                history_units: 'Eenheeten',
                history_balance_after: 'Saldo duerno',
                history_notes: 'Bemierkungen',
                history_at: 'Wéini',
                cap_warning_banner: 'Dir hutt Är Warnschwell fir dëse Rechnungsmount iwwerschratt. Iwwerleet Iech, zousätzlech Kreditter ze léisen.',
                cap_paused_banner: "Monatlech Limit erreecht. Beweis-Aweisunge sinn pauséiert bis nächste Mount oder bis d'Limit erhéicht gëtt.",
                section_redeem: 'Kredittpaket aléisen',
                redeem_purchase_id: 'Rechnungsnummer',
                redeem_package: 'Paket',
                redeem_button: 'Aléisen',
                redeem_pending: 'Erléist…',
                redeem_success: 'Kreditter aléist',
                redeem_failed: 'Aléise feelgeschloen',
                redeem_option_template: '{name} — {units} {units_label} — {price}',
                redeem_option_units: 'Unitéiten',
                alert_dismissing: 'Entlooss…',
                alert_kind: {
                    cap_reached: 'Mount Cap erreecht',
                    low_balance: 'Niddereg Gläichgewiicht',
                    auth_failure: 'Authentifikatioun Feeler',
                    merkle_mismatch: 'Merkle Mëssverständnis',
                    batch_terminal_failure: 'Batch Terminal Feeler',
                    direct_terminal_failure: 'Direkte Soumissioun Terminal Feeler',
                },
                usage_kind: {
                    consume: 'Konsuméiere',
                    refund: 'Remboursement',
                    grant: 'Grant',
                    adjustment: 'Upassung',
                    reservation: 'Reservatioun',
                    credit_grant: 'Credit granted',
                    consumption: 'Proof consumed',
                    refund_failed_proof: 'Refund (failed proof)',
                },
                package_starter: 'Startënnerlagen',
                package_growth: 'Wuesstem',
                package_enterprise: 'Entreprise',
                // Sprint 48.5.x — self-service "Buy More" billing (Flow A + B).
                buy_more: {
                    buy_button: 'Méi Crédits kafen',
                    purchase_title: 'Méi Crédits kafen',
                    purchase_subtitle: 'Wielt e Package. Dir gitt op déi sécher Stripe-Kees weidergeleet, an Är Rechnung kënnt per E-Mail.',
                    checkout_button: 'Weider zur Kees',
                    checkout_pending: 'Kees gëtt opgemaach…',
                    cancel_button: 'Ofbriechen',
                    purchase_error: "D'Kees konnt net gestart ginn. Probéiert w.e.g. gläich nach eng Kéier.",
                    redeem_subtitle: "Schonn bezuelt? Wielt de Package deen Dir kaaft hutt an gitt d'Rechnungs-ID aus Ärer E-Mail an fir d'Crédits derbäizesetzen.",
                    error_not_found: "Et gouf keng Rechnung mat där Nummer fonnt. Iwwerpréift d'Rechnungsnummer a probéiert et nach eng Kéier.",
                    error_not_paid: 'Dës Rechnung ass nach net als bezuelt markéiert.',
                    error_org_mismatch: 'Dës Rechnung gouf net fir Är Organisatioun ausgestallt.',
                    error_package_mismatch: 'Dës Rechnung entsprécht net dem ausgewielte Package.',
                    error_billing_unavailable: 'De Billing-Service konnt net erreecht ginn. Probéiert w.e.g. gläich nach eng Kéier.',
                },
                cap_over_banner: 'The monthly cap has been reached. Proofs keep being submitted because pausing at the cap is switched off.',
                resume_paused_button: 'Resume paused proofs',
                resume_paused_hint: 'Proofs held back when the monthly cap was reached are released by themselves when the cap is raised or removed, or when pausing at the cap is switched off. Use this once a new month has begun.',
                resume_paused_success: '{count} paused proofs released.',
                resume_paused_none: 'No paused proofs to release.',
                resume_paused_failed: 'The paused proofs could not be released.',
            },
        },
        files_config: {
            title: 'Fichier-Konfiguratioun',
            subtitle: "Konfiguréiert d'Limitte fir den Eropluedung, d'Opbewahrungspolitiken an d'Iwwerschreiwunge pro Besëtzertyp fir d'Plattform-Uschloss-Säit.",
            section_global: 'Global Standardwäerter',
            section_global_help: 'Dës Limitte gëlle fir all Fichier dee vu Benotzer an Ärer Organisatioun eropgelueden gëtt, ausser eng Iwwerschreiwung pro Besëtzertyp hei drënner ännert se.',
            section_overrides: 'Iwwerschreiwunge pro Besëtzertyp',
            section_overrides_help: 'Iwwerschreift déi global Limitte fir e bestëmmte Besëtzertyp. Wäerter pro Besëtzertyp gewannen ëmmer iwwer déi global Astellung.',
            max_file_size_label: 'Maximal Fichier-Gréisst',
            max_file_size_hint: 'Pro eenzelen Eropluedung (MiB)',
            max_total_attachments_label: 'Maximal total Uschlëss',
            max_total_attachments_hint: 'Iwwer déi ganz Organisatioun',
            max_total_size_label: 'Maximal total Späicherung',
            max_total_size_hint: 'Iwwer déi ganz Organisatioun (MiB)',
            allowed_mime_types_label: 'Erlaabt Fichier-Typen',
            allowed_mime_types_placeholder: 'MIME-Typen auswielen',
            retention_days_label: 'Opbewahrungsperiod (Deeg)',
            retention_days_hint: "Wéi laang d'Fichiere musse behale ginn, ier se geläscht kënne ginn. Loosst eidel fir keng Opbewahrungspolitik.",
            legal_hold_label: 'Gesetzlech Opbewahrung',
            legal_hold_hint: "Wann aktivéiert, brauch d'definitive Läsche vun engem Fichier vun dësem Besëtzertyp d'Erlaabnis fir d'Iwwerschreiwung vun der gesetzlecher Opbewahrung an eng explizit Aweisung vum Operateur.",
            save: 'Ännerunge späicheren',
            saving: 'Späicheren…',
            reset: 'Zerécksetzen',
            save_success: 'Astellunge gespäichert',
            empty_state_title: 'Plattform-Standardwäerter ginn benotzt',
            empty_state_body: 'Är Organisatioun leeft am Moment op der Standard-Fichier-Politik vun der Plattform. Ännert egal wéi ee Wäert hei drënner fir eng Iwwerschreiwung op Organisatiounsniveau ze erstellen.',
            read_only_banner: "Dir hutt nëmme Liesrechter op d'Fichier-Konfiguratioun. Kontaktéiert e Plattform-Administrateur fir Ännerungen ze maachen.",
            owner_type_default_badge: 'Standard',
            owner_type_overridden_badge: 'Iwwerschriwwen',
            owner_type_label: 'Besëtzertyp',
            owner_type_collapse: 'Aklappen',
            owner_type_expand: 'Opklappen',
            override_save: 'Iwwerschreiwung späicheren',
            override_remove: 'Iwwerschreiwung ewechhuelen',
            mime_labels: {
                'application/pdf': 'PDF-Dokument',
                'image/png': 'PNG-Bild',
                'image/jpeg': 'JPEG-Bild',
                'image/jpg': 'JPG-Bild',
                'application/msword': 'Word-Dokument (al)',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word-Dokument',
                'application/vnd.ms-excel': 'Excel-Tabell (al)',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel-Tabell',
                'text/csv': 'CSV-Fichier',
                'text/plain': 'Einfachen Text',
                'message/rfc822': 'E-Mail-Message',
            },
            global_legal_hold_label: 'Organization-wide legal hold',
            global_legal_hold_hint: 'When enabled, NO file across any owner type can be hard-deleted unless the operator has the legal_hold_override permission and explicitly opts in.',
            global_retention_days_label: 'Default retention period (days)',
            global_retention_days_hint: 'How long files must be kept before they become eligible for deletion. Leave blank for no retention policy. Per-owner-type overrides below can tighten.',
            global_abandoned_grace_label: 'Abandoned upload grace window (hours)',
            global_abandoned_grace_hint: 'How long a file that no saved record has claimed is left alone before the abandoned-upload sweep soft-deletes it. At least 24 hours, so a form that is still open is never affected.',
            global_abandoned_grace_invalid: 'Enter a whole number of hours from {min} to {max}.',
        },
        files_browser: {
            title: 'Fichier-Browser',
            subtitle: 'Sicht, filtréiert a verwalt all Uschloss deen iwwer déi ganz Organisatioun eropgelueden gouf.',
            filter_owner_type: 'Besëtzertyp',
            filter_uploader: 'Eropgelueden vun',
            filter_from: 'Eropgelueden no',
            filter_to: 'Eropgelueden virun',
            filter_size_min: 'Min. Gréisst (MiB)',
            filter_size_max: 'Max. Gréisst (MiB)',
            filter_mime: 'Fichier-Typ',
            filter_deleted_state: 'Läschestatus',
            filter_deleted_any: 'All Fichieren',
            filter_deleted_only: 'Nëmme geläscht',
            filter_deleted_exclude: 'Geläscht ausschléissen',
            filter_orphaned: 'Nëmme verwaist',
            filter_search_placeholder: 'Fichier-Numm sichen…',
            filter_apply: 'Filteren uwenden',
            filter_clear: 'Filtere läschen',
            col_filename: 'Fichier-Numm',
            col_owner_type: 'Besëtzertyp',
            col_owner_entity: 'Besëtzer',
            col_uploader: 'Eropgelueden vun',
            col_uploaded_at: 'Eropgelueden',
            col_size: 'Gréisst',
            col_mime: 'Typ',
            col_state: 'Status',
            col_actions: 'Aktiounen',
            state_active: 'Aktiv',
            state_deleted: 'Geläscht',
            state_orphaned: 'Verwaist',
            action_view_detail: 'Detailer weisen',
            action_download: 'Eroflueden',
            action_ai_edits: 'KI-Ännerungen',
            action_soft_delete: 'Mëll läschen',
            action_restore: 'Restauréieren',
            action_hard_delete: 'Definitiv läschen',
            bulk_soft_delete: 'Ausgewielter mëll läschen',
            bulk_export_csv: 'CSV exportéieren',
            bulk_select_all: 'Alles auswielen',
            reason_label: 'Grond',
            reason_min_10_chars: "E Grond ass erfuerderlech (op d'mannst 10 Zeechen)",
            reason_min_20_chars: "E Grond ass erfuerderlech (op d'mannst 20 Zeechen)",
            legal_hold_override_label: 'Gesetzlech Opbewahrung iwwerschreiwen',
            legal_hold_override_warning: 'Dëse Fichier ass ënner gesetzlecher Opbewahrung. Säi definitivt Läsche gëtt an der Audit-Spur mat Ärem Grond an dem Iwwerschreiwungs-Markéierer registréiert.',
            empty_state_title: 'Keng Fichiere passen op Är Filteren',
            empty_state_body: "Passt Är Filteren un fir Resultater ze gesinn, oder läscht se fir all d'Fichieren ze weisen.",
            pagination_prev: 'Zréck',
            pagination_next: 'Weider',
            pagination_page: 'Säit {page} vun {total}',
            pagination_size_label: 'Pro Säit',
            pagination_total: 'Ganzen: {count}',
            toast_soft_deleted: 'Fichier mëll geläscht',
            toast_restored: 'Fichier restauréiert',
            toast_hard_deleted: 'Fichier definitiv geläscht',
            toast_bulk_deleted: '{count} Fichier(en) geläscht',
            toast_error: 'Aktioun feelgeschloen',
            confirm_soft_delete_title: 'Fichier mëll läschen',
            confirm_restore_title: 'Fichier restauréieren',
            confirm_hard_delete_title: 'Fichier definitiv läschen',
            confirm_bulk_soft_delete_title: 'Ausgewielt Fichiere mëll läschen',
            confirm_submit: 'Bestätegen',
            confirm_cancel: 'Ofbriechen',
            col_view_entity: 'View',
        },
        files_detail: {
            modal_title: 'Fichier-Detailer',
            close: 'Zoumaachen',
            meta_filename: 'Fichier-Numm',
            meta_size: 'Gréisst',
            meta_mime: 'Typ',
            meta_owner_type: 'Besëtzertyp',
            meta_owner_entity: 'Besëtzer-Entitéit',
            meta_uploader: 'Eropgelueden vun',
            meta_uploaded_at: 'Eropgelueden',
            meta_storage_path: 'Späicherwee',
            meta_deleted_state: 'Läschestatus',
            audit_section_title: 'Audit-Spur',
            audit_load_more: 'Méi Evenementer lueden',
            audit_empty: 'Keng Audit-Evenementer fir dëse Fichier registréiert.',
            audit_config_changed: 'Konfiguratioun geännert',
            audit_soft_deleted: 'Mëll geläscht',
            audit_restored: 'Restauréiert',
            audit_hard_deleted: 'Definitiv geläscht',
            audit_downloaded: 'Erofgelueden',
            audit_quota_breached: 'Quotaiwwerschreiwung versicht',
            audit_owner_type_download_denied: 'Ofruff refuséiert',
            /*
             * The audit row as ONE composed string, not three JSX fragments glued
             * with English.
             *
             * The row used to render `{' by '}` and `{' on '}` as literals, so every
             * locale read "Endgültig gelöscht by Anna Schmidt on 11. Apr 2026" — the
             * event label and the actor came from `t` and the user map, the
             * connective words did not. A locale that orders these differently
             * cannot be rendered correctly by concatenation at all, which is why
             * this is a template rather than two `by`/`on` keys.
             *
             * `{actor}` is a link, so the page splits this on the placeholders and
             * substitutes nodes rather than calling `.replace`.
             */
            audit_row: '{event} vum {actor} den {at}',
            action_download: 'Eroflueden',
            action_soft_delete: 'Mëll läschen',
            action_restore: 'Restauréieren',
            action_hard_delete: 'Definitiv läschen',
            effective_settings_title: 'Effektiv Politik',
            effective_max_file_size: 'Maximal Fichier-Gréisst',
            effective_retention_days: 'Opbewahrung',
            effective_legal_hold: 'Gesetzlech Opbewahrung',
            effective_legal_hold_yes: 'Aktiv',
            effective_legal_hold_no: 'Keng',
        },
        files_dashboard: {
            title: 'Fichier-Dashboard',
            subtitle: 'Späichernotzung, Wuesstem a verwaist Fichieren iwwer déi ganz Organisatioun.',
            kpi_total_files: 'Total Fichieren',
            kpi_total_size: 'Total Späicherung',
            kpi_near_quota: 'Quotanotzung',
            kpi_orphaned: 'Verwaist Fichieren',
            chart_by_owner_type: 'Fichiere pro Besëtzertyp',
            chart_growth_30d: 'Wuesstem op 30 Deeg',
            toggle_count: 'No Unzuel',
            toggle_size: 'No Gréisst',
            orphaned_panel_title: 'Verwaist Fichieren (Top 20)',
            orphaned_view_all: 'All verwaist Fichieren weisen',
            orphaned_empty: 'Keng verwaist Fichiere fonnt.',
            growth_empty: 'Keng Eropluede-Aktivitéit an de leschten 30 Deeg.',
            retention_n_days: '{days} Dag/Deeg',
            retention_none: 'Keng Politik',
        },
    },
    category_labels: {
        'user-guide': 'User Guide',
        'admin-guide': 'Admin Guide',
        'getting-started': 'Ugefaangen',
        faq: 'FAQ',
    },
    tag_labels: {
        faq: 'FAQ',
        dsar: 'DSAR',
        dsr: 'DSR',
        cmp: 'CMP',
        hmac: 'HMAC',
        sso: 'SSO',
        mfa: 'MFA',
        rbac: 'RBAC',
        api: 'API',
        'api-keys': 'API Schlësselen',
        webhook: 'Webhook',
        webhooks: 'Webhooks',
        admin: 'Admin',
        aesirx: 'AesirX',
        purposes: 'Zwecker',
        preferences: 'Preferenzen',
        onboarding: 'Onboarding',
        'first-steps': 'Éischt Schrëtt',
        login: 'Login',
        navigation: 'Navigatioun',
        users: 'Benotzer',
        user: 'Benotzer',
        roles: 'Rollen',
        role: 'Roll',
        'access-control': 'Zougang Kontroll',
        'access-management': 'Zougang Management',
        permissions: 'Permissiounen',
        permission: 'Erlaabnis',
        consent: 'Zoustëmmung',
        'consent-management': 'Zoustëmmung Management',
        privacy: 'Privatsphär',
        pdpl: 'PDPL',
        gdpr: 'GDPR',
        iso27001: 'ISO 27001',
        audit: 'Audit',
        auditing: 'Audit',
        breach: 'Verstouss',
        incident: 'Tëschefall',
        incidents: 'Tëschefäll',
        notification: 'Notifikatioun',
        notifications: 'Notifikatiounen',
        reporting: 'Berichterstattung',
        risk: 'Risiko',
        vendor: 'Verkeefer',
        vendors: 'Verkeefer',
        'vendor-risk': 'Verkeefer Risiko',
        assessment: 'Bewäertung',
        assessments: 'Bewäertungen',
        dpia: 'DPIA',
        'cross-border': 'Grenziwwerschreidend',
        transfer: 'Transfert',
        transfers: 'Iwwerweisungen',
        deletion: 'Läschen',
        retention: 'Retention',
        'data-mapping': 'Data Mapping',
        discovery: 'Entdeckung',
        classification: 'Klassifikatioun',
        workflow: 'Workflow',
        workflows: 'Workflows',
        template: 'Schabloun',
        templates: 'Schablounen',
        catalog: 'Katalog',
        monitoring: 'Iwwerwachung',
        tasks: 'Aufgaben',
        task: 'Aufgab',
        'task-management': 'Aufgab Management',
        organizations: 'Organisatiounen',
        org: 'Organisatioun',
        governance: 'Gouvernance',
        compliance: 'Konformitéit',
        forms: 'Formen',
        form: 'Form',
        connectors: 'Connectoren',
        connector: 'Connector',
        integration: 'Integratioun',
        integrations: 'Integratiounen',
        security: 'Sécherheet',
        encryption: 'Verschlësselung',
        backup: 'Backupsatellit',
        recovery: 'Erhuelung',
        maintenance: 'Ënnerhalt',
        settings: 'Astellungen',
        configuration: 'Configuratioun',
        sla: 'SLA',
        escalation: 'Eskalatioun',
        remediation: 'Sanéierung',
        evidence: 'Beweiser',
        'case-management': 'Fall Gestioun',
        profile: 'Profil',
        delegation: 'Delegatioun',
        'service-accounts': 'Service Konten',
        license: 'Lizenz',
        'license-management': 'Lizenz Management',
        plan: 'Plang',
        plans: 'Pläng',
        dashboard: 'Dashboard',
        'web-consent': 'Web Zoustëmmung',
        'partner-marketplace': 'Partner Maartplaz',
        partner: 'Partner',
        partners: 'Partner',
        deliverables: 'Liwwerungen',
        'service-tiers': 'Service Tiers',
        'support-tickets': 'Ënnerstëtzung Ticketen',
        'audit-trail': 'Audit Trail',
        'first-time-setup': 'Éischt-Zäit Setup',
        setup: 'Setup',
        quickstart: 'Quick Start',
        tour: 'Tour',
        tours: 'Touren',
        'sample-data': 'Prouf Daten',
        'data-subject': 'Donnéeën Sujet',
        'data-subjects': 'Donnéeën Sujeten',
        pseudonymous: 'Pseudonym',
        identified: 'Identifizéiert',
        'help-center': 'Hëllef Zentrum',
        article: 'Artikel',
        articles: 'Artikelen',
        search: 'Sichen',
        'platform-settings': 'Plattform Astellunge',
        'platform-config': 'Plattform Configuratioun',
        'module-config': 'Modul Configuratioun',
        'module-configuration': 'Modul Configuratioun',
        'rights-requests': 'Rechter Ufroen',
        'access-accountability': 'Zougang Rechenschaftspflicht',
        'data-discovery': 'Daten Entdeckung',
        'incident-ops': 'Tëschefall Operatiounen',
        'deletion-orchestration': 'Läschen Orchestratioun',
        'compliance-forms': 'Konformitéit Formen',
        'program-governance': 'Programm Gouvernance',
        localization: 'Lokalisatioun',
        'privacy-scanner': 'Privatsphär Scanner',
        'consent-properties': 'Zoustëmmung Properties',
        acceptances: 'Akzeptanz',
        access: 'Zougang',
        'access-review': 'Zougang Kritik',
        'access-reviews': 'Zougang Rezensiounen',
        accountability: 'Rechenschaftspflicht',
        acknowledgment: 'Unerkennung',
        'action-plans': 'Aktioun Pläng',
        activation: 'Aktivéierung',
        activations: 'Aktivatiounen',
        'active-response': 'Aktiv Äntwert',
        activity: 'Aktivitéit',
        adequacy: 'Adequatitéit',
        'admin-guide': 'Admin Guide',
        aggregation: 'Aggregatioun',
        agreements: 'Accorden',
        ai: 'AI',
        alerts: 'Alarmer',
        analytics: 'Analytics',
        anchoring: 'Verankerung',
        approval: 'Genehmegung',
        approvals: 'Genehmegungen',
        apps: 'Apps',
        assets: 'Verméigen',
        assignments: 'Aufgaben',
        attestation: 'Attestatioun',
        'audit-log': 'Audit Log',
        'audit-pack': 'Audit Pack',
        authentication: 'Authentifikatioun',
        authority: 'Autoritéit',
        'auto-play': 'Auto-Spill',
        automated: 'Automatiséiert',
        automation: 'Automatisatioun',
        baseline: 'Baseline',
        batches: 'Chargen',
        bidirectional: 'Bidirektional',
        bilingual: 'Zweesproocheg',
        bindings: 'Bindungen',
        blockchain: 'Blockchain',
        board: 'Verwaltungsrot',
        brand: 'Mark',
        brds: 'BRDs',
        'break-glass': 'Break-Glass',
        builder: 'Builder',
        bulk: 'Bulk',
        'bulk-sync': 'Bulk Sync',
        bundles: 'Bundles',
        calendar: 'Kalenner',
        campaigns: 'Campagnen',
        cases: 'Fäll',
        categories: 'Kategorien',
        category: 'Kategorie',
        certification: 'Zertifikatioun',
        chain: 'Kette',
        change: 'Änneren',
        'change-awareness': 'Bewosstsinn änneren',
        'change-log': 'Ännere Log',
        'change-management': 'Changement Management',
        'change-requests': 'Ännerung Demanden',
        'cmp-validation': 'CMP Validatioun',
        collection: 'Kollektioun',
        comparison: 'Verglach',
        completeness: 'Vollständegkeet',
        completion: 'Fäerdegstellung',
        'compliance-posture': 'Konformitéit Haltung',
        'compliance-proof': 'Konformitéit Beweis',
        'compliance-reviews': 'Konformitéit Rezensiounen',
        config: 'Config',
        confirmation: 'Bestätegung',
        conflicts: 'Konflikter',
        consolidation: 'Konsolidéierung',
        continuous: 'Kontinuéierlech',
        contracts: 'Kontrakter',
        controls: 'Kontrollen',
        cookies: 'Cookien',
        cooperation: 'Zesummenaarbecht',
        coordination: 'Koordinatioun',
        'corporate-governance': 'Corporate Governance',
        'corrective-actions': 'Korrigéierend Aktiounen',
        coverage: 'Ofdeckung',
        'credential-vault': 'Umeldungsinformatioun Vault',
        credentials: 'Umeldungsinformatiounen',
        cycles: 'Cyclen',
        dashboards: 'Dashboards',
        'data-assets': 'Daten Verméigen',
        'data-flows': 'Daten Flows',
        'data-sensitivity': 'Daten Sensibilitéit',
        'data-steward': 'Daten Steward',
        deadlines: 'Termin',
        'decree-356': 'Dekret 356',
        dedup: 'Dedup',
        definitions: 'Definitiounen',
        deliverable: 'Liwwerbar',
        departments: 'Departementer',
        'dependency-order': 'Ofhängegkeet Uerdnung',
        deployment: 'Détachement',
        detection: 'Detektioun',
        diff: 'Diff',
        'digital-properties': 'Digital Properties',
        dimensions: 'Dimensiounen',
        disable: 'Desaktivéieren',
        'disaster-recovery': 'Katastroph Erhuelung',
        disclosure: 'Offenbarung',
        documentation: 'Dokumentatioun',
        documents: 'Dokumenter',
        domains: 'Domains',
        dossier: 'Dossier',
        dpa: 'DPA',
        drift: 'Drift',
        'drift-detection': 'Drift Detectioun',
        'due-diligence': 'Due Diligence',
        email: 'Email',
        emergency: 'Noutfall',
        enable: 'Aktivéieren',
        'enterprise-map': 'Enterprise Kaart',
        erasure: 'Läschen',
        evaluation: 'Evaluatioun',
        'event-bus': 'Event Bus',
        exceptions: 'Ausnahmen',
        execution: 'Ausféierung',
        executive: 'Exekutiv',
        exercises: 'Übungen',
        export: 'Export',
        exports: 'Exporter',
        filing: 'Areeche',
        filter: 'Filter',
        filtering: 'Filteren',
        findings: 'Befunde',
        flows: 'Flëss',
        'follow-up': 'Suivi',
        forensic: 'Forensesch',
        forensics: 'Forensik',
        'form-filler': 'Form Filler',
        framework: 'Kader',
        frameworks: 'Kaderen',
        'gap-analysis': 'Gap Analyse',
        'gap-report': 'Gap Rapport',
        gaps: 'Lücken',
        gates: 'Gates',
        'getting-started': 'Ugefaangen',
        'golden-templates': 'Golden Schablounen',
        grc: 'GRC',
        'group-management': 'Group Management',
        groups: 'Gruppen',
        'guided-tours': 'Visite guidée',
        guides: 'Guiden',
        handling: 'Ëmgank',
        'hands-on': 'Hands-On',
        health: 'Gesondheet',
        help: 'Hëllef',
        hierarchy: 'Hierarchie',
        history: 'Geschicht',
        i18n: 'i18n',
        iam: 'IAM',
        'identity-provider': 'Identitéit Provider',
        implementation: 'Ëmsetzung',
        import: 'Import',
        improvement: 'Verbesserung',
        'incident-response': 'Tëschefall Äntwert',
        inconsistencies: 'Inkonsistenz',
        instances: 'Instanzen',
        intake: 'Intake',
        'intake-key': 'Intake Schlëssel',
        interactive: 'Interaktiv',
        international: 'International',
        inventory: 'Inventar',
        investigation: 'Enquête',
        investigations: 'Ermëttlungen',
        'ip-allowlist': 'IP Erlaabnes Lëscht',
        issues: 'Problemer',
        justification: 'Justifikatioun',
        justifications: 'Justifikatiounen',
        kanban: 'Kanban',
        keyboard: 'Keyboard',
        keys: 'Schlësselen',
        'knowledge-base': 'Wëssen Base',
        koncernstyring: 'Corporate Governance',
        kpi: 'KPI',
        labeling: 'Etikettéieren',
        language: 'Sprooch',
        'language-packs': 'Sprooch Packs',
        languages: 'Sproochen',
        lawfulness: 'Gerechtegkeet',
        layout: 'Layout',
        'legal-basis': 'Juristesch Basis',
        'lessons-learned': 'Lektioune geléiert',
        levels: 'Niveauen',
        library: 'Bibliothéik',
        lifecycle: 'Liewenszyklus',
        lineage: 'Lineage',
        links: 'Linken',
        listings: 'Oplëschtungen',
        live: 'Live',
        locale: 'Lokal',
        log: 'Log',
        logging: 'Logged',
        'managed-service': 'Gestioun Service',
        mapping: 'Mapping',
        mappings: 'Mappings',
        'market-packs': 'Maart Packs',
        marketplace: 'Maartplaz',
        maturity: 'Maturitéit',
        metrics: 'Metriken',
        models: 'Modeller',
        module: 'Modul',
        modules: 'Moduler',
        mps: 'MPS',
        'multi-factor': 'Multi-Faktor',
        'multi-market': 'Multi-Maart',
        'multi-org': 'Multi-Org',
        'multi-property': 'Multi-Property',
        'my-tasks': 'Meng Aufgaben',
        obligations: 'Obligatiounen',
        offboarding: 'Offboarding',
        oidc: 'OIDC',
        orchestration: 'Orchestratioun',
        'org-groups': 'Org Gruppen',
        organization: 'Organisatioun',
        overdue: 'Iwwerhaapt',
        overview: 'Iwwersiicht',
        'owner-review': 'Besëtzer Kritik',
        package: 'Package',
        packages: 'Packagen',
        packs: 'Päck',
        pagination: 'Paginatioun',
        'parent-child': 'Elteren-Kand',
        patterns: 'Musteren',
        performance: 'Leeschtung',
        periodic: 'Periodesch',
        periods: 'Perioden',
        personal: 'Perséinlech',
        'personal-data': 'Perséinlech Donnéeën',
        personas: 'Personnagen',
        planning: 'Planung',
        platform: 'Plattform',
        playbooks: 'Spillbicher',
        poc: 'PoC',
        'point-in-time': 'Punkt-an-Zäit',
        policies: 'Politiken',
        portfolio: 'Portfolio',
        'post-incident': 'Post-Tëschefall',
        'power-user': 'Power Benotzer',
        practice: 'Praxis',
        pricing: 'Präisser',
        'privacy-impact': 'Privatsphär Impakt',
        procedures: 'Prozeduren',
        processes: 'Prozesser',
        processing: 'Veraarbechtung',
        'processing-activities': 'Veraarbechtung Aktivitéiten',
        procurement: 'Beschaffung',
        productivity: 'Produktivitéit',
        profiles: 'Profiler',
        programs: 'Programmer',
        progress: 'Fortschrëtt',
        proof: 'Beweis',
        propagation: 'Verbreedung',
        properties: 'Eegeschaften',
        'pub-sub': 'Pub-Sub',
        'public-form': 'Ëffentlech Form',
        quality: 'Qualitéit',
        questionnaires: 'Questionnaires',
        'quick-links': 'Quick Linken',
        'quick-start': 'Quick Start',
        'quick-starts': 'Quick Starts',
        raci: 'RACI',
        readiness: 'Bereetschaft',
        'real-time': 'Echtzäit',
        reassessments: 'Nei Bewäertungen',
        reconciliation: 'Reconciliatioun',
        record: 'Rekord',
        records: 'Records',
        recurring: 'Widderhuelend',
        register: 'Aschreiwen',
        registry: 'Registry',
        regulations: 'Reglementer',
        regulator: 'Regulateur',
        regulatory: 'Regulatioun',
        'regulatory-frameworks': 'Regulatiounskader',
        remap: 'Remap',
        reminders: 'Erënnerungen',
        reports: 'Rapporten',
        requests: 'Ufroen',
        requirements: 'Ufuerderunge',
        response: 'Äntwert',
        'response-plans': 'Äntwert Pläng',
        'response-team': 'Äntwert Team',
        'response-templates': 'Äntwert Schablounen',
        'response-time': 'Äntwert Zäit',
        responses: 'Äntwerten',
        responsibility: 'Responsabilitéit',
        results: 'Resultater',
        review: 'Iwwerpréiwung',
        reviews: 'Rezensiounen',
        'right-to-delete': 'Recht ze läschen',
        rights: 'Rechter',
        'risk-tiers': 'Risiko Niveauen',
        rollouts: 'Rollouts',
        rollup: 'Rollup',
        ropa: 'ROPA',
        routing: 'Routing',
        rpo: 'RPO',
        rto: 'RTO',
        rules: 'Regelen',
        runs: 'Lafen',
        saml: 'SAML',
        scan: 'Scannen',
        scanning: 'Scannen',
        scc: 'SCC',
        schedule: 'Zäitplang',
        scheduled: 'Fuerplang',
        schedules: 'Zäitplang',
        scheduling: 'Zäitplang',
        scores: 'Partituren',
        scoring: 'Score',
        scripts: 'Scripten',
        'security-reviews': 'Sécherheet Rezensiounen',
        'sensitive-data': 'Sensibel Donnéeën',
        sensitivity: 'Sensibilitéit',
        'separation-of-duties': 'Trennung vun Flichten',
        service: 'Service',
        sessions: 'Sessiounen',
        'setup-wizard': 'Setup Wizard',
        severity: 'Gravitéit',
        'shared-governance': 'Gemeinsam Gouvernance',
        'shared-settings': 'Shared Astellungen',
        shortcuts: 'Ofkiirzungen',
        sidebar: 'Sidebar',
        signals: 'Signaler',
        'single-sign-on': 'Single Sign-On',
        snapshots: 'Snapshots',
        sod: 'SoD',
        sorting: 'Sortéieren',
        sources: 'Quellen',
        staging: 'Inszenéierung',
        standards: 'Standarden',
        status: 'Status',
        structure: 'Struktur',
        'sub-processor': 'Sub-Prozessor',
        'sub-processors': 'Ënner-Veraarbechter',
        submissions: 'Soumissioun',
        subprocessors: 'Ënnerprozessoren',
        subscription: 'Abonnement',
        suggestions: 'Virschléi',
        summaries: 'Zesummefaassungen',
        supplements: 'Ergänzunge',
        support: 'Ënnerstëtzung',
        sync: 'Synchroniséiert',
        system: 'System',
        systems: 'Systemer',
        tables: 'Dëscher',
        tagging: 'Tagging',
        'tamper-proof': 'Tamper-Proof',
        taxonomies: 'Taxonomien',
        taxonomy: 'Taxonomie',
        tenants: 'Locataire',
        termination: 'Terminatioun',
        'third-party': 'Drëtte-Partei',
        'ticket-sync': 'Ticket Sync',
        tickets: 'Ticketen',
        tiers: 'Tiers',
        tokens: 'Stongen',
        totp: 'TOTP',
        traceability: 'Traceabilitéit',
        tracking: 'Tracking',
        trail: 'Trail',
        training: 'Training',
        translation: 'Iwwersetzung',
        translations: 'Iwwersetzungen',
        transparency: 'Transparenz',
        trends: 'Trends',
        unified: 'Vereenegt',
        updates: 'Aktualiséierungen',
        validation: 'Validatioun',
        vault: 'Vault',
        'vendor-requests': 'Verkeefer Ufroen',
        verification: 'Verifikatioun',
        versioning: 'Versionéierung',
        versions: 'Versiounen',
        vietnam: 'Vietnam',
        violations: 'Violatioune',
        visualization: 'Visualiséierung',
        waivers: 'Verzichten',
        'web-records': 'Web Records',
        website: 'Websäit',
        websites: 'Websäiten',
        weights: 'Gewiichter',
        'white-label': 'White-Label',
        withdrawal: 'Réckzuch',
    },
    module_labels: {
        // Sprint 63.25.0 (spec G2) — the source modules that had no label.
        compliance_rules: 'Compliance Rules',
        access_review: 'Access Reviews',
        consolidation: 'Consolidation',
        case_audit: 'Module Audit Trail',
        rights_requests: 'Ufroe fir Rechter',
        data_mapping: 'Dateninventaire',
        governance: 'Gouvernance',
        consent: 'Zoustëmmungs-Gouvernance',
        consent_properties: 'Zoustëmmungseegeschafte',
        assessments: 'DPIA & Evaluatiounen',
        classification: 'Dateklassifikatioun',
        monitoring: 'Iwwerwaachungsprogrammer',
        program: 'Iwwerwaachungsprogramm',
        incidents: 'Tëschefall-Äntwert',
        connectors: 'Konnektoren',
        vendor_risk: 'Verkeefer Risiko',
        deletion_orchestration: 'Läschorchestréierung',
        discovery: 'Dateentdeckung',
        incident_ops: 'Tëschefalloperatiounen',
        program_governance: 'Programmsteierung',
        access_accountability: 'Zougangsverantwortung',
        localization: 'Lokaliséierung',
        sector_overlays: 'Sector Overlays',
        partner_workspace: 'Partner-Aarbechtsberäich',
        privacy_scanner: 'Dateschutz-Scanner',
        onboarding: 'Onboarding',
        compliance_forms: 'Compliance-Formulairen',
        audit_trail: 'Audit-Protokoll',
        data_subjects: 'Betraffe Persounen',
        organizations: 'Organisatiounen',
        task_management: 'Aufgab Management',
        deletion_orch: 'Läschorchestréierung',
        forms: 'Compliance-Formulairen',
        partner_marketplace: 'Partner-Maartplaz',
        dashboard: 'Iwwersiicht',
        platform_settings: 'Plattformastellungen',
        help_center: 'Hëllef-Zentrum',
    },
    module_descriptions: {
        rights_requests: 'Verwalte Ufroen vu Betraffenen op Rechter, inklusiv Zougang, Läschung an Portabilitéit. Verfolleg de Liewenszyklus vun Ufroen, weist Handler zou a halt regelméisseg Fristen an.',
        data_mapping: 'Kartéier Dateflëss an Ärer Organisatioun. Verfolleg Veraarbechtungsaktivitéiten, Datekategorien, Zwecker, Retentiounspolitiken an Systemintegratiounen.',
        governance: "Iwwerwaachung an d'Beweiser dohannert. Verwalt de Fournisseursregëster a säi Liewenszyklus, genehmegt d'Entscheedungen, déi dëse Modul kontrolléiert, iwwerwaacht Compliance-Ofwäichungen an halt d'Registere fir Vertrauen an Identitéit, reglementaresch Artefakten, Bezuelungen an eKYC, a Plattformbeweiser.",
        consent: 'Verwalte Zoustëmmungssammlung, -späicherung an -zréckzéiung. Verfolleg Zoustëmmungsrecorden, Zwecker an Integratioun mat Zoustëmmungsmanagement-Plattformen.',
        consent_properties: 'Konfiguréier Zoustëmmungseigenschaften, Gruppen a gëllen Schablounen. Definéier widderverwäertbar Zoustëmmungskonfiguratiounen fir eng konsistent Deployment.',
        assessments: 'Féier Dateschutz-Folgenabschätzungen (DPIA) an Dateschutzrisiko-Evaluatiounen duerch. Evaluéier Veraarbechtungsaktivitéiten op Compliance-Risiken.',
        classification: 'Klassiféier Dateressourcen no Sensibilitéitsniveau. Applizéier Taxonomie-Labelen, verfolleg Liewenszyklus-Recorden an identifizéier Klassifikatiounslücken.',
        monitoring: "Setzt Iwwerwaachungsprogrammer op fir Compliance-Metriken, Schlësselrisikoindikateuren an d'Effektivitéit vu Kontrollen am Laf vun der Zäit ze verfolgen.",
        incidents: 'Mellt a verwalte Dateschutz-Tëschefäll an Datebrëch. Verfolleg Ermëttlungs-, Notifikatiounen- an Behiewungsaarbechtsflëss.',
        connectors: 'Konfiguréier Datequell-Konnektoren an Integratiounen. Verbannt extern Systemer fir automatiséiert Dateentdeckung an Iwwerwaachung.',
        vendor_risk: 'Evaluéier a verfolleg Risike vu Drëttubidder-Fournisseuren. Bewert Fournisseuren, verfolleg Risikominderung a verwalte Fournisseur-Risikokonfiguratiounen.',
        deletion_orchestration: "Orchestréier d'Läsche vu Daten iwwer Systemer. Verfolleg Läschuffroen, verifizéier Beweisketten a garantéier komplett Dateläschung.",
        discovery: 'Entdeckt perséinlech Daten iwwer verbonne Systemer. Scannt Datequellen fir en ëmfaassend Dateinventaire opzebauen.',
        incident_ops: "Operationellt Tëschefallmanagement mat Playbooks, Eskalatiounsaarbechtsflëss a SLA-Tracking fir d'Léisung vun Tëschefäll.",
        program_governance: 'Verfolleg déi allgemeng Reife vum Dateschutzprogramm, Compliance-Scorer, Beweislücken an Executive-Reporting iwwer Är ganz Organisatioun.',
        access_accountability: 'Ermëttelt an iwwerpréift Zougangsmuster vun Benotzer. Verfolleg Zougangskonfiguratiounen, iwwerwaacht verdächteg Aktivitéit a setzt Verantwortlechkeet duerch.',
        localization: 'Installéier a verwalte Maart-Lokaliséierungspaketen. Füügt regelméisseg Cadreen, Sproochpaketen an regiounsspezifesch Compliance-Inhalt derbäi.',
        sector_overlays: 'Install and manage optional sector overlays, and open the sector registers an activated overlay brings into use, organised by sector. Available in the Enterprise package.',
        partner_workspace: 'Schaffe mat externen Partner zesummen. Deelt Compliance-Daten, verfolleg Partner-Obligatiounen a verwalte Partner-Bezéiungen.',
        privacy_scanner: "Scannt Websäiten op Dateschutz-Compliance-Problemer. Iwwerwaacht Cookie-Benotzung, Tracking-Skripten an d'Präsenz vu Dateschutzrichtlinnen an Echtzäit.",
        onboarding: "Féiert nei Organisatiounen duerch d'Plattformastellung. Schrëtt-fir-Schrëtt Onboarding-Assistent fir déi initial Konfiguratioun an den Dateimport.",
        compliance_forms: 'Erstellt a verwalte Compliance-Formulairen aus engem Schablounekatalog. Fëllt regelméisseg Formulairen aus, verfolleg Soumissiounen a generéiert Rapporten.',
        audit_trail: 'Kuckt e kompletten Audit-Protokoll vun alle Systemaktiounen. Verfolleg wien wat gemaach huet, wéini a wou iwwer all Moduler.',
        organizations: 'Verwalte Organisatiounen a Multi-Tenant-Astellungen. Konfiguréier Organisatiounsprofiler, Hierarchien a Cross-Org-Bezéiungen.',
    },
    privacy_scanner: {
        anomaly_explain: {
            explain_label: 'Anomalien erklären',
            explaining: 'Erklären…',
            results_heading: 'Anomalie Erklärungen ({severity})',
            tokens_label: '≈ {count} Tokens',
            items_label: '{count} Artikelen',
            remediation_label: 'Sanéierung Schrëtt',
            items_detail_label: 'Betraff Elementer',
            accept_label: 'Akzeptéieren Erklärung',
            reject_label: 'Erklärung refuséieren',
            error_generic: "Konnt d'Anomalie net erklären. Probéiert nach eng Kéier oder kontrolléiert datt de Scan erfollegräich ofgeschloss ass.",
        },
        cookie_insight: {
            insight_label: 'Cookie & Tracker AI Asiicht',
            producing: 'Produzéiert Asiicht…',
            results_heading: 'Cookie & Tracker Asiicht ({severity})',
            tokens_label: '≈ {count} Tokens',
            items_label: '{count} Artikelen',
            classification_label: 'Klassifikatioun',
            severity_label: 'Gravitéit',
            banner_copy_label: 'Proposéiert Bannerkopie',
            retention_label: 'Retention Rot',
            citations_label: 'Zitater',
            kind_cookie: 'Cookie',
            kind_tracker: 'Tracker',
            class_necessary: 'Noutwendeg',
            class_analytics: 'Analytics',
            class_marketing: 'Marketing',
            class_social: 'Sozial',
            accept_label: 'Akzeptéieren Asiicht',
            reject_label: 'Refusen Asiicht',
            no_items: 'Keng Cookien oder Tracker goufen an dësem Scan festgestallt.',
            error_generic: 'Konnt de Cookie Asiicht net produzéieren. Probéiert nach eng Kéier oder kontrolléiert datt de Scan erfollegräich ofgeschloss ass.',
        },
        monitoring: {
            title: 'Dateschutz-Iwwerwaachung',
            new_scan: 'Neie Scan',
            scan_url: 'URL fir ze scannen',
            scan_frequency: 'Scan-Frequenz',
            last_scan: 'Leschte Scan',
            score: 'Dateschutz-Score',
            issues: 'Problemer fonnt',
            history: 'Scan-Geschicht',
            settings: 'Iwwerwaachungs-Astellungen',
        },
        real_time: {
            title: 'Echtzäit-Scanner',
            scan_now: 'Elo scannen',
            scanning: 'Gëtt gescannt...',
            results: 'Scan-Resultater',
            no_results: 'Nach keng Scan-Resultater.',
            cookies: 'Cookien',
            trackers: 'Tracker',
            consent_banners: 'Zoustëmmungs-Banneren',
            third_party: 'Drëttubidder-Skripten',
            archive: 'Resultater-Archiv',
            settings: 'Scanner-Astellungen',
        },
        dashboard: {
            page_title: 'Privatsphär Iwwerwachung Dashboard',
            privacy_monitoring: 'Privatsphär Iwwerwachung',
            plan_label: '{package} Plang',
            expires_label: 'expiréiert {date}',
            settings: 'Astellungen',
            refresh: 'Erfrëschen',
            refreshing_dashboard: 'Erfrëschend Iwwerwachung Dashboard',
            refresh_aria: 'Iwwerwachung Dashboard erfrëschen',
            not_configured_title: 'Privatsphär Iwwerwaachung net konfiguréiert',
            not_configured_desc: "Connect Äre AesirX Privatsphär Iwwerwachung Service fir d'Verfollegung vun der Websäit Konformitéit ze starten.",
            configure_monitoring: 'Configuréieren Iwwerwachung',
            license_expired_title: 'Lizenz ofgelaaf',
            license_expired_desc: 'Är AesirX Privatsphär Iwwerwachung Lizenz ass ofgelaf. W.e.g. erneiert et fir Ënnerbriechung vum Service ze vermeiden.',
            compliance_score: 'Konformitéit Score',
            cookies_detected: 'Cookien entdeckt',
            trackers_label: 'Trackers',
            open_issues: 'Oppen Emissiounen',
            score_good: 'Gutt',
            score_needs_improvement: 'Braucht Verbesserung',
            score_critical: 'Kritesch',
            action_required: 'Aktioun néideg',
            cookies_sr: 'Cookien entdeckt',
            trackers_sr: 'Tracker fonnt',
            open_issues_sr: 'oppen Themen',
            action_required_sr: 'Aktioun néideg',
            metric_label: 'Metresch',
            value_label: 'Wäert',
            status_label: 'Status',
            aria_metrics: 'Privatsphär Iwwerwachung Metriken',
            plan_limit_warning: 'Scannen URL: {current}/{max} - Dir hutt Är Planglimit erreecht.',
            manage_domains: 'Verwalte Domainen oder upgrade Äre Plang.',
            monitored_domains: 'Iwwerwaacht Domainen',
            domain_added: 'Domain {domain} dobäigesat',
            domain_removed: 'Domain {domain} geläscht',
            failed_add_domain: "D'Domain derbäigesat ass net fäerdeg",
            failed_remove_domain: 'Konnt net ewechhuelen Domain',
            failed_load: 'Ausgefall iwwerwaacht Dashboard ze lueden',
            new_domain_aria: 'Neit Domain URL',
            add_domain: 'Dobäizemaachen Domain',
            adding_domain: 'Füügt ...',
            no_domains: 'Keng iwwerwaacht Domainen nach konfiguréiert.',
            aria_domains_table: 'Iwwerwaacht Domainen',
            col_domain: 'Domain',
            col_compliance_score: 'Konformitéit Score',
            col_status: 'Status',
            col_cookies: 'Cookien',
            col_trackers: 'Trackers',
            col_issues: 'Problemer',
            col_last_scan: 'Last Scan',
            col_action: 'Aktioun',
            compliance_status_sr: 'Konformitéitsstatus',
            showing_chart: 'Weist Chart Vue',
            showing_table: 'Weist Daten Dësch Vue',
            loading_sr: 'Lueden Iwwerwachung Dashboard',
            col_framework_tags: 'Kader Tags',
            col_last_monitored: 'Last iwwerwaacht',
            framework_tags_sr: 'applicabel reglementaresche Kaderen',
            last_monitored_sr: 'kontinuéierlech Iwwerwachung Zäitstempel',
            audit_trail_heading: 'Audit Trail',
            audit_trail_info: 'All Iwwerwaachungsscan gëtt Zäitstempel a protokolléiert. Domain-Niveau Konformitéit Ännerungen produzéieren en onverännerbaren Auditrekord dee fir reglementaresch Iwwerpréiwung exportéiert ka ginn.',
            cybersecurity_law_info_heading: 'Cybersecurity Law Detection',
            cybersecurity_law_info: 'Iwwerwaacht Domaine ginn automatesch géint Cybersecurity Gesetz Ufuerderunge gepréift (zB Vietnam Cybersecurity Law, NIS2 Direktiv). Framework Tags weisen op wéi eng Reglementer gëllen. Themen ginn no reglementaresche Kader fir geziilte Sanéierung kategoriséiert.',
        },
        scan: {
            page_title: 'Echtzäit Scan',
            title: 'Echtzäit Privatsphär Scanner',
            settings: 'Astellungen',
            results_archive: 'Resultater Archiv',
            launch_scan: 'Start Scan',
            target_url: 'Zil URL',
            protocol_aria: 'Protokoll',
            url_required: 'URL ass erfuerderlech',
            url_whitespace: 'Huelt w.e.g. Wäissraum aus dem URL',
            url_invalid: 'Gitt w.e.g. e gültege Domain un (zB example.com)',
            add_subpages: 'Subpages addéieren',
            add_subpages_aria: 'Dobäizemaachen Ënnersäiten, {count} dobäigesat',
            start_scan: 'Start Scan',
            launching: 'Launch…',
            scan_launched: 'Scan erfollegräich lancéiert',
            scan_failed: 'Ausgefall fir de Scan ze starten',
            scan_progress: 'Scan Fortschrëtt',
            scanning_in_progress: 'Scannen amgaang…',
            scan_id: 'Scan ID',
            status_label: 'Status',
            status_unknown: 'unknown',
            progress_label: 'Fortschrëtt',
            pages_label: 'Säiten',
            refresh_status: 'Erfrëschen Status',
            new_scan: 'Neie Scan',
            scan_completed: 'Scan fäerdeg, Resultater verfügbar',
            failed_check_status: 'Ausgefall Scan Status ze kontrolléieren',
            scan_results: 'Scan Resultater',
            main_page: 'Haaptsäit',
            page_n: 'Säit {n}',
            result_pages_nav: 'Resultat Säiten',
            page_url: 'Säit URL',
            risk_score: 'Risiko Score',
            risk_score_sr: 'Risiko Score',
            cookies_label: 'Cookien',
            cookies_sr: 'Cookien entdeckt',
            scripts_label: 'Scripten',
            scripts_sr: 'Scripten entdeckt',
            issues_label: 'Problemer',
            issues_sr: 'Problemer fonnt',
            issues_found: 'Problemer fonnt',
            aria_issues_table: 'Probleemer fonnt beim Scan',
            col_severity: 'Gravitéit',
            col_category: 'Kategorie',
            col_title: 'Titel',
            col_remediation: 'Sanéierung',
            severity_sr: 'severity',
            scan_result_metrics: 'Scan Resultat Metriken',
            view_full_detail: 'View Full Detail',
            col_consent_category: 'Zoustëmmung Kategorie',
            consent_category_sr: 'Zoustëmmung Kategoriséierung',
            compliance_evidence_heading: 'Konformitéit Beweis',
            compliance_evidence_info: 'Scan Befunde ginn automatesch mat Konformitéitsfäll verbonnen. All Thema kann op eng Zoustëmmungskategorie verfollegt ginn (wesentlech, funktionell, Analyse, Marketing) a produzéiert Audit-prett Beweiser fir reglementaresch Iwwerpréiwung.',
            rule_config_heading: 'Regel Configuratioun',
            rule_config_info: 'Scan Regele sinn konfiguréierbar pro reglementaresche Kader. Cookie Kontrollen, Skript Detektioun, a Verkeeferanalyse kënnen iwwer Astellunge aktivéiert oder deaktivéiert ginn. Benotzerdefinéiert Reegele kënnen definéiert ginn fir Är Konformitéitsufuerderunge vun Ärer Organisatioun ze passen.',
            case_linkage_heading: 'Konformitéit Fall Verknëppung',
            case_linkage_info: "Themen, déi während Echtzäitscannen fonnt goufen, kënne mat Konformitéitsfäll fir Tracking a Sanéierung verbonne ginn. Navigéiert op d'Scan Resultat Detail Säit fir Fallassociatiounen ze gesinn.",
            subpages_modal_title: 'Subpages addéieren',
            subpages_modal_desc: 'Füügt bis zu 5 Ënnersäit URLen fir niewent dem Haaptdomän ze scannen.',
            subpage_n: 'Ënnersäit {n}',
        },
        result_detail: {
            page_title: 'Scan Resultat Detail',
            title: 'Scan Resultat',
            back_to_archive: 'Zréck op den Archiv',
            failed_load: 'Ausgefall Scan Resultat ze lued',
            loading_sr: 'Scanresultat lueden…',
            scan_id: 'Scan ID',
            target_url: 'Zil URL',
            status_label: 'Status',
            status_sr: 'status',
            started: 'Ugefaangen',
            completed: 'Ofgeschloss',
            pages_scanned: 'Säiten gescannt',
            https_label: 'HTTPS',
            highest_risk_score: 'héchste Risiko Score',
            most_cookies: 'Déi meescht Cookien',
            most_vendors: 'Déi meescht Drëtt Partei Verkeefer',
            most_issues: 'Déi meescht Themen',
            across_pages: 'iwwer Säiten',
            highest_values_aria: 'Héchste Wäerter op all Säiten',
            scanned_pages_nav: 'Gescannt Säiten',
            main_page: 'Haaptsäit',
            page_n: 'Säit {n}',
            page_url: 'Säit URL',
            risk_score: 'Risiko Score',
            overall_risk_sr: 'allgemeng Risiko Score',
            cookies_label: 'Cookien',
            cookies_sr: 'Cookien entdeckt',
            vendors_label: 'Drëtt Partei Verkeefer',
            vendors_sr: 'Drëtt Partei Ubidder entdeckt',
            issues_label: 'Problemer',
            issues_sr: 'Problemer fonnt',
            risk_breakdown_aria: 'Risiko Score Decompte',
            issues_found: 'Problemer fonnt',
            aria_issues_table: 'Probleemer fonnt beim Scan',
            col_severity: 'Gravitéit',
            col_category: 'Kategorie',
            col_title: 'Titel',
            col_remediation: 'Sanéierung',
            severity_sr: 'severity',
            cookies_heading: 'Cookien ({count})',
            aria_cookies_table: 'Cookien entdeckt',
            col_name: 'Numm',
            col_domain: 'Domain',
            col_path: 'Wee',
            col_secure: 'Séchert',
            col_httponly: 'Http Nëmmen',
            col_samesite: 'SameSite',
            vendors_heading: 'Drëtt Partei Verkeefer ({count})',
            aria_vendors_table: 'Drëtt Partei Ubidder entdeckt',
            col_hostname: 'Hostnumm',
            scripts_heading: 'Scripten / Beaconen ({count})',
            aria_scripts_table: 'Scripten a Beaconen entdeckt',
            col_url: 'URL',
            col_type: 'Typ',
            links_heading: 'Linken',
            first_party: 'Éischt Partei',
            third_party: 'Drëtt Partei',
            col_risk_category: 'Risiko Kategorie',
            col_regulatory_framework: 'Regulatiouns Kader',
            col_actionable_detail: 'Handlech Detail',
            risk_category_sr: 'reglementaresche Risiko Kategorie',
            risk_category_mapping_heading: 'Risiko Kategorie Mapping',
            risk_category_mapping_info: 'All Scanfunktioun gëtt op eng reglementaresch Risikokategorie mapéiert (Datensammlung, grenziwwerschreidend Transfert, Zoustëmmungsmanagement, Dateschutz, Sécherheetskontrollen). Dës Mapping erméiglecht geziilte Sanéierung prioritär duerch reglementaresch Impakt.',
            compliance_case_heading: 'Compliance Case Association',
            compliance_case_info: 'Scan Resultater si verbonne mat Konformitéitsfäll fir Enn-zu-Enn Tracking. Themen kënnen direkt vu Scanresultater an de Konformitéitsfall Workflow eskaléiert ginn.',
        },
        results_archive: {
            page_title: 'Scan Resultater Archiv',
            title: 'Scan Resultater Archiv',
            back_to_scanner: 'Zréck op Scanner',
            failed_load: 'Ausgefall Scan Resultater lueden',
            results_loaded: 'Scan Resultater gelueden, {count} records',
            total_label: 'Ganzen: {count}',
            aria_table: 'Scan Resultater Archiv',
            col_scan_date: 'Scan Datum',
            col_target_url: 'Zil URL',
            col_duration: 'Dauer',
            col_issues_found: 'Problemer fonnt',
            col_risk_score: 'Risiko Score',
            col_status: 'Status',
            col_actions: 'Aktiounen',
            scan_status_sr: 'Scan Status',
            view_result: 'Vue',
            no_results: 'Keng Scanresultater fonnt.',
            loading_sr: 'Lueden Scan Resultater',
            pagination_aria: 'Scan Resultater Pagination',
            col_risk_category: 'Risiko Kategorie',
            risk_category_sr: 'reglementaresche Risiko Kategorie',
            archive_retention_heading: 'Archiv Retention',
            archive_retention_info: "Scan Resultater gi fir d'Dauer gespäichert an der Dateschutzpolitik vun Ärer Organisatioun. Archivéiert Resultater enthalen voll Scan Kontext (Kadertags, Risikokategorien, Sanéierungsleitung) a kënnen als CSV fir reglementaresche Beweiser exportéiert ginn.",
            export_context_info: 'Exportéiert CSV Dateien enthalen e reglementaresche Kontext: Risikokategorie, applicabel Kaderen, an handhabbare Sanéierungsdetailer fir all Erfindung.',
        },
        scanner_settings: {
            page_title: 'Scanner Astellungen',
            title: 'Scanner Astellungen',
            back_to_scanner: 'Zréck op Scanner',
            failed_load: 'Ausgefall Scanner Configuratioun lued',
            endpoint_url: 'Endpunkt URL',
            save_success: 'Scanner Konfiguratioun erfollegräich gespäichert.',
            save_failed: 'Ausgefall Scanner Configuratioun ze späicheren',
            test_connection: 'Test Verbindung',
            testing: 'Testen ...',
            saving: 'Spuert ...',
            loading_sr: 'Luede Scanner Astellunge',
            endpoint_fixed_hint: 'The scanner address is fixed by the platform and cannot be changed here. Test Connection checks the AesirX credentials saved under Monitoring Settings.',
        },
        monitoring_settings: {
            page_title: 'Privatsphär Iwwerwachung Astellunge',
            title: 'Iwwerwachung Astellungen',
            back_to_dashboard: 'Zréck op Dashboard',
            failed_load: 'Ausgefall Iwwerwachung Configuratioun ze lued',
            credentials_heading: 'AesirX Umeldungsinformatiounen',
            connected_as: 'Verbonne als {username}',
            credentials_desc: 'Gitt Är AesirX Kont Umeldungsinformatiounen un Privatsphär Iwwerwachung ze verbannen. Fannt Är Member ID an Ärer AesirX Kont Profil Säit.',
            client_id: 'Client ID',
            client_secret: 'Client Geheimnis',
            username: 'AesirX Benotzernumm (E-Mail)',
            password: 'AesirX Passwuert',
            placeholder_keep: '(loosst eidel fir existent ze halen)',
            credential_form_aria: 'AesirX Umeldungsformular',
            test_connection: 'Test Verbindung',
            testing: 'Testen…',
            test_aria: 'Test AesirX Verbindung',
            testing_aria: 'Test Verbindung',
            save_configuration: 'Späicheren Configuratioun',
            saving: 'Spuert…',
            save_aria: 'Späichert Umeldungsinformatiounen',
            saving_aria: 'Spueren Umeldungsinformatiounen',
            credentials_saved: 'AesirX Umeldungsinformatiounen erfollegräich gespäichert.',
            credentials_saving: 'AesirX Umeldungsinformatiounen gespäichert. Konfiguratioun nei lueden…',
            credentials_save_failed: "Konnt d'Umeldungsinformatiounen net späicheren",
            client_id_required: 'Client ID a Benotzernumm sinn néideg.',
            all_fields_required: 'All Umeldungsfelder sinn erfuerderlech.',
            email_heading: 'E-Mail Notifikatiounen',
            receive_emails: 'Kritt Scan Bericht E-Mailen',
            email_toggle_aria: 'Toggle Scan Bericht E-Mailen',
            only_on_change: 'Nëmmen Email wann Risiko Status Ännerungen',
            only_on_change_aria: 'Toggle Email nëmmen op Risiko Status änneren',
            emails_enabled: 'Scan Bericht E-Mailen aktivéiert',
            emails_disabled: 'Scan Bericht E-Maile behënnert',
            only_change_on: 'Nëmmen E-Mail op Risiko änneren',
            only_change_off: 'E-Mail op all Scan',
            email_failed: 'Ausgefall E-Mail-Astellung ze aktualiséieren',
            email_settings_aria: 'Email Notifikatioun Astellunge',
            loading_sr: 'Luede Iwwerwachung Astellunge',
        },
        entity_types: {
            user: 'Benotzer',
            case: 'Fall',
            incident: 'Tëschefall',
            assessment: 'Bewäertung',
            vendor: 'Verkeefer',
            consent: 'Zoustëmmung',
            policy: 'Politik',
            data_map: 'Daten Kaart',
        },
        actions: {
            create: 'Schafen',
            update: 'Update',
            delete: 'Läschen',
            view: 'Vue',
            export: 'Export',
            status_change: 'Status änneren',
        },
    },
    forms: {
        // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
        // no locale declared, so each fell through to its hardcoded English third
        // argument and rendered English in all 16 locales. The two DPIA row-action
        // tooltips were worse: their fallbacks are themselves translated keys, so the
        // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
        // action read 'Approve/Reject' where it intends 'Review'.
        title: 'Form',
        dashboard: {
            title: 'Formulairë-Iwwersiicht',
            total_templates: 'Total Schablounen',
            installed: 'Installéiert',
            available: 'Verfügbar',
            by_market: 'No Maart',
            back_dashboard: 'Back op Dashboard',
            browse_templates: 'Duerchsichen Schablounen',
            catalog_coverage: 'Katalog Ofdeckung',
            quick_start: 'Séier Starten',
            recent_instances: 'Rezent Instanzen',
            total_instances: 'Gesamt Instanzen',
            fetch_failed: 'Konnt net fetch dashboard Donnéeën..',
            no_instances: 'Keng Instanzen fonnt.',
            form_metrics: 'Formulairemetriken-Iwwersiicht',
            templates_total: 'Schablounen insgesamt',
            instances_total: 'Instanzen insgesamt',
            installed_of_available: '{installed} vun {available} installéiert',
            templates_in_category: 'Schablounen an {category}',
            uncategorized: 'Uncategorized',
            other_framework: 'Other',
            category_templates_aria: '{category} templates',
            no_templates: 'No form templates available.',
        },
        templates: {
            title: 'Formulaire-Schablounen',
            new: 'Nei Schabloun',
            edit: 'Schabloun änneren',
            name: 'Schablounennumm',
            form_type: 'Formulaire-Typ',
            market: 'Maart',
            language: 'Sprooch',
            schema: 'Formulaire-Schema',
            preview: 'Virschau',
            duplicate: 'Schabloun duplizéieren',
            grid_view: 'Raster Ukucken',
            list_view: 'Lëscht Ukucken',
            fetch_failed: 'Konnt net fetch Schablounen..',
            filter_templates: 'Schablounen filteren',
            version_history: 'Versiounshistorique',
            no_version_history: 'Keng Versiounshistorique — dëst ass déi initial Versioun.',
            form_id: 'Formulaire-ID',
            source_instrument: 'Quellinstrument',
            issuer: 'Ausställer',
            all_frameworks: 'All Kaderen',
            view_mode: 'Ansichtsmode',
            classification: 'Klassifikatioun',
        },
        catalog: {
            title: 'Formulaire-Katalog',
            install: 'Installéieren',
            uninstall: 'Desinstalléieren',
            open_form: 'Open form',
            install_all: 'All installéieren',
            installed_tab: 'Installéiert',
            catalog_tab: 'Katalog',
            search_placeholder: 'Schablounen sichen...',
            no_templates: 'Keng Schablounen fonnt.',
            template_count: '{count} Schablounen',
            all_markets: 'All Mäert',
            sections: 'Sektiounen',
            all_categories: 'All Kategorien',
            category: 'Kategorie',
            category_install_failed: 'Konnt net install category Schablounen..',
            category_install_success: 'Kategorie Schablounen installed.',
            fetch_failed: 'Konnt net fetch Schablounen..',
            fetch_installed_failed: 'Konnt net fetch installed Schablounen..',
            fields: 'Felder',
            install_failed: 'Konnt net install Schabloun..',
            install_success: 'Schabloun installed successfully.',
            installing: 'Installéiere...',
            removing: 'Ewechhuele...',
            uninstall_confirm: 'Are you sure you want op uninstall dëst Schabloun?',
            uninstall_failed: 'Konnt net uninstall Schabloun..',
            uninstall_success: 'Schabloun uninstalled successfully.',
            installation_summary: 'Installatiounsiwwersiicht',
            filter_catalog: 'Katalog filteren',
            all_frameworks: 'All Kaderen',
            templates_installed_count: '{count} Schabloun(en) erfollegräich installéiert',
            all_already_installed: 'All Schablounen an dëser Kategorie sinn scho installéiert',
            category_install_partial: '{installed} of {requested} template(s) installed. {refused} restricted register(s) were not installed — adding those requires the regulatory-artifact custody permission.',
            category_install_none: 'No templates were installed. {refused} restricted register(s) in this category require the regulatory-artifact custody permission.',
            form_id: 'Formulaire-ID',
            issuer: 'Ausställer',
            catalog_tabs: 'Formulairekatalog-Tabs',
        },
        filler: {
            back_templates: 'Back op Schablounen',
            no_sections: 'Keng sections fonnt.',
            draft_created: 'Entworf created.',
            draft_saved: 'Entworf saved.',
            fetch_template_failed: 'Konnt net fetch Schabloun..',
            save_draft: 'Späicheren Entworf',
            save_draft_failed: 'Konnt net save Entworf..',
            saving: 'Späichere...',
            select_option: 'Auswielen eng option',
            submit: 'Ofschécken',
            submit_failed: 'Konnt net submit..',
            submitting: 'Ofschécke...',
            title: 'Formulaire-Assistent',
            form_id_label: 'Formulaire-ID',
            schema_label: 'Schema',
            form_number_label: 'Form',
            framework_label: 'Kader',
            issuer_label: 'Ausställer',
            source_label: 'Quell',
            market_label: 'Maart',
            section_of: 'Sektioun {current} vu(n) {total}',
            form_navigation: 'Formulairenavigatioun',
            form_progress: 'Formulairefortschrëtt: Sektioun {current} vu(n) {total}',
            removed_item: 'Element {index} aus {field} ewechgeholl',
            added_item: 'Neit Element bei {field} derbäigesat',
            go_to_previous: 'Vireg Sektioun, Sektioun {current} vu(n) {total}',
            go_to_next: 'Nächst Sektioun, Sektioun {current} vu(n) {total}',
            risk_matrix_placeholder: '1–5',
        },
        filing: {
            title: 'Areeche-Status',
            advance: 'Areeche-Status aktualiséieren...',
            not_started: 'Net ugefaangen',
            drafting: 'Entworf',
            internal_review: 'Intern Iwwerpréiwung',
            approved_for_submission: 'Fir Areechen ugeholl',
            submitted_to_authority: "Un d'Autoritéit geschéckt",
            supplement_requested: 'Ergänzung gefrot',
            supplement_in_progress: 'Ergänzung am Gaang',
            resubmitted: 'Nei agereecht',
            accepted: 'Ugeholl',
            rejected: 'Ofgelehnt',
            closed: 'Zougemaach',
            // Sprint 63.37.0 (Stream J) — the filing milestones the instance page lists.
            milestone_submitted_at: 'Submitted',
            milestone_supplement_requested_at: 'Supplement requested',
            milestone_supplement_submitted_at: 'Supplement submitted',
            milestone_accepted_at: 'Accepted',
            milestone_rejected_at: 'Rejected',
            milestone_closed_at: 'Closed',
        },
        instances: {
            approved_by: 'Genehmegt By',
            form_answers: 'Formulaire Äntwerten',
            column_header: 'Form',
            no_answers: 'Keng answers fonnt.',
            view_outputs: 'Ukucken Ausgaben',
            approve: 'Genehmegen',
            approve_failed: 'Konnt net approve..',
            approving: 'Genehmege...',
            detail_title: 'Instanz Detail',
            fetch_failed: 'Konnt net fetch Instanzen..',
            generate_failed: 'Konnt net generate Ausgab..',
            generate_output: 'Generéieren Ausgab',
            generating: 'Generéiere...',
            submitted_at: 'Ofgeschéckt At',
            instance_metadata: 'Formulaireinstanz-Metadaten',
            filing_status_steps: 'Archivéierungsstatus-Schrëtt',
            current_status: 'aktuellen Status',
            filing_status_update_failed: 'Feeler beim Aktualiséiere vum Andreesstatus',
            // Sprint 63.37.0 (I1) — the export buttons and the sample notice of an instance.
            form_number: 'Form number',
            export_pdf: 'Export PDF',
            export_docx: 'Export Word (DOCX)',
            exporting: 'Exporting {format}…',
            export_done: '{format} exported. The download has started.',
            export_failed: 'The {format} could not be exported.',
            export_not_approved: 'This form cannot be exported yet. Approve it first; a form inside a record is exported once it is completed or finalized.',
            sample_notice: 'This form is sample data. Every page of its exports carries the stamp "{stamp}".',
            // Sprint 63.37.0 (Stream J) — the finalize action and the form actions a parent record's row offers.
            finalize: 'Finalize',
            finalized: 'Form finalized.',
            finalize_failed: 'The form could not be finalized.',
            edit_form: 'Edit',
            view_form: 'View',
            open_instance: 'Open {form}',
            // Sprint 63.40.0 (decision 8) — the page description and the notification dossier.
            detail_description: 'The answers of one form, its exports and, where the form opens one, the notification dossier prepared from it.',
            notice_dossier: {
                title: 'Notification dossier',
                description: 'The platform prepares this dossier from the completed form. It does not file it: your organisation files it with the receiving authority.',
                prepare: 'Prepare the dossier for {form}',
                preparing: 'Preparing the dossier for {form}…',
                prepared: 'The dossier for {form} is prepared.',
                prepare_failed: 'The dossier could not be prepared.',
                prepare_after_final: 'Complete and approve the form to prepare its dossier.',
                load_failed: 'The dossier could not be loaded.',
                reload: 'Load the dossier again',
                loading: 'Loading the dossier…',
                reference: 'Dossier reference',
                receiving_authority: 'Receiving authority',
                receiving_authority_hint: 'As the installed form states it.',
                addressee_label: 'Name of the receiving body',
                addressee_hint: 'The installed form names a class of body, not one body. Enter the body you address this dossier to. Where the completed form already names it on its own addressee line, the dossier starts with that name. A name you enter or clear here is kept and does not change the form. You can leave it empty until it is decided.',
                addressee_recorded: 'Recorded name',
                not_recorded: 'Not recorded',
                statements_title: 'Your statements',
                statements_description: 'Only you, as the applicant, can make these statements. The platform records your answer and does not decide it.',
                statement_basis: 'Basis: {basis}',
                stated_yes: 'This applies',
                stated_no: 'This does not apply',
                stated_unanswered: 'Not answered',
                authority_step_title: "The receiving body's step",
                authority_step_note: "This step is the receiving body's. No deadline is set against your organisation.",
                save: 'Save the dossier',
                saving: 'Saving the dossier…',
                saved: 'The dossier is saved.',
                save_failed: 'The dossier could not be saved.',
                export_title: 'Export the dossier',
                export_hint: "The submission slip prints the receiving body's name as saved on the dossier. Until a name is saved, its addressee line is left blank.",
                export_open: 'Preview the submission slip',
            },
        },
        outputs: {
            back_instance: 'Back op Instanz',
            no_outputs_msg: 'Nach keng Ausgaben generéiert.',
            no_outputs_title: 'No Ausgaben',
            output_name: 'Ausgab Numm',
            download: 'Eroflueden',
            download_failed: 'Konnt net download..',
            fetch_failed: 'Konnt net fetch outputs..',
            format: 'Format',
            generated_at: 'Generéiert At',
            generated_by: 'Generéiert By',
            size: 'Gréisst',
            title: 'Formulaire Ausgaben',
            print: 'Drécken',
            print_outputs: 'Formulairerausgaben drécken',
            printing: 'Formulairerausgaben gi gedréckt',
            pdf_preview: 'PDF-Virschau',
            downloading: '{name} gëtt erofgelueden',
            preview_in_new_window: '{name} ({format}) an engem neie Fënster uweisen',
            // Sprint 62.45.0 AC9 — column headers of the form print export (PDF table).
            print_col_field: 'Field',
            print_col_value: 'Value',
            // Sprint 62.45.0 AC9 — metadata lines of the form print export.
            print_meta_instance: 'Instance ID: {id}',
            print_meta_status: 'Status: {status}',
            print_meta_generated: 'Generated: {timestamp}',
            // Sprint 63.37.0 (H2) — the header lines of a printed official form.
            official_header_form_number: 'Form number',
            official_header_instrument: 'Source instrument',
            official_header_issuer: 'Issued by',
            official_header_authority: 'Authority',
            // Sprint 62.58.0 (AC-A4) — the draft-instrument notice an output prints before its title.
            draft_notice_frame: 'Prepared under a draft instrument that is not in force. Nothing in this document is an obligation.',
            draft_notice_framework: '{name} — pack version {version}',
            draft_notice_regenerate: 'Generated before the draft notice was added — generate it again.',
            // Sprint 62.58.0 gate E12.5 pass 2 (P3-5) — an output whose printed notices no longer match the frameworks' state.
            draft_notice_status_changed: "The framework's status has changed since this was generated — generate it again.",
        },
        // Sprint 62.58.0 (AC-A3) — what an instance surface of a draft instrument's template adds.
        draft_notice: {
            instance_note: 'Filling in this form does not make anything in it an obligation.',
        },
        cybersec_domains: {
            scope: 'Ëmfang an Uwendbarkeet',
            risk: 'Risikobewäertung a Kontrollzuuerdnung',
            policy: 'Organisatoresch Sécherheetspolitiken',
            localization: 'Datelokaliséierung a lokal Präsenz',
            vendor: 'Drëttparteien a Liwwerketten',
            authority: 'Behandlung vu behërdlechen Ufroen',
            incident: 'Tëschefallreaktioun a Beweisséierung',
            training: 'Formatioun a Bereetschaft',
        },
        market_finalization_domains: {
            data_law: 'Dategesetz — Governance an Operationell Kontrollen',
            pdpl_2025: 'PDPL 2025 — Dateschutz-Governance an Betraffenerechter',
            decree_356: 'Dekret 356/2025 — Liwweranten- a grenziwwerschreidend Kontrollen',
            telecom: 'Telekommunikatiounsgesetz — Vertraulechkeet an Autoritéitsuffroen',
        },
        nav: {
            filing_case_links: 'Areechtungsfalllinken',
            section_routing: 'Sektiounsweiderleitung',
            submission_packages: 'Areechtungspäck',
        },
        filing_case_links: {
            title: 'Areechtungsfalllinken',
            link_form: 'Formulaire mat Areechtungsfall verbannen',
            filing_case_type: 'Areechtungsfalltyp',
            form_instance: 'Formulairinstanz',
            select_form_instance: 'Formulairinstanz auswielen...',
            select_filing_case: 'Areechtungsfall auswielen...',
            no_links_found: 'Keng Areechtungsfalllinke fonnt.',
            linked: 'Verbannen',
            filing_case_id: 'Archivéierungsfall-ID',
            link_created: 'Archivéierungsfall-Link erstallt',
            link_deleted: 'Areechtungsfalllink geläscht',
            aria_delete_link: 'Link {name} läschen',
            case_type_dpia_dossier: 'DSFA-Dossier',
            case_type_cross_border_filing: 'Grenziwwerschreidend Areechtung',
            case_type_breach_notification: 'Dateschutzverletungsnotifikatioun',
            no_instances_guidance: 'Nach keng Formularinstanzen. Erstellt eng aus Formularschablounen.',
            no_filings_guidance: 'Nach keng Areechtungsfäll. Erstellt een aus Bewäertungen.',
        },
        section_routing: {
            title: 'Sektiounsweiderleitung',
            assign_section: 'Sektioun zouweisen',
            department: 'Abteilung',
            select_department: 'Abteilung auswielen...',
            assignee: 'Zougewisene Persoun',
            select_assignee: 'Zougewisene Persoun auswielen...',
            section: 'Sektioun',
            select_section: 'Sektioun auswielen...',
            select_form_instance: 'Formulairinstanz auswielen...',
            no_assignments_found: 'Keng Sektiouns-Zouweisunge fonnt.',
            select_instance_prompt: 'Wielt eng Formulairinstanz fir Sektiouns-Zouweisungen ze gesinn.',
            complete_section: 'Sektioun ofschléissen',
            status_pending: 'Aussteend',
            status_in_progress: 'Am Gaang',
            status_completed: 'Ofgeschloss',
            status_reviewed: 'Iwwerpréift',
            form_instance_id: 'Formulaireinstanz-ID',
            assignment_created: 'Sektiouns-Zouweissung erstallt',
            assignment_deleted: 'Sektiouns-Zouweissung geläscht',
            status_updated: 'Sektiouns-Status aktualiséiert',
            aria_delete_assignment: 'Zouweissung {name} läschen',
            deadline: 'Frist',
            deadline_help: 'Optional. Zouweissungen no dësem Datum ginn als iwwerfälleg ugewisen.',
            overdue: 'Iwwerfälleg',
            days_remaining: 'Deeg iwwreg',
            approval_requested: 'Fir Iwwerpréiwung agereecht',
            form_approved: 'Formulaire guttgeheescht',
            form_rejected: 'Formulaire ofgeleent',
            no_departments_guidance: 'Keng Abteilungen konfiguréiert. Setzt se an den Organisatiounsastellungen op.',
            no_sections_guidance: "Keng Sektiounen an der Schabloun definéiert. Vergewëssert Iech, datt d'Formularschabloun Sektiounen huet.",
            select_instance_first: 'Wielt als éischt eng Formularinstanz fir déi verfügbar Sektiounen ze lueden.',
        },
        submission_packages: {
            supporting_documents: 'Supporting Documents',
            supporting_documents_help: 'Files filed with this package. Each is stored against the package and listed on its record.',
            no_supporting_documents: 'No supporting documents attached yet.',
            attach_supporting_document: 'Attach a supporting document',
            supporting_document_attached: 'Supporting document attached',
            title: 'Areechtungspäck',
            assemble_package: 'Areechtungspaket zesummestellen',
            dossier: 'Dossier',
            select_dossier: 'Dossier auswielen...',
            package_label: 'Paketbezeechnung',
            cover_letter: 'Begleetbréif',
            checklist: 'Kontrollëlëscht',
            evidence_index: 'Beweisverzeechnes',
            completeness: 'Vollstännegkeet',
            incomplete_warning: 'Kann net zesummestellen: Formulaire huet onvollstänneg Sektiounen.',
            no_packages_found: 'Keng Areechtungspäck fonnt.',
            version: 'Versioun',
            package_assembled: 'Andragspaket zesummegestallt',
            package_deleted: 'Areechtungspaket geläscht',
            aria_delete_package: 'Paket {name} läschen',
            status_draft: 'Entworf',
            status_complete: 'Komplett',
            status_submitted: 'Agereecht',
            status_accepted: 'Akzeptéiert',
            status_rejected: 'Refuséiert',
            package_updated: 'Areechtungspaket aktualiséiert',
            document_generated: 'Dokument generéiert',
            documents: 'Dokumenter',
            generate: 'Generéieren',
            preview: 'Virschau',
            download: 'Eroflueden',
            download_all: 'Alles eroflueden',
            no_dossiers_guidance: 'Nach keng Dossieren. Erstellt een aus Bewäertungen.',
            package_plan_help: 'Am Paketplang abegraffen (automatesch Dokumenterstellung verfügbar):',
            cover_letter_help: 'Offiziellen Uwwerbrief un déi zoustänneg Autoritéit',
            checklist_help: 'Compliance-Checklëscht déi verifizéiert datt all Areechtungsufuerderungen erfëllt sinn',
            evidence_index_help: 'Index vun ënnerstëtzenden Noweisdokumenter déi an der Areechtung referenzéiert ginn',
            completeness_skipped: 'Vollständegkeetskontroll iwwersprong — keng Formularinstanz mat dësem Dossier verbonnen.',
            completeness_unavailable: 'Vollständegkeetskontroll net verfügbar — et gëtt mat der Paketzesummesetzung weidergemaach.',
            duplicate_package_error: 'Et gëtt schonn en Areechtungspak fir dësen Dossier.',
            doc_cover_letter: 'Begleetbréif',
            doc_checklist: 'Kontrollëlëscht',
            doc_evidence_index: 'Noweisverzeichnes',
            export_csv: 'CSV exportéieren',
            export_success: 'Linken vun den Areechtungsfäll erfollegräich exportéiert',
            no_assignments_guidance: 'Nach keng Sektiounszouweisungen. Weisen Sektiounen un Abteilungen an Teammembere fir mam Routing unzefänken.',
            completeness_hint: 'Completeness is the share of the six dossier fields a package cannot be filed without that the dossier holds: dossier type, framework, authority, submission method, applicant organisation and applicant contact.',
            completeness_not_measured: 'Not measured: assembled before completeness was computed',
            missing_fields_label: 'Missing from the dossier',
            dossier_field_dossier_type: 'Dossier type',
            dossier_field_regulatory_framework_code: 'Framework',
            dossier_field_authority: 'Authority',
            dossier_field_submission_method: 'Submission method',
            dossier_field_applicant_org_name: 'Applicant organisation',
            dossier_field_applicant_contact: 'Applicant contact',
        },
        // Sprint 63.37.0 (D4) — the one official-standing badge (`OfficialFormBadge`).
        official_standing: {
            official: 'Official form',
            internal: 'Internal form',
        },
        template_picker: {
            title: 'E Schabloun auswielen',
            hint: 'Wielt eng regulatoresch Formulairschabloun oder fänkt mat engem eidele Formulaire un.',
            market_filter: 'No Maart filteren',
            framework_filter: 'No Kader filteren',
            all_markets: 'All Märten',
            all_frameworks: 'All Kaderen',
            search_placeholder: 'Schalounen sichen...',
            blank_form: 'Eidele / generescht Formulaire',
            blank_form_desc: 'Ouni Schabloun erstellen — Felder manuell ausfëllen.',
            no_templates: 'Keng Schalounen fir dëse Filter fonnt.',
            templates_unreadable: 'Not every template for this form could be loaded, so the list below may be empty or incomplete. That does not mean there are none.',
            templates_read_subject: 'the form templates',
            filters_unreadable: 'The market and framework filters are not shown: their choices could not be loaded.',
            select: 'Auswielen',
            link_error: 'Schablounverknüpfung fehlgeschloen — Dir kënnt eng Schabloun spéider an der Ännervisioun verknüpfen.',
            internal_badge: 'Intern Schabloun',
            legacy_badge: 'Veroudert',
            draft_badge: 'Entworf',
            upcoming_badge: 'Kommend',
            hidden_badge: 'Verstoppt',
            show_hidden_toggle: 'Verstoppten / verouderte Schalounen uweisen',
            show_hidden_toggle_hint: 'Admin-Vue: enthält Schalounen déi als internal_only, hidden, legacy oder draft markéiert sinn.',
        },
        cover_letter_modal: {
            title: 'Begleetbréif: Virschau a Redaktioun',
            iframe_title: 'Virschau vum Begleetbréif',
            stage_label: 'Etapp',
            stage_initial: 'Éischt Agabe',
            stage_supplement: 'Zousazäntwert',
            stage_resubmission: 'Nei Agabe',
            subject_label: 'Sujet',
            body_label: 'Haaptparagraf',
            notes_label: 'Zousätzlech Notizen',
            refresh_preview: 'Virschau aktualiséieren',
            confirm: 'Bestätegen a späicheren',
            regenerate: 'Nei generéieren',
            snapshot_saved: 'Begleetbréif gespäichert',
        },
        related_panel: {
            title: 'Verbonne Reguléierungsformularen',
            open_form: 'Formulaire ausfëllen',
            no_forms: 'Keng verbonne Formulairen fonnt.',
            hint_vn: 'VN-spezifesch Reguléierungsformularen verfügbar — wielt e Formulaire fir den Compliance-Assistent opzemaachen.',
            internal_badge: 'Intern',
            official_badge: 'Offiziell',
            loading: 'Verbonne Formulairen ginn gelueden...',
            error: 'Verbonne Formulairen konnten net geluede ginn.',
            form_id: 'Formulaire-ID',
            form_name: 'Formulairenumm',
            framework: 'Regelwierk',
        },
        view_form: 'Formulaire weisen',
        edit_form: 'Formulaire änneren',
        save_draft: 'Entworf späicheren',
        module_settings: 'Modulastellungen',
        validation_errors: "Verbessert w.e.g. d'Validatiounsfeeler",
        custom_name_help: 'Fakultativ — Standard ass den Template-Numm wann et eidel bleift',
        select_placeholder: 'Auswielen...',
        // Gate E12.5 pass 3 (QA nit) — the stored-text fallback of a date-and-time field.
        datetime_stored_text_hint: 'Recorded before this field took a date and time; kept as written.',
        previous_section: 'Zréck',
        next_section: 'Weider',
        loading: 'Am Luede...',
        array_add: 'Derbäisetzen',
        array_remove: 'Ewechhuelen',
        array_item_label: '{label} Element {index}',
        array_add_item: 'Element zu {label} bäifügen',
        array_remove_item: 'Element {index} vun {label} ewechhuelen',
        legacy_banner: 'Dësen Entrée gouf virun de vereenegte Formulairen erstallt. E puer Donnéeë kéinten tëschent der Entitéit an der Formulairinstanz ofweichen.',
        field_required: '{label} ass erfuerderlech',
        section_announcement: 'Sektioun {current} vun {total}: {title}',
        sections_nav_label: 'Form Sektiounen',
        no_sections: 'Keng Schabloun Sektiounen verfügbar.',
        section_label_tpl: 'Sektioun {index}: {title}',
        field_shown_announcement: '{field} gëtt elo ugewisen.',
        field_required_announcement: '{field} ass elo obligatoresch.',
        submit_blocked_hint_one: '1 obligatorescht Feld brauch nach eng Äntwert.',
        submit_blocked_hint_many: '{count} obligatoresch Felder brauche nach eng Äntwert.',
        module_settings_auto_applied: 'Modulspezifesch Astellunge ginn automatesch applizéiert.',
        operation_failed: 'Operatioun gescheitert',
        failed_to_load_form: "D'Formulaire konnt net geluede ginn",
        failed_to_save_form: "D'Formulaire konnt net gespäichert ginn",
        failed_to_submit_form: "D'Formulaire konnt net geschéckt ginn",
        failed_to_load_template: "D'Schabloun konnt net geluede ginn",
        failed_to_save_draft: 'Den Entworf konnt net gespäichert ginn',
        failed_to_create: 'Erstellen huet net fonctionnéiert',
        no_content_generated: 'No content generated',
    },
    pack_catalog: {
        title: 'Pack-Katalog',
        install: 'Pack installéieren',
        uninstall: 'Pack desinstalléieren',
        install_batch: 'Cluster installéieren',
        installed_tab: 'Installéiert',
        catalog_tab: 'Katalog',
        pack_details: 'Pack-Detailer',
        frameworks: 'Kaderreegelungen',
        requirements: 'Ufuerderungen',
        artifacts: 'Artefakten',
        language_pack: 'Sproochpack',
        dependencies: 'Ofhängegkeeten',
        no_packs: 'Keng Packen fonnt.',
    },
    platform_pages: {
        users: {
            title: 'Benotzer',
            new: 'Neie Benotzer',
            invite: 'Benotzer aludden',
            edit: 'Benotzer änneren',
            name: 'Numm',
            email: 'E-Mail',
            role: 'Roll',
            status: 'Status',
            identity_type: 'Identitéitstyp',
            initial_password: 'Initial password',
            require_password_change_action: 'Require password change',
            require_password_change_confirm: '{name} will reach only the change-password step, from their next request, until they choose a new password. Continue?',
            require_password_change_done: 'The user must now change their password.',
            require_password_change_failed: 'The password change could not be required.',
            password_change_pending: 'Password change required',
            initial_password_note: 'Share it through a secure channel. While a password change is required, the user can do nothing but choose a new password.',
            last_login: 'Lescht Umeldung',
            failed_login_attempts: 'Failed Login Attempts',
            never_signed_in: 'Never',
            deactivate: 'Desaktivéieren',
            reactivate: 'Reaktivéieren',
            unlock: 'Entspären',
            reset_password: 'Passwuert zrécksetzen',
            remove: 'Läschen',
            remove_confirm: '"{name}" läschen? Dës Aktioun ass permanent an de Benotzer verléiert all Zougang. De Späicher gëtt fir Auditzwecker behalen.',
            user_details_loaded: 'User details loaded',
            password_required: 'Password is required',
            password_min_length: 'Password must be at least 12 characters',
            password_uppercase: 'Password must contain at least one uppercase letter',
            password_lowercase: 'Password must contain at least one lowercase letter',
            password_digit: 'Password must contain at least one digit',
            password_special: 'Password must contain at least one special character',
            deactivate_failed: 'Failed to deactivate user',
            reactivate_failed: 'Failed to reactivate user',
            remove_failed: 'Failed to remove user',
            // fix-sprint-20261017-1500 F4 — UserDetailPage's error toasts read these, not English literals.
            update_failed: 'Failed to update user',
            unlock_failed: 'Failed to unlock user',
            assign_role_failed: 'Failed to assign role',
            revoke_role_failed: 'Failed to revoke role',
            create_failed: 'Failed to create user',
            count_one: '{total} user',
            count_other: '{total} users',
            announce_loaded: 'Users loaded',
        },
        roles: {
            title: 'Rollen',
            new: 'Nei Roll',
            edit: 'Roll änneren',
            name: 'Rollennumm',
            description: 'Beschreiwung',
            permissions: 'Berechtigungen',
            users_count: 'Benotzer',
            detail: 'Rollendetail',
            all_tiers: 'All Niveauen',
            tier: 'Niveau',
            type_system: 'System',
            type_custom: 'Personaliséiert',
            role_name_placeholder: 'z. B. regionalen Compliance-Manager',
            permissions_heading: 'Permissions',
            permissions_selected: '({count} selected)',
            select_all_offered: 'Select all',
            clear_all: 'Clear all',
            grant_bounds_hint: 'You can grant only permissions you hold yourself. Permissions that no system role holds by design, such as AML case access, can be granted only by someone who holds the role-assignment permission in their own right.',
            permission_not_held_summary: 'You do not hold these permissions, so you cannot grant them:',
            permission_not_held_item: 'You do not hold this permission',
            system_role_fixed: 'A system role’s permissions are fixed. Create a custom role to grant other permissions.',
            permission_required: 'At least one permission is required.',
            system_role_no_delete: 'System roles cannot be deleted.',
            create_failed: 'Failed to create role',
            delete_failed: 'Failed to delete role',
            update_failed: 'Failed to update role',
            delete_blocked_active_users: 'This role is still assigned to {count} user(s). Move them to another role, then delete this one.',
            delete_blocked_sso_config: 'Single sign-on still uses this role ({names}), as the default role for new users or in its group mapping. Change that setting first, then delete this role.',
            announce_loaded: 'Roles loaded',
            announce_detail_loaded: 'Role details loaded',
        },
        profile: {
            title: 'Mäi Profil',
            name: 'Numm',
            display_name: 'Uweisungsnumm',
            email: 'E-Mail',
            phone: 'Telefon',
            department: 'Abteilung',
            job_title: 'Beruffsbezeeechnung',
            language: 'Sprooch',
            role: 'Roll',
            persona: 'Erkannte Persona',
            last_login: 'Lescht Umeldung',
            save: 'Ännerungen späicheren',
            save_success: 'Profil erfollegräich aktualiséiert',
            module_tips_section: 'Modultipps',
            tips_toggle: 'Regulatoresch Tipps a Modulen weisen',
            dismissed_tips: 'Ausgeblendete Tipps',
            reset_dismissed: 'All ausgeblendete Tipps zrécksetzen',
            notification_prefs: 'Notifikatiounspreferenzen',
            notification_prefs_link: 'Notifikatiounspreferenze verwalten →',
            change_password: 'Passwuert änneren',
            current_password: 'Aktuellt Passwuert',
            new_password: 'Neit Passwuert',
            confirm_password: 'Passwuert bestätegen',
            accessibility: 'Accessibiliteit',
            high_contrast: 'Heichkontrast-Modus',
            high_contrast_desc: 'Kontrast erhoeijen fir besser Liesbarkeet. Erfellt WCAG 2.1 AAA.',
            passwords_no_match: 'Passwierder stëmmen net iwwereneen',
            password_changed_logout: 'Passwuert geännert. Dir gitt ofgemellt.',
            failed_update: 'Profil konnt net aktualiséiert ginn',
            failed_change_password: 'Passwuert konnt net geännert ginn',
            none: 'Keen',
            never: 'Ni',
            password_requirements: 'Min. 12 Zeechen, Groussbuschstaf, Klengbuschstaf, Ziffer, Spezialzeechen',
            announce_updated: 'Profile updated',
            announce_loaded: 'Profile loaded',
        },
        security: {
            mfa_title: 'MFA-Astellungen',
            mfa_enable: 'MFA aktivéieren',
            mfa_disable: 'MFA desaktivéieren',
            mfa_setup: 'Scannt dësen QR-Code mat Ärer Authentifizéierungs-App',
            sso_title: 'SSO-Konfiguratioun',
            sso_provider: 'Ubidder',
            sso_client_id: 'Client-ID',
            sso_redirect: 'Weiderleedungs-URI',
            sso_idp_section: 'Identity Provider',
            sso_idp_entity_id: 'Identity provider entity ID',
            sso_idp_sso_url: 'Identity provider sign-in URL',
            sso_idp_binding: 'Request binding',
            sso_binding_redirect: 'HTTP Redirect',
            sso_binding_post: 'HTTP POST',
            sso_allow_idp_initiated: 'Accept sign-ins started at the identity provider',
            sso_jit_provisioning: 'Create a user on first sign-in',
            sso_default_role: 'Role for users created on first sign-in',
            sso_default_role_none: 'No default — refuse the sign-in',
            sso_assertions_signed: 'Signed assertions are always required.',
            sso_acs_hint: 'Give your identity provider this assertion consumer URL:',
            // ── Sprint 63.6.1 — OpenID Connect ────────────────────────────
            sso_oidc_issuer: 'Issuer',
            sso_oidc_issuer_hint: 'The issuer identifier your provider publishes, exactly as it appears in its discovery document. The platform reads that document from this address and refuses a configuration whose issuer does not match.',
            sso_oidc_redirect_uri: 'Redirect URI to register',
            sso_oidc_redirect_uri_hint: 'Register this exact address with your provider. The platform decides it from the configuration, so it cannot be edited here.',
            sso_oidc_redirect_uri_pending: 'Available once the configuration is saved',
            sso_oidc_client_auth: 'Client authentication',
            sso_oidc_client_auth_basic: 'Client secret, in the Authorization header',
            sso_oidc_client_auth_post: 'Client secret, in the request body',
            sso_oidc_scopes: 'Scopes',
            sso_oidc_scopes_hint: 'Separate with spaces. Leave empty for openid, profile and email. The openid scope is always requested: without it the provider returns no identity token.',
            sso_oidc_scopes_placeholder: 'openid profile email',
            sso_oidc_identity_claim_note: 'Identity comes from the email claim, and the address must be one your provider reports as verified. No other claim is read, and no claim grants a permission.',
            sso_certificates: 'Signing certificates',
            sso_certificates_empty: 'No signing certificate. A sign-in cannot be verified until one is added.',
            sso_certificate_pem: 'Certificate (PEM)',
            sso_certificate_add: 'Add certificate',
            sso_certificate_expires: 'Expires',
            sso_certificate_remove: 'Remove certificate',
            sso_certificate_limit: 'Two certificates are held at most, so a rotation needs no downtime.',
            sso_verify: 'Verify configuration',
            sso_verify_hint: 'Runs a full sign-in against this configuration and reports every check. It creates no session and no user, so you stay signed in.',
            sso_verify_report: 'Verification report',
            sso_verify_passed: 'Every check passed.',
            sso_verify_failed: 'Some checks failed.',
            sso_check_passed: 'Passed',
            sso_check_failed: 'Failed',
            sso_enforcement: 'SSO-only sign-in',
            sso_enforcement_off: 'Off — everyone can sign in with a password.',
            sso_enforcement_on: 'On — only exempt administrators can sign in with a password.',
            sso_enforcement_activate: 'Turn on SSO-only sign-in',
            sso_enforcement_deactivate: 'Turn off SSO-only sign-in',
            sso_enforcement_exempt: 'Administrators who keep password sign-in',
            sso_enforcement_before: 'Before you can turn this on:',
            sso_enforcement_need_verify: 'Verify this configuration and complete a sign-in through it, within the last 15 minutes.',
            sso_enforcement_need_exempt: 'Name at least one active administrator who has a password.',
            sso_enforcement_recovery: 'If the identity provider stops working, an exempt administrator can sign in with a password and turn this off; deactivating the configuration also restores password sign-in for everyone.',
            sso_mfa_heading: "Accept this provider's MFA",
            sso_mfa_intro: "By default the platform asks for its own MFA after every sign-in through this provider. Turn this on only if you trust this provider's MFA to stand in for the platform's.",
            sso_mfa_accept: "Accept this provider's MFA instead of the platform's",
            sso_mfa_state_off: "Off. The platform's own MFA applies to every sign-in through this provider.",
            sso_mfa_state_on: "On. A sign-in counts as MFA-verified only when the values the provider reports match the ones you list below. Every other sign-in is held at the platform's own MFA.",
            sso_mfa_no_meaning: "The platform attaches no meaning to any value. Only values you enter here, matched exactly and with the same capitalisation, count. A value you have not listed, a sign-in older than the maximum age and a missing value all fall back to the platform's own MFA.",
            sso_mfa_amr: 'Accepted authentication method values (amr)',
            sso_mfa_amr_hint: 'The exact amr values your provider reports for an MFA sign-in, separated by spaces or commas. OpenID Connect only.',
            sso_mfa_acr_oidc: 'Accepted authentication context values (acr)',
            sso_mfa_acr_saml: 'Accepted authentication context values (AuthnContextClassRef)',
            sso_mfa_acr_hint: 'The exact values your provider reports for an MFA sign-in, separated by spaces or commas.',
            sso_mfa_lists_rule: 'List at least one value, or nothing will count. When you list more than one kind of value, a sign-in must match every kind.',
            sso_mfa_empty_warning: 'Nothing is listed, so no sign-in through this provider will count as MFA-verified.',
            sso_mfa_max_age: 'Maximum authentication age (seconds)',
            sso_mfa_max_age_hint: 'A sign-in at the provider older than this does not count. Leave empty for no age check.',
            sso_mfa_max_age_invalid: 'Enter a whole number of seconds greater than zero, or leave the field empty.',
            sso_revoke_sessions: "End this provider's sessions",
            sso_revoke_confirm_title: "End this provider's sessions?",
            sso_revoke_confirm_message: 'Everyone currently signed in through {name} is signed out and must sign in again. Sessions that arrived by password or through another provider are not ended. Federated sessions created before sessions recorded which configuration they came from cannot be attributed to one provider, so they are ended as well.',
            sso_revoke_confirm_button: 'End sessions',
            sso_revoke_working: 'Ending sessions…',
            sso_revoke_result_heading: 'Sessions ended',
            sso_revoke_result_for: 'Provider: {name}',
            sso_revoke_count_total: 'Sessions ended',
            sso_revoke_count_legacy: 'Of these, sessions that could not be attributed to a configuration',
            sso_revoke_legacy_note: "These sessions pre-date the field that records which configuration a session came from, so they are ended whichever provider's sessions you end.",
            sso_revoke_announce: 'Sessions ended: {count}. Of these, {legacy} could not be attributed to a configuration because they pre-date the field that records it.',
            sso_revoke_failed: "Failed to end this provider's sessions.",
            sso_mfa_accepted_badge: 'Provider MFA accepted',
            api_keys_title: 'API-Schlësselen',
            api_keys_new: 'Neien API-Schlëssel',
            api_keys_name: 'Schlësselnumm',
            api_keys_last_used: 'Lescht benotzt',
            api_keys_expires: 'Leeft of',
            api_keys_revoke: 'Zréckzéien',
            service_accounts_title: 'Service-Konten',
            ip_allowlist_title: 'IP-Erlaabnislëscht',
            ip_add: 'IP-Adress derbäisetzen',
            ip_address: 'IP-Adress',
            ip_description: 'Beschreiwung',
            delegations_title: 'Delegatiounen',
            delegations_new: 'Nei Delegatioun',
            delegations_from: 'Vum Benotzer',
            delegations_to: 'Op Benotzer',
            delegations_expires: 'Leeft of',
            break_glass_title: 'Noutfall-Zougang',
            break_glass_requester_only: 'You can request break-glass access here and follow your own requests below. Other people\u2019s requests, active sessions and history are shown to approvers.',
            break_glass_my_requests: 'Your requests',
            break_glass_request: 'Zougang ufroen',
            break_glass_reason: 'Grond fir den Zougang',
            break_glass_duration: 'Dauer',
            session_settings_title: 'Sessiouns-Astellungen',
            eviction_policy_section: 'Verdrängungspolitik',
            session_timeout: 'Sessiouns-Timeout (Minutten)',
            session_max_concurrent: 'Max. gläichzäiteg Sessiounen',
            session_inactivity_warning: 'Inactivity Warning (minutes)',
            session_timeout_hint: 'Sessions will expire after this duration of total time. Valid range: 5-480 minutes.',
            session_max_concurrent_hint: 'Maximum number of active sessions per user. Valid range: 1-20.',
            session_inactivity_warning_hint: 'Show a warning after this many minutes of inactivity. Must be less than the session timeout.',
            session_timeout_range_error: 'Session timeout must be between 5 and 480 minutes.',
            session_max_concurrent_range_error: 'Max concurrent sessions must be between 1 and 20.',
            session_inactivity_warning_range_error: 'Inactivity warning must be between 1 minute and less than the session timeout.',
            session_settings_load_error: 'Failed to load session settings.',
            session_settings_save_error: 'Failed to save session settings.',
            eviction_policy_hint: 'What happens when a user exceeds the max concurrent sessions limit.',
            eviction_policy_reject: 'Reject New Login - Prevent new login until an existing session is terminated',
            eviction_policy_evict_oldest: 'Evict Oldest Session - Automatically terminate the oldest session to allow new login',
            active_sessions_title: 'Aktiv Sessiounen',
            active_sessions_device: 'Apparat',
            active_sessions_ip: 'IP-Adress',
            active_sessions_started: 'Gestart',
            active_sessions_revoke: 'Sessioun zréckzéien',
            active_sessions_confirm_terminate: 'Sidd Dir sécher, Dir wëllt dës Sessioun ofbriechen?',
            active_sessions_confirm_terminate_current: 'Dëst ass Är aktuell Sessioun. Ofschloss et wäert Iech direkt ausmellen. Fuert weider?',
            active_sessions_confirm_terminate_all: 'Sidd Dir sécher datt Dir all aner Sessiounen ofschléissen wëllt? Nëmmen Är aktuell Sessioun bleift aktiv.',
            active_sessions_terminated: 'Sessioun erfollegräich ofgeschloss.',
            active_sessions_all_terminated: 'All aner Sessiounen goufen ofgeschloss.',
            active_sessions_terminate_all: 'All aner Sessiounen ofschléissen',
            active_sessions_last_activity: 'Déi lescht Aktivitéit',
            active_sessions_current: 'Aktuell Sëtzung',
            active_sessions_revoke_selected: 'Ausgewielten zréckhuelen',
            active_sessions_confirm_terminate_selected: 'Sidd Dir sécher, datt Dir déi ausgewielten Sëtzunge wëllt ofschléissen?',
            active_sessions_selected_terminated: 'Ausgewielten Sëtzungen ofgeschloss.',
            ip_allowlist_desc: 'Verwalte erlaabt IP Adressen fir API Zougang.',
            ip_no_entries: 'Keng IP Adressen konfiguréiert.',
            session_settings_desc: 'Konfiguréieren Sessioun Timeout a concurrent Sessioun Limiten.',
            session_settings_save_policy: 'Retten Sëtzung Politik',
            session_settings_saved: 'Sessioun Astellunge erfollegräich aktualiséiert.',
            session_settings_idle_timeout: 'Idle Timeout (Minuten)',
            session_settings_absolute_timeout: 'Absolute Timeout (Minuten)',
            session_settings_help_idle: 'Sessioun wäert no dëser Period vun Inaktivitéit oflafen.',
            session_settings_help_absolute: "D'Sessioun leeft no dëser Gesamtdauer onofhängeg vun der Aktivitéit of.",
            session_settings_help_max: 'Maximal Zuel vu simultan Sessiounen pro Benotzer.',
            sso_note: "Ännerungen un der SSO Konfiguratioun kënnen d'Benotzer erfuerderen fir nei ze authentifizéieren.",
            service_accounts_warning: 'Dëse Schlëssel API gëtt nëmmen eemol gewisen. Kopéiert et elo a späichert et sécher.',
            service_accounts_new: 'Neie Service Kont',
            service_accounts_name: 'Kont Numm',
            service_accounts_role: 'Roll',
            service_accounts_created: 'Geschaf',
            service_accounts_desc: 'Verwalte Maschinn-zu-Maschinn Servicekonten fir API Integratiounen.',
            break_glass_warning: 'Break-Glas Zougang gëtt iwwerpréift a protokolléiert. Benotzt nëmme fir echt Noutfäll.',
            break_glass_min_chars: '{count} / {min} Minimum Zeechen',
            break_glass_permission_count: '{count} Erlaabnes(n) ausgewielt',
            break_glass_submitted: 'Break-Glas Ufro presentéiert. Waart op Genehmegung.',
            break_glass_approved: 'Break-Glas Ufro guttgeheescht.',
            break_glass_denied: 'Break-Glas Ufro refuséiert.',
            break_glass_confirm_activate: 'Approve this break-glass request? The approval is recorded with the permissions requested and the time it expires. It does not itself change what the requester can do.',
            break_glass_confirm_title: 'Confirméieren Aktivatioun',
            break_glass_pending: 'Pending Ufroen',
            break_glass_active_sessions: 'Approved Break-Glass Requests',
            break_glass_time_remaining: 'Zäit Rescht',
            break_glass_expired: 'Ofgelaaf',
            break_glass_approved_denied_by: 'Genehmegt / Dementéiert Vun',
            delegations_desc: 'Managen temporär Erlaabnis Delegatiounen tëscht Benotzer.',
            delegations_my: 'Meng Delegatiounen',
            delegations_to_me: 'Delegéiert fir mech',
            delegations_scope_hint: 'An “all my permissions” delegation carries what you hold today. It does not change if your role changes later.',
            delegations_window_hint: 'The delegate holds these permissions from 00:00 on the start day until 23:59 on the end day, in your time zone, or until you revoke the delegation, checked on every request. Each use is recorded in the security audit trail.',
            delegations_start_in_past: 'The start cannot be before today.',
            delegations_end_before_start: 'The end must be after the start.',
            delegations_legacy_all: 'Grants nothing: made before delegations recorded what they carry. Revoke it and create a new one.',
            delegations_not_delegable_hint: 'Permissions held only through a named custom role or by no organisation, and single sign-on and group-user administration, cannot be delegated and are not listed.',
            delegations_reason: 'Reason',
            delegations_start: 'Start',
            delegations_end: 'End',
            delegations_scope_all: 'All Permissiounen',
            delegations_scope_specific: 'Spezifesch Permissiounen',
            delegations_created: 'Delegatioun erfollegräich erstallt.',
            delegations_revoked: 'Delegatioun zréckgezunn.',
            delegations_confirm_revoke: 'Sidd Dir sécher, Dir wëllt dës Delegatioun zréckzéien?',
            delegations_confirm_revoke_title: 'Confirméieren Revoke',
            delegations_permission_count: '{count} Erlaabnis(en)',
            delegations_no_longer_delegable: 'No longer delegable, so not granted: {permissions}',
            api_keys_status: 'Status',
            api_keys_prefix: 'Schlëssel Präfix',
            api_keys_permissions_count: 'Permissiounen',
            api_keys_ip_count: 'IP Restriktiounen',
            api_keys_created: 'Geschaf',
            api_keys_confirm_revoke_title: 'Confirméieren Revoke',
            api_keys_confirm_revoke_message: 'Sidd Dir sécher datt Dir den API Schlëssel "{name}" zréckzéien wëllt? Dës Aktioun kann net réckgängeg gemaach ginn.',
            api_keys_confirm_rotate_title: 'Confirméieren Rotatioun',
            api_keys_confirm_rotate_message: 'Rotate the API key "{name}"? A new key is issued now; the current key keeps working for 24 hours so you can replace it, then stops. If the key may be compromised, revoke it instead.',
            api_keys_created_success: 'API Schlëssel erfollegräich erstallt.',
            api_keys_revoked_success: 'API Schlëssel "{name}" gouf zréckgezunn.',
            api_keys_rotated_success: 'API Schlëssel "{name}" gouf rotéiert. Kopéiert den neie Schlëssel hei drënner.',
            api_keys_load_error: 'Ausgefall fir API Schlësselen ze lueden.',
            api_keys_create_error: 'Ausgefall fir API Schlëssel ze kreéieren.',
            api_keys_revoke_error: 'Ausgefall API Schlëssel zréckzéien.',
            api_keys_rotate_error: 'Ausgefall fir API Schlëssel ze rotéieren.',
            api_keys_allowed_origins: 'Allowed origins',
            api_keys_allowed_origins_hint: 'One origin per line: scheme, host and optional port, e.g. https://app.example.com. When set, a request must carry an Origin header that matches one entry exactly, or it is refused. Leave empty for a server-to-server key.',
            api_keys_rate_limit: 'Requests per minute',
            api_keys_rate_limit_hint: 'Leave empty for no limit. Over the limit, a request is refused with 429 and a Retry-After header until the next minute starts.',
            api_keys_rate_limit_invalid: 'Requests per minute must be a whole number from 1 to 10,000.',
            api_keys_status_at_creation: 'Status at creation',
            api_keys_status_option_active: 'Active: the key works as soon as it is created',
            api_keys_status_option_inactive: 'Inactive: the key authenticates nothing until you activate it',
            api_keys_col_origins: 'Origins',
            api_keys_col_rate_limit: 'Limit per minute',
            api_keys_no_limit: 'No limit',
            api_keys_edit_title: 'Edit API key',
            api_keys_save: 'Save changes',
            api_keys_updated_success: 'API key "{name}" has been updated.',
            api_keys_update_error: 'Failed to update API key.',
            api_keys_edit_action: 'Edit key {name}',
            api_keys_activate_action: 'Activate key {name}',
            api_keys_pause_action: 'Pause key {name}',
            api_keys_rotate_action: 'Rotate key {name}',
            api_keys_revoke_action: 'Revoke key {name}',
            api_keys_copy_raw_key: 'Copy API key to clipboard',
            api_keys_activated_success: 'API key "{name}" is active.',
            api_keys_paused_success: 'API key "{name}" is paused. It authenticates nothing until it is activated.',
            api_keys_status_error: 'Failed to change the API key status.',
            api_keys_status_rotated: 'Rotated',
            api_keys_rotated_grace_until: 'Still works until {time}. Revoke it to stop it now.',
            service_accounts_load_error: 'Failed to load service accounts.',
            service_accounts_created_success: 'Service account created successfully.',
            service_accounts_create_error: 'Failed to create service account.',
            service_accounts_confirm_deactivate_title: 'Confirm Deactivation',
            service_accounts_confirm_deactivate_message: 'Are you sure you want to deactivate the service account "{name}"? All associated API keys will stop working.',
            service_accounts_deactivated_success: 'Service account "{name}" has been deactivated.',
            service_accounts_deactivate_error: 'Failed to deactivate service account.',
            service_accounts_deactivate_action: 'Deactivate service account {name}',
            service_accounts_table_label: 'Service accounts',
            service_accounts_name_placeholder: 'e.g., Data Sync Bot',
            service_accounts_purpose_placeholder: 'e.g., Automated data synchronization',
            permission_picker_selected_count: '{count} selected',
            permission_picker_result_count: '{count} of {total} permissions shown',
            permission_picker_selected_heading: 'Selected permissions',
            permission_picker_remove: 'Remove {permission}',
            break_glass_duration_15: '15 Minutten',
            break_glass_duration_30: '30 Minutten',
            break_glass_duration_60: '60 Minutten (1h)',
            break_glass_duration_120: '120 Minutten (2h)',
            break_glass_duration_240: '240 Minutten (4h)',
            sod_rules_title: 'Segregatioun vun Flichten Regelen',
            sod_violations_report: 'Violatioune Rapport',
            sod_create_rule: 'Schafen Regel',
            sod_col_name: 'Numm',
            sod_col_description: 'Beschreiwung',
            sod_col_conflict_count: 'Konflikt Zuel',
            sod_col_severity: 'Gravitéit',
            sod_col_status: 'Status',
            sod_col_actions: 'Aktiounen',
            sod_no_rules: 'Keng SoD Regele fonnt',
            sod_severity_warning: 'Warnung',
            sod_severity_blocking: 'Blockéieren',
            sod_active: 'Aktiv',
            sod_inactive: 'Inaktiv',
            sod_edit: 'Edit',
            sod_exceptions: 'Ausnahmen',
            sod_deactivate: 'Deaktivéieren',
            sod_confirm_deactivate_title: 'Confirméieren Deaktivéierung',
            sod_confirm_deactivate_message: 'Regel "{name} deaktivéieren? Et wäert net méi Violatioune Fändel.',
            sod_deactivate_error: 'Ausgefall Regel ze deaktivéieren',
            sod_rule_count: '{count} rules',
            active_sessions_loaded: 'Active sessions loaded',
            api_keys_loaded: 'API keys loaded',
            backup_codes_regenerated: 'Backup codes regenerated successfully.',
            break_glass_loaded: 'Break glass data loaded',
            delegations_loaded: 'Delegations loaded',
            ip_added: 'IP address added.',
            ip_allowlist_loaded: 'IP allowlist loaded',
            ip_removed: 'IP address removed.',
            mfa_disabled: 'MFA has been disabled.',
            mfa_status_loaded: 'MFA status loaded',
            service_accounts_loaded: 'Service accounts loaded',
            session_settings_loaded: 'Session settings loaded',
            // ── Sprint 63.7.0: organisation-level MFA enforcement + IP allowlist enforcement ──
            mfa_org_enforcement_heading: 'Organisation enforcement',
            // fix-sprint-20261017-1500 F6 — a failed policy read is stated, with a retry.
            mfa_org_policy_read_failed: "The organisation's MFA policy could not be read, so its current level is not shown.",
            mfa_org_policy_retry_subject: "the organisation's MFA policy",
            mfa_org_enforcement_intro: 'Choose whether this organisation requires a second factor. The default is Optional, which is what the platform has always done: people who set up an authenticator are asked for a code, and people who do not are not. Raising the level never locks anyone out — a person who is required to have a second factor and does not yet have one is asked to set one up at their next sign-in, and can complete that themselves.',
            mfa_level_optional: 'Optional',
            mfa_level_optional_desc: 'Each person decides. Anyone who has set up an authenticator is asked for a code; anyone who has not signs in with their password alone.',
            mfa_level_admin_required: 'Required for administrators',
            mfa_level_admin_required_desc: "Anyone whose role can change this organisation's security settings must have a second factor. This is decided by what the role can do, not by what the role is called, so your own custom roles are covered too.",
            mfa_level_all_required: 'Required for everyone',
            mfa_level_all_required_desc: 'Everyone in this organisation must have a second factor.',
            mfa_enforcement_current: 'Current',
            mfa_enforcement_apply: 'Use this level',
            mfa_unenrolled_count: '{count} people would be asked to set up an authenticator at their next sign-in.',
            mfa_enforcement_confirm_title: 'Change the enforcement level?',
            mfa_enforcement_confirm_message: 'This sets the level to {level}. {count} people will be asked to set up an authenticator the next time they sign in. They can complete it themselves and nobody is locked out.',
            mfa_enforcement_saved: 'Enforcement level updated.',
            mfa_enforcement_save_failed: 'The enforcement level could not be changed.',
            mfa_enforcement_no_lockout: 'Raising the level cannot lock this organisation out. Someone who is required to have a second factor and does not yet have one still signs in; they are taken to a setup screen and can finish it without help.',
            mfa_admin_heading: 'Two-factor authentication',
            mfa_admin_enrolled: 'Set up',
            mfa_admin_not_enrolled: 'Not set up',
            mfa_admin_backup_codes_remaining: '{count} unused backup codes',
            mfa_admin_reason_label: 'Reason',
            mfa_admin_reason_hint: 'At least 20 characters. This is recorded in the audit trail and is the only lasting record of why.',
            mfa_admin_reset: 'Reset',
            mfa_admin_disable: 'Turn off',
            mfa_admin_revoke_codes: 'Revoke backup codes',
            mfa_admin_action_done: 'Done. This person has been notified.',
            mfa_admin_action_failed: 'That could not be completed.',
            // ── fix-sprint-20260921-2110: EN placeholders pending Phase 25 (R-57) ──
            mfa_page_intro: 'Set up and manage the second verification step for your own account. If your organisation requires one, the level it has chosen is shown below.',
            mfa_not_enrolled_intro: 'Protect your account with a second verification step.',
            mfa_scan_instruction: 'Scan the QR code with your authenticator app, then enter the 6-digit code it shows to confirm.',
            mfa_qr_alt: 'QR code holding the setup key for your authenticator app. If you cannot scan it, the same key is written out in the field below.',
            mfa_secret_manual_label: 'Or enter this setup key manually',
            mfa_confirm_code_label: 'Enter the 6-digit code from your authenticator app',
            mfa_enrolled_description: 'This account is protected by a second verification step.',
            mfa_enabled_since: 'In use since {date}.',
            mfa_status_load_failed: 'Your two-factor status could not be loaded.',
            mfa_enroll_failed: 'Setting up a second factor could not be started.',
            mfa_invalid_code: 'That code was not accepted. Check your authenticator app and enter the code it shows now.',
            mfa_regenerate_failed: 'New backup codes could not be generated.',
            mfa_disable_confirm_title: 'Turn off two-factor authentication?',
            mfa_disable_confirm_message: 'Your account will be protected by its password alone, which makes it easier for someone else to sign in as you. You can set up a second factor again at any time.',
            mfa_disable_failed: 'Two-factor authentication could not be turned off.',
            mfa_backup_codes_heading: 'Your backup codes',
            mfa_backup_codes_save_now: 'These codes are shown once and cannot be shown again. Save them somewhere safe now — each one signs you in if you lose your authenticator, and each works only once.',
            mfa_backup_codes_file_heading: 'AesirX ComplianceOne — MFA backup codes',
            mfa_backup_codes_file_notice: 'Store these codes in a safe place. Each code can only be used once.',
            mfa_backup_codes_file_generated: 'Generated: {date}',
            mfa_enforcement_apply_named: 'Use this level: {level}',
            mfa_admin_no_read_notice: 'You can see whether this person has a second factor and you can take it away. You cannot see what it is, and you cannot set one up on their behalf.',
            ip_enforcement_heading: 'Enforcement',
            ip_enforcement_dry_run: 'This list is not refusing anything. It is being checked against every signed-in request and the results are recorded, so you can see who would be refused before anyone is.',
            ip_enforcement_active: 'Enforcing since {date}.',
            ip_enforcement_would_block: '{count} recorded occasions in the last 30 days where a signed-in request came from an address this list does not cover.',
            ip_enforcement_would_block_zero: 'No signed-in request from an address outside this list has been recorded in the last 30 days. That can also mean nobody has signed in, so it is a reason to look rather than an all-clear.',
            ip_enforcement_confirm_button: 'Start enforcing',
            ip_enforcement_withdraw_button: 'Stop enforcing',
            ip_enforcement_confirm_title: 'Start refusing requests from outside this list?',
            ip_enforcement_confirm_message: 'From now on, a signed-in request from an address this list does not cover is refused.',
            ip_enforcement_confirmed: 'This list is now being enforced.',
            ip_enforcement_withdrawn: 'This list is no longer being enforced.',
            ip_enforcement_sessions_note: 'Turning this on does not sign anybody out. Each request is judged as it arrives, so a session from an address that is no longer covered simply stops working. To end sessions now, use Active Sessions.',
            ip_enforcement_edit_withdraws: 'Editing the ranges stops enforcement until you confirm again, because the recorded figures you confirmed against were about the old list.',
            ip_configure: 'Configure',
            ip_configure_title: 'Configure IP Allowlist',
            ip_configure_enabled_hint: 'When enabled, only requests from listed IP addresses will be allowed.',
            ip_enforcement_mode_label: 'Enforcement Mode',
            ip_enforcement_mode_all_access: 'All Access — restrict both UI and API',
            ip_enforcement_mode_api_keys_only: 'API Keys Only — only restrict API key access',
            ip_address_placeholder: '203.0.113.1 or 192.168.1.0/24',
            ip_description_placeholder: 'e.g., Office network',
            ip_entries_table_label: 'IP allowlist entries',
            ip_remove_action: 'Remove {cidr} from allowlist',
            ip_remove_confirm_title: 'Confirm Remove',
            ip_remove_confirm_message: 'Are you sure you want to remove {cidr} from the allowlist?',
            ip_load_failed: 'Failed to load IP allowlist settings.',
            ip_cidr_invalid: 'Invalid format. Enter a single IP (203.0.113.1) or CIDR range (192.168.1.0/24)',
            ip_cidr_duplicate: 'This CIDR is already in the allowlist.',
            ip_add_failed: 'Failed to add IP address.',
            ip_remove_failed: 'Failed to remove IP address.',
            ip_save_failed: 'Failed to save settings.',
            ip_enforcement_confirm_caller_covered: 'You are connecting from {ip}, and this list covers it, so you will not lose access to this screen.',
            ip_enforcement_confirm_caller_not_covered: 'You are connecting from {ip}, which this list does not cover. Confirming is refused rather than locking you out, so add this address to the list first.',
            ip_enforcement_confirm_caller_unknown: 'Whether this list covers the address you are connecting from could not be established on this screen. It is checked again when you confirm, and a confirmation that would lock you out is refused.',
            ip_enforcement_readiness_unreadable: 'The 30-day record of what this list would have refused could not be read, so this screen cannot show you the figure.',
            sso_metadata_url: 'Metadata / discovery URL',
            sso_metadata_url_hint: 'The OpenID Connect discovery or metadata document published by the identity provider. This is not the redirect URI: the redirect URI to register with the provider is shown above, is derived by the platform, and cannot be typed here.',
            sso_client_secret: 'Client Secret',
            sso_issuer_placeholder: 'https://login.example.com',
            sso_sp_entity_id_placeholder: 'https://grc.example.com/saml/metadata',
            sso_configs_table_label: 'SSO configurations',
            sso_configs_unreadable: 'The SSO configurations could not be read, so this screen cannot say whether this organisation has any.',
            sso_configs_retry: 'Reload',
            sso_configs_retry_busy: 'Reloading…',
            sso_configs_retry_subject: 'the SSO configurations',
            sso_load_failed: 'Failed to load SSO configurations.',
            sso_report_read_failed: 'Failed to read the verification report.',
            sso_save_failed: 'Failed to save SSO configuration.',
            sso_delete_failed: 'Failed to delete SSO configuration.',
            sso_certificate_add_failed: 'Failed to add the certificate.',
            sso_verify_start_failed: 'Failed to start the verification run.',
            sso_enforcement_on_failed: 'Failed to turn on SSO-only sign-in.',
            sso_enforcement_off_failed: 'Failed to turn off SSO-only sign-in.',
            sso_updated: 'SSO configuration updated successfully.',
            sso_created: 'SSO configuration created successfully.',
            sso_delete_confirm_title: 'Confirm Delete',
            sso_delete_confirm_message: 'Are you sure you want to delete the SSO configuration "{name}"?',
            sso_deleted: 'SSO configuration "{name}" deleted.',
            sso_delete_end_sessions: "Also end this provider's sessions",
            sso_delete_end_sessions_hint: 'Deleting a configuration stops new sign-ins through it but does not end the sessions it has already started. Leave this ticked to end them now. If you untick it, you can end them later under "Deleted configurations".',
            sso_delete_sessions_not_ended: 'The configuration was deleted, but its sessions could not be ended. End them under "Deleted configurations".',
            sso_deleted_heading: 'Deleted configurations',
            sso_deleted_intro: 'These configurations no longer accept sign-ins. Deleting a configuration does not end the sessions it started; end them here with "End this provider\'s sessions".',
            sso_deleted_table_label: 'Deleted SSO configurations',
            sso_deleted_unreadable: 'The deleted SSO configurations could not be read.',
            sso_deleted_retry_subject: 'the deleted SSO configurations',
            sso_configs_loaded: 'SSO configurations loaded',
            active_sessions_select_aria: 'Select session from {ip}',
            active_sessions_terminate_aria: 'Terminate session from {ip}',
            active_sessions_terminate_current_aria: 'Terminate session from {ip} (current session)',
            active_sessions_unknown_ip: 'unknown IP',
            active_sessions_select_all_aria: 'Select all sessions',
        },
        access_reviews: {
            title: 'Zougangsiwwerpréifungen',
            campaigns: 'Iwwerpréifungskampagnen',
            new_campaign: 'Nei Kampagne',
            campaign_name: 'Kampagnennumm',
            scope: 'Ëmfang',
            reviewers: 'Revisoren',
            due_date: 'Fällegkeetsdatum',
            progress: 'Fortschrëtt',
            my_reviews: 'Meng Iwwerpréifungen',
            approve: 'Genehmegen',
            revoke: 'Zréckzéien',
            analytics: 'Rollen-Analytik',
            analytics_title: 'Rollen-Analytik',
            permission_usage: 'Berechtigungs-Notzung',
            role_coverage: 'Rollen-Ofdeckung',
            campaign_detail: 'Campagne Detailer',
            campaign_description: 'Beschreiwung',
            campaign_status: 'Status',
            campaign_deadline: 'Frist',
            campaign_scope_type: 'Ëmfang Typ',
            campaign_completion: 'Fäerdegstellung',
            campaign_total_items: 'Ganzen Artikelen',
            campaign_pending_items: 'Erwaardung',
            campaign_confirmed_items: 'Confirméiert',
            campaign_revoked_items: 'Entlooss',
            campaign_escalated_items: 'Eskaléiert',
            campaign_launch: 'Lancéiere Campagne',
            campaign_cancel: 'Campagne annuléieren',
            campaign_confirm_launch: 'Sidd Dir sécher datt Dir dës Campagne lancéiere wëllt?',
            campaign_confirm_cancel: 'Sidd Dir sécher datt Dir dës Campagne annuléiere wëllt?',
            campaign_launched: 'Campagne erfollegräich lancéiert.',
            campaign_cancelled: 'Campagne annuléiert.',
            review_items: 'Iwwerpréiwen Artikelen',
            review_item_user: 'Benotzer',
            review_item_role: 'Roll',
            review_item_reviewer: 'Rezensor',
            review_item_decision: 'Entscheedung',
            review_item_justify: 'Justifikatioun',
            review_item_confirm: 'Confirméieren',
            review_item_revoke: 'Revokéieren',
            review_item_escalate: 'Eskaléieren',
            all_decisions: 'All Entscheedungen',
            bulk_action: 'Bulk Aktioun',
            bulk_confirm: 'Bulk Confirméieren',
            bulk_revoke: 'Bulk Revoque',
            selected_count: '{count} ausgewielt',
            justify_required: 'Justifikatioun ass néideg',
            scope_all_users: 'All Benotzer',
            scope_role_owner: 'Roll Besëtzer',
            scope_department: 'Departement',
            back_to_campaigns: 'Zréck op Campagnen',
            recommendations: 'Recommandatiounen',
            refreshing: 'Erfrëschend ...',
            decide: 'Entscheeden',
            decision_by: 'Entscheedung vum',
            loading_items: 'Lueden Artikelen ...',
            loading_review_items: 'Lueden Bewäertungsartikelen ...',
            campaign_not_found: 'Kampagne net fonnt',
            loading_campaign: "Lueden d'Campagnedetailer ...",
            campaign_completed: 'Campagne erfollegräich ofgeschloss.',
            confirm_cancel_title: 'Confirméieren Ofbriechen',
            justify_bulk_confirm: 'Begrënnung fir bulk confirméieren:',
            justify_bulk_revoke: 'Begrënnung fir bulk Réckzuch:',
            items_selected: '{count} Element(en) ausgewielt',
            justify_bulk_required: 'Justifikatioun ass erfuerderlech fir bulk Aktiounen',
            create_campaign_title: 'Erstellt Zougang Bewäertungskampagne',
            name_required: 'Numm ass néideg',
            scope_type_required: 'Ëmfang Typ ass erfuerderlech',
            reviewer_strategy_required: 'Bewäertungsstrategie ass erfuerderlech',
            deadline_required: 'Frist ass erfuerderlech',
            reviewer_id_required: 'Rezensor ID ass fir spezifesch Benotzerstrategie erfuerderlech',
            invalid_json: 'Invalid JSON',
            scope_filter_label: 'Ëmfang Filter (JSON)',
            reviewer_strategy_label: 'Bewäertung Strategie',
            reviewer_id_label: 'Bewäertung ID',
            deadline_label: 'Frist',
            create_campaign_btn: 'Erstelle Campagne',
            scope_type_label: 'Ëmfang Typ',
            scope_by_role: 'Duerch Roll',
            scope_by_department: 'Vun Departement',
            scope_custom: 'Benotzerdefinéiert',
            reviewer_manager: 'Manager',
            reviewer_specific_user: 'Spezifesch Benotzer',
            enter_json_filter: 'Gitt JSON Filter an',
            hint_label: 'Hiweis:',
            role_usage: 'Roll Benotzung',
            over_privileged_users: 'Iwwer-Privilegéiert Benotzer',
            role_name_col: 'Roll Numm',
            assigned_users: 'Zougewisen Benotzer',
            active_users_30d: 'Aktiv Benotzer (30d)',
            total_perms: 'Total Perms',
            used_perms: 'Benotzt Perms',
            unused_perms: 'Onbenotzt Perms',
            no_data_available: 'Keng Daten verfügbar',
            roles_col: 'Rollen',
            total_col: 'Ganzen',
            used_col: 'Benotzt',
            unused_pct: 'Onbenotzt %',
            current_count: 'Aktuelle Grof',
            recommended_count: 'Recommandéiert Grof',
            rationale: 'Begrënnung',
            loading_analytics: 'Lueden Analyse ...',
            loading_role_analytics: 'Lueden Roll analytesch Daten ...',
            computed_at: 'Berechent op:',
            form_errors: "D'Form huet {count} Feeler(en). Korrigéiert se w.e.g.",
            scope_filter_invalid_json: 'Ëmfangfilter enthält ongülteg JSON',
            scope_hint_all_users: 'Kee Filter brauch - all Benotzer ginn abegraff.',
            review_type_label: 'Iwwerpréifungstyp',
            review_type_required: 'Iwwerpréifungstyp ass erfuerderlech',
            reviewer_ids_label: 'Iwwerpréifer-IDs',
            reviewer_ids_hint: 'Gitt Benotzer-IDs aginn, getrennt duerch Komma',
            reviewer_ids_required: 'Mindestens eng Iwwerpréifer-ID ass erfuerderlech',
            start_date_label: 'Ufanksdatum',
            start_date_required: 'Ufanksdatum ass erfuerderlech',
            end_date_label: 'Enddatum',
            end_date_required: 'Enddatum ass erfuerderlech',
            review_type_user_access: 'Benutzerzougang',
            review_type_role_permissions: 'Rollberechtigungen',
            review_type_service_accounts: 'Servicekonte',
            review_type_privileged_access: 'Privilegéierte Zougang',
            review_type_application_access: 'Applikatiounszougang',
            auto_revoke_label: 'Auto-Widerruf bei Oflehnung',
            campaigns_title: 'Zougang Kritik Campagnen',
            create_campaign_label: '+ Kampagne erstellen',
            no_campaigns_found: 'Keng Campagnen fonnt',
            loading_campaigns: 'Luede Kampagnen ...',
            no_reviews_assigned: 'Keng Bewäertunge ginn Iech zougewisen',
            loading_reviews: 'Lued Rezensiounen ...',
            review_decision_title: 'Iwwerpréiwen Decisioun',
            justification_label: 'Justifikatioun',
            announce_loaded_review_items: 'Lueden {count} iwwerpréiwen Artikelen',
            announce_loaded_campaigns: 'Luede {count} Kampagnen',
            announce_decision_submitted: 'Entscheedung "{decision}" fir Iwwerpréiwung Element presentéiert',
            announce_decision_submitted_for: 'D\'Entscheedung "{decision}" proposéiert fir {name}',
            announce_bulk_decision: 'Bulk {decision} op {count} Elementer applizéiert',
        },
        attestation: {
            training_ids_label: 'Training Covered',
            training_ids_help: 'The training courses this attestation covers. The link is carried into each campaign the schedule generates.',
            training_ids_none: 'No training linked',
            training_ids_count: '{count} course(s)',
            training_ids_load_error: 'Training courses could not be read. The list may be incomplete.',
            title: 'Attestatiounspläng',
            new: 'Neie Plang',
            edit: 'Plang änneren',
            create_title: 'Attestatiounsplang erstellen',
            edit_title: 'Attestatiounsplang änneren',
            schedule_history: 'Planghistoire',
            next_run_date: 'Nächsten Ausféierungsdatum',
            schedule_name: 'Plangnumm',
            frequency: 'Frequenz',
            next_run: 'Nächste Laf',
            last_run: 'Leschte Laf',
            history: 'Attestatiounsgeschicht',
            description: 'Beschreiwung',
            scope: 'Ëmfang',
            status: 'Status',
            created_at: 'Geschaf',
            updated_at: 'Aktualiséiert',
            trigger_now: 'Ausléiser Elo',
            back_to_schedules: 'Zréck op Zäitplang',
            confirm_trigger: 'Trigger this schedule now?',
            confirm_delete: 'Delete this schedule?',
            triggered: 'Zäitplang erfollegräich ausgeléist.',
            deleted: 'Zäitplang geläscht.',
            saved: 'Zäitplang erfollegräich gespäichert.',
            campaigns_generated: 'Campagnen generéiert',
            run_date: 'Run Datum',
            run_status: 'Run Status',
            create_schedule: 'Schafen Zäitplang',
            view_history: 'View Geschicht',
            deactivate: 'Deaktivéieren',
            overdue: 'OVERDUE',
            overdue_sr: 'De Zäitplang ass eriwwer',
            confirm_trigger_title: 'Confirméieren Aktioun',
            confirm_trigger_message: 'Are you sure you want to trigger this schedule now?',
            confirm_deactivate_title: 'Confirméieren Deaktivéierung',
            confirm_deactivate_message: 'Sidd Dir sécher datt Dir dësen Zäitplang desaktivéiere wëllt?',
            triggered_success: 'Zäitplang erfollegräich ausgeléist.',
            triggered_unmapped_reviewer: 'The cycle was started, but the reviewer rule "{strategy}" has no source in the platform, so the campaign names no reviewer: whoever launches it reviews its items.',
            trigger_scope_unresolvable: 'This schedule’s scope cannot be turned into a review campaign. Edit the schedule so its scope filter names at least one role, department or user, or choose a supported scope.',
            trigger_reviewer_not_found: 'The reviewer named on this schedule is no longer an active user of this organisation. Edit the schedule and choose another reviewer.',
            reviewer_not_found: 'The reviewer ID does not name an active user of this organisation',
            scope_unsupported: 'This scope type is not supported: a review campaign scopes by user, role and department only',
            deactivated_success: 'Zäitplang deaktivéiert.',
            name_label: 'Numm*',
            frequency_label: 'Frequenz *',
            scope_type_label: 'Ëmfang Typ *',
            scope_filter_label: 'Ëmfang Filter (JSON)',
            reviewer_strategy_label: 'Bewäertungsstrategie *',
            reviewer_id_label: 'Bewäertung ID *',
            frequency_quarterly: 'Trimester',
            frequency_semi_annual: 'Semi-jährlechen',
            frequency_annual: 'Jährlech',
            scope_all_users: 'All Benotzer',
            scope_by_role: 'Duerch Roll',
            scope_by_department: 'Vun Departement',
            scope_custom: 'Benotzerdefinéiert',
            reviewer_manager: 'Manager',
            reviewer_role_owner: 'Roll Besëtzer',
            reviewer_specific_user: 'Spezifesch Benotzer',
            computed_next_run_date: 'Berechent nächst Run Datum',
            current_scheduled_date: 'Aktuell geplangten Datum',
            recomputed_frequency_changed: '(recomputed - Frequenz geännert)',
            estimated_days_hint: 'Geschätzte baséiert op {days} Deeg vun haut',
            validation_name_required: 'Numm ass néideg',
            validation_frequency_required: 'Frequenz ass néideg',
            validation_scope_type_required: 'Ëmfang Typ ass erfuerderlech',
            validation_reviewer_strategy_required: 'Bewäertungsstrategie ass erfuerderlech',
            validation_reviewer_id_required: 'Rezensor ID ass fir spezifesch Benotzerstrategie erfuerderlech',
            validation_invalid_json: 'Invalid JSON',
            validation_scope_filter_invalid_json: 'Ëmfangfilter enthält ongülteg JSON',
            form_errors: "D'Form huet {count} Feeler(en). Korrigéiert se w.e.g.",
            create_schedule_btn: 'Schafen Zäitplang',
            schedule_history_title: 'Zäitplang Geschicht',
            campaign_name_col: 'Campagne Numm',
            items_col: 'Artikelen',
            no_campaigns_yet: 'Keng Campagnen generéiert nach',
            loading_history: 'Luede Geschicht ...',
            loading_schedule: 'Lueden Zäitplang ...',
            total_campaigns: 'Total Campagnen: {count}',
            results_per_page: 'Resultater pro Säit',
            page_size_option: '{size} / Säit',
        },
        sod: {
            title: 'Aufgabentrennung-Reegelen',
            new: 'Nei Reegel',
            edit: 'Reegel änneren',
            create_title: 'SoD-Reegel erstellen',
            edit_title: 'SoD-Reegel änneren',
            rule_name: 'Reegelnumm',
            conflicting_permissions: 'Konfliktberechtigungen',
            permission_a: 'Berechtigung A',
            permission_b: 'Berechtigung B',
            violations: 'SoD-Verletzungen',
            violation_user: 'Benotzer',
            violation_rule: 'Verletzter Reegel',
            violation_date: 'Festgestallt um',
            exceptions: 'SoD-Ausnahmen',
            exception_reason: "Grond fir d'Ausnahm",
            exception_approver: 'Genehmegt vun',
            exception_status: 'Ausnam Status',
            has_exception: 'Huet Ausnam',
            no_exception: 'Keng Ausnam',
            grant_exception: 'Grant Ausnam',
            granting: 'Erlaabt ...',
            grant_new_exception: 'Grant New Ausnam',
            exceptions_for: 'Ausnahmen fir: {name}',
            back_to_rules: 'Zréck op Regelen',
            no_violations: 'Keng Violatioune fonnt',
            no_exceptions_found: 'Keng Ausnahmen fir dës Regel fonnt',
            all_exception_statuses: 'All Ausnam Status',
            user_id_label: 'Benotzer ID',
            justification_label: 'Justifikatioun',
            compensating_controls_label: 'Kompenséieren Kontrollen',
            expires_at_label: 'Erléist um',
            granting_exception_for: 'Eng Ausnahm fir Regel ginn: {name}',
            severity: 'Gravitéit',
            severity_warning: 'Warnung',
            severity_blocking: 'Blockéieren',
            violation_count: '{count} Violatioun(en)',
            exception_count: '{count} Ausnam(en)',
            rule_description: 'Beschreiwung',
            confirm_delete: 'Sidd Dir sécher, datt Dir dës Regel läschen wëllt?',
            deleted: 'Regel geläscht.',
            framework_code: 'Regelementarescht Kader',
            name_label: 'Numm*',
            severity_label: 'Gravitéit *',
            conflict_pairs_legend: 'Konflikt Erlaabnis Pairen',
            conflict_pairs_hint: 'All Pair definéiert zwou Permissiounen déi net vum selwechte Benotzer solle gehal ginn.',
            placeholder_permission_a: '-- Erlaabnes A --',
            placeholder_permission_b: '-- Erlaabnes B --',
            permission_a_for_pair: 'Erlaabnis A fir Pair {index}',
            permission_b_for_pair: 'Erlaabnis B fir Pair {index}',
            conflict_pair_label: 'Konfliktpaar {index}',
            remove_conflict_pair: 'Konfliktpaar ewechhuelen {index}',
            add_pair: '+ Pair addéieren',
            placeholder_name: 'z.B. Bezuelen Genehmegung Trennung',
            create_rule: 'Schafen Regel',
            loading_rule: 'Lueden SoD Regel ...',
            failed_load_rule: 'Ausgefall Regel ze lueden.',
            failed_create_rule: 'Ausgefall SoD Regel ze kreéieren.',
            failed_update_rule: 'Ausgefall SoD Regel ze aktualiséieren.',
            validation_name_required: 'Numm ass néideg.',
            validation_pair_required: "Op d'mannst ee Konflikt Erlaabnis Pair ass néideg.",
            validation_pair_both_required: 'Pair {index}: Béid Permissiounen musse gewielt ginn.',
            validation_pair_must_differ: 'Pair {index}: Erlaabnis A an Erlaabnis B mussen ënnerschiddlech sinn.',
            added_conflict_pair: 'Konfliktpaar dobäigesat {index}',
            removed_conflict_pair: 'Geläscht Konfliktpaar {index}',
            announce_loaded_violations: 'Lueden {count} Violatioune',
            announce_loaded_exceptions: 'Lueden {count} Ausnahmen',
            announce_loaded_rules: 'Lueden {count} SoD Regelen',
            announce_loading_exceptions: 'Lueden Ausnamen…',
            regulatory_info: {
                sod_context: 'SoD-Reegelen setzen dei vun der PDPL gefuerdert regulatoresch Rollentrennung duerch. Verstoess mat engem framework_code koennen a kaderspecifesch Noweispakete abezu ginn (UC-VN-09).',
                violation_export_context: 'SoD-Verstoussexporter enthalen framework_code, Konfliktrollen, Schweregrad, Ersteller an Detektiounszeitpunkt fir Noweispakete (P-VN-10).',
                exception_context: 'SoD-Ausnahmen produzeren pruefungsfaeheg Beweiser: Genehmiger, Begruendung, kompenserend Kontrollen an Verfallsdatum. Ofgelafe Ausnahme bleiwen siichtbar (UC-VN-09 Schratt 5).',
            },
        },
        regulatory_info: {
            attestation_context: 'Attestation schedules repeat an access review on a cycle (quarterly, semi-annual or annual). Each cycle creates a review campaign for all users, for named roles or departments, or for named users, and a named reviewer answers it. A reviewer rule the campaign model cannot express (manager or role owner) is shown when the cycle is started, and whoever launches the campaign then reviews its items. Schedule history lists the campaigns each schedule has generated.',
            api_keys_context: 'API key telemetry constitutes account evidence per VN_CYBERSEC_LAW_2025 Section B.5. Each key record includes last_used_at, ip_allowlist, permissions, status, and expires_at.',
            session_context: 'Session management enforces access controls per Telecom Law confidentiality requirements (Section B.4). Session events produce audit trail entries.',
            break_glass_context: 'Break-glass procedures produce regulatory justification evidence: requestor identity, justification, permissions, duration, approver identity, and approval/denial timestamps.',
            license_context: 'License settings gate modules by tier using PACKAGE_DEFINITIONS. Framework-specific modules require appropriate license tiers. Tier changes produce audit trail entries.',
            module_config_context: 'Module configuration supports framework-driven enablement. Disabling a module required by an active regulatory framework may break evidence completeness.',
            notification_context: 'Compliance-critical notifications (breach deadlines, attestation due dates) are marked with a compliance_critical flag and cannot be fully suppressed.',
            delegation_context: 'Delegation governance produces audit-grade evidence: delegator_id, delegate_id, permission scope, start/end dates, and status. Expired delegations remain visible.',
            access_control_context: 'Access control configuration meets Cybersecurity Law requirements (VN_CYBERSEC_LAW_2025): IP allowlist, MFA, service accounts, and SSO changes are audit-trailed.',
        },
    },
    cmp: {
        integration: {
            title: 'CMP-Integratioun',
            status: 'Verbindungsstatus',
            endpoint: 'CMP-Endpunkt',
            last_sync: 'Lescht Synchronisatioun',
            configure: 'Konfiguréieren',
        },
        purpose_mapping: {
            title: 'Zweck-Mapping',
            cmp_purpose: 'CMP-Zweck',
            grc_purpose: 'GRC-Zweck',
            mapping_status: 'Mapping-Status',
            auto_map: 'Automatesch Mapping',
            new_mapping: 'Neit Mapping',
        },
        webhook: {
            title: 'Webhook-Konfiguratioun',
            webhook_url: 'Webhook-URL',
            secret: 'Webhook-Geheimnis',
            events: 'Evenement-Typen',
            test_webhook: 'Webhook testen',
        },
        bulk_sync: {
            title: 'Masse-Synchronisatioun',
            sync_now: 'Elo synchroniséieren',
            last_run: 'Leschte Laf',
            records_synced: 'Synchroniséiert Datensätz',
            sync_history: 'Synchronisatiounsgeschicht',
            syncing: 'Gëtt synchroniséiert...',
        },
        sync_settings: {
            sync_failures: 'Synchroniséierung Failures',
            sync_secret_warning: 'Synchroniséierung Geheimnes (save dëst now — it will not be shown again):',
            push_description: 'Konfiguréieren den GRC Suite op push case Status updates op AesirX CMP.',
            technical_description: "Dës Konfiguratioun kontrolléiert wéi d'GRC Suite Fallstatusupdates op AesirX CMP dréckt.",
            direction_label: 'Richtung',
            direction_value: 'GRC Suite → AesirX CMP (ausgangend)',
            auth_label: 'Authentifikatioun',
            auth_value: 'HMAC-SHA256 signature an X-GRC-Signature header',
            retry_label: 'Retry Richtlinn',
            retry_value: '3 retries mat exponential backoff (30s, 2min, 10min)',
            inbound_label: 'Erakommend Konfiguratioun',
            inbound_value: 'Erakommend Webhook (CMP → GRC) is configured an',
            cmp_webhook_settings: 'CMP Webhook Astellungen',
            page_title: 'CMP Synchronisatiounsastellungen',
            outbound_sync_configured: 'Ausgaang Synchronisatioun konfiguréiert.',
            configure_sync_failed: 'Synchronisatioun konnt net konfiguréiert ginn',
            configuration_updated: 'Konfiguratioun aktualiséiert.',
            update_configuration_failed: 'Konfiguratioun konnt net aktualiséiert ginn',
            connection_test_failed: 'Verbindungstest gescheitert',
            test_result_message: 'Test un {endpoint} geschéckt. Status: {status}',
            endpoint_placeholder: 'https://your-cmp-domain/api/grc-sync',
            events: {
                new_to_under_review: 'Nei → Ënner Iwwerpréiwen (assigned)',
                under_review_to_identity_verification: 'Ënner Iwwerpréiwen → Identity Verifikatioun',
                under_review_to_in_progress: 'Ënner Iwwerpréiwen → In Fortschrëtt',
                in_progress_to_completed: 'In Fortschrëtt → Ofgeschloss',
                in_progress_to_denied: 'In Fortschrëtt → Denied',
                any_to_closed: 'Any → Zougemaach',
            },
        },
    },
    settings_pages: {
        webhooks: {
            title: 'Webhooks',
            new: 'Neie Webhook',
            url: 'URL',
            url_placeholder: 'https://example.com/webhook',
            events: 'Evenementer',
            secret: 'Geheimnis',
            active: 'Aktiv',
            test: 'Testen',
            create_failed: 'Konnt net create Webhook..',
            create_success: 'Webhook erfollegräich erstellt.',
            deactivate_failed: 'Konnt net deactivate Webhook..',
            deactivate_success: 'Webhook deaktivéiert.',
            fetch_failed: 'Konnt net fetch webhooks..',
            send_test_failed: 'Konnt net send test..',
            test_failed: 'Webhook test gescheitert.',
            test_success: 'Webhook-Test erfollegräich.',
            edit_title: 'Webhook änneren',
            announce_loaded: 'Webhooks gelueden',
            update_success: 'Webhook erfollegräich aktualiséiert.',
            update_failed: 'Konnt de Webhook net aktualiséieren.',
            activate_success: 'Webhook aktivéiert.',
            activate_failed: 'Konnt de Webhook net aktivéieren.',
        },
        domains: {
            title: 'Domainen',
            new: 'Nei Domain',
            domain_name: 'Domainennumm',
            verified: 'Verifizéiert',
            verify: 'Verifizéieren',
            add_failed: 'Konnt net add Domän..',
            add_success: 'Domän added successfully.',
            domain_placeholder: 'z.B. grc.example.com',
            fetch_failed: 'Konnt net fetch domains..',
            remove_failed: 'Konnt net remove Domän..',
            remove_success: 'Domän removed successfully.',
            txt_record: 'TXT Datesat',
            verify_dns_failed: 'DNS verification gescheitert.',
            verify_failed: 'Konnt net verify Domän..',
            verify_success: 'Domän verified successfully.',
            announce_loaded: 'Domains loaded',
        },
        export_schedules: {
            title: 'Export-Pläng',
            new: 'Neie Plang',
            schedule_name: 'Numm',
            frequency: 'Frequenz',
            format: 'Format',
            last_run: 'Leschte Laf',
            next_run: 'Nächste Laf',
            create_failed: 'Konnt net create Zäitplang..',
            create_success: 'Plangen created successfully.',
            day_of_month: 'Dag vun Month',
            day_of_week: 'Dag vun Week',
            deactivate_failed: 'Konnt net deactivate Zäitplang..',
            deactivate_success: 'Plangen deactivated.',
            fetch_failed: 'Konnt net fetch schedules..',
            freq_daily: 'Deeglech',
            freq_monthly: 'Monatleg',
            freq_weekly: 'Wëchentlech',
            recipients: 'Empfänger',
            time_utc: 'Zäit (UTC)',
            trigger_failed: 'Konnt net trigger Export..',
            trigger_success: 'Exportéieren triggered successfully.',
            type_audit_trail: 'Auditprotokoll',
            type_compliance_report: 'Konformitéit Bericht',
            type_custom: 'Personaliséiert',
            type_incident_report: 'Tëschefall Bericht',
            type_ropa: 'ROPA',
            announce_loaded: 'Export schedules loaded',
            execution_note: "Each run produces the file as the schedule's run actor, shown under Runs as: the person who created the schedule, or who last changed what it produces, with that person's current permissions. Each run is recorded below. Recipients receive an e-mail with a link to this page; the file downloads under each reader's own permissions.",
            paused_reason_label: 'Paused by the platform',
            type_withdrawn: 'Withdrawn type',
            runs: 'Runs',
            runs_for: 'Runs for {name}',
            runs_empty: 'This schedule has not run yet.',
            runs_fetch_failed: 'Failed to load the runs.',
            close_runs: 'Close runs',
            run_status: 'Status',
            run_scheduled_for: 'Run for',
            run_trigger: 'Started by',
            run_attempts: 'Attempts',
            run_reason: 'Reason',
            run_file: 'File',
            run_status_running: 'Running',
            run_status_completed: 'Completed',
            run_status_failed: 'Failed',
            run_trigger_scheduled: 'Schedule',
            run_trigger_manual: 'Manual trigger',
            run_will_retry: 'It will be tried again.',
            download: 'Download',
            triggering: 'Exporting…',
            disabling: 'Disabling…',
            trigger_completed: 'Export completed. The file is in the run list.',
            trigger_run_failed: 'The export ran and failed: {reason}',
            failure_actor_inactive: 'The person this schedule runs as is no longer an active member, so the export cannot run as them.',
            failure_actor_lacks_permission: 'The person this schedule runs as no longer holds the permission this export needs.',
            failure_export_type_withdrawn: 'This export type was withdrawn, so the schedule cannot produce it. To export again, a new schedule with another export type is needed.',
            failure_export_too_large: 'The export selects more rows than one file can hold.',
            failure_generation_failed: 'The file could not be generated.',
            failure_interrupted: 'The run was interrupted before it finished.',
            failure_never_executed: 'Requested before scheduled exports were executed, so this run never ran and produced no file.',
            report: {
                incident_title: 'Incident report',
                compliance_title: 'Compliance report — framework coverage',
                window: 'Period: {start} to {end}',
                count: 'Rows: {count}',
                overall: 'Overall mapped coverage: {pct}',
                overall_none: 'Overall mapped coverage: not measurable (no installed framework declares requirements)',
                col_reference: 'Reference',
                col_title: 'Title',
                col_status: 'Status',
                col_severity: 'Severity',
                col_created: 'Created',
                col_closed: 'Closed',
                col_framework: 'Framework',
                col_requirements: 'Requirements',
                col_mapped: 'Mapped',
                col_coverage: 'Coverage',
                col_open_gaps: 'Open gaps',
            },
            email: {
                subject: 'Scheduled export ready: {name}',
                body: 'The scheduled export "{name}" ran for {date}. Open the run list to download the file: {link}',
                body_html: 'The scheduled export "{name}" ran for {date}. Open the run list to download the file:',
            },
            type_none_held: 'You hold none of the permissions an export type needs, so you cannot create a schedule: a schedule runs as the person who creates it.',
            type_offered_hint: 'Only the export types you may export yourself are offered: the schedule runs as you.',
            grant_ropa: 'the permission to export the record of processing activities as PDF',
            grant_audit_trail: 'the permission to export audit events',
            grant_compliance_report: 'the permission to read regulatory frameworks',
            grant_incident_report: 'the permission to run incident exports',
            refusal_permission_not_held: 'You do not hold {permission}, which this export needs, so you cannot make this schedule produce it.',
            refusal_permission_not_held_generic: 'You do not hold the permission this export needs.',
            refusal_schedule_inactive: 'This schedule is switched off, so it cannot be run. Enable it first.',
            refusal_export_type_withdrawn: "This schedule's export type was withdrawn, so it cannot be run or switched on. To keep exporting, create a schedule with another export type using New Schedule.",
            col_runs_as: 'Runs as',
            run_executed_as: 'Ran as',
            run_delivered_to: 'E-mail queued for',
            run_delivered_none: 'No e-mail queued',
            run_delivered_hint: 'An address under E-mail queued for means the e-mail was placed in the sending queue, not that it arrived. The Notification Admin Queue, open to administrators of notifications, shows whether each e-mail was sent.',
            enable: 'Enable',
            enabling: 'Enabling…',
            enable_success: 'Schedule enabled. Its next run is counted from now.',
            enable_failed: 'The schedule could not be enabled.',
            refusal_run_actor_cannot_run: 'This schedule runs as {name}, who cannot run it now: they are no longer an active member, or no longer hold the permission its export needs. It can be switched on once {name} can run it again: ask an administrator to restore their membership or permission. To export now, create a schedule with New Schedule: it runs as you.',
            refusal_run_actor_inactive: 'This schedule runs as {name}, who is no longer an active member of the organisation, so its runs would be refused and it cannot be switched on. It can be switched on once {name} is an active member again: ask an administrator to restore their membership. To export now, create a schedule with New Schedule: it runs as you.',
            refusal_run_actor_lacks_permission: 'This schedule runs as {name}, who no longer holds {permission}, which its export needs, so it cannot be switched on. It can be switched on once {name} holds it again: ask an administrator to give {name} that permission. To export now, create a schedule with New Schedule: it runs as you.',
            run_actor_unnamed: 'the person shown as Runs as',
            people_names_failed: 'The user directory could not be loaded, so a person may be shown by their account identifier instead of their name.',
        },
        email_log: {
            title: 'E-Mail-Protokoll',
            recipient: 'Empfänger',
            subject: 'Sujet',
            sent_at: 'Geschéckt um',
            status: 'Status',
            resend: 'Erëm schécken',
            fetch_failed: 'Konnt net fetch E-Mail logs..',
            announce_loaded: 'Email log loaded',
        },
    },
    organizations: {
        title: 'Organisatiounen',
        create: 'Erstellen Organisatioun',
        edit: 'Änneren Organisatioun',
        name: 'Numm',
        slug: 'Schleck',
        timezone: 'Zäitzon',
        locale: 'Standard Sprooch',
        parent: 'Iwwergeurdnet Organisatioun',
        group: 'Grupp',
        org_group: 'Organisatiounsgrupp',
        no_group: '(Keng – net an engem Grupp)',
        created: 'Erstellt',
        no_parent: '(Keng – eenzelen)',
        no_orgs: 'Keng organizations fonnt',
        load_error: 'Konnt net load organizations',
        save_error: 'Konnt net save Organisatioun',
        created_msg: 'Organisatioun created',
        updated: 'Organisatioun updated',
        deactivate: 'Deaktivéieren',
        deactivated: 'Organisatioun deactivated',
        confirm_deactivate: 'Are you sure you want op deactivate dëst Organisatioun?',
        loaded_results: 'Organizations gelueden, {count} Resultater',
        form_loaded: 'Organization-Formulaire gelueden',
        locale_vi_vn: 'Tiếng Việt (vi-VN)',
        locale_en_us: 'Englesch (en-US)',
        // fix-sprint #431 — the organisation's country (ISO 3166-1 alpha-2).
        country: 'Country',
        country_not_recorded: 'Not recorded',
        country_hint: 'The country this organisation is established in. The cross-border transfer check on rights requests compares a data subject’s country with it; while it is not recorded, that check is not evaluated.',
        // Gate E12.5 remediation 2 (fix-sprint #431) — the row Edit reads the record first.
        edit_reading: 'Reading the organisation’s record…',
        edit_read_error: 'The organisation’s record could not be read, so its current values are not shown and it cannot be saved here.',
        no_head: 'No Head',
        all_frameworks: 'All Kader',
        assigned_at: 'Zougewisen um',
    },
    intake_configs: {
        title: 'Ëffentlech Opnam Configs',
        create: 'Erstellen Konfiguratioun',
        edit: 'Änneren Opnam Konfiguratioun',
        label_col: 'Etikett',
        key: 'Opnam Schlëssel',
        key_hint: 'Ëffentlech URL will be: /rights-request?key=',
        mapped_org: 'Zil Organisatioun',
        status: 'Status',
        enabled: 'Aktivéiert',
        disabled: 'Deaktivéiert',
        request_types: 'Aktivéiert Ufro Typen',
        language: 'Standard Sprooch',
        public_url: 'Ëffentlech URL',
        no_configs: 'Keng Opnam configs fonnt Erstellen one op enable public rights request forms..',
        load_error: 'Konnt net load Opnam configs',
        save_error: 'Konnt net save',
        delete_error: 'Konnt net delete',
        created_msg: 'Konfiguratioun created',
        updated: 'Konfiguratioun updated',
        deleted: 'Opnam config deleted',
        confirm_delete: 'Läschen dëst Opnam config?',
        select_types: 'Auswielen bei least one request type',
        select_org: 'Auswielen eng target Organisatioun',
        routing_mode: 'Routing-Modus',
        single_org: 'Eenzel Organisatioun',
        org_group_mode: 'Organisatiounsgrupp',
        org_group: 'Organisatiounsgrupp',
        select_group: 'Eng Organisatiounsgrupp auswielen...',
        group_root_hint: "Gëtt automatesch op d'Stammorganisatioun vum Grupp gesat wann de Grupp ausgewielt gëtt.",
        loaded_results: 'Intake configurations gelueden, {count} Resultater',
        form_loaded: 'Intake configuration-Formulaire gelueden',
    },
    frequency: {
        weekly: 'Wëchentlech',
        monthly: 'Monatslech',
        quarterly: 'Trimesterlech',
        semi_annually: 'Hallef-Jährlech',
        annually: 'Jährlech',
        custom: 'Personaliséiert',
    },
    /**
     * Sprint 65.6.0 (AC8) — DPO-01 and PUB-01, the privacy office's two registers.
     *
     * One block with two children rather than two top-level blocks: the
     * publication vocabulary (`channels`, `publication_states`, the target
     * editor's labels) is shared by both registers, and a second spelling of it
     * would be free to drift from the first — the defect `PUBLICATION_CHANNELS`
     * is declared once in shared to avoid.
     *
     * R-57: EN and VI are authored. The other fourteen locales carry the EN
     * string for structural parity and are translated by the Phase 25 batch —
     * stated here rather than left to be inferred from the text.
     */
    privacy_office: {
        publication_states: {
            not_published: 'Not published',
            published: 'Published',
            withdrawn: 'Withdrawn',
        },
        channels: {
            public_website: 'Public website',
            mobile_app: 'Mobile app',
            intranet: 'Intranet',
            physical_notice: 'Physical notice',
            direct_notice: 'Direct notice to people',
            other: 'Other',
        },
        filter_publication_state: 'Publication',
        targets_label: 'Where it was published',
        targets_hint: 'At least one place a person can go and read it. A publication with no location is a claim nobody can check.',
        target_channel: 'Channel',
        target_locator: 'Address or location',
        target_locator_hint: 'A web address for a website, a screen name for an app, or a described location for a notice board.',
        target_locale: 'Language (optional)',
        target_add: 'Add another location',
        target_remove: 'Remove this location',
        field_published_on: 'Published on',
        field_published_on_hint: 'The date it actually became public, not the date you are entering it here.',
        appointments: {
            title: 'Data Protection Officer appointments',
            subtitle: 'Record who is appointed, the decision that appointed them, and where the appointment was made public.',
            module_guide: 'Record the appointment of your data protection officer as a register entry rather than a filled-in form: who is appointed, who decided it and when, when the appointment takes effect, and where you said so publicly. An appointment is saved as a draft and raises no duty until you bring it into effect — that transition is the one that says your organisation has a named officer in post. Only an appointment that is in effect can be published, and a publication must say where a person can go and read it.',
            nav: 'DPO appointments',
            record: 'Record appointment',
            table_label: 'Data protection officer appointments',
            sr_loading: 'Loading appointments',
            announce_loaded: '{count} appointments loaded',
            none: 'No appointments match these filters.',
            filters_label: 'Filter appointments',
            filter_status: 'Status',
            col_appointee: 'Appointed person',
            col_position: 'Position',
            col_decision: 'Appointing decision',
            col_takes_effect: 'Takes effect',
            col_status: 'Status',
            col_publication: 'Publication',
            takes_effect_unknown: 'Not stated',
            statuses: {
                draft: 'Draft',
                in_effect: 'In effect',
                ended: 'Ended',
                superseded: 'Superseded',
            },
            action_take_effect: 'Bring into effect',
            action_publish: 'Record publication',
            action_end: 'End appointment',
            modal_record_title: 'Record a DPO appointment',
            modal_record_hint: 'Record a decision your organisation has already taken. The appointment is saved as a draft and raises no duty until you bring it into effect.',
            field_full_name: 'Full name',
            field_position_title: 'Position title',
            field_contact_email: 'Contact email',
            field_contact_email_hint: 'Written as name@example.com. This is the address the public sees.',
            field_contact_phone: 'Contact phone (optional)',
            field_decided_by: 'Decided by',
            field_decided_by_hint: 'The body or person with the authority to appoint.',
            field_decided_on: 'Decision date',
            field_decision_reference: 'Your reference for the decision (optional)',
            field_takes_effect_on: 'Takes effect on (optional)',
            field_takes_effect_hint: 'Leave this empty if the start date is not settled. The duty is then raised undated with that reason, rather than dated from the day you typed it in.',
            field_ends_on: 'Ends on (optional)',
            modal_take_effect_title: 'Bring the appointment into effect',
            modal_take_effect_hint: 'From this point your organisation has a named officer in post. State the date it started if it is not already recorded.',
            field_correction_note: 'Note about the correction (optional)',
            modal_publish_title: 'Record where the appointment was published',
            modal_publish_hint: 'Only an appointment that is in effect can be published — a person is not named publicly as your officer before your organisation says they are one.',
            modal_end_title: 'End the appointment',
            modal_end_hint: 'An appointment that ran its course and one replaced by a newer appointment are different answers to "why is this person no longer the officer". Both are kept.',
            field_end_outcome: 'Outcome',
            field_end_reason: 'Reason',
            field_end_date: 'End date (optional)',
            toast_recorded: 'Appointment recorded.',
            toast_in_effect: 'Appointment is now in effect.',
            toast_published: 'Publication recorded.',
            toast_ended: 'Appointment ended.',
        },
        notices: {
            title: 'Privacy notices',
            subtitle: 'Draft a revision of a privacy notice, for the public or for named data subjects, have it approved, and record where it was published.',
            module_guide: 'Keep a register of the privacy notices your organisation gives, to the public or to named data subjects of its processing activities: which revision is live, who approved the text, and where a person can read it. Approval and publication are two separate acts, usually by two different people, so the register can show a notice that was approved and never actually published — the failure this record exists to make visible. A notice cannot go straight from draft to published, and a published notice that is taken down keeps its publication history with the reason it was withdrawn.',
            nav: 'Privacy notices',
            draft: 'New revision',
            table_label: 'Privacy notices',
            sr_loading: 'Loading privacy notices',
            announce_loaded: '{count} privacy notices loaded',
            none: 'No privacy notices match these filters.',
            filters_label: 'Filter privacy notices',
            filter_status: 'Status',
            col_title: 'Title',
            col_revision: 'Revision',
            col_locales: 'Languages',
            col_status: 'Status',
            col_approval: 'Approved by',
            col_publication: 'Publication',
            approval_none: 'Not approved',
            statuses: {
                draft: 'Draft',
                approved: 'Approved',
                published: 'Published',
                withdrawn: 'Withdrawn',
                superseded: 'Superseded',
            },
            action_approve: 'Approve',
            action_publish: 'Publish',
            action_withdraw: 'Withdraw',
            action_supersede: 'Supersede',
            modal_draft_title: 'New privacy notice revision',
            modal_draft_hint: 'The revision is the version number your published text carries. It is not a version this platform counts for you.',
            field_notice_title: 'Title',
            field_revision: 'Revision',
            field_content_url: 'Where the text lives',
            field_content_url_hint: 'The address of the notice text itself — the page, document or file a reader would open.',
            field_locales: 'Languages',
            field_locales_hint: 'One language code per line, or separated by commas.',
            field_supersedes: 'Replaces which notice (optional)',
            modal_approve_title: 'Approve the notice',
            modal_approve_hint: 'Approving records who signed the text off. It does not give the notice to its audience — publishing is a separate act.',
            field_approved_by: 'Approved by (optional)',
            field_approved_by_hint: 'Leave this empty to record yourself as the approver.',
            field_approval_reference: 'Your reference for the approval (optional)',
            modal_publish_title: 'Publish the notice',
            modal_publish_hint: 'Record that the approved text is now given to its audience, and where a person can read it.',
            publish_needs_approval: 'This notice carries no recorded approval, so it cannot be published. Record who approved the text first.',
            modal_withdraw_title: 'Withdraw the notice',
            modal_withdraw_hint: 'Withdrawing keeps the publication history: "we published this and took it down" is a different answer from "we never published this".',
            field_withdraw_reason: 'Reason for withdrawing',
            modal_supersede_title: 'Supersede the notice',
            modal_supersede_hint: 'Superseding closes this revision because a newer one has replaced it, and it is the last thing that can happen to it in this register.',
            field_supersede_reason: 'Reason for superseding',
            toast_drafted: 'Privacy notice revision created.',
            toast_approved: 'Privacy notice approved.',
            toast_published: 'Privacy notice published.',
            toast_withdrawn: 'Privacy notice withdrawn.',
            toast_superseded: 'Privacy notice superseded.',
        },
    },
    /* Sprint 65.18.0 — continuity (IRP-01). English text held for structural
       parity; real translation deferred to Phase 25 per R-57. */
    /* Sprint 65.19.0 -- legal matters (toolkit row LEG-01). */
    /* 2026-09-24 owner ruling — legacy instrument notice. English text held for
       structural parity; real translation deferred to Phase 25 per R-57. */
    framework_lifecycle_notice: {
        legacy_chip: 'Legacy — no longer in force',
        legacy_notice_label: 'Legacy instrument notice',
        legacy_body: 'This instrument has lapsed or been replaced. Results determined under it may still apply under transition rules. Check the current instrument before relying on it.',
        suspended_chip: 'Suspended — not an obligation',
        suspended_notice_label: 'Suspended instrument notice',
        suspended_body: 'The application of this instrument is suspended, so its duties are not owed while the suspension lasts. Check what ends the suspension before relying on anything recorded under it.',
    },
    framework_articles: {
        title: 'Articles',
        article: 'Article',
        heading: 'Heading',
        state: 'Platform coverage',
        totals_summary: '{total} articles in this instrument — {mapped} implemented by a requirement, {declared} recorded as imposing no duty, {unmapped} not yet mapped.',
        not_article_structured: 'This instrument is not divided into articles — it uses control identifiers — so there is no article map to show. That is correct for this framework and not a gap.',
        not_mapped_yet: 'This instrument is divided into articles and the installed pack does not yet carry them. Until it does, the platform cannot say which of its provisions are implemented.',
        structure_unverified: 'Nobody has read this instrument\u2019s official text yet, so the platform does not know how it divides its provisions and cannot say which of them are implemented. This is a statement about the platform, not about the instrument.',
        structure_not_declared: 'This record does not say how its instrument divides its provisions, so the platform does not know whether it has an article map. That is a gap in the installed pack, not a statement about the instrument.',
        structure_not_required: 'This record comes from a market pack, and a market pack is not required to declare how its instrument divides its provisions, so the platform has no article map to show for it. That is not a gap in the installed pack.',
        structure_origin_unknown: 'This record does not say how its instrument divides its provisions, and the platform cannot tell which kind of pack it was installed from, so it does not know whether an article map should exist.',
        coverage_line: 'Article coverage: {coverage}',
        coverage_levels: {
            mapped: 'Every article is accounted for',
            partial: 'Some articles are not yet mapped',
            not_assessed: 'Not assessed',
        },
        load_failed: 'The articles for this framework could not be read.',
        recorded_text: {
            column: 'Recorded text',
            read: 'Read',
            read_aria: 'Read the recorded text of Article {article}',
            dialog_title: 'Article {article} — recorded text',
            load_failed: 'The recorded text of this article could not be read.',
            load_more: 'Show more',
            source_line: 'Source: {title}',
            page_single: 'page {page} of the source',
            pages_range: 'pages {start}–{end} of the source',
            extraction: {
                ocr: 'read by OCR from the signed scan',
                pdf_text_layer: 'extracted from the PDF text layer',
                docx_text: 'extracted from the DOCX original',
                html_text: 'extracted from the official web page',
                unrecorded: 'extraction method not recorded',
            },
            missing: 'The recorded source does not carry this article\u2019s text, so none is shown. Only its heading is known.',
            partial: 'The source ends without a closing mark after this article, so the text may run on into material that follows it.',
            notice: 'This is the instrument\u2019s original Vietnamese text as recorded from its signed source. It may contain recognition errors, it does not certify current legal effect, and no translation is provided.',
        },
        source_tab: {
            title: 'Source',
            intro: 'The instrument\u2019s recorded text, read in full: chapters, articles, annexes and page marks as the signed source carries them.',
            source_select: 'Recorded source',
            page_mark: 'Page {page}',
            progress: 'Showing {shown} of {total} characters',
            load_more: 'Show more',
            load_all: 'Show the rest',
            load_failed: 'The recorded source text could not be read.',
            no_recorded_source: 'No recorded source text is held for this framework. Its requirements and articles are on the other tabs.',
            composite: 'This framework groups other instruments and has no text of its own. Read the source of each of them:',
            superseded_draft: 'This consultation draft was later promulgated, and its own text is not held. Read the instrument that replaced it:',
            open_in_source: 'In source',
            open_in_source_aria: 'Show Article {article} in the full source',
            article_not_in_source: 'The recorded source does not carry Article {article}\u2019s heading, so it cannot be shown in place.',
        },
        states: {
            mapped: 'Implemented',
            no_duty_declared: 'No duty on the controller',
            not_yet_mapped: 'Not yet mapped',
        },
    },
    credit_information: {
        title: 'Credit-Information Uses',
        scope_note: 'Law 91/2025/QH15 Điều 27(1)(b) prohibits using a data subject’s credit information for credit scoring, credit rating, credit-information assessment or creditworthiness assessment unless that data subject has consented. Decree 356/2025/NĐ-CP Điều 8 details the Article: consent must state the purposes — including those four where they are carried out — the collection sources and sharing parties, the retention period and how consent may be withdrawn; compliance is assessed once every 01 year; and disclosure or loss of sensitive data in this sector is notified to the authority and the data subject within 72 hours.',
        boundary_heading: 'Scope and boundary — from your installed frameworks',
        boundary_frame: 'These statements come from the frameworks installed for your organisation, shown as each framework states them. This register records that a use took place, for which purpose, and the consent it rested on. The platform computes no credit score, produces no rating, assesses no creditworthiness and makes no lending decision. A use no consent covers is refused here rather than recorded with a warning beside it.',
        boundary_none: 'No installed framework declares a boundary statement for this register. This describes the content installed for your organisation, not your organisation’s scope.',
        boundary_load_error: 'The installed frameworks’ boundary statements could not be read, so none is shown. This does not mean that no framework declares one.',
        boundary_retry_subject: 'the boundary statements',
        purposes_load_failed: 'The purposes your installed frameworks declare could not be read, so a use cannot be recorded yet. This does not mean that none is declared.',
        purposes_retry_subject: 'the declared purposes',
        consent_check_timing: 'The consent is checked as it stands when you record the use, not as it stood at the time of use. A consent withdrawn, or given, between those two moments is judged as it is now.',
        kpi_label: 'Register summary',
        kpi_total: 'Total',
        kpi_total_hint: 'all uses recorded in this register',
        kpi_on_this_page: 'on this page',
        kpi_not_loaded: 'not loaded',
        uses: 'Recorded uses',
        record: 'Record a use',
        data_subject_ref: 'Data subject reference',
        information_description: 'What credit information was used',
        purpose: 'Purpose',
        used_at: 'Used at',
        consent_evidence_id: 'Consent evidence',
        consent_evidence_hint: 'The consent-evidence entry this use rests on. It is resolved and checked on the server — naming one that does not cover this purpose is refused.',
        consent_purpose_id: 'Which purpose on that consent',
        consent_purpose_hint: 'Which purpose named on that consent covers this use. Your organisation decides which of its own purposes corresponds; the platform checks that the one you name is genuinely on that consent.',
        consent_state: 'Consent',
        record_withdrawal: 'Record a withdrawal',
        withdrawn_at: 'Withdrawn on',
        no_purposes_installed: 'No framework installed for your organisation declares a credit-information purpose, so a use cannot be recorded against one here.',
        empty_uses: 'No credit-information use has been recorded yet.',
        load_failed: 'The credit-information register could not be read.',
        record_failed: 'The use could not be recorded.',
        recorded: 'Use recorded.',
        withdrawal_recorded: 'Withdrawal recorded.',
        withdrawal_failed: 'The withdrawal could not be recorded.',
        states: {
            consent_in_force: 'In force',
            consent_withdrawn: 'Withdrawn',
        },
    },
    legal_matters: {
        title: 'Legal Matters',
        scope_note: 'No installed instrument requires the controller to operate this register. Law 91/2025/QH15 Điều 4(1)(đ) declares that a data subject may complain and may denounce, and Decree 356/2025/NĐ-CP Điều 36(6) assigns the resolution of complaints and denunciations to the state authority — so a right is declared, and no duty to receive it falls on you. Running this register is a capability your organisation chooses, and any rhythm shown here is your own rather than a statutory deadline.',
        matters: 'Matters',
        receive: 'Record a matter',
        kind: 'Kind',
        origin: 'Rests on',
        right_code: 'Which right',
        raised_by: 'Raised by',
        subject: 'What it concerns',
        state_label: 'State',
        received_at: 'Received',
        acknowledged_at: 'Acknowledged',
        acknowledge: 'Record the acknowledgement',
        resolve: 'Record the conclusion',
        resolution_summary: 'What was concluded',
        concluded_at: 'Concluded on',
        not_acknowledged: 'Not yet acknowledged',
        // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
        intake_from: 'From rights request {reference}',
        right_not_recorded: 'The intake did not say which right it raises; a person records it.',
        record_right: 'Record the right',
        record_right_hint: 'Choose the right this matter rests on, from those the installed frameworks declare at the provision the intake was made under.',
        right_recorded: 'Right recorded',
        // fix-sprint-20261026-0900 Finding 6 — what a matter awaiting its right rests on,
        // the related-matters column, and the refusals of Record the right.
        rests_on_provision: 'At {basis} of {framework}',
        rests_on_provision_no_basis: 'Under {framework}; no provision is stated',
        related_matters: 'Related matters',
        no_related_matters: 'None',
        record_right_closed: 'This matter is closed, so the right it rests on can no longer be recorded.',
        record_right_not_at_provision: 'That right does not rest on the provision the intake was made under. Choose one of the rights offered.',
        record_right_not_declared: 'No installed framework declares that right. Choose another.',
        record_right_already_recorded: 'The right this matter rests on has already been recorded.',
        suggested_link: 'Possibly the same matter as “{subject}”: an earlier request gave the same email address, which is not verified.',
        suggested_link_open: 'Open that matter',
        suggested_link_accept: 'Same matter',
        suggested_link_dismiss: 'Not the same matter',
        suggested_link_accepted: 'A person recorded this as the same matter as “{subject}”.',
        suggested_link_dismissed: 'A person recorded this as not the same matter as “{subject}”.',
        suggested_link_decided: 'Decision recorded',
        linked_matter_not_found: 'The legal matter this link points to could not be read.',
        linked_matter_shown: 'The legal matter the link points to is shown first.',
        no_rights_installed: 'No framework installed for your organisation declares a right, so a matter cannot rest on one here.',
        rights_loading: 'Reading the rights your installed frameworks declare…',
        rights_load_failed: 'The rights your installed frameworks declare could not be read, so a matter cannot rest on one until they are.',
        rights_retry_subject: 'the declared rights',
        matters_retry_subject: 'the legal-matters register',
        loading: 'Loading the legal-matters register',
        right_placeholder: 'Choose the right',
        withdrawn_on_by: 'Withdrawn at {at}, recorded by {by}.',
        withdrawal_reason_not_recorded: 'No reason was recorded for this withdrawal.',
        timeline_acknowledged_before_received: 'The acknowledgement cannot be earlier than when the matter was received.',
        timeline_concluded_before_received: 'The conclusion cannot be earlier than when the matter was received.',
        timeline_concluded_before_acknowledged: 'The conclusion cannot be earlier than the acknowledgement.',
        kpi_row_label: 'Legal-matters summary',
        kpi_not_loaded: 'Not loaded',
        kpi_total: 'Matters',
        kpi_total_hint: 'in the register',
        kpi_on_this_page: 'on this page',
        withdraw: 'Record a withdrawal',
        withdrawal_reason: 'Why it was withdrawn',
        withdrawn: 'Withdrawal recorded.',
        empty_matters: 'No matter has been recorded yet.',
        load_failed: 'The legal-matters register could not be read.',
        create_failed: 'The record could not be saved.',
        created: 'Recorded.',
        acknowledged: 'Acknowledgement recorded.',
        resolved: 'Conclusion recorded.',
        kinds: {
            complaint: 'Complaint',
            denunciation: 'Denunciation',
            claim: 'Claim',
            litigation: 'Litigation',
            regulatory_penalty: 'Regulatory penalty',
            other: 'Other',
        },
        origins: {
            statutory_right: 'A right an installed framework declares',
            elective: 'A matter your organisation chose to track',
        },
        states: {
            received: 'Received',
            acknowledged: 'Acknowledged',
            resolved: 'Resolved',
            withdrawn: 'Withdrawn',
        },
        raised_by_options: {
            data_subject: 'Data subject',
            employee: 'Employee',
            regulator: 'Regulator',
            counterparty: 'Counterparty',
            other: 'Other',
        },
    },
    continuity: {
        title: 'Continuity Exercises',
        scope_note: 'No installed instrument requires continuity exercises. Law 91/2025/QH15 and Decree 356/2025/NĐ-CP were both read in full and neither imposes this control, so any rhythm shown here is your organisation’s own and never a statutory deadline. An exercise that raised improvement actions is not finished until each has been retested.',
        plans: 'Continuity plans',
        exercises: 'Exercises',
        plan_name: 'Plan name',
        plan_scope: 'Scope',
        plan_owner: 'Owner',
        scope_note_label: 'Say what it covers',
        create_plan: 'Add a plan',
        schedule: 'Schedule an exercise',
        kind: 'Kind',
        status_label: 'Status',
        outcome_label: 'Outcome',
        held_at: 'Held on',
        participants: 'Participants',
        open_loops: 'Improvement actions awaiting retest',
        no_open_loops: 'All retested',
        last_reviewed: 'Last reviewed',
        never_reviewed: 'Never reviewed',
        empty_plans: 'No continuity plan has been recorded yet.',
        empty_exercises: 'No exercise has been recorded yet.',
        complete: 'Complete the exercise',
        cancel: 'Cancel the exercise',
        cancel_reason: 'Why it was cancelled',
        completed: 'Exercise completed.',
        cancelled: 'Exercise cancelled.',
        retested: 'Retest recorded.',
        record_retest: 'Record a retest',
        retested_at: 'Retested on',
        retest_note: 'What the retest showed',
        participant_name: 'Who led it',
        participant_role: 'Their role',
        summary: 'What happened',
        improvement_action: 'Improvement action (optional)',
        action_owner: 'Action owner',
        load_failed: 'The continuity register could not be read.',
        create_failed: 'The record could not be saved.',
        created: 'Recorded.',
        retry_plans_subject: 'the continuity plans',
        retry_exercises_subject: 'the exercises',
        complete_for: 'Complete the exercise: {exercise}',
        cancel_for: 'Cancel the exercise: {exercise}',
        record_retest_for: 'Record a retest for {exercise}: {action}',
        completing_heading: 'Completing the exercise: {exercise}',
        cancelling_heading: 'Cancelling the exercise: {exercise}',
        retest_heading: 'Recording a retest for {exercise}: {action}',
        scopes: {
            systems: 'Systems',
            premises: 'Premises',
            people: 'People',
            suppliers: 'Suppliers',
            data_recovery: 'Data recovery',
            other: 'Other',
        },
        kinds: {
            tabletop: 'Tabletop',
            simulation: 'Simulation',
            live: 'Live',
            other: 'Other',
        },
        outcomes: {
            objectives_met: 'Objectives met',
            objectives_partially_met: 'Objectives partially met',
            objectives_not_met: 'Objectives not met',
            not_completed: 'Not completed',
        },
        statuses: {
            scheduled: 'Scheduled',
            completed: 'Completed',
            cancelled: 'Cancelled',
        },
    },
    /* Sprint 65.17.0 — de-identification runs (ANON-01). English text held for
     structural parity; real translation deferred to Phase 25 per R-57. */
    deidentification: {
        title: 'De-identification Runs',
        scope_note: 'Decree 356/2025/NĐ-CP Điều 10(6) requires that a data subject be able to rectify, anonymise and delete identification records in artificial-intelligence and metaverse systems. Outside that scope this is a control your organisation chooses to run. A run is not complete until somebody has judged what remains re-identifiable.',
        population: 'Population transformed',
        method: 'Method',
        method_note: 'Name the method',
        create: 'Record a run',
        created: 'De-identification run recorded.',
        empty: 'No de-identification run has been recorded yet.',
        status_label: 'Status',
        sample_label: 'Sample',
        residual_risk_label: 'Residual re-identification risk',
        load_failed: 'The de-identification runs could not be read.',
        create_failed: 'The run could not be recorded.',
        residual_risk_absent: 'Not yet judged',
        record_judgement: 'Record the sample and judgement',
        record_judgement_for: 'Record the sample and judgement for {population}',
        sample_outcome: 'Sample outcome',
        records_examined: 'Records examined',
        sample_finding: 'What the sample found',
        residual_risk_reasoning: 'Residual re-identification risk — your reasoning',
        residual_risk_hint: 'The platform derives no figure. What remains re-identifiable depends on data your organisation does not hold, so this is recorded as your judgement, against your account and the moment you save it.',
        updated: 'De-identification run updated.',
        update_failed: 'The run could not be updated.',
        approve_for: 'Approve the run for {population}',
        reject: 'Reject the run',
        reject_for: 'Reject the run for {population}',
        reject_heading_for: 'Rejecting the run for {population}',
        decision_note: 'Why the run was rejected',
        approved: 'De-identification run approved.',
        rejected: 'De-identification run rejected.',
        decide_failed: 'The decision could not be recorded.',
        total_runs: '{count} run(s) recorded',
        methods: {
            suppression: 'Suppression',
            generalisation: 'Generalisation',
            pseudonymisation: 'Pseudonymisation',
            aggregation: 'Aggregation',
            noise_addition: 'Noise addition',
            hashing_with_deleted_key: 'Hashing with the key deleted',
            other: 'Other',
        },
        sample_outcomes: {
            not_sampled: 'Not sampled',
            sample_clean: 'Sample found no identifiers',
            sample_found_identifiers: 'Sample found identifiers',
        },
        statuses: {
            draft: 'Draft',
            awaiting_approval: 'Awaiting approval',
            approved: 'Approved',
            rejected: 'Rejected',
        },
    },
    training: {
        title: 'Training & Awareness',
        subtitle: 'Assign training to named people and record each completion against the evidence that shows it happened.',
        module_guide: 'Define the training your organisation requires, assign it to named people with a deadline, and close each assignment against evidence — a completed form, an uploaded certificate or a recorded attestation. An assignment cannot be marked complete on its own; exempting someone is a decision recorded against the person who took it, with a reason. Completion rates here feed the training component of your audit-readiness score.',
        nav_assignments: 'Training & Awareness',
        new_course: 'New course',
        assign: 'Assign training',
        summary_open: 'Open',
        summary_completed: 'Completed',
        summary_overdue: 'Overdue',
        summary_exempted: 'Exempted',
        filters_label: 'Filter training assignments',
        filter_status: 'Status',
        filter_overdue_only: 'Overdue only',
        focused_heading: 'Assignment:',
        sr_loading: 'Loading training assignments',
        no_assignments: 'No training assignments match these filters.',
        table_label: 'Training assignments',
        col_course: 'Course',
        col_assignee: 'Assignee',
        col_due: 'Due',
        col_status: 'Status',
        col_evidence: 'Evidence',
        overdue_badge: 'Overdue',
        evidence_none: 'None recorded',
        exempted_by_label: 'Exempted by',
        cancelled_by_label: 'Withdrawn by',
        cancellation_reason_not_recorded: 'Withdrawn — no reason was stored',
        action_start: 'Start',
        action_complete: 'Record completion',
        action_exempt: 'Exempt',
        action_cancel: 'Withdraw',
        modal_course_title: 'New training course',
        field_course_title: 'Course title',
        field_course_description: 'Description',
        field_recurrence_months: 'Repeat every (months)',
        field_recurrence_hint: 'Leave empty for one-off training. This is an internal cadence, not a statutory deadline.',
        modal_assign_title: 'Assign training',
        field_course: 'Course',
        no_courses_hint: 'No active courses yet. Define a course before assigning training.',
        courses_heading: 'Courses',
        courses_table_label: 'Active training courses',
        no_courses: 'No active courses yet.',
        col_description: 'Description',
        col_recurrence: 'Repeats',
        recurrence_one_off: 'One-off',
        recurrence_every: 'Every {count} months',
        action_assign_course: 'Assign',
        field_assignees: 'People',
        field_assignees_hint: 'One user id per line, or separated by commas. Each person gets their own assignment and their own task.',
        field_due_at: 'Due date',
        modal_complete_title: 'Record completion',
        modal_complete_hint: 'A completion must cite the evidence that shows the training was done — a submitted form, an uploaded file or a recorded attestation. The assignment cannot be closed without it.',
        field_evidence_kind: 'Evidence type',
        field_evidence_reference: 'Evidence reference',
        modal_exempt_title: 'Exempt from training',
        modal_exempt_hint: 'An exemption is recorded against you, with the reason you give. It is never counted as a completion.',
        field_exemption_reason: 'Reason for the exemption',
        modal_cancel_title: 'Withdraw assignment',
        field_cancel_reason: 'Reason for withdrawing',
        toast_started: 'Training assignment started.',
        toast_course_created: 'Training course created.',
        toast_assigned: 'Training assigned.',
        toast_completed: 'Completion recorded.',
        toast_exempted: 'Exemption recorded.',
        toast_cancelled: 'Assignment withdrawn.',
        announce_loaded: '{count} training assignments loaded',
        statuses: {
            assigned: 'Assigned',
            in_progress: 'In progress',
            completed: 'Completed',
            exempted: 'Exempted',
            cancelled: 'Withdrawn',
        },
        evidence_kinds: {
            form_instance: 'Completed form',
            attachment: 'Uploaded file',
            attestation: 'Recorded attestation',
        },
    },
    task_management: {
        // Sprint 63.32.0 (#543) — the module a task came from, in the reader's language.
        source_modules: {
            rights_requests: 'Rights Requests',
            assessments: 'DPIA & Assessments',
            incidents: 'Incident Response',
            incident_ops: 'Incident Operations',
            monitoring: 'Monitoring Programs',
            vendor_risk: 'Vendor Risk',
            governance: 'Governance',
            deletion_orchestration: 'Deletion Orchestration',
            access_review: 'Access Reviews',
            consent: 'Consent Governance',
            consent_properties: 'Consent Properties',
            forms: 'Compliance Forms',
            localization: 'Localization',
            program_governance: 'Program Governance',
            privacy_scanner: 'Privacy Scanner',
            classification: 'Data Classification',
            discovery: 'Data Discovery',
            data_mapping: 'Data Mapping',
            compliance_operations: 'Compliance Work Programme',
            training: 'Training',
            privacy_office: 'Privacy Office',
            authority_operations: 'Authority Operations',
            ai_governance: 'AI Governance',
            regulatory_classification: 'Regulatory Classification',
            org_settings: 'Organization Profile',
            customs_tax_administration: 'Customs Tax Administration',
            manual: 'Manual',
        },
        nav: {
            calendar: 'Kalenner',
            all_tasks: 'All Aufgaben',
            my_tasks: 'Meng Aufgaben',
            overdue: 'Iwwerfälleg',
            board: 'Verwaltungsrot',
            workflows: 'Workflows',
        },
        list: {
            title: 'All Aufgaben',
            my_title: 'Meng Aufgaben',
            overdue_title: 'Iwwerfälleg Aufgaben',
            new_task: 'Aufgab erstellen',
            no_tasks: 'Keng Aufgaben fonnt.',
            empty_state: 'Keng Aufgabe fonnt. Aufgabe ginn aus Modulaarbechtsofleef generéiert wéi Bewäertungen, Tëschefäll a Revisiounskampagnen.',
            total: 'Gesamt Aufgaben',
            search_placeholder: 'Aufgabe sichen...',
            bulk_actions: 'Massenaktiounen',
            change_status: 'Status änneren',
            change_priority: 'Prioritéit änneren',
            assign: 'Zouweisen',
            sr_my_tasks_count: '{count} Aufgaben zougewisen',
            sr_days_overdue: '{count} Deeg iwwerfälleg',
            sla_label: 'SLA',
        },
        detail: {
            workflow_step: 'Workflow step',
            title: 'Aufgabedetail',
            description: 'Beschreiwung',
            workflow_progress: 'Workflow-Fortschrëtt',
            checklist: 'Checklëscht',
            dependencies: 'Ofhängegkeeten',
            comments: 'Kommentaren',
            activity_timeline: 'Aktivitéitszäitachs',
            assignees: 'Zougewisener',
            add_assignee: 'Zougewisenen derbäisetzen',
            remove_assignee: 'Zougewisenen ewechhuelen',
            due_date: 'Fällegkeetsdatum',
            start_date: 'Startdatum',
            sla_status: 'SLA-Status',
            sla_on_track: 'Am Zäitplang',
            sla_at_risk: 'A Gefor',
            sla_breached: 'Verletzt',
            tags: 'Tags',
            source_entity: 'Quellentitéit',
            view_source: 'Quell uweisen',
            add_dependency: 'Ofhängegkeet derbäisetzen',
            remove_dependency: 'Ofhängegkeet ewechhuelen',
            write_comment: 'Kommentar schreiwen...',
            post_comment: 'Publizéieren',
            edit_comment: 'Änneren',
            delete_comment: 'Läschen',
            add_checklist_item: 'Element derbäisetzen',
            no_dependencies: 'Keng Ofhängegkeeten',
            no_comments: 'Nach keng Kommentaren',
            blocks: 'Blockéiert',
            blocked_by: 'Blockéiert vun',
            related: 'Verbonnen',
            working_days_remain: 'Aarbechtsdeeg iwwreg',
            created_by: 'Erstallt vun',
            completed_at: 'Ofgeschloss um',
            remove_assignee_aria: 'Remove assignee: {name}',
            // Sprint 63.32.0 (#543) — the seeded role names an assignment can carry.
            assignee_role_labels: {
                platform_admin: 'Platform Admin',
                compliance_owner: 'Compliance Owner',
                case_manager: 'Case Manager',
                auditor: 'Auditor',
                approver: 'Approver',
                partner_external_counsel: 'External Counsel (Partner)',
                processor_liaison: 'Processor Liaison',
            },
            remove_dependency_aria: 'Remove dependency: {name}',
            no_assignees: 'Keng Zougewisener',
            add_assignee_placeholder: 'Benotzer-ID aginn...',
            edited: 'beaarbecht',
            timeline: 'Aktivitéitszäitachs',
            no_timeline: 'Nach keng Aktivitéit',
            timeline_reason: 'Reason',
            no_workflow: 'Kee Workflow zougewisen',
            add_dependency_placeholder: 'Aufgaben-ID aginn...',
            sr_status_updated: 'Status aktualiséiert op {status}',
            sr_priority_updated: 'Prioritéit aktualiséiert op {priority}',
            // Sprint 63.30.0 (#590, #574) — due date edit and Escalate.
            save_due_date: 'Save due date',
            due_date_saved: 'Due date set to {date}',
            due_date_save_failed: 'The due date could not be saved.',
            escalate: 'Escalate',
            escalate_title: 'Escalate this task',
            escalate_description: 'Escalating moves the task to Escalated. Your reason is recorded with the status change and shown beside it in the Activity Timeline.',
            escalate_reason: 'Reason for escalating',
            escalate_reason_required: 'Enter a reason for escalating.',
            escalating: 'Escalating...',
            sr_escalated: 'Task escalated',
            escalate_failed: 'The task could not be escalated.',
            convert_to_workflow: 'Run als Workflow',
            convert_modal_title: 'Konvertéiert Aufgab op Workflow Run',
            convert_workflow_label: 'Workflow',
            convert_workflow_placeholder: 'Wielt e Workflow ...',
            convert_attachments_label: 'Uschlëss',
            convert_attachments_empty: 'Keng Uschlëss op dëser Aufgab.',
            convert_persona_label: 'Persona Iwwerschreiden (optional)',
            convert_submit: 'Start Workflow Run',
            convert_starting: 'Start Workflow Run…',
            convert_started: 'Workflow Laf ugefaang - Ouverture Laf ...',
            convert_disabled_in_flight: 'E Workflow Laf vun dëser Aufgab ass schonn amgaang.',
            linked_workflow_chip: 'Vun {workflow_title}',
            linked_chat_chip: 'Vun AI Chat',
            linked_more: '+{count} méi',
        },
        calendar: {
            title: 'Aufgabekalenner',
            page_title: 'Kalenner',
            create_task: 'Aufgab erstellen',
            month: 'Mount',
            week: 'Woch',
            day: 'Dag',
            today: 'Haut',
            more_tasks: '+{count} méi',
            no_tasks_day: 'Keng Aufgaben dësen Dag',
            all_day: 'De ganzen Dag',
            quick_create_tooltip: 'Aufgab fir dësen Dag erstellen',
            view_changed: 'Kalenneransicht op {view} geännert',
            tasks_in_view: '{count} Aufgaben an der Usiicht',
            task_in_view: '{count} Aufgab an Vue',
            filter_my_tasks: 'Meng Aufgaben',
            filter_overdue: 'Iwwerfällig',
            filter_high_priority: 'Héich Prioritéit',
            filter_all: 'Alleguer',
        },
        filters: {
            assignee: 'Zougewisenen',
            me: 'Mech',
            role: 'Roll',
            status: 'Status',
            priority: 'Prioritéit',
            task_type: 'Aufgabentyp',
            source_module: 'Quellmodul',
            organization: 'Organisatioun',
            sla_breached_only: 'Nëmme SLA-verletzt weisen',
            clear_filters: 'Filteren läschen',
            apply_filters: 'Uwenden',
            source_label: 'Quell',
            source_manual: 'Manuell',
            source_module_kind: 'Modul-generéiert',
            source_chat: 'Vun AI Chat',
            source_workflow_run: 'Vun AI Workflow Run',
        },
        status: {
            pending: 'Waardend',
            in_progress: 'Am Gaang',
            blocked: 'Blockéiert',
            completed: 'Ofgeschloss',
            overdue: 'Iwwerfälleg',
            escalated: 'Eskaléiert',
            cancelled: 'Annuléiert',
        },
        priority: {
            critical: 'Kritesch',
            high: 'Héich',
            medium: 'Mëttel',
            low: 'Niddreg',
        },
        task_types: {
            manual_task: 'Manuell Aufgab',
            dsr_case: 'DSR-Fall',
            dsr_review: 'DSR-Iwwerpréiwung',
            dsr_escalation: 'DSR-Eskalatioun',
            dpia_review: 'DPIA-Iwwerpréiwung',
            dpia_approval: 'DPIA-Geneemegung',
            transfer_review: 'Transferiwwerpréiwung',
            incident_task: 'Tëschefallaufgab',
            incident_notification: 'Tëschefallnotifikatioun',
            playbook_step: 'Playbook-Schrëtt',
            regulator_deadline: 'Regulatorfrist',
            monitoring_task: 'Iwwerwaachungsaufgab',
            kpi_alert: 'KPI-Alarm',
            vendor_review: 'Liwwerantiwwerpréiwung',
            vendor_questionnaire: 'Liwwerantfroebogen',
            governance_review: 'Steierungsiwwerpréiwung',
            drift_alert: 'Ofweichungsalarm',
            governance_approval: 'Steierungsgeneemegung',
            deletion_task: 'Läschaufgab',
            deletion_verification: 'Läschverifizéierung',
            access_review: 'Zougangsiwwerpréiwung',
            sod_alert: 'SoD-Alarm',
            consent_workflow: 'Zoustëmmungsworkflow',
            consent_alert: 'Zoustëmmungsalarm',
            property_review: 'Eegeschaftsiwwerpréiwung',
            inconsistency_alert: 'Inkonsistenzalarm',
            form_review: 'Formulaireiwwerpréiwung',
            dossier_deadline: 'Dossierfrist',
            submission_task: 'Andreechtungsaufgab',
            translation_review: 'Iwwersetzungsiwwerpréiwung',
            governance_task: 'Steierungsaufgab',
            reporting_deadline: 'Berichtfrist',
            scanner_alert: 'Scanner-Alarm',
            classification_review: 'Klassifizéierungsiwwerpréiwung',
            lifecycle_task: 'Liewenszyklusaufgab',
            discovery_review: 'Entdeckungsiwwerpréiwung',
            forseti_task: 'Forseti Task',
        },
        training_assignment: 'Formatiounszouweisung',
        task_type_groups: {
            training: 'Formatioun',
            manual: 'Manuell',
            dsr: 'DSR / Rechteufroën',
            assessments: 'Bewäertungen',
            incidents: 'Tëschefäll',
            incident_ops: 'Tëschefalloperatiounen',
            monitoring: 'Iwwerwaachung',
            vendor_risk: 'Liwwerantrisiko',
            governance: 'Steierung',
            deletion: 'Läschung',
            access_review: 'Zougangsiwwerpréiwung',
            consent: 'Zoustëmmung',
            consent_properties: 'Zoustëmmungseegeschafte',
            forms: 'Formulairen',
            localization: 'Lokaliséierung',
            program_governance: 'Programmsteierung',
            privacy_scanner: 'Dateschutz-Scanner',
            classification: 'Klassifizéierung',
            discovery: 'Entdeckung',
            forseti: 'Forseti',
        },
        create: {
            title: 'Aufgab erstellen',
            task_title: 'Titel',
            task_description: 'Beschreiwung',
            task_type: 'Aufgabentyp',
            task_type_label: 'Aufgabentyp',
            task_type_placeholder: 'Aufgabentyp wielen',
            priority: 'Prioritéit',
            assignees: 'Zougewisener',
            assignee_search_placeholder: 'Benotzer sichen...',
            assignee_search_no_results: 'No users found.',
            assignee_search_loading: 'Loading…',
            tags_placeholder: 'tag1, tag2',
            remove_assignee: '{name} ewechhuelen',
            due_date: 'Fällegkeetsdatum',
            start_date: 'Startdatum',
            checklist: 'Checklëscht',
            tags: 'Tags',
            add_checklist_item: 'Checklëschtelement derbäisetzen',
            validation: {
                title_required: 'Titel ass erfuerderlech',
                title_max_length: 'Titel muss 500 Zeechen oder manner sinn',
                description_max_length: 'Beschreiwung muss 5000 Zeechen oder manner sinn',
                due_date_before_start: 'Fällegkeetsdatum muss nom Startdatum sinn',
                max_reminders: 'Maximal 5 Erënnerungen pro Aufgab',
            },
            reminders: {
                title: 'Erënnerungen',
                add: 'Erënnerung derbäisetzen',
                at_due_date: 'Um Fällegkeetsdatum',
                minutes_before: '{count} Minutte virdrun',
                hours_before: '{count} Stonnen virdrun',
                days_before: '{count} Deeg virdrun',
                weeks_before: '{count} Woche virdrun',
                custom: 'Personaliséiert',
                channel_platform: 'Plattform',
                channel_email: 'E-Mail',
                channel_both: 'Béid',
                offset_label: 'Erënnerung {n}: Zäitpunkt',
                channel_label: 'Erënnerung {n}: Kanal',
                remove_label: 'Erënnerung {n} ewechhuelen',
            },
            workflow: {
                label: 'Workflow',
                none: 'Keen (manuell Statusmanagement)',
                steps_count: '{count} Schrëtt',
                preview_label: 'Workflow-Schrëtt',
            },
        },
        errors: {
            fetch_failed: 'Aufgaben konnten net gelueden ginn.',
            save_failed: 'Aufgab konnt net gespäichert ginn.',
            delete_failed: 'Aufgab konnt net geläscht ginn.',
            status_change_failed: 'Aufgabestatus konnt net geännert ginn.',
            assign_failed: 'Aufgab konnt net zougewisen ginn.',
            comment_failed: 'Kommentar konnt net gespäichert ginn.',
            checklist_failed: 'Feeler beim Aktualiséiere vun der Checklëscht.',
            dependency_failed: 'Feeler beim Aktualiséiere vun der Ofhängegkeet.',
            workflow_failed: 'Workflow konnt net gespäichert ginn.',
            workflow_activate_failed: 'Workflow konnt net aktivéiert ginn.',
            workflow_delete_failed: 'Workflow konnt net geläscht ginn.',
            template_install_failed: 'Virlag konnt net installéiert ginn.',
            search_failed: 'Aufgabesich fehlgeschloen.',
            convert_failed: 'Konnt den Workflow Laf net starten.',
            convert_quota_exhausted: 'Token Eemer niddereg. Fëllt op virum nei lafen.',
        },
        dependency_types: {
            blocks: 'Blockéiert',
            blocked_by: 'Blockéiert vun',
            related: 'Verbonnen',
        },
        workflow: {
            current_step: 'Aktuellen Schrëtt',
            transition: 'Iwwergank',
            history: 'Schrëtthistorik',
            no_workflow: 'Kee Workflow zougewisen',
            detail_title: 'Workflow-Detailer',
            list_title: 'Workflow-Definitiounen',
            create_workflow: 'Workflow erstellen',
            browse_templates: 'Virlagen duerchsichen',
            template_catalog_title: 'Workflow-Virlagekatalog',
            name: 'Numm',
            description: 'Beschreiwung',
            task_types: 'Aufgabentypen',
            steps: 'Schrëtt',
            version: 'Versioun',
            created: 'Erstallt',
            actions: 'Aktiounen',
            status_draft: 'Entworf',
            status_active: 'Aktiv',
            status_archived: 'Archivéiert',
            activate: 'Aktivéieren',
            archive: 'Archivéieren',
            save: 'Späicheren',
            edit: 'Änneren',
            delete: 'Läschen',
            clone: 'Klonen',
            install: 'Installéieren',
            installed: 'Installéiert',
            system: 'System',
            general_settings: 'Allgemeng Astellungen',
            default_assignment: 'Standardzouweisung',
            default_sla: 'Standard SLA',
            workflow_steps: 'Workflow-Schrëtt',
            add_step: 'Schrëtt derbäisetzen',
            step_name: 'Schrëttnumm',
            step_status: 'Statuswäert',
            assignment_strategy: 'Zouweisungsstrategie',
            assignment_role: 'Zouweisungsroll',
            sla_hours: 'SLA-Stonnen',
            sla_calendar_type: 'Kalennertyp',
            working_days: 'Aarbechtsdeeg',
            calendar_days: 'Kalennerdeeg',
            is_terminal: 'Terminalschrëtt',
            required_fields: 'Erfuerderlech Felder',
            checklist_template: 'Checklëschtevirlag',
            transitions: 'Iwwergäng',
            add_transition: 'Iwwergank derbäisetzen',
            from_step: 'Vun',
            to_step: 'Op',
            permission: 'Erlaabnes',
            sla_action: 'SLA-Aktioun',
            notify: 'Notifizéieren',
            escalation_rules: 'Eskalatiounsreegelen',
            add_rule: 'Reegel derbäisetzen',
            trigger: 'Ausléiser',
            threshold: 'Schwellwäert',
            action: 'Aktioun',
            target_role: 'Zilroll',
            trigger_sla_threshold: 'SLA-Schwellwäert',
            trigger_sla_breach: 'SLA-Verletzung',
            trigger_blocked_duration: 'Blockéiert Dauer',
            trigger_manual: 'Manuell',
            action_notify: 'Notifizéieren',
            action_escalate: 'Eskaléieren',
            action_reassign: 'Nei zouweisen',
            sla_start: 'Start',
            sla_pause: 'Paus',
            sla_resume: 'Weidermaachen',
            sla_stop: 'Stop',
            strategy_manual: 'Manuell',
            strategy_role_based: 'Rollebaséiert',
            strategy_round_robin: 'Ronn Robin',
            strategy_least_loaded: 'Am mannsten belaascht',
            strategy_creator: 'Ersteller',
            confirm_delete: 'Sidd Dir sécher datt Dir dëse Workflow läsche wëllt?',
            confirm_archive: 'Sidd Dir sécher datt Dir dëse Workflow archivéiere wëllt?',
            cannot_delete_active: 'Aktiv Workflows kënnen net geläscht ginn. Archivéiert als éischt.',
            validation_errors: 'Workflow-Validéierungsfeeler',
            step_count: '{count} Schrëtt',
            no_workflows: 'Keng Workflows fonnt.',
            no_templates: 'Keng Virlagen disponibel.',
            sr_templates_count: '{count} Schabloune verfügbar',
            template_installed: 'Virlag erfollegräich installéiert.',
            activate_success: 'Workflow erfollegräich aktivéiert.',
            archive_success: 'Workflow erfollegräich archivéiert.',
            save_success: 'Workflow erfollegräich gespäichert.',
            delete_success: 'Workflow erfollegräich geläscht.',
            applicable_task_types: 'Ubruecht Aufgabentypen',
            steps_column: 'Schrëtt',
            status_value_hint: 'Workflow-Statusschlëssel, dee vun Iwwergäng benotzt gëtt. Klengbuschtawen, Zuelen an Ënnerstrécher.',
            from_status: 'Vu Status',
            to_status: 'Op Status',
            required_permissions: 'Erfuerderlech Berechtegungen',
            transition_required_fields: 'Erfuerderlech Felder (Iwwergang)',
            auto_assign: 'Automatesch Zouweisung',
            auto_assign_role: 'Roll fir automatesch Zouweisung',
            threshold_percent: 'Schwell (%)',
            blocked_hours: 'Blockéiert Stonnen',
            notify_roles: 'Rollen benoriichtegen',
            escalate_to_role: 'Eskaléieren op Roll',
            notification_template: 'Benoriichtegungsschabloun',
            action_escalate_tier: 'Niveau eskaléieren',
            create_modal_title: 'Workflow erstellen',
            create_modal_description: 'Definéiert Schrëtt, Iwwergäng an Eskalatiounsreegelen. De Workflow gëtt als Entworf erstallt.',
            create_modal_footer_note: 'Als Entworf erstallt. Aktivéiert en, wann de Grafik valid ass.',
            validation_name_required: 'De Workflow-Numm ass erfuerderlech.',
            validation_task_types_required: 'Wielt op mannst een ubruecht Aufgabentyp.',
            validation_min_one_step: 'Füügt op mannst ee Schrëtt bäi.',
            validation_min_one_transition: 'Füügt op mannst een Iwwergang bäi.',
            validation_step_name_required: 'De Schrëttnumm ass erfuerderlech.',
            validation_status_value_required: 'De Statuswäert ass erfuerderlech.',
            validation_status_value_duplicate: "De Statuswäert muss iwwer d'Schrëtt eenzegaarteg sinn.",
            validation_transition_endpoints_required: 'Wielt souwuel ee Vu-Status wéi och een Op-Status.',
            validation_transition_unknown_status: 'Dësen Iwwergang bezitt sech op ee Status, deen ee Schrëtt net definéiert.',
            validation_sla_positive: 'SLA-Stonnen musse méi grouss wéi null sinn. Loosst eidel fir keng SLA.',
            validation_threshold_percent_range: "D'Schwell muss tëscht 0 an 100 leien.",
            validation_blocked_hours_positive: 'Blockéiert Stonnen musse méi grouss wéi null sinn.',
            warning_no_terminal_step: "Kee Schlussschrëtt. D'Aktivéierung wäert feelen, bis ee Schrëtt als Schluss markéiert ass.",
            warning_step_no_outgoing: 'Net-Schlussschrëtt ouni erausgoend Iwwergäng: {statuses}',
            warning_unreachable_steps: 'Schrëtt, déi net vum éischte Schrëtt aus erreechbar sinn: {statuses}',
            orphaned_transitions_warning: '{count} Iwwergäng bezéien sech op ee geläschte Schrëtt.',
            remove_orphaned_transitions: 'Verwaist Iwwergäng ewechhuelen',
            transitions_renamed_announcement: '{count} Iwwergäng op den neie Statuswäert aktualiséiert.',
            add_checklist_item: 'Checklëscht-Element bäifügen',
            checklist_item_label: 'Checklëscht-Element',
        },
        columns: {
            priority: 'Prioritéit',
            title: 'Titel',
            status: 'Status',
            type: 'Typ',
            assignees: 'Zougewisener',
            due_date: 'Fällegkeetsdatum',
            source: 'Quell',
            actions: 'Aktiounen',
        },
        groups: {
            overdue: 'Iwwerfälleg',
            due_today: 'Haut fälleg',
            due_this_week: 'Dës Woch fälleg',
            due_later: 'Méi spéit fälleg',
            no_due_date: 'Kee Fällegkeetsdatum',
            closed: 'Ofgeschloss oder annuléiert',
        },
        board: {
            title: 'Aufgabeboard',
            drag_hint: 'Aufgaben tëschent Spalten zéien fir de Status ze änneren',
            empty_column: 'Keng Aufgaben',
            keyboard_hint: 'Tastatur: Feillen zum Navigéieren, Leertouch fir eng Opgab opzehuelen, Lénks/Riets fir Kolonn ze wielen, Enter fir ofzeleeën, Escape fir ofzebriechen.',
            sr_moving: '{title} gëtt verschoben. Iwwer {column}. Enter fir ofzeleeën, Escape fir ofzebriechen.',
            sr_tasks_shown: '{count} Aufgaben ugewisen',
            sr_picked_up: '{title} opgehuewen',
            sr_returned: '{title} zréck op {column}',
            sr_dropped: '{title} an {column} ofgeluecht, Positioun {position} vu(n) {total}',
            sr_move_cancelled: 'Récklung ofgebrach',
            sr_over_column: 'Iwwer {column}',
            sr_moved_to: '{title} geréckelt op {column}',
            invalid_transition: 'Kann Aufgab net vu {from} op {to} versetzen.',
            terminal_status: 'Des Aufgab huet en Endstatus a kann net versat ginn.',
            invalid_drop_target: 'Ongultegen Ofleegobjektiv',
        },
        team: {
            title: 'Teamaarbechtsbelaaschtung',
            assignee: 'Zougewisenen',
            active: 'Aktiv',
            overdue: 'Iwwerfälleg',
            blocked: 'Blockéiert',
            completed_7d: 'Ofgeschloss (7d)',
            load: 'Belaaschtung',
            load_high: 'Héich',
            load_medium: 'Mëttel',
            load_normal: 'Normal',
        },
        graph: {
            title: 'Ofhängegkeet Grafik',
            list_view: 'Lëscht View',
            graph_view: 'Grafik Vue',
            zoom_in: 'Zoom an',
            zoom_out: 'Auszoomen',
            reset_zoom: 'Zerécksetzen',
            blocks_label: 'blocks',
            related_label: 'related',
            no_dependencies: 'Keng Ofhängegkeete fir ze visualiséieren.',
        },
        add_dependency_modal: {
            title: 'Ofhängegkeet derbäisetzen',
            search_placeholder: 'Sich Aufgaben nom Titel oder ID ...',
            no_results: 'Keng Aufgaben fonnt.',
            this_blocks: 'Dës Aufgab blockéiert ausgewielt',
            this_blocked_by: 'Dës Aufgab ass blockéiert duerch ausgewielt',
            related: 'Verbonnen',
            max_dependencies: 'Maximal 10 Ofhängegkeeten pro Aufgab.',
            circular_error: 'Kann net derbäi: géif eng kreesfërmeg Ofhängegkeet schafen.',
        },
        conflict_detection: {
            title: 'Konfliktdetektioun',
            unresolved: 'ongeléist',
            no_conflicts: 'Keng Konflikter erkannt',
            section: 'Sektioun',
            field: 'Feld',
            dept_a: 'Departement A',
            value_a: 'Wäert A',
            dept_b: 'Departement B',
            value_b: 'Wäert B',
            severity: 'Schwieregkeet',
            status: 'Status',
            resolved: 'Geléist',
            open: 'Oppen',
            resolve: 'Léisen',
            load_failed: 'Feeler beim load conflicts',
            resolve_success: 'Konflikt erfollegräich geléist',
            resolve_failed: 'Feeler beim resolve conflict',
            severity_critical: 'Kritesch',
            severity_warning: 'Warnung',
            severity_info: 'Info',
        },
        consolidation: {
            title: 'Konsolidéierungsfortschrëtt',
            add_child: 'Ënneropgab derbäisetzen',
            child_task_id: 'Ënneropgab-ID',
            department_id: 'Departement-ID',
            link: 'Verlinken',
            overall_completion: 'Gesamtofschloss',
            departments_complete: 'Departementer ofgeschloss',
            overdue: 'Iwwerfälleg',
            ready: 'Prett',
            not_ready: 'Net prett',
            master_status: 'Haaptstatus',
            department_breakdown: 'Departementsopschlësselung',
            department: 'Departement',
            status: 'Status',
            completion: 'Ofschloss',
            due_date: 'Fällegkeetsdatum',
            overdue_flag: 'Iwwerfälleg',
            child_tasks: 'Ënneropgaben',
            task: 'Opgab',
            load_failed: 'Feeler beim load consolidation data',
            child_linked: 'Ënneropgab erfollegräich verlinkt',
            link_failed: 'Feeler beim link child task',
            confirm_unlink: 'Sidd Dir sécher, dass Dir dës Ënneropgab wëllt entkoppelen?',
            child_unlinked: 'Ënneropgab entkoppelt',
            unlink_failed: 'Entkoppelung vun der Ënneropgab gescheitert',
            unlink: 'Entkoppelen',
            status_completed: 'Ofgeschloss',
            status_in_progress: 'Am gaang',
            status_pending: 'Ausstänneg',
            select_child_task: 'Ënneraufgab wielen',
            select_department: 'Departement wielen',
        },
        section_assignments: {
            title: 'Sektiouns-Zouweisungen',
            assign_section: 'Sektioun zouweisen',
            section_ref: 'Sektiouns-Referenz',
            section_label: 'Sektiouns-Bezeechnung',
            department_id: 'Departement-ID',
            department: 'Departement',
            assignee_id: 'Zougewisenen-ID',
            assignee: 'Assignee',
            mandatory: 'Obligatoresch',
            assign: 'Zouweisen',
            no_assignments: 'Keng Sektiouns-Zouweisungen',
            status: 'Status',
            completion: 'Ofschloss',
            mark_complete: 'Ofschléissen',
            load_failed: 'Feeler beim load section assignments',
            assign_success: 'Sektioun erfollegräich zougewisen',
            assign_failed: 'Feeler beim assign section',
            status_updated: 'Status aktualiséiert',
            status_update_failed: 'Feeler beim Aktualiséiere vum Status',
            section_ref_placeholder: 'z.B. 3.2',
            status_completed: 'Ofgeschloss',
            status_in_progress: 'Am Fortschrëtt',
            status_pending: 'Erwaardung',
            select_department: 'Wielt Departement',
            select_assignee: "Wielt d'Astellunge",
        },
        overdue_columns: {
            days_overdue: 'Deeg ze speit',
        },
        regulatory_info: {
            deadline_context: 'Beim Erstelle vun DPIA-Aufgaben solle Faellegkeetsdatume mat den PDPL-Afreechtfrishe reenerstammen: Abteilungsinput = 10 Aarbechtsdeeg. Cybersecherheits- an Telekomfrishe gellen och.',
            overdue_deadline_context: 'Regulatoresch Fristen aus Paketdaten: PDPL Abteilungsinput = 10 Aarbechtsdeeg, Ergaenzungsaentwert = 15 Aarbechtsdeeg, Neiandreenung = 10 Aarbechtsdeeg. SLA-verletzt Aufgabe ginn visuell markeiiert.',
            section_assignment_context: 'DPIA Multi-Abteilungs-Workflow (PDPL-P1): DPO weist Dossierabschnietter un Abteilunge zou. Abteilungsinputfrist: 10 Aarbechtsdeeg.',
            template_catalog_context: 'Systemvirlagen decken rahmenspezifisch Prozesser of: PDPL DPIA, Dateschutzgesetz-Attesteiierung, Cybersecherheits-Vorfallreaktioun, Telekom-Beheerdeufroe an KI-Registreiierung. Systemvirlagen sin net beaarbechtbar.',
            workflow_instantiation_context: 'Installeiert Virlagen kennen zu aktiven Workflows instanzeiiert ginn. Aufgaben ginn automatesch mat berechenten Faellegkeetsdatume erstallt.',
        },
        overdue: {
            days_overdue: 'Deeg iwwerfälleg',
        },
    },
    notifications: {
        bell: {
            title: 'Notifikatiounen',
            unread_count: 'unread',
        },
        drawer: {
            title: 'Notifikatiounen',
            mark_all_read: 'Markéiert alles als gelies',
            no_notifications: 'Keng Notifikatiounen',
            filter: {
                all: 'All',
                unread: 'Ongelies',
                tasks: 'Aufgaben',
                approvals: 'Genehmegungen',
                alerts: 'Alarmer',
            },
        },
        preferences: {
            title: 'Notifikatioun Preferenzen',
            description: 'Konfiguréieren wéi Dir Notifikatiounen kritt',
            category: {
                label: 'Kategorie',
                task_assigned: 'Aufgab zougewisen',
                task_sla_approaching: 'SLA Approche',
                task_overdue: 'Aufgab eriwwer',
                task_completed: 'Aufgab fäerdeg',
                task_escalated: 'Aufgab Eskaléiert',
                workflow_transition: 'Workflow Transitioun',
                approval_requested: 'Genehmegung gefrot',
                comment_added: 'Kommentar dobäigesat',
                weekly_digest: 'Wochemaart Digest',
                sla_breach_report: 'SLA Verstouss Bericht',
                system_alert: 'System Alarm',
                // English until Phase 25 (R-57).
                rights_request_received: 'New Rights Request',
                // English until Phase 25 (R-57).
                acknowledgment_reminder: 'Acknowledgment Reminder',
                approval_expired: 'Approval Request Expired',
                scheduled_export_delivered: 'Scheduled Export Delivered',
                cross_border_transfer_added: 'Cross-Border Transfer Added',
            },
            channel: {
                platform: 'Plattform',
                email: 'Email',
            },
            quiet_hours: {
                title: 'Roueg Stonnen',
                start: 'Starten',
                end: 'End',
                timezone: 'Zäitzone',
            },
            digest: {
                title: 'Digest Frequenz',
                daily: 'Deeglech',
                weekly: 'Wochemaart',
                none: 'Keen',
            },
            watched: {
                title: 'Iwwerwaacht Entitéiten',
                empty: 'Keng iwwerwaacht Entitéite',
            },
            save: 'Späicheren Preferenzen',
            saving: 'Spuert ...',
            saved_success: 'Virléiften erfollegräich gespäichert',
            load_error: 'Konnt gespäichert Virléiften net lueden. Defaults weisen - Ännerungen ginn gespäichert wann Dir op Späicheren klickt.',
            enabled: 'Aktivéiert',
            disabled: 'Behënnert',
            unwatch: 'Unwatch',
            announce_loaded: 'Notification preferences loaded',
            save_error: 'Could not save your notification preferences. Your changes are still on the page; try saving again.',
        },
        admin_queue: {
            title: 'Notifikatiounsadministratiounswuerteschlang',
            description: 'E-Mail-Liwwerstatus iwwerwaachen an gescheitert Messagen nach eng Kéier probéieren',
            load_error: 'E-Mail-Wuerteschlangdaten konnten net gelueden ginn',
            retry_error: 'E-Mail konnt net nach eng Kéier probéiert ginn',
            filter_status: 'No Status filteren',
            all_statuses: 'All',
            refresh: 'Aktualiséieren',
            status_queued: 'An der Wuerteschlang',
            status_sent: 'Geschéckt',
            status_failed: 'Gescheitert',
            status_bounced: 'Zréckgewisen',
            status_sending: 'Sending',
            col_recipient: 'Empfänger',
            col_category: 'Kategorie',
            col_template: 'Schabloun',
            col_status: 'Status',
            col_retries: 'Widderhuelungen',
            col_created: 'Erstallt',
            col_sent: 'Geschéckt um',
            col_actions: 'Aktiounen',
            no_items: 'Keng E-Mail-Wuerteschlangentréi fonnt',
            retry: 'Nach eng Kéier probéieren',
            retrying: 'Gëtt nach eng Kéier probéiert...',
            prev: 'Zréck',
            next: 'Weider',
            access_denied_title: 'Administrateur-Zougang erfuerderlech',
            access_denied: 'Dës Säit ass nëmme fir Administrateuren zougänglech. Kontaktéiert w.e.g. Ären Administrateur wann Dir Zougang braucht.',
            announce_queue_loaded: 'E-Mail Schlaang gelueden, {count} Elementer',
            announce_processed: 'Veraarbechtte {count} E-Mail(en)',
            process_now: 'Prozess Elo',
            processing_ellipsis: 'Veraarbechtung…',
        },
    },
    ui_assist: {
        toolbar: {
            title: 'UI Assist',
            activate: 'Aktivéieren',
            deactivate: 'Deaktivéieren',
            connected: 'Ugeschloss',
            disconnected: 'Deconnectéiert',
            disconnected_hint: 'Disconnected - lafen `npm run relais` fir mam Claude CLI ze verbannen',
            open_panel: 'Open UI Assist Panel',
            history: 'Geschicht',
            settings: 'Astellungen',
        },
        prompt: {
            placeholder: "Beschreift d'Ännerung déi Dir maache wëllt ...",
            send: 'Schécken',
            cancel: 'Ofbriechen',
            char_count: '{count} / {max}',
        },
        session: {
            streaming: 'Veraarbechtung…',
            complete: 'Komplett',
            error: 'Feeler',
            abort: 'Ofbriechen',
            abort_confirm: 'Sidd Dir sécher datt Dir wëllt ofbriechen?',
            aborted_hint: "D'Sitzung gouf ënnerbrach — Dir kënnt vun hei aus weiderfueren.",
            undo: 'Undoen',
            redo: 'Redo',
            retry: 'Nach eng Kéier probéieren',
            dismiss: 'Entlooss',
        },
        label: {
            component: 'Komponent',
            element: 'Element',
            file: 'Fichier',
        },
        response: {
            you: 'You',
            claude: 'Claude',
            empty: 'Nee Messagen nach. Wielt Elementer a schéckt eng Ufro fir unzefänken.',
        },
        panel: {
            tab_conversation: 'Chat',
            tab_tasks: 'Aufgaben',
            tab_sprint: 'Sprint',
            tab_sessions: 'Sessiounen',
            tab_lobby: 'Lobby',
            follow_up: 'Follegt Up',
            followup_placeholder: 'Stellt eng Suivi Fro ...',
            interrupt_placeholder: "Tippt fir z'ënnerbriechen an ëmzeleeden…",
            send_interrupt: 'Schécken & Ënnerbriechen',
            will_interrupt: 'Brécht déi aktuell Äntwert of a schéckt dëst amplaz',
            expand: 'Op Vollbild vergréisseren',
            collapse: 'Op Säitepanel verklengen',
            no_sessions: 'Keng vireg Sessiounen. Wielt Elementer an schéckt eng Ufro fir unzefänken.',
            session_active: 'Aktiv',
            session_ended: 'Ofgeschloss',
            session_messages: '{count} Messagen',
            switch_session: 'Op dës Sessioun wiesselen',
            load_session: 'Lued Sëtzung Geschicht',
            session_restored: 'Sessioun aus der Geschicht restauréiert',
            archive_session: 'Archiv Sëtzung',
            archive_confirm: "Dës Sessioun archivéieren? D'Sessiounsdatei gëtt geläscht.",
            new_session: 'Nei Sëtzung',
            new_session_desc: 'Benotzt Aktivéieren fir en UI Element ze wielen an eng nei Sessioun unzefänken',
            start_new_session: 'Start New Sessioun',
            no_active_session_chat: 'Start eng nei Sessioun oder benotzt Aktivéieren fir en UI Element op der Säit ze wielen fir ze starten.',
            no_active_session_tasks: 'Start eng nei Sessioun oder wielt eng existent Sessioun fir Aufgaben ze kreéieren.',
            no_active_session_sprint: 'Start eng nei Sessioun oder wielt eng existent Sessioun fir e Sprint ze plangen.',
            previous_sessions: 'Virdrun Sessiounen',
        },
        tasks: {
            none_found: 'Keng Aufgaben an der Äntwert fonnt. Frot de Claude Aufgaben ze proposéieren.',
            selected: 'selected',
            create_selected: 'Erstelle Aufgaben',
            execute_tasks: 'Aufgaben ausféieren',
            creating: 'Erstellt…',
            created: 'Aufgaben erstallt',
            create_error: 'Ausgefall Aufgab ze schafen',
            plan_sprint: 'Sprint plangen',
            priority_critical_desc: 'Blocker — direkt fléckegen',
            priority_high_desc: 'Wichteg — an dësem Sprint léisen',
            priority_medium_desc: 'Normal — fir kommende Sprint plangen',
            priority_low_desc: 'Gutt ze hunn — Backlog-Kandidat',
            add_custom: 'Aufgab derbäisetzen',
            add_title_placeholder: 'Aufgabentitel…',
            add_desc_placeholder: 'Beschreiwung (optional)',
            add_save: 'Derbäisetzen',
            add_cancel: 'Ofbriechen',
        },
        sprint: {
            title: 'Sprint Planner',
            assigned: 'assigned',
            new_placeholder: 'Sprint Numm (zB Sprint 18.1.0)',
            add: 'Add',
            backlog: 'Réckstand',
            move_to: 'Plënneren op…',
            empty: 'Erstellt eng Sprintgrupp a gitt Aufgaben un.',
            create_all: 'Erstellt All Aufgaben',
            remove: 'Ewechzehuelen',
            drop_here: 'Assignéiert Aufgaben aus dem Réckstand hei drënner',
            add_task: 'Füügt Aufgab aus dem Réckstand ...',
        },
        feature_disabled: 'UI Assist ass net aktivéiert',
        permission_denied: 'Dir hutt keng Erlaabnis fir UI Assist ze benotzen',
    },
    license: {
        title: 'Lizenz & Package',
        current_license: 'Aktuell Lizenz',
        license_key: 'Lizenz Schlëssel',
        activate: 'Aktivéiert Lizenz',
        deactivate: 'Desaktivéiere Lizenz',
        validate_now: 'Elo validéieren',
        status: 'Lizenz Status',
        active: 'Aktiv',
        expired: 'Ofgelaaf',
        suspended: 'Suspendéiert',
        invalid: 'Invalid',
        pending: 'Erwaardung',
        no_license: 'Keng Lizenz',
        expires_at: 'Erléist',
        last_validated: 'Last validéiert',
        domain: 'Lizenzéiert Domain',
        activation_success: 'Lizenz aktivéiert erfollegräich.',
        activation_failed: 'Lizenz Aktivéierung gescheitert.',
        domain_mismatch: 'Lizenz Domain entsprécht net déi aktuell Domain.',
        deactivation_success: 'Lizenz desaktivéiert.',
        validation_success: 'Lizenz erfollegräich validéiert.',
        validation_failed: 'Lizenz Validatioun gescheitert.',
        enter_key: 'Gitt Äre Lizenzschlëssel un',
        confirm_deactivate: 'Sidd Dir sécher datt Dir dës Lizenz desaktivéiere wëllt? Modul Restriktiounen gëllen baséiert op der Default Tierm.',
        announce_loaded: 'License settings loaded',
        verification_stale: 'This licence has not been verified with the licence service for more than {days} days, so it reads as unverified. It reads as valid again once a revalidation succeeds.',
        scope_installation: 'The licence and its package apply to the whole installation: every organisation on it. Only a platform administrator can activate, deactivate or change them.',
    },
    package_mgmt: {
        title: 'Package',
        module_column: 'Modul',
        current_tier: 'Aktuelle Package',
        light: 'Liicht',
        standard: 'Standard',
        enterprise_unlimited: 'Enterprise Onlimitéiert',
        change_package: 'Package änneren',
        upgrade: 'Upgrade',
        downgrade: 'Downgrade',
        target_tier: 'Zil Package',
        new_license_key: 'Lizenz Key fir Target Package',
        confirm_change: 'Confirméieren Package Change',
        change_success: 'Package erfollegräich geännert.',
        change_failed: 'Package Ännerung ausgefall.',
        modules_included: 'Moduler abegraff',
        modules_restricted: 'Net verfügbar am aktuelle Package',
        upgrade_to_enable: "Upgrade fir dëse Modul z'aktivéieren.",
        module_availability: 'Modul Disponibilitéit',
        comparison: 'Package Verglach',
        power_users: 'Power Benotzer',
        unlimited: 'Onlimitéiert',
        impact_warning: 'Downgrading wäert déi folgend Moduler deaktivéieren:',
        invalid_license: 'De geliwwerte Lizenzschlëssel ass net valabel fir dëse Package.',
        tier_mismatch: 'De Lizenzschlëssel ass fir en anere Package Tier.',
    },
    board_report: {
        title: 'Verwaltungsrot-Compliancebericht',
        subtitle: 'Zesummefaassung vun der Compliance iwwer all Moduler',
        date_from: 'Vun Datum',
        date_to: 'Bis Datum',
        language: 'Berichtsprooch',
        generate: 'Bericht erstellen',
        generating: 'Bericht gëtt erstallt...',
        generate_requires_export: 'Generating a report needs the permission to export governance reports. Ask an administrator to grant it.',
        download_pdf: 'PDF eroflueden',
        close: 'Bericht zoumaachen',
        open_board_report: 'Verwaltungsrotbericht erstellen',
        close_board_report: 'Verwaltungsrotbericht zoumaachen',
        report_period: 'Berichtszäitraum',
        generated_at: 'Erstallt den',
        generated_by: 'Erstallt vun',
        organization: 'Organisatioun',
        section_rights_requests: 'Rechtsufroën',
        section_vendors: 'Liwweranten',
        section_dpias: 'DSFAen',
        section_consent: 'Zoustëmmungsrichtlinnen',
        section_data_mapping: 'Datekartéierung',
        section_incidents: 'Virgäng',
        section_classification: 'Dateklassifizéierung',
        section_governance: 'Gouvernance',
        open: 'Op',
        closed: 'Zou',
        sla_compliance: 'SLA-Anhaltung',
        total: 'Gesamt',
        expired_dpa: 'Ofgelafe DPA',
        high_risk: 'Héicht Risiko',
        completed: 'Ofgeschloss',
        overdue: 'Iwwerfälleg',
        policies_active: 'Aktiv Richtlinnen',
        coverage: 'Ofdeckung',
        systems: 'Systemer',
        completeness: 'Vollstännegkeet',
        avg_resolution_days: 'Duerchschn. Léisungszäit (Deeg)',
        labeled: 'Gekennzeechent',
        unlabeled_systems: 'Ongekennzeechent Systemer',
        compliance_score: 'Compliance-Bewäertung',
        drift_events_open: 'Oppe Ofwäichungen',
        no_data: 'Keng Donnéeën fir den ausgewielte Zäitraum verfügbar.',
        error_date_range: 'Wielt w.e.g. souwuel Start- wéi Enddatum.',
        table_category: 'Kategorie',
        table_metric: 'Metrik',
        table_value: 'Wäert',
        showing_chart_view: 'Grafik-Ansicht gëtt ugewisen',
        showing_table_view: 'Datentabell-Ansicht gëtt ugewisen',
        error_generate: 'Verwaltungsrotbericht konnt net erstallt ginn.',
        playbook_evidence_title: 'Beweis vun der Playbook-Ausféierung',
        playbook_evidence_description: 'Ausféierungsaktivitéit pro Playbook an ugehaang Beweisdateien fir déi gemellte Period.',
        playbook_name: 'Spillbuch',
        playbook_execution_count: 'Ausféierungen',
        playbook_steps_total: 'Schrëtter',
        playbook_steps_required: 'Beweis erfuerderlech',
        playbook_steps_attached: 'Beweis ugehaang',
        playbook_coverage_pct: 'Ofdeckung',
        playbook_evidence_files: 'Beweisdateien',
        playbook_no_evidence: 'Keng Playbook-Ausféierungen an dëser Period.',
        playbook_download_file: 'Datei eroflueden',
    },
    framework_module_links: {
        title: 'Rahmen Verknëpplung',
        modules: 'Verknëppelte Moduler',
        add_module: 'Modul addéieren',
        module_code: 'Modulcode',
        required: 'Obligatoresch',
        notes: 'Notizen',
        link: 'Verknëppen',
        no_modules: 'Keng Moduler verknëppelt',
        personas: 'Verknëppelte Personas',
        add_persona: 'Persona addéieren',
        persona_id: 'Persona-ID',
        role_description: 'Rollbeschreiwung',
        no_personas: 'Keng Personas verknëppelt',
    },
    framework_channels: {
        title: 'Aschreiwungskanäl & Fristungen',
        channels: 'Aschreiwungskanäl',
        add_channel: 'Kanal addéieren',
        channel_type: 'Typ',
        name: 'Numm',
        instructions: 'Instruktioune',
        active: 'Aktiv',
        no_channels: 'Keng Aschreiwungskanäl konfiguriert',
        portal: 'Portal',
        in_person: 'Perséinlech',
        postal: 'Post',
        email: 'E-Mail',
        add: 'Addéieren',
        deadlines: 'Regulatoresch Fristungen',
        add_deadline: 'Frist addéieren',
        label: 'Bezeichnung',
        days: 'Deeg',
        day_type: 'Dagtyp',
        calendar_days: 'Kalenderdeeg',
        working_days: 'Aarbechtsdeeg',
        description: 'Beschreiwung',
        no_deadlines: 'Keng Fristungen konfiguriert',
    },
    regulatory_updates: {
        title: 'Regulatoresch Aktualiséierungen & Rahmen-Dashboard',
        coverage_dashboard: 'Rahmenofdeckungs-Dashboard',
        no_frameworks: 'Keng Rahmenwierker fonnt',
        framework: 'Rahmewierker',
        status: 'Status',
        modules: 'Moduler',
        templates: 'Virlagen',
        forms: 'Formulairen',
        channels: 'Kanäl',
        deadlines: 'Fristungen',
        pending: 'Ausstehend Aktualiséierungen',
        updates: 'Regulatoresch Aktualiséierungen',
        create_update: 'Aktualiséierung erstellen',
        framework_id: 'Rahmewierker-ID',
        update_title: 'Titel',
        description: 'Beschreiwung',
        effective_date: 'Gëltigkeetsdatum',
        create: 'Erstellen',
        no_updates: 'Keng regulatoresch Aktualiséierungen',
        impact: 'Auswierkung',
        assess: 'Bewäerten',
    },
    module_tips: lbModuleTips,
    framework_rights: {
        be_informed: 'Recht op Informatioun',
        consent: 'Recht op Zoustëmmung',
        refuse_consent: "Recht d'Zoustëmmung ze refuséieren",
        withdraw_consent: "Recht d'Zoustëmmung zeréckzezéien",
        access: 'Recht op Zougang',
        rectification: 'Recht op Berichtegung',
        data_provision: 'Recht op Datëbereetstellung',
        deletion: 'Recht op Läschung',
        restriction_of_processing: 'Recht op Aschränkung vun der Veraarbechtung',
        object_to_processing: "Recht Asproch géint d'Veraarbechtung ze erhiewen",
        complaint: 'Recht eng Reklamatioun anzeginn',
        denunciation: 'Recht op Dénoncéierung',
        lawsuit: 'Recht en Prozess unzefänken',
        claim_damages: 'Recht op Schuedenersaz',
        request_protection_measures: 'Recht Schutzmoossnahmen ze froen',
    },
    framework_content: {
        EU_EPRIVACY_DIRECTIVE: {
            display_name: 'Direktiv 2002/58/EG (ePrivacy-Direktiv)',
            description: "Mammendirektiv fir d'national Ëmsetzunge vun der ePrivacy. Si deckt Cookien, d'Vertraulechkeet vun den elektronesche Kommunikatiounen an d'direkt Vermaartung. All Memberstaat muss se an d'national Recht ëmsetzen.",
        },
        EU_GDPR: {
            display_name: 'Datebschutz-Grondverordnung (EU) 2016/679 (DSGVO/RGPD)',
            description: "D'Datebschutz-Grondverordnung (DSGVO/RGPD) ass eng EU-Verordnung iwwer den Datebschutz an d'Privatsphär. Si gëllt direkt an alle Memberstaate vun der EU an dem EWR.",
        },
        EU_NIS2_DIRECTIVE: {
            display_name: 'Direktiv (EU) 2022/2555 (NIS2)',
            description: "Mammendirektiv fir d'national Ëmsetzunge vun der NIS2. Si deckt d'Risikomanagement vun der Cybersécherheet, d'Notifikatioun vu Virfäll an d'Liwwerketten-Sécherheet fir wesentlech a wichteg Entitéiten.",
        },
        VN_AI_LAW_2026: {
            display_name: 'Vietnamesescht KI-Gesetz (134/2025/QH15) — a Kraaft den 01. Mäerz 2026',
            description: "Vietnamesescht KI-Gesetz (Luật Trí tuệ nhân tạo 134/2025/QH15) iwwer d'Entwécklung, den Asaz an d'Notzung vu Systemer fir kënschtlech Intelligenz. Et leet d'Risikoklassifikatioun, d'Transparenz- a Kennzeechnungsflichten, d'Verflichtungen zur Notifikatioun vu Virfäll, d'Verflichtungen zur Impaktbewäertung an e kontrolléierte Testmechanismus (Sandbox) fir KI-Aktivitéiten a Vietnam fest.",
        },
        VN_AI_DECREE_142_2026: {
            display_name: 'Decree 142/2026/NĐ-CP — AI Law Implementation',
            description: 'Decree 142/2026/NĐ-CP (Nghị định số 142/2026/NĐ-CP) detailing certain articles and implementing measures of the Vietnam AI Law (Law 134/2025/QH15). Issued 2026-04-30, effective 2026-05-01. Lead authority: Ministry of Science and Technology. Operationalises AI risk classification, classification dossier, reclassification review, high-risk conformity assessment, high-risk risk-management system, transparency / explanation / technical marking / visible labeling, serious AI incident reporting (72h / 5wd / 15d), state-agency AI impact assessment (Mẫu AI02), and the controlled testing / AI sandbox lifecycle (Mẫu AI03a-AI09b). Submission via the AI one-stop e-portal or MoST transitional channel. No statutory fines.',
        },
        VN_CYBERSEC_LAW: {
            display_name: 'Vietnamesescht Cybersécherheetsgesetz (Ära 24/2018)',
            description: "Vietnam säi Gesetz iwwer d'Cybersécherheet (Gesetz Nr. 24/2018/QH14), dat d'Ufuerderunge fir Cybersécherheet, Datelokaliséierung an Notifikatioun vu Virfäll fest leet. A Kraaft bis et duerch d'Gesetz 116/2025/QH15 ofgeléist gëtt, dat de 01.07.2026 a Kraaft trëtt.",
        },
        VN_CYBERSEC_DECREE_53: {
            display_name: 'Dekret 53 — Ëmsetzung vum Cybersécherheetsgesetz',
            description: "Dekret 53/2022/ND-CP zur Ëmsetzung vum Cybersécherheetsgesetz; reegelt d'Bestëmmung vum Uwendungsberäich, d'Ausléiser fir d'Datelokaliséierung, d'Ufuerderunge fir lokal Präsenz an d'Workflowe fir d'Zesummenaarbecht mat de Behéierden.",
        },
        VN_CYBERSEC_LAW_2025: {
            display_name: "Gesetz iwwer d'Cybersécherheet (116/2025/QH15)",
            description: "Vietnam säi konsolidéiert Gesetz iwwer d'Cybersécherheet (Gesetz 116/2025/QH15), dat d'Gesetz 24/2018/QH14 ersetzt; a Kraaft den 1. Juli 2026. Et konsolidéiert den Uwendungsberäich vum Dekret 53/2022/NĐ-CP (kuckt VN_CYBERSEC_DECREE_53).",
        },
        VN_DATA_LAW: {
            display_name: 'Vietnamesescht Datengesetz',
            description: "Vietnam säin Datengesetz (Gesetz 60/2024/QH15), dat ëmfaassend Ufuerderungen un d'Datengouvernance fest leet, ënner anerem Dateninventar, Klassifikatioun, Gouvernance vum Datedeelen a Gouvernance vum grenziwwerschreidende Datentransfert. Et iwwerschneit sech mat VN_PDPL_LAW_2025 (perséinlech Aspekter) a mat VN_CYBERSEC_LAW_2025 (Datelokaliséierung/Klassifikatioun).",
        },
        VN_DATA_LAW_DECREE_165_2025: {
            display_name: 'Dekret 165/2025/NĐ-CP (Ëmsetzung vum Datengesetz)',
            description: "Dekret 165/2025/NĐ-CP zur Ëmsetzung vum vietnameseschen Datengesetz (Gesetz 60/2024/QH15). Et stellt offiziell Formulairen zur Verfügung fir d'Impaktbewäertung vum grenziwwerschreidende Datentransfert, fir d'Joresberichterstattung iwwer d'Risikobewäertung a fir den Zougang zur nationaler Datebank. Et definéiert 11 offiziell Mẫu số-Formulairen.",
        },
        VN_DATA_EXCHANGES_DRAFT_2026: {
            display_name: 'Projet vun Dekret iwwer Datenbörsen 2026',
            description: "Projet vun Dekret iwwer de Betrib vun Datenaustauschplattformen (Dự thảo Nghị định quy định về hoạt động của sàn dữ liệu). Et reegelt d'Klassifikatioun vun de Plattformen, d'Berechtegung vun den Deelhuewer, d'Kontrolle fir d'Lëschtung vun Dateprodukter a -déngschter, d'Sandbox-Tester, de Workflow vun den Transaktiounen, d'elektronescht Vertragsschloss, d'Zertifikater vun den Eegentumsrechter iwwer Donnéeën an d'Behandlung vu Reklamatiounen a Sträitfäll. Aktuell an ëffentlecher Konsultatioun; nach net promulgéiert. Bezitt sech op d'Datengesetz 60/2024/QH15, d'Gesetz iwwer den Schutz vu perséinlechen Donnéeën 91/2025/QH15, d'Gesetz iwwer d'Cybersécherheet 116/2025/QH15 an d'Gesetz iwwer elektronesch Transaktiounen 20/2023/QH15.",
        },
        VN_ECOMMERCE_LAW_2025: {
            display_name: 'Gesetz iwwer den elektronesche Commerce (122/2025/QH15)',
            description: "Vietnamesescht Gesetz iwwer den elektronesche Commerce (Luật Thương mại điện tử 122/2025/QH15), dat e vereenheetlecht nationaalt Regimm fir den elektronesche Commerce schaaft. Et deckt den Akt vun den Inland- a grenziwwerschreidende Plattformen, d'Plattformgouvernance an d'Verantwortlechkeet, de Konsumenteschutz, d'Kontrollen iwwer Verkeefer a Filialen, d'Behandlung vu Reklamatiounen, d'Inhaltsmoderéierung an d'Duerchsetzung vum Eraushuele vun Inhalter, sou wéi d'administrativ Prozedurprostall fir de staatleche Gestiounssystem fir den elektronesche Commerce of.",
        },
        VN_PDPD_DECREE_13: {
            display_name: 'Dekret 13/2023/NĐ-CP',
            description: "Dekret 13/2023/ND-CP, den operationelle Mechanismus fir d'PDPL-Verflichtungen, ënner anerem d'Aféierung vum Dossier, d'Gouvernance-Artefakter, d'grenziwwerschreidend Mechanik an d'administrativ Prozeduren vum MoPS. Ass den 1. Januar 2026 op Grond vum Artikel 42 Absaz 2 vum Dekret 356/2025/NĐ-CP ausser Kraaft getrueden; d'Gesetz 91/2025/QH15 regelt elo de Schutz vu perséinlechen Donnéeën.",
        },
        VN_PDPL_LAW_2025: {
            display_name: 'Gesetz iwwer de Schutz vu perséinlechen Donnéeën (91/2025/QH15)',
            description: "Vietnam säi Gesetz iwwer de Schutz vu perséinlechen Donnéeën (Gesetz 91/2025/QH15), dat e ëmfaassende Kader fir de Schutz vu perséinlechen Donnéeën schaaft, a Kraaft den 1. Januar 2026. Et deckt d'Conformitéitsthemen A–J: legitim Veraarbechtungsgrondlagen, Zoustëmmung fir sensibel Donnéeën, Rechter vun de Betraffenen (Zougang/Berichtegung/Läschung/Aschränkung/Widderspréch/Iwwerdroebarkeet), Notifikatioun vu Verstéiss un d'Behéierd an un d'Betraffen, DPIA fir héich-riskant Veraarbechtungen, Gouvernance vu grenziwwerschreidenden Transferten, Bestëmmung vum DPO, Verzeechness vun de Veraarbechtungsaktivitéiten a vertraglech Verflichtungen vum Veraarbechter. Notifikatiounsverflichtungen un d'Behéierd iwwer den MoPS (VN_PDPL_ADMIN_PROCEDURES_778_2026). Querverweiser: VN_PDPL_DECREE_356_2025 (Ëmsetzungsdekret), VN_CYBERSEC_LAW_2025 (Iwwerschneidunge mat der Cybersécherheet), VN_DATA_LAW (Datengouvernance).",
        },
        VN_PDPL_DECREE_356_2025: {
            display_name: 'Dekret 356/2025/ND-CP (Ëmsetzung vum PDPL)',
            description: "Dekret 356/2025/ND-CP zur Ëmsetzung vum Gesetz iwwer de Schutz vu perséinlechen Donnéeën (Gesetz 91/2025/QH15). Et deckt d'Aféierung vum Dossier, d'Gouvernance-Artefakter, d'grenziwwerschreidend Mechanik an d'Zertifizéierung vun den Déngschtleeschter. Inventar vun den Annex-Formulairen: Phụ lục I–VIII (Impaktbewäertung, grenziwwerschreidenden Transfert, Zertifizéierung). Administrativ Prozeduren vum MoPS iwwer VN_PDPL_ADMIN_PROCEDURES_778_2026 (Decisioun 778/QĐ-BCA-A05). Workflow vun der Zertifizéierung: Demande → Ausstellung → Demande zur Ännerung → Neiausstellung → Réckzuch. Querverweiser: VN_PDPL_LAW_2025 (Stammgesetz), VN_CYBERSEC_LAW_2025 (Datelokaliséierung), VN_DATA_LAW (Iwwerschneidunge mat der Datengouvernance).",
        },
        VN_PDPL_ADMIN_PROCEDURES_778_2026: {
            display_name: 'Administrativ Prozeduren vum MPS A05 (Decisioun 778/QĐ-BCA-A05)',
            description: "Decisioun 778/QĐ-BCA-A05 (Februar 2026) — Administrativ Prozeduren fir de Schutz vu perséinlechen Donnéeën ënner A05 (MPS). Definéiert d'Prozeduren fir d'Aginn vum Dossier, d'Veraarbechtung an d'Ausstellung vum Resultat, ënner anerem d'DLCN-HC-Formulairen an déi statutaresch Mẫu số-Formulairen.",
        },
        VN_PDPL_ENFORCEMENT_DRAFT_2026: {
            display_name: 'Projet vun Duerchsetzungsdekret 2026',
            description: "Projet vun Duerchsetzungs- a Sanktiounsdekret fir Verstéiss géint d'Gesetz iwwer de Schutz vu perséinlechen Donnéeën (Gesetz 91/2025/QH15). Definéiert d'Geldstrofen-Spannen, d'verschäerfend a verschwächend Faktoren an d'Duerchsetzungsprozeduren. Aktuell am Projetsstatus, an Erwaardung vun der Promulgatioun.",
        },
        VN_MOPS_ADMIN_SIMPLIFICATION_NQ22_2026: {
            display_name: 'Government Resolution NQ22/2026 — MoPS Administrative Simplification (Temporary Overlay)',
            description: 'Temporary procedural overlay (effective 2026-04-29, expiring 2027-03-01) introducing three official channels (National Public Service Portal, direct submission to MoPS, postal submission to MoPS) for PDPL Decree 356/2025 and Decision 778/2026 dossier submissions. Email is NOT a NQ22 official channel — a separate unofficial email-ZIP submission guidance (VN_PDPL_GUIDANCE_EMAIL_ZIP_DOSSIER_SUBMISSION_2026) is shipped with requires_legal_review: true. Procedural overlay only — does NOT alter substantive obligations, fine ranges, or the binding force of underlying frameworks. Review task scheduled 2027-02-01.',
        },
        VN_TELECOM_LAW_2023: {
            display_name: 'Vietnamesescht Telekomgesetz 2023',
            description: 'Vietnamesescht Telekomgesetz 2023 (Luật Viễn thông), e Sektorouverlay fir Telekom-/ISP-/Plattformenentreprisen mat Verflichtungen zur Vertraulechkeet, Kontroll vun der Offenleeung an Behandlung vun de Behéierdeufroen. Querverweiser: VN_CYBERSEC_LAW_2025 (Sécherheet vun den Informatiounssystemer), VN_PDPL_LAW_2025 (Veraarbechtung vun de Benotzerdaten), VN_DATA_LAW (Datengouvernance).',
        },
        VN_TELECOM_DECREE_163_2024: {
            display_name: 'Dekret 163/2024/NĐ-CP (Ëmsetzung vum Telekomgesetz)',
            description: "Dekret 163/2024/NĐ-CP zur Ëmsetzung vum Telekomgesetz (Gesetz 24/2023/QH15). Et stellt offiziell Formulairen zur Verfügung fir d'Notifikatioun vun Telekomdéngschter, d'Lizenzéierung, d'Aschreiwung an d'Plang fir d'Entwécklung vun Datenzentren. Et definéiert 7 offiziell Mẫu số-Formulairen.",
        },
    },
    framework_requirements: {
        vn_dp_draft_f: {
            title: 'Reklamatiounen, Sträitfäll a Risikomanagement',
            description: "D'Plattformbedreiwer musse Mechanisme fir d'Annahme vu Reklamatiounen, e Workflow fir d'Behandlung vu Sträitfäll mat Bewäisbewahrung, Kontrolle fir d'Identifizéierung vu Risiken a Fréiwarnungen, an eng periodesch Iwwerpréiwung vun de Risiken am Beräich vum Datebschutz, vun der Cybersécherheet a vum Zougangsmanagement zur Verfügung stellen.",
        },
        vn_dp_draft_e: {
            title: 'Workflow vun den Transaktiounen an Zertifikat vun den Eegentumsrechter iwwer Donnéeën',
            description: "Transaktioune verlaangen d'Aschreiwung vum Kont, d'Approbatioun vun der Lëschtung, e elektronesche Vertragsschloss, eng Bestätegung vun der Bezuelung, eng Liwwerungsprotokoll an, falls applikabel, d'Ausstellung vun engem Zertifikat vun den Eegentumsrechter iwwer Donnéeën. D'Transaktiounsgeschicht an d'Réckverfollegbarkeet musse an engem Transaktiounsregëster bewahrt ginn.",
        },
        vn_dp_draft_d: {
            title: 'Kontrollen am Sandbox- an Testëmfeld',
            description: "Testëmfelder mussen isoléiert sinn, hir Zougäng aschrünken, sécherheetsiwwerwaacht ginn an iwwerpréift ginn, ier eng Ausgab eraus geholl gëtt. Keng Quelldate dierfen aus dem Testëmfeld eraus geholl ginn an d'Schluss/d'Ofschalten vu Testsessiounen muss protokolléiert ginn.",
        },
        vn_dp_draft_c: {
            title: "Kontrolle fir d'Lëschtung vun Dateprodukter a -déngschter",
            description: "D'Lëschtunge mussen duerch Beweiser vun der legaler Quell an de Besëtz-/Notzungsrechter, technesch Validéierungsprotokoller, Qualitéitsoffeleeungen a Protokoller vun der Iwwerpréiwung an Approbatioun duerch d'Plattform ënnerstëtzt ginn. Verbueden Donnéeë mussen virun der Lëschtung erausgefiltert ginn.",
        },
        vn_dp_draft_b: {
            title: 'Berechtegung an Onboarding vun den Deelhuewer',
            description: "D'Plattformbedreiwer mussen de juristesche Status, d'juristesch Kapazitéit an d'Ufuerderunge fir konditionéiert Aktivitéite vu Verkeefer a Keefer iwwerpréiwen, d'Transaktiouns- a Bezuelungskonten aschreiwen an e Register vun den Deelhuewer féieren.",
        },
        vn_dp_draft_a: {
            title: 'Klassifikatioun an Operatiounsmodell vun der Datenplattform',
            description: "D'Plattformbedreiwer mussen eng Klassifikatioun vum Plattformtyp festleeën (national Datenplattform vs aner Datenplattformen), d'Berechtegung an d'Verantwortlechkeete vum Bedreiwer definéieren an d'Tariffer, d'Käschten, d'Operatiounsregelen an d'technesch Gouvernance dokumentéieren.",
        },
        d165_r5: {
            title: 'Zougang zu den Informatiounen vun der nationaler Datebank',
            description: "Demande fir d'Erausziehe/Notzung vun Informatiounen aus der nationaler digitaler Datebank verwenden virgeschriwwe Formulairen (Mẫu số 07a/07b).",
        },
        d165_r4: {
            title: "Joresberichterstattung iwwer d'Risikobewäertung",
            description: "D'Joresrapporten iwwer d'Risikobewäertung musse mat de virgeschriwwene Formulairen agereecht ginn (Mẫu số 05a/05b, 06).",
        },
        d165_r3: {
            title: 'Notifikatioun vum Enn vum grenziwwerschreidende Transfert',
            description: "D'Enn vum grenziwwerschreidende Transfert muss mat de virgeschriwwene Formulairen notifizéiert ginn (Mẫu số 04a/04b).",
        },
        d165_r2: {
            title: 'Notifikatioun vum Resultat vun der Bewäertung',
            description: "D'Behéierd stellt Notifikatioune vum Bewäertungsresultat op dem virgeschriwwene Formulair aus (Mẫu số 03).",
        },
        d165_r1: {
            title: 'Aginn vum Impaktbewäertungsdossier fir grenziwwerschreidend Datentransferten',
            description: "D'Organisatioune mussen d'Dossiere fir d'Impaktbewäertung vum grenziwwerschreidende Datentransfert/-veraarbechtung op de virgeschriwwene Formulairen aginn (Mẫu số 01a/01b, 02).",
        },
        a964190d_8d1d_4284_bc7d_4023e2866751: {
            title: 'Cookie-Aweis a vergläichbar Technologien',
            description: "D'Späichere vun oder den Zougang zu Informatiounen op engem Endgerät ass nëmme mat virgängege informéierte Cookie-Aweis erlaabt, ausser fir technesch néideg Späicherung.",
        },
        '8437d96d_cf01_4f87_89f6_4faaf8eefdf6': {
            title: 'Vertraulechkeet vun de Kommunikatiounen',
            description: "D'Memberstaate stelle sécher, datt d'Vertraulechkeet vun de Kommunikatiounen an de bezeechente Verkéiersdaten iwwer en ëffentlecht Kommunikatiounsnetz a vu ëffentlech zougängleche elektronesche Kommunikatiounsdéngschter respektéiert gëtt.",
        },
        c5420073_50c6_4981_816a_398460e5f546: {
            title: 'Aweis fir direkt Vermaartung (Opt-in)',
            description: "D'Notzung vun elektronesche Kommunikatiounssystemer fir direkt Vermaartung verlaangt de virgängegen Aweis vum Abonnent.",
        },
        c8bf67d2_874b_4647_92d7_39aaa3230819: {
            title: 'Soft-Opt-in fir bestoend Cliente',
            description: "D'Direkt Vermaartung u bestoend Cliente fir ähnlech Produkter/Déngschter ass ouni Aweis erlaabt, wann d'Méiglechkeet zum Opt-out ugebuede gëtt.",
        },
        b546df40_9217_4204_b71c_5af5022a0ff9: {
            title: 'Notifikatioun vu Verstéiss fir Telekomdéngschtleeschter',
            description: "D'Uweenter vu ëffentlech zougängleche elektronesche Kommunikatiounsdéngschter mussen der zoustänneger nationaler Behéierd Donnéeëverletzunge vu perséinlechen Donnéeën notifizéieren.",
        },
        '5bd66ae4_dee3_4611_9104_71742df55484': {
            title: 'Recht op Widderspréch géint direkt Vermaartung',
            description: "D'Abonnente mussen d'Méiglechkeet kréien, jee zu all Moment a gratis Widderspréch géint den Empfank vu Messagen vun der direkter Vermaartung anzeleeën.",
        },
        a10af31f_ba28_494e_ab50_f8679e86a0b0: {
            title: 'Opbewahrung a Läschen vu Verkéiersdaten',
            description: "Verkéiersdaten, déi sech op Abonnenten a Benotzer bezéien, mussen geläscht oder anonymiséiert ginn, soubal se net méi fir d'Iwwerdroung gebraucht ginn, ausser fir Rechnungsstellung oder fir Connexiounsbezuelungen.",
        },
        '8577e60f_66a8_4651_8336_d561db9929ad': {
            title: "Konditioune fir d'Veraarbechtung vu Standuertsdaten",
            description: "Standuertsdaten, déi keng Verkéiersdaten sinn, däerfen nëmmen anonymiséiert oder mam Aweis vum Benotzer veraarbecht ginn, am Ëmfang an iwwer d'Dauer, déi néideg sinn.",
        },
        '7f3abb41_8713_4276_93b5_6902f419a016': {
            title: 'Rechtméissegkeet vun der Veraarbechtung — Aweis',
            description: "D'Veraarbechtung ass nëmme rechtméisseg, wann d'betraffe Persoun hiren Aweis fir d'Veraarbechtung vu hire perséinlechen Donnéeë fir ee oder méi spezifesch Zwecker ginn huet.",
        },
        '1c2a945a_8eac_428d_8105_e80e95288ea4': {
            title: 'Rechtméissegkeet vun der Veraarbechtung — Kontrakt',
            description: "D'Veraarbechtung ass néideg fir d'Erfëllung vun engem Kontrakt, wou d'betraffe Persoun Partei ass.",
        },
        '2b50c0fb_ee96_412e_a092_c3c1f3f817e8': {
            title: 'Rechtméissegkeet vun der Veraarbechtung — gesetzlech Verflichtung',
            description: "D'Veraarbechtung ass néideg fir d'Anhalen vun enger gesetzlecher Verflichtung, déi de Verantwortleche betrëfft.",
        },
        '702ea760_9e5c_4c32_babc_0b5094ca9735': {
            title: 'Rechtméissegkeet vun der Veraarbechtung — vital Interessen',
            description: "D'Veraarbechtung ass néideg fir d'vital Interesse vun der betraffener Persoun oder vun enger anerer natierlecher Persoun ze schützen.",
        },
        '8b55ce88_2d5e_467b_a55c_8c5b0efab67e': {
            title: 'Rechtméissegkeet vun der Veraarbechtung — ëffentlecht Interesse',
            description: "D'Veraarbechtung ass néideg fir d'Erfëllung vun enger Aufgab, déi am ëffentlechen Interesse oder an Ausübung vun enger ëffentlecher Gewalt erfëllt gëtt.",
        },
        '8a72dd16_ee3c_4947_b5cd_9484719adad8': {
            title: 'Rechtméissegkeet vun der Veraarbechtung — berechtegt Interessen',
            description: "D'Veraarbechtung ass néideg fir d'berechtegt Interesse vum Verantwortlechen oder vun enger Drëttpartei ze wahren.",
        },
        afefc965_8f83_4d75_a9fc_92d4d4f549d6: {
            title: 'Veraarbechtung vu spezielle Kategorien (sensibel Donnéeën)',
            description: "D'Veraarbechtung vu spezielle Kategorie vu perséinlechen Donnéeën (rassesch Hierkonft, politesch Meenungen, Gesondheet, asw.) ass verbueden, ausser et sinn déi spezifesch Konditioune vum Art. 9(2) erfëllt.",
        },
        '10a2d63f_744d_4222_91f6_86a5a4cdaefd': {
            title: 'Konditioune fir den Aweis',
            description: 'De Verantwortleche muss bewäisen, datt den Aweis ginn ass. Den Aweis muss fräi, spezifesch, informéiert an ondéifdäiteg sinn. De Réckzuch muss esou einfach sinn, wéi den Aweis selwer ze ginn.',
        },
        '27697f99_efef_49e6_b8e7_c2dc550d1791': {
            title: 'Aweis vum Kand — digital Déngschter',
            description: "D'Veraarbechtung vu perséinlechen Donnéeë vun engem Kand ënner 16 Joer (oder ënner enger méi nidereger Altersgrenz, déi vum Memberstaat festgeluecht ass, awer net ënner 13 Joer) verlaangt den elterlechen Aweis fir Déngschter vun der Informatiounsgesellschaft.",
        },
        '53883480_508e_4f8e_984f_9354c18de285': {
            title: 'Transparent Informatioun a Kommunikatioun',
            description: "De Verantwortleche stellt d'Informatiounen iwwer d'Veraarbechtung an enger präziser, transparenter, verständlecher an einfach zougänglecher Form zur Verfügung.",
        },
        b5eb22b7_78f2_4c55_b342_827d5f8ac90c: {
            title: 'Informatioun bei direkter Erhebung',
            description: 'Wann perséinlech Donnéeë bei der betraffener Persoun erhuewe ginn, stellt de Verantwortleche bei der Erhebung déi virgeschriwwen Informatiounen zur Verfügung.',
        },
        e55f7dbf_cd2a_4713_887e_8c64744e2d95: {
            title: 'Informatioun bei indirekter Erhebung',
            description: 'Wann perséinlech Donnéeën net bei der betraffener Persoun erhuewe goufen, stellt de Verantwortleche déi virgeschriwwen Informatiounen bannent enger raisonabler Frist zur Verfügung.',
        },
        '6bc5ec07_0589_4593_882a_13f41c31e1f6': {
            title: 'Recht op Zougang',
            description: "D'betraffe Persoun huet d'Recht op eng Bestätegung vun der Veraarbechtung an op den Zougang zu hire perséinlechen Donnéeën an zu de begleedend Informatiounen.",
        },
        '0e011e25_dcea_4a1e_ae55_583e683cea44': {
            title: 'Recht op Berichtegung',
            description: "D'betraffe Persoun huet d'Recht, ouni onnéidegt Verzögere d'Berichtegung vu falsche perséinlechen Donnéeën ze verlaangen.",
        },
        e8ec5a55_f657_4a99_9b92_3e9861067d28: {
            title: 'Recht op Läschen (Recht op Vergiess ginn)',
            description: "D'betraffe Persoun huet d'Recht, ouni onnéidegt Verzögere d'Läsche vu perséinlechen Donnéeën ze verlaangen, wann déi virgeschriwwen Grënn applikabel sinn.",
        },
        '5b0c92fd_cd08_47be_bfd2_0540ad73f84f': {
            title: 'Recht op Aschränkung vun der Veraarbechtung',
            description: "D'betraffe Persoun huet d'Recht op d'Aschränkung vun der Veraarbechtung, wann déi virgeschriwwen Konditiounen erfëllt sinn.",
        },
        '3b829862_39f4_41be_8f2b_d469469a801b': {
            title: 'Notifikatiounsverflichtung — Berichtegung/Läschen/Aschränkung',
            description: "De Verantwortleche muss all Berichtegung, Läschen oder Aschränkung un all Empfänger matdeelen, deem d'Donnéeë matgedeelt goufen.",
        },
        '9cf34be6_121b_4fdf_ad7c_c4773696522e': {
            title: 'Recht op Iwwerdroebarkeet vun den Donnéeën',
            description: "D'betraffe Persoun huet d'Recht, hir perséinlech Donnéeën an engem strukturéierten, gängege a maschinneliesbaarem Format ze kréien.",
        },
        becae433_90b9_4cc0_bd33_df50976c1561: {
            title: 'Recht op Widderspréch',
            description: "D'betraffe Persoun huet d'Recht, géint eng Veraarbechtung op Basis vum ëffentlechen oder berechtegten Interesse, abegraff Profilage, Widderspréch anzeleeën.",
        },
        '4ef387f1_439c_4579_82a3_01565e6dc05d': {
            title: 'Automatiséiert Eenzeldecisiounsfindung, abegraff Profilage',
            description: "D'betraffe Persoun huet d'Recht, net enger Decisioun ënnerworf ze sinn, déi sech ausschliisslech op eng automatiséiert Veraarbechtung stäipt an déi rechtlech oder ähnlech wesentlech Konsequenzen huet.",
        },
        '667ae5eb_a39a_4809_a032_923cc804eaed': {
            title: "Notifikatioun vun enger Donnéeëverletzung un d'Opsiichtsbehéierd (72 Std.)",
            description: "Am Fall vun enger Donnéeëverletzung notifizéiert de Verantwortlechen d'Opsiichtsbehéierd bannent 72 Stonnen no der Kenntnisnam, ausser et ass onwarscheinlech, datt e Risiko entsteet.",
        },
        '40e4c2ad_bc85_436c_814f_265f02c8c3b1': {
            title: 'Notifikatioun vun enger Donnéeëverletzung un déi betraffe Persounen (héije Risiko)',
            description: "Wann eng Donnéeëverletzung wahrscheinlech zu engem héije Risiko fir d'Rechter a Fräiheete féiert, deelt de Verantwortleche se ouni onnéidegt Verzögere der betraffener Persoun mat.",
        },
        '9dbb47bd_172f_4335_b7f3_4558476c4192': {
            title: 'Äntwert op Demanden vu betraffene Persounen (30 Deeg)',
            description: "De Verantwortleche muss op d'Demanden vun de betraffene Persounen ouni onnéidegt Verzögeren a bannent engem Mount no der Empfangung äntwerten; déi Frist kann bei komplexe Demanden ëm zwee weider Méint verlängert ginn.",
        },
        '175d16b7_a0fc_44ff_bb9e_5ccb5d3de5a9': {
            title: 'Datebschutz-Folgeanalyse (DPIA)',
            description: "Wann eng Veraarbechtung wahrscheinlech zu engem héije Risiko fir d'Rechter a Fräiheete féiert, féiert de Verantwortleche viru der Veraarbechtung eng Datebschutz-Folgeanalyse (DPIA) duerch.",
        },
        e54a7b09_33dd_4cda_8e21_d10a51e5e925: {
            title: 'Viraus Konsultatioun vun der Opsiichtsbehéierd',
            description: "De Verantwortleche konsultéiert d'Opsiichtsbehéierd, wann eng DPIA ergëtt, datt d'Veraarbechtung ouni Reduktiounsmoossnamen zu engem héije Risiko géif féieren.",
        },
        '047b7838_bae5_4861_9c38_c99fedec9c96': {
            title: 'Lëscht vun obligatoresche Veraarbechtungen, déi eng DPIA verlaangen',
            description: "Eng DPIA ass obligatoresch fir systematesch Bewäertungen/Profilage, fir d'Veraarbechtung vu spezielle Kategorien op grousser Skala an fir déi systematesch Iwwerwaachung vu ëffentlech zougängleche Beräicher.",
        },
        aa08dc83_7c1a_441d_a8e7_960f65c8de87: {
            title: 'Verzeechness vun de Veraarbechtungsaktivitéiten (Verantwortlechen)',
            description: 'All Verantwortleche muss e Verzeechness vun den Aktivitéite féieren, déi ënner senger Verantwortung lafen, ënner anerem Zwecker, Kategorien, Empfänger, Transferten an Opbewahrung.',
        },
        b7eb6296_5962_449c_b267_d322d7919122: {
            title: 'Verzeechness vun de Veraarbechtungsaktivitéiten (Veraarbechter)',
            description: 'All Veraarbechter muss e Verzeechness vu all Kategorie vu Veraarbechtungsaktivitéite féieren, déi am Numm vun engem Verantwortleche gemaach ginn.',
        },
        '0d57e6d5_bbf5_4ea6_9a89_0f57bed61b5b': {
            title: 'Sécherheet vun der Veraarbechtung',
            description: 'De Verantwortlechen an de Veraarbechter setzen ugemoss technesch an organisatoresch Moossnamen ëm, fir eng dem Risiko ugemoss Sécherheet ze garantéieren.',
        },
        ad85edfc_3c92_4ce4_a414_3ba6186cae98: {
            title: 'Datebschutz duerch Technikgestaltung an duerch datebschutzfrëndlech Standardastellungen',
            description: 'De Verantwortleche setzt ugemoss Moossnamen ëm, fir sécherzestellen, datt nëmme perséinlech Donnéeë veraarbecht ginn, déi fir all spezifesche Zweck néideg sinn (Datemënergung).',
        },
        '933b438d_53b8_4b6a_b1db_8e7f1b429ede': {
            title: 'Bestëmmung vum Datebaschutzbeoptraagten (DSB)',
            description: "De Verantwortlechen an de Veraarbechter bestëmmen e Datebaschutzbeoptraagten (DSB), wann d'Veraarbechtung vun enger ëffentlecher Behéierd duerchgefouert gëtt, eng reegelméisseg a systematesch Iwwerwaachung beinhalt oder eng Veraarbechtung vu spezielle Kategorien op grousser Skala betrëfft.",
        },
        '28810a5d_d818_411a_9e8c_792d1b2c9b2f': {
            title: 'Allgemeng Prinzipie fir Iwwerdroungen',
            description: "All Iwwerdroung vu perséinlechen Donnéeën un en Drëttland ass nëmmen erlaabt, wann d'Konditioune vum Kapitel V respektéiert ginn.",
        },
        '60add64b_bb0c_44e7_baaf_666af65b2d0f': {
            title: 'Iwwerdroungen op Basis vun engem Adequatsbeschloss',
            description: "Eng Iwwerdroung däerf stattfannen, wann d'Kommissioun beschloss huet, datt dat Drëttland en adequatem Schutzniveau garantéiert.",
        },
        cde8c7cd_3727_40ce_a321_e6b72b335179: {
            title: 'Iwwerdroungen ënner ugemoss Garantien',
            description: 'An der Ofwiesenheet vun engem Adequatsbeschloss däerfen Iwwerdrounge stattfannen, wann ugemoss Garantie virgesinn sinn, ënner anerem SCC oder BCR.',
        },
        '0e8edaa1_2156_44d9_95f2_1b284f581658': {
            title: 'Standardvertragsklauselen (SCC)',
            description: 'Standardvertragsklauselen, déi vun der Kommissioun oder vun enger Opsiichtsbehéierd ugeholl ginn, bidden ugemoss Garantië fir international Iwwerdroungen.',
        },
        '608a8de7_6c75_4f88_bd4c_1cc308780ab9': {
            title: 'Verbindlech intern Datebschutzregelen (BCR)',
            description: 'Verbindlech intern Datebschutzregelen (BCR), déi vun enger zoustänneger Opsiichtsbehéierd genehmegt sinn, bidden ugemoss Garantië fir international Iwwerdroungen am Konzern.',
        },
        '420ced80_0d3b_4a28_adad_14ba54df3e40': {
            title: 'Ofwäichunge fir spezifesch Situatiounen',
            description: 'An der Ofwiesenheet vun Adequatsbeschloss oder Garantië däerfen Iwwerdroungen op Basis vum ausdréckleche Aweis, der vertraglecher Néidegkeet, vum ëffentlechen Interesse, vu Rechtsforderungen, vu vitale Interessen oder vum ëffentleche Register stattfannen.',
        },
        c86fbc0d_7188_486c_8ea5_96b3fe957c6f: {
            title: 'Verantwortung an Formatioun vun de Leedungsorganen',
            description: "D'Leedungsorgane vu wesentlechen a wichtegen Entitéite mussen d'Moossnamen zum Risikomanagement vun der Cybersécherheet genehmegen a sech reegelméisseg formen.",
        },
        ffb95a09_993c_4db0_a4bb_c48000e43b33: {
            title: "Moossname fir d'Risikomanagement vun der Cybersécherheet",
            description: "D'wesentlech a wichteg Entitéite mussen ugemoss a proportionéiert technesch, operationell an organisatoresch Moossname huelen, fir d'Risike vun der Cybersécherheet ze beherrschen.",
        },
        '6a7c8c8f_4b81_4b47_adb7_c6abd12acd4b': {
            title: "Politiken a Prozedure fir d'Behandlung vu Virfäll",
            description: "D'Entitéite setze Politiken zur Behandlung vu Virfäll ëm, ënner anerem Erkennung, Analyse, Konfinéierung a Reaktiounsprozeduren.",
        },
        de019eab_9992_4361_be7c_3e592f209d76: {
            title: 'Businesskontinuitéit a Kriisemanagement',
            description: "D'Entitéite setze Moossnamen zur Businesskontinuitéit ëm, ënner anerem Backup-Management, Katastrophewiederherstellung a Kriisemanagement.",
        },
        '6b5f482f_301c_4f69_bd91_f9ae1b21de6c': {
            title: 'Liwwerketten-Sécherheet',
            description: "D'Entitéite mussen d'sécherheetsrelevant Aspekter vun de Bezéiungen zu de direkte Liwweranten an Déngschtleeschter behandelen, ënner anerem d'Risikobewäertunge vun der Liwwerkette.",
        },
        c8e7738b_358c_4c79_9fc2_a564f15a6e37: {
            title: 'Sécherheet bei Erwerf, Entwécklung an Maintenance vun Netz- an Informatiounssystemer',
            description: "D'Entitéite setze Sécherheetsmoossnamen am Erwerf, an der Entwécklung an an der Maintenance vun Netz- an Informatiounssystemer ëm, ënner anerem d'Behandlung vu Schwachstellen.",
        },
        '72520ed1_51ff_4983_a785_9be1f72cf7dd': {
            title: 'Behandlung an Offeleeung vu Schwachstellen',
            description: "D'Entitéite setze Politiken a Prozedure fir d'koordinéiert Offeleeung an d'Behandlung vu Schwachstellen ëm.",
        },
        f009e65d_d55e_4c98_9532_63c150694698: {
            title: 'Bewäertung vun der Effikassitéit vum Cybersécherheets-Risikomanagement',
            description: "D'Entitéite setze Politiken a Prozeduren ëm, fir d'Effikassitéit vun de Cybersécherheets-Risikomanagementmoossnamen ze evaluéieren.",
        },
        '00e88824_675d_4b37_960c_757b3176c079': {
            title: 'Politike fir Kryptographie a Verschlësselung',
            description: "D'Entitéite setze Politiken a Prozedure fir d'Notzung vu Kryptographie an, wou ugemoss, fir d'Verschlësselung ëm.",
        },
        de29d287_81f1_46b1_b327_9cbf06ddad3d: {
            title: 'HR-Sécherheet, Zougangskontroll, Asset-Management',
            description: "D'Entitéite setze Sécherheetsmoossnamen am Beräich vun de mënschleche Ressourcen, Zougangskontrollpolitiken an Asset-Management ëm.",
        },
        '750f4459_9ba4_415e_bc47_5b0ec27d35a2': {
            title: 'Multifaktor-Authentifikatioun',
            description: "D'Entitéite setze Multifaktor-Authentifikatioun oder Léisunge fir kontinuéierlech Authentifikatioun, geséchert Stëmm-/Video-/Textkommunikatiounen a geséchert Notkommunikatiounssystemer ëm.",
        },
        '359474f9_1820_4e3e_9031_a593b66ccde4': {
            title: 'Fréiwarnung un de CSIRT (24 Std.)',
            description: "D'Entitéite musse bannent 24 Stonnen no der Kenntnisnam vun engem wesentleche Virfall eng Fréiwarnung beim zoustännegen CSIRT aginn.",
        },
        '66acd92c_3091_4148_b935_68b0d24308e7': {
            title: 'Notifikatioun vum Virfall un de CSIRT (72 Std.)',
            description: "D'Entitéite musse bannent 72 Stonnen no der Kenntnisnam vun engem wesentleche Virfall eng Notifikatioun beim zoustännegen CSIRT aginn.",
        },
        '68269688_06b2_43ea_8e3e_a84532086557': {
            title: 'Schlussbericht un de CSIRT (1 Mount)',
            description: "D'Entitéite musse spéitstens ee Mount no der Notifikatioun vum Virfall e Schlussbericht beim zoustännegen CSIRT aginn.",
        },
        c8512bde_b064_4926_b333_06b9c95cecd7: {
            title: 'Bestëmmung vun der Uwendung op KI-Aktivitéiten',
            description: "Feststellen, ob d'Organisatioun KI-Aktivitéiten ausféiert, déi dem vietnameseschen KI-Gesetz ënnerleien, an d'Decisioun zur Uwendung dokumentéieren.",
        },
        '3a87ad63_179a_45ba_8743_98d865df80ab': {
            title: 'Risikoklassifikatioun vu KI-Systemer no Risikoniveau',
            description: 'All KI-System no Risikoniveau klasséieren (minimal, limitéiert, héich, inakzeptabel) mat der gefouerter Klassifikatiounsmethodologie.',
        },
        '0f4475d3_77a6_4b41_bfe2_d4b299b1bd45': {
            title: 'Féierung vun den Antrag am KI-Systemregëster',
            description: 'E Register vun de KI-Systemer féieren mat Systemnumm, Besëtzer, Zweck, Asazkontext, Risikoniveau a Bedeelegung vum Liwwerant.',
        },
        '2a73ce94_906a_4c95_8a45_a5bdf2a8118c': {
            title: 'KI-Systemdossier mat Artefakt-Checklëscht a Bewäiser',
            description: 'E Systemdossier mat erfuerderlecher Artefakt-Checklëscht, Bewäisbibliothéik a Versiounsgeschicht opbauen an erhalen.',
        },
        e92cfb42_d91b_444f_adbc_c03903844eac: {
            title: "Transparenzverflichtunge fir d'Bedreiwer vu KI-Systemer",
            description: "Transparenzmoossname fir KI-Systemer ëmsetzen, ënner anerem d'Notifikatioun vun de Benotzer, d'Offeleeung vun der KI-Bedeelegung an d'Systemdokumentatioun.",
        },
        '45eb43d7_9b63_4166_aa0d_fa6aeeaa70ba': {
            title: 'Kennzeechnung, Watermarking a Metadatesignéierung vu KI-Ausgaben',
            description: "Kennzeechnung a Watermarking fir KI-generéierten Inhalt ëmsetzen, ënner anerem d'Metadatesignéierung wou applikabel.",
        },
        '9566c94d_0dd3_419c_869f_6707d2a972f9': {
            title: 'Offeleeungsverflichtunge fir KI-generéierten Inhalt',
            description: "De Benotzer offeleeën, wann den Inhalt KI-generéiert ass, a Mechanismen ubidden, fir datt d'Benotzer d'KI-Bedeelegung erkenne kënnen.",
        },
        '21d39ea5_df78_45c6_8710_b203db229e25': {
            title: 'Erklärbarkeet an u Benotzer geriicht Transparenzflichten',
            description: 'Erklärbar Ausgabe fir KI-Decisiounen, déi Eenzelpersoune betreffen, ubidden an u Benotzer geriicht Transparenzdokumentatioun erhalen.',
        },
        f523fbc2_156d_466d_ad50_6a0c51b3ac9d: {
            title: 'Verflichtungen zur mënschlecher Iwwerwaachung fir héich-riskant KI-Systemer',
            description: "Sécherstellen, datt Mechanisme fir d'mënschlech Iwwerwaachung vun héich-riskante KI-Systemer agesat sinn, ënner anerem Eskalatiouns- an Iwwerschreiwungskapazitéiten.",
        },
        bd180a89_4976_4790_9e50_f1c5c97dc576: {
            title: 'Iwwerwaachung an Erkennung vu KI-Virfäll',
            description: 'Iwwerwaachungskapazitéiten ëmsetzen, fir Virfäll, Stéierungen an negativ Resultater vu KI-Systemer ze erkennen.',
        },
        e7c1c60b_856c_4d3d_b6c9_092cde9b56bd: {
            title: "Notifikatioun vun engem schwéiere KI-Virfall un d'Behéierd",
            description: 'Schwéier KI-Virfäll der zoustänneger Behéierd bannent der virgeschriwwener Frist (72 Stonnen) notifizéieren.',
        },
        '97f15adc_558a_49f2_bb06_86597cb9579b': {
            title: "Anhalen vun de Fristen fir d'Notifikatioun vu KI-Virfäll",
            description: "D'Notifikatiounsfriste fir KI-Virfäll respektéieren, ënner anerem d'Erstnotifikatioun, d'Updates an d'Ofschlossberichter.",
        },
        '96c33d7e_fe52_4730_adac_70c8c477c88e': {
            title: 'KI-Impaktbewäertung fir héich-riskant Systemer',
            description: 'Impaktbewäertunge fir héich-riskant KI-Systemer duerchféieren, déi Risiken, Massnamen zur Reduktioun, mënschlech Iwwerwaachung an Iwwerwaachungspläng ofdecken.',
        },
        '0f3b72c0_1030_44ff_ba53_596f45601cd7': {
            title: 'Bestëmmung vun der Berechtegung fir kontrolléiert Tester / Sandbox',
            description: "D'Berechtegung fir d'Deelnam u kontrolléierten Tester (Sandbox) feststellen an d'Berechtegungsbewäertung dokumentéieren.",
        },
        '4a6aed20_f655_4182_a760_aef1f329d5df': {
            title: 'Aginn vun der Sandbox-Demande a Verfollegung vun der Approbatioun',
            description: 'Sandbox-Demanden bei der zoustänneger Behéierd aginn an de Status vun der Approbatioun iwwer de Liewenszyklus vum Aginn verfollegen.',
        },
        '8376988d_cd32_491e_a5b8_3340d3973358': {
            title: 'Sandbox-Iwwerwaachungspunkten an Ofschloss/Sortie',
            description: 'Periodesch Iwwerwaachungspunkten während der Sandbox-Deelnam duerchféieren a Berichter zum Ofschloss/zur Sortie virbereeden.',
        },
        '7cb2b544_0512_4702_a092_9f1c10872376': {
            title: 'Dokumentatioun vu Befreiungen/Ausnamen vun der Kennzeechnung mat Begrënnung',
            description: 'All Befreiung oder Ausnam vun der Kennzeechnung mat Begrënnung an Bewäiser dokumentéieren.',
        },
        dccd7fbf_872a_4716_87a4_b9c5ce8e2387: {
            title: 'Datelokaliséierung fir vietnamesesch Benotzer',
            description: 'Donnéeë vu vietnamesesche Benotzer op Déngschter, déi a Vietnam ugebuede ginn, mussen op vietnamesescht Territoire gespäichert ginn.',
        },
        '9eb86a03_e165_4963_8813_2272b638d529': {
            title: 'Notifikatioun vu Cybersécherheetsvirfäll',
            description: 'Cybersécherheetsvirfäll mussen den zoustännege Behéierde rechtzäiteg notifizéiert ginn.',
        },
        '736ac7de_7a6a_4bfd_b2be_c594e290038a': {
            title: 'Klassifikatioun vun den Informatiounssystemer',
            description: "D'Informatiounssystemer mussen no Wichtegkeetsniveau klasséiert ginn an entspriechend Sécherheetsmoossnamen ugewannt ginn.",
        },
        ffb807cf_f121_4b52_8e50_e091c33180c9: {
            title: 'Cybersécherheetsrisikobewäertung',
            description: "Reegelméisseg Cybersécherheetsrisikobewäertunge mussen fir d'Informatiounssystemer duerchgefouert ginn.",
        },
        a4de518b_a5f1_481f_9b08_f8d2bc9db7e5: {
            title: 'Bestëmmung vum Uwendungsberäich (Decisiounsbam)',
            description: "D'Organisatioune mussen mat dem virgeschriwwenen Decisiounsbam bestëmmen, ob si dem Uwendungsberäich vum Dekret 53 ënnerleien.",
        },
        '01f99e9a_5e30_4d14_aae5_a2ce322a700b': {
            title: 'Bewäertung an Bereetschaft vun der Datelokaliséierung',
            description: "D'Organisatioune mussen d'Ufuerderunge fir d'Datelokaliséierung bewäerten an d'Bereetschaft beweisen.",
        },
        cf24f011_dc23_4d84_aef5_994dde440571: {
            title: "Checklëscht fir d'Bereetschaft vun der Späicherung an der Opbewahrung",
            description: "Eng Checklëscht fir d'Bereetschaft vun der Späicherung an der Opbewahrung muss fir d'Conformitéit mat der Datelokaliséierung ofgeschloss ginn.",
        },
        b3884cb8_c785_4258_9517_9e393be4f4e1: {
            title: 'Bewäertung vun der Ufuerderung un déi lokal Präsenz',
            description: "D'Organisatioune mussen bewäerten, ob d'Ufuerderungen un déi lokal Präsenz uwendbar sinn, an entspriechend planen.",
        },
        c1dc645b_cb1a_42a6_8ca5_9b15cfa84156: {
            title: 'Empfank an Verifikatioun vu Behéierdeufroen',
            description: 'Behéierdeufroe mussen am Aklang mat de virgeschriwwene Prozeduren empfaangen, verifizéiert a behandelt ginn.',
        },
        d4571651_f39d_4c51_8f2d_7248c37f7b14: {
            title: 'Äntwert op eng Behéierdeufro bannent der Frist',
            description: "D'Äntwerten op Behéierdeufroe musse bannent der virgeschriwwener Frist geliwwert ginn.",
        },
        '66c1df70_3456_40e9_b645_6c6bd3be702e': {
            title: 'Protokoll vun der Behandlung vu Behéierdeufroen',
            description: 'E Protokoll vun all Aktivitéiten zur Behandlung vu Behéierdeufroe muss erhale ginn.',
        },
        '04bbba2a_9804_4199_a517_054b6f4689fb': {
            title: 'Bestëmmung vun der Uwendung an dem Uwendungsberäich (konsolidéiert)',
            description: "D'Organisatioune musse feststellen, ob si dem Uwendungsberäich vum konsolidéierte Cybersécherheetsgesetz ënnerleien, dat den Uwendungsberäich vum ale Gesetz 24/2018 a vum Dekret 53/2022 ofdeckt.",
        },
        '89a21eaf_cf09_4fec_bbb4_a1dadd652f6e': {
            title: 'Klassifikatioun vun den Informatiounssystemer no Wichtegkeetsniveau',
            description: "D'Informatiounssystemer mussen no Wichtegkeetsniveau klasséiert ginn (allgemeng, wichteg, kritesch) mat de korrespondéierende Sécherheetsverflichtungen.",
        },
        '83ad981e_23ff_4a92_bebd_cd24487e912f': {
            title: "Gouvernancestrukturen an verantwortlecht Personal fir d'Cybersécherheet",
            description: "D'Organisatioune mussen Gouvernancestrukturen fir d'Cybersécherheet etabléieren a verantwortlecht Personal designéieren.",
        },
        '98d9a523_fefb_400b_bb7f_ebaf609a6ed4': {
            title: 'Periodesch Cybersécherheetsrisikobewäertung',
            description: 'Reegelméisseg Cybersécherheetsrisikobewäertunge mussen fir all klasséiert Informatiounssystemer duerchgefouert ginn.',
        },
        '5236539f_4c1a_4eff_b2eb_fc11eb4eab23': {
            title: 'Sécherheetsmoossname pro Klassifikatioun vum Informatiounssystem',
            description: 'Technesch Sécherheetsmoossname mussen entspriechend dem Klassifikatiounsniveau vun all Informatiounssystem ugewannt ginn.',
        },
        b69a8be2_ae0c_408c_b8f1_73b573eca876: {
            title: 'Zougangskontroll an Identitéitsmanagement',
            description: "Moossname fir d'Zougangskontroll an d'Identitéitsmanagement mussen fir d'Informatiounssystemer ëmgesat ginn.",
        },
        ac774c26_feb7_4123_b799_c54f77bd87b5: {
            title: 'Protokollering, Iwwerwaachung an Auditspur',
            description: "D'Informatiounssystemer mussen Funktiounen fir Protokollering, Iwwerwaachung an Auditspur erhalen.",
        },
        a86cb01d_239b_44e2_9e09_e6973bcf9658: {
            title: 'Schwachstellemanagement an Patching',
            description: 'Prozesser fir Schwachstellemanagement an Patching musse etabléiert an erhale ginn.',
        },
        '826a789f_0c03_4918_81a1_6aa1cc2c205c': {
            title: 'Datelokaliséierung fir Donnéeë vu vietnamesesche Benotzer',
            description: 'Donnéeë vu vietnamesesche Benotzer op Déngschter, déi a Vietnam ugebuede ginn, mussen op vietnamesescht Territoire gespäichert ginn.',
        },
        '8bee2b51_ace8_4118_89c8_010b96640016': {
            title: 'Ufuerderungen un déi lokal Präsenz fir auslännesch Déngschtleeschter',
            description: 'Auslännesch Déngschtleeschter, déi a Vietnam aktiv sinn, mussen eng lokal Präsenz wéi virgeschriwwen erhalen.',
        },
        '74d89250_b45f_41b6_bfe3_f915071580de': {
            title: 'Architektur vun der lokaler Späicherung an Dokumentatioun vun de grenziwwerschreidende Flëss',
            description: "D'Architektur vun der lokaler Späicherung muss dokumentéiert ginn an d'grenziwwerschreidend Datenflëss mussen ofgebillt a kontrolléiert ginn.",
        },
        '04dec6f8_3280_494c_8506_292804b16d4d': {
            title: 'Erkennung an Reaktioun op Cybersécherheetsvirfäll',
            description: "D'Organisatioune musse Kapazitéiten zur Erkennung vu Cybersécherheetsvirfäll an zur Reaktioun doropper hunn.",
        },
        faeda71b_5050_497d_b08b_e3f3f5313ad4: {
            title: "Obligatoresch Notifikatioun vu Virfäll un d'zoustänneg Behéierd",
            description: 'Qualifizéierend Cybersécherheetsvirfäll mussen dem MPS/MIC bannent de virgeschriwwene Friste gemellt ginn.',
        },
        d732b5a2_2b2c_48a0_955d_97a39daf132d: {
            title: "Anhalen vun de Fristen fir d'Notifikatioun vu Virfäll",
            description: "D'Notifikatioune vu Virfäll musse d'virgeschriwwen Fristen anhalen (initial Bericht bannent 24 Stonnen, kompletten Bericht bannent 72 Stonnen).",
        },
        '8e2dffb0_3481_44f0_baf2_130ddd8cfe12': {
            title: 'Behandlung vu Behéierdeufroen an Zesummenaarbecht',
            description: "D'Behéierdeufroe mussen direkt behandelt ginn an d'Zesummenaarbecht muss am Aklang mat de virgeschriwwene Prozeduren ageleet ginn.",
        },
        e4c5b870_1d61_48fe_a6c2_1b9f5a458ea3: {
            title: 'Falregister fir Behéierdeufroen a Protokoll vun den Offeleeungen',
            description: 'All Behéierdeufroen an Offeleeunge musse mat enger vollstänneger Auditspur an engem Falregister protokolléiert ginn.',
        },
        '8eff8b17_65bc_4f94_8ece_c509a9fd9abb': {
            title: 'Sécherheetsbewäertung vu Liwwerant an Drëttpartei',
            description: 'Drëttpartei-Liwwerante mat Zougang zu den Informatiounssystemer mussen eng Sécherheetsbewäertung duerchlafen.',
        },
        '69891e1b_97f6_464c_9cce_b1276794fe4a': {
            title: 'Kontroll vum Liwwerantenzougang zu den Donnéeën an Notifikatioun vu Virfäll',
            description: 'De Liwwerantenzougang zu den Donnéeë muss dokumentéiert ginn a vertraglech Verflichtungen zur Notifikatioun vu Virfäll mussen etabléiert ginn.',
        },
        d89b6bc0_7a9e_4816_b31e_7c0abc02e90c: {
            title: 'Sensibiliséierungsformatioun an Iwwungen am Beräich vun der Sécherheet',
            description: 'Reegelméisseg Sensibiliséierungsformatiounen an Tabletop-Iwwunge zur Cybersécherheet musse stattfannen.',
        },
        f9c5d3cc_5bb2_4b30_a5fb_ada34e407981: {
            title: 'Periodesch Pen-Tester a Sécherheetsaudit',
            description: 'Periodesch Penetratiounstester a Sécherheetsaudite musse bei klasséierten Informatiounssystemer duerchgefouert ginn.',
        },
        '88dd9877_d2e2_4e41_a248_ae39b1ff478f': {
            title: 'Erhalen vun engem digitale Dateninventar',
            description: "D'Organisatioune mussen e ëmfaassend digitalt Dateninventar erhalen, dat all Datenobjeten katalogiséiert.",
        },
        '18e4bfd1_42cf_4d87_870f_5a34242962ef': {
            title: 'Datenklassifikatioun no Sensibilitéit a Wichtegkeet',
            description: "D'Donnéeë mussen no de Sensibilitéits- a Wichtegkeetsniveauen, déi vum Datengesetz definéiert ginn, klasséiert ginn.",
        },
        b89981ac_7559_46f9_a74c_a143976dde4e: {
            title: 'Festleeën vun de Behandlungsregele pro Klassifikatiounsniveau',
            description: 'Behandlungsregele mussen fir all Datenklassifikatiounsniveau etabléiert ginn.',
        },
        '3bf8019d_b72c_41d7_82cf_fc367efd9526': {
            title: 'Verfollegung vum Datedeelen an der Offeleeung mat Approbatiounen',
            description: 'All Aktivitéite vum Datedeelen an der Offeleeung mussen mat entspriechenden Approbatioune verfollegt ginn.',
        },
        '8623eca2_4cf9_41bd_bfbd_2300c06ca1f3': {
            title: 'Gouvernance vum grenziwwerschreidende Datentransfert',
            description: "Grenziwwerschreidend Datentransferte mussen d'Gouvernanceufuerderunge vum Datengesetz erfëllen.",
        },
        '006b8aa6_9379_4eff_b8ab_935f9a087601': {
            title: 'Erhale vu Gouvernancepolitiken a Verantwortlechkeeten',
            description: "D'Organisatioune mussen Datengouvernancepolitiken erhalen a Gouvernanceverantwortlechkeeten zouweisen.",
        },
        f12fc090_5c3e_4fa8_9813_74bea0359c5c: {
            title: 'Jährlech Gouvernance-Attestatioun',
            description: "Eng jährlech Gouvernance-Attestatioun muss ofgeschloss ginn, fir d'lafend Conformitéit ze bestätegen.",
        },
        f6c3060a_5e52_4f97_b1af_c6a5f00bfaee: {
            title: 'Datenrisikobewäertung',
            description: "Eng Datenrisikobewäertung muss duerchgefouert ginn, fir d'Risiken an der Datengouvernance ze identifizéieren a se ze reduzéieren.",
        },
        '6f228998_1c61_49cf_a585_3d3e9baff55a': {
            title: 'Verantwortlechkeet a Gouvernanceverflichtunge vum Plattformbedreiwer',
            description: 'Verantwortlechkeetsstrukture vum Plattformbedreiwer etabléieren, ënner anerem Gouvernancepolitiken, Iwwerwaachungsverantwortlechkeeten an Duerchsetzungsverflichtungen.',
        },
        eee1d423_3783_4530_bc64_eef6fd9d02dc: {
            title: 'Verëffentlechung vu Plattformregelen an Transparenz vun den Informatiounen',
            description: 'Plattformregelen, Handelskonditiounen an operationell Offeleeungen op eng transparent an zougänglech Manéier publizéieren.',
        },
        '50b57433_7a51_4e92_9c84_c748d6865b70': {
            title: 'Notifikatioun vun e-Commerce-Verkafs-Websäit/App un den MOIT',
            description: 'Notifikatioune fir e-Commerce-Verkafs-Websäiten an -Applikatiounen iwwer Online.gov.vn oder designéiert Kanäl beim MOIT aginn.',
        },
        '92854abe_6654_4155_8495_f3419a8a8da0': {
            title: 'Aschreiwung vun e-Commerce-Déngschtplattformen/-Marketplaces beim MOIT',
            description: "E-Commerce-Déngschtplattformen a Marketplaces beim MOIT aschreiwen, ënner anerem d'Aginn vum Dossier an d'Verfollegung vun der Approbatioun.",
        },
        b8bff437_805e_4a4a_a62e_89cb90b89d3f: {
            title: 'Erhalen vum Verkeeferregëster (Inland an Auslänner)',
            description: 'E Register vun all Verkeefer op der Plattform erhalen, ënner anerem Inland- an auslännesch Verkeefer mat Onboarding, Verifikatioun a Verfollegung vum Liewenszyklus.',
        },
        '75778639_5235_4d13_a728_fa772c27fc0c': {
            title: 'Register vun Livestream-Verkeefer a Conformitéitskontrollen',
            description: 'E Register vu Livestream-Verkeefer mat Identitéitsverifikatioun, Aktivitéitsverfollegung a Conformitéitskontrollen erhalen.',
        },
        '405f2932_742a_4bcb_9784_1a3d81f2e714': {
            title: 'Register vun Affiliate- a Marketingpartner',
            description: 'E Register vun Affiliate-Marketer a Referralpartner erhalen, mat Vertragsstatus, Inhaltsrichtlinnen a Conformitéitsbestätegungen.',
        },
        cce5a4eb_a576_459c_95b5_bdd0e8ed30a8: {
            title: 'Produktlëschtung an Duerchsetzung bei verbuedenen/konditionéierte Wueren',
            description: "Kontrolle fir d'Produktlëschtung ëmsetzen, ënner anerem d'Screening, Iwwerwaachung an Duerchsetzungsmoossnamen fir verbuede a konditionéiert Wueren.",
        },
        '9fc01e19_18dd_4d60_b95a_c1f4bcdbb6ae': {
            title: 'Konsumentereklamatiounsregëster mat Annahme, Aktiounen an Auditspur',
            description: 'E Reklamatiounsregister erhalen mat Annahmeprotokoller, Untersuchungsaktiounen, Léisungsergebnisser an enger vollstänneger Auditspur.',
        },
        '8a21764e_e5f4_4934_9913_c235f4c462ca': {
            title: 'Behandlungsprozedur a Léisungsfristen fir Reklamatiounen',
            description: 'Behandlungsprozeduren fir Reklamatioune mat definéierten Fristen fir Empfangsbestätegung, Untersuchung a Léisung etabléieren.',
        },
        '8406373c_ec31_4bf8_810f_72d9d73b3de8': {
            title: 'Inhaltsmoderéierung, Iwwerwaachung an Duerchsetzung vum Eraushuelen',
            description: "Prozeduren fir d'Inhaltsmoderéierung, d'Iwwerwaachung an d'Duerchsetzung vum Eraushuelen vu illegalen, verbuedenen oder verstoussenden Inhalter ëmsetzen.",
        },
        '6f32c315_323b_4ce6_a4bb_3262a5b56afe': {
            title: 'Register vun Eraushuel- an Duerchsetzungsmoossname mat Bewäisbewahrung',
            description: 'E Register vun Eraushuel- an Duerchsetzungsmoossname mat Decisiounsprotokoller, Bewäisbewahrung an Notifikatiounen un de Verkeefer erhalen.',
        },
        c0bbbfbf_29bd_4813_89f1_956eab9f1a23: {
            title: 'Register vu Plattformpolitiken an Offeleeungen (Versiounen, Verëffentlechungsdatumer, Akzeptanz)',
            description: 'E Register vu Plattformpolitiken an Offeleeunge mat Versiounsgeschicht, Verëffentlechungsdatumer a Bewäiser vun der Akzeptanz erhalen.',
        },
        c9fcbe90_622d_43ca_9ec6_db26c0eda490: {
            title: "Verflichtungen zur Konsumenteninformatioun — korrekt Produktinformatioun a géint d'Täuschung",
            description: 'Korrekt, kloer Produkt- an Déngschtinformatiounen sécherstellen a Moossname géint täuschend Praktiken op der Plattform ëmsetzen.',
        },
        '8f49603b_454c_4470_ab07_2f845a447595': {
            title: 'Operationell Konditiounen an Aschreiwung/Notifikatioun fir auslännesch Plattformen',
            description: "D'operationell Konditiounen fir auslännesch Plattformen, déi vietnamesesch Benotzer beddéngen, anhalen, ënner anerem d'Aschreiwungs- oder Notifikatiounsufuerderungen.",
        },
        e0dc85cc_5d2a_4142_b1a6_cc2fde891f7b: {
            title: 'Lokal Vertriedung oder Bevollmächtegung fir auslännesch Plattformen',
            description: 'E lokale Vertrieder oder eng bevollmächtegt Entitéit a Vietnam fir auslännesch Plattformen designéieren, wéi vum Gesetz erfuerdert.',
        },
        '5719f47c_f298_429e_90c3_df6ceed856e1': {
            title: 'Koordinatioun vun der Behandlung vu grenziwwerschreidende Verkeefer a Reklamatiounen',
            description: "D'Behandlung vu grenziwwerschreidende Verkeefer an d'Léisung vu Reklamatioune koordinéieren, ënner anerem d'Eskalatioun an d'Bewäismanagement.",
        },
        c0b3db29_9161_4cef_a889_94e3b85d78fe: {
            title: 'Interaktioun mam staatleche Gestiounssystem fir den e-Commerce a Berichtsbereetschaft',
            description: "Op d'Interaktioun mam staatleche Gestiounssystem fir den e-Commerce virbereeden, ënner anerem d'Mapping vun Datefelder an d'Berichtsfäegkeet.",
        },
        '145a7016_720a_4a06_badd_00145842821e': {
            title: 'Notifikatioun vu wesentlechen Ännerungen no der Approbatioun',
            description: 'Notifikatioune fir wesentlech Ännerungen u Plattformdetailer, -politiken oder -operatiounen no der initialer Approbatioun aginn.',
        },
        '4bc0ee2a_610d_490d_8122_d6d1afadaa22': {
            title: 'Notifikatioun vum Schlussennn an der Deaktivéierung',
            description: "Notifikatioune vum Schlussenn oder vun der Deaktivéierung aginn, wann d'e-Commerce-Operatiounen agestallt ginn, ënner anerem d'ëffentlech Bekanntmaachung an d'Behandlung vum Konsumenteschutz.",
        },
        be95bc85_2b25_4de7_a3e7_154b780289e4: {
            title: 'Berechtegung an Erhalen vun de Programmer fir Trustmark- a Glafwierdegkeetsbewäertung',
            description: "D'Berechtegung fir Programmer fir Trustmark- a Glafwierdegkeetsbewäertung bewäerten an d'Conformitéitsbewäiser fir d'lafend Deelnam erhalen.",
        },
        f639afe2_18f2_4c0a_82d9_626308994267: {
            title: "Zoustëmmung fir d'Veraarbechtung vu perséinlechen Donnéeën",
            description: "D'Veraarbechtung vu perséinlechen Donnéeën verlaangt d'Zoustëmmung vum Betraffenen, ausser wann eng Befreiung gëllt.",
        },
        '5dd8e1b9_ac2d_48af_8c62_538937f0a4f7': {
            title: 'Sensitive personal data: tell the data subject the data is sensitive',
            description: 'For processing sensitive personal data, the data subject must be told that the data to be processed is sensitive personal data (Art. 11(8)). Art. 28 adds the measures for protecting it: applying the measures of Art. 26(2) and Art. 27; designating a unit with the function of protecting personal data and staff in charge of it, and exchanging their details with the specialised personal data protection agency; and notifying the data subject that their sensitive personal data is being processed, except in the cases of Art. 13(4), Art. 17 and Art. 18.',
        },
        '5a958c76_6943_4449_a15c_ddb503ce526f': {
            title: 'Recht op Zougang',
            description: "D'Betraffe hunn d'Recht op Zougang zu hire perséinlechen Donnéeën, déi de Verantwortleche fir d'Veraarbechtung hält.",
        },
        '1ae0a58b_65a5_4a32_9b2c_0baed7799a32': {
            title: 'Recht op Berichtegung',
            description: "D'Betraffe hunn d'Recht, d'Berichtegung vun ongenauen perséinlechen Donnéeën ze froen.",
        },
        '25c61e57_273f_48cb_a318_d6315ad744f1': {
            title: 'Recht op Läschung',
            description: "D'Betraffe hunn d'Recht, d'Läschung vun hire perséinlechen Donnéeën ze froen.",
        },
        '0eafe047_ed8b_4a9a_bda9_b4ef2ae353c2': {
            title: 'Recht op Aschränkung vun der Veraarbechtung',
            description: "D'Betraffe hunn d'Recht, d'Aschränkung vun der Veraarbechtung vun hire perséinlechen Donnéeën ze froen.",
        },
        '77f9677a_f436_43ec_a947_3bdc44fc192a': {
            title: 'Recht op Widderspréch',
            description: "D'Betraffe hunn d'Recht, géint d'Veraarbechtung vun hire perséinlechen Donnéeën Widderspréch ze maachen.",
        },
        c8b19e70_782c_49c9_8a71_14bef4147d6b: {
            title: 'Recht op Réckzuch vun der Zoustëmmung',
            description: "D'Betraffe hunn d'Recht, eng virdru gegebene Zoustëmmung zu all Moment zréckzezéien.",
        },
        '7f472a1c_b37f_498d_adc7_df8d8eafbae9': {
            title: "Notifikatioun vu Verstéiss un d'Behéierd (72 Stonnen)",
            description: "De Verantwortleche fir d'Veraarbechtung muss dem Ministère vun der Ëffentlecher Sécherheet bannent 72 Stonnen no der Entdeckung vun engem Verstouss vu perséinlechen Donnéeën Bescheed soen.",
        },
        '98bed7ab_e814_43f5_8e70_e95c413266a1': {
            title: "Notifikatioun vu Verstéiss un d'Betraffen",
            description: "De Verantwortleche fir d'Veraarbechtung muss déi betraffe Persounen ouni iwwerflësseg Verzögerung vun engem Verstouss vu perséinlechen Donnéeë informéieren.",
        },
        '404a3576_dd84_4bac_acf4_29719615c45e': {
            title: 'DPIA fir héich-riskant Veraarbechtungen',
            description: 'Eng Datebschutz-Impaktbewäertung muss fir Veraarbechtungsaktivitéiten duerchgefouert ginn, déi méiglecherweis e héicht Risiko duerstellen.',
        },
        '29d9ed94_9360_457c_9aca_37aefac66969': {
            title: 'Bewäertung vum grenziwwerschreidenden Transfert',
            description: 'Eng Impaktbewäertung muss virum Transfert vu perséinlechen Donnéeën aus Vietnam eraus duerchgefouert ginn.',
        },
        '9620ff08_b83c_4e1f_9350_eba835b145fe': {
            title: 'Verzeechness vu Veraarbechtungsaktivitéiten',
            description: "D'Verantwortlech fir d'Veraarbechtung mussen e Verzeechness vun de Veraarbechtungsaktivitéiten (RoPA) féieren, dat all Veraarbechtungsaktivitéiten dokumentéiert.",
        },
        '96ee0ff3_3ead_431e_aa57_08ccff4821fc': {
            title: 'Akten zu Veraarbechtungsofkommessen',
            description: 'Akten zu Veraarbechtungsofkommesse mat Veraarbechter an Ënnerveraarbechter mussen erhale ginn.',
        },
        '434dc319_1922_49e4_b757_37622135c4b2': {
            title: 'Schutzmoossname fir grenziwwerschreidend Transferten',
            description: 'Transferte vu perséinlechen Donnéeën aus Vietnam eraus verlaange entspriechend Schutzmoossnamen an dokumentéiert Bewäertungen.',
        },
        '3372c651_d5a8_4cae_ae57_2fc684ad0768': {
            title: 'Bestätegung bannent 2 Aarbechtsdeeg',
            description: 'Demande vu Betraffene musse bannent 2 Aarbechtsdeeg no Empfank bestätegt ginn.',
        },
        b15fa62d_3f52_4e42_8442_f4969e08883a: {
            title: 'Ofschloss bannent 15 Aarbechtsdeeg',
            description: 'Demande vu Betraffene mussen bannent 15 Aarbechtsdeeg ofgeschloss ginn, mat enger méiglecher Verlängerung vu 5 Aarbechtsdeeg.',
        },
        '5d81233c_f671_4827_b874_827762edfc20': {
            title: 'Aginn vum Impaktbewäertungsdossier beim MoPS',
            description: 'Organisatiounen, déi héich-riskant Veraarbechtungen ausféieren, mussen e Impaktbewäertungsdossier no der virgeschriwwener administrativer Prozedur beim Ministère vun der Ëffentlecher Sécherheet aginn.',
        },
        '83bf5bf8_edbd_4f0a_9f8e_5f47db01324d': {
            title: 'Aginn vum grenziwwerschreidenden Transfertdossier beim MoPS',
            description: "Organisatiounen, déi perséinlech Donnéeë vu Vietnam an d'Ausland transferéieren, mussen e Impaktbewäertungsdossier fir de grenziwwerschreidenden Transfert beim Ministère vun der Ëffentlecher Sécherheet aginn.",
        },
        '9454e465_e618_4b6f_80b3_d79fdd9cc5cb': {
            title: "Zertifizéierung vum Déngschtleeschter fir d'Veraarbechtung",
            description: "Organisatiounen, déi d'Veraarbechtung vu perséinlechen Donnéeën als Service ubidden, mussen e Zertifikat fir d'Veraarbechtung als Service vum Ministère vun der Ëffentlecher Sécherheet kréien.",
        },
        b88aec34_046a_4e0a_9c77_ddad27ea4bc4: {
            title: "Impaktbewäertungsdossier wann d'Ausléiser erfëllt sinn",
            description: "E Impaktbewäertungsdossier muss agereecht ginn, wann d'virgeschriwwen Ausléiser erfëllt sinn.",
        },
        '9d34a206_b85e_4649_a23b_e2c6f1398402': {
            title: 'Impaktdossier fir grenziwwerschreidenden Transfert',
            description: 'E Impaktdossier fir de grenziwwerschreidenden Transfert muss fir international Datentransferte virbereet an agereecht ginn.',
        },
        '210d3ec3_e6f2_424b_ae25_cad76bc13320': {
            title: 'Designatioun vum Datebschutzbeoptraagten',
            description: 'En Datebschutzbeoptraagte muss wéi vum Dekret 13 erfuerdert designéiert ginn.',
        },
        '776183b2_2797_40e8_8d81_c4f512c566d2': {
            title: 'Gouvernance-Artefakter (Politiken, Rollen, Prozeduren)',
            description: 'Gouvernance-Artefakter, ënner anerem Politiken, Rollen a Prozeduren, mussen erhale ginn.',
        },
        '4f1385f1_6dfc_4368_b4af_1040cd3f77b7': {
            title: 'Vollstännegkeet vum Bewäisepak an onverännerlech Auditspur',
            description: "D'Bewäisepäck mussen vollstänneg sinn an eng onverännerlech Auditspur muss erhale ginn.",
        },
        f6826e26_8991_4a0f_9117_481490230bdd: {
            title: 'Ënnerstëtzung vum Workflow vum Aginn-Fall',
            description: "D'Workflowe vum Aginn-Fall musse fir Impaktbewäertungs- a grenziwwerschreidend Dossiere ënnerstëtzt ginn.",
        },
        '8a28ee33_305a_491f_a93f_90e59f8da91b': {
            title: 'Vertraglech Ufuerderungen un de Veraarbechter',
            description: "Vertraglech Ufuerderungen un d'Datenveraarbechter mussen etabléiert an iwwerwaacht ginn.",
        },
        '4fd36e83_8286_40e0_b9b2_a4bc5e8377b3': {
            title: 'Jährlech Conformitéitsiwwerpréiwung',
            description: "Eng jährlech Conformitéitsiwwerpréiwung muss duerchgefouert ginn, fir d'lafend Anhalen ze verifizéieren.",
        },
        '8b6b6c13_0d32_4439_819f_4bd8cff02217': {
            title: 'Akte vun den Aginn bei den Behéierden',
            description: 'Akte vun all Aginn bei den Behéierde mussen erhale ginn.',
        },
        e7ce6c23_03dc_42dd_bccd_fa571c3afee3: {
            title: "Legitim Grondlage fir d'Veraarbechtung vu perséinlechen Donnéeën",
            description: "D'Veraarbechtung vu perséinlechen Donnéeë muss op de legitime Grondlagen, déi vum PDPL definéiert sinn, bauen.",
        },
        c919c66c_6a74_4403_bb97_04b773d270bf: {
            title: 'Sensitive personal data: identify it, protect it, and say so when seeking consent',
            description: "Law 91/2025/QH15 defines sensitive personal data in Article 2(3) as personal data tied to an individual's privacy which, when infringed, directly affects the lawful rights and interests of agencies, organisations and individuals, on a list the Government issues; Decree 356/2025/NĐ-CP Article 4(1) is that list. The Law attaches no consent rule to sensitive personal data as a class; sector articles carry their own. Article 26(1)(a) requires the data subject's consent in collecting and processing personal data for health information and in insurance business, except in the Article 19(1) cases. Article 27(1)(b) forbids using a data subject's credit information for credit scoring, credit rating, credit information assessment or creditworthiness assessment without their consent. Article 31(3)(a) forbids location tracking by radio-frequency identification and other technologies, except with the data subject's consent, at the request of a competent authority as provided by law, or where the law provides otherwise. Among what the instruments attach to sensitive data: in processing it, an agency or organisation must set rules limiting access rights, a processing procedure and security measures (Decree 356 Article 4(2)); when consent to process it is sought, the data subject must be told that the data to be processed is sensitive personal data (Decree 356 Article 6(4)); a transfer of it must carry physical security for storage and transmission devices, encryption, anonymisation and other security measures (Decree 356 Article 7(2)); an organisation or individual in finance, banking or credit information must fully comply with the rules on protecting sensitive personal data (Law 91 Article 27(1)(a)), and where a data subject's sensitive data in that sector is disclosed or lost, whoever directly collected it must notify the specialised personal data protection agency and the data subject within 72 hours of discovering it (Decree 356 Article 8(3)); and a small business, start-up, household business or micro-enterprise that directly processes sensitive personal data does not have the option or exemption Law 91 Article 38(2)–(3) and Decree 356 Article 41 give from Articles 21 and 22 and Article 33(2) of the Law. Record which of the organisation's personal data falls on that list.",
        },
        '419cc7c3_a257_4293_a054_304dc2fcb8df': {
            title: 'Rechter vun de Betraffenen (Zougang, Berichtegung, Läschung, Aschränkung, Widderspréch, Iwwerdroebarkeet)',
            description: "D'Betraffe hunn ëmfaassend Rechter, ënner anerem Zougang, Berichtegung, Läschung, Aschränkung, Widderspréch an Iwwerdroebarkeet.",
        },
        '638e6e25_3a74_4864_8bff_8e21ab82860b': {
            title: "Notifikatioun vu Verstéiss un d'Behéierd",
            description: "D'Verantwortlech fir d'Veraarbechtung mussen der zoustänneger Behéierd Verstéiss vu perséinlechen Donnéeë bannent der virgeschriwwener Frist notifizéieren.",
        },
        '6e75c111_66b1_4726_8c27_6537846edb30': {
            title: "Notifikatioun vu Verstéiss un d'Betraffen",
            description: "D'Verantwortlech fir d'Veraarbechtung mussen déi betraffe Persounen iwwer Verstéiss vu perséinlechen Donnéeën informéieren.",
        },
        f686123d_3bdd_4a61_a53c_efe5b52d9058: {
            title: 'Datebschutz-Impaktbewäertung',
            description: 'Eng Datebschutz-Impaktbewäertung muss fir héich-riskant Veraarbechtungsaktivitéiten duerchgefouert ginn.',
        },
        '554ea5b6_092d_4295_a57a_1bd9b2eef291': {
            title: 'Gouvernance vum grenziwwerschreidenden Transfert',
            description: "Grenziwwerschreidend Transferte vu perséinlechen Donnéeë mussen d'Gouvernanceufuerderungen anhalen.",
        },
        cf9a5b29_46fd_4072_b877_13b8a69df82c: {
            title: 'Designatioun vum Datebschutzbeoptraagten',
            description: "D'Organisatioune musse wéi erfuerdert e Datebschutzbeoptraagte designéieren.",
        },
        f5a4965d_eb0c_47df_9933_f185ae46463b: {
            title: 'Verzeechness vu Veraarbechtungsaktivitéiten',
            description: "D'Organisatioune mussen Verzeechnesser vun all Veraarbechtungsaktivitéite féieren.",
        },
        ce8986ff_684d_41ec_8147_b3f2a1c5206c: {
            title: 'Vertraglech Verflichtunge vum Veraarbechter',
            description: 'Vertraglech Verflichtunge mussen mat den Datenveraarbechter etabléiert ginn.',
        },
        '7a3d7554_7b6f_49d7_8864_4a5cd71fd6bd': {
            title: 'Aginn vum Impaktbewäertungsdossier',
            description: 'Impaktbewäertungsdossiere mussen no Vorschrëft bei der zoustänneger Behéierd agereecht ginn.',
        },
        '65dba62f_0788_44c1_89e3_09d802e28eb9': {
            title: 'Impaktdossier fir grenziwwerschreidenden Transfert',
            description: 'E Impaktdossier fir de grenziwwerschreidenden Transfert muss fir international Datentransferte virbereet an agereecht ginn.',
        },
        '0eccb520_b1fa_4fa3_a3ab_2d30823dd00e': {
            title: 'Ernennung an Notifikatioun vum DPO',
            description: "En Datebschutzbeoptraagte muss ernannt ginn an d'Behéierd muss informéiert ginn.",
        },
        '43ead19e_982a_4615_bafd_e6a3a4e80b3a': {
            title: 'Gouvernancepolitiken a -prozeduren',
            description: 'Gouvernancepolitiken a -prozedure mussen etabléiert an erhale ginn.',
        },
        '9ebc7a42_e1f1_49c4_ac90_22a691fd2219': {
            title: 'Bewäisepak an onverännerlech Auditspur',
            description: "D'Bewäisepäck mussen vollstänneg sinn an eng onverännerlech Auditspur muss erhale ginn.",
        },
        '5b0ba1c0_7b86_4c80_b9f9_26e4ff9652c2': {
            title: 'Liewenszyklus vum Aginn-Fall',
            description: "D'Liewenszyklen vum Aginn-Fall musse iwwer déi virgeschriwwe Stadien geféiert ginn.",
        },
        '83f2f346_47bb_4377_a711_3cef26742f39': {
            title: 'Vertraglech Ufuerderungen un de Veraarbechter',
            description: "Vertraglech Ufuerderungen un d'Datenveraarbechter mussen etabléiert an iwwerwaacht ginn.",
        },
        cb972e06_f773_492e_8ad5_f65d1e999b79: {
            title: "Zertifizéierung vum Déngschtleeschter fir d'Veraarbechtung",
            description: 'Organisatiounen, déi Datenveraarbechtungsdéngschter ubidden, musse sech zertifizéieren loossen.',
        },
        '4ba36ab5_39f0_4d79_927e_c39587bed226': {
            title: 'Akte vun den Aginn bei den Behéierden',
            description: 'Akte vun all Aginn bei den Behéierde mussen erhale ginn.',
        },
        f7a62c88_3cda_4031_bc52_1598661a5128: {
            title: 'Jährlech Conformitéitsiwwerpréiwung',
            description: "Eng jährlech Conformitéitsiwwerpréiwung muss duerchgefouert ginn, fir d'lafend Anhalen ze verifizéieren.",
        },
        '3e97f4d4_60ad_46a8_ad2b_615d4ca04aee': {
            title: "Technesch Moossname fir d'Datesécherheet",
            description: "Technesch Moossname fir d'Datesécherheet musse ëmgesat an erhale ginn.",
        },
        '45f4e745_a923_4879_94ce_5a2b41b7e886': {
            title: 'Ufuerderungen un déi offiziell Mẫu số-Formulaire vum Dekret 356',
            description: "Déi 13 offiziell administrativ Mẫu số-Formulairen (Mẫu số 01a–10), déi vum Dekret 356/2025/NĐ-CP virgeschriwwe sinn, musse fir déi entspriechend Aginn beim Ministère vun der Ëffentlecher Sécherheet benotzt ginn. D'Punkten Phụ lục I–VIII sinn intern Mappings vu Datestrukturen, keng offiziell reglementaresch Formulairen.",
        },
        ff452604_7461_4c12_aeab_cb7dd1fb5ba3: {
            title: 'Aginn vum DPIA-Dossier beim A05',
            description: "D'Organisatioune mussen DPIA-Dossiere beim A05/MPS fir Veraarbechtungsaktivitéiten aginn, déi sensibel perséinlech Donnéeën oder grouss-skaleg Veraarbechtunge betreffen.",
        },
        '003600ae_7a0f_4034_b206_72f59409b8f3': {
            title: 'Aginn vum grenziwwerschreidenden Transfertdossier',
            description: "D'Organisatioune mussen Dossiere fir de grenziwwerschreidenden Datentransfert dem A05/MPS zur Iwwerpréiwung an Approbatioun aginn.",
        },
        '4632b20c_293a_4c7e_968e_4ce178c3b7d8': {
            title: 'Demande fir e Veraarbechtungsdéngschtzertifikat',
            description: 'Datenveraarbechtungsdéngschtleeschter mussen Demande fir Zertifikater beim A05/MPS aginn.',
        },
        be96add4_ebdb_4b6c_9426_4dc87b7cd2ce: {
            title: 'Notifikatioun vun der Veraarbechtung vu perséinlechen Donnéeën',
            description: "D'Verantwortlech mussen dem A05/MPS d'Veraarbechtungsaktivitéite vu perséinlechen Donnéeën op de virgeschriwwene Formulairen notifizéieren (Mẫu số 01a/01b).",
        },
        '39f50471_27cc_46bd_acff_9aa2211d43ab': {
            title: "Joresberichterstattung iwwer d'Situatioun",
            description: "D'Verantwortlech mussen jährlech Berichter iwwer d'Situatioun vun der Veraarbechtung vu perséinlechen Donnéeën aginn (Mẫu số 09).",
        },
        '3b45c6fa_d66a_448a_9ff1_597caa055e09': {
            title: 'Vertraulechkeet vun den Benotzerinformatiounen als Standard',
            description: "D'Benotzerinformatioune musse standardméisseg vertraulech behandelt ginn.",
        },
        '2994510e_b1f8_4cdd_947c_d9542746433b': {
            title: 'Minimal organisatoresch an technesch Schutzkontrollen',
            description: 'Minimal organisatoresch an technesch Schutzkontrolle mussen ëmgesat ginn.',
        },
        '8643160f_9a10_4d93_baf5_082c6a7c768a': {
            title: 'Offeleeung nëmme mat enger korrekter Grondlag',
            description: "D'Offeleeung vu Benotzerinformatiounen ass nëmme mat enger korrekter juristescher Grondlag erlaabt.",
        },
        c1fb7d85_fdc0_402e_ab0c_ff134bd72dfa: {
            title: 'Obligatoresch Protokollering an Auditéierbarkeet vun den Offeleeungen',
            description: 'All Offeleeunge musse protokolléiert an auditéierbar sinn.',
        },
        efa36bc0_abae_417a_b676_9509af37f89d: {
            title: 'Intern Approbatiounsworkflow fir Offeleeungen',
            description: "En interne Workflow fir d'Approbatioun muss fir all Offeleeunge etabléiert ginn.",
        },
        d086fdcd_9a23_4500_893c_db8ac62bd787: {
            title: 'Behandlung vun den Äntwerten op Behéierdeufroen',
            description: "D'Behéierdeufroe mussen am Aklang mat de virgeschriwwene Prozedure behandelt an beäntwert ginn.",
        },
    },
    framework_rights_descriptions: {
        be_informed: "De Betraffenen huet d'Recht, iwwer d'Aktivitéiten zur Veraarbechtung vu perséinlechen Donnéeën informéiert ze ginn.",
        consent: "De Betraffenen huet d'Recht, der Veraarbechtung vu sengen perséinlechen Donnéeën zouzestëmmen.",
        refuse_consent: "De Betraffenen huet d'Recht, der Veraarbechtung vu sengen perséinlechen Donnéeën net zouzestëmmen.",
        withdraw_consent: "De Betraffenen huet d'Recht, de Réckzuch vun enger virdru gegebener Zoustëmmung ze froen.",
        access: "De Betraffenen huet d'Recht, seng perséinlech Donnéeën, déi de Verantwortleche fir d'Veraarbechtung hält, anzegesinn an op si zouzegräifen.",
        rectification: "De Betraffenen huet d'Recht, ongenau perséinlech Donnéeën ze korrigéieren oder hir Korrektur ze froen.",
        data_provision: "De Betraffenen huet d'Recht, d'Iwwergaab vu sengen perséinlechen Donnéeën ze froen (yêu cầu cung cấp dữ liệu cá nhân).",
        deletion: "De Betraffenen huet d'Recht, d'Läschung oder Zerstéierung vu perséinlechen Donnéeën ze froen, ënner Vorbehalt vun den Ausnamen am Gesetz.",
        restriction_of_processing: "De Betraffenen huet d'Recht, d'Aschränkung vun der Veraarbechtung vu sengen perséinlechen Donnéeën ze froen.",
        object_to_processing: "De Betraffenen huet d'Recht, eng Demande ze schécken, mat där hie sech géint d'Veraarbechtung vu sengen perséinlechen Donnéeë wiert.",
        complaint: "De Betraffenen huet d'Recht, eng Reklamatioun (khiếu nại) iwwer d'Veraarbechtung vu perséinlechen Donnéeën anzereechen.",
        denunciation: "De Betraffenen huet d'Recht, eng Denonzéierung (tố cáo) iwwer Verstéiss géint de Schutz vu perséinlechen Donnéeën ze maachen.",
        lawsuit: "De Betraffenen huet d'Recht, juristesch Schrëtt (khởi kiện) fir Verstéiss géint seng Datebschutzrechter z'ënnerhuelen.",
        claim_damages: "De Betraffenen huet d'Recht, Schuedeersaz fir de Schued, deen duerch Verstéiss géint de Schutz vu perséinlechen Donnéeë verursaacht gouf, ze fuerderen.",
        request_protection_measures: "De Betraffenen huet d'Recht, vun den zoustänneg Behéierden oder vu relevanten Organisatiounen/Eenzelpersounen ze froen, datt si Moossname fir de Schutz vu sengen perséinlechen Donnéeën ëmsetzen.",
    },
    framework_procedures: {
        VN_DP_DRAFT_P7: {
            name: 'Risikomanagement an Iwwerwaachung (Projet)',
            steps: {
                '1': 'Risikokategorien identifizéieren',
                '2': 'Fréiwarnungskontrollen erhalen',
                '3': 'Risiken bei der Erfaassung, Späicherung, Veraarbechtung, dem Deelen an den Transaktiounen iwwerpréiwen',
                '4': 'Risiken am Beräich vum Datebschutz, vun der Cybersécherheet a vum Zougangsmanagement protokolléieren',
                '5': 'Behiewungsmoossnamen ausléisen',
                '6': 'Iwwerpréiwungsgeschicht bewahren',
            },
        },
        VN_DP_DRAFT_P6: {
            name: 'Reklamatiounen a Sträitfäll (Projet)',
            steps: {
                '1': 'Reklamatioun / Sträitfall empfänken',
                '2': 'Vollstännegkeet vun den erfuerderlechen Informatiounen iwwerpréiwen; Ergänzung bannent 24 Stonne froen, falls onvollstänneg',
                '3': "Bannent 3 Aarbechtsdeeg no Empfank vun enger vollstänneger Demande d'Parteien iwwer d'Behandlungsapproche an d'erwaart Frist informéieren",
                '4': 'Äntwerten an Begleetdokumenter ufroen',
                '5': "Sträitfaktoren an d'Verantwortung vun der Plattform bewäerten",
                '6': 'Léisungsergebnis protokolléieren',
                '7': 'Bewäiser bewahren an de Fall ofschléissen',
            },
        },
        VN_DP_DRAFT_P5: {
            name: 'Liwwerung an Zertifikat vun den Eegentumsrechter iwwer Donnéeën (Projet)',
            steps: {
                '1': 'Produkt/Déngscht no Vertragsmethod liwweren',
                '2': 'Liwwerungseventuell an -zäit protokolléieren',
                '3': 'Zertifikat vun den Eegentumsrechter iwwer Donnéeë generéieren',
                '4': 'Ofschloss vun der Transaktioun aschreiwen',
                '5': 'Spéider Recherche/Verifikatioun aktivéieren wou erfuerdert',
            },
        },
        VN_DP_DRAFT_P4: {
            name: 'Initiatioun vun der Transaktioun a Vertragsschloss (Projet)',
            steps: {
                '1': 'Lëschtung an Bewäertungsresultat iwwerpréiwen',
                '2': 'Workflow fir Akaaf, Verhandlung oder Versteigerung initiéieren',
                '3': 'Elektronesche Vertrag generéieren',
                '4': 'Acceptanz vu béide Parteien sécherstellen',
                '5': 'Ofschloss vun der Bezuelung protokolléieren',
                '6': 'Liwwerung autoriséieren',
            },
        },
        VN_DP_DRAFT_P3: {
            name: 'Zougang zu Sandbox / Testëmfeld (Projet)',
            steps: {
                '1': 'Sandbox-Demande aginn',
                '2': 'Ëmfang, erlaabt Donnéeën, Zäitdauer a Veraarbechtungsgenehmegungen definéieren',
                '3': 'Isoléiert technescht Ëmfeld zur Verfügung stellen',
                '4': 'Sécherheet an Zougang während dem Test iwwerwaachen',
                '5': 'Ausgab virum Eraushuelen iwwerpréiwen',
                '6': 'Eraushuelen nëmme do approbéieren, wou et erlaabt ass',
                '7': 'Testsessioun ofschléissen an Ëmfeld botzen',
            },
        },
        VN_DP_DRAFT_P2: {
            name: 'Lëschtung vun Dateprodukter / -déngschter (Projet)',
            steps: {
                '1': 'Lëschtungsdemande erstellen',
                '2': 'Bewäiser zu Quell an Eegentums-/Notzungsrecht erfaassen',
                '3': 'Technesch Charakteristiken, Format, API-Detailer a Beschränkungen protokolléieren',
                '4': 'Qualitéitsoffeleeunge protokolléieren',
                '5': 'Lëschtungsdossier zur Plattformiwwerpréiwung aginn',
                '6': "D'Plattform féiert Iwwerpréiwunge vun der Vollstännegkeet, Legalitéit an technesch Iwwerpréiwungen duerch",
                '7': 'Lëschtung approbéieren oder ofleenen',
                '8': 'Falls approbéiert, Lëschtung mat ugehäschtem Bewäertungsresultat publizéieren',
            },
        },
        VN_DP_DRAFT_P1: {
            name: 'Onboarding vum Deelhuewer (Projet)',
            steps: {
                '1': 'Typ vum Deelhuewer feststellen: Verkeefer oder Keefer',
                '2': 'Juristesche Status a juristesch Kapazitéit verifizéieren',
                '3': 'Zousätzlech Konditioune verifizéieren, falls konditionéiert Aktivitéiten uwendbar sinn',
                '4': 'Transaktiounskont aschreiwen',
                '5': 'Bezuelungskont verbannen',
                '6': 'Plattformdeelnam approbéieren',
                '7': 'Deelhuewerakt an Auditspur späicheren',
            },
        },
        AI_A1: {
            name: 'Aschreiwung / Notifikatioun vu KI-Systemer',
            steps: {
                '1': 'Iwwerpréiwung vun der Uwendung a Klassifikatioun vum KI-System',
                '2': 'Aschreiwungs-/Notifikatiounsdossier opbauen',
                '3': 'Intern Approbatioun',
                '4': 'Iwwer Kanal aginn',
                '5': 'Schleef fir Ännerung — falls zréckgeschéckt, aktualiséieren a nei aginn',
                '6': 'Ofschléissen a Bestätegung späicheren',
            },
        },
        AI_A2: {
            name: "Workflow fir d'KI-Risikoklassifikatioun",
            steps: {
                '1': 'Antrag am KI-Systemregëster erstellen',
                '2': 'Klassifikatiounsassistent ausféieren',
                '3': 'Bewäiser ugehäschten',
                '4': 'Klassifikatioun approbéieren',
                '5': 'Ausgaben mam Systemdossier verbannen',
            },
        },
        AI_A3: {
            name: 'Conformitéit bei Kennzeechnung an Offeleeung',
            steps: {
                '1': 'Kennzeechnungsverflichtungen pro System a Notzungsfall identifizéieren',
                '2': 'Kennzeechnungsnotizen ëmsetzen',
                '3': 'Bewäiser erfaassen — Bildschirmfotoen, UI-Text, technesch Metadaten',
                '4': 'Befreiunge behandelen an Begrënnung protokolléieren',
                '5': 'Approbéieren a versionéieren',
            },
        },
        AI_A4: {
            name: 'Notifikatioun vu schwéiere Virfäll',
            steps: {
                '1': 'KI-Virfall-Akt erstellen',
                '2': 'Schweregrad klasséieren',
                '3': 'Akndämmungsmoossnamen ausféieren a Bewäiser erfaassen',
                '4': 'Templateexport vum Bericht iwwer schwéier Virfäll generéieren',
                '5': "Iwwer Kanal un d'Behéierd aginn",
                '6': 'Behéierdeäntwerten a -ofschloss verfollegen',
            },
        },
        AI_A5: {
            name: 'KI-Impaktbewäertung',
            steps: {
                '1': 'Bewäertungsakt erstellen',
                '2': 'Erfuerderlech Sektiounen befëllen',
                '3': 'Bewäiser ugehäschten — Datenflëss, Modelldokumentatioun, Kontrollen',
                '4': 'Intern Approbatioun',
                '5': 'Bericht exportéieren an am Dossier späicheren',
            },
        },
        AI_A6: {
            name: 'Deelnam u kontrolléierten Tester / Sandbox',
            steps: {
                '1': 'Akten zur Berechtegung an Bereetschaft fëllen',
                '2': 'Demande virbereeden an exportéieren',
                '3': 'Iwwer Kanal aginn',
                '4': 'Approbatioun verfollegen',
                '5': 'Iwwerwaachungspunkten an Protokoller ausféieren',
                '6': 'Ofschlossbericht a Closure-Pak fäerdegmaachen',
            },
        },
        CYBERSEC_C1: {
            name: 'Sécherheetsbewäertung vu krittesche Informatiounssystemer',
            steps: {
                '1': 'Informatiounssystemer no den nationalen Cybersécherheetsufuerderunge klasséieren',
                '2': 'Sécherheetsbewäertung a Risikoevaluatioun fir klasséiert Systemer duerchféieren',
                '3': 'Erfuerderlech Sécherheetsmoossnamen a Basislinnkontrollen ëmsetzen',
                '4': 'Bewäiser vun der Conformitéit dokumentéieren a periodesch Iwwerpréiwunge plangen',
            },
        },
        CYBERSEC_C2: {
            name: 'Notifikatioun vu Cybersécherheetsvirfäll',
            steps: {
                '1': 'De Cybersécherheetsvirfall erkennen, klasséieren a bewäerten',
                '2': 'Direkt Reaktiounsmoossnamen ausféieren a Bewäiser bewahren',
                '3': "Un d'Behéierd melden an un der nationaler Koordinatioun fir d'Reaktioun op Virfäll deelhuelen",
                '4': 'Iwwerpréiwung no dem Virfall duerchféieren a geléiert Erkenntnisser dokumentéieren',
            },
        },
        CYBERSEC_C3: {
            name: 'Verifikatioun vun der Conformitéit mat der Datelokaliséierung',
            steps: {
                '1': "Bewäerten, ob d'Ausléiser fir d'Datelokaliséierung op Basis vum Déngschttyp an de Datekategorien uwendbar sinn",
                '2': "Verifizéieren, datt d'Architektur vun der lokaler Späicherung an d'Kontrollen iwwer grenziwwerschreidend Flëss agesat sinn",
                '3': 'Ufuerderungen un déi lokal Präsenz (Filial / Vertriedungsbüro) bewäerten, wou applikabel',
                '4': "Conformitéitsbewäiser dokumentéieren a fir d'Iwwerpréiwung duerch d'Behéierd erhalen",
            },
        },
        CYBERSEC_C4: {
            name: 'Äntwert op eng Demande zur Behéierdezesummenaarbecht',
            steps: {
                '1': "D'Legitimitéit an den Ëmfang vun der Behéierdeufro empfänken a verifizéieren",
                '2': 'Erfuerderlech Informatioune sammelen a virbereeden oder erfuerderlech Aktiounen ausféieren',
                '3': "Äntwert un d'Behéierd bannent der erfuerderlecher Frist aginn",
                '4': "D'Offeleeung protokolléieren an de Fall ofschléissen",
            },
        },
        CYBERSEC_C5: {
            name: "Verifikatioun vun der Qualifikatioun vum Personal fir d'Cybersécherheet",
            steps: {
                '1': 'Sécherheetspositioun vum Liwwerant mat dem Cybersécherheetsbewäertungskader bewäerten',
                '2': 'Ëmfang vum Liwwerantenzougang zu den Donnéeë verifizéieren an am Protokoll vum Liwwerantenzougang aschreiwen',
                '3': "Klausel iwwer d'Notifikatioun vu Virfäll duerch de Liwwerant a vertraglech Cybersécherheetsufuerderungen iwwerpréiwen",
                '4': "Periodesch Sécherheetsiwwerpréiwunge vum Liwwerant plangen an d'Conformitéit iwwerwaachen",
            },
        },
        DATA_D1: {
            name: 'Setup vum Dateninventar an der Datenklassifikatioun',
            steps: {
                '1': 'All Datenobjeten mat Typen a Quelle am Dateninventar-Register katalogiséieren',
                '2': 'All Objet no Sensibilitéitsniveau klasséieren',
                '3': 'Behandlungsregele pro Klassifikatiounsniveau festleeën',
                '4': 'Bewäiser erfaassen — Bildschirmfotoen, Architektur-Diagrammer, Zougangskontrollen',
                '5': 'Periodesch Iwwerpréiwunge plangen',
            },
        },
        DATA_D2: {
            name: 'Gouvernance vum Datedeelen',
            steps: {
                '1': 'Nei Aarrangementer vum Datedeelen iwwer eng Datedeelrisikobewäertung evaluéieren',
                '2': 'Interne Approbatiounsworkflow ofschléissen',
                '3': 'Datedeel-Eventer am Datedeel-/Offeleeungsprotokoll protokolléieren',
                '4': 'Bewäiser erfaassen — Approbatiounsakten, vertraglech Dokumenter',
                '5': 'Periodesch Iwwerpréiwung vun den Aarrangementer vum Datedeelen',
            },
        },
        DATA_D3: {
            name: 'Bewäertung vum grenziwwerschreidende Datentransfert',
            steps: {
                '1': 'Grenziwwerschreidend Transferten am Dateninventar identifizéieren',
                '2': 'Risikobewäertung vum Transfert duerchféieren',
                '3': 'Schutzmoossnamen a Gouvernancemoossnamen uwannen',
                '4': 'An der grenziwwerschreidender Sektioun vun der Gouvernance-Attestatioun dokumentéieren',
                '5': 'Bewäertungspak fir den Audit exportéieren',
            },
        },
        DATA_D4: {
            name: 'Jährlech Gouvernance-Attestatioun',
            steps: {
                '1': 'RACI-Matrix vun der Gouvernance iwwerpréiwen',
                '2': 'Politikstatus iwwerpréiwen (Gouvernance, Klassifikatioun, Deelen, Opbewahrung)',
                '3': 'Attestatiounspak ofschléissen',
                '4': 'Intern Approbatioun erhalen',
                '5': 'Attestatioun exportéieren a archivéieren',
            },
        },
        DATA_D5: {
            name: 'Behandlung vu Daten-Virfäll',
            steps: {
                '1': 'De Virfall mat enger Decisiounsakt klasséieren',
                '2': 'Reaktioun op de Virfall no dem Playbook ausféieren',
                '3': 'Bewäiser erfaassen — Logs, Bildschirmfotoen, Kommunikatiounen',
                '4': 'Iwwerpréiwung no dem Virfall duerchféieren',
                '5': "Falls Notifikatioun un d'Behéierd erfuerderlech: Aginn-Fall erstellen an un den MIC aginn",
                '6': 'Ofschléissen a archivéieren',
            },
        },
        ECOM_P1: {
            name: 'Notifikatioun (Verkafs-Websäit/App)',
            steps: {
                '1': 'Klassifikatioun feststellen: Verkafs-Säit/App vs Marketplace/Déngschtplattform',
                '2': 'Internen Dossierakt mat Firmenidentitéit, Websäit/App-Info an erfuerderlechen Offeleeungen erstellen',
                '3': 'Bewäisepak generéieren — Bildschirmfotoe vun Offeleeungen, Politik-URLen, Versiounsgeschicht',
                '4': 'Iwwer Online.gov.vn aginn — Dokumenter eropluedem, Zäitstempel a Bestätegung protokolléieren',
                '5': 'Veraarbechtung verfollegen — falls fir Ännerung zréckgeschéckt, Grënn erfaassen an nei aginn',
                '6': 'Ofschléissen — Bestätegung späicheren, Bewäiser an Auditspur verbannen',
            },
        },
        ECOM_P2: {
            name: 'Aschreiwung (Déngschtplattformen / Marketplaces)',
            steps: {
                '1': 'Plattformmodell a Verflichtunge feststellen — Marketplace vs Mëschform vs Sozial-Commerce',
                '2': "Aschreiwungsdossier opbauen — Demandeformular, Schema fir d'Bereetstellung vun de Servicer, AGB, Plattformregelen",
                '3': 'Intern Approbatiounen — juristeschen/Conformitéitsverantwortlechen zouweisen, fir Aginn approbéieren',
                '4': 'Iwwer Online.gov.vn aginn — Dossier eropluedem, Zäitstempel protokolléieren',
                '5': 'Veraarbechtungsschleef — falls fir Ännerung zréckgeschéckt, aktualiséieren an nei aginn',
                '6': 'Approbatioun an Operationaliséierung — Bestätegung späicheren, lafend Conformitéitsregëster erhalen',
            },
        },
        ECOM_P3: {
            name: 'Notifikatioun vun Aktualiséierungen/Ännerungen',
            steps: {
                '1': 'Ännerungsausléiser identifizéieren — wesentlech Ännerungen un Dossier-Elementer',
                '2': 'Ännerungspak generéieren — Ännerungsresumé, aktualiséiert Dokumenter, Bewäisaktualiséierungen',
                '3': 'Update aginn an Aginn-Status protokolléieren',
                '4': 'Veraarbechtung verfollegen an ofschléissen',
            },
        },
        ECOM_P4: {
            name: 'Schlussennn / Deaktivéierung',
            steps: {
                '1': "Schlusspak virbereeden — Datum vum Schlussennn, Grond, Plang fir d'Behandlung vum Konsumenteschutz, Beweis vun der ëffentlecher Bekanntmaachung",
                '2': 'Schlussennn aginn — Zäitstempel a Bestätegung protokolléieren',
                '3': 'Ofschléissen — finale Status a Bewäiser späicheren',
            },
        },
        ECOM_P5: {
            name: 'Programm fir Trustmark / Glafwierdegkeetsbewäertung',
            steps: {
                '1': 'Iwwerpréiwung vun der Berechtegung an der Bereetschaft',
                '2': 'Dossier mat dem offiziellen Formulair virbereeden',
                '3': 'Aginn a verfollegen',
                '4': 'Lafend Conformitéitsbewäiser erhalen',
            },
        },
        PDPL_P1: {
            name: 'Aginn vum DPIA beim MPS',
            steps: {
                '1': 'DPIA mat dem Formularewee vum Dekret 356 ofschléissen (Mẫu số 02a/02b)',
                '2': 'Begleetdokumenter a Beleegbewäiser zesummestellen',
                '5': "Dossier a Bewäiser vum Aginn fir d'Auditspur opbewahren",
            },
        },
        PDPL_P2: {
            name: 'Demande fir grenziwwerschreidenden Transfert',
            steps: {
                '1': 'Dossier fir de grenziwwerschreidenden Transfert mat de Formulairen vum Dekret 356 ofschléissen (Mẫu số 01a/01b, Mẫu số 09)',
            },
        },
        PDPL_P3: {
            name: 'Demande fir e Datenveraarbechtungsdéngschtzertifikat',
            steps: {
                '1': 'Demandedossier zur Zertifizéierung no de Vorschrëfte vum Dekret 356 virbereeden',
                '2': 'Demande beim MPS mat den erfuerderleche Beleegdokumenter aginn',
                '3': 'Op Iwwerpréiwungsbemierkungen an Ergänzungsufroe vum MPS äntweren',
            },
        },
        PDPL_P4: {
            name: 'Behandlung vun Rechterdemande vum Betraffenen',
            steps: {
                '1': 'Demande empfänken a protokolléieren; Identitéit vum Betraffene bannent 2 Aarbechtsdeeg verifizéieren',
                '2': "Typ vun der Demande bewäerten an d'uwendbar Frist (10/15/20 Deeg pro Typ) feststellen",
                '3': 'Erfëllungsmoossnamen iwwer all relevant Systemer an Ofdeelungen ausféieren',
                '4': 'Äntwert un de Betraffenen mat Resultat a Begrënnung virbereeden a schécken',
                '5': "Fall ofschléissen a Bewäiser fir den Audit opbewahren; all Begrënnung fir d'Oflenungspflicht dokumentéieren",
            },
        },
        PDPL_P5: {
            name: 'Notifikatioun vu Verstéiss un den MPS',
            steps: {
                '1': 'De Verstouss erkennen a bewäerten; feststellen, ob d’Notifikatiounsverflichtung gëllt, an d’applicabel Notifikatiounsfrist aus den installéierten Fristdatesätz erausfannen',
                '2': 'Notifikatiounspak no de PDPL-Ufuerderunge virbereeden',
                '3': 'D’Notifikatioun beim MPS bannent der Notifikatiounsfrist aginn, déi aus den installéierten Fristdatesätz ervirgeet, gerechent vun der Entdeckung un',
                '4': 'Op Folge-Ufroe vum MPS äntweren; Ergänzungen a Koordinatiounsbewäiser aginn',
            },
        },
        TELECOM_T1: {
            name: 'Behandlung vu Behéierdeufroen',
            steps: {
                '1': "D'Behéierdeufro iwwer den Annahmeakt empfänken a protokolléieren",
                '2': 'Behéierdeidentitéit, juristesch Grondlag an Ëmfang vun der Demande verifizéieren',
                '3': 'Intern juristesch Iwwerpréiwung duerchféieren a Managementapprobatioun erhalen',
                '4': 'Äntwertpak mat zesummegestallten Bewäiser virbereeden',
                '5': 'Äntwertbréif redigéieren an approbéieren',
                '6': "Aginn-Fall erstellen an d'Äntwert iwwer Kanal aginn",
                '7': 'Offeleeung protokolléieren a Bestätegung vun der Behéierd verfollegen',
                '8': "Fall ofschléissen a Bewäiser fir d'Auditspur opbewahren",
            },
        },
        TELECOM_T2: {
            name: 'Offeleeungsmanagement',
            steps: {
                '1': 'Offeleeungsdemande empfänken (Benotzerzoustëmmung, juristesch Verflichtung oder Behéierdeufro)',
                '2': "Approbatiounsworkflow fir d'Offeleeung ofschléissen",
                '3': 'Offeleeung ausféieren an am Protokollregëster aschreiwen',
                '4': 'Bewäiser erfaassen — Approbatiounsakten, Kommunikatiounsakten',
                '5': 'Periodesch Iwwerpréiwung vum Offeleeungsprotokoll',
            },
        },
        TELECOM_T3: {
            name: 'Setup vun der Vertraulechkeetspolitik an der Zougangskontroll',
            steps: {
                '1': 'Vertraulechkeetspolitik redigéieren an approbéieren',
                '2': "Politik fir d'Zougangskontroll an d'Protokollerung redigéieren an approbéieren",
                '3': 'Veraarbechtungsaktivitéiten am Register vun der Veraarbechtung vun de Benotzerinformatiounen dokumentéieren',
                '4': 'Bewäiser erfaassen — Politikdokumenter, Ëmsetzungsbewäiser',
                '5': 'Periodesch Iwwerpréiwunge plangen',
            },
        },
        TELECOM_T4: {
            name: 'Bewäertung vun engem Telekomvirfall',
            steps: {
                '1': 'De Virfall klasséieren a bewäerten',
                '2': 'Notifikatiounsufuerderunge feststellen',
                '3': "Falls Notifikatioun un d'Behéierd erfuerderlech: Aginn-Fall erstellen",
                '4': 'Bewäiser erfaassen a bewahren',
                '5': 'Léisung an Ofschloss',
            },
        },
        TELECOM_T5: {
            name: 'Management vun Opbewahrung a Läschung',
            steps: {
                '1': 'Plang vun Opbewahrung a Läschung pro Datekategorie definéieren',
                '2': 'Läschungsprozeduren ëmsetzen',
                '3': 'Bewäiser erfaassen — Läschungsakten',
                '4': 'Periodesch Iwwerpréiwung vun de Pläng',
            },
        },
    },
    framework_fines: {
        VN_CYBERSEC_LAW: {},
        VN_ECOMMERCE_LAW_2025: {
            'ECOM-FINE-A': 'Aschreiwung / Notifikatioun / administrativ Bereetschaft',
            'ECOM-FINE-B': 'Reklamatiounen a Konsumenteschutz',
            'ECOM-FINE-C': 'Iwwerwaachung an Duerchsetzung vum Eraushuelen',
            'ECOM-FINE-D': 'Grenziwwerschreidend Operatiounen',
            'ECOM-FINE-E': 'Plattformregelen an Transparenz',
        },
        VN_TELECOM_LAW_2023: {
            'TELECOM-FINE-A': 'Versoe bei der Behandlung vu Behéierdeufroen',
            'TELECOM-FINE-B': 'Vertraulechkeet an onerlaabten Offeleeung',
            'TELECOM-FINE-C': 'Versoe bei der Zougangskontroll an der Protokollerung',
            'TELECOM-FINE-D': "Verstéiss géint d'Opbewahrung an d'Läschung",
            'TELECOM-FINE-E': 'Versoe bei der Bewäertung vun Telekomvirfäll',
        },
    },
    framework_fines_detail: {
        VN_TELECOM_LAW_2023: {
            'TELECOM-FINE-E': {
                fine_individual: 'Bis zu 100 Milliounen VND',
                fine_org: 'Bis zu 200 Milliounen VND',
                deadlines: 'Direkt no der Entdeckung',
                remediation: [
                    'Mandatory incident classification',
                    'Mandatory severity and decision records',
                    'Mandatory evidence preservation for confidentiality incidents',
                    'Mandatory post-incident closure',
                ],
            },
            'TELECOM-FINE-D': {
                fine_individual: 'Bis zu 100 Milliounen VND',
                fine_org: 'Bis zu 200 Milliounen VND',
                deadlines: 'Lafend Verflichtung',
                remediation: [
                    'Mandatory retention schedule implementation',
                    'Mandatory deletion evidence preservation',
                    'Mandatory retention exception decision records',
                ],
            },
            'TELECOM-FINE-C': {
                fine_individual: 'Bis zu 100 Milliounen VND',
                fine_org: 'Bis zu 200 Milliounen VND',
                deadlines: 'Lafend Verflichtung',
                remediation: [
                    'Mandatory access-control policy implementation',
                    'Mandatory logging of access events',
                    'Mandatory review of access rights',
                ],
            },
            'TELECOM-FINE-B': {
                fine_individual: 'Bis zu 100 Milliounen VND',
                fine_org: 'Bis zu 200 Milliounen VND',
                deadlines: 'Lafend Verflichtung',
                remediation: [
                    'Mandatory confidentiality policy implementation',
                    'Mandatory disclosure approval workflow',
                    'Mandatory disclosure trail maintenance',
                    'Mandatory user information access controls',
                ],
            },
            'TELECOM-FINE-A': {
                fine_individual: 'Bis zu 100 Milliounen VND',
                fine_org: 'Bis zu 200 Milliounen VND',
                deadlines: 'Wéi an der Behéierdeufro spezifizéiert',
                remediation: [
                    'Mandatory intake and verification of authority request',
                    'Mandatory response within prescribed timeline',
                    'Mandatory disclosure log entry',
                    'Mandatory evidence preservation',
                ],
            },
        },
        VN_ECOMMERCE_LAW_2025: {
            'ECOM-FINE-E': {
                fine_individual: 'Bis zu 80 Milliounen VND',
                fine_org: 'Bis zu 160 Milliounen VND',
                deadlines: 'Lafend Verflichtung',
                remediation: [
                    'Mandatory platform rules publication and versioning',
                    'Mandatory user-facing disclosures',
                    'Mandatory seller/affiliate/livestream obligations evidenced',
                    'Mandatory control history and approval chain',
                ],
            },
            'ECOM-FINE-D': {
                fine_individual: 'Bis zu 80 Milliounen VND',
                fine_org: 'Bis zu 160 Milliounen VND',
                deadlines: 'Lafend Verflichtung',
                remediation: [
                    'Mandatory cross-border obligations assessment',
                    'Mandatory authorized representative readiness',
                    'Mandatory cross-border operations register',
                    'Mandatory evidence of compliance action',
                ],
            },
            'ECOM-FINE-C': {
                fine_individual: 'Bis zu 80 Milliounen VND',
                fine_org: 'Bis zu 160 Milliounen VND',
                deadlines: 'Lafend Verflichtung',
                remediation: [
                    'Mandatory review of prohibited or unlawful listings',
                    'Mandatory takedown decision records',
                    'Mandatory evidence preservation',
                    'Mandatory seller notification and closure trail',
                ],
            },
            'ECOM-FINE-B': {
                fine_individual: 'Bis zu 80 Milliounen VND',
                fine_org: 'Bis zu 160 Milliounen VND',
                deadlines: 'Wéi pro Reklamatiounsbehandlungsverflichtungen spezifizéiert',
                remediation: [
                    'Mandatory complaint intake procedure',
                    'Mandatory resolution record',
                    'Mandatory complaint audit trail',
                    'Mandatory escalation for cross-border seller issues',
                ],
            },
            'ECOM-FINE-A': {
                fine_individual: 'Bis zu 80 Milliounen VND',
                fine_org: 'Bis zu 160 Milliounen VND',
                deadlines: 'Virum Ufank vun den e-Commerce-Operatiounen',
                remediation: [
                    'Mandatory platform classification',
                    'Mandatory notification or registration completion',
                    'Mandatory evidence of portal submission',
                    'Mandatory update/termination tracking',
                ],
            },
        },
        VN_CYBERSEC_LAW: {},
    },
    framework_procedures_detail: {
        VN_DP_DRAFT_P7: {
            trigger: 'Lafenden Betrib vun der Plattform',
            outputs: [
                'Risk assessment record',
                'Early warning log',
                'Remediation record',
                'Review trail',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Plattformbedreiwer / Risiko',
                '2': 'Plattformbedreiwer / Sécherheet',
                '3': 'Plattformbedreiwer / Risiko',
                '4': 'Plattformbedreiwer / Sécherheet',
                '5': 'Plattformbedreiwer',
                '6': 'Konformitéit',
            },
            step_outputs: {
                '1': ['Risk assessment record'],
                '2': ['Early warning log'],
                '3': ['Risk review findings'],
                '4': ['Risk register entries'],
                '5': ['Remediation record'],
                '6': ['Review trail'],
            },
        },
        VN_DP_DRAFT_P6: {
            trigger: 'En Deelhuewer reecht eng Reklamatioun oder Sträitfalldemande an',
            outputs: [
                'Complaint intake record',
                'Dispute resolution record',
                'Evidence pack',
                'Case closure record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Plattformbedreiwer',
                '2': 'Plattformbedreiwer',
                '3': 'Plattformbedreiwer',
                '4': 'Plattformbedreiwer',
                '5': 'Plattformbedreiwer / Juristesch',
                '6': 'Plattformbedreiwer',
                '7': 'Konformitéit',
            },
            step_outputs: {
                '1': ['Complaint intake record'],
                '2': ['Completeness check', 'Supplement request (if any)'],
                '3': ['Handling approach notice'],
                '4': ['Evidence pack'],
                '5': ['Assessment findings'],
                '6': ['Dispute resolution record'],
                '7': ['Case closure record', 'Archived evidence pack'],
            },
        },
        VN_DP_DRAFT_P5: {
            trigger: "D'Liwwerung gëtt ofgeschloss, nodeems d'Vertrags- a Bezuelungskonditiounen erfëllt sinn",
            outputs: [
                'Delivery record',
                'Certificate of property rights over data',
                'Transaction register entry',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Verkeefer / Plattformbedreiwer',
                '2': 'Plattformbedreiwer',
                '3': 'Plattformbedreiwer',
                '4': 'Plattformbedreiwer',
                '5': 'Plattformbedreiwer',
            },
            step_outputs: {
                '1': ['Delivery record'],
                '2': ['Delivery event log entry'],
                '3': ['Property rights certificate'],
                '4': ['Transaction register entry'],
                '5': ['Verification access record'],
            },
        },
        VN_DP_DRAFT_P4: {
            trigger: 'E Keefer initiéiert eng Transaktioun fir Donnéeën, Produkt oder Service',
            outputs: [
                'Transaction initiation record',
                'Electronic contract',
                'Payment confirmation',
                'Delivery authorization record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Keefer',
                '2': 'Keefer / Plattformbedreiwer',
                '3': 'Plattformbedreiwer',
                '4': 'Keefer / Verkeefer',
                '5': 'Plattformbedreiwer / Bezuelung',
                '6': 'Plattformbedreiwer',
            },
            step_outputs: {
                '1': ['Listing review decision'],
                '2': ['Transaction initiation record'],
                '3': ['Electronic contract record'],
                '4': ['Contract acceptance record'],
                '5': ['Payment confirmation'],
                '6': ['Delivery authorization record'],
            },
        },
        VN_DP_DRAFT_P3: {
            trigger: 'En Deelhuewer wëll Dateprodukter/-déngschter am isoléierten Ëmfeld evaluéieren',
            outputs: [
                'Sandbox request',
                'Sandbox scope record',
                'Output review record',
                'Sandbox termination record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Deelhuewer',
                '2': 'Plattformbedreiwer / Deelhuewer',
                '3': 'Plattformbedreiwer / IT',
                '4': 'Plattformbedreiwer / Sécherheet',
                '5': 'Plattformbedreiwer',
                '6': 'Plattformbedreiwer',
                '7': 'Plattformbedreiwer / IT',
            },
            step_outputs: {
                '1': ['Sandbox request'],
                '2': ['Sandbox scope record'],
                '3': ['Provisioned environment'],
                '4': ['Security monitoring log'],
                '5': ['Output review record'],
                '6': ['Extraction approval decision'],
                '7': ['Sandbox termination record'],
            },
        },
        VN_DP_DRAFT_P2: {
            trigger: 'E Verkeefer wëll Donnéeën, Produkt oder Datendéngscht fir den Handel lëschten',
            outputs: [
                'Listing application',
                'Lawful-source verification record',
                'Technical validation record',
                'Quality disclosure record',
                'Listing approval record',
                'Listing register entry',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Verkeefer',
                '2': 'Verkeefer / Juristesch',
                '3': 'Verkeefer / IT',
                '4': 'Verkeefer',
                '5': 'Verkeefer',
                '6': 'Plattformbedreiwer',
                '7': 'Plattformbedreiwer',
                '8': 'Plattformbedreiwer',
            },
            step_outputs: {
                '1': ['Listing application'],
                '2': ['Lawful-source verification record'],
                '3': ['Technical validation record'],
                '4': ['Quality disclosure record'],
                '5': ['Submitted listing dossier'],
                '6': ['Review findings'],
                '7': ['Listing approval record'],
                '8': ['Published listing', 'Listing register entry'],
            },
        },
        VN_DP_DRAFT_P1: {
            trigger: 'E Verkeefer oder Keefer trëtt der Datenplattform bäi',
            outputs: [
                'Seller / buyer onboarding record',
                'Participant register entry',
                'Account setup evidence',
                'Approval record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Plattformbedreiwer',
                '2': 'Plattformbedreiwer / Juristesch',
                '3': 'Plattformbedreiwer / Juristesch',
                '4': 'Plattformbedreiwer',
                '5': 'Plattformbedreiwer',
                '6': 'Plattformbedreiwer',
                '7': 'Konformitéit',
            },
            step_outputs: {
                '1': ['Participant type decision'],
                '2': ['Legal capacity verification record'],
                '3': ['Conditional-business check record'],
                '4': ['Transaction account registration record'],
                '5': ['Payment account link record'],
                '6': ['Participation approval record'],
                '7': ['Participant register entry', 'Audit trail'],
            },
        },
        PDPL_P1: {
            trigger: 'Ufank vun der Veraarbechtung vu perséinlechen Donnéeën, déi eng Impaktbewäertung verlaangt',
            outputs: ['DPIA dossier', 'Submission confirmation', 'Supplement package'],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'DPO / Privatsphärenteam',
                '2': 'Privatsphärenteam',
                '3': 'Juristesch / Compliance',
                '4': 'DPO / Privatsphärenteam',
                '5': 'Compliance-Team',
            },
            step_outputs: {
                '1': ['Completed DPIA dossier'],
                '2': ['Dossier package', 'Cover letter'],
                '3': ['Submission confirmation', 'Tracking reference'],
                '4': ['Supplement package', 'Updated dossier'],
                '5': ['Archived dossier', 'Audit evidence pack'],
            },
        },
        PDPL_P2: {
            trigger: "Absicht, perséinlech Donnéeë vu Vietnam an d'Ausland ze transferéieren",
            outputs: ['Transfer dossier', 'Submission confirmation', 'Supplement response'],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'DPO / Privatsphärenteam',
                '2': 'Juristesch / Compliance',
                '3': 'DPO / Privatsphärenteam',
            },
            step_outputs: {
                '1': ['Transfer dossier package'],
                '2': ['Submission confirmation', 'Tracking reference'],
                '3': ['Supplement response', 'Updated dossier'],
            },
        },
        PDPL_P3: {
            trigger: 'Organisatioun, déi Datenveraarbechtungsdéngschter ubitt, déi Zertifizéierung verlaangt',
            outputs: ['Certification dossier', 'Certificate', 'Re-issue and exchange record'],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'Juristesch / Compliance',
                '2': 'Juristesch / Compliance',
                '3': 'DPO / Privatsphärenteam',
                '4': 'Compliance-Team',
            },
            step_outputs: {
                '1': ['Certification application dossier'],
                '2': ['Submission confirmation'],
                '3': ['Supplement response'],
                '4': ['Certificate record', 'Re-issue and exchange record'],
            },
        },
        PDPL_P4: {
            trigger: "Empfank vun enger Demande iwwer d'Rechter vum Betraffenen",
            outputs: [
                'DSR intake record',
                'Identity verification',
                'Response letter',
                'Case closure report',
            ],
            channels: ['Online portal', 'Email', 'In-person'],
            responsible: {
                '1': 'Privatsphärenteam / Clientservice',
                '2': 'DPO / Privatsphärenteam',
                '3': 'Ofdeelungsverantwortlecher / IT',
                '4': 'DPO / Privatsphärenteam',
                '5': 'Compliance-Team',
            },
            step_outputs: {
                '1': ['DSR intake record', 'Identity verification record'],
                '2': ['Request assessment', 'Deadline assignment'],
                '3': ['Fulfillment evidence', 'Department confirmations'],
                '4': ['Response letter'],
                '5': ['Case closure report', 'Audit evidence pack'],
            },
        },
        PDPL_P5: {
            trigger: 'Entdeckung vun engem Verstouss oder Verstouss géint de Schutz vu perséinlechen Donnéeën',
            outputs: [
                'Breach assessment record',
                'Notification package',
                'Submission confirmation',
                'Supplement package',
            ],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'Reaktiounsteam fir Virfäll / DPO',
                '2': 'DPO / Privatsphärenteam',
                '3': 'Juristesch / Compliance',
                '4': 'DPO / Reaktiounsteam fir Virfäll',
            },
            step_outputs: {
                '1': ['Breach assessment record', 'Notification decision record'],
                '2': ['Breach notification package'],
                '3': ['Submission confirmation', 'Tracking reference'],
                '4': ['Supplement package', 'Coordination evidence'],
            },
        },
    },
    framework_guidance_references: {
        'Authority practice (unofficial) — email-ZIP submission of PDPL evidence packs during NQ22 era': 'Authority practice (unofficial) — email-ZIP submission of PDPL evidence packs during NQ22 era',
        'Cross-reference frameworks listed on VN_ECOMMERCE_DECREE_248_2026.': 'Cross-reference frameworks listed on VN_ECOMMERCE_DECREE_248_2026.',
        'Cross-reference frameworks on VN_DIGITAL_TECH_INDUSTRY_LAW_2025.': 'Cross-reference frameworks on VN_DIGITAL_TECH_INDUSTRY_LAW_2025.',
        'Cross-reference frameworks on VN_DIGITAL_TRANSFORMATION_LAW_2025.': 'Cross-reference frameworks on VN_DIGITAL_TRANSFORMATION_LAW_2025.',
        'Government/MOIT public reporting.': 'Government/MOIT public reporting.',
        'Government/MOIT public reporting; deadline VN_ECOM248_DEADLINE_TAKEDOWN_RESPONSE.': 'Government/MOIT public reporting; deadline VN_ECOM248_DEADLINE_TAKEDOWN_RESPONSE.',
        'MPS consultation dossier — draft information-systems protection decree.': 'MPS consultation dossier — draft information-systems protection decree.',
        'MPS consultation — draft general implementing decree.': 'MPS consultation — draft general implementing decree.',
        'Sector overlay install model (mirrors VN_BANKING_CIRCULAR_83_2025).': 'Sector overlay install model (mirrors VN_BANKING_CIRCULAR_83_2025).',
        'Shared draft enforcement overlay VN_CYBER_PDPL_ENFORCEMENT_DRAFT_2026 (home pack: vn-pdpl).': 'Shared draft enforcement overlay VN_CYBER_PDPL_ENFORCEMENT_DRAFT_2026 (home pack: vn-pdpl).',
    },
    framework_deadline_references: {
        'ComplianceOne internal review': 'ComplianceOne internal review',
        'ComplianceOne internal review date': 'ComplianceOne internal review date',
        'Draft personnel provisions': 'Draft personnel provisions',
        'Draft specialist provisions': 'Draft specialist provisions',
        'Internal child-instrument review.': 'Internal child-instrument review.',
        'Internal platform review.': 'Internal platform review.',
        'Internal recommendation based on draft lifecycle governance': 'Internal recommendation based on draft lifecycle governance',
        'Internal recommended digital-service procurement/leasing vendor review (before procurement/leasing and on material change).': 'Internal recommended digital-service procurement/leasing vendor review (before procurement/leasing and on material change).',
        'Internal recommended digital-transformation control review.': 'Internal recommended digital-transformation control review.',
        'Internal recommended review of digital-system minimum requirements (annual or on material change).': 'Internal recommended review of digital-system minimum requirements (annual or on material change).',
        'National digital technology industry database — ad-hoc update at authority request (where direct scope is confirmed).': 'National digital technology industry database — ad-hoc update at authority request (where direct scope is confirmed).',
        'National digital technology industry database — quarterly update (where direct scope is confirmed).': 'National digital technology industry database — quarterly update (where direct scope is confirmed).',
        'Official MPS consultation page': 'Official MPS consultation page',
        'Platform operational control — not a statutory deadline': 'Platform operational control — not a statutory deadline',
        'PDPL Art. 9': 'PDPL Art. 9',
        'PDPL Art. 17': 'PDPL Art. 17',
        'PDPL Art. 23': 'PDPL Art. 23',
        'Decree 13 Art. 24': 'Dekret 13 Art. 24',
        'MoPS Admin Procedures': 'Administrativ Prozeduren vum MoPS',
        'Annual cycle': 'Joreszyklus',
        'Decree 13/2023/NĐ-CP Art. 24': 'Dekret 13/2023/NĐ-CP Art. 24',
        'Decree 356/2025/NĐ-CP Art. 25(4)': 'Dekret 356/2025/NĐ-CP Art. 25(4)',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 2': 'Dekret 356/2025/NĐ-CP Art. 5 Abs. 2',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 2–5': 'Dekret 356/2025/NĐ-CP Art. 5 Abs. 2–5',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 3': 'Dekret 356/2025/NĐ-CP Art. 5 Abs. 3',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 4': 'Dekret 356/2025/NĐ-CP Art. 5 Abs. 4',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 5': 'Dekret 356/2025/NĐ-CP Art. 5 Abs. 5',
        'Decree 356/2025/NĐ-CP Art. 8(3)': 'Dekret 356/2025/NĐ-CP Art. 8(3)',
        'Draft Decree on Data Platforms 2026': 'Projet vun Dekret iwwer Datenplattformen 2026',
        'Law 122/2025/QH15 Ch. III; NĐ 98/2020; NĐ 24/2025': 'Gesetz 122/2025/QH15 Kap. III; NĐ 98/2020; NĐ 24/2025',
        'Law 122/2025/QH15 Ch. IV (Consumer Protection)': 'Gesetz 122/2025/QH15 Kap. IV (Konsumenteschutz)',
        'Law 122/2025/QH15 — Online.gov.vn operational procedure': 'Gesetz 122/2025/QH15 — operationell Prozedur Online.gov.vn',
        'Law 134/2025/QH15 Article 12': 'Gesetz 134/2025/QH15 Artikel 12',
        'Law 134/2025/QH15 Article 21': 'Gesetz 134/2025/QH15 Artikel 21',
        'Law 134/2025/QH15 Article 27': 'Gesetz 134/2025/QH15 Artikel 27',
        'Law 134/2025/QH15 Articles 9–10': 'Gesetz 134/2025/QH15 Artikelen 9–10',
        'Law 24/2023/QH15 — article mapping pending': 'Gesetz 24/2023/QH15 — Artikelmapping aussténeg',
        'Law 60/2024/QH15 (internal control target)': 'Gesetz 60/2024/QH15 (intern Kontrollziel)',
        'Law 91/2025/QH15 Art. 21': 'Gesetz 91/2025/QH15 Art. 21',
        'Law 91/2025/QH15 Art. 23': 'Gesetz 91/2025/QH15 Art. 23',
        'Policy-defined': 'Politikdefinéiert',
        Quarterly: 'Véierjäerlech',
        'QĐ 778/QĐ-BCA-A05 administrative procedure': 'Administrativ Prozedur QĐ 778/QĐ-BCA-A05',
        'internal control target (Law 116/2025/QH15 Art. 24(2)(b), 41(5) — exact timeline pending implementing instrument)': 'Intern Kontrollziel (Gesetz 116/2025/QH15 Art. 24(2)(b), 41(5) — präzis Frist a Erwaardung vum Ëmsetzungsinstrument)',
        'internal control target (Law 116/2025/QH15 Art. 41(3) — exact timeline pending implementing instrument)': 'Intern Kontrollziel (Gesetz 116/2025/QH15 Art. 41(3) — präzis Frist a Erwaardung vum Ëmsetzungsinstrument)',
        'source verification pending': 'Quellverifikatioun aussténeg',
        'Circular 83/2025/TT-NHNN — Ad-hoc Internal Audit Reporting to SBV (Layer 5)': "Circular 83/2025/TT-NHNN — Ad-hoc-Bericht vun der interner Auditatioun un d'SBV (Schicht 5)",
        'Circular 83/2025/TT-NHNN — Annual ICAAP Report (Phụ lục III) (Layer 5)': 'Circular 83/2025/TT-NHNN — Jäerleche ICAAP-Bericht (Phụ lục III) (Schicht 5)',
        'Circular 83/2025/TT-NHNN — Annual Phụ lục I (Layer 5)': 'Circular 83/2025/TT-NHNN — Jäerleche Phụ lục I (Schicht 5)',
        'Circular 83/2025/TT-NHNN — Annual Phụ lục IV (Layer 5)': 'Circular 83/2025/TT-NHNN — Jäerleche Phụ lục IV (Schicht 5)',
        'Circular 83/2025/TT-NHNN — Annual Risk Management Report (Phụ lục II) (Layer 5)': 'Circular 83/2025/TT-NHNN — Jäerleche Risikomanagementbericht (Phụ lục II) (Schicht 5)',
        'Circular 83/2025/TT-NHNN — Asset Management Responsibility (Layer 2, theme C)': "Circular 83/2025/TT-NHNN — Verantwortung fir d'Verméigensgestioun (Schicht 2, Theema C)",
        "Circular 83/2025/TT-NHNN — Board / Members' Council Oversight (Layer 2, theme A)": 'Circular 83/2025/TT-NHNN — Iwwerwaachung duerch Verwaltungsrot / Memberenrot (Schicht 2, Theema A)',
        'Circular 83/2025/TT-NHNN — Board Committees (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Verwaltungsrotskommissiounen (Schicht 2, Theema A)',
        'Circular 83/2025/TT-NHNN — Board Decisions and Minutes (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Verwaltungsrotsentscheedungen a Protokoller (Schicht 2, Theema A)',
        'Circular 83/2025/TT-NHNN — Branch Control Procedures (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Kontrollprozeduren fir Filialen (Schicht 2, Theema C)',
        'Circular 83/2025/TT-NHNN — Compliance Assessment of Internal Rules (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Konformitéitsbewäertung vun den interne Regelen (Schicht 2, Theema D)',
        'Circular 83/2025/TT-NHNN — Compliance Function (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Konformitéitsfunktioun (Schicht 2, Theema D)',
        'Circular 83/2025/TT-NHNN — Compliance Remediation Monitoring (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Iwwerwaachung vun der Konformitéitsverbesserung (Schicht 2, Theema D)',
        'Circular 83/2025/TT-NHNN — Concentration Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Konzentratiounsrisiko (Schicht 3, Theema E)',
        'Circular 83/2025/TT-NHNN — Conflict of Interest (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Interessekonflikt (Schicht 2, Theema C)',
        'Circular 83/2025/TT-NHNN — Control Culture (Layer 2, theme A/B)': 'Circular 83/2025/TT-NHNN — Kontrollkultur (Schicht 2, Theema A/B)',
        'Circular 83/2025/TT-NHNN — Credit Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Kredittrisiko (Schicht 3, Theema E)',
        'Circular 83/2025/TT-NHNN — Delegated Authority (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Delegéiert Autoritéit (Schicht 2, Theema C)',
        'Circular 83/2025/TT-NHNN — Early Implementation Notification (Layer 5)': 'Circular 83/2025/TT-NHNN — Notifikatioun vu fréizäiteger Ëmsetzung (Schicht 5)',
        'Circular 83/2025/TT-NHNN — General Director Responsibility (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Verantwortung vum Generaldirekter (Schicht 2, Theema A)',
        'Circular 83/2025/TT-NHNN — ICAAP (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — ICAAP (Schicht 3, Theema E)',
        'Circular 83/2025/TT-NHNN — Interest-Rate Risk in the Banking Book (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Zënssaazrisiko am Bankebuch (Schicht 3, Theema E)',
        'Circular 83/2025/TT-NHNN — Internal Audit Charter (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Charta vun der interner Auditatioun (Schicht 2, Theema H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Engagements (Theme H)': 'Circular 83/2025/TT-NHNN — Engagementer vun der interner Auditatioun (Theema H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Findings Communication (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Kommunikatioun vu Befunde vun der interner Auditatioun (Schicht 2, Theema H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Findings and Corrective Actions (Theme H)': 'Circular 83/2025/TT-NHNN — Befunde vun der interner Auditatioun a Korrekturmoossnamen (Theema H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Follow-Up (Theme H)': 'Circular 83/2025/TT-NHNN — Suivi vun der interner Auditatioun (Theema H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Governance': 'Circular 83/2025/TT-NHNN — Gouvernance vun der interner Auditatioun',
        'Circular 83/2025/TT-NHNN — Internal Audit Independence (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Onofhängegkeet vun der interner Auditatioun (Schicht 2, Theema H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Plan (Layer 2/Theme H)': 'Circular 83/2025/TT-NHNN — Plang vun der interner Auditatioun (Schicht 2/Theema H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Reporting to SBV (Layer 2, theme H)': "Circular 83/2025/TT-NHNN — Bericht vun der interner Auditatioun un d'SBV (Schicht 2, Theema H)",
        'Circular 83/2025/TT-NHNN — Internal Control System Governance (Layer 2)': 'Circular 83/2025/TT-NHNN — Gouvernance vum interne Kontrollsystem (Schicht 2)',
        'Circular 83/2025/TT-NHNN — Internal Reporting Procedure (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Intern Beriichtsprozedur (Schicht 2, Theema C)',
        'Circular 83/2025/TT-NHNN — Internal Reports Register (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Register vun internen Berichter (Schicht 4, Theema F)',
        'Circular 83/2025/TT-NHNN — Law-Change Monitoring (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Iwwerwaachung vu Gesetzesännerungen (Schicht 2, Theema D)',
        'Circular 83/2025/TT-NHNN — Legal and Reputational Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Juristesch a Reputatiounsrisiko (Schicht 3, Theema E)',
        'Circular 83/2025/TT-NHNN — Limit Breach Escalation (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Escaladéierung bei Iwwerschreidung vu Limitten (Schicht 3, Theema E)',
        'Circular 83/2025/TT-NHNN — Liquidity Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Liquiditéitsrisiko (Schicht 3, Theema E)',
        'Circular 83/2025/TT-NHNN — MIS Annual Review (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Jäerlech Iwwerpréiwung vum MIS (Schicht 4, Theema F)',
        'Circular 83/2025/TT-NHNN — MIS Backup and Continuity (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Sauvegarde a Kontinuitéit vum MIS (Schicht 4, Theema F)',
        'Circular 83/2025/TT-NHNN — MIS Escalation Mechanisms (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Escaladéierungsmechanismen vum MIS (Schicht 4, Theema F)',
        'Circular 83/2025/TT-NHNN — MIS Reliability and Confidentiality (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Zouverlässegkeet a Vertraulechkeet vum MIS (Schicht 4, Theema F)',
        'Circular 83/2025/TT-NHNN — MIS Risk Data Sources (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Risikodatequellen vum MIS (Schicht 4, Theema F)',
        'Circular 83/2025/TT-NHNN — Management Information System (Layer 4)': 'Circular 83/2025/TT-NHNN — Gestiounsinformatiounssystem (Schicht 4)',
        'Circular 83/2025/TT-NHNN — Market Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Maartrisiko (Schicht 3, Theema E)',
        'Circular 83/2025/TT-NHNN — Material Risk Register (Layer 3)': 'Circular 83/2025/TT-NHNN — Register vu materielle Risiken (Schicht 3)',
        'Circular 83/2025/TT-NHNN — Model Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Modellrisiko (Schicht 3, Theema E)',
        'Circular 83/2025/TT-NHNN — New Product Review (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Iwwerpréiwung vu neie Produkter (Schicht 3, Theema E)',
        'Circular 83/2025/TT-NHNN — Operational Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Operationelle Risiko (Schicht 3, Theema E)',
        'Circular 83/2025/TT-NHNN — Outsourcing BCP (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Geschäftskontinuitéitsplang fir Outsourcing (Schicht 2, Theema G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Contracts (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Outsourcingkontrakter (Schicht 2, Theema G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Controls': 'Circular 83/2025/TT-NHNN — Outsourcingkontrollen',
        'Circular 83/2025/TT-NHNN — Outsourcing Due Diligence (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Due Diligence vum Outsourcing (Schicht 2, Theema G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Fallback Provider (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Backup-Déngschtleeschter fir Outsourcing (Schicht 2, Theema G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Materiality (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Wesentlechkeet vum Outsourcing (Schicht 2, Theema G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Monitoring (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Iwwerwaachung vum Outsourcing (Schicht 2, Theema G)',
        'Circular 83/2025/TT-NHNN — Phụ lục V/VI Technical References (Layer 5)': 'Circular 83/2025/TT-NHNN — Technesch Referenzen Phụ lục V/VI (Schicht 5)',
        'Circular 83/2025/TT-NHNN — Policy Register (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Politikregister (Schicht 2, Theema C)',
        'Circular 83/2025/TT-NHNN — Professional Ethics (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Beruffsethik (Schicht 2, Theema C)',
        'Circular 83/2025/TT-NHNN — Risk Appetite (Layer 3)': 'Circular 83/2025/TT-NHNN — Risikoappetit (Schicht 3)',
        'Circular 83/2025/TT-NHNN — Risk Committee Coordination (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Koordinatioun vum Risikokomitee (Schicht 2, Theema E)',
        'Circular 83/2025/TT-NHNN — Risk Data Governance (Layer 3, theme E/F) — phased to 2028-01-01': 'Circular 83/2025/TT-NHNN — Gouvernance vu Risikodonnéeën (Schicht 3, Theema E/F) — gestaffelt op 2028-01-01',
        'Circular 83/2025/TT-NHNN — Risk Limit Review (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Iwwerpréiwung vu Risikolimitten (Schicht 3, Theema E)',
        'Circular 83/2025/TT-NHNN — Risk Limits Framework (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Kader fir Risikolimitten (Schicht 3, Theema E)',
        'Circular 83/2025/TT-NHNN — Risk Management Function (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Risikomanagementfunktioun (Schicht 2, Theema E)',
        'Circular 83/2025/TT-NHNN — Risk Methodology and Models (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Risikomethodologie a Modeller (Schicht 2, Theema E)',
        'Circular 83/2025/TT-NHNN — Risk Reporting (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Risikoberichterstattung (Schicht 2, Theema E)',
        'Circular 83/2025/TT-NHNN — Risk Reporting Cadence (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Frequenz vun der Risikoberichterstattung (Schicht 4, Theema F)',
        'Circular 83/2025/TT-NHNN — SBV Remediation Submission (Layer 5)': "Circular 83/2025/TT-NHNN — Aginn vu Verbesserungsmoossnamen un d'SBV (Schicht 5)",
        'Circular 83/2025/TT-NHNN — SBV Submission Channels (Layer 5)': 'Circular 83/2025/TT-NHNN — Aginnskanäl vun der SBV (Schicht 5)',
        'Circular 83/2025/TT-NHNN — Sector Applicability (Layer 1)': 'Circular 83/2025/TT-NHNN — Sektoriell Uwendbarkeet (Schicht 1)',
        'Circular 83/2025/TT-NHNN — Segregation of Duties (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Trennung vun de Funktiounen (Schicht 2, Theema C)',
        'Circular 83/2025/TT-NHNN — Senior Management Supervision (Layer 2, theme A)': "Circular 83/2025/TT-NHNN — Iwwerwaachung duerch d'Direktioun (Schicht 2, Theema A)",
        'Circular 83/2025/TT-NHNN — Strategic Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Strategesche Risiko (Schicht 3, Theema E)',
        'Circular 83/2025/TT-NHNN — Stress Testing (Layer 3, theme E) — phased to 2028-01-01': 'Circular 83/2025/TT-NHNN — Stresstester (Schicht 3, Theema E) — gestaffelt op 2028-01-01',
        'Circular 83/2025/TT-NHNN — Supervisory Board (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Iwwerwaachungsrot (Schicht 2, Theema A)',
        'Circular 83/2025/TT-NHNN — Supervisory Findings Register (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Register vun Iwwerwaachungsbefunde (Schicht 4, Theema F)',
        'Circular 83/2025/TT-NHNN — Three Lines Roles and Escalation (Layer 2, theme B)': 'Circular 83/2025/TT-NHNN — Rollen an Escaladéierung vun den Dräi Linnen (Schicht 2, Theema B)',
        'Circular 83/2025/TT-NHNN — Three Lines of Defence (Layer 2)': 'Circular 83/2025/TT-NHNN — Dräi Linne vu Verdeedegung (Schicht 2)',
        'Circular 83/2025/TT-NHNN — Violation Escalation (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Verstoussescaladéierung (Schicht 2, Theema D)',
        // Sprint 62.49.1 (E12.5 pass 1, P3) — consumer-rights and food-traceability references.
        'Law 19/2023/QH15 — Điều 31 khoản 2': 'Law 19/2023/QH15 — Điều 31 khoản 2',
        'Law 19/2023/QH15 and Decree 55/2024/ND-CP official Government portal metadata.': 'Law 19/2023/QH15 and Decree 55/2024/ND-CP official Government portal metadata.',
        'Circular 11/2026/TT-BCT — Điều 6 khoản 2': 'Circular 11/2026/TT-BCT — Điều 6 khoản 2',
        'Circular 11/2026/TT-BCT — Điều 12 khoản 2': 'Circular 11/2026/TT-BCT — Điều 12 khoản 2',
        'Circular 11/2026/TT-BCT — Điều 15 khoản 1': 'Circular 11/2026/TT-BCT — Điều 15 khoản 1',
        'Circular 11/2026/TT-BCT — Điều 15 khoản 2': 'Circular 11/2026/TT-BCT — Điều 15 khoản 2',
        'Circular 63/2026/TT-BCT — Điều 10 khoản 2': 'Circular 63/2026/TT-BCT — Điều 10 khoản 2',
        'Circular 63/2026/TT-BCT — Điều 10 khoản 3': 'Circular 63/2026/TT-BCT — Điều 10 khoản 3',
        'Circular 63/2026/TT-BCT — Điều 19 khoản 1': 'Circular 63/2026/TT-BCT — Điều 19 khoản 1',
    },
    framework_authorities: {
        'Quốc hội (National Assembly)': 'Quốc hội (Nationalversammlung)',
        'Ministry of Public Security / A05 Department': 'Ministère vun der Ëffentlecher Sécherheet / Departement A05',
        'Ministry of Public Security': 'Ministère vun der Ëffentlecher Sécherheet',
        'Ministry of Information and Communications': 'Ministère vun der Informatioun an de Kommunikatiounen',
        'Government of Vietnam': 'Regierung vu Vietnam',
        'European Data Protection Board (EDPB)': 'Europäeschen Datebschutzausschoss (EDPB)',
        'ENISA / National CSIRTs': 'ENISA / national CSIRTen',
        'State Bank of Vietnam': 'Staatsbank vu Vietnam',
    },
    tour_titles: {
        access_accountability: 'Zougang Rechenschaftspflicht Tour',
        assessments: 'Bewäertungen Tour',
        audit_trail: 'Audit Trail Tour',
        classification: 'Klassifikatioun Tour',
        compliance_forms: 'Compliance Forms Tour',
        connectors: 'Connectoren Tour',
        consent_properties: 'Zoustëmmung Properties Tour',
        consent: 'Zoustëmmung Tour',
        dashboard: 'Dashboard Tour',
        data_mapping: 'Donnéeën Mapping Tour',
        data_subjects: 'Tour vun de Betraffenen',
        deletion_orchestration: 'Läschen Orchestratioun Tour',
        discovery: 'Entdeckung Tour',
        governance: 'Gouvernance Tour',
        help_center: 'Hëllef Center Tour',
        incident_ops: 'Tëschefall Operatiounen Tour',
        incidents: 'Tëschefäll Tour',
        localization: 'Lokalisatioun Tour',
        monitoring: 'Iwwerwachung Tour',
        onboarding: 'Onboarding Tour',
        organizations: 'Organisatiounen Tour',
        partner_workspace: 'Partner Workspace Tour',
        platform_settings: 'Plattform Astellunge Tour',
        privacy_scanner: 'Privatsphär Scanner Tour',
        program_governance: 'Programm Gouvernance Tour',
        rights_requests: 'Rechter Demanden Tour',
        task_management: 'Aufgab Management Tour',
        vendor_risk: 'Verkeefer Risiko Tour',
    },
    tour_descriptions: {
        access_accountability: 'Als Internal Audit Lead (P-VN-10), iwwerpréift Zougangsbeweiser, Audit Erlaabnis Notzung, an ënnersicht anomal Mustere fir GDPR Artikel 5(1)(f) Integritéit a Vertraulechkeet ze weisen.',
        assessments: "Wéi d'Entreprise DPO (P-VN-01), féiert DPIAs no Dekret 356 Mau so 02a, identifizéieren Konformitéitslücken, a bewäerten grenziwwerschreidend Transferrisiken ënner PDPL.",
        audit_trail: 'Als Internal Audit Lead (P-VN-10), frot Cross-Modul Beweiser, montéiert Audit Packs, an exportéiert tamper-evident Logbicher fir reglementaresch Soumissioun.',
        classification: "Als Data Steward (P-VN-05), definéiert d'Dateklassifikatiouns-Taxonomien, gitt Sensibilitéitslabelen, ëmzesetzen Retentiounspolitik, an léist Ofdeckungslücken erfuerderlech fir PDPL Konformitéit.",
        compliance_forms: "Wéi d'Entreprise DPO (P-VN-01), duerchsichen, ausfëllen, a presentéieren offiziell vietnamesesch Reguléierungsformulairen abegraff Phu luc I, Phu luc II, a Mau sou Template fir PDP Komitee Areeche.",
        connectors: "Als IT System Lead (P-VN-05), erfaasst d'Geschäftsfuerderunge fir Integratiounen, map Connectoren op d'Konformitéitsdatenfloss, a verifizéiere POC Bereetschaft ier Dir live geet.",
        consent_properties: "Wéi d'Enterprise DPO (P-VN-01), konfiguréieren CMP Détachement: digital Propriétéit Aschreiwung, Golden Schablounen fir konsequent PDPL Zoustëmmung Banneren, a verbindlech Matrixentgasung datt Eegeschafte zu Veraarbechtung Aktivitéiten Link.",
        consent: 'Als Enterprise DPO (P-VN-01), verwalten den Zoustëmmungspolitik Liewenszyklus: definéieren Sammlungszwecker, ëmsetzen PDPL Réckzuchsrechter, an integréieren mat Ärer Zoustëmmung Management Plattform.',
        dashboard: "Als Enterprise DPO (P-VN-01), orientéiert Iech ronderëm d'Konformitéitspositiounskommandozentrum, déi PDPL Deadlinen opléist, SLAs verletzen, an Audit-Bereetschaft KPIs.",
        data_mapping: 'Als Data Steward (P-VN-05), baut Äre ROPA-Inventar, kartéiert grenziwwerschreidend Datefloss, an ënnerhält de Veraarbechtungsaktivitéitsregister erfuerdert vum PDPL.',
        data_subjects: "Als Entreprise-Datebschutzbeoptraagten oder Verantwortlechen fir d'Bildungskonformitéit, verwalt vereenegt Profiler vu Betraffenen, déi Zoustëmmungsregistratiounen, Rechterufroen an Zäitleeschtaktivitéit iwwer d'Plattform verbannen.",
        deletion_orchestration: "Als DPO (P-VN-01), z'iwwerpréiwen Enn-zu-Enn Datenläsche iwwer intern Systemer an Drëtt-Partei Ubidder, mat kryptografesche Beweisketten fir GDPR Artikel 17 Recht-ze-läsche Konformitéit.",
        discovery: 'Als Data Steward (P-VN-05), lafen automatiséiert Scans fir perséinlech Donnéeën iwwer verbonne Systemer ze entdecken, iwwerpréift inszenéiert Erkenntnisser a fiddert Resultater an de Klassifikatiounsmodul fir PDPL Etikettéierung.',
        governance: "Als Vendor Risk Manager (P-VN-08), behalen d'Verkeeferregistratioun, verfollegen Ënnerprozessorketten, bewäerten grenziwwerschreidend Transfert Risiken, an ëmsetzen Gouvernance Playbooks ënner PDPL.",
        help_center: "Fir all Personas - DPOs, Auditeuren, IT Leads, a Compliance Manager - fannt Äntwerten, How-to Guides, a Modulspezifesch Dokumentatioun fir Är alldeeglech Konformitéit Workflows z'ënnerstëtzen.",
        incident_ops: "Als Incident Commander (P-VN-09), aktivéiert Äntwert Playbooks, verfollegt SLA Konformitéit géint d'72-Stonn PDPL Notifikatiounsfenster, verwalten Eskalatioune a koordinéiert Verkeefer Äntwert.",
        incidents: 'Als Incident Commander (P-VN-09), entdeckt a mellt Dateverletzungen mat 72-Stonn PDPL Notifikatiounsverpflichtungen un de PDP Comité, Triage no Gravitéit, a bauen Audit-Grad Tëschefall Dossieren.',
        localization: 'Als Enterprise DPO (P-VN-01), verwalten de reglementaresche Kader Liewenszyklus: installéieren Vietnamesesch Maartpäck, aktivéieren PDPL an Dekret 13 Kaderen, Kaart Ufuerderunge fir Plattformmoduler, a verwalten Iwwersetzungen.',
        monitoring: 'Als Enterprise DPO (P-VN-01), etabléiert lafend Konformitéits Iwwerwaachungsprogrammer, verfollegt reglementaresch Ännerungen, moosst KPIs a verwalten reglementaresche Interaktiounsfäll ënner PDPL.',
        onboarding: 'Als Compliance Trainer (P-VN-02), liwwert Training iwwer guidéiert Touren, lued realistesch Probedaten fir praktesch Übungen, a lafen de Setup-Wizard fir Quick-Start Szenarien.',
        organizations: 'Als DPO (P-VN-01), konfiguréiert Multi-Organisatioun Konformitéitsstrukturen, etabléiert Departementhierarchien a router Datesubjekt Ufroen iwwer Äre Firmegrupp.',
        partner_workspace: "Als Partner Account Manager, onboard Partner Organisatiounen, konfiguréieren Serviceniveauen, verfollegen d'Konformitéit Liwwerungen, a verwalten de Partnermaart.",
        platform_settings: 'Als Plattform Administrator, verwalten all operationell Konfiguratioun - Identitéit, Sécherheet, Integratiounen, a Modulzougang déi Äre Konformitéitsprogramm ënnersträichen.',
        privacy_scanner: "Als Privatsphär Analyst (P-VN-06), lafen kontinuéierlech Iwwerwaachungsscans fir Cookie Zoustëmmung Violatioune, onerlaabten Tracker, a Politiklücken z'entdecken - generéiert Beweiser fir ePrivacy an GDPR Konformitéit.",
        program_governance: 'Navigéiert tëscht Maturitéitsmodeller, Bewäertungen, Beweislücken, Exekutiv Zesummefaassungen a méi. All Ënnermodul dréit zu enger holistescher Vue op Är intern Programmgesondheet bäi.',
        rights_requests: 'Als Enterprise DPO (P-VN-01), verwalten Datesubjekt Rechter Ufroe ënner PDPL mat 15-Deeg gesetzlecher Frist, Identitéitsverifizéierung an Audit-Grad Opléisungsspuren.',
        task_management: 'Gitt Konformitéitsaufgaben iwwer Personnagen, verfollegt SLA Frist, an automatiséiert Workflows déi Äert Team verantwortlech halen.',
        vendor_risk: 'Als Vendor Risk Manager (P-VN-08), score Drëtt Partei Risiko, ëmzesetzen Gate Bewäertungen, an Ausléiser nei Bewäertungen - ënnerscheet vu Programm Governance, déi Är intern Reife verfollegt.',
    },
    consolidation: {
        title: 'Konsolidierungsfaell',
        loading_cases: 'Loading consolidation cases',
        loading_case_detail: 'Loading case detail',
        new_case: 'Erstellt Konsolidéierungsfall',
        case_count: 'case',
        created: 'Konsolidéierungsfall erstallt',
        create: 'Schafen Fall',
        creating: 'Erstellt ...',
        cancel: 'Ofbriechen',
        no_cases: 'Keng Konsolidéierungsfäll fonnt.',
        all_statuses: 'All Status',
        all_purposes: 'All Zwecker',
        filter_status: 'Filter no Status',
        filter_purpose: 'Filter no Zweck',
        col_title: 'Titel',
        col_purpose: 'Zweck',
        col_status: 'Status',
        col_progress: 'Fortschrëtt',
        col_deadline: 'Frist',
        col_department: 'Departement',
        col_version: 'Versioun',
        col_evidence: 'Beweiser',
        sections_accepted: 'accepted',
        prev: 'Virdrun',
        next: 'Nächst',
        page: 'Säit',
        prev_page: 'Virdrun Säit',
        next_page: 'Nächst Säit',
        field_title: 'Titel',
        field_purpose: 'Zweck',
        field_framework: 'Kader Code',
        field_deadline: 'Frist',
        field_reviewer: 'Bewäertung Benotzer ID',
        title_placeholder: 'Gitt Fall Titel',
        framework_placeholder: 'z.B. PDPD-2023',
        reviewer_placeholder: 'Gitt Rezensor Benotzer ID',
        // fix-sprint-20260821-2140 F6 — structural parity only. R-57 defers real
        // translation of the 14 non-EN/VI locales to Phase 25 (sprint-57.7.0); these
        // land EN-identical and are counted there rather than repaired here.
        field_summary: 'Summary',
        field_details: 'Details',
        field_risk_assessment: 'Risk Assessment',
        field_mitigation_measures: 'Mitigation Measures',
        purposes: {
            dpia_filing: 'DPIA Areeche',
            cross_border_filing: 'Kräiz-Grenz Areeche',
            annual_governance_attestation: 'Annual Governance Attestation',
            breach_package: 'Breach Package',
            audit_inspection_pack: 'Audit Inspektioun Pack',
        },
        statuses: {
            draft: 'Entworf',
            collecting: 'Sammelen',
            consolidating: 'Konsolidéieren',
            review: 'Iwwerpréiwung',
            corrections: 'Korrekturen',
            pending_approval: 'Erwaardung Genehmegung',
            approved: 'Genehmegt',
            completed: 'Ofgeschloss',
            closed: 'Zougemaach',
            rejected: 'Refuséiert',
        },
        section_statuses: {
            pending: 'Erwaardung',
            in_progress: 'Am Fortschrëtt',
            submitted: 'Erlaabt',
            accepted: 'Akzeptéiert',
            needs_correction: 'Braucht Korrektur',
            corrected: 'Korrigéiert',
        },
        flag_types: {
            missing_section: 'Vermësst Sektioun',
            conflicting_entry: 'Konflikt Entrée',
            stale_entry: 'Stale Entrée',
            missing_evidence: 'Vermësst Beweiser',
            incomplete_data: 'Onkomplett Donnéeën',
            duplicate_entry: 'Duplizéiert Entrée',
        },
        flag_severities: {
            critical: 'Kritesch',
            warning: 'Warnung',
            info: 'Info',
        },
        sections_title: 'Sektiounen',
        no_sections: 'Keng Rubriken zougewisen nach.',
        flags_title: 'Fändelen',
        no_flags: 'Keng Fändelen fonnt.',
        open: 'open',
        approval_chain: 'Genehmegung Kette',
        pending: 'Erwaardung',
        audit_trail: 'Audit Trail',
        no_audit: 'Nee Audit Evenementer.',
        auto_assign: 'Auto-Assign Sektiounen',
        start_collection: 'Start Kollektioun',
        review_case: 'Iwwerpréiwen Fall',
        generate_export: 'Generéiere Export',
        review_title: 'Konsolidéierung Bewäertung',
        advance_approval: 'Viraus op Genehmegung',
        back_to_case: 'Zréck op de Fall',
        accept: 'Akzeptéieren',
        return_section: 'Retour',
        return_comment: 'Kommentéieren zréck ...',
        resolve: 'Opléisen',
        dismiss: 'Entlooss',
        resolution_notes: 'Resolutioun Notizen ...',
        notes: 'Notizen',
        section_accepted: 'Sektioun ugeholl',
        section_returned: 'Sektioun zréck fir Korrektur',
        advanced_to_approval: "De Fall ass fortgeschratt bis op d'Zustimmung",
        section_data: 'Sektioun Daten',
        save_progress: 'Fortschrëtter späicheren',
        submit_section: 'Sektioun ofginn',
        submitting: 'Ofschécken ...',
        resubmit: 'Nees ofginn',
        progress_saved: 'Fortschrëtter gespäichert',
        section_submitted: 'Sektioun presentéiert',
        correction_required: 'Korrektur erfuerderlech',
        evidence_title: 'Beweiser',
        no_evidence: 'Keng Beweiser verbonnen.',
        add_evidence: 'Füügt Beweiser',
        evidence_added: 'Beweis dobäi',
        evidence_files: 'Beweisdateien',
        uploaded_by: 'eropgelueden vun',
        comments_title: 'Kommentaren',
        no_comments: 'Nee Kommentaren nach.',
        comment_placeholder: 'Füügt e Kommentar ...',
        add_comment: 'Kommentéieren dobäi',
        comment_added: 'Kommentéieren dobäi',
        view_data: 'View presentéiert Donnéeën',
        export_title: 'Dossier Export',
        export_generated: 'Export Package generéiert',
        regenerate: 'Regeneréieren',
        regenerating: 'Regeneréieren ...',
        generated_at: 'Generéiert am',
        artifacts_title: 'Export Artefakter',
        artifact_type: 'Artefakt',
        file_name: 'Dateinumm',
        file_size: 'Gréisst',
        generated: 'Generéiert',
        download: 'Download',
        artifacts: {
            master_report: 'Master Rapport',
            evidence_bundle: 'Evidence Bundle (ZIP)',
            contributor_lineage: 'Contributor Lineage',
            conflict_resolution: 'Konflikt Léisung',
            approval_history: 'Genehmegung Geschicht',
        },
        official_forms: 'Offiziell Formen',
        forms_description: 'Déi folgend offiziell Forme kënnen aus de konsolidéierten Donnéeën generéiert ginn. Offiziell Forme si getrennt vun internen Aarbechtspabeieren.',
        announce_loaded_cases: 'Lueden {count} Konsolidéierungsfäll',
        announce_case_detail_loaded: 'Fall Detail gelueden',
        announce_case_transitioned: 'Fall iwwergaangen op {status}',
        announce_flag_resolved: 'Fändel geléist',
        announce_flag_dismissed: 'Fändel entlooss',
        case_detail_loaded: 'Fall Detail gelueden',
        sections_auto_assigned: 'Sektiounen automatesch aus Departement Hierarchie zougewisen',
        errors: {
            load_failed: 'Ausgefall Daten ze lueden. Probéiert w.e.g. nach eng Kéier.',
            create_failed: 'Ausgefall Fall ze schafen. Probéiert w.e.g. nach eng Kéier.',
            required_fields: 'Fëllt w.e.g. all erfuerderlech Felder aus.',
            not_found: 'Net fonnt.',
            comment_required: 'Kommentar ass erfuerderlech wann Dir eng Sektioun zréckschéckt.',
            notes_required: 'Notizen sinn erfuerderlech wann Dir e Fändel léist oder entlooss.',
        },
    },
    rights_requests: {
        import: {
            title: 'Fäll importéieren',
            nav_title: 'Massenimport',
            description: "Luet eng CSV-Datei erop fir Rechtsufro-Fäll am Grupp z'importéieren.",
            select_file: 'CSV-Datei auswielen',
            file_help: 'CSV mat Iwwerschrëften déi mat de Fallfelder iwweranenee stëmmen.',
            column_count_help: 'A row whose number of values differs from the number of columns in the header is not imported; it is listed under Errors with its row number.',
            // fix-sprint #431 — was hard-coded English on the Import page.
            required_columns: 'Required columns:',
            file_too_large: 'Fichier ze grouss. Maximal Gréisst ass 2MB.',
            preview: 'Virschau',
            columns_detected: 'Erkannte Spalten: {count}',
            column_mapping: 'Spalten-Zuweisung',
            import_button: 'Importéieren',
            import_complete: 'Import ofgeschloss',
            import_error: 'Import feelgeschloen',
            result: 'Importresultat',
            total: 'Gesamt',
            imported: 'Importéiert',
            skipped: 'Iwwersprongen',
            failed: 'Feelgeschloen',
            errors: 'Feeler',
            error_row: 'Zeil',
            error_field: 'Feld',
            error_message: 'Noriicht',
            history: 'Importverlaf',
            no_history: 'Keen Importverlaf.',
            history_file: 'Dateinumm',
            history_date: 'Datum',
        },
        batch: {
            nav_title: 'Batch Prozess',
            page_title: 'DSR Batch Prozess',
            page_subtitle: "Eroplueden e CSV vun waarden DSRs. Forseti entworf eng Per-Ufro Unerkennung Äntwert a Schlaangen eng Verifikatioun Aufgab pro Zeil. D'DPO Bewäertungen + akzeptéiert jidderee virum schéckt.",
            upload_heading: 'Eroplueden',
            csv_hint: 'Néideg Kolonnen: request_id, requester_email, request_type. Optional: Frist (ISO-8601), Notizen.',
            rows_heading: '{count} Reihen parséiert',
            submit_label: 'Batch lafen',
            submitting: 'Entworf…',
            col_request_id: 'Ufro ID',
            col_email: 'Ufro Email',
            col_type: 'Typ',
            col_status: 'Status',
            col_actions: 'Aktiounen',
            status_queued: 'Queue',
            status_drafted: 'Entworf',
            status_failed: 'Ausgefall',
            status_accepted: 'Akzeptéiert',
            status_rejected: 'Refuséiert',
            status_parsing_error: 'Parse Feeler',
            templates: {
                access: 'Acknowledging your data subject access request. We will respond by {deadline} with a copy of the personal data we process about you, the purposes and the recipients, per [{frameworks}]. Please reply with any clarifying detail; otherwise we will proceed with full disclosure.',
                deletion: 'Acknowledging your erasure request under [{frameworks}]. We will identify the records in scope, apply any retention that still applies (a legal hold, a contractual obligation) and confirm completion by {deadline}.',
                correction: 'Acknowledging your correction request under [{frameworks}]. Please reply with the field or fields you want corrected and the new values; we will route the request to the relevant business unit and confirm by {deadline}.',
                objection: 'Acknowledging your objection to processing under [{frameworks}]. We will pause the relevant processing while we review your reasons and respond by {deadline}.',
                portability: 'Acknowledging your data portability request under [{frameworks}]. We will export your data in scope in a structured, commonly used, machine-readable format and confirm by {deadline}.',
                restriction: 'Acknowledging your restriction request under [{frameworks}]. We will mark the relevant records as restricted and respond by {deadline}.',
                consent_withdrawal: 'Acknowledging your withdrawal of consent under [{frameworks}]. Future processing that relies solely on the withdrawn consent will stop; processing on another basis (a legal obligation, a contract) continues unless you object to it separately. We will confirm by {deadline}.',
                protective_measures: 'Acknowledging your request for personal data protection measures and solutions under [{frameworks}]. We will assess and implement the applicable safeguards and confirm by {deadline}.',
                processing_information: 'Acknowledging your request to be informed about the processing of your personal data under [{frameworks}]. We will tell you what personal data we process about you, why, and who receives it, and respond by {deadline}.',
                consent_decision: 'Acknowledging your decision on consent to the processing of your personal data under [{frameworks}]. We will record it and apply it to the processing that relies on your consent, and confirm by {deadline}.',
                deadline_fallback: 'the deadline set by the law that applies to your request',
            },
            errors: {
                csv_empty: 'The CSV file is empty.',
                csv_missing_header: 'The CSV file is missing the required column: {header}.',
                row_missing_field: 'A required field is missing (request_id, requester_email or request_type).',
                row_unknown_type: 'Unknown request type: {type}.',
                read_failed: 'The CSV file could not be read.',
                run_failed: 'The batch could not be run.',
                decision_failed: 'The decision could not be saved.',
            },
            synthesis_statuses: {
                model: 'Drafted by the AI model',
                template_parse_failed: 'Standard template: the AI model returned no usable text',
                template_provider_failed: 'Standard template: the AI model call failed',
                template_not_configured: 'Standard template: no AI model is configured',
                template_by_design: 'Standard template: this workflow does not use the AI model for this field',
            },
            csv_picker_label: 'CSV file',
            draft_source: 'Draft source',
            draft_language: 'Draft language: {locale}',
            accept: 'Akzeptéieren',
            reject: 'Refuséieren',
            permission_denied: 'You need both the case import and Forseti chat permissions to run a batch.',
            col_draft: 'Drafted response',
            view_draft: 'View drafted response',
            frameworks_cited: 'Frameworks cited',
        },
    },
    assessment_triggers: {
        title: 'Bewäertung Ausléiser',
        subtitle: 'Konfiguréiert automatiséiert Bewäertungskreatioun an DPO Notifikatiounen fir Privatsphär Workflows.',
        auto_create_dpia_label: 'Automatesch erstellen DPIA wann Veraarbechtungsaktivitéit markéiert als DPIA erfuerderlech',
        auto_notify_new_purpose_label: 'Automatesch Notifikatioun DPO wann nei Zweck erstallt mat sensiblen Datekategorien',
        auto_notify_cross_border_label: 'Auto Notifikatioun DPO wann grenziwwerschreidend Transfert der Veraarbechtungsaktivitéit bäigefüügt',
        default_dpo_label: 'Standard DPO',
        default_dpo_placeholder: 'Wielt Standard DPO...',
        notification_channels_label: 'Notifikatioun Channels',
        channel_in_app: 'An-App',
        channel_email: 'Email',
        save_success: 'Bewäertung Ausléiser Astellunge gespäichert.',
        save_error: "Ausgefall fir d'Ausléiser-Astellunge vun der Bewäertung ze späicheren.",
        fetch_error: 'Ausgefall Bewäertung Ausléiser Astellungen ze lued.',
        fetch_users_error: 'Ausgefall Benotzer ze lueden.',
        announce_loaded: 'Bewäertung Ausléiser Astellunge gelueden',
        dpia_status: 'DPIA Status',
        dpia_linked: 'DPIA Verlinkt',
        dpia_required_no_dpia: 'DPIA erfuerderlech - Net erstallt',
        create_dpia: 'Erstellt DPIA',
        create_dpia_success: 'DPIA erstallt a verbonne mat Aktivitéit.',
        create_dpia_error: 'Ausgefall fir DPIA fir Aktivitéit ze kreéieren.',
        view_dpia: 'Kuckt DPIA',
        dpo_warning_title: 'The default DPO is not an active user',
        dpo_warning_body: 'The default DPO is no longer an active user of this organisation. New DPIAs are created with no reviewer named, so any user who can approve DPIAs may decide them. Choose an active user as the default DPO.',
        auto_notify_cross_border_help: 'When a processing activity is saved with a cross-border destination it did not list before, the default DPO is notified on the channels below. With no default DPO, the Compliance Owners are notified.',
        auto_notify_processor_change_label: 'Raise a DPIA reassessment when a processor changes',
        auto_notify_processor_change_help: "A change to a vendor, a sub-processor or a processing activity's processor details raises one reassessment task, with a checklist of the evidence to re-check, for each DPIA that names that vendor or activity. A DPIA whose previous reassessment task is still open does not get a second one.",
        notification_channels_help: "Where the cross-border notification is sent. Each recipient's own notification preferences still apply.",
    },
    data_subjects: {
        title: 'Profiler vun betraffene Persounen',
        nav_title: 'Betraffene Persounen',
        nav: {
            profiles: 'Profiler',
        },
        create: 'Profil erstellen',
        edit: 'Profil beaarbechten',
        delete: 'Profil läschen',
        delete_confirm: 'Sidd Dir sécher, datt Dir dësen Dateschutzprofil läschen wëllt?',
        display_name: 'Affichage Numm',
        email: 'Email',
        phone: 'Telefon',
        employee_id: 'Employé ID',
        student_id: 'Student ID',
        notes: 'Notizen',
        external_refs: 'Extern Referenzen',
        cases_count: 'Fäll',
        workflows_count: 'Zoustëmmung Records',
        last_activity: 'Déi lescht Aktivitéit',
        timeline: 'Timeline',
        link_case: 'Link Case',
        link_workflow: 'Link Workflow',
        no_profiles: 'Nee daten Sujet Profiler fonnt.',
        search_placeholder: 'Sich no Numm, E-Mail, Telefon oder ID ...',
        create_success: 'Date Sujet Profil erstallt.',
        create_error: 'Ausgefall fir Datesubjektprofil ze kreéieren.',
        update_success: 'Date Sujet Profil aktualiséiert.',
        update_error: 'Konnt net aktualiséieren Datesubjektprofil.',
        delete_success: 'Date Sujet Profil geläscht.',
        delete_error: 'Ausgefall fir Datesubjektprofil ze läschen.',
        link_case_success: 'Fall verbonne mat Profil.',
        link_case_error: 'Ausgefall Fall ze Profil ze verbannen.',
        link_workflow_success: 'Workflow verbonne mam Profil.',
        link_workflow_error: 'Konnt de Workflow mam Profil net verbannen.',
        load_error: 'Ausgefall fir Datesubjektprofiler ze lueden.',
        detail_load_error: 'Konnt den Datesubjektprofil net lueden.',
        timeline_load_error: 'Ausgefall Zäitlinn ze lueden.',
        tab_cases: 'DSR Fäll',
        tab_consent: 'Zoustëmmung Records',
        tab_timeline: 'Timeline',
        tab_consent_workflows: 'Zoustëmmung Workflows',
        tab_web_consent: 'CMP Zoustëmmung',
        web_consent_empty: 'Keng CMP Zoustëmmungsrecords fir dëst Thema fonnt.',
        web_consent_pagination: 'CMP consent record pages',
        web_consent_actions: {
            grant: 'Granted',
            revoke: 'Revoked',
            update: 'Updated',
            reject_all: 'Rejected all',
            accept_all: 'Accepted all',
        },
        web_consent_load_error: 'Ausgefall CMP Zoustëmmung records ze lued.',
        col_policy_version: 'Politik Versioun',
        col_versions: 'Versiounen',
        col_consent_version: 'Zoustëmmung',
        col_cookie_declaration_version: 'Cookie Deklaratioun',
        col_purposes: 'Zwecker',
        purpose_granted: 'Gewëss',
        purpose_denied: 'Refuséiert',
        granted_domains: 'Gewëss',
        denied_domains: 'Refuséiert',
        col_action: 'Aktioun',
        col_timestamp: 'Zäitstempel',
        col_domain: 'Domain',
        no_cases: 'Keng verbonne Fäll.',
        no_workflows: 'Keng verbonne Zoustëmmung Workflows.',
        no_timeline: 'Keng Timeline Evenementer.',
        case_id: 'Fall ID',
        case_status: 'Status',
        case_type: 'Ufro Typ',
        case_created: 'Geschaf',
        workflow_id: 'Workflow ID',
        workflow_type: 'Typ',
        workflow_status: 'Status',
        workflow_created: 'Geschaf',
        timeline_date: 'Datum',
        timeline_event: 'Event',
        timeline_details: 'Detailer',
        timeline_time: 'Zäit',
        timeline_type: 'Typ',
        timeline_summary: 'Resumé',
        type_case: 'DSR Fall',
        type_consent_workflow: 'Zoustëmmung Workflow',
        profile_info: 'Profil Informatiounen',
        announce_loaded: 'Date Sujet Profiler gelueden',
        announce_detail_loaded: 'Date Sujet Profil gelueden',
        select_case: '— Wielt e Fall —',
        select_workflow: '— Wielt e Workflow —',
        no_cases_hint: 'Verknäppung Fäll fir si hei ze gesinn.',
        no_workflows_hint: 'Verknäppung Zousteemmungs-Workflows fir si hei ze gesinn.',
        case_options_read_subject: 'the cases offered for linking',
        workflow_options_read_subject: 'the consent workflows offered for linking',
        external_identifiers: 'External identifiers',
        external_identifiers_desc: 'The identifiers the installed record kinds declare for a data subject. An erasure request is checked against the records issued under them.',
        external_identifiers_none_declared: 'No installed record kind declares an external identifier for a data subject.',
        external_identifiers_load_error: 'The external identifier fields could not be read.',
        external_identifiers_edit_unavailable: 'The external identifier fields could not be read, so they are not changed by this save.',
        external_identifier_hint: 'Leave blank to remove it.',
        external_ref_conflicts_heading: 'Identifier conflicts',
        external_ref_conflict: 'On {date}, a {kind} record was created for this profile with a different {label} from the one recorded here. The recorded value was kept; check which is correct.',
        external_ref_conflict_unlabelled: 'On {date}, an issued record was created for this profile with a different {label} from the one recorded here. The recorded value was kept; check which is correct.',
        external_ref_conflict_open_record: 'Open the issued record',
        profile_stale: 'This profile was changed after you opened it, so your change was not saved. The profile has been reloaded; make your change again.',
        identifier_produced_by_record: 'The {label} was recorded by an issued record, so it cannot be removed or changed here.',
    },
    flows: {
        map: {
            description_prefix: '{systems} Systemer · {flows} fléissen · {crossBorder} grenziwwerschreidend · {highRisk} héich/kritesch Risiko',
            switch_to_list: "Wiesselt op d'Lëscht Vue",
            switch_to_list_aria: 'Wiesselen op Dësch Vue',
            svg_aria: 'Dateflusskaart: {systems} Systemer, {flows} Flëss, {crossBorder} grenziwwerschreidend',
            selected_system: 'Ausgewielt System',
            drill_hint: "Öffnen d'Lëschttab fir déi verlinkte Fluxen, DPIAen a Verkeefer Uerderungen fir dëse System z'änneren.",
        },
    },
    packages: {
        light: {
            name: 'Liicht',
            description: 'Wesentlech Privacy-Compliance-Tools fir kleng Equipen',
        },
        standard: {
            name: 'Standard',
            description: 'Vollstänneg ausgestatte Compliance-Plattform fir wuessend Organisatiounen',
        },
        enterprise_unlimited: {
            name: 'Enterprise Onlimitéiert',
            description: 'Compliance op Enterprise-Niveau mat Multi-Org-Ënnerstëtzung an dedizéierten Servicer',
        },
    },
    forseti: {
        // Sprint 63.32.0 (Q1, #543) — the record summary's facts, rendered on screen;
        // the model prompt keeps the English summary.
        summary_fields: {
            accepted_until: 'Accepted until',
            active_timers: 'Active timers',
            actual_release: 'Actual release',
            ad_hoc_answered: 'Ad-hoc reports answered',
            ad_hoc_report_entries: 'Ad-hoc report entries',
            affected_systems: 'Affected systems',
            amended_on: 'Last amended',
            amendments: 'Amendments',
            answered_on: 'Answered',
            approval_mode: 'Approval mode',
            approved: 'Approved',
            approved_on: 'Approved',
            approved_risk_tier: 'Approved risk tier',
            artifact_kind: 'Artefact kind',
            authorisation_kind: 'Authorisation kind',
            authority: 'Authority',
            authority_reference_recorded: 'Authority reference recorded',
            authority_request: 'Authority request',
            authority_requests: 'Authority requests',
            batches: 'Batches',
            breach_notification: 'Breach notification',
            categories: 'Category tags',
            channel: 'Channel',
            claimant: 'Claimant',
            classification: 'Classification',
            classifications: 'Classifications',
            closed_on: 'Closed',
            closure_approved_on: 'Closure approved',
            completed_on: 'Completed',
            conformity_records: 'Conformity records',
            conformity_route: 'Conformity route',
            consent_cover_state: 'Consent cover',
            consent_evidence_referenced: 'Consent evidence referenced',
            corrective_measures: 'Corrective measures',
            country: 'Country',
            covered_services: 'Covered services',
            credentials: 'Credentials',
            cross_border_transfers: 'Cross-border transfers',
            cross_mapping_recorded_on: 'Cross-mapping recorded',
            cryptographic_assets: 'Cryptographic assets',
            current_version: 'Current version',
            data_categories: 'Data categories',
            data_element: 'Data element',
            data_tier: 'Data tier',
            dataset: 'Dataset',
            deadline: 'Deadline',
            decided_on: 'Decided',
            declared_on: 'Declared',
            declared_quantity: 'Declared quantity',
            deployment_context: 'Deployment',
            deposit_submission_recorded: 'Deposit submission recorded',
            description_recorded: 'Description recorded',
            destination_country: 'Destination country',
            details_recorded: 'Details recorded',
            dictionary_version: 'Dictionary version',
            document_type: 'Document type',
            documents: 'Documents',
            domains_evaluated: 'Domains evaluated',
            dpa_status: 'DPA status',
            dpo_assigned: 'DPO assigned',
            due: 'Due',
            effective_on: 'Effective',
            evidence: 'Evidence references',
            evidence_attachments: 'Evidence attachments',
            evidence_pack_linked: 'Evidence pack linked',
            expires_on: 'Expires',
            filing_linked: 'Filing record linked',
            filing_type: 'Filing type',
            financial_crime_signed: 'Financial-crime function signed',
            fine_areas: 'Fine areas cited',
            form_type: 'Form',
            format: 'Format',
            framework: 'Framework',
            gate_kind: 'Deposit gate kind',
            generated_tasks: 'Generated tasks',
            identity_linked: 'Linked to an identity record',
            identity_verification: 'Identity verification',
            incident: 'Incident',
            incidents: 'Incidents',
            intake_evidence: 'Intake evidence',
            intake_kind: 'Intake kind',
            issued_on: 'Issued',
            jurisdiction: 'Jurisdiction',
            kind: 'Kind',
            kind_declared_by_pack: 'Kind declared by an installed pack',
            legal_basis: 'Legal basis',
            legal_review_status: 'Legal review',
            legal_reviews: 'Legal reviews',
            licence_kind: 'Licence kind',
            lifecycle_events: 'Lifecycle events',
            lifecycle_stage: 'Lifecycle stage',
            linked_activities: 'Linked activities',
            linked_authority_requests: 'Linked authority requests',
            linked_dpia: 'Linked DPIA',
            linked_frameworks: 'Linked frameworks',
            location: 'Location',
            lodged_by_role: 'Lodged by',
            lodged_on: 'Lodged',
            model_version: 'Model version',
            name: 'Name',
            notification_entries: 'Notification entries',
            notify_decision: 'Notification decision',
            offerings: 'Named offerings',
            onboarding_status: 'Onboarding',
            open_findings: 'Open findings',
            ot_asset: 'OT asset',
            outcome_reports: 'Outcome reports',
            overall_score: 'Overall score',
            parcel: 'Parcel',
            period_end: 'Period end',
            period_start: 'Period start',
            periodic_report_entries: 'Periodic report entries',
            personal_data: 'Personal data',
            phases_completed: 'Phases completed',
            phases_total: 'Phases',
            planned_release: 'Planned release',
            playbook_version: 'Playbook version',
            pre_existing_on_commencement: 'Pre-existing on commencement',
            priority: 'Priority',
            privacy_signed: 'Privacy function signed',
            procedure_reference: 'Procedure reference',
            product: 'Product',
            products: 'Products',
            proof_attachments: 'Proof attachments',
            provider: 'Provider',
            provider_type: 'Provider type',
            publication_evidence: 'Publication evidence',
            published_on: 'Published',
            purpose_recorded: 'Purpose recorded',
            purposes: 'Purposes recorded',
            questions_answered: 'Questions answered',
            questions_total: 'Questions',
            raised_on: 'Raised',
            rationale_recorded: 'Rationale recorded',
            readiness_record: 'Readiness record',
            recall_scope: 'Recall scope',
            receipt_recorded: 'Receipt recorded',
            received_on: 'Received',
            recipient: 'Recipient',
            recipient_class: 'Recipient class',
            reclassification_changed_fields: 'Inputs changed since the approved run',
            reclassification_review: 'Reclassification prompt review',
            reclassification_signal_since: 'Reclassification signal since',
            reconciliation_exceptions: 'Reconciliation exceptions',
            record_kind: 'Record kind',
            recorded_on: 'Recorded',
            reference: 'Reference',
            releasable_marked_on: 'Marked releasable',
            relied_on_events: 'Parcel events relied on',
            remediation_plans: 'Remediation plans',
            remediation_tasks: 'Remediation tasks raised',
            renewal_of_earlier: 'Renewal of an earlier credential',
            replaces: 'Replaces',
            reporting_years: 'Reporting years',
            representative_consents: 'Representative consent records',
            request_type: 'Request type',
            requester_recorded: 'Requester on record',
            responses: 'Responses',
            responsible_person_recorded: 'Responsible person recorded',
            retention_duty: 'Retention duty',
            review_due: 'Review due',
            revoked_on: 'Revoked',
            rights_evidence: 'Rights evidence entries',
            risk_level: 'Risk level',
            root_cause_recorded: 'Root cause recorded',
            rule_type: 'Rule type',
            sellers: 'Sellers',
            serial_reference: 'Serial reference',
            service_type: 'Service type',
            sharing_events: 'Sharing events',
            sharing_refusals: 'Refused sharing attempts',
            shipment: 'Shipment',
            sla_state: 'Deadline clock',
            source_country: 'Source country',
            stage: 'Stage',
            state: 'State',
            status: 'Status',
            sub_status: 'Sub-status',
            subject: 'Subject',
            subject_kind: 'Subject kind',
            submission_kind: 'Submission kind',
            submitted_on: 'Submitted',
            successor_recorded: 'Successor credential recorded',
            supplied_on: 'Supplied',
            takedown_reason_recorded: 'Takedown reason recorded',
            taken_down_on: 'Taken down',
            target_date: 'Target date',
            technical_actions: 'Technical actions',
            technical_regulation: 'Technical regulation',
            title: 'Title',
            transfer_assessment_referenced: 'Transfer assessment referenced',
            transfer_mechanism: 'Transfer mechanism',
            transfer_review_state: 'Transfer review',
            transfer_source_recorded: 'Transfer source institution recorded',
            valid_from: 'Valid from',
            valid_until: 'Valid until',
            vendor: 'Vendor',
            vendor_type: 'Vendor type',
            version: 'Version',
            versions: 'Versions recorded',
            withdrawal_determination_recorded: 'Withdrawal determination recorded',
        },
        summary_codes: {
            acknowledged: 'Acknowledged',
            active: 'Active',
            all: 'All approvers',
            any: 'Any approver',
            approved: 'Approved',
            archived: 'Archived',
            blocked: 'Blocked',
            breached: 'Breached',
            cancelled: 'Cancelled',
            closed: 'Closed',
            completed: 'Completed',
            critical: 'Critical',
            declared: 'Declared',
            do_not_notify: 'Do not notify',
            draft: 'Draft',
            escalated: 'Escalated',
            expired: 'Expired',
            filed: 'Filed',
            high: 'High',
            in_progress: 'In progress',
            in_review: 'In review',
            inactive: 'Inactive',
            known_data_subject: 'A known data subject',
            labelled_claimant: 'A labelled claimant',
            lodged: 'Lodged',
            low: 'Low',
            medium: 'Medium',
            new: 'New',
            not_started: 'Not started',
            notify: 'Notify',
            open: 'Open',
            overdue: 'Overdue',
            paused: 'Paused',
            pending: 'Pending',
            personal_data: 'Personal data',
            published: 'Published',
            received: 'Received',
            rejected: 'Rejected',
            resolved: 'Resolved',
            revoked: 'Revoked',
            signed: 'Signed',
            special_category: 'Special-category personal data',
            submitted: 'Submitted',
            suspended: 'Suspended',
            taken_down: 'Taken down',
            unclassified: 'Unclassified',
            undecided: 'Undecided',
            under_review: 'Under review',
            unverified: 'Unverified',
            verified: 'Verified',
            withdrawn: 'Withdrawn',
        },
        summary_flag_yes: 'Yes',
        summary_flag_no: 'No',
        summary_not_recorded: 'Not recorded',
        summary_fields_label: 'Record facts',
        providerSettings: {
            title: 'Forseti AI Provider Astellunge',
            subtitle: 'Configuréieren de Claude Provider fir de Forseti AI Legal Advisor.',
            healthHeading: 'Provider Gesondheet',
            statusLabel: 'Status',
            statusOk: 'OK',
            statusFailed: 'Net verfügbar',
            providerLabel: 'Provider',
            providerClaudeApi: 'Claude API',
            providerCliRelay: 'Relais Claude CLI',
            providerAesirManaged: 'Aesir-verwalte Tokens',
            lastCheckedLabel: 'Lescht gepréift',
            reasonLabel: 'Grond',
            refreshButton: 'Erfrëschen',
            apiKeyHeading: 'Claude API Schlëssel',
            apiKeyHint: 'Astellung vun engem API Schlëssel aktivéiert de Claude API Modus. Loosst net agesat fir de lokalen CLI Relais ze benotzen (Entwéckler / Self-Host Modus).',
            apiKeyLabel: 'API Schlëssel',
            apiKeyMinLength: 'Minimum 32 Zeechen. Verschlësselte am Rescht; ni méi gewisen.',
            apiKeyTooShort: "API Schlëssel muss op d'mannst 32 Zeechen sinn.",
            apiKeySaved: 'API Schlëssel gespäichert. Erfrëschend Gesondheet…',
            apiKeyCleared: 'API Schlëssel geläscht. Forseti benotzt elo de lokale CLI Relais.',
            setKeyButton: 'Späicheren API Schlëssel',
            clearKeyButton: 'Clear API Schlëssel (benotzt CLI Relais)',
        },
        // Owner ruling 2026-10-10 — Forseti → AI Model (provider, model, effort).
        aiModel: {
            title: 'AI Model',
            sidebarLabel: 'AI Model',
            subtitle: 'Choose the AI provider, model and effort behind every Forseti request. Your organisation connects its own provider account with its own API key, and that provider bills you directly for what Forseti uses.',
            loadError: 'The AI model settings could not be read.',
            retrySubject: 'the AI model settings',
            generalHeading: 'General',
            generalDescription: 'The active provider answers every Forseti chat, workflow, tabular review and redline. Only a provider with a saved API key can be made active.',
            activeProviderLabel: 'Active provider',
            activeProviderHint: 'A provider without a saved API key cannot be selected. Save its key in its card below first.',
            providerNoKey: '{provider} (no API key saved)',
            thinkingLabel: 'Show the reasoning summary in chat',
            thinkingHint: 'When the model returns a summary of its reasoning, Forseti shows it above the answer in interactive chat. Workflows and reviews are not affected.',
            relayProviderLabel: 'Local CLI relay (development)',
            relayUrlLabel: 'Relay address',
            relayUrlHint: 'The address of the local CLI relay. Used only in development and self-hosted installations.',
            saveGeneral: 'Save general settings',
            generalSaved: 'General settings saved.',
            providersHeading: 'Providers',
            providersDescription: 'Each provider keeps its own API key, model and effort. Saving a provider does not make it active unless you tick Make this the active provider.',
            cardDescription: "Models from {provider}, called with your organisation's own API key.",
            cardDescriptionCompatible: "Any endpoint that implements the OpenAI API, such as a model gateway or a self-hosted model server, called with that endpoint's API key.",
            activeBadge: 'Active',
            keySetBadge: 'API key saved',
            keyMissingBadge: 'No API key',
            apiKeyLabel: 'API key',
            apiKeySavedHint: 'A key ending {hint} is saved. Leave this blank to keep the saved key.',
            apiKeyNoneHint: 'No key is saved. The key is stored encrypted and is never shown again after you save it.',
            removeKey: 'Remove key',
            removeKeyTitle: 'Remove the saved API key?',
            removeKeyMessage: 'Forseti cannot call {provider} again until a new key is saved. The model and effort settings stay as they are.',
            removeKeyConfirm: 'Remove key',
            removing: 'Removing the key…',
            keyRemoved: 'The {provider} API key was removed.',
            modelLabel: 'Model',
            modelHint: 'Choose a suggested model or type any model id {provider} offers.',
            modelHintCompatible: 'Type the model id exactly as the endpoint expects it.',
            modelRequired: 'Enter a model.',
            fastModelLabel: 'Fast model (optional)',
            fastModelHint: 'Used for conversation titles and tabular extraction. Leave blank to use the main model.',
            effortLabel: 'Effort',
            effortHint: 'How much reasoning the model spends on each answer. Levels the selected model does not accept are unavailable.',
            effortUnsupported: '{effort} is not available for this model.',
            efforts: {
                default: 'Model default',
                low: 'Low',
                medium: 'Medium',
                high: 'High',
                xhigh: 'Extra high',
                max: 'Maximum',
            },
            apiModeLabel: 'API mode',
            apiModeHint: 'Automatic uses the Responses API for models that require it and Chat Completions for the rest.',
            apiModes: {
                auto: 'Automatic',
                responses: 'Responses API',
                chat_completions: 'Chat Completions API',
            },
            reasoningModeLabel: 'Reasoning mode',
            reasoningModeHint: 'Pro spends more reasoning on each answer and costs more. It is offered only for models that support it.',
            reasoningModeUnavailable: 'Pro is not available for the selected model.',
            reasoningModes: {
                standard: 'Standard',
                pro: 'Pro',
            },
            serviceTierLabel: 'Service tier',
            serviceTierHint: 'Project default uses the tier set on your provider project.',
            serviceTiers: {
                auto: 'Project default',
                default: 'Standard',
            },
            baseUrlLabel: 'Base URL',
            baseUrlHint: "The endpoint's API root over HTTPS, for example https://openrouter.ai/api/v1.",
            baseUrlRequired: "Enter the endpoint's base URL.",
            makeActiveLabel: 'Make this the active provider',
            saveButton: 'Save',
            saved: '{provider} settings saved.',
            testButton: 'Test connection',
            testing: 'Testing…',
            testNote: 'Tests the saved settings, so save your changes first. The test sends one short request containing no customer data, which your provider may charge for.',
            unsavedNote: 'This card has unsaved changes. The test uses the saved settings.',
            lastTestHeading: 'Last test',
            testNever: 'Not tested yet.',
            testPassed: 'Passed',
            testFailed: 'Failed',
            testCheckedAt: 'Checked {time}',
            testError: 'The connection test could not be run.',
            saveError: 'The settings could not be saved.',
            removeError: 'The API key could not be removed.',
            errors: {
                api_key_required: 'Save an API key for this provider before making it active.',
                api_key_required_for_new_host: 'The base URL now names another host. Enter the API key for that host before saving; the saved key is not sent to a new host.',
                invalid_api_key: 'The provider rejected this API key.',
                invalid_api_key_format: 'This does not look like a key for this provider. Check that you copied the whole key.',
                unsupported_model_effort: 'The selected model does not accept this effort level.',
                model_requires_responses: 'This model needs the Responses API. Choose Automatic or Responses API.',
                base_url_required: "Enter the endpoint's base URL.",
                invalid_base_url: 'Enter a valid HTTPS URL.',
                unsupported_provider_options: 'These options are not available for this provider.',
            },
        },
        usage: {
            title: 'Benotzung',
            recentHeading: 'Rezent Aktivitéit',
            historyHeading: 'Déi lescht 12 Perioden',
            noActivity: 'Dës Period nach keng Aktivitéit.',
            featureLabel: 'Fonktioun',
            whenLabel: 'Wéini',
            periodLabel: 'Period',
            byFeatureHeading: 'Dëse Mount duerch Feature',
            byFeatureEmpty: 'Keng Fonktioun Aktivitéit nach dës Period.',
            featureColumn: 'Fonktioun',
            featureUnitsColumn: 'Unitéiten',
            featureRunsColumn: 'Lafen',
            byUserHeading: 'Dëse Mount vum Benotzer (Admin View)',
            byUserEmpty: 'Nach keng Aktivitéit pro Benotzer dës Period.',
            userColumn: 'Benotzer',
            reconciliationLinkLabel: 'View Reconciliatioun Drift Rapport',
            reconciliationLinkHint: 'Aesir-intern Admin nëmmen.',
            // Owner ruling 2026-10-10 — consumption by provider, model and effort.
            subtitleProvider: "What Forseti has used, and the provider, model and effort each request ran on. Your organisation's AI provider bills you directly for this consumption.",
            routingHeading: 'Current routing',
            routingDescription: 'The provider, model and effort each Forseti feature uses now.',
            routingProviderLabel: 'Active provider',
            routingFeatureColumn: 'Feature',
            routingModelColumn: 'Model',
            routingEffortColumn: 'Effort',
            routingChangeLink: 'Change the AI model settings',
            routingLoadError: 'The current routing could not be read.',
            routingRetrySubject: 'the current routing',
            periodHeading: 'Consumption',
            periodDescription: 'Requests and tokens for the period you choose. The current month is shown first.',
            fromLabel: 'From',
            toLabel: 'To',
            applyPeriod: 'Show period',
            periodInvalid: 'The start date must be on or before the end date.',
            summaryLoadError: 'Consumption for this period could not be read.',
            summaryRetrySubject: 'consumption for this period',
            totalsCalls: 'Requests',
            totalsInput: 'Input tokens',
            totalsOutput: 'Output tokens',
            totalsCached: 'Cached input tokens',
            byModelHeading: 'By provider, model and effort',
            byFeaturePeriodHeading: 'By feature',
            providerColumn: 'Provider',
            modelColumn: 'Model',
            effortColumn: 'Effort',
            callsColumn: 'Requests',
            inputColumn: 'Input tokens',
            outputColumn: 'Output tokens',
            cachedColumn: 'Cached input tokens',
            tokensColumn: 'Tokens (in / out)',
            timeColumn: 'Time',
            noConsumption: 'Forseti made no requests in this period.',
            notRecorded: 'Not recorded',
            recentDescription: 'The most recent requests in the selected period.',
            recentLoadError: 'Recent activity could not be read.',
            recentRetrySubject: 'recent activity',
            byUserPeriodHeading: 'By user',
            byUserBasis: 'Counted over the {count} most recent requests in this period.',
            historyMonthlyHeading: 'Monthly consumption',
            historyConsumedColumn: 'Consumed units',
            historyLoadError: 'Monthly consumption could not be read.',
            historyRetrySubject: 'monthly consumption',
            historyEmpty: 'No earlier months recorded yet.',
            featureLabels: {
                chat: 'Chat',
                workflow: 'Workflow lafen',
                tabular: 'Tabular Extraktioun',
                redline: 'Redline iwwerpréiwen',
                title: 'Gespréich Titel',
                unknown: 'Aner',
            },
        },
        reconciliation: {
            title: 'Forseti Quota Reconciliatioun',
            subtitle: 'Drift between the fast quota counter cache and the authoritative billing ledger. The reconciler runs every 5 minutes and records any drift to the structured log channel. Aesir-internal admin only.',
            backLink: 'Zréck op Forseti Benotzung',
            stubHeading: 'Where reconciliation results are recorded',
            stubBody: 'This page does not show run history. Each reconciliation run writes its drift figures to the structured log channel; read them there.',
            permissionDenied: 'Dës Säit ass limitéiert op Aesir-intern Administrateuren. Wann Dir mengt Dir sollt Zougang hunn, Kontakt Är AesirX Vertrieder.',
        },
        projects: {
            title: 'Projeten',
            subtitle: 'Aarbechtsberäicher fir Dokumenter, Gespréicher, Workflows a Rezensiounen.',
            modeLabel: 'Vue',
            tabMine: 'Meng Projeten',
            tabShared: 'Mat mir gedeelt',
            searchLabel: 'Sich Projeten',
            searchPlaceholder: 'Sichen…',
            newButton: 'Neie Projet',
            colName: 'Numm',
            colVisibility: 'Visibilitéit',
            colDocs: 'Dokumenter',
            colMembers: 'Memberen',
            colUpdated: 'Aktualiséiert',
            visibilityOwn: 'Eegen nëmmen',
            visibilityShared: 'Gedeelt',
            emptyMine: 'Nach keng Projeten. Erstellt een fir unzefänken.',
            emptyShared: 'Keng Projete goufen mat Iech gedeelt.',
            pageHint: 'Weist {shown} vun {total}.',
            createTitle: 'Neie Projet',
            createSubmit: 'Schafen Projet',
            fieldName: 'Numm',
            fieldDescription: 'Beschreiwung (optional)',
            fieldCmNumber: 'Fall / Matière Nummer (optional)',
            fieldVisibility: 'Visibilitéit',
            errorNameRequired: 'Numm ass néideg.',
            errorSelectorRequired: 'Wielt e Benotzer oder Organisatiounsgrupp fir ze addéieren.',
            renameButton: 'ëmbenennen',
            renamePrompt: 'Projet ëmbenennen',
            deleteButton: 'Läschen',
            deleteConfirm: 'Delete this project? It will disappear from your projects and cannot be restored from the product.',
            deleteFolderConfirm: 'Dësen Dossier läschen? Dokumenter dobannen bleiwen am Projet, just unfiled.',
            removeMemberConfirm: 'Dëse Member ewechhuelen?',
            foldersHeading: 'Classeure',
            foldersEmpty: 'Nach keng Dossieren.',
            addFolderButton: 'Neien Dossier',
            addFolderTitle: 'Neien Dossier',
            documentsHeading: 'Dokumenter',
            documentsEmpty: 'Keng Dokumenter befestegt nach.',
            colFilename: 'Dateinumm',
            colAdded: 'Dobäigesat',
            unattachButton: 'Unattachéieren',
            membersHeading: 'Memberen',
            membersEmpty: 'Nëmme Besëtzer.',
            addMemberButton: 'Member dobäi ginn',
            addMemberTitle: 'Member dobäi ginn',
            addMemberSubmit: 'Member dobäi ginn',
            colMember: 'Member',
            colRole: 'Roll',
            selectorTypeLabel: 'Addéieren duerch',
            selectorUser: 'Benotzer ID',
            selectorGroup: 'Organisatioun Grupp ID',
            fieldUserId: 'Benotzer',
            fieldGroupId: 'Org Grupp',
            fieldRole: 'Roll',
            roleAdmin: 'Admin',
            roleEditor: 'Redakter',
            roleViewer: 'Betrachter',
            notFound: 'Projet net fonnt.',
            selectUserPlaceholder: 'Wielt e Benotzer ...',
            selectGroupPlaceholder: 'Wielt eng Organisatiounsgrupp ...',
            noUsersAvailable: 'Keng Benotzer verfügbar.',
            noGroupsAvailable: 'Keng Organisatiounsgruppen verfügbar.',
            loadFailed: 'Ausgefall Optiounen ze lued.',
            deleteProjectTitle: 'Projet läschen?',
            deleteFolderTitle: 'Dossier läschen?',
            removeMemberTitle: 'Member ewechhuelen?',
            removeMemberConfirmGeneric: 'Dëse Member aus dem Projet ewechhuelen?',
            addDocumentButton: 'Dokument bäifüügen',
            addDocumentTitle: 'Dokument zum Projet bäifüügen',
            fieldDocument: 'Dokument auswielen',
            selectDocPlaceholder: 'No engem Dokument sichen...',
            ownerLabel: 'Systembesëtzer',
        },
        chat: {
            sidebarLabel: 'Chats',
            overviewTitle: 'Forseti',
            overviewSubtitle: 'Ären AI Legal & Compliance Advisor - baséiert op Äre Locatairedaten an Ären installéierte Reguléierungspäck.',
            personaSelectorLabel: 'Persona',
            personaHeading: 'Wielt eng Persoun',
            startChatButton: 'Start en neien Chat',
            startingChat: 'Chat starten…',
            recentHeading: 'Rezent Chats',
            recentEmpty: 'Keng Chats nach - fänkt een uewen un.',
            untitledChat: 'Neie Chat',
            chatLoadFailed: 'Forseti could not load this chat.',
            streamFailed: 'Forseti could not finish this reply. Your message is still in the box.',
            personaSwitchFailed: 'The persona could not be changed.',
            renameFailed: 'The chat could not be renamed.',
            deleteFailed: 'The chat could not be deleted.',
            messageListLabel: 'Gespréich',
            detailSubtitle: 'Persona: {persona}',
            composeHeading: 'Schéckt e Message',
            composeLabel: 'Message Forseti',
            composeDisabledPlaceholder: 'This chat is read-only.',
            composeDisabledHint: 'You can read this conversation but not add to it.',
            switchPersonaHeading: 'Persona',
            presetPersona: 'Viragesate Persona',
            changePersona: 'Persona änneren',
            hidePersonaOptions: 'Personaoptioune verstoppen',
            starterHeading: 'Fänkt mat enger vun dësen Aktiounen un',
            renameButton: 'ëmbenennen',
            renamePrompt: 'Umbenennen Chat',
            deleteButton: 'Läschen',
            deleteConfirm: 'Delete this chat? It will disappear from your chats and cannot be restored from the product.',
            notFound: 'Chat net fonnt.',
            emptyChat: 'Keng Messagen nach - sot Hallo fir e Gespréich unzefänken.',
            roleUser: 'Dir',
            roleAssistant: 'Forseti',
            roleSystem: 'System',
            thinkingLabel: 'Denken',
            toolCallPlaceholder: 'Tool Call: ',
            toolResultPlaceholder: 'Tool result hidden.',
            docEditPlaceholder: 'Dokument Ännerungen proposéiert (V{version}).',
            disclaimer: 'Forseti ënnerstëtzt Är Uerteel - et ersetzt net lizenzéierte Rot.',
            sendButton: 'Schécken',
            sending: 'Schéckt…',
            composePlaceholder: 'Frot de Forseti ...',
            composingPlaceholder: 'Forseti reagéiert ...',
            streamingPlaceholder: 'Forseti schreift ...',
            promptInjectionTitle: 'Forseti huet e puer Kontrollstonnen erofgelooss.',
            promptInjectionBody: 'Stripped Mustere: {patterns}. Forseti wäert nëmmen Äre Message Text gesinn.',
            rateLimitTitle: 'Dir schéckt Messagen ze séier.',
            rateLimitRetryAfter: 'Probéiert nach eng Kéier an {seconds}s.',
            messageLength: '{count} / {max} characters',
            messageTooLong: 'Messages to Forseti are limited to {max} characters. Shorten the message or split it in two.',
            counselReviewTitle: 'De Forseti ass hei onsécher.',
            counselReviewBody: 'Confirméiert mat lizenzéierte Beroder ier Dir op dës Äntwert handelt.',
            legalConclusionRefused: 'Forseti refuséiert eng juristesch Conclusioun ouni Grondlag auszeginn. Probéiert als éischt search_regulatory oder eskaléiert fir ze beroden.',
            toolCallDone: 'Gemaach.',
            toolCallArgsLabel: 'Argumenter',
            toolCallResultLabel: 'Resultat',
            toolResultLogged: 'Tool Resultat am Audit Log ageholl.',
            toolResultAria: 'Tool Resultat',
            documentEditAria: 'Dokument änneren',
            createTaskFromMessage: 'Schafen Aufgab aus dëser',
            createTaskHeading: 'Erstellt Aufgab aus dësem Message',
            createTaskTitleLabel: 'Titel',
            createTaskTitleRequired: 'Titel ass néideg.',
            createTaskDescriptionLabel: 'Beschreiwung',
            createTaskAssigneeLabel: 'Assignee',
            createTaskAssigneePlaceholder: 'Wielt den Ugebueden…',
            createTaskAssigneeNone: '- net zougewisen -',
            createTaskDueDateLabel: 'Verfallsdatum',
            createTaskWorkflowLabel: 'Verknëppelt Workflow (optional)',
            createTaskWorkflowPlaceholder: 'Wielt e Workflow ...',
            createTaskWorkflowNone: '- keng -',
            renameModalSubmit: 'ëmbenennen',
            renameModalSaving: 'Spuert…',
            deleteConfirmTitle: 'Chat läschen?',
            createTaskSubmit: 'Schafen Aufgab',
            createTaskSubmitting: 'Erstellt…',
            cancelButton: 'Ofbriechen',
            autoRouteHintMessage: 'Dës Fro ass méi am {persona} säi Radhaus.',
            autoRouteSwitchButton: 'Wiesselt op {persona}',
            autoRouteSwitching: 'Wiesselt…',
            handoffSuggestionMessage: 'Oder öffnen en neien Chat mat {persona} mat dëser Fro weider.',
            handoffSuggestionButton: 'Start neie Chat mat {persona}',
            handoffSuggestionWorking: 'Ouverture…',
            handoffSuggestionDismiss: 'Net elo',
            handoffSuggestionFailed: 'The new chat could not be opened.',
            persona_display: {
                'forseti-generalist': 'Forseti',
                'forseti-compliance': 'Compliance-Spezialist',
                'forseti-legal': 'Riichtsberoder',
                'forseti-dpo': 'DSB-Beroder',
                'forseti-risk': 'Risiko-Analyst',
                'forseti-auditor': 'Auditeursbléck',
            },
            exportButton: 'PDF exportéieren',
            exportRunning: 'Exportéiert…',
            exportSuccess: 'Exportéiert: {filename} (Fichier-Browser opmaachen).',
            exportOpenFile: 'Am Fichier-Browser opmaachen',
            exportDownload: 'PDF eroflueden',
            citation: {
                verifyBadge: '[Verifizéieren]',
                verifyAria: 'Zitatioun konnt net verifizéiert ginn - iwwerpréiwen ier Dir vertrauen',
                popoverAria: 'Quell fir {label}',
                openSource: 'Open Source Säit',
            },
        },
        auditTrail: {
            forsetiBadge: 'Forseti',
            forsetiBadgeTooltip: 'AI-generéiert Event vum Forseti Audit Trail.',
        },
        rowAction: {
            ask: 'Frot de Forseti',
            tooltipOpen: 'Frot de Forseti iwwer dëst {recordType}',
            tooltipLocked: 'Dëst {recordType} ass gespaart - Forseti kann Froen beäntweren, awer keng Ännerungen maachen.',
        },
        // ── Sprint 63.22.0 — the global Forseti launcher and its launchpad ──
        launcher: {
            openLabel: 'Open Forseti',
            tooltip: 'Ask Forseti (Ctrl+Shift+F)',
            modalTitle: 'Forseti',
            personaHeading: 'Choose who to ask',
            scopeHeading: 'What the chat is about',
            askAboutRecord: 'Ask about this {record}',
            generalChat: 'General chat',
            attachRecord: 'Attach a record',
            attachRecordRequired: 'Choose a record to attach, or choose General chat.',
            recordScopeLost: 'This page no longer shows a record, so this will be a general chat.',
            recentChats: 'Recent chats',
            recentEmpty: 'You have no chats yet.',
            resumeChat: 'Resume {title} — {persona}, {date}',
            startChat: 'Start chat',
            starting: 'Starting chat…',
            openFullView: 'Open in full view',
            loadFailed: 'Forseti could not load your personas and recent chats.',
            noPersonas: 'No Forseti persona is enabled for your organisation.',
        },
        advisorRunPanel: {
            // fix-sprint-20260627-2217-followup F11 — the chat's back control names
            // where it goes (WCAG 2.4.6); it returns to the launchpad.
            backToOptions: 'Back to Forseti options',
            title: 'Forseti - iwwer dëse Rekord',
            heading: 'Forseti — {recordType}',
            summaryLabel: 'Resumé',
            suggestedPersona: 'Proposéiert Persoun',
            askForseti: 'Frot de Forseti',
            runWorkflow: 'Run Workflow',
            viewLineage: 'View Audit Lineage',
            createPdplEvidencePack: 'PDPL-Beweispak erstellen',
            prepareBreachDraft: 'Notifikatiounsentworf virbereeden',
            primaryActions: {
                projectSummary: 'Projetdokument zesummefaassen',
                supplementSummary: 'Zousazufro zesummefaassen',
                authoritySummary: 'Autoritéitsufro zesummefaassen',
                rightsRequestResponse: 'DSR-Äntwert entwerfen',
                dpiaQuickReview: 'DPIA-Schnellprüfung ausféieren',
                taskControlWalkthrough: 'Kontrollbeweiser duerchginn',
                vendorRiskNarrative: 'Fournisseursrisiko-Narrativ generéieren',
                incidentLetter: 'PDPL-Verletzungsbréif entwerfen',
                ropaSufficiency: 'RoPA-Genuchsamkeetscheck ausféieren',
                vendorDpaReview: 'Fournisseur-DPA-Prüfung ausféieren',
            },
            builtinWorkflowViews: {
                ropaSufficiency: {
                    title: 'RoPA Sufficiency Check',
                    notice: 'Runs the existing RoPA sufficiency workflow for this processing activity. The run may create draft follow-up tasks; operators still review and execute them.',
                },
                vendorDpaReview: {
                    title: 'Vendor DPA Review',
                    notice: 'Runs the existing Vendor DPA Review workflow for this vendor contract. The run may create draft follow-up tasks; operators still review every finding and task.',
                },
                vendorRiskNarrative: {
                    title: 'Vendor Risk Narrative',
                    notice: 'Runs the existing vendor risk narrative workflow for this vendor. Output remains draft evidence; the human risk owner reviews and approves it before use.',
                },
                incidentLetter: {
                    title: 'PDPL Breach Notification Letter',
                    notice: 'Runs the existing VN PDPL breach-notification letter workflow for this incident or playbook activation. Output remains draft only; formal regulator filing still requires human sign-off.',
                },
                projectSummary: {
                    title: 'Project Document Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this Forseti project. The workflow is read-only and does not create tasks, evidence, or record updates.',
                },
                supplementSummary: {
                    title: 'Supplement Request Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this supplement request. The workflow is read-only and does not create tasks, evidence, record updates, or filing decisions.',
                },
                authoritySummary: {
                    title: 'Authority Request Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this authority request. The workflow is read-only and does not create tasks, evidence, record updates, filing decisions, or authority-response drafts.',
                },
                rightsRequestResponse: {
                    title: 'DSR Response Draft',
                    notice: 'Runs the existing DSR response draft workflow for this rights request. Output remains draft only; sending and case updates still require the existing human-reviewed rights-request flow.',
                },
                dpiaQuickReview: {
                    title: 'DPIA Quick Review',
                    notice: 'Runs the existing DPIA Quick Review workflow for this DPIA. The run may create draft follow-up tasks; operators still review every finding and task.',
                },
                taskControlWalkthrough: {
                    title: 'Control Evidence Walkthrough',
                    notice: 'Runs the existing compliance-control walkthrough for this task. The workflow must retrieve regulatory anchors before citing them, does not issue legal conclusions or mark controls/evidence approved, and only creates draft evidence placeholders when the operator explicitly requests them.',
                },
            },
            breachDraftNotice: 'Forseti bereet nëmmen Entworf-Inhalter fir dat verlinkt Mẫu số 08 vir. Et entscheet net, ob eng Meldung néideg ass, a reecht oder schéckt de Formulaire net an.',
            breachDraftReady: 'Mẫu số 08-Entworf prett fir d’Iwwerpréiwung',
            breachDraftReviewBeforeSelecting: 'Virum Auswiele préiwen',
            workflowHeading: 'Relevant Workflows',
            workflowLoading: 'Relevant Workflows gi gelueden…',
            workflowEmpty: 'Kee passende Workflow fonnt. Erstellt e guidéierte Workflow fir dësen Datesaz.',
            workflowError: 'Relevant Workflows konnten net gelueden ginn.',
            selectWorkflow: '{title} auswielen',
            workflowSelected: 'De Workflow ass ausgewielt a prett fir dësen Datesaz auszeféieren.',
            workflowRunSelected: 'Ausgewielte Workflow ausféieren',
            workflowStarting: 'Workflow gëtt gestart…',
            workflowRunError: 'Dëse Workflow konnt net gestart ginn.',
            workflowCreateGuided: 'E guidéierte Workflow fir dësen Datesaz erstellen',
            lineageHeading: 'Audit-Verlaf fir dësen Datesaz',
            lineageLoading: 'Audit-Verlaf gëtt gelueden…',
            lineageEmpty: 'Et gi keng Forseti-Verlafseventer fir dësen Datesaz opgeholl.',
            lineageError: 'Audit-Verlaf konnt net gelueden ginn.',
            retry: 'Nach eng Kéier probéieren',
            chatContextLoading: 'De Kontext vum Datesaz gëtt gelueden…',
            chatGuidance: {
                general: 'Forseti kann dësen Datesaz zesummefaassen, feelend Informatioun identifizéieren an Iwwerpréiwungsschrëtt virschloen.',
                privacy: 'Forseti kann Dateschutzfakten iwwerpréiwen, d’Flichte vun den installéierte Kaderen kontrolléieren an hëllefen en Entworf fir mënschlech Iwwerpréiwung virzebereeden.',
                compliance: 'Forseti kann Konformitéitslücken identifizéieren, Ufuerderungen op installéiert Quelle baséieren an verantwortlech nächst Schrëtt virschloen.',
                legal: 'Forseti kann den Datesaz zesummefaassen, fundéiert Quellmaterial fannen an hëllefen en Entworf fir d’juristesch Iwwerpréiwung virzebereeden.',
                risk: 'Forseti kann erfaasste Risiken aschätzen, feelend Fakten ervirhiewen a verhältnisméisseg Iwwerpréiwungsschrëtt virschloen.',
                audit: 'Forseti kann Beweislücken ënnersichen, den Datesaz mat installéierte Kontrollen a Verbindung bréngen an auditbereet Nofollegschrëtt virschloen.',
            },
            starterPrompts: {
                identifyGaps: 'Wéi eng Informatioun oder Beweiser feelen an dësem Datesaz?',
                reviewFrameworks: 'Wéi eng Flichte vun eisen installéierte Kadere soll ech fir dësen Datesaz iwwerpréiwen?',
                prepareDraft: 'Hëlleft mir en Entworf virzebereeden, deen nëmmen op dësem Datesaz a fundéierten installéierte Quelle baséiert.',
                nextSteps: 'Wéi eng Iwwerpréiwungsschrëtt soll ech als nächst maachen?',
                summarizeRecord: 'Faasst dësen Datesaz zesummen a markéiert Punkten, déi mënschlech Iwwerpréiwung brauchen.',
                assessRisk: 'Bewäert déi erfaasste Risiken, identifizéiert feelend Fakten a schlo Iwwerpréiwungsschrëtt vir.',
            },
            disclaimer: 'Forseti ënnerstëtzt Är Uerteel - et ersetzt net lizenzéierte Rot.',
            recordType: {
                project: 'Projet',
                dpia: 'DPIA',
                dossier_filing: 'Dossier Areechung',
                transfer_assessment: 'Kräiz-Grenz Transfert',
                vendor: 'Verkeefer',
                vendor_contract: 'Verkeefer Kontrakt',
                vendor_acceptance: 'Verkeefer Risiko Akzeptanz',
                incident: 'Tëschefall',
                incident_op: 'Tëschefall Operatioun',
                breach_notification: 'Verstouss Notifikatioun',
                authority_request: 'Autoritéit Ufro',
                supplement_request: 'Zousaz Ufro',
                rights_request: 'Rechter Ufro',
                contract: 'Kontrakt',
                task: 'Aufgab',
                data_mapping_activity: 'RoPA Aktivitéit',
                compliance_rule: 'Konformitéit Regel',
                program: 'Programm',
                governance_document: 'Gouvernance Dokument',
                remediation_plan: 'Sanéierungsplang',
                cross_border_filing: 'Kräiz-Grenz Areeche',
                enforcement_case: 'Enforcement Case',
                authority_direction: 'Authority Direction',
                regulated_account: 'Regulated Account',
                regulatory_placeholder: 'Regulatory Placeholder',
                sector_licence: 'Sector Licence',
                credit_information_supply: 'Credit-Information Supply',
                data_service_provider: 'Data-Service Provider Record',
                regulatory_disclosure: 'Regulatory Disclosure',
                issued_record: 'Student Record',
                publication: 'Publication',
            },
        },
        autocomplete: {
            // fix-sprint-20260627-2217-followup F9, verifier round 1 — the 409 refusal
            // when no installed framework grounds the draft (raw English before).
            error_no_grounding_scope: 'Forseti cannot draft this DPIA: no framework for this frame is installed with a usable status, so there is no regulatory text to ground the draft on. Nothing was drafted.',
            button_label: 'Frot de Forseti dëst auszeschaffen',
            drafting: 'Entworf…',
            hint_pick_activity: "Wielt eng verlinkt Veraarbechtungsaktivitéit fir Forseti Drafting z'aktivéieren.",
            draft_origin_label: 'Forseti Entworf - Iwwerpréiwung pro Feld',
            tokens_label: '≈ {count} Tokens aus Ärem monatlecht Eemer',
            accept_selected: 'Akzeptéieren ausgewielt',
            reject_all: 'Entworf refuséieren',
            field_checkbox_label: 'Gëlle Forseti Entworf fir {field}',
            field_labels: {
                description: 'Beschreiwung',
                purpose: 'Zweck',
                data_subjects: 'Donnéeën Sujeten',
                data_types: 'Daten Zorte',
                necessity_justification: 'Noutwennegkeet & Proportionalitéit',
                consultation_notes: 'Konsultatiounsnotizen',
                dpo_opinion: 'DPO Meenung',
                system_ids: 'Verknëppelt Systemer',
                vendor_ids: 'Verknëppelt Ubidder',
            },
            error_generic: 'Forseti konnt dëst DPIA net ausschaffen. Probéiert nach eng Kéier oder raffinéiert den Aktivitéitsrekord.',
            variant_legend: 'Kader Kader',
            variant_label_gdpr: 'GDPR',
            variant_none_available: 'Keen installéierte Framework verfügbar',
            variant_label_pdpl: 'PDPL',
            variant_label_nis2: 'NIS2',
            variant_label_iso27001: 'ISO 27001',
            variant_label_soc2: 'SOC 2',
            conflict_banner: 'Opgepasst: {count} Kräiz-Kaderkonflikt(en) gëllen fir dës Variant. Iwwerpréift ier Dir akzeptéiert.',
        },
        formDraft: {
            button_label: 'Frot Forseti fir dëse Formulaire ze entwerfen',
            drafting: 'Gëtt entworf…',
            draft_origin_label: 'Forseti-Entworf — iwwerpréift all Feld',
            tokens_label: '≈ {count} Tokens aus Ärem Mountskontingent',
            accept_selected: 'Ausgewielt iwwerhuelen',
            reject_all: 'Entworf refuséieren',
            field_checkbox_label: 'Forseti-Entworf fir {field} uwenden',
            excluded_note: '{count} Feld(er) kënnen net entworf ginn (Referenzen, Unhäng oder nëmme liesbar).',
            error_generic: 'Forseti konnt dëse Formulaire net entwerfen. Probéiert w.e.g. nach eng Kéier.',
            empty_draft: 'Forseti huet keng Feld-Entworf fir dëse Formulaire produzéiert.',
        },
        tia: {
            // fix-sprint-20260627-2217-followup F9, verifier round 1 — the 409 refusal
            // when no installed framework grounds the draft (raw English before).
            error_no_grounding_scope: 'Forseti cannot draft this TIA: no framework that governs this transfer is installed with a usable status, so there is no regulatory text to ground the draft on. Nothing was drafted.',
            button_label: 'Frot de Forseti fir dës TIA auszeschaffen',
            drafting: 'TIA ausschaffen...',
            hint_pick_destination: "Setzt d'Destinatiounsland op den Transfertrekord ier Forseti entworf ka ginn.",
            draft_origin_label: 'Forseti TIA Entworf - Iwwerpréiwung pro Feld',
            tokens_label: '≈ {count} Tokens aus Ärem monatlecht Eemer',
            accept_selected: 'Akzeptéieren ausgewielt',
            reject_all: 'Entworf refuséieren',
            field_checkbox_label: 'Gëlle Forseti Entworf fir {field}',
            error_generic: 'Forseti konnt dës TIA net ausschaffen. Probéiert nach eng Kéier oder raffinéiert den Transfert Rekord.',
            usage_details: 'Detailer zur AI-Notzung',
            applied_status: 'Draft accepted. The selected fields are saved to the transfer record.',
            recommended_updates_title: 'Recommandéiert Datesaz-Aktualiséierungen',
            mechanism_recommendation: 'Transfermechanismus: {value}',
            dpo_review_recommendation: 'Ask your DPO to review this sensitive transfer.',
            review_date_warning: 'Setzt en Iwwerpréiwungsdeadline ier Dir dësen sensiblen Transfer aktualiséiert.',
            processing_activity_warning: 'Verknäppt op d’mannst eng Veraarbechtungsaktivitéit / ROPA-Datesaz ier Dir d’TIA ofschléisst.',
            go_to_field: 'Op d’Feld goen',
            adequacy_label: 'Destinatioun Regime',
            adequacy_label_source_route: 'Transfer route',
            adequacy_status_adequacy_decision: 'EU adequacy decision (exports from the EU/EEA)',
            adequacy_status_non_adequacy_with_sccs: 'No EU adequacy decision — GDPR Art. 46 safeguard for exports from the EU/EEA',
            adequacy_status_derogation: 'GDPR Art. 49 derogation (exports from the EU/EEA)',
            adequacy_needs_action: 'Action needed',
            adequacy_for_information: 'For information',
            adequacy_status_transitional: 'Iwwergangs / EEA Ofkiirzung',
            adequacy_status_unknown: 'Onbekannt - Destinatioun setzen',
            adequacy_status_source_jurisdiction_route: 'Transfer out of Vietnam — impact-assessment dossier route',
            adequacy_summary_no_destination: 'No destination country is set. Select the destination before Forseti drafts a TIA.',
            adequacy_summary_intra_eea: 'Transfer within the EEA ({code}) — for an export from the EU/EEA, no third-country TIA is required under GDPR Arts. 44–50. A transfer out of Vietnam is assessed under Law 91/2025/QH15 Art. 20 and Decree 356/2025/NĐ-CP Arts. 17–18 unless an evidenced exception applies.',
            adequacy_summary_inbound: 'Vietnam destination — an exporter in the EU/UK establishes its own transfer mechanism; the Vietnam-origin transfer impact-assessment rules do not apply to an inbound route. GDPR applies only where the record establishes an EU/EEA jurisdiction trigger.',
            adequacy_summary_us_dpf: 'US recipient certified under the Data Privacy Framework — for an export from the EU/EEA, covered by the European Commission adequacy decision of 10 July 2023 while the recipient stays on the active DPF list. Confirm the certification at https://www.dataprivacyframework.gov before relying on it.',
            adequacy_summary_us_no_dpf: 'US destination not under the DPF — for an export from the EU/EEA, no EU adequacy decision covers a non-certified recipient, and the Schrems II surveillance-law concerns (FISA §702, EO 12333) call for supplementary measures.',
            adequacy_summary_eu_adequacy: '{code} has an active European Commission adequacy decision — an export from the EU/EEA may proceed without SCCs or supplementary measures, subject to the Commission’s ongoing monitoring.',
            adequacy_summary_eu_no_adequacy: '{code} has no active EU adequacy decision. An export from the EU/EEA may rely on GDPR Art. 46 safeguards (standard contractual clauses, BCRs, certification or a code of conduct) with a Schrems II assessment of the destination, or on a GDPR Art. 49 derogation.',
            adequacy_summary_source_jurisdiction_route: 'Transfer out of Vietnam to {code} — assessed on the route Law 91/2025/QH15 Art. 20 states: the transfer impact-assessment dossier (Decree 356/2025/NĐ-CP Arts. 17–18), unless an exception those instruments state is evidenced. The EU adequacy classification is not used to frame this route; where the record establishes that the GDPR reaches the exporter for this processing (Art. 3), its Chapter V applies in addition.',
            adequacy_summary_source_jurisdiction_route_exemption: 'Transfer out of Vietnam to {code} — assessed on the route Law 91/2025/QH15 Art. 20 states: the transfer impact-assessment dossier (Decree 356/2025/NĐ-CP Arts. 17–18), unless an exception those instruments state is evidenced. A possible exception is flagged from the record; it is not a confirmed exception and needs affirmative evidence and Compliance/Legal review. The EU adequacy classification is not used to frame this route; where the record establishes that the GDPR reaches the exporter for this processing (Art. 3), its Chapter V applies in addition.',
            conflict_banner: 'Opgepasst: {count} Kräiz-Kaderkonflikt(en) gëllen fir dësen Transfertregime. Iwwerpréift ier Dir akzeptéiert.',
            field_label_description: 'Description',
            field_label_purpose: 'Purpose',
            field_label_transfer_mechanism_detail: 'Transfer mechanism detail',
            field_label_recipient_name: 'Recipient name',
            field_label_recipient_details: 'Recipient details',
            field_label_data_subjects: 'Data subjects',
            field_label_data_types: 'Data types',
        },
        // Sprint 48.3.6 — shared draft output-guardrail labels (TIA + DPIA + all draft workflows).
        guardrails: {
            missing_facts_title: 'Feelend erfuerderlech Faiten',
            missing_facts_hint: 'Forseti huet Faiten markéiert, déi net am Dossier stinn. Felder, déi dovun ofhänken, sinn net virausgewielt — gitt d’Faiten un oder akzeptéiert d’Feld bewosst.',
            risk_label: 'Kalibréiert Risiko',
            risk_low: 'Niddreg',
            risk_medium: 'Mëttel',
            risk_high: 'Héich',
            jurisdiction_label: 'Juridictiouns-Ausléiser',
            jurisdiction_none_installed: 'Kee installéierte Kader deckt dës Bewäertung of — et gouf keng Rechtsuerdnung bewäert. Installéiert e passend Reglementéierungspak, ier Dir Iech op déi juristesch Conclusiounen an dësem Entworf verléisst.',
            jurisdiction_none_assessed: 'Forseti huet keng Bewäertung vun der Juridictioun zréckginn. Et gouf näischt bewäert — dat ass kee Befund, datt keng Uerdnung gëllt.',
            jurisdiction_inactive: 'The framework covering this assessment is installed but no longer in force — so no legal regime was assessed. There is nothing to install: activate the current instrument, or the one that replaces it, before relying on the legal conclusions here.',
            jurisdiction_resolving: 'D’Nimm vun de bewäerten Uerdnunge gi gelueden…',
            trigger_applicable: 'Applicabel',
            trigger_not_applicable: 'Net ausgeléist',
            trigger_unknown: 'Onbekannt — Fait feelt',
            recipient_role_label: 'Roll vum Empfänger',
            recipient_role_controller: 'Verantwortlechen',
            recipient_role_processor: 'Operateur',
            recipient_role_joint_controller: 'Gemeinsam Verantwortlechen',
            recipient_role_unknown: 'Onbekannt — net uginn',
            opt_in_hint: 'Juristesch Konklusioun — wielt se nëmmen, wann Dir se mat den disponibele Faiten akzeptéiert.',
        },
        mcp: {
            pageTitle: 'MCP Connector Schlësselen',
            pageSubtitle: 'Connect Claude, Slack agents and other MCP clients to Forseti. A connected client reads your organisation’s records within the access you grant, and anything it proposes to change waits in the approval queue until an administrator approves it.',
            issueButton: 'Neie Schlëssel erausginn',
            issueModalTitle: 'MCP API Schlëssel erausginn',
            labelField: 'Label',
            labelHint: 'Eng kuerz Beschreiwung (zB "AI Fäegkeet Labo - Reguléierungsfuerschung").',
            permittedToolsField: 'Zulässlech Tools',
            expiresAtField: 'Erléist (optional)',
            expiresAtHint: 'Loosst eidel fir ni ofleeft.',
            submit: 'Ausgab Schlëssel',
            cancel: 'Ofbriechen',
            keyDisplayedOnce: 'Kopéiert dëse Schlëssel elo - Dir kënnt et NET erëm gesinn. Späichert et an Ärem Geheimmanager (1Password, AWS Secrets Manager, Vault, etc.).',
            keyDisplayedOnceClose: 'Ech hunn et kopéiert - no',
            colLabel: 'Label',
            colPrefix: 'Präfix',
            colStatus: 'Status',
            colTools: 'Tools',
            colCreatedAt: 'Geschaf',
            colLastUsed: 'Last benotzt',
            colCalls: 'Appellen',
            colActions: 'Aktiounen',
            statusActive: 'Aktiv',
            statusRevoked: 'Entlooss',
            statusExpired: 'Ofgelaaf',
            revokeAction: 'Revokéieren',
            rotateAction: 'Rotéieren',
            confirmRevokeTitle: 'Dëse MCP Schlëssel zréckzéien?',
            confirmRevokeBody: 'Extern Claude Clienten, déi dëse Schlëssel benotzen, verléieren direkt Zougang. Dëst kann net réckgängeg gemaach ginn.',
            confirmRevoke: 'Schlëssel zréckzéien',
            rotatedTitle: 'Schlëssel rotéiert',
            rotatedBody: 'Den neie Kloertext Schlëssel ass hei ënnen. Den ale Schlëssel schafft weider fir 5 Minutten sou datt all Uruff am Fluch fäerdeg ass.',
            emptyState: 'Keng MCP Schlësselen ausgestallt nach.',
            errorIssue: 'Konnt den MCP Schlëssel net ausginn.',
            errorList: 'Konnt MCP Schlësselen net lueden.',
            errorRevoke: 'Konnt de Schlëssel net zréckzéien.',
            errorRotate: 'Konnt de Schlëssel net rotéieren.',
            neverUsed: '-',
            neverExpires: 'Ni',
            // Sprint 63.23.0 — the connector page leads with how to connect.
            reloadKeys: 'Reload API keys',
            issuing: 'Issuing…',
            labelRequired: 'Enter a label for the key.',
            toolsRequired: 'Choose at least one tool.',
            webhookField: 'Status callback URL (optional)',
            webhookHint: 'ComplianceOne posts the outcome of each queued write to this address, signed with a secret that is shown once.',
            webhookInvalid: 'Enter a full address, for example https://example.com/hooks/mcp.',
            webhookSecretLabel: 'Webhook signing secret',
            webhookSecretOnce: 'Copy the signing secret now. It is shown once.',
            toolGroupRead: 'Read tools',
            toolGroupWrite: 'Write tools',
            toolGroupWriteHint: 'A write tool never changes a record directly. Each call goes to the approval queue and runs only after an administrator approves it.',
            toolGroupMemory: 'Organisation memory',
            toolGroupSubagent: 'Persona handoff',
            toolQueued: 'goes to the approval queue',
            allToolsInScope: 'Every tool its scopes allow',
            copy: 'Copy',
            copyValue: 'Copy {label}',
            copiedValue: '{label} copied',
            copyFailed: 'Could not copy {label}. Select the value and copy it yourself.',
            apiKeyLabel: 'API key',
            registers: {
                clients: 'OAuth clients',
                grants: 'Connected apps',
                keys: 'API keys',
            },
            connection: {
                title: 'Connection details',
                intro: 'Every client needs the server URL. OAuth clients also use the addresses below; clients that use an API key send it as a bearer token.',
                serverUrl: 'Server URL',
                transport: 'Transport',
                transportStreamableHttp: 'Streamable HTTP',
                protectedResourceMetadataUrl: 'Protected resource metadata URL',
                asMetadataUrl: 'Authorization server metadata URL',
                authorizeUrl: 'Authorize URL',
                tokenUrl: 'Token URL',
                registrationUrl: 'Client registration URL',
                revokeUrl: 'Revocation URL',
                scopes: 'Scopes',
                loading: 'Loading connection details…',
                error: 'The connection details could not be read, so no server URL is shown.',
                reload: 'Reload connection details',
            },
            dcr: {
                label: 'Let clients register themselves',
                hint: 'When this is on, a client such as Claude can register itself and ask a signed-in user to approve it. It has no access until a user of this organisation approves it on the consent screen. Switching it off disconnects apps that registered themselves at their next call; clients registered below and API keys keep working. Switching it back on lets those apps work again.',
                stateOn: 'On — clients can register themselves.',
                stateOff: 'Off — only clients registered below can connect.',
                saving: 'Saving…',
                error: 'Could not change whether clients can register themselves.',
                confirmOffTitle: 'Switch off self-registration?',
                confirmOffBody: 'Apps that registered themselves, such as Claude, are disconnected at their next call for everyone in this organisation, and nobody can approve a new one. Clients registered below and API keys keep working. The connections are suspended, not revoked: switching self-registration back on lets them work again.',
                confirmOff: 'Switch off',
            },
            tabs: {
                label: 'How to connect',
                claude: 'Claude',
                slack: 'Slack and other agent builders',
                apiKey: 'API key',
            },
            claudeTab: {
                step1: 'In Claude, add a custom connector.',
                step2: 'Paste the server URL. Claude finds out how to sign in by itself.',
                step3: 'Sign in to ComplianceOne when asked, then read the consent screen and approve it.',
                consentHeading: 'What the consent screen shows',
                consentItemClient: 'the app’s name and the address it will send you back to;',
                consentItemOrg: 'your organisation;',
                consentItemScopes: 'what it may do — read your organisation’s records, and propose changes for approval if that is requested — and the tools that reach;',
                consentItemOrigin: 'whether the app registered itself or an administrator registered it.',
                actsAsUser: 'Claude then acts as the person who approved it, with no more access than that person’s own role gives them.',
                registrationOff: 'Clients cannot register themselves in this organisation, so Claude cannot connect with the server URL alone. Switch self-registration on, or register a client for Claude under OAuth clients.',
            },
            slackTab: {
                intro: 'Agent builders such as Slack ask for OAuth fields. Register a client under OAuth clients, then copy each value into the builder’s form.',
                colField: 'Field in the agent builder',
                colValue: 'Value',
                fieldServerUrl: 'MCP server URL',
                fieldClientId: 'Client ID',
                fieldClientSecret: 'Client secret',
                fieldAuthorizeUrl: 'Authorize URL',
                fieldTokenUrl: 'Token URL',
                fieldScopes: 'Scopes',
                valueClientId: 'Shown on the client’s row under OAuth clients.',
                valueScopes: 'Enter only the scopes shown on your client’s row under OAuth clients. A scope the client was not registered with is refused.',
                valueClientSecret: 'Shown once, when you register the client or rotate its secret. Store it in the agent builder straight away.',
                redirectNote: 'The agent builder shows the redirect URI it uses. Add it to the client exactly as shown: ComplianceOne sends people back only to a redirect URI registered on the client.',
                registerCta: 'Register a client',
            },
            apiKeyTab: {
                intro: 'Scripts and custom clients can use an API key instead of OAuth. Issue a key under API keys and send it on every request.',
                headerLabel: 'Header',
                configLabel: 'Client configuration (JSON)',
                curlLabel: 'Check the connection with curl',
                actsAsIssuer: 'A call made with a key acts as the administrator who issued it, limited to the tools chosen on the key.',
            },
            clients: {
                errorRevokedClient: 'This client has been revoked, so it has no secret to rotate.',
                paginationLabel: 'OAuth clients pages',
                intro: 'Clients that agent builders use with a client ID and secret. Clients that registered themselves are listed here too.',
                registerButton: 'Register client',
                empty: 'No OAuth clients yet.',
                error: 'The OAuth clients could not be read.',
                reload: 'Reload OAuth clients',
                colName: 'Name',
                colClientId: 'Client ID',
                colType: 'Type',
                colOrigin: 'Registered by',
                colRedirects: 'Redirect URIs',
                colScopes: 'Scopes',
                colTools: 'Tools',
                colLastUsed: 'Last used',
                colStatus: 'Status',
                colActions: 'Actions',
                typeConfidential: 'Confidential',
                typePublic: 'Public',
                originAdmin: 'An administrator',
                originDynamic: 'The client itself',
                statusActive: 'Active',
                statusRevoked: 'Revoked',
                rotateSecret: 'Rotate secret',
                revoke: 'Revoke',
                modalTitle: 'Register OAuth client',
                nameField: 'Name',
                nameHint: 'People see this name on the consent screen.',
                nameRequired: 'Enter a name.',
                typeField: 'Client type',
                typeConfidentialHint: 'Confidential — the client keeps a secret on a server. Use this for Slack and other agent builders.',
                typePublicHint: 'Public — the client cannot keep a secret (a desktop or browser app) and proves itself with PKCE instead.',
                redirectField: 'Redirect URIs',
                redirectHint: 'The exact addresses the client sends people back to. A loopback address such as localhost accepts any port.',
                redirectUriLabel: 'Redirect URI {n}',
                addRedirect: 'Add another redirect URI',
                removeRedirect: 'Remove redirect URI {n}',
                redirectInvalid: 'Enter a full address, for example https://example.com/callback.',
                redirectRequired: 'Add at least one redirect URI.',
                scopesField: 'Scopes',
                scopesRequired: 'Choose at least one scope.',
                toolsField: 'Limit to these tools (optional)',
                toolsHint: 'Leave every box clear to allow each tool the chosen scopes reach.',
                submit: 'Register client',
                saving: 'Registering…',
                errorCreate: 'Could not register the client.',
                createdTitle: 'Client registered',
                secretTitle: 'New client secret',
                secretOnce: 'Copy the client secret now. It is shown once and cannot be shown again; rotate the secret to get a new one.',
                publicNoSecret: 'This is a public client, so it has no secret. It signs in with PKCE.',
                clientIdLabel: 'Client ID',
                clientSecretLabel: 'Client secret',
                confirmRotateTitle: 'Rotate the client secret?',
                confirmRotateBody: 'The current secret stops working now. Put the new secret into the agent builder straight away.',
                errorRotate: 'Could not rotate the client secret.',
                confirmRevokeTitle: 'Revoke this OAuth client?',
                confirmRevokeBody: 'Every app connected through this client loses access now, and its connections are revoked. This cannot be undone.',
                confirmRevoke: 'Revoke client',
                errorRevoke: 'Could not revoke the client.',
            },
            grants: {
                errorNotFound: 'This connection no longer exists; it may already have been revoked.',
                paginationLabel: 'Connected apps pages',
                intro: 'Apps that people in this organisation approved on the consent screen. Revoking one stops its next call.',
                empty: 'No connected apps.',
                error: 'The connected apps could not be read.',
                reload: 'Reload connected apps',
                colApp: 'App',
                colUser: 'Approved by',
                colScopes: 'Scopes',
                colCreated: 'Connected',
                colLastUsed: 'Last used',
                colActions: 'Actions',
                revoke: 'Revoke',
                confirmRevokeTitle: 'Revoke this connection?',
                confirmRevokeBody: 'The app loses access now. The person can connect it again by approving it on the consent screen.',
                confirmRevoke: 'Revoke connection',
                errorRevoke: 'Could not revoke the connection.',
            },
            myApps: {
                errorNotFound: 'This app is no longer connected; it may already have been revoked.',
                title: 'Connected apps',
                intro: 'Apps you have allowed to use ComplianceOne on your behalf. Each acts as you, with no more access than your role gives you.',
                empty: 'You have not connected any apps.',
                error: 'Your connected apps could not be read.',
                reload: 'Reload connected apps',
                colApp: 'App',
                colScopes: 'Scopes',
                colCreated: 'Connected',
                colLastUsed: 'Last used',
                colActions: 'Actions',
                revoke: 'Revoke',
                confirmRevokeTitle: 'Revoke this app?',
                confirmRevokeBody: 'The app loses access now. You can connect it again by approving it on the consent screen.',
                confirmRevoke: 'Revoke app',
                errorRevoke: 'Could not revoke the app.',
            },
        },
        memory: {
            pageTitle: 'Forseti Erënnerung',
            pageSubtitle: 'Browst a curate den org-spezifesche Kontext Forseti huet iwwer Gespréicher an Entscheedungen gesammelt. Curated Entréen gespaart géint Nuetsretentioun a bleiwen fir all Persona verfügbar.',
            filterCategory: 'Kategorie',
            filterAllCategories: 'All Kategorien',
            totalCount: '{count} Entréen',
            colAnchor: 'Anker',
            colCategory: 'Kategorie',
            colSource: 'Quell',
            colContent: 'Inhalt',
            colCreatedAt: 'Geschaf',
            colActions: 'Aktiounen',
            editButton: 'Edit',
            deleteButton: 'Läschen',
            promoteButton: 'Promotioun zu curated',
            emptyState: 'Nach keng Erënnerungen - Forseti sammelt Kontext wéi Dir chat.',
            categories: {
                framework_interpretation: 'Kader Interpretatioun',
                process_preference: 'Prozess Präferenz',
                escalation_threshold: 'Eskalatiounsschwell',
                decision_context: 'Entscheedung Kontext',
                glossary_term: 'Glossar Begrëff',
                auto_inferred: 'Auto ofgeleet',
            },
            sources: {
                admin_curated: 'Curéiert',
                user_explicit: 'Explizit',
                auto: 'Ofgeleet',
            },
            deleteConfirmTitle: 'Dës Erënnerung läschen?',
            deleteConfirmBody: 'Läschen wäert dësen Anker vun all zukünfteg Forseti Chat Boot erofhuelen. Anker: {anchor}',
            deleteConfirmLabel: 'Läschen',
            deleteCancelLabel: 'Ofbriechen',
            editModalTitle: 'Erënnerung änneren',
            editAnchorLabel: 'Anker',
            editCategoryLabel: 'Kategorie',
            editContentLabel: 'Inhalt',
            editContentCount: '{n}/{max} Zeechen',
            editEmptyError: 'Inhalt kann net eidel ginn.',
            editTooLongError: 'Inhalt muss ≤1000 Zeechen sinn.',
            editAutoPromoteHint: 'Wann Dir dës automatesch ofgeleet Erënnerung späichert, markéiert se als Benotzerbestätegt an hëlt se aus der Retentiounsweep ewech.',
            editCancelButton: 'Ofbriechen',
            editSaveButton: 'Späicheren',
            editSaving: 'Spuert…',
            loadFailed: 'The memories could not be loaded.',
            deleteFailed: 'The memory could not be deleted.',
            promoteFailed: 'The memory could not be promoted to curated.',
            saveFailed: 'The memory could not be saved.',
        },
        auditLineage: {
            title: 'Audit Lineage',
            subtitle: 'Spuer all AI-generéiert Artefakt un engem Rekord verbonnen. Chats, Tool Appellen, Aufgaben, Beweisen Entworf, an Dokument Versiounen.',
            filterForm: 'Filteren',
            recordType: 'Rekord Typ',
            recordId: 'Rekord ID',
            dateStart: 'Vun',
            dateEnd: 'Zu',
            search: 'Sichen',
            loading: 'Lueden…',
            exportPdf: 'Export PDF',
            exportCsv: 'Export CSV',
            nodesHeading: 'Lineage Noden',
            colType: 'Typ',
            colLabel: 'Label',
            colOccurredAt: 'Entstanen am',
            colActor: 'Schauspiller',
            colId: 'ID',
            noResults: 'Keng Beroder Aktivitéit fir dëse Rekord.',
            draftBadge: 'AI-generéiert Entworf',
            prev: 'Virdrun',
            next: 'Nächst',
            viewToggleTable: 'Kuckt als Dësch',
            viewToggleGraph: 'Kuckt als Grafik',
            graphAriaLabel: 'Lineage Grafik (visuell). Benotzt den Dësch Vue fir Hëllefstechnologie.',
            graphLegendDraft: 'Entworf AI Beweiser',
            graphEmpty: 'Keng Node fir Grafik.',
            paginationAriaLabel: 'Paginatioun',
            exportSuccessTemplate: '{format}: {filename}',
            exportErrorTemplate: 'Export ausgefall: {message}',
            truncatedNotice: "Dës Hierkonftskette ass net komplett. D'Plattform liest héchstens eng fix Zuel u Zeilen aus all Quell, an d'Limitt gouf erreecht fir: {sources}. Zeilen iwwer enger Limitt, wann et der gëtt, ginn net ugewisen. Schränkt den Datumsberäich an, fir se mat anzebezéien.",
            truncatedChatLinksNotice: 'This lineage is not complete: only the {limit} newest chats of this record are used to find the tool calls, workflow runs, tasks, evidence and document versions that were started from a chat. Anything reached only through an older chat is not shown, and narrowing the date range does not bring it in.',
            recordTypes: {
                project: 'Projet',
                dpia: 'DPIA',
                transfer_assessment: 'Cross-border transfer assessment',
                dossier_filing: 'Dossier filing',
                cross_border_filing: 'Cross-border filing',
                vendor: 'Verkeefer',
                incident: 'Tëschefall',
                incident_op: 'Tëschefall Operatioun',
                rights_request: 'Rechter Ufro',
                contract: 'Kontrakt',
                task: 'Aufgab',
                data_mapping_activity: 'Donnéeën Mapping Aktivitéit',
                compliance_rule: 'Konformitéit Regel',
                program: 'Programm',
            },
            nodeTypes: {
                chat: 'Chat',
                tool_call: 'Tool Opruff',
                workflow_run: 'Workflow lafen',
                task: 'Aufgab',
                evidence: 'Beweiser',
                document_version: 'Dokument Versioun',
            },
            recordIdManualHint: "This record type has no list here. Enter the record's own id, as it appears in the address of its page.",
            recordIdManualLabel: 'Record ID',
            colDetails: 'Details',
        },
        integrityCenter: {
            verdicts: {
                ok: 'OK',
                drifted: 'Gedrängt',
                hash_mismatch: 'Hash Mëssverständnis',
                merkle_mismatch: 'Merkle Mëssverständnis',
                source_missing: 'Quell vermësst',
                not_found: 'Net fonnt',
                chain_unreachable: 'Kette net erreechbar',
                service_unreachable: 'Service onerreechbar',
                pending: 'Erwaart Verankerung',
            },
        },
        evidenceDraft: {
            draftBadge: 'AI-generéiert DRAFT',
            pendingReviewBadge: 'Erwaardung iwwerpréiwen',
            approvedBadge: 'Genehmegt',
            rejectedBadge: 'Refuséiert',
            moveToReviewButton: 'Mark prett fir Iwwerpréiwung',
            approveButton: 'Zoustëmmen',
            rejectButton: 'Refuséieren',
            rejectReasonLabel: 'Ofleenung Grond',
            humanSignoffBanner: 'Forseti ënnerstëtzt Är Uerteel - et ersetzt net lizenzéierte Rot. AI-generéiert Beweiser erfuerdert mënschlech Genehmegung ier et als formell Beweis zielt.',
            pageTitle: 'AI Beweis Entworf',
            pageSubtitle: 'Iwwerpréift, approuvéiert oder refuséiert AI-generéiert Beweiser. Entworf musse vun engem Mënsch guttgeheescht ginn ier se als formell Beweis zielen.',
            filterAll: 'All handhabbar',
            filterDrafts: 'Nëmmen Entworf',
            filterPending: 'Erwaardung iwwerpréiwen',
            filterApproved: 'Kuerzem guttgeheescht',
            filterRejected: 'Kuerzem refuséiert',
            colId: 'Beweis ID',
            colStatus: 'Status',
            colSource: 'Quell',
            colCreatedAt: 'Geschaf',
            colActions: 'Aktiounen',
            empty: 'Kee AI Beweisentwécklungen an dësem Status.',
            confirmApprove: 'Genehmegt dës AI-generéiert Beweiser als formell?',
            confirmReject: 'Refuséiert dëse Beweisprojet. Dëst kann net réckgängeg gemaach ginn.',
            reasonRequired: 'E Oflehnungsgrond ass erfuerderlech.',
            rejectModalTitle: 'Entworf Beweiser refuséieren',
            approveModalTitle: 'Genehmegt Entworf Beweiser',
            operationFailed: 'Operatioun gescheitert. Probéiert w.e.g. nach eng Kéier.',
            moveToReviewSuccess: 'Markéiert prett fir Iwwerpréiwung.',
            approveSuccess: 'Genehmegt.',
            rejectSuccess: 'Refuséiert.',
            sourceLabelWorkflowRunTitle: 'Workflow: {title}',
            sourceLabelWorkflowRunId: 'Workflow lafen {id}',
            sourceLabelChatTitle: 'Chat: {title}',
            sourceLabelChatId: 'Forseti Chat {id}',
            sourceLabelEmpty: '-',
            filtersAriaLabel: 'Filter Status',
        },
        workflows: {
            pageTitle: 'KI-Workflows',
            pageSubtitle: 'Gespäichert KI-Prompt-Schablounen, déi Aufgaben an Beweis-Entworf erstellen.',
            newWorkflow: 'Neie Workflow',
            newWorkflowHeading: 'Neie KI-Workflow',
            tabAll: 'All',
            tabBuiltin: 'Agebauten Pak',
            tabMine: 'Meng Workflows',
            tabDetail: 'Detailer',
            tabLinkedTasks: 'Verlinkte Aufgaben',
            builtinBadge: 'Agebaut',
            builtinSubtitle: 'Agebauten Workflow.',
            userSubtitle: 'Personaliséierte Workflow.',
            emptyHint: 'Nach keng Workflows.',
            loadTimeoutError: 'Workflows konnten nom 10 Sekonnen net gelueden ginn. Probéiert nei oder kuckt am Browser-Netzwierk-Tab no enger blockéierter Ufro.',
            loadError: 'Konnt Workflows net lueden.',
            hideError: 'Verstoppen feelgeschloen.',
            unhideError: 'Weisen feelgeschloen.',
            retry: 'Erëm probéieren',
            producesTasks: 'Erstellt Aufgaben',
            producesEvidence: 'Erstellt Beweis-Entworf',
            hideButton: 'Aus menger Lëscht verstoppen',
            unhideButton: 'Weisen',
            runButton: 'Ausféieren',
            editButton: 'Änneren',
            shareButton: 'Deelen',
            backToList: 'Zréck zur Lëscht',
            requireTitleAndPrompt: 'Titel a Prompt sinn erfuerderlech.',
            fieldTitle: 'Titel',
            fieldDescription: 'Beschreiwung',
            fieldType: 'Typ',
            fieldPersona: 'Persona-Affinitéit',
            fieldPractice: 'Praxis',
            fieldPrompt: 'Prompt (Markdown)',
            fieldProducesTasks: 'Erstellt Aufgaben',
            fieldProducesEvidence: 'Erstellt Beweis-Entworf',
            fieldProjectId: 'Projet (optional)',
            fieldAttachmentIds: 'Unhäng',
            fieldParentRecord: 'Iwwergeuerdent Datesaz (optional)',
            fieldPersonaOverride: 'Persona-Iwwerschreiwung (optional)',
            typeAssistant: 'Assistent',
            typeTabular: 'Tabular',
            personaNone: '— keng —',
            parentNone: '— keng —',
            parentIdPlaceholder: 'ID vum iwwergeuerdente Datesaz',
            selectProjectPlaceholder: 'Wielt e Projet…',
            selectProjectFirst: 'Wielt e Projet, fir Unhäng auszewielen.',
            selectAttachmentsPlaceholder: 'Wielt Dokumenter…',
            noAttachmentsInProject: 'Dëse Projet huet keng ugehängten Dokumenter.',
            selectParentRecordPlaceholder: 'Wielt en Datesaz…',
            selectParentRecordFirst: "Wielt fir d'éischt en Datesaz-Typ.",
            noRecordsForType: 'Keng Datesätz vun dësem Typ verfügbar.',
            shareUserSelectorLabel: 'Empfänger (Benotzer)',
            shareGroupSelectorLabel: 'Empfänger (Organisatiounsgruppen)',
            selectUsersPlaceholder: 'Wielt Benotzer…',
            selectOrgGroupsPlaceholder: 'Wielt Organisatiounsgruppen…',
            loadingOptions: 'Lueden…',
            loadFailed: 'Konnt Optiounen net lueden.',
            parentRecordTypes: {
                dpia: 'DPIA',
                vendor: 'Liwwerant',
                incident: 'Tëschefall',
                rights_request: 'Rechter-Ufro',
                contract: 'Kontrakt',
                data_mapping_activity: 'RoPA-Aktivitéit',
                compliance_rule: 'Compliance-Regel',
                program: 'Programm',
            },
            cancelButton: 'Ofbriechen',
            closeButton: 'Zoumaachen',
            createButton: 'Erstellen',
            creating: 'Erstellt…',
            saveButton: 'Späicheren',
            saving: 'Späichert…',
            shareWorkflowHeading: 'Workflow deelen',
            shareIntro: 'Deelt: {title}',
            shareUserIds: 'Benotzer-IDen (mat Espace oder Komma getrennt)',
            shareGroupIds: 'Organisatiounsgrupp-IDen (mat Espace oder Komma getrennt)',
            shareAllowEdit: "Empfänger erlaben z'änneren",
            shareRequireRecipient: "Gitt op d'mannst eng Benotzer- oder Grupp-ID un.",
            shareSuccess: 'Gedeelt.',
            sharing: 'Deelt…',
            runWorkflowHeading: 'Ausféieren: {title}',
            runStarting: 'Start…',
            runStatusLabel: 'Status: {status}',
            runProducedCounts: 'Aufgaben: {tasks} · Beweis-Entwërf: {evidence} · Unhäng: {attachments}',
            runPanelHeading: 'Workflow-Ausféierung',
            runPanelHeadingWithTitle: 'Workflow-Ausféierung: {title}',
            runViewTasks: 'Generéiert Aufgaben uweisen',
            runToolCallWarning: 'Forseti attempted a tool this workflow could not run, and returned the attempt as text. Nothing was produced from it.',
            runNoEnvelopeWarning: 'Forseti returned a result this workflow could not read, so no tasks or evidence were created from it.',
            runNoArtefactsWarning: 'This run completed without creating any tasks or draft evidence. Review the result before treating the work as done.',
            runLoadFailed: 'D’Workflow-Ausféierung konnt net geluede ginn.',
            runStreamFailed: 'D’Verbindung fir de Workflow-Fortschrëtt ass feelgeschloen.',
            runStatusValues: {
                pending: 'Ausstoend',
                running: 'Leeft',
                completed: 'Ofgeschloss',
                succeeded: 'Erfollegräich',
                failed: 'Feelgeschloen',
                cancelled: 'Ofgebrach',
            },
            runProgressStatus: 'Status: {status}',
            runProgressTaskCreated: 'Aufgab erstallt.',
            runProgressEvidenceDrafted: 'Beweis-Entworf erstallt.',
            runProgressAttachmentGenerated: 'Unhang generéiert.',
            guidedWorkflowTitle: 'Guidéierte Workflow fir {recordType}',
            guidedWorkflowDescription: 'Fir Datesätz vum Typ {recordType} erstallt. Iwwerpréift a personaliséiert dëse Workflow ier Dir e späichert.',
            guidedWorkflowPrompt: 'Iwwerpréift de geliwwerten Datesaz vum Typ {recordType}, identifizéiert feelend Fakten a proposéiert fundéiert nächst Schrëtt fir mënschlech Iwwerpréiwung.',
            promptHeading: 'Prompt',
            editWorkflowHeading: 'Workflow änneren',
            linkedTasksHint: 'Tasks created by this workflow show up in the Tasks list, filtered by this workflow’s ID.',
            notFound: 'Workflow net fonnt.',
            schedules: {
                tabTitle: 'Zäitpläng',
                sectionHeading: 'Geplangte Ausféierungen',
                empty: 'Nach keng Zäitpläng. Erstellt een, fir dëse Workflow automatesch auszeféieren.',
                addButton: 'Zäitplang dobäisetzen',
                addHeading: 'Workflow plangen',
                editHeading: 'Zäitplang änneren',
                fieldFrequency: 'Frequenz',
                fieldDayOfWeek: 'Wochendag',
                fieldDayOfMonth: 'Dag vum Mount',
                fieldTime: 'Zäit (UTC)',
                fieldTimezone: 'Zäitzon',
                fieldProject: 'Projet (optional)',
                fieldAttachments: 'Standard-Unhäng',
                fieldPersonaOverride: 'Persona-Iwwerschreiwung (optional)',
                frequencyDaily: 'Deeglech',
                frequencyWeekly: 'Wëchentlech',
                frequencyMonthly: 'Mounatlech',
                weekdaySun: 'Sonndeg',
                weekdayMon: 'Méindeg',
                weekdayTue: 'Dënschdeg',
                weekdayWed: 'Mëttwoch',
                weekdayThu: 'Donneschdeg',
                weekdayFri: 'Freideg',
                weekdaySat: 'Samschdeg',
                statusActive: 'Aktiv',
                statusPaused: 'Paus',
                nextRunLabel: 'Nächst Ausféierung',
                lastRunLabel: 'Lescht Ausféierung',
                pauseButton: 'Paus',
                resumeButton: 'Weiderféieren',
                deleteButton: 'Läschen',
                triggerButton: 'Elo ausféieren',
                savingButton: 'Späichert…',
                saveButton: 'Späicheren',
                triggered: "Ausféierung gestart — kuckt d'Ausféierungsgeschicht.",
            },
            branching: {
                helpHeading: 'Bedingt Prompt-Syntax',
                helpBody: 'Use {% if persona.slug == "forseti-legal" %}…{% endif %} to vary the prompt by context. Variables with a value: persona.slug (the persona chosen when the run starts, otherwise the workflow’s persona), org.country and org.locale (your organisation’s country and default language), and project.name (the project the run belongs to). attachment.framework_code, attachment.locale and project.tier are accepted but currently have no source, so {{ }} on them renders empty and a condition on them is always false. Nested {% if %} blocks are not supported.',
            },
            runCancelButton: 'Cancel run',
            runCancelling: 'Cancelling…',
            runCancelRequested: 'Cancellation requested. The model request already under way cannot be recalled, but when it returns its answer is discarded and no tasks, draft evidence or files are created.',
            runCancelledNote: 'This run was cancelled. No tasks, draft evidence or files were created from it.',
            runCancelFailed: 'Could not cancel the run.',
        },
        crossFrameworkConflicts: {
            pageTitle: 'Kräiz-Kader Konflikter',
            pageSubtitle: 'Curated Katalog vu wou Är installéiert Regulatiounspäck op konfliktende Weeër interagéieren. Forseti Flächen; Mënschen entscheeden.',
            noInstalledPacks: "Keng reglementaresch Päck fir dës Organisatioun installéiert - installéieren op d'mannst zwee Päck fir Cross-Framework Konflikter opzebauen.",
            noConflicts: 'Keng Konflikter passen mat den aktuellen Filteren.',
            allAcknowledged: 'All sichtbare Konflikt gouf unerkannt. Schéin Aarbecht.',
            columnSeverity: 'Gravitéit',
            columnTitle: 'Titel',
            columnFrameworks: 'Kaderen',
            columnTopic: 'Thema',
            columnStatus: 'Status',
            filterFrameworkPlaceholder: 'All Kaderen',
            filterTopicPlaceholder: 'All Themen',
            filterSeverityPlaceholder: 'All Schwieregkeeten',
            filterStatusPlaceholder: 'All Status',
            filterStatusAcknowledged: 'Unerkannt',
            filterStatusUnacknowledged: 'Net unerkannt',
            severityCritical: 'Kritesch',
            severityHigh: 'Héich',
            severityMedium: 'Mëttelméisseg',
            severityLow: 'Niddereg',
            statusAcknowledged: 'Unerkannt',
            statusInReview: 'An Iwwerpréiwung',
            statusResolved: 'Geléist',
            statusWontFix: 'Wäert net fixen',
            statusUnacknowledged: 'Net unerkannt',
            drawerSummaryHeading: 'Resumé',
            drawerSideAHeading: 'Säit A',
            drawerSideBHeading: 'Säit B',
            drawerRequirementsLabel: 'Ufuerderunge zitéiert',
            drawerProvisionsLabel: 'Zitéert Bestëmmungen',
            drawerSuggestedResolutionHeading: 'Resolutioun proposéiert',
            drawerLegalCounselBadge: 'Juristesch Beroder review néideg',
            drawerAckHeading: 'Unerkennung',
            drawerAckStatusLabel: 'Status',
            drawerAckAssigneeLabel: 'Zougewisen an',
            drawerAckDueDateLabel: 'Verfallsdatum',
            drawerAckNotesLabel: 'Notizen',
            drawerAckButtonAcknowledge: 'Unerkennen',
            drawerAckButtonUpdate: 'Update',
            drawerAckButtonUnacknowledge: 'Unerkennen',
            // fix-sprint-20260912-2123 F23 — R-57: structural parity, EN value carried.
            drawerCloseLabel: 'Close',
            drawerAckAssigneePlaceholder: 'User ID',
            drawerAckSaveFailed: 'Could not save the acknowledgement.',
            drawerAckUnacknowledgeFailed: 'Could not remove the acknowledgement.',
            loadFailed: 'Could not load conflicts.',
            searchPlaceholder: 'Sichen Titel oder Resumé ...',
            searchButton: 'Sichen',
            searchButtonAriaLabel: 'Run Konflikt Sich',
            exportSuccessTemplate: 'CSV: {filename}',
            exportSuccessTemplateXlsx: 'XLSX: {filename}',
            exportErrorTemplate: 'Export ausgefall: {message}',
            exportAriaLabel: 'Export Konflikter',
            // fix-sprint D1 F23 — the page-level load failure now renders through
            // `withRetry`, which needs the control's label, its busy label and the
            // subject a screen reader hears after it.
            reload: 'Reload',
            reloading: 'Reloading…',
            retrySubject: 'the conflict list',
        },
        tabularReviews: {
            pageTitle: 'Tabularesch Iwwerpréiwungen',
            pageSubtitle: 'Vergläicht N Dokumenter iwwer M Attributer mat begrënnten Zitater.',
            newReview: 'Nei Iwwerpréiwung',
            newReviewHeading: 'Nei tabularesch Iwwerpréiwung',
            emptyHint: 'Nach keng tabularesch Iwwerpréiwungen.',
            docsLabel: '{n} Dokumenter',
            colsLabel: '{n} Kolonnen',
            detailSubtitle: '{docs} Dokumenter · {cols} Kolonnen',
            addColumn: 'Kolonn dobäisetzen',
            addColumnHeading: 'Kolonn dobäisetzen',
            columnInfo: 'Prompt & Format weisen',
            columnInfoAria: 'Prompt a Format fir d’Kolonn {name} weisen',
            columnInfoPrompt: 'Prompt',
            columnInfoFormat: 'Retourformat',
            columnInfoPresets: 'Erlaabt Wäerter',
            columnInfoCitations: 'Zitater',
            columnInfoCitationsRequired: 'Erfuerderlech',
            removeColumn: 'Kolonn ewechhuelen',
            removeColumnAria: 'Kolonn {name} ewechhuelen',
            removeColumnConfirm: 'D\'Kolonn "{name}" ewechhuelen? Seng extrahéiert Zelle ginn geläscht.',
            deleteReview: 'Läschen',
            deleteReviewAria: 'Iwwerpréiwung {title} läschen',
            deleteReviewConfirm: 'D\'Iwwerpréiwung "{title}" läschen? Dëst läscht permanent seng Kolonnen, Zellen an Diskussiounen.',
            runAll: 'All ausféieren',
            running: 'Leeft…',
            cancel: 'Ofbriechen',
            exportXlsx: 'Excel exportéieren',
            exportCsv: 'CSV exportéieren',
            exporting: 'Exportéiert…',
            backToList: 'Zréck zur Lëscht',
            notFound: 'Tabularesch Iwwerpréiwung net fonnt.',
            loadFailed: 'The tabular review could not be read. Reload to try again.',
            emptyPayload: 'The server answered without the tabular review. Reload to try again.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retrySubject: 'this tabular review',
            cancelFailed: 'The run could not be cancelled.',
            removeColumnFailed: 'The column could not be removed.',
            exportFailed: 'The export could not be produced.',
            noDocuments: "Hänkt Dokumenter un, fir d'Iwwerpréiwung ze starten.",
            noColumns: 'Setzt Kolonnen dobäi, fir ze definéieren wat extrahéiert gëtt.',
            tableAriaLabel: 'Extraktiounsgrid',
            threadsCountAria: '{n} Diskussiounsbäitrag/-bäiträg',
            startDiscussionAria: 'Diskussioun starten',
            docColumn: 'Dokument',
            statusPending: 'Ausstoend',
            statusRunning: 'Leeft…',
            statusFailed: 'Feelgeschloen: {msg}',
            statusCancelled: 'Ofgebrach',
            citationsCount: '{n} Zitat(er)',
            citationsView: 'Quellpassagen weisen',
            citationPage: 'Säit {n}',
            titleRequired: 'Titel ass erfuerderlech.',
            fieldTitle: 'Titel',
            fieldProjectId: 'Projet (optional)',
            fieldAttachmentIds: 'Unhäng',
            fieldColumnName: 'Kolonn-Numm',
            fieldColumnNameHelp: 'E kuerzen, beschreiwende Label fir dës Kolonn am Extraktiouns-Gitter.',
            columnNamePlaceholder: 'z. B. Vertragsufank',
            fieldExtractionPrompt: 'Extraktiouns-Prompt',
            fieldExtractionPromptHelp: 'Beschreift d Informatioun, déi Dir wëllt erausziehen. Sidd präzis fir besser Resultater.',
            columnPromptPlaceholder: 'No wat soll Forseti an den Dokumenter sichen?',
            fieldReturnFormat: 'Retourformat',
            fieldReturnFormatHelp: 'Kontrolléiert, wéi d KI hir Äntwert formatéiert (z. B. als Datum oder Zuel).',
            fieldPresets: 'Erlaabt Wäerter (een pro Linn)',
            fieldPresetsPlaceholder: 'jo\nnee\ndeelweis',
            fieldCitationsRequired: 'Zitat erfuerderlech',
            formatShortText: 'Kuerzen Text',
            formatLongText: 'Laangen Text',
            formatEnum: 'Enum',
            formatNumber: 'Zuel',
            formatDate: 'Datum',
            formatList: 'Lëscht',
            columnRequireFields: 'Numm a Prompt sinn erfuerderlech.',
            addColumnError: 'Kolonn dobäisetzen huet net funktionéiert.',
            cancelButton: 'Ofbriechen',
            createButton: 'Erstellen',
            creating: 'Erstellt…',
            addButton: 'Dobäisetzen',
            adding: 'Setzt dobäi…',
            runConfirmHeading: 'Extraktioun ausféieren',
            estimateBody: 'Geschätzt ~{units} verrechenbar Eenheeten ({confidence} Vertrauen).',
            starting: 'Start…',
            confirmRun: 'Ausféierung bestätegen',
            charts: {
                title: 'Kolonn-Diagrammer',
                tabsAria: 'Diagramm-Kolonnen',
                enumAria: 'Verdeelung vun Enum-Wäerter',
                numberAria: 'Histogramm vun Zuelverdeelung',
                dateAria: 'Datums-Zäitlinn',
                enumEmpty: 'Keng ofgeschloss Zellen.',
                numberEmpty: 'Keng numeresch Wäerter.',
                numberExcluded: '{n} Zell(en) ausgeschloss (eidel / net-numeresch / net ofgeschloss).',
                dateEmpty: 'Keng Datumswäerter.',
                dateExcluded: '{n} Zell(en) ausgeschloss.',
                unsupportedFormat: 'Net ënnerstëtzt Kolonnformat.',
            },
            threads: {
                drawerTitle: 'Diskussioun',
                loading: 'Lueden…',
                empty: "Nach keng Bäiträg. Start d'Gespréich.",
                composerLabel: 'Neien Kommentar',
                composerPlaceholder: 'Kommentar dobäisetzen…',
                post: 'Schécken',
                posting: 'Schéckt…',
                edit: 'Änneren',
                editLabel: 'Bäitrag änneren',
                delete: 'Läschen',
                save: 'Späicheren',
                cancel: 'Ofbriechen',
                close: 'Zoumaachen',
                editedBadge: 'geännert',
                loadError: 'Konnt den Thread net lueden',
                postError: 'Schécken feelgeschloen',
                editError: 'Änneren feelgeschloen',
                deleteError: 'Läschen feelgeschloen',
            },
            compare: {
                pageTitle: 'Iwwerpréiwungen vergläichen',
                leftReviewLabel: 'Lénks Iwwerpréiwung',
                rightReviewLabel: 'Riets Iwwerpréiwung',
                pickReview: 'Wielt eng Iwwerpréiwung fir ze vergläichen',
                noSharedDocuments: "Dës Iwwerpréiwungen deelen keng Dokumenter — Verglach erfuerdert op d'mannst een gemeinsame Unhang.",
                sharedDocumentsHeading: 'Gedeelt Dokumenter',
                leftOnlyHeading: 'Kolonnen nëmmen an der lénker Iwwerpréiwung',
                rightOnlyHeading: 'Kolonnen nëmmen an der rietser Iwwerpréiwung',
                differs: 'Differéiert',
                identical: 'Identesch',
                emptyValue: '—',
                compareButton: 'Vergläichen mat…',
                documentHeading: 'Dokument',
                errorMissingIds: 'Souwuel lénks wéi och riets Iwwerpréiwungs-IDen sinn erfuerderlech.',
                errorComparisonFailed: 'Verglach feelgeschloen.',
                errorLoadFailed: 'Konnt Iwwerpréiwungen net lueden.',
                pickerLoading: 'Lueden…',
                pickerNoOtherReviews: 'Keng aner Iwwerpréiwungen verfügbar fir ze vergläichen.',
                pickerCancel: 'Ofbriechen',
            },
        },
        documentReview: {
            pageTitle: 'Dokument Iwwerpréiwung',
            pageSubtitle: 'Akzeptéieren oder refuséieren AI-virgeschloe Ännerunge fir dëst Dokument.',
            sidePanelLabel: 'AI redlines',
            sidePanelHeading: 'AI redlines',
            noPendingEdits: 'Keng waarden Ännerungen.',
            pendingCount: '{count} ofhängeg',
            acceptAllButton: 'Akzeptéieren all',
            rejectAllButton: 'All refuséieren',
            acceptAllTitle: 'Akzeptéieren all waarden Ännerungen?',
            acceptAllConfirm: 'Dëst akzeptéiert {count} pendend Ännerungen an erstellt eng oder méi nei Versiounen. Fuert weider?',
            editsHeading: 'Ännerungen',
            editsEmpty: 'Keng Ännerungen ze iwwerpréiwen.',
            versionsHeading: 'Versioun Geschicht',
            versionsEmpty: 'Nach keng Versiounen.',
            editLabel: 'Edit',
            editLabelWithId: 'Änneren #{id}',
            acceptButton: 'Akzeptéieren',
            rejectButton: 'Refuséieren',
            accepting: 'Akzeptéieren…',
            rejecting: 'Refuséiert…',
            diffDeletedAria: 'Geläscht',
            diffInsertedAria: 'Agesat',
            statusPending: 'Erwaardung',
            statusAccepted: 'Akzeptéiert',
            statusRejected: 'Refuséiert',
            versionLabel: 'Versioun',
            versionAriaLabel: 'Versioun {n}',
            versionAriaLabelCurrent: 'Versioun {n} ({current})',
            versionCurrentTag: 'aktuell',
            versionSourceLabel: 'Quell',
            versionCreatedLabel: 'Geschaf',
            versionModelLabel: 'Modell',
            sourceUpload: 'Eroplueden',
            sourceUserUpload: 'Benotzer eropluede',
            sourceAssistantEdit: 'Assistent Edit',
            sourceUserAccept: 'Benotzer akzeptéiert Ännerung',
            sourceUserReject: 'Benotzer refuséiert Ännerung',
            sourceGenerated: 'Generéiert',
            extractionExtracted: 'Text extrahéiert',
            extractionFailed: 'Textextraktioun feelgeschloen',
            extractionUnsupported: 'Den Text konnt net extrahéiert ginn',
            extractionPages: '{n} Säiten',
            extractionWords: '{n} Wierder',
            extractionReasonScanned: 'Dëst schéngt e gescannt Dokument oder e Dokument mat nëmme Biller ze sinn. Forseti kann et nach net liesen — lued eng textbaséiert Datei erop oder paste den Text direkt.',
            extractionReasonEncrypted: 'D’Datei ass mat engem Passwuert geschützt. Huel de Schutz ewech a lued se nach eng Kéier erop.',
            extractionReasonMalformed: 'D’Datei konnt net geparst ginn a ass méiglecherweis beschiedegt.',
            extractionReasonParserUnavailable: 'The server’s PDF reader is not installed correctly, so the text could not be read. This says nothing about your file. Ask an administrator to check the installation.',
            extractionReasonTooLarge: 'D’Datei ass ze grouss fir ze veraarbechten.',
            extractionReasonTimeout: 'D’Veraarbechtung huet ze laang gedauert. Probéier eng méi kleng oder méi einfach Datei.',
            extractionReasonUnknown: 'Et sinn keng weider Detailer verfügbar.',
            // Sprint B - Forseti actions on the document review detail page.
            // EN values pending the next 56.x locale fan-out sprint.
            askForsetiButton: 'Ask Forseti',
            runRedlineButton: 'Run redline',
            forsetiModalTitleAsk: 'Ask Forseti — {name}',
            forsetiModalTitleRedline: 'Run redline — {name}',
            forsetiGuidanceAsk: 'Ask Forseti about this document. Pick a starter below or type your own question.',
            forsetiGuidanceRedline: 'Forseti will read this document and propose redline edits. Pick the redline starter below to begin — edits appear in the panel for you to accept or reject.',
            forsetiStarterRedline: 'Read attachment {id} and propose redline edits for legal and compliance issues',
            forsetiStarterSummarise: 'Summarise the key obligations in attachment {id}',
            forsetiStarterRisks: 'Review attachment {id} for legal risks and missing clauses',
            forsetiStarterFrameworks: 'Which compliance frameworks apply to attachment {id}?',
            textPaneLabel: 'Dokumenttext',
            textPaneTitle: 'Dokumenttext',
            textPaneVersion: 'Versioun {n}',
            textPaneEmpty: 'Et ass keen Text fir dës Versioun verfügbar. Gescannte PDFen an e puer Dokumenttypen hu keen extrahéierbaren Text.',
            downloadVersionAria: 'Download version {n}',
            downloadVersionButton: 'Download',
            rejectAllConfirm: 'This rejects {count} pending edits. The reason you give is recorded on each one.',
            rejectAllTitle: 'Reject all pending edits?',
            rejectReasonShown: 'Reason',
            rejectConfirmButton: 'Confirm rejection',
            loadRedlinesFailed: 'Failed to load redlines',
            acceptEditsFailed: 'Failed to accept edits',
            rejectEditsFailed: 'Failed to reject edits',
            batchPartialFailure: '{failed} of {total} failed: {reason}',
            actionFailed: 'Action failed',
            rejectReasonLabel: 'Reason for rejecting (optional)',
        },
        documentReviewIndex: {
            sidebarLabel: 'Dokument-Iwwerpréiwung',
            pageTitle: 'Dokument-Iwwerpréiwung',
            pageSubtitle: 'Dokumenter an Ärer Organisatioun mat KI-virgeschloen Ännerungen, déi op Iwwerpréiwung waarden, oder Forseti-baséiert Versiounen.',
            colDocument: 'Dokument',
            colLinkedRecord: 'Verlinkten Datesaz',
            colPendingEdits: 'Ausstoend Ännerungen',
            colLastActivity: 'Lescht Aktivitéit',
            colOpen: 'Opmaachen',
            open: 'Opmaachen',
            empty: 'Nach keng Dokumenter ënner Forseti-Iwwerpréiwung. Dokumenter komme an dës Schlaang, wann Forseti eng Ännerung wärend engem Chat virschléit, wann en KI-Workflow eng Redline produzéiert, oder wann Dir en Dokument iwwer de Knäppchen uewen zur Iwwerpréiwung schéckt.',
            loading: 'Lueden…',
            loadError: 'Konnt Dokumenter ënner Iwwerpréiwung net lueden.',
            retryButton: 'Erëm probéieren',
            noLinkedRecord: '—',
            pendingZero: 'Keng',
            sendForReviewButton: 'En Dokument zur Iwwerpréiwung schécken',
            sendForReviewTitle: 'En Dokument zur Iwwerpréiwung schécken',
            sendForReviewSubtitle: 'Lued en .txt-, .md-, .pdf- oder .docx-Dokument erop (bis zu 50 MB). Et gëtt zur Iwwerpréiwungsschlaang dobäigesat a Forseti mécht en Redline-Run drop.',
            sendForReviewTabUpload: 'Neien eroplueden',
            sendForReviewPickerLabel: 'Unhäng sichen',
            sendForReviewPickerPlaceholder: 'Tippt fir ze sichen…',
            sendForReviewPickerEmpty: 'Keng iwwerenstëmmend Unhäng. Probéiert eng aner Sich.',
            sendForReviewSubmit: 'Zur Iwwerpréiwung schécken',
            sendForReviewCancel: 'Ofbriechen',
            sendForReviewSubmitting: 'Schéckt…',
            sendForReviewError: "Konnt d'Dokument net zur Iwwerpréiwung schécken. Probéiert nei.",
            sendForReviewSuccess: 'Dokument zur Iwwerpréiwung geschéckt.',
            sendForReviewAcceptedExt: 'Akzeptéiert: {ext}.',
            sendForReviewMaxSize: 'Maximal Gréisst {mb} MB.',
        },
        chats: {
            exportError: 'Export feelgeschloen.',
        },
    },
    pdplEvidencePacks: {
        title: 'PDPL-Beweispakéiten',
        subtitle: 'Vum Client gesteiert Ofginn un MoPS A05',
        nav: { emailProviders: 'E-Mail-Provider' },
        providers: {
            pageTitle: 'E-Mail-Provider',
            pageSubtitle: "Konfiguréiert den ausgaangende Mail-Provider vun Ärer Organisatioun fir d'Ofginn vu PDPL-Beweispakéiten un MoPS A05.",
            architectureRuleHeading: 'Architekturregel (gespaart)',
            architectureRuleBody: 'AesirX ass net den Ofsender vun Beweispaket-E-Mailen. All Verschécken benotzt e Provider, deen Ärer Organisatioun gehéiert a vun hir kontrolléiert gëtt. AesirX verschéckt ni an Ärem Numm.',
            empty: 'Nach keng Provider konfiguréiert.',
            createCta: 'Provider uleeën',
            statusPendingConsent: 'Wart op Zoustëmmung',
            statusPendingConnectionCheck: 'Wart op Verbindungstest',
            statusActive: 'Aktiv',
            statusConnectionCheckFailed: 'Verbindungstest feelgeschloen',
            statusDisabled: 'Deaktivéiert',
            acceptConsentCta: 'Zoustëmmung accepteieren',
            runConnectionCheckCta: 'Verbindungstest ausféieren',
            disableCta: 'Deaktivéieren',
            disableConfirm: "Wann dëse Provider deaktivéiert gëtt, ginn direkt Verschécken gestoppt, bis en neie Provider konfiguréiert an d'Zoustëmmung nei bestätegt ass. Weiderfueren?",
            columnDisplayName: 'Numm',
            columnFromAddress: 'Ofsenderadress',
            columnStatus: 'Status',
            columnLastCheck: 'Lescht Iwwerpréiwung',
            columnActions: 'Aktiounen',
            createModalTitle: 'Neie SMTP-Provider',
            createDisplayNameLabel: 'Affichagename',
            createFromAddressLabel: 'Ofsenderadress',
            createReplyToLabel: 'Äntwertadress (fakultativ)',
            createSmtpHostLabel: 'SMTP-Host',
            createSmtpPortLabel: 'SMTP-Port',
            createSmtpUsernameLabel: 'SMTP-Benotzernumm',
            createSmtpPasswordLabel: 'SMTP-Passwuert',
            createSmtpEncryptionLabel: 'Verschlësselung',
            createSmtpEncryptionStarttls: 'STARTTLS',
            createSmtpEncryptionTls: 'TLS (implizit)',
            createSmtpEncryptionNone: 'Keng',
            createOauthM365Tab: 'Microsoft 365',
            createOauthGoogleTab: 'Google Workspace',
            createOauthDisabledBadge: 'Geschwënn',
            createOauthDisabledHint: "OAuth-Provider sinn nach net aktivéiert. Benotzt fir d'éischt SMTP.",
            createSubmitCta: 'Provider uleeën',
            createCancelCta: 'Ofbriechen',
            createErrorInvalidFromAddress: "D'Ofsenderadress muss eng gülteg E-Mail-Boîte sinn.",
            createErrorDuplicateFromAddress: 'Et ass schonn en aneren Provider fir dës Ofsenderadress konfiguréiert.',
            createErrorGeneric: "De Provider konnt net ugeluecht ginn. Iwwerpréift d'Formular a probéiert et nees.",
            consentModalTitle: 'Architekturregel bestätegen',
            consentLocaleToggleEn: 'Englesch',
            consentLocaleToggleVi: 'Vietnamesesch',
            consentAcknowledgeCta: 'Ech bestätegen — Zoustëmmung accepteieren',
            consentCancelCta: 'Ofbriechen',
            consentErrorMismatch: 'Den Zoustëmmungstext gouf geännert a passt net méi mat der kanonescher Erklärung iwwereneen. Open den Dialog nees a späichert nei.',
            testModalTitle: 'Verbindungstest',
            testRunningCta: 'Verbindung gëtt getest…',
            testRunCta: 'Verbindungstest ausféieren',
            testRetryCta: 'Verbindungstest nees probéieren',
            testSuccessHeading: 'Verbindung erfollegräich',
            testSuccessBody: 'Authentifiéiert géint {host}:{port} an {latencyMs} ms. De Provider ass prett fir direkt Verschécken.',
            testFailureHeading: 'Verbindung feelgeschloen',
            testFailureBody: "Den Test konnt net ofgeschloss ginn. Iwwerpréift d'Ursaach hei drënner a probéiert et no der Korrektur nees.",
            testFailureReasonLabel: 'Feelerursaach',
            testCloseCta: 'Zoumaachen',
            detailDrawerTitle: 'Providerdetailer',
            detailDrawerHistoryHeading: 'Historik vun de Verbindungstesten',
            detailDrawerConsentHeading: 'Zoustëmmungsbestätegung',
            detailDrawerNoHistory: 'Nach keng Verbindungstesten dokumentéiert.',
            detailDrawerNoConsent: "D'Zoustëmmung ass nach net bestätegt.",
            outcomeSuccess: 'Erfollegräich',
            outcomeFailure: 'Feelgeschloen',
        },
        builder: {
            pageTitle: 'Beweispakéit zesummestellen',
            pageSubtitle: "Lued déi 6 Deelfichieren erop, wielt de Pakettyp a stellt déi deterministesch 8-deeleg ZIP fir d'Aginn zesummen.",
            packTypeLabel: 'Pakettyp',
            packTypeDpia: 'Processing impact-assessment pack (notice 02a/02b; report 10)',
            packTypeOtia: 'Cross-border impact-assessment pack (notice 01a/01b; report 09)',
            part01CoverLetter: 'Deel 01 — Begleitbréif (PDF)',
            part02AuthorityForm: 'Deel 02 — Autoritéitsformular (PDF)',
            part03Supporting: 'Deel 03 — Ënnerstëtzend Beweiser (≥1)',
            part04Safeguards: 'Deel 04 — Schutzmoossnamen',
            part04SafeguardsRequiredOtia: '(erfuerderlech fir OTIA)',
            part05PriorCorrespondence: 'Deel 05 — Fréier Korrespondenz (optional)',
            part06Signature: 'Deel 06 — Ënnerschrëft- a Stempelbeweis (≥1)',
            uploadPickFile: 'Fichier wielen',
            uploadPickFiles: 'Fichieren wielen',
            uploadRemoveFile: 'Ewechhuelen',
            requiredMissing: 'Erfuerderlechen Deel feelt',
            assembleCta: 'Paket zesummestellen',
            assembling: 'Stellt zesummen…',
            assembleSuccessHeading: 'Paket zesummegestallt',
            assembleSuccessSummary: '{fileCount} Fichieren · {bytes} Byte · SHA-256 {shaPrefix}…',
            composeNextCta: 'E-Mail-Entworf schreiwen',
            warningsHeading: 'Validatiouns-Warnungen',
            assembleError: "D'Paket konnt net zesummegestallt ginn. Iwwerpréift d'Daten a probéiert et nees.",
            forsetiAssistHeading: 'Forseti AI-Ënnerstëtzung (optionell)',
            forsetiAssistHint: "Lafft den PDPL Evidence Pack Assembly Advisor fir feelend Deeler ze weisen, Begleetschrëft-Prosa ze entwerfen an Sécherheetsläck ze markéieren. AesirX ass net den Sender — d'AI berät nëmmen; Dir genehmegt all Artefakt.",
            forsetiAssistCta: 'Forseti Workflow-Bibliothéik opmaachen',
            forsetiAiRunIdLabel: 'Verlinkten Forseti AI-Laaf-ID (klieft fir AI-Ënnerstëtzung opzehuelen)',
        },
        draft: {
            pageTitle: 'E-Mail-Entworf',
            pageSubtitle: "Schreift an iwwerschafft d'E-Mail-Entworf. Direkt Schécken kënnt an enger spéiderer Versioun.",
            composeCta: 'Entworf schreiwen',
            composing: 'Schreift…',
            noDraftYet: 'Nach keen Entworf. Schreift een fir unzefänken.',
            localeToggleLabel: 'Sprooch',
            localeEn: 'Englesch',
            localeVi: 'Vietnamesesch',
            subjectLabel: 'Sujet',
            bodyLabel: 'Inhalt',
            saveCta: 'Ännerunge späicheren',
            saving: 'Späichert…',
            saveError: 'Den Entworf konnt net gespäichert ginn. Probéiert et nees.',
            manifestSummaryLabel: 'Manifest-Iwwersiicht',
            coverLetterLabel: 'Begleitbréif (PDF)',
            coverLetterDownloadCta: 'PDF eroflueden',
            editHistoryHeading: 'Ännerungshistorik',
            editHistoryEmpty: 'Nach keng Ännerungen.',
            editHistoryEntry: 'v{version} · {fields} · {when} · {who}',
            unofficialChannelHeading: 'NQ22 inoffiziellen Kanal-Notiz',
            uploadSentProofCta: 'Manuelle Versandbeweis eroplueden',
            versionLabel: 'Versioun',
            statusDraft: 'Entworf',
            statusEdited: 'Iwwerschafft',
            statusFinalized: 'Ofgeschloss',
        },
        sentProof: {
            modalTitle: 'Manuelle Versandbeweis eroplueden',
            architectureLockNote: 'AesirX ass net de Sender. Dëse Beweis hält de manuelle Versand fest, deen Dir iwwer Äert eegent Mail-Programm gemaach hutt.',
            fileLabel: 'Beweisfichier (.eml, .png, .jpg)',
            envelopeFromLabel: 'Vun',
            envelopeToLabel: 'Un',
            envelopeSubjectLabel: 'Sujet (beim Schécken)',
            envelopeSentAtLabel: 'Geschéckt um',
            envelopeAttachmentShaLabel: 'Unhang SHA-256 (beim Schécken)',
            uploadCta: 'Beweis eroplueden',
            uploading: 'Luet erop…',
            uploadSuccess: 'Versandbeweis eropgelueden.',
            uploadErrorInvalidMime: 'Fichiertyp net erlaabt. Benotzt .eml, .png, .jpg oder .jpeg.',
            uploadErrorGeneric: 'De Beweis konnt net eropgelueden ginn. Probéiert et nees.',
            existingProofsHeading: 'Registréiert Versandbeweiser',
            existingProofsEmpty: 'Nach keng Versandbeweiser registréiert.',
            proofEntry: '{when} · {filename}',
        },
        packDetail: {
            pageTitle: 'Beweispakéit',
            notFoundMessage: 'Pak net fonnt — et kéint geläscht ginn sinn oder de Link ass falsch.',
            manifestHeading: 'Manifest',
            dossierLabel: 'Dossier',
            packShaLabel: 'Paket-SHA-256',
            fileCountLabel: 'Fichieren',
            bytesLabel: 'Gréisst',
            composeDraftCta: 'Entworf schreiwen',
            sendComingSoonCta: 'Direkt schécken: kënnt geschwënn',
            sendComingSoonHint: "De Flux fir direkt Schécken ass an aktiver Entwécklung. Benotzt 'Manuelle Versandbeweis eroplueden' op der Entworfssäit bis derhinner.",
            sendCta: 'Iwwer konfiguréierten Ubidder verschécken',
            sendDisabledNoProviderHint: 'Konfiguréiert als éischt e kontrolléierten E-Mail-Ubidder. AesirX verschéckt ni a Ärem Numm.',
            timelineHeading: 'Versandgeschicht',
        },
        // Sprint 58.4.2 (Phase 25 / Sprint E2) — direct-send + timeline + authority response + hard-copy follow-up.
        send: {
            // Send dialog
            dialogTitle: 'Beweispakéit verschécken',
            providerPickerLabel: 'Konfiguréierten E-Mail-Ubidder',
            providerPickerHint: 'AesirX verschéckt ni a Ärem Numm. Wielt ee vun Äre konfiguréierten Ubidder hei drënner.',
            providerPickerEmpty: 'Keng aktiv Ubidder konfiguréiert. Konfiguréiert ee op der Säit fir E-Mail-Ubidder ier Dir verschéckt.',
            providerPickerEmptyCta: 'E-Mail-Ubidder opmaachen',
            draftPickerLabel: 'E-Mail-Entworf',
            draftPickerOption: '{locale} · v{version}',
            draftPickerEmpty: 'Et goufen nach keng Entwierf fir dëse Pak geschriwwen. Schreift als éischt een op der Entworfssäit.',
            recipientToLabel: 'Un',
            recipientToHint: 'Eng oder méi E-Mail-Adressen, getrennt duerch Kommaen.',
            recipientCcLabel: 'Cc (mat Komma getrennt)',
            recipientCcHint: 'Optional. Komma-getrennte Lëscht vun E-Mail-Adressen.',
            recipientBccLabel: 'Bcc (mat Komma getrennt)',
            recipientBccHint: 'Optional. Komma-getrennte Lëscht vun E-Mail-Adressen.',
            consentReaffirmHeading: 'Astëmmung vum Ubidder bekräftegen',
            submitCta: 'Elo verschécken',
            sending: 'Gëtt verschéckt…',
            cancelCta: 'Ofbriechen',
            sendSuccess: 'Versand registréiert. De Pak gouf iwwer Äre konfiguréierten Ubidder iwwerdroen.',
            sendErrorGeneric: 'De Pak konnt net verschéckt ginn. Iwwerpréift d’Feelermeldung hei drënner a probéiert et nach eemol.',
            sendErrorAlreadyFinalized: 'Dësen Entworf gouf scho fir en erfollegräiche Versand benotzt. Schreift een neien Entworf ier Dir nach eng Kéier verschéckt.',
            sendErrorInvalidRecipients: 'Eng oder méi Empfängeradressen sinn keng gülteg E-Mail-Boîten.',
            // Timeline
            timelineEmpty: 'Nach keng Versendungen registréiert. Benotzt uewen "Iwwer konfiguréierten Ubidder verschécken" fir iwwer Ären eegene SMTP / Microsoft 365 / Google Workspace Ubidder ze iwwerdroen.',
            timelineProviderLabel: 'Iwwer Ären konfiguréierten Ubidder verschéckt',
            timelineSentAtLabel: 'Verschéckt',
            timelineRecipientLabel: 'Empfänger',
            timelineOutcomeSent: 'Verschéckt',
            timelineOutcomeFailed: 'Feelgeschloen',
            timelineViewDetailsCta: 'Detailer kucken',
            // Authority-response chips
            authorityChipNone: 'Op Äntwert waarden',
            authorityChipAcknowledgement: 'Empfangsbestätegung',
            authorityChipAcceptance: 'Unhuelung',
            authorityChipRejection: 'Refus',
            authorityChipSupplementRequest: 'Ufro fir Ergänzung',
            authorityChipNoResponse: 'Keng Äntwert',
            // Hard-copy chips
            hardCopyChipNone: 'Keng Pabeier-Suiviëlung',
            hardCopyChipPending: 'Verschéckt — Liwwerung steet aus',
            hardCopyChipDelivered: 'Geliwwert',
            // Drawer
            drawerTitle: 'Versanddatesaz',
            drawerEnvelopeHeading: 'Ëmschlag',
            drawerEnvelopeFromLabel: 'Vu',
            drawerEnvelopeToLabel: 'Un',
            drawerEnvelopeCcLabel: 'Cc',
            drawerEnvelopeBccLabel: 'Bcc',
            drawerEnvelopeSubjectLabel: 'Sujet',
            drawerEnvelopeMessageIdLabel: 'Message-ID',
            drawerOutcomeHeading: 'Resultat',
            drawerSnapshotHeading: 'Schnappschoss vun der Chain-of-Custody',
            drawerSnapshotPackShaLabel: 'Paket-SHA-256 beim Versand',
            drawerSnapshotDraftVersionLabel: 'Entworfsversioun beim Versand',
            drawerSnapshotAttachmentLabel: 'Iwwerdroen ZIP',
            drawerSnapshotAttachmentDownloadCta: 'ZIP eroflueden',
            drawerAuthorityResponseHeading: 'Äntwert vun der Autoritéit',
            drawerHardCopyHeading: 'Pabeier-Suiviëlung',
            drawerSentByLabel: 'Verschéckt vun',
            drawerSendIdLabel: 'ID vum Versanddatesaz',
            drawerSmtpResponseLabel: 'SMTP-Äntwert',
            drawerFailureReasonLabel: 'Grond fir de Feeler',
            drawerLatencyLabel: 'Latenz',
            drawerLatencyValue: '{ms} ms',
            drawerAcceptedLabel: 'Akzeptéiert',
            drawerRejectedLabel: 'Ofgewisen',
            drawerCloseCta: 'Zoumaachen',
            // Authority-response panel
            authorityRecordCta: 'Äntwert erfaassen',
            authorityReclassifyCta: 'Nei klasséieren',
            authorityClassificationLabel: 'Klassifikatioun',
            authorityReceivedAtLabel: 'Krut den',
            authorityFreeTextLabel: 'Notizen (fakultativ)',
            authorityEvidenceLabel: 'Beweisdatei (.eml / .pdf / .png / .jpg — fakultativ)',
            authorityReasonLabel: 'Grond fir d’Nei-Klassifizéierung',
            authorityHistoryHeading: 'Geschicht vun den Nei-Klassifikatiounen',
            authorityHistoryEmpty: 'Keng Nei-Klassifikatiounen erfaasst.',
            authorityHistoryEntry: '{when} · {previous} → {next} · {who}{reason}',
            authoritySubmitCta: 'Äntwert späicheren',
            authoritySubmitting: 'Gëtt gespäichert…',
            authorityErrorInvalidMime: 'Dëse Beweisdateityp ass net erlaabt. Benotzt .eml, .pdf, .png oder .jpg.',
            authorityErrorGeneric: 'D’Äntwert konnt net gespäichert ginn. Probéiert et w.e.g. nach eng Kéier.',
            authorityRecordedAtLabel: 'Erfaasst den',
            authorityRecordedByLabel: 'Erfaasst vun',
            authorityEvidenceFilenameLabel: 'Beweis',
            authorityEvidenceDownloadCta: 'Beweis eroflueden',
            // Hard-copy panel
            hardCopyRecordCta: 'Pabeier-Suiviëlung erfaassen',
            hardCopyConfirmCta: 'Liwwerung bestätegen',
            hardCopyEditNotesCta: 'Liwwernotizen änneren',
            hardCopyPostedAtLabel: 'Opginn den',
            hardCopyCourierLabel: 'Coursier',
            hardCopyTrackingLabel: 'Suivinummer',
            hardCopyRecipientLabel: 'Empfänger um Etikett',
            hardCopyReceiptLabel: 'Quittungs-Beweis (.pdf / .png / .jpg — fakultativ)',
            hardCopyReceiptDownloadCta: 'Quittung eroflueden',
            hardCopyDeliveredAtLabel: 'Geliwwert den',
            hardCopyDeliveryNotesLabel: 'Liwwernotizen (fakultativ)',
            hardCopySubmitCta: 'Suiviëlung späicheren',
            hardCopySubmitting: 'Gëtt gespäichert…',
            hardCopyErrorInvalidMime: 'Dëse Quittungsdateityp ass net erlaabt. Benotzt .pdf, .png oder .jpg.',
            hardCopyErrorGeneric: 'D’Suiviëlung konnt net gespäichert ginn. Probéiert et w.e.g. nach eng Kéier.',
        },
    },
    // Sprint 62.53.0 — organisation-profile statements: the recorded attributes'
    // names and the applicability review a statement of "unknown" raises.
    org_profile_statements: {
        attribute_names: {
            data_exploitation_routes: 'Data exploitation routes',
            regulated_entity_types: 'Regulated entity types',
        },
        review_task: {
            title: 'Applicability review: {attribute}',
            description: 'Your organization stated that it does not know its answer for “{attribute}”. The requirements that depend on it await an applicability review and are not treated as out of scope. Record the answer on the Organization profile page.',
        },
        nav_label: 'Organization profile',
        page: {
            title: 'Organization profile',
            description: 'What your organization states about itself: the industry sectors it operates in, the data exploitation routes it takes part in and the regulated-entity role it holds. Your installed frameworks read these statements to decide which of their requirements apply to you. A requirement that depends on an answer you have not given, or have stated as unknown, awaits an applicability review and is never treated as out of scope.',
            sectors_heading: 'Industry sectors',
        },
        section: {
            state_legend: 'Your answer',
            state_selected: 'Selected',
            state_selected_help: 'One or more of the options below apply to your organization. Tick each one that does.',
            state_none: 'None of these',
            state_none_help: 'None of the options apply. Requirements that depend only on them read as out of scope.',
            state_unknown: 'Unknown — review needed',
            state_unknown_help: 'You do not know yet. An applicability review is added to your task list, and the requirements that depend on this answer are never treated as out of scope.',
            values_legend: 'Options that apply',
            values_disabled_hint: 'Choose “Selected” to pick the options that apply.',
            values_required: 'Choose at least one option, or choose another answer.',
            used_by_label: 'Used by',
            used_by_none: 'No installed framework names this option.',
            used_by_item: '{framework} ({count} requirements)',
            used_by_item_one: '{framework} (1 requirement)',
            used_by_item_reference: '{framework} ({count} requirements, installed for reference only)',
            used_by_item_reference_one: '{framework} (1 requirement, installed for reference only)',
            note_label: 'Note',
            note_hint: 'Optional. The basis for this answer, in your own words (up to {max} characters).',
            evidence_heading: 'Evidence',
            evidence_hint: 'Files that support this answer. A file named by a saved statement is kept for as long as the statement history exists.',
            evidence_none: 'No evidence attached.',
            evidence_not_held: 'File no longer held',
            evidence_upload_label: 'Attach evidence',
            evidence_unbind: 'Remove from statement',
            evidence_unbind_label: 'Remove {filename} from this statement',
            evidence_unbind_not_held_label: 'Remove the file that is no longer held from this statement',
            evidence_limit: 'A statement can name at most {max} files.',
            current_none: 'No statement recorded yet. Requirements that depend on this answer await an applicability review.',
            current_revision: 'Current statement: revision {revision}, recorded {date}.',
            save: 'Save statement',
            saving: 'Saving…',
            reset: 'Reset',
            saved: 'Statement saved.',
            save_failed: 'The statement could not be saved.',
            conflict: 'Someone else changed this statement while you were editing it (you read revision {expected}; it is now revision {current}). Nothing was saved, and your changes are still on screen.',
            reload_current: 'Reload the current statement',
            evidence_not_held_error: 'Some evidence files named in this statement are no longer held ({count}). Remove them from the statement and save again.',
            load_failed: 'This statement could not be read.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the statement for {attribute}',
            read_only: 'You can view this statement but not change it.',
            history_show: 'Show statement history ({count})',
            history_hide: 'Hide statement history',
            history_caption: 'Statement history, newest first',
            history_revision: 'Revision',
            history_date: 'Recorded',
            history_answer: 'Answer',
            history_values: 'Options',
            history_note: 'Note',
            history_evidence: 'Evidence files',
            history_none: '—',
        },
        attribute_descriptions: {
            data_exploitation_routes: 'The routes by which your organization takes part in the exploitation of data held or offered by the State. Requirements that depend on a route apply only when you select it.',
            regulated_entity_types: 'The regulated-entity role your organization holds in the credit-cooperative and deposit-protection system, if any. Requirements addressed to a role apply only when you select it.',
        },
        value_labels: {
            data_exploitation_routes: {
                public_data_access_request: 'Requesting access to State-held data',
                state_data_centre_exploitation: 'Exploiting data at a State data centre',
                state_data_incubator: 'Taking part in a State data incubator',
                data_controlled_testing: 'Controlled testing of a data solution',
                state_data_centre_data_sharing: 'Sharing data with a State data centre',
                state_data_centre_investment: 'Investing in or sponsoring a State data centre',
                foreign_party_data_cooperation: 'Data cooperation with a foreign party',
                state_data_incentive_application: 'Applying for a State data incentive',
            },
            regulated_entity_types: {
                credit_cooperative: 'People’s credit fund',
                cooperative_central_bank: 'Cooperative bank',
                deposit_insurer: 'Deposit insurer',
                microfinance_institution: 'Microfinance institution',
            },
        },
        value_help: {
            data_exploitation_routes: {
                public_data_access_request: 'Your organization requests access to, or the exploitation of, data held by State bodies through a formal request.',
                state_data_centre_exploitation: 'Your organization exploits data that a State data creation and exploitation centre makes available.',
                state_data_incubator: 'Your organization takes part in a data incubation programme the State runs.',
                data_controlled_testing: 'Your organization tests a data product, service or solution under a controlled-testing mechanism.',
                state_data_centre_data_sharing: 'Your organization supplies or shares data with a State data centre, without exploiting the centre’s data.',
                state_data_centre_investment: 'Your organization invests in, contributes assets to or sponsors a State data centre, without exploiting the centre’s data.',
                foreign_party_data_cooperation: 'Your organization cooperates with a foreign organization or individual in these data activities.',
                state_data_incentive_application: 'Your organization applies for, or receives, a State incentive or support for data activities. Applying is not exploiting data.',
            },
            regulated_entity_types: {
                credit_cooperative: 'Your organization is a credit institution organised as a cooperative that takes deposits from and lends to its members.',
                cooperative_central_bank: 'Your organization is the cooperative bank of the credit-cooperative system.',
                deposit_insurer: 'Your organization insures the deposits held at credit institutions.',
                microfinance_institution: 'Your organization is a microfinance institution.',
            },
        },
        review: {
            title: 'Applicability review',
            description: 'Requirements of your installed frameworks that wait on an answer on this page. Each one applies, or is out of scope, once the answer is recorded; until then it is not treated as out of scope.',
            state_not_stated: 'No statement recorded',
            state_selected: 'Selected',
            state_none: 'None of these',
            state_unknown: 'Unknown — review needed',
            group_state: 'Current answer: {state}',
            group_count: '{count} requirements await this answer.',
            group_count_one: '1 requirement awaits this answer.',
            group_empty: 'No requirement of an installed framework awaits this answer.',
            group_empty_reference: 'No requirement of a directly applicable framework awaits this answer.',
            reference_intro: 'These frameworks are installed for reference only, so their requirements are not applied to your organization. If they were installed as directly applicable, these requirements would await this answer:',
            reference_item: '{framework} ({count} requirements)',
            reference_item_one: '{framework} (1 requirement)',
            column_framework: 'Framework',
            column_requirement: 'Requirement',
            load_failed: 'The applicability review could not be read.',
            retry_subject: 'the applicability review',
        },
        outcome: {
            column: 'Applies to your organization',
            applicable: 'Applies',
            out_of_scope: 'Out of scope',
            not_applicable: 'Reference only — not assigned to any organization',
            installed_for_reference: 'Installed for reference only — not applied to your organization',
            not_entitled: 'Not in your package — not applied to your organization',
            not_declared: 'Not declared',
            profile_incomplete: 'Awaiting your organization profile',
            profile_incomplete_named: 'Awaiting your organization profile: {attributes}',
            attributes: {
                sector: 'industry sectors',
                data_exploitation_routes: 'data exploitation routes',
                regulated_entity_types: 'regulated entity types',
            },
        },
    },
    // Sprint 63.37.0 (E0, E5) — English until the Phase 25 locale pass (R-57).
    org_identity: {
        nav_label: 'Organisation identity',
        module_guide: 'Organisation identity: the legal name, offices, registration, tax code, contacts and legal representative that official forms and cover letters are filled from.',
        title: 'Organisation identity',
        description: "Record your organisation's legal name, offices, registration, tax code, contacts and legal representative once. Official forms are filled from this record when you choose Fill from record, and cover letters read it too.",
        section_names: 'Names and offices',
        section_registration: 'Registration',
        section_contact: 'Contact details',
        section_representative: 'Legal representative',
        field_legal_name: 'Legal name',
        field_legal_name_help: 'The name exactly as it appears on the registration certificate.',
        field_trading_name: 'Trading name',
        field_trading_name_help: 'A short or trading name, if the organisation uses one.',
        field_head_office_address: 'Head office address',
        field_transaction_office_address: 'Transaction office address',
        field_transaction_office_address_help: 'Leave blank if it is the same as the head office.',
        field_registration_number: 'Registration Number',
        field_registration_instrument: 'Registration document',
        field_registration_instrument_help: 'The document that records the registration, for example a business registration certificate or an establishment decision.',
        field_registration_issuer: 'Issued by',
        field_registration_issue_date: 'Date of issue',
        field_registration_issue_place: 'Place of issue',
        field_tax_code: 'Tax Code',
        field_tax_code_help: 'The tax code is recorded separately from the registration number, even where the two are the same.',
        field_phone: 'Phone',
        field_email: 'Email',
        field_website: 'Website',
        field_website_help: 'Starting with https:// or http://.',
        field_legal_representative_name: 'Full name',
        field_legal_representative_title: 'Position',
        save: 'Save identity',
        saving: 'Saving…',
        saved: 'Organisation identity saved.',
        save_failed: 'The organisation identity could not be saved.',
        fix_fields: 'Some fields need correcting before the identity can be saved.',
        load_failed: 'The organisation identity could not be read.',
        retry: 'Retry',
        conflict_message: 'Someone else saved the organisation identity after you opened this page. Your changes were not saved. Reload the saved identity, then make your changes again.',
        conflict_reload: 'Reload the saved identity',
        conflict_reloaded: 'The saved identity is shown. Make your changes again, then save.',
        loading: 'Loading the organisation identity',
        read_only: 'You can view the organisation identity. Saving it needs the settings write permission.',
        restricted_title: 'You do not have access to the organisation identity',
        restricted_message: 'Ask an administrator for the settings read permission.',
        last_saved: 'Last saved {date}',
        never_saved: 'Nothing has been recorded yet. Fields left blank are left blank on forms too.',
        error_too_long: 'This value is too long.',
        error_invalid_date: 'Enter a real date.',
        error_invalid_email: 'Enter an email address, for example name@example.com.',
        error_invalid_url: 'Enter a web address starting with https:// or http://.',
        error_invalid_phone: 'Use digits, spaces and + ( ) . - only.',
        error_invalid: 'This value is not accepted.',
    },
    form_prefill: {
        draft_undeclared: "The form does not say where {count} of the draft's fields go, so they were not drafted.",
        fill_from_record: 'Fill from record',
        fill_from_named_record: 'Fill from {record}',
        filling: 'Filling…',
        result: 'Filled {filled} fields; {differs} differ from the record.',
        failed: 'The form could not be filled from the record.',
        from_source: 'From {source}',
        not_recorded_in_source: 'Not recorded in {source}',
        family_organisation: 'the organisation identity',
        family_dpo: 'the DPO appointment',
        family_dpia: 'the DPIA',
        family_transfer: 'the transfer assessment',
        family_incident: 'the incident',
        family_dossier: 'the dossier filing',
        record_generic: 'the record',
        hint: 'Fills empty fields only. A value you entered is never replaced.',
    },
    // Owner rulings 2026-10-26 — idea #717 and OQ-86-6. English until the
    // Phase 25 locale work (R-57); structural parity with en.ts.
    customs_tax_exports: {
        titles: {
            dossiers: 'Customs tax dossiers and documents received',
            payments: 'Customs tax payments',
            duty_free_lists: 'Duty-free lists',
            examinations: 'Customs tax examinations',
            debt_instruments: 'Customs tax debt instruments',
            transition_statement: 'Transition statement',
        },
        sections: {
            dossiers: 'Dossiers',
            documents_received: 'Documents received',
            figures_side_by_side: 'Stated figures side by side',
            deductions: 'Deductions as customs recorded them',
            usage_notifications: 'Usage notifications recorded',
            steps: 'Steps',
            office_examinations: 'At the customs office',
            determinations: 'Determinations recorded',
            refund_records: 'Refund records',
            enforcement_notices: 'Enforcement notices received',
            instalment_payments: 'Payments recorded against instalment notices (each as its document states it; no total)',
            noted_alternatives: 'Noted alternative readings of the usage schedule',
        },
        columns: {
            paper_form_number: 'Paper form number',
            electronic_indicator_set: 'Electronic indicator set',
            matched_by_title: 'Matched by title',
            issued_by_authority: 'Issued by the authority',
            dossier: 'Dossier',
            procedure: 'Procedure',
            register_role: 'Register role',
            customs_records: 'Customs records',
            channel: 'Channel',
            signed_off_on: 'Signed off on',
            lodged_on: 'Lodged on',
            confirmation_answer: 'Confirmation answer',
            install_mode_when_made: 'Install mode when made',
            review_approval: 'Review approval',
            document: 'Document',
            kind: 'Kind',
            number_as_printed: 'Number as printed',
            received_on: 'Received on',
            stated_figures: 'Stated figures',
            stated_deadlines: 'Stated deadlines',
            reply: 'Reply',
            payment: 'Payment',
            paid_on: 'Paid on',
            stated_amount: 'Stated amount',
            payer_class: 'Payer class',
            payment_date_basis: 'Payment-date basis',
            document_number: 'Document number',
            pays: 'Pays',
            receipt: 'Receipt',
            received_document: 'Received document',
            figure: 'Figure',
            payment_figure_as_stated: 'Payment figure (as stated)',
            document_figure_as_stated: 'Document figure (as stated)',
            comparison: 'Comparison',
            closed: 'Closed',
            list: 'List',
            reference: 'Reference',
            acknowledged_on: 'Acknowledged on',
            imports_ended_on: 'Imports ended on',
            expires_on: 'Expires on',
            deduction: 'Deduction',
            customs_record: 'Customs record',
            goods_as_stated: 'Goods as stated',
            unit_as_stated: 'Unit as stated',
            cleared_as_stated: 'Cleared (as stated)',
            remaining_as_stated: 'Remaining (as stated)',
            notification: 'Notification',
            notice_kind: 'Notice kind',
            financial_year_ended_on: 'Financial year ended on',
            notified_on: 'Notified on',
            examination: 'Examination',
            purpose: 'Purpose',
            decision_signed_on: 'Decision signed on',
            decision_sent_on: 'Decision sent on',
            announced_on: 'Announced on',
            re_examination_of: 'Re-examination of',
            step: 'Step',
            occurred_on: 'Occurred on',
            refused_to_sign: 'Refused to sign',
            record: 'Record',
            authority_request: 'Authority request',
            explanation_sent_on: 'Explanation sent on',
            outcome_on: 'Outcome on',
            instrument: 'Instrument',
            role: 'Role',
            effective_on: 'Effective on',
            instalment_band: 'Instalment band',
            approved_on: 'Approved on',
            stated_debt_figure: 'Stated debt figure',
            guarantee_valid_until: 'Guarantee valid until',
            record_type: 'Record type',
            governing_date: 'Governing date',
            outcome: 'Outcome',
            reading: 'Reading',
            open: 'Open',
            determination: 'Determination',
            determined_on: 'Determined on',
            refund_record: 'Refund record',
            dossier_register_role: 'Dossier register role',
            classification: 'Classification',
            decision_document: 'Decision document',
            offset_notice_document: 'Offset notice received',
            post_refund_examination: 'Post-refund examination',
            stated_case: 'Case as the decision states it',
            stated_case_source: 'Case stated in',
            stated_case_citation: 'Provision cited for the case',
            received_as: 'Received as',
            document_date: 'Document date',
            amount_stated_for_notice: 'Amount stated for this notice',
            financial_year_reported: 'Financial year reported (ending)',
            noted_alternative_year: 'Noted alternative financial year (ending)',
            note: 'Note',
            reading_kind: 'Kind of reading',
            classification_case: 'Case the classification notice states',
            acceptance_notice: "Acceptance notice (the authority's clock)",
            handling_time: "Handling time (the authority's clock)",
            stated_case_choices: 'Installed cases matched (as stated)',
            stated_due_on: 'Due date as stated',
            period_ceiling: 'Longest instalment period',
            stated_plan_end: 'Latest end the Law allows',
            enforcement_from: "Enforcement threshold (the authority's clock)",
        },
        cells: {
            review_approved: 'Approved by {name} on {date}',
            review_approved_earlier_version: 'Approved by {name} on {date}, against an earlier version of the record',
            register_role_refund: 'Refund',
            register_role_overpaid_refund: 'Refund of an overpaid amount',
            noted_not_due: 'Noted reading, not a due date',
            received_as_third_party: 'Received as a third party',
            received_as_enforced_taxpayer: 'The taxpayer under enforcement',
            clock_authority_ends: "The authority's clock ends {date} ({clock})",
            clock_authority_ends_before_excluded: "The authority's clock ends {date} before the time not counted is added ({clock})",
            clock_authority_no_date: "The authority's clock; no date, because the event it runs from is not recorded ({clock})",
            clock_period_ends: 'The period ends {date} ({clock})',
            clock_closed: 'Closed on {date}',
            clock_not_applicable: 'Not applicable to this record ({clock})',
            clock_not_live: 'Not in force yet ({clock})',
            clock_classification_not_recorded: 'No date: the classification the authority notified is not recorded',
            clock_not_declared: 'No installed clock declares it',
            clock_instrument_not_installed: 'Stated in an instrument that is not installed ({instruments}); no date',
            clock_no_date: 'No date ({clock})',
            plan_end_by_ceiling: "{date}, the end of the longest period; a shorter schedule in the written acceptance is the plan's own",
            plan_end_by_guarantee: "{date}, the end of the guarantee's validity, which comes first; a shorter schedule in the written acceptance is the plan's own",
            clock_not_counted_before_commencement: 'Not counted by the platform: debts outstanding at the end of {outstanding} follow the Law ({reference}), but no point is shown for a due date before {activation}',
            clock_not_counted_before_commencement_unnamed: 'Not counted by the platform: no point is shown for a due date before {activation}',
            meta_boundary: 'Boundary ({framework}): {summary}',
            meta_boundary_none: 'Boundary: no installed framework declares a boundary statement for these registers.',
            meta_amounts_as_stated: 'Every amount is a figure a person entered from a document; nothing here is computed.',
            meta_statement_for: 'Statement for: {date}',
            meta_nothing_remains: 'Nothing remains open under the earlier rules.',
            meta_records_remain: 'Records remain open under the earlier rules.',
            meta_transition_unavailable: 'The transition reading is not available on this installation.',
            role_notified_debt: 'Tax debt as notified',
            role_freeze: 'Freeze',
            role_freeze_end: 'End of a freeze',
            role_write_off: 'Write-off',
            role_restoration: 'Restoration',
            role_instalment_request: 'Instalment request',
            role_instalment_notice: 'Instalment notice',
            received_document_label: '{kind} {number}, dated {date}',
            received_document_label_no_number: '{kind}, received {date}',
            case_no_longer_installed: 'A case from an instrument that is no longer installed',
        },
    },
    cross_border_filing_fence: {
        register_customs_tax_dossiers: 'Tax Dossiers and Received Documents',
        refused: 'This form is recorded in {register}, not as a cross-border filing. Record the dossier there.',
        open_register: 'Open {register}',
        option_owned_elsewhere: '{form} — recorded in {register}',
        picker_hint: 'Some installed forms are recorded in {register} and cannot be chosen here.',
        advisory_title: 'This form is now recorded in {register}',
        advisory_body: 'This filing was recorded here before forms like it moved to {register}. It stays readable here and has not been changed. Record new dossiers on this form in {register}.',
        list_marker: 'Now recorded in {register}',
    },
    // Sprint 63.43.0 — English for key parity until Phase 25 (R-57; sprint 57.7.0).
    sector_overlays_module: {
        nav: {
            overview: 'Overview',
        },
        overview: {
            title: 'Sector Overlays',
            subtitle: 'What your active sector overlays bring into use, grouped by sector.',
            loading: 'Loading sector overlays…',
            load_failed: 'Sector overlays could not be loaded.',
            not_entitled_title: 'Sector Overlays is not in your package',
            not_entitled_body: "Sector Overlays is an Enterprise module. Your organization's package does not include it, so no sector overlay is shown here.",
            empty_title: 'No sector overlay is active',
            empty_body: 'Install an overlay in the Overlay Catalog and choose Direct to bring its views into use here. An overlay installed for reference only adds no views.',
            open_catalog: 'Open the Overlay Catalog',
            organization_profile: 'Organization profile',
            organization_profile_hint: "Your organization's sectors decide which overlays apply to you directly.",
            provided_by: 'Provided by',
            views_heading: 'Views in this sector',
            shared_views_heading: 'Shared views this sector uses',
            shared_view_location: 'In {location}',
            open_view_aria: '{view} — {location}',
            no_visible_views: "None of this sector's views is open to your role.",
            sector_count_one: '1 overlay active',
            sector_count: '{count} overlays active',
        },
    },
    evidence_reports: {
        intendedFilingReadinessHint: 'A prospective filing date is required for statutory readiness for a new dossier. You may leave it blank to save a draft or gap report. An existing dossier filing can supply the basis.',
        captureUnconfirmed: 'Capture may still be running. Retry this same capture to check or recover it; a completed version is reused.',
        retryCapture: 'Check or retry this capture',
        sampleEvidenceLimitation: 'Required content may be complete, but admitted evidence is synthetic sample data and cannot establish readiness for statutory filing. Internal or training exports remain available.',
        referenceRendition: 'Reference rendition',
        languagePreservationHint: 'Language selection changes headings and labels. Saved answers and evidence retain their authored language. This output does not translate or certify the supplied text.',
        title: 'Prepare an evidence report',
        intro: 'Define the question and scope, review actual records, and freeze an attributable report. Blank templates do not demonstrate completed work.',
        legacy: 'Legacy catalog export',
        legacyHint: 'Catalog exports contain framework references. They are not reviewed evidence reports.',
        name: 'Report title',
        framework: 'Installed framework',
        purpose: 'Purpose',
        internal_readiness: 'Internal readiness review',
        authority_request: 'Response to an authority request',
        processing_assessment: 'Processing assessment dossier',
        transfer_assessment: 'Transfer assessment dossier',
        dossier_update: 'Dossier update',
        assessmentKind: 'Assessment type',
        dpia: 'Processing assessment',
        otia: 'Transfer assessment',
        applicant: 'Applicant',
        organization: 'Organization',
        individual: 'Individual',
        role: 'Organization role',
        controller: 'Controller',
        controller_processor: 'Controller and processor',
        processor: 'Processor',
        other: 'Other',
        coverage: 'Coverage',
        bounded: 'Selected activity / bounded scope',
        organization_complete: 'Complete organization and processing lifetime',
        coverageStatement: 'Scope and coverage statement',
        coverageHint: 'State what this report covers and excludes. A selected activity does not establish organization-wide statutory coverage.',
        recipient: 'Intended recipient',
        questions: 'Request questions (one per line)',
        language: 'Report language',
        en: 'English',
        vi: 'Vietnamese',
        asOf: 'Evidence as of',
        timezone: 'IANA timezone',
        periodStart: 'Period start (optional)',
        periodEnd: 'Period end (optional)',
        intendedFiling: 'Intended filing date',
        filing: 'Existing dossier filing',
        sources: 'Select actual source records',
        sourceKind: 'Source type',
        sourceSearch: 'Search authorized source records',
        sourceHint: 'Only existing records you may read are offered. Select none for an honest gap report. Search or change page to reach further records.',
        search: 'Search',
        next: 'Next page',
        previous: 'Previous page',
        remove: 'Remove',
        review: 'Review evidence and requirements',
        preview: 'Refresh review and preview',
        freeze: 'Freeze report and generate files',
        busy: 'Working…',
        failed: 'The request failed. Your entries have been retained.',
        required: 'Complete the required fields before continuing.',
        emptySources: 'No source records selected. Missing content remains explicit.',
        candidate: 'Source evidence',
        noCandidates: 'No accessible evidence supplied. This report contains gaps, not proof of completed work.',
        openSource: 'Open source record',
        inspect: 'Inspect captured content',
        sourceRevision: 'Source revision',
        state: 'Source state',
        include: 'Include',
        exclude: 'Exclude',
        decision: 'Evidence decision',
        reason: 'Reason for decision',
        requirementLinks: 'Requirements supported',
        requirements: 'Requirement assessment',
        applicability: 'Applicability',
        applicable: 'Applicable',
        not_applicable: 'Not applicable',
        undetermined: 'Undetermined',
        adequacy: 'Content adequacy',
        unreviewed: 'Unreviewed',
        adequate: 'Adequate',
        partial: 'Partial',
        unsupported: 'Unsupported',
        missing: 'Missing content',
        present: 'Content present',
        supported: 'Supported requirements',
        blockers: 'Mandatory blockers',
        ready: 'Ready for selected purpose',
        notReady: 'Not ready for selected purpose',
        statutoryReady: 'Statutory content readiness',
        readinessHint: 'Content readiness, internal approval, dispatch, official receipt and authority outcome are separate decisions. An adverse finding can be part of a complete assessment.',
        diagnostics: 'Profile and source limitations',
        supplement: 'Supplemental assessment content',
        conclusions: 'Assessment conclusions and residual risks',
        limitations: 'Limitations and unresolved questions',
        disclosure: 'Release decision',
        full: 'Release full reviewed content',
        redacted_release: 'Release selected evidence only',
        releaseReason: 'Reason and recipient disclosure review',
        releaseEvidence: 'Evidence permitted in the released copy',
        gapTask: 'Assign gap action',
        taskTitle: 'Action title',
        assignee: 'Responsible person',
        dueDate: 'Due date',
        createTask: 'Create task',
        taskCreated: 'Gap action created',
        openTask: 'Open task',
        taskHint: 'Completing a task does not establish adequate evidence. Return and refresh after the source record has been corrected.',
        previewTitle: 'Readable report preview',
        version: 'Version',
        snapshot: 'Frozen report',
        capturedAt: 'Captured at',
        capturedBy: 'Captured by',
        reviewedBy: 'Reviewed by',
        manifestHash: 'Snapshot manifest hash',
        files: 'Report and evidence files',
        download: 'Download',
        noFiles: 'Files are not available for this version.',
        newVersion: 'Revise as a new version',
        frozenHint: 'This is the frozen version. Changes require a new preview and version; existing approval applies only to its exact version.',
        pending: 'Files pending',
        finalized: 'Files finalized',
        failedState: 'File generation failed',
        sourceContent: 'Captured source content',
        attachments: 'Source attachments',
        close: 'Close',
        saveReview: 'Apply review to preview',
        profile: 'Assessment profile',
        noProfile: 'No supported assessment profile is installed for this selection.',
        returnReview: 'Return to review',
        create: 'Create report',
        cancel: 'Cancel',
        dpiaSource: 'Processing assessments',
        transfer_assessmentSource: 'Transfer assessments',
        processing_activitySource: 'Processing activities',
        systemSource: 'Systems',
        vendorSource: 'Vendors',
        consentSource: 'Consent records',
        rights_requestSource: 'Rights requests',
        incidentSource: 'Incidents',
        form_instanceSource: 'Completed / draft form instances',
        attachmentSource: 'Attachments',
        evidenceSource: 'Evidence records',
        selected: 'Selected source records',
        sourceUnavailable: 'Source unavailable or permission restricted',
        supplementHint: 'Provide the missing substantive assessment content. Empty content remains a gap.',
        choose: 'Choose…',
        page: 'Page',
        exemption: 'Assert an exemption',
        exemptionReason: 'Exemption grounds and supporting evidence',
        approvalStatus: 'Internal approval / dispatch status',
        scope: 'Report scope',
        sourceOutcomes: 'Source retrieval outcomes',
        openReportForm: 'Complete the assessment report form',
        openNoticeForm: 'Complete the applicant notice',
        formHint: 'Complete and finalize these installed forms, then select their saved instances as source records. Opening a blank form does not supply evidence.',
        reportTitle: 'Evidence report',
        final: 'Final saved content',
        draft: 'Draft content',
        historical: 'Historical content',
        unverified: 'Unverified source',
        sourceStatusOther: 'Other status',
        captureCurrent: 'Capture current saved content',
        captureHint: 'New or corrected records saved after this instant are outside this report. Keep this time for a historical report, or update it deliberately to include current saved content.',
        sourceFacts: 'Recorded facts',
        provenance: 'Source provenance',
        approveReportConfirm: 'Approve version {version} for release to the report’s intended recipient? This records your approval; it does not record statutory filing, official receipt or an authority decision.',
        consent_evidenceSource: 'Consent receipts and proofs',
        documentRoles: 'Supporting document components',
        historicalUse: 'Use historical evidence for this reporting period',
        historicalHint: 'Historical evidence can support a bounded internal review or authority response only when dated admitted supporting files establish validity throughout the selected period. It does not establish current statutory readiness.',
        historicalReason: 'Reason for using historical evidence',
        validFrom: 'Valid from (ISO date and time with offset)',
        validUntil: 'Valid until (ISO date and time with offset)',
        historicalProof: 'Dated supporting files admitted to this report',
        savedContentErrors: 'Saved form content requires correction',
        historicalCoverageError: 'The stated validity range must cover the entire reporting period.',
        linkTask: 'Link an existing task',
    },
};
//# sourceMappingURL=lb.js.map